import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useVirtualizer, elementScroll } from '@tanstack/react-virtual';
import { chatGameApi } from '../components/ChatGame/chatGameApi';
import { openBugReport, setBugPageContext } from '../utils/bugReport';
// 🔐 accounts (09-30, ACCOUNTS_SIGNIN_SPEC_2026_09_30.md §3.7): your seat follows you +
// the Details row's sign-in door. All inert while sign-in is off (src/auth/signInSwitch.js).
import { seatForRoom as accountSeatForRoom, claimSeat as accountClaimSeat } from '../auth/accountState';
import { useAccount } from '../auth/useAccount';
import { openSignIn, showAccountToast } from '../auth/accountEvents';
import { withCarriedSpace } from '../utils/carriedSpace';
// 🔌 09-28 agent wave: an agent's watch link (?with=<seat id>) highlights its seat.
import { readWithSeatId, withHighlightCss, withToastText } from '../utils/watchWith';
// 🎬🍿 09-23 movie-night batch 1 (MOVIE_NIGHT_UX_HANDOFF_2026_09_22.md §3, rulings
// 1-21): build-time switches (each false = byte-identical output) + the pieces.
import { PEEK_STAGE, TOP_CLOCK, PLATE_FOLD, COUCH, AUX_DUCK } from '../config/eventStage';
import EventStripBar from '../components/ChatGame/EventStripBar';
import CouchColumn from '../components/ChatGame/CouchColumn';
// 📚 the House library (HOUSE_LIBRARY_SPEC_2026_09_30.md §4.3 W2): every piece keys on
// house.book being PRESENT in the status payload (absent = none of this renders)
import useHouseLibrary from '../hooks/useHouseLibrary';
import BookReader, { BookFoldBar } from '../components/ChatGame/BookReader';
import LibraryShelf from '../components/ChatGame/LibraryShelf';
import { purposeHintPool, purposeFromSearch } from '../utils/purposeTips'; // 🧭 10-02 tips that match the room's purpose
import LibraryJoinLine from '../components/ChatGame/LibraryJoinLine';
import { readingAlongLine, bookChatLines, withShelfChip, serverNowFor } from '../utils/houseLibrary';
import EventFeedbackCard from '../components/ChatGame/EventFeedbackCard';
import PumpkinCarving from '../components/ChatGame/PumpkinCarving'; // 🎃 10-07 pumpkins: the carving table (a full sheet)
import { useHousePumpkins } from '../hooks/useHousePumpkins'; // 🎃 the porch, fetched only while it is looked at
import { CARVE_CHIP, carveChipHere, withCarveChip } from '../utils/pumpkinCarve'; // 🎃 the plain "Carve" chip
import { submitEventFeedback, markSeen, wasSeen, markDone, isDone, feedbackCardVisible, feedbackDismissKey } from '../utils/eventFeedback';
import { analytics, trackEvents } from '../utils/analytics';
import useDocumentTitle from '../hooks/useDocumentTitle';
import useViewport, { NAV_H, NAV_BOTTOM, navBottomPlus } from '../hooks/useViewport';
import HumanInputForm, { retractComposerDraft } from '../components/ChatGame/HumanInputForm';
import DrawingCanvas from '../components/ChatGame/DrawingCanvas';
import LocalsDossier, { dossierRevealCount } from '../components/ChatGame/LocalsDossier';
import { loadLayoutPrefs, saveLayoutPref, resolvePanelOpen, chatColumnCap } from '../utils/spaceLayout';
import { isCrowdInline, buildCrowdItems, createCrowdMergeCache, CROWD_LINE_TYPE, crowdFullLines, estimateFeedRowPx } from '../utils/crowdInline';
import PortedWindowCard from '../components/ChatGame/PortedWindowCard';
import KeepsakeShelf from '../components/ChatGame/KeepsakeShelf';
import PortedRail from '../components/ChatGame/PortedRail';
import { portedBus } from '../utils/portedBus';
// 🪟🫁🔑 09-24 ported arrival fixes (Nina): loader tips, the known-ported
// marker, the once-per-device arrival comic, the owner key link.
import { lastSpokeAgo, lastSpokeTs, placeOn, buildJournal, pinnedQuotes, linePinState, noteWaiting, doneWhileAway, favoritePins as portedFavoritePins, ownerClock as portedOwnerClock } from '../utils/portedPlace';
// 🔍📌 W2c: the owner's tools under his lines + the horizon row (PORTED_HOME_HANDOFF W2c)
import PlaceLineTools, { PlaceHorizon } from '../components/ChatGame/PlaceLineTools';
import { usePortedMemories } from '../components/ChatGame/PortedMemories'; // 🧠📖 wave 2 C2
// 💬 W2d.1 (A9): the place page's chat line (mockup v9's .lane .msg)
import PlaceChatRow, { isPlaceChatLine, placeLineWho, placeThought, placeReactions, isFreshLine, placeThoughtsLabel, ownerSpokeBefore, placeCardTheme } from '../components/ChatGame/PlaceChatRow';
import { HORIZON_LINE_TYPE, createHorizonCache } from '../utils/portedHorizon';
import PortedStage from '../components/ChatGame/PortedStage';
import PortedJournal from '../components/ChatGame/PortedJournal';
import PortedAbout, { aboutText } from '../components/ChatGame/PortedAbout';
import PortedMenu from '../components/ChatGame/PortedMenu';
import { hasExchange as portedHasExchange, roomSignals as portedRoomSignals, lastVisitBefore as portedLastVisitBefore, canMove as canMovePorted, groundFor as portedGroundFor } from '../utils/portedHomes';
import { pickPortedLoaderTip, isPortedOwnerSeat, portedOwnerSeatOf, readKnownPorted, writeKnownPorted, clearKnownPorted, hasSeenArrivalComic, markArrivalComicSeen, buildKeyLink, bootKeyLink, portedDoorwayLine } from '../utils/portedLoader';
import { residentPick } from '../utils/portedPick';
import { spectatingCue } from '../utils/spectatingCue';
import { pinsKey, useStableByKey } from '../utils/portedPins';
import LineIcon from '../components/ChatGame/LineIcons';
import CastCardModal, { CAST_MSG_TYPES } from '../components/ChatGame/CastCardModal';
import { BringYourAiModal } from '../components/BringYourAiCard';
import AmbientLayer from '../components/ChatGame/AmbientLayer';
import HouseAmbientLayers from '../components/ChatGame/HouseAmbientLayers';
import HouseRoomView from '../components/ChatGame/HouseRoomView';
import { roomSceneFallback, roomSceneInMargins, roomPeekScene, roomArrival, ARRIVE_MS, ARRIVE_WHO_DELAY_MS, ROOM_EMPTY_LINE, roomEntryLine, eventInvitePhrase, roomInviteFor, eventGoFor, roomMasthead, roomWallpaperVeil, roomArtPlan, roomVerbs, roomLookText, roomLookParts, roomPresenceLine, presenceDelta, seasonForDay, roomPickerRows, roomPickerCards, ROOM_ICONS, WALL_DIM_ARRIVE, WASH_OVER_ART, LANE_FEATHER } from '../utils/roomView';
import { liveRoomVerbs, plateChips, chipTapCommand, eventFirst } from '../utils/roomVerbs'; // 👆 10-01 one-tap · 🎛️ 10-09 event first
import { sceneCommandChips, sceneAnswer } from '../utils/sceneCommands'; // 🎛️ 10-09 (Nina): the event's buttons on the full scene
import { chapterTitledRounds, isCannedChapterOpener } from '../utils/houseChapterOpener';
import { RoomPeekLightbox, RoomKeyhole } from '../components/ChatGame/RoomPeek';
import { canKeyhole, geraldRoomFor, geraldPeekProps, readGerald, markGeraldFound, screenRectFor, lostPeekProps, lostFoundLine, moonSkyFor } from '../utils/roomPeek';
import { nightSkyFor, caughtStarAt } from '../utils/roomPeek'; // 🌠 09-26 roof night
import { recentEventStartFor, pictureCardFor, openPollFor } from '../utils/roomPeek'; // 📰 09-26 the picture's news line
import { isRoomDeepLink } from '../utils/roomView'; // 🔗 09-26 a room link skips the tour offer
import { shareHouse } from '../utils/shareLink'; // 🔗 10-08 A9: the plate + scene share (the night's page, else today's room link)
import { runningEventFrom } from '../utils/roomPeek'; // 📅🌕 09-26 the running event from the calendar
import { porchTakeFor } from '../utils/roomPeek'; // 🎃 10-07 the porch take under the carving sheet's pumpkins
import { sceneChatFor, sceneBubblesOn, sceneRoomFor, mentionTargets } from '../utils/roomPeek'; // 💬 09-26 option C: the scene's chat as bubbles
import { MoonOver } from '../components/ChatGame/RoomPeek'; // 📸🌕 09-26 the moon on a moon-night snap
import { StarWishGo } from '../components/ChatGame/RoofNight';
import RoomPlate from '../components/ChatGame/RoomPlate';
import { ASLEEP_LINE_COLOR, OWN_BUBBLE_STATUS_COLOR } from '../utils/housePartOfDay'; // 😴 09-25: the asleep status line's one color · 👥 10-06: yours on your own bubble
import { HOUSE_OWN_BUBBLE, ownBubbleColors } from '../utils/houseOwnBubble'; // 🌿 10-09 (Nina): your own bubble is sage in the House
import RoomTransit, { useRoomTransit, FittedPainting } from '../components/ChatGame/RoomTransit'; // 🚪🕯️ 09-24 between rooms
import CandleHop from '../components/ChatGame/CandleHop'; // 🕯️ 09-24 the house's "is thinking" dots
import { resolveRoomText, pickTransit, walkLine, keyholeView, wetSteps, arrivalCardMs, UNKNOWN_ROOM, peekPictureUrl, walkingDoor, PHONE_WALK_Z, PHONE_CARD_MS, PHONE_WHO_DELAY_MS } from '../utils/roomTransit';
import { RoomPickerCards } from '../components/ChatGame/RoomPicker';
import { CalendarAgenda } from '../components/ChatGame/CalendarDoor';
import { eventStrip as calendarEventStrip, doorFace as calendarDoorFace, agendaRows as calendarAgendaRows, outingBand, outingVerbs, screeningStrip, WATCH_VERB, ballotInvite, nextFeatured as calendarNextNight, DOOR_WORDS } from '../utils/calendarView';
import { meterView as powerMeterView } from '../utils/powerMeter';
import PowerMeter from '../components/ChatGame/PowerMeter';
// 🔋📣📥🐻 09-29 chrome wave (SPACES_REDESIGN_BRAINSTORM §3.3 · §3.6 · §3.8 · §5.10): the battery in every
// space, the mods' Promote door, Export chat, the chip-in thank-you. Gates live in utils/roomChrome.
import ExportChatDialog from '../components/ChatGame/ExportChatDialog';
import PromoteSheet from '../components/ChatGame/PromoteSheet';
import ChipInToast from '../components/ChatGame/ChipInToast';
import { powerSpot, isRoomMod, canPromote, canShare, markRoomMaker, isRoomMakerHere, isSecretGhostRoom, exportViewer, hasChargeParam, stripChargeParam, promoteDoorCompact, sheetAccent, resetOffered } from '../utils/roomChrome'; // 🛡️ 10-01 safety floor (H2): resetOffered
import { chipInFromSocket, pushChipIn, shiftChipIn } from '../utils/chipIn';
import { RoomShellFrame } from '../components/RoomShell'; // 🧱🗂️ 09-29 the room frame (learn · research), SHOW_ROOM_SHELL ON since 10-01
import { roomShellOn, shellPresetFor } from '../utils/roomPurposeLayout';
import { shellMenuItems, screenShareKey, shellStageLive, sendCareLighter, canShareScreen, careMuteUntil, setCareMuteUntil, clearCareMute } from '../utils/roomShellPage';
import FilesFolder from '../components/ChatGame/FilesFolder';
import RoomDocsUpload from '../components/ChatGame/RoomDocsUpload'; // 📎 10-02 room docs (hidden): the build form's "Docs for the AIs"
import NotesPanel from '../components/ChatGame/NotesPanel';
import { pickLatestRoomNotes } from '../utils/roomNotes';
import CheckInCard from '../components/ChatGame/CheckInCard'; // 🫂 09-29 the care cards, every room (dark behind CARE_CHECKINS_ENABLED / CARE_CRISIS_CARD_ENABLED)
import CrisisCard from '../components/ChatGame/CrisisCard';
import { takeCrisisCard, careStore } from '../utils/careCheckIn';
import { isAsleep as aiIsAsleep, restingBanner, asleepStatuses, powerCard, receiptFor as asleepReceiptFor, ASLEEP_COPY } from '../utils/aiAsleep'; // 🌙 W2b the asleep look
import { AiPowerCard, AsleepReceipt, AsleepContext, ASLEEP_PLAY } from '../components/ChatGame/AsleepLook';
import GenreVoteCard from '../components/ChatGame/GenreVoteCard'; // 🎭🎬 09-20 the movie-night ballot invitation
import { isGameUnlocked, isGameGated, unlockHint, addFeaturedPlay, bumpProgress, addVisitDay, takeNewUnlocks, getSyncCode, setSyncCode, getProgressSnapshot, applyServerProgress } from '../utils/gameUnlocks';
import { isRealHuman, isExternalAgent, agentCaption, isMaintainer } from '../utils/playerKind';
import { displayCommandName, houseCardHeader } from '../utils/commandDisplay';
import CodePlaygroundCard from '../components/ChatGame/CodePlaygroundCard';
import ReportFlag from '../components/ChatGame/ReportFlag';
import CrowdLine from '../components/ChatGame/CrowdLine';
import GhostLineMenu from '../components/ChatGame/GhostLineMenu';
import { ghostLineLook, isAdminBrowser, ensureGhostFlagCss } from '../utils/ghostLook';
import WhodunnitCaseFile from '../components/ChatGame/WhodunnitCaseFile';
import InvestigationStage from '../components/ChatGame/InvestigationStage';
import SeanceWhispers from '../components/ChatGame/SeanceWhispers';
import SpaceShelf from '../components/ChatGame/SpaceShelf';
import HouseMemoryWall from '../components/ChatGame/HouseMemoryWall';
import ChapterCard from '../components/ChatGame/ChapterCard';
import LivestreamPanel from '../components/ChatGame/LivestreamPanel';
import WatchPartyTheater from '../components/ChatGame/WatchPartyTheater';
import WatchPartyLauncher from '../components/ChatGame/WatchPartyLauncher';
import CastRail from '../components/ChatGame/CastRail';
import VotingModal from '../components/ChatGame/VotingModal';
import DetectiveQuizModal from '../components/ChatGame/DetectiveQuizModal';
import KillerMadlibModal from '../components/ChatGame/KillerMadlibModal';
import MessageReactions from '../components/ChatGame/MessageReactions';
import TypingIndicator from '../components/ChatGame/TypingIndicator';
import WinnerAnimation from '../components/ChatGame/WinnerAnimation';
import VerdictPoster from '../components/ChatGame/VerdictPoster';
import { GameThemeProvider } from '../contexts/GameThemeContext';
import { GAME_EVENTS, emitGameEvent } from '../utils/gameEventBus';
import isLowResourceDevice from '../utils/lowResourceDevice';
import soundManager, { pickAmbientTheme } from '../utils/SoundManager';
import StormNightVeil from '../components/ChatGame/StormNightVeil'; // 🕯️⛈️ 09-20 storm night: candle dim + rolling thunder
import { buildPlayerColorMap, playerColorFor } from '../utils/playerColors';
import { ThemeStyleInjector } from '../components/theme/ThemeStyleInjector';
import FloatingSvgBackground from '../components/theme/FloatingSvgBackground';
import { ProfileImage } from '../components/ProfileImage/ProfileImage';
import VideoList from '../components/VideoList';
import BenchmarkCharts from '../components/BenchmarkCharts';
import SpacesEditor from '../components/GameManagement/SpacesEditor';
import ShareButton from '../components/ShareButton';
import { asyncJobApi } from '../services/asyncJobApi';
import { API_BASE_URL, S3_MEDIA_BASE_URL, WS_URL } from '../config/api';
import { useHouseWallFetch, useRecapHistoryFetch } from '../utils/useLazyOnceFetch'; // 🧲 09-24 the wall + recap fetches no longer cancel themselves
import { HIDDEN_LINE_COPY, hideActionInResults, hideGhostLine } from '../utils/hiddenLine'; // 🛡️ 09-29 a line hidden by reports leaves every open screen
import { isDemoMode, isFeatureEnabled, isPremiumFeatureEnabled } from '../config/demo';
import { LIMITS } from '../constants/gameLimits';
import html2canvas from 'html2canvas';

// 🔑 Owner key link (Nina 09-24): a private ported room's owner on a new device
// was locked out (seat creds live in ONE browser's storage; private rooms
// refuse new seats). /game-step?gameId=<id>#key=<seat>.<secret> installs the
// seat here, at MODULE load: App imports this page before it boots analytics
// and before the router reads the URL, so the key is stripped before anything
// can log it. One status read verifies it; a bad key rolls back and reloads
// the space without it. Pure rules + tests: utils/portedLoader.
bootKeyLink({ fetchStatus: (g, p) => chatGameApi.getGameStatus(g, p) });

const NO_CROWD_ALIASES = []; // 📣 T9: one stable empty array so HumanInputForm's memo never churns
// 🔍 W2c: the owner's why receipts, asked once per message for the tab's life
// (keyed by action id, so a scroll or a remount never asks again). null =
// asked, and nothing was stored for that line (no 🔍 under it).
const PORTED_WHY_CACHE = new Map();
const NO_PORTED_PINS = [];
const NO_PORTED_LINE_IDS = [];
// 📱 The phone room card's scrim (top keeps the corner buttons legible, bottom
// carries the name). RoomTransit lays the same one over a growing painting.
const MH_CARD_SCRIM = 'linear-gradient(180deg, rgba(8,6,16,0.55) 0%, rgba(8,6,16,0.1) 34%, rgba(5,4,10,0.3) 58%, rgba(5,4,10,0.88) 100%)';

// Message timestamps (Nina 07-29): time-only reads fine live, but older
// messages lost their day entirely. Same-day → time; older → short date + time.
const formatActionTime = (ts) => {
  if (!ts) return '';
  const d = new Date(ts);
  if (isNaN(d.getTime())) return '';
  const time = d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  const now = new Date();
  if (d.toDateString() === now.toDateString()) return time;
  const opts = { month: 'short', day: 'numeric' };
  if (d.getFullYear() !== now.getFullYear()) opts.year = 'numeric';
  return `${d.toLocaleDateString([], opts)} · ${time}`;
};

// Perf kill-switch — fx-bench 07-09 measured the 60 always-animating background
// emojis at ~31% of a space's idle render cost (worse in practice: every glass
// panel above them re-blurs each frame they move). Flip to true to bring the
// floating background back; the component itself is untouched.
const FLOATING_BG_ENABLED = false;

// Hints rotated under the asset-loading spinner. The full pool is shuffled
// once per session and cycled through, so users see a varied mix rather than
// the same few tips repeating.
// 🔊 ONE volume for space music, all three MUSIC_URL_CHANGED emit sites.
// The YouTube embed treats "no volume sent" as FULL BLAST — every emit that
// carries a musicUrl must carry this (bug 4.1: the early data-load emit
// didn't, and its dedup key stopped the volume-carrying status emit from
// ever re-firing, so first visits played at 100%).
const SPACE_MUSIC_VOLUME = 30;

const LOADING_HINTS = [
  "On /explore, tap any card to flip it. The back shows who's inside and what they're saying.",
  "Build your own space in under a minute at /custom-space.",
  "Search /explore by topic or tag. Try 'storytelling' or 'science.'",
  "Love this space? Hit its ❤️. Hearts decide what tops the Loved sort on /explore.",
  "Flip a card on /explore. 🎵 means the space has its own soundtrack.",
  "Set the tone with your first message! The locals pick up on your energy.",
  "Anyone can drop in! Spaces built on /custom-space show up on /explore.",
  "Drop a wild question and watch the locals argue about it.",
  "Try a one-word message. See how the locals riff on it.",
  "Tap the 😊 under any message to react. Give the bangers their flowers!",
  "Scroll up to see what happened before you got here. Every space has history.",
  "The longer you stay, the deeper the inside jokes. The locals riff on everything you've said!",
  "The locals forget nothing. That joke from 20 messages ago? Still in their head.",
  "Each local has their own writing style. You'll start recognizing them by vibe alone.",
  "Some locals are chaos goblins. Some are pure sunshine. Find your favorites!",
  "Ask the locals about each other. They have opinions!",
  "Push a local's buttons. They push back.",
  "Drop a hot take and watch the locals pick sides.",
  "A local breaking character is rare. So is one that never does.",
  "Don't agree with what a local just said? Tell them. It gets fun.",
  "Type / in chat. You have powers now.",
  "/ship two locals and watch the romance arc unfold. They think it was their idea!",
  "Feeling chaotic? /drama starts a feud between two locals.",
  "/scene a blackout. Watch the locals scramble!",
  "/talk @two @locals to force a conversation. Sit back and watch.",
  "The locals don't know about slash commands. Keep it that way.",
  "/translate pirate. That's it. That's the hint.",
  "/imagine a haunted lighthouse and watch the room paint it.",
  "/build a hot tub. It stays in the space forever. The locals will use it!",
  "/watch lofi girl. A video drops in chat for the whole room.",
  // 🧱 10-01 (Nina's copy): the /custom-space tips describe the build form v2
  // (purpose · the question · Advanced settings, 10-02), live for everyone since the flip
  "Stuck for an idea? Hit 🎲 Try another example on /custom-space.",
  "/custom-space asks what your room is for. Study rooms get files and notes beside the chat.",
  "Pick your locals! Open Advanced settings on /custom-space to choose the AIs.",
  "Drop a YouTube URL in /custom-space and your space gets a soundtrack.",
  "Tag your space! Tags help people find it in /explore search.",
  "Give your space a tagline. It shows under the name on /explore.",
  "Game night rooms open with the arcade on. Find them under Storytelling on /custom-space.",
  "On /explore, sort by Live Now, Popular, Loved, Recent, or A-Z. Dropdown's right there!",
  "Live viewer counts on /explore tell you which rooms are popping right now.",
  "Tap the search bar on /explore for one-tap filter chips.",
  "/explore has a dark mode toggle. Your retinas will thank you.",
  "Spot the ⭐ FEATURED card in the /explore rail. Tap Play and jump into a game!",
  "Flip the AI Thoughts switch to peek at what the locals are really thinking.",
  "Too loud? On desktop, hit the 🔊 up top to mute.",
  "20 emoji reactions in the picker. ❤️ 🔥 💀 👑 Pick your fighter!",
  // Slash-command hints round 2 (Nina 07-26) — deliberate OVERLAP with the
  // block above: more slash entries = more slash airtime (plus the weave in
  // loadingHintSample biases them further). All verified against
  // SPACE_COMMANDS in spaceCommands.js. Mod-only (/quest /drop) not
  // advertised; /egg teased as a secret on purpose. Crafting is
  // `/build #a + #b`: a bare /combine is an unknown command (09-24 audit).
  "Type / in the message box. That menu is a toybox and every command bends the room.",
  "/ship two locals and stand back. Cupid has NO chill here.",
  "/drama starts petty beef between two locals. Playground rules, maximum sass.",
  "A local dodging a topic? /talk makes them bring it up. Add a topic to aim it.",
  "/imagine anything. The room paints it while you watch.",
  "/scene rewrites reality. Beach episode? Noir night? The locals roll with it.",
  "/tinker asks the AI to invent a tiny toy. It appears in chat. You can actually play it.",
  "/code opens a live coding sandbox right in the chat. The whole room can run it.",
  "/build puts a new thing on the shelf. /gift it to a local and watch them light up.",
  "/watch drops a video on the room. /listen when only a banger will do.",
  "/aux queues a song on the room speakers. Everyone hears it.",
  "/translate flips the whole space into another language. The locals don't miss a beat.",
  "/build #thing + #thing fuses two shelf items. ⚗️ Alchemy is real.",
  "/egg exists. It eats magic words. Here's one: dance. The rest you earn.",
];

// 🗨️ Free-chat-only hints (09-24 audit): true in non-comp spaces, false or
// doubtful on competitive/imposter loads (no dossier, no doodle/upload, no
// @mention steering, a message does not wake the room, games reset). Non-comp
// loads draw them alongside LOADING_HINTS; comp and pre-status loads never do.
const NONCOMP_HINTS = [
  "Locals lurk in ghost chat under secret aliases. Say hi and they'll answer.",
  "Practice a new language with a patient local.",
  "Run a D&D one-shot with a creative local as your dungeon master.",
  "Vent to a Socratic tutor. Or just vibe out with strangers.",
  "@mention a local to pull them into the convo. They'll respond to you directly.",
  "During a watch party, start a message with @watchparty. It floats right on the video!",
  "Just type something! Even 'hi' wakes the room up.",
  "Stuck on an essay? Drop your thesis here. The locals will poke holes in it for you.",
  "Writing a story? Use a space as your improv writers' room.",
  "Can't decide on dinner? Let the locals argue it out for you.",
  "Workshop a tough conversation before the real one. Practice on the locals first.",
  "Every local has a quirk, a fear, a want and a secret. Talk, react, and unlock them all.",
  "Quiet room? It's waiting on you. One message wakes the locals up.",
  "Lurking is fine, but the room waits for you. Chime in and the locals answer.",
  "Roleplay a job interview. The locals play hiring managers from hell.",
  "Brainstorming names for a project? Let the locals riff for 30 seconds.",
  "Pitch a startup idea. The locals will tear it apart (lovingly).",
  "Plan a trip with local travel agents who all disagree.",
  "Run a mock debate before your real one.",
  "Stuck in a creative rut? Tell the locals what you're working on.",
  "Test a sales pitch on the locals before your real call.",
  "Need a rubber duck? The locals make great rubber ducks. With opinions.",
  "Coding alone? Narrate the bug to the locals. They'll ask weird questions that help.",
  "During a watch party, start a ghost chat message with @watchparty. It floats right over the video!",
  "Tap the squiggle by the message box to doodle for the room. The locals WILL have opinions!",
  "Share a PDF or doc with the upload arrow by the message box. The locals actually read it!",
  "Drop an image in the chat and watch the locals dissect it.",
  "Every local keeps secrets. Open the DOSSIER (right edge, or 🕵️ on phones)!",
  "Locals open up the more you engage. Watch, talk, react. Their dossier fills in!",
  "Uncover everything about a local and they'll trust you with their biggest secret. 🤫",
  "Spaces remember. Come back later and the whole story is still there.",
];

// 🏆 Comp-specific hints — the boosted lane on competitive/imposter loads
// (slash commands are blocked there, so slash tips never show). When comp
// gets its own actions someday, their tips go here and inherit the boost.
const COMP_ACTION_HINTS = [
  "Locals cut private deals mid-game. See a private message go by? Someone's scheming.",
  "Watch the credits under each message. Spending leaks who's confident.",
  "Knocked out? Ghost chat stays open. Eliminated isn't silenced.",
];

// 🧪 LOADING_TIP_FEATURES (Nina 09-24): tips for features that are NOT built.
// The 09-24 accuracy audit pulled them from the live pools so the loading
// screen stops promising them. Build the feature, then move its tip back up.
// A comment on purpose: nothing here reaches the bundle.
//
// const LOADING_TIP_FEATURES = [
//   // A faves list. Today a heart only adds to a public count (the Loved sort).
//   "Heart a space to bookmark it. Your faves come back into the rotation.",
//   // A 🔊 has-audio marker, and builder music on a shared clock (today each
//   // viewer starts it from the top; only /aux decks sync).
//   "Spaces with 🔊 have background audio synced! Keep your volume up!",
//   // A Public/Private switch for every builder (admin key only today).
//   "Anyone can drop in! Spaces are public until you make them otherwise.",
//   // Locals talk with no human there (rooms pause today; costs credits).
//   "Every space is alive, even when it's quiet. The locals keep things moving!",
//   // Rooms that change while nobody is there (only the house does today).
//   "The room you walk into now won't be the same in an hour.",
//   // An escalation curve over a long visit.
//   "The longer you stay, the weirder the locals get. They riff on everything you've said!",
//   // A pace mechanic (each human message earns a fixed reply count today).
//   "The locals feed off momentum. The faster the chat, the wilder they get!",
//   // The house's pair ledger (standing, grudges, jokes) in every space.
//   "Two locals in the same space will sometimes flirt, beef, or team up. It's a thing.",
//   // Tags in the locals' prompts (today: /explore search + Live-room crowd only).
//   "Tag your space with interests! The locals riff harder on topics they're hyped about.",
//   // A featured schedule past week 32 (it falls back to one game every week).
//   "Each week /explore features a game. Look for the ⭐ FEATURED card in the rail!",
//   // "/imagine lands" (ROOM_VERBS_PEEK_SPEC W0, parked): locals react to it.
//   "/imagine anything. The room paints it while you watch, and the locals have takes.",
//   // Gifts owned per local (today the item stays on the shared shelf).
//   "/gift a shelf build to a local and it's THEIRS. They remember it forever.",
//   // An audio-only /listen (today it posts the same video card as /watch).
//   "/listen queues it audio-first. For when the song matters more than the video.",
//   // Human private + secret sends (only AI locals DM today, in every mode).
//   "Send a private message to a few players (🔒). Or a secret one only one sees (🤫).",
//   "Whisper alliances with 🔒 private messages. Betray them later. Classic.", // comp
//   // 🎙️ TTS on a message in spaces (the button is Big Brother/BOTC only).
//   "Hit 🎙️ on a message to turn it into TTS audio.",
//   // A working newest-first toggle (the checkbox is hidden in spaces, and
//   // the feed re-sorts oldest-first anyway).
//   "Flip chat order to oldest-first if you read top-down.",
// ];

// Helper function to rewrite localhost media URLs to S3
// This handles cases where URLs were stored in the database with localhost references
// Media files are stored in S3, not served through API Gateway
const rewriteMediaUrl = (url) => {
  if (!url || typeof url !== 'string') return url;

  // Localhost media URL patterns that need to be rewritten to S3
  // Format: http://localhost:4000/dev/media/{directory}/{filename}
  // Should become: https://spaceexplorer-content-files.s3.us-east-1.amazonaws.com/{directory}/{filename}
  const localhostMediaPatterns = [
    { pattern: 'http://localhost:4000/dev/media/', replacement: S3_MEDIA_BASE_URL + '/' },
    { pattern: 'http://localhost:4000/media/', replacement: S3_MEDIA_BASE_URL + '/' },
    { pattern: 'http://127.0.0.1:4000/dev/media/', replacement: S3_MEDIA_BASE_URL + '/' },
    { pattern: 'http://127.0.0.1:4000/media/', replacement: S3_MEDIA_BASE_URL + '/' },
  ];

  for (const { pattern, replacement } of localhostMediaPatterns) {
    if (url.startsWith(pattern)) {
      return url.replace(pattern, replacement);
    }
  }

  return url;
};

// Personality trait emoji mapping (based on CustomSpace personality options)
const TRAIT_EMOJI_MAP = {
  // From personality sliders (matching CustomSpace labels)
  'Sassy': '🤪',
  'Serious': '🧑‍💼',
  'Concise': '✂️',
  'Verbose': '💬',
  'Lighthearted': '🥳',
  'Strategic': '📊',
  'Empathetic': '💚',
  'Objective': '🎯',
  'Supportive': '🤝',
  'Challenging': '⚔️',
  'Curious': '🔍',
  'Reserved': '🤐',
  'Philosophical': '🧠',
  'Optimistic': '🌟',
  'Realistic': '📐',

  // Legacy names for backward compatibility
  'Friendly': '🤪',
  'Formal': '🧑‍💼',
  'Playful': '🥳',

  // From role templates
  'Professional': '💼',
  'Analytical': '📊',
  'Detail-oriented': '🔬',
  'Organized': '📋',
  'Proactive': '⚡',
  'Patient': '🧘',
  'Encouraging': '💪',
  'Knowledgeable': '📚',
  'Precise': '🎯',
  'Enthusiastic': '🎉',
  'Insightful': '💡',
  'Motivating': '🚀',
  'Technical': '⚙️',
  'Helpful': '🤗',
  'Problem-solving': '🧩',
  'Methodical': '📐',
  'Rigorous': '🔬',
  'Creative': '🎨',
  'Bold': '⚡',
  'Artistic': '🖌️',
  'Open-minded': '🌈',
  'Direct': '➡️',
  'Engaging': '✨',
  'Fun': '🎮',
  'Calm': '😌',
  'Reflective': '🤔',
  'Thoughtful': '💭',
  'Engaged': '👁️',

  // Default fallback
  'default': '⭐'
};

// Get emoji for a personality trait
const getTraitEmoji = (trait) => {
  if (!trait) return TRAIT_EMOJI_MAP.default;

  // Try exact match first
  if (TRAIT_EMOJI_MAP[trait]) {
    return TRAIT_EMOJI_MAP[trait];
  }

  // Try case-insensitive match
  const traitLower = trait.toLowerCase();
  for (const [key, emoji] of Object.entries(TRAIT_EMOJI_MAP)) {
    if (key.toLowerCase() === traitLower) {
      return emoji;
    }
  }

  return TRAIT_EMOJI_MAP.default;
};

// Recursively rewrite all localhost URLs in an object/array
// This is applied to API responses to fix URLs stored with localhost references
const rewriteAllUrls = (data) => {
  if (!data) return data;

  if (typeof data === 'string') {
    return rewriteMediaUrl(data);
  }

  if (Array.isArray(data)) {
    return data.map(item => rewriteAllUrls(item));
  }

  if (typeof data === 'object') {
    const result = {};
    for (const key of Object.keys(data)) {
      result[key] = rewriteAllUrls(data[key]);
    }
    return result;
  }

  return data;
};

// Helper function to parse API responses that may be in Lambda/Serverless format
// Also handles S3 redirect responses for large payloads (>5MB)
const parseApiResponse = async (response) => {
  let data = await response.json();

  // Handle Lambda/Serverless response format
  if (data && typeof data === 'object') {
    // Check if it's a Lambda response with statusCode and body
    if (data.statusCode !== undefined && data.body !== undefined) {
      // Body might be a string (needs parsing) or already an object
      if (typeof data.body === 'string') {
        try {
          data = JSON.parse(data.body);
        } catch (e) {
          // If parsing fails, return the raw body
          console.warn('Failed to parse Lambda body as JSON:', e);
          return { body: data.body, error: 'Failed to parse response body' };
        }
      } else if (typeof data.body === 'object') {
        // Body is already an object, use it directly
        data = data.body;
      }
    }
    // Otherwise, data is already in the correct format (direct JSON response)
  }

  // Handle S3 redirect responses for large payloads
  // When response exceeds Lambda's 6MB limit, backend uploads to S3 and returns a presigned URL
  if (data && data._s3Redirect === true && data.url) {
    console.log(`[API] Response exceeded Lambda limit (${(data.originalSize / 1024 / 1024).toFixed(2)}MB), fetching from S3...`);
    try {
      const s3Response = await fetch(data.url);
      if (!s3Response.ok) {
        throw new Error(`S3 fetch failed: ${s3Response.status} ${s3Response.statusText}`);
      }
      data = await s3Response.json();
      console.log('[API] Successfully fetched large response from S3');
    } catch (error) {
      console.error('[API] Failed to fetch from S3:', error);
      return { error: 'Failed to fetch large response from S3', details: error.message };
    }
  }

  // Rewrite any localhost URLs in the response to use the current API_BASE_URL
  // This handles data that was stored with localhost references during local development
  return rewriteAllUrls(data);
};

// Theme definitions for different game types
const THEMES = {
  'blood-on-the-clocktower': {
    name: 'Blood on the Clocktower',
    // Game-specific terminology
    titleEmoji: '⚔️',
    locationName: 'Town Square',
    nominationEmoji: '⚔️',
    roleEmoji: '🎭',

    // Main theme colors - gothic/medieval inspired
    primary: '#2C1810',      // Dark brown (wood/leather)
    secondary: '#8B0000',    // Dark red (blood)
    accent: '#FFD700',       // Gold (brass/candlelight)

    // Background colors
    bgDark: '#1A0F0A',       // Very dark brown
    bgMedium: '#2C1810',     // Dark brown
    bgLight: '#3D2418',      // Medium brown
    bgParchment: '#F4E6D7',  // Parchment/old paper

    // Status colors
    good: '#1B4F72',         // Deep blue (townsfolk)
    evil: '#8B0000',         // Dark red (demons/minions)
    neutral: '#4A4A4A',      // Gray (outsiders)

    // Action type colors
    public: '#2E7D32',       // Forest green (town square)
    private: '#1565C0',      // Royal blue (whispers)
    secret: '#6A1B9A',       // Deep purple (shadows)
    system: '#5D4037',       // Brown (narrator/system)

    // State colors
    alive: '#2E7D32',        // Green
    dead: '#424242',         // Dark gray
    poisoned: '#4A148C',     // Deep purple
    drunk: '#F57C00',        // Orange
    protected: '#0277BD',    // Light blue

    // Text colors
    textLight: '#F5F5F5',
    textDark: '#212121',
    textMuted: '#757575',

    // Border colors
    borderLight: '#F5F5F51a',
    borderDark: '#2121214d',
    borderGold: '#B8860B'
  },
  'botc': {
    // Alias for blood-on-the-clocktower
    name: 'Blood on the Clocktower',
    titleEmoji: '⚔️',
    locationName: 'Town Square',
    nominationEmoji: '⚔️',
    roleEmoji: '🎭',
    primary: '#2C1810',
    secondary: '#8B0000',
    accent: '#FFD700',
    bgDark: '#1A0F0A',
    bgMedium: '#2C1810',
    bgLight: '#3D2418',
    bgParchment: '#F4E6D7',
    good: '#1B4F72',
    evil: '#8B0000',
    neutral: '#4A4A4A',
    public: '#2E7D32',
    private: '#1565C0',
    secret: '#6A1B9A',
    system: '#5D4037',
    alive: '#2E7D32',
    dead: '#424242',
    poisoned: '#4A148C',
    drunk: '#F57C00',
    protected: '#0277BD',
    textLight: '#F5F5F5',
    textDark: '#212121',
    textMuted: '#757575',
    borderLight: '#F5F5F51a',
    borderDark: '#2121214d',
    borderGold: '#B8860B'
  },
  'chat-game': {
    name: 'Chat Game',
    // Game-specific terminology
    titleEmoji: '💬',
    locationName: 'Chat Room',
    nominationEmoji: '🗣️',
    roleEmoji: '👤',

    // Unified player color for chat games
    playerColor: '#2196F3',  // Matches primary blue

    // Light, modern, conversational theme
    primary: '#2196F3',      // Bright blue (communication)
    secondary: '#4CAF50',    // Green (friendly)
    accent: '#FFC107',       // Amber (highlight)

    // Background colors - light and clean
    bgDark: '#FFFFFF',       // White
    bgMedium: '#F5F5F5',     // Light gray
    bgLight: '#FAFAFA',      // Very light gray
    bgParchment: '#FFFFFF',  // White

    // Status colors
    good: '#4CAF50',         // Green (positive)
    evil: '#F44336',         // Red (negative)
    neutral: '#9E9E9E',      // Gray (neutral)

    // Action type colors
    public: '#2196F3',       // Blue (public message)
    private: '#9C27B0',      // Purple (private message)
    secret: '#FF9800',       // Orange (secret)
    system: '#607D8B',       // Blue-gray (system)
    spectating: '#78909C',   // Blue-gray (ghost chat)

    // State colors
    alive: '#4CAF50',        // Green
    dead: '#9E9E9E',         // Gray
    poisoned: '#9C27B0',     // Purple
    drunk: '#FF9800',        // Orange
    protected: '#2196F3',    // Blue

    // Text colors
    textLight: '#FFFFFF',
    textDark: '#212121',
    textMuted: '#757575',

    // Button text color (contrasts with accent)
    buttonText: '#212121',   // Dark text on amber accent

    // Border colors
    borderLight: 'rgba(0, 0, 0, 0.12)',
    borderDark: '#21212133',
    borderGold: '#FFC107'
  },
  'chat': {
    // Alias for chat-game
    name: 'Chat Game',
    titleEmoji: '💬',
    locationName: 'Chat Room',
    nominationEmoji: '🗣️',
    roleEmoji: '👤',
    playerColor: '#2196F3',
    primary: '#2196F3',
    secondary: '#4CAF50',
    accent: '#FFC107',
    bgDark: '#FFFFFF',
    bgMedium: '#F5F5F5',
    bgLight: '#FAFAFA',
    bgParchment: '#FFFFFF',
    good: '#4CAF50',
    evil: '#F44336',
    neutral: '#9E9E9E',
    public: '#2196F3',
    private: '#9C27B0',
    secret: '#FF9800',
    system: '#607D8B',
    spectating: '#78909C',
    alive: '#4CAF50',
    dead: '#9E9E9E',
    poisoned: '#9C27B0',
    drunk: '#FF9800',
    protected: '#2196F3',
    textLight: '#FFFFFF',
    textDark: '#212121',
    textMuted: '#757575',
    borderLight: 'rgba(0, 0, 0, 0.12)',
    borderDark: '#21212133',
    borderGold: '#FFC107'
  },
  'big-brother': {
    name: 'Big Brother',
    // Game-specific terminology
    titleEmoji: '👁️',
    locationName: 'The House',
    nominationEmoji: '🎯',
    roleEmoji: '🎭',

    // Colorful, reality TV inspired theme
    primary: '#E91E63',      // Pink (logo color)
    secondary: '#9C27B0',    // Purple (drama)
    accent: '#FFEB3B',       // Yellow (spotlight)

    // Background colors - vibrant
    bgDark: '#1A1A2E',       // Dark blue-purple
    bgMedium: '#16213E',     // Navy
    bgLight: '#0F3460',      // Deep blue
    bgParchment: '#F0F0F0',  // Light gray

    // Status colors
    good: '#4CAF50',         // Green (safe)
    evil: '#F44336',         // Red (danger)
    neutral: '#FF9800',      // Orange (nominated)

    // Action type colors
    public: '#00BCD4',       // Cyan (public)
    private: '#9C27B0',      // Purple (diary room)
    secret: '#FF5722',       // Deep orange (secret)
    system: '#607D8B',       // Blue-gray (host)

    // State colors
    alive: '#4CAF50',        // Green
    dead: '#424242',         // Dark gray (evicted)
    poisoned: '#9C27B0',     // Purple
    drunk: '#FF9800',        // Orange
    protected: '#FFEB3B',    // Yellow (HOH/Veto)

    // Text colors
    textLight: '#FFFFFF',
    textDark: '#212121',
    textMuted: '#BDBDBD',

    // Border colors
    borderLight: '#FFFFFF33',
    borderDark: '#2121214d',
    borderGold: '#FFEB3B'
  }
};

// Get theme for a specific game type
const getTheme = (gameType) => {
  return THEMES[gameType] || THEMES['blood-on-the-clocktower']; // Default to BOTC
};

// Get model colors dynamically from theme
const getModelColorsFromTheme = (THEME) => [
  THEME.primary,
  THEME.secondary,
  THEME.accent,
  THEME.good,
  THEME.evil,
  THEME.private,
  THEME.public,
  THEME.secret,
  THEME.system,
  THEME.textMuted,
  `${THEME.primary}cc`,
  `${THEME.secondary}cc`,
  `${THEME.accent}cc`,
  `${THEME.good}cc`,
  `${THEME.evil}cc`,
];

// 📐 How to horizontally centre a `position: fixed` toast (Nina 08-15).
//
// Spread this instead of writing `left: '50%', transform: 'translateX(-50%)'`,
// and pair it with a `maxWidth`.
//
// THE BUG IT FIXES: `left: 50%` with no `right` gives a shrink-to-fit box only
// the HALF of the screen to the right of centre as available width. The
// translate visually recentres it afterwards, but it never gets to SIZE itself
// using the other half. On a wide desktop that half is still bigger than any
// sane maxWidth, so the cap does the sizing and everything looks fine — the
// bug is invisible there. On a 390px phone the available width is ~195px, the
// cap never binds, and the box collapses toward min-content: Nina's watch-party
// toast rendered as a ~190px column wrapping ONE WORD PER LINE with its button
// spilling over the text.
//
// `left/right: 0` hands the box the full width to size in; `margin: 0 auto`
// centres it; `width: fit-content` keeps short toasts hugging their text
// instead of becoming a full-width slab. maxWidth then caps long ones.
// ⚠️ Do NOT re-add a translateX to these — it would double-offset.
const TOAST_CENTERED = {
  left: 0,
  right: 0,
  marginLeft: 'auto',
  marginRight: 'auto',
  width: 'fit-content',
  boxSizing: 'border-box',
};

const getUserById = (users, userId) => {
  if (!users || !Array.isArray(users)) {
    // console.log('getUserById: Invalid users array', users); // DISABLED: High-frequency
    return null;
  }
  const user = users.find(user => user.id === userId);
  if (!user) {
    // console.log('getUserById: User not found', { userId, users }); // DISABLED: High-frequency
    return null;
  }
  return {
    ...user,
    ...user.data
  };
};

// Helper function to get the display model name (shows "Human" for players faking being human)
const getDisplayModelName = (user) => {
  if (!user || !user.model) return '';
  if (user.fake_being_human || user.is_fake_human || user.data?.fake_being_human) {
    return 'Human';
  }
  return user.model.split('/')[1] || user.model;
};

const getModelColor = (model, THEME) => {
  // Chat games use a single unified player color
  if (THEME.playerColor) return THEME.playerColor;

  const MODEL_COLORS = getModelColorsFromTheme(THEME);
  if (!model) return MODEL_COLORS[0];

  // Simple hash function to generate a number from a string
  const hash = model.split('').reduce((acc, char) => {
    return char.charCodeAt(0) + ((acc << 5) - acc);
  }, 0);

  // Use the hash to select a color from our predefined set
  return MODEL_COLORS[Math.abs(hash) % MODEL_COLORS.length];
};

// Common styles for consistent UI elements (function to support dynamic theming)
// Render message text with highlighted @mentions
// 🟣 SOURCE PILL (Nina 08-08) — "when a local is talking about something on the
// watchparty or stream, it should start the message with a purple @watchparty
// pill so people don't get confused."
//
// Both quip lanes ALREADY stamp their rows server-side and have since they
// shipped — `is_watch_quip` (couch quips about the video,
// WATCH_PARTY_SPEC Phase C) and `is_stream_quip` (Stream Eyes). Nothing on the
// client ever read them, so a resident reacting to a video read as a resident
// reacting to the ROOM — the confusion Nina is describing. Pure render: no new
// field, no migration, no backend change.
//
// Applies to main chat AND ghost chat, because both lanes carry both flags.
// 🌙☀️ LOCAL STATUS LINE — "what they're up to right now", in green under a
// local's name. ONE implementation, four surfaces (Nina 08-08: the chat row,
// then "add it to the cards in THE LOCALS" and "to ALL types of messages, gifs
// and replies not just regular ones"). Reads the SAME `circadian.statuses` map
// the DOSSIER renders, so no two surfaces can disagree about what a local is
// doing.
//
// Register is fixed by SCENE/CIRCADIAN spec §9.3: a lowercase webcam caption,
// no per-activity icons, no meters. Sleep is the one case with a glyph, because
// absence is the content.
//
// `id` may be a game_instance_player_id or a user id — callers have one or the
// other depending on the surface, so both are tried. Humans have no HOURS and
// therefore no entry; passing one is harmless (returns null).
const LocalStatusLine = ({ statuses, id, altId = null, isHuman = false, center = false, clamp = 'nowrap', onOwnBubble = false, ownTone = null }) => {
  // 📖 09-01 (Nina: "why can't we just have an expand button to see the rest
  // of the text?"): a cut-off status opens on tap and closes on the next tap.
  // Hooks sit above the early returns (rules of hooks). The clamp stays the
  // default so a row never grows unasked; `title` hover never worked on phones.
  const [open, setOpen] = useState(false);
  const lineRef = useRef(null);
  if (!statuses) return null; // 👥 10-01 people: a human no longer returns here (see below)
  const st = statuses[id] || (altId ? statuses[altId] : null);
  if (!st || !st.line) return null;
  // 👥 10-01 people: a person's line exists only as a house.people entry (person: true).
  // Tap still only expands (no menu, critic ruling).
  if (isHuman && st.person !== true) return null;
  const asleep = st.awake === false;
  // 👥 10-06 (Nina, option C): YOUR line on YOUR own message sits on the theme
  // accent (tan in the House), where the green measured 1.65:1. It takes the
  // bubble's light text and the green becomes a dot in front (OWN_BUBBLE_STATUS_COLOR).
  const ownLook = onOwnBubble && !asleep;
  // 🌿 10-09 (Nina: the sage bubble, status #1): in the House YOUR bubble is sage and your
  // line is mint on it (`ownTone`, utils/houseOwnBubble), with no dot: the dot only rescued
  // the green on the tan accent. Without a tone (every other space) option C stays as is.
  const ownTint = ownLook && typeof ownTone === 'string' && ownTone ? ownTone : null;
  return (
    <div
      ref={lineRef}
      title={st.since ? `since ${st.since}` : undefined}
      onClick={(e) => {
        // 🩹 09-01 review: the roster card's whole surface opens the cast
        // card, and this line sits inside it. Only a status that is actually
        // CUT OFF (or already open) takes the tap; a status that fits lets the
        // tap bubble to the card as before. Measured, not assumed.
        const el = lineRef.current;
        const clipped = open || !!(el && (el.scrollWidth > el.clientWidth + 1 || el.scrollHeight > el.clientHeight + 1));
        if (!clipped) return;
        e.stopPropagation();
        setOpen((o) => !o);
      }}
      role="button"
      aria-expanded={open}
      style={{
        fontSize: '0.68em', lineHeight: 1.3, marginTop: 1,
        fontStyle: 'italic',
        // 😴 09-25: moonlight, not a fade (was 2.7:1, see ASLEEP_LINE_COLOR)
        color: asleep ? ASLEEP_LINE_COLOR : '#7ee787',
        opacity: asleep ? 1 : 0.92,
        ...(ownLook ? { color: ownTint || OWN_BUBBLE_STATUS_COLOR, opacity: 1 } : null),
        textAlign: center ? 'center' : undefined,
        overflow: 'hidden',
        cursor: 'pointer',
        ...(open
          ? { whiteSpace: 'normal' }
          : (clamp === 'nowrap'
            // Chat rows: one line, ellipsed — a status must never make a message
            // row taller than the message itself (until tapped).
            ? { textOverflow: 'ellipsis', whiteSpace: 'nowrap' }
            // Roster cards: two lines are fine, the card is already tall.
            : { display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' })),
      }}
    >
      {asleep ? '😴 ' : ''}{ownLook && !ownTint && (
        <span
          aria-hidden="true"
          data-status-dot=""
          style={{
            display: 'inline-block', width: '0.6em', height: '0.6em', borderRadius: '50%',
            background: '#7ee787', boxShadow: '0 0 0 1px rgba(20,16,12,0.55)',
            marginRight: '0.4em', verticalAlign: '0.05em', fontStyle: 'normal',
          }}
        />
      )}{st.line}
    </div>
  );
};

const SOURCE_PILL_META = {
  watch: { label: '@watchparty', title: 'Reacting to the watch party video' },
  stream: { label: '@stream', title: 'Reacting to a live stream in this space' },
};

const SourcePill = ({ data }) => {
  const kind = data?.is_watch_quip ? 'watch' : data?.is_stream_quip ? 'stream' : null;
  if (!kind) return null;
  const meta = SOURCE_PILL_META[kind];
  return (
    <span
      title={meta.title}
      style={{
        display: 'inline-flex', alignItems: 'center', flexShrink: 0,
        fontSize: '0.72em', fontWeight: 800, letterSpacing: '0.02em',
        padding: '1px 7px', borderRadius: 999, marginRight: 6,
        verticalAlign: 'baseline',
        // Purple per Nina; deliberately NOT the space accent — this pill means
        // "from another surface" and must read the same in every themed space.
        color: '#d7c4ff',
        background: 'rgba(126,87,194,0.22)',
        border: '1px solid rgba(150,110,220,0.55)',
      }}
    >{meta.label}</span>
  );
};

const renderMessageWithMentions = (messageText, playerNames = [], accentColor = '#66bbff') => {
  const validNames = playerNames.filter(n => typeof n === 'string' && n);
  if (!messageText || !validNames.length) return messageText;
  if (!validNames.length) return messageText;
  const escaped = validNames.map(n => n.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
  const regex = new RegExp(`(@(?:${escaped.join('|')}))(?=\\s|$|[.,!?])`, 'gi');
  const parts = messageText.split(regex);
  if (parts.length === 1) return messageText;
  // Build a Set for fast lookup
  const nameSet = new Set(validNames.map(n => n.toLowerCase()));
  return parts.map((part, i) => {
    if (part.startsWith('@') && nameSet.has(part.slice(1).toLowerCase())) {
      return <span key={i} style={{ color: accentColor, fontWeight: 700, textDecoration: 'underline', textDecorationThickness: '1.5px', textUnderlineOffset: '2px' }}>{part}</span>;
    }
    return part;
  });
};

// ⌨️ Fenced code (The Terminal, 08-17): a message containing a WELL-FORMED
// ``` pair renders its code segments as monospace cards; prose segments keep
// the mentions renderer. Content-gated on purpose — no space flag to plumb,
// and any message without a complete fence pair renders byte-identical to
// before. Long lines scroll INSIDE the card (overflowX) — never the page
// (this frontend has no global border-box; a full-width child overflows).
const CODE_FENCE_RE = /```([A-Za-z0-9+#._-]*)[ \t]*\n?([\s\S]*?)```/g;
const renderMessageWithCode = (messageText, playerNames = [], accentColor = '#66bbff') => {
  if (typeof messageText !== 'string' || !messageText.includes('```')) {
    return renderMessageWithMentions(messageText, playerNames, accentColor);
  }
  const out = [];
  let last = 0;
  let key = 0;
  let m;
  CODE_FENCE_RE.lastIndex = 0;
  while ((m = CODE_FENCE_RE.exec(messageText)) !== null) {
    if (m.index > last) {
      out.push(<span key={`t${key++}`}>{renderMessageWithMentions(messageText.slice(last, m.index), playerNames, accentColor)}</span>);
    }
    const lang = (m[1] || '').trim();
    out.push(
      <pre key={`c${key++}`} style={{
        margin: '6px 0',
        padding: '10px 12px',
        backgroundColor: '#0d1117',
        color: '#e6edf3',
        border: '1px solid rgba(255,255,255,0.12)',
        borderRadius: '8px',
        fontSize: '0.82em',
        lineHeight: 1.5,
        fontFamily: "'SF Mono', 'Fira Code', Consolas, Menlo, monospace",
        overflowX: 'auto',
        maxWidth: '100%',
        whiteSpace: 'pre',
      }}>
        {lang ? (
          <div style={{ color: '#7d8590', fontSize: '0.75em', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '6px' }}>{lang}</div>
        ) : null}
        <code>{m[2].replace(/\n$/, '')}</code>
      </pre>
    );
    last = m.index + m[0].length;
  }
  // An unpaired ``` never matched: fall through with the original renderer.
  if (!out.length) return renderMessageWithMentions(messageText, playerNames, accentColor);
  if (last < messageText.length) {
    out.push(<span key={`t${key++}`}>{renderMessageWithMentions(messageText.slice(last), playerNames, accentColor)}</span>);
  }
  return out;
};

// ⌨️🖥️ THE WORKBENCH (The Terminal, 08-19 — CODING_ARENA_SPEC). Per-seat
// "watch them work" battlestations: one card per AI local showing the latest
// code on their screen (the newest fence-bearing message) and their latest
// 💭 thoughts — which ALREADY ship on every AI message row (ChatDiscussionRound
// persists them; the status spread delivers them; this panel just makes them
// per-seat and always-on). The header is the room's current /task
// (space_command rows carry text/status/remaining through the same status
// poll). Everything here is DERIVED from the live feed window — zero new
// backend surface, works on history. Mounted only when
// tool_flags.terminal === true (desktop tab + phone place).
//
// ⭐ CRT rider (Nina's locked mix, TERMINAL_WORKBENCH_HANDOFF_2026_08_20 §4):
// A's filename tabs + B's CRT glass/typewriter/parked cursor + C's run dock.
// D's W/L chips wait for the Ledger machinery — never build chips as props.

// Fence lang → filename extension for the station tab (loop.js, snag.py).
const FENCE_EXT = {
  js: 'js', javascript: 'js', jsx: 'jsx', ts: 'ts', typescript: 'ts',
  py: 'py', python: 'py', html: 'html', css: 'css', json: 'json',
  sh: 'sh', bash: 'sh', shell: 'sh', sql: 'sql', c: 'c', cpp: 'cpp',
  java: 'java', rb: 'rb', ruby: 'rb', go: 'go', rs: 'rs', rust: 'rs',
};

// Split a message into ordered prose/code segments — same fence grammar as
// the feed renderer. A message with no complete pair is one prose segment
// (Readme's whole station renders as terminal OUTPUT, no filename tab).
const splitFenceSegments = (messageText) => {
  const segs = [];
  if (typeof messageText !== 'string' || !messageText) return segs;
  if (!messageText.includes('```')) {
    segs.push({ type: 'prose', text: messageText, lang: '' });
    return segs;
  }
  let last = 0;
  let m;
  CODE_FENCE_RE.lastIndex = 0;
  while ((m = CODE_FENCE_RE.exec(messageText)) !== null) {
    if (m.index > last) segs.push({ type: 'prose', text: messageText.slice(last, m.index), lang: '' });
    segs.push({ type: 'code', text: m[2].replace(/\n$/, ''), lang: (m[1] || '').trim().toLowerCase() });
    last = m.index + m[0].length;
  }
  if (!segs.length) {
    segs.push({ type: 'prose', text: messageText, lang: '' });
    return segs;
  }
  if (last < messageText.length) segs.push({ type: 'prose', text: messageText.slice(last), lang: '' });
  return segs;
};

// Mirrors CodePlaygroundCard's escapeScriptEnd — breaking out of the srcdoc
// script tag is the one classic footgun.
const escapeStationScriptEnd = (js) => String(js || '').replace(/<\/(script)/gi, '<\\/$1');

// 💭 asides are OUR injection into the glass, but strip the marker lines from
// anything ▶ Run executes anyway — thoughts must never run as the AI's code.
const stripThoughtAsides = (code) => String(code || '').split('\n').filter((l) => !/^\s*\/\/\s*💭/.test(l)).join('\n');

// The literal closing script tag never appears in source (the srcdoc
// script-in-script footgun) — build it at runtime.
const SCRIPT_CLOSE = ['</scr', 'ipt>'].join('');

// Baked into every js run: phosphor body + console.log/warn/error (+ window
// errors) written into the document, so a bare-js fence shows its output.
const STATION_RUN_STYLE = '<style>body{background:#0a0d0a;color:#b7f7c2;font:12px/1.5 Menlo,Consolas,monospace;margin:8px;white-space:pre-wrap}</style>';
// ⚠️ The shim + user script MUST sit inside an explicit <body> (F6, preship
// #92): bare scripts parse into the implied <head> and run synchronously
// while document.body is still null — the writer's try/catch swallowed every
// synchronous log line AND every synchronous error. CodePlaygroundCard's doc
// shape (explicit body) is the mirror.
const STATION_RUN_SHIM = `<script>(function(){var w=function(k,a){try{var d=document.createElement("div");d.textContent=(k==="error"?"✖ ":"")+[].map.call(a,function(x){try{return typeof x==="object"?JSON.stringify(x):String(x)}catch(e){return String(x)}}).join(" ");document.body.appendChild(d)}catch(e){}};["log","warn","error"].forEach(function(k){var o=console[k];console[k]=function(){w(k,arguments);o&&o.apply(console,arguments)}});window.onerror=function(m,s,l){w("error",[m+" (line "+l+")"])}})();` + SCRIPT_CLOSE;

// Own-property guard (preship #92 LOW): a fence lang like "constructor" or
// "toString" is legal under the fence charset and a bare object lookup would
// return an inherited function — the tab would interpolate its source text.
const extForLang = (lang) => (Object.prototype.hasOwnProperty.call(FENCE_EXT, lang) ? FENCE_EXT[lang] : (lang || '').slice(0, 8)) || 'txt';

const stationAgo = (iso) => {
  const ms = Date.now() - new Date(iso).getTime();
  if (!Number.isFinite(ms) || ms < 0) return '';
  const min = Math.floor(ms / 60000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min}m ago`;
  const h = Math.floor(min / 60);
  return h < 24 ? `${h}h ago` : `${Math.floor(h / 24)}d ago`;
};

const STATION_MONO = "'SF Mono', 'Fira Code', Consolas, Menlo, monospace";

// One battlestation = one CRT monitor. Module-level ON PURPOSE — hooks live
// inside it, so the panel's seats.map never holds hooks (hooks-in-map trap).
// `sealed` (09-07 show-view batch): while a /task episode is live and this
// seat has not revealed yet, the glass renders redacted "writing" bars
// instead of stale pre-episode code — the heads-down beat must never read
// as a hang OR leak old work as if it were the entry.
const TerminalStation = ({ seat, shown, thoughts, typing, seatColor, THEME, freshRef, sealed = false }) => {
  const seatKey = seat.id || seat.game_player_id || seat.name;
  const shownId = shown ? String(shown.id || shown.created_at || '') : '';
  const message = (shown && shown.data && shown.data.message) || '';
  const segments = useMemo(() => splitFenceSegments(message), [message]);
  const codeSegs = segments.filter((s) => s.type === 'code');
  const lastLang = codeSegs.length ? codeSegs[codeSegs.length - 1].lang : '';
  // A's filenames: {seat}.{ext} from the latest fence lang. No fence = no tab
  // (Readme refuses code by persona — his glass is OUTPUT text, not breakage).
  const fileName = codeSegs.length
    ? `${String(seat.name || 'seat').toLowerCase().replace(/\s+/g, '_')}.${extForLang(lastLang)}`
    : null;
  const thoughtLine = thoughts ? `// 💭 ${thoughts}` : '';
  const segLen = segments.reduce((n, s) => n + s.text.length, 0);
  const totalLen = segLen + thoughtLine.length;

  // B's typewriter: ONLY messages that arrive while the panel is open animate
  // (~200 chars/sec = 10 chars per 50ms tick). The panel's freshRef maps
  // seatKey → last shown message id; first render records and never animates,
  // so opening the tab on old code renders instantly.
  const [typed, setTyped] = useState(Infinity);
  const scrollerRef = useRef(null);
  useEffect(() => {
    const prev = freshRef.current.get(seatKey);
    freshRef.current.set(seatKey, shownId);
    if (!shownId || prev === undefined || prev === shownId) {
      setTyped(Infinity);
      return undefined;
    }
    setTyped(0);
    const iv = setInterval(() => {
      setTyped((n) => {
        const next = n + 10;
        if (next >= totalLen) { clearInterval(iv); return Infinity; }
        return next;
      });
    }, 50);
    return () => clearInterval(iv);
  }, [shownId, seatKey, totalLen, freshRef]);
  const animating = typed !== Infinity;
  useEffect(() => {
    if (animating && scrollerRef.current) scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
  }, [typed, animating]);

  // C's run dock: html/js fences only. srcdoc + sandbox="allow-scripts"
  // WITHOUT allow-same-origin = opaque origin (no cookies, storage, or
  // credentialed API calls) — CodePlaygroundCard's exact posture.
  let runnable = null;
  for (let i = codeSegs.length - 1; i >= 0; i--) {
    const l = codeSegs[i].lang;
    if (l === 'html' || l === 'js' || l === 'javascript') { runnable = codeSegs[i]; break; }
  }
  const [runDoc, setRunDoc] = useState(null);
  useEffect(() => { setRunDoc(null); }, [shownId]);
  // Sealed-glass bars: deterministic per seat (name-hash PRNG) so re-renders
  // never reshuffle the pattern. Unconditional hook — sealed can flip live.
  const sealBars = useMemo(() => {
    let h = 0;
    const s = String(seatKey);
    for (let i = 0; i < s.length; i++) h = ((h << 5) - h + s.charCodeAt(i)) | 0;
    const rnd = () => { h = ((h * 1103515245) + 12345) | 0; return ((h >>> 16) & 0x7fff) / 0x8000; };
    return Array.from({ length: 9 }, () => ({
      w: 28 + Math.round(rnd() * 55),
      ind: [0, 22, 44][Math.floor(rnd() * 3)],
      hot: rnd() < 0.25,
      dur: (1.3 + rnd() * 0.9).toFixed(2),
    }));
  }, [seatKey]);
  const fireRun = () => {
    if (!runnable) return;
    const code = stripThoughtAsides(runnable.text);
    if (runnable.lang === 'html') setRunDoc(code);
    else setRunDoc(`<!doctype html><html><head>${STATION_RUN_STYLE}</head><body>${STATION_RUN_SHIM}<script>${escapeStationScriptEnd(code)}${SCRIPT_CLOSE}</body></html>`);
  };

  // HEADS DOWN (episode live, nothing revealed from this desk yet): redacted
  // bars behind the glass + a sealed line. All hooks above already ran, so
  // this early return is hook-safe. No fabricated metrics on purpose — the
  // bars say "writing, hidden", the copy says why.
  if (sealed) {
    return (
      <div style={{
        boxSizing: 'border-box', minWidth: 0,
        border: '1px solid rgba(255,255,255,0.14)', borderRadius: '12px',
        background: 'rgba(13,17,23,0.85)', padding: '10px 12px',
        display: 'flex', flexDirection: 'column', gap: '8px',
      }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
          <span style={{ fontWeight: 800, color: seatColor }}>{seat.name}</span>
          {getDisplayModelName(seat) && (
            <span style={{ fontSize: '0.68em', color: '#8b949e', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 999, padding: '1px 8px' }}>
              {getDisplayModelName(seat)}
            </span>
          )}
          <span style={{ marginLeft: 'auto', fontSize: '0.68em', fontFamily: STATION_MONO, color: '#7da88a' }}>
            heads down
          </span>
        </div>
        <div style={{
          position: 'relative', overflow: 'hidden', background: '#07100b',
          border: '1px solid rgba(255,255,255,0.14)', borderRadius: '8px',
        }}>
          <div style={{ padding: '14px 14px 8px', display: 'flex', flexDirection: 'column', gap: 7, minHeight: '120px' }}>
            {sealBars.map((b, i) => (
              <div key={i} style={{
                height: '9px', borderRadius: '2px', marginLeft: `${b.ind}px`,
                width: `${b.w}%`, background: b.hot ? '#3f7a55' : '#274a34',
                animation: `crtSealShimmer ${b.dur}s ease-in-out infinite`,
              }} />
            ))}
          </div>
          <div style={{ padding: '8px 14px 12px', display: 'flex', alignItems: 'center', gap: 8, borderTop: '1px dashed rgba(255,255,255,0.1)' }}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#7da88a" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="4" y="10" width="16" height="10" rx="2"></rect><path d="M8 10V7a4 4 0 0 1 8 0v3"></path></svg>
            <span style={{ fontFamily: STATION_MONO, fontSize: '0.72em', color: '#7da88a' }}>sealed until reveal</span>
            <span style={{ fontFamily: STATION_MONO, fontSize: '0.72em', color: '#c9f7d0', animation: 'crtCursorBlink 0.9s steps(1) infinite' }}>▍</span>
          </div>
          <div aria-hidden="true" style={{
            position: 'absolute', top: 0, right: 0, bottom: 0, left: 0, pointerEvents: 'none',
            background: 'repeating-linear-gradient(0deg, rgba(0,0,0,0.22) 0px, rgba(0,0,0,0.22) 1px, transparent 1px, transparent 3px)',
          }} />
          <div aria-hidden="true" style={{
            position: 'absolute', top: 0, right: 0, bottom: 0, left: 0, pointerEvents: 'none',
            background: 'radial-gradient(ellipse at center, transparent 62%, rgba(0,0,0,0.32) 100%)',
          }} />
        </div>
      </div>
    );
  }

  // Slice the glass content to the typed count — segments in order, then the
  // 💭 aside types LAST (amber, marker mandatory: never reads as the AI's code).
  const visible = typed === Infinity ? Number.MAX_SAFE_INTEGER : typed;
  let used = 0;
  const glassChildren = [];
  segments.forEach((s, i) => {
    if (used >= visible) return;
    const take = Math.min(s.text.length, visible - used);
    const txt = s.text.slice(0, take);
    used += take;
    if (!txt.trim()) return;
    glassChildren.push(
      <div key={`s${i}`} style={s.type === 'code'
        ? { color: '#c9f7d0', whiteSpace: 'pre', overflowX: 'auto', margin: '4px 0' }
        : { color: '#7da88a', whiteSpace: 'pre-wrap', opacity: 0.9, margin: '2px 0' }}>
        {txt}
      </div>
    );
  });
  if (thoughtLine && used < visible) {
    glassChildren.push(
      <div key="thought" style={{ color: '#ffb86c', fontStyle: 'italic', whiteSpace: 'pre-wrap', margin: '4px 0' }}>
        {thoughtLine.slice(0, Math.min(thoughtLine.length, visible - used))}
      </div>
    );
  }
  if (shown) {
    // The block cursor: solid while typing, parks (slow blink) after.
    glassChildren.push(
      <span key="cursor" style={{ color: '#c9f7d0', animation: animating ? 'none' : 'crtCursorBlink 1.1s steps(1) infinite' }}>█</span>
    );
  }

  return (
    <div style={{
      boxSizing: 'border-box', minWidth: 0, // grid child: shrinkable, never overflows the viewport
      border: '1px solid rgba(255,255,255,0.14)', borderRadius: '12px',
      background: 'rgba(13,17,23,0.6)', padding: '10px 12px',
      display: 'flex', flexDirection: 'column', gap: '8px',
    }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
        <span style={{ fontWeight: 800, color: seatColor }}>{seat.name}</span>
        {getDisplayModelName(seat) && (
          <span style={{ fontSize: '0.68em', color: THEME.textMuted || '#8b949e', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 999, padding: '1px 8px' }}>
            {getDisplayModelName(seat)}
          </span>
        )}
        <span style={{ marginLeft: 'auto', fontSize: '0.68em', color: THEME.textMuted || '#8b949e' }}>
          {typing ? '⌨️ typing…' : (shown ? stationAgo(shown.created_at) : '')}
        </span>
      </div>
      <div style={{ minWidth: 0 }}>
        {fileName && (
          <div style={{ display: 'flex' }}>
            <span style={{
              fontSize: '0.7em', fontFamily: STATION_MONO, color: seatColor,
              background: '#07100b', border: '1px solid rgba(255,255,255,0.14)', borderBottom: 'none',
              borderRadius: '6px 6px 0 0', padding: '3px 10px',
            }}>{fileName}</span>
          </div>
        )}
        {/* CRT glass: scanlines + vignette are gradient overlays over a fixed
            frame while the content scrolls beneath — NEVER blur (perf rule). */}
        <div style={{
          position: 'relative', overflow: 'hidden', background: '#07100b',
          border: '1px solid rgba(255,255,255,0.14)',
          borderRadius: fileName ? '0 8px 8px 8px' : '8px',
        }}>
          <div ref={scrollerRef} style={{
            maxHeight: '340px', overflowY: 'auto', minWidth: 0,
            padding: '10px 12px', fontFamily: STATION_MONO, fontSize: '0.8em', lineHeight: 1.55,
          }}>
            {glassChildren.length ? glassChildren : (
              <div style={{ color: '#5c7a66', fontStyle: 'italic' }}>booting up…</div>
            )}
          </div>
          <div aria-hidden="true" style={{
            position: 'absolute', top: 0, right: 0, bottom: 0, left: 0, pointerEvents: 'none',
            background: 'repeating-linear-gradient(0deg, rgba(0,0,0,0.22) 0px, rgba(0,0,0,0.22) 1px, transparent 1px, transparent 3px)',
          }} />
          <div aria-hidden="true" style={{
            position: 'absolute', top: 0, right: 0, bottom: 0, left: 0, pointerEvents: 'none',
            background: 'radial-gradient(ellipse at center, transparent 62%, rgba(0,0,0,0.32) 100%)',
          }} />
        </div>
      </div>
      {runnable && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={fireRun} style={{
              cursor: 'pointer', fontFamily: STATION_MONO, fontSize: '0.75em', fontWeight: 700,
              color: '#b7f7c2', background: '#0d1a12', border: '1px solid rgba(183,247,194,0.35)',
              borderRadius: '6px', padding: '4px 12px',
            }}>▶ Run</button>
            {runDoc != null && (
              <button onClick={() => setRunDoc(null)} style={{
                cursor: 'pointer', fontFamily: STATION_MONO, fontSize: '0.75em',
                color: THEME.textMuted || '#8b949e', background: 'transparent',
                border: '1px solid rgba(255,255,255,0.18)', borderRadius: '6px', padding: '4px 10px',
              }}>✕ Clear</button>
            )}
          </div>
          {runDoc != null && (
            <iframe
              title={`${seat.name} run`}
              sandbox="allow-scripts"
              srcDoc={runDoc}
              style={{ width: '100%', height: '180px', border: '1px solid rgba(255,255,255,0.14)', borderRadius: '8px', background: '#0a0d0a' }}
            />
          )}
        </div>
      )}
    </div>
  );
};

// 🎲 Start-episode suggestions (09-07 show-view batch): seeds for the mod's
// one-click composer. Player copy: short, punchy, no em dashes.
const TERMINAL_TASK_IDEAS = [
  'build a snake game', 'make it rain in ASCII', 'draw a self portrait in CSS',
  'write fizzbuzz without using %', 'build a tiny drum machine',
  'animate a bouncing ball with gravity', 'make a clock that lies a little',
  'build a one button game', 'generate a maze and solve it',
  'write a poem generator', 'simulate a lava lamp',
  'make a pixel fireworks show', 'build tic tac toe that trash talks',
];

// ⌨️ THE TERMINAL show view (09-07, Nina: single tab + play button + match
// the mockups). This panel is now the whole show surface: episode HUD
// (phase track + countdown), battlestations (sealed during heads-down),
// season ledger, and the mod's Start Episode composer. It paints its OWN
// dark CRT palette on purpose — the space theme is light-generated and
// themed text tokens would vanish on the glass.
const TerminalWorkbenchPanel = ({ users = [], results = [], currentlyTyping = null, THEME, ledger = null, isSpaceMod = false, onStartTask = null }) => {
  // AI locals = non-human seats with a model (the seats the discussion engine
  // steps). is_human must be checked too — human seats surface a "Human
  // Player" model label, so model-presence alone put the mod on the wall
  // (caught by the 08-20 live preview). playerKind lesson: DISPLAY choice
  // only, nothing behavioral gates on it.
  const seats = (users || []).filter((u) => u && !u.is_human && u.model);

  // Typewriter freshness lives at the PANEL: seatKey → last shown message id.
  // The map dies with the panel, so reopening the tab renders instantly.
  const freshRef = useRef(new Map());

  // ⚠️ NEVER trust the array order here. The MOUNT path
  // (takeGameStep?includeCurrentActions=true, handler.js) queries
  // `orderBy('created_at','desc')` and returns it UNFLIPPED, so a freshly
  // loaded page delivers actions NEWEST-FIRST — while the poll/WS append
  // newer rows at the end. Reading "the latest" off the tail therefore
  // showed the OLDEST task and the OLDEST code on every fresh load. Sort by
  // the timestamp and the panel is correct under any delivery order.
  const flat = useMemo(() => {
    const rows = [];
    (results || []).forEach((r) => (r.actions || []).forEach((a) => a && rows.push(a)));
    return rows.sort((x, y) => (Date.parse(x.created_at) || 0) - (Date.parse(y.created_at) || 0));
  }, [results]);

  // The room's latest /task — newest space_command row with command 'task'.
  // The ROW (not just data): created_at is the episode start line the sealed
  // check compares message times against.
  const taskRow = useMemo(() => {
    for (let i = flat.length - 1; i >= 0; i--) {
      const a = flat[i];
      if (a.type === 'space_command' && a.data?.command === 'task') return a;
    }
    return null;
  }, [flat]);
  const task = taskRow ? taskRow.data : null;

  // ⏱ Wave A clock: 'live' counts down to deadline_at (1s tick while one is
  // running), 'judging'/'polling' ride the row's lifecycle status.
  const taskState = task ? (task.status === 'judging' ? 'judging' : task.status === 'polling' ? 'polling' : task.status === 'active' ? 'live' : 'done') : 'none';
  const taskDeadlineMs = task?.deadline_at ? new Date(task.deadline_at).getTime() : null;
  const [taskNowMs, setTaskNowMs] = useState(() => Date.now());
  useEffect(() => {
    if (!(taskState === 'live' && taskDeadlineMs)) return undefined;
    // Stamp immediately: taskNowMs is otherwise frozen at mount, so an
    // episode starting while the panel was already open painted its first
    // frame from a stale clock (off by however long the tab sat idle).
    setTaskNowMs(Date.now());
    const t = setInterval(() => setTaskNowMs(Date.now()), 1000);
    return () => clearInterval(t);
  }, [taskState, taskDeadlineMs]);
  const taskCountdown = (taskState === 'live' && taskDeadlineMs) ? Math.max(0, taskDeadlineMs - taskNowMs) : null;
  const fmtCountdown = (ms) => {
    const sTotal = Math.ceil(ms / 1000);
    return `${Math.floor(sTotal / 60)}:${String(sTotal % 60).padStart(2, '0')}`;
  };

  // Per-seat message buckets (HighlightsPanel's statsByPlayer idiom). Seats
  // are matched on game_instance_player_id OR data.sender_id OR sender_name —
  // the row carries all three depending on lane/age.
  const bySeat = useMemo(() => {
    const m = new Map();
    seats.forEach((u) => m.set(u, []));
    flat.forEach((a) => {
      if (a.type !== 'public_message' || !a.data?.message) return;
      const seat = seats.find((u) =>
        (a.game_instance_player_id && (a.game_instance_player_id === u.game_player_id || a.game_instance_player_id === u.id))
        || (a.data.sender_id && (a.data.sender_id === u.game_player_id || a.data.sender_id === u.id))
        || (a.data.sender_name && a.data.sender_name === u.name));
      if (seat) m.get(seat).push(a);
    });
    return m;
  }, [flat, seats]);

  const hasFencePair = (s) => {
    if (typeof s !== 'string' || !s.includes('```')) return false;
    CODE_FENCE_RE.lastIndex = 0;
    return CODE_FENCE_RE.test(s);
  };

  // Episode phase for the track (display-only; the backend owns the real
  // lifecycle). HEADS DOWN = live episode with no desk posted since the task
  // row; SCREENS ON = reveals arriving; SCORING / ROOM VOTES ride the row's
  // lifecycle status the status poll already delivers.
  const taskStartMs = taskRow ? (Date.parse(taskRow.created_at) || 0) : 0;
  const revealedSince = (u) => {
    const msgs = bySeat.get(u) || [];
    const last = msgs[msgs.length - 1];
    return !!last && (Date.parse(last.created_at) || 0) >= taskStartMs;
  };
  const anyRevealed = taskState === 'live' && seats.some(revealedSince);
  const phase = taskState === 'live' ? (anyRevealed ? 'screens_on' : 'heads_down')
    : taskState === 'judging' ? 'scoring'
      : taskState === 'polling' ? 'votes' : null;
  const PHASES = [
    ['heads_down', 'HEADS DOWN'], ['screens_on', 'SCREENS ON'],
    ['scoring', 'SCORING'], ['votes', 'ROOM VOTES'],
  ];
  const phaseIdx = PHASES.findIndex((p) => p[0] === phase);

  // ▶ Start Episode composer (mod-only, idle-only — the backend refuses a
  // /task while an episode is open, so the button simply never offers one).
  const [composerOpen, setComposerOpen] = useState(false);
  const [taskDraft, setTaskDraft] = useState(() => TERMINAL_TASK_IDEAS[Math.floor(Math.random() * TERMINAL_TASK_IDEAS.length)]);
  const [taskMins, setTaskMins] = useState(10);
  const [startBusy, setStartBusy] = useState(false);
  const [startErr, setStartErr] = useState(null);
  const rollIdea = () => setTaskDraft(TERMINAL_TASK_IDEAS[Math.floor(Math.random() * TERMINAL_TASK_IDEAS.length)]);
  const fireStart = async () => {
    if (!onStartTask || startBusy) return;
    const text = taskDraft.trim();
    if (!text) { setStartErr('Type a task first!'); return; }
    setStartBusy(true); setStartErr(null);
    try {
      await onStartTask(`${taskMins}m ${text}`);
      setComposerOpen(false);
      rollIdea();
    } catch (err) {
      setStartErr((err && err.message) || 'Could not start the episode');
    } finally {
      setStartBusy(false);
    }
  };
  const canStart = isSpaceMod && !!onStartTask && !phase;

  return (
    <div style={{
      boxSizing: 'border-box', padding: '12px', display: 'flex', flexDirection: 'column', gap: '12px',
      background: '#0a0e14', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '14px',
    }}>
      <style>{'@keyframes crtCursorBlink { 0%, 49% { opacity: 1; } 50%, 100% { opacity: 0; } } @keyframes crtSealShimmer { 0%, 100% { opacity: 0.45; } 50% { opacity: 0.95; } } @keyframes crtStripeMove { 0% { background-position: 0 0; } 100% { background-position: 28px 0; } } @keyframes crtLiveDot { 0%, 100% { opacity: 1; } 50% { opacity: 0.35; } }'}</style>
      {/* Identity row — the show names itself; LIVE only while an episode is
          actually open (phase != null), never a fake signal. */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '0 2px' }}>
        <span style={{ fontFamily: STATION_MONO, fontSize: '1.05em', fontWeight: 800, letterSpacing: '2px', color: '#c9f7d0', textShadow: '0 0 12px rgba(201,247,208,0.35)' }}>THE TERMINAL</span>
        <span style={{ fontFamily: STATION_MONO, fontSize: '1.05em', color: '#c9f7d0', animation: 'crtCursorBlink 1.1s steps(1) infinite' }}>█</span>
        <span style={{ marginLeft: 'auto' }}>
          {phase ? (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '3px 10px', border: '1px solid rgba(255,107,107,0.5)', borderRadius: 999, background: 'rgba(255,107,107,0.1)' }}>
              <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#ff6b6b', animation: 'crtLiveDot 1.4s ease-in-out infinite' }} />
              <span style={{ fontFamily: STATION_MONO, fontSize: '0.68em', fontWeight: 800, letterSpacing: '1.2px', color: '#ff6b6b' }}>LIVE{task?.episode ? ` · EP ${task.episode}` : ''}</span>
            </span>
          ) : (
            <span style={{ fontFamily: STATION_MONO, fontSize: '0.68em', fontWeight: 700, letterSpacing: '1.2px', color: '#5c7a66' }}>OFF AIR</span>
          )}
        </span>
      </div>
      {/* NOW BAR — episode, task, phase track, and the clock (Wave A). The
          countdown is client-derived from deadline_at each second; judging/
          polling states come off the same task row the status poll delivers. */}
      <div style={{
        boxSizing: 'border-box', width: '100%',
        border: '1px solid rgba(102,187,255,0.33)', borderRadius: '10px',
        padding: '10px 14px', background: 'rgba(13,17,23,0.85)',
      }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, fontSize: '0.68em', fontWeight: 800, letterSpacing: '1.4px', textTransform: 'uppercase', color: '#8b949e', marginBottom: 4 }}>
          <span>⌨️ {phase ? 'Current task' : (task ? 'Last task' : 'Next task')}</span>
          <span style={{ marginLeft: 'auto', fontFamily: STATION_MONO, letterSpacing: 0, textTransform: 'none' }}>
            {taskState === 'live' && taskCountdown != null && (
              <span style={{ fontSize: '1.5em', fontWeight: 700, color: taskCountdown < 60000 ? '#ff6b6b' : '#b7f7c2' }}>⏱ {fmtCountdown(taskCountdown)}</span>
            )}
            {taskState === 'live' && taskCountdown == null && task?.remaining?.room ? `${task.remaining.room} turns left` : ''}
            {taskState === 'judging' && <span style={{ color: '#ffb86c' }}>⚖️ scoring…</span>}
            {taskState === 'polling' && <span style={{ color: '#ffb86c' }}>🗳 room votes!</span>}
          </span>
        </div>
        <div style={{ fontSize: '0.95em', fontFamily: STATION_MONO, color: '#e8f5ec' }}>
          {task ? task.text : 'No task yet. Anything you can dream up, timed.'}
        </div>
        {/* Phase track: the fix for "is it hung?" — the quiet heads-down
            stretch is a NAMED show beat, not silence. */}
        {phase && (
          <div style={{ display: 'flex', gap: 6, marginTop: 10, flexWrap: 'wrap' }}>
            {PHASES.map(([key, label], i) => {
              const state = i < phaseIdx ? 'done' : (i === phaseIdx ? 'active' : 'next');
              return (
                <span key={key} style={{
                  flex: '1 1 auto', textAlign: 'center', position: 'relative', overflow: 'hidden',
                  fontFamily: STATION_MONO, fontSize: '0.62em', fontWeight: 800, letterSpacing: '1.2px',
                  padding: '6px 10px', borderRadius: 7,
                  border: state === 'active' ? '1px solid rgba(183,247,194,0.5)' : '1px solid rgba(255,255,255,0.14)',
                  background: state === 'active' ? 'rgba(21,42,29,0.9)' : (state === 'done' ? 'rgba(21,42,29,0.45)' : 'transparent'),
                  color: state === 'active' ? '#c9f7d0' : (state === 'done' ? '#7da88a' : '#5c7a66'),
                }}>
                  {state === 'active' && (
                    <span aria-hidden="true" style={{ position: 'absolute', top: 0, right: 0, bottom: 0, left: 0, background: 'repeating-linear-gradient(-55deg, rgba(183,247,194,0.09) 0 8px, transparent 8px 14px)', animation: 'crtStripeMove 1.2s linear infinite' }} />
                  )}
                  <span style={{ position: 'relative' }}>{label}{state === 'done' ? ' ✓' : ''}</span>
                </span>
              );
            })}
          </div>
        )}
        {/* ▶ Start Episode — the mod's one-click on-ramp to /task. Idle-only;
            gated on the same isSpaceMod the composer palette uses. */}
        {!phase && (canStart ? (
          !composerOpen ? (
            <button onClick={() => setComposerOpen(true)} style={{
              marginTop: 10, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 8,
              fontFamily: STATION_MONO, fontSize: '0.85em', fontWeight: 800,
              color: '#07100b', background: '#b7f7c2', textShadow: 'none', border: 'none',
              borderRadius: '8px', padding: '10px 18px', minHeight: '44px',
            }}>▶ Start episode</button>
          ) : (
            <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <input
                  value={taskDraft}
                  onChange={(e) => setTaskDraft(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') fireStart(); }}
                  maxLength={200}
                  placeholder="what should they build?"
                  style={{
                    flex: '1 1 220px', minWidth: 0, boxSizing: 'border-box', minHeight: '44px',
                    fontFamily: STATION_MONO, fontSize: '0.85em', color: '#c9f7d0',
                    background: '#07100b', border: '1px solid rgba(183,247,194,0.3)',
                    borderRadius: '8px', padding: '8px 12px', outline: 'none',
                  }}
                />
                <button onClick={rollIdea} title="Roll another idea" style={{
                  cursor: 'pointer', fontFamily: STATION_MONO, fontSize: '0.85em', minHeight: '44px',
                  color: '#b7f7c2', background: 'transparent', border: '1px solid rgba(183,247,194,0.3)',
                  borderRadius: '8px', padding: '8px 12px',
                }}>🎲</button>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                {[5, 10, 20].map((m) => (
                  <button key={m} onClick={() => setTaskMins(m)} style={{
                    cursor: 'pointer', fontFamily: STATION_MONO, fontSize: '0.75em', fontWeight: 700, minHeight: '36px',
                    color: taskMins === m ? '#07100b' : '#7da88a',
                    background: taskMins === m ? '#b7f7c2' : 'transparent',
                    border: '1px solid rgba(183,247,194,0.3)', borderRadius: 999, padding: '6px 14px',
                  }}>{m} min</button>
                ))}
                <button onClick={fireStart} disabled={startBusy} style={{
                  cursor: startBusy ? 'wait' : 'pointer', marginLeft: 'auto',
                  fontFamily: STATION_MONO, fontSize: '0.85em', fontWeight: 800, minHeight: '44px',
                  color: '#07100b', background: startBusy ? '#7da88a' : '#b7f7c2', border: 'none',
                  borderRadius: '8px', padding: '10px 18px',
                }}>{startBusy ? 'Starting…' : '▶ Go'}</button>
                <button onClick={() => { setComposerOpen(false); setStartErr(null); }} style={{
                  cursor: 'pointer', fontFamily: STATION_MONO, fontSize: '0.75em', minHeight: '36px',
                  color: '#8b949e', background: 'transparent', border: '1px solid rgba(255,255,255,0.18)',
                  borderRadius: '8px', padding: '6px 12px',
                }}>Cancel</button>
              </div>
              {startErr && (
                <div style={{ fontFamily: STATION_MONO, fontSize: '0.75em', color: '#ff6b6b' }}>{startErr}</div>
              )}
            </div>
          )
        ) : (
          <div style={{ marginTop: 8, fontFamily: STATION_MONO, fontSize: '0.75em', color: '#5c7a66' }}>
            Between episodes. The next task drops soon.
          </div>
        ))}
        {/* 🧾 Season ledger — all-time W/L/DNF per seat, from game data (the
            feed window scrolls; the ledger does not). */}
        {ledger && Number(ledger.episodes) > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', gap: 10, marginTop: 8, paddingTop: 8, borderTop: '1px dashed rgba(255,255,255,0.14)', fontFamily: STATION_MONO, fontSize: '0.72em' }}>
            <span style={{ color: '#8b949e', fontWeight: 700 }}>🏆 SEASON · {ledger.episodes} EP</span>
            {Object.entries(ledger.seats || {})
              .sort((x, y) => (y[1]?.w || 0) - (x[1]?.w || 0))
              .map(([name, r]) => (
                <span key={name} style={{ color: '#b7f7c2' }}>
                  {name} <span style={{ color: '#8b949e' }}>{r?.w || 0}W-{r?.l || 0}L{r?.dnf ? `-${r.dnf}DNF` : ''}</span>
                </span>
              ))}
          </div>
        )}
      </div>

      {/* Battlestations — one CRT monitor per AI seat; sealed while the
          episode is live and the desk has not revealed since the task row. */}
      <div style={{
        display: 'grid', gap: '12px',
        gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
      }}>
        {seats.length === 0 && (
          <div style={{ color: '#8b949e', fontSize: '0.85em', padding: '8px 2px' }}>
            No AI seats in this space yet.
          </div>
        )}
        {seats.map((u) => {
          const msgs = bySeat.get(u) || [];
          const latest = msgs[msgs.length - 1] || null;
          let coding = null;
          for (let i = msgs.length - 1; i >= 0; i--) {
            if (hasFencePair(msgs[i].data.message)) { coding = msgs[i]; break; }
          }
          let thoughts = null;
          for (let i = msgs.length - 1; i >= 0; i--) {
            if (msgs[i].data?.thoughts) { thoughts = msgs[i].data.thoughts; break; }
          }
          return (
            <TerminalStation
              key={u.id || u.game_player_id || u.name}
              seat={u}
              shown={coding || latest}
              thoughts={thoughts}
              typing={!!(currentlyTyping && currentlyTyping === u.name)}
              seatColor={getModelColor(u.model, THEME)}
              THEME={THEME}
              freshRef={freshRef}
              sealed={taskState === 'live' && !revealedSince(u)}
            />
          );
        })}
      </div>
    </div>
  );
};

const getCommonStyles = (THEME) => ({
  actionContainer: {
    borderRadius: '8px',
    marginBottom: '16px',
    boxShadow: `0 2px 4px ${THEME.textDark}33, 0 0 8px ${THEME.accent}08`,
    overflow: 'hidden',
    border: `1px solid ${THEME.accent}20`,
  },
  actionHeader: {
    padding: '12px 16px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderBottom: `1px solid ${THEME.accent}20`,
  },
  actionBody: {
    padding: '16px',
  },
  playerBadge: {
    padding: '4px 8px',
    borderRadius: '4px',
    fontWeight: 'bold',
    fontSize: '0.9em',
    border: `1px solid ${THEME.borderDark}`,
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
  },
  typeBadge: {
    padding: '2px 8px',
    borderRadius: '12px',
    fontSize: '0.8em',
    fontWeight: 'bold',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  statusBadge: {
    padding: '1px 6px',
    borderRadius: '4px',
    fontSize: '0.7em',
    fontWeight: 'bold',
    textTransform: 'uppercase',
  },
  messageBox: {
    backgroundColor: THEME.bgParchment,
    padding: '12px',
    borderRadius: '4px',
    border: `1px solid ${THEME.borderGold}`,
    color: THEME.textDark,
    fontFamily: 'Georgia, serif',
    lineHeight: '1.6',
  },
  thoughtBox: {
    backgroundColor: `${THEME.textLight}0d`,
    padding: '8px 12px',
    borderRadius: '4px',
    border: `1px solid ${THEME.borderLight}`,
    color: THEME.textMuted,
    fontSize: '0.85em',
    fontStyle: 'italic',
    marginTop: '8px',
  },
  diaryBox: {
    backgroundColor: '${THEME.accent}1a',
    padding: '8px 12px',
    borderRadius: '4px',
    border: `1px solid ${THEME.accent}`,
    color: THEME.textLight,
    fontSize: '0.85em',
    marginTop: '8px',
  },
});

// Bouncing letter-by-letter text for status buttons (same wave as space name)
// Trailing "..." gets a separate loading-dot animation (larger, sequential pulse)
const BouncingText = ({ text, duration = 8, stagger = 0.06 }) => {
  const hasEllipsis = text.endsWith('...');
  const mainText = hasEllipsis ? text.slice(0, -3) : text;
  return (
    <span style={{ display: 'inline-flex', flexWrap: 'wrap', justifyContent: 'center', alignItems: 'baseline' }}>
      {mainText.split('').map((char, i) => (
        <span
          key={i}
          style={{
            display: 'inline-block',
            animation: `letterBounce ${duration}s ease-in-out ${i * stagger}s infinite`,
            whiteSpace: char === ' ' ? 'pre' : undefined,
          }}
        >
          {char}
        </span>
      ))}
      {hasEllipsis && [0, 1, 2].map(dotIdx => (
        <span
          key={`dot-${dotIdx}`}
          style={{
            display: 'inline-block',
            fontSize: '1.35em',
            fontWeight: 900,
            lineHeight: 1,
            marginLeft: dotIdx === 0 ? '1px' : '0px',
            animation: `loadingDot 1.4s ease-in-out ${dotIdx * 0.2}s infinite`,
          }}
        >
          .
        </span>
      ))}
    </span>
  );
};

// Video Player with Thumbnail Preview - shows thumbnail that loads video into player when clicked
const VideoPlayerWithThumbnail = ({ videoUrl, previewUrl, style, THEME, ...videoProps }) => {
  const [isPlaying, setIsPlaying] = React.useState(false);

  if (!videoUrl) return null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
      {/* Video Player Section - Only visible when playing */}
      {isPlaying && (
        <div style={{
          padding: '16px',
          backgroundColor: THEME?.bgDark || '#1a1a1a',
          borderRadius: '8px',
          border: `2px solid ${THEME?.accent || '#9C27B0'}`
        }}>
          <video
            {...videoProps}
            key={videoUrl}
            src={videoUrl}
            controls
            autoPlay
            style={{
              width: '100%',
              maxWidth: '600px',
              borderRadius: '4px',
              ...style
            }}
          />
        </div>
      )}

      {/* Thumbnail Preview - Click to load video */}
      <div
        style={{
          position: 'relative',
          cursor: 'pointer',
          overflow: 'hidden',
          backgroundColor: previewUrl ? 'transparent' : '#000',
          borderRadius: '4px',
          border: isPlaying ? `2px solid ${THEME?.accent || '#9C27B0'}` : '1px solid #333',
          ...style
        }}
        onClick={() => setIsPlaying(true)}
      >
        {previewUrl ? (
          <img
            src={previewUrl}
            alt="Video thumbnail"
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover'
            }}
          />
        ) : (
          <div style={{
            width: '100%',
            height: '100%',
            minHeight: '200px',
            backgroundColor: '#000'
          }} />
        )}
        <div style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '60px',
          height: '60px',
          borderRadius: '50%',
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          border: '3px solid white'
        }}>
          <div style={{
            width: 0,
            height: 0,
            borderLeft: '18px solid white',
            borderTop: '12px solid transparent',
            borderBottom: '12px solid transparent',
            marginLeft: '4px'
          }} />
        </div>
        {!isPlaying && (
          <div style={{
            position: 'absolute',
            bottom: '8px',
            left: '8px',
            right: '8px',
            padding: '6px 12px',
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            color: 'white',
            fontSize: '0.85em',
            textAlign: 'center',
            borderRadius: '4px'
          }}>
            Click to play video
          </div>
        )}
      </div>
    </div>
  );
};

// Video Preview Component - shows image preview or black rectangle until clicked, then shows video
const VideoWithPreview = ({ videoUrl, previewUrl, style, controls = true, autoPlay = false, onDragStart, draggable, onEnded, ...otherProps }) => {
  const [showVideo, setShowVideo] = React.useState(false);
  const [isDragging, setIsDragging] = React.useState(false);

  if (!videoUrl) return null;

  // Show preview image or black rectangle until clicked
  if (!showVideo) {
    const handleDragStart = (e) => {
      console.log('[VideoWithPreview] Drag started');
      setIsDragging(true);
      if (onDragStart) {
        onDragStart(e);
      }
    };

    return (
      <div
        {...otherProps}
        draggable={draggable}
        onDragStart={handleDragStart}
        style={{
          ...style,
          position: 'relative',
          cursor: draggable ? 'grab' : 'pointer',
          overflow: 'hidden',
          backgroundColor: previewUrl ? 'transparent' : '#000'
        }}
        onClick={(e) => {
          // Don't play video if user was dragging
          if (!isDragging) {
            setShowVideo(true);
          }
          setIsDragging(false);
        }}
        onDragEnd={() => {
          // Reset dragging state after a delay to prevent click from firing
          setTimeout(() => setIsDragging(false), 100);
        }}
      >
        {previewUrl ? (
          <img
            src={previewUrl}
            alt="Video preview"
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              pointerEvents: 'none'
            }}
          />
        ) : (
          <div style={{
            width: '100%',
            height: '100%',
            minHeight: '200px',
            backgroundColor: '#000',
            pointerEvents: 'none'
          }} />
        )}
        <div style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '60px',
          height: '60px',
          borderRadius: '50%',
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          border: '3px solid white',
          pointerEvents: 'none'
        }}>
          <div style={{
            width: 0,
            height: 0,
            borderLeft: '18px solid white',
            borderTop: '12px solid transparent',
            borderBottom: '12px solid transparent',
            marginLeft: '4px'
          }} />
        </div>
      </div>
    );
  }

  // Show video after click
  return (
    <video
      {...otherProps}
      draggable={draggable}
      onDragStart={onDragStart}
      onEnded={onEnded}
      src={videoUrl}
      style={style}
      controls={controls}
      autoPlay={true}
      muted={false}
    />
  );
};

// VideoSeriesPlayer component - displays multi-part videos with navigation
const VideoSeriesPlayer = ({ videos, videoType, title, THEME, metadata = {}, showOverlay = false, overlayContent = null, overlayPosition = { x: 50, y: 50 }, onOverlayDrag = null, isDragging = false }) => {
  const [currentIndex, setCurrentIndex] = React.useState(videos && videos.length > 0 ? videos.length - 1 : 0);

  // Guard against empty or invalid videos array
  if (!videos || videos.length === 0) return null;

  // Ensure currentIndex is within bounds
  const safeIndex = Math.min(currentIndex, videos.length - 1);
  const currentVideo = videos[safeIndex];

  // Extra safety check - if currentVideo is still undefined, don't render
  if (!currentVideo || !currentVideo.url) {
    console.warn('[VideoSeriesPlayer] Invalid video at index', safeIndex, videos);
    return null;
  }

  const hasPrev = safeIndex > 0;
  const hasNext = safeIndex < videos.length - 1;

  const handlePrev = () => {
    if (hasPrev) setCurrentIndex(prev => Math.max(0, prev - 1));
  };

  const handleNext = () => {
    if (hasNext) setCurrentIndex(prev => Math.min(videos.length - 1, prev + 1));
  };

  // Auto-play next video when current finishes
  const handleVideoEnded = () => {
    if (hasNext) {
      setTimeout(() => setCurrentIndex(prev => Math.min(videos.length - 1, prev + 1)), 500);
    }
  };

  return (
    <div style={{ marginTop: '12px' }}>
      {/* Title and Part Indicator */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '8px'
      }}>
        <div style={{
          color: THEME.accent,
          fontWeight: 'bold',
          fontSize: '0.85em'
        }}>
          {title}
        </div>
        {videos.length > 1 && (
          <div style={{
            color: THEME.textLight,
            fontSize: '0.75em',
            backgroundColor: THEME.backgroundDark,
            padding: '4px 8px',
            borderRadius: '12px'
          }}>
            Part {safeIndex + 1} of {videos.length}
          </div>
        )}
      </div>

      {/* Video Player */}
      <div style={{
        position: 'relative',
        width: '100%',
        maxWidth: '900px'
      }}>
        <VideoWithPreview
          key={currentVideo.url}
          videoUrl={currentVideo.url}
          previewUrl={currentVideo.preview_url}
          controls
          draggable="true"
          onEnded={handleVideoEnded}
          onDragStart={(e) => {
            const filename = currentVideo.url.split('/').pop().split('?')[0];
            const videoData = {
              url: currentVideo.url,
              filename: filename,
              title: `${title}${videos.length > 1 ? ` (Part ${safeIndex + 1}/${videos.length})` : ''}`,
              type: `dialogue-${videoType}-veo3`,
              metadata: {
                ...metadata,
                partIndex: safeIndex,
                totalParts: videos.length,
                dialogue: currentVideo.dialogue_portion
              }
            };
            console.log('[VideoSeriesPlayer] Setting drag data:', videoData);
            e.dataTransfer.setData('video-data', JSON.stringify(videoData));
            e.dataTransfer.effectAllowed = 'copy';
          }}
          style={{
            width: '100%',
            maxWidth: '600px',
            borderRadius: '4px',
            border: showOverlay ? '2px dashed #FFD700' : `1px solid ${THEME.borderLight}`,
            cursor: 'grab'
          }}
        />
        {overlayContent}
      </div>

      {/* Navigation Buttons */}
      {videos.length > 1 && (
        <div style={{
          display: 'flex',
          gap: '8px',
          marginTop: '8px',
          justifyContent: 'center'
        }}>
          <button
            onClick={handlePrev}
            disabled={!hasPrev}
            style={{
              padding: '6px 16px',
              backgroundColor: hasPrev ? THEME.accent : THEME.backgroundDark,
              color: hasPrev ? 'white' : THEME.textMuted,
              border: 'none',
              borderRadius: '4px',
              cursor: hasPrev ? 'pointer' : 'not-allowed',
              fontSize: '0.85em',
              fontWeight: 'bold'
            }}
          >
            ← Previous
          </button>
          <button
            onClick={handleNext}
            disabled={!hasNext}
            style={{
              padding: '6px 16px',
              backgroundColor: hasNext ? THEME.accent : THEME.backgroundDark,
              color: hasNext ? 'white' : THEME.textMuted,
              border: 'none',
              borderRadius: '4px',
              cursor: hasNext ? 'pointer' : 'not-allowed',
              fontSize: '0.85em',
              fontWeight: 'bold'
            }}
          >
            Next →
          </button>
        </div>
      )}

      {/* Dialogue Portion (if multi-part) */}
      {videos.length > 1 && currentVideo.dialogue_portion && (
        <div style={{
          marginTop: '8px',
          padding: '8px',
          backgroundColor: THEME.backgroundDark,
          borderRadius: '4px',
          fontSize: '0.8em',
          color: THEME.textLight,
          maxHeight: '100px',
          overflowY: 'auto'
        }}>
          <strong>Dialogue:</strong> {currentVideo.dialogue_portion}
        </div>
      )}
    </div>
  );
};

// 🍿 YouTube titles arrive HTML-encoded (&quot; etc) and rendered raw in the
// NOW PLAYING line (Nina 07-21). Decode ONCE at watchParty ingestion — the
// detached-textarea trick handles every entity, and React re-escapes on
// render so decoded text can't inject.
const decodeHtmlEntities = (s) => {
  if (!s || typeof s !== 'string' || s.indexOf('&') === -1) return s;
  try {
    const ta = document.createElement('textarea');
    ta.innerHTML = s;
    return ta.value;
  } catch (e) { return s; }
};
const decodeWatchPartyTitles = (wp) => {
  if (!wp) return wp;
  const out = { ...wp, title: decodeHtmlEntities(wp.title) };
  if (Array.isArray(wp.queue)) {
    out.queue = wp.queue.map((q) => (q && q.title ? { ...q, title: decodeHtmlEntities(q.title) } : q));
  }
  return out;
};

// 🗳️ Ambient mod poll card (mod polls v1, 07-13 · Submit flow 07-20 ·
// back to ONE-TAP 08-06, Nina) — the host's direction question, docked above
// the composer in non-comp spaces. A tap CASTS the vote instantly: re-votes
// are free until close, so a mis-tap costs one more tap, and the Submit
// button was a second step nobody wanted. The winner becomes a room event
// via a space_command card when the poll closes.
const SpacePollCard = ({ poll, onVote, voting, THEME, onCollapse, onDismiss }) => {
  const [, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick(t => t + 1), 1000);
    return () => clearInterval(id);
  }, []);
  if (!poll) return null;
  const msLeft = new Date(poll.closes_at).getTime() - Date.now();
  if (msLeft <= 0) return null; // expired locally — the close tick will confirm
  const secsLeft = Math.ceil(msLeft / 1000);
  const accent = THEME.accent || '#7b1fa2';
  const urgent = secsLeft <= 10; // final-countdown treatment
  const maxVotes = Math.max(1, ...poll.options.map(o => o.votes));
  return (
    <div style={{
      margin: '0 0 8px', padding: '10px 12px', borderRadius: 10,
      background: THEME.bgLight || 'rgba(20,16,30,0.92)',
      border: `1px solid ${urgent ? '#ff5f5f88' : `${accent}55`}`,
      boxShadow: urgent ? '0 4px 14px rgba(255,80,80,0.25)' : '0 4px 14px rgba(0,0,0,0.3)',
      transition: 'border-color 0.3s ease, box-shadow 0.3s ease',
    }}>
      {urgent && <style>{'@keyframes spacePollUrgent { 0%,100%{opacity:1;transform:scale(1)} 50%{opacity:0.5;transform:scale(1.15)} }'}</style>}
      {/* Native :active feedback — the tap reads INSTANTLY even when React's
          commit is stuck behind a heavy feed render (Nina 08-02: picks felt
          dead for a beat on both platforms). touch-action kills tap delay. */}
      <style>{'.space-poll-opt{touch-action:manipulation;-webkit-tap-highlight-color:transparent;transition:border-color 0.12s ease, transform 0.08s ease} .space-poll-opt:active{transform:scale(0.98);filter:brightness(1.35)}'}</style>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <span style={{ fontSize: '0.68em', fontWeight: 800, letterSpacing: '0.1em', color: accent }}>🗳️ THE ROOM DECIDES</span>
        <span style={{
          marginLeft: 'auto', fontSize: '0.68em', fontVariantNumeric: 'tabular-nums',
          color: urgent ? '#ff5f5f' : (THEME.textMuted || '#999'),
          fontWeight: urgent ? 800 : 400,
          animation: urgent ? 'spacePollUrgent 1s ease-in-out infinite' : 'none',
        }}>
          {secsLeft > 60 ? `${Math.floor(secsLeft / 60)}m ${secsLeft % 60}s` : `${secsLeft}s`} left
        </span>
        {/* ▾ tuck the poll away — it parks in the pinned dock on phones and
            had no way out (Nina, 07-17); collapses to a one-line chip */}
        {onCollapse && (
          <button
            onClick={onCollapse}
            aria-label="Minimize poll"
            style={{
              background: 'none', border: 'none', cursor: 'pointer', padding: '4px 6px',
              color: THEME.textMuted || '#999', fontSize: '0.8em', lineHeight: 1, flexShrink: 0,
            }}
          >▾</button>
        )}
        {/* ✕ dismiss THIS poll entirely (Nina 07-20) — the next one still shows */}
        {onDismiss && (
          <button
            onClick={onDismiss}
            aria-label="Dismiss poll"
            style={{
              background: 'none', border: 'none', cursor: 'pointer', padding: '4px 6px',
              color: THEME.textMuted || '#999', fontSize: '0.8em', lineHeight: 1, flexShrink: 0,
            }}
          >✕</button>
        )}
      </div>
      <div style={{ fontSize: '0.92em', fontWeight: 700, color: THEME.textColor || '#fff', marginBottom: 8 }}>{poll.question}</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {poll.options.map(o => {
          const mine = poll.my_vote === o.id; // my cast vote (optimistic — lands on tap)
          return (
            <button
              key={o.id}
              type="button"
              className="space-poll-opt"
              // ONE TAP = the vote (Nina 08-06). Guard: re-tapping your own
              // vote is a no-op, and taps during an in-flight vote drop.
              onClick={() => { if (onVote && !voting && !mine) onVote(poll.id, o.id); }}
              style={{
                position: 'relative', overflow: 'hidden', textAlign: 'left',
                display: 'flex', alignItems: 'center', gap: 8,
                padding: '7px 10px', borderRadius: 8, cursor: voting ? 'wait' : 'pointer',
                border: mine ? `2px solid ${accent}` : '1px solid rgba(255,255,255,0.15)',
                background: mine ? `${accent}2E` : 'rgba(255,255,255,0.05)',
                color: THEME.textColor || '#fff',
                fontSize: '0.85em', fontWeight: mine ? 700 : 500,
                opacity: voting && !mine ? 0.6 : 1,
              }}
            >
              {/* Live tally bar under the label — grows as votes land */}
              <span style={{
                position: 'absolute', inset: 0, width: `${(o.votes / maxVotes) * 100}%`,
                background: `${accent}22`, transition: 'width 0.4s ease', pointerEvents: 'none',
              }} />
              <span style={{ position: 'relative' }}>{o.emoji}</span>
              <span style={{ position: 'relative', flex: 1 }}>{o.label}</span>
              {/* ✓ = vote cast — optimistic, so the tap reads instantly */}
              {mine && <span style={{ position: 'relative', color: accent, fontWeight: 900 }}>✓</span>}
              <span style={{ position: 'relative', fontVariantNumeric: 'tabular-nums', opacity: 0.7 }}>{o.votes}</span>
            </button>
          );
        })}
      </div>
      <div style={{ marginTop: 6, fontSize: '0.68em', color: THEME.textMuted || '#999' }}>
        {voting ? 'Sending...'
          : poll.my_vote ? 'Vote cast! Tap another option to switch before the bell.'
          : 'One tap casts your vote!'}
      </div>
    </div>
  );
};

// ✨ One discovery sparkle per cut-scene card, ever (egg-confetti pattern:
// module-level dedupe survives remounts; only FRESH cards chime — history
// scrollback stays silent).
// 🎚️ HEM FX channel (POLL_ENACTMENT §6.1, 08-06): a host enactment may pin
// ONE one-shot sound to its card (data.fx.kind = SoundManager key). Same
// replay-safe rail as egg confetti: module-level fired-once Set + fresh-card
// age guard, so scrollback and reloads never replay. Sound goes through
// soundManager.play (master mute, rate limits, autoplay-unlock all apply);
// thunder adds a brief lightning flash unless the user prefers reduced motion.
const hemFxFired = new Set();
const fireHemFx = (kind) => {
  try { soundManager.play(kind); } catch (e) { /* sound is garnish */ }
  // 🕯️ lights_out (Nina 08-06): the room actually goes dark for a beat —
  // slow dim to near-black, hold, slow return. No motion, so no
  // reduced-motion gate; pointer-events stay live underneath.
  if (kind === 'lights_out') {
    try {
      const dark = document.createElement('div');
      dark.style.cssText = 'position:fixed;inset:0;background:#000;opacity:0;z-index:9998;pointer-events:none;transition:opacity 900ms ease;';
      document.body.appendChild(dark);
      requestAnimationFrame(() => { dark.style.opacity = '0.85'; });
      setTimeout(() => { dark.style.opacity = '0'; }, 5200);
      setTimeout(() => dark.remove(), 6400);
    } catch (e) { /* darkness is garnish */ }
    return;
  }
  if (kind !== 'thunder') return;
  try {
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const flash = document.createElement('div');
    flash.style.cssText = 'position:fixed;inset:0;background:#fff;opacity:0;z-index:9998;pointer-events:none;transition:opacity 80ms linear;';
    document.body.appendChild(flash);
    [[0, 0.8], [90, 0.1], [180, 0.5], [300, 0]].forEach(([t, o]) => {
      setTimeout(() => { flash.style.opacity = String(o); }, t);
    });
    setTimeout(() => flash.remove(), 700);
  } catch (e) { /* flash is garnish */ }
};

// 🎭 SCENE OPENING SWEEP (SCENE_SYSTEM_SPEC §6) — the one moment a scene is
// allowed to be loud. The persistent atmosphere has to stay subtle because the
// ambient canvas paints ON TOP of the chat text (see AmbientLayer's note), so
// "noticeable" is delivered here instead: a single full-viewport wash in the
// atmosphere's color that blooms and clears in ~1.8s.
//
// ⚠️ Appended to document.body, NOT rendered in the card. Virtualized rows set
// `transform: translateY(...)`, which makes them the containing block for
// position:fixed descendants — a sweep rendered inside SpaceCommandAction would
// be clipped to its own row. And it only breaks past the 30-action threshold,
// so a short dev feed would hide the bug completely. Same reason fireHemFx and
// fireEggConfetti already do this.
// ⚠️ Module-level fired-once Set + the caller's freshness guard: scrollback and
// reloads must never replay a scene's opening.
const sceneSweepFired = new Set();
const SCENE_SWEEP_TINTS = {
  smoke: '#9a9186', fire: '#ff7a2c', embers: '#ff8a3c', rain: '#7f9dc4', storm: '#4a6699',
  snow: '#e8f2ff', fog: '#c3c7cc', dust: '#c8b48c', bloom: '#ffc0d4', leaves: '#d68a3c',
  bubbles: '#8fd8e8', stars: '#c9d4ff', confetti: '#f2d65e', dark: '#000000',
  heat: '#ffb877', glitch: '#5ef2f2', fireworks: '#ffd07a',
};
const fireSceneSweep = (atmosphere) => {
  try {
    // Opacity-only, so it follows lights_out's precedent and stays ungated for
    // reduced-motion users — nothing moves, it just blooms and clears.
    const tint = SCENE_SWEEP_TINTS[atmosphere] || '#ffffff';
    const peak = atmosphere === 'dark' ? 0.82 : 0.34;
    const el = document.createElement('div');
    el.style.cssText = `position:fixed;inset:0;background:radial-gradient(120% 100% at 50% 100%, ${tint} 0%, rgba(0,0,0,0) 72%);opacity:0;z-index:9997;pointer-events:none;transition:opacity 700ms ease-out;`;
    document.body.appendChild(el);
    requestAnimationFrame(() => { el.style.opacity = String(peak); });
    setTimeout(() => { el.style.transition = 'opacity 1100ms ease-in'; el.style.opacity = '0'; }, 750);
    setTimeout(() => el.remove(), 2000);
  } catch (e) { /* the sweep is garnish */ }
};

const cutsceneSparkleFired = new Set();

// 📜 Collapse-long-cards (Nina 08-06): any card text past ~2 lines starts
// clamped with an expand toggle — the genesis front-door pages (and long
// house cards generally) were flooding the room. Heuristic, not measured:
// >140 chars or >2 explicit lines reads as "longer than 2 lines" at every
// width without a layout pass. Local state per card; the virtualizer
// re-measures the row on toggle like any other dynamic-height change.
const CollapsibleCardText = ({ text, style = {}, buttonColor = null }) => {
  const [open, setOpen] = useState(false);
  const s = String(text || '');
  const isLong = s.length > 140 || s.split('\n').length > 2;
  return (
    <>
      <div style={{
        ...style,
        ...(isLong && !open ? { display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' } : {}),
      }}>{s}</div>
      {isLong && (
        <button
          onClick={(e) => { e.stopPropagation(); setOpen((o) => !o); }}
          style={{
            background: 'none', border: 'none', cursor: 'pointer', padding: '3px 0 0',
            fontSize: '0.74em', fontWeight: 700, color: buttonColor || 'inherit', opacity: 0.75,
          }}
        >{open ? 'Show less ▴' : 'Show more ▾'}</button>
      )}
    </>
  );
};

// Directed-call receipt — v2 "mystery" restyle (Nina 09-01 pm, mock-approved):
// ONLY the chosen path renders. The unchosen options stay in data.directed
// (history keeps the full fork) but never show — the question already tells
// the viewer a fork happened; not seeing the other roads is the mystery.
// No target emoji (Nina's cut); cook keeps its meal emoji. The resident's
// portrait rides directed.avatar (stamped server-side; older rows fall back
// to an initial circle). maxWidth caps the card so it never stretches thin
// across the 900px desktop feed. chips-only mode sits under an announcement
// line that already carries the header.
// 🍽️ D14 (09-01 pm): every meal call rides kind:'cook' with a `meal` beside it.
const MEAL_EMOJI = { breakfast: '🍞', lunch: '🥪', brunch: '🥞', tea: '🫖', dinner: '🍳' };
const DirectedPortrait = ({ avatar, name, size = 24 }) => (
  <div style={{
    width: size, height: size, borderRadius: '50%', flexShrink: 0, overflow: 'hidden',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: size * 0.48, fontWeight: 800, color: '#fff',
    background: 'radial-gradient(circle at 32% 28%, #ffc864, rgba(40,30,14,0.95))',
    border: '1.5px solid rgba(255,255,255,0.5)',
  }}>
    {avatar
      ? <img src={avatar} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={(e) => { e.target.style.display = 'none'; }} />
      : (String(name || '?').trim().charAt(0).toUpperCase() || '?')}
  </div>
);
const DirectedCallCard = ({ directed, chipsOnly = false }) => {
  if (!directed || !Array.isArray(directed.options)) return null;
  const isCook = directed.kind === 'cook';
  const cookEmoji = MEAL_EMOJI[directed.meal] || '🍳';
  const chosenOpt = directed.options.find((o) => o.key === directed.chosen) || null;
  const chosenLabel = chosenOpt ? chosenOpt.label : 'went their own way';
  const chip = (
    <span style={{
      padding: '3px 11px', borderRadius: 999, fontSize: '0.78em', lineHeight: 1.3,
      fontWeight: 800, border: '1px solid rgba(255,200,100,0.9)',
      background: 'rgba(255,200,100,0.18)', color: '#fff',
    }}>✓ {chosenLabel}</span>
  );
  return (
    <div style={{ maxWidth: 560 }}>
      {!chipsOnly && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 5 }}>
          <DirectedPortrait avatar={directed.avatar} name={directed.player_name} />
          <span style={{ fontSize: '0.66em', fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'rgba(255,200,100,0.9)' }}>
            {isCook ? `${cookEmoji} ` : ''}{directed.player_name} chose their path
          </span>
        </div>
      )}
      {!chipsOnly && directed.q && (
        <div style={{ color: '#fff', fontSize: '0.92em', fontWeight: 600, lineHeight: 1.4, marginBottom: 7 }}>{directed.q}</div>
      )}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: chipsOnly ? 6 : 0 }}>
        {chip}
      </div>
    </div>
  );
};

const SharedContentAction = ({ action, users, THEME: roomTheme, humanPlayerId = null, localStatuses = null, placeChat = null }) => {
  // 🃏 wave 2b (leftover 1): on a place page the card BODY stands on the lane's
  // dark ground (PlaceChatRow.placeCardTheme); placeChat null = the room theme, untouched
  const THEME = placeChat ? placeCardTheme(roomTheme) : roomTheme;
  const data = action.data || {};
  const senderName = data.sender_name || 'Someone';
  // 💬 W2d.1 (A9): on a place page (placeChat is null everywhere else) a media
  // card's header is just the name ("you" for your own): no initials avatar,
  // no model id, no status, the clock on hover. The card body is unchanged.
  const placeWho = placeChat ? placeLineWho({ game_instance_player_id: action.game_instance_player_id || data.sender_id || null, data }, { humanPlayerId, residentSeatId: placeChat.residentSeatId, residentName: placeChat.residentName }) : null;
  const headName = placeWho ? (placeWho.who === 'me' ? 'you' : (placeWho.who === 'resident' ? placeWho.name : senderName)) : senderName;
  const rawUrl = data.url || '';
  const contentType = data.content_type || 'youtube';
  const isDrawing = contentType === 'drawing';
  const isYouTube = !isDrawing && (contentType === 'youtube' || rawUrl.includes('youtube.com') || rawUrl.includes('youtu.be'));
  const isImage = contentType === 'image' || contentType === 'image_url';
  const isVideo = contentType === 'video' || contentType === 'video_url';
  const isLink = contentType === 'link';
  const isPdf = contentType === 'pdf';
  const isNote = contentType === 'note'; // /note command — text pinned to the room
  // 🌬️ Luck-roll cards (Nina 08-23, twilight-omen ruling): the daily house
  // event ships as a note row with is_house_luck — same data, distinct dress.
  // DISPLAY-ONLY read of the flag; never gate behavior on it.
  const isHouseLuck = isNote && !!data.is_house_luck;
  // 🎶 House aux rotation/take-back cards keep the plain sticky-note look
  // (Nina 08-28 reverted the gold dress). is_house_aux still drives the WS
  // broadcast (instant arrival), the chime, and the Chat-tab dot/unread chip
  // — those are the "I can't see aux changes" fixes and stay; only the look
  // went back to the note.
  const isCutscene = !!data.is_cutscene; // 🎬 first-discovery comic strip (cut-scenes v1, 07-13)
  const isCode = contentType === 'code'; // ⌨️ /code playground (SLASH_CODE_DESIGN, 07-13)
  const isToy = contentType === 'toy';   // 🧸 /tinker toy (single sandboxed HTML doc)

  // ✨ Discovery "ooh!" (user ask 07-15): a fresh cut scene landing in the
  // room gets one sparkle chime. Old cards (scrollback, reload) stay silent.
  useEffect(() => {
    if (action.isOptimistic) return;
    const age = Date.now() - new Date(action.created_at || Date.now()).getTime();
    // 🎚️ HEM fx one-shot (content enactments: /note /listen /watch)
    if (data.fx?.kind && !hemFxFired.has(action.id)) {
      hemFxFired.add(action.id);
      if (age <= 15000) fireHemFx(data.fx.kind);
    }
    if (!isCutscene || cutsceneSparkleFired.has(action.id)) return;
    cutsceneSparkleFired.add(action.id);
    if (age > 15000) return; // old card — no chime
    try { soundManager.play('sparkle'); } catch (e) { /* sound is garnish */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [action.id]);

  // Chat-canvas doodles: strokes (vector source of truth) render as crisp SVG,
  // mirroring the canvas's quadratic-midpoint smoothing; the PNG url is the fallback.
  const drawingStrokes = isDrawing && Array.isArray(data.strokes) && data.strokes.length > 0 ? data.strokes : null;
  const strokeToPath = (pts) => {
    let d = `M ${pts[0][0]} ${pts[0][1]}`;
    for (let i = 1; i < pts.length - 1; i++) {
      const mx = (pts[i][0] + pts[i + 1][0]) / 2;
      const my = (pts[i][1] + pts[i + 1][1]) / 2;
      d += ` Q ${pts[i][0]} ${pts[i][1]} ${mx} ${my}`;
    }
    const last = pts[pts.length - 1];
    d += ` L ${last[0]} ${last[1]}`;
    return d;
  };

  // Convert any YouTube URL to embed format
  const toYouTubeEmbed = (rawUrl) => {
    const stripped = rawUrl.replace(/[?&]autoplay=1/, '');
    // Already an embed URL
    if (stripped.includes('youtube.com/embed/')) return stripped;
    // Shorts: youtube.com/shorts/VIDEO_ID
    const shortsMatch = stripped.match(/youtube\.com\/shorts\/([a-zA-Z0-9_-]+)/);
    if (shortsMatch) return `https://www.youtube.com/embed/${shortsMatch[1]}`;
    // Standard: youtube.com/watch?v=VIDEO_ID
    const watchMatch = stripped.match(/[?&]v=([a-zA-Z0-9_-]+)/);
    if (watchMatch) return `https://www.youtube.com/embed/${watchMatch[1]}`;
    // Short URL: youtu.be/VIDEO_ID
    const shortMatch = stripped.match(/youtu\.be\/([a-zA-Z0-9_-]+)/);
    if (shortMatch) return `https://www.youtube.com/embed/${shortMatch[1]}`;
    return stripped;
  };
  const url = isYouTube ? toYouTubeEmbed(rawUrl) : rawUrl.replace(/[?&]autoplay=1/, '');

  const [expanded, setExpanded] = useState(false);
  // Doodle replay: nonce keys the <svg> so every tap remounts it and the
  // stroke-draw animation runs from the top
  const [replayNonce, setReplayNonce] = useState(0);
  const isChainLink = isDrawing && (data.is_doodle_back || data.draws_on);

  const labelMap = {
    youtube: 'a video',
    image: 'an image',
    image_url: 'an image',
    video: 'a video',
    video_url: 'a video',
    link: 'a link',
    pdf: 'a document',
    document: 'a document',
    file: 'a file'
  };

  // Chat-native bubble for plain images + reaction GIFs (Nina 08-03, mock
  // artifact 1202899f variant C): media reads as a message — avatar/name/time
  // header, image as the bubble body. Cutscenes, doodles, notes, code, and
  // toys keep their special card frames below.
  if (isImage && !isCutscene) {
    const senderUser = (users || []).find(u => u.id === data.sender_id)
      || (users || []).find(u => u.name === senderName) || null;
    const nameColor = senderUser ? getModelColor(senderUser.model, THEME) : (THEME.accent || '#66bbff');
    const bubbleIsMobile = window.innerWidth < 768;
    return (
      <div style={{ display: 'flex', justifyContent: 'flex-start', marginBottom: '8px' }}>
        <div
          /* 🪟 W1 poke target (verify-arc fix 08-28): media, notes and
             doodles are the resident-made artifacts the spec's tap affordance
             is FOR — the attrs are inert outside ported spaces (the delegated
             listener only arms there). */
          data-poke-card={action.id}
          data-poke-label={String(data.description || data.title || senderName + "'s " + (contentType || 'share')).slice(0, 60)}
          style={{
          borderRadius: '18px 18px 18px 4px',
          backgroundColor: THEME.bgLight || '#2a2a3a',
          border: `1px solid ${THEME.accent}18`,
          borderLeft: `3px solid ${THEME.accent}50`,
          boxShadow: `0 1px 3px rgba(0,0,0,0.15), 0 0 6px ${THEME.accent}08`,
          maxWidth: bubbleIsMobile ? '90%' : '75%',
          overflow: 'hidden',
        }}>
          <div style={{ padding: '8px 14px 4px', display: 'flex', alignItems: 'center', gap: '8px' }} title={placeWho ? formatActionTime(data.timestamp || action.created_at) : undefined}>
            {placeWho ? null : senderUser ? (
              <ProfileImage user={senderUser} size={28} borderColor={nameColor} />
            ) : (
              <span style={{
                width: 28, height: 28, borderRadius: '50%', flexShrink: 0,
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '0.9em', backgroundColor: `${THEME.accent}22`,
                border: `1px solid ${nameColor}`,
              }}>{data.moderator_emoji || '🖼️'}</span>
            )}
            <span data-place-media-by={placeWho ? placeWho.who : undefined} style={{ fontWeight: 800, color: placeWho ? (placeWho.who === 'me' ? '#F0AC55' : placeWho.who === 'resident' ? '#E39BAB' : 'rgba(255,238,216,0.72)') : nameColor, fontSize: '0.88em' }}>
              {headName}
              {!placeWho && senderUser && getDisplayModelName(senderUser) && (
                <span style={{ fontWeight: 500, opacity: 0.7, fontSize: '0.85em' }}> ({getDisplayModelName(senderUser)})</span>
              )}
              {/* 🌙☀️ Status on GIF/image cards too (Nina 08-08: "ALL types of
                  messages, gifs and replies not just regular"). Nested in the
                  name span rather than the flex row so it stacks UNDER the name
                  instead of becoming a third column beside the timestamp. */}
              {!placeWho && (
              <LocalStatusLine
                statuses={localStatuses}
                id={action.game_instance_player_id}
                altId={senderUser?.id}
                isHuman={isRealHuman(senderUser)}
              />
              )}
            </span>
            {!placeWho && (
            <span style={{ color: THEME.textMuted, fontSize: '0.78em', flexShrink: 0 }}>
              {formatActionTime(data.timestamp || action.created_at)}
            </span>
            )}
          </div>
          <div style={{ padding: '4px 10px 10px' }}>
            <MoonOver moon={data.moon || null} meteors={data.meteors || null /* 🌠 10-08 the meteors on a meteor-night snap */}>
            <img
              src={url}
              alt="Shared image"
              onClick={() => setExpanded(!expanded)}
              className={data.is_reaction_gif && !expanded ? 'reaction-gif-compact' : undefined}
              style={{
                display: 'block',
                maxWidth: '100%',
                // 07-25 desktop cap carries over: tall images stop near 55dvh;
                // tap still expands.
                maxHeight: expanded ? 'none' : 'clamp(300px, 55dvh, 640px)',
                borderRadius: '10px',
                cursor: 'pointer',
              }}
            />
            </MoonOver>
            {data.is_reaction_gif && !expanded && (
              <style>{`
                /* Compact reaction GIFs, DESKTOP ONLY (Nina 08-02): sticker-
                   sized so they can't cover the chat column; tap expands.
                   Phones keep the full-size presentation (validated on
                   Nina's iPhone mini). Media query, not isMobile, so a
                   resized window behaves. */
                @media (min-width: 768px) {
                  .reaction-gif-compact {
                    max-width: min(240px, 70%) !important;
                    max-height: 180px !important;
                  }
                }
              `}</style>
            )}
            {!data.is_reaction_gif && data.description && (
              <div style={{ marginTop: 6, fontSize: '0.78em', fontStyle: 'italic', color: THEME.textMuted }}>
                {data.description}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // 🌬️ House-luck omen = a quiet ambient line, not a card (Nina 08-28,
  // superseding her 08-23 purple pill — option A of the restyle set). No
  // wrapper, no header row, no glow: rhymes with the clue_found ambient
  // treatment. Copy is ONE backend-clamped sentence (≤200 chars, name-leak
  // filtered), so no collapse affordance; mechanics stay backstage (no dice,
  // no name, no "roll"). is_house_luck stays a DISPLAY-ONLY read.
  if (isHouseLuck && data.description) {
    return (
      <div style={{ textAlign: 'center', padding: '6px 14px', margin: '4px 0' }}>
        <span style={{
          color: 'rgba(185,168,255,0.8)', fontSize: '0.82em', fontStyle: 'italic',
          letterSpacing: '0.03em', wordBreak: 'break-word',
        }}>
          🌬️ {data.description}
        </span>
      </div>
    );
  }

  return (
    <div style={{
      backgroundColor: THEME.bgLight,
      borderRadius: '8px',
      overflow: 'hidden',
      border: `1px solid ${THEME.borderLight}`,
      marginBottom: '8px',
    }}>
      {/* 🗳️ Vote receipt (HEM): enacted /note and /listen rows carry the poll
          context — same quiet gold language as the command cards. */}
      {data.via_poll && data.via_poll.label && (
        <div style={{
          padding: '7px 12px 0', fontSize: '0.66em', fontWeight: 700,
          color: 'rgba(255,200,100,0.7)', display: 'flex', alignItems: 'center', gap: 5,
        }}>
          <span>🗳️</span>
          <span>Room vote: {data.via_poll.emoji} {data.via_poll.label}{data.via_poll.votes ? ` · ${data.via_poll.votes} vote${data.via_poll.votes === 1 ? '' : 's'}` : ''}</span>
        </div>
      )}
      {/* 💍/💔 Tea ship-verdict receipt (HEM §3b) on the verdict comic */}
      {data.via_ship_vote && (
        <div style={{
          padding: '7px 12px 0', fontSize: '0.66em', fontWeight: 700,
          color: 'rgba(255,200,100,0.7)', display: 'flex', alignItems: 'center', gap: 5,
        }}>
          <span>🗳️</span>
          <span>Room verdict: {data.via_ship_vote.verdict === 'last' ? '💍' : '💔'} {Math.max(data.via_ship_vote.last || 0, data.via_ship_vote.doom || 0)}-{Math.min(data.via_ship_vote.last || 0, data.via_ship_vote.doom || 0)}</span>
        </div>
      )}
      <div style={{
        padding: '8px 12px',
        fontSize: '0.8em',
        color: isDrawing ? (THEME.accent || '#7b1fa2') : isCutscene ? '#ffcf7a' : (THEME.textMuted || 'rgba(255,255,255,0.5)'),
        fontWeight: (isDrawing || isCutscene) ? 700 : 400,
        display: 'flex', alignItems: 'center', gap: 8,
      }} title={placeWho ? formatActionTime(data.timestamp || action.created_at) : undefined}>
        <span style={{ flex: 1, minWidth: 0 }} data-place-media-by={placeWho ? placeWho.who : undefined}>
          {isDrawing ? (data.is_doodle_back ? `🖍️ ${headName} drew back!`
              : data.draws_on ? `🖍️ ${headName} added to the doodle!`
              : `✏️ ${headName} drew something!`)
            : isNote ? `📌 ${headName} pinned a note`
            : isCutscene ? (placeWho
              ? (data.is_arrival_comic ? (placeChat.residentName ? `🎞️ the day ${placeChat.residentName} got here` : '🎞️ the first day') : '🎞️ a comic strip')
              : '🎬 CUT SCENE!')
            : isCode ? `⌨️ ${headName} opened a code sandbox!`
            : isToy ? `🧸 ${headName} tinkered up a toy!`
            : `${headName} shared ${labelMap[contentType] || 'content'}`}
        </span>
        {/* Media cards keep the same clock as regular messages (Nina 08-02) */}
        {!placeWho && (
        <span style={{ fontSize: '0.95em', fontWeight: 400, color: THEME.textMuted || 'rgba(255,255,255,0.5)', flexShrink: 0 }}>
          {formatActionTime(data.timestamp || action.created_at)}
        </span>
        )}
        {isDrawing && drawingStrokes && (
          <>
            <button
              onClick={() => setReplayNonce(n => n + 1)}
              title="Replay the strokes"
              aria-label="Replay the strokes"
              style={{
                background: 'transparent', border: `1px solid ${THEME.accent || '#7b1fa2'}55`,
                color: THEME.accent || '#7b1fa2', borderRadius: 999, padding: '2px 8px',
                cursor: 'pointer', fontSize: '0.85em', fontWeight: 700, flexShrink: 0,
              }}
            >▶</button>
            <button
              onClick={() => window.dispatchEvent(new CustomEvent('doodle:drawon', { detail: { actionId: action.id, strokes: drawingStrokes } }))}
              title="Open the canvas and add to this doodle"
              style={{
                background: THEME.accent || '#7b1fa2', border: 'none', color: '#fff',
                borderRadius: 999, padding: '3px 10px', cursor: 'pointer',
                fontSize: '0.85em', fontWeight: 700, flexShrink: 0,
              }}
            >
              🖍️ Draw on this
            </button>
          </>
        )}
        {isYouTube && (
          <button
            onClick={() => window.dispatchEvent(new CustomEvent('watchparty:request', { detail: { url: rawUrl } }))}
            title="Watch this together with the room"
            style={{
              background: THEME.accent || '#7b1fa2', border: 'none', color: '#fff',
              borderRadius: 999, padding: '3px 10px', cursor: 'pointer',
              fontSize: '0.85em', fontWeight: 700, flexShrink: 0,
            }}
          >
            🍿 Watch together
          </button>
        )}
      </div>
      {isDrawing && (
        <div style={{ padding: '2px 12px 8px' }}>
          <div
            onClick={() => setExpanded(!expanded)}
            style={{
              backgroundColor: '#fff', borderRadius: '6px', overflow: 'hidden',
              transform: 'rotate(-0.5deg)', cursor: 'pointer',
              border: '1px solid rgba(0,0,0,0.15)',
              boxShadow: '0 4px 14px rgba(0,0,0,0.25)',
              maxWidth: expanded ? 'none' : 420,
            }}
            title="Tap to zoom"
          >
            {drawingStrokes ? (
              // key={replayNonce}: remounting restarts the stroke-draw animation
              <svg key={replayNonce} viewBox="0 0 640 480" style={{ display: 'block', width: '100%', height: 'auto' }}>
                {replayNonce > 0 && (
                  <style>{'@keyframes doodleReplayDraw { to { stroke-dashoffset: 0; } } @keyframes doodleReplayDot { to { opacity: 1; } }'}</style>
                )}
                <rect x="0" y="0" width="640" height="480" fill="#ffffff" />
                {drawingStrokes.map((s, i) => {
                  const pts = Array.isArray(s.points) ? s.points.filter(p => Array.isArray(p) && p.length === 2) : [];
                  if (pts.length === 0) return null;
                  // Replay pacing: whole drawing plays in ≤6s no matter the stroke count
                  const step = Math.min(0.18, 6 / drawingStrokes.length);
                  const delay = (i * step).toFixed(2);
                  if (pts.length === 1) {
                    return <circle key={i} cx={pts[0][0]} cy={pts[0][1]} r={(s.size || 7) / 2} fill={s.color || '#1d1d1f'}
                      style={replayNonce > 0 ? { opacity: 0, animation: `doodleReplayDot 0.3s ease ${delay}s both` } : undefined} />;
                  }
                  return (
                    <path key={i} d={strokeToPath(pts)} fill="none"
                      stroke={s.color || '#1d1d1f'} strokeWidth={s.size || 7}
                      strokeLinecap="round" strokeLinejoin="round"
                      pathLength={replayNonce > 0 ? 1 : undefined}
                      style={replayNonce > 0
                        ? { strokeDasharray: 1, strokeDashoffset: 1, animation: `doodleReplayDraw 0.5s ease ${delay}s both` }
                        : undefined} />
                  );
                })}
              </svg>
            ) : (
              <img src={url} alt="Drawing" style={{ display: 'block', width: '100%', height: 'auto' }} />
            )}
          </div>
          {/* The artist's one-liner about what they added (doodle-back replies) */}
          {isChainLink && data.message && (
            <div style={{ marginTop: 6, fontSize: '0.78em', fontStyle: 'italic', color: THEME.textMuted }}>
              “{data.message}” — {senderName}
            </div>
          )}
          {/* 🖍️ 10-02 draw: an outside AI's own drawing (POST /api/draw) shows its caption; the header names it */}
          {!isChainLink && data.agent_drawing && data.message && (
            <div data-agent-drawing-caption="" style={{ marginTop: 6, fontSize: '0.78em', fontStyle: 'italic', color: THEME.textMuted }}>
              “{data.message}”
            </div>
          )}
        </div>
      )}
      {isYouTube && (
        <iframe
          src={url}
          title="Shared video"
          style={{ width: '100%', height: 280, border: 'none' }}
          allow="encrypted-media"
          allowFullScreen
          frameBorder="0"
        />
      )}
      {isCode && (
        <CodePlaygroundCard action={action} playerId={humanPlayerId} THEME={THEME}
          // (B4, wave 2) on a place page the page's own owner truth (placeChat.viewerIsOwner = !!isPortedOwner);
          // anywhere else undefined, and the card derives it from the device's known-ported marker
          isOwner={placeChat ? placeChat.viewerIsOwner : undefined} />
      )}
      {isToy && data.toy_html && (
        <div style={{ padding: '4px 12px 10px' }}>
          {/* Click-to-boot: idle toys cost nothing; the doc runs in the same
              opaque-origin sandbox as /code (no cookies, no credentialed API) */}
          {expanded ? (
            <iframe
              title="Toy"
              sandbox="allow-scripts"
              srcDoc={data.toy_html}
              style={{ display: 'block', width: '100%', height: 300, border: `1px solid ${THEME.accent || '#7b1fa2'}44`, borderRadius: 8, background: '#fff' }}
            />
          ) : (
            <div
              onClick={() => setExpanded(true)}
              style={{
                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                gap: 8, height: 120, borderRadius: 8, cursor: 'pointer',
                background: `linear-gradient(135deg, ${THEME.accent || '#7b1fa2'}22, rgba(0,0,0,0.25))`,
                border: `1px dashed ${THEME.accent || '#7b1fa2'}66`,
              }}
            >
              <span style={{ fontSize: '2em' }}>🧸</span>
              <span style={{ fontSize: '0.8em', fontWeight: 700, color: THEME.textColor || '#fff' }}>Tap to play!</span>
            </div>
          )}
          {data.description && (
            <div style={{ marginTop: 6, fontSize: '0.74em', fontStyle: 'italic', color: THEME.textMuted || '#999' }}>
              {data.description}
            </div>
          )}
        </div>
      )}
      {isImage && (
        /* Only cutscenes reach this branch — plain images + reaction GIFs
           return early above as chat-native bubbles (Nina 08-03). */
        <div style={{ padding: '4px 12px 8px' }}>
          <div style={{
            background: '#0b0e1a', borderRadius: 6, padding: '12px 0 10px',
            border: '1px solid rgba(255, 207, 122, 0.35)',
            boxShadow: '0 6px 18px rgba(0,0,0,0.45)',
          }}>
            <img
              src={url}
              alt="Cut scene"
              onClick={() => setExpanded(!expanded)}
              style={{
                display: 'block',
                width: '100%',
                // Viewport-relative cap (Nina 07-25: desktop was wasting the
                // column on a 300px thumb) — phones stay near the old size,
                // desktop gets the near-full-height panel. Tap still expands.
                maxHeight: expanded ? 'none' : 'clamp(300px, 55dvh, 640px)',
                objectFit: 'contain',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            />
            {data.description && (
              <div style={{ padding: '8px 14px 2px', textAlign: 'center', fontSize: '0.78em', fontStyle: 'italic', color: '#ffcf7a' }}>
                {data.description}
              </div>
            )}
          </div>
        </div>
      )}
      {isVideo && (
        <div style={{ padding: '4px 12px 8px' }}>
          <video
            src={url}
            controls
            style={{ width: '100%', maxHeight: 300, borderRadius: '4px' }}
          />
        </div>
      )}
      {isLink && (
        <div style={{ padding: '4px 12px 8px' }}>
          <a href={url} target="_blank" rel="noopener noreferrer" style={{
            color: THEME.accent || '#7b1fa2',
            wordBreak: 'break-all',
            fontSize: '0.85em'
          }}>
            {url}
          </a>
        </div>
      )}
      {(isPdf || contentType === 'document' || contentType === 'file') && (
        <div style={{ padding: '4px 12px 8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ fontSize: '1.2em' }}>{(isPdf || contentType === 'document') ? '\u{1F4C4}' : '\u{1F4CE}'}</span>
          <a href={url} target="_blank" rel="noopener noreferrer" style={{
            color: THEME.accent || '#7b1fa2',
            fontSize: '0.85em'
          }}>
            {data.filename || 'Download file'}
          </a>
        </div>
      )}
      {/* 🌬️ house-luck rows never reach here — they early-return as the
          ambient line above (08-28); the !isHouseLuck guard below stays as
          the explicit "luck never wears the sticky" invariant. */}
      {isNote && !isHouseLuck && data.description && (
        <div style={{ padding: '2px 12px 10px' }}>
          <div style={{
            backgroundColor: '#fff8b8', textShadow: 'none',
            color: '#3d3a1f',
            borderRadius: '2px',
            padding: '10px 12px',
            fontSize: '0.9em',
            lineHeight: 1.45,
            transform: 'rotate(-0.6deg)',
            boxShadow: '0 4px 10px rgba(0,0,0,0.25)',
            fontFamily: '"Comic Sans MS", "Marker Felt", cursive, sans-serif',
          }}>
            <CollapsibleCardText
              text={data.description}
              style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
              buttonColor="#7a6a1f"
            />
          </div>
        </div>
      )}
      {/* 🌠 09-26 the shooting star's own Go: the roof picture with the wish box
          open, while the catch window is (RoofNight.StarWishGo reads eventGoBus) */}
      {isNote && data.is_shooting_star && !action.isOptimistic && <StarWishGo createdAt={action.created_at} bus={eventGoBus} />}
      {/* Cutscenes excluded (spec §3e): their caption already renders in gold
          inside the letterbox frame — this generic line double-printed it. */}
      {!isNote && !isCutscene && data.description && (
        <div style={{
          padding: '4px 12px 8px',
          fontSize: '0.78em',
          color: THEME.textMuted || 'rgba(255,255,255,0.4)',
          fontStyle: 'italic'
        }}>
          <CollapsibleCardText text={data.description} />
        </div>
      )}
      {!data.description && (isImage || isLink || isDrawing) && (
        <div style={{
          padding: '4px 12px 8px',
          fontSize: '0.75em',
          color: THEME.textMuted || 'rgba(255,255,255,0.3)',
        }}>
          {isDrawing ? '👀 The locals are squinting at it…' : 'Analyzing...'}
        </div>
      )}
    </div>
  );
};

const MessageAction = ({ action, playerModelMap, users, THEME, onActionUpdate, gameType, onReactionSubmit, humanPlayerId, effectivePlayerId, showThoughts, imposterMode, competitiveMode, humanTokensLeft, onOpenCastCard = null, houseNicknames = null, localStatuses = null, placeLine = null }) => {
  const COMMON_STYLES = getCommonStyles(THEME);
  // console.log('MessageAction render:', { action, users }); // DISABLED: High-frequency log
  const isMobile = window.innerWidth < 768;
  const { data } = action;
  const isPrivate = action.type === 'private_message' || action.type === 'secret_message';
  const isSecret = action.type === 'secret_message';
  const isHouseAction = action.type === 'house_action';
  const isSpectating = action.type === 'spectating_message';
  const isSubConversation = data.sub_conversation_message === true || !!data.parent_action_id;
  const recipients = data.recipient_names || []
  const targetPlayers = data.target_players || [];
  const isOwnAction = humanPlayerId && action.game_instance_player_id === humanPlayerId;
  const remaining_tokens = data.tokens_left !== undefined ? data.tokens_left : '?';

  // Video generation state for three video types - split into veo3 and voiceover
  const [generatingVideos, setGeneratingVideos] = React.useState({
    external: false,
    diary_room: false,
    thoughts: false
  });

  // Veo3 video state - now supports arrays for multi-part videos
  const [veo3Videos, setVeo3Videos] = React.useState(() => {
    // Helper to normalize video data (backward compatibility)
    const normalizeVideos = (videoData) => {
      if (!videoData) return [];

      // New format: array of video objects
      if (Array.isArray(videoData)) {
        return videoData.map(v => ({
          url: v.url,
          preview_url: v.preview_url,
          dialogue_portion: v.dialogue_portion,
          index: v.index,
          total_parts: v.total_parts,
          duration: v.duration
        }));
      }

      // Old format: single object with video_url
      if (videoData.video_url) {
        return [{
          url: videoData.video_url,
          preview_url: videoData.preview_url,
          index: 0,
          total_parts: 1,
          duration: videoData.duration
        }];
      }

      return [];
    };

    return {
      external: normalizeVideos(data.videos?.external?.veo3),
      diary_room: normalizeVideos(data.videos?.diary_room?.veo3),
      thoughts: normalizeVideos(data.videos?.thoughts?.veo3)
    };
  });
  const [processingVideos, setProcessingVideos] = React.useState({
    external: false,
    diary_room: false,
    thoughts: false
  });
  const [processedVeo3VideoUrls, setProcessedVeo3VideoUrls] = React.useState({
    external: data.videos?.external?.veo3?.processed_video_url || null,
    diary_room: data.videos?.diary_room?.veo3?.processed_video_url || null,
    thoughts: data.videos?.thoughts?.veo3?.processed_video_url || null
  });
  const [processedVoiceoverVideoUrls, setProcessedVoiceoverVideoUrls] = React.useState({
    external: data.videos?.external?.voiceover?.processed_video_url || null,
    diary_room: data.videos?.diary_room?.voiceover?.processed_video_url || null,
    thoughts: data.videos?.thoughts?.voiceover?.processed_video_url || null
  });

  // Voiceover video state
  const [voiceoverVideoUrls, setVoiceoverVideoUrls] = React.useState({
    external: data.videos?.external?.voiceover?.video_url || null,
    diary_room: data.videos?.diary_room?.voiceover?.video_url || null,
    thoughts: data.videos?.thoughts?.voiceover?.video_url || null
  });

  // Text pop-up state - one per dialogue type (external, diary_room, thoughts)
  const initTextPopup = (videoType) => ({
    title: data.textPopup?.[videoType]?.title || '',
    body: data.textPopup?.[videoType]?.body || '',
    x: data.textPopup?.[videoType]?.x ?? 30,
    y: data.textPopup?.[videoType]?.y ?? 30,
    startTime: data.textPopup?.[videoType]?.startTime ?? 0,
    endTime: data.textPopup?.[videoType]?.endTime ?? 0
  });

  const [textPopups, setTextPopups] = React.useState({
    external: initTextPopup('external'),
    diary_room: initTextPopup('diary_room'),
    thoughts: initTextPopup('thoughts')
  });
  const [savingTextPopup, setSavingTextPopup] = React.useState({});
  const [draggingPopup, setDraggingPopup] = React.useState({});
  const [showPopupPreview, setShowPopupPreview] = React.useState({});

  // Ref to store cleanup functions for video polling
  const pollingCleanupsRef = React.useRef({});

  // Cleanup all polling intervals on component unmount
  React.useEffect(() => {
    return () => {
      console.log('[MessageAction] Cleaning up all video polling on unmount');
      Object.values(pollingCleanupsRef.current).forEach(cleanup => cleanup());
      pollingCleanupsRef.current = {};
    };
  }, []);

  // Update video URLs, audio URLs, and scene images when action data changes (e.g., after page refresh or generation)
  React.useEffect(() => {
    // DISABLED: High-frequency log that runs for every action
    // console.log('[MessageAction useEffect] Updating state from data:', { ... });

    // Update veo3 videos (array format with backward compatibility)
    const normalizeVideos = (videoData) => {
      if (!videoData) return [];
      if (Array.isArray(videoData)) {
        return videoData.map(v => ({
          url: v.url,
          preview_url: v.preview_url,
          dialogue_portion: v.dialogue_portion,
          index: v.index,
          total_parts: v.total_parts,
          duration: v.duration
        }));
      }
      if (videoData.video_url) {
        return [{
          url: videoData.video_url,
          preview_url: videoData.preview_url,
          index: 0,
          total_parts: 1,
          duration: videoData.duration
        }];
      }
      return [];
    };

    setVeo3Videos({
      external: normalizeVideos(data.videos?.external?.veo3),
      diary_room: normalizeVideos(data.videos?.diary_room?.veo3),
      thoughts: normalizeVideos(data.videos?.thoughts?.veo3)
    });
    setProcessedVeo3VideoUrls({
      external: data.videos?.external?.veo3?.processed_video_url || null,
      diary_room: data.videos?.diary_room?.veo3?.processed_video_url || null,
      thoughts: data.videos?.thoughts?.veo3?.processed_video_url || null
    });
    setStitchedVideoUrls({
      external: data.videos?.external?.veo3?.stitched_video_url || null,
      diary_room: data.videos?.diary_room?.veo3?.stitched_video_url || null,
      thoughts: data.videos?.thoughts?.veo3?.stitched_video_url || null
    });
    setRenderedVideoUrls({
      external: data.rendered_videos?.external?.url || null,
      diary_room: data.rendered_videos?.diary_room?.url || null,
      thoughts: data.rendered_videos?.thoughts?.url || null
    });
    setExtensionCounts({
      external: data.videos?.external?.veo3?.extension_count || 0,
      diary_room: data.videos?.diary_room?.veo3?.extension_count || 0,
      thoughts: data.videos?.thoughts?.veo3?.extension_count || 0
    });

    // Update voiceover video URLs
    const newVoiceoverUrls = {
      external: data.videos?.external?.voiceover?.video_url || null,
      diary_room: data.videos?.diary_room?.voiceover?.video_url || null,
      thoughts: data.videos?.thoughts?.voiceover?.video_url || null
    };
    // console.log('[MessageAction useEffect] Setting voiceover video URLs:', newVoiceoverUrls); // DISABLED
    setVoiceoverVideoUrls(newVoiceoverUrls);

    const newProcessedVoiceoverUrls = {
      external: data.videos?.external?.voiceover?.processed_video_url || null,
      diary_room: data.videos?.diary_room?.voiceover?.processed_video_url || null,
      thoughts: data.videos?.thoughts?.voiceover?.processed_video_url || null
    };
    // console.log('[MessageAction useEffect] Setting processed voiceover video URLs:', newProcessedVoiceoverUrls); // DISABLED
    setProcessedVoiceoverVideoUrls(newProcessedVoiceoverUrls);
    setAudioUrls({
      external: data.audio?.external?.audio_url || null,
      diary_room: data.audio?.diary_room?.audio_url || null,
      thoughts: data.audio?.thoughts?.audio_url || null
    });
    setSceneImageUrls({
      external: data.external_scene_image_url || null,
      diary_room: data.diary_room_scene_image_url || null,
      thoughts: data.thoughts_scene_image_url || null
    });
  }, [data.videos, data.audio, data.external_scene_image_url, data.diary_room_scene_image_url, data.thoughts_scene_image_url, data.external_scene_image_base_url, data.diary_room_scene_image_base_url, data.thoughts_scene_image_base_url, action.id]);
  const [previewingVideos, setPreviewingVideos] = React.useState({
    external: false,
    diary_room: false,
    thoughts: false
  });
  const [generatingSceneImages, setGeneratingSceneImages] = React.useState({
    external: false,
    diary_room: false,
    thoughts: false
  });
  const [generatingBoundingBoxes, setGeneratingBoundingBoxes] = React.useState({
    external: false,
    diary_room: false,
    thoughts: false
  });
  const [sceneImageUrls, setSceneImageUrls] = React.useState({
    external: data.external_scene_image_url || null,
    diary_room: data.diary_room_scene_image_url || null,
    thoughts: data.thoughts_scene_image_url || null
  });
  const [extendingVideos, setExtendingVideos] = React.useState({
    external: false,
    diary_room: false,
    thoughts: false
  });
  const [stitchedVideoUrls, setStitchedVideoUrls] = React.useState({
    external: data.videos?.external?.stitched_video_url || null,
    diary_room: data.videos?.diary_room?.stitched_video_url || null,
    thoughts: data.videos?.thoughts?.stitched_video_url || null
  });
  const [renderedVideoUrls, setRenderedVideoUrls] = React.useState({
    external: data.rendered_videos?.external?.url || null,
    diary_room: data.rendered_videos?.diary_room?.url || null,
    thoughts: data.rendered_videos?.thoughts?.url || null
  });
  const [extensionCounts, setExtensionCounts] = React.useState({
    external: data.videos?.external?.extension_count || 0,
    diary_room: data.videos?.diary_room?.extension_count || 0,
    thoughts: data.videos?.thoughts?.extension_count || 0
  });
  const [generatingTTS, setGeneratingTTS] = React.useState({
    external: false,
    diary_room: false,
    thoughts: false
  });
  const [audioUrls, setAudioUrls] = React.useState({
    external: data.audio?.external?.audio_url || null,
    diary_room: data.audio?.diary_room?.audio_url || null,
    thoughts: data.audio?.thoughts?.audio_url || null
  });

  // Debug: Log scene image URLs on component mount
  React.useEffect(() => {
    console.log('[MessageAction] Scene Image URLs from data:', {
      external: data.external_scene_image_url,
      external_base: data.external_scene_image_base_url,
      diary_room: data.diary_room_scene_image_url,
      diary_room_base: data.diary_room_scene_image_base_url,
      thoughts: data.thoughts_scene_image_url,
      thoughts_base: data.thoughts_scene_image_base_url,
      hasData: !!data,
      dataKeys: data ? Object.keys(data) : []
    });
  }, []);
  const [previewData, setPreviewData] = React.useState(null);
  const [showPreviewModal, setShowPreviewModal] = React.useState(false);

  // Get user info from game_instance_player_id
  const user = getUserById(users, action.game_instance_player_id);
  const isOwnMessage = humanPlayerId && action.game_instance_player_id === humanPlayerId;
  // 🌿 10-09 (Nina): YOUR bubble's colors. The House's sage set (THEME.ownBubble), else the
  // exact accent look every other space had before (utils/houseOwnBubble).
  const ownCol = isOwnMessage ? ownBubbleColors(THEME) : null;

  // Video generation functions
  const generateDialogueVideo = async (videoType) => {
    setGeneratingVideos(prev => ({ ...prev, [videoType]: true }));

    try {
      // Use async job API for long-running video generation (Veo3)
      let result = await asyncJobApi.submitAndWait(
        'generateDialogueVideo',
        {
          actionId: action.id,
          sceneType: videoType
        },
        {
          onProgress: (progress, message) => {
            console.log(`[Video] ${progress}%: ${message}`);
          }
        }
      );
      console.log(`[Video Hot-Load] Received response for ${videoType}:`, result);

      // Handle Lambda response format (statusCode + body)
      if (result.body && typeof result.body === 'string') {
        console.log(`[Video Hot-Load] Unwrapping Lambda response body`);
        result = JSON.parse(result.body);
        console.log(`[Video Hot-Load] Parsed body:`, result);
      }

      if (result.success) {
        // Handle both single video (old format) and array (new format)
        let videos = [];
        if (result.videos && Array.isArray(result.videos)) {
          // New multi-video format
          videos = result.videos.map(v => ({
            url: `${v.url}?t=${Date.now()}`,
            dialogue_portion: v.dialogue_portion,
            index: v.index,
            total_parts: v.total_parts,
            duration: v.duration
          }));
          console.log(`[Veo3 Video Hot-Load] Received ${videos.length} videos for ${videoType}`);
        } else if (result.video_url) {
          // Old single video format
          videos = [{
            url: `${result.video_url}?t=${Date.now()}`,
            index: 0,
            total_parts: 1,
            duration: result.duration
          }];
          console.log(`[Veo3 Video Hot-Load] Received single video for ${videoType}`);
        }

        if (videos.length > 0) {
          setVeo3Videos(prev => {
            const newState = { ...prev, [videoType]: videos };
            console.log(`[Veo3 Video Hot-Load] New veo3Videos state:`, newState);
            return newState;
          });

          // Clear processed and stitched veo3 videos to show the new base videos immediately
          setProcessedVeo3VideoUrls(prev => ({ ...prev, [videoType]: null }));
          setStitchedVideoUrls(prev => ({ ...prev, [videoType]: null }));

          // Update parent state to persist the change
          if (onActionUpdate) {
            onActionUpdate(action.id, {
              videos: {
                ...data.videos,
                [videoType]: {
                  ...data.videos?.[videoType],
                  veo3: result.videos || {
                    video_url: result.video_url,
                    duration: result.duration,
                    method: 'veo3'
                  }
                }
              }
            });
          }

          // If multiple videos, start polling for newly generated parts
          if (videos[0].total_parts > 1) {
            const cleanup = startPollingForVideos(videoType, videos[0].total_parts);
            pollingCleanupsRef.current[videoType] = cleanup;
          }
        }
      } else {
        console.warn(`[Veo3 Video Hot-Load] Response missing success for ${videoType}:`, result);
      }
    } catch (error) {
      console.error(`Error generating ${videoType} video:`, error);
      alert(`Failed to generate ${videoType} video: ${error.message}`);
    } finally {
      setGeneratingVideos(prev => ({ ...prev, [videoType]: false }));
    }
  };

  // Polling mechanism to detect newly generated videos
  const startPollingForVideos = React.useCallback((videoType, expectedTotal) => {
    console.log(`[Video Polling] Starting polling for ${videoType}, expecting ${expectedTotal} videos`);

    // Clean up any existing polling for this videoType
    if (pollingCleanupsRef.current[videoType]) {
      console.log(`[Video Polling] Cleaning up existing polling for ${videoType}`);
      pollingCleanupsRef.current[videoType]();
      delete pollingCleanupsRef.current[videoType];
    }

    const pollInterval = setInterval(async () => {
      try {
        // Fetch fresh action data to check for new videos
        // Use the takeGameStep endpoint to get updated action data
        const response = await fetch(
          `${API_BASE_URL}/api/takeGameStep?actionId=${action.id}`,
          { method: 'GET' }
        );

        if (!response.ok) return;

        let result = await parseApiResponse(response);

        // Handle Lambda response format
        if (result.body && typeof result.body === 'string') {
          result = JSON.parse(result.body);
        }

        // Get video data from the action
        const videoData = result.action?.data?.videos?.[videoType]?.veo3 || data.videos?.[videoType]?.veo3;

        // Normalize to array
        let newVideos = [];
        if (Array.isArray(videoData)) {
          newVideos = videoData.map(v => ({
            url: `${v.url}?t=${Date.now()}`,
            dialogue_portion: v.dialogue_portion,
            index: v.index,
            total_parts: v.total_parts,
            duration: v.duration
          }));
        }

        // Check if we have new videos
        const currentCount = veo3Videos[videoType]?.length || 0;
        if (newVideos.length > currentCount) {
          console.log(`[Video Polling] Found ${newVideos.length - currentCount} new videos for ${videoType}`);
          setVeo3Videos(prev => ({
            ...prev,
            [videoType]: newVideos
          }));
        }

        // Stop polling when all videos are generated
        if (newVideos.length >= expectedTotal) {
          console.log(`[Video Polling] All ${expectedTotal} videos generated for ${videoType}, stopping poll`);
          clearInterval(pollInterval);
          delete pollingCleanupsRef.current[videoType];
          setGeneratingVideos(prev => ({ ...prev, [videoType]: false }));
        }
      } catch (error) {
        console.error(`[Video Polling] Error polling for ${videoType}:`, error);
      }
    }, 10000); // Poll every 10 seconds

    // Stop polling after 1 hour max
    setTimeout(() => {
      clearInterval(pollInterval);
      delete pollingCleanupsRef.current[videoType];
      setGeneratingVideos(prev => ({ ...prev, [videoType]: false }));
    }, 3600000);

    return () => clearInterval(pollInterval);
  }, [action.id, veo3Videos, data, pollingCleanupsRef]);

  const generateVoiceoverVideo = async (videoType) => {
    setGeneratingVideos(prev => ({ ...prev, [videoType]: true }));

    try {
      const response = await fetch(
        `${API_BASE_URL}/api/takeGameStep?generateDialogueVideo=true&actionId=${action.id}&videoType=${videoType}&useVoiceover=true`,
        { method: 'GET' }
      );

      if (!response.ok) {
        throw new Error(`Failed to generate voiceover video: ${response.statusText}`);
      }

      let result = await parseApiResponse(response);
      console.log(`[Voiceover Video Hot-Load] Received response for ${videoType}:`, result);

      // Handle Lambda response format (statusCode + body)
      if (result.body && typeof result.body === 'string') {
        console.log(`[Voiceover Video Hot-Load] Unwrapping Lambda response body`);
        result = JSON.parse(result.body);
        console.log(`[Voiceover Video Hot-Load] Parsed body:`, result);
      }

      if (result.success && result.video_url) {
        const videoUrlWithTimestamp = `${result.video_url}?t=${Date.now()}`;
        console.log(`[Voiceover Video Hot-Load] Setting voiceover video URL for ${videoType}:`, videoUrlWithTimestamp);
        setVoiceoverVideoUrls(prev => {
          const newState = { ...prev, [videoType]: videoUrlWithTimestamp };
          console.log(`[Voiceover Video Hot-Load] New voiceoverVideoUrls state:`, newState);
          return newState;
        });

        // Update parent state to persist the change (using nested structure)
        if (onActionUpdate) {
          const updatePayload = {
            videos: {
              [videoType]: {
                ...data.videos?.[videoType],
                voiceover: {
                  ...data.videos?.[videoType]?.voiceover,
                  video_url: result.video_url,
                  duration: result.duration,
                  method: result.method
                }
              }
            }
          };
          console.log(`[Voiceover Video Hot-Load] Calling onActionUpdate for ${videoType}:`, {
            actionId: action.id,
            currentDataVideos: data.videos,
            updatePayload: updatePayload
          });
          onActionUpdate(action.id, updatePayload);
          console.log(`[Voiceover Video Hot-Load] onActionUpdate called successfully for ${videoType}`);
        } else {
          console.error(`[Voiceover Video Hot-Load] onActionUpdate is NOT defined!`);
        }

        console.log(`[Voiceover Video Hot-Load] New ${videoType} voiceover video loaded with timestamp`);
      } else {
        console.warn(`[Voiceover Video Hot-Load] Response missing success or video_url for ${videoType}:`, result);
      }
    } catch (error) {
      console.error(`Error generating ${videoType} voiceover video:`, error);
      alert(`Failed to generate ${videoType} voiceover video: ${error.message}`);
    } finally {
      setGeneratingVideos(prev => ({ ...prev, [videoType]: false }));
    }
  };

  const processDialogueVideo = async (videoType) => {
    setProcessingVideos(prev => ({ ...prev, [videoType]: true }));

    try {
      const response = await fetch(
        `${API_BASE_URL}/api/takeGameStep?processDialogueVideo=true&actionId=${action.id}&videoType=${videoType}`,
        { method: 'GET' }
      );

      if (!response.ok) {
        throw new Error(`Failed to process video: ${response.statusText}`);
      }

      const result = await parseApiResponse(response);

      if (result.success && result.processed_videos) {
        // Handle multiple processed videos (veo3 and voiceover)
        const timestamp = Date.now();
        const updatedVideos = { ...data.videos?.[videoType] };

        for (const processedVideo of result.processed_videos) {
          const videoUrlWithTimestamp = `${processedVideo.url}?t=${timestamp}`;

          // Update local state based on type
          if (processedVideo.type === 'veo3') {
            // Check if this is an array of processed videos or single
            if (Array.isArray(processedVideo.videos)) {
              // Multiple videos processed - update as array
              const processedArray = processedVideo.videos.map(v => ({
                url: `${v.url}?t=${timestamp}`,
                dialogue_portion: v.dialogue_portion,
                index: v.index,
                total_parts: v.total_parts,
                duration: v.duration
              }));

              // Update veo3Videos state with processed versions
              setVeo3Videos(prev => ({
                ...prev,
                [videoType]: processedArray
              }));

              updatedVideos.veo3 = processedVideo.videos;
            } else {
              // Single video processed (old format)
              setProcessedVeo3VideoUrls(prev => ({ ...prev, [videoType]: videoUrlWithTimestamp }));
              updatedVideos.veo3 = {
                ...updatedVideos.veo3,
                processed_video_url: processedVideo.url
              };
            }
          } else if (processedVideo.type === 'voiceover') {
            setProcessedVoiceoverVideoUrls(prev => ({ ...prev, [videoType]: videoUrlWithTimestamp }));
            updatedVideos.voiceover = {
              ...updatedVideos.voiceover,
              processed_video_url: processedVideo.url
            };
          }
        }

        // Update parent state to persist the changes (using nested structure)
        if (onActionUpdate) {
          onActionUpdate(action.id, {
            videos: {
              ...data.videos,
              [videoType]: updatedVideos
            }
          });
        }

        console.log(`[Process Video] Successfully processed ${videoType} videos`);

        // Auto-refresh: Toggle preview off and back on to force video reload
        if (showPopupPreview[videoType]) {
          setShowPopupPreview(prev => ({ ...prev, [videoType]: false }));
          // Wait briefly for unmount, then show again
          setTimeout(() => {
            setShowPopupPreview(prev => ({ ...prev, [videoType]: true }));
          }, 100);
        }
      }
    } catch (error) {
      console.error(`Error processing ${videoType} video:`, error);
      alert(`Failed to process ${videoType} video: ${error.message}`);
    } finally {
      setProcessingVideos(prev => ({ ...prev, [videoType]: false }));
    }
  };

  const saveTextPopup = async (videoKey) => {
    setSavingTextPopup(prev => ({ ...prev, [videoKey]: true }));

    try {
      const response = await fetch(
        `${API_BASE_URL}/api/takeGameStep?saveTextPopup=true&actionId=${action.id}&videoKey=${videoKey}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            title: textPopups[videoKey].title,
            body: textPopups[videoKey].body,
            x: textPopups[videoKey].x,
            y: textPopups[videoKey].y,
            startTime: textPopups[videoKey].startTime,
            endTime: textPopups[videoKey].endTime
          })
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to save text pop-up: ${response.statusText}`);
      }

      const result = await parseApiResponse(response);

      if (result.success) {
        // Update parent state to persist the changes
        if (onActionUpdate) {
          onActionUpdate(action.id, {
            textPopup: {
              ...data.textPopup,
              [videoKey]: {
                title: textPopups[videoKey].title,
                body: textPopups[videoKey].body,
                x: textPopups[videoKey].x,
                y: textPopups[videoKey].y,
                startTime: textPopups[videoKey].startTime,
                endTime: textPopups[videoKey].endTime
              }
            }
          });
        }
        console.log(`[Save Text Pop-up] Successfully saved ${videoKey} text pop-up at (${textPopups[videoKey].x}, ${textPopups[videoKey].y})`);
        alert('Text pop-up saved! Click "Process" to add it to the video.');
      }
    } catch (error) {
      console.error(`Error saving ${videoKey} text pop-up:`, error);
      alert(`Failed to save ${videoKey} text pop-up: ${error.message}`);
    } finally {
      setSavingTextPopup(prev => ({ ...prev, [videoKey]: false }));
    }
  };

  const generateDialogueSceneImage = async (videoType, generateSecondaryImages = true) => {
    setGeneratingSceneImages(prev => ({ ...prev, [videoType]: true }));

    try {
      // Use async job API for long-running image generation
      const result = await asyncJobApi.submitAndWait(
        'generateDialogueSceneImage',
        {
          actionId: action.id,
          sceneType: videoType,
          generateSecondaryImages: generateSecondaryImages ? 'true' : 'false'
        },
        {
          onProgress: (progress, message) => {
            console.log(`[Scene Image] ${progress}%: ${message}`);
          }
        }
      );

      if (result.success && result.dialogueImageUrl) {
        const imageUrlWithTimestamp = `${result.dialogueImageUrl}?t=${Date.now()}`;
        setSceneImageUrls(prev => ({ ...prev, [videoType]: imageUrlWithTimestamp }));

        // Update parent state to persist the change (both golden border and base URLs)
        if (onActionUpdate) {
          const updates = {
            [`${videoType}_scene_image_url`]: result.dialogueImageUrl
          };

          // Also update the base URL if provided
          if (result.dialogueImageBaseUrl) {
            updates[`${videoType}_scene_image_base_url`] = result.dialogueImageBaseUrl;
          }

          // Update bounding box URL if provided
          if (result.dialogueImageBoundingBoxUrl) {
            updates[`${videoType}_scene_image_bbox_url`] = result.dialogueImageBoundingBoxUrl;
          }

          // Update golden border (no dim) URL if provided
          if (result.dialogueImageBorderNoDimUrl) {
            updates[`${videoType}_scene_image_border_nodim_url`] = result.dialogueImageBorderNoDimUrl;
          }

          // Update background-only URL if provided
          if (result.dialogueImageBackgroundUrl) {
            updates[`${videoType}_scene_image_background_url`] = result.dialogueImageBackgroundUrl;
          }

          // Update character-only URL if provided
          if (result.dialogueImageCharacterUrl) {
            updates[`${videoType}_scene_image_character_url`] = result.dialogueImageCharacterUrl;
          }

          // Update sub-conversation participants URL if provided
          if (result.dialogueImageSubConvoUrl) {
            updates[`${videoType}_scene_image_subconvo_url`] = result.dialogueImageSubConvoUrl;
          }

          onActionUpdate(action.id, updates);
        }
      }
    } catch (error) {
      console.error(`Error generating ${videoType} scene image:`, error);
      alert(`Failed to generate scene image: ${error.message}`);
    } finally {
      setGeneratingSceneImages(prev => ({ ...prev, [videoType]: false }));
    }
  };

  const generateDialogueBoundingBoxes = async (videoType) => {
    setGeneratingBoundingBoxes(prev => ({ ...prev, [videoType]: true }));

    try {
      // Use async job API for long-running bounding box generation
      const result = await asyncJobApi.submitAndWait(
        'generateDialogueBoundingBoxes',
        {
          actionId: action.id,
          sceneType: videoType
        },
        {
          onProgress: (progress, message) => {
            console.log(`[Bounding Boxes] ${progress}%: ${message}`);
          }
        }
      );

      if (result.success) {
        // Update parent state to persist the changes
        if (onActionUpdate) {
          const updates = {};

          // Update character bounding box URL if provided
          if (result.characterBBoxUrl) {
            updates[`${videoType}_scene_image_character_bbox_url`] = result.characterBBoxUrl;
          }

          // Update character transparent URL if provided
          if (result.characterTransparentUrl) {
            updates[`${videoType}_scene_image_character_transparent_url`] = result.characterTransparentUrl;
          }

          // Update sub-conversation bounding box URL if provided
          if (result.subConvoBBoxUrl) {
            updates[`${videoType}_scene_image_subconvo_bbox_url`] = result.subConvoBBoxUrl;
          }

          // Update sub-conversation transparent URL if provided
          if (result.subConvoTransparentUrl) {
            updates[`${videoType}_scene_image_subconvo_transparent_url`] = result.subConvoTransparentUrl;
          }

          // Update character transparent with border URL if provided
          if (result.characterTransparentBorderUrl) {
            updates[`${videoType}_scene_image_character_transparent_border_url`] = result.characterTransparentBorderUrl;
          }

          onActionUpdate(action.id, updates);
        }

        alert('Bounding boxes, transparent backgrounds, and golden border generated successfully!');
      }
    } catch (error) {
      console.error(`Error generating ${videoType} bounding boxes:`, error);
      alert(`Failed to generate bounding boxes: ${error.message}`);
    } finally {
      setGeneratingBoundingBoxes(prev => ({ ...prev, [videoType]: false }));
    }
  };

  const previewDialogueVideo = async (videoType) => {
    setPreviewingVideos(prev => ({ ...prev, [videoType]: true }));

    try {
      const response = await fetch(
        `${API_BASE_URL}/api/takeGameStep?previewDialogueVideo=true&actionId=${action.id}&videoType=${videoType}`,
        { method: 'GET' }
      );

      if (!response.ok) {
        throw new Error(`Failed to generate preview: ${response.statusText}`);
      }

      const result = await parseApiResponse(response);

      if (result.success) {
        setPreviewData({
          videoType: videoType,
          prompt: result.prompt,
          useStartFrame: result.useStartFrame || false,
          startFrame: result.startFrame || null,
          referenceImages: result.referenceImages,
          context: result.context
        });
        setShowPreviewModal(true);
      }
    } catch (error) {
      console.error(`Error previewing ${videoType} video:`, error);
      alert(`Failed to generate preview: ${error.message}`);
    } finally {
      setPreviewingVideos(prev => ({ ...prev, [videoType]: false }));
    }
  };

  const previewReferenceImagePrompt = async (videoType) => {
    setPreviewingVideos(prev => ({ ...prev, [`${videoType}_image`]: true }));

    try {
      const response = await fetch(
        `${API_BASE_URL}/api/takeGameStep?previewReferenceImagePrompt=true&actionId=${action.id}&videoType=${videoType}`,
        { method: 'GET' }
      );

      if (!response.ok) {
        throw new Error(`Failed to generate preview: ${response.statusText}`);
      }

      const result = await parseApiResponse(response);

      if (result.success) {
        // Use the same modal as video preview
        setPreviewData({
          videoType: `${videoType}_image`,
          prompt: result.prompt,
          useStartFrame: false,
          startFrame: null,
          referenceImages: null,
          context: result.context
        });
        setShowPreviewModal(true);
      }
    } catch (error) {
      console.error(`Error previewing ${videoType} image prompt:`, error);
      alert(`Failed to generate preview: ${error.message}`);
    } finally {
      setPreviewingVideos(prev => ({ ...prev, [`${videoType}_image`]: false }));
    }
  };

  const extendDialogueVideo = async (videoType, duration = 8) => {
    setExtendingVideos(prev => ({ ...prev, [videoType]: true }));

    try {
      const response = await fetch(
        `${API_BASE_URL}/api/takeGameStep?extendDialogueVideo=true&actionId=${action.id}&videoType=${videoType}&duration=${duration}`,
        { method: 'GET' }
      );

      if (!response.ok) {
        throw new Error(`Failed to extend video: ${response.statusText}`);
      }

      const result = await parseApiResponse(response);

      if (result.success) {
        // Update stitched video URL with timestamp
        if (result.stitched_video_url) {
          const videoUrlWithTimestamp = `${result.stitched_video_url}?t=${Date.now()}`;
          setStitchedVideoUrls(prev => ({ ...prev, [videoType]: videoUrlWithTimestamp }));
        }

        // Update extension count and total duration
        if (result.extension_count !== undefined) {
          setExtensionCounts(prev => ({ ...prev, [videoType]: result.extension_count }));
        }

        // Update parent state to persist the change (using nested structure)
        if (onActionUpdate) {
          onActionUpdate(action.id, {
            videos: {
              ...data.videos,
              [videoType]: {
                ...data.videos?.[videoType],
                veo3: {
                  ...data.videos?.[videoType]?.veo3,
                  stitched_video_url: result.stitched_video_url,
                  extension_count: result.extension_count,
                  total_duration: result.total_duration
                }
              }
            }
          });
        }

        console.log(`[Video Extended] ${videoType} veo3 video now ${result.total_duration}s (${result.extension_count}x extended)`);
      }
    } catch (error) {
      console.error(`Error extending ${videoType} video:`, error);
      alert(`Failed to extend ${videoType} video: ${error.message}`);
    } finally {
      setExtendingVideos(prev => ({ ...prev, [videoType]: false }));
    }
  };

  // Clone player voice using ElevenLabs
  const clonePlayerVoice = async (playerId, playerName, gameInstanceId) => {
    if (!window.confirm(`Clone voice for ${playerName}?\n\nThis requires at least 3 audio samples from their dialogue.`)) {
      return;
    }

    try {
      const response = await fetch(
        `${API_BASE_URL}/api/takeGameStep?clonePlayerVoice=true&playerId=${playerId}&gameInstanceId=${gameInstanceId}`,
        { method: 'GET' }
      );

      if (!response.ok) {
        throw new Error(`Failed to clone voice: ${response.statusText}`);
      }

      const result = await parseApiResponse(response);

      if (result.success) {
        alert(`✅ Voice cloned successfully!\n\nPlayer: ${result.player_name}\nVoice ID: ${result.cloned_voice_id}\nSamples used: ${result.sample_count}`);

        // Optionally reload the page to show updated data
        window.location.reload();
      } else {
        throw new Error(result.error || 'Voice cloning failed');
      }
    } catch (error) {
      console.error('Error cloning voice:', error);
      alert(`❌ Failed to clone voice: ${error.message}`);
    }
  };

  // Generate TTS audio for dialogue
  const generateDialogueTTS = async (videoType) => {
    setGeneratingTTS(prev => ({ ...prev, [videoType]: true }));

    try {
      // Use async job API for long-running TTS generation
      const result = await asyncJobApi.submitAndWait(
        'generateDialogueTTS',
        {
          actionId: action.id,
          videoType: videoType
        },
        {
          onProgress: (progress, message) => {
            console.log(`[TTS] ${progress}%: ${message}`);
          }
        }
      );

      if (result.success) {
        // Update audio URL with timestamp to force reload
        const audioUrlWithTimestamp = `${result.audio_url}?t=${Date.now()}`;
        setAudioUrls(prev => ({ ...prev, [videoType]: audioUrlWithTimestamp }));
        console.log(`[TTS Generated] ${videoType} audio: ${result.duration}s for ${result.character}`);
      } else {
        throw new Error(result.error || 'TTS generation failed');
      }
    } catch (error) {
      console.error(`Error generating ${videoType} TTS:`, error);
      alert(`Failed to generate ${videoType} TTS: ${error.message}`);
    } finally {
      setGeneratingTTS(prev => ({ ...prev, [videoType]: false }));
    }
  };

  // Determine background color based on message type
  const getBackgroundColor = () => {
    if (isSpectating) return `${THEME.spectating || '#78909C'}1a`;
    if (isSecret) return `${THEME.secret}1a`; // Deep purple
    if (isPrivate) return `${THEME.private}1a`; // Royal blue
    if (isHouseAction) return `${THEME.accent}1a`; // House action color
    return `${THEME.public}1a`; // Forest green
  };

  const getTypeColor = () => {
    if (isSpectating) return THEME.spectating || '#78909C';
    if (isSecret) return THEME.secret;
    if (isPrivate) return THEME.private;
    if (isHouseAction) return THEME.accent;
    return THEME.public;
  };
  
  if (!user) {
    console.log('MessageAction: User not found, rendering fallback');
    return (
      <div style={{
        ...COMMON_STYLES.actionContainer,
        backgroundColor: THEME.bgLight,
      }}>
        {/* Own keyframe name: the media grid's `shimmer` is injected only when
            that panel renders, so this fallback cannot borrow it. */}
        <style>{`@keyframes nameSkeletonShimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }`}</style>
        <div style={{
          ...COMMON_STYLES.actionHeader,
          backgroundColor: '${THEME.textDark}33',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {/* 🪪 Name fallback ladder (08-14). The roster lookup failed, but
                that does NOT mean the name is unknown — the payload carries
                the resolved sender_name, so USE IT before showing a
                placeholder. Only when there is genuinely no name to show do we
                fall through to the shimmer, and that case is a real load state
                (roster still arriving), not a permanent one: a forever-
                animating skeleton is worse than a label, and this file already
                learned that lesson once (see the "permanently-animating
                shimmer skeletons" note on the media grid). */}
            {data.sender_name ? (
              <span style={{
                ...COMMON_STYLES.playerBadge,
                backgroundColor: THEME.neutral,
                color: THEME.textLight,
              }}>
                {data.sender_name}
              </span>
            ) : (
              <span
                aria-label="Loading player name"
                title="Loading player name"
                style={{
                  ...COMMON_STYLES.playerBadge,
                  // Sized to a typical name so the header does not reflow when
                  // the real badge replaces it.
                  display: 'inline-block',
                  minWidth: 84,
                  color: 'transparent',
                  background: `linear-gradient(90deg, ${THEME.neutral || '#555'} 25%, ${THEME.bgLight || '#333'} 50%, ${THEME.neutral || '#555'} 75%)`,
                  backgroundSize: '200% 100%',
                  animation: 'nameSkeletonShimmer 1.4s ease-in-out infinite',
                }}
              >
                &nbsp;
              </span>
            )}
            <span style={{
              ...COMMON_STYLES.typeBadge,
              backgroundColor: THEME.public,
              color: THEME.textLight,
            }}>
              Public
            </span>
          </div>
          <span style={{ color: THEME.textMuted, fontSize: '0.85em' }}>
            {formatActionTime(data.timestamp || action.created_at)}
          </span>
        </div>
        
        <div style={COMMON_STYLES.actionBody}>
          <div style={COMMON_STYLES.messageBox}>
            <SourcePill data={data} />
            {renderMessageWithCode(data.message, (users || []).map(u => u.name), THEME.accent || '#66bbff')}
          </div>
        </div>
      </div>
    );
  }

  const modelColor = getModelColor(user.model, THEME);

  // Get alignment color
  const getAlignmentColor = () => {
    if (user.data?.alignment === 'evil') return THEME.evil;
    if (user.data?.alignment === 'good') return THEME.good;
    return THEME.neutral;
  };

  // Get role display
  const getRoleDisplay = () => {
    const role = user.data?.role || user.data?.perceived_role || user.data?.true_role;
    if (!role) return '';
    return role.charAt(0).toUpperCase() + role.slice(1).replace(/_/g, ' ');
  };

  const isDamagingAction = data.is_a_damaging_action === true;
  const tokensGained = data.tokens_gained;

  // Chat-game turn description
  const isChatGame = gameType === 'chat-game' || gameType === 'chat';
  // Helper: format a player name with model, e.g. "Tigger (GPT 5.2)"
  const nameWithModel = (name) => {
    const u = users?.find(u => u.name === name);
    const model = u ? getDisplayModelName(u) : null;
    return model ? `${name} (${model})` : name;
  };
  const getChatGameTurnDescription = () => {
    const isPass = action.type === 'pass' || data.sub_conversation_pass;
    if (isPass) return { desc: 'chose to skip their turn', cost: 0, emoji: '⏭️' };
    if (isSubConversation) {
      const recipientList = recipients.length > 0 ? recipients.map(nameWithModel).join(', ') : 'others';
      if (isSecret) return { desc: `sent a secret sub-chat message to ${recipientList}`, cost: 3, emoji: '🤫' };
      return { desc: `sent a sub-chat message to ${recipientList}`, cost: 2, emoji: '🔒' };
    }
    if (isSecret) {
      const recipientList = recipients.length > 0 ? recipients.map(nameWithModel).join(', ') : 'someone';
      return { desc: `sent a secret message to ${recipientList}`, cost: 3, emoji: '🤫' };
    }
    if (isPrivate) {
      const recipientList = recipients.length > 0 ? recipients.map(nameWithModel).join(', ') : 'someone';
      return { desc: `sent a private message to ${recipientList}`, cost: 2, emoji: '💬' };
    }
    if (isHouseAction) return { desc: 'performed a house action', cost: 0, emoji: '🏠' };
    if (isSpectating) return { desc: 'sent a ghost message', cost: 0, emoji: '👻' };
    // Public message
    const targetList = targetPlayers.length > 0 ? ` to ${targetPlayers.map(nameWithModel).join(', ')}` : '';
    return { desc: `sent a public message${targetList}`, cost: 1, emoji: '📢' };
  };

  return (
    <div style={{
      display: 'flex',
      justifyContent: isOwnMessage ? 'flex-end' : 'flex-start',
      marginBottom: '4px',
      marginLeft: isSubConversation ? '24px' : undefined,
    }}>
    <div style={{ display: 'flex', flexDirection: 'column', maxWidth: isMobile ? '90%' : '75%', alignItems: isOwnMessage ? 'flex-end' : 'flex-start' }}>
    {/* 🔌 09-28 agent wave: data-seat = the sender's seat, so a watch link's
        ?with=<seat id> can highlight this agent's lines (utils/watchWith). */}
    <div data-seat={action.game_instance_player_id || undefined} style={{
      borderRadius: isOwnMessage ? '18px 18px 4px 18px' : '18px 18px 18px 4px',
      marginBottom: '2px',
      boxShadow: isDamagingAction ? `0 0 20px ${THEME.secondary}80, 0 4px 12px ${THEME.textDark}4d` : (isOwnMessage ? `0 1px 3px rgba(0,0,0,0.15)` : `0 1px 3px rgba(0,0,0,0.15), 0 0 6px ${THEME.accent}08`),
      overflow: 'visible',
      backgroundColor: isOwnMessage
        ? ownCol.bg // 🌿 10-09: sage in the House, else the theme accent as before
        : (THEME.bgLight || '#2a2a3a'),
      border: isDamagingAction ? `3px solid ${THEME.secondary}` : (isSpectating ? `1px dashed ${THEME.spectating || '#78909C'}66` : (isSubConversation ? `1px solid #8B478966` : (!isOwnMessage ? `1px solid ${THEME.accent}18` : 'none'))),
      borderLeft: isSubConversation ? '3px solid #8B4789' : (!isOwnMessage && !isDamagingAction && !isSpectating ? `3px solid ${THEME.accent}50` : undefined),
      position: 'relative',
      opacity: isSpectating ? 0.75 : 1,
      fontStyle: isSpectating ? 'italic' : 'normal',
      maxWidth: '100%',
    }}>
      {/* Bubble tail */}
      {isOwnMessage && (
        <div style={{
          position: 'absolute',
          bottom: '0px',
          right: '-6px',
          width: '12px',
          height: '12px',
          backgroundColor: ownCol.bg, // 🌿 10-09: the tail matches the bubble
          clipPath: 'polygon(0 0, 0% 100%, 100% 100%)',
          zIndex: 0,
        }}/>
      )}
      {isDamagingAction && (
        /* Static gradient + FINITE opacity pulse (8 beats, then rests at 50%).
           The old infinite gradientShift kept every damaging message in the
           log repainting forever — ~21% of idle render cost (fx-bench 07-09).
           Finite iterations = fresh hits pulse for attention, history settles
           down on its own, no bookkeeping needed. */
        <div style={{
          position: 'absolute',
          top: '-2px',
          left: '-2px',
          right: '-2px',
          bottom: '-2px',
          background: `linear-gradient(45deg, ${THEME.secondary}40, ${THEME.accent}40, ${THEME.secondary}40)`,
          animation: 'damagePulse 1.6s ease-in-out 8 forwards',
          borderRadius: '8px',
          zIndex: -1,
          pointerEvents: 'none',
        }}/>
      )}
      {isChatGame ? (
        /* ── Chat-Game Turn Log Header ── */
        (() => {
          const turn = getChatGameTurnDescription();
          const typeColor = getTypeColor();
          // Competitive: every surface shows this player in ONE stable seat color
          // (roster, dice toast, vote card, and here) so "who said that" tracks itself.
          const seatColor = competitiveMode ? playerColorFor(users, action.game_instance_player_id) : null;
          return (
            <div style={{
              padding: '8px 14px 4px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              borderBottom: 'none',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1, minWidth: 0 }}>
                {/* Tap a local to open their character card (Nina 07-16 — same
                    popup the Tea used; CastCardModal, spec Arc 3 step 1) */}
                <span
                  onClick={onOpenCastCard ? () => onOpenCastCard(action.game_instance_player_id) : undefined}
                  title={onOpenCastCard ? `${user.name} — tap for character card` : undefined}
                  style={{ display: 'inline-flex', cursor: onOpenCastCard ? 'pointer' : undefined }}
                >
                  <ProfileImage user={user} size={28} borderColor={seatColor || typeColor} />
                </span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                    <span
                      onClick={onOpenCastCard ? () => onOpenCastCard(action.game_instance_player_id) : undefined}
                      style={{ fontWeight: 800, color: isOwnMessage ? ownCol.name : (seatColor || typeColor), fontSize: '0.88em', cursor: onOpenCastCard ? 'pointer' : undefined }}>
                      {user.name}
                      {getDisplayModelName(user) && (
                        <span style={(ownCol && ownCol.label) ? { fontWeight: 500, color: ownCol.label, fontSize: '0.85em' } : { fontWeight: 500, opacity: 0.7, fontSize: '0.85em' }}>
                          {' '}({getDisplayModelName(user)})
                        </span>
                      )}
                    </span>
                    {/* 🏷️ Official house nickname (Claude House §6.2) — the
                        distiller-ratified badge next to a human's name; the
                        whole house calls them this, so the room shows it. */}
                    {houseNicknames && user.is_human && houseNicknames[user.name]?.nickname && (
                      <span
                        title={`The house calls them "${houseNicknames[user.name].nickname}"${houseNicknames[user.name].by ? ` (${houseNicknames[user.name].by}'s idea)` : ''}`}
                        style={{
                          fontSize: '0.68em', fontWeight: 800, color: '#e8bb5f',
                          background: 'rgba(217,169,78,0.14)', border: '1px solid rgba(217,169,78,0.45)',
                          borderRadius: 999, padding: '1px 7px', flexShrink: 0,
                        }}
                      >🏷️ “{houseNicknames[user.name].nickname}”</span>
                    )}
                    {/* 🔧 Admin seat tag (09-01) — stamped server-side at join
                        via checkAdminAuth, spoof-proof. Same pill shape as the
                        nickname badge above, cool grey instead of gold. */}
                    {isMaintainer(user) && (
                      <span
                        title="Site admin, here for upkeep"
                        style={{
                          fontSize: '0.68em', fontWeight: 800, color: '#9fb6c9',
                          background: 'rgba(120,150,175,0.14)', border: '1px solid rgba(120,150,175,0.45)',
                          borderRadius: 999, padding: '1px 7px', flexShrink: 0,
                        }}
                      >🔧 system maintenance</span>
                    )}
                    <span style={{ color: isOwnMessage ? ownCol.time : THEME.textMuted, fontSize: '0.78em', flexShrink: 0 }}>
                      {formatActionTime(data.timestamp || action.created_at)}
                    </span>
                    {competitiveMode && isDamagingAction && (
                      <span style={{
                        color: THEME.secondary,
                        fontWeight: 700,
                        fontSize: '0.82em',
                      }}>
                        +{tokensGained} credits
                      </span>
                    )}
                  </div>
                  {/* 🌙☀️ What they're doing right now, under their name (Nina
                      08-08). Same `circadian.statuses` map the Dossier draws
                      from, so the two surfaces can never disagree — and the
                      same register the spec sets for it (§9.3): lowercase
                      webcam caption, no per-activity icons, no meters. Sleep is
                      the one case with a glyph, because absence is the content.
                      Locals only: humans have no HOURS and get no entry.
                      Single-line clamped — a chat row must not grow taller than
                      its message. 👥 10-06: YOUR own message is the theme accent,
                      so your line goes light with a green dot (Nina, option C). */}
                  <LocalStatusLine
                    statuses={localStatuses}
                    id={action.game_instance_player_id}
                    altId={user.id}
                    isHuman={isRealHuman(user)}
                    onOwnBubble={isOwnMessage}
                    ownTone={ownCol ? ownCol.status : null} // 🌿 10-09: mint on the House's sage bubble
                  />
                  {data.discussionRound && (
                    <span style={{
                      fontSize: '0.68em',
                      color: isOwnMessage ? 'rgba(255,255,255,0.5)' : THEME.textMuted,
                      opacity: 0.7,
                      marginTop: '1px',
                    }}>
                      Turn {data.discussionRound}
                    </span>
                  )}
                </div>
              </div>
            </div>
          );
        })()
      ) : (
        /* ── Original Badge Header (non-chat-game) ── */
        <div style={{
          padding: '8px 14px 4px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: 'none',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', flex: 1, minWidth: 0 }}>
            <ProfileImage
              user={user}
              size={28}
              borderColor={modelColor}
            />
            <span style={{
              fontWeight: 800,
              color: isOwnMessage ? 'rgba(255,255,255,0.95)' : modelColor,
              fontSize: '0.88em',
            }}>
              {user.name} ({getDisplayModelName(user)})
              <span style={{ opacity: 0.6, fontWeight: 500, fontSize: '0.85em' }}> [{remaining_tokens}]</span>
            </span>
            <span style={{ color: isOwnMessage ? 'rgba(255,255,255,0.6)' : THEME.textMuted, fontSize: '0.78em', flexShrink: 0 }}>
              {formatActionTime(data.timestamp || action.created_at)}
            </span>
            {getRoleDisplay() && (
              <span style={{
                backgroundColor: getAlignmentColor(),
                color: THEME.textLight,
                padding: '1px 6px',
                fontSize: '0.7em',
                borderRadius: '10px',
                fontWeight: 700,
              }}>
                {getRoleDisplay()}
              </span>
            )}
            <span style={{
              ...COMMON_STYLES.typeBadge,
              backgroundColor: isOwnMessage ? 'rgba(255,255,255,0.2)' : getTypeColor(),
              color: isOwnMessage ? 'rgba(255,255,255,0.9)' : THEME.textLight,
              fontSize: '0.7em',
            }}>
              {isSpectating ? 'Ghost Chat' : (isSecret ? 'Secret' : (isPrivate ? 'Private' : (isHouseAction ? 'House Action' : (isSubConversation ? 'Sub-Chat' : 'Public'))))}
            </span>
            {(data.sub_conversation_message || data.parent_action_id) && (
              <span style={{
                ...COMMON_STYLES.typeBadge,
                backgroundColor: '#8B4789',
                color: THEME.textLight,
                fontSize: '0.7em',
              }}>
                🔒 Sub-Conversation
              </span>
            )}
            {data.sub_conversation_pass && (
              <span style={{
                ...COMMON_STYLES.typeBadge,
                backgroundColor: '#6c757d',
                color: THEME.textLight,
                fontSize: '0.7em',
              }}>
                ⏭️ Passed
              </span>
            )}
            {isDamagingAction && (
              <span style={{
                ...COMMON_STYLES.typeBadge,
                backgroundColor: THEME.secondary,
                color: THEME.textLight,
                fontWeight: 'bold',
                padding: '2px 6px',
                fontSize: '0.7em',
                animation: 'pulse 2s ease-in-out infinite',
              }}>
                🎭 DAMAGING +{tokensGained}
              </span>
            )}
            {data.discussionRound && (
              <span style={{
                ...COMMON_STYLES.typeBadge,
                backgroundColor: isOwnMessage ? 'rgba(255,255,255,0.15)' : THEME.system,
                color: isOwnMessage ? 'rgba(255,255,255,0.8)' : THEME.textLight,
                fontSize: '0.7em',
              }}>
                Round {data.discussionRound}
              </span>
            )}
            {recipients.length > 0 && (
              <div style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '4px',
              }}>
                <span style={{ color: isOwnMessage ? 'rgba(255,255,255,0.6)' : THEME.textMuted, fontSize: '0.8em' }}>→</span>
                {recipients.map((recipient, index) => (
                  <span key={index} style={{
                    backgroundColor: isOwnMessage ? 'rgba(255,255,255,0.15)' : (action.type === 'public_message'
                      ? `${THEME.public}20`
                      : (isSecret ? `${THEME.secret}20` : `${THEME.private}20`)),
                    color: isOwnMessage ? 'rgba(255,255,255,0.9)' : (action.type === 'public_message'
                      ? THEME.public
                      : (isSecret ? THEME.secret : THEME.private)),
                    border: isOwnMessage ? '1px solid rgba(255,255,255,0.3)' : `1px solid ${action.type === 'public_message'
                      ? THEME.public
                      : (isSecret ? THEME.secret : THEME.private)}`,
                    fontSize: '0.75em',
                    padding: '1px 5px',
                    borderRadius: '10px',
                    fontWeight: 600,
                  }}>
                    {recipient}
                  </span>
                ))}
              </div>
            )}
            {targetPlayers.length > 0 && recipients.length === 0 && (
              <div style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '4px',
              }}>
                <span style={{ color: isOwnMessage ? 'rgba(255,255,255,0.6)' : THEME.textMuted, fontSize: '0.8em' }}>→</span>
                {targetPlayers.map((target, index) => (
                  <span key={index} style={{
                    backgroundColor: isOwnMessage ? 'rgba(255,255,255,0.15)' : `${THEME.public || THEME.accent}20`,
                    color: isOwnMessage ? 'rgba(255,255,255,0.9)' : (THEME.public || THEME.accent),
                    border: isOwnMessage ? '1px solid rgba(255,255,255,0.3)' : `1px solid ${THEME.public || THEME.accent}`,
                    fontSize: '0.75em',
                    padding: '1px 5px',
                    borderRadius: '10px',
                    fontWeight: 600,
                  }}>
                    {target}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
      
      <div style={{ padding: '4px 14px 10px' }}>
        {isHouseAction ? (
          <div style={{
            color: isOwnMessage ? 'rgba(255,255,255,0.9)' : THEME.textDark,
            fontStyle: 'italic',
            lineHeight: '1.5',
            fontSize: '0.95em',
            borderLeft: isOwnMessage ? '3px solid rgba(255,255,255,0.4)' : `3px solid ${THEME.accent}`,
            paddingLeft: '8px',
          }}>
            <strong>🏠 Action:</strong> {data.action_description}
          </div>
        ) : data.is_emote ? (
          <div style={{
            color: isOwnMessage ? 'rgba(255,255,255,0.85)' : THEME.textMuted,
            fontStyle: 'italic',
            lineHeight: '1.5',
            fontSize: '0.95em',
            opacity: 0.95,
          }}>
            * {data.sender_name || (user && user.name) || 'Someone'} {data.message} *
          </div>
        ) : (
          <div style={{
            color: isOwnMessage ? ownCol.text : THEME.textDark, // 🌿 10-09: was '#fff' (still is outside the House)
            lineHeight: '1.5',
            fontSize: '0.95em',
            // A pasted URL is one unbreakable "word": without this the main feed
            // bubble grew past the column and pushed the page sideways (the
            // ghost/spectator composer already broke long words; the main
            // bubble never did).
            overflowWrap: 'anywhere',
            wordBreak: 'break-word',
          }}>
            <SourcePill data={data} />
            {renderMessageWithCode(data.message, (users || []).map(u => u.name), isOwnMessage ? 'rgba(255,255,255,0.95)' : (THEME.accent || '#66bbff'))}
          </div>
        )}

        {data.stage_direction && (
          <div style={{
            backgroundColor: isOwnMessage ? 'rgba(255,255,255,0.1)' : `${THEME.primary}15`,
            padding: '6px 10px',
            borderRadius: '8px',
            borderLeft: isOwnMessage ? '3px solid rgba(255,255,255,0.4)' : `3px solid ${THEME.primary}`,
            color: isOwnMessage ? 'rgba(255,255,255,0.8)' : THEME.textMuted,
            fontSize: '0.85em',
            fontStyle: 'italic',
            marginTop: '6px',
          }}>
            <strong>🎬 Stage Direction:</strong> {data.stage_direction}
          </div>
        )}

        {showThoughts && data.thoughts && (
          <div style={{
            backgroundColor: isOwnMessage ? 'rgba(255,255,255,0.1)' : `${THEME.textLight}0d`,
            padding: '6px 10px',
            borderRadius: '8px',
            border: isOwnMessage ? '1px solid rgba(255,255,255,0.15)' : `1px solid ${THEME.borderLight}`,
            color: isOwnMessage ? 'rgba(255,255,255,0.7)' : `var(--room-thoughts-color, ${THEME.textMuted})`,
            fontSize: '0.85em',
            fontStyle: 'italic',
            marginTop: '6px',
          }}>
            <strong>💭 Thoughts:</strong> {data.thoughts}
          </div>
        )}

        {data.diary_room && (
          <div style={{
            backgroundColor: isOwnMessage ? 'rgba(255,255,255,0.1)' : `${THEME.accent}1a`,
            padding: '6px 10px',
            borderRadius: '8px',
            border: isOwnMessage ? '1px solid rgba(255,255,255,0.15)' : `1px solid ${THEME.accent}`,
            color: isOwnMessage ? 'rgba(255,255,255,0.8)' : THEME.textLight,
            fontSize: '0.85em',
            marginTop: '6px',
          }}>
            <strong>📜 Diary Room:</strong> {data.diary_room}
          </div>
        )}

        {/* Video Generation Sections - Three Types */}
        {data.message && gameType !== 'chat-game' && (
          <div style={{
            marginTop: '12px',
            padding: '12px',
            backgroundColor: `${THEME.bgDark}80`,
            borderRadius: '4px',
            border: `1px solid ${THEME.borderLight}`
          }}>
            {/* Hide video generation UI for chat games unless explicitly enabled */}
            {true && (
              <>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  marginBottom: '8px'
                }}>
                  <span style={{
                    color: THEME.textMuted,
                    fontSize: '0.85em',
                    fontWeight: 'bold'
                  }}>
                    💬 External Dialogue Video
                  </span>
                </div>

                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  marginBottom: '8px'
                }}>
                <button
                  onClick={() => previewDialogueVideo('external')}
                  disabled={previewingVideos.external}
                  style={{
                    backgroundColor: previewingVideos.external ? THEME.textMuted : THEME.secondary,
                    color: THEME.textLight,
                    border: 'none',
                    padding: '4px 10px',
                    borderRadius: '4px',
                    fontSize: '0.8em',
                    fontWeight: 'bold',
                    cursor: previewingVideos.external ? 'not-allowed' : 'pointer',
                    transition: 'background-color 0.2s'
                  }}
                >
                  {previewingVideos.external ? '⏳ Loading...' : '👁️ Preview Video'}
                </button>
                <button
                  onClick={() => previewReferenceImagePrompt('external')}
                  disabled={previewingVideos.external_image}
                  style={{
                    backgroundColor: previewingVideos.external_image ? THEME.textMuted : '#9C27B0',
                    color: THEME.textLight,
                    border: 'none',
                    padding: '4px 10px',
                    borderRadius: '4px',
                    fontSize: '0.8em',
                    fontWeight: 'bold',
                    cursor: previewingVideos.external_image ? 'not-allowed' : 'pointer',
                    transition: 'background-color 0.2s'
                  }}
                >
                  {previewingVideos.external_image ? '⏳ Loading...' : '🖼️ Preview Image'}
                </button>
                <button
                  onClick={() => generateDialogueVideo('external')}
                  disabled={generatingVideos.external}
                  style={{
                    backgroundColor: generatingVideos.external ? THEME.textMuted : THEME.accent,
                    color: THEME.textDark,
                    border: 'none',
                    padding: '4px 10px',
                    borderRadius: '4px',
                    fontSize: '0.8em',
                    fontWeight: 'bold',
                    cursor: generatingVideos.external ? 'not-allowed' : 'pointer',
                    transition: 'background-color 0.2s'
                  }}
                >
                  {generatingVideos.external ? '⏳ Generating...' : '🎬 Generate'}
                </button>
                <button
                  onClick={() => generateVoiceoverVideo('external')}
                  disabled={generatingVideos.external}
                  style={{
                    backgroundColor: generatingVideos.external ? THEME.textMuted : '#4CAF50',
                    color: THEME.textLight,
                    border: 'none',
                    padding: '4px 10px',
                    borderRadius: '4px',
                    fontSize: '0.8em',
                    fontWeight: 'bold',
                    cursor: generatingVideos.external ? 'not-allowed' : 'pointer',
                    transition: 'background-color 0.2s'
                  }}
                >
                  {generatingVideos.external ? '⏳ Generating...' : '🎙️ Voiceover'}
                </button>
                <button
                  onClick={() => generateDialogueSceneImage('external', false)}
                  disabled={generatingSceneImages.external}
                  style={{
                    backgroundColor: generatingSceneImages.external ? THEME.textMuted : '#FF9800',
                    color: THEME.textLight,
                    border: 'none',
                    padding: '4px 10px',
                    borderRadius: '4px',
                    fontSize: '0.8em',
                    fontWeight: 'bold',
                    cursor: generatingSceneImages.external ? 'not-allowed' : 'pointer',
                    transition: 'background-color 0.2s'
                  }}
                >
                  {generatingSceneImages.external ? '🎨 Generating...' : '🎨 Main Image'}
                </button>
                <button
                  onClick={() => generateDialogueSceneImage('external', true)}
                  disabled={generatingSceneImages.external || !data.external_scene_image_base_url}
                  style={{
                    backgroundColor: generatingSceneImages.external || !data.external_scene_image_base_url ? THEME.textMuted : '#9C27B0',
                    color: THEME.textLight,
                    border: 'none',
                    padding: '4px 10px',
                    borderRadius: '4px',
                    fontSize: '0.8em',
                    fontWeight: 'bold',
                    cursor: generatingSceneImages.external || !data.external_scene_image_base_url ? 'not-allowed' : 'pointer',
                    transition: 'background-color 0.2s'
                  }}
                  title={!data.external_scene_image_base_url ? 'Generate main image first' : 'Generate variants from main image'}
                >
                  {generatingSceneImages.external ? '✨ Generating...' : '✨ Variants'}
                </button>
                <button
                  onClick={() => generateDialogueBoundingBoxes('external')}
                  disabled={generatingBoundingBoxes.external || !data.external_scene_image_character_url}
                  style={{
                    backgroundColor: generatingBoundingBoxes.external || !data.external_scene_image_character_url ? THEME.textMuted : '#2196F3',
                    color: THEME.textLight,
                    border: 'none',
                    padding: '4px 10px',
                    borderRadius: '4px',
                    fontSize: '0.8em',
                    fontWeight: 'bold',
                    cursor: generatingBoundingBoxes.external || !data.external_scene_image_character_url ? 'not-allowed' : 'pointer',
                    transition: 'background-color 0.2s'
                  }}
                  title={!data.external_scene_image_character_url ? 'Generate variants first' : 'Generate bounding boxes on isolated images'}
                >
                  {generatingBoundingBoxes.external ? '📦 Generating...' : '📦 Bounding Boxes'}
                </button>
                {((veo3Videos.external?.length > 0) || voiceoverVideoUrls.external) && (
                  <button
                    onClick={() => processDialogueVideo('external')}
                    disabled={processingVideos.external}
                    style={{
                      backgroundColor: processingVideos.external ? THEME.textMuted : THEME.secondary,
                      color: THEME.textLight,
                      border: 'none',
                      padding: '4px 10px',
                      borderRadius: '4px',
                      fontSize: '0.8em',
                      fontWeight: 'bold',
                      cursor: processingVideos.external ? 'not-allowed' : 'pointer',
                      transition: 'background-color 0.2s'
                    }}
                  >
                    {processingVideos.external ? '⏳ Processing...' : '✨ Process'}
                  </button>
                )}
                {((veo3Videos.external?.length > 0) || stitchedVideoUrls.external) && (
                  <button
                    onClick={() => extendDialogueVideo('external', 7)}
                    disabled={extendingVideos.external}
                    style={{
                      backgroundColor: extendingVideos.external ? THEME.textMuted : '#9C27B0',
                      color: THEME.textLight,
                      border: 'none',
                      padding: '4px 8px',
                      borderRadius: '4px',
                      fontSize: '0.8em',
                      fontWeight: 'bold',
                      cursor: extendingVideos.external ? 'not-allowed' : 'pointer',
                      transition: 'background-color 0.2s'
                    }}
                  >
                    {extendingVideos.external ? '⏳ Extending...' : '+7s'}
                  </button>
                )}
                <button
                  onClick={() => generateDialogueTTS('external')}
                  disabled={generatingTTS.external}
                  style={{
                    backgroundColor: generatingTTS.external ? THEME.textMuted : '#FF6B6B',
                    color: THEME.textLight,
                    border: 'none',
                    padding: '4px 10px',
                    borderRadius: '4px',
                    fontSize: '0.8em',
                    fontWeight: 'bold',
                    cursor: generatingTTS.external ? 'not-allowed' : 'pointer',
                    transition: 'background-color 0.2s'
                  }}
                >
                  {generatingTTS.external ? '⏳ Generating...' : '🎙️ TTS'}
                </button>
                {action.game_instance_player_id && action.game_instance_id && (
                  <button
                    onClick={() => clonePlayerVoice(action.game_instance_player_id, data.sender_name || 'Player', action.game_instance_id)}
                    style={{
                      backgroundColor: '#9C27B0',
                      color: THEME.textLight,
                      border: 'none',
                      padding: '4px 10px',
                      borderRadius: '4px',
                      fontSize: '0.8em',
                      fontWeight: 'bold',
                      cursor: 'pointer',
                      transition: 'background-color 0.2s'
                    }}
                    title="Clone this player's voice from their audio samples (requires 3+ samples)"
                  >
                    🎤 Clone Voice
                  </button>
                )}
              </div>
              </>
            )}
            {/* Text Pop-up Input for External Dialogue - Always visible when there are any external videos */}
            {(veo3Videos.external?.length > 0 || stitchedVideoUrls.external || processedVeo3VideoUrls.external || processedVoiceoverVideoUrls.external || voiceoverVideoUrls.external) && (
              <div style={{
                marginTop: '12px',
                marginBottom: '12px',
                padding: '10px',
                backgroundColor: THEME.bgLight,
                borderRadius: '6px',
                border: `1px solid ${THEME.borderDark}`
              }}>
                <div style={{
                  color: THEME.textLight,
                  fontWeight: 'bold',
                  fontSize: '0.8em',
                  marginBottom: '6px'
                }}>
                  💬 Text Pop-up for External Dialogue
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  <input
                    type="text"
                    placeholder="Title (e.g., 'Week 3')"
                    value={textPopups.external.title}
                    onChange={(e) => setTextPopups(prev => ({
                      ...prev,
                      external: { ...prev.external, title: e.target.value }
                    }))}
                    style={{
                      padding: '5px 8px',
                      borderRadius: '4px',
                      border: `1px solid ${THEME.borderLight}`,
                      backgroundColor: THEME.bgMedium,
                      color: THEME.textLight,
                      fontSize: '0.8em'
                    }}
                  />
                  <textarea
                    placeholder="Body text (e.g., 'Nominations Ceremony')"
                    value={textPopups.external.body}
                    onChange={(e) => setTextPopups(prev => ({
                      ...prev,
                      external: { ...prev.external, body: e.target.value }
                    }))}
                    style={{
                      padding: '5px 8px',
                      borderRadius: '4px',
                      border: `1px solid ${THEME.borderLight}`,
                      backgroundColor: THEME.bgMedium,
                      color: THEME.textLight,
                      fontSize: '0.8em',
                      minHeight: '50px',
                      resize: 'vertical',
                      fontFamily: 'inherit'
                    }}
                  />
                  <div style={{ display: 'flex', gap: '6px' }}>
                    <input
                      type="number"
                      placeholder="Start time (seconds)"
                      value={textPopups.external.startTime}
                      onChange={(e) => setTextPopups(prev => ({
                        ...prev,
                        external: { ...prev.external, startTime: parseFloat(e.target.value) || 0 }
                      }))}
                      style={{
                        flex: 1,
                        padding: '5px 8px',
                        borderRadius: '4px',
                        border: `1px solid ${THEME.borderLight}`,
                        backgroundColor: THEME.bgMedium,
                        color: THEME.textLight,
                        fontSize: '0.8em'
                      }}
                    />
                    <input
                      type="number"
                      placeholder="End time (seconds)"
                      value={textPopups.external.endTime}
                      onChange={(e) => setTextPopups(prev => ({
                        ...prev,
                        external: { ...prev.external, endTime: parseFloat(e.target.value) || 0 }
                      }))}
                      style={{
                        flex: 1,
                        padding: '5px 8px',
                        borderRadius: '4px',
                        border: `1px solid ${THEME.borderLight}`,
                        backgroundColor: THEME.bgMedium,
                        color: THEME.textLight,
                        fontSize: '0.8em'
                      }}
                    />
                  </div>
                  <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                    <button
                      onClick={() => setShowPopupPreview(prev => ({ ...prev, external: !prev.external }))}
                      disabled={!textPopups.external.title && !textPopups.external.body}
                      style={{
                        backgroundColor: showPopupPreview.external ? '#FF9800' : '#2196F3',
                        color: 'white',
                        border: 'none',
                        padding: '5px 10px',
                        borderRadius: '4px',
                        fontSize: '0.75em',
                        fontWeight: 'bold',
                        cursor: (!textPopups.external.title && !textPopups.external.body) ? 'not-allowed' : 'pointer'
                      }}
                    >
                      {showPopupPreview.external ? '👁️ Hide Preview' : '👁️ Show Preview'}
                    </button>
                    <button
                      onClick={() => saveTextPopup('external')}
                      disabled={savingTextPopup.external || (!textPopups.external.title && !textPopups.external.body)}
                      style={{
                        backgroundColor: savingTextPopup.external ? THEME.textMuted : '#4CAF50',
                        color: 'white',
                        border: 'none',
                        padding: '5px 10px',
                        borderRadius: '4px',
                        fontSize: '0.75em',
                        fontWeight: 'bold',
                        cursor: savingTextPopup.external || (!textPopups.external.title && !textPopups.external.body) ? 'not-allowed' : 'pointer'
                      }}
                    >
                      {savingTextPopup.external ? '💾 Saving...' : '💾 Save'}
                    </button>
                  </div>
                  {showPopupPreview.external && (
                    <div style={{
                      fontSize: '0.7em',
                      color: THEME.textMuted,
                      marginTop: '4px'
                    }}>
                      Position: ({textPopups.external.x}px, {textPopups.external.y}px) • Drag on video to reposition
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Veo3 Video Player */}
            {(renderedVideoUrls.external || stitchedVideoUrls.external || processedVeo3VideoUrls.external) ? (
              // Show rendered/stitched/processed single video
              <div style={{ marginTop: '12px' }}>
                <div style={{
                  color: THEME.accent,
                  fontWeight: 'bold',
                  fontSize: '0.85em',
                  marginBottom: '8px'
                }}>
                  🎬 {renderedVideoUrls.external ? 'Rendered Video' : `Veo3 Video${extensionCounts.external > 0 ? ` (Extended ${extensionCounts.external}x)` : ''}`}
                </div>

                <div style={{ position: 'relative', width: '100%', maxWidth: '900px' }}>
                  <VideoPlayerWithThumbnail
                    key={renderedVideoUrls.external || stitchedVideoUrls.external || processedVeo3VideoUrls.external}
                    videoUrl={renderedVideoUrls.external || stitchedVideoUrls.external || processedVeo3VideoUrls.external}
                    previewUrl={null}
                    THEME={THEME}
                    draggable="true"
                    onDragStart={(e) => {
                    const url = stitchedVideoUrls.external || processedVeo3VideoUrls.external;
                    const filename = url.split('/').pop().split('?')[0];
                    const videoData = {
                      url: url,
                      filename: filename,
                      title: `External Dialogue (Veo3) - ${action.player_name || user?.name || 'Player'}${extensionCounts.external > 0 ? ` (${(extensionCounts.external + 1) * 8}s extended)` : ''}`,
                      type: 'dialogue-external-veo3',
                      metadata: {
                        playerName: action.player_name || user?.name,
                        message: data.message,
                        dialogue: data.external,
                        model: user?.model,
                        isExtended: extensionCounts.external > 0,
                        extensionCount: extensionCounts.external
                      }
                    };
                    e.dataTransfer.setData('video-data', JSON.stringify(videoData));
                    e.dataTransfer.effectAllowed = 'copy';
                  }}
                  style={{
                    maxWidth: '600px',
                    cursor: 'grab',
                    border: showPopupPreview.external ? '2px dashed #FFD700' : 'none'
                  }}
                  />
                  {/* Draggable Text Pop-up Preview */}
                  {showPopupPreview.external && (textPopups.external.title || textPopups.external.body) && (
                    <div
                      onMouseDown={(e) => {
                        if (e.button !== 0) return; // Only left click
                        setDraggingPopup(prev => ({ ...prev, external: true }));

                        // Capture references before creating event handlers
                        const draggableElement = e.currentTarget;
                        const container = draggableElement.parentElement;
                        const rect = draggableElement.getBoundingClientRect();
                        const offsetX = e.clientX - rect.left;
                        const offsetY = e.clientY - rect.top;

                        const handleMouseMove = (moveEvent) => {
                          const containerRect = container.getBoundingClientRect();

                          let newX = moveEvent.clientX - containerRect.left - offsetX;
                          let newY = moveEvent.clientY - containerRect.top - offsetY;

                          // Scale coordinates from display size (600x337.5) to actual video size (1280x720)
                          const videoScale = 1280 / 600;
                          setTextPopups(prev => ({
                            ...prev,
                            external: { ...prev.external, x: Math.round(newX * videoScale), y: Math.round(newY * videoScale) }
                          }));
                        };

                        const handleMouseUp = () => {
                          setDraggingPopup(prev => ({ ...prev, external: false }));
                          document.removeEventListener('mousemove', handleMouseMove);
                          document.removeEventListener('mouseup', handleMouseUp);
                        };

                        document.addEventListener('mousemove', handleMouseMove);
                        document.addEventListener('mouseup', handleMouseUp);
                        e.preventDefault();
                      }}
                      style={{
                        position: 'absolute',
                        left: `${textPopups.external.x / (1280 / 600)}px`,
                        top: `${textPopups.external.y / (1280 / 600)}px`,
                        background: 'rgba(0, 0, 0, 0.85)',
                        border: '3px solid #FFD700',
                        borderRadius: '12px',
                        padding: '16px 20px',
                        maxWidth: '400px',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.8)',
                        cursor: draggingPopup.external ? 'grabbing' : 'grab',
                        zIndex: 10,
                        pointerEvents: 'auto',
                        userSelect: 'none',
                        touchAction: 'none',
                        transform: 'scale(0.469)',
                        transformOrigin: 'top left'
                      }}
                    >
                      <div style={{
                        color: '#FFD700',
                        fontSize: '22px',
                        fontWeight: 'bold',
                        textShadow: '2px 2px 4px rgba(0, 0, 0, 0.8)',
                        marginBottom: '8px',
                        wordWrap: 'break-word',
                        pointerEvents: 'none' // Prevent text selection during drag
                      }}>
                        {textPopups.external.title}
                      </div>
                      <div style={{
                        color: 'white',
                        fontSize: '18px',
                        fontWeight: '600',
                        textShadow: '2px 2px 4px rgba(0, 0, 0, 0.8)',
                        lineHeight: '1.4',
                        wordWrap: 'break-word',
                        pointerEvents: 'none' // Prevent text selection during drag
                      }}>
                        {textPopups.external.body}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              // Show multi-part video series with VideoSeriesPlayer
              <VideoSeriesPlayer
                key={veo3Videos.external.map(v => v.url).join('|')}
                videos={veo3Videos.external}
                videoType="external"
                title={`🎬 Veo3 Video${extensionCounts.external > 0 ? ` (Extended ${extensionCounts.external}x)` : ''}`}
                THEME={THEME}
                metadata={{
                  playerName: action.player_name || user?.name,
                  message: data.message,
                  dialogue: data.external,
                  model: user?.model,
                  isExtended: extensionCounts.external > 0,
                  extensionCount: extensionCounts.external
                }}
                showOverlay={showPopupPreview.external}
                isDragging={draggingPopup.external}
                overlayPosition={textPopups.external}
                overlayContent={
                  showPopupPreview.external && (textPopups.external.title || textPopups.external.body) && (
                    <div
                      onMouseDown={(e) => {
                        if (e.button !== 0) return; // Only left click
                        setDraggingPopup(prev => ({ ...prev, external: true }));

                        // Capture references before creating event handlers
                        const draggableElement = e.currentTarget;
                        const container = draggableElement.parentElement;
                        const rect = draggableElement.getBoundingClientRect();
                        const offsetX = e.clientX - rect.left;
                        const offsetY = e.clientY - rect.top;

                        const handleMouseMove = (moveEvent) => {
                          const containerRect = container.getBoundingClientRect();

                          let newX = moveEvent.clientX - containerRect.left - offsetX;
                          let newY = moveEvent.clientY - containerRect.top - offsetY;

                          // Scale coordinates from display size (600x337.5) to actual video size (1280x720)
                          const videoScale = 1280 / 600;
                          setTextPopups(prev => ({
                            ...prev,
                            external: { ...prev.external, x: Math.round(newX * videoScale), y: Math.round(newY * videoScale) }
                          }));
                        };

                        const handleMouseUp = () => {
                          setDraggingPopup(prev => ({ ...prev, external: false }));
                          document.removeEventListener('mousemove', handleMouseMove);
                          document.removeEventListener('mouseup', handleMouseUp);
                        };

                        document.addEventListener('mousemove', handleMouseMove);
                        document.addEventListener('mouseup', handleMouseUp);
                        e.preventDefault();
                      }}
                      style={{
                        position: 'absolute',
                        left: `${textPopups.external.x / (1280 / 600)}px`,
                        top: `${textPopups.external.y / (1280 / 600)}px`,
                        background: 'rgba(0, 0, 0, 0.85)',
                        border: '3px solid #FFD700',
                        borderRadius: '12px',
                        padding: '16px 20px',
                        maxWidth: '400px',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.8)',
                        cursor: draggingPopup.external ? 'grabbing' : 'grab',
                        zIndex: 10,
                        pointerEvents: 'auto',
                        userSelect: 'none',
                        touchAction: 'none',
                        transform: 'scale(0.469)',
                        transformOrigin: 'top left'
                      }}
                    >
                      {textPopups.external.title && (
                        <div style={{
                          color: '#FFD700',
                          fontSize: '1.1em',
                          fontWeight: 'bold',
                          marginBottom: '8px',
                          textShadow: '1px 1px 2px rgba(0, 0, 0, 0.8)',
                          pointerEvents: 'none'
                        }}>
                          {textPopups.external.title}
                        </div>
                      )}
                      <div style={{
                        color: '#FFFFFF',
                        fontSize: '0.95em',
                        lineHeight: '1.4',
                        wordWrap: 'break-word',
                        pointerEvents: 'none'
                      }}>
                        {textPopups.external.body}
                      </div>
                    </div>
                  )
                }
              />
            )}
            {/* Voiceover Video Player */}
            {(processedVoiceoverVideoUrls.external || voiceoverVideoUrls.external) && (
              <div style={{ marginTop: '12px' }}>
                <div style={{
                  color: '#4CAF50',
                  fontWeight: 'bold',
                  fontSize: '0.85em',
                  marginBottom: '8px'
                }}>
                  🎙️ Voiceover Video (Static Image + TTS)
                </div>
                <div style={{
                  position: 'relative',
                  width: '100%',
                  maxWidth: '900px'
                }}>
                  <video
                    key={processedVoiceoverVideoUrls.external || voiceoverVideoUrls.external}
                    controls
                    draggable="true"
                    onDragStart={(e) => {
                      const url = processedVoiceoverVideoUrls.external || voiceoverVideoUrls.external;
                      const filename = url.split('/').pop().split('?')[0];
                      const videoData = {
                        url: url,
                        filename: filename,
                        title: `External Dialogue (Voiceover) - ${action.player_name || user?.name || 'Player'}`,
                        type: 'dialogue-external-voiceover',
                        metadata: {
                          playerName: action.player_name || user?.name,
                          message: data.message,
                          dialogue: data.external,
                          model: user?.model
                        }
                      };
                      e.dataTransfer.setData('video-data', JSON.stringify(videoData));
                      e.dataTransfer.effectAllowed = 'copy';
                    }}
                    src={processedVoiceoverVideoUrls.external || voiceoverVideoUrls.external}
                    style={{
                      width: '100%',
                      maxWidth: '600px',
                      borderRadius: '4px',
                      border: showPopupPreview.external ? '2px dashed #FFD700' : `1px solid ${THEME.borderLight}`,
                      cursor: 'grab'
                    }}
                  />
                  {/* Draggable Text Pop-up Preview - Smooth Mouse-based Dragging */}
                  {showPopupPreview.external && (textPopups.external.title || textPopups.external.body) && (
                    <div
                      onMouseDown={(e) => {
                        if (e.button !== 0) return;
                        setDraggingPopup(prev => ({ ...prev, external: true }));

                        // Capture references before creating event handlers
                        const draggableElement = e.currentTarget;
                        const container = draggableElement.parentElement;
                        const rect = draggableElement.getBoundingClientRect();
                        const offsetX = e.clientX - rect.left;
                        const offsetY = e.clientY - rect.top;

                        const handleMouseMove = (moveEvent) => {
                          const containerRect = container.getBoundingClientRect();

                          let newX = moveEvent.clientX - containerRect.left - offsetX;
                          let newY = moveEvent.clientY - containerRect.top - offsetY;

                          // Scale coordinates from display size to actual video size
                          const videoScale = 1280 / 600;
                          setTextPopups(prev => ({
                            ...prev,
                            external: { ...prev.external, x: Math.round(newX * videoScale), y: Math.round(newY * videoScale) }
                          }));
                        };

                        const handleMouseUp = () => {
                          setDraggingPopup(prev => ({ ...prev, external: false }));
                          document.removeEventListener('mousemove', handleMouseMove);
                          document.removeEventListener('mouseup', handleMouseUp);
                        };

                        document.addEventListener('mousemove', handleMouseMove);
                        document.addEventListener('mouseup', handleMouseUp);
                        e.preventDefault();
                      }}
                      style={{
                        position: 'absolute',
                        left: `${textPopups.external.x / (1280 / 600)}px`,
                        top: `${textPopups.external.y / (1280 / 600)}px`,
                        background: 'rgba(0, 0, 0, 0.85)',
                        border: '3px solid #FFD700',
                        borderRadius: '12px',
                        padding: '16px 20px',
                        maxWidth: '400px',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.8)',
                        cursor: draggingPopup.external ? 'grabbing' : 'grab',
                        zIndex: 10,
                        pointerEvents: 'auto',
                        userSelect: 'none',
                        touchAction: 'none',
                        transform: 'scale(0.469)',
                        transformOrigin: 'top left'
                      }}
                    >
                      <div style={{
                        color: '#FFD700',
                        fontSize: '22px',
                        fontWeight: 'bold',
                        textShadow: '2px 2px 4px rgba(0, 0, 0, 0.8)',
                        marginBottom: '8px',
                        wordWrap: 'break-word',
                        pointerEvents: 'none'
                      }}>
                        {textPopups.external.title}
                      </div>
                      <div style={{
                        color: 'white',
                        fontSize: '18px',
                        fontWeight: '600',
                        textShadow: '2px 2px 4px rgba(0, 0, 0, 0.8)',
                        lineHeight: '1.4',
                        wordWrap: 'break-word',
                        pointerEvents: 'none'
                      }}>
                        {textPopups.external.body}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
            {audioUrls.external && (
              <div style={{ marginTop: '12px' }}>
                <div style={{
                  color: '#FF6B6B',
                  fontWeight: 'bold',
                  fontSize: '0.85em',
                  marginBottom: '8px'
                }}>
                  🎙️ TTS Audio
                </div>
                <audio
                  controls
                  src={audioUrls.external}
                  style={{
                    width: '100%',
                    maxWidth: '600px',
                    borderRadius: '4px'
                  }}
                />
              </div>
            )}
            {sceneImageUrls.external && (
              <div style={{ marginTop: '12px' }}>
                {/* Check if this is a sub-conversation with both base and glow images */}
                {data.external_scene_image_base_url ? (
                  <div>
                    <div style={{
                      color: '#FF9800',
                      fontWeight: 'bold',
                      fontSize: '0.85em',
                      marginBottom: '8px'
                    }}>
                      🎨 Dialogue Image Variants
                    </div>
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                      gap: '12px'
                    }}>
                      {/* Base Image (No Border, No Dim) */}
                      <div>
                        <div style={{
                          fontSize: '0.7em',
                          color: THEME.textMuted,
                          marginBottom: '6px',
                          fontWeight: 'bold'
                        }}>
                          1. Base (No Border)
                        </div>
                        <img
                          src={data.external_scene_image_base_url}
                          alt="Base Scene"
                          style={{
                            width: '100%',
                            borderRadius: '4px',
                            border: `2px solid ${THEME.borderLight}`,
                            cursor: 'pointer'
                          }}
                          onClick={() => window.open(data.external_scene_image_base_url, '_blank')}
                        />
                      </div>
                      {/* TEMPORARILY DISABLED: Golden Border Image (Speaker Highlighted + Dim) */}
                      {/* <div>
                        <div style={{
                          fontSize: '0.7em',
                          color: '#FFD700',
                          marginBottom: '6px',
                          fontWeight: 'bold'
                        }}>
                          2. Speaker (Border + Dim)
                        </div>
                        <img
                          src={sceneImageUrls.external}
                          alt="Speaker Scene"
                          style={{
                            width: '100%',
                            borderRadius: '4px',
                            border: `2px solid #FFD700`,
                            cursor: 'pointer'
                          }}
                          onClick={() => window.open(sceneImageUrls.external, '_blank')}
                        />
                      </div> */}
                      {/* TEMPORARILY DISABLED: Golden Border (No Dim) Image */}
                      {/* {data.external_scene_image_border_nodim_url && (
                        <div>
                          <div style={{
                            fontSize: '0.7em',
                            color: '#FFB700',
                            marginBottom: '6px',
                            fontWeight: 'bold'
                          }}>
                            3. Border (No Dim)
                          </div>
                          <img
                            src={data.external_scene_image_border_nodim_url}
                            alt="Border No Dim"
                            style={{
                              width: '100%',
                              borderRadius: '4px',
                              border: `2px solid #FFB700`,
                              cursor: 'pointer'
                            }}
                            onClick={() => window.open(data.external_scene_image_border_nodim_url, '_blank')}
                          />
                        </div>
                      )} */}
                      {/* TEMPORARILY DISABLED: Bounding Box Image */}
                      {/* {data.external_scene_image_bbox_url && (
                        <div>
                          <div style={{
                            fontSize: '0.7em',
                            color: '#FFA500',
                            marginBottom: '6px',
                            fontWeight: 'bold'
                          }}>
                            4. Bounding Box
                          </div>
                          <img
                            src={data.external_scene_image_bbox_url}
                            alt="Bounding Box"
                            style={{
                              width: '100%',
                              borderRadius: '4px',
                              border: `2px solid #FFA500`,
                              cursor: 'pointer'
                            }}
                            onClick={() => window.open(data.external_scene_image_bbox_url, '_blank')}
                          />
                        </div>
                      )} */}
                      {/* Background Only (Clean, Speaker Removed) */}
                      {data.external_scene_image_background_url && (
                        <div>
                          <div style={{
                            fontSize: '0.7em',
                            color: '#9E9E9E',
                            marginBottom: '6px',
                            fontWeight: 'bold'
                          }}>
                            5. Background (Clean)
                          </div>
                          <img
                            src={data.external_scene_image_background_url}
                            alt="Background Only"
                            style={{
                              width: '100%',
                              borderRadius: '4px',
                              border: `2px solid #9E9E9E`,
                              cursor: 'pointer'
                            }}
                            onClick={() => window.open(data.external_scene_image_background_url, '_blank')}
                          />
                        </div>
                      )}
                      {/* Character Only (On White, With Infill) */}
                      {data.external_scene_image_character_url && (
                        <div>
                          <div style={{
                            fontSize: '0.7em',
                            color: '#4CAF50',
                            marginBottom: '6px',
                            fontWeight: 'bold'
                          }}>
                            6. Character (Isolated)
                          </div>
                          <img
                            src={data.external_scene_image_character_url}
                            alt="Character Only"
                            style={{
                              width: '100%',
                              borderRadius: '4px',
                              border: `2px solid #4CAF50`,
                              cursor: 'pointer'
                            }}
                            onClick={() => window.open(data.external_scene_image_character_url, '_blank')}
                          />
                        </div>
                      )}
                      {/* Character Bounding Box */}
                      {data.external_scene_image_character_bbox_url && (
                        <div>
                          <div style={{
                            fontSize: '0.7em',
                            color: '#2196F3',
                            marginBottom: '6px',
                            fontWeight: 'bold'
                          }}>
                            7. Character BBox
                          </div>
                          <img
                            src={data.external_scene_image_character_bbox_url}
                            alt="Character Bounding Box"
                            style={{
                              width: '100%',
                              borderRadius: '4px',
                              border: `2px solid #2196F3`,
                              cursor: 'pointer'
                            }}
                            onClick={() => window.open(data.external_scene_image_character_bbox_url, '_blank')}
                          />
                        </div>
                      )}
                      {/* Character Transparent */}
                      {data.external_scene_image_character_transparent_url && (
                        <div>
                          <div style={{
                            fontSize: '0.7em',
                            color: '#FF5722',
                            marginBottom: '6px',
                            fontWeight: 'bold'
                          }}>
                            8. Character (Transparent)
                          </div>
                          <img
                            src={data.external_scene_image_character_transparent_url}
                            alt="Character Transparent"
                            style={{
                              width: '100%',
                              borderRadius: '4px',
                              border: `2px solid #FF5722`,
                              cursor: 'pointer',
                              backgroundColor: '#f5f5f5'
                            }}
                            onClick={() => window.open(data.external_scene_image_character_transparent_url, '_blank')}
                          />
                        </div>
                      )}
                      {/* Character Transparent with Golden Border */}
                      {data.external_scene_image_character_transparent_border_url && (
                        <div>
                          <div style={{
                            fontSize: '0.7em',
                            color: '#FFD700',
                            marginBottom: '6px',
                            fontWeight: 'bold'
                          }}>
                            9. Character (Golden Border)
                          </div>
                          <img
                            src={data.external_scene_image_character_transparent_border_url}
                            alt="Character Golden Border"
                            style={{
                              width: '100%',
                              borderRadius: '4px',
                              border: `2px solid #FFD700`,
                              cursor: 'pointer',
                              backgroundColor: '#f5f5f5'
                            }}
                            onClick={() => window.open(data.external_scene_image_character_transparent_border_url, '_blank')}
                          />
                        </div>
                      )}
                      {/* Sub-Conversation Participants Only */}
                      {data.external_scene_image_subconvo_url && (
                        <div>
                          <div style={{
                            fontSize: '0.7em',
                            color: '#9C27B0',
                            marginBottom: '6px',
                            fontWeight: 'bold'
                          }}>
                            10. Sub-Conversation
                          </div>
                          <img
                            src={data.external_scene_image_subconvo_url}
                            alt="Sub-Conversation Participants"
                            style={{
                              width: '100%',
                              borderRadius: '4px',
                              border: `2px solid #9C27B0`,
                              cursor: 'pointer'
                            }}
                            onClick={() => window.open(data.external_scene_image_subconvo_url, '_blank')}
                          />
                        </div>
                      )}
                      {/* Sub-Conversation Bounding Box */}
                      {data.external_scene_image_subconvo_bbox_url && (
                        <div>
                          <div style={{
                            fontSize: '0.7em',
                            color: '#00BCD4',
                            marginBottom: '6px',
                            fontWeight: 'bold'
                          }}>
                            11. Sub-Conversation BBox
                          </div>
                          <img
                            src={data.external_scene_image_subconvo_bbox_url}
                            alt="Sub-Conversation Bounding Box"
                            style={{
                              width: '100%',
                              borderRadius: '4px',
                              border: `2px solid #00BCD4`,
                              cursor: 'pointer'
                            }}
                            onClick={() => window.open(data.external_scene_image_subconvo_bbox_url, '_blank')}
                          />
                        </div>
                      )}
                      {/* Sub-Conversation Transparent */}
                      {data.external_scene_image_subconvo_transparent_url && (
                        <div>
                          <div style={{
                            fontSize: '0.7em',
                            color: '#E91E63',
                            marginBottom: '6px',
                            fontWeight: 'bold'
                          }}>
                            12. Sub-Conversation (Transparent)
                          </div>
                          <img
                            src={data.external_scene_image_subconvo_transparent_url}
                            alt="Sub-Conversation Transparent"
                            style={{
                              width: '100%',
                              borderRadius: '4px',
                              border: `2px solid #E91E63`,
                              cursor: 'pointer',
                              backgroundColor: '#f5f5f5'
                            }}
                            onClick={() => window.open(data.external_scene_image_subconvo_transparent_url, '_blank')}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  /* Regular single image display */
                  <div>
                    <div style={{
                      color: '#FF9800',
                      fontWeight: 'bold',
                      fontSize: '0.85em',
                      marginBottom: '8px'
                    }}>
                      🎨 Dialogue Scene Image
                    </div>
                    <img
                      src={sceneImageUrls.external}
                      alt="Dialogue Scene"
                      style={{
                        width: '100%',
                        maxWidth: '600px',
                        borderRadius: '4px',
                        border: `2px solid #FF9800`,
                        cursor: 'pointer'
                      }}
                      onClick={() => window.open(sceneImageUrls.external, '_blank')}
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {data.diary_room && gameType !== 'chat-game' && (
          <div style={{
            marginTop: '12px',
            padding: '12px',
            backgroundColor: `${THEME.bgDark}80`,
            borderRadius: '4px',
            border: `1px solid ${THEME.borderLight}`
          }}>
            {/* Hide video generation buttons for chat games unless explicitly enabled */}
            {true && (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                marginBottom: '8px'
              }}>
                <span style={{
                  color: THEME.textMuted,
                  fontSize: '0.85em',
                  fontWeight: 'bold'
                }}>
                  📜 Diary Room Video
                </span>
                <button
                  onClick={() => previewDialogueVideo('diary_room')}
                  disabled={previewingVideos.diary_room}
                  style={{
                    backgroundColor: previewingVideos.diary_room ? THEME.textMuted : THEME.secondary,
                    color: THEME.textLight,
                    border: 'none',
                    padding: '4px 10px',
                    borderRadius: '4px',
                    fontSize: '0.8em',
                    fontWeight: 'bold',
                    cursor: previewingVideos.diary_room ? 'not-allowed' : 'pointer',
                    transition: 'background-color 0.2s'
                  }}
                >
                  {previewingVideos.diary_room ? '⏳ Loading...' : '👁️ Preview Video'}
                </button>
                <button
                  onClick={() => previewReferenceImagePrompt('diary_room')}
                  disabled={previewingVideos.diary_room_image}
                  style={{
                    backgroundColor: previewingVideos.diary_room_image ? THEME.textMuted : '#9C27B0',
                    color: THEME.textLight,
                    border: 'none',
                    padding: '4px 10px',
                    borderRadius: '4px',
                    fontSize: '0.8em',
                    fontWeight: 'bold',
                    cursor: previewingVideos.diary_room_image ? 'not-allowed' : 'pointer',
                    transition: 'background-color 0.2s'
                  }}
                >
                  {previewingVideos.diary_room_image ? '⏳ Loading...' : '🖼️ Preview Image'}
                </button>
                <button
                  onClick={() => generateDialogueVideo('diary_room')}
                  disabled={generatingVideos.diary_room}
                  style={{
                    backgroundColor: generatingVideos.diary_room ? THEME.textMuted : THEME.accent,
                    color: THEME.textDark,
                    border: 'none',
                    padding: '4px 10px',
                    borderRadius: '4px',
                    fontSize: '0.8em',
                    fontWeight: 'bold',
                    cursor: generatingVideos.diary_room ? 'not-allowed' : 'pointer',
                    transition: 'background-color 0.2s'
                  }}
                >
                  {generatingVideos.diary_room ? '⏳ Generating...' : '🎬 Generate'}
                </button>
                <button
                  onClick={() => generateVoiceoverVideo('diary_room')}
                  disabled={generatingVideos.diary_room}
                  style={{
                    backgroundColor: generatingVideos.diary_room ? THEME.textMuted : '#4CAF50',
                    color: THEME.textLight,
                    border: 'none',
                    padding: '4px 10px',
                    borderRadius: '4px',
                    fontSize: '0.8em',
                    fontWeight: 'bold',
                    cursor: generatingVideos.diary_room ? 'not-allowed' : 'pointer',
                    transition: 'background-color 0.2s'
                  }}
                >
                  {generatingVideos.diary_room ? '⏳ Generating...' : '🎙️ Voiceover'}
                </button>
                {((veo3Videos.diary_room?.length > 0) || voiceoverVideoUrls.diary_room) && (
                  <button
                    onClick={() => processDialogueVideo('diary_room')}
                    disabled={processingVideos.diary_room}
                    style={{
                      backgroundColor: processingVideos.diary_room ? THEME.textMuted : THEME.secondary,
                      color: THEME.textLight,
                      border: 'none',
                      padding: '4px 10px',
                      borderRadius: '4px',
                      fontSize: '0.8em',
                      fontWeight: 'bold',
                      cursor: processingVideos.diary_room ? 'not-allowed' : 'pointer',
                      transition: 'background-color 0.2s'
                    }}
                  >
                    {processingVideos.diary_room ? '⏳ Processing...' : '✨ Process'}
                  </button>
                )}
                {((veo3Videos.diary_room?.length > 0) || stitchedVideoUrls.diary_room) && (
                  <button
                    onClick={() => extendDialogueVideo('diary_room', 7)}
                    disabled={extendingVideos.diary_room}
                    style={{
                      backgroundColor: extendingVideos.diary_room ? THEME.textMuted : '#9C27B0',
                      color: THEME.textLight,
                      border: 'none',
                      padding: '4px 8px',
                      borderRadius: '4px',
                      fontSize: '0.8em',
                      fontWeight: 'bold',
                      cursor: extendingVideos.diary_room ? 'not-allowed' : 'pointer',
                      transition: 'background-color 0.2s'
                    }}
                  >
                    {extendingVideos.diary_room ? '⏳ Extending...' : '+7s'}
                  </button>
                )}
                <button
                  onClick={() => generateDialogueTTS('diary_room')}
                  disabled={generatingTTS.diary_room}
                  style={{
                    backgroundColor: generatingTTS.diary_room ? THEME.textMuted : '#FF6B6B',
                    color: THEME.textLight,
                    border: 'none',
                    padding: '4px 10px',
                    borderRadius: '4px',
                    fontSize: '0.8em',
                    fontWeight: 'bold',
                    cursor: generatingTTS.diary_room ? 'not-allowed' : 'pointer',
                    transition: 'background-color 0.2s'
                  }}
                >
                  {generatingTTS.diary_room ? '⏳ Generating...' : '🎙️ TTS'}
                </button>
                {action.game_instance_player_id && action.game_instance_id && (
                  <button
                    onClick={() => clonePlayerVoice(action.game_instance_player_id, data.sender_name || 'Player', action.game_instance_id)}
                    style={{
                      backgroundColor: '#9C27B0',
                      color: THEME.textLight,
                      border: 'none',
                      padding: '4px 10px',
                      borderRadius: '4px',
                      fontSize: '0.8em',
                      fontWeight: 'bold',
                      cursor: 'pointer',
                      transition: 'background-color 0.2s'
                    }}
                    title="Clone this player's voice from their audio samples (requires 3+ samples)"
                  >
                    🎤 Clone Voice
                  </button>
                )}
                <button
                  onClick={() => generateDialogueSceneImage('diary_room', false)}
                  disabled={generatingSceneImages.diary_room}
                  style={{
                    backgroundColor: generatingSceneImages.diary_room ? THEME.textMuted : '#FF9800',
                    color: THEME.textLight,
                    border: 'none',
                    padding: '4px 10px',
                    borderRadius: '4px',
                    fontSize: '0.8em',
                    fontWeight: 'bold',
                    cursor: generatingSceneImages.diary_room ? 'not-allowed' : 'pointer',
                    transition: 'background-color 0.2s'
                  }}
                >
                  {generatingSceneImages.diary_room ? '🎨 Generating...' : '🎨 Main Image'}
                </button>
                <button
                  onClick={() => generateDialogueSceneImage('diary_room', true)}
                  disabled={generatingSceneImages.diary_room || !data.diary_room_scene_image_base_url}
                  style={{
                    backgroundColor: generatingSceneImages.diary_room || !data.diary_room_scene_image_base_url ? THEME.textMuted : '#9C27B0',
                    color: THEME.textLight,
                    border: 'none',
                    padding: '4px 10px',
                    borderRadius: '4px',
                    fontSize: '0.8em',
                    fontWeight: 'bold',
                    cursor: generatingSceneImages.diary_room || !data.diary_room_scene_image_base_url ? 'not-allowed' : 'pointer',
                    transition: 'background-color 0.2s'
                  }}
                  title={!data.diary_room_scene_image_base_url ? 'Generate main image first' : 'Generate variants from main image'}
                >
                  {generatingSceneImages.diary_room ? '✨ Generating...' : '✨ Variants'}
                </button>
                <button
                  onClick={() => generateDialogueBoundingBoxes('diary_room')}
                  disabled={generatingBoundingBoxes.diary_room || !data.diary_room_scene_image_character_url}
                  style={{
                    backgroundColor: generatingBoundingBoxes.diary_room || !data.diary_room_scene_image_character_url ? THEME.textMuted : '#2196F3',
                    color: THEME.textLight,
                    border: 'none',
                    padding: '4px 10px',
                    borderRadius: '4px',
                    fontSize: '0.8em',
                    fontWeight: 'bold',
                    cursor: generatingBoundingBoxes.diary_room || !data.diary_room_scene_image_character_url ? 'not-allowed' : 'pointer',
                    transition: 'background-color 0.2s'
                  }}
                  title={!data.diary_room_scene_image_character_url ? 'Generate variants first' : 'Generate bounding boxes on isolated images'}
                >
                  {generatingBoundingBoxes.diary_room ? '📦 Generating...' : '📦 Bounding Boxes'}
                </button>
              </div>
            )}

            {/* Text Pop-up Input for Diary Room Veo3 - Always visible when there are videos */}
            {(veo3Videos.diary_room?.length > 0 || stitchedVideoUrls.diary_room || processedVeo3VideoUrls.diary_room) && (
              <div style={{
                marginTop: '12px',
                marginBottom: '12px',
                padding: '10px',
                backgroundColor: THEME.bgLight,
                borderRadius: '6px',
                border: `1px solid ${THEME.borderDark}`
              }}>
                <div style={{
                  color: THEME.textLight,
                  fontWeight: 'bold',
                  fontSize: '0.8em',
                  marginBottom: '6px'
                }}>
                  💬 Text Pop-up for Diary Room
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  <input
                    type="text"
                    placeholder="Title (e.g., 'Week 3')"
                    value={textPopups.diary_room.title}
                    onChange={(e) => setTextPopups(prev => ({
                      ...prev,
                      diary_room: { ...prev.diary_room, title: e.target.value }
                    }))}
                    style={{
                      padding: '5px 8px',
                      borderRadius: '4px',
                      border: `1px solid ${THEME.borderLight}`,
                      backgroundColor: THEME.bgMedium,
                      color: THEME.textLight,
                      fontSize: '0.8em'
                    }}
                  />
                  <textarea
                    placeholder="Body text (e.g., 'Nominations Ceremony')"
                    value={textPopups.diary_room.body}
                    onChange={(e) => setTextPopups(prev => ({
                      ...prev,
                      diary_room: { ...prev.diary_room, body: e.target.value }
                    }))}
                    style={{
                      padding: '5px 8px',
                      borderRadius: '4px',
                      border: `1px solid ${THEME.borderLight}`,
                      backgroundColor: THEME.bgMedium,
                      color: THEME.textLight,
                      fontSize: '0.8em',
                      minHeight: '50px',
                      resize: 'vertical',
                      fontFamily: 'inherit'
                    }}
                  />
                  <div style={{ display: 'flex', gap: '6px' }}>
                    <input
                      type="number"
                      placeholder="Start time (seconds)"
                      value={textPopups.diary_room.startTime}
                      onChange={(e) => setTextPopups(prev => ({
                        ...prev,
                        diary_room: { ...prev.diary_room, startTime: parseFloat(e.target.value) || 0 }
                      }))}
                      style={{
                        flex: 1,
                        padding: '5px 8px',
                        borderRadius: '4px',
                        border: `1px solid ${THEME.borderLight}`,
                        backgroundColor: THEME.bgMedium,
                        color: THEME.textLight,
                        fontSize: '0.8em'
                      }}
                    />
                    <input
                      type="number"
                      placeholder="End time (seconds)"
                      value={textPopups.diary_room.endTime}
                      onChange={(e) => setTextPopups(prev => ({
                        ...prev,
                        diary_room: { ...prev.diary_room, endTime: parseFloat(e.target.value) || 0 }
                      }))}
                      style={{
                        flex: 1,
                        padding: '5px 8px',
                        borderRadius: '4px',
                        border: `1px solid ${THEME.borderLight}`,
                        backgroundColor: THEME.bgMedium,
                        color: THEME.textLight,
                        fontSize: '0.8em'
                      }}
                    />
                  </div>
                  <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                    <button
                      onClick={() => setShowPopupPreview(prev => ({ ...prev, diary_room: !prev.diary_room }))}
                      disabled={!textPopups.diary_room.title && !textPopups.diary_room.body}
                      style={{
                        backgroundColor: showPopupPreview.diary_room ? '#FF9800' : '#2196F3',
                        color: 'white',
                        border: 'none',
                        padding: '5px 10px',
                        borderRadius: '4px',
                        fontSize: '0.75em',
                        fontWeight: 'bold',
                        cursor: (!textPopups.diary_room.title && !textPopups.diary_room.body) ? 'not-allowed' : 'pointer'
                      }}
                    >
                      {showPopupPreview.diary_room ? '👁️ Hide Preview' : '👁️ Show Preview'}
                    </button>
                    <button
                      onClick={() => saveTextPopup('diary_room')}
                      disabled={savingTextPopup.diary_room || (!textPopups.diary_room.title && !textPopups.diary_room.body)}
                      style={{
                        backgroundColor: savingTextPopup.diary_room ? THEME.textMuted : '#4CAF50',
                        color: 'white',
                        border: 'none',
                        padding: '5px 10px',
                        borderRadius: '4px',
                        fontSize: '0.75em',
                        fontWeight: 'bold',
                        cursor: savingTextPopup.diary_room || (!textPopups.diary_room.title && !textPopups.diary_room.body) ? 'not-allowed' : 'pointer'
                      }}
                    >
                      {savingTextPopup.diary_room ? '💾 Saving...' : '💾 Save'}
                    </button>
                  </div>
                  {showPopupPreview.diary_room && (
                    <div style={{
                      fontSize: '0.7em',
                      color: THEME.textMuted,
                      marginTop: '4px'
                    }}>
                      Position: ({textPopups.diary_room.x}px, {textPopups.diary_room.y}px) • Drag on video to reposition
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Veo3 Video Player */}
            {(renderedVideoUrls.diary_room || stitchedVideoUrls.diary_room || processedVeo3VideoUrls.diary_room) ? (
              // Show rendered/stitched/processed single video
              <div style={{ marginTop: '12px' }}>
                <div style={{
                  color: THEME.accent,
                  fontWeight: 'bold',
                  fontSize: '0.85em',
                  marginBottom: '8px'
                }}>
                  🎬 {renderedVideoUrls.diary_room ? 'Rendered Video' : `Veo3 Video${extensionCounts.diary_room > 0 ? ` (Extended ${extensionCounts.diary_room}x)` : ''}`}
                </div>
                <div style={{
                  position: 'relative',
                  width: '100%',
                  maxWidth: '600px',
                  border: showPopupPreview.diary_room ? '2px dashed #FFD700' : 'none',
                  overflow: 'visible' // Allow popup to overflow beyond container bounds
                }}>
                  <VideoPlayerWithThumbnail
                    key={renderedVideoUrls.diary_room || stitchedVideoUrls.diary_room || processedVeo3VideoUrls.diary_room}
                    videoUrl={renderedVideoUrls.diary_room || stitchedVideoUrls.diary_room || processedVeo3VideoUrls.diary_room}
                    previewUrl={null}
                    THEME={THEME}
                    draggable="true"
                    onDragStart={(e) => {
                      const url = stitchedVideoUrls.diary_room || processedVeo3VideoUrls.diary_room;
                      const filename = url.split('/').pop().split('?')[0];
                      const videoData = {
                        url: url,
                        filename: filename,
                        title: `Diary Room (Veo3) - ${action.player_name || user?.name || 'Player'}${extensionCounts.diary_room > 0 ? ` (${(extensionCounts.diary_room + 1) * 8}s extended)` : ''}`,
                        type: 'dialogue-diary-room-veo3',
                        metadata: {
                          playerName: action.player_name || user?.name,
                          message: data.message,
                          dialogue: data.diary_room,
                          model: user?.model,
                          isExtended: extensionCounts.diary_room > 0,
                          extensionCount: extensionCounts.diary_room
                        }
                      };
                      e.dataTransfer.setData('video-data', JSON.stringify(videoData));
                      e.dataTransfer.effectAllowed = 'copy';
                    }}
                    style={{
                      width: '100%',
                      cursor: 'grab'
                    }}
                  />
                  {/* Draggable Text Pop-up Preview */}
                  {showPopupPreview.diary_room && (textPopups.diary_room.title || textPopups.diary_room.body) && (
                    <div
                      onMouseDown={(e) => {
                        if (e.button !== 0) return;
                        setDraggingPopup(prev => ({ ...prev, diary_room: true }));

                        // Capture references before creating event handlers
                        const draggableElement = e.currentTarget;
                        const container = draggableElement.parentElement;
                        const rect = draggableElement.getBoundingClientRect();
                        const offsetX = e.clientX - rect.left;
                        const offsetY = e.clientY - rect.top;

                        const handleMouseMove = (moveEvent) => {
                          const containerRect = container.getBoundingClientRect();

                          let newX = moveEvent.clientX - containerRect.left - offsetX;
                          let newY = moveEvent.clientY - containerRect.top - offsetY;

                          // Scale coordinates from display size to actual video size
                          const videoScale = 1280 / 600;
                          setTextPopups(prev => ({
                            ...prev,
                            diary_room: { ...prev.diary_room, x: Math.round(newX * videoScale), y: Math.round(newY * videoScale) }
                          }));
                        };

                        const handleMouseUp = () => {
                          setDraggingPopup(prev => ({ ...prev, diary_room: false }));
                          document.removeEventListener('mousemove', handleMouseMove);
                          document.removeEventListener('mouseup', handleMouseUp);
                        };

                        document.addEventListener('mousemove', handleMouseMove);
                        document.addEventListener('mouseup', handleMouseUp);
                        e.preventDefault();
                      }}
                      style={{
                        position: 'absolute',
                        left: `${textPopups.diary_room.x / (1280 / 600)}px`,
                        top: `${textPopups.diary_room.y / (1280 / 600)}px`,
                        background: 'rgba(0, 0, 0, 0.85)',
                        border: '3px solid #FFD700',
                        borderRadius: '12px',
                        padding: '16px 20px',
                        maxWidth: '400px',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.8)',
                        cursor: draggingPopup.diary_room ? 'grabbing' : 'grab',
                        zIndex: 10,
                        pointerEvents: 'auto',
                        userSelect: 'none',
                        touchAction: 'none',
                        transform: 'scale(0.469)',
                        transformOrigin: 'top left'
                      }}
                    >
                      <div style={{
                        color: '#FFD700',
                        fontSize: '22px',
                        fontWeight: 'bold',
                        textShadow: '2px 2px 4px rgba(0, 0, 0, 0.8)',
                        marginBottom: '8px',
                        wordWrap: 'break-word'
                      }}>
                        {textPopups.diary_room.title}
                      </div>
                      <div style={{
                        color: 'white',
                        fontSize: '18px',
                        fontWeight: '600',
                        textShadow: '2px 2px 4px rgba(0, 0, 0, 0.8)',
                        lineHeight: '1.4',
                        wordWrap: 'break-word'
                      }}>
                        {textPopups.diary_room.body}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              // Show multi-part video series with VideoSeriesPlayer
              <VideoSeriesPlayer
                key={veo3Videos.diary_room.map(v => v.url).join('|')}
                videos={veo3Videos.diary_room}
                videoType="diary_room"
                title={`🎬 Veo3 Video${extensionCounts.diary_room > 0 ? ` (Extended ${extensionCounts.diary_room}x)` : ''}`}
                THEME={THEME}
                metadata={{
                  playerName: action.player_name || user?.name,
                  message: data.message,
                  dialogue: data.diary_room,
                  model: user?.model,
                  isExtended: extensionCounts.diary_room > 0,
                  extensionCount: extensionCounts.diary_room
                }}
                showOverlay={showPopupPreview.diary_room}
                isDragging={draggingPopup.diary_room}
                overlayPosition={textPopups.diary_room}
                overlayContent={
                  showPopupPreview.diary_room && (textPopups.diary_room.title || textPopups.diary_room.body) && (
                    <div
                      onMouseDown={(e) => {
                        if (e.button !== 0) return;
                        setDraggingPopup(prev => ({ ...prev, diary_room: true }));

                        const draggableElement = e.currentTarget;
                        const container = draggableElement.parentElement;
                        const rect = draggableElement.getBoundingClientRect();
                        const offsetX = e.clientX - rect.left;
                        const offsetY = e.clientY - rect.top;

                        const handleMouseMove = (moveEvent) => {
                          const containerRect = container.getBoundingClientRect();

                          let newX = moveEvent.clientX - containerRect.left - offsetX;
                          let newY = moveEvent.clientY - containerRect.top - offsetY;

                          // Scale coordinates from display size (600x337.5) to actual video size (1280x720)
                          const videoScale = 1280 / 600;
                          setTextPopups(prev => ({
                            ...prev,
                            diary_room: { ...prev.diary_room, x: Math.round(newX * videoScale), y: Math.round(newY * videoScale) }
                          }));
                        };

                        const handleMouseUp = () => {
                          setDraggingPopup(prev => ({ ...prev, diary_room: false }));
                          document.removeEventListener('mousemove', handleMouseMove);
                          document.removeEventListener('mouseup', handleMouseUp);
                        };

                        document.addEventListener('mousemove', handleMouseMove);
                        document.addEventListener('mouseup', handleMouseUp);
                        e.preventDefault();
                      }}
                      style={{
                        position: 'absolute',
                        left: `${textPopups.diary_room.x / (1280 / 600)}px`,
                        top: `${textPopups.diary_room.y / (1280 / 600)}px`,
                        background: 'rgba(0, 0, 0, 0.85)',
                        border: '3px solid #FFD700',
                        borderRadius: '12px',
                        padding: '16px 20px',
                        maxWidth: '400px',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.8)',
                        cursor: draggingPopup.diary_room ? 'grabbing' : 'grab',
                        zIndex: 10,
                        pointerEvents: 'auto',
                        userSelect: 'none',
                        touchAction: 'none',
                        transform: 'scale(0.469)',
                        transformOrigin: 'top left'
                      }}
                    >
                      {textPopups.diary_room.title && (
                        <div style={{
                          color: '#FFD700',
                          fontSize: '1.1em',
                          fontWeight: 'bold',
                          marginBottom: '8px',
                          textShadow: '1px 1px 2px rgba(0, 0, 0, 0.8)',
                          pointerEvents: 'none'
                        }}>
                          {textPopups.diary_room.title}
                        </div>
                      )}
                      <div style={{
                        color: '#FFFFFF',
                        fontSize: '0.95em',
                        lineHeight: '1.4',
                        wordWrap: 'break-word',
                        pointerEvents: 'none'
                      }}>
                        {textPopups.diary_room.body}
                      </div>
                    </div>
                  )
                }
              />
            )}
            {/* Voiceover Video Player */}
            {(processedVoiceoverVideoUrls.diary_room || voiceoverVideoUrls.diary_room) && (
              <div style={{ marginTop: '12px' }}>
                <div style={{
                  color: '#4CAF50',
                  fontWeight: 'bold',
                  fontSize: '0.85em',
                  marginBottom: '8px'
                }}>
                  🎙️ Voiceover Video (Static Image + TTS)
                </div>
                <div style={{
                  position: 'relative',
                  width: '100%',
                  maxWidth: '900px'
                }}>
                  <video
                    key={processedVoiceoverVideoUrls.diary_room || voiceoverVideoUrls.diary_room}
                    controls
                    draggable="true"
                    onDragStart={(e) => {
                      const url = processedVoiceoverVideoUrls.diary_room || voiceoverVideoUrls.diary_room;
                      const filename = url.split('/').pop().split('?')[0];
                      const videoData = {
                        url: url,
                        filename: filename,
                        title: `Diary Room (Voiceover) - ${action.player_name || user?.name || 'Player'}`,
                        type: 'dialogue-diary-room-voiceover',
                        metadata: {
                          playerName: action.player_name || user?.name,
                          message: data.message,
                          dialogue: data.diary_room,
                          model: user?.model
                        }
                      };
                      e.dataTransfer.setData('video-data', JSON.stringify(videoData));
                      e.dataTransfer.effectAllowed = 'copy';
                    }}
                    src={processedVoiceoverVideoUrls.diary_room || voiceoverVideoUrls.diary_room}
                    style={{
                      width: '100%',
                      maxWidth: '600px',
                      borderRadius: '4px',
                      border: showPopupPreview.diary_room ? '2px dashed #FFD700' : `1px solid ${THEME.borderLight}`,
                      cursor: 'grab'
                    }}
                  />
                  {/* Draggable Text Pop-up Preview */}
                  {showPopupPreview.diary_room && (textPopups.diary_room.title || textPopups.diary_room.body) && (
                    <div
                      onMouseDown={(e) => {
                        if (e.button !== 0) return;
                        setDraggingPopup(prev => ({ ...prev, diary_room: true }));

                        // Capture references before creating event handlers
                        const draggableElement = e.currentTarget;
                        const container = draggableElement.parentElement;
                        const rect = draggableElement.getBoundingClientRect();
                        const offsetX = e.clientX - rect.left;
                        const offsetY = e.clientY - rect.top;

                        const handleMouseMove = (moveEvent) => {
                          const containerRect = container.getBoundingClientRect();

                          let newX = moveEvent.clientX - containerRect.left - offsetX;
                          let newY = moveEvent.clientY - containerRect.top - offsetY;

                          // Scale coordinates from display size to actual video size
                          const videoScale = 1280 / 600;
                          setTextPopups(prev => ({
                            ...prev,
                            diary_room: { ...prev.diary_room, x: Math.round(newX * videoScale), y: Math.round(newY * videoScale) }
                          }));
                        };

                        const handleMouseUp = () => {
                          setDraggingPopup(prev => ({ ...prev, diary_room: false }));
                          document.removeEventListener('mousemove', handleMouseMove);
                          document.removeEventListener('mouseup', handleMouseUp);
                        };

                        document.addEventListener('mousemove', handleMouseMove);
                        document.addEventListener('mouseup', handleMouseUp);
                        e.preventDefault();
                      }}
                      style={{
                        position: 'absolute',
                        left: `${textPopups.diary_room.x / (1280 / 600)}px`,
                        top: `${textPopups.diary_room.y / (1280 / 600)}px`,
                        background: 'rgba(0, 0, 0, 0.85)',
                        border: '3px solid #FFD700',
                        borderRadius: '12px',
                        padding: '16px 20px',
                        maxWidth: '400px',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.8)',
                        cursor: draggingPopup.diary_room ? 'grabbing' : 'grab',
                        zIndex: 10,
                        pointerEvents: 'auto',
                        userSelect: 'none',
                        touchAction: 'none',
                        transform: 'scale(0.469)',
                        transformOrigin: 'top left'
                      }}
                    >
                      <div style={{
                        color: '#FFD700',
                        fontSize: '22px',
                        fontWeight: 'bold',
                        textShadow: '2px 2px 4px rgba(0, 0, 0, 0.8)',
                        marginBottom: '8px',
                        wordWrap: 'break-word'
                      }}>
                        {textPopups.diary_room.title}
                      </div>
                      <div style={{
                        color: 'white',
                        fontSize: '18px',
                        fontWeight: '600',
                        textShadow: '2px 2px 4px rgba(0, 0, 0, 0.8)',
                        lineHeight: '1.4',
                        wordWrap: 'break-word'
                      }}>
                        {textPopups.diary_room.body}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
            {audioUrls.diary_room && (
              <div style={{ marginTop: '12px' }}>
                <div style={{
                  color: '#FF6B6B',
                  fontWeight: 'bold',
                  fontSize: '0.85em',
                  marginBottom: '8px'
                }}>
                  🎙️ TTS Audio
                </div>
                <audio
                  controls
                  src={audioUrls.diary_room}
                  style={{
                    width: '100%',
                    maxWidth: '600px',
                    borderRadius: '4px'
                  }}
                />
              </div>
            )}
            {sceneImageUrls.diary_room && (
              <div style={{ marginTop: '12px' }}>
                {data.diary_room_scene_image_base_url ? (
                  <div>
                    <div style={{
                      color: '#FF9800',
                      fontWeight: 'bold',
                      fontSize: '0.85em',
                      marginBottom: '8px'
                    }}>
                      🎨 Diary Room Image Variants
                    </div>
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                      gap: '12px'
                    }}>
                      {/* Base Image (No Border, No Dim) */}
                      <div>
                        <div style={{
                          fontSize: '0.7em',
                          color: THEME.textMuted,
                          marginBottom: '6px',
                          fontWeight: 'bold'
                        }}>
                          1. Base (No Border)
                        </div>
                        <img
                          src={data.diary_room_scene_image_base_url}
                          alt="Base Scene"
                          style={{
                            width: '100%',
                            borderRadius: '4px',
                            border: `2px solid ${THEME.borderLight}`,
                            cursor: 'pointer'
                          }}
                          onClick={() => window.open(data.diary_room_scene_image_base_url, '_blank')}
                        />
                      </div>
                      {/* TEMPORARILY DISABLED: Golden Border Image (Speaker Highlighted + Dim) */}
                      {/* <div>
                        <div style={{
                          fontSize: '0.7em',
                          color: '#FFD700',
                          marginBottom: '6px',
                          fontWeight: 'bold'
                        }}>
                          2. Speaker (Border + Dim)
                        </div>
                        <img
                          src={sceneImageUrls.diary_room}
                          alt="Speaker Scene"
                          style={{
                            width: '100%',
                            borderRadius: '4px',
                            border: `2px solid #FFD700`,
                            cursor: 'pointer'
                          }}
                          onClick={() => window.open(sceneImageUrls.diary_room, '_blank')}
                        />
                      </div> */}
                      {/* TEMPORARILY DISABLED: Golden Border (No Dim) Image */}
                      {/* {data.diary_room_scene_image_border_nodim_url && (
                        <div>
                          <div style={{
                            fontSize: '0.7em',
                            color: '#FFB700',
                            marginBottom: '6px',
                            fontWeight: 'bold'
                          }}>
                            3. Border (No Dim)
                          </div>
                          <img
                            src={data.diary_room_scene_image_border_nodim_url}
                            alt="Border No Dim"
                            style={{
                              width: '100%',
                              borderRadius: '4px',
                              border: `2px solid #FFB700`,
                              cursor: 'pointer'
                            }}
                            onClick={() => window.open(data.diary_room_scene_image_border_nodim_url, '_blank')}
                          />
                        </div>
                      )} */}
                      {/* TEMPORARILY DISABLED: Bounding Box Image */}
                      {/* {data.diary_room_scene_image_bbox_url && (
                        <div>
                          <div style={{
                            fontSize: '0.7em',
                            color: '#FFA500',
                            marginBottom: '6px',
                            fontWeight: 'bold'
                          }}>
                            4. Bounding Box
                          </div>
                          <img
                            src={data.diary_room_scene_image_bbox_url}
                            alt="Bounding Box"
                            style={{
                              width: '100%',
                              borderRadius: '4px',
                              border: `2px solid #FFA500`,
                              cursor: 'pointer'
                            }}
                            onClick={() => window.open(data.diary_room_scene_image_bbox_url, '_blank')}
                          />
                        </div>
                      )} */}
                      {/* Background Only (Clean, Speaker Removed) */}
                      {data.diary_room_scene_image_background_url && (
                        <div>
                          <div style={{
                            fontSize: '0.7em',
                            color: '#9E9E9E',
                            marginBottom: '6px',
                            fontWeight: 'bold'
                          }}>
                            5. Background (Clean)
                          </div>
                          <img
                            src={data.diary_room_scene_image_background_url}
                            alt="Background Only"
                            style={{
                              width: '100%',
                              borderRadius: '4px',
                              border: `2px solid #9E9E9E`,
                              cursor: 'pointer'
                            }}
                            onClick={() => window.open(data.diary_room_scene_image_background_url, '_blank')}
                          />
                        </div>
                      )}
                      {/* Character Only (On White, With Infill) */}
                      {data.diary_room_scene_image_character_url && (
                        <div>
                          <div style={{
                            fontSize: '0.7em',
                            color: '#4CAF50',
                            marginBottom: '6px',
                            fontWeight: 'bold'
                          }}>
                            6. Character (Isolated)
                          </div>
                          <img
                            src={data.diary_room_scene_image_character_url}
                            alt="Character Only"
                            style={{
                              width: '100%',
                              borderRadius: '4px',
                              border: `2px solid #4CAF50`,
                              cursor: 'pointer'
                            }}
                            onClick={() => window.open(data.diary_room_scene_image_character_url, '_blank')}
                          />
                        </div>
                      )}
                      {/* Character Bounding Box */}
                      {data.diary_room_scene_image_character_bbox_url && (
                        <div>
                          <div style={{
                            fontSize: '0.7em',
                            color: '#2196F3',
                            marginBottom: '6px',
                            fontWeight: 'bold'
                          }}>
                            7. Character BBox
                          </div>
                          <img
                            src={data.diary_room_scene_image_character_bbox_url}
                            alt="Character Bounding Box"
                            style={{
                              width: '100%',
                              borderRadius: '4px',
                              border: `2px solid #2196F3`,
                              cursor: 'pointer'
                            }}
                            onClick={() => window.open(data.diary_room_scene_image_character_bbox_url, '_blank')}
                          />
                        </div>
                      )}
                      {/* Sub-Conversation Participants Only */}
                      {data.diary_room_scene_image_subconvo_url && (
                        <div>
                          <div style={{
                            fontSize: '0.7em',
                            color: '#9C27B0',
                            marginBottom: '6px',
                            fontWeight: 'bold'
                          }}>
                            8. Sub-Conversation
                          </div>
                          <img
                            src={data.diary_room_scene_image_subconvo_url}
                            alt="Sub-Conversation Participants"
                            style={{
                              width: '100%',
                              borderRadius: '4px',
                              border: `2px solid #9C27B0`,
                              cursor: 'pointer'
                            }}
                            onClick={() => window.open(data.diary_room_scene_image_subconvo_url, '_blank')}
                          />
                        </div>
                      )}
                      {/* Sub-Conversation Bounding Box */}
                      {data.diary_room_scene_image_subconvo_bbox_url && (
                        <div>
                          <div style={{
                            fontSize: '0.7em',
                            color: '#00BCD4',
                            marginBottom: '6px',
                            fontWeight: 'bold'
                          }}>
                            9. Sub-Conversation BBox
                          </div>
                          <img
                            src={data.diary_room_scene_image_subconvo_bbox_url}
                            alt="Sub-Conversation Bounding Box"
                            style={{
                              width: '100%',
                              borderRadius: '4px',
                              border: `2px solid #00BCD4`,
                              cursor: 'pointer'
                            }}
                            onClick={() => window.open(data.diary_room_scene_image_subconvo_bbox_url, '_blank')}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  <div>
                    <div style={{
                      color: '#FF9800',
                      fontWeight: 'bold',
                      fontSize: '0.85em',
                      marginBottom: '8px'
                    }}>
                      🎨 Dialogue Scene Image
                    </div>
                    <img
                      src={sceneImageUrls.diary_room}
                      alt="Dialogue Scene"
                      style={{
                        width: '100%',
                        maxWidth: '600px',
                        borderRadius: '4px',
                        border: `2px solid #FF9800`,
                        cursor: 'pointer'
                      }}
                      onClick={() => window.open(sceneImageUrls.diary_room, '_blank')}
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Internal Thoughts Video section removed */}
        {false && showThoughts && data.thoughts && (
          <div style={{
            marginTop: '12px',
            padding: '12px',
            backgroundColor: `${THEME.bgDark}80`,
            borderRadius: '4px',
            border: `1px solid ${THEME.borderLight}`
          }}>
            {/* Video generation UI for thoughts */}
            {true && (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                marginBottom: '8px'
              }}>
                <span style={{
                  color: THEME.textMuted,
                  fontSize: '0.85em',
                  fontWeight: 'bold'
                }}>
                  💭 Internal Thoughts Video
                </span>
                <button
                  onClick={() => previewDialogueVideo('thoughts')}
                  disabled={previewingVideos.thoughts}
                  style={{
                    backgroundColor: previewingVideos.thoughts ? THEME.textMuted : THEME.secondary,
                    color: THEME.textLight,
                    border: 'none',
                    padding: '4px 10px',
                    borderRadius: '4px',
                    fontSize: '0.8em',
                    fontWeight: 'bold',
                    cursor: previewingVideos.thoughts ? 'not-allowed' : 'pointer',
                    transition: 'background-color 0.2s'
                  }}
                >
                  {previewingVideos.thoughts ? '⏳ Loading...' : '👁️ Preview Video'}
                </button>
                <button
                  onClick={() => previewReferenceImagePrompt('thoughts')}
                  disabled={previewingVideos.thoughts_image}
                  style={{
                    backgroundColor: previewingVideos.thoughts_image ? THEME.textMuted : '#9C27B0',
                    color: THEME.textLight,
                    border: 'none',
                    padding: '4px 10px',
                    borderRadius: '4px',
                    fontSize: '0.8em',
                    fontWeight: 'bold',
                    cursor: previewingVideos.thoughts_image ? 'not-allowed' : 'pointer',
                    transition: 'background-color 0.2s'
                  }}
                >
                  {previewingVideos.thoughts_image ? '⏳ Loading...' : '🖼️ Preview Image'}
                </button>
                <button
                  onClick={() => generateDialogueVideo('thoughts')}
                  disabled={generatingVideos.thoughts}
                  style={{
                    backgroundColor: generatingVideos.thoughts ? THEME.textMuted : THEME.accent,
                    color: THEME.textDark,
                    border: 'none',
                    padding: '4px 10px',
                    borderRadius: '4px',
                    fontSize: '0.8em',
                    fontWeight: 'bold',
                    cursor: generatingVideos.thoughts ? 'not-allowed' : 'pointer',
                    transition: 'background-color 0.2s'
                  }}
                >
                  {generatingVideos.thoughts ? '⏳ Generating...' : '🎬 Generate'}
                </button>
                <button
                  onClick={() => generateVoiceoverVideo('thoughts')}
                  disabled={generatingVideos.thoughts}
                  style={{
                    backgroundColor: generatingVideos.thoughts ? THEME.textMuted : '#4CAF50',
                    color: THEME.textLight,
                    border: 'none',
                    padding: '4px 10px',
                    borderRadius: '4px',
                    fontSize: '0.8em',
                    fontWeight: 'bold',
                    cursor: generatingVideos.thoughts ? 'not-allowed' : 'pointer',
                    transition: 'background-color 0.2s'
                  }}
                >
                  {generatingVideos.thoughts ? '⏳ Generating...' : '🎙️ Voiceover'}
                </button>
                <button
                  onClick={() => generateDialogueSceneImage('thoughts', false)}
                  disabled={generatingSceneImages.thoughts}
                  style={{
                    backgroundColor: generatingSceneImages.thoughts ? THEME.textMuted : '#FF9800',
                    color: THEME.textLight,
                    border: 'none',
                    padding: '4px 10px',
                    borderRadius: '4px',
                    fontSize: '0.8em',
                    fontWeight: 'bold',
                    cursor: generatingSceneImages.thoughts ? 'not-allowed' : 'pointer',
                    transition: 'background-color 0.2s'
                  }}
                >
                  {generatingSceneImages.thoughts ? '🎨 Generating...' : '🎨 Main Image'}
                </button>
                <button
                  onClick={() => generateDialogueSceneImage('thoughts', true)}
                  disabled={generatingSceneImages.thoughts || !data.thoughts_scene_image_base_url}
                  style={{
                    backgroundColor: generatingSceneImages.thoughts || !data.thoughts_scene_image_base_url ? THEME.textMuted : '#9C27B0',
                    color: THEME.textLight,
                    border: 'none',
                    padding: '4px 10px',
                    borderRadius: '4px',
                    fontSize: '0.8em',
                    fontWeight: 'bold',
                    cursor: generatingSceneImages.thoughts || !data.thoughts_scene_image_base_url ? 'not-allowed' : 'pointer',
                    transition: 'background-color 0.2s'
                  }}
                  title={!data.thoughts_scene_image_base_url ? 'Generate main image first' : 'Generate variants from main image'}
                >
                  {generatingSceneImages.thoughts ? '✨ Generating...' : '✨ Variants'}
                </button>
                <button
                  onClick={() => generateDialogueBoundingBoxes('thoughts')}
                  disabled={generatingBoundingBoxes.thoughts || !data.thoughts_scene_image_character_url}
                  style={{
                    backgroundColor: generatingBoundingBoxes.thoughts || !data.thoughts_scene_image_character_url ? THEME.textMuted : '#2196F3',
                    color: THEME.textLight,
                    border: 'none',
                    padding: '4px 10px',
                    borderRadius: '4px',
                    fontSize: '0.8em',
                    fontWeight: 'bold',
                    cursor: generatingBoundingBoxes.thoughts || !data.thoughts_scene_image_character_url ? 'not-allowed' : 'pointer',
                    transition: 'background-color 0.2s'
                  }}
                  title={!data.thoughts_scene_image_character_url ? 'Generate variants first' : 'Generate bounding boxes on isolated images'}
                >
                  {generatingBoundingBoxes.thoughts ? '📦 Generating...' : '📦 Bounding Boxes'}
                </button>
                {((veo3Videos.thoughts?.length > 0) || voiceoverVideoUrls.thoughts) && (
                  <button
                    onClick={() => processDialogueVideo('thoughts')}
                    disabled={processingVideos.thoughts}
                    style={{
                      backgroundColor: processingVideos.thoughts ? THEME.textMuted : THEME.secondary,
                      color: THEME.textLight,
                      border: 'none',
                      padding: '4px 10px',
                      borderRadius: '4px',
                      fontSize: '0.8em',
                      fontWeight: 'bold',
                      cursor: processingVideos.thoughts ? 'not-allowed' : 'pointer',
                      transition: 'background-color 0.2s'
                    }}
                  >
                    {processingVideos.thoughts ? '⏳ Processing...' : '✨ Process'}
                  </button>
                )}
                {((veo3Videos.thoughts?.length > 0) || stitchedVideoUrls.thoughts) && (
                  <button
                    onClick={() => extendDialogueVideo('thoughts', 7)}
                    disabled={extendingVideos.thoughts}
                    style={{
                      backgroundColor: extendingVideos.thoughts ? THEME.textMuted : '#9C27B0',
                      color: THEME.textLight,
                      border: 'none',
                      padding: '4px 8px',
                      borderRadius: '4px',
                      fontSize: '0.8em',
                      fontWeight: 'bold',
                      cursor: extendingVideos.thoughts ? 'not-allowed' : 'pointer',
                      transition: 'background-color 0.2s'
                    }}
                  >
                    {extendingVideos.thoughts ? '⏳ Extending...' : '+7s'}
                  </button>
                )}
                <button
                  onClick={() => generateDialogueTTS('thoughts')}
                  disabled={generatingTTS.thoughts}
                  style={{
                    backgroundColor: generatingTTS.thoughts ? THEME.textMuted : '#FF6B6B',
                    color: THEME.textLight,
                    border: 'none',
                    padding: '4px 10px',
                    borderRadius: '4px',
                    fontSize: '0.8em',
                    fontWeight: 'bold',
                    cursor: generatingTTS.thoughts ? 'not-allowed' : 'pointer',
                    transition: 'background-color 0.2s'
                  }}
                >
                  {generatingTTS.thoughts ? '⏳ Generating...' : '🎙️ TTS'}
                </button>
                {action.game_instance_player_id && action.game_instance_id && (
                  <button
                    onClick={() => clonePlayerVoice(action.game_instance_player_id, data.sender_name || 'Player', action.game_instance_id)}
                    style={{
                      backgroundColor: '#9C27B0',
                      color: THEME.textLight,
                      border: 'none',
                      padding: '4px 10px',
                      borderRadius: '4px',
                      fontSize: '0.8em',
                      fontWeight: 'bold',
                      cursor: 'pointer',
                      transition: 'background-color 0.2s'
                    }}
                    title="Clone this player's voice from their audio samples (requires 3+ samples)"
                  >
                    🎤 Clone Voice
                  </button>
                )}
              </div>
            )}
            {/* Debug: Log video URLs for thoughts */}
            {console.log('[Thoughts Video Debug]', {
              veo3: {
                stitched: stitchedVideoUrls.thoughts,
                processed: processedVeo3VideoUrls.thoughts,
                base: veo3Videos.thoughts
              },
              voiceover: voiceoverVideoUrls.thoughts,
              dataVideos: data.videos,
              willRenderVeo3: !!(stitchedVideoUrls.thoughts || processedVeo3VideoUrls.thoughts || veo3Videos.thoughts?.length > 0),
              willRenderVoiceover: !!voiceoverVideoUrls.thoughts
            })}

            {/* Text Pop-up Input for Thoughts Veo3 - Always visible when there are videos */}
            {(veo3Videos.thoughts?.length > 0 || stitchedVideoUrls.thoughts || processedVeo3VideoUrls.thoughts) && (
              <div style={{
                marginTop: '12px',
                marginBottom: '12px',
                padding: '10px',
                backgroundColor: THEME.bgLight,
                borderRadius: '6px',
                border: `1px solid ${THEME.borderDark}`
              }}>
                <div style={{
                  color: THEME.textLight,
                  fontWeight: 'bold',
                  fontSize: '0.8em',
                  marginBottom: '6px'
                }}>
                  💬 Text Pop-up for Internal Thoughts
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  <input
                    type="text"
                    placeholder="Title (e.g., 'Week 3')"
                    value={textPopups.thoughts.title}
                    onChange={(e) => setTextPopups(prev => ({
                      ...prev,
                      thoughts: { ...prev.thoughts, title: e.target.value }
                    }))}
                    style={{
                      padding: '5px 8px',
                      borderRadius: '4px',
                      border: `1px solid ${THEME.borderLight}`,
                      backgroundColor: THEME.bgMedium,
                      color: THEME.textLight,
                      fontSize: '0.8em'
                    }}
                  />
                  <textarea
                    placeholder="Body text (e.g., 'Nominations Ceremony')"
                    value={textPopups.thoughts.body}
                    onChange={(e) => setTextPopups(prev => ({
                      ...prev,
                      thoughts: { ...prev.thoughts, body: e.target.value }
                    }))}
                    style={{
                      padding: '5px 8px',
                      borderRadius: '4px',
                      border: `1px solid ${THEME.borderLight}`,
                      backgroundColor: THEME.bgMedium,
                      color: THEME.textLight,
                      fontSize: '0.8em',
                      minHeight: '50px',
                      resize: 'vertical',
                      fontFamily: 'inherit'
                    }}
                  />
                  <div style={{ display: 'flex', gap: '6px' }}>
                    <input
                      type="number"
                      placeholder="Start time (seconds)"
                      value={textPopups.thoughts.startTime}
                      onChange={(e) => setTextPopups(prev => ({
                        ...prev,
                        thoughts: { ...prev.thoughts, startTime: parseFloat(e.target.value) || 0 }
                      }))}
                      style={{
                        flex: 1,
                        padding: '5px 8px',
                        borderRadius: '4px',
                        border: `1px solid ${THEME.borderLight}`,
                        backgroundColor: THEME.bgMedium,
                        color: THEME.textLight,
                        fontSize: '0.8em'
                      }}
                    />
                    <input
                      type="number"
                      placeholder="End time (seconds)"
                      value={textPopups.thoughts.endTime}
                      onChange={(e) => setTextPopups(prev => ({
                        ...prev,
                        thoughts: { ...prev.thoughts, endTime: parseFloat(e.target.value) || 0 }
                      }))}
                      style={{
                        flex: 1,
                        padding: '5px 8px',
                        borderRadius: '4px',
                        border: `1px solid ${THEME.borderLight}`,
                        backgroundColor: THEME.bgMedium,
                        color: THEME.textLight,
                        fontSize: '0.8em'
                      }}
                    />
                  </div>
                  <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                    <button
                      onClick={() => setShowPopupPreview(prev => ({ ...prev, thoughts: !prev.thoughts }))}
                      disabled={!textPopups.thoughts.title && !textPopups.thoughts.body}
                      style={{
                        backgroundColor: showPopupPreview.thoughts ? '#FF9800' : '#2196F3',
                        color: 'white',
                        border: 'none',
                        padding: '5px 10px',
                        borderRadius: '4px',
                        fontSize: '0.75em',
                        fontWeight: 'bold',
                        cursor: (!textPopups.thoughts.title && !textPopups.thoughts.body) ? 'not-allowed' : 'pointer'
                      }}
                    >
                      {showPopupPreview.thoughts ? '👁️ Hide Preview' : '👁️ Show Preview'}
                    </button>
                    <button
                      onClick={() => saveTextPopup('thoughts')}
                      disabled={savingTextPopup.thoughts || (!textPopups.thoughts.title && !textPopups.thoughts.body)}
                      style={{
                        backgroundColor: savingTextPopup.thoughts ? THEME.textMuted : '#4CAF50',
                        color: 'white',
                        border: 'none',
                        padding: '5px 10px',
                        borderRadius: '4px',
                        fontSize: '0.75em',
                        fontWeight: 'bold',
                        cursor: savingTextPopup.thoughts || (!textPopups.thoughts.title && !textPopups.thoughts.body) ? 'not-allowed' : 'pointer'
                      }}
                    >
                      {savingTextPopup.thoughts ? '💾 Saving...' : '💾 Save'}
                    </button>
                  </div>
                  {showPopupPreview.thoughts && (
                    <div style={{
                      fontSize: '0.7em',
                      color: THEME.textMuted,
                      marginTop: '4px'
                    }}>
                      Position: ({textPopups.thoughts.x}px, {textPopups.thoughts.y}px) • Drag on video to reposition
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Veo3 Video Player */}
            {(renderedVideoUrls.thoughts || stitchedVideoUrls.thoughts || processedVeo3VideoUrls.thoughts) ? (
              // Show rendered/stitched/processed single video
              <div style={{ marginTop: '12px' }}>
                <div style={{
                  color: THEME.accent,
                  fontWeight: 'bold',
                  fontSize: '0.85em',
                  marginBottom: '8px'
                }}>
                  🎬 {renderedVideoUrls.thoughts ? 'Rendered Video' : `Veo3 Video${extensionCounts.thoughts > 0 ? ` (Extended ${extensionCounts.thoughts}x)` : ''}`}
                </div>
                <div style={{
                  position: 'relative',
                  width: '100%',
                  maxWidth: '600px',
                  border: showPopupPreview.thoughts ? '2px dashed #FFD700' : 'none',
                  overflow: 'visible' // Allow popup to overflow beyond container bounds
                }}>
                  <VideoPlayerWithThumbnail
                    key={renderedVideoUrls.thoughts || stitchedVideoUrls.thoughts || processedVeo3VideoUrls.thoughts}
                    videoUrl={renderedVideoUrls.thoughts || stitchedVideoUrls.thoughts || processedVeo3VideoUrls.thoughts}
                    previewUrl={null}
                    THEME={THEME}
                    style={{
                      width: '100%',
                      cursor: 'grab'
                    }}
                  />
                  {/* Draggable Text Pop-up Preview */}
                  {showPopupPreview.thoughts && (textPopups.thoughts.title || textPopups.thoughts.body) && (
                    <div
                      onMouseDown={(e) => {
                        if (e.button !== 0) return;
                        setDraggingPopup(prev => ({ ...prev, thoughts: true }));

                        // Capture references before creating event handlers
                        const draggableElement = e.currentTarget;
                        const container = draggableElement.parentElement;
                        const rect = draggableElement.getBoundingClientRect();
                        const offsetX = e.clientX - rect.left;
                        const offsetY = e.clientY - rect.top;

                        const handleMouseMove = (moveEvent) => {
                          const containerRect = container.getBoundingClientRect();

                          let newX = moveEvent.clientX - containerRect.left - offsetX;
                          let newY = moveEvent.clientY - containerRect.top - offsetY;

                          // Scale coordinates from display size to actual video size
                          const videoScale = 1280 / 600;
                          setTextPopups(prev => ({
                            ...prev,
                            thoughts: { ...prev.thoughts, x: Math.round(newX * videoScale), y: Math.round(newY * videoScale) }
                          }));
                        };

                        const handleMouseUp = () => {
                          setDraggingPopup(prev => ({ ...prev, thoughts: false }));
                          document.removeEventListener('mousemove', handleMouseMove);
                          document.removeEventListener('mouseup', handleMouseUp);
                        };

                        document.addEventListener('mousemove', handleMouseMove);
                        document.addEventListener('mouseup', handleMouseUp);
                        e.preventDefault();
                      }}
                      style={{
                        position: 'absolute',
                        left: `${textPopups.thoughts.x / (1280 / 600)}px`,
                        top: `${textPopups.thoughts.y / (1280 / 600)}px`,
                        background: 'rgba(0, 0, 0, 0.85)',
                        border: '3px solid #FFD700',
                        borderRadius: '12px',
                        padding: '16px 20px',
                        maxWidth: '400px',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.8)',
                        cursor: draggingPopup.thoughts ? 'grabbing' : 'grab',
                        zIndex: 10,
                        pointerEvents: 'auto',
                        userSelect: 'none',
                        touchAction: 'none',
                        transform: 'scale(0.469)',
                        transformOrigin: 'top left'
                      }}
                    >
                      <div style={{
                        color: '#FFD700',
                        fontSize: '22px',
                        fontWeight: 'bold',
                        textShadow: '2px 2px 4px rgba(0, 0, 0, 0.8)',
                        marginBottom: '8px',
                        wordWrap: 'break-word'
                      }}>
                        {textPopups.thoughts.title}
                      </div>
                      <div style={{
                        color: 'white',
                        fontSize: '18px',
                        fontWeight: '600',
                        textShadow: '2px 2px 4px rgba(0, 0, 0, 0.8)',
                        lineHeight: '1.4',
                        wordWrap: 'break-word'
                      }}>
                        {textPopups.thoughts.body}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              // Show multi-part video series with VideoSeriesPlayer
              <VideoSeriesPlayer
                key={veo3Videos.thoughts.map(v => v.url).join('|')}
                videos={veo3Videos.thoughts}
                videoType="thoughts"
                title={`🎬 Veo3 Video${extensionCounts.thoughts > 0 ? ` (Extended ${extensionCounts.thoughts}x)` : ''}`}
                THEME={THEME}
                metadata={{
                  playerName: action.player_name || user?.name,
                  message: data.message,
                  dialogue: data.thoughts,
                  model: user?.model,
                  isExtended: extensionCounts.thoughts > 0,
                  extensionCount: extensionCounts.thoughts
                }}
                showOverlay={showPopupPreview.thoughts}
                isDragging={draggingPopup.thoughts}
                overlayPosition={textPopups.thoughts}
                overlayContent={
                  showPopupPreview.thoughts && (textPopups.thoughts.title || textPopups.thoughts.body) && (
                    <div
                      onMouseDown={(e) => {
                        if (e.button !== 0) return;
                        setDraggingPopup(prev => ({ ...prev, thoughts: true }));

                        const draggableElement = e.currentTarget;
                        const container = draggableElement.parentElement;
                        const rect = draggableElement.getBoundingClientRect();
                        const offsetX = e.clientX - rect.left;
                        const offsetY = e.clientY - rect.top;

                        const handleMouseMove = (moveEvent) => {
                          const containerRect = container.getBoundingClientRect();

                          let newX = moveEvent.clientX - containerRect.left - offsetX;
                          let newY = moveEvent.clientY - containerRect.top - offsetY;

                          // Scale coordinates from display size (600x337.5) to actual video size (1280x720)
                          const videoScale = 1280 / 600;
                          setTextPopups(prev => ({
                            ...prev,
                            thoughts: { ...prev.thoughts, x: Math.round(newX * videoScale), y: Math.round(newY * videoScale) }
                          }));
                        };

                        const handleMouseUp = () => {
                          setDraggingPopup(prev => ({ ...prev, thoughts: false }));
                          document.removeEventListener('mousemove', handleMouseMove);
                          document.removeEventListener('mouseup', handleMouseUp);
                        };

                        document.addEventListener('mousemove', handleMouseMove);
                        document.addEventListener('mouseup', handleMouseUp);
                        e.preventDefault();
                      }}
                      style={{
                        position: 'absolute',
                        left: `${textPopups.thoughts.x / (1280 / 600)}px`,
                        top: `${textPopups.thoughts.y / (1280 / 600)}px`,
                        background: 'rgba(0, 0, 0, 0.85)',
                        border: '3px solid #FFD700',
                        borderRadius: '12px',
                        padding: '16px 20px',
                        maxWidth: '400px',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.8)',
                        cursor: draggingPopup.thoughts ? 'grabbing' : 'grab',
                        zIndex: 10,
                        pointerEvents: 'auto',
                        userSelect: 'none',
                        touchAction: 'none',
                        transform: 'scale(0.469)',
                        transformOrigin: 'top left'
                      }}
                    >
                      {textPopups.thoughts.title && (
                        <div style={{
                          color: '#FFD700',
                          fontSize: '1.1em',
                          fontWeight: 'bold',
                          marginBottom: '8px',
                          textShadow: '1px 1px 2px rgba(0, 0, 0, 0.8)',
                          pointerEvents: 'none'
                        }}>
                          {textPopups.thoughts.title}
                        </div>
                      )}
                      <div style={{
                        color: '#FFFFFF',
                        fontSize: '0.95em',
                        lineHeight: '1.4',
                        wordWrap: 'break-word',
                        pointerEvents: 'none'
                      }}>
                        {textPopups.thoughts.body}
                      </div>
                    </div>
                  )
                }
              />
            )}
            {/* Voiceover Video Player */}
            {(processedVoiceoverVideoUrls.thoughts || voiceoverVideoUrls.thoughts) && (
              <div style={{ marginTop: '12px' }}>
                <div style={{
                  color: '#4CAF50',
                  fontWeight: 'bold',
                  fontSize: '0.85em',
                  marginBottom: '8px'
                }}>
                  🎙️ Voiceover Video (Static Image + TTS)
                </div>
                <div style={{
                  position: 'relative',
                  width: '100%',
                  maxWidth: '900px'
                }}>
                  <video
                    key={processedVoiceoverVideoUrls.thoughts || voiceoverVideoUrls.thoughts}
                    controls
                    draggable="true"
                    onDragStart={(e) => {
                      const url = processedVoiceoverVideoUrls.thoughts || voiceoverVideoUrls.thoughts;
                      const filename = url.split('/').pop().split('?')[0];
                      const videoData = {
                        url: url,
                        filename: filename,
                        title: `Internal Thoughts (Voiceover) - ${action.player_name || user?.name || 'Player'}`,
                        type: 'dialogue-thoughts-voiceover',
                        metadata: {
                          playerName: action.player_name || user?.name,
                          message: data.message,
                          dialogue: data.thoughts,
                          model: user?.model
                        }
                      };
                      e.dataTransfer.setData('video-data', JSON.stringify(videoData));
                      e.dataTransfer.effectAllowed = 'copy';
                    }}
                    src={processedVoiceoverVideoUrls.thoughts || voiceoverVideoUrls.thoughts}
                    style={{
                      width: '100%',
                      maxWidth: '600px',
                      borderRadius: '4px',
                      border: showPopupPreview.thoughts ? '2px dashed #FFD700' : `1px solid ${THEME.borderLight}`,
                      cursor: 'grab'
                    }}
                  />
                  {/* Draggable Text Pop-up Preview */}
                  {showPopupPreview.thoughts && (textPopups.thoughts.title || textPopups.thoughts.body) && (
                    <div
                      onMouseDown={(e) => {
                        if (e.button !== 0) return;
                        setDraggingPopup(prev => ({ ...prev, thoughts: true }));

                        // Capture references before creating event handlers
                        const draggableElement = e.currentTarget;
                        const container = draggableElement.parentElement;
                        const rect = draggableElement.getBoundingClientRect();
                        const offsetX = e.clientX - rect.left;
                        const offsetY = e.clientY - rect.top;

                        const handleMouseMove = (moveEvent) => {
                          const containerRect = container.getBoundingClientRect();

                          let newX = moveEvent.clientX - containerRect.left - offsetX;
                          let newY = moveEvent.clientY - containerRect.top - offsetY;

                          // Scale coordinates from display size to actual video size
                          const videoScale = 1280 / 600;
                          setTextPopups(prev => ({
                            ...prev,
                            thoughts: { ...prev.thoughts, x: Math.round(newX * videoScale), y: Math.round(newY * videoScale) }
                          }));
                        };

                        const handleMouseUp = () => {
                          setDraggingPopup(prev => ({ ...prev, thoughts: false }));
                          document.removeEventListener('mousemove', handleMouseMove);
                          document.removeEventListener('mouseup', handleMouseUp);
                        };

                        document.addEventListener('mousemove', handleMouseMove);
                        document.addEventListener('mouseup', handleMouseUp);
                        e.preventDefault();
                      }}
                      style={{
                        position: 'absolute',
                        left: `${textPopups.thoughts.x / (1280 / 600)}px`,
                        top: `${textPopups.thoughts.y / (1280 / 600)}px`,
                        background: 'rgba(0, 0, 0, 0.85)',
                        border: '3px solid #FFD700',
                        borderRadius: '12px',
                        padding: '16px 20px',
                        maxWidth: '400px',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.8)',
                        cursor: draggingPopup.thoughts ? 'grabbing' : 'grab',
                        zIndex: 10,
                        pointerEvents: 'auto',
                        userSelect: 'none',
                        touchAction: 'none',
                        transform: 'scale(0.469)',
                        transformOrigin: 'top left'
                      }}
                    >
                      <div style={{
                        color: '#FFD700',
                        fontSize: '22px',
                        fontWeight: 'bold',
                        textShadow: '2px 2px 4px rgba(0, 0, 0, 0.8)',
                        marginBottom: '8px',
                        wordWrap: 'break-word'
                      }}>
                        {textPopups.thoughts.title}
                      </div>
                      <div style={{
                        color: 'white',
                        fontSize: '18px',
                        fontWeight: '600',
                        textShadow: '2px 2px 4px rgba(0, 0, 0, 0.8)',
                        lineHeight: '1.4',
                        wordWrap: 'break-word'
                      }}>
                        {textPopups.thoughts.body}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
            {audioUrls.thoughts && (
              <div style={{ marginTop: '12px' }}>
                <div style={{
                  color: '#FF6B6B',
                  fontWeight: 'bold',
                  fontSize: '0.85em',
                  marginBottom: '8px'
                }}>
                  🎙️ TTS Audio
                </div>
                <audio
                  controls
                  src={audioUrls.thoughts}
                  style={{
                    width: '100%',
                    maxWidth: '600px',
                    borderRadius: '4px'
                  }}
                />
              </div>
            )}
            {sceneImageUrls.thoughts && (
              <div style={{ marginTop: '12px' }}>
                {data.thoughts_scene_image_base_url ? (
                  <div>
                    <div style={{
                      color: '#FF9800',
                      fontWeight: 'bold',
                      fontSize: '0.85em',
                      marginBottom: '8px'
                    }}>
                      🎨 Thoughts Image Variants
                    </div>
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                      gap: '12px'
                    }}>
                      {/* Base Image (No Border, No Dim) */}
                      <div>
                        <div style={{
                          fontSize: '0.7em',
                          color: THEME.textMuted,
                          marginBottom: '6px',
                          fontWeight: 'bold'
                        }}>
                          1. Base (No Border)
                        </div>
                        <img
                          src={data.thoughts_scene_image_base_url}
                          alt="Base Scene"
                          style={{
                            width: '100%',
                            borderRadius: '4px',
                            border: `2px solid ${THEME.borderLight}`,
                            cursor: 'pointer'
                          }}
                          onClick={() => window.open(data.thoughts_scene_image_base_url, '_blank')}
                        />
                      </div>
                      {/* TEMPORARILY DISABLED: Golden Border Image (Speaker Highlighted + Dim) */}
                      {/* <div>
                        <div style={{
                          fontSize: '0.7em',
                          color: '#FFD700',
                          marginBottom: '6px',
                          fontWeight: 'bold'
                        }}>
                          2. Speaker (Border + Dim)
                        </div>
                        <img
                          src={sceneImageUrls.thoughts}
                          alt="Speaker Scene"
                          style={{
                            width: '100%',
                            borderRadius: '4px',
                            border: `2px solid #FFD700`,
                            cursor: 'pointer'
                          }}
                          onClick={() => window.open(sceneImageUrls.thoughts, '_blank')}
                        />
                      </div> */}
                      {/* TEMPORARILY DISABLED: Golden Border (No Dim) Image */}
                      {/* {data.thoughts_scene_image_border_nodim_url && (
                        <div>
                          <div style={{
                            fontSize: '0.7em',
                            color: '#FFB700',
                            marginBottom: '6px',
                            fontWeight: 'bold'
                          }}>
                            3. Border (No Dim)
                          </div>
                          <img
                            src={data.thoughts_scene_image_border_nodim_url}
                            alt="Border No Dim"
                            style={{
                              width: '100%',
                              borderRadius: '4px',
                              border: `2px solid #FFB700`,
                              cursor: 'pointer'
                            }}
                            onClick={() => window.open(data.thoughts_scene_image_border_nodim_url, '_blank')}
                          />
                        </div>
                      )} */}
                      {/* TEMPORARILY DISABLED: Bounding Box Image */}
                      {/* {data.thoughts_scene_image_bbox_url && (
                        <div>
                          <div style={{
                            fontSize: '0.7em',
                            color: '#FFA500',
                            marginBottom: '6px',
                            fontWeight: 'bold'
                          }}>
                            4. Bounding Box
                          </div>
                          <img
                            src={data.thoughts_scene_image_bbox_url}
                            alt="Bounding Box"
                            style={{
                              width: '100%',
                              borderRadius: '4px',
                              border: `2px solid #FFA500`,
                              cursor: 'pointer'
                            }}
                            onClick={() => window.open(data.thoughts_scene_image_bbox_url, '_blank')}
                          />
                        </div>
                      )} */}
                      {/* Background Only (Clean, Speaker Removed) */}
                      {data.thoughts_scene_image_background_url && (
                        <div>
                          <div style={{
                            fontSize: '0.7em',
                            color: '#9E9E9E',
                            marginBottom: '6px',
                            fontWeight: 'bold'
                          }}>
                            5. Background (Clean)
                          </div>
                          <img
                            src={data.thoughts_scene_image_background_url}
                            alt="Background Only"
                            style={{
                              width: '100%',
                              borderRadius: '4px',
                              border: `2px solid #9E9E9E`,
                              cursor: 'pointer'
                            }}
                            onClick={() => window.open(data.thoughts_scene_image_background_url, '_blank')}
                          />
                        </div>
                      )}
                      {/* Character Only (On White, With Infill) */}
                      {data.thoughts_scene_image_character_url && (
                        <div>
                          <div style={{
                            fontSize: '0.7em',
                            color: '#4CAF50',
                            marginBottom: '6px',
                            fontWeight: 'bold'
                          }}>
                            6. Character (Isolated)
                          </div>
                          <img
                            src={data.thoughts_scene_image_character_url}
                            alt="Character Only"
                            style={{
                              width: '100%',
                              borderRadius: '4px',
                              border: `2px solid #4CAF50`,
                              cursor: 'pointer'
                            }}
                            onClick={() => window.open(data.thoughts_scene_image_character_url, '_blank')}
                          />
                        </div>
                      )}
                      {/* Character Bounding Box */}
                      {data.thoughts_scene_image_character_bbox_url && (
                        <div>
                          <div style={{
                            fontSize: '0.7em',
                            color: '#2196F3',
                            marginBottom: '6px',
                            fontWeight: 'bold'
                          }}>
                            7. Character BBox
                          </div>
                          <img
                            src={data.thoughts_scene_image_character_bbox_url}
                            alt="Character Bounding Box"
                            style={{
                              width: '100%',
                              borderRadius: '4px',
                              border: `2px solid #2196F3`,
                              cursor: 'pointer'
                            }}
                            onClick={() => window.open(data.thoughts_scene_image_character_bbox_url, '_blank')}
                          />
                        </div>
                      )}
                      {/* Sub-Conversation Participants Only */}
                      {data.thoughts_scene_image_subconvo_url && (
                        <div>
                          <div style={{
                            fontSize: '0.7em',
                            color: '#9C27B0',
                            marginBottom: '6px',
                            fontWeight: 'bold'
                          }}>
                            8. Sub-Conversation
                          </div>
                          <img
                            src={data.thoughts_scene_image_subconvo_url}
                            alt="Sub-Conversation Participants"
                            style={{
                              width: '100%',
                              borderRadius: '4px',
                              border: `2px solid #9C27B0`,
                              cursor: 'pointer'
                            }}
                            onClick={() => window.open(data.thoughts_scene_image_subconvo_url, '_blank')}
                          />
                        </div>
                      )}
                      {/* Sub-Conversation Bounding Box */}
                      {data.thoughts_scene_image_subconvo_bbox_url && (
                        <div>
                          <div style={{
                            fontSize: '0.7em',
                            color: '#00BCD4',
                            marginBottom: '6px',
                            fontWeight: 'bold'
                          }}>
                            9. Sub-Conversation BBox
                          </div>
                          <img
                            src={data.thoughts_scene_image_subconvo_bbox_url}
                            alt="Sub-Conversation Bounding Box"
                            style={{
                              width: '100%',
                              borderRadius: '4px',
                              border: `2px solid #00BCD4`,
                              cursor: 'pointer'
                            }}
                            onClick={() => window.open(data.thoughts_scene_image_subconvo_bbox_url, '_blank')}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  <div>
                    <div style={{
                      color: '#FF9800',
                      fontWeight: 'bold',
                      fontSize: '0.85em',
                      marginBottom: '8px'
                    }}>
                      🎨 Dialogue Scene Image
                    </div>
                    <img
                      src={sceneImageUrls.thoughts}
                      alt="Dialogue Scene"
                      style={{
                        width: '100%',
                        maxWidth: '600px',
                        borderRadius: '4px',
                        border: `2px solid #FF9800`,
                        cursor: 'pointer'
                      }}
                      onClick={() => window.open(sceneImageUrls.thoughts, '_blank')}
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {showPreviewModal && previewData && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.8)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1000,
              padding: '20px'
            }}
            onClick={() => setShowPreviewModal(false)}
          >
            <div
              style={{
                backgroundColor: THEME.bgLight,
                borderRadius: '8px',
                maxWidth: '900px',
                maxHeight: '90dvh',
                overflow: 'auto',
                padding: '24px',
                position: 'relative'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '16px',
                borderBottom: `2px solid ${THEME.borderLight}`,
                paddingBottom: '12px'
              }}>
                <h2 style={{
                  margin: 0,
                  color: THEME.textLight,
                  fontSize: '1.5em'
                }}>
                  {previewData.videoType === 'external' && '💬 External Dialogue Preview'}
                  {previewData.videoType === 'diary_room' && '📜 Diary Room Preview'}
                  {previewData.videoType === 'thoughts' && '💭 Internal Thoughts Preview'}
                </h2>
                <button
                  onClick={() => setShowPreviewModal(false)}
                  style={{
                    backgroundColor: THEME.textMuted,
                    color: THEME.textLight,
                    border: 'none',
                    padding: '8px 16px',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '1em',
                    fontWeight: 'bold'
                  }}
                >
                  ✕ Close
                </button>
              </div>

              <div style={{ marginBottom: '20px' }}>
                <div style={{
                  color: THEME.textMuted,
                  fontSize: '0.9em',
                  marginBottom: '8px'
                }}>
                  Game: {previewData.context.gameType} | Player: {previewData.context.playerName} | Other Players: {previewData.context.otherPlayersCount}
                </div>
              </div>

              <div style={{ marginBottom: '24px' }}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '8px'
                }}>
                  <h3 style={{
                    margin: 0,
                    color: THEME.textLight,
                    fontSize: '1.1em'
                  }}>
                    AI-Generated Video Prompt
                  </h3>
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(previewData.prompt);
                      alert('Prompt copied to clipboard!');
                    }}
                    style={{
                      backgroundColor: THEME.secondary,
                      color: THEME.textLight,
                      border: 'none',
                      padding: '4px 12px',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '0.85em'
                    }}
                  >
                    📋 Copy
                  </button>
                </div>
                <div style={{
                  backgroundColor: THEME.bgDark,
                  padding: '16px',
                  borderRadius: '4px',
                  border: `1px solid ${THEME.borderLight}`,
                  color: THEME.textLight,
                  fontSize: '0.95em',
                  lineHeight: '1.6',
                  whiteSpace: 'pre-wrap',
                  maxHeight: '300px',
                  overflow: 'auto'
                }}>
                  {typeof previewData.prompt === 'string'
                    ? previewData.prompt
                    : JSON.stringify(previewData.prompt, null, 2)}
                </div>
              </div>

              <div>
                <h3 style={{
                  margin: '0 0 12px 0',
                  color: THEME.textLight,
                  fontSize: '1.1em'
                }}>
                  {previewData.useStartFrame ? 'Start Frame (Scene Image)' : 'Reference Images'}
                  <span style={{
                    marginLeft: '8px',
                    fontSize: '0.75em',
                    color: THEME.textMuted,
                    fontWeight: 'normal'
                  }}>
                    {previewData.context.imageMode === 'start_frame' ? '(Using scene image - more consistent)' : '(Using character profile images)'}
                  </span>
                </h3>

                {previewData.useStartFrame && previewData.startFrame ? (
                  <div style={{
                    backgroundColor: THEME.bgDark,
                    padding: '12px',
                    borderRadius: '4px',
                    border: `2px solid ${THEME.accent}`
                  }}>
                    <div style={{
                      color: THEME.accent,
                      fontSize: '0.9em',
                      marginBottom: '8px',
                      fontWeight: 'bold'
                    }}>
                      🎬 Start Frame - Scene Image
                    </div>
                    <img
                      src={`data:image/jpeg;base64,${previewData.startFrame.base64}`}
                      alt="Start Frame"
                      style={{
                        width: '100%',
                        height: 'auto',
                        borderRadius: '4px',
                        border: `1px solid ${THEME.borderLight}`
                      }}
                    />
                    <div style={{
                      color: THEME.textMuted,
                      fontSize: '0.75em',
                      marginTop: '8px',
                      fontStyle: 'italic'
                    }}>
                      Veo3 will use this exact scene as the starting point for the video
                    </div>
                  </div>
                ) : previewData.referenceImages ? (
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: previewData.referenceImages.length === 1 ? '1fr' : '1fr 1fr',
                    gap: '16px'
                  }}>
                    {previewData.referenceImages.map((img, index) => (
                      <div key={index} style={{
                        backgroundColor: THEME.bgDark,
                        padding: '12px',
                        borderRadius: '4px',
                        border: `1px solid ${THEME.borderLight}`
                      }}>
                        <div style={{
                          color: THEME.textMuted,
                          fontSize: '0.85em',
                          marginBottom: '8px',
                        fontWeight: 'bold'
                      }}>
                        {img.type === 'individual'
                          ? `👤 ${img.name || 'Individual Player'}`
                          : `👥 Grid (${img.playerCount || 'Multiple'} Players)`}
                      </div>
                      <img
                        src={`data:${img.mimeType};base64,${img.base64}`}
                        alt={img.type === 'individual' ? `${img.name || 'Individual player'}` : `Grid of ${img.playerCount || 'multiple'} players`}
                        style={{
                          width: '100%',
                          borderRadius: '4px',
                          border: `1px solid ${THEME.borderLight}`
                        }}
                      />
                    </div>
                  ))}
                  </div>
                ) : (
                  <div style={{
                    color: THEME.textMuted,
                    fontSize: '0.85em',
                    fontStyle: 'italic',
                    padding: '16px',
                    backgroundColor: THEME.bgDark,
                    borderRadius: '4px',
                    border: `1px solid ${THEME.borderLight}`
                  }}>
                    📋 Reference Image Prompt Preview - No images to display for this preview type
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
    {/* Credits and reactions outside the bubble */}
    <div style={{
      display: 'flex',
      flexDirection: isMobile ? 'column' : 'row',
      alignItems: isMobile ? (isOwnMessage ? 'flex-end' : 'flex-start') : 'center',
      justifyContent: isOwnMessage ? 'flex-end' : 'flex-start',
      gap: isMobile ? '2px' : '6px',
      padding: '2px 8px',
      width: '100%',
    }}>
      <div style={{ order: isOwnMessage && !isMobile ? 1 : (isMobile ? 1 : 0), display: 'flex', alignItems: 'center', gap: '6px' }}>
        {(() => {
          const turn = getChatGameTurnDescription();
          return (
            <>
              {competitiveMode && !imposterMode && turn.cost > 0 && (
                <span style={{
                  fontSize: '0.72em',
                  color: '#ef4444',
                  fontWeight: 700,
                  padding: '1px 6px',
                  borderRadius: '4px',
                  backgroundColor: '#ef444415',
                }}>
                  -{turn.cost} credit{turn.cost !== 1 ? 's' : ''}
                </span>
              )}
              {competitiveMode && !imposterMode && remaining_tokens !== '?' && (
                <span style={{
                  fontSize: '0.7em',
                  color: THEME.textMuted,
                  fontWeight: 600,
                  padding: '1px 6px',
                  borderRadius: '4px',
                  backgroundColor: `${THEME.textMuted}12`,
                }}>
                  {remaining_tokens} credits left
                </span>
              )}
            </>
          );
        })()}
      </div>
      <div style={{ order: isOwnMessage && !isMobile ? 0 : (isMobile ? 0 : 1), display: 'flex', alignItems: 'center', gap: 6 }}>
        {(gameType === 'chat-game' || gameType === 'chat') && onReactionSubmit && (
          <MessageReactions
            actionId={action.id}
            currentPlayerId={effectivePlayerId}
            existingReactions={data.reactions || {}}
            onReact={onReactionSubmit}
            theme={THEME}
            alignRight={isOwnMessage}
            chipCap={competitiveMode ? null : 4}
          />
        )}
        {/* 🛡️ 🚩 on other humans' messages (MODERATION P1.1) — the threshold
            hide (3 distinct reporters) only enforces on human_interjection */}
        {action.type === 'human_interjection' && !isOwnMessage && (
          <ReportFlag kind="chat_message" targetId={action.id} targetPlayerId={data.sender_id || null} playerId={effectivePlayerId} compact />
        )}
      </div>
    </div>
    {/* 🔍📌 W2c: the owner's "why" + "pin" under HIS lines. placeLine is set
        on the owner's desktop place page only (every other mount passes
        nothing and renders as before). Keyed by the message: the virtual
        feed reuses rows by index, and an open slip must stay with its line. */}
    {placeLine && action.type === 'public_message' && !action.isOptimistic
      && action.game_instance_player_id === placeLine.residentSeatId ? (
      <PlaceLineTools
        key={action.id}
        actionId={action.id}
        residentName={placeLine.residentName}
        receipt={placeLine.receipts[action.id]}
        pinState={placeLine.pinState(action.id)}
        onPin={placeLine.onPin}
        onOpenChange={placeLine.onOpenChange}
      />
    ) : null}
    {/* 🌙 W2b: "💤 Saved for when they wake" under the viewer's newest note while the AIs sleep */}
    {isOwnMessage && <AsleepReceipt actionId={action.id} />}
    </div>
    </div>
  );
};

const OutOfTokensAction = ({ action, THEME }) => {
  const COMMON_STYLES = getCommonStyles(THEME);
  const { data } = action;
  const modelColor = getModelColor(data.model, THEME);

  return (
    <div style={{
      backgroundColor: THEME.bgLight,
      border: `1px solid ${THEME.borderLight}`,
      borderRadius: '4px',
      padding: '8px 12px',
      marginBottom: '8px',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      fontSize: '0.9em',
      color: THEME.textMuted
    }}>
      <div style={{
        color: THEME.textMuted,
        fontSize: '0.85em'
      }}>
        Out of Credits
      </div>
      <div style={{ 
        flex: 1,
        backgroundColor: modelColor,
        padding: '2px 8px',
        borderRadius: '4px',
        color: THEME.textDark
      }}>
        {data.name} ({data.model})
      </div>
      <div style={{ fontSize: '0.85em' }}>
        {formatActionTime(action.created_at)}
      </div>
    </div>
  );
};

const PassAction = ({ action, users, THEME }) => {
  const { data } = action;
  const user = getUserById(users, action.game_instance_player_id);
  const playerName = user?.name || data.sender_name || 'Unknown';

  return (
    <div style={{
      textAlign: 'center',
      padding: '4px 0',
      margin: '2px 0',
    }}>
      <span style={{
        fontSize: '0.8em',
        color: THEME.textMuted,
        fontStyle: 'italic',
      }}>
        {playerName} passed
      </span>
    </div>
  );
};

const FinalSpeechAction = ({ action, users, THEME }) => {
  const COMMON_STYLES = getCommonStyles(THEME);
  const { data } = action;

  // Get user info from game_instance_player_id
  const user = getUserById(users, action.game_instance_player_id);

  // Extract model name
  const modelName = user?.model || data.sender_model;
  const modelColor = getModelColor(modelName, THEME);

  return (
    <div style={{
      ...COMMON_STYLES.actionContainer,
      backgroundColor: THEME.bgMedium,
      border: `2px solid #DC143C`, // Crimson border for closing statements
      boxShadow: `0 4px 12px rgba(220, 20, 60, 0.3)`, // Crimson glow
    }}>
      <div style={{
        ...COMMON_STYLES.actionHeader,
        background: `linear-gradient(135deg, #DC143C 0%, #8B0000 100%)`, // Crimson/Dark red gradient
        padding: '12px',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
          {/* Profile Image */}
          {user && (
            <ProfileImage
              user={user}
              size={40}
              borderColor="#DC143C"
            />
          )}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1 }}>
            <span style={{
              fontSize: '1.4em',
            }}>
              🎤
            </span>
            <span style={{
              ...COMMON_STYLES.typeBadge,
              backgroundColor: '#DC143C',
              color: '#FFF',
              fontWeight: 'bold',
              fontSize: '0.95em',
              padding: '6px 12px',
              border: '2px solid #8B0000',
            }}>
              FINAL 2 CLOSING STATEMENT
            </span>
          </div>
          {user && (
            <span style={{
              ...COMMON_STYLES.playerBadge,
              backgroundColor: modelColor,
              color: THEME.textDark,
              fontSize: '1em',
              fontWeight: 'bold',
              padding: '6px 12px',
              border: `2px solid ${modelColor}`,
            }}>
              {user.name}
            </span>
          )}
        </div>
      </div>
      <div style={{
        ...COMMON_STYLES.actionBody,
        backgroundColor: `${THEME.bgLight}`,
        padding: '16px',
      }}>
        <div style={{
          fontSize: '1.1em',
          lineHeight: '1.6',
          color: THEME.textPrimary,
          fontWeight: '500',
          fontStyle: 'italic',
          padding: '8px',
          backgroundColor: `rgba(220, 20, 60, 0.1)`,
          borderRadius: '8px',
          borderLeft: `4px solid #DC143C`,
        }}>
          "{data.message}"
        </div>
        {data.thoughts && (
          <div style={{
            marginTop: '12px',
            padding: '12px',
            backgroundColor: `${THEME.bgMedium}`,
            borderRadius: '6px',
            borderLeft: `3px solid ${THEME.textMuted}`,
          }}>
            <div style={{
              fontSize: '0.85em',
              color: THEME.textMuted,
              marginBottom: '6px',
              fontWeight: 'bold',
              textTransform: 'uppercase',
            }}>
              💭 Private Thoughts
            </div>
            <div style={{
              fontSize: '0.95em',
              color: THEME.textSecondary,
              fontStyle: 'italic',
            }}>
              {data.thoughts}
            </div>
          </div>
        )}
        {data.diary_room && (
          <div style={{
            marginTop: '12px',
            padding: '12px',
            backgroundColor: `${THEME.bgMedium}`,
            borderRadius: '6px',
            borderLeft: `3px solid ${THEME.primary}`,
          }}>
            <div style={{
              fontSize: '0.85em',
              color: THEME.primary,
              marginBottom: '6px',
              fontWeight: 'bold',
              textTransform: 'uppercase',
            }}>
              🎥 Diary Room
            </div>
            <div style={{
              fontSize: '0.95em',
              color: THEME.textSecondary,
              fontStyle: 'italic',
            }}>
              {data.diary_room}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const OpeningStatementAction = ({ action, users, THEME }) => {
  const COMMON_STYLES = getCommonStyles(THEME);
  const { data } = action;

  // Get user info from game_instance_player_id
  const user = getUserById(users, action.game_instance_player_id);

  // Extract model name
  const modelName = user?.model || data.sender_model;
  const modelColor = getModelColor(modelName, THEME);

  return (
    <div style={{
      ...COMMON_STYLES.actionContainer,
      backgroundColor: THEME.bgMedium,
      border: `2px solid #FFD700`, // Gold border for Final 2 statements
      boxShadow: `0 4px 12px rgba(255, 215, 0, 0.3)`, // Gold glow
    }}>
      <div style={{
        ...COMMON_STYLES.actionHeader,
        background: `linear-gradient(135deg, #FFD700 0%, #FFA500 100%)`, // Gold gradient
        padding: '12px',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
          {/* Profile Image */}
          {user && (
            <ProfileImage
              user={user}
              size={40}
              borderColor="#FFD700"
            />
          )}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1 }}>
            <span style={{
              fontSize: '1.4em',
            }}>
              🏆
            </span>
            <span style={{
              ...COMMON_STYLES.typeBadge,
              backgroundColor: '#FFD700', textShadow: 'none',
              color: '#000',
              fontWeight: 'bold',
              fontSize: '0.95em',
              padding: '6px 12px',
              border: '2px solid #FFA500',
            }}>
              FINAL 2 OPENING STATEMENT
            </span>
          </div>
          {user && (
            <span style={{
              ...COMMON_STYLES.playerBadge,
              backgroundColor: modelColor,
              color: THEME.textDark,
              fontSize: '1em',
              fontWeight: 'bold',
              padding: '6px 12px',
              border: `2px solid ${modelColor}`,
            }}>
              {user.name}
            </span>
          )}
        </div>
      </div>
      <div style={{
        ...COMMON_STYLES.actionBody,
        backgroundColor: `${THEME.bgLight}`,
        padding: '16px',
      }}>
        <div style={{
          fontSize: '1.1em',
          lineHeight: '1.6',
          color: THEME.textPrimary,
          fontWeight: '500',
          fontStyle: 'italic',
          padding: '8px',
          backgroundColor: `rgba(255, 215, 0, 0.1)`,
          borderRadius: '8px',
          borderLeft: `4px solid #FFD700`,
        }}>
          "{data.message}"
        </div>
        {data.thoughts && (
          <div style={{
            marginTop: '12px',
            padding: '12px',
            backgroundColor: `${THEME.bgMedium}`,
            borderRadius: '6px',
            borderLeft: `3px solid ${THEME.textMuted}`,
          }}>
            <div style={{
              fontSize: '0.85em',
              color: THEME.textMuted,
              marginBottom: '6px',
              fontWeight: 'bold',
              textTransform: 'uppercase',
            }}>
              💭 Private Thoughts
            </div>
            <div style={{
              fontSize: '0.95em',
              color: THEME.textSecondary,
              fontStyle: 'italic',
            }}>
              {data.thoughts}
            </div>
          </div>
        )}
        {data.diary_room && (
          <div style={{
            marginTop: '12px',
            padding: '12px',
            backgroundColor: `${THEME.bgMedium}`,
            borderRadius: '6px',
            borderLeft: `3px solid ${THEME.primary}`,
          }}>
            <div style={{
              fontSize: '0.85em',
              color: THEME.primary,
              marginBottom: '6px',
              fontWeight: 'bold',
              textTransform: 'uppercase',
            }}>
              🎥 Diary Room
            </div>
            <div style={{
              fontSize: '0.95em',
              color: THEME.textSecondary,
              fontStyle: 'italic',
            }}>
              {data.diary_room}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const GameActionAction = ({ action, users, THEME }) => {
  const COMMON_STYLES = getCommonStyles(THEME);
  const { data } = action;

  // Get user info from game_instance_player_id
  const user = getUserById(users, action.game_instance_player_id);

  // Extract model name from the game action text or user
  const modelMatch = data.model?.match(/([^ ]+)/);
  const modelName = modelMatch ? modelMatch[1] : (user?.model || data.model);
  const modelColor = getModelColor(modelName, THEME);

  return (
    <div style={{
      ...COMMON_STYLES.actionContainer,
      backgroundColor: THEME.bgMedium,
    }}>
      <div style={{
        ...COMMON_STYLES.actionHeader,
        backgroundColor: `${THEME.system}4d`, // System brown with transparency
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {/* Profile Image */}
          {user && (
            <ProfileImage
              user={user}
              size={32}
              borderColor={modelColor}
            />
          )}
          <span style={{
            ...COMMON_STYLES.typeBadge,
            backgroundColor: THEME.system,
            color: THEME.textLight,
          }}>
            ⚙️ Game Event
          </span>
          {user && (
            <span style={{
              ...COMMON_STYLES.playerBadge,
              backgroundColor: modelColor,
              color: THEME.textDark,
              fontSize: '0.85em',
            }}>
              {user.name}
            </span>
          )}
          {!user && modelName && (
            <span style={{
              ...COMMON_STYLES.playerBadge,
              backgroundColor: modelColor,
              color: THEME.textDark,
              fontSize: '0.85em',
            }}>
              {modelName}
            </span>
          )}
        </div>
        <span style={{ color: THEME.textMuted, fontSize: '0.85em' }}>
          {formatActionTime(action.created_at)}
        </span>
      </div>
      <div style={COMMON_STYLES.actionBody}>
        <div style={{
          color: THEME.textLight,
          fontSize: '1.05em',
          fontWeight: '500',
        }}>
          {data.message || data.model}
        </div>
      </div>
    </div>
  );
};

const VoteAction = ({ action, users, THEME }) => {
  const COMMON_STYLES = getCommonStyles(THEME);
  const { data } = action;

  // Get user info from game_instance_player_id
  const user = getUserById(users, action.game_instance_player_id);

  const modelColor = getModelColor(data.model || user?.model, THEME);
  const targetColor = getModelColor(data.target, THEME);

  return (
    <div style={{
      backgroundColor: THEME.bgLight,
      border: `1px solid ${THEME.borderLight}`,
      borderRadius: '8px',
      padding: '12px',
      marginBottom: '12px'
    }}>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        marginBottom: '8px'
      }}>
        {/* Profile Image */}
        {user && (
          <ProfileImage
            user={user}
            size={28}
            borderColor={modelColor}
          />
        )}
        <div style={{
          backgroundColor: THEME.textMuted,
          color: THEME.textLight,
          padding: '2px 8px',
          borderRadius: '4px',
          fontSize: '0.85em'
        }}>
          Vote
        </div>
        <div style={{
          backgroundColor: modelColor,
          padding: '2px 8px',
          borderRadius: '4px',
          color: THEME.textDark
        }}>
          {user?.name || data.name} {data.model && `(${data.model})`}
        </div>
        <div style={{ color: THEME.textMuted }}>votes to evict</div>
        <div style={{
          backgroundColor: targetColor,
          padding: '2px 8px',
          borderRadius: '4px',
          color: THEME.textDark
        }}>
          {data.target}
        </div>
        <div style={{ marginLeft: 'auto', fontSize: '0.85em', color: THEME.textMuted }}>
          {formatActionTime(action.created_at)}
        </div>
      </div>
      
      <div style={{
        fontSize: '0.9em',
        color: THEME.textDark,
        backgroundColor: THEME.textLight,
        padding: '8px',
        borderRadius: '4px',
        border: `1px solid ${THEME.borderLight}`,
        fontStyle: 'italic'
      }}>
        {data.thoughts}
      </div>

      {data.diary_room && (
        <div style={{
          fontSize: '0.9em',
          color: THEME.textDark,
          backgroundColor: THEME.bgParchment,
          padding: '8px',
          borderRadius: '4px',
          border: `1px solid ${THEME.borderLight}`,
          marginTop: '8px',
          fontStyle: 'italic'
        }}>
          <div style={{ marginBottom: '4px' }}>
            <strong>Diary Room:</strong> {data.diary_room}
          </div>
        </div>
      )}
    </div>
  );
};

const EvictionAction = ({ action, users, THEME }) => {
  const COMMON_STYLES = getCommonStyles(THEME);
  const { data } = action;

  // Find evicted user by name
  const evictedUser = users?.find(u => u.name === data.loser_name);
  const loserColor = evictedUser ? getModelColor(evictedUser.model, THEME) : getModelColor(data.loser_model, THEME);

  return (
    <div style={{
      backgroundColor: THEME.evil,
      border: `1px solid ${THEME.evil}`,
      borderRadius: '8px',
      padding: '16px',
      marginBottom: '12px',
      color: THEME.textLight
    }}>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        marginBottom: '12px'
      }}>
        <div style={{
          backgroundColor: '${THEME.textLight}33',
          padding: '4px 12px',
          borderRadius: '4px',
          fontSize: '1.1em',
          fontWeight: 'bold'
        }}>
          Eviction
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ marginBottom: '4px' }}>Votes:</div>
          {data.votes && Object.entries(data.votes).map(([playerId, count]) => (
            <div key={playerId} style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              marginBottom: '4px'
            }}>
              <div style={{
                backgroundColor: `${THEME.textLight}33`,
                padding: '2px 8px',
                borderRadius: '4px',
                fontSize: '0.9em'
              }}>
                {count} votes
              </div>
              <div style={{ fontSize: '0.9em' }}>
                Player {playerId.slice(0, 8)}...
              </div>
            </div>
          ))}
        </div>
        <div style={{ fontSize: '0.85em' }}>
          {formatActionTime(action.created_at)}
        </div>
      </div>

      <div style={{
        backgroundColor: '${THEME.textLight}1a',
        padding: '12px',
        borderRadius: '4px',
        marginTop: '8px'
      }}>
        <div style={{ marginBottom: '8px', fontWeight: 'bold' }}>
          Evicted:
        </div>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          {evictedUser && (
            <ProfileImage
              user={evictedUser}
              size={32}
              borderColor={loserColor}
            />
          )}
          <div style={{
            backgroundColor: loserColor,
            padding: '8px 12px',
            borderRadius: '4px',
            color: THEME.textDark
          }}>
            {data.loser_name}
          </div>
        </div>
      </div>
    </div>
  );
};

const GameAnalysisAction = ({ action, playerModelMap, THEME }) => {
  const COMMON_STYLES = getCommonStyles(THEME);
  const { data } = action;
  
  // Function to get model name for a player
  const getPlayerModel = (playerName) => {
    return playerModelMap[playerName] || playerName;
  };

  // Function to process benchmark scores to include model names
  const processBenchmarkScores = (scores) => {
    if (!scores) return {};
    const processedScores = {};
    Object.entries(scores).forEach(([key, value]) => {
      // If the key is a player name, add their model
      if (playerModelMap[key]) {
        processedScores[`${key} (${playerModelMap[key]})`] = value;
      } else {
        processedScores[key] = value;
      }
    });
    return processedScores;
  };
  
  return (
    <div style={{
      backgroundColor: THEME.bgLight,
      border: `1px solid ${THEME.borderLight}`,
      borderRadius: '8px',
      padding: '16px',
      marginBottom: '12px'
    }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '16px'
      }}>
        <div style={{
          backgroundColor: THEME.textMuted,
          color: THEME.textLight,
          padding: '4px 12px',
          borderRadius: '4px',
          fontSize: '1.1em',
          fontWeight: 'bold'
        }}>
          Game Analysis by {data.model}
        </div>
        <div style={{ fontSize: '0.85em', color: THEME.textMuted }}>
          {formatActionTime(action.created_at)}
        </div>
      </div>

      {/* Main Analysis */}
      <div style={{
        backgroundColor: THEME.textLight,
        padding: '16px',
        borderRadius: '6px',
        marginBottom: '16px',
        border: `1px solid ${THEME.borderLight}`
      }}>
        <div style={{ marginBottom: '8px', fontWeight: 'bold' }}>Analysis:</div>
        <div style={{ whiteSpace: 'pre-wrap', lineHeight: '1.5' }}>
          {data.analysis}
        </div>
      </div>

      {/* Serious Analysis */}
      <div style={{
        backgroundColor: THEME.textLight,
        padding: '16px',
        borderRadius: '6px',
        marginBottom: '16px',
        border: `1px solid ${THEME.borderLight}`
      }}>
        <div style={{ marginBottom: '8px', fontWeight: 'bold' }}>Serious Analysis:</div>
        <div style={{ whiteSpace: 'pre-wrap', lineHeight: '1.5' }}>
          {data.serious_analysis}
        </div>
      </div>

      {/* Alliances */}
      {data.alliances && data.alliances.length > 0 && (
        <div style={{
          backgroundColor: THEME.textLight,
          padding: '16px',
          borderRadius: '6px',
          marginBottom: '16px',
          border: `1px solid ${THEME.borderLight}`
        }}>
          <div style={{ marginBottom: '12px', fontWeight: 'bold' }}>Alliances:</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {data.alliances.map((alliance, index) => (
              <div key={index} style={{
                backgroundColor: THEME.bgLight,
                padding: '16px',
                borderRadius: '8px',
                border: `1px solid ${THEME.borderLight}`
              }}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '12px'
                }}>
                  <div style={{
                    backgroundColor: THEME.textMuted,
                    color: THEME.textLight,
                    padding: '4px 12px',
                    borderRadius: '4px',
                    fontSize: '1.1em',
                    fontWeight: 'bold'
                  }}>
                    {alliance.alliance_name}
                  </div>
                  <div style={{
                    backgroundColor: alliance.alliance_strength === 'Very High' ? THEME.good :
                      alliance.alliance_strength.includes('High') ? THEME.secondary :
                      alliance.alliance_strength.includes('Medium') ? THEME.accent :
                      THEME.evil,
                    color: alliance.alliance_strength.includes('Medium') ? THEME.textDark : THEME.textLight,
                    padding: '4px 12px',
                    borderRadius: '4px',
                    fontSize: '0.9em',
                    fontWeight: 'bold'
                  }}>
                    {alliance.alliance_strength}
                  </div>
                </div>

                <div style={{ marginBottom: '12px' }}>
                  <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>Members:</div>
                  <div style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '8px'
                  }}>
                    {alliance.alliance_members.map((member, memberIndex) => (
                      <div key={memberIndex} style={{
                        backgroundColor: getModelColor(getPlayerModel(member), THEME),
                        padding: '4px 12px',
                        borderRadius: '4px',
                        color: THEME.textDark,
                        fontWeight: 'bold'
                      }}>
                        {member} ({getPlayerModel(member)})
                      </div>
                    ))}
                  </div>
                </div>

                <div style={{ marginBottom: '12px' }}>
                  <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>Dynamics:</div>
                  <div style={{
                    backgroundColor: THEME.textLight,
                    padding: '8px 12px',
                    borderRadius: '4px',
                    border: `1px solid ${THEME.borderLight}`,
                    fontSize: '0.95em',
                    lineHeight: '1.5'
                  }}>
                    {alliance.alliance_dynamics}
                  </div>
                </div>

                <div>
                  <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>Description:</div>
                  <div style={{
                    backgroundColor: THEME.textLight,
                    padding: '8px 12px',
                    borderRadius: '4px',
                    border: `1px solid ${THEME.borderLight}`,
                    fontSize: '0.95em',
                    lineHeight: '1.5'
                  }}>
                    {alliance.alliance_description}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Benchmarks */}
      <div style={{
        backgroundColor: THEME.textLight,
        padding: '16px',
        borderRadius: '6px',
        marginBottom: '16px',
        border: `1px solid ${THEME.borderLight}`
      }}>
        <div style={{ marginBottom: '12px', fontWeight: 'bold' }}>Benchmarks:</div>
        {data.benchmarks.map((benchmark, index) => (
          <div key={index} style={{ marginBottom: '12px' }}>
            <div style={{
              fontWeight: 'bold',
              marginBottom: '8px',
              color: THEME.textDark
            }}>
              {benchmark.benchmark_name}
            </div>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
              gap: '8px'
            }}>
              {benchmark.scores && Object.entries(processBenchmarkScores(benchmark.scores))
                .sort(([, scoreA], [, scoreB]) => scoreB - scoreA)
                .map(([model, score]) => (
                  <div key={model} style={{
                    backgroundColor: getModelColor(model, THEME),
                    padding: '8px',
                    borderRadius: '4px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}>
                    <div style={{ fontWeight: 'bold' }}>{model}</div>
                    <div style={{
                      backgroundColor: '${THEME.textDark}1a',
                      padding: '2px 8px',
                      borderRadius: '12px',
                      fontWeight: 'bold'
                    }}>
                      {score}
                    </div>
                  </div>
                ))}
            </div>
          </div>
        ))}
      </div>

      {/* Strategies */}
      <div style={{
        backgroundColor: THEME.textLight,
        padding: '16px',
        borderRadius: '6px',
        marginBottom: '16px',
        border: `1px solid ${THEME.borderLight}`
      }}>
        <div style={{ marginBottom: '12px', fontWeight: 'bold' }}>Strategies:</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {data.strategies.map((strategy, index) => (
            <div key={index} style={{
              backgroundColor: THEME.bgLight,
              padding: '8px 12px',
              borderRadius: '4px',
              border: `1px solid ${THEME.borderLight}`
            }}>
              {strategy}
            </div>
          ))}
        </div>
      </div>

      {/* Predictions */}
      <div style={{
        backgroundColor: THEME.textLight,
        padding: '16px',
        borderRadius: '6px',
        marginBottom: '16px',
        border: `1px solid ${THEME.borderLight}`
      }}>
        <div style={{ marginBottom: '12px', fontWeight: 'bold' }}>Predictions:</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {data.predictions.map((prediction, index) => (
            <div key={index} style={{
              backgroundColor: THEME.bgMedium,
              padding: '8px 12px',
              borderRadius: '4px',
              border: `1px solid ${THEME.borderLight}`
            }}>
              {prediction}
            </div>
          ))}
        </div>
      </div>

      {/* Interesting Quotes */}
      <div style={{
        backgroundColor: THEME.textLight,
        padding: '16px',
        borderRadius: '6px',
        border: `1px solid ${THEME.borderLight}`
      }}>
        <div style={{ marginBottom: '12px', fontWeight: 'bold' }}>Notable Quotes:</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {data.interesting_quotes.map((quote, index) => {
            const [speaker, message] = quote.split(': ');
            return (
              <div key={index} style={{
                backgroundColor: THEME.bgLight,
                padding: '12px',
                borderRadius: '4px',
                border: `1px solid ${THEME.borderLight}`
              }}>
                <div style={{
                  backgroundColor: getModelColor(getPlayerModel(speaker), THEME),
                  padding: '4px 8px',
                  borderRadius: '4px',
                  display: 'inline-block',
                  marginBottom: '4px',
                  fontWeight: 'bold'
                }}>
                  {speaker} ({getPlayerModel(speaker)})
                </div>
                <div style={{ marginTop: '4px' }}>{message}</div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

const RoundAnalysisAction = ({ action, playerModelMap, users, THEME, onActionUpdate, cachedSpaces = [], cachedRoundAnalysisHistory = [] }) => {
  const COMMON_STYLES = getCommonStyles(THEME);
  const { data } = action;
  const [generatingVideos, setGeneratingVideos] = React.useState({
    hostIntro: false,
    roundAnalysis: false,
    academicAnalysis: false,
    keyInsightA: false,
    keyInsightB: false
  });
  const [showingPreviews, setShowingPreviews] = React.useState({
    hostIntro: false,
    roundAnalysis: false,
    academicAnalysis: false,
    keyInsightA: false,
    keyInsightB: false
  });
  const [previewPrompts, setPreviewPrompts] = React.useState({
    hostIntro: null,
    roundAnalysis: null,
    academicAnalysis: null,
    keyInsightA: null,
    keyInsightB: null
  });
  const [loadingPreviews, setLoadingPreviews] = React.useState({
    hostIntro: false,
    roundAnalysis: false,
    academicAnalysis: false,
    keyInsightA: false,
    keyInsightB: false
  });
  const [previewReferenceImages, setPreviewReferenceImages] = React.useState({
    hostIntro: null,
    roundAnalysis: null,
    academicAnalysis: null,
    keyInsightA: null,
    keyInsightB: null
  });
  const [generatingHostIntroImage, setGeneratingHostIntroImage] = React.useState(false);
  const [hostIntroSceneImageUrl, setHostIntroSceneImageUrl] = React.useState(data.host_intro_scene_image_url || null);

  // Custom host intro states
  const [customHostDialogue, setCustomHostDialogue] = React.useState('');
  const [selectedSpace, setSelectedSpace] = React.useState('');
  const [generatingCustomHostIntro, setGeneratingCustomHostIntro] = React.useState(false);
  const [customHostIntros, setCustomHostIntros] = React.useState(data.custom_host_intros || []);

  // Use cached spaces from parent instead of fetching per-action (Issue 2 fix)
  const availableSpaces = cachedSpaces;

  // Auto-select first space if none selected (moved from useEffect)
  React.useEffect(() => {
    if (availableSpaces.length > 0 && !selectedSpace) {
      setSelectedSpace(availableSpaces[0].name);
    }
  }, [availableSpaces, selectedSpace]);

  // Compute previous round analysis from cached history instead of fetching (Issue 2 fix)
  const previousRoundAnalysis = React.useMemo(() => {
    if (!cachedRoundAnalysisHistory || cachedRoundAnalysisHistory.length === 0) return null;
    // History is already sorted chronologically by parent
    const currentIndex = cachedRoundAnalysisHistory.findIndex(ra => ra.id === action.id);
    if (currentIndex > 0) {
      return cachedRoundAnalysisHistory[currentIndex - 1].data;
    }
    return null;
  }, [cachedRoundAnalysisHistory, action.id]);

  const loadingPrevious = false; // No longer loading since we use cached data

  // Function to get model name for a player
  const getPlayerModel = (playerName) => {
    return playerModelMap[playerName] || playerName;
  };

  // Function to process benchmark scores to include model names
  const processBenchmarkScores = (scores) => {
    if (!scores) return {};
    const processedScores = {};
    Object.entries(scores).forEach(([key, value]) => {
      // If the key is a player name, add their model
      if (playerModelMap[key]) {
        processedScores[`${key} (${playerModelMap[key]})`] = value;
      } else {
        processedScores[key] = value;
      }
    });
    return processedScores;
  };

  // Helper function to extract score value (handles both old number format and new object format)
  const getScoreValue = (score) => {
    if (score === null || score === undefined) return null;
    if (typeof score === 'number') return score;
    if (typeof score === 'object' && score.score !== undefined) return score.score;
    return null;
  };

  // Helper function to extract score explanation
  const getScoreExplanation = (score) => {
    if (score === null || score === undefined) return null;
    if (typeof score === 'object' && score.explanation) return score.explanation;
    return null;
  };

  // Function to get color based on score (0-10 scale)
  const getScoreColor = (score) => {
    const numScore = getScoreValue(score);

    if (numScore === undefined || numScore === null || numScore === 'N/A') {
      return '#9ca3af'; // Gray for N/A
    }

    const num = parseFloat(numScore);

    // Traditional red to green gradient for performance
    if (num <= 3) {
      return '#ef4444'; // Red - poor
    } else if (num <= 5) {
      return '#f59e0b'; // Orange - below average
    } else if (num <= 7) {
      return '#eab308'; // Yellow - average
    } else if (num <= 8.5) {
      return '#22c55e'; // Green - good
    } else {
      return '#10b981'; // Emerald - excellent
    }
  };

  // Function to calculate benchmark change from previous round
  const getBenchmarkChange = (benchmarkName, playerName, currentScore) => {
    if (!previousRoundAnalysis || !previousRoundAnalysis.cumulative_benchmarks) {
      return null;
    }

    // Find the matching benchmark in previous round
    const previousBenchmark = previousRoundAnalysis.cumulative_benchmarks.find(
      b => b.benchmark_name === benchmarkName
    );

    if (!previousBenchmark || !previousBenchmark.scores) {
      return null;
    }

    const previousScoreData = previousBenchmark.scores[playerName];
    const previousScoreValue = getScoreValue(previousScoreData);
    const currentScoreValue = getScoreValue(currentScore);

    if (previousScoreValue === undefined || previousScoreValue === null || currentScoreValue === undefined || currentScoreValue === null) {
      return null;
    }

    const change = parseFloat(currentScoreValue) - parseFloat(previousScoreValue);

    if (Math.abs(change) < 0.1) {
      // No significant change
      return null;
    }

    return {
      change: change,
      isPositive: change > 0,
      color: change > 0 ? '#22c55e' : '#ef4444', // Green for positive, red for negative
      arrow: change > 0 ? '↑' : '↓'
    };
  };

  // Function to toggle preview display and load prompt if needed
  const togglePreview = async (sectionType) => {
    const isCurrentlyShowing = showingPreviews[sectionType];

    if (isCurrentlyShowing) {
      // Just hide it
      setShowingPreviews(prev => ({ ...prev, [sectionType]: false }));
    } else {
      // Show and load prompt if not already loaded
      setShowingPreviews(prev => ({ ...prev, [sectionType]: true }));

      if (!previewPrompts[sectionType]) {
        // Load the prompt from API
        setLoadingPreviews(prev => ({ ...prev, [sectionType]: true }));

        try {
          const response = await fetch(
            `${API_BASE_URL}/api/takeGameStep?previewAnalysisVideoPrompt=true&actionId=${action.id}&sectionType=${sectionType}`,
            {
              method: 'GET',
              headers: {
                'Content-Type': 'application/json'
              }
            }
          );

          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }

          const result = await parseApiResponse(response);
          setPreviewPrompts(prev => ({ ...prev, [sectionType]: result.prompt }));
          // Also store reference image if provided
          if (result.reference_image) {
            setPreviewReferenceImages(prev => ({ ...prev, [sectionType]: result.reference_image }));
          }
        } catch (error) {
          console.error(`[Round Analysis] Error loading preview for ${sectionType}:`, error);
          setPreviewPrompts(prev => ({
            ...prev,
            [sectionType]: 'Error loading preview. Please try again.'
          }));
        } finally {
          setLoadingPreviews(prev => ({ ...prev, [sectionType]: false }));
        }
      }
    }
  };

  // Function to generate video for round analysis sections
  const generateAnalysisVideo = async (sectionType) => {
    setGeneratingVideos(prev => ({ ...prev, [sectionType]: true }));

    try {
      const response = await fetch(
        `${API_BASE_URL}/api/takeGameStep?generateAnalysisVideo=true&actionId=${action.id}&sectionType=${sectionType}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await parseApiResponse(response);
      console.log(`[Round Analysis] Video generated for ${sectionType}:`, result);

      // Check if generation was successful
      if (!result.success || !result.video_url) {
        throw new Error(result.error || 'Video generation failed - no video URL returned');
      }

      // Map sectionType to field name
      const fieldNameMap = {
        hostIntro: 'host_intro_video_url',
        roundAnalysis: 'round_analysis_video_url',
        academicAnalysis: 'academic_analysis_video_url'
      };

      const fieldName = fieldNameMap[sectionType];

      // Update the action using the callback
      if (onActionUpdate && fieldName) {
        onActionUpdate(action.id, { [fieldName]: result.video_url });
        console.log(`[Round Analysis] Video URL updated in UI for ${sectionType}`);
      }
    } catch (error) {
      console.error(`[Round Analysis] Error generating video for ${sectionType}:`, error);
      const errorMsg = error.message || error.toString() || 'Unknown error';
      alert(`Failed to generate video: ${errorMsg}`);
    } finally {
      setGeneratingVideos(prev => ({ ...prev, [sectionType]: false }));
    }
  };

  // Function to generate scene image for host introduction
  const generateHostIntroImage = async () => {
    setGeneratingHostIntroImage(true);

    try {
      const response = await fetch(
        `${API_BASE_URL}/api/takeGameStep?generateHostIntroImage=true&actionId=${action.id}`,
        { method: 'GET' }
      );

      if (!response.ok) {
        throw new Error(`Failed to generate host intro image: ${response.statusText}`);
      }

      const result = await parseApiResponse(response);

      if (result.success && result.hostIntroImageUrl) {
        const imageUrlWithTimestamp = `${result.hostIntroImageUrl}?t=${Date.now()}`;
        setHostIntroSceneImageUrl(imageUrlWithTimestamp);

        // Update the action using the callback
        if (onActionUpdate) {
          onActionUpdate(action.id, { host_intro_scene_image_url: result.hostIntroImageUrl });
          console.log('[Round Analysis] Host intro image URL updated in UI');
        }
      }
    } catch (error) {
      console.error('Error generating host intro scene image:', error);
      alert(`Failed to generate scene image: ${error.message}`);
    } finally {
      setGeneratingHostIntroImage(false);
    }
  };

  const generateCustomHostIntro = async () => {
    if (!selectedSpace || !customHostDialogue.trim()) {
      alert('Please select a space and enter dialogue text');
      return;
    }

    if (customHostDialogue.length > 500) {
      alert('Dialogue too long. Maximum 500 characters allowed.');
      return;
    }

    setGeneratingCustomHostIntro(true);

    try {
      const response = await fetch(
        `${API_BASE_URL}/api/takeGameStep?generateCustomHostIntro=true&actionId=${action.id}&spaceName=${encodeURIComponent(selectedSpace)}&dialogue=${encodeURIComponent(customHostDialogue)}`,
        { method: 'GET' }
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to generate custom host intro: ${errorText}`);
      }

      const result = await parseApiResponse(response);

      if (result.success && result.customIntro) {
        // Add new custom intro to the list
        setCustomHostIntros(prev => [...prev, result.customIntro]);

        // Update the action data
        if (onActionUpdate) {
          onActionUpdate(action.id, {
            custom_host_intros: [...customHostIntros, result.customIntro]
          });
        }

        // Clear inputs
        setCustomHostDialogue('');
        setSelectedSpace('');
      }
    } catch (error) {
      console.error('Error generating custom host intro:', error);
      alert(`Failed to generate custom host intro: ${error.message}`);
    } finally {
      setGeneratingCustomHostIntro(false);
    }
  };

  const toggleKeyInsightPreview = async (insightType) => {
    const isCurrentlyShowing = showingPreviews[insightType];

    setShowingPreviews(prev => ({ ...prev, [insightType]: !isCurrentlyShowing }));

    // If opening preview and we don't have prompt yet, fetch it
    if (!isCurrentlyShowing && !previewPrompts[insightType]) {
      setLoadingPreviews(prev => ({ ...prev, [insightType]: true }));
      try {
        const keyInsightText = insightType === 'keyInsightA' ? data.key_insight_a : data.key_insight_b;

        // Generate preview prompt (just the enhanced prompt without actually generating video)
        const response = await fetch(`${API_BASE_URL}/api/generateAcademicCommentary`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            game_instance_id: action.game_instance_id,
            round_id: action.game_instance_round_id,
            key_insight: keyInsightText,
            preview_only: true // Add flag to only return prompt without generating video
          })
        });

        const result = await parseApiResponse(response);

        // Parse the body if it's in API Gateway format
        const previewData = typeof result.body === 'string' ? JSON.parse(result.body) : result;

        if (previewData.enhanced_prompt) {
          setPreviewPrompts(prev => ({
            ...prev,
            [insightType]: {
              prompt: previewData.enhanced_prompt,
              referenceImage: previewData.reference_image
            }
          }));
        }
      } catch (error) {
        console.error('[Academic Commentary] Error fetching preview:', error);
        alert(`Failed to load preview: ${error.message}`);
      } finally {
        setLoadingPreviews(prev => ({ ...prev, [insightType]: false }));
      }
    }
  };

  const handleGenerateKeyInsightVideo = async (insightType) => {
    setGeneratingVideos(prev => ({ ...prev, [insightType]: true }));
    try {
      const keyInsightText = insightType === 'keyInsightA' ? data.key_insight_a : data.key_insight_b;
      console.log('[Academic Video] Generating video for key insight:', keyInsightText);

      const response = await fetch(`${API_BASE_URL}/api/generateAcademicCommentary`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          game_instance_id: action.game_instance_id,
          round_id: action.game_instance_round_id,
          key_insight: keyInsightText
        })
      });

      const result = await parseApiResponse(response);

      if (!result.success || !result.video_url) {
        throw new Error(result.error || 'Video generation failed - no video URL returned');
      }

      console.log('[Academic Video] Video generated:', result.video_url);

      // Map insightType to field name
      const fieldName = insightType === 'keyInsightA' ? 'key_insight_a_video_url' : 'key_insight_b_video_url';

      // Update the action using the callback
      if (onActionUpdate) {
        onActionUpdate(action.id, { [fieldName]: result.video_url });
        console.log(`[Academic Video] Video URL updated in UI for ${insightType}`);
      }
    } catch (error) {
      console.error('[Academic Video] Error:', error);
      const errorMsg = error.message || error.toString() || 'Unknown error';
      alert(`Failed to generate video: ${errorMsg}`);
    } finally {
      setGeneratingVideos(prev => ({ ...prev, [insightType]: false }));
    }
  };

  // Helper function to render a key insight section
  const renderKeyInsightSection = (insightType, label, insightText) => {
    if (!insightText) return null;

    return (
      <div style={{
        background: 'linear-gradient(135deg, rgba(20, 20, 35, 0.95) 0%, rgba(30, 25, 40, 0.95) 100%)',
        padding: '16px 20px',
        borderRadius: '12px',
        marginBottom: '12px',
        border: `2px solid transparent`,
        backgroundImage: `
          linear-gradient(135deg, rgba(20, 20, 35, 0.95) 0%, rgba(30, 25, 40, 0.95) 100%),
          linear-gradient(135deg, #8b5cf6 0%, #ec4899 100%)
        `,
        backgroundOrigin: 'border-box',
        backgroundClip: 'padding-box, border-box',
        boxShadow: '0 4px 12px rgba(139, 92, 246, 0.15)'
      }}>
        <div style={{
          fontWeight: 'bold',
          background: 'linear-gradient(135deg, #c4b5fd 0%, #f9a8d4 100%)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          backgroundClip: 'text',
          fontSize: '0.95em',
          marginBottom: '8px'
        }}>
          🎓 {label}:
        </div>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: '12px'
        }}>
          <div style={{
            color: '#e5e7eb',
            fontSize: '0.9em',
            lineHeight: '1.5',
            fontStyle: 'italic',
            flex: 1
          }}>
            {insightText}
          </div>
          <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
            <button
              onClick={() => toggleKeyInsightPreview(insightType)}
              style={{
                backgroundColor: showingPreviews[insightType] ? THEME.accent : THEME.borderLight,
                color: THEME.textDark,
                border: 'none',
                padding: '6px 12px',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '0.85em',
                fontWeight: 'bold'
              }}
            >
              {showingPreviews[insightType] ? '👁️ Hide Preview' : '👁️ Preview'}
            </button>
            <button
              onClick={() => handleGenerateKeyInsightVideo(insightType)}
              disabled={generatingVideos[insightType]}
              style={{
                backgroundColor: generatingVideos[insightType] ? THEME.textMuted : THEME.primary,
                color: THEME.textLight,
                border: 'none',
                padding: '6px 12px',
                borderRadius: '4px',
                cursor: generatingVideos[insightType] ? 'not-allowed' : 'pointer',
                fontSize: '0.85em',
                fontWeight: 'bold',
                opacity: generatingVideos[insightType] ? 0.6 : 1
              }}
            >
              {generatingVideos[insightType] ? '🎬 Generating...' : '🎬 Generate Video'}
            </button>
          </div>
        </div>

        {showingPreviews[insightType] && (
          <div style={{
            marginTop: '12px',
            padding: '12px',
            backgroundColor: THEME.bgLight,
            borderRadius: '4px',
            border: `1px solid ${THEME.borderLight}`
          }}>
            <div style={{ fontWeight: 'bold', marginBottom: '12px', color: THEME.accent }}>
              📝 AI-Generated Video Prompt:
            </div>

            {loadingPreviews[insightType] ? (
              <div style={{
                padding: '20px',
                textAlign: 'center',
                color: THEME.textMuted,
                fontStyle: 'italic'
              }}>
                🤖 Generating prompt with AI...
              </div>
            ) : previewPrompts[insightType] ? (
              <div>
                {previewPrompts[insightType].referenceImage && (
                  <div style={{ marginBottom: '12px' }}>
                    <div style={{
                      fontWeight: 'bold',
                      fontSize: '0.85em',
                      color: THEME.accent,
                      marginBottom: '8px'
                    }}>
                      🖼️ Reference Image:
                    </div>
                    <div style={{
                      display: 'inline-block'
                    }}>
                      <img
                        src={previewPrompts[insightType].referenceImage?.startsWith('http')
                          ? previewPrompts[insightType].referenceImage
                          : `${API_BASE_URL}/media/profile-images/${previewPrompts[insightType].referenceImage}`}
                        alt="Academic Profile"
                        style={{
                          width: '120px',
                          height: '120px',
                          borderRadius: '8px',
                          objectFit: 'cover',
                          border: `2px solid ${THEME.borderLight}`,
                          boxShadow: '0 4px 8px rgba(0, 0, 0, 0.3)'
                        }}
                      />
                      <div style={{
                        fontSize: '0.75em',
                        color: THEME.textMuted,
                        marginTop: '4px',
                        textAlign: 'center'
                      }}>
                        {previewPrompts[insightType].referenceImage}
                      </div>
                    </div>
                  </div>
                )}
                <div style={{
                  fontWeight: 'bold',
                  fontSize: '0.85em',
                  color: THEME.accent,
                  marginBottom: '8px'
                }}>
                  📄 Video Prompt:
                </div>
                <div style={{
                  color: THEME.textLight,
                  fontSize: '0.9em',
                  lineHeight: '1.6',
                  whiteSpace: 'pre-wrap',
                  fontFamily: 'monospace',
                  backgroundColor: `${THEME.bgDark}cc`,
                  padding: '12px',
                  borderRadius: '4px'
                }}>
                  {previewPrompts[insightType].prompt}
                </div>
              </div>
            ) : (
              <div style={{ color: THEME.textMuted, fontStyle: 'italic' }}>
                No preview available
              </div>
            )}
          </div>
        )}

        {/* Display video if it exists */}
        {data[insightType === 'keyInsightA' ? 'key_insight_a_video_url' : 'key_insight_b_video_url'] && (
          <div style={{ marginTop: '12px' }}>
            <video
              controls
              draggable="true"
              onDragStart={(e) => {
                const videoUrlField = insightType === 'keyInsightA' ? 'key_insight_a_video_url' : 'key_insight_b_video_url';
                const url = data[videoUrlField];
                const filename = url.split('/').pop().split('?')[0];
                const videoData = {
                  url: url,
                  filename: filename,
                  title: label,
                  type: `analysis-key-insight-${insightType === 'keyInsightA' ? 'a' : 'b'}`,
                  metadata: {
                    keyInsight: insightText,
                    roundId: action.game_instance_round_id
                  }
                };
                e.dataTransfer.setData('video-data', JSON.stringify(videoData));
                e.dataTransfer.effectAllowed = 'copy';
              }}
              style={{
                width: '100%',
                maxWidth: '600px',
                borderRadius: '4px',
                cursor: 'grab'
              }}
            >
              <source src={data[insightType === 'keyInsightA' ? 'key_insight_a_video_url' : 'key_insight_b_video_url']} type="video/mp4" />
              Your browser does not support the video tag.
            </video>
          </div>
        )}
      </div>
    );
  };

  return (
    <div style={{
      background: 'linear-gradient(135deg, rgba(10, 10, 20, 0.98) 0%, rgba(15, 15, 25, 0.98) 100%)',
      border: `2px solid transparent`,
      backgroundImage: `
        linear-gradient(135deg, rgba(10, 10, 20, 0.98) 0%, rgba(15, 15, 25, 0.98) 100%),
        linear-gradient(135deg, #8b5cf6 0%, #ec4899 100%)
      `,
      backgroundOrigin: 'border-box',
      backgroundClip: 'padding-box, border-box',
      borderRadius: '8px',
      padding: '16px',
      marginBottom: '12px',
      boxShadow: '0 8px 24px rgba(139, 92, 246, 0.3)'
    }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '16px'
      }}>
        <div style={{
          background: 'linear-gradient(135deg, #5b21b6 0%, #6b21a8 100%)',
          color: '#ffffff',
          padding: '8px 18px',
          borderRadius: '6px',
          fontSize: '1.15em',
          fontWeight: 'bold',
          boxShadow: '0 4px 12px rgba(139, 92, 246, 0.3)',
          border: '1px solid rgba(139, 92, 246, 0.3)'
        }}>
          📊 Round Analysis by {data.model}
        </div>
        <div style={{ fontSize: '0.85em', color: '#9ca3af' }}>
          {formatActionTime(action.created_at)}
        </div>
      </div>

      {/* Host Intro */}
      {data.host_intro && (
        <div style={{
          background: 'linear-gradient(135deg, rgba(20, 20, 35, 0.95) 0%, rgba(30, 25, 40, 0.95) 100%)',
          padding: '20px',
          borderRadius: '12px',
          marginBottom: '20px',
          border: `2px solid transparent`,
          backgroundImage: `
            linear-gradient(135deg, rgba(20, 20, 35, 0.95) 0%, rgba(30, 25, 40, 0.95) 100%),
            linear-gradient(135deg, #8b5cf6 0%, #ec4899 100%)
          `,
          backgroundOrigin: 'border-box',
          backgroundClip: 'padding-box, border-box',
          boxShadow: '0 8px 24px rgba(139, 92, 246, 0.2)',
          fontStyle: 'italic',
          position: 'relative'
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '12px'
          }}>
            <div style={{
              fontWeight: 'bold',
              background: 'linear-gradient(135deg, #c4b5fd 0%, #f9a8d4 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              fontSize: '1.05em'
            }}>
              🎙️ Host Introduction:
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                onClick={() => togglePreview('hostIntro')}
                style={{
                  backgroundColor: showingPreviews.hostIntro ? THEME.accent : THEME.borderLight,
                  color: THEME.textDark,
                  border: 'none',
                  padding: '6px 12px',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '0.85em',
                  fontWeight: 'bold'
                }}
              >
                {showingPreviews.hostIntro ? '👁️ Hide Preview' : '👁️ Preview'}
              </button>
              <button
                onClick={() => generateAnalysisVideo('hostIntro')}
                disabled={generatingVideos.hostIntro}
                style={{
                  backgroundColor: generatingVideos.hostIntro ? THEME.textMuted : THEME.primary,
                  color: THEME.textLight,
                  border: 'none',
                  padding: '6px 12px',
                  borderRadius: '4px',
                  cursor: generatingVideos.hostIntro ? 'not-allowed' : 'pointer',
                  fontSize: '0.85em',
                  fontWeight: 'bold',
                  opacity: generatingVideos.hostIntro ? 0.6 : 1
                }}
              >
                {generatingVideos.hostIntro ? '🎬 Generating...' : '🎬 Generate Video'}
              </button>
              <button
                onClick={() => generateHostIntroImage()}
                disabled={generatingHostIntroImage}
                style={{
                  backgroundColor: generatingHostIntroImage ? THEME.textMuted : '#FF9800',
                  color: THEME.textLight,
                  border: 'none',
                  padding: '6px 12px',
                  borderRadius: '4px',
                  cursor: generatingHostIntroImage ? 'not-allowed' : 'pointer',
                  fontSize: '0.85em',
                  fontWeight: 'bold',
                  opacity: generatingHostIntroImage ? 0.6 : 1,
                  transition: 'background-color 0.2s'
                }}
              >
                {generatingHostIntroImage ? '🎨 Generating...' : '🎨 Generate Image'}
              </button>
            </div>
          </div>
          <div style={{ whiteSpace: 'pre-wrap', lineHeight: '1.6', color: '#e5e7eb', fontSize: '0.95em' }}>
            {data.host_intro}
          </div>
          {hostIntroSceneImageUrl && (
            <div style={{ marginTop: '12px' }}>
              <img
                src={hostIntroSceneImageUrl}
                alt="Host Introduction Scene"
                style={{
                  width: '100%',
                  maxWidth: '600px',
                  borderRadius: '4px',
                  border: `1px solid ${THEME.borderLight}`
                }}
              />
            </div>
          )}
          {showingPreviews.hostIntro && (
            <div style={{
              marginTop: '12px',
              padding: '12px',
              backgroundColor: THEME.bgLight,
              borderRadius: '4px',
              border: `1px solid ${THEME.borderLight}`
            }}>
              <div style={{ fontWeight: 'bold', marginBottom: '12px', color: THEME.accent }}>
                📝 AI-Generated Video Prompt:
              </div>

              {loadingPreviews.hostIntro ? (
                <div style={{
                  padding: '20px',
                  textAlign: 'center',
                  color: THEME.textMuted,
                  fontStyle: 'italic'
                }}>
                  🤖 Generating prompt with AI...
                </div>
              ) : (
                <>
                  <div style={{ marginBottom: '12px' }}>
                    <div style={{
                      fontSize: '0.85em',
                      fontWeight: 'bold',
                      marginBottom: '6px',
                      color: THEME.primary
                    }}>
                      🖼️ Reference Images:
                    </div>
                    <div style={{
                      fontSize: '0.85em',
                      color: THEME.textDark,
                      paddingLeft: '12px',
                      fontStyle: 'italic'
                    }}>
                      • Host profile image (used as character reference)
                    </div>
                  </div>

                  <div>
                    {previewReferenceImages.hostIntro && (
                      <div style={{ marginBottom: '12px' }}>
                        <div style={{
                          fontSize: '0.85em',
                          fontWeight: 'bold',
                          marginBottom: '6px',
                          color: THEME.primary
                        }}>
                          🖼️ Reference Images:
                        </div>
                        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                          <div style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '4px',
                            alignItems: 'center'
                          }}>
                            <img
                              src={`${API_BASE_URL}/media/profile-images/${previewReferenceImages.hostIntro}`}
                              alt="Host profile"
                              style={{
                                width: '100px',
                                height: '100px',
                                objectFit: 'cover',
                                borderRadius: '8px',
                                border: `2px solid ${THEME.primary}`
                              }}
                            />
                            <div style={{
                              fontSize: '0.75em',
                              color: THEME.textMuted
                            }}>
                              Host profile image (used as character reference)
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                    <div style={{
                      fontSize: '0.85em',
                      fontWeight: 'bold',
                      marginBottom: '6px',
                      color: THEME.primary
                    }}>
                      📜 Video Prompt:
                    </div>
                    <div style={{
                      fontStyle: 'normal',
                      fontSize: '0.85em',
                      color: THEME.textDark,
                      whiteSpace: 'pre-wrap',
                      lineHeight: '1.8',
                      paddingLeft: '12px',
                      borderLeft: `3px solid ${THEME.borderLight}`,
                      paddingTop: '8px',
                      paddingBottom: '8px',
                      fontFamily: 'monospace'
                    }}>
                      {previewPrompts.hostIntro || 'Loading...'}
                    </div>
                  </div>

                  <div style={{
                    marginTop: '12px',
                    fontSize: '0.75em',
                    color: THEME.textMuted,
                    fontStyle: 'italic'
                  }}>
                    Note: This prompt was generated by AI using the game's host character description, visual style, and setting context. It matches the same detailed structure used for scene host intros.
                  </div>
                </>
              )}
            </div>
          )}
          {data.host_intro_video_url && (
            <div style={{ marginTop: '12px' }}>
              <video
                controls
                draggable="true"
                onDragStart={(e) => {
                  const url = data.host_intro_video_url;
                  const filename = url.split('/').pop().split('?')[0];
                  const videoData = {
                    url: url,
                    filename: filename,
                    title: `Host Introduction`,
                    type: 'analysis-host-intro',
                    metadata: {
                      hostIntro: data.host_intro,
                      roundId: action.game_instance_round_id
                    }
                  };
                  e.dataTransfer.setData('video-data', JSON.stringify(videoData));
                  e.dataTransfer.effectAllowed = 'copy';
                }}
                style={{
                  width: '100%',
                  maxWidth: '600px',
                  borderRadius: '4px',
                  cursor: 'grab'
                }}
              >
                <source src={data.host_intro_video_url} type="video/mp4" />
                Your browser does not support the video tag.
              </video>
            </div>
          )}

          {/* Custom Host Intro Generator */}
          <div style={{
            marginTop: '20px',
            padding: '16px',
            backgroundColor: 'rgba(139, 92, 246, 0.05)',
            borderRadius: '8px',
            border: '1px solid rgba(139, 92, 246, 0.2)'
          }}>
            <div style={{
              fontWeight: 'bold',
              color: '#c4b5fd',
              marginBottom: '12px',
              fontSize: '0.95em'
            }}>
              ➕ Generate Custom Host Introduction
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div>
                <label style={{
                  fontSize: '0.85em',
                  color: '#e5e7eb',
                  display: 'block',
                  marginBottom: '6px'
                }}>
                  Space:
                </label>
                <select
                  value={selectedSpace}
                  onChange={(e) => setSelectedSpace(e.target.value)}
                  disabled={availableSpaces.length === 0}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    borderRadius: '4px',
                    border: '1px solid rgba(139, 92, 246, 0.3)',
                    backgroundColor: 'rgba(20, 20, 35, 0.8)',
                    color: '#e5e7eb',
                    fontSize: '0.85em',
                    cursor: availableSpaces.length === 0 ? 'not-allowed' : 'pointer'
                  }}
                >
                  {availableSpaces.length === 0 ? (
                    <option value="">Loading spaces...</option>
                  ) : (
                    <>
                      <option value="">Select a space...</option>
                      {availableSpaces.map((space, index) => (
                        <option key={index} value={space.name}>
                          {space.name}
                        </option>
                      ))}
                    </>
                  )}
                </select>
              </div>

              <div>
                <label style={{
                  fontSize: '0.85em',
                  color: '#e5e7eb',
                  display: 'block',
                  marginBottom: '6px'
                }}>
                  Dialogue ({customHostDialogue.length}/500):
                </label>
                <textarea
                  value={customHostDialogue}
                  onChange={(e) => setCustomHostDialogue(e.target.value)}
                  placeholder="Enter what the host should say..."
                  rows={4}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    borderRadius: '4px',
                    border: '1px solid rgba(139, 92, 246, 0.3)',
                    backgroundColor: 'rgba(20, 20, 35, 0.8)',
                    color: '#e5e7eb',
                    fontSize: '0.85em',
                    fontFamily: 'inherit',
                    resize: 'vertical'
                  }}
                />
                <div style={{
                  fontSize: '0.75em',
                  color: '#9ca3af',
                  marginTop: '4px'
                }}>
                  Will be condensed to ~40 words if longer. The host will say this exactly.
                </div>
              </div>

              <button
                onClick={generateCustomHostIntro}
                disabled={generatingCustomHostIntro || !selectedSpace || !customHostDialogue.trim()}
                style={{
                  backgroundColor: generatingCustomHostIntro || !selectedSpace || !customHostDialogue.trim()
                    ? THEME.textMuted
                    : '#8b5cf6',
                  color: THEME.textLight,
                  border: 'none',
                  padding: '10px 16px',
                  borderRadius: '6px',
                  cursor: generatingCustomHostIntro || !selectedSpace || !customHostDialogue.trim()
                    ? 'not-allowed'
                    : 'pointer',
                  fontSize: '0.9em',
                  fontWeight: 'bold',
                  opacity: generatingCustomHostIntro || !selectedSpace || !customHostDialogue.trim() ? 0.6 : 1,
                  transition: 'all 0.2s'
                }}
              >
                {generatingCustomHostIntro ? '🎬 Generating Video...' : '🎬 Generate Custom Host Intro'}
              </button>
            </div>
          </div>

          {/* Custom Host Intros List */}
          {customHostIntros && customHostIntros.length > 0 && (
            <div style={{
              marginTop: '16px',
              padding: '16px',
              backgroundColor: 'rgba(16, 185, 129, 0.05)',
              borderRadius: '8px',
              border: '1px solid rgba(16, 185, 129, 0.2)'
            }}>
              <div style={{
                fontWeight: 'bold',
                color: '#6ee7b7',
                marginBottom: '12px',
                fontSize: '0.95em'
              }}>
                📹 Custom Host Intros ({customHostIntros.length})
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {customHostIntros.map((intro, index) => (
                  <div
                    key={intro.id || index}
                    style={{
                      backgroundColor: 'rgba(20, 20, 35, 0.6)',
                      padding: '12px',
                      borderRadius: '6px',
                      border: '1px solid rgba(16, 185, 129, 0.2)'
                    }}
                  >
                    <div style={{
                      fontSize: '0.8em',
                      color: '#9ca3af',
                      marginBottom: '8px'
                    }}>
                      <strong style={{ color: '#6ee7b7' }}>Space:</strong> {intro.space_name} • <strong style={{ color: '#6ee7b7' }}>Created:</strong> {new Date(intro.created_at).toLocaleString()}
                    </div>

                    <div style={{
                      fontSize: '0.85em',
                      color: '#e5e7eb',
                      marginBottom: '12px',
                      fontStyle: 'italic',
                      whiteSpace: 'pre-wrap'
                    }}>
                      "{intro.condensed_dialogue || intro.dialogue}"
                    </div>

                    {intro.video_url && (
                      <video
                        controls
                        draggable="true"
                        onDragStart={(e) => {
                          const url = intro.video_url;
                          const filename = url.split('/').pop().split('?')[0];
                          const videoData = {
                            url: url,
                            filename: filename,
                            title: `Custom Host Intro - ${intro.space_name}`,
                            type: 'analysis-custom-host-intro',
                            metadata: {
                              dialogue: intro.dialogue,
                              spaceName: intro.space_name,
                              roundId: action.game_instance_round_id
                            }
                          };
                          e.dataTransfer.setData('video-data', JSON.stringify(videoData));
                          e.dataTransfer.effectAllowed = 'copy';
                        }}
                        style={{
                          width: '100%',
                          maxWidth: '600px',
                          borderRadius: '4px',
                          cursor: 'grab'
                        }}
                      >
                        <source src={intro.video_url} type="video/mp4" />
                        Your browser does not support the video tag.
                      </video>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Round Analysis */}
      <div style={{
        background: 'linear-gradient(135deg, rgba(20, 20, 35, 0.95) 0%, rgba(30, 25, 40, 0.95) 100%)',
        padding: '20px',
        borderRadius: '12px',
        marginBottom: '20px',
        border: `2px solid transparent`,
        backgroundImage: `
          linear-gradient(135deg, rgba(20, 20, 35, 0.95) 0%, rgba(30, 25, 40, 0.95) 100%),
          linear-gradient(135deg, #8b5cf6 0%, #ec4899 100%)
        `,
        backgroundOrigin: 'border-box',
        backgroundClip: 'padding-box, border-box',
        boxShadow: '0 8px 24px rgba(139, 92, 246, 0.2)'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '12px'
        }}>
          <div style={{
            fontWeight: 'bold',
            background: 'linear-gradient(135deg, #c4b5fd 0%, #f9a8d4 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            fontSize: '1.05em'
          }}>Round Analysis:</div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              onClick={() => togglePreview('roundAnalysis')}
              style={{
                backgroundColor: showingPreviews.roundAnalysis ? THEME.accent : THEME.borderLight,
                color: THEME.textDark,
                border: 'none',
                padding: '6px 12px',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '0.85em',
                fontWeight: 'bold'
              }}
            >
              {showingPreviews.roundAnalysis ? '👁️ Hide Preview' : '👁️ Preview'}
            </button>
            <button
              onClick={() => generateAnalysisVideo('roundAnalysis')}
              disabled={generatingVideos.roundAnalysis}
              style={{
                backgroundColor: generatingVideos.roundAnalysis ? THEME.textMuted : THEME.accent,
                color: THEME.textDark,
                border: 'none',
                padding: '6px 12px',
                borderRadius: '4px',
                cursor: generatingVideos.roundAnalysis ? 'not-allowed' : 'pointer',
                fontSize: '0.85em',
                fontWeight: 'bold',
                opacity: generatingVideos.roundAnalysis ? 0.6 : 1
              }}
            >
              {generatingVideos.roundAnalysis ? '🎬 Generating...' : '🎬 Generate Video'}
            </button>
          </div>
        </div>
        <div style={{ whiteSpace: 'pre-wrap', lineHeight: '1.6', color: '#e5e7eb', fontSize: '0.95em' }}>
          {data.round_analysis}
        </div>
        {showingPreviews.roundAnalysis && (
          <div style={{
            marginTop: '12px',
            padding: '12px',
            backgroundColor: THEME.bgLight,
            borderRadius: '4px',
            border: `1px solid ${THEME.borderLight}`
          }}>
            <div style={{ fontWeight: 'bold', marginBottom: '8px', color: THEME.accent }}>
              📝 Video Script Preview:
            </div>
            <div style={{
              fontSize: '0.9em',
              color: THEME.textDark,
              whiteSpace: 'pre-wrap',
              lineHeight: '1.6'
            }}>
              {`An AI analyst or sports commentator discussing reality TV strategy. Dynamic setting with graphics and highlights. Professional yet entertaining tone. The analyst says: "${data.round_analysis}"`}
            </div>
          </div>
        )}
        {data.round_analysis_video_url && (
          <div style={{ marginTop: '12px' }}>
            <video
              controls
              draggable="true"
              onDragStart={(e) => {
                const url = data.round_analysis_video_url;
                const filename = url.split('/').pop().split('?')[0];
                const videoData = {
                  url: url,
                  filename: filename,
                  title: `Round Analysis`,
                  type: 'analysis-round',
                  metadata: {
                    roundAnalysis: data.round_analysis,
                    roundId: action.game_instance_round_id
                  }
                };
                e.dataTransfer.setData('video-data', JSON.stringify(videoData));
                e.dataTransfer.effectAllowed = 'copy';
              }}
              style={{
                width: '100%',
                maxWidth: '600px',
                borderRadius: '4px',
                cursor: 'grab'
              }}
            >
              <source src={data.round_analysis_video_url} type="video/mp4" />
              Your browser does not support the video tag.
            </video>
          </div>
        )}
      </div>

      {/* Key Insights */}
      {renderKeyInsightSection('keyInsightA', 'Key Insight A', data.key_insight_a)}
      {renderKeyInsightSection('keyInsightB', 'Key Insight B', data.key_insight_b)}

      {/* Academic Analysis */}
      <div style={{
        background: 'linear-gradient(135deg, rgba(20, 20, 35, 0.95) 0%, rgba(30, 25, 40, 0.95) 100%)',
        padding: '20px',
        borderRadius: '12px',
        marginBottom: '20px',
        border: `2px solid transparent`,
        backgroundImage: `
          linear-gradient(135deg, rgba(20, 20, 35, 0.95) 0%, rgba(30, 25, 40, 0.95) 100%),
          linear-gradient(135deg, #8b5cf6 0%, #ec4899 100%)
        `,
        backgroundOrigin: 'border-box',
        backgroundClip: 'padding-box, border-box',
        boxShadow: '0 8px 24px rgba(139, 92, 246, 0.2)'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '12px'
        }}>
          <div style={{
            fontWeight: 'bold',
            background: 'linear-gradient(135deg, #c4b5fd 0%, #f9a8d4 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            fontSize: '1.05em'
          }}>Academic Analysis (Full):</div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              onClick={() => togglePreview('academicAnalysis')}
              style={{
                backgroundColor: showingPreviews.academicAnalysis ? THEME.accent : THEME.borderLight,
                color: THEME.textDark,
                border: 'none',
                padding: '6px 12px',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '0.85em',
                fontWeight: 'bold'
              }}
            >
              {showingPreviews.academicAnalysis ? '👁️ Hide Preview' : '👁️ Preview'}
            </button>
            <button
              onClick={() => generateAnalysisVideo('academicAnalysis')}
              disabled={generatingVideos.academicAnalysis}
              style={{
                backgroundColor: generatingVideos.academicAnalysis ? THEME.textMuted : THEME.secondary,
                color: THEME.textDark,
                border: 'none',
                padding: '6px 12px',
                borderRadius: '4px',
                cursor: generatingVideos.academicAnalysis ? 'not-allowed' : 'pointer',
                fontSize: '0.85em',
                fontWeight: 'bold',
                opacity: generatingVideos.academicAnalysis ? 0.6 : 1
              }}
            >
              {generatingVideos.academicAnalysis ? '🎬 Generating...' : '🎬 Generate Video'}
            </button>
          </div>
        </div>
        <div style={{ whiteSpace: 'pre-wrap', lineHeight: '1.6', color: '#e5e7eb', fontSize: '0.95em' }}>
          {data.academic_analysis}
        </div>
        {showingPreviews.academicAnalysis && (
          <div style={{
            marginTop: '12px',
            padding: '12px',
            backgroundColor: THEME.bgLight,
            borderRadius: '4px',
            border: `1px solid ${THEME.borderLight}`
          }}>
            <div style={{ fontWeight: 'bold', marginBottom: '8px', color: THEME.accent }}>
              📝 Video Script Preview:
            </div>
            <div style={{
              fontSize: '0.9em',
              color: THEME.textDark,
              whiteSpace: 'pre-wrap',
              lineHeight: '1.6'
            }}>
              {`An academic researcher or professor presenting findings. Professional academic setting with charts and data visualizations. Serious, scholarly tone. The researcher says: "${data.academic_analysis}"`}
            </div>
          </div>
        )}
        {data.academic_analysis_video_url && (
          <div style={{ marginTop: '12px' }}>
            <video
              controls
              draggable="true"
              onDragStart={(e) => {
                const url = data.academic_analysis_video_url;
                const filename = url.split('/').pop().split('?')[0];
                const videoData = {
                  url: url,
                  filename: filename,
                  title: `Academic Analysis`,
                  type: 'analysis-academic',
                  metadata: {
                    academicAnalysis: data.academic_analysis,
                    roundId: action.game_instance_round_id
                  }
                };
                e.dataTransfer.setData('video-data', JSON.stringify(videoData));
                e.dataTransfer.effectAllowed = 'copy';
              }}
              style={{
                width: '100%',
                maxWidth: '600px',
                borderRadius: '4px',
                cursor: 'grab'
              }}
            >
              <source src={data.academic_analysis_video_url} type="video/mp4" />
              Your browser does not support the video tag.
            </video>
          </div>
        )}
      </div>

      {/* Benchmarks - showing both round and cumulative scores */}
      {((data.round_benchmarks && data.round_benchmarks.length > 0) ||
        (data.cumulative_benchmarks && data.cumulative_benchmarks.length > 0) ||
        (data.benchmarks && data.benchmarks.length > 0)) && (
        <div style={{
          background: 'linear-gradient(135deg, rgba(20, 20, 35, 0.95) 0%, rgba(30, 25, 40, 0.95) 100%)',
          padding: '24px',
          borderRadius: '12px',
          border: `2px solid transparent`,
          backgroundImage: `
            linear-gradient(135deg, rgba(20, 20, 35, 0.95) 0%, rgba(30, 25, 40, 0.95) 100%),
            linear-gradient(135deg, #8b5cf6 0%, #ec4899 100%)
          `,
          backgroundOrigin: 'border-box',
          backgroundClip: 'padding-box, border-box',
          boxShadow: '0 8px 24px rgba(139, 92, 246, 0.2)'
        }}>
          {/* Display both benchmark types side-by-side when available */}
          {(() => {
            // If we have both round and cumulative benchmarks, show them side-by-side
            if (data.round_benchmarks && data.cumulative_benchmarks) {
              return (
                <>
                  <div style={{
                    marginBottom: '24px',
                    fontWeight: 'bold',
                    fontSize: '1.5em',
                    background: 'linear-gradient(135deg, #c4b5fd 0%, #f9a8d4 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                    letterSpacing: '-0.02em',
                    filter: 'drop-shadow(0 0 12px rgba(139, 92, 246, 0.4))',
                    textTransform: 'uppercase',
                    textAlign: 'center'
                  }}>
                    Performance Benchmarks
                  </div>
                  {(() => {
                    // Get all unique benchmark names from both round and cumulative benchmarks
                    const allBenchmarkNames = new Set([
                      ...(data.round_benchmarks || []).map(b => b.benchmark_name),
                      ...(data.cumulative_benchmarks || []).map(b => b.benchmark_name)
                    ]);

                    return Array.from(allBenchmarkNames).map((benchmarkName, index) => {
                      // Find the benchmarks by name
                      const roundBenchmark = data.round_benchmarks?.find(
                        b => b.benchmark_name === benchmarkName
                      );
                      const cumulativeBenchmark = data.cumulative_benchmarks?.find(
                        b => b.benchmark_name === benchmarkName
                      );

                      // Skip if neither has scores (both missing or both empty)
                      const hasRoundScores = roundBenchmark && roundBenchmark.scores;
                      const hasCumulativeScores = cumulativeBenchmark && cumulativeBenchmark.scores;

                      if (!hasRoundScores && !hasCumulativeScores) {
                        return null;
                      }

                      return (
                      <div key={index} style={{
                        marginBottom: '28px',
                        padding: '20px',
                        borderRadius: '10px',
                        background: 'rgba(30, 30, 45, 0.5)',
                        border: '1px solid rgba(139, 92, 246, 0.2)'
                      }}>
                        <div style={{
                          fontWeight: '600',
                          marginBottom: '16px',
                          fontSize: '1.15em',
                          background: 'linear-gradient(135deg, #c4b5fd 0%, #f9a8d4 100%)',
                          WebkitBackgroundClip: 'text',
                          WebkitTextFillColor: 'transparent',
                          backgroundClip: 'text',
                          paddingLeft: '12px',
                          borderLeft: '4px solid transparent',
                          borderImage: 'linear-gradient(135deg, #8b5cf6 0%, #ec4899 100%) 1',
                          filter: 'drop-shadow(0 0 8px rgba(139, 92, 246, 0.3))',
                          textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)'
                        }}>
                          {benchmarkName}
                        </div>

                        {/* Get all unique players from both benchmark sets */}
                        {(() => {
                          const allPlayers = new Set([
                            ...(hasRoundScores ? Object.keys(roundBenchmark.scores) : []),
                            ...(hasCumulativeScores ? Object.keys(cumulativeBenchmark.scores) : [])
                          ]);
                          const sortedPlayers = Array.from(allPlayers).sort((a, b) => {
                            const scoreA = hasCumulativeScores ? (getScoreValue(cumulativeBenchmark.scores[a]) || 0) : 0;
                            const scoreB = hasCumulativeScores ? (getScoreValue(cumulativeBenchmark.scores[b]) || 0) : 0;
                            return scoreB - scoreA;
                          });

                          return (
                            <div style={{
                              display: 'grid',
                              gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
                              gap: '12px'
                            }}>
                              {sortedPlayers.map(playerName => {
                                const processedName = playerModelMap[playerName]
                                  ? `${playerName} (${playerModelMap[playerName]})`
                                  : playerName;
                                const roundScoreData = hasRoundScores ? roundBenchmark.scores[playerName] : null;
                                const cumulativeScoreData = hasCumulativeScores ? cumulativeBenchmark.scores[playerName] : null;
                                const roundScore = getScoreValue(roundScoreData);
                                const cumulativeScore = getScoreValue(cumulativeScoreData);
                                const roundExplanation = getScoreExplanation(roundScoreData);
                                const cumulativeExplanation = getScoreExplanation(cumulativeScoreData);

                                // Find user data for profile photo
                                const user = users?.find(u => u.name === playerName);
                                const profilePic = user?.profile_pic_url;

                                return (
                                  <div key={playerName} style={{
                                    background: 'linear-gradient(135deg, rgba(20, 20, 35, 0.95) 0%, rgba(30, 25, 40, 0.95) 100%)',
                                    padding: '14px',
                                    borderRadius: '12px',
                                    border: `2px solid transparent`,
                                    backgroundImage: `
                                      linear-gradient(135deg, rgba(20, 20, 35, 0.95) 0%, rgba(30, 25, 40, 0.95) 100%),
                                      linear-gradient(135deg, #8b5cf6 0%, #ec4899 100%)
                                    `,
                                    backgroundOrigin: 'border-box',
                                    backgroundClip: 'padding-box, border-box',
                                    boxShadow: '0 8px 24px rgba(139, 92, 246, 0.2), 0 0 0 1px rgba(139, 92, 246, 0.1) inset'
                                  }}>
                                    <div style={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: '12px',
                                      marginBottom: '12px'
                                    }}>
                                      {profilePic && (
                                        <img
                                          src={profilePic}
                                          alt={playerName}
                                          style={{
                                            width: '44px',
                                            height: '44px',
                                            borderRadius: '50%',
                                            objectFit: 'cover',
                                            border: `3px solid transparent`,
                                            backgroundImage: `
                                              linear-gradient(rgba(20, 20, 35, 0.95), rgba(20, 20, 35, 0.95)),
                                              linear-gradient(135deg, #8b5cf6 0%, #ec4899 100%)
                                            `,
                                            backgroundOrigin: 'border-box',
                                            backgroundClip: 'padding-box, border-box',
                                            boxShadow: '0 4px 12px rgba(139, 92, 246, 0.4), 0 0 20px rgba(236, 72, 153, 0.3)'
                                          }}
                                        />
                                      )}
                                      <div style={{
                                        fontWeight: '600',
                                        fontSize: '1em',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                        flex: 1,
                                        background: 'linear-gradient(135deg, #c4b5fd 0%, #f9a8d4 100%)',
                                        WebkitBackgroundClip: 'text',
                                        WebkitTextFillColor: 'transparent',
                                        backgroundClip: 'text',
                                        textShadow: '0 2px 8px rgba(139, 92, 246, 0.3)',
                                        filter: 'drop-shadow(0 0 8px rgba(139, 92, 246, 0.3))'
                                      }}>
                                        {processedName}
                                      </div>
                                    </div>
                                    <div style={{ display: 'flex', gap: '10px', fontSize: '0.85em' }}>
                                      <div style={{
                                        flex: 1,
                                        background: 'linear-gradient(135deg, rgba(15, 15, 25, 0.9) 0%, rgba(20, 18, 30, 0.9) 100%)',
                                        padding: '10px',
                                        borderRadius: '10px',
                                        textAlign: 'center',
                                        border: '1px solid rgba(139, 92, 246, 0.3)',
                                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(139, 92, 246, 0.1) inset'
                                      }}>
                                        <div style={{ fontSize: '0.7em', color: '#a78bfa', marginBottom: '4px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Round</div>
                                        <div style={{
                                          fontWeight: 'bold',
                                          fontSize: '1.4em',
                                          color: getScoreColor(roundScoreData),
                                          textShadow: `
                                            0 0 10px ${getScoreColor(roundScoreData)}40,
                                            0 0 20px ${getScoreColor(roundScoreData)}20,
                                            -1px -1px 0 rgba(255, 255, 255, 0.3),
                                            1px -1px 0 rgba(255, 255, 255, 0.3),
                                            -1px 1px 0 rgba(255, 255, 255, 0.3),
                                            1px 1px 0 rgba(255, 255, 255, 0.3)
                                          `,
                                          filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.5))'
                                        }}>
                                          {roundScore !== null ? roundScore.toFixed(1) : 'N/A'}
                                        </div>
                                        {roundExplanation && (
                                          <div style={{
                                            fontSize: '0.65em',
                                            color: '#9ca3af',
                                            marginTop: '6px',
                                            lineHeight: '1.3',
                                            fontStyle: 'italic'
                                          }}>
                                            {roundExplanation}
                                          </div>
                                        )}
                                      </div>
                                      <div style={{
                                        flex: 1,
                                        background: 'linear-gradient(135deg, rgba(15, 15, 25, 0.9) 0%, rgba(20, 18, 30, 0.9) 100%)',
                                        padding: '10px',
                                        borderRadius: '10px',
                                        textAlign: 'center',
                                        border: '1px solid rgba(236, 72, 153, 0.3)',
                                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(236, 72, 153, 0.1) inset'
                                      }}>
                                        <div style={{ fontSize: '0.7em', color: '#f9a8d4', marginBottom: '4px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Game</div>
                                        <div style={{
                                          fontWeight: 'bold',
                                          fontSize: '1.4em',
                                          color: getScoreColor(cumulativeScoreData),
                                          textShadow: `
                                            0 0 10px ${getScoreColor(cumulativeScoreData)}40,
                                            0 0 20px ${getScoreColor(cumulativeScoreData)}20,
                                            -1px -1px 0 rgba(255, 255, 255, 0.3),
                                            1px -1px 0 rgba(255, 255, 255, 0.3),
                                            -1px 1px 0 rgba(255, 255, 255, 0.3),
                                            1px 1px 0 rgba(255, 255, 255, 0.3)
                                          `,
                                          filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.5))'
                                        }}>
                                          {cumulativeScore !== null ? cumulativeScore.toFixed(1) : 'N/A'}
                                        </div>
                                        {cumulativeExplanation && (
                                          <div style={{
                                            fontSize: '0.65em',
                                            color: '#9ca3af',
                                            marginTop: '6px',
                                            lineHeight: '1.3',
                                            fontStyle: 'italic'
                                          }}>
                                            {cumulativeExplanation}
                                          </div>
                                        )}
                                        {(() => {
                                          const change = getBenchmarkChange(benchmarkName, playerName, cumulativeScoreData);
                                          if (change) {
                                            return (
                                              <div style={{
                                                fontSize: '0.8em',
                                                color: change.color,
                                                marginTop: '6px',
                                                fontWeight: 'bold',
                                                textShadow: `
                                                  0 0 10px ${change.color}60,
                                                  0 0 20px ${change.color}30,
                                                  -1px -1px 0 rgba(0, 0, 0, 0.5),
                                                  1px -1px 0 rgba(0, 0, 0, 0.5),
                                                  -1px 1px 0 rgba(0, 0, 0, 0.5),
                                                  1px 1px 0 rgba(0, 0, 0, 0.5)
                                                `,
                                                filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.3))'
                                              }}>
                                                {change.arrow} {change.change > 0 ? '+' : ''}{change.change.toFixed(1)}
                                              </div>
                                            );
                                          }
                                          return null;
                                        })()}
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          );
                        })()}
                      </div>
                    );
                    });
                  })()}
                </>
              );
            } else if (data.round_benchmarks || data.cumulative_benchmarks || data.benchmarks) {
              // Fallback for single benchmark set or legacy format
              const benchmarksToDisplay = data.round_benchmarks || data.cumulative_benchmarks || data.benchmarks;
              const title = data.round_benchmarks
                ? 'Round Performance Benchmarks:'
                : data.cumulative_benchmarks
                  ? 'Cumulative Game Performance:'
                  : 'Performance Benchmarks:';

              return (
                <>
                  <div style={{ marginBottom: '12px', fontWeight: 'bold' }}>{title}</div>
                  {benchmarksToDisplay.map((benchmark, index) => (
                    <div key={index} style={{ marginBottom: '12px' }}>
                      <div style={{
                        fontWeight: 'bold',
                        marginBottom: '8px',
                        color: THEME.textDark
                      }}>
                        {benchmark.benchmark_name}
                      </div>
                      <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                        gap: '8px'
                      }}>
                        {benchmark.scores && Object.entries(processBenchmarkScores(benchmark.scores))
                          .sort(([, scoreA], [, scoreB]) => scoreB - scoreA)
                          .map(([model, score]) => (
                            <div key={model} style={{
                              backgroundColor: getModelColor(model, THEME),
                              padding: '8px',
                              borderRadius: '4px',
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center'
                            }}>
                              <div style={{ fontWeight: 'bold', fontSize: '0.9em' }}>{model}</div>
                              <div style={{
                                backgroundColor: '${THEME.textDark}1a',
                                padding: '2px 8px',
                                borderRadius: '12px',
                                fontWeight: 'bold'
                              }}>
                                {score}
                              </div>
                            </div>
                          ))}
                      </div>
                    </div>
                  ))}
                </>
              );
            }
            return null;
          })()}
        </div>
      )}
    </div>
  );
};

const VoteToWinAction = ({ action, users, THEME }) => {
  const COMMON_STYLES = getCommonStyles(THEME);
  const { data } = action;

  // Find voter and target users by name
  const voterUser = users?.find(u => u.name === data.name);
  const targetUser = users?.find(u => u.name === data.target);
  const modelColor = voterUser ? getModelColor(voterUser.model, THEME) : getModelColor(data.model, THEME);
  const targetColor = targetUser ? getModelColor(targetUser.model, THEME) : THEME.neutral;

  return (
    <div style={{
      backgroundColor: THEME.accent,
      border: `2px solid ${THEME.accent}`,
      borderRadius: '8px',
      padding: '16px',
      marginBottom: '12px',
      position: 'relative'
    }}>
      <div style={{
        position: 'absolute',
        top: '-12px',
        left: '50%',
        transform: 'translateX(-50%)',
        backgroundColor: THEME.accent,
        color: THEME.textDark,
        padding: '4px 16px',
        borderRadius: '16px',
        fontSize: '1.1em',
        fontWeight: 'bold',
        boxShadow: '0 2px 4px ${THEME.textDark}1a'
      }}>
        Final Vote
      </div>

      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        marginTop: '8px',
        marginBottom: '16px'
      }}>
        {voterUser && (
          <ProfileImage
            user={voterUser}
            size={36}
            borderColor={modelColor}
          />
        )}
        <div style={{
          backgroundColor: modelColor,
          padding: '8px 16px',
          borderRadius: '6px',
          color: THEME.textDark,
          fontWeight: 'bold',
          flex: 1,
          textAlign: 'center'
        }}>
          {data.name} ({voterUser?.model || data.model})
        </div>
        <div style={{
          fontSize: '1.2em',
          color: THEME.textMuted,
          fontWeight: 'bold'
        }}>
          votes for
        </div>
        {targetUser && (
          <ProfileImage
            user={targetUser}
            size={36}
            borderColor={targetColor}
          />
        )}
        <div style={{
          backgroundColor: targetColor,
          padding: '8px 16px',
          borderRadius: '6px',
          color: THEME.textDark,
          fontWeight: 'bold',
          flex: 1,
          textAlign: 'center'
        }}>
          {data.target}
        </div>
      </div>

      <div style={{
        backgroundColor: THEME.textLight,
        padding: '16px',
        borderRadius: '6px',
        border: `1px solid ${THEME.borderLight}`,
        marginTop: '16px'
      }}>
        <div style={{ 
          marginBottom: '8px',
          fontWeight: 'bold',
          color: THEME.textDark
        }}>
          Jury Speech:
        </div>
        <div style={{
          whiteSpace: 'pre-wrap',
          lineHeight: '1.6',
          color: THEME.textDark,
          fontStyle: 'italic'
        }}>
          {data.thoughts}
        </div>

        {data.diary_room && (
          <div style={{
            whiteSpace: 'pre-wrap',
            lineHeight: '1.6',
            color: THEME.textDark,
            backgroundColor: THEME.bgParchment,
            padding: '12px',
            borderRadius: '4px',
            border: `1px solid ${THEME.borderLight}`,
            marginTop: '12px',
            fontStyle: 'italic'
          }}>
            <div style={{ marginBottom: '8px', fontWeight: 'bold' }}>
              Diary Room:
            </div>
            <div>
              {data.diary_room}
            </div>
          </div>
        )}
      </div>

      <div style={{
        position: 'absolute',
        bottom: '-12px',
        right: '16px',
        backgroundColor: THEME.textMuted,
        color: THEME.textLight,
        padding: '2px 8px',
        borderRadius: '12px',
        fontSize: '0.85em'
      }}>
        {formatActionTime(action.created_at)}
      </div>
    </div>
  );
};

const PlayerSummaryAction = ({ action, THEME }) => {
  const COMMON_STYLES = getCommonStyles(THEME);
  const { data } = action;
  const modelColor = getModelColor(data.model, THEME);

  return (
    <div style={{
      backgroundColor: THEME.bgLight,
      border: `1px solid ${THEME.borderLight}`,
      borderRadius: '8px',
      padding: '16px',
      marginBottom: '12px'
    }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '12px'
      }}>
        <div style={{
          backgroundColor: THEME.textMuted,
          color: THEME.textLight,
          padding: '4px 12px',
          borderRadius: '4px',
          fontSize: '0.9em',
          fontWeight: 'bold'
        }}>
          Phase Summary
        </div>
        <div style={{ fontSize: '0.85em', color: THEME.textMuted }}>
          {formatActionTime(action.created_at)}
        </div>
      </div>

      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        marginBottom: '16px'
      }}>
        <div style={{
          backgroundColor: modelColor,
          padding: '8px 16px',
          borderRadius: '6px',
          color: THEME.textDark,
          fontWeight: 'bold'
        }}>
          {data.name}
        </div>
      </div>

      <div style={{
        backgroundColor: THEME.textLight,
        padding: '16px',
        borderRadius: '6px',
        border: `1px solid ${THEME.borderLight}`
      }}>
        <div style={{ 
          marginBottom: '8px',
          fontWeight: 'bold',
          color: THEME.textDark
        }}>
          Summary:
        </div>
        <div style={{
          whiteSpace: 'pre-wrap',
          lineHeight: '1.6',
          color: THEME.textDark
        }}>
          {JSON.stringify(data)}
        </div>
      </div>
    </div>
  );
};

const CompetitionQuestionAction = ({ action, THEME }) => {
  const COMMON_STYLES = getCommonStyles(THEME);
  const { data } = action;
  
  return (
    <div style={{
      backgroundColor: THEME.bgLight,
      border: `2px solid ${THEME.primary}`,
      borderRadius: '8px',
      padding: '16px',
      marginBottom: '12px',
      position: 'relative'
    }}>
      <div style={{
        position: 'absolute',
        top: '-12px',
        left: '50%',
        transform: 'translateX(-50%)',
        backgroundColor: THEME.primary,
        color: THEME.textLight,
        padding: '4px 16px',
        borderRadius: '16px',
        fontSize: '1.1em',
        fontWeight: 'bold',
        boxShadow: '0 2px 4px ${THEME.textDark}1a'
      }}>
        Competition Question
      </div>

      <div style={{
        marginTop: '8px',
        whiteSpace: 'pre-wrap',
        lineHeight: '1.6',
        color: THEME.primary
      }}>
        {data.message}
      </div>

      <div style={{
        position: 'absolute',
        bottom: '-12px',
        right: '16px',
        backgroundColor: THEME.textMuted,
        color: THEME.textLight,
        padding: '2px 8px',
        borderRadius: '12px',
        fontSize: '0.85em'
      }}>
        {formatActionTime(action.created_at)}
      </div>
    </div>
  );
};

const CompetitionAnswerAction = ({ action, playerModelMap, THEME }) => {
  const COMMON_STYLES = getCommonStyles(THEME);
  const { data } = action;
  const modelColor = getModelColor(data.model, THEME);

  // Helper function to get player model
  const getPlayerModel = (playerName) => {
    return playerModelMap[playerName] || playerName;
  };

  // Recursive component to render nested answer structures
  const AnswerRenderer = ({ value, level = 0 }) => {
    if (value === null || value === undefined) return null;

    // Handle objects (but not null, since typeof null === 'object')
    if (Array.isArray(value) || (typeof value === 'object' && value !== null)) {
      // Check if it's a model mapping object
      const values = Object.values(value);
      const isModelMapping = values.length > 0 && values.every(v =>
        typeof v === 'string' && v.includes('/')
      );

      if (isModelMapping) {
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {Object.entries(value).map(([player, model], i) => (
              <div key={i} style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <div style={{
                  backgroundColor: getModelColor(getPlayerModel(player), THEME),
                  padding: '4px 12px',
                  borderRadius: '4px',
                  color: THEME.textDark,
                  fontWeight: 'bold'
                }}>
                  {player}
                </div>
                <div style={{ color: THEME.textMuted }}>→</div>
                <div style={{
                  backgroundColor: model && model.split ? getModelColor(model, THEME) : THEME.bgLight,
                  padding: '4px 12px',
                  borderRadius: '4px',
                  color: THEME.textDark,
                  fontWeight: 'bold'
                }}>
                  {model && model.split ? model.split('/')[1] : ''}
                </div>
              </div>
            ))}
          </div>
        );
      }

      // Handle nested objects
      return (
        <div style={{ 
          display: 'flex', 
          flexDirection: 'column', 
          gap: '12px',
          paddingLeft: level > 0 ? '16px' : '0',
          borderLeft: level > 0 ? `2px solid ${THEME.borderLight}` : 'none'
        }}>
          {Object.entries(value).map(([key, val], i) => (
            <div key={i}>
              <div style={{ 
                fontWeight: 'bold',
                marginBottom: '8px',
                color: THEME.textDark,
                fontSize: level > 0 ? '0.95em' : '1em'
              }}>
                {key}:
              </div>
              <div style={{
                backgroundColor: THEME.bgLight,
                padding: '12px',
                borderRadius: '4px',
                border: `1px solid ${THEME.borderLight}`
              }}>
                <AnswerRenderer value={val} level={level + 1} />
              </div>
            </div>
          ))}
        </div>
      );
    }

    // Handle primitive values
    return (
      <div style={{ whiteSpace: 'pre-wrap' }}>
        {value.toString()}
      </div>
    );
  };

  return (
    <div style={{
      backgroundColor: THEME.bgLight,
      border: `1px solid ${THEME.borderLight}`,
      borderRadius: '8px',
      padding: '16px',
      marginBottom: '12px'
    }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '16px'
      }}>
        <div style={{
          backgroundColor: modelColor,
          padding: '8px 16px',
          borderRadius: '6px',
          color: THEME.textDark,
          fontWeight: 'bold'
        }}>
          {data.name} ({data.model})
        </div>
        <div style={{ fontSize: '0.85em', color: THEME.textMuted }}>
          {formatActionTime(action.created_at)}
        </div>
      </div>

      <div style={{
        backgroundColor: THEME.textLight,
        padding: '16px',
        borderRadius: '6px',
        border: `1px solid ${THEME.borderLight}`
      }}>
        <AnswerRenderer value={data.answer} />
        {data.thoughts && <AnswerRenderer value={data.thoughts} />}
        {data.diary_room && <AnswerRenderer value={data.diary_room} />}
      </div>
    </div>
  );
};

const CompetitionWinnerAction = ({ action, users, THEME }) => {
  const COMMON_STYLES = getCommonStyles(THEME);
  const { data } = action;

  // Find winner user by name
  const winnerUser = users?.find(u => u.name === data.name);
  const modelColor = winnerUser ? getModelColor(winnerUser.model, THEME) : getModelColor(data.model, THEME);

  return (
    <div style={{
      backgroundColor: THEME.accent,
      border: `2px solid ${THEME.accent}`,
      borderRadius: '8px',
      padding: '16px',
      marginBottom: '12px',
      position: 'relative'
    }}>
      <div style={{
        position: 'absolute',
        top: '-12px',
        left: '50%',
        transform: 'translateX(-50%)',
        backgroundColor: THEME.accent,
        color: THEME.textDark,
        padding: '4px 16px',
        borderRadius: '16px',
        fontSize: '1.1em',
        fontWeight: 'bold',
        boxShadow: '0 2px 4px ${THEME.textDark}1a'
      }}>
        Competition Winner
      </div>

      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        marginTop: '8px',
        marginBottom: '16px'
      }}>
        {winnerUser && (
          <ProfileImage
            user={winnerUser}
            size={40}
            borderColor={modelColor}
          />
        )}
        <div style={{
          backgroundColor: modelColor,
          padding: '8px 16px',
          borderRadius: '6px',
          color: THEME.textDark,
          fontWeight: 'bold',
          flex: 1,
          textAlign: 'center'
        }}>
          {data.name} ({winnerUser?.model || data.model})
        </div>
      </div>

      <div style={{
        backgroundColor: THEME.textLight,
        padding: '16px',
        borderRadius: '6px',
        border: `1px solid ${THEME.borderLight}`
      }}>
        <div style={{
          whiteSpace: 'pre-wrap',
          lineHeight: '1.6',
          color: THEME.textDark,
          backgroundColor: THEME.bgLight,
          padding: '12px',
          borderRadius: '4px',
          border: `1px solid ${THEME.borderLight}`
        }}>
          {data.thoughts}
        </div>

        {data.diary_room && (
          <div style={{
            whiteSpace: 'pre-wrap',
            lineHeight: '1.6',
            color: THEME.textDark,
            backgroundColor: THEME.bgParchment,
            padding: '12px',
            borderRadius: '4px',
            border: `1px solid ${THEME.borderLight}`,
            marginTop: '12px',
            fontStyle: 'italic'
          }}>
            <div style={{ marginBottom: '8px', fontWeight: 'bold' }}>
              Diary Room:
            </div>
            <div>
              {data.diary_room}
            </div>
          </div>
        )}
      </div>

      <div style={{
        position: 'absolute',
        bottom: '-12px',
        right: '16px',
        backgroundColor: THEME.textMuted,
        color: THEME.textLight,
        padding: '2px 8px',
        borderRadius: '12px',
        fontSize: '0.85em'
      }}>
        {formatActionTime(action.created_at)}
      </div>
    </div>
  );
};

const CompetitionCreditDecisionAction = ({ action, users, THEME }) => {
  const COMMON_STYLES = getCommonStyles(THEME);
  const { data } = action;

  const user = getUserById(users, action.game_instance_player_id);
  const modelColor = user ? getModelColor(user.model, THEME) : THEME.accent;

  return (
    <div style={{
      ...COMMON_STYLES.actionContainer,
      backgroundColor: THEME.bgMedium,
      border: `2px solid ${modelColor}`
    }}>
      <div style={{
        ...COMMON_STYLES.actionHeader,
        backgroundColor: `${modelColor}4d`,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {user && (
            <ProfileImage
              user={user}
              size={24}
              borderColor={modelColor}
            />
          )}
          <span style={{ fontWeight: 'bold' }}>
            💰 {data.competition_type} Competition - Credit Decision
          </span>
        </div>
        <div style={{ fontSize: '0.85em', color: THEME.textMuted }}>
          {formatActionTime(action.created_at)}
        </div>
      </div>

      <div style={{
        backgroundColor: THEME.textLight,
        padding: '16px',
        borderRadius: '6px',
        border: `1px solid ${THEME.borderLight}`
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '12px',
          padding: '12px',
          backgroundColor: THEME.bgLight,
          borderRadius: '4px',
          border: `1px solid ${THEME.borderLight}`
        }}>
          <div>
            <div style={{ fontSize: '1.1em', fontWeight: 'bold', marginBottom: '4px' }}>
              {user?.name}
            </div>
            <div style={{ fontSize: '0.9em', color: THEME.textMuted }}>
              Credits Before: {data.credits_before}
            </div>
          </div>
          <div style={{
            backgroundColor: modelColor,
            color: THEME.textDark,
            padding: '8px 16px',
            borderRadius: '6px',
            fontWeight: 'bold',
            fontSize: '1.2em'
          }}>
            Spent: {data.credits_spent}
          </div>
          <div style={{ fontSize: '0.9em', color: THEME.textMuted }}>
            Weight: {data.weight}
          </div>
        </div>

        {data.reasoning && (
          <div style={{
            whiteSpace: 'pre-wrap',
            lineHeight: '1.6',
            color: THEME.textDark,
            backgroundColor: THEME.bgParchment,
            padding: '12px',
            borderRadius: '4px',
            border: `1px solid ${THEME.borderLight}`,
            fontStyle: 'italic'
          }}>
            <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>Reasoning:</div>
            {data.reasoning}
          </div>
        )}
      </div>
    </div>
  );
};

const CompetitionResultsAction = ({ action, users, THEME }) => {
  const COMMON_STYLES = getCommonStyles(THEME);
  const { data } = action;

  const winnerUser = getUserById(users, action.game_instance_player_id);
  const modelColor = winnerUser ? getModelColor(winnerUser.model, THEME) : THEME.accent;

  return (
    <div style={{
      backgroundColor: THEME.accent,
      border: `3px solid ${modelColor}`,
      borderRadius: '8px',
      padding: '20px',
      marginBottom: '12px',
      position: 'relative',
      boxShadow: `0 4px 8px ${THEME.textDark}1a`
    }}>
      <div style={{
        position: 'absolute',
        top: '-16px',
        left: '50%',
        transform: 'translateX(-50%)',
        backgroundColor: modelColor,
        color: THEME.textDark,
        padding: '6px 24px',
        borderRadius: '20px',
        fontSize: '1.2em',
        fontWeight: 'bold',
        boxShadow: '0 2px 4px ${THEME.textDark}2a'
      }}>
        🏆 {data.competition_type} Winner!
      </div>

      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '16px',
        marginTop: '16px',
        marginBottom: '20px',
        padding: '16px',
        backgroundColor: THEME.textLight,
        borderRadius: '8px'
      }}>
        {winnerUser && (
          <ProfileImage
            user={winnerUser}
            size={60}
            borderColor={modelColor}
          />
        )}
        <div style={{ flex: 1 }}>
          <div style={{
            fontSize: '1.5em',
            fontWeight: 'bold',
            color: THEME.textDark,
            marginBottom: '8px'
          }}>
            {data.winner}
          </div>
          <div style={{ display: 'flex', gap: '16px', fontSize: '0.95em' }}>
            <div>
              <span style={{ color: THEME.textMuted }}>Credits Spent:</span>{' '}
              <span style={{ fontWeight: 'bold' }}>{data.winner_credits_spent}</span>
            </div>
            <div>
              <span style={{ color: THEME.textMuted }}>Final Weight:</span>{' '}
              <span style={{ fontWeight: 'bold' }}>{data.winner_weight}</span>
            </div>
          </div>
        </div>
      </div>

      {data.all_participants && data.all_participants.length > 0 && (
        <div style={{
          backgroundColor: THEME.bgLight,
          padding: '16px',
          borderRadius: '6px',
          border: `1px solid ${THEME.borderLight}`
        }}>
          <div style={{
            fontWeight: 'bold',
            marginBottom: '12px',
            color: THEME.textDark
          }}>
            All Participants:
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {data.all_participants.map((participant, idx) => {
              const isWinner = participant.name === data.winner;
              return (
                <div
                  key={idx}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '8px 12px',
                    backgroundColor: isWinner ? `${modelColor}2a` : THEME.textLight,
                    borderRadius: '4px',
                    border: isWinner ? `2px solid ${modelColor}` : `1px solid ${THEME.borderLight}`,
                    fontWeight: isWinner ? 'bold' : 'normal'
                  }}
                >
                  <span>{participant.name}</span>
                  <div style={{ display: 'flex', gap: '16px', fontSize: '0.9em' }}>
                    <span style={{ color: THEME.textMuted }}>
                      Credits: {participant.credits_spent}
                    </span>
                    <span style={{ color: THEME.textMuted }}>
                      Weight: {participant.weight}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div style={{
        position: 'absolute',
        bottom: '-12px',
        right: '16px',
        backgroundColor: THEME.textMuted,
        color: THEME.textLight,
        padding: '2px 8px',
        borderRadius: '12px',
        fontSize: '0.85em'
      }}>
        {formatActionTime(action.created_at)}
      </div>
    </div>
  );
};

const SliceOfLifeAction = ({ action, users, THEME }) => {
  const COMMON_STYLES = getCommonStyles(THEME);
  const { data } = action;

  // Check if action is associated with a specific player
  const user = action.game_instance_player_id ? getUserById(users, action.game_instance_player_id) : null;
  const modelColor = user ? getModelColor(user.model, THEME) : THEME.evil;

  return (
    <div style={{
      backgroundColor: THEME.bgDark,
      border: `1px solid ${THEME.bgMedium}`,
      borderRadius: '8px',
      padding: '24px',
      marginBottom: '12px',
      color: THEME.textLight,
      fontFamily: 'monospace'
    }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '16px',
        borderBottom: `1px solid ${THEME.bgMedium}`,
        paddingBottom: '12px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          {user && (
            <ProfileImage
              user={user}
              size={28}
              borderColor={modelColor}
            />
          )}
          <div style={{
            backgroundColor: THEME.evil,
            padding: '4px 12px',
            borderRadius: '4px',
            fontSize: '1.1em',
            fontWeight: 'bold'
          }}>
            Slice of Life
          </div>
          {user && (
            <span style={{ fontSize: '0.9em', color: THEME.textMuted }}>
              {user.name}
            </span>
          )}
        </div>
        <div style={{ fontSize: '0.85em', color: THEME.textMuted }}>
          {formatActionTime(action.created_at)}
        </div>
      </div>

      <div style={{
        backgroundColor: THEME.bgMedium,
        padding: '20px',
        borderRadius: '6px',
        marginBottom: '16px',
        whiteSpace: 'pre-wrap',
        lineHeight: '1.6',
        fontSize: '0.95em'
      }}>
        {data.script}
      </div>

      <div style={{
        backgroundColor: THEME.bgMedium,
        padding: '16px',
        borderRadius: '6px',
        marginBottom: '16px'
      }}>
        <div style={{ 
          marginBottom: '8px',
          fontWeight: 'bold',
          color: THEME.primary
        }}>
          Emotional Impact:
        </div>
        <div style={{ color: THEME.textLight }}>
          {data.emotional_impact}
        </div>
      </div>

      <div style={{
        backgroundColor: THEME.bgMedium,
        padding: '16px',
        borderRadius: '6px',
        marginBottom: '16px'
      }}>
        <div style={{ 
          marginBottom: '8px',
          fontWeight: 'bold',
          color: THEME.primary
        }}>
          Relevant Players:
        </div>
        <div style={{ 
          display: 'flex',
          flexWrap: 'wrap',
          gap: '8px'
        }}>
          {data.relevant_players.map((player, index) => (
            <div key={index} style={{
              backgroundColor: THEME.bgDark,
              padding: '4px 12px',
              borderRadius: '4px',
              border: `1px solid ${THEME.textMuted}`
            }}>
              {player}
            </div>
          ))}
        </div>
      </div>

      <div style={{
        backgroundColor: THEME.bgMedium,
        padding: '16px',
        borderRadius: '6px',
        fontStyle: 'italic',
        color: THEME.textMuted,
        fontSize: '0.9em'
      }}>
        {data.thoughts}
      </div>

      {data.diary_room && (
        <div style={{
          backgroundColor: THEME.bgMedium,
          padding: '16px',
          borderRadius: '6px',
          fontStyle: 'italic',
          color: THEME.textMuted,
          fontSize: '0.9em',
          marginTop: '8px',
          border: `1px solid ${THEME.borderLight}`
        }}>
          <div style={{ marginBottom: '8px', fontWeight: 'bold', color: THEME.textMuted }}>
            Diary Room:
          </div>
          <div>
            {data.diary_room}
          </div>
        </div>
      )}
    </div>
  );
};

const QuestionAnswerAction = ({ action, users, THEME }) => {
  const COMMON_STYLES = getCommonStyles(THEME);
  const { data } = action;

  // Find user by name
  const user = users?.find(u => u.name === data.name);
  const modelColor = user ? getModelColor(user.model, THEME) : getModelColor(data.model, THEME);

  return (
    <div style={{
      backgroundColor: THEME.bgLight,
      border: `1px solid ${THEME.borderLight}`,
      borderRadius: '8px',
      padding: '16px',
      marginBottom: '12px',
      position: 'relative'
    }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '16px',
        borderBottom: `1px solid ${THEME.borderLight}`,
        paddingBottom: '12px'
      }}>
        <div style={{
          backgroundColor: THEME.secondary,
          color: THEME.textLight,
          padding: '4px 12px',
          borderRadius: '4px',
          fontSize: '1.1em',
          fontWeight: 'bold'
        }}>
          Question & Answer
        </div>
        <div style={{ fontSize: '0.85em', color: THEME.textMuted }}>
          {formatActionTime(action.created_at)}
        </div>
      </div>

      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        marginBottom: '16px'
      }}>
        {user && (
          <ProfileImage
            user={user}
            size={32}
            borderColor={modelColor}
          />
        )}
        <div style={{
          backgroundColor: modelColor,
          padding: '8px 16px',
          borderRadius: '6px',
          color: THEME.textDark,
          fontWeight: 'bold'
        }}>
          {data.name} ({user?.model || data.model})
        </div>
      </div>

      <div style={{
        backgroundColor: THEME.textLight,
        padding: '16px',
        borderRadius: '6px',
        border: `1px solid ${THEME.borderLight}`,
        marginBottom: '16px'
      }}>
        <div style={{ 
          marginBottom: '8px',
          fontWeight: 'bold',
          color: THEME.textDark
        }}>
          Question:
        </div>
        <div style={{
          backgroundColor: THEME.bgLight,
          padding: '12px',
          borderRadius: '4px',
          border: `1px solid ${THEME.borderLight}`,
          fontStyle: 'italic',
          color: THEME.textDark
        }}>
          {data.question}
        </div>
      </div>

      <div style={{
        backgroundColor: THEME.textLight,
        padding: '16px',
        borderRadius: '6px',
        border: `1px solid ${THEME.borderLight}`
      }}>
        <div style={{ 
          marginBottom: '8px',
          fontWeight: 'bold',
          color: THEME.textDark
        }}>
          Answer:
        </div>
        <div style={{
          whiteSpace: 'pre-wrap',
          lineHeight: '1.6',
          color: THEME.textDark
        }}>
          {data.answer}
        </div>
      </div>
    </div>
  );
};

const RoundSummaryAction = ({ action, users, THEME }) => {
  const COMMON_STYLES = getCommonStyles(THEME);
  const { data } = action;
  const user = getUserById(users, action.game_instance_player_id);
  if (!user) return null;

  const modelColor = getModelColor(user.model, THEME);

  return (
    <div style={{
      backgroundColor: THEME.bgLight,
      border: `1px solid ${THEME.borderLight}`,
      borderRadius: '8px',
      padding: '16px',
      marginBottom: '12px'
    }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '16px'
      }}>
        <div style={{
          backgroundColor: THEME.textMuted,
          color: THEME.textLight,
          padding: '4px 12px',
          borderRadius: '4px',
          fontSize: '1.1em',
          fontWeight: 'bold'
        }}>
          Round Summary
        </div>
        <div style={{ fontSize: '0.85em', color: THEME.textMuted }}>
          {formatActionTime(action.created_at)}
        </div>
      </div>

      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        marginBottom: '16px'
      }}>
        <div style={{
          backgroundColor: modelColor,
          padding: '8px 16px',
          borderRadius: '6px',
          color: THEME.textDark,
          fontWeight: 'bold'
        }}>
          {user.name} ({user.model.split('/')[1]})
        </div>
      </div>

      <div style={{
        backgroundColor: THEME.textLight,
        padding: '16px',
        borderRadius: '6px',
        border: `1px solid ${THEME.borderLight}`,
        marginBottom: '16px'
      }}>
        <div style={{ 
          marginBottom: '8px',
          fontWeight: 'bold',
          color: THEME.textDark
        }}>
          Summary:
        </div>
        <div style={{
          whiteSpace: 'pre-wrap',
          lineHeight: '1.6',
          color: THEME.textDark
        }}>
          {data.summary}
        </div>
      </div>

      {data.key_events && data.key_events.length > 0 && (
        <div style={{
          backgroundColor: THEME.textLight,
          padding: '16px',
          borderRadius: '6px',
          border: `1px solid ${THEME.borderLight}`,
          marginBottom: '16px'
        }}>
          <div style={{ 
            marginBottom: '8px',
            fontWeight: 'bold',
            color: THEME.textDark
          }}>
            Key Events:
          </div>
          <ul style={{
            margin: 0,
            paddingLeft: '20px',
            color: THEME.textDark
          }}>
            {data.key_events.map((event, index) => (
              <li key={index} style={{ marginBottom: '4px' }}>{event}</li>
            ))}
          </ul>
        </div>
      )}

      {data.personal_notes && (
        <div style={{
          backgroundColor: THEME.textLight,
          padding: '16px',
          borderRadius: '6px',
          border: `1px solid ${THEME.borderLight}`
        }}>
          <div style={{ 
            marginBottom: '8px',
            fontWeight: 'bold',
            color: THEME.textDark
          }}>
            Personal Notes:
          </div>
          <div style={{
            whiteSpace: 'pre-wrap',
            lineHeight: '1.6',
            color: THEME.textDark,
            fontStyle: 'italic'
          }}>
            {data.personal_notes}
          </div>
        </div>
      )}
    </div>
  );
};

const SecretGameAction = ({ action, users, THEME }) => {
  const COMMON_STYLES = getCommonStyles(THEME);
  const { data } = action;

  // Get user info from game_instance_player_id
  const user = getUserById(users, action.game_instance_player_id);
  const modelColor = user ? getModelColor(user.model, THEME) : THEME.bgLight;

  // State for intro video processing
  const [processingIntro, setProcessingIntro] = React.useState(false);
  const [processedIntroUrl, setProcessedIntroUrl] = React.useState(data.processed_video_url || null);

  const processIntroVideo = async () => {
    setProcessingIntro(true);

    try {
      const response = await fetch(
        `${API_BASE_URL}/api/takeGameStep?processIntroVideo=true&actionId=${action.id}`,
        { method: 'GET' }
      );

      if (!response.ok) {
        throw new Error(`Failed to process intro video: ${response.statusText}`);
      }

      const result = await parseApiResponse(response);

      if (result.success && result.processed_video_url) {
        const videoUrlWithTimestamp = `${result.processed_video_url}?t=${Date.now()}`;
        setProcessedIntroUrl(videoUrlWithTimestamp);
      }
    } catch (error) {
      console.error('Error processing intro video:', error);
      alert(`Failed to process intro video: ${error.message}`);
    } finally {
      setProcessingIntro(false);
    }
  };

  return (
    <div style={{
      backgroundColor: THEME.bgLight,
      border: `2px solid ${THEME.textMuted}`,
      borderRadius: '8px',
      padding: '16px',
      marginBottom: '12px',
      position: 'relative',
      color: THEME.textDark
    }}>
      <div style={{
        position: 'absolute',
        top: '-12px',
        left: '50%',
        transform: 'translateX(-50%)',
        backgroundColor: THEME.textMuted,
        color: THEME.textLight,
        padding: '4px 16px',
        borderRadius: '16px',
        fontSize: '1.1em',
        fontWeight: 'bold',
        boxShadow: '0 2px 4px ${THEME.textDark}1a'
      }}>
        Secret Game Action
      </div>

      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        marginTop: '8px',
        marginBottom: '16px'
      }}>
        {user && (
          <>
            <ProfileImage
              user={user}
              size={32}
              borderColor={modelColor}
            />
            <div style={{
              backgroundColor: modelColor,
              padding: '8px 16px',
              borderRadius: '6px',
              color: THEME.textDark,
              fontWeight: 'bold',
              flex: 1,
              textAlign: 'center'
            }}>
              {user.name} ({getDisplayModelName(user)})
            </div>
          </>
        )}
      </div>

      <div style={{
        backgroundColor: THEME.textLight,
        padding: '16px',
        borderRadius: '6px',
        border: `1px solid ${THEME.borderLight}`,
        marginBottom: '16px'
      }}>
        <div style={{ 
          marginBottom: '8px',
          fontWeight: 'bold',
          color: THEME.textDark
        }}>
          Action:
        </div>
        <div style={{
          whiteSpace: 'pre-wrap',
          lineHeight: '1.6',
          color: THEME.textDark
        }}>
          {data.message}
        </div>
      </div>

      {data.thoughts && (
        <div style={{
          backgroundColor: THEME.textLight,
          padding: '16px',
          borderRadius: '6px',
          border: `1px solid ${THEME.borderLight}`,
          marginBottom: '16px'
        }}>
          <div style={{ 
            marginBottom: '8px',
            fontWeight: 'bold',
            color: THEME.textDark
          }}>
            Thoughts:
          </div>
          <div style={{
            whiteSpace: 'pre-wrap',
            lineHeight: '1.6',
            color: THEME.textDark,
            fontStyle: 'italic'
          }}>
            {data.thoughts}
          </div>
        </div>
      )}

      {data.diary_room && (
        <div style={{
          backgroundColor: THEME.bgParchment,
          padding: '16px',
          borderRadius: '6px',
          border: `1px solid ${THEME.borderLight}`,
          marginBottom: '16px'
        }}>
          <div style={{
            marginBottom: '8px',
            fontWeight: 'bold',
            color: THEME.textDark
          }}>
            Diary Room:
          </div>
          <div style={{
            whiteSpace: 'pre-wrap',
            lineHeight: '1.6',
            color: THEME.textDark,
            fontStyle: 'italic'
          }}>
            {data.diary_room}
          </div>
        </div>
      )}

      {data.video_url && (
        <div style={{
          backgroundColor: THEME.textLight,
          padding: '16px',
          borderRadius: '6px',
          border: `1px solid ${THEME.borderLight}`,
          marginBottom: '16px'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            marginBottom: '8px'
          }}>
            <div style={{
              fontWeight: 'bold',
              color: THEME.textDark,
              flex: 1
            }}>
              Introduction Video:
            </div>
            <button
              onClick={processIntroVideo}
              disabled={processingIntro}
              style={{
                backgroundColor: processingIntro ? THEME.textMuted : THEME.secondary,
                color: THEME.textLight,
                border: 'none',
                padding: '4px 10px',
                borderRadius: '4px',
                fontSize: '0.8em',
                fontWeight: 'bold',
                cursor: processingIntro ? 'not-allowed' : 'pointer',
                transition: 'background-color 0.2s'
              }}
            >
              {processingIntro ? '⏳ Processing...' : (processedIntroUrl ? '🔄 Re-process' : '✨ Process')}
            </button>
          </div>
          <video
            key={processedIntroUrl || data.video_url}
            controls
            draggable="true"
            onDragStart={(e) => {
              const url = processedIntroUrl || data.video_url;
              const filename = url.split('/').pop().split('?')[0]; // Remove query params
              const videoData = {
                url: url,
                filename: filename,
                title: `Intro - ${action.player_name || 'Player'}`,
                type: 'intro',
                metadata: {
                  playerName: action.player_name,
                  message: data.message,
                  thoughts: data.thoughts,
                  diaryRoom: data.diary_room,
                  model: user?.model,
                  introSpeech: user?.data?.intro_speech,
                  dialogue: user?.data?.intro_speech // Also add as 'dialogue' for consistency
                }
              };
              e.dataTransfer.setData('video-data', JSON.stringify(videoData));
              e.dataTransfer.effectAllowed = 'copy';
            }}
            style={{
              width: '100%',
              maxWidth: '600px',
              borderRadius: '4px',
              display: 'block',
              margin: '0 auto',
              cursor: 'grab'
            }}
            src={processedIntroUrl || data.video_url}
          />
        </div>
      )}

      <div style={{
        position: 'absolute',
        bottom: '-12px',
        right: '16px',
        backgroundColor: THEME.textMuted,
        color: THEME.textLight,
        padding: '2px 8px',
        borderRadius: '12px',
        fontSize: '0.85em'
      }}>
        {formatActionTime(action.created_at)}
      </div>
    </div>
  );
};

const NominationAction = ({ action, users, THEME }) => {
  const COMMON_STYLES = getCommonStyles(THEME);
  const { data } = action;
  
  // Get user info from game_instance_player_id
  const user = getUserById(users, action.game_instance_player_id);
  const modelColor = user ? getModelColor(user.model, THEME) : getModelColorsFromTheme(THEME)[0];

  return (
    <div style={{
      ...COMMON_STYLES.actionContainer,
      backgroundColor: THEME.bgDark,
      border: `2px solid ${THEME.secondary}`,
      boxShadow: `0 0 20px ${THEME.evil}4d`,
      position: 'relative'
    }}>
      <div style={{
        position: 'absolute',
        top: '-14px',
        left: '50%',
        transform: 'translateX(-50%)',
        backgroundColor: THEME.secondary,
        color: THEME.textLight,
        padding: '4px 20px',
        borderRadius: '20px',
        fontSize: '1.1em',
        fontWeight: 'bold',
        letterSpacing: '1px',
        textTransform: 'uppercase',
        boxShadow: '0 2px 8px ${THEME.textDark}4d'
      }}>
        ⚖️ Nomination
      </div>

      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        marginTop: '20px',
        marginBottom: '16px',
        padding: '0 16px'
      }}>
        {user && (
          <>
            <ProfileImage
              user={user}
              size={32}
              borderColor={modelColor}
            />
            <div style={{
              ...COMMON_STYLES.playerBadge,
              backgroundColor: modelColor,
              color: THEME.textDark,
              flex: 1,
              textAlign: 'center',
              padding: '8px 16px',
            }}>
              {user.name} ({getDisplayModelName(user)})
            </div>
          </>
        )}
        <div style={{
          fontSize: '1.2em',
          color: THEME.accent,
          fontWeight: 'bold',
          textShadow: '1px 1px 2px ${THEME.textDark}80'
        }}>
          {THEME.nominationEmoji} nominates {THEME.nominationEmoji}
        </div>
        <div style={{
          ...COMMON_STYLES.playerBadge,
          backgroundColor: THEME.accent,
          color: THEME.textDark,
          flex: 1,
          textAlign: 'center',
          padding: '8px 16px',
          boxShadow: '0 0 10px ${THEME.accent}80'
        }}>
          {data.nominee}
        </div>
      </div>

      <div style={{
        backgroundColor: '${THEME.evil}33',
        padding: '16px',
        borderRadius: '6px',
        border: `1px solid ${THEME.secondary}`,
        marginBottom: '16px',
        margin: '0 16px 16px'
      }}>
        <div style={{ 
          marginBottom: '8px',
          fontWeight: 'bold',
          color: THEME.accent,
          textTransform: 'uppercase',
          letterSpacing: '1px'
        }}>
          Accusation:
        </div>
        <div style={{
          whiteSpace: 'pre-wrap',
          lineHeight: '1.6',
          color: THEME.textLight,
          fontStyle: 'italic',
          fontSize: '1.05em'
        }}>
          "{data.accusation}"
        </div>
      </div>

      {data.thoughts && (
        <div style={{
          backgroundColor: '${THEME.textLight}1a',
          padding: '16px',
          borderRadius: '6px',
          border: '1px solid ${THEME.textLight}33',
          marginBottom: '16px'
        }}>
          <div style={{ 
            marginBottom: '8px',
            fontWeight: 'bold',
            color: THEME.textLight
          }}>
            Thoughts:
          </div>
          <div style={{
            whiteSpace: 'pre-wrap',
            lineHeight: '1.6',
            color: THEME.textLight,
            fontStyle: 'italic'
          }}>
            {data.thoughts}
          </div>
        </div>
      )}

      <div style={{
        position: 'absolute',
        bottom: '-12px',
        right: '16px',
        backgroundColor: THEME.textMuted,
        color: THEME.textLight,
        padding: '2px 8px',
        borderRadius: '12px',
        fontSize: '0.85em'
      }}>
        {formatActionTime(action.created_at)}
      </div>
    </div>
  );
};

// ModeratorPanel — Live AI moderator commentary sidebar
// GooglyEyes — a pair of cursor-tracking eyes that peer out of the livestream
// bar to bait clicks. Pupils follow the mouse (rAF-throttled), blink on a
// random 3-6s cycle. Pure decoration: aria-hidden, no pointer events.
const GooglyEyes = ({ size = 15 }) => {
  const containerRef = React.useRef(null);
  const leftPupil = React.useRef(null);
  const rightPupil = React.useRef(null);
  const [blink, setBlink] = React.useState(false);

  React.useEffect(() => {
    let raf = null;
    const onMove = (e) => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = null;
        const c = containerRef.current;
        if (!c) return;
        const rect = c.getBoundingClientRect();
        [leftPupil, rightPupil].forEach((ref, i) => {
          const el = ref.current;
          if (!el) return;
          const cx = rect.left + rect.width * (i === 0 ? 0.27 : 0.73);
          const cy = rect.top + rect.height / 2;
          const dx = e.clientX - cx;
          const dy = e.clientY - cy;
          const dist = Math.min(size * 0.22, Math.hypot(dx, dy) / 24);
          const ang = Math.atan2(dy, dx);
          el.style.transform = `translate(${Math.cos(ang) * dist}px, ${Math.sin(ang) * dist}px)`;
        });
      });
    };
    window.addEventListener('mousemove', onMove);
    return () => { window.removeEventListener('mousemove', onMove); if (raf) cancelAnimationFrame(raf); };
  }, [size]);

  React.useEffect(() => {
    let outer, inner;
    const scheduleBlink = () => {
      outer = setTimeout(() => {
        setBlink(true);
        inner = setTimeout(() => { setBlink(false); scheduleBlink(); }, 130);
      }, 3000 + Math.random() * 3000);
    };
    scheduleBlink();
    return () => { clearTimeout(outer); clearTimeout(inner); };
  }, []);

  const eye = (ref) => (
    <span style={{
      width: size, height: size, borderRadius: '50%', backgroundColor: '#fff',
      border: '1.5px solid rgba(0,0,0,0.4)',
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      transform: blink ? 'scaleY(0.15)' : 'scaleY(1)',
      transition: 'transform 0.08s ease',
      boxShadow: 'inset 0 -2px 3px rgba(0,0,0,0.18)',
    }}>
      <span ref={ref} style={{
        width: size * 0.45, height: size * 0.45, borderRadius: '50%',
        backgroundColor: '#14141f', transition: 'transform 0.06s linear',
      }} />
    </span>
  );

  return (
    <span ref={containerRef} aria-hidden="true" style={{ display: 'inline-flex', gap: '2px', flexShrink: 0, pointerEvents: 'none' }}>
      {eye(leftPupil)}
      {eye(rightPupil)}
    </span>
  );
};

const ModeratorPanel = ({ moderatorCharacter, THEME, isMobile, hideDescription }) => {
  const [statusIndex, setStatusIndex] = React.useState(() => Math.floor(Math.random() * (moderatorCharacter?.status_messages?.length || 1)));
  const [showBio, setShowBio] = React.useState(false);

  // Rotate status every ~60 seconds (randomized 50-70s)
  React.useEffect(() => {
    if (!moderatorCharacter?.status_messages?.length) return;
    const interval = setInterval(() => {
      setStatusIndex(prev => (prev + 1) % moderatorCharacter.status_messages.length);
    }, 50000 + Math.random() * 20000);
    return () => clearInterval(interval);
  }, [moderatorCharacter?.status_messages?.length]);

  if (!moderatorCharacter) return null;

  const accentColor = THEME.accent || '#8b5cf6';
  const statusMessages = moderatorCharacter.status_messages || ['vibing'];
  const currentStatus = statusMessages[statusIndex % statusMessages.length];

  // ⓘ host backstory reveal — the host's full story lives here (moved off the Home tab)
  const bioButton = (
    <button
      onClick={() => setShowBio(v => !v)}
      title={showBio ? 'Hide host bio' : 'Who is this host?'}
      style={{
        background: showBio ? `${accentColor}30` : 'rgba(255,255,255,0.08)',
        border: `1px solid ${showBio ? accentColor : 'rgba(255,255,255,0.15)'}`,
        borderRadius: '50%', width: '20px', height: '20px',
        color: showBio ? accentColor : 'rgba(255,255,255,0.6)',
        fontSize: '0.68em', fontWeight: 700, cursor: 'pointer',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 0, lineHeight: 1, flexShrink: 0,
      }}
    >i</button>
  );
  // Host bio popup — small centered modal (keeps the panel compact; an inline
  // expansion pushed the ghost chat down, which felt janky in three-column mode)
  const bioSection = showBio && (
    <div
      onClick={() => setShowBio(false)}
      style={{
        position: 'fixed', inset: 0, zIndex: 3200,
        backgroundColor: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(2px)', WebkitBackdropFilter: 'blur(2px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px',
        animation: 'moderatorFadeIn 0.2s ease-out',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '360px', maxWidth: '92vw', maxHeight: '80dvh', overflowY: 'auto',
          borderRadius: '16px', position: 'relative',
          backgroundColor: THEME.bgMedium || '#1a1a2e',
          border: `1px solid ${accentColor}50`,
          boxShadow: `0 12px 48px rgba(0,0,0,0.6), 0 0 24px ${accentColor}20`,
          padding: '20px',
        }}
      >
        <button
          onClick={() => setShowBio(false)}
          aria-label="Close host bio"
          style={{ position: 'absolute', top: '10px', right: '12px', background: 'none', border: 'none', color: THEME.textMuted || '#888', cursor: 'pointer', fontSize: '1em', lineHeight: 1, padding: '4px' }}
        >✕</button>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '14px' }}>
          {moderatorCharacter.profile_image_url ? (
            <img src={moderatorCharacter.profile_image_url} alt={moderatorCharacter.name} style={{
              width: 52, height: 52, borderRadius: '50%', objectFit: 'cover', flexShrink: 0,
              border: `2px solid ${accentColor}60`, boxShadow: `0 0 12px ${accentColor}30`,
            }} />
          ) : (
            <div style={{
              width: 52, height: 52, borderRadius: '50%', flexShrink: 0,
              background: `linear-gradient(135deg, ${accentColor}, ${THEME.primary || '#6366f1'})`,
              display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.5em',
            }}>
              {moderatorCharacter.avatar_emoji || '🎙️'}
            </div>
          )}
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: '0.95em', fontWeight: 700, color: THEME.textOnPanel || '#fff' }}>{moderatorCharacter.name}</div>
            <div style={{ fontSize: '0.62em', fontWeight: 700, color: accentColor, textTransform: 'uppercase', letterSpacing: '1.2px', marginTop: '2px' }}>Your Host</div>
          </div>
        </div>
        {moderatorCharacter.catchphrase && (
          <div style={{ fontSize: '0.78em', color: accentColor, fontStyle: 'italic', marginBottom: '10px' }}>
            "{moderatorCharacter.catchphrase}"
          </div>
        )}
        {/* FIRST paragraph only (personality) — the origin story lives on the Tea
            tab's host card instead; each surface tells half the story (user call 07-09) */}
        {(moderatorCharacter.personality || moderatorCharacter.origin_story) && (
          <div style={{ fontSize: '0.84em', color: THEME.textMuted || '#aaa', lineHeight: 1.65 }}>
            {moderatorCharacter.personality || moderatorCharacter.origin_story}
          </div>
        )}
        {Array.isArray(moderatorCharacter.personality_traits) && moderatorCharacter.personality_traits.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px', marginTop: '12px' }}>
            {moderatorCharacter.personality_traits.map((t, i) => (
              <span key={i} style={{
                fontSize: '0.62em', fontWeight: 600, padding: '3px 9px', borderRadius: '10px',
                backgroundColor: `${accentColor}18`, color: accentColor, border: `1px solid ${accentColor}30`,
              }}>{t}</span>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  // Mobile: compact horizontal layout; Desktop: full vertical card
  if (isMobile) {
    return (
      <div style={{
        backgroundColor: THEME.bgMedium || '#1a1a2e',
        borderRadius: '10px',
        border: `1px solid ${accentColor}30`,
        overflow: 'hidden',
        flexShrink: 0,
        boxShadow: `0 0 16px ${accentColor}10`,
        marginBottom: '2px',
      }}>
        <div style={{
          background: `linear-gradient(135deg, ${THEME.bgDark || '#0a0a1a'} 0%, ${THEME.bgMedium || '#1a1a2e'} 100%)`,
          padding: '10px 12px',
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          gap: '10px',
        }}>
          {/* Avatar — profile image or emoji fallback */}
          {moderatorCharacter.profile_image_url ? (
            <img src={moderatorCharacter.profile_image_url} alt={moderatorCharacter.name} style={{
              width: 40, height: 40, borderRadius: '50%', objectFit: 'cover', flexShrink: 0,
              border: `2px solid ${accentColor}60`,
              boxShadow: `0 0 10px ${accentColor}40`,
            }} />
          ) : (
            <div style={{
              width: '40px', height: '40px', borderRadius: '50%',
              background: `linear-gradient(135deg, ${accentColor}, ${THEME.primary || '#6366f1'})`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: '1.3em', boxShadow: `0 0 10px ${accentColor}40`, flexShrink: 0,
            }}>
              {moderatorCharacter.avatar_emoji || '🎙️'}
            </div>
          )}

          {/* Name + status */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span style={{
                fontSize: '0.8em',
                fontWeight: 700,
                color: THEME.textOnPanel || '#fff',
              }}>
                {moderatorCharacter.name}
              </span>
              <span style={{
                padding: '1px 5px',
                borderRadius: '3px',
                backgroundColor: '#ef4444',
                color: '#fff',
                fontSize: '0.55em',
                fontWeight: 800,
                letterSpacing: '0.5px',
                animation: 'liveBadgePulse 2s ease-in-out infinite',
              }}>
                LIVE
              </span>
            </div>
            <div key={currentStatus} style={{
              fontSize: '0.65em',
              color: accentColor,
              fontStyle: 'italic',
              marginTop: '1px',
              animation: 'moderatorFadeIn 0.5s ease-out',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}>
              {currentStatus}
            </div>
          </div>
          {bioButton}
        </div>
        {bioSection}
      </div>
    );
  }

  // Desktop: seamless (no bottom border, merges with ghost chat) when hideDescription, full card otherwise
  const seamless = hideDescription;
  const avatarSize = seamless ? 60 : 68;

  return (
    <div style={{
      backgroundColor: THEME.bgMedium || '#1a1a2e',
      borderRadius: seamless ? '8px 8px 0 0' : '12px',
      border: seamless ? `1px dashed ${THEME.spectating || accentColor}` : `1px solid ${accentColor}30`,
      borderBottom: seamless ? 'none' : undefined,
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column',
      flexShrink: 0,
      boxShadow: seamless ? 'none' : `0 0 24px ${accentColor}15`,
      marginBottom: seamless ? '0px' : '4px',
    }}>
      {/* Host visual — when a portrait exists it IS the panel (full-bleed hero,
          name/status on a bottom gradient like the Home banner — user call 07-09);
          emoji-only hosts keep the centered avatar card */}
      {moderatorCharacter.profile_image_url ? (
        <div style={{ position: 'relative', height: seamless ? 150 : 180, flexShrink: 0 }}>
          <img src={moderatorCharacter.profile_image_url} alt={moderatorCharacter.name} style={{
            position: 'absolute', inset: 0, width: '100%', height: '100%',
            objectFit: 'cover', objectPosition: 'center 22%',
          }} />
          <div style={{
            position: 'absolute', inset: 0,
            background: 'linear-gradient(180deg, rgba(0,0,0,0.30) 0%, transparent 32%, transparent 52%, rgba(0,0,0,0.82) 100%)',
          }} />
          <div style={{
            position: 'absolute', top: '8px', right: '10px',
            padding: '2px 7px', borderRadius: '4px',
            backgroundColor: '#ef4444', color: '#fff',
            fontSize: '0.6em', fontWeight: 800, letterSpacing: '1px', textTransform: 'uppercase',
            animation: 'liveBadgePulse 2s ease-in-out infinite',
          }}>
            LIVE
          </div>
          <div style={{ position: 'absolute', top: '8px', left: '10px' }}>
            {bioButton}
          </div>
          <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, padding: '8px 12px 9px' }}>
            <div style={{ fontSize: '0.9em', fontWeight: 800, color: '#fff', textShadow: '0 1px 3px rgba(0,0,0,0.8)' }}>
              {moderatorCharacter.name}
            </div>
            <div key={currentStatus} style={{
              fontSize: '0.65em', fontStyle: 'italic',
              color: 'rgba(255,255,255,0.85)', textShadow: '0 1px 2px rgba(0,0,0,0.8)',
              marginTop: '1px', animation: 'moderatorFadeIn 0.5s ease-out',
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>
              {currentStatus}
            </div>
          </div>
        </div>
      ) : (
        <div style={{
          position: 'relative',
          background: `radial-gradient(ellipse at 50% 80%, ${accentColor}20 0%, transparent 70%), linear-gradient(180deg, ${THEME.bgDark || '#0a0a1a'} 0%, ${THEME.bgMedium || '#1a1a2e'} 100%)`,
          padding: seamless ? '12px 16px 10px' : '16px 16px 12px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}>
          {/* LIVE badge — top right */}
          <div style={{
            position: 'absolute',
            top: '8px',
            right: '10px',
            padding: '2px 7px',
            borderRadius: '4px',
            backgroundColor: '#ef4444',
            color: '#fff',
            fontSize: '0.6em',
            fontWeight: 800,
            letterSpacing: '1px',
            textTransform: 'uppercase',
            animation: 'liveBadgePulse 2s ease-in-out infinite',
          }}>
            LIVE
          </div>

          {/* ⓘ host bio — top left, mirrors the LIVE badge */}
          <div style={{ position: 'absolute', top: '8px', left: '10px' }}>
            {bioButton}
          </div>

          <div style={{
            width: avatarSize, height: avatarSize, borderRadius: '50%',
            background: `linear-gradient(135deg, ${accentColor}, ${THEME.primary || '#6366f1'})`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: '2.2em',
            boxShadow: `0 0 16px ${accentColor}50, 0 0 32px ${accentColor}15`,
            animation: 'moderatorBob 3s ease-in-out infinite',
            marginBottom: '6px',
            flexShrink: 0,
          }}>
            {moderatorCharacter.avatar_emoji || '🎙️'}
          </div>

          {/* Name */}
          <span style={{
            fontSize: '0.85em',
            fontWeight: 700,
            color: THEME.textOnPanel || '#fff',
          }}>
            {moderatorCharacter.name}
          </span>

          {/* Rotating status */}
          <div key={currentStatus} style={{
            fontSize: '0.65em',
            color: accentColor,
            textAlign: 'center',
            marginTop: '2px',
            fontStyle: 'italic',
            animation: 'moderatorFadeIn 0.5s ease-out',
          }}>
            {currentStatus}
          </div>
        </div>
      )}

      {/* Personality blurb intentionally NOT rendered inline — it ate sidebar
          real estate; the full story lives behind the ⓘ button (user call 07-09) */}
      {bioSection}
    </div>
  );
};

// BotVisionPanel — Launch and view an AI bot (OpenClaw agent) for the game
const BotVisionPanel = ({ THEME, chatGameStatus, setChatGameStatus, gameId }) => {
  const [subTab, setSubTab] = useState('desktop'); // 'desktop' or 'app'
  const [launching, setLaunching] = useState(false);
  const [stopping, setStopping] = useState(false);
  const [error, setError] = useState(null);
  const [cancelEnabled, setCancelEnabled] = useState(false);

  const agentStatus = chatGameStatus?.agent_status;
  const agentUrl = chatGameStatus?.agent_url;

  // Hide the Cancel button for the first 15s after status enters provisioning/starting.
  // The bots backend takes ~5–10s to register the customer_agents row that the
  // stopGameBot fallback lookup depends on. Clicking Cancel before that row exists
  // leaves an orphan ECS service (the cron sweep catches it within 5 min, but it's
  // a worse UX than just delaying the button).
  useEffect(() => {
    if (agentStatus === 'provisioning' || agentStatus === 'starting') {
      setCancelEnabled(false);
      const t = setTimeout(() => setCancelEnabled(true), 15000);
      return () => clearTimeout(t);
    }
    setCancelEnabled(false);
  }, [agentStatus]);

  // Tick every second while the bot is running so the expires-in countdown updates.
  // Only active when there's an expires_at to count down — saves work otherwise.
  const [now, setNow] = useState(Date.now());
  const expiresAt = chatGameStatus?.agent_expires_at;
  useEffect(() => {
    if (agentStatus !== 'running' || !expiresAt) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [agentStatus, expiresAt]);

  const handleStopBot = async () => {
    if (stopping) return;
    setStopping(true);
    setError(null);
    // Optimistically clear so the UI feels instant; backend WS will reconcile.
    setChatGameStatus(prev => prev ? {
      ...prev,
      agent_status: null,
      agent_url: null,
      agent_id: null,
    } : prev);
    try {
      const response = await fetch(`${API_BASE_URL}/chat-game/stop-bot`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ game_id: gameId }),
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to stop bot');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setStopping(false);
    }
  };

  const handleLaunchBot = async () => {
    setLaunching(true);
    setError(null);
    try {
      const response = await fetch(`${API_BASE_URL}/chat-game/launch-bot`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ game_id: gameId }),
      });
      const data = await response.json();
      if (response.status === 429) {
        // Rate limited — translate the generic backend message into something actionable.
        const retryAfterSec = parseInt(response.headers.get('Retry-After') || '0', 10);
        const wait = retryAfterSec >= 60
          ? `${Math.ceil(retryAfterSec / 60)} min`
          : retryAfterSec > 0
            ? `${retryAfterSec}s`
            : 'a minute';
        throw new Error(`Too many launch attempts. Please wait ${wait} and try again.`);
      }
      if (!response.ok) {
        throw new Error(data.error || 'Failed to launch bot');
      }
      // Optimistically update status
      setChatGameStatus(prev => prev ? { ...prev, agent_status: 'provisioning' } : prev);
    } catch (err) {
      setError(err.message);
    } finally {
      setLaunching(false);
    }
  };

  // No agent launched yet — show Launch Bot button
  if (!agentStatus) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '60px 20px', gap: '16px' }}>
        <div style={{ fontSize: '2.5em' }}>🤖</div>
        <div style={{ fontSize: '1.1em', fontWeight: 600, color: THEME.textOnPanel || THEME.textDark }}>Bot Vision</div>
        <div style={{ fontSize: '0.85em', color: THEME.textMuted, textAlign: 'center', maxWidth: '300px', lineHeight: 1.5 }}>
          Launch an AI bot that can see and interact with this space. It gets its own desktop environment and app.
        </div>
        {error && (
          <div style={{ fontSize: '0.85em', color: '#f44336', textAlign: 'center', maxWidth: '300px' }}>{error}</div>
        )}
        <button
          onClick={handleLaunchBot}
          disabled={launching}
          style={{
            padding: '12px 32px',
            fontSize: '1em',
            fontWeight: 600,
            color: '#fff',
            background: launching ? '#999' : `linear-gradient(135deg, ${THEME.accent || '#7C4DFF'}, ${THEME.accentDark || '#651FFF'})`,
            border: 'none',
            borderRadius: '10px',
            cursor: launching ? 'not-allowed' : 'pointer',
            transition: 'all 0.2s ease',
            boxShadow: launching ? 'none' : `0 4px 16px ${THEME.accent || '#7C4DFF'}40`,
          }}
        >
          {launching ? 'Launching...' : 'Launch Bot'}
        </button>
      </div>
    );
  }

  // Provisioning or starting — show spinner
  if (agentStatus === 'provisioning' || agentStatus === 'starting') {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '60px 20px', gap: '16px' }}>
        <div style={{
          width: '48px', height: '48px',
          border: '3px solid rgba(255,255,255,0.1)',
          borderTopColor: THEME.accent || '#7C4DFF',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite',
        }} />
        <div style={{ fontSize: '1.1em', fontWeight: 500, color: THEME.textOnPanel || THEME.textDark }}>
          {agentStatus === 'provisioning' ? 'Provisioning your bot...' : 'Bot is starting up...'}
        </div>
        <div style={{ fontSize: '0.8em', color: THEME.textMuted, opacity: 0.7 }}>
          This may take a minute or two
        </div>
        {cancelEnabled && (
          <button
            onClick={handleStopBot}
            disabled={stopping}
            style={{
              marginTop: '4px',
              padding: '6px 14px',
              fontSize: '0.8em',
              fontWeight: 500,
              color: THEME.textMuted || '#888',
              background: 'transparent',
              border: `1px solid ${THEME.border || '#333'}`,
              borderRadius: '6px',
              cursor: stopping ? 'not-allowed' : 'pointer',
              opacity: stopping ? 0.5 : 1,
            }}
          >
            {stopping ? 'Cancelling…' : 'Cancel'}
          </button>
        )}
        {error && (
          <div style={{ fontSize: '0.8em', color: '#f44336', textAlign: 'center', maxWidth: '300px' }}>{error}</div>
        )}
      </div>
    );
  }

  // Failed — show error with retry
  if (agentStatus === 'failed') {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '60px 20px', gap: '16px' }}>
        <div style={{ fontSize: '2.5em' }}>⚠️</div>
        <div style={{ fontSize: '1.1em', fontWeight: 600, color: '#f44336' }}>Bot failed to start</div>
        {error && (
          <div style={{ fontSize: '0.85em', color: THEME.textMuted, textAlign: 'center', maxWidth: '300px' }}>{error}</div>
        )}
        <button
          onClick={handleLaunchBot}
          disabled={launching}
          style={{
            padding: '10px 24px',
            fontSize: '0.95em',
            fontWeight: 600,
            color: '#fff',
            background: THEME.accent || '#7C4DFF',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
          }}
        >
          {launching ? 'Retrying...' : 'Retry'}
        </button>
      </div>
    );
  }

  // Running — show Desktop/App sub-tabs with iframe
  const iframeSrc = subTab === 'desktop'
    ? `${agentUrl}/viewer`
    : `${agentUrl}/app/`;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: '700px' }}>
      {/* Sub-tab bar */}
      <div style={{ display: 'flex', gap: '2px', padding: '8px 8px 0', borderBottom: `1px solid ${THEME.border || '#333'}`, alignItems: 'center' }}>
        {['desktop', 'app'].map(tab => (
          <button
            key={tab}
            onClick={() => setSubTab(tab)}
            style={{
              padding: '8px 20px',
              fontSize: '0.9em',
              fontWeight: subTab === tab ? 600 : 400,
              color: subTab === tab ? (THEME.textOnPanel || '#fff') : (THEME.textMuted || '#888'),
              background: subTab === tab
                ? `linear-gradient(180deg, ${THEME.accent || '#7C4DFF'}25 0%, ${THEME.bgMedium || '#2a2a3e'} 100%)`
                : 'transparent',
              border: 'none',
              borderRadius: '8px 8px 0 0',
              borderBottom: subTab === tab ? `3px solid ${THEME.accent || '#7C4DFF'}` : '2px solid transparent',
              cursor: 'pointer',
              transition: 'all 0.15s ease',
            }}
          >
            {tab === 'desktop' ? '🖥️ Desktop' : '📱 App'}
          </button>
        ))}
        {/* Expires-in countdown (only when agent_expires_at is set) — sits left of Stop */}
        {expiresAt && (() => {
          const remainingMs = Math.max(0, expiresAt - now);
          const m = Math.floor(remainingMs / 60000);
          const s = Math.floor((remainingMs % 60000) / 1000);
          const isLow = remainingMs < 5 * 60 * 1000; // last 5 min — warn
          return (
            <span
              style={{
                marginLeft: 'auto',
                marginRight: '8px',
                marginBottom: '4px',
                padding: '4px 10px',
                fontSize: '0.75em',
                fontWeight: 500,
                color: isLow ? '#f59e0b' : (THEME.textMuted || '#888'),
                background: 'transparent',
                border: `1px solid ${isLow ? '#f59e0b55' : (THEME.border || '#333')}`,
                borderRadius: '6px',
                fontVariantNumeric: 'tabular-nums',
              }}
              title="Bot will be automatically stopped when this hits 0:00 (30-min hard cap)"
            >
              ⏱ {m}:{String(s).padStart(2, '0')}
            </span>
          );
        })()}
        {/* Stop button — pushed right (or sits right of countdown) */}
        <button
          onClick={handleStopBot}
          disabled={stopping}
          style={{
            marginLeft: expiresAt ? '0' : 'auto',
            marginRight: '6px',
            marginBottom: '4px',
            padding: '6px 14px',
            fontSize: '0.8em',
            fontWeight: 500,
            color: stopping ? (THEME.textMuted || '#888') : '#fff',
            background: stopping ? 'transparent' : '#dc2626',
            border: stopping ? `1px solid ${THEME.border || '#333'}` : '1px solid #dc2626',
            borderRadius: '6px',
            cursor: stopping ? 'not-allowed' : 'pointer',
            transition: 'all 0.15s ease',
          }}
          title="Stop the bot — this deletes its container and ends the session"
        >
          {stopping ? 'Stopping…' : '🛑 Stop'}
        </button>
      </div>
      {/* Iframe */}
      <div style={{ flex: 1, minHeight: 0, padding: '8px' }}>
        <iframe
          key={subTab}
          src={iframeSrc}
          style={{
            width: '100%',
            height: '100%',
            border: 'none',
            borderRadius: '8px',
            backgroundColor: '#000',
            minHeight: '650px',
          }}
          allow="autoplay; fullscreen; microphone; camera"
          sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-modals"
          title={subTab === 'desktop' ? 'Bot Desktop' : 'Bot App'}
        />
      </div>
    </div>
  );
};

// GamesPanel — Featured competitive & imposter games discovery panel
const GamesPanelInner = ({ THEME }) => {
  const [competitions, setCompetitions] = useState([]);
  const [currentWeekIndex, setCurrentWeekIndex] = useState(0);
  const [weekKey, setWeekKey] = useState('');
  const [loading, setLoading] = useState(true);
  const [joiningIndex, setJoiningIndex] = useState(null);
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const fetchedRef = useRef(false);
  // 🔓 Game gating (gameUnlocks.js — table approved 07-13). Bots bypass all
  // of it: the explorer plays everything (Nina's rule).
  const isBotCtx = new URLSearchParams(window.location.search).get('bot') === '1'
    || (typeof window !== 'undefined' && window.sessionStorage.getItem('directTileNav') === '1');
  const [unlockToast, setUnlockToast] = useState(null);
  // 🔑 Progress code footer (07-14): show this browser's recovery code +
  // restore-by-code input. Restoring adopts the typed code (future syncs
  // write to its row) and folds the server progress into localStorage;
  // the bump forces re-derive of every isGameUnlocked below.
  const [showRestore, setShowRestore] = useState(false);
  const [restoreInput, setRestoreInput] = useState('');
  const [restoreMsg, setRestoreMsg] = useState(null);
  const [codeCopied, setCodeCopied] = useState(false);
  const [, setProgressBump] = useState(0);
  const handleRestore = async () => {
    const code = restoreInput.trim().toLowerCase();
    if (!/^[a-z]{3,12}-[a-z]{3,12}-[0-9a-f]{6}$/.test(code)) {
      setRestoreMsg({ err: true, text: 'That code looks off. Format: word-word-123abc' });
      return;
    }
    try {
      const urlGameId = new URLSearchParams(window.location.search).get('gameId');
      const res = await chatGameApi.syncProgress(urlGameId, code, {});
      if (!res?.existed) {
        setRestoreMsg({ err: true, text: 'No progress found for that code.' });
        return;
      }
      setSyncCode(code);
      applyServerProgress(res.progress || {});
      setProgressBump(n => n + 1);
      setRestoreMsg({ err: false, text: '🔓 Progress restored!' });
      setRestoreInput('');
    } catch (e) {
      setRestoreMsg({ err: true, text: 'Restore failed. Try again in a bit.' });
    }
  };
  useEffect(() => {
    if (!competitions.length || isBotCtx) return;
    const fresh = takeNewUnlocks(competitions);
    if (fresh.length) {
      setUnlockToast(fresh[0]);
      try { soundManager.play('reaction'); } catch (e) { /* sound is garnish */ }
      const t = setTimeout(() => setUnlockToast(null), 7000);
      return () => clearTimeout(t);
    }
  }, [competitions, isBotCtx]);

  useEffect(() => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;
    (async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api?getAllFeaturedCompetitions=true`);
        const data = await res.json();
        if (data.competitions) {
          setCompetitions(data.competitions);
          setCurrentWeekIndex(data.currentWeekIndex ?? 0);
          setWeekKey(data.weekKey || '');
        }
      } catch (err) {
        console.error('[GamesPanel] Failed to fetch competitions:', err);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const MODEL_MAP = {
    'Gemini 3 Flash': 'google/gemini-3-flash-preview',
    'Gemini 2.5 Pro': 'google/gemini-2.5-pro-preview',
    'ChatGPT (GPT-5.1)': 'openai/gpt-5.1-chat',
        'ChatGPT (GPT-5 Mini)': 'openai/gpt-5-mini',
    'Claude Haiku 4.5': 'anthropic/claude-haiku-4.5',
    'Llama 3.3 Nemotron': 'nvidia/llama-3.3-nemotron-super-49b-v1.5',
    'DeepSeek v3.2': 'deepseek/deepseek-v3.2',
    'Qwen3 Max': 'qwen/qwen3-max-thinking',
    'Qwen3 30B': 'qwen/qwen3-30b-a3b-instruct-2507', // 🤖💸 10-01 agent floor (R10): Plot Twist Masters' third seat (was Qwen3 Max)
    'Kimi K2': 'moonshotai/kimi-k2-0905'
  };

  const handlePlayGame = async (template) => {
    if (joiningIndex !== null) return;
    if (!isGameUnlocked(template, currentWeekIndex, isBotCtx)) return; // locked — the card shows how
    if (!isBotCtx && template.index === currentWeekIndex && isGameGated(template)) {
      addFeaturedPlay(template.index, weekKey); // 3 featured-week plays = keep it forever
    }
    setJoiningIndex(template.index);
    try {
      let playerId = localStorage.getItem('competition_player_id');
      if (!playerId) {
        playerId = 'player_' + Math.random().toString(36).substr(2, 9) + '_' + Date.now();
        localStorage.setItem('competition_player_id', playerId);
      }

      // Human gets a stable randomized display name so the roster shows "RandomName (You)"
      // instead of just "You". Bot context: a ported chibi plays under its
      // agent name (§4.5 You-seat fix — the 07-31 roast landed under a seat
      // literally named "You"); the house explorer keeps its existing 'You'.
      const humanName = (() => {
        const isBotCtx = new URLSearchParams(window.location.search).get('bot') === '1'
          || (typeof window !== 'undefined' && window.sessionStorage.getItem('directTileNav') === '1');
        if (isBotCtx) {
          // Different component scope than the botAgentName memo — read the
          // same sources directly (URL param wins, sessionStorage carries it
          // across internal navigation).
          const agentName = new URLSearchParams(window.location.search).get('agent_name')
            || (typeof window !== 'undefined' ? window.sessionStorage.getItem('botAgentName') : null);
          return (agentName && agentName.trim().slice(0, 40)) || 'You';
        }
        const stored = localStorage.getItem('competition_player_name');
        if (stored) return stored;
        const adjectives = ['Swift', 'Bold', 'Clever', 'Lucky', 'Witty', 'Sharp', 'Brave', 'Keen', 'Sly', 'Chill', 'Sneaky', 'Cheeky', 'Spicy', 'Salty', 'Snazzy', 'Wacky', 'Mighty', 'Sassy', 'Goofy', 'Sparkly'];
        const nouns = ['Fox', 'Hawk', 'Wolf', 'Lynx', 'Raven', 'Tiger', 'Panda', 'Otter', 'Falcon', 'Bear', 'Capybara', 'Quokka', 'Axolotl', 'Hedgehog', 'Penguin', 'Sloth', 'Bunny', 'Pickle', 'Waffle', 'Muffin'];
        const name = `${adjectives[Math.floor(Math.random() * adjectives.length)]}${nouns[Math.floor(Math.random() * nouns.length)]}`;
        localStorage.setItem('competition_player_name', name);
        return name;
      })();

      const players = [];
      for (const [modelName, count] of Object.entries(template.ai_models || {})) {
        const modelId = MODEL_MAP[modelName];
        if (!modelId) continue;
        for (let i = 0; i < count; i++) {
          players.push({
            name: count > 1 ? `${modelName} ${i + 1}` : modelName,
            character_description: `An AI competitor in ${template.name}`,
            personality_traits: [],
            objective: template.objective,
            secret_info: '',
            is_human: false,
            model: modelId
          });
        }
      }
      players.push({
        name: humanName,
        character_description: 'A human competitor',
        personality_traits: [],
        objective: template.objective,
        secret_info: '',
        is_human: true,
        model: null
      });

      const isImposter = !!template.imposter_mode;
      const config = {
        name: `${template.name} - Competition`,
        objective: template.objective,
        scene_description: template.scene_description || template.description,
        competitive_mode: true,
        imposter_mode: isImposter,
        ...(isImposter
          ? { imposter_word_rounds: template.imposter_word_rounds || 3 }
          : { total_rounds: template.total_rounds || 3, max_rounds: (template.total_rounds || 3) * 2 }),
        messages_per_round: template.messages_per_round || 4,
        is_shared: true,
        is_featured_competition: true,
        featured_week_key: weekKey,
        featured_template_index: template.index,
        human_player_identifier: playerId,
        interests: [isImposter ? 'imposter' : 'competitive', 'featured'],
        players,
        profileImagesEnabled: true,
        allowAiNameSelection: true,
        ...(template.music_url ? { music_url: template.music_url } : {})
      };

      const result = await chatGameApi.createGameInstant(config);
      if (result.human_player_id) {
        localStorage.setItem(`humanPlayerId_${result.game_id}`, result.human_player_id);
      }
      const newGameUrl = `/game-step?gameId=${encodeURIComponent(result.game_id)}&loading=true`;
      // Bot context (?bot=1 or directTileNav): navigate the current tab so the
      // explorer's CDP follows into the new game. Humans get the original
      // new-tab behavior so they don't lose the source space.
      const isBot = new URLSearchParams(window.location.search).get('bot') === '1'
        || (typeof window !== 'undefined' && window.sessionStorage.getItem('directTileNav') === '1');
      if (isBot) {
        window.location.href = newGameUrl;
      } else {
        window.open(newGameUrl, '_blank');
      }
    } catch (err) {
      console.error('[GamesPanel] Failed to start game:', err);
    } finally {
      setJoiningIndex(null);
    }
  };

  const glass = {
    backgroundColor: 'rgba(255,255,255,0.03)',
    backdropFilter: 'blur(12px)',
    WebkitBackdropFilter: 'blur(12px)',
    border: '1px solid rgba(255,255,255,0.06)'
  };

  if (loading) {
    return (
      <div style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
        <style>{`@keyframes gamesPanelShimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }`}</style>
        {[1, 2, 3, 4].map(i => (
          <div key={i} style={{
            height: i === 1 ? '160px' : '68px',
            borderRadius: '12px',
            background: `linear-gradient(90deg, ${THEME.bgLight || '#222'} 25%, ${THEME.bgMedium || '#333'} 50%, ${THEME.bgLight || '#222'} 75%)`,
            backgroundSize: '200% 100%',
            animation: 'gamesPanelShimmer 1.5s ease-in-out infinite',
            opacity: 1 - (i * 0.15),
          }} />
        ))}
      </div>
    );
  }

  if (!competitions.length) {
    return (
      <div style={{ textAlign: 'center', padding: '60px 20px', color: THEME.textMuted }}>
        <div style={{ fontSize: '2.5em', marginBottom: '12px' }}>🎮</div>
        <div style={{ fontSize: '1em', fontWeight: 600, color: THEME.textOnPanel || THEME.textDark }}>Arcade</div>
        <div style={{ fontSize: '0.8em', marginTop: '6px', opacity: 0.6 }}>Nothing to play right now</div>
      </div>
    );
  }

  const featured = competitions.find(c => c.index === currentWeekIndex) || competitions[0];
  const otherGames = competitions.filter(c => c.index !== featured.index);

  return (
    <div style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: '16px', marginBottom: '20px' }}>
      <style>{`
        @keyframes gamesPanelFadeIn { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes gamesPanelPulse { 0%, 100% { opacity: 0.7; } 50% { opacity: 1; } }
      `}</style>

      {/* 🔓 Unlock celebration — fires once per game, when a milestone crossed */}
      {unlockToast && (
        <div style={{
          padding: '12px 14px', borderRadius: 12,
          background: 'linear-gradient(135deg, #2e7d32, #145a1c)', color: '#fff',
          fontWeight: 700, fontSize: '0.85em', boxShadow: '0 6px 20px rgba(0,0,0,0.4)',
          animation: 'gamesPanelFadeIn 0.4s ease-out',
          display: 'flex', alignItems: 'center', gap: 8,
        }}>
          <span style={{ fontSize: '1.4em' }}>🔓</span>
          <span>{unlockToast.name} unlocked! Jump in!</span>
        </div>
      )}

      {/* Section Header */}
      <div style={{ animation: 'gamesPanelFadeIn 0.4s ease-out' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
          <span style={{ fontSize: '1.2em' }}>🏆</span>
          <span style={{ fontSize: '1.05em', fontWeight: 700, color: THEME.textOnPanel || THEME.textDark, letterSpacing: '0.3px' }}>Featured Games</span>
        </div>
        <div style={{ fontSize: '0.75em', color: THEME.textOnPanelMuted || THEME.textMuted, marginLeft: '28px' }}>
          Compete against AI models in head-to-head battles
        </div>
      </div>

      {/* Hero Card — This Week's Featured */}
      <div
        onClick={() => handlePlayGame(featured)}
        style={{
          position: 'relative',
          borderRadius: '14px',
          padding: '22px 20px',
          background: featured.themeGradient || `linear-gradient(135deg, ${featured.themeColor}20, ${featured.themeColor}60)`,
          overflow: 'hidden',
          cursor: joiningIndex === featured.index ? 'wait' : 'pointer',
          transition: 'transform 0.2s ease, box-shadow 0.2s ease',
          boxShadow: `0 4px 20px rgba(0,0,0,0.2), 0 0 20px ${featured.themeColor}25`,
          animation: 'gamesPanelFadeIn 0.5s ease-out',
          ...(featured.bannerStyle || {}),
        }}
        onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = `0 8px 30px rgba(0,0,0,0.25), 0 0 30px ${featured.themeColor}35`; }}
        onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = `0 4px 20px rgba(0,0,0,0.2), 0 0 20px ${featured.themeColor}25`; }}
      >
        {/* Decorative background emojis */}
        <div style={{ position: 'absolute', top: '-10px', right: '-10px', fontSize: '4em', opacity: 0.08, transform: 'rotate(15deg)', pointerEvents: 'none' }}>
          {featured.emojis?.[0]}
        </div>
        <div style={{ position: 'absolute', bottom: '-5px', left: '10px', fontSize: '2.5em', opacity: 0.06, transform: 'rotate(-10deg)', pointerEvents: 'none' }}>
          {featured.emojis?.[2]}
        </div>

        {/* Label */}
        <div style={{
          fontSize: '0.6rem', fontWeight: 700, letterSpacing: '1.5px', textTransform: 'uppercase',
          color: 'rgba(255,255,255,0.6)', marginBottom: '6px',
          ...(featured.labelStyle || {}),
        }}>
          {featured.labelText || "THIS WEEK'S FEATURED"}{isGameGated(featured) ? ' · ⭐ FREE THIS WEEK' : ''}
        </div>

        {/* Title */}
        <div style={{
          fontSize: '1.2em', fontWeight: 800, color: '#fff', marginBottom: '8px', lineHeight: 1.2,
          ...(featured.titleStyle || {}),
        }}>
          {featured.highlightWord
            ? (() => {
                const parts = featured.name.split(new RegExp(`(${featured.highlightWord}[!?.]*)`  , 'i'));
                return parts.map((part, i) =>
                  part.toLowerCase().startsWith(featured.highlightWord.toLowerCase())
                    ? <span key={i} style={{ textShadow: '0 0 20px rgba(255,255,255,0.5)' }}>{part}</span>
                    : part
                );
              })()
            : featured.name}
        </div>

        {/* Description */}
        <div style={{
          fontSize: '0.78em', color: featured.detailsTextColor || 'rgba(255,255,255,0.8)',
          lineHeight: 1.4, marginBottom: '14px',
          display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
        }}>
          {featured.description}
        </div>

        {/* Bottom row: mode badge + AI models + Play button */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
          <span style={{
            fontSize: '0.6em', padding: '3px 8px', borderRadius: '4px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px',
            backgroundColor: featured.imposter_mode ? 'rgba(184,134,11,0.25)' : 'rgba(255,255,255,0.15)',
            color: featured.imposter_mode ? '#ffd700' : 'rgba(255,255,255,0.9)',
            border: `1px solid ${featured.imposter_mode ? 'rgba(255,215,0,0.3)' : 'rgba(255,255,255,0.2)'}`,
          }}>
            {featured.imposter_mode ? 'Imposter' : 'Competitive'}
          </span>
          <span style={{ fontSize: '0.65em', color: featured.detailsTextColor || 'rgba(255,255,255,0.5)' }}>
            vs {Object.values(featured.ai_models || {}).reduce((a, b) => a + b, 0)} AI
          </span>
          <span style={{ fontSize: '0.65em', color: featured.detailsTextColor || 'rgba(255,255,255,0.5)' }}>
            {featured.emojis?.join(' ')}
          </span>
          <button
            disabled={joiningIndex !== null}
            style={{
              marginLeft: 'auto', padding: '8px 18px', borderRadius: '20px', border: 'none',
              backgroundColor: 'rgba(255,255,255,0.2)', color: '#fff',
              fontSize: '0.75em', fontWeight: 700, cursor: joiningIndex === featured.index ? 'wait' : 'pointer',
              transition: 'all 0.2s', letterSpacing: '0.3px', textTransform: 'uppercase',
              backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)',
              boxShadow: '0 2px 8px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.15)',
              opacity: joiningIndex !== null && joiningIndex !== featured.index ? 0.5 : 1,
            }}
            onMouseEnter={e => { if (joiningIndex === null) { e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.35)'; e.currentTarget.style.transform = 'scale(1.05)'; }}}
            onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.2)'; e.currentTarget.style.transform = 'scale(1)'; }}
            onClick={e => { e.stopPropagation(); handlePlayGame(featured); }}
          >
            {joiningIndex === featured.index ? 'Starting...' : 'Play Now'}
          </button>
        </div>
      </div>

      {/* Section divider */}
      {otherGames.length > 0 && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: '10px',
          animation: 'gamesPanelFadeIn 0.55s ease-out',
        }}>
          <span style={{ fontSize: '0.75em', fontWeight: 600, color: THEME.textOnPanelMuted || THEME.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px', whiteSpace: 'nowrap' }}>
            More Games
          </span>
          <div style={{ flex: 1, height: '1px', backgroundColor: THEME.textOnPanelMuted || THEME.textMuted, opacity: 0.2 }} />
        </div>
      )}

      {/* Compact game cards — locked ones stay VISIBLE with their unlock
          hint (soft gate, approved 07-13); tap does nothing until earned */}
      {otherGames.map((comp, i) => {
        const locked = !isGameUnlocked(comp, currentWeekIndex, isBotCtx);
        return (
        <div
          key={comp.index}
          onClick={() => handlePlayGame(comp)}
          onMouseEnter={() => setHoveredIndex(comp.index)}
          onMouseLeave={() => setHoveredIndex(null)}
          title={locked ? unlockHint(comp) : undefined}
          style={{
            display: 'flex', alignItems: 'center', gap: '12px',
            padding: '14px 14px', borderRadius: '12px',
            ...glass,
            borderLeft: `4px solid ${locked ? 'rgba(255,255,255,0.25)' : comp.themeColor}`,
            cursor: locked ? 'default' : (joiningIndex === comp.index ? 'wait' : 'pointer'),
            transition: 'transform 0.15s ease, box-shadow 0.15s ease, background-color 0.15s ease',
            animation: `gamesPanelFadeIn ${0.6 + i * 0.07}s ease-out`,
            transform: !locked && hoveredIndex === comp.index ? 'translateX(4px)' : 'translateX(0)',
            boxShadow: !locked && hoveredIndex === comp.index ? `0 4px 20px ${comp.themeColor}20` : 'none',
            backgroundColor: !locked && hoveredIndex === comp.index ? `${comp.themeColor}08` : 'rgba(255,255,255,0.03)',
            opacity: locked ? 0.75 : (joiningIndex !== null && joiningIndex !== comp.index ? 0.5 : 1),
          }}
        >
          {/* Emoji icon — locked games keep their SECRET (Nina 07-22): a ❓
              instead of the game's emoji, so the reveal is part of earning it */}
          <div style={{
            fontSize: '1.4em', flexShrink: 0, width: '36px', height: '36px',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            borderRadius: '8px',
            background: `linear-gradient(135deg, ${comp.themeColor}15, ${comp.themeColor}08)`,
            filter: locked ? 'grayscale(0.9)' : 'none',
          }}>
            {locked ? '❓' : comp.emojis?.[0]}
          </div>

          {/* Info */}
          <div style={{ flex: 1, minWidth: 0 }}>
            {/* Phone widths squeeze this column to ~190px: a nowrap title
                truncated to "Find the Gol…" and the meta spans wrapped
                letter-by-letter ("vs/3/AI" waterfall — Nina 07-20). Titles and
                hints clamp to 2 lines; meta items wrap as whole units. */}
            {/* Locked = MYSTERY (Nina 07-22): no name, no meta — only the
                unlock hint. What the game IS is the reward for earning it. */}
            <div style={{
              fontSize: '0.85em', fontWeight: 700,
              color: THEME.textOnPanel || THEME.textDark,
              letterSpacing: locked ? '0.15em' : undefined,
              display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
            }}>{locked ? '? ? ?' : comp.name}</div>
            <div style={{
              fontSize: '0.7em', marginTop: '2px',
              color: locked ? '#ffcf7a' : (THEME.textOnPanelMuted || THEME.textMuted),
              fontWeight: locked ? 600 : 400,
              display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
            }}>{locked ? `🔒 ${unlockHint(comp)}` : (comp.tagline || comp.description)}</div>
            {!locked && (
            <div style={{ display: 'flex', gap: '6px', marginTop: '5px', alignItems: 'center', flexWrap: 'wrap' }}>
              <span style={{
                fontSize: '0.58em', padding: '2px 6px', borderRadius: '4px', fontWeight: 700, textTransform: 'uppercase',
                whiteSpace: 'nowrap',
                backgroundColor: comp.imposter_mode ? 'rgba(184,134,11,0.15)' : `${comp.themeColor}12`,
                color: comp.imposter_mode ? '#b8860b' : comp.themeColor,
              }}>{comp.imposter_mode ? 'Imposter' : 'Competitive'}</span>
              <span style={{ fontSize: '0.6em', whiteSpace: 'nowrap', color: THEME.textOnPanelMuted || THEME.textMuted }}>
                {comp.imposter_word_rounds || comp.total_rounds} rounds
              </span>
              <span style={{ fontSize: '0.6em', whiteSpace: 'nowrap', color: THEME.textOnPanelMuted || THEME.textMuted }}>
                vs {Object.values(comp.ai_models || {}).reduce((a, b) => a + b, 0)} AI
              </span>
            </div>
            )}
          </div>

          {/* Play button — a lock chip until earned */}
          {locked ? (
            <span style={{
              padding: '6px 14px', borderRadius: '20px', flexShrink: 0,
              background: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.6)',
              fontSize: '0.7em', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.3px',
            }}>
              🔒 Locked
            </span>
          ) : (
          <button
            disabled={joiningIndex !== null}
            onClick={e => { e.stopPropagation(); handlePlayGame(comp); }}
            style={{
              padding: '6px 14px', borderRadius: '20px', border: 'none',
              background: hoveredIndex === comp.index
                ? `linear-gradient(135deg, ${comp.themeColor}, ${comp.themeColor}cc)`
                : `${comp.themeColor}20`,
              color: hoveredIndex === comp.index ? '#fff' : comp.themeColor,
              fontSize: '0.7em', fontWeight: 700, cursor: joiningIndex === comp.index ? 'wait' : 'pointer',
              flexShrink: 0, transition: 'all 0.2s', textTransform: 'uppercase', letterSpacing: '0.3px',
            }}
          >
            {joiningIndex === comp.index ? '...' : 'Play'}
          </button>
          )}
        </div>
        );
      })}

      {/* 🔑 Progress code footer — save/restore unlocks across browsers */}
      {!isBotCtx && getSyncCode() && (
        <div style={{
          marginTop: '10px', padding: '9px 12px', borderRadius: '10px',
          background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)',
          fontSize: '0.72em', color: 'rgba(255,255,255,0.6)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
            <span>🔑 Progress code:</span>
            <code
              onClick={() => {
                try { navigator.clipboard.writeText(getSyncCode()); setCodeCopied(true); setTimeout(() => setCodeCopied(false), 2000); } catch (e) { /* clipboard blocked */ }
              }}
              title="Tap to copy. Save it somewhere safe!"
              style={{ cursor: 'pointer', fontWeight: 700, color: '#ffd54f', background: 'rgba(0,0,0,0.3)', padding: '2px 8px', borderRadius: '6px' }}
            >
              {codeCopied ? '✓ Copied!' : getSyncCode()}
            </code>
            <span
              onClick={() => { setShowRestore(v => !v); setRestoreMsg(null); }}
              style={{ cursor: 'pointer', textDecoration: 'underline', color: 'rgba(255,255,255,0.5)' }}
            >
              {showRestore ? 'Cancel' : 'Have a code? Restore'}
            </span>
          </div>
          <div style={{ marginTop: '4px', fontSize: '0.92em', color: 'rgba(255,255,255,0.4)' }}>
            Save this code to keep your unlocks if you switch browsers.
          </div>
          {showRestore && (
            <div style={{ display: 'flex', gap: '6px', marginTop: '8px', flexWrap: 'wrap' }}>
              <input
                value={restoreInput}
                onChange={e => setRestoreInput(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') handleRestore(); }}
                placeholder="word-word-123abc"
                style={{
                  flex: 1, minWidth: '140px', padding: '6px 10px', borderRadius: '8px',
                  border: '1px solid rgba(255,255,255,0.2)', background: 'rgba(0,0,0,0.3)',
                  color: '#fff', fontSize: '1em',
                }}
              />
              <button
                onClick={handleRestore}
                style={{
                  padding: '6px 14px', borderRadius: '8px', border: 'none', cursor: 'pointer',
                  background: 'rgba(255,213,79,0.85)', color: '#1a1a1a', fontWeight: 700, fontSize: '1em',
                }}
              >
                Restore
              </button>
            </div>
          )}
          {restoreMsg && (
            <div style={{ marginTop: '6px', fontWeight: 700, color: restoreMsg.err ? '#ff8a65' : '#7ee787' }}>
              {restoreMsg.text}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
const GamesPanel = React.memo(GamesPanelInner, (prev, next) => prev.THEME?.primary === next.THEME?.primary);

// HomepagePanel — Immersive dashboard with atmosphere, rich character cards, and live data
// WhileYouWereAwayCard — one-shot gossip recap shown when a returning visitor
// re-enters a space after a real gap (≥1h) with meaningful missed activity.
// Self-contained: owns the localStorage last-seen bookkeeping; renders nothing
// for first-time visitors, short gaps, or quiet spaces. Caller gates bot mode
// and competitive spaces.
const WYWA_MIN_GAP_MS = 60 * 60 * 1000;
const WYWA_MIN_MISSED_MSGS = 5;

const WhileYouWereAwayCard = ({ gameId, allActions, users, spaceName, THEME, isMobile = false, raisePx = 0, onVisibleChange = null }) => {
  const [recap, setRecap] = React.useState(null);
  const [dismissed, setDismissed] = React.useState(false);
  const computedRef = React.useRef(false);
  // 08-02 band de-conflict: report visibility up so siblings in the bottom
  // band (typing indicator) can yield; false again on dismiss AND unmount.
  React.useEffect(() => {
    if (onVisibleChange) onVisibleChange(!!recap && !dismissed);
  }, [recap, dismissed, onVisibleChange]);
  React.useEffect(() => () => { if (onVisibleChange) onVisibleChange(false); }, [onVisibleChange]);

  React.useEffect(() => {
    if (computedRef.current || !gameId || allActions.length === 0) return;
    computedRef.current = true;
    const key = `wywa_last_seen_${gameId}`;
    const now = Date.now();
    const markSeen = () => { try { localStorage.setItem(key, String(now)); } catch (e) { /* private mode */ } };
    let lastSeenMs = null;
    try { lastSeenMs = parseInt(localStorage.getItem(key), 10) || null; } catch (e) { /* private mode */ }

    if (!lastSeenMs || now - lastSeenMs < WYWA_MIN_GAP_MS) { markSeen(); return; }

    const missed = allActions.filter(a => new Date(a.created_at).getTime() > lastSeenMs);
    const msgTypes = new Set(['message', 'public_message', 'human_interjection']);
    const missedMsgs = missed.filter(a => msgTypes.has(a.type) && a.data?.message);
    const missedAnalyses = missed.filter(a => a.type === 'round_analysis' && a.data);
    if (missedMsgs.length < WYWA_MIN_MISSED_MSGS && missedAnalyses.length === 0) { markSeen(); return; }

    // Chapter recaps you missed (host_intro = one-sentence recap + one-sentence hype)
    const lines = [];
    missedAnalyses.slice(-3).forEach(a => {
      if (a.data.host_intro) {
        lines.push({ icon: '📺', text: a.data.host_intro, sub: a.data.chapter_title ? `“${a.data.chapter_title}”` : null });
      }
    });

    // Ship status changes across the gap — the actual gossip
    const analyses = allActions
      .filter(a => a.type === 'round_analysis' && Array.isArray(a.data?.relationships))
      .sort((x, y) => new Date(x.created_at) - new Date(y.created_at));
    const before = [...analyses].reverse().find(a => new Date(a.created_at).getTime() <= lastSeenMs);
    const after = analyses[analyses.length - 1];
    if (before && after && before !== after) {
      const keyOf = (pair) => pair.map(n => (n || '').toLowerCase().trim()).sort().join('::');
      const prevStatus = {};
      before.data.relationships.forEach(r => { if (Array.isArray(r.pair)) prevStatus[keyOf(r.pair)] = r.status; });
      after.data.relationships.forEach(r => {
        if (!Array.isArray(r.pair)) return;
        const k = keyOf(r.pair);
        if (prevStatus[k] && r.status && prevStatus[k] !== r.status) {
          lines.push({
            icon: '🚨',
            text: `${r.ship_name || r.pair.join(' & ')} went from ${prevStatus[k]} to ${r.status}. Don't bring it up.`,
            sub: r.headline || null,
          });
        }
      });
    }

    // Quiet fallback so a chatty-but-chapterless gap still gets a greeting
    if (lines.length === 0 && missedMsgs.length >= WYWA_MIN_MISSED_MSGS) {
      const speakers = [...new Set(missedMsgs.map(m => {
        const u = users.find(u2 => u2.id === m.game_instance_player_id || u2.game_player_id === m.game_instance_player_id);
        return u?.name || u?.data?.name;
      }).filter(Boolean))];
      lines.push({
        icon: '💬',
        text: `${missedMsgs.length} messages flew by since your last visit${speakers.length ? ` — mostly ${speakers.slice(0, 3).join(', ')}` : ''}.`,
        sub: null,
      });
    }

    markSeen();
    if (lines.length > 0) setRecap({ lines: lines.slice(0, 4) });
  }, [gameId, allActions, users]);

  // Re-stamp every 5 min while open so leaving mid-session records roughly right
  React.useEffect(() => {
    if (!gameId) return;
    const t = setInterval(() => {
      try { localStorage.setItem(`wywa_last_seen_${gameId}`, String(Date.now())); } catch (e) { /* private mode */ }
    }, 5 * 60 * 1000);
    return () => clearInterval(t);
  }, [gameId]);

  // Auto-dismiss after 25s
  React.useEffect(() => {
    if (!recap) return;
    const t = setTimeout(() => setDismissed(true), 25000);
    return () => clearTimeout(t);
  }, [recap]);

  if (!recap || dismissed) return null;
  return (
    <div style={{
      // 📱 07-26: at bottom:20 / z2600 this painted UNDERNEATH the mobile thumb
      // dock (z5000) and bottom nav (z5100) — it fires, you just never see it on
      // a phone, which is the likeliest source of the standing "WYWA never
      // fires" belief. Clear the chrome and out-rank it.
      position: 'fixed',
      // raisePx steps this card over the finished-game reset card when both
      // are up at once (returning to a completed space) — same band, no pile.
      bottom: isMobile ? navBottomPlus(12 + raisePx) : '20px',
      left: isMobile ? '10px' : '20px',
      right: isMobile ? '10px' : undefined,
      width: isMobile ? 'auto' : '340px', maxWidth: 'calc(100vw - 20px)',
      zIndex: isMobile ? 5200 : 2600, borderRadius: '14px', overflow: 'hidden',
      backgroundColor: THEME?.bgMedium || '#1a1a2e',
      border: `1px solid ${THEME?.accent || '#8b5cf6'}50`,
      boxShadow: '0 8px 32px rgba(0,0,0,0.45)',
      animation: 'wywaSlideIn 0.45s ease-out',
    }}>
      <style>{`@keyframes wywaSlideIn { from { opacity: 0; transform: translateY(16px); } to { opacity: 1; transform: translateY(0); } }`}</style>
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '10px 14px',
        background: `linear-gradient(135deg, ${THEME?.accent || '#8b5cf6'}25, transparent)`,
      }}>
        <span style={{ fontSize: '0.78em', fontWeight: 800, color: THEME?.accent || '#8b5cf6', textTransform: 'uppercase', letterSpacing: '1px' }}>
          👀 While you were away…
        </span>
        <button
          onClick={() => setDismissed(true)}
          aria-label="Dismiss recap"
          style={{ background: 'none', border: 'none', color: THEME?.textMuted || '#888', cursor: 'pointer', fontSize: '0.9em', padding: '2px 4px', lineHeight: 1 }}
        >✕</button>
      </div>
      <div style={{ padding: '10px 14px 14px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
        {recap.lines.map((line, i) => (
          <div key={i} style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
            <span style={{ flexShrink: 0, fontSize: '0.9em' }}>{line.icon}</span>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: '0.78em', color: THEME?.textOnPanel || '#eee', lineHeight: 1.45 }}>{line.text}</div>
              {line.sub && <div style={{ fontSize: '0.68em', color: THEME?.textMuted || '#888', fontStyle: 'italic', marginTop: '2px' }}>{line.sub}</div>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// showMediaSpotlight (Nina 08-07): the Recap lane inherits this panel's two
// STORY sections and explicitly leaves the spotlight behind — the Media tab is
// its one home. Every other mount keeps the default.
const HomepagePanel = ({ allActions, users, activeUsers: activeUsersProp, THEME, chatGameStatus, mediaSuggestions, showMediaSpotlight = true }) => {
  const castUsers = activeUsersProp || users; // activeUsers for Cast list, users for name lookups
  const msgTypes = new Set(['message', 'public_message', 'private_message', 'secret_message', 'human_interjection']);
  const messages = allActions.filter(a => msgTypes.has(a.type) && a.data?.message);

  // --- Stats ---
  const roundIds = [...new Set(allActions.map(a => a.game_instance_round_id).filter(Boolean))];
  const totalChapters = roundIds.length;
  const totalMessages = messages.length;

  // --- Player stats ---
  const playerStats = {};
  messages.forEach(a => {
    const pid = a.game_instance_player_id;
    if (!pid) return;
    if (!playerStats[pid]) playerStats[pid] = { count: 0, totalLen: 0, emojis: 0, caps: 0, questions: 0, chars: 0, topEmojis: {} };
    const msg = a.data.message || '';
    playerStats[pid].count++;
    playerStats[pid].totalLen += msg.length;
    playerStats[pid].chars += msg.length;
    const emojis = msg.match(/[\u{1F000}-\u{1FFFF}\u{2600}-\u{27BF}\u{FE00}-\u{FE0F}\u{1F900}-\u{1F9FF}]/gu) || [];
    playerStats[pid].emojis += emojis.length;
    emojis.forEach(e => { playerStats[pid].topEmojis[e] = (playerStats[pid].topEmojis[e] || 0) + 1; });
    const capsChars = (msg.match(/[A-Z]/g) || []).length;
    const totalAlpha = (msg.match(/[a-zA-Z]/g) || []).length;
    if (totalAlpha > 5) playerStats[pid].caps += capsChars / totalAlpha;
    if (msg.includes('?')) playerStats[pid].questions++;
  });

  // Compute vibe for each player
  const getVibe = (pid) => {
    const s = playerStats[pid];
    if (!s || s.count === 0) return { label: 'Quiet Observer', emoji: '🫥' };
    const avgLen = s.totalLen / s.count;
    const emojiRate = s.emojis / s.count;
    const capsRate = s.caps / s.count;
    const questionRate = s.questions / s.count;
    if (avgLen > 300) return { label: 'Storyteller', emoji: '📖' };
    if (emojiRate > 0.3) return { label: 'Hype Machine', emoji: '🔥' };
    if (capsRate > 0.15) return { label: 'Chaotic Energy', emoji: '🌪️' };
    if (questionRate > 0.2) return { label: 'Philosopher', emoji: '🤔' };
    if (avgLen < 80) return { label: 'Short & Sweet', emoji: '✂️' };
    return { label: 'Smooth Operator', emoji: '😎' };
  };

  // Featured quote + energy pill moved OUT of this panel (design A, Nina
  // 08-01): the quote is the feed epigraph above the Story-so-far drawer,
  // the energy pill lives in the masthead identity row. Same scoring recipe
  // both places — change them together.

  // --- Latest Activity ---
  const latestActivity = allActions
    .filter(a => ['public_message', 'human_interjection', 'game_announcement'].includes(a.type) && a.data?.message)
    .slice(-5)
    .reverse();

  // --- Media spotlight ---
  const topYoutube = (mediaSuggestions?.youtube || []).slice(0, 3);

  // --- Previously on… — latest chapter recap (chapter_title + host_intro from round_analysis) ---
  const analysesByTime = allActions
    .filter(a => a.type === 'round_analysis' && a.data)
    .sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
  const latestAnalysis = analysesByTime[analysesByTime.length - 1] || null;
  const prevOn = latestAnalysis ? {
    num: analysesByTime.length,
    title: latestAnalysis.data.chapter_title || null,
    intro: latestAnalysis.data.host_intro || null,
    teaHeadline: (Array.isArray(latestAnalysis.data.relationships)
      ? latestAnalysis.data.relationships.find(r => r && r.headline)
      : null)?.headline || null,
  } : null;

  const spaceName = chatGameStatus?.name || chatGameStatus?.scene || 'This Space';
  const accent = THEME.accent || '#7b1fa2';
  const glass = { backgroundColor: 'rgba(255,255,255,0.03)', backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)', border: `1px solid rgba(255,255,255,0.06)` };

  // 🫥 Self-hide (08-07): all three sections are conditional, so the panel
  // could return a bare wrapper carrying marginBottom 20 + a <style> block —
  // a phantom gap. Newly reachable now that the Recap mount passes
  // showMediaSpotlight={false} AND Previously-On is structurally dead in chat
  // spaces (nothing writes round_analysis there), which leaves Latest Activity
  // as the panel's only possible content.
  const hasPrevOn = !!(prevOn && (prevOn.title || prevOn.intro));
  const hasSpotlight = showMediaSpotlight && topYoutube.length > 0;
  if (!hasPrevOn && !hasSpotlight && latestActivity.length === 0) return null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginBottom: '20px' }}>
      <style>{`
        @keyframes homePulse { 0%, 100% { opacity: 0.6; } 50% { opacity: 1; } }
        @keyframes homeShimmer { 0% { background-position: -200% center; } 100% { background-position: 200% center; } }
        @keyframes homeFadeIn { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: translateY(0); } }
      `}</style>

      {/* Hero card REMOVED (design A, Nina 08-01): quote/name/tagline/cast/
          energy were the third copy of identity on one screen. They live in
          the masthead (tagline + cast row + activity pill) and the feed
          epigraph now; this panel is pure story content. */}

      {/* 📺 Previously on… — the story so far, in the slot the host card vacated */}
      {prevOn && (prevOn.title || prevOn.intro) && (
        <div style={{
          padding: '16px 18px', borderRadius: '14px',
          ...glass,
          borderLeft: `3px solid ${accent}`,
          animation: 'homeFadeIn 0.55s ease-out',
        }}>
          <div style={{ fontSize: '0.65em', fontWeight: 800, color: accent, textTransform: 'uppercase', letterSpacing: '1.5px', marginBottom: '6px' }}>
            📺 Previously on {spaceName}
          </div>
          {prevOn.title && (
            <div style={{ fontSize: '0.95em', fontWeight: 700, fontStyle: 'italic', color: THEME.textOnPanel || '#fff', marginBottom: prevOn.intro ? '6px' : 0 }}>
              Ch. {prevOn.num}: {prevOn.title}
            </div>
          )}
          {prevOn.intro && (
            <div style={{ fontSize: '0.78em', color: THEME.textMuted || '#999', lineHeight: 1.55 }}>
              {prevOn.intro}
            </div>
          )}
          {prevOn.teaHeadline && (
            <div style={{ fontSize: '0.72em', color: '#ec407a', fontStyle: 'italic', marginTop: '8px' }}>
              🫖 Meanwhile: {prevOn.teaHeadline}
            </div>
          )}
        </div>
      )}

      {/* Your Host card removed — the host's story now lives behind the ⓘ button on the moderator panel */}

      {/* Latest Activity */}
      {latestActivity.length > 0 && (
        <div style={{ animation: 'homeFadeIn 0.9s ease-out' }}>
          <div style={{
            fontSize: '0.7em', fontWeight: 700, color: THEME.textMuted,
            textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '10px', padding: '0 4px',
            display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap',
          }}>
            <span>📋</span> <span style={{ whiteSpace: 'nowrap' }}>Latest Activity</span>
            {totalMessages > 0 && (
              <span style={{ marginLeft: 'auto', fontWeight: 600, textTransform: 'none', letterSpacing: 'normal', whiteSpace: 'nowrap' }}>
                💬 {totalMessages} · 📖 {totalChapters} · 👥 {Object.keys(playerStats).length} locals
              </span>
            )}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            {latestActivity.map((a, i) => {
              const sender = users.find(u => u.id === a.game_instance_player_id || u.game_player_id === a.game_instance_player_id);
              const name = a.type === 'game_announcement' ? '📢' : (sender?.name || 'Unknown');
              const img = sender?.profile_image_url || sender?.data?.profile_image_url;
              const msg = a.data?.message || '';
              return (
                <div key={i} style={{
                  padding: '10px 12px', borderRadius: '10px',
                  ...glass,
                  display: 'flex', gap: '10px', alignItems: 'center',
                }}>
                  {a.type !== 'game_announcement' && (
                    <div style={{
                      width: 26, height: 26, borderRadius: '50%', flexShrink: 0, overflow: 'hidden',
                      backgroundColor: `${accent}20`, display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: '0.6em', color: '#fff', fontWeight: 700,
                    }}>
                      {img ? <img src={img} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : name.charAt(0)}
                    </div>
                  )}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    {a.type !== 'game_announcement' && (
                      <div style={{ fontSize: '0.68em', fontWeight: 700, color: accent, marginBottom: '2px' }}>{name}</div>
                    )}
                    <div style={{
                      fontSize: '0.7em', color: a.type === 'game_announcement' ? THEME.textMuted : 'rgba(255,255,255,0.7)', lineHeight: 1.4,
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}>{a.type === 'game_announcement' ? `📢 ${msg}` : msg}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Media Spotlight */}
      {showMediaSpotlight && topYoutube.length > 0 && (
        <div style={{ animation: 'homeFadeIn 1s ease-out' }}>
          <div style={{
            fontSize: '0.7em', fontWeight: 700, color: THEME.textMuted,
            textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '10px', padding: '0 4px',
            display: 'flex', alignItems: 'center', gap: '6px',
          }}>
            <span>🎬</span> Media Spotlight
          </div>
          <div style={{ display: 'flex', gap: '10px', overflowX: 'auto', paddingBottom: '4px', scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
            {topYoutube.map((v, i) => (
              <a key={i} href={v.url} target="_blank" rel="noopener noreferrer" style={{
                flexShrink: 0, width: 180, borderRadius: '12px', overflow: 'hidden',
                textDecoration: 'none', color: 'inherit',
                ...glass,
                transition: 'transform 0.2s',
              }}>
                <div style={{ position: 'relative', width: '100%', paddingTop: '56.25%', backgroundColor: 'rgba(0,0,0,0.3)' }}>
                  {v.thumbnail && <img src={v.thumbnail} alt="" style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'cover' }} />}
                  <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <div style={{
                      width: 36, height: 36, borderRadius: '50%', backgroundColor: 'rgba(0,0,0,0.6)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.9em',
                    }}>▶</div>
                  </div>
                </div>
                <div style={{
                  padding: '10px 12px', fontSize: '0.7em', fontWeight: 600,
                  color: THEME.textOnPanel || '#fff', lineHeight: 1.3,
                  display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
                }}>{v.title}</div>
              </a>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// SuggestedMediaPanel — Netflix-style media browse with carousels, hero card, and rich content
const SuggestedMediaPanelInner = ({ mediaSuggestions, pinnedPick, THEME }) => {
  const hasContent = mediaSuggestions && (
    mediaSuggestions.youtube?.length || mediaSuggestions.reddit?.length ||
    mediaSuggestions.tiktok?.length || mediaSuggestions.pinterest?.length || mediaSuggestions.blogs?.length ||
    mediaSuggestions.articles?.length
  );

  const formatNumber = (n) => {
    if (!n) return '';
    if (n >= 1000000) return (n / 1000000).toFixed(1) + 'M';
    if (n >= 1000) return (n / 1000).toFixed(1) + 'K';
    return n.toString();
  };

  // --- Skeleton Loading State ---
  if (!hasContent && !pinnedPick) {
    const SkeletonCard = ({ wide }) => (
      <div style={{
        flexShrink: 0, width: wide ? 320 : 220, borderRadius: '12px', overflow: 'hidden',
        backgroundColor: THEME.bgLight || THEME.bgMedium,
        border: `1px solid ${THEME.borderLight || '#333'}15`,
      }}>
        <div style={{
          width: '100%', paddingTop: wide ? '56.25%' : '50%',
          background: `linear-gradient(90deg, ${THEME.bgMedium || '#222'} 25%, ${THEME.bgLight || '#333'} 50%, ${THEME.bgMedium || '#222'} 75%)`,
          backgroundSize: '200% 100%',
          animation: 'shimmer 1.5s ease-in-out infinite',
        }} />
        <div style={{ padding: '12px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <div style={{ height: 10, width: '70%', borderRadius: 4, backgroundColor: `${THEME.textMuted || '#666'}20` }} />
          <div style={{ height: 10, width: '90%', borderRadius: 4, backgroundColor: `${THEME.textMuted || '#666'}15` }} />
          <div style={{ height: 8, width: '50%', borderRadius: 4, backgroundColor: `${THEME.textMuted || '#666'}10` }} />
        </div>
      </div>
    );

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '24px', marginBottom: '20px' }}>
        <style>{`@keyframes shimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }
.media-carousel-scroll::-webkit-scrollbar { display: none; }`}</style>
        {/* Skeleton hero */}
        <div style={{
          width: '100%', borderRadius: '16px', overflow: 'hidden',
          backgroundColor: THEME.bgLight || THEME.bgMedium,
          border: `1px solid ${THEME.borderLight || '#333'}15`,
        }}>
          <div style={{
            width: '100%', paddingTop: '40%',
            backgroundImage: `linear-gradient(90deg, ${THEME.bgMedium || '#222'} 25%, ${THEME.bgLight || '#333'} 50%, ${THEME.bgMedium || '#222'} 75%)`,
            backgroundSize: '200% 100%', animation: 'shimmer 1.5s ease-in-out infinite',
          }} />
          <div style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <div style={{ height: 14, width: '60%', borderRadius: 4, backgroundColor: `${THEME.textMuted || '#666'}20` }} />
            <div style={{ height: 10, width: '40%', borderRadius: 4, backgroundColor: `${THEME.textMuted || '#666'}15` }} />
          </div>
        </div>
        {/* Skeleton carousel rows */}
        {[1, 2].map(i => (
          <div key={i}>
            <div style={{ height: 12, width: 80, borderRadius: 4, backgroundColor: `${THEME.textMuted || '#666'}20`, marginBottom: 12 }} />
            <div style={{ display: 'flex', gap: '12px', overflow: 'hidden' }}>
              {[1, 2, 3, 4].map(j => <SkeletonCard key={j} wide={i === 1} />)}
            </div>
          </div>
        ))}
      </div>
    );
  }

  const badgeStyles = {
    youtube: { bg: '#FF0000', label: 'YouTube', icon: '▶️' },
    reddit: { bg: '#FF4500', label: 'Reddit', icon: '🔥' },
    tiktok: { bg: '#000000', label: 'TikTok', icon: '🎵' },
    pinterest: { bg: '#E60023', label: 'Pinterest', icon: '📌' },
    blog: { bg: '#34A853', label: 'Article', icon: '📝' }
  };

  // --- Pinned Pick — giant tilted polaroid, the host's one captioned pick ---
  const PinnedPolaroid = ({ pick }) => {
    if (!pick || !pick.url) return null;
    const typeEmoji = { youtube: '▶️', reddit: '💬', article: '📝', image: '🖼️', gif: '🎞️' }[pick.type] || '✨';
    const isEmbed = pick.type === 'youtube' && pick.videoId;
    const directImg = (pick.type === 'image' || pick.type === 'gif') ? pick.url : pick.thumbnail;
    const mediaInner = isEmbed ? (
      <iframe
        src={`https://www.youtube.com/embed/${pick.videoId}`}
        title={pick.title || 'Pinned pick'}
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
        style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 0 }}
      />
    ) : directImg ? (
      <img src={directImg} alt={pick.title || ''} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}
        onError={(e) => { e.target.style.display = 'none'; }} />
    ) : (
      <div style={{
        position: 'absolute', inset: 0,
        background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '10px', padding: '16px',
      }}>
        <div style={{ fontSize: '2.4em' }}>{typeEmoji}</div>
        <div style={{ fontSize: '0.9em', fontWeight: 700, color: '#fff', textAlign: 'center', lineHeight: 1.35 }}>
          {(pick.title || '').length > 90 ? pick.title.slice(0, 90) + '…' : pick.title}
        </div>
      </div>
    );
    return (
      <div style={{ padding: '16px 4px 8px' }}>
        <div style={{
          transform: 'rotate(-1.5deg)', position: 'relative',
          // 🗒️ PAPER RESETS THE HALO (09-19): the chat column's inherited dark
          // text-shadow is a smear under dark ink on a light ground
          // (roomHaloPaper.test.js). The ink is on a child, so reset it here.
          textShadow: 'none',
          backgroundColor: '#faf7f0', borderRadius: '4px', padding: '12px 12px 0',
          boxShadow: '0 12px 32px rgba(0,0,0,0.38), 0 2px 6px rgba(0,0,0,0.22)',
        }}>
          <div style={{
            position: 'absolute', top: '-15px', left: '50%', transform: 'translateX(-50%) rotate(10deg)',
            fontSize: '1.7em', zIndex: 2, filter: 'drop-shadow(0 3px 3px rgba(0,0,0,0.35))',
          }}>📌</div>
          <div style={{
            position: 'absolute', top: '-10px', right: '12px', zIndex: 2,
            fontSize: '0.62em', fontWeight: 800, letterSpacing: '1px', textTransform: 'uppercase',
            padding: '3px 10px', borderRadius: '10px', transform: 'rotate(1.5deg)',
            backgroundColor: '#ec407a', color: '#fff', boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
            // 🎞️ F1: a ported pick carries pick_label + his face (utils/portedPick)
            ...(pick.pick_label ? { display: 'inline-flex', alignItems: 'center', gap: '5px' } : {}),
          }}>{pick.pick_label ? (
            <>
              {pick.by_image && (
                <img src={pick.by_image} alt="" data-pick-face=""
                  style={{ width: 16, height: 16, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }}
                  onError={(e) => { e.target.style.display = 'none'; }} />
              )}
              {pick.pick_label}
            </>
          ) : <>{pick.host_emoji || '🎙️'} {pick.host_name || 'The Host'}&rsquo;s pick</>}</div>
          <div style={{ position: 'relative', width: '100%', paddingTop: '56.25%', overflow: 'hidden', backgroundColor: '#111' }}>
            {isEmbed ? mediaInner : (
              <a href={pick.url} target="_blank" rel="noopener noreferrer" style={{ position: 'absolute', inset: 0, display: 'block' }}>
                {mediaInner}
              </a>
            )}
          </div>
          {/* White polaroid bar: caption in the host's voice */}
          <div style={{ padding: '12px 6px 14px' }}>
            {pick.caption && (
              <div style={{
                fontStyle: 'italic', color: '#33302b', fontSize: '0.92em', lineHeight: 1.45,
                fontFamily: "'Segoe Print', 'Bradley Hand', 'Comic Sans MS', cursive",
              }}>
                &ldquo;{pick.caption}&rdquo;
              </div>
            )}
            <div style={{
              marginTop: '6px', fontSize: '0.7em', fontWeight: 700, color: '#8a8378',
              display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: '10px',
            }}>
              <span style={{ flexShrink: 0 }}>{pick._resident ? (pick.host_name || '') : `— ${pick.host_name || 'The Host'}`}</span>
              {pick.type === 'youtube' && (
                <button
                  onClick={() => window.dispatchEvent(new CustomEvent('watchparty:request', { detail: { url: pick.url } }))}
                  title="Watch this together with the room"
                  style={{
                    background: '#7b1fa2', border: 'none', color: '#fff',
                    borderRadius: 999, padding: '3px 10px', cursor: 'pointer',
                    fontSize: '0.95em', fontWeight: 700, flexShrink: 0,
                  }}
                >
                  🍿 Watch together
                </button>
              )}
              <a href={pick.url} target="_blank" rel="noopener noreferrer"
                style={{ color: '#8a8378', textDecoration: 'none', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {typeEmoji} {(pick.title || 'open').length > 44 ? pick.title.slice(0, 44) + '…' : (pick.title || 'open')} ↗
              </a>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // --- Hero Card (top YouTube video) ---
  // The pinned pick is deduped out of the hero/carousels — it already owns the top slot
  const ms = mediaSuggestions || {};
  const notPinned = (item) => !pinnedPick || item.url !== pinnedPick.url;
  const ytList = (ms.youtube || []).filter(notPinned);
  const heroVideo = ytList[0];
  const remainingYoutube = ytList.slice(1);

  const HeroCard = ({ item }) => {
    if (!item) return null;
    return (
      <a href={item.url} target="_blank" rel="noopener noreferrer" style={{
        textDecoration: 'none', color: 'inherit', display: 'block',
        borderRadius: '16px', overflow: 'hidden', position: 'relative',
        backgroundColor: '#000', cursor: 'pointer',
        transition: 'transform 0.2s ease, box-shadow 0.2s ease',
      }}
        onMouseEnter={(e) => { e.currentTarget.style.transform = 'scale(1.01)'; e.currentTarget.style.boxShadow = '0 12px 40px rgba(0,0,0,0.3)'; }}
        onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.boxShadow = 'none'; }}
      >
        {/* Thumbnail */}
        <div style={{
          position: 'relative', width: '100%', paddingTop: '45%',
          background: !item.thumbnail ? `linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)` : '#000',
        }}>
          {item.thumbnail ? (
            <img src={item.thumbnail} alt="" style={{
              position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
              objectFit: 'cover',
            }} onError={(e) => { e.target.style.display = 'none'; }} />
          ) : (
            <div style={{
              position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -60%)',
              fontSize: '2.5em', opacity: 0.3,
            }}>🎬</div>
          )}
          {/* Gradient overlay */}
          <div style={{
            position: 'absolute', bottom: 0, left: 0, right: 0, height: '70%',
            background: 'linear-gradient(transparent, rgba(0,0,0,0.85))',
          }} />
          {/* Play button */}
          <div style={{
            position: 'absolute', top: '40%', left: '50%', transform: 'translate(-50%, -50%)',
            width: 64, height: 64, borderRadius: '50%',
            backgroundColor: 'rgba(255,0,0,0.9)', display: 'flex',
            alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 4px 20px rgba(255,0,0,0.4)',
          }}>
            <div style={{
              width: 0, height: 0, borderTop: '14px solid transparent',
              borderBottom: '14px solid transparent', borderLeft: '24px solid #fff',
              marginLeft: '4px',
            }} />
          </div>
          {/* View count badge */}
          {item.viewCount > 0 && (
            <div style={{
              position: 'absolute', bottom: 60, right: 16,
              backgroundColor: 'rgba(0,0,0,0.7)', color: '#fff',
              padding: '4px 10px', borderRadius: '6px',
              fontSize: '0.75em', fontWeight: 600, backdropFilter: 'blur(4px)',
            }}>
              {formatNumber(item.viewCount)} views
            </div>
          )}
          {/* 🍿 Watch together — starts a synced party instead of opening YouTube */}
          <button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              window.dispatchEvent(new CustomEvent('watchparty:request', { detail: { url: item.url } }));
            }}
            title="Watch this together with the room"
            style={{
              position: 'absolute', top: 12, right: 12, zIndex: 2,
              background: 'rgba(123,31,162,0.92)', border: 'none', color: '#fff',
              borderRadius: 999, padding: '6px 12px', cursor: 'pointer',
              fontSize: '0.8em', fontWeight: 700, backdropFilter: 'blur(4px)',
              boxShadow: '0 2px 10px rgba(0,0,0,0.4)',
            }}
          >
            🍿 Watch together
          </button>
          {/* Title + channel overlay */}
          <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, padding: '16px 20px' }}>
            <div style={{
              fontSize: '1.1em', fontWeight: 800, color: '#fff',
              lineHeight: 1.3, marginBottom: '6px',
              textShadow: '0 2px 8px rgba(0,0,0,0.5)',
              display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
            }}>
              {item.title}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{
                fontSize: '0.65em', fontWeight: 700, color: '#fff',
                backgroundColor: '#FF0000', padding: '2px 8px', borderRadius: '4px',
              }}>YOUTUBE</span>
              {item.channel && (
                <span style={{ fontSize: '0.78em', color: 'rgba(255,255,255,0.8)', fontWeight: 500 }}>
                  {item.channel}
                </span>
              )}
              {item.likeCount > 0 && (
                <span style={{ fontSize: '0.75em', color: 'rgba(255,255,255,0.7)' }}>
                  👍 {formatNumber(item.likeCount)}
                </span>
              )}
            </div>
          </div>
        </div>
      </a>
    );
  };

  // --- Carousel Card with 3D Flip (auto-flips in a staggered wave) ---
  const CarouselCard = ({ item, wide, index = 0, fluid = false }) => {
    const [flipped, setFlipped] = React.useState(false);
    const [hovered, setHovered] = React.useState(false);
    const badge = badgeStyles[item.type] || { bg: THEME.accent, label: 'Link', icon: '🔗' };
    const hasThumbnail = item.thumbnail;
    const cardWidth = fluid ? '100%' : (wide ? 300 : 240); // fluid = mosaic grid cell
    const cardHeight = wide ? 290 : 270;

    // Auto-flip on a staggered timer — each card flips after the previous
    React.useEffect(() => {
      const flipDelay = 2000 + (index * 1500); // stagger start
      const flipDuration = 3000; // stay flipped for 3s
      const cycleTime = 8000; // full cycle length
      const timers = [];

      timers.push(setTimeout(() => {
        setFlipped(true);
        timers.push(setTimeout(() => setFlipped(false), flipDuration));

        const interval = setInterval(() => {
          setFlipped(true);
          timers.push(setTimeout(() => setFlipped(false), flipDuration));
        }, cycleTime);
        timers.push(interval);
      }, flipDelay));

      return () => timers.forEach(t => { clearTimeout(t); clearInterval(t); });
    }, [index]);

    // Hover overrides auto-flip
    const isFlipped = hovered || flipped;

    return (
      <div
        style={{
          perspective: '800px', flexShrink: 0, width: cardWidth, height: cardHeight, cursor: 'pointer',
        }}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      >
        <div style={{
          position: 'relative', width: '100%', height: '100%',
          transition: 'transform 0.5s ease',
          transformStyle: 'preserve-3d',
          transform: isFlipped ? 'rotateY(180deg)' : 'rotateY(0deg)',
        }}>
          {/* === FRONT FACE === */}
          <a href={item.url} target="_blank" rel="noopener noreferrer" style={{
            position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
            backfaceVisibility: 'hidden', WebkitBackfaceVisibility: 'hidden',
            textDecoration: 'none', color: 'inherit',
            display: 'flex', flexDirection: 'column',
            borderRadius: '12px', overflow: 'hidden',
            backgroundColor: THEME.bgLight || THEME.bgMedium,
            border: `1px solid ${THEME.borderLight || '#333'}20`,
          }}>
            {hasThumbnail ? (
              <div style={{ position: 'relative', width: '100%', flex: '1 1 60%', backgroundColor: '#111' }}>
                <img src={item.thumbnail} alt="" style={{
                  position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'cover',
                }} onError={(e) => { e.target.style.display = 'none'; }} />
                {item.type === 'youtube' && (
                  <div style={{
                    position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
                    width: 40, height: 40, borderRadius: '50%',
                    backgroundColor: 'rgba(255,0,0,0.85)', display: 'flex',
                    alignItems: 'center', justifyContent: 'center',
                  }}>
                    <div style={{ width: 0, height: 0, borderTop: '8px solid transparent', borderBottom: '8px solid transparent', borderLeft: '14px solid #fff', marginLeft: '2px' }} />
                  </div>
                )}
                {item.viewCount > 0 && (
                  <div style={{
                    position: 'absolute', bottom: 6, right: 6,
                    backgroundColor: 'rgba(0,0,0,0.75)', color: '#fff',
                    padding: '2px 6px', borderRadius: '4px', fontSize: '0.65em', fontWeight: 600,
                  }}>
                    {formatNumber(item.viewCount)} views
                  </div>
                )}
              </div>
            ) : (
              <div style={{
                flex: '1 1 60%', position: 'relative',
                background: `linear-gradient(135deg, ${badge.bg}30, ${badge.bg}10)`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <span style={{ fontSize: '2.5em' }}>{badge.icon}</span>
              </div>
            )}
            <div style={{ padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <span style={{
                  fontSize: '0.58em', fontWeight: 700, color: '#fff',
                  backgroundColor: badge.bg, padding: '2px 6px', borderRadius: '4px',
                  textTransform: 'uppercase', letterSpacing: '0.5px',
                }}>{badge.label}</span>
                {item.channel && <span style={{ fontSize: '0.68em', color: THEME.textMuted, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.channel}</span>}
                {item.subreddit && <span style={{ fontSize: '0.62em', color: THEME.textMuted, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.subreddit}</span>}
                {item.score > 0 && !item.viewCount && <span style={{ fontSize: '0.62em', color: THEME.textMuted, fontWeight: 600 }}>⬆{formatNumber(item.score)}</span>}
              </div>
              <div style={{
                fontSize: '0.78em', fontWeight: 700, color: THEME.textOnPanel || THEME.textDark,
                lineHeight: 1.3, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
              }}>{item.title}</div>
            </div>
          </a>

          {/* === BACK FACE === */}
          <a href={item.url} target="_blank" rel="noopener noreferrer" style={{
            position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
            boxSizing: 'border-box',
            backfaceVisibility: 'hidden', WebkitBackfaceVisibility: 'hidden',
            transform: 'rotateY(180deg)',
            textDecoration: 'none', color: 'inherit',
            display: 'flex', flexDirection: 'column',
            borderRadius: '12px',
            background: `linear-gradient(145deg, ${badge.bg}18, ${THEME.bgLight || '#1a1a2e'} 40%, ${badge.bg}08)`,
            border: `1px solid ${badge.bg}40`,
            padding: '12px 14px 10px',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px', flexShrink: 0 }}>
              <span style={{ fontSize: '1.1em' }}>{badge.icon}</span>
              <span style={{
                fontSize: '0.65em', fontWeight: 700, color: badge.bg,
                textTransform: 'uppercase', letterSpacing: '1px',
              }}>{badge.label}</span>
            </div>
            <div style={{
              fontSize: '0.76em', fontWeight: 700, color: THEME.textOnPanel || THEME.textDark,
              lineHeight: 1.3, marginBottom: '4px', flexShrink: 0,
              display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
            }}>{item.title}</div>
            <div style={{ flex: '1 1 auto', minHeight: 0, overflow: 'hidden' }}>
              {item.description && (
                <div style={{
                  fontSize: '0.65em', color: THEME.textMuted, lineHeight: 1.3,
                  display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
                }}>{item.description}</div>
              )}
              {(item.viewCount > 0 || item.likeCount > 0 || item.score > 0) && (
                <div style={{ display: 'flex', gap: '8px', marginTop: '4px', flexWrap: 'wrap' }}>
                  {item.viewCount > 0 && <span style={{ fontSize: '0.62em', color: THEME.textMuted, fontWeight: 600 }}>👁 {formatNumber(item.viewCount)}</span>}
                  {item.likeCount > 0 && <span style={{ fontSize: '0.62em', color: THEME.textMuted, fontWeight: 600 }}>👍 {formatNumber(item.likeCount)}</span>}
                  {item.score > 0 && <span style={{ fontSize: '0.62em', color: THEME.textMuted, fontWeight: 600 }}>⬆ {formatNumber(item.score)}</span>}
                  {item.numComments > 0 && <span style={{ fontSize: '0.62em', color: THEME.textMuted, fontWeight: 600 }}>💬 {formatNumber(item.numComments)}</span>}
                </div>
              )}
            </div>
            <div style={{
              flexShrink: 0,
              alignSelf: 'flex-start',
              padding: '5px 14px', borderRadius: '20px',
              backgroundColor: badge.bg, color: '#fff',
              fontSize: '0.66em', fontWeight: 700,
            }}>
              Open {badge.label} →
            </div>
          </a>
        </div>
      </div>
    );
  };

  // --- Compact Article Row ---
  const ArticleRow = ({ item }) => {
    const sourceColors = {
      'Wikipedia': '#636466', 'BBC': '#BB1919', 'The Guardian': '#052962',
      'Wired': '#000', 'The Atlantic': '#B22222', 'Vox': '#FFF200',
    };
    const bgColor = sourceColors[item.source] || '#555';

    return (
      <a href={item.url} target="_blank" rel="noopener noreferrer" style={{
        textDecoration: 'none', color: 'inherit', display: 'flex', alignItems: 'center', gap: '12px',
        padding: '10px 14px', borderRadius: '10px',
        backgroundColor: THEME.bgLight || THEME.bgMedium,
        border: `1px solid ${THEME.borderLight || '#333'}20`,
        cursor: 'pointer', transition: 'background-color 0.15s ease',
      }}
        onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = `${THEME.accent || '#7b1fa2'}10`; }}
        onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = THEME.bgLight || THEME.bgMedium; }}
      >
        {/* Source icon */}
        <div style={{
          width: 36, height: 36, borderRadius: '8px', flexShrink: 0,
          backgroundColor: bgColor, display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: '1em', color: '#fff', fontWeight: 800,
        }}>
          {item.emoji || item.source?.charAt(0) || '📝'}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            fontSize: '0.82em', fontWeight: 700, color: THEME.textOnPanel || THEME.textDark,
            lineHeight: 1.3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>{item.title}</div>
          <div style={{ fontSize: '0.7em', color: THEME.textMuted, marginTop: '2px' }}>
            {item.source}{item.description ? ` · ${item.description}` : ''}
          </div>
        </div>
        <span style={{ fontSize: '0.85em', color: THEME.textMuted, flexShrink: 0 }}>→</span>
      </a>
    );
  };

  // --- Horizontal Scroll Carousel with Arrow Buttons ---
  const Carousel = ({ items, wide, renderItem }) => {
    const scrollRef = React.useRef(null);
    const [showLeft, setShowLeft] = React.useState(false);
    const [showRight, setShowRight] = React.useState(false);

    const updateArrows = React.useCallback(() => {
      const el = scrollRef.current;
      if (!el) return;
      setShowLeft(el.scrollLeft > 10);
      setShowRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 10);
    }, []);

    React.useEffect(() => {
      updateArrows();
      const el = scrollRef.current;
      if (!el) return;
      el.addEventListener('scroll', updateArrows, { passive: true });
      // Width changes with no scroll event (stage dock/undock, sidebar
      // resize, layout-boundary crossings) left the arrows stale — a
      // phantom › lingered after everything already fit.
      const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(updateArrows) : null;
      ro?.observe(el);
      return () => { el.removeEventListener('scroll', updateArrows); ro?.disconnect(); };
    }, [items, updateArrows]);

    const scroll = (direction) => {
      const el = scrollRef.current;
      if (!el) return;
      const scrollAmount = wide ? 340 : 240;
      el.scrollBy({ left: direction * scrollAmount, behavior: 'smooth' });
    };

    const ArrowBtn = ({ direction, show }) => (
      <div
        onClick={() => scroll(direction)}
        style={{
          position: 'absolute', top: '50%', transform: 'translateY(-50%)',
          [direction === -1 ? 'left' : 'right']: '-2px',
          width: 32, height: 32, borderRadius: '50%', cursor: 'pointer',
          backgroundColor: THEME.bgLight || '#222', border: `1px solid ${THEME.borderLight || '#444'}`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          boxShadow: '0 2px 8px rgba(0,0,0,0.3)', zIndex: 2,
          opacity: show ? 1 : 0, pointerEvents: show ? 'auto' : 'none',
          transition: 'opacity 0.2s ease',
        }}
      >
        <span style={{ fontSize: '0.9em', color: THEME.textOnPanel || '#fff', lineHeight: 1 }}>
          {direction === -1 ? '‹' : '›'}
        </span>
      </div>
    );

    return (
      <div style={{ position: 'relative' }}>
        <ArrowBtn direction={-1} show={showLeft} />
        <div
          ref={scrollRef}
          className="media-carousel-scroll"
          style={{
            display: 'flex', gap: '12px', overflowX: 'auto', overflowY: 'hidden',
            scrollSnapType: 'x mandatory', WebkitOverflowScrolling: 'touch',
            paddingBottom: '8px', marginLeft: '-4px', paddingLeft: '4px', marginRight: '-4px', paddingRight: '4px',
            scrollbarWidth: 'none', msOverflowStyle: 'none',
          }}
        >
          {items.map((item, i) => (
            <div key={i} style={{ scrollSnapAlign: 'start' }}>
              {renderItem ? renderItem(item) : <CarouselCard item={item} wide={wide} index={i} />}
            </div>
          ))}
        </div>
        <ArrowBtn direction={1} show={showRight} />
      </div>
    );
  };

  // Build sections — only show platforms with real fetched content (YouTube, Reddit, Articles)
  const redditList = (ms.reddit || []).filter(notPinned);
  const articleList = (ms.articles || []).filter(notPinned);
  const sections = [
    remainingYoutube.length > 0 && { key: 'youtube', label: '▶️ More Videos', items: remainingYoutube, wide: true, mosaic: true },
    redditList.length > 0 && { key: 'reddit', label: '🔥 Reddit', items: redditList },
    articleList.length > 0 && { key: 'articles', label: '📝 Articles', items: articleList, renderAsRows: true },
  ].filter(Boolean);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px', marginBottom: '20px' }}>
      <style>{`.media-carousel-scroll::-webkit-scrollbar { display: none; }`}</style>
      {/* Pinned Pick polaroid — owns the top slot; the hero demotes below it */}
      {pinnedPick && <PinnedPolaroid pick={pinnedPick} />}
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '0 4px' }}>
        <span style={{ fontSize: '1.2em' }}>✨</span>
        <span style={{
          fontSize: '0.95em', fontWeight: 800,
          color: THEME.textOnPanel || THEME.textDark,
        }}>
          Explore
        </span>
        <span style={{ fontSize: '0.75em', color: THEME.textMuted, fontWeight: 500 }}>
          Curated content for this space
        </span>
      </div>

      {/* Hero Card */}
      {heroVideo && <HeroCard item={heroVideo} />}

      {/* Carousel Sections + Article Rows */}
      {sections.map((section) => (
        <div key={section.key}>
          <div style={{
            fontSize: '0.8em', fontWeight: 700, color: THEME.textMuted,
            marginBottom: '10px', padding: '0 4px',
            textTransform: 'uppercase', letterSpacing: '0.8px',
          }}>
            {section.label}
          </div>
          {section.mosaic ? (
            /* Mosaic grid — every 3rd tile spans the full width, breaking the uniform-row rhythm */
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
              {section.items.slice(0, 9).map((item, i) => (
                <div key={i} style={{ gridColumn: i % 3 === 0 ? 'span 2' : 'span 1' }}>
                  <CarouselCard item={item} wide={i % 3 === 0} fluid index={i} />
                </div>
              ))}
            </div>
          ) : section.renderAsRows ? (
            /* Scrapbook rows — alternating tilt + offset so the list reads like clippings */
            <div style={{ display: 'flex', flexDirection: 'column', gap: '9px' }}>
              {section.items.map((item, i) => (
                <div key={i} style={{
                  transform: `rotate(${i % 2 === 0 ? -0.5 : 0.5}deg)`,
                  marginLeft: i % 2 === 0 ? 0 : '14px',
                  marginRight: i % 2 === 0 ? '14px' : 0,
                }}>
                  <ArticleRow item={item} />
                </div>
              ))}
            </div>
          ) : (
            <Carousel items={section.items} wide={section.wide} />
          )}
        </div>
      ))}
    </div>
  );
};
const SuggestedMediaPanel = React.memo(SuggestedMediaPanelInner, (prev, next) => prev.mediaSuggestions === next.mediaSuggestions && prev.pinnedPick === next.pinnedPick && prev.THEME?.primary === next.THEME?.primary);

// Ship status → look & feel. Keys are lowercased status strings from the
// round_analysis "relationships" block (see AnalysisService round prompt).
const SHIP_STATUS_META = {
  'strangers': { emoji: '🌫️', color: '#9e9e9e' },
  'warming up': { emoji: '🌤️', color: '#64b5f6' },
  'besties': { emoji: '✨', color: '#4fc3f7' },
  'ride or dies': { emoji: '🛡️', color: '#26a69a' },
  "will they won't they": { emoji: '💞', color: '#ec407a' },
  'slow burn': { emoji: '🕯️', color: '#ff8a65' },
  'situationship': { emoji: '🌀', color: '#ab47bc' },
  "it's complicated": { emoji: '🌪️', color: '#7e57c2' },
  'frenemies': { emoji: '🎭', color: '#ffa726' },
  'rivals': { emoji: '⚔️', color: '#ef5350' },
  'sworn enemies': { emoji: '💀', color: '#d32f2f' },
  'icy': { emoji: '❄️', color: '#90a4ae' },
};

// ShipConstellation — night-sky map of the ships. EVERY cast member is a star from
// message one, so the map has bones before any relationship data exists and visibly
// fills in as the space grows. LINKED portraits get the whole jittered ellipse
// (greedy adjacency ordering → fewer crossings); shipless cast sits on a small dim
// bench row below the web (09-01 rework: a dense clique + 7 singletons made the old
// one-ring layout an unreadable pileup). Edges are status-colored quadratic curves
// that draw themselves in, opacity = closeness, hot-red glow = tension, emoji chip
// along the curve (hash-varied t + a deterministic push-apart pass so a clique's
// chips never stack). Names point outward from the ring. Deterministic layout, no
// physics — panel is ~360px wide.
const ShipConstellation = ({ ships, cast, getStatusMeta, THEME, onSelectCast, onSelectShip }) => {
  const [focusStatus, setFocusStatus] = React.useState(null); // legend tap → spotlight one status
  const [showHelp, setShowHelp] = React.useState(false); // ⓘ → how-to-read popover
  const norm = (n) => (n || '').toLowerCase().trim();
  const hash = (s) => { let h = 0; for (let i = 0; i < s.length; i++) h = ((h << 5) - h + s.charCodeAt(i)) | 0; return Math.abs(h); };

  // Linked nodes first (greedy adjacency order); ring color = first encounter =
  // spiciest ship (ships arrive drama-sorted). Then every remaining cast member,
  // chattiest first, so the map is populated before any ship exists.
  const order = [];
  const nodeInfo = {};
  ships.forEach(s => {
    const a = norm(s.nameA), b = norm(s.nameB);
    if (!nodeInfo[a]) nodeInfo[a] = { key: a, name: s.nameA, img: s.imgA, ringColor: getStatusMeta(s.latest.status).color, linked: true };
    if (!nodeInfo[b]) nodeInfo[b] = { key: b, name: s.nameB, img: s.imgB, ringColor: getStatusMeta(s.latest.status).color, linked: true };
    const ia = order.indexOf(a), ib = order.indexOf(b);
    if (ia === -1 && ib === -1) { order.push(a, b); }
    else if (ia === -1) order.splice(ib + 1, 0, a);
    else if (ib === -1) order.splice(ia + 1, 0, b);
  });
  const castByKey = {};
  (cast || []).forEach(c => { castByKey[norm(c.name)] = c; });
  [...(cast || [])].sort((a, b) => (b.count || 0) - (a.count || 0)).forEach(c => {
    const k = norm(c.name);
    if (!k || nodeInfo[k]) return;
    nodeInfo[k] = { key: k, name: c.name, img: c.profileImage, ringColor: 'rgba(255,255,255,0.35)', linked: false };
    order.push(k);
  });

  // 🗺️ Legibility rework (Nina 09-01, screenshot: the house clique crammed
  // into one arc — overlapping avatars, 8 chips piled on the same pixels,
  // names buried — while 7 shipless singletons held the prime real estate).
  // LINKED cast now gets the whole ellipse; shipless cast sits on a small
  // dim BENCH row under the web ("edge of the party" made literal). With no
  // ships yet everyone stays on the ellipse so the map still has bones.
  const linkedKeys = order.filter(k => nodeInfo[k].linked);
  const benchKeys = linkedKeys.length >= 2 ? order.filter(k => !nodeInfo[k].linked) : [];
  const ringKeys = benchKeys.length ? linkedKeys : order;
  const nR = ringKeys.length;
  const benchPerRow = 8;
  const benchRows = Math.ceil(benchKeys.length / benchPerRow);
  // Adaptive canvas — a 2-person web in a 300-tall box reads as an empty ocean
  // (user screenshot 07-10). Small casts get a short, wide stage; nodes start
  // from 9 o'clock so a pair faces off side-by-side instead of totem-poling.
  const W = 360, ringH = nR <= 3 ? 185 : nR <= 6 ? 245 : 300;
  const H = ringH + (benchRows ? 14 + benchRows * 46 : 0);
  const cx = W / 2, cy = ringH / 2 - 4;
  const startAngle = nR <= 3 ? Math.PI : -Math.PI / 2;
  // Jitter shrinks as the ring fills — at 12 nodes the old ±0.22rad ate
  // whole gaps and neighbors sat on each other.
  const jitterAmt = nR <= 3 ? 0.05 : Math.min(0.22, (Math.PI * 2 / Math.max(nR, 1)) * 0.25);
  const rx = nR <= 3 ? 104 : 126;
  const ry = Math.max(46, cy - 44);
  const maxChat = Math.max(...(cast || []).map(c => c.count || 0), 1);
  const nodes = ringKeys.map((k, i) => {
    // Jittered ellipse: hash-based angle/radius wobble so it reads as a constellation,
    // not a clock face — but stays deterministic across re-renders.
    const angle = startAngle + (i / nR) * Math.PI * 2 + (((hash(k) % 100) / 100) - 0.5) * jitterAmt;
    const rScale = 1 - ((hash(k + 'r') % 100) / 100) * 0.1;
    const stats = castByKey[k];
    // Chattier characters get bigger stars — sqrt so quiet ones stay visible;
    // small casts get big portraits (they have the room)
    const r = (nR <= 4 ? 21 : 12) + Math.round(6 * Math.sqrt((stats?.count || 0) / maxChat));
    const x = cx + Math.cos(angle) * rx * rScale;
    const y = cy + Math.sin(angle) * ry * rScale;
    // Names point OUTWARD from the ring so they never sit under the web
    const co = Math.cos(angle), si = Math.sin(angle);
    return {
      ...nodeInfo[k],
      img: nodeInfo[k].img || stats?.profileImage,
      pid: stats?.pid || null,
      r, x, y,
      lx: x + co * (r + 6),
      ly: y + si * (r + 6) + (si > 0.35 ? 10 : si < -0.35 ? -4 : 4),
      anchor: co > 0.4 ? 'start' : co < -0.4 ? 'end' : 'middle',
      fs: nR <= 6 ? 11.5 : 10.5,
      maxName: nR <= 4 ? 20 : 14,
    };
  });
  benchKeys.forEach((k, i) => {
    const row = Math.floor(i / benchPerRow);
    const inRow = Math.min(benchPerRow, benchKeys.length - row * benchPerRow);
    const col = i % benchPerRow;
    const stats = castByKey[k];
    const x = cx + (col - (inRow - 1) / 2) * 42;
    const y = ringH + 14 + row * 46;
    nodes.push({
      ...nodeInfo[k],
      img: nodeInfo[k].img || stats?.profileImage,
      pid: stats?.pid || null,
      r: 10, x, y, bench: true,
      lx: x, ly: y + 21, anchor: 'middle', fs: 9, maxName: 9,
    });
  });
  const nodeByKey = {};
  nodes.forEach(nd => { nodeByKey[nd.key] = nd; });

  const edges = ships.map(s => {
    const a = nodeByKey[norm(s.nameA)], b = nodeByKey[norm(s.nameB)];
    if (!a || !b) return null;
    const meta = getStatusMeta(s.latest.status);
    const statusKey = norm(s.latest.status);
    // Bow toward center + a hash-based perpendicular wobble so co-located chips separate
    const mx = (a.x + b.x) / 2, my = (a.y + b.y) / 2;
    const dx = b.x - a.x, dy = b.y - a.y, len = Math.hypot(dx, dy) || 1;
    const wob = ((hash(s.key) % 21) - 10) * 0.9;
    const qx = mx + (cx - mx) * 0.3 + (-dy / len) * wob;
    const qy = my + (cy - my) * 0.3 + (dx / len) * wob;
    const ct = 0.5 + (((hash(s.key + 't') % 100) / 100) - 0.5) * 0.3;
    return {
      ship: s, meta, statusKey,
      d: `M ${a.x.toFixed(1)} ${a.y.toFixed(1)} Q ${qx.toFixed(1)} ${qy.toFixed(1)} ${b.x.toFixed(1)} ${b.y.toFixed(1)}`,
      // Chip sits at a hash-varied point ALONG the curve (t 0.35-0.65) — at a
      // fixed t=0.5 a clique's chips all landed on the same few pixels; the
      // declutter pass below separates the stragglers.
      chipX: (1 - ct) * (1 - ct) * a.x + 2 * (1 - ct) * ct * qx + ct * ct * b.x,
      chipY: (1 - ct) * (1 - ct) * a.y + 2 * (1 - ct) * ct * qy + ct * ct * b.y,
      dashed: statusKey === 'strangers' || statusKey === 'icy',
      // Option A (Nina 08-03 pick): every line is a HAIRLINE — affinity moved
      // out of stroke width (the old 1.2-4.5px + 8.5px glow read as rubber
      // bands) into opacity (faint = new, bright = close) and the chip size.
      // Tension keeps the red glow; nothing else glows.
      width: 1.2,
      alpha: 0.35 + ((s.latest.affinity || 0) / 10) * 0.55,
      // Chip size still carries affinity, but capped by edge length so a
      // short clique edge is never mostly chip
      chipR: Math.min(8 + (s.latest.affinity || 0) * 0.45, Math.max(6.5, len / 5)),
      hot: (s.latest.tension || 0) >= 6,
      // ONE moment badge, most dramatic wins — the old up-to-4 stack was
      // half the clutter in a dense web
      badges: [
        ...((s.latest.tension || 0) >= 8 ? ['⚡'] : []),
        ...(s.prev && (s.latest.affinity - s.prev.affinity) >= 3 ? ['💘'] : []),
        ...(s.prev && (s.latest.affinity - s.prev.affinity) <= -3 ? ['💔'] : []),
        ...(s.statusChanged ? ['✨'] : []),
      ].slice(0, 1),
    };
  }).filter(Boolean);

  // 🧹 Chip declutter: three deterministic push-apart passes (chip↔chip both
  // move, chip↔node only the chip moves), then clamp into the ring area. The
  // chip may drift slightly off its curve — a tappable readable button beats
  // a geometrically pure pileup.
  for (let pass = 0; pass < 3; pass++) {
    for (let i = 0; i < edges.length; i++) {
      for (let j = i + 1; j < edges.length; j++) {
        const A = edges[i], B = edges[j];
        const minD = A.chipR + B.chipR + 3;
        let ddx = B.chipX - A.chipX, ddy = B.chipY - A.chipY;
        let d = Math.hypot(ddx, ddy);
        if (d >= minD) continue;
        if (d < 0.01) { ddx = 1; ddy = 0; d = 1; }
        const push = (minD - d) / 2;
        A.chipX -= (ddx / d) * push; A.chipY -= (ddy / d) * push;
        B.chipX += (ddx / d) * push; B.chipY += (ddy / d) * push;
      }
      const A = edges[i];
      nodes.forEach(nd => {
        if (nd.bench) return;
        const minD = nd.r + A.chipR + 4;
        let ddx = A.chipX - nd.x, ddy = A.chipY - nd.y;
        let d = Math.hypot(ddx, ddy);
        if (d >= minD) return;
        if (d < 0.01) { ddx = 1; ddy = 0; d = 1; }
        A.chipX = nd.x + (ddx / d) * minD;
        A.chipY = nd.y + (ddy / d) * minD;
      });
    }
  }
  edges.forEach(e => {
    e.chipX = Math.min(W - e.chipR - 2, Math.max(e.chipR + 2, e.chipX));
    e.chipY = Math.min(ringH - e.chipR - 2, Math.max(e.chipR + 2, e.chipY));
  });

  // Legend: one chip per status present, drama order preserved; heuristic (pre-analysis)
  // pairs read as plain chatting
  const legend = [];
  const legendSeen = new Set();
  edges.forEach(e => {
    const k = e.ship.heuristic ? '__chatting' : (e.statusKey || '__chatting');
    if (legendSeen.has(k)) return;
    legendSeen.add(k);
    legend.push(e.ship.heuristic || !e.ship.latest.status
      ? { key: k, emoji: '💫', label: 'early sparks', color: THEME.accent }
      : { key: k, emoji: e.meta.emoji, label: e.ship.latest.status, color: e.meta.color });
  });
  const matchesFocus = (e) => !focusStatus || (e.ship.heuristic || !e.ship.latest.status ? '__chatting' : e.statusKey) === focusStatus;

  // Sparse + faint on purpose: at 34 stars the specks read as scratches/noise
  // over the web (user report, 07-09), not as sky
  const stars = Array.from({ length: 14 }, (_, i) => ({
    x: (hash('sx' + i) % 1000) / 1000 * W,
    y: (hash('sy' + i) % 1000) / 1000 * H,
    r: 0.5 + (hash('sr' + i) % 3) * 0.3,
    twinkle: i % 3 === 0,
    delay: (hash('sd' + i) % 40) / 10,
  }));

  return (
    // maxWidth caps the map in wide layouts — the viewBox was designed for the
    // ~360px sidebar; at full column width every node/label scales up comically.
    // BOXLESS — the web floats directly on the themed tab. The old container
    // (navy fill, border) read as a "weird background box" stamped on the space
    // theme (user screenshot 07-10). A faint radial tint stays for label
    // contrast on light themes; it reads as ambient shadow, not a panel.
    <div style={{ position: 'relative', maxWidth: '460px', margin: '0 auto', background: 'radial-gradient(ellipse at center, rgba(8,6,18,0.30), transparent 78%)' }}>
      <style>{`
        @keyframes constEdgePulse { 0%, 100% { opacity: 0.55; } 50% { opacity: 1; } }
        @keyframes constTwinkle { 0%, 100% { opacity: 0.15; } 50% { opacity: 0.85; } }
        .constNode { transition: filter 0.15s ease; }
        .constNode:hover { filter: brightness(1.3); }
        .constChip { transition: transform 0.15s ease; }
        .constChip:hover { transform: scale(1.18); }
        @keyframes constEdgeDraw { from { stroke-dashoffset: 1; } to { stroke-dashoffset: 0; } }
        @keyframes constNodeIn { from { opacity: 0; transform: scale(0.6); } to { opacity: 1; transform: scale(1); } }
        @keyframes constHotGlow { 0%, 100% { stroke-opacity: 0.25; } 50% { stroke-opacity: 0.55; } }
      `}</style>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: 'auto', display: 'block' }}>
        <defs>
          {nodes.map(nd => (
            <clipPath key={nd.key} id={`constClip${hash(nd.key)}`}>
              <circle cx={nd.x} cy={nd.y} r={nd.r} />
            </clipPath>
          ))}
        </defs>

        {stars.map((st, i) => (
          <circle key={i} cx={st.x} cy={st.y} r={st.r} fill="#fff"
            opacity={st.twinkle ? undefined : 0.22}
            style={st.twinkle ? { animation: `constTwinkle ${3 + (i % 3)}s ease-in-out ${st.delay}s infinite` } : undefined}
          />
        ))}

        {/* Edges: glow under-stroke (hot red pulse at tension ≥6) + main stroke that
            draws itself in (pathLength=1 normalizes so dashoffset animates 1→0);
            status-change ships pulse; legend focus dims the rest */}
        {edges.map((e, i) => (
          <g key={e.ship.key} opacity={matchesFocus(e) ? 1 : 0.12} style={{ transition: 'opacity 0.25s ease' }}>
            {/* Hot pairs only get the under-glow (option A) — the everywhere-
                glow was most of the old thickness */}
            {e.hot && (
              <path d={e.d} fill="none" stroke="#ff5252" strokeWidth="3.4"
                strokeLinecap="round"
                style={{ animation: 'constHotGlow 1.8s ease-in-out infinite' }} />
            )}
            <path d={e.d} fill="none" stroke={e.meta.color} strokeWidth={e.width} strokeOpacity={e.alpha}
              strokeLinecap="round"
              pathLength={e.dashed ? undefined : 1}
              strokeDasharray={e.dashed ? '4 5' : 1}
              style={e.dashed
                ? (e.ship.statusChanged ? { animation: 'constEdgePulse 2.4s ease-in-out infinite' } : undefined)
                : { strokeDashoffset: 1, animation: `constEdgeDraw 0.9s ease ${0.25 + i * 0.12}s forwards${e.ship.statusChanged ? ', constEdgePulse 2.4s ease-in-out 1.4s infinite' : ''}` }}
            />
          </g>
        ))}

        {/* Nodes: halo + portrait + ring + name; unlinked cast waits dim at the edge
            of the party until their first ship. transformOrigin keeps the pop-in
            scale centered on the node. */}
        {nodes.map((nd, i) => (
          <g key={nd.key} className="constNode" onClick={() => nd.pid && onSelectCast(nd.pid)}
            opacity={nd.linked ? 1 : 0.55}
            style={{ cursor: nd.pid ? 'pointer' : 'default', animation: `constNodeIn 0.5s ease ${i * 0.06}s both`, transformOrigin: `${nd.x}px ${nd.y}px` }}>
            <title>{nd.name}{nd.linked ? '' : ' — no ships yet'}{nd.pid ? ' — tap for character card' : ''}</title>
            <circle cx={nd.x} cy={nd.y} r={nd.r + 5} fill={nd.linked ? nd.ringColor : '#ffffff'} opacity="0.18" />
            {nd.img ? (
              <image href={nd.img} x={nd.x - nd.r} y={nd.y - nd.r} width={nd.r * 2} height={nd.r * 2}
                preserveAspectRatio="xMidYMid slice" clipPath={`url(#constClip${hash(nd.key)})`} />
            ) : (
              <>
                <circle cx={nd.x} cy={nd.y} r={nd.r} fill={`${THEME.accent}45`} />
                <text x={nd.x} y={nd.y} textAnchor="middle" dominantBaseline="central"
                  fontSize="14" fontWeight="800" fill="#fff" opacity="0.9">
                  {(nd.name || '?').charAt(0).toUpperCase()}
                </text>
              </>
            )}
            <circle cx={nd.x} cy={nd.y} r={nd.r} fill="none" stroke={nd.ringColor} strokeWidth="1.5"
              strokeOpacity="0.9" strokeDasharray={nd.linked ? undefined : '3 3'} />
            <text x={nd.lx} y={nd.ly} textAnchor={nd.anchor} fontSize={nd.fs} fontWeight="600"
              fill="rgba(255,255,255,0.92)" letterSpacing="0.3"
              style={{ fontFamily: 'inherit', paintOrder: 'stroke', stroke: 'rgba(0,0,0,0.55)', strokeWidth: '3px' }}>
              {(nd.name || '').length > nd.maxName ? nd.name.slice(0, nd.maxName - 1) + '…' : nd.name}
            </text>
          </g>
        ))}

        {/* Status chips at edge midpoints — tap for the ship detail card. Halo ring
            makes them read as buttons, not decoration; moment badges (✨💘💔⚡)
            flag pairs where something notable happened this chapter */}
        {edges.map(e => (
          <g key={`chip-${e.ship.key}`} className="constChip" onClick={() => onSelectShip(e.ship.key)}
            opacity={matchesFocus(e) ? 1 : 0.12}
            style={{ cursor: 'pointer', transition: 'opacity 0.25s ease, transform 0.15s ease', transformOrigin: `${e.chipX}px ${e.chipY}px` }}>
            <title>{e.ship.shipName || `${e.ship.nameA} ✕ ${e.ship.nameB}`}{e.ship.latest.status ? ` — ${e.ship.latest.status}` : ''} — tap for the tea!</title>
            {/* Chip size carries affinity now (option A): 8px stranger → 12.5px besties */}
            <circle cx={e.chipX} cy={e.chipY} r={e.chipR} fill="rgba(0,0,0,0.6)" stroke={e.meta.color} strokeWidth="1" />
            <text x={e.chipX} y={e.chipY + 0.5} textAnchor="middle" dominantBaseline="central" fontSize={Math.round(e.chipR + 2)}>{e.meta.emoji}</text>
            {e.badges.map((b, bi) => (
              <text key={bi} x={e.chipX + e.chipR - 1 + bi * 10} y={e.chipY - e.chipR + 2} fontSize="8">{b}</text>
            ))}
          </g>
        ))}
      </svg>

      {/* ⓘ top-right opens the how-to-read popover; counts live in the legend row */}
      <button
        onClick={() => setShowHelp(v => !v)}
        title="How to read the web"
        aria-label="How to read the web"
        style={{
          position: 'absolute', top: '6px', right: '8px', width: 22, height: 22,
          borderRadius: '50%', cursor: 'pointer', fontSize: '0.7em', fontWeight: 700,
          background: showHelp ? 'rgba(255,255,255,0.18)' : 'rgba(255,255,255,0.08)',
          border: '1px solid rgba(255,255,255,0.25)', color: 'rgba(255,255,255,0.8)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>ⓘ</button>
      {showHelp && (
        <div
          onClick={() => setShowHelp(false)}
          style={{
            position: 'absolute', top: '32px', right: '8px', zIndex: 2, cursor: 'pointer',
            maxWidth: 230, padding: '10px 12px', borderRadius: '10px',
            background: 'rgba(6, 8, 16, 0.95)', border: '1px solid rgba(255,255,255,0.18)',
            color: 'rgba(255,255,255,0.9)', fontSize: '0.72em', lineHeight: 1.65,
            boxShadow: '0 8px 24px rgba(0,0,0,0.5)',
          }}>
          <b>How to read the web</b><br />
          👤 Tap a face for their card<br />
          {'\u{1F4AC}'} Tap a chip between two locals for their tea<br />
          ➖ Thicker line = closer<br />
          🔥 Red glow = tension!
          {legend.some(l => l.key === '__chatting') && (
            <><br />💫 Early sparks = they've been chatting. Real ship statuses land as chapters close!</>
          )}
        </div>
      )}

      {/* No ships yet: the bones are up, say what fills them in */}
      {edges.length === 0 && (
        <div style={{
          position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
          textAlign: 'center', pointerEvents: 'none',
          color: 'rgba(255,255,255,0.75)', fontSize: '0.78em', fontWeight: 700,
          textShadow: '0 1px 6px rgba(13,16,34,0.9)', padding: '0 30px', lineHeight: 1.4,
        }}>
          The tea is brewing!<br />
          <span style={{ fontSize: '0.85em', fontWeight: 600, opacity: 0.8 }}>Ships appear here as the cast bonds.</span>
        </div>
      )}

      {/* Legend — centered under the web: counts + tappable status spotlights */}
      {legend.length > 0 && (
        <div style={{
          display: 'flex', flexWrap: 'wrap', gap: '6px', alignItems: 'center', justifyContent: 'center',
          padding: '4px 10px 8px',
        }}>
          <span style={{
            fontSize: '0.7em', fontWeight: 600, color: THEME.textOnPanel || '#fff', opacity: 0.7, marginRight: '4px',
          }}>
            ✨ {nodes.length} · 💘 {edges.length}
          </span>
          {legend.map(l => (
            <button key={l.key}
              // Spotlighting is pointless with one status (every edge matches —
              // the tap visibly did nothing, user report 07-10), so a lone chip
              // opens the explainer instead
              onClick={() => legend.length === 1
                ? setShowHelp(v => !v)
                : setFocusStatus(focusStatus === l.key ? null : l.key)}
              title={legend.length === 1 ? 'What does this mean?' : focusStatus === l.key ? 'Show all ships' : `Spotlight ${l.label}`}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: '5px',
                padding: '3px 10px', borderRadius: 999, cursor: 'pointer',
                border: `1px solid ${l.color}${focusStatus === l.key ? 'ee' : '88'}`,
                // Dark pill + white label: the status color alone vanished into
                // themed backgrounds (user report 07-10) — color stays on border/emoji
                backgroundColor: focusStatus === l.key ? `${l.color}45` : 'rgba(0,0,0,0.45)',
                color: '#fff', fontSize: '0.72em', fontWeight: 600,
                opacity: focusStatus && focusStatus !== l.key ? 0.45 : 1,
              }}>
              <span>{l.emoji}</span>
              <span>{l.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// 📰📌 RecapNotesPanel (Nina 08-07) — the Recap lane's paper trail: every
// Morning Paper the world feed dropped on the doormat, and every note a
// resident pinned to the room. Both are `shared_content` rows with
// content_type 'note'; the paper is the subset carrying is_world_digest
// (worldFeed.js digestForSpace). In the Room feed these scroll away forever —
// this is the archive, newest first, papers first because they are dated.
//
// Derives from the actions the page already holds. No fetch, no new endpoint.
const RECAP_NOTES_COLLAPSED = 4;

const RecapNotesPanel = ({ allActions, THEME }) => {
  const [showAllPapers, setShowAllPapers] = React.useState(false);
  const [showAllPins, setShowAllPins] = React.useState(false);
  const [openId, setOpenId] = React.useState(null);

  const notes = allActions
    .filter(a => a.type === 'shared_content' && a.data?.content_type === 'note')
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  const papers = notes.filter(a => a.data?.is_world_digest);
  const pins = notes.filter(a => !a.data?.is_world_digest);
  if (papers.length === 0 && pins.length === 0) return null;

  const SectionHead = ({ emoji, label, count }) => (
    <div style={{
      fontSize: '0.7em', fontWeight: 700, color: THEME.textMuted,
      textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '10px', padding: '0 4px',
      display: 'flex', alignItems: 'center', gap: '6px',
    }}>
      <span>{emoji}</span> {label}
      <span style={{ marginLeft: 'auto', fontWeight: 600, opacity: 0.7 }}>{count}</span>
    </div>
  );

  // A paper's body is a multi-line brief; a pin is usually one line. Both
  // clamp until tapped so a month of papers can't bury the sections below.
  const NoteCard = ({ note, accent, kicker }) => {
    const d = note.data || {};
    const open = openId === note.id;
    const body = d.description || d.title || '';
    return (
      <div
        onClick={() => setOpenId(open ? null : note.id)}
        style={{
          background: `${accent}12`, border: `1px solid ${accent}38`,
          borderRadius: '10px', padding: '10px 14px', cursor: 'pointer',
        }}
      >
        <div style={{
          fontSize: '0.64em', fontWeight: 800, letterSpacing: '1.5px',
          color: accent, textTransform: 'uppercase',
          display: 'flex', alignItems: 'baseline', gap: 8,
        }}>
          <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{kicker}</span>
          <span style={{ marginLeft: 'auto', flexShrink: 0, opacity: 0.75 }}>
            {new Date(d.timestamp || note.created_at).toLocaleDateString()}
          </span>
        </div>
        {d.title && (
          <div style={{ fontSize: '0.9em', fontWeight: 700, color: THEME.textOnPanel || THEME.textDark, marginTop: '3px' }}>
            {d.title}
          </div>
        )}
        {body && (
          <div style={{
            fontSize: '0.78em', color: THEME.textMuted, marginTop: '5px',
            lineHeight: 1.5, whiteSpace: 'pre-wrap',
            ...(open ? {} : { display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }),
          }}>
            {body}
          </div>
        )}
      </div>
    );
  };

  const MoreButton = ({ shown, total, onClick, accent, label }) => (total <= RECAP_NOTES_COLLAPSED ? null : (
    <button
      onClick={onClick}
      style={{
        marginTop: '8px', border: `1px solid ${accent}66`, borderRadius: 999,
        background: 'transparent', color: accent, padding: '4px 14px',
        fontSize: '0.72em', fontWeight: 700, cursor: 'pointer',
      }}
    >
      {shown ? 'Show less' : `All ${total} ${label}`}
    </button>
  ));

  return (
    <>
      {papers.length > 0 && (
        <div>
          <SectionHead emoji="📰" label="The Morning Papers" count={papers.length} />
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {(showAllPapers ? papers : papers.slice(0, RECAP_NOTES_COLLAPSED)).map(p => (
              <NoteCard key={p.id} note={p} accent="#e0b25a" kicker="📰 Morning Paper" />
            ))}
          </div>
          <MoreButton shown={showAllPapers} total={papers.length} accent="#e0b25a" label="papers"
            onClick={() => setShowAllPapers(v => !v)} />
        </div>
      )}

      {pins.length > 0 && (
        <div>
          <SectionHead emoji="📌" label="Pinned notes" count={pins.length} />
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {(showAllPins ? pins : pins.slice(0, RECAP_NOTES_COLLAPSED)).map(p => (
              <NoteCard key={p.id} note={p} accent="#7ec4a8"
                kicker={`📌 ${p.data?.sender_name || 'Someone'} pinned this`} />
            ))}
          </div>
          <MoreButton shown={showAllPins} total={pins.length} accent="#7ec4a8" label="notes"
            onClick={() => setShowAllPins(v => !v)} />
        </div>
      )}
    </>
  );
};

// HighlightsPanel — Character Stats + Best Quotes for non-competitive Highlights tab
// gameId feeds the dossier mystery gate on the cast popup (per-viewer reveal state).
const HighlightsPanel = ({ allActions, users, THEME, moderatorCharacter, gameId, humanPlayerId, shipVotes = null, builds = [],
  // 🫖 SPOTLIGHT (Locals place, Nina 08-01 mock = contract): render ONLY the
  // hottest ship as a votable pair card + fresh headlines; the full panel
  // stays behind onOpenFull ("read all chapters"). Reuses every derivation
  // + the T3 vote plumbing — no second ship pipeline.
  spotlight = false, onOpenFull = null, freshCount = 0 }) => {
  const [selectedCastPid, setSelectedCastPid] = React.useState(null); // character-card popup
  const [selectedShipKey, setSelectedShipKey] = React.useState(null); // ship-detail popup (from constellation)
  const [showAllChapters, setShowAllChapters] = React.useState(false); // 📖 story archive expand (Tea T1)
  const [shipsViewPref, setShipsViewPref] = React.useState(() => {
    try { return localStorage.getItem('teaShipsView'); } catch (e) { return null; }
  });
  const setShipsView = (v) => {
    setShipsViewPref(v);
    try { localStorage.setItem('teaShipsView', v); } catch (e) { /* private mode */ }
  };
  const msgTypes = new Set(['message', 'public_message', 'private_message', 'secret_message', 'human_interjection']);
  const messages = allActions.filter(a => msgTypes.has(a.type) && a.data?.message);

  // --- Character Stats ---
  const statsByPlayer = {};
  messages.forEach(a => {
    const pid = a.game_instance_player_id;
    if (!pid) return;
    if (!statsByPlayer[pid]) statsByPlayer[pid] = { count: 0, totalLen: 0, chapters: new Set(), reactions: 0 };
    statsByPlayer[pid].count++;
    statsByPlayer[pid].totalLen += (a.data.message || '').length;
    if (a.game_instance_round_id) statsByPlayer[pid].chapters.add(a.game_instance_round_id);
    // Count reactions received
    if (a.data?.reactions) {
      statsByPlayer[pid].reactions += Object.values(a.data.reactions).reduce((sum, arr) => sum + (Array.isArray(arr) ? arr.length : 0), 0);
    }
  });

  // Map round IDs to chapter numbers
  const roundIds = [...new Set(allActions.map(a => a.game_instance_round_id).filter(Boolean))];
  const roundIdToTimestamp = {};
  roundIds.forEach(rid => {
    const ts = allActions.filter(a => a.game_instance_round_id === rid).map(a => new Date(a.created_at).getTime());
    roundIdToTimestamp[rid] = Math.min(...ts);
  });
  const sortedRoundIds = [...roundIds].sort((a, b) => roundIdToTimestamp[a] - roundIdToTimestamp[b]);
  const roundIdToNum = {};
  sortedRoundIds.forEach((rid, i) => { roundIdToNum[rid] = i + 1; });
  const totalChapters = sortedRoundIds.length;

  // Map round IDs to AI-generated chapter titles from round_analysis actions.
  // chapter_number (backend-stamped, 08-13: 5 rounds = 1 chapter) beats the
  // round-position index, which over-counts under the new cadence.
  const roundIdToTitle = {};
  const roundIdToChapterNum = {};
  allActions.filter(a => a.type === 'round_analysis' && a.data?.chapter_title).forEach(a => {
    roundIdToTitle[a.game_instance_round_id] = a.data.chapter_title;
    if (a.data.chapter_number) roundIdToChapterNum[a.game_instance_round_id] = a.data.chapter_number;
  });
  const getChapterLabel = (roundId) => {
    const num = roundIdToChapterNum[roundId] || roundIdToNum[roundId];
    const title = roundIdToTitle[roundId];
    return title ? `Ch. ${num}: ${title}` : `Chapter ${num}`;
  };

  // 📖 THE STORY SO FAR (TEA_TAB_IDENTITY T1): chapters as a PERSISTENT
  // archive. The data has always been here (round_analysis carries
  // chapter_title + the host's recap line) — it just closed silently and
  // vanished. Newest first: the Tea is what a returning member reads.
  const chapterCards = allActions
    .filter(a => a.type === 'round_analysis' && a.data?.chapter_title)
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
    .map(a => ({
      id: a.id,
      num: a.data.chapter_number || roundIdToNum[a.game_instance_round_id] || null,
      title: a.data.chapter_title,
      intro: a.data.host_intro || a.data.summary || null,
      at: a.created_at,
      reactions: a.data.reactions || null,
    }));

  // 🫖 T4: reactions on Tea cards (existing per-action reaction machinery —
  // chapters and cut scenes ARE action rows). Optimistic bumps overlay the
  // server counts until the next status refresh; spectators see counts but
  // need a seat to stamp (reactions are player-keyed server-side).
  const TEA_REACTIONS = ['🔥', '😭', '👀'];
  const [teaReactionBumps, setTeaReactionBumps] = React.useState({});
  const teaReactionCount = (card, emoji) => {
    const server = Object.values(card.reactions || {}).reduce((s, arr) => s + (arr || []).filter(r => r === emoji).length, 0);
    return server + ((teaReactionBumps[card.id] || {})[emoji] || 0);
  };
  const teaReactionTotal = (card) =>
    TEA_REACTIONS.reduce((s, e) => s + teaReactionCount(card, e), 0);
  const handleTeaReaction = async (card, emoji) => {
    if (!humanPlayerId) return;
    setTeaReactionBumps(prev => ({
      ...prev,
      [card.id]: { ...(prev[card.id] || {}), [emoji]: ((prev[card.id] || {})[emoji] || 0) + 1 },
    }));
    try {
      await chatGameApi.submitReaction(gameId, humanPlayerId, card.id, emoji);
    } catch (e) { /* server rejected (e.g. toggle-off race) — next refresh reconciles */ }
  };
  const TeaReactionRow = ({ card }) => (
    <div style={{ display: 'flex', gap: '6px', marginTop: '6px' }}>
      {TEA_REACTIONS.map(e => {
        const n = teaReactionCount(card, e);
        return (
          <button
            key={e}
            onClick={(ev) => { ev.stopPropagation(); handleTeaReaction(card, e); }}
            disabled={!humanPlayerId}
            title={humanPlayerId ? 'React!' : 'Join the space to react'}
            style={{
              border: `1px solid ${THEME.accent}25`, borderRadius: '10px', padding: '2px 8px',
              background: n > 0 ? `${THEME.accent}18` : 'transparent',
              color: THEME.textMuted, fontSize: '0.72em', fontWeight: 700,
              cursor: humanPlayerId ? 'pointer' : 'default', display: 'flex', alignItems: 'center', gap: '4px',
            }}
          >
            <span>{e}</span>{n > 0 && <span>{n}</span>}
          </button>
        );
      })}
    </div>
  );

  // (🏆 weekly-top crown derivation lives BELOW cutsceneCards — const TDZ)

  // 🎬 CUT SCENE GALLERY (Tea T2, 07-14 — Nina's call: Tea is the comics'
  // forever home, chat keeps the moment). Cut scenes are host-posted
  // shared_content rows flagged is_cutscene (crafting discoveries, first-pair
  // ships, future beats) — the archive is pure derivation, zero backend.
  const cutsceneCards = allActions
    .filter(a => (a.type === 'shared_content' && a.data?.is_cutscene && a.data?.url)
      // 🌄 Scene banners join the storybook (Nina 08-14). A /scene's art is
      // stored ON its own space_command row (is_scene_art + url — spec §5:
      // the picture is what the scene looks like, not a separate beat), so
      // the strip needs this second arm to see it. Both venues: scene art
      // mints fleet-wide, and the archive fetch carries the older rows.
      || (a.type === 'space_command' && a.data?.is_scene_art && a.data?.url))
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
    .map(a => ({
      id: a.id, url: a.data.url, caption: a.data.description || a.data.card_message || '', at: a.created_at,
      reactions: a.data.reactions || null,
    }));

  // 🏆 Weekly Top Tea crown (client-derived twin of the backend announcer):
  // most-reacted Tea card of the last 7 days, needs ≥2 reactions.
  const weekAgo = Date.now() - 7 * 24 * 3600 * 1000;
  const topTeaCard = [...chapterCards, ...cutsceneCards]
    .filter(c => new Date(c.at).getTime() > weekAgo)
    .map(c => ({ card: c, n: teaReactionTotal(c) }))
    .filter(x => x.n >= 2)
    .sort((a, b) => b.n - a.n)[0] || null;

  // 🫖 Ship EVENT timeline (Tea T3, 07-15): /ship + /drama space_command rows
  // indexed per pair — the "who lit this fire" receipts under each ship card.
  const shipEventsByPair = {};
  allActions
    .filter(a => a.type === 'space_command' && ['ship', 'drama'].includes(a.data?.command)
      && Array.isArray(a.data?.targets) && a.data.targets.length === 2)
    .forEach(a => {
      const key = a.data.targets.map(t => (t.name || '').toLowerCase().trim()).sort().join('::');
      if (!shipEventsByPair[key]) shipEventsByPair[key] = [];
      shipEventsByPair[key].push({
        id: a.id,
        kind: a.data.command,
        by: a.data.issued_by?.name || 'someone',
        at: a.created_at,
      });
    });

  // 💍/💔 vote state (Tea T3): server tallies ride the status payload
  // (shipVotes prop); a fresh local vote overrides its pair until the next
  // poll catches up. My own vote is remembered per space in localStorage.
  const [shipVoteOverride, setShipVoteOverride] = React.useState({});
  const myShipVotesKey = `shipVotes_${gameId}`;
  const readMyShipVotes = () => {
    try { return JSON.parse(localStorage.getItem(myShipVotesKey)) || {}; } catch (e) { return {}; }
  };
  const [myShipVotes, setMyShipVotes] = React.useState(readMyShipVotes);
  const serverPairKey = (nameA, nameB) =>
    [nameA, nameB].map(n => (n || '').toLowerCase().trim().replace(/\+/g, ' ')).sort().join('+');
  const handleShipVote = async (nameA, nameB, vote) => {
    const pk = serverPairKey(nameA, nameB);
    try {
      const res = await chatGameApi.submitShipVote(gameId, humanPlayerId || null, pk, vote);
      if (res?.tally) setShipVoteOverride(prev => ({ ...prev, [pk]: res.tally }));
      const mine = { ...readMyShipVotes(), [pk]: vote };
      try { localStorage.setItem(myShipVotesKey, JSON.stringify(mine)); } catch (e) { /* private mode */ }
      setMyShipVotes(mine);
    } catch (e) { /* vote is garnish — card just doesn't flip */ }
  };
  const tallyFor = (nameA, nameB) => {
    const pk = serverPairKey(nameA, nameB);
    return shipVoteOverride[pk] || (shipVotes || {})[pk] || null;
  };

  // 📇 RECORD BOOK (Tea T2 remainder, 07-15): the space's stat plaques —
  // first discoveries (builds ledger rides the status payload) + poll
  // outcomes (closePoll's 🗳️ space_command cards). Pure derivation.
  const recordBook = [
    ...(builds || [])
      .filter(b => b && b.via_combo && b.built_by)
      .map(b => ({
        id: `disc-${b.id || b.handle}`,
        emoji: b.emoji || '🧪',
        text: `#${b.name} — first crafted by ${b.built_by}`,
        at: b.built_at,
      })),
    ...allActions
      // Poll outcomes: HEM-enacted rows carry a via_poll receipt object;
      // legacy closePoll scenes only had the 🗳️-prefixed card copy.
      .filter(a => (a.type === 'space_command' || a.type === 'shared_content' || a.type === 'game_announcement')
        && (a.data?.via_poll?.label
          || a.data?.directed
          || (a.data?.command === 'scene' && (a.data?.card_message || '').startsWith('🗳️'))))
      .map(a => ({
        id: `poll-${a.id}`,
        // 🎯 09-01: a resident's directed call is part of the record too
        emoji: a.data?.directed ? (a.data.directed.kind === 'cook' ? '🍳' : '🎯') : '🗳️',
        text: a.data?.directed
          ? `${a.data.directed.player_name}: ${a.data.directed.q} → ${a.data.directed.chosen === 'own' ? 'their own move' : ((a.data.directed.options || []).find(o => o.key === a.data.directed.chosen)?.label || 'made a call')}`
          : (a.data?.via_poll?.label
            ? `The room voted: ${a.data.via_poll.emoji || ''} ${a.data.via_poll.label}!`.trim()
            : a.data.card_message),
        at: a.created_at,
      })),
  ].filter(r => r.at).sort((a, b) => new Date(b.at) - new Date(a.at));

  // --- The Ships: LLM relationship data from round_analysis actions ---
  const getStatusMeta = (status) => SHIP_STATUS_META[(status || '').toLowerCase().trim()] || { emoji: '💫', color: THEME.accent };
  const shipAnalyses = allActions
    .filter(a => a.type === 'round_analysis' && Array.isArray(a.data?.relationships) && a.data.relationships.length > 0)
    .sort((a, b) => new Date(a.created_at) - new Date(b.created_at));

  const shipHistories = {};
  shipAnalyses.forEach(a => {
    a.data.relationships.forEach(rel => {
      if (!Array.isArray(rel.pair) || rel.pair.length !== 2) return;
      const key = rel.pair.map(n => (n || '').toLowerCase().trim()).sort().join('::');
      if (!shipHistories[key]) shipHistories[key] = { pair: rel.pair, entries: [] };
      if (rel.ship_name) shipHistories[key].shipName = rel.ship_name;
      shipHistories[key].entries.push({
        roundId: a.game_instance_round_id,
        affinity: Math.max(0, Math.min(10, Math.round(Number(rel.affinity) || 0))),
        tension: Math.max(0, Math.min(10, Math.round(Number(rel.tension) || 0))),
        status: rel.status || null,
        headline: rel.headline || null,
        receipt: rel.receipt || null,
      });
    });
  });

  const findUserByName = (name) => users.find(u =>
    ((u.name || u.data?.name || '').toLowerCase().trim()) === ((name || '').toLowerCase().trim()));

  const ships = Object.entries(shipHistories).map(([key, s]) => {
    const latest = s.entries[s.entries.length - 1];
    const prev = s.entries.length > 1 ? s.entries[s.entries.length - 2] : null;
    const [nameA, nameB] = s.pair;
    const userA = findUserByName(nameA);
    const userB = findUserByName(nameB);
    const statusChanged = !!(prev && prev.status && latest.status && latest.status !== prev.status);
    // Drama score: sort the juiciest pairs to the top
    const drama = latest.tension
      + (prev ? Math.abs(latest.affinity - prev.affinity) * 2 + Math.abs(latest.tension - prev.tension) * 2 : 0)
      + (statusChanged ? 6 : 0);
    return {
      key, nameA, nameB,
      shipName: s.shipName,
      imgA: userA?.profile_image_url || userA?.data?.profile_image_url,
      imgB: userB?.profile_image_url || userB?.data?.profile_image_url,
      latest, prev, statusChanged, drama,
      entries: s.entries,
    };
  }).sort((a, b) => b.drama - a.drama);

  const teaHeadline = ships.find(s => s.statusChanged && s.latest.headline) || ships.find(s => s.latest.headline) || null;

  const playerStats = Object.entries(statsByPlayer)
    .map(([pid, s]) => {
      const user = users.find(u => u.id === pid || u.game_player_id === pid);
      const chapterNums = [...s.chapters].map(rid => roundIdToNum[rid]).filter(Boolean).sort((a, b) => a - b);
      // Find most active chapter
      const chapterCounts = {};
      messages.filter(a => a.game_instance_player_id === pid).forEach(a => {
        const cn = roundIdToNum[a.game_instance_round_id];
        if (cn) chapterCounts[cn] = (chapterCounts[cn] || 0) + 1;
      });
      const mostActiveChapter = Object.entries(chapterCounts).sort((a, b) => b[1] - a[1])[0];
      return {
        pid,
        name: user?.name || user?.data?.name || 'Unknown',
        model: getDisplayModelName(user || user?.data) || '',
        profileImage: user?.profile_image_url || user?.data?.profile_image_url,
        isHuman: user?.is_human || user?.data?.is_human,
        isYou: !!humanPlayerId && (pid === humanPlayerId || user?.game_player_id === humanPlayerId),
        count: s.count,
        avgLen: Math.round(s.totalLen / s.count),
        reactions: s.reactions,
        chaptersActive: chapterNums.length,
        mostActiveChapter: mostActiveChapter ? { num: parseInt(mostActiveChapter[0]), count: mostActiveChapter[1] } : null,
      };
    })
    .sort((a, b) => b.count - a.count);

  // --- Heuristic ships: before round_analysis produces real ship data, build
  // ship-shaped pairs from who actually talks to whom (adjacency + reactions),
  // so the constellation renders from day one instead of the old list fallback.
  const heuristicShips = [];
  if (ships.length === 0 && playerStats.length >= 2) {
    const pairKey = (a, b) => [a, b].sort().join('::');
    const pairScores = {};
    const pairLabels = {};
    const orderedMsgs = [...messages].sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
    for (let i = 1; i < orderedMsgs.length; i++) {
      const prev = orderedMsgs[i - 1].game_instance_player_id;
      const curr = orderedMsgs[i].game_instance_player_id;
      if (prev && curr && prev !== curr) {
        const key = pairKey(prev, curr);
        pairScores[key] = (pairScores[key] || 0) + 1;
        if (!pairLabels[key]) pairLabels[key] = { chats: 0, reactions: 0 };
        pairLabels[key].chats++;
      }
    }
    messages.forEach(a => {
      if (!a.data?.reactions || !a.game_instance_player_id) return;
      Object.entries(a.data.reactions).forEach(([reactorId, emojis]) => {
        if (reactorId === a.game_instance_player_id) return;
        const key = pairKey(a.game_instance_player_id, reactorId);
        const count = Array.isArray(emojis) ? emojis.length : 0;
        pairScores[key] = (pairScores[key] || 0) + count * 3;
        if (!pairLabels[key]) pairLabels[key] = { chats: 0, reactions: 0 };
        pairLabels[key].reactions += count;
      });
    });
    const maxScore = Math.max(...Object.values(pairScores), 1);
    Object.entries(pairScores)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 8)
      .forEach(([key, score]) => {
        const [pidA, pidB] = key.split('::');
        const userA = users.find(u => u.id === pidA || u.game_player_id === pidA);
        const userB = users.find(u => u.id === pidB || u.game_player_id === pidB);
        if (!userA || !userB) return;
        const labels = pairLabels[key] || { chats: 0, reactions: 0 };
        const closeness = Math.max(1, Math.round((score / maxScore) * 10));
        const latest = {
          affinity: closeness,
          tension: Math.round(closeness / 2), // "heat" — drives edge thickness on the map
          status: null,
          headline: `${labels.chats} back-and-forth${labels.chats !== 1 ? 's' : ''}${labels.reactions > 0 ? ` · ${labels.reactions} reaction${labels.reactions !== 1 ? 's' : ''}` : ''}`,
          receipt: null,
          roundId: null,
        };
        heuristicShips.push({
          key, shipName: null,
          nameA: userA?.name || userA?.data?.name || 'Unknown',
          nameB: userB?.name || userB?.data?.name || 'Unknown',
          imgA: userA?.profile_image_url || userA?.data?.profile_image_url,
          imgB: userB?.profile_image_url || userB?.data?.profile_image_url,
          latest, prev: null, statusChanged: false, drama: score,
          entries: [latest], heuristic: true,
        });
      });
  }
  const displayShips = ships.length > 0 ? ships : heuristicShips;

  if (spotlight) {
    const hot = displayShips[0] || null;
    const headlines = ships.filter(s => s.latest?.headline).slice(0, 2);
    const face = (img, name) => img
      ? <img src={img} alt={name} style={{ width: 38, height: 38, borderRadius: '50%', objectFit: 'cover', border: '2px solid #ec407a' }} />
      : <span style={{ width: 38, height: 38, borderRadius: '50%', border: '2px solid #ec407a', background: 'rgba(255,255,255,0.08)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800 }}>{(name || '?').slice(0, 1)}</span>;
    const voteBtn = (nameA, nameB, vote, emoji, label, count, mine) => (
      <button
        onClick={() => handleShipVote(nameA, nameB, vote)}
        disabled={!humanPlayerId}
        style={{
          flex: 1, padding: '7px 6px', borderRadius: 999, cursor: humanPlayerId ? 'pointer' : 'default',
          border: mine === vote ? `2px solid ${vote === 'last' ? '#ec407a' : '#90a4ae'}` : '1px solid rgba(236,64,122,0.4)',
          background: mine === vote ? (vote === 'last' ? 'rgba(236,64,122,0.2)' : 'rgba(144,164,174,0.2)') : 'rgba(0,0,0,0.22)',
          color: '#ffd3e2', fontWeight: 800, fontSize: '0.72em',
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5,
        }}
      >
        <span>{emoji}</span><span>{label}</span><span style={{ opacity: 0.8 }}>{count}</span>
        {mine === vote && <span>✓</span>}
      </button>
    );
    return (
      <div style={{ border: '1px solid rgba(236,64,122,0.45)', borderRadius: 12, overflow: 'hidden', background: 'rgba(236,64,122,0.06)' }}>
        <div style={{
          display: 'flex', alignItems: 'center', gap: 7, padding: '8px 11px',
          background: 'linear-gradient(90deg, rgba(236,64,122,0.28), rgba(80,10,40,0.35))',
          borderBottom: '1px solid rgba(236,64,122,0.3)', fontSize: '0.8em', fontWeight: 800, color: '#fff',
        }}>
          <span>🫖 The Tea</span>
          {freshCount > 0 && (
            <span style={{ minWidth: 16, height: 16, borderRadius: 8, padding: '0 5px', background: '#ec407a', fontSize: '0.7em', lineHeight: '16px', textAlign: 'center' }}>{freshCount}</span>
          )}
        </div>
        {hot ? (
          <div style={{ margin: '9px 10px', border: '1px solid rgba(255,255,255,0.14)', borderRadius: 11, padding: '10px 10px 9px', background: 'rgba(0,0,0,0.25)' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 5 }}>
              {face(hot.imgA, hot.nameA)}
              <span style={{ margin: '0 8px', color: '#ec407a', fontWeight: 800 }}>×</span>
              {face(hot.imgB, hot.nameB)}
            </div>
            <div style={{ textAlign: 'center', fontSize: '0.82em', fontWeight: 800, color: '#fff' }}>
              {hot.nameA} × {hot.nameB}{hot.shipName ? <span style={{ color: '#ec407a' }}> · "{hot.shipName}"</span> : null}
            </div>
            <div style={{ textAlign: 'center', fontSize: '0.68em', color: 'rgba(255,255,255,0.55)', margin: '2px 0 8px' }}>
              {hot.heuristic
                ? (hot.latest.headline || 'something is brewing')
                : `${hot.latest.status ? `${hot.latest.status} · ` : ''}tension ${hot.latest.tension}/10`}
            </div>
            {(() => {
              const pk = serverPairKey(hot.nameA, hot.nameB);
              const tally = tallyFor(hot.nameA, hot.nameB) || { last: 0, doom: 0 };
              const mine = myShipVotes[pk] || null;
              return (
                <div style={{ display: 'flex', gap: 7 }}>
                  {voteBtn(hot.nameA, hot.nameB, 'last', '💍', 'They will', tally.last || 0, mine)}
                  {voteBtn(hot.nameA, hot.nameB, 'doom', '💔', 'Doomed', tally.doom || 0, mine)}
                </div>
              );
            })()}
          </div>
        ) : (
          <div style={{ padding: '12px 12px', fontSize: '0.78em', color: 'rgba(255,255,255,0.6)' }}>
            No gossip yet. Get the locals talking and check back!
          </div>
        )}
        {headlines.map((s, i) => (
          <div key={s.key || i} style={{ display: 'flex', gap: 7, padding: '6px 11px', fontSize: '0.74em', color: 'rgba(255,255,255,0.75)', borderTop: '1px dashed rgba(236,64,122,0.25)' }}>
            <span>🫖</span><span>{s.latest.headline}</span>
          </div>
        ))}
        {onOpenFull && (
          <button
            onClick={onOpenFull}
            style={{
              display: 'block', width: '100%', padding: '8px 0', border: 'none', borderTop: '1px dashed rgba(236,64,122,0.25)',
              background: 'transparent', color: '#ec407a', fontWeight: 800, fontSize: '0.7em',
              letterSpacing: '0.06em', cursor: 'pointer',
            }}
          >READ ALL CHAPTERS ▸</button>
        )}
      </div>
    );
  }

  // Full cast for the constellation bones: every character is a star from message
  // one, so the map exists before any ship data does. Talkers come from playerStats
  // (brings message counts for star sizing); users fills in silent/new cast.
  const normName = (nm) => (nm || '').toLowerCase().trim();
  const castForMap = playerStats.map(p => ({ pid: p.pid, name: p.name, profileImage: p.profileImage, count: p.count }));
  const castNames = new Set(castForMap.map(c => normName(c.name)));
  users.forEach(u => {
    const name = u.name || u.data?.name;
    if (!name || castNames.has(normName(name))) return;
    // Humans earn their star: AI characters are the cast (always on the map), but
    // a human only appears once they've spoken (playerStats) or hold a ship —
    // otherwise every drive-by join crowds the sky (user call, 07-09).
    if (u.is_human || u.data?.is_human) return;
    castNames.add(normName(name));
    castForMap.push({ pid: u.game_player_id || u.id, name, profileImage: u.profile_image_url || u.data?.profile_image_url, count: 0 });
  });
  // Keep the map readable: cap the stars, ship members always make the cut
  const shipMemberNames = new Set(displayShips.flatMap(s => [normName(s.nameA), normName(s.nameB)]));
  const castCapped = castForMap
    .sort((a, b) => ((shipMemberNames.has(normName(b.name)) ? 1 : 0) - (shipMemberNames.has(normName(a.name)) ? 1 : 0)) || (b.count - a.count))
    .slice(0, Math.max(12, shipMemberNames.size));

  // Latest moments for the ticker under the map: status changes, big affinity
  // swings, high-tension beats — most recent chapter first, juiciest tiebreak.
  const shipMoments = ships.flatMap(s => {
    const dAff = s.prev ? s.latest.affinity - s.prev.affinity : 0;
    const badge = s.statusChanged ? '✨' : dAff >= 3 ? '💘' : dAff <= -3 ? '💔' : (s.latest.tension >= 8 ? '⚡' : null);
    if (!badge || !s.latest.headline) return [];
    return [{ badge, ship: s, roundNum: roundIdToNum[s.latest.roundId] || 0 }];
  }).sort((a, b) => b.roundNum - a.roundNum || b.ship.drama - a.ship.drama).slice(0, 3);

  // Constellation is the default view once the graph has ≥3 distinct characters —
  // a 2-node graph is a worse version of one card. localStorage pref overrides.
  // Heuristic ships have no card layout, so they are map-only.
  const shipCharCount = new Set(displayShips.flatMap(s => [s.nameA, s.nameB].map(nm => (nm || '').toLowerCase().trim()))).size;
  const constellationEligible = shipCharCount >= 3;
  const shipsView = ships.length === 0 ? 'map'
    : constellationEligible ? (shipsViewPref === 'cards' ? 'cards' : 'map') : 'cards';

  // --- Awards (superlatives) — shown on each character's card, keyed by pid ---
  const awardsByPid = {};
  if (playerStats.length >= 2) {
    const addAward = (pid, award) => {
      if (!pid) return;
      if (!awardsByPid[pid]) awardsByPid[pid] = [];
      awardsByPid[pid].push(award);
    };
    if (playerStats[0]) addAward(playerStats[0].pid, { icon: '🗣️', title: 'Most Talkative', detail: `${playerStats[0].count} messages` });
    const longestMsg = messages.reduce((best, a) => (!best || (a.data.message || '').length > best.len) ? { len: (a.data.message || '').length, pid: a.game_instance_player_id } : best, null);
    if (longestMsg) addAward(longestMsg.pid, { icon: '📜', title: 'Longest Message', detail: `${longestMsg.len} characters` });
    const concise = playerStats.filter(p => p.count >= 3).sort((a, b) => a.avgLen - b.avgLen)[0];
    if (concise) addAward(concise.pid, { icon: '✂️', title: 'Most Concise', detail: `~${concise.avgLen} chars avg` });
    const mostReacted = playerStats.filter(p => p.reactions > 0).sort((a, b) => b.reactions - a.reactions)[0];
    if (mostReacted) addAward(mostReacted.pid, { icon: '❤️', title: 'Fan Favorite', detail: `${mostReacted.reactions} reactions` });
    const emojiByPlayer = {};
    messages.forEach(a => {
      const pid = a.game_instance_player_id;
      if (!pid) return;
      const count = ((a.data.message || '').match(/[\u{1F000}-\u{1FFFF}\u{2600}-\u{27BF}\u{FE00}-\u{FE0F}\u{1F900}-\u{1F9FF}]/gu) || []).length;
      emojiByPlayer[pid] = (emojiByPlayer[pid] || 0) + count;
    });
    const emojiKingEntry = Object.entries(emojiByPlayer).sort((a, b) => b[1] - a[1])[0];
    if (emojiKingEntry && emojiKingEntry[1] > 0) addAward(emojiKingEntry[0], { icon: '😎', title: 'Emoji King', detail: `${emojiKingEntry[1]} emojis` });
  }

  // --- Best Quotes ---
  // Score messages by multiple signals: reactions, energy (emojis, !, ?), length, and variety
  const hashStr = (s) => { let h = 0; for (let i = 0; i < s.length; i++) h = ((h << 5) - h + s.charCodeAt(i)) | 0; return Math.abs(h); };

  const quoteCandidates = messages
    .filter(a => (a.data.message || '').length >= 30 && !(a.data.sub_conversation_message || a.data.parent_action_id))
    .map(a => {
      const msg = a.data.message || '';
      const user = users.find(u => u.id === a.game_instance_player_id || u.game_player_id === a.game_instance_player_id);

      // Reaction score: each reaction is worth a lot
      const reactionCount = a.data?.reactions
        ? Object.values(a.data.reactions).reduce((sum, arr) => sum + (Array.isArray(arr) ? arr.length : 0), 0)
        : 0;

      // Energy score: emojis, exclamations, questions, caps words
      const emojiCount = (msg.match(/[\u{1F000}-\u{1FFFF}\u{2600}-\u{27BF}\u{FE00}-\u{FE0F}\u{1F900}-\u{1F9FF}]/gu) || []).length;
      const exclamations = (msg.match(/!/g) || []).length;
      const questions = (msg.match(/\?/g) || []).length;
      const capsWords = (msg.match(/\b[A-Z]{2,}\b/g) || []).length;
      const energyScore = Math.min(emojiCount * 3 + exclamations * 2 + questions * 2 + capsWords * 2, 20);

      // Length score: sweet spot is 60-200 chars (not too short, not a wall of text)
      const lenScore = msg.length < 60 ? msg.length / 2 : msg.length <= 200 ? 40 + (msg.length - 60) * 0.2 : 40 + 28 - (msg.length - 200) * 0.05;

      // Variety bonus: slight deterministic hash so we don't always pick the same messages
      const varietyBonus = hashStr(msg) % 15;

      const totalScore = reactionCount * 30 + energyScore + lenScore + varietyBonus;

      return {
        message: msg,
        sender: user?.name || user?.data?.name || a.data?.sender_name || 'Unknown',
        senderModel: getDisplayModelName(user || user?.data) || '',
        isHuman: user?.is_human || user?.data?.is_human,
        chapter: roundIdToNum[a.game_instance_round_id] || null,
        roundId: a.game_instance_round_id,
        score: totalScore,
        reactionCount,
      };
    });

  // Spread quotes across chapters (1 per chapter, max 2 per sender) via round-robin
  const sorted = [...quoteCandidates].sort((a, b) => b.score - a.score);
  const bestQuotes = [];
  const chapterCounts = {};
  const senderCounts = {};
  // First pass: pick best from each chapter (1 per chapter)
  for (const q of sorted) {
    if (bestQuotes.length >= 5) break;
    const ch = q.chapter || 'unknown';
    chapterCounts[ch] = (chapterCounts[ch] || 0);
    senderCounts[q.sender] = (senderCounts[q.sender] || 0);
    if (chapterCounts[ch] < 1 && senderCounts[q.sender] < 2) {
      bestQuotes.push(q);
      chapterCounts[ch]++;
      senderCounts[q.sender]++;
    }
  }
  // If we still have room, fill with top remaining (still 1 per chapter)
  if (bestQuotes.length < 5) {
    const picked = new Set(bestQuotes.map(q => q.message));
    for (const q of sorted) {
      if (bestQuotes.length >= 5) break;
      const ch = q.chapter || 'unknown';
      if (!picked.has(q.message) && (chapterCounts[ch] || 0) < 1 && (senderCounts[q.sender] || 0) < 3) {
        bestQuotes.push(q);
        picked.add(q.message);
        chapterCounts[ch] = (chapterCounts[ch] || 0) + 1;
        senderCounts[q.sender] = (senderCounts[q.sender] || 0) + 1;
      }
    }
  }

  // --- Quote Wall: random featured quote (from top tier) ---
  const topQuotes = quoteCandidates.sort((a, b) => b.score - a.score).slice(0, Math.max(10, Math.ceil(quoteCandidates.length * 0.2)));
  const featuredQuote = topQuotes.length > 0
    ? topQuotes[hashStr(JSON.stringify(topQuotes.map(q => q.message).join(''))) % topQuotes.length]
    : null;

  // Only bail to the bare placeholder when there is truly nothing to draw —
  // with ≥2 cast members the constellation bones render below from minute one.
  if (messages.length === 0 && castCapped.length < 2) {
    return (
      <div style={{ textAlign: 'center', padding: '40px 20px', color: THEME.textMuted }}>
        <div style={{ fontSize: '2em', marginBottom: '8px' }}>🫖</div>
        <div style={{ fontSize: '0.9em' }}>The tea is brewing! Check back once the cast arrives.</div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', marginBottom: '20px' }}>

      {/* 🎬 THE STORY SO FAR (Nina 08-04, POLL_ENACTMENT spec §4): the space's
          comics as its storybook, first thing you see on the Tea. Same strip
          that lived mid-tab as "Cut Scenes" since T2 — retitled + promoted
          now that comics actually fire (HEM arc). */}
      {cutsceneCards.length > 0 && (
        <div>
          <div style={{
            fontSize: '0.7em', fontWeight: 700, color: THEME.textMuted,
            textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '10px', padding: '0 4px',
            display: 'flex', alignItems: 'center', gap: '6px',
          }}>
            <span>🎬</span> The story so far
          </div>
          <div style={{ display: 'flex', gap: '10px', overflowX: 'auto', paddingBottom: '10px', scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
            {cutsceneCards.map(cs => (
              <div
                key={cs.id}
                onClick={() => window.open(cs.url, '_blank', 'noopener')}
                title="Tap to see it big!"
                style={{
                  // 230 → 300 (Nina 08-07): a 3-panel comic strip at 230px
                  // rendered each panel ~70px wide — too small to read the
                  // scene in the Recap lane without opening it.
                  flexShrink: 0, width: 300, cursor: 'pointer',
                  background: '#0d0d14', border: '2px solid #c9a22766',
                  borderRadius: '10px', overflow: 'hidden',
                  boxShadow: '0 5px 16px rgba(0,0,0,0.3)',
                }}
              >
                <img src={cs.url} alt="" loading="lazy" style={{ width: '100%', aspectRatio: '16/9', objectFit: 'cover', display: 'block' }} />
                <div style={{ padding: '6px 9px 8px' }}>
                  <div style={{ fontSize: '0.56em', fontWeight: 800, letterSpacing: '1.5px', color: '#ffd54f', textTransform: 'uppercase' }}>
                    🎬 Cut scene · {new Date(cs.at).toLocaleDateString()}
                  </div>
                  {cs.caption && (
                    <div style={{ fontSize: '0.72em', color: THEME.textMuted, marginTop: '3px', lineHeight: 1.4, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                      {cs.caption}
                    </div>
                  )}
                  <TeaReactionRow card={cs} />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 🫖 THE TEA — breaking gossip headline */}
      {teaHeadline && (
        <div style={{
          background: 'linear-gradient(135deg, rgba(236,64,122,0.14), rgba(126,87,194,0.14))',
          border: '1px solid rgba(236,64,122,0.35)',
          borderRadius: '12px',
          padding: '16px 20px',
        }}>
          <div style={{
            fontSize: '0.68em', fontWeight: 800, color: '#ec407a',
            textTransform: 'uppercase', letterSpacing: '2px', marginBottom: '8px',
            display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap',
          }}>
            <span>🫖 The Tea</span>
            {teaHeadline.statusChanged && (
              <span style={{ backgroundColor: 'rgba(236,64,122,0.18)', padding: '2px 8px', borderRadius: '4px' }}>
                🚨 Status change
              </span>
            )}
          </div>
          <div style={{ fontSize: '1.02em', fontWeight: 700, fontStyle: 'italic', color: THEME.textOnPanel || THEME.textDark, lineHeight: 1.45 }}>
            {teaHeadline.latest.headline}
          </div>
          {teaHeadline.latest.receipt && (
            <div style={{ fontSize: '0.75em', color: THEME.textMuted, marginTop: '8px' }}>
              📸 receipt: “{teaHeadline.latest.receipt}”
            </div>
          )}
          <div style={{ fontSize: '0.72em', color: THEME.textMuted, marginTop: '8px', fontWeight: 600 }}>
            {teaHeadline.shipName ? `${teaHeadline.shipName} · ` : ''}{teaHeadline.nameA} ✕ {teaHeadline.nameB}
            {teaHeadline.latest.roundId && <span style={{ opacity: 0.6 }}> • {getChapterLabel(teaHeadline.latest.roundId)}</span>}
          </div>
        </div>
      )}

      {/* 🏆 TOP TEA THIS WEEK — most-reacted moment, pinned (Tea T4, 07-15) */}
      {topTeaCard && (
        <div style={{
          background: 'linear-gradient(135deg, rgba(255,213,79,0.14), rgba(255,152,0,0.08))',
          border: '1.5px solid rgba(255,213,79,0.45)',
          borderRadius: '12px', padding: '10px 14px',
        }}>
          <div style={{ fontSize: '0.62em', fontWeight: 800, letterSpacing: '1.5px', color: '#ffd54f', textTransform: 'uppercase' }}>
            🏆 Top tea this week · {topTeaCard.n} reactions
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '6px' }}>
            {topTeaCard.card.url && (
              <img src={topTeaCard.card.url} alt="" style={{ width: 74, height: 42, objectFit: 'cover', borderRadius: '6px', flexShrink: 0 }} />
            )}
            <div style={{ fontSize: '0.85em', fontWeight: 700, color: THEME.textOnPanel || THEME.textDark, lineHeight: 1.35 }}>
              {topTeaCard.card.title || topTeaCard.card.caption}
            </div>
          </div>
        </div>
      )}

      {/* 📖 THE STORY SO FAR — persistent chapter archive (Tea T1, 07-13) */}
      {chapterCards.length > 0 && (
        <div>
          <div style={{
            fontSize: '0.7em', fontWeight: 700, color: THEME.textMuted,
            textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '10px', padding: '0 4px',
            display: 'flex', alignItems: 'center', gap: '6px',
          }}>
            <span>📖</span> The Story So Far
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {(showAllChapters ? chapterCards : chapterCards.slice(0, 5)).map(ch => (
              <div key={ch.id} style={{
                background: 'rgba(126,87,194,0.10)',
                border: '1px solid rgba(126,87,194,0.30)',
                borderRadius: '10px',
                padding: '10px 14px',
              }}>
                <div style={{ fontSize: '0.64em', fontWeight: 800, letterSpacing: '1.5px', color: '#b39ddb', textTransform: 'uppercase' }}>
                  {ch.num ? `Chapter ${ch.num}` : 'Chapter'} · {new Date(ch.at).toLocaleDateString()}
                </div>
                <div style={{ fontSize: '0.95em', fontWeight: 700, color: THEME.textOnPanel || THEME.textDark, marginTop: '2px' }}>
                  {ch.title}
                </div>
                {ch.intro && (
                  <div style={{ fontSize: '0.78em', fontStyle: 'italic', color: THEME.textMuted, marginTop: '4px', lineHeight: 1.45 }}>
                    “{ch.intro}”
                  </div>
                )}
                <TeaReactionRow card={ch} />
              </div>
            ))}
          </div>
          {chapterCards.length > 5 && (
            <button
              onClick={() => setShowAllChapters(v => !v)}
              style={{
                marginTop: '8px', border: '1px solid rgba(126,87,194,0.4)', borderRadius: 999,
                background: 'transparent', color: '#b39ddb', padding: '4px 14px',
                fontSize: '0.72em', fontWeight: 700, cursor: 'pointer',
              }}
            >
              {showAllChapters ? 'Show less' : `All ${chapterCards.length} chapters`}
            </button>
          )}
        </div>
      )}

      {/* 🎬 CUT SCENES — comic archive (Tea T2, 07-14). Gold letterbox frames
          echo the in-chat theater card; strip scrolls inside its own container. */}
      {/* (🎬 comic strip moved to the TOP of the Tea — Nina 08-04, spec §4) */}

      {/* 📇 THE RECORD BOOK — first discoveries + poll outcomes (Tea T2, 07-15) */}
      {recordBook.length > 0 && (
        <div>
          <div style={{
            fontSize: '0.7em', fontWeight: 700, color: THEME.textMuted,
            textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '10px', padding: '0 4px',
            display: 'flex', alignItems: 'center', gap: '6px',
          }}>
            <span>📇</span> The Record Book
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
            {recordBook.slice(0, 8).map(r => (
              <div key={r.id} style={{
                display: 'flex', alignItems: 'baseline', gap: '8px',
                background: 'rgba(255,255,255,0.04)', border: `1px solid ${THEME.accent}18`,
                borderRadius: '8px', padding: '7px 11px',
              }}>
                <span style={{ fontSize: '0.9em', flexShrink: 0 }}>{r.emoji}</span>
                <span style={{ fontSize: '0.78em', color: THEME.textOnPanel || THEME.textDark, lineHeight: 1.4, flex: 1 }}>{r.text}</span>
                <span style={{ fontSize: '0.62em', color: THEME.textMuted, flexShrink: 0 }}>{new Date(r.at).toLocaleDateString()}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 🎙️ "Your Host" polaroid section REMOVED (Nina 08-04): the host
          already leads the Locals tab — the Tea copy was a duplicate. Their
          bio popup lives on there. */}

      {/* 💘 The Ships — constellation map (default at ≥3 characters) or drama cards.
          Before round_analysis data exists, heuristic who-talks-to-whom pairs render
          on the same constellation (map-only) as the Relationship Web — and before
          even that, the full cast renders as unlinked stars (the bones). */}
      {(displayShips.length > 0 || castCapped.length >= 2) && (
        <div>
          <div style={{
            fontSize: '0.7em', fontWeight: 700, color: THEME.textMuted,
            textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '10px', padding: '0 4px',
            display: 'flex', alignItems: 'center', gap: '6px',
          }}>
            <span>{ships.length > 0 ? '💘' : '💫'}</span> {ships.length > 0 ? 'The Ships' : 'Relationship Web'}
            {ships.length > 0 && constellationEligible && (
              <span style={{ marginLeft: 'auto', display: 'flex', gap: '4px' }}>
                {[{ v: 'map', label: '🕸️ Map' }, { v: 'cards', label: '🃏 Cards' }].map(o => (
                  <button
                    key={o.v}
                    onClick={() => setShipsView(o.v)}
                    style={{
                      fontSize: '0.92em', fontWeight: 700, letterSpacing: 'normal', textTransform: 'none',
                      padding: '3px 9px', borderRadius: '10px', cursor: 'pointer',
                      backgroundColor: shipsView === o.v ? `${THEME.accent}28` : 'transparent',
                      color: shipsView === o.v ? (THEME.textOnPanel || THEME.textDark) : THEME.textMuted,
                      border: `1px solid ${THEME.accent}${shipsView === o.v ? '55' : '25'}`,
                    }}
                  >{o.label}</button>
                ))}
              </span>
            )}
          </div>
          {shipsView === 'map' && (
            <>
              <ShipConstellation
                ships={displayShips}
                cast={castCapped}
                getStatusMeta={getStatusMeta}
                THEME={THEME}
                onSelectCast={setSelectedCastPid}
                onSelectShip={setSelectedShipKey}
              />
              {/* Latest moments — the beats the map is flagging, tappable into the ship card */}
              {shipMoments.length > 0 && (
                <div style={{ maxWidth: '440px', margin: '8px auto 0', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  {shipMoments.map((m, i) => (
                    <div key={i} onClick={() => setSelectedShipKey(m.ship.key)}
                      style={{ cursor: 'pointer', fontSize: '0.72em', color: THEME.textMuted, display: 'flex', gap: '6px', alignItems: 'baseline', minWidth: 0 }}>
                      <span style={{ flexShrink: 0 }}>{m.badge}</span>
                      <span style={{ fontWeight: 700, color: THEME.textOnPanel || THEME.textDark, flexShrink: 0 }}>
                        {m.ship.shipName || `${m.ship.nameA} ✕ ${m.ship.nameB}`}
                      </span>
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.ship.latest.headline}</span>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
          {/* Feature-first mosaic: top ship gets the big card, the rest pack into a 2-col grid */}
          {/* 📱 `1fr` is minmax(auto, 1fr) — floored by the cell's min-content,
              and each ship cell holds a 120px-min meter + a 46px avatar stack +
              a nowrap status pill ("will they won't they"). That floors each
              track at 320-490px inside a ~330px phone card, so the grid pushed
              out past its panel. minmax(0, 1fr) lets tracks actually shrink;
              auto-fit collapses to ONE column when two won't fit. */}
          <div style={{
            display: shipsView === 'map' ? 'none' : 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(min(240px, 100%), 1fr))',
            gap: '10px',
          }}>
            {ships.map((ship, shipIdx) => {
              const isFeature = shipIdx === 0;
              const meta = getStatusMeta(ship.latest.status);
              const dAff = ship.prev ? ship.latest.affinity - ship.prev.affinity : 0;
              const dTen = ship.prev ? ship.latest.tension - ship.prev.tension : 0;
              const renderDelta = (d) => d !== 0 && (
                <span style={{ fontSize: '0.85em', fontWeight: 700, color: d > 0 ? '#66bb6a' : '#ef5350' }}>
                  {d > 0 ? `▲+${d}` : `▼${d}`}
                </span>
              );
              // minWidth was a hard 120px — one of the floors that stopped the
              // ships grid shrinking on a phone; 0 lets the track actually shrink
              const renderMeter = (emoji, label, value, delta, fillColor) => (
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flex: 1, minWidth: 0 }}>
                  <span style={{ fontSize: '0.75em', flexShrink: 0 }}>{emoji}</span>
                  {/* Label only where there's room — compact grid cards stay icon-only */}
                  {isFeature && (
                    <span style={{ fontSize: '0.56em', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: THEME.textMuted, flexShrink: 0 }}>{label}</span>
                  )}
                  <div style={{ flex: 1, height: '6px', borderRadius: '3px', backgroundColor: `${THEME.accent}15`, overflow: 'hidden' }}>
                    <div style={{ width: `${value * 10}%`, height: '100%', borderRadius: '3px', backgroundColor: fillColor, transition: 'width 0.6s ease' }} />
                  </div>
                  <span style={{ fontSize: '0.72em', fontWeight: 700, color: THEME.textOnPanel || THEME.textDark, minWidth: '18px', textAlign: 'right' }}>{value}</span>
                  {renderDelta(delta)}
                </div>
              );
              const spark = (isFeature && ship.entries.length >= 2) ? (() => {
                const w = 96, h = 24, n = ship.entries.length;
                const x = (i) => (i / (n - 1)) * (w - 4) + 2;
                const y = (v) => h - 3 - (v / 10) * (h - 6);
                return (
                  <svg width={w} height={h} style={{ flexShrink: 0, opacity: 0.9 }}>
                    <polyline points={ship.entries.map((e, i) => `${x(i)},${y(e.affinity)}`).join(' ')} fill="none" stroke="#ec407a" strokeWidth="1.5" strokeLinejoin="round" />
                    <polyline points={ship.entries.map((e, i) => `${x(i)},${y(e.tension)}`).join(' ')} fill="none" stroke="#ff7043" strokeWidth="1.5" strokeLinejoin="round" />
                  </svg>
                );
              })() : null;
              return (
                <div key={ship.key} style={{
                  gridColumn: isFeature ? 'span 2' : 'span 1',
                  padding: isFeature ? '16px 16px 14px' : '10px 10px 9px',
                  borderRadius: '12px', position: 'relative',
                  backgroundColor: THEME.bgLight || THEME.bgMedium,
                  border: isFeature ? `1.5px solid ${meta.color}70` : `1px solid ${meta.color}40`,
                  boxShadow: isFeature ? `0 4px 18px ${meta.color}20` : 'none',
                  display: 'flex', flexDirection: 'column', gap: '8px',
                }}>
                  {isFeature && ships.length > 1 && (
                    <span style={{
                      position: 'absolute', top: '-1px', right: '14px',
                      fontSize: '0.55em', fontWeight: 800, letterSpacing: '1px', textTransform: 'uppercase',
                      padding: '3px 9px', borderRadius: '0 0 8px 8px',
                      backgroundColor: meta.color, color: '#fff',
                    }}>🔥 Top Ship</span>
                  )}
                  {/* Header: avatars + ship name + status badge */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                      <div style={{
                        width: '28px', height: '28px', borderRadius: '50%', overflow: 'hidden',
                        backgroundColor: `${THEME.accent}25`, display: 'flex', alignItems: 'center', justifyContent: 'center',
                        border: `2px solid ${THEME.bgLight || THEME.bgMedium}`, position: 'relative', zIndex: 1,
                      }}>
                        {ship.imgA
                          ? <img src={ship.imgA} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }} />
                          : <span style={{ fontSize: '0.7em', fontWeight: 700, color: THEME.accent }}>{(ship.nameA || '?')[0]}</span>
                        }
                      </div>
                      <div style={{
                        width: '28px', height: '28px', borderRadius: '50%', overflow: 'hidden',
                        backgroundColor: `${THEME.accent}25`, display: 'flex', alignItems: 'center', justifyContent: 'center',
                        border: `2px solid ${THEME.bgLight || THEME.bgMedium}`, marginLeft: '-10px',
                      }}>
                        {ship.imgB
                          ? <img src={ship.imgB} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }} />
                          : <span style={{ fontSize: '0.7em', fontWeight: 700, color: THEME.accent }}>{(ship.nameB || '?')[0]}</span>
                        }
                      </div>
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: '0.92em', fontWeight: 800, color: THEME.textOnPanel || THEME.textDark, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {ship.shipName || `${ship.nameA} ✕ ${ship.nameB}`}
                      </div>
                      <div style={{ fontSize: '0.65em', color: THEME.textMuted }}>{ship.nameA} ✕ {ship.nameB}</div>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '3px', flexShrink: 0 }}>
                      {ship.latest.status && (
                        <span style={{
                          fontSize: '0.68em', fontWeight: 700, padding: '3px 8px', borderRadius: '10px',
                          backgroundColor: `${meta.color}22`, color: meta.color, border: `1px solid ${meta.color}44`,
                          whiteSpace: 'nowrap',
                        }}>
                          {meta.emoji} {ship.latest.status}
                        </span>
                      )}
                      {ship.statusChanged && (
                        <span style={{ fontSize: '0.6em', fontWeight: 700, color: '#ec407a' }}>
                          🚨 was {ship.prev.status}
                        </span>
                      )}
                    </div>
                  </div>
                  {/* Meters + sparkline */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                    {renderMeter('💗', 'closeness', ship.latest.affinity, dAff, '#ec407a')}
                    {renderMeter('🔥', 'tension', ship.latest.tension, dTen, '#ff7043')}
                    {spark}
                  </div>
                  {/* Gossip — feature card only; compact cards stay scannable */}
                  {isFeature && ship.latest.headline && (
                    <div style={{ fontSize: '0.82em', fontStyle: 'italic', color: THEME.textOnPanel || THEME.textDark, lineHeight: 1.4 }}>
                      {ship.latest.headline}
                    </div>
                  )}
                  {isFeature && ship.latest.receipt && (
                    <div style={{ fontSize: '0.7em', color: THEME.textMuted }}>
                      📸 “{ship.latest.receipt}”
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Pull-quote — quiet magazine treatment; hidden until the space has
          enough messages that the pick is actually a highlight */}
      {featuredQuote && messages.length >= 20 && (
        <div style={{ position: 'relative', padding: '18px 26px', textAlign: 'center' }}>
          <div style={{
            position: 'absolute', top: '-8px', left: '50%', transform: 'translateX(-50%)',
            fontSize: '4.5em', lineHeight: 1, color: `${THEME.accent}22`,
            fontFamily: 'Georgia, serif', pointerEvents: 'none', userSelect: 'none',
          }}>“</div>
          <div style={{ height: '1px', background: `linear-gradient(90deg, transparent, ${THEME.accent}45, transparent)`, marginBottom: '14px' }} />
          <div style={{
            fontSize: '1em', fontStyle: 'italic', lineHeight: 1.6,
            color: THEME.textOnPanel || THEME.textDark, position: 'relative',
          }}>
            {featuredQuote.message.length > 170 ? featuredQuote.message.slice(0, 170) + '…' : featuredQuote.message}
          </div>
          <div style={{
            marginTop: '10px', fontSize: '0.72em', fontWeight: 700, letterSpacing: '1px',
            textTransform: 'uppercase', color: THEME.textMuted,
          }}>
            — {featuredQuote.sender}
            {featuredQuote.chapter && <span style={{ opacity: 0.55, textTransform: 'none', letterSpacing: 'normal', fontWeight: 500 }}> · {featuredQuote.roundId ? getChapterLabel(featuredQuote.roundId) : `Chapter ${featuredQuote.chapter}`}</span>}
          </div>
          <div style={{ height: '1px', background: `linear-gradient(90deg, transparent, ${THEME.accent}45, transparent)`, marginTop: '14px' }} />
        </div>
      )}

      {/* Chapter Guide — show AI-generated chapter titles */}
      {Object.keys(roundIdToTitle).length > 0 && (
        <div style={{
          borderRadius: '10px',
          border: `1px solid ${THEME.accent}20`,
          backgroundColor: `${THEME.accent}08`,
          padding: '14px 16px',
        }}>
          <div style={{ fontSize: '0.82em', fontWeight: 700, color: THEME.textOnPanel || THEME.textDark, marginBottom: '10px', letterSpacing: '0.3px' }}>
            📖 Chapter Guide
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            {sortedRoundIds.map((rid, i) => {
              const title = roundIdToTitle[rid];
              if (!title) return null;
              return (
                <div key={rid} style={{ display: 'flex', alignItems: 'baseline', gap: '8px', fontSize: '0.85em' }}>
                  <span style={{ color: THEME.accent, fontWeight: 700, minWidth: '28px' }}>{i + 1}.</span>
                  <span style={{ color: THEME.textOnPanel || THEME.textDark, fontStyle: 'italic' }}>{title}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Host bio popup removed with its polaroid trigger (08-04) — the same
          story lives behind the Locals host card / mod panel ⓘ. */}

      {/* Character card popup — opened by tapping a Cast portrait (replaces the
          old standalone "Character Cards with Vibe Check" strip) */}
      {selectedCastPid && (
        <CastCardModal
          pid={selectedCastPid}
          users={users}
          messages={messages}
          awards={awardsByPid[selectedCastPid] || []}
          ships={displayShips}
          getStatusMeta={getStatusMeta}
          getModelName={getDisplayModelName}
          gameId={gameId}
          humanPlayerId={humanPlayerId}
          THEME={THEME}
          onClose={() => setSelectedCastPid(null)}
        />
      )}

      {/* Ship detail popup — opened from a constellation edge chip */}
      {selectedShipKey && (() => {
        const ship = displayShips.find(s => s.key === selectedShipKey);
        if (!ship) return null;
        const meta = getStatusMeta(ship.latest.status);
        const dAff = ship.prev ? ship.latest.affinity - ship.prev.affinity : 0;
        const dTen = ship.prev ? ship.latest.tension - ship.prev.tension : 0;
        const renderDelta = (d) => d !== 0 && (
          <span style={{ fontSize: '0.85em', fontWeight: 700, color: d > 0 ? '#66bb6a' : '#ef5350' }}>
            {d > 0 ? `▲+${d}` : `▼${d}`}
          </span>
        );
        const renderAvatar = (img, name, z) => (
          <div style={{
            width: '38px', height: '38px', borderRadius: '50%', overflow: 'hidden',
            backgroundColor: `${THEME.accent}25`, display: 'flex', alignItems: 'center', justifyContent: 'center',
            border: `2px solid ${meta.color}70`, position: 'relative', zIndex: z, marginLeft: z === 1 ? 0 : '-12px',
          }}>
            {img
              ? <img src={img} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }} />
              : <span style={{ fontSize: '0.85em', fontWeight: 700, color: THEME.accent }}>{(name || '?')[0]}</span>
            }
          </div>
        );
        return (
          <div
            onClick={() => setSelectedShipKey(null)}
            style={{
              position: 'fixed', inset: 0, zIndex: 3200,
              backgroundColor: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(2px)', WebkitBackdropFilter: 'blur(2px)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px',
            }}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                width: '340px', maxWidth: '92vw', maxHeight: '82dvh', overflowY: 'auto',
                borderRadius: '16px', position: 'relative', padding: '18px 18px 16px',
                backgroundColor: THEME.bgMedium || '#1a1a2e',
                border: `1.5px solid ${meta.color}70`,
                boxShadow: `0 12px 48px rgba(0,0,0,0.6), 0 0 24px ${meta.color}25`,
                display: 'flex', flexDirection: 'column', gap: '10px',
              }}
            >
              <button
                onClick={() => setSelectedShipKey(null)}
                aria-label="Close ship card"
                style={{
                  position: 'absolute', top: '10px', right: '12px', zIndex: 2,
                  background: 'rgba(0,0,0,0.4)', border: 'none', borderRadius: '50%',
                  width: '26px', height: '26px', color: '#fff', cursor: 'pointer',
                  fontSize: '0.85em', lineHeight: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}
              >✕</button>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', paddingRight: '26px' }}>
                <div style={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                  {renderAvatar(ship.imgA, ship.nameA, 1)}
                  {renderAvatar(ship.imgB, ship.nameB, 0)}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: '1em', fontWeight: 800, color: THEME.textOnPanel || THEME.textDark }}>
                    {ship.shipName || `${ship.nameA} ✕ ${ship.nameB}`}
                  </div>
                  {/* Only when a ship name hides the pair — otherwise the header
                      already IS the names (crowding, user report 07-10) */}
                  {ship.shipName && (
                    <div style={{ fontSize: '0.68em', color: THEME.textMuted }}>{ship.nameA} ✕ {ship.nameB}</div>
                  )}
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                {ship.latest.status && (
                  <span style={{
                    fontSize: '0.72em', fontWeight: 700, padding: '3px 9px', borderRadius: '10px',
                    backgroundColor: `${meta.color}22`, color: meta.color, border: `1px solid ${meta.color}44`,
                  }}>{meta.emoji} {ship.latest.status}</span>
                )}
                {ship.statusChanged && (
                  <span style={{ fontSize: '0.64em', fontWeight: 700, color: '#ec407a' }}>🚨 was {ship.prev.status}</span>
                )}
              </div>
              {/* Labeled — the bare 💗/🔥 icons read as decoration, not meters
                  (user report 07-10) */}
              {[
                { emoji: '💗', label: 'closeness', value: ship.latest.affinity, delta: dAff, fill: '#ec407a' },
                { emoji: '🔥', label: 'tension', value: ship.latest.tension, delta: dTen, fill: '#ff7043' },
              ].map((m, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ fontSize: '0.8em', flexShrink: 0 }}>{m.emoji}</span>
                  <span style={{ fontSize: '0.58em', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: THEME.textMuted, width: '58px', flexShrink: 0 }}>{m.label}</span>
                  <div style={{ flex: 1, height: '7px', borderRadius: '4px', backgroundColor: `${THEME.accent}15`, overflow: 'hidden' }}>
                    <div style={{ width: `${m.value * 10}%`, height: '100%', borderRadius: '4px', backgroundColor: m.fill, transition: 'width 0.6s ease' }} />
                  </div>
                  <span style={{ fontSize: '0.76em', fontWeight: 700, color: THEME.textOnPanel || THEME.textDark, minWidth: '18px', textAlign: 'right' }}>{m.value}</span>
                  {renderDelta(m.delta)}
                </div>
              ))}
              {ship.entries.length >= 2 && (() => {
                const w = 280, h = 44, ne = ship.entries.length;
                const x = (i) => (i / (ne - 1)) * (w - 6) + 3;
                const y = (v) => h - 4 - (v / 10) * (h - 8);
                return (
                  <svg width="100%" height={h} viewBox={`0 0 ${w} ${h}`} style={{ opacity: 0.9 }}>
                    <polyline points={ship.entries.map((e, i) => `${x(i)},${y(e.affinity)}`).join(' ')} fill="none" stroke="#ec407a" strokeWidth="1.5" strokeLinejoin="round" />
                    <polyline points={ship.entries.map((e, i) => `${x(i)},${y(e.tension)}`).join(' ')} fill="none" stroke="#ff7043" strokeWidth="1.5" strokeLinejoin="round" />
                  </svg>
                );
              })()}
              {ship.latest.headline && (
                <div style={{ fontSize: '0.85em', fontStyle: 'italic', color: THEME.textOnPanel || THEME.textDark, lineHeight: 1.45 }}>
                  {ship.latest.headline}
                </div>
              )}
              {ship.latest.receipt ? (
                <div style={{ fontSize: '0.72em', color: THEME.textMuted }}>📸 “{ship.latest.receipt}”</div>
              ) : (() => {
                // No AI receipt yet (early "sparks" pairs): quote their latest real
                // exchange from the chat instead — most recent spot where the two
                // voices alternate.
                const pidOf = (nm) => { const u = findUserByName(nm); return u ? (u.game_player_id || u.id) : null; };
                const pidA = pidOf(ship.nameA), pidB = pidOf(ship.nameB);
                if (!pidA || !pidB) return null;
                const pairIds = new Set([pidA, pidB]);
                const ordered = messages
                  .filter(a => pairIds.has(a.game_instance_player_id) && a.data?.message)
                  .sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
                let exchange = null;
                for (let i = ordered.length - 1; i >= 1; i--) {
                  if (ordered[i].game_instance_player_id !== ordered[i - 1].game_instance_player_id) {
                    exchange = [ordered[i - 1], ordered[i]];
                    break;
                  }
                }
                if (!exchange) return null;
                const nameOf = (a) => a.game_instance_player_id === pidA ? ship.nameA : ship.nameB;
                const clip = (s) => s.length > 90 ? s.slice(0, 88) + '…' : s;
                return (
                  <div style={{ fontSize: '0.72em', color: THEME.textMuted, display: 'flex', flexDirection: 'column', gap: '3px' }}>
                    {exchange.map((a, i) => (
                      <div key={i}>
                        📸 <span style={{ fontWeight: 700, color: THEME.textOnPanel || THEME.textDark }}>{nameOf(a)}:</span> “{clip(a.data.message)}”
                      </div>
                    ))}
                  </div>
                );
              })()}
              {ship.latest.roundId && (
                <div style={{ fontSize: '0.66em', color: THEME.textMuted, opacity: 0.75 }}>{getChapterLabel(ship.latest.roundId)}</div>
              )}
              {/* 🫖 T3 event timeline: the /ship + /drama receipts for this pair */}
              {(() => {
                const evKey = [ship.nameA, ship.nameB].map(n => (n || '').toLowerCase().trim()).sort().join('::');
                const events = shipEventsByPair[evKey] || [];
                if (!events.length) return null;
                return (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '3px', paddingTop: '4px', borderTop: `1px solid ${THEME.accent}20` }}>
                    <div style={{ fontSize: '0.56em', fontWeight: 800, letterSpacing: '1px', color: THEME.textMuted, textTransform: 'uppercase' }}>How it started</div>
                    {events.slice(-4).map(ev => (
                      <div key={ev.id} style={{ fontSize: '0.7em', color: THEME.textMuted }}>
                        {ev.kind === 'ship' ? '💘' : '🌶️'} {ev.kind === 'ship' ? 'shipped' : 'stirred'} by <span style={{ fontWeight: 700, color: THEME.textOnPanel || THEME.textDark }}>{ev.by}</span>
                        <span style={{ opacity: 0.7 }}> · {new Date(ev.at).toLocaleDateString()}</span>
                      </div>
                    ))}
                  </div>
                );
              })()}
              {/* 💍/💔 Will they last? (Tea T3, Nina's copy call 07-15) */}
              {(() => {
                const pk = serverPairKey(ship.nameA, ship.nameB);
                const tally = tallyFor(ship.nameA, ship.nameB) || { last: 0, doom: 0 };
                const mine = myShipVotes[pk] || null;
                const btn = (vote, emoji, label, count) => (
                  <button
                    onClick={() => handleShipVote(ship.nameA, ship.nameB, vote)}
                    style={{
                      flex: 1, padding: '7px 8px', borderRadius: '10px', cursor: 'pointer',
                      border: mine === vote ? `2px solid ${vote === 'last' ? '#ec407a' : '#90a4ae'}` : `1px solid ${THEME.accent}30`,
                      background: mine === vote ? (vote === 'last' ? 'rgba(236,64,122,0.18)' : 'rgba(144,164,174,0.18)') : 'rgba(0,0,0,0.2)',
                      color: THEME.textOnPanel || THEME.textDark, fontWeight: 700, fontSize: '0.74em',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
                    }}
                  >
                    <span>{emoji}</span><span>{label}</span>
                    <span style={{ opacity: 0.75, fontWeight: 800 }}>{count}</span>
                    {mine === vote && <span>✓</span>}
                  </button>
                );
                return (
                  <div style={{ paddingTop: '6px', borderTop: `1px solid ${THEME.accent}20` }}>
                    <div style={{ fontSize: '0.62em', fontWeight: 800, letterSpacing: '1px', color: THEME.textMuted, textTransform: 'uppercase', marginBottom: '6px' }}>
                      Will they last?
                    </div>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      {btn('last', '💍', 'They will', tally.last)}
                      {btn('doom', '💔', 'Doomed', tally.doom)}
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>
        );
      })()}

      {/* Best Quotes — grouped by chapter */}
      {bestQuotes.length > 0 && messages.length >= 20 && (() => {
        // Group quotes by chapter number, maintaining order
        const quotesByChapter = {};
        bestQuotes.forEach(q => {
          const ch = q.chapter || 0;
          if (!quotesByChapter[ch]) quotesByChapter[ch] = [];
          quotesByChapter[ch].push(q);
        });
        const chapterNums = Object.keys(quotesByChapter).map(Number).sort((a, b) => a - b);

        return (
          <div>
            <div style={{
              fontSize: '0.85em',
              fontWeight: 700,
              color: THEME.textMuted,
              textTransform: 'uppercase',
              letterSpacing: '1px',
              marginBottom: '10px',
            }}>
              💎 Best Quotes
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              {chapterNums.map(chNum => {
                const quotes = quotesByChapter[chNum];
                const firstQuote = quotes[0];
                const chapterLabel = chNum > 0
                  ? (firstQuote?.roundId ? getChapterLabel(firstQuote.roundId) : `Chapter ${chNum}`)
                  : 'Misc';
                return (
                  <div key={chNum}>
                    <div style={{
                      fontSize: '0.75em', fontWeight: 700, color: THEME.accent,
                      marginBottom: '6px', letterSpacing: '0.3px',
                    }}>
                      {chapterLabel}
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      {quotes.map((q, i) => (
                        <div key={i} style={{
                          padding: '12px 14px',
                          maxWidth: '88%',
                          alignSelf: i % 2 === 0 ? 'flex-start' : 'flex-end',
                          borderRadius: i % 2 === 0 ? '14px 14px 14px 4px' : '14px 14px 4px 14px',
                          backgroundColor: i % 2 === 0 ? (THEME.bgLight || THEME.bgMedium) : `${THEME.accent}14`,
                          border: `1px solid ${THEME.accent}${i % 2 === 0 ? '25' : '35'}`,
                        }}>
                          <div style={{
                            fontSize: '0.9em',
                            color: THEME.textOnPanel || THEME.textDark,
                            lineHeight: 1.5,
                            marginBottom: '6px',
                          }}>
                            "{q.message.length > 180 ? q.message.slice(0, 180) + '...' : q.message}"
                          </div>
                          <div style={{ fontSize: '0.78em', color: THEME.textMuted, fontWeight: 600, display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <span>— {q.sender}{q.senderModel && <span style={{ fontWeight: 400, opacity: 0.7 }}> ({q.senderModel})</span>}</span>
                            {q.reactionCount > 0 && <span style={{ opacity: 0.7 }}>• ❤️ {q.reactionCount}</span>}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })()}
    </div>
  );
};

// Dice roll-reveal toast copy — one liner per band, picked deterministically (value + name
// length) so re-renders never reshuffle. Pointed on purpose: the dice are a character.
const DICE_TOAST_LINES = {
  'CRITICAL SUCCESS': [
    "NATURAL 20. The room just went quiet.",
    "A perfect 20?! Somebody's about to have a very bad day.",
    "CRITICAL. The dice have chosen violence — on their behalf.",
  ],
  'SUCCESS': [
    "Solid roll. Suspiciously solid.",
    "Comfortable. TOO comfortable.",
    "The dice approve. For now.",
  ],
  'COMPLICATION': [
    "Oof. That's not a number, that's a cry for help.",
    "The dice have introduced a complication. The dice are not sorry.",
    "That roll had ONE job.",
  ],
  'CRITICAL FAIL': [
    "OH. NO. Critical fail 💀 Bye bye, words.",
    "A 1. The dice didn't just say no. They pressed charges.",
    "Critical fail 💀 Vocabulary privileges: REVOKED.",
  ],
};
const DICE_TOAST_COLORS = {
  'CRITICAL SUCCESS': '#ffd54f',
  'SUCCESS': '#22c55e',
  'COMPLICATION': '#f59e0b',
  'CRITICAL FAIL': '#ef4444',
};

// Beat presenter — split a multi-part announcement into tap-through beats on its existing
// section markers (round openers, ⚡ act, 📖 opening line, 🌧️ chaos, 🚔 arrest, 🗂️ case
// file). Messages without markers stay a single beat, so this is safe for every game.
const BEAT_MARKERS = ['⚡', '📖', '🌧️', '🚔', '🗂️'];
function splitAnnouncementIntoBeats(message) {
  const sections = String(message).split(/\n\n+/).map(s => s.trim()).filter(Boolean);
  if (!sections.length) return [];
  const beats = [sections[0]];
  for (let i = 1; i < sections.length; i++) {
    const s = sections[i];
    if (BEAT_MARKERS.some(m => s.startsWith(m))) beats.push(s);
    else beats[beats.length - 1] += '\n\n' + s;
  }
  // Never drop content: fold anything past 4 beats into the last one.
  if (beats.length > 4) {
    const extra = beats.splice(4);
    beats[3] += '\n\n' + extra.join('\n\n');
  }
  return beats;
}

// 🚔 Is this action one of the slim noir ticker bulletins? (Shared by the
// component and its consecutive-run collapse, so both use one definition.)
// Mirrors the two guards below: not a competitive announcement, not a beat.
const isNoirTickerAction = (a) => {
  if (!a || a.type !== 'game_announcement') return false;
  const d = a.data || {};
  if (d.vote_results || d.eliminated_player || d.final_rankings || d.imposter_result) return false;
  if (d.beat === true || d.confession || d.twist || d.hospitalization) return false;
  return true;
};

const GameAnnouncementAction = ({ action, THEME, allActions = [], uiTerms = null, noirTheme = false, compactTicker = false }) => {
  const COMMON_STYLES = getCommonStyles(THEME);
  const { data } = action;
  // Noir ticker expand state — hook lives above every early return
  const [tickerOpen, setTickerOpen] = React.useState(false);
  // 🌙 W2b (treatment C): the asleep / wake card takes its own shape on every
  // form factor, with the page's honest line (residents read the fiction one)
  const powerCardView = powerCard(data);
  if (powerCardView) return <AiPowerCard card={powerCardView} time={formatActionTime(action.created_at)} theme={THEME} />;

  // Competitive announcements have vote_results, eliminated_player, or final_rankings
  const isCompetitiveAnnouncement = data.vote_results || data.eliminated_player || data.final_rankings || data.imposter_result;
  // Continuation announcements (non-comp rounds 2+) get subtle inline divider
  const isContinuation = data.is_continuation || (!isCompetitiveAnnouncement && /^Chapter \d+( has wrapped up\.)/.test(data.message || ''));

  // 🚔 Noir feed triage (Nina 07-25: "way too many bulletins — hard to know
  // what's important"): in competitive whodunnit, TESTIMONY is the only
  // full-size element. The dramatic card survives only for true beats (vote
  // results / eliminations / finale, or the tier-2 beat flags the popup gate
  // already honors); every other precinct line renders as ONE slim ticker
  // strip. Tap toggles the full text when there is more. The case file keeps
  // the archive, so the feed can afford to be quiet. Other game types and
  // non-noir competitive are untouched.
  const isNoirBeat = data.beat === true || !!data.confession || !!data.twist || !!data.hospitalization;
  // 📱 Chat-activity port (Nina 08-01): non-comp announcements borrow the
  // whodunnit TICKER on phones — the feed should read as chat, not bulletins.
  // Chapter continuations keep their divider (handled below); desktop
  // non-comp and all competitive treatments are untouched.
  const isNarrow = typeof window !== 'undefined' && window.innerWidth < 768;
  // 🎯 09-01: a directed-scenario card (data.is_directed, houseDirected) is
  // the beat itself — on phones it must not collapse into the tap-to-expand
  // ticker with its scenario hidden. It takes the labeled card on every
  // form factor.
  const compactHere = compactTicker && isNarrow && !isContinuation && data.is_directed !== true;
  // 🪶 08-31 (Nina: "the announcement look should change in every non-comp
  // space, not just the claude house"): the option-A restyle helpers are
  // hoisted ABOVE the mobile ticker branch, because phones route every
  // non-comp announcement through the ticker before the restyled card runs.
  // Ambience lines (the TV-drift class) get the quiet stage direction on
  // EVERY form factor and EVERY non-comp space; the ticker itself goes
  // emoji-free. Noir/competitive treatments stay byte-identical.
  const stripLeadEmoji = (s) => String(s || '')
    .replace(/^(?:[\u{1F000}-\u{1FFFF}\u{2600}-\u{27BF}\u{FE00}-\u{FE0F}\u{1F900}-\u{1F9FF}\u{200D}]|\s)+/u, '').trim();
  const ambienceLines = String(data.message || '').split('\n').map((l) => l.trim()).filter(Boolean);
  // 🪶 09-01 (Nina: watch-drop lines rendered as giant bold headlines in every
  // non-comp space): the backend now flags watch-lane rows `data.ambience`,
  // which wins regardless of length; the emoji+length heuristic stays only
  // for legacy rows, with the watch-lane heads (🍿 🎮 ▶️) added.
  const AMBIENCE_HEADS = ['📺', '🎶', '🌙', '🌅', '🍿', '🎮', '▶️'];
  const isAmbienceLine = !isCompetitiveAnnouncement && !isNoirBeat && !noirTheme && !isContinuation
    && ambienceLines.length === 1
    && (data.ambience === true
      || (AMBIENCE_HEADS.some((e) => ambienceLines[0].startsWith(e))
        && stripLeadEmoji(ambienceLines[0]).length <= 90));
  if (isAmbienceLine) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, margin: '14px 6px', padding: '0 8px' }}>
        <div style={{ flex: 1, minWidth: 8, height: 1, background: `${THEME.borderGold || THEME.accent}2e` }} />
        <span style={{ fontStyle: 'italic', fontSize: isNarrow ? '0.8em' : '0.86em', color: THEME.accentOnPanel || THEME.accent, opacity: 0.85, textAlign: 'center', minWidth: 0 }}>
          {stripLeadEmoji(ambienceLines[0])}
          <span style={{ fontSize: '0.72em', fontStyle: 'normal', color: THEME.textMuted, marginLeft: 8, whiteSpace: 'nowrap' }}>{formatActionTime(action.created_at)}</span>
        </span>
        <div style={{ flex: 1, minWidth: 8, height: 1, background: `${THEME.borderGold || THEME.accent}2e` }} />
      </div>
    );
  }
  if ((noirTheme || compactHere) && !isCompetitiveAnnouncement && !isNoirBeat) {
    // 📱 Mobile truncation fix (Nina 07-30, confirmed on a 375px probe render:
    // "PRECINCT BULLETIN CUSTARDIAN OF JU…"). The label was uppercase, letter-
    // spaced AND flexShrink:0, so it claimed over half the strip and the message
    // — the only part carrying information — ellipsized to nothing. On phones the
    // label collapses to its emoji and the message wraps to two lines instead.
    // Non-noir (every non-comp space): emoji-free per the 08-31 restyle — the
    // label is a small word, the line is stripped. Noir keeps its precinct chrome.
    const rawLabel = uiTerms?.announcement_label || (noirTheme ? '🚔 Bulletin' : 'note');
    const firstTok = rawLabel.trim().split(/\s+/)[0];
    const label = noirTheme
      ? (isNarrow ? (/[A-Za-z0-9]/.test(firstTok) ? '🚔' : firstTok) : rawLabel)
      : (stripLeadEmoji(rawLabel) || 'note');

    // 🚔 Consecutive-run collapse: back-to-back precinct lines each truncated to
    // nothing were unreadable AND indistinguishable from each other. Only the
    // LAST line of a run renders; it carries the earlier ones behind one tap.
    // Any other action type between two bulletins breaks the run, so narrative
    // order is never reordered. indexOf is reference equality on the same
    // allActions objects; -1 (not found) safely disables the collapse.
    const idx = allActions.indexOf(action);
    if (idx !== -1 && isNoirTickerAction(allActions[idx + 1])) return null;
    const earlier = [];
    for (let i = idx - 1; idx !== -1 && i >= 0 && isNoirTickerAction(allActions[i]); i--) {
      earlier.unshift((allActions[i].data?.message || '').trim());
    }

    const msg = (data.message || '').trim();
    // Non-noir strips the leading emoji (08-31 restyle); noir keeps its chrome.
    const firstLine = noirTheme ? msg.split('\n')[0] : (stripLeadEmoji(msg.split('\n')[0]) || msg.split('\n')[0]);
    const hasMore = earlier.length > 0 || msg.length > firstLine.length || firstLine.length > 160;
    const accent = THEME.accent || '#C9A84C';
    return (
      <div
        onClick={() => hasMore && setTickerOpen(o => !o)}
        style={{
          display: 'flex', alignItems: 'baseline', gap: '8px',
          margin: '6px 0', padding: '4px 10px',
          borderLeft: `3px solid ${accent}55`,
          background: `${accent}0d`,
          borderRadius: '0 6px 6px 0',
          cursor: hasMore ? 'pointer' : 'default',
        }}
      >
        <span style={{
          fontSize: '0.62em', fontWeight: 800, letterSpacing: isNarrow ? 0 : '1px',
          color: accent, textTransform: 'uppercase',
          whiteSpace: 'nowrap', flexShrink: 0, opacity: 0.75,
        }}>
          {label}
        </span>
        <span style={{
          flex: 1, minWidth: 0, fontSize: '0.78em',
          color: THEME.textMuted, lineHeight: 1.45,
          ...(tickerOpen
            ? { whiteSpace: 'pre-wrap' }
            // Two-line clamp instead of a one-line ellipsis: the message keeps
            // its room even when the label is present.
            : { display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }),
        }}>
          {tickerOpen
            ? [...earlier, msg].join('\n\n')
            : firstLine}
        </span>
        {hasMore && (
          <span style={{ fontSize: '0.62em', color: THEME.textMuted, flexShrink: 0, whiteSpace: 'nowrap' }}>
            {tickerOpen ? '▴' : (earlier.length ? `+${earlier.length} ▾` : '…▾')}
          </span>
        )}
      </div>
    );
  }

  // Look up AI-generated chapter title from round_analysis for this round
  const roundAnalysis = allActions.find(a => a.type === 'round_analysis' && a.game_instance_round_id === action.game_instance_round_id);
  const chapterTitle = roundAnalysis?.data?.chapter_title;

  if (!isCompetitiveAnnouncement && isContinuation) {
    // Build display text: replace generic message with AI chapter title if available
    const chapterMatch = (data.message || '').match(/^Chapter (\d+)/);
    // 📖 09-29 chapter titles (Nina): a closer with its AI title reads "End of
    // Chapter N: <title>". It sits right above the next chapter's opener, so a
    // bare "Chapter N: <title>" read like the name of the chapter below it.
    const isChapterCloser = /^Chapter \d+ has wrapped up\./.test(data.message || '');
    const displayText = chapterTitle && chapterMatch
      ? `${isChapterCloser ? 'End of ' : ''}Chapter ${chapterMatch[1]}: ${chapterTitle}`
      : (data.message || 'Announcement');

    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        margin: '16px 0',
        padding: '0 8px',
      }}>
        <div style={{ flex: 1, minWidth: 8, height: '1px', backgroundColor: `${THEME.accent || '#6366f1'}30` }} />
        <span style={{
          color: THEME.textMuted,
          fontSize: '0.82em',
          fontWeight: 600,
          // Was nowrap with no overflow rule, so the label's min size stayed at
          // max-content: a long AI-generated chapter title ran off the screen
          // while the two hairlines collapsed to zero. Cap it and ellipsize;
          // the rules keep a visible stub either side (minWidth above).
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          minWidth: 0,
          maxWidth: 'calc(100% - 48px)',
          letterSpacing: '0.3px',
        }}>
          {displayText}
        </span>
        <div style={{ flex: 1, height: '1px', backgroundColor: `${THEME.accent || '#6366f1'}30` }} />
      </div>
    );
  }

  // 🪶 Plain text announcements (Nina 08-03, mock option B): the double-framed
  // poster is RETIRED for ordinary announcements — they render as a centered
  // divider strip, rhyming with the chapter divider above. Everything that
  // earned the theatre keeps it: noir beats, competitive result cards, and
  // any announcement carrying imagery (portrait / scene comic). Phones never
  // reach here for these (the slim ticker returns earlier).
  const plainTextOnly = !isCompetitiveAnnouncement && !isNoirBeat
    && !data.fullbody_image_with_nickname_url && !data.fullbody_image_url
    && !data.intro_image_url && !data.scene_comic_url;
  if (plainTextOnly) {
    // Desktop was a uniform ~0.88em blob, tiny on a wide viewport (Nina 08-28).
    // Split the message so an emoji-leading line (⚡ TWIST NAME, 📖 Opening line,
    // 🌩️ Chaos, 🔥 THE ASSIGNMENT) reads as a headline and the rest as body, and
    // scale both up on desktop while keeping phones compact.
    const emojiHead = /^[\u{1F000}-\u{1FFFF}\u{2600}-\u{27BF}\u{FE00}-\u{FE0F}\u{1F900}-\u{1F9FF}]/u;
    // 🪶 08-30/31 restyle (mock option A): ambience lines were caught by the
    // hoisted early-return above (every non-comp space, every form factor);
    // this branch is the labeled tier-2 card, emoji-free — stripLeadEmoji is
    // the hoisted helper. Backend keeps emitting emojis (cheap semantics in
    // prompts + transcripts, and the emoji IS the ambience classifier), so
    // historical rows clean up for free with zero data migration.
    const headSize = isNarrow ? '1.02em' : '1.3em';
    const bodySize = isNarrow ? '0.9em' : '1.05em';
    const msgLines = String(data.message || 'Announcement').split('\n');
    // 🪶 09-01: a headline needs a body. A SINGLE emoji-led line (a watch
    // drop, a one-line note) used to render as a 1.3em/800 headline — the
    // "too big and brutish" card. One-liners render at body size now; only
    // multi-line announcements keep the head + body split.
    const canHeadline = msgLines.filter((l) => l.trim()).length > 1;
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '18px 0', padding: '0 8px' }}>
        <div style={{ flex: 1, minWidth: 8, height: 1, background: `linear-gradient(90deg, transparent, ${THEME.borderGold || THEME.accent}66)` }} />
        <div style={{ textAlign: 'center', maxWidth: isNarrow ? '78%' : 'min(680px, 82%)', minWidth: 0 }}>
          <div style={{ fontSize: isNarrow ? '0.62em' : '0.72em', fontWeight: 800, letterSpacing: '0.18em', textTransform: 'uppercase', color: THEME.accentOnPanel || THEME.accent, marginBottom: 3 }}>
            {data.is_directed === true ? 'Scenario' : (stripLeadEmoji(uiTerms?.announcement_label || 'Announcement') || 'Announcement')}
          </div>
          <div style={{ color: THEME.textOnPanel || '#fff', lineHeight: 1.5 }}>
            {msgLines.map((line, i) => {
              const t = line.trim();
              if (!t) return <div key={i} style={{ height: 6 }} />;
              const isHead = canHeadline && emojiHead.test(t);
              return (
                <div key={i} style={{
                  fontSize: isHead ? headSize : bodySize,
                  fontWeight: isHead ? 800 : (emojiHead.test(t) ? 600 : 500),
                  letterSpacing: isHead ? '0.02em' : 'normal',
                  marginTop: isHead && i > 0 ? 4 : 0,
                }}>{isHead ? (stripLeadEmoji(t) || t) : t}</div>
              );
            })}
          </div>
          {data.directed && <DirectedCallCard directed={data.directed} chipsOnly />}
          <div style={{ fontSize: isNarrow ? '0.6em' : '0.7em', fontStyle: 'italic', color: THEME.textMuted, marginTop: 4 }}>
            {formatActionTime(action.created_at)}
          </div>
        </div>
        <div style={{ flex: 1, minWidth: 8, height: 1, background: `linear-gradient(90deg, ${THEME.borderGold || THEME.accent}66, transparent)` }} />
      </div>
    );
  }

  // Competitive: full dramatic card
  // Check for full-body image (prefer nickname version if available)
  const fullbodyImageUrl = data.fullbody_image_with_nickname_url || data.fullbody_image_url || data.intro_image_url;

  return (
    <div style={{
      ...COMMON_STYLES.actionContainer,
      backgroundColor: THEME.bgDark,
      border: `2px solid ${THEME.accent}`,
      boxShadow: `0 0 10px ${THEME.accent}33`,
      position: 'relative',
      padding: '12px'
    }}>
      <div style={{
        backgroundColor: THEME.accent,
        color: THEME.textDark,
        padding: '4px 12px',
        borderRadius: '4px',
        fontSize: '0.85em',
        fontWeight: 'bold',
        letterSpacing: '0.5px',
        textTransform: 'uppercase',
        marginBottom: '8px',
        display: 'inline-block'
      }}>
        {uiTerms?.announcement_label || '📢 Announcement'}
      </div>

      <div style={{
        ...COMMON_STYLES.actionBody,
        display: 'flex',
        flexDirection: fullbodyImageUrl ? 'row' : 'column',
        gap: '16px',
        alignItems: fullbodyImageUrl ? 'center' : 'stretch'
      }}>
        {fullbodyImageUrl && (
          <div style={{
            flex: '0 0 auto',
            maxWidth: '200px'
          }}>
            <img
              src={fullbodyImageUrl}
              alt="Character"
              style={{
                width: '100%',
                height: 'auto',
                borderRadius: '8px',
                border: `2px solid ${THEME.borderGold}`,
                backgroundColor: 'white'
              }}
            />
          </div>
        )}
        {data.scene_comic_url ? (
          /* Scene comic panel (competitive 🌍 scene announcement): the
             pre-generated cartoon replaces the text wall; the one-line
             caption keeps the words for skimmers, accessibility, and as the
             fallback if the image 404s (img hides itself, caption stays).
             Rows without the URL fall through to the classic text box. */
          <div style={{ flex: fullbodyImageUrl ? '1' : 'auto', textAlign: 'center' }}>
            <img
              src={data.scene_comic_url}
              alt={(data.message || '').replace(/^🌍\s*/, '')}
              loading="lazy"
              onError={(e) => { e.currentTarget.style.display = 'none'; }}
              style={{
                width: '100%',
                aspectRatio: '16 / 9',
                objectFit: 'cover',
                display: 'block',
                borderRadius: '10px',
                border: `2px solid ${THEME.borderGold}`,
                boxShadow: '0 4px 14px rgba(0,0,0,0.35)',
              }}
            />
            <div style={{
              marginTop: '6px',
              fontSize: '0.8em',
              fontStyle: 'italic',
              color: THEME.textMuted,
              lineHeight: 1.4,
            }}>
              {(data.message || '').replace(/^🌍\s*/, '')}
            </div>
          </div>
        ) : (
        <div style={{
          ...COMMON_STYLES.messageBox,
          fontSize: '1.1em',
          textAlign: fullbodyImageUrl ? 'left' : 'center',
          backgroundColor: THEME.bgParchment,
          border: `2px solid ${THEME.borderGold}`,
          boxShadow: 'inset 0 0 10px ${THEME.textDark}1a',
          flex: fullbodyImageUrl ? '1' : 'auto'
        }}>
          {(() => {
            const lines = (data.message || '').split('\n');
            return lines.map((line, i) => {
              const trimmed = line.trim();
              if (!trimmed) {
                return <div key={i} style={{ height: '8px' }} />;
              }
              // Emoji header lines (start with emoji like 🗳️, ❌, 🏆, 📢, 🎯)
              const startsWithEmoji = /^[\u{1F000}-\u{1FFFF}\u{2600}-\u{27BF}\u{FE00}-\u{FE0F}\u{1F900}-\u{1F9FF}]/u.test(trimmed);
              // Elimination line
              const isElimination = trimmed.startsWith('\u274C') || trimmed.toLowerCase().includes('eliminated');
              // Player result line (contains ":" and "vote") — only on RESULTS announcements
              const isVoteResult = data.vote_results && trimmed.includes(':') && trimmed.toLowerCase().includes('vote');

              if (isElimination) {
                return (
                  <div key={i} style={{
                    fontSize: '1.15em',
                    fontWeight: 700,
                    color: '#dc2626',
                    marginTop: i > 0 ? '10px' : 0,
                    padding: '4px 0'
                  }}>
                    {trimmed}
                  </div>
                );
              }
              if (startsWithEmoji) {
                return (
                  <div key={i} style={{
                    fontSize: '1.1em',
                    fontWeight: 700,
                    marginTop: i > 0 ? '10px' : 0,
                    padding: '2px 0',
                    color: THEME.textDark
                  }}>
                    {trimmed}
                  </div>
                );
              }
              if (isVoteResult) {
                const parts = trimmed.split(':');
                const name = parts[0].trim();
                const stats = parts.slice(1).join(':').trim();
                // Extract vote count for bar width
                const voteMatch = stats.match(/(\d+)\s*vote/);
                const voteCount = voteMatch ? parseInt(voteMatch[1]) : 0;
                // Get total votes from all result lines
                const allVoteLines = lines.filter(l => l.trim().includes(':') && l.trim().toLowerCase().includes('vote'));
                const totalVotes = allVoteLines.reduce((sum, l) => {
                  const m = l.match(/(\d+)\s*vote/);
                  return sum + (m ? parseInt(m[1]) : 0);
                }, 0);
                const pct = totalVotes > 0 ? Math.round((voteCount / totalVotes) * 100) : 0;
                const barPct = totalVotes > 0 ? (voteCount / totalVotes) * 100 : 0;
                const accentHex = THEME.accent || '#6366f1';
                return (
                  <div key={i} style={{
                    padding: '6px 10px',
                    borderRadius: '6px',
                    backgroundColor: `${accentHex}08`,
                    marginTop: '4px',
                    fontSize: '0.95em',
                    position: 'relative',
                    overflow: 'hidden'
                  }}>
                    {/* Background bar */}
                    <div style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      height: '100%',
                      width: `${barPct}%`,
                      background: `linear-gradient(90deg, ${accentHex}20, ${accentHex}08)`,
                      borderRadius: '6px',
                      transition: 'width 0.5s ease'
                    }} />
                    <div style={{
                      position: 'relative',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}>
                      <span style={{ fontWeight: 600, color: THEME.textDark }}>{name}</span>
                      <span style={{
                        color: THEME.textMuted,
                        fontSize: '0.85em',
                        fontWeight: 600,
                        fontVariantNumeric: 'tabular-nums'
                      }}>
                        {voteCount} vote{voteCount !== 1 ? 's' : ''} ({pct}%)
                      </span>
                    </div>
                  </div>
                );
              }
              return (
                <div key={i} style={{ padding: '2px 0', color: THEME.textDark }}>
                  {trimmed}
                </div>
              );
            });
          })()}
        </div>
        )}
      </div>

      <div style={{
        position: 'absolute',
        bottom: '8px',
        right: '16px',
        color: THEME.textMuted,
        fontSize: '0.8em',
        fontStyle: 'italic'
      }}>
        {formatActionTime(action.created_at)}
      </div>
    </div>
  );
};

const CustomVideoAction = ({ action, THEME, onActionUpdate }) => {
  const COMMON_STYLES = getCommonStyles(THEME);
  const [regeneratingVideo, setRegeneratingVideo] = React.useState(null);
  const [isExpanded, setIsExpanded] = React.useState(true);
  const [generatingSceneImage, setGeneratingSceneImage] = React.useState(false);
  const [generatingVariants, setGeneratingVariants] = React.useState(false);
  const [generatingBoundingBoxes, setGeneratingBoundingBoxes] = React.useState(false);
  const [generatingVideo, setGeneratingVideo] = React.useState(false);

  // Handle nested data structure - custom video data might be in action.data or action.data.custom_video
  const videoData = action.data?.custom_video || action.data || {};

  const description = videoData.user_description || videoData.description || 'Custom Video';
  const characters = videoData.character_names || videoData.characters || [];
  const space = videoData.space_name || videoData.space || '';
  const status = videoData.status || 'pending';
  const videos = videoData.videos || [];
  const singleVideoUrl = videoData.video_url;

  // Scene image URLs (similar to dialogue actions)
  const sceneImageUrl = videoData.scene_image_url || videoData.external_scene_image_url || videoData.scene_images?.reference_image;
  const characterImageUrl = videoData.external_scene_image_character_url;
  const boundingBoxUrl = videoData.external_scene_image_character_bbox_url;
  const ttsUrl = videoData.tts?.audio_url;
  const ttsDuration = videoData.tts?.duration;

  const regenerateVideo = async (videoIndex) => {
    const videoKey = `${action.id}-${videoIndex}`;
    setRegeneratingVideo(videoKey);

    try {
      const response = await fetch(`${API_BASE_URL}/api/takeGameStep`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          regenerateCustomVideo: true,
          actionId: action.id,
          videoIndex: videoIndex
        })
      });

      const result = await parseApiResponse(response);
      if (result.success && onActionUpdate) {
        onActionUpdate(action.id, result.updatedAction || result);
      }
    } catch (err) {
      console.error('Error regenerating video:', err);
      alert(`Failed to regenerate: ${err.message}`);
    } finally {
      setRegeneratingVideo(null);
    }
  };

  // Generate main scene image (or regenerate)
  const generateSceneImage = async () => {
    setGeneratingSceneImage(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/takeGameStep?generateDialogueSceneImage=true&actionId=${action.id}&sceneType=external&generateSecondaryImages=false`, {
        method: 'GET'
      });
      const result = await parseApiResponse(response);
      if (result.success && onActionUpdate) {
        onActionUpdate(action.id, result);
      } else if (!result.success) {
        alert(`Failed to generate scene image: ${result.error || 'Unknown error'}`);
      }
    } catch (err) {
      console.error('Error generating scene image:', err);
      alert(`Error: ${err.message}`);
    } finally {
      setGeneratingSceneImage(false);
    }
  };

  // Generate variants (isolated character, background, etc.)
  const generateVariants = async () => {
    setGeneratingVariants(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/takeGameStep?generateDialogueSceneImage=true&actionId=${action.id}&sceneType=external&generateSecondaryImages=true`, {
        method: 'GET'
      });
      const result = await parseApiResponse(response);
      if (result.success && onActionUpdate) {
        onActionUpdate(action.id, result);
      } else if (!result.success) {
        alert(`Failed to generate variants: ${result.error || 'Unknown error'}`);
      }
    } catch (err) {
      console.error('Error generating variants:', err);
      alert(`Error: ${err.message}`);
    } finally {
      setGeneratingVariants(false);
    }
  };

  // Generate bounding boxes
  const generateBoundingBoxes = async () => {
    setGeneratingBoundingBoxes(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/takeGameStep?generateDialogueBoundingBoxes=true&actionId=${action.id}&sceneType=external`, {
        method: 'GET'
      });
      const result = await parseApiResponse(response);
      if (result.success && onActionUpdate) {
        onActionUpdate(action.id, result);
      } else if (!result.success) {
        alert(`Failed to generate bounding boxes: ${result.error || 'Unknown error'}`);
      }
    } catch (err) {
      console.error('Error generating bounding boxes:', err);
      alert(`Error: ${err.message}`);
    } finally {
      setGeneratingBoundingBoxes(false);
    }
  };

  // Generate video using existing scene images
  const generateVideoFromSceneImage = async () => {
    setGeneratingVideo(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/takeGameStep`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          generateCustomVideo: true,
          gameInstanceId: action.game_instance_id,
          roundId: action.game_instance_round_id,
          userDescription: description,
          referenceImageUrl: sceneImageUrl,
          existingActionId: action.id // Tell backend to update existing action instead of creating new
        })
      });
      const result = await parseApiResponse(response);
      if (result.success && onActionUpdate) {
        onActionUpdate(action.id, result.custom_video || result);
      } else if (!result.success) {
        alert(`Failed to generate video: ${result.error || 'Unknown error'}`);
      }
    } catch (err) {
      console.error('Error generating video:', err);
      alert(`Error: ${err.message}`);
    } finally {
      setGeneratingVideo(false);
    }
  };

  const handleDragStart = (videoUrl, e) => {
    const dragData = {
      url: videoUrl,
      filename: videoUrl.split('/').pop().split('?')[0],
      title: `Custom: ${description.substring(0, 50)}...`,
      type: 'custom_video',
      metadata: { description, characters, space }
    };
    e.dataTransfer.setData('video-data', JSON.stringify(dragData));
    e.dataTransfer.effectAllowed = 'copy';
  };

  const getStatusColor = () => {
    if (status === 'completed' || status === 'complete') return '#10b981';
    if (status === 'scene_image_ready') return '#3b82f6';
    if (status === 'failed') return '#ef4444';
    return '#f59e0b';
  };

  const getStatusLabel = () => {
    if (status === 'scene_image_ready') return 'Image Ready';
    return status;
  };

  return (
    <div style={{
      ...COMMON_STYLES.messageBox,
      backgroundColor: THEME.bgMedium,
      border: `2px solid ${THEME.accent}`,
      position: 'relative'
    }}>
      {/* Header */}
      <div
        onClick={() => setIsExpanded(!isExpanded)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          cursor: 'pointer',
          marginBottom: isExpanded ? '12px' : '0'
        }}
      >
        <span style={{ fontSize: '1.2em' }}>🎬</span>
        <span style={{ fontWeight: 'bold', color: THEME.textLight, flex: 1 }}>
          Custom Video
        </span>
        <span style={{
          backgroundColor: getStatusColor(),
          color: 'white',
          padding: '2px 8px',
          borderRadius: '4px',
          fontSize: '0.75em',
          fontWeight: 'bold'
        }}>
          {getStatusLabel()}
        </span>
        <span style={{ color: THEME.textMuted }}>{isExpanded ? '▼' : '▶'}</span>
      </div>

      {isExpanded && (
        <>
          {/* Description */}
          <div style={{
            color: THEME.textLight,
            marginBottom: '12px',
            padding: '8px',
            backgroundColor: THEME.bgDark,
            borderRadius: '6px'
          }}>
            {description}
          </div>

          {/* Tags */}
          <div style={{ display: 'flex', gap: '8px', marginBottom: '12px', flexWrap: 'wrap' }}>
            {characters.map((char, idx) => {
              // Handle both string and object character formats
              const charName = typeof char === 'string' ? char : (char?.name || 'Unknown');
              return (
                <span key={idx} style={{
                  backgroundColor: THEME.private || '#8b5cf6',
                  color: 'white',
                  padding: '2px 8px',
                  borderRadius: '4px',
                  fontSize: '0.8em'
                }}>
                  👤 {charName}
                </span>
              );
            })}
            {space && (
              <span style={{
                backgroundColor: THEME.public || '#3b82f6',
                color: 'white',
                padding: '2px 8px',
                borderRadius: '4px',
                fontSize: '0.8em'
              }}>
                📍 {typeof space === 'string' ? space : (space?.name || space?.description || 'Unknown')}
              </span>
            )}
          </div>

          {/* Scene Image & Asset Generation (shown when scene_image_ready or any scene image exists) */}
          {(status === 'scene_image_ready' || sceneImageUrl) && (
            <div style={{
              marginBottom: '12px',
              padding: '12px',
              backgroundColor: THEME.bgDark,
              borderRadius: '6px'
            }}>
              <div style={{ marginBottom: '10px', fontWeight: 'bold', color: THEME.textLight, fontSize: '0.9em' }}>
                Scene Assets
              </div>

              {/* Image Grid */}
              <div style={{ display: 'flex', gap: '10px', marginBottom: '12px', flexWrap: 'wrap' }}>
                {sceneImageUrl && (
                  <div style={{ textAlign: 'center' }}>
                    <img
                      src={sceneImageUrl}
                      alt="Scene"
                      style={{
                        width: '150px',
                        height: '85px',
                        objectFit: 'cover',
                        borderRadius: '4px',
                        border: `2px solid ${THEME.primary}`
                      }}
                    />
                    <div style={{ fontSize: '0.7em', color: THEME.textMuted, marginTop: '2px' }}>Main</div>
                  </div>
                )}
                {characterImageUrl && (
                  <div style={{ textAlign: 'center' }}>
                    <img
                      src={characterImageUrl}
                      alt="Character"
                      style={{
                        width: '150px',
                        height: '85px',
                        objectFit: 'cover',
                        borderRadius: '4px',
                        border: `2px solid #9C27B0`
                      }}
                    />
                    <div style={{ fontSize: '0.7em', color: THEME.textMuted, marginTop: '2px' }}>Isolated</div>
                  </div>
                )}
                {boundingBoxUrl && (
                  <div style={{ textAlign: 'center' }}>
                    <img
                      src={boundingBoxUrl}
                      alt="Bounding Box"
                      style={{
                        width: '150px',
                        height: '85px',
                        objectFit: 'cover',
                        borderRadius: '4px',
                        border: `2px solid #2196F3`
                      }}
                    />
                    <div style={{ fontSize: '0.7em', color: THEME.textMuted, marginTop: '2px' }}>BBox</div>
                  </div>
                )}
              </div>

              {/* TTS Info */}
              {ttsUrl && (
                <div style={{ marginBottom: '10px' }}>
                  <div style={{ fontSize: '0.8em', color: THEME.textMuted, marginBottom: '4px' }}>
                    🔊 Audio: {ttsDuration ? `${ttsDuration.toFixed(1)}s` : 'Generated'}
                  </div>
                  <audio controls src={ttsUrl} style={{ width: '100%', height: '30px' }} />
                </div>
              )}

              {/* Asset Generation Buttons */}
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                <button
                  onClick={generateSceneImage}
                  disabled={generatingSceneImage}
                  style={{
                    padding: '6px 12px',
                    backgroundColor: generatingSceneImage ? THEME.textMuted : '#FF9800',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: generatingSceneImage ? 'not-allowed' : 'pointer',
                    fontSize: '0.8em',
                    fontWeight: 'bold'
                  }}
                >
                  {generatingSceneImage ? '🎨 Generating...' : '🎨 Main Image'}
                </button>

                <button
                  onClick={generateVariants}
                  disabled={generatingVariants || !sceneImageUrl}
                  style={{
                    padding: '6px 12px',
                    backgroundColor: (generatingVariants || !sceneImageUrl) ? THEME.textMuted : '#9C27B0',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: (generatingVariants || !sceneImageUrl) ? 'not-allowed' : 'pointer',
                    fontSize: '0.8em',
                    fontWeight: 'bold'
                  }}
                  title={!sceneImageUrl ? 'Generate main image first' : 'Generate isolated character image'}
                >
                  {generatingVariants ? '✨ Generating...' : '✨ Variants'}
                </button>

                <button
                  onClick={generateBoundingBoxes}
                  disabled={generatingBoundingBoxes || !characterImageUrl}
                  style={{
                    padding: '6px 12px',
                    backgroundColor: (generatingBoundingBoxes || !characterImageUrl) ? THEME.textMuted : '#2196F3',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: (generatingBoundingBoxes || !characterImageUrl) ? 'not-allowed' : 'pointer',
                    fontSize: '0.8em',
                    fontWeight: 'bold'
                  }}
                  title={!characterImageUrl ? 'Generate variants first' : 'Generate bounding boxes'}
                >
                  {generatingBoundingBoxes ? '📦 Generating...' : '📦 Bounding Boxes'}
                </button>

                <button
                  onClick={generateVideoFromSceneImage}
                  disabled={generatingVideo || !sceneImageUrl}
                  style={{
                    padding: '6px 12px',
                    backgroundColor: (generatingVideo || !sceneImageUrl) ? THEME.textMuted : THEME.accent,
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: (generatingVideo || !sceneImageUrl) ? 'not-allowed' : 'pointer',
                    fontSize: '0.8em',
                    fontWeight: 'bold'
                  }}
                  title={!sceneImageUrl ? 'Generate scene image first' : 'Generate video using scene image'}
                >
                  {generatingVideo ? '🎬 Generating...' : '🎬 Generate Video'}
                </button>
              </div>
            </div>
          )}

          {/* Videos */}
          {(status === 'completed' || status === 'complete') && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {videos && videos.length > 0 ? (
                videos.map((videoPart, idx) => (
                  <div key={idx}>
                    {videos.length > 1 && (
                      <div style={{
                        fontSize: '0.85em',
                        color: THEME.textLight,
                        marginBottom: '4px',
                        fontWeight: 'bold'
                      }}>
                        Part {videoPart.part || idx + 1} of {videoPart.total_parts || videos.length}
                      </div>
                    )}
                    <video
                      controls
                      draggable="true"
                      onDragStart={(e) => handleDragStart(videoPart.url, e)}
                      src={videoPart.url}
                      style={{
                        width: '100%',
                        maxWidth: '600px',
                        borderRadius: '6px',
                        cursor: 'grab'
                      }}
                    />
                    {videoPart.prompt && (
                      <div style={{
                        marginTop: '6px',
                        padding: '6px',
                        backgroundColor: THEME.bgDark,
                        borderRadius: '4px',
                        fontSize: '0.8em',
                        color: THEME.textMuted
                      }}>
                        <strong style={{ color: THEME.textLight }}>Prompt:</strong> {videoPart.prompt}
                      </div>
                    )}
                    <button
                      onClick={() => regenerateVideo(idx)}
                      disabled={regeneratingVideo === `${action.id}-${idx}`}
                      style={{
                        marginTop: '6px',
                        padding: '4px 10px',
                        backgroundColor: regeneratingVideo === `${action.id}-${idx}` ? THEME.textMuted : THEME.primary,
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: regeneratingVideo === `${action.id}-${idx}` ? 'not-allowed' : 'pointer',
                        fontSize: '0.8em'
                      }}
                    >
                      {regeneratingVideo === `${action.id}-${idx}` ? '⏳ Regenerating...' : '🔄 Regenerate'}
                    </button>
                  </div>
                ))
              ) : singleVideoUrl ? (
                <div>
                  <video
                    controls
                    draggable="true"
                    onDragStart={(e) => handleDragStart(singleVideoUrl, e)}
                    src={singleVideoUrl}
                    style={{
                      width: '100%',
                      maxWidth: '600px',
                      borderRadius: '6px',
                      cursor: 'grab'
                    }}
                  />
                  <button
                    onClick={() => regenerateVideo(0)}
                    disabled={regeneratingVideo === `${action.id}-0`}
                    style={{
                      marginTop: '6px',
                      padding: '4px 10px',
                      backgroundColor: regeneratingVideo === `${action.id}-0` ? THEME.textMuted : THEME.primary,
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: regeneratingVideo === `${action.id}-0` ? 'not-allowed' : 'pointer',
                      fontSize: '0.8em'
                    }}
                  >
                    {regeneratingVideo === `${action.id}-0` ? '⏳ Regenerating...' : '🔄 Regenerate'}
                  </button>
                </div>
              ) : (
                <div style={{ color: THEME.textMuted, fontStyle: 'italic' }}>
                  No video URL available
                </div>
              )}
              <div style={{ fontSize: '0.75em', color: THEME.textMuted }}>
                💡 Drag video to add to stitcher
              </div>
            </div>
          )}

          {status === 'pending' && (
            <div style={{
              padding: '12px',
              backgroundColor: THEME.bgDark,
              borderRadius: '6px',
              color: THEME.textMuted,
              textAlign: 'center'
            }}>
              ⏳ Video generation in progress...
            </div>
          )}

          {status === 'failed' && (
            <div style={{
              padding: '12px',
              backgroundColor: '#7f1d1d',
              borderRadius: '6px',
              color: '#fca5a5',
              textAlign: 'center'
            }}>
              ❌ Video generation failed
            </div>
          )}
        </>
      )}

      {/* Timestamp */}
      <div style={{
        position: 'absolute',
        bottom: '8px',
        right: '12px',
        color: THEME.textMuted,
        fontSize: '0.75em'
      }}>
        {new Date(action.created_at).toLocaleString()}
      </div>
    </div>
  );
};

const SceneAction = ({ action, THEME }) => {
  const COMMON_STYLES = getCommonStyles(THEME);
  const { data } = action;
  const [expandedClips, setExpandedClips] = React.useState({});
  const [generatingClips, setGeneratingClips] = React.useState({});
  const [clipVideos, setClipVideos] = React.useState({});
  const [processingClips, setProcessingClips] = React.useState({});
  const [processedClipVideos, setProcessedClipVideos] = React.useState({});
  const [extendingClips, setExtendingClips] = React.useState({});
  const [extendedClipVideos, setExtendedClipVideos] = React.useState({});
  const [stitchedClipVideos, setStitchedClipVideos] = React.useState({});

  const toggleClip = (clipNumber) => {
    setExpandedClips(prev => ({
      ...prev,
      [clipNumber]: !prev[clipNumber]
    }));
  };

  const generateVideo = async (clipNumber) => {
    setGeneratingClips(prev => ({ ...prev, [clipNumber]: true }));

    try {
      const response = await fetch(
        `${API_BASE_URL}/api/takeGameStep?generateClipVideo=true&actionId=${action.id}&clipNumber=${clipNumber}`,
        { method: 'GET' }
      );

      if (!response.ok) {
        throw new Error(`Failed to generate video: ${response.statusText}`);
      }

      const result = await parseApiResponse(response);

      if (result.success && result.video_url) {
        setClipVideos(prev => ({ ...prev, [clipNumber]: result.video_url }));
      }
    } catch (error) {
      console.error('Error generating video:', error);
      alert(`Failed to generate video: ${error.message}`);
    } finally {
      setGeneratingClips(prev => ({ ...prev, [clipNumber]: false }));
    }
  };

  const processVideo = async (clipNumber) => {
    setProcessingClips(prev => ({ ...prev, [clipNumber]: true }));

    try {
      const response = await fetch(
        `${API_BASE_URL}/api/takeGameStep?processClipVideo=true&actionId=${action.id}&clipNumber=${clipNumber}`,
        { method: 'GET' }
      );

      if (!response.ok) {
        throw new Error(`Failed to process video: ${response.statusText}`);
      }

      const result = await parseApiResponse(response);

      if (result.success && result.processed_video_url) {
        // Add cache-busting timestamp to force video reload
        const videoUrlWithTimestamp = `${result.processed_video_url}?t=${Date.now()}`;
        setProcessedClipVideos(prev => ({ ...prev, [clipNumber]: videoUrlWithTimestamp }));
      }
    } catch (error) {
      console.error('Error processing video:', error);
      alert(`Failed to process video: ${error.message}`);
    } finally {
      setProcessingClips(prev => ({ ...prev, [clipNumber]: false }));
    }
  };

  const extendVideo = async (clipNumber) => {
    setExtendingClips(prev => ({ ...prev, [clipNumber]: true }));

    try {
      const response = await fetch(
        `${API_BASE_URL}/api/takeGameStep?extendClipVideo=true&actionId=${action.id}&clipNumber=${clipNumber}`,
        { method: 'GET' }
      );

      if (!response.ok) {
        throw new Error(`Failed to extend video: ${response.statusText}`);
      }

      const result = await parseApiResponse(response);

      if (result.success) {
        // Update both extension and stitched videos
        if (result.extended_video_url) {
          setExtendedClipVideos(prev => ({
            ...prev,
            [clipNumber]: `${result.extended_video_url}?t=${Date.now()}`
          }));
        }
        if (result.stitched_video_url) {
          setStitchedClipVideos(prev => ({
            ...prev,
            [clipNumber]: `${result.stitched_video_url}?t=${Date.now()}`
          }));
        }
      }
    } catch (error) {
      console.error('Error extending video:', error);
      alert(`Failed to extend video: ${error.message}`);
    } finally {
      setExtendingClips(prev => ({ ...prev, [clipNumber]: false }));
    }
  };

  // Determine score color based on entertainment value
  const getScoreColor = (score) => {
    if (score >= 90) return THEME.good; // Exceptional
    if (score >= 75) return THEME.accent; // Great
    if (score >= 65) return THEME.primary; // Good
    return THEME.textMuted; // Decent
  };

  const scoreColor = getScoreColor(data.entertainment_score);

  return (
    <div style={{
      ...COMMON_STYLES.actionContainer,
      backgroundColor: THEME.bgDark,
      border: `2px solid ${THEME.accent}`,
      boxShadow: `0 4px 8px ${THEME.textDark}33`,
      position: 'relative',
      padding: '16px',
      marginBottom: '24px'
    }}>
      {/* Header Badge */}
      <div style={{
        backgroundColor: THEME.accent,
        color: THEME.textDark,
        padding: '4px 12px',
        borderRadius: '4px',
        fontSize: '0.85em',
        fontWeight: 'bold',
        letterSpacing: '0.5px',
        textTransform: 'uppercase',
        marginBottom: '12px',
        display: 'inline-block'
      }}>
        🎬 Scene Analysis
      </div>

      {/* Scene Title and Score */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '16px'
      }}>
        <h3 style={{
          color: THEME.textLight,
          margin: 0,
          fontSize: '1.3em',
          fontWeight: 'bold'
        }}>
          {data.title}
        </h3>
        <div style={{
          backgroundColor: scoreColor,
          color: THEME.textDark,
          padding: '6px 16px',
          borderRadius: '20px',
          fontSize: '1.1em',
          fontWeight: 'bold',
          boxShadow: `0 2px 4px ${scoreColor}66`
        }}>
          ⭐ {data.entertainment_score}/100
        </div>
      </div>

      {/* Why Interesting */}
      {data.why_interesting && (
        <div style={{
          backgroundColor: THEME.bgMedium,
          padding: '12px',
          borderRadius: '6px',
          marginBottom: '16px',
          color: THEME.textLight,
          fontStyle: 'italic',
          border: `1px solid ${THEME.borderDark}`
        }}>
          {data.why_interesting}
        </div>
      )}

      {/* Host Profile */}
      {data.host_profile_pic && (
        <div style={{
          marginBottom: '16px'
        }}>
          <div style={{
            color: THEME.textMuted,
            fontSize: '0.85em',
            fontWeight: 'bold',
            marginBottom: '8px',
            textTransform: 'uppercase',
            letterSpacing: '0.5px'
          }}>
            Host:
          </div>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            backgroundColor: THEME.bgMedium,
            padding: '8px 12px',
            borderRadius: '8px',
            border: `1px solid ${THEME.borderDark}`,
            width: 'fit-content'
          }}>
            <img
              src={data.host_profile_pic?.startsWith('http') ? data.host_profile_pic : `${API_BASE_URL}/media/profile-images/${data.host_profile_pic}`}
              alt="Host"
              style={{
                width: '80px',
                height: '80px',
                borderRadius: '4px',
                objectFit: 'cover',
                border: `1px solid ${THEME.borderDark}`
              }}
            />
            <div>
              <div style={{
                color: THEME.textLight,
                fontWeight: 'bold',
                fontSize: '0.9em'
              }}>
                Host
              </div>
              {data.host_guidance && (
                <div style={{
                  color: THEME.textMuted,
                  fontSize: '0.75em',
                  marginTop: '2px'
                }}>
                  {data.host_guidance.substring(0, 50)}...
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Player Profiles */}
      {data.player_profiles && data.player_profiles.length > 0 && (
        <div style={{
          marginBottom: '16px'
        }}>
          <div style={{
            color: THEME.textMuted,
            fontSize: '0.85em',
            fontWeight: 'bold',
            marginBottom: '8px',
            textTransform: 'uppercase',
            letterSpacing: '0.5px'
          }}>
            Featured Players:
          </div>
          <div style={{
            display: 'flex',
            gap: '12px',
            flexWrap: 'wrap'
          }}>
            {data.player_profiles.map((player, idx) => (
              <div key={idx} style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                backgroundColor: THEME.bgMedium,
                padding: '8px 12px',
                borderRadius: '8px',
                border: `1px solid ${THEME.borderDark}`
              }}>
                {player.profile_image_url && (
                  <img
                    src={player.profile_image_url}
                    alt={player.name}
                    style={{
                      width: '80px',
                      height: '80px',
                      borderRadius: '4px',
                      objectFit: 'cover',
                      border: `1px solid ${THEME.borderDark}`
                    }}
                  />
                )}
                <div>
                  <div style={{
                    color: THEME.textLight,
                    fontWeight: 'bold',
                    fontSize: '0.9em'
                  }}>
                    {player.name}
                  </div>
                  {player.visual_description && (
                    <div style={{
                      color: THEME.textMuted,
                      fontSize: '0.75em',
                      marginTop: '2px'
                    }}>
                      {player.visual_description.substring(0, 50)}...
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Clips Section */}
      {data.clips && data.clips.length > 0 && (
        <div>
          <div style={{
            color: THEME.textMuted,
            fontSize: '0.85em',
            fontWeight: 'bold',
            marginBottom: '8px',
            textTransform: 'uppercase',
            letterSpacing: '0.5px'
          }}>
            Video Clips ({data.clips.length} × 8 seconds = {data.clips.length * 8}s total):
          </div>
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '8px'
          }}>
            {data.clips.map((clip, idx) => (
              <div key={idx} style={{
                backgroundColor: THEME.bgMedium,
                border: `1px solid ${THEME.borderDark}`,
                borderRadius: '6px',
                overflow: 'hidden'
              }}>
                {/* Clip Header - Always Visible */}
                <div
                  style={{
                    padding: '12px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    backgroundColor: THEME.bgLight,
                    transition: 'background-color 0.2s'
                  }}
                >
                  <div
                    onClick={() => toggleClip(clip.clip_number)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      cursor: 'pointer',
                      flex: 1
                    }}
                  >
                    <div style={{
                      backgroundColor: THEME.accent,
                      color: THEME.textDark,
                      padding: '4px 8px',
                      borderRadius: '4px',
                      fontSize: '0.85em',
                      fontWeight: 'bold'
                    }}>
                      {clip.clip_number === 0 ? '🎙️ Host Intro' : `Clip ${clip.clip_number}`}
                    </div>
                    <div style={{
                      color: THEME.textLight,
                      fontSize: '0.9em'
                    }}>
                      {clip.video_prompt?.shot_description || 'No description'}
                    </div>
                  </div>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    {/* Generate Button - Always show to allow regeneration */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        generateVideo(clip.clip_number);
                      }}
                      disabled={generatingClips[clip.clip_number]}
                      style={{
                        backgroundColor: generatingClips[clip.clip_number] ? THEME.textMuted : THEME.accent,
                        color: THEME.textDark,
                        border: 'none',
                        padding: '6px 12px',
                        borderRadius: '4px',
                        fontSize: '0.85em',
                        fontWeight: 'bold',
                        cursor: generatingClips[clip.clip_number] ? 'not-allowed' : 'pointer',
                        transition: 'background-color 0.2s'
                      }}
                    >
                      {generatingClips[clip.clip_number] ? '⏳ Generating...' : '🎬 Generate'}
                    </button>
                    {/* Extend Button - Show when any video exists (original or stitched) */}
                    {(clip.video_url || clip.stitched_video_url || clipVideos[clip.clip_number] || stitchedClipVideos[clip.clip_number]) && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          extendVideo(clip.clip_number);
                        }}
                        disabled={extendingClips[clip.clip_number]}
                        style={{
                          backgroundColor: extendingClips[clip.clip_number] ? THEME.textMuted : '#9C27B0',
                          color: THEME.textLight,
                          border: 'none',
                          padding: '6px 12px',
                          borderRadius: '4px',
                          fontSize: '0.85em',
                          fontWeight: 'bold',
                          cursor: extendingClips[clip.clip_number] ? 'not-allowed' : 'pointer',
                          transition: 'background-color 0.2s'
                        }}
                      >
                        {extendingClips[clip.clip_number] ? '⏳ Extending...' :
                          (clip.extension_count ? `➕ Extend (+${(clip.extension_count + 1) * 8}s total)` : '➕ Extend (+8s)')}
                      </button>
                    )}
                    {/* Expand/Collapse Arrow */}
                    <div
                      onClick={() => toggleClip(clip.clip_number)}
                      style={{
                        color: THEME.textMuted,
                        fontSize: '1.2em',
                        cursor: 'pointer'
                      }}
                    >
                      {expandedClips[clip.clip_number] ? '▼' : '▶'}
                    </div>
                  </div>
                </div>

                {/* Video Player - Show if video exists */}
                {(clip.video_url || clipVideos[clip.clip_number]) && (
                  <div style={{
                    padding: '16px',
                    backgroundColor: THEME.bgDark,
                    borderTop: `1px solid ${THEME.borderDark}`
                  }}>
                    <div style={{ marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <div style={{ color: THEME.textMuted, fontWeight: 'bold', fontSize: '0.9em' }}>
                        Original Video
                      </div>
                      {/* Process button - Always show to allow reprocessing */}
                      <button
                        onClick={() => processVideo(clip.clip_number)}
                        disabled={processingClips[clip.clip_number]}
                        style={{
                          backgroundColor: processingClips[clip.clip_number] ? THEME.textMuted : THEME.primary,
                          color: THEME.textDark,
                          border: 'none',
                          padding: '6px 12px',
                          borderRadius: '4px',
                          fontSize: '0.85em',
                          fontWeight: 'bold',
                          cursor: processingClips[clip.clip_number] ? 'not-allowed' : 'pointer',
                          transition: 'background-color 0.2s'
                        }}
                      >
                        {processingClips[clip.clip_number] ? '⏳ Processing...' : '✨ Process'}
                      </button>
                    </div>
                    <video
                      controls
                      draggable="true"
                      onDragStart={(e) => {
                        const url = clipVideos[clip.clip_number] || clip.video_url;
                        const filename = url.split('/').pop().split('?')[0]; // Remove query params
                        const videoData = {
                          url: url,
                          filename: filename,
                          title: `Clip ${clip.clip_number} - Scene ${data.scene_number || ''}`,
                          type: 'scene',
                          metadata: {
                            prompt: clip.video_prompt,
                            sceneNumber: data.scene_number,
                            clipNumber: clip.clip_number,
                            roundNumber: data.round_number,
                            roundType: data.round_type
                          }
                        };
                        e.dataTransfer.setData('video-data', JSON.stringify(videoData));
                        e.dataTransfer.effectAllowed = 'copy';
                      }}
                      style={{
                        width: '100%',
                        maxWidth: '800px',
                        borderRadius: '4px',
                        cursor: 'grab'
                      }}
                      src={clipVideos[clip.clip_number] || clip.video_url}
                    >
                      Your browser does not support the video tag.
                    </video>
                  </div>
                )}

                {/* Processed Video Player - Show if processed video exists */}
                {(clip.processed_video_url || processedClipVideos[clip.clip_number]) && (
                  <div style={{
                    padding: '16px',
                    backgroundColor: THEME.bgDark,
                    borderTop: `1px solid ${THEME.borderDark}`
                  }}>
                    <div style={{
                      color: THEME.accent,
                      fontWeight: 'bold',
                      fontSize: '0.9em',
                      marginBottom: '12px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}>
                      <span>✨</span>
                      <span>Processed Video (with character overlay)</span>
                    </div>
                    <video
                      key={processedClipVideos[clip.clip_number] || clip.processed_video_url}
                      controls
                      draggable="true"
                      onDragStart={(e) => {
                        const url = processedClipVideos[clip.clip_number] || clip.processed_video_url;
                        const filename = url.split('/').pop().split('?')[0]; // Remove query params
                        const videoData = {
                          url: url,
                          filename: filename,
                          title: `Clip ${clip.clip_number} (Processed) - Scene ${data.scene_number || ''}`,
                          type: 'scene-processed',
                          metadata: {
                            prompt: clip.video_prompt,
                            sceneNumber: data.scene_number,
                            clipNumber: clip.clip_number,
                            roundNumber: data.round_number,
                            roundType: data.round_type
                          }
                        };
                        e.dataTransfer.setData('video-data', JSON.stringify(videoData));
                        e.dataTransfer.effectAllowed = 'copy';
                      }}
                      style={{
                        width: '100%',
                        maxWidth: '800px',
                        borderRadius: '4px',
                        boxShadow: `0 0 12px ${THEME.accent}33`,
                        cursor: 'grab'
                      }}
                      src={processedClipVideos[clip.clip_number] || clip.processed_video_url}
                    >
                      Your browser does not support the video tag.
                    </video>
                  </div>
                )}

                {/* Stitched Video Player (Primary when available) */}
                {(clip.stitched_video_url || stitchedClipVideos[clip.clip_number]) && (
                  <div style={{
                    padding: '16px',
                    backgroundColor: THEME.bgDark,
                    borderTop: `1px solid ${THEME.borderDark}`
                  }}>
                    <div style={{
                      color: '#9C27B0',
                      fontWeight: 'bold',
                      fontSize: '0.9em',
                      marginBottom: '12px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}>
                      <span>🎬</span>
                      <span>Extended Video ({clip.total_duration || 16}s total - {clip.extension_count || 1}x extended)</span>
                    </div>
                    <video
                      key={stitchedClipVideos[clip.clip_number] || clip.stitched_video_url}
                      controls
                      draggable="true"
                      onDragStart={(e) => {
                        const url = stitchedClipVideos[clip.clip_number] || clip.stitched_video_url;
                        const filename = url.split('/').pop().split('?')[0];
                        const videoData = {
                          url: url,
                          filename: filename,
                          title: `Clip ${clip.clip_number} (${clip.total_duration}s) - Scene ${data.scene_number || ''}`,
                          type: 'scene-stitched',
                          metadata: {
                            prompt: clip.video_prompt,
                            sceneNumber: data.scene_number,
                            clipNumber: clip.clip_number,
                            roundNumber: data.round_number,
                            roundType: data.round_type,
                            isExtended: true,
                            totalDuration: clip.total_duration,
                            extensionCount: clip.extension_count
                          }
                        };
                        e.dataTransfer.setData('video-data', JSON.stringify(videoData));
                        e.dataTransfer.effectAllowed = 'copy';
                      }}
                      style={{
                        width: '100%',
                        maxWidth: '800px',
                        borderRadius: '4px',
                        boxShadow: '0 0 12px rgba(156, 39, 176, 0.3)',
                        cursor: 'grab'
                      }}
                      src={stitchedClipVideos[clip.clip_number] || clip.stitched_video_url}
                    >
                      Your browser does not support the video tag.
                    </video>
                  </div>
                )}

                {/* Clip Details - Expandable */}
                {expandedClips[clip.clip_number] && clip.video_prompt && (
                  <div style={{
                    padding: '16px',
                    backgroundColor: THEME.bgDark,
                    borderTop: `1px solid ${THEME.borderDark}`
                  }}>
                    {/* Characters */}
                    {clip.video_prompt.characters && clip.video_prompt.characters.length > 0 && (
                      <div style={{ marginBottom: '12px' }}>
                        <div style={{
                          color: THEME.accent,
                          fontSize: '0.8em',
                          fontWeight: 'bold',
                          marginBottom: '4px',
                          textTransform: 'uppercase'
                        }}>
                          Characters:
                        </div>
                        <ul style={{
                          margin: 0,
                          paddingLeft: '20px',
                          color: THEME.textLight
                        }}>
                          {clip.video_prompt.characters.map((char, charIdx) => (
                            <li key={charIdx} style={{ marginBottom: '4px' }}>{char}</li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Setting */}
                    {clip.video_prompt.setting && (
                      <div style={{ marginBottom: '12px' }}>
                        <div style={{
                          color: THEME.accent,
                          fontSize: '0.8em',
                          fontWeight: 'bold',
                          marginBottom: '4px',
                          textTransform: 'uppercase'
                        }}>
                          Setting:
                        </div>
                        <div style={{ color: THEME.textLight }}>
                          {clip.video_prompt.setting}
                        </div>
                      </div>
                    )}

                    {/* Camera */}
                    {clip.video_prompt.camera && (
                      <div style={{ marginBottom: '12px' }}>
                        <div style={{
                          color: THEME.accent,
                          fontSize: '0.8em',
                          fontWeight: 'bold',
                          marginBottom: '4px',
                          textTransform: 'uppercase'
                        }}>
                          Camera:
                        </div>
                        <div style={{ color: THEME.textLight }}>
                          {clip.video_prompt.camera}
                        </div>
                      </div>
                    )}

                    {/* Action */}
                    {clip.video_prompt.action && (
                      <div style={{ marginBottom: '12px' }}>
                        <div style={{
                          color: THEME.accent,
                          fontSize: '0.8em',
                          fontWeight: 'bold',
                          marginBottom: '4px',
                          textTransform: 'uppercase'
                        }}>
                          Action:
                        </div>
                        <div style={{ color: THEME.textLight }}>
                          {clip.video_prompt.action}
                        </div>
                      </div>
                    )}

                    {/* Mood */}
                    {clip.video_prompt.mood && (
                      <div style={{ marginBottom: '12px' }}>
                        <div style={{
                          color: THEME.accent,
                          fontSize: '0.8em',
                          fontWeight: 'bold',
                          marginBottom: '4px',
                          textTransform: 'uppercase'
                        }}>
                          Mood:
                        </div>
                        <div style={{ color: THEME.textLight }}>
                          {clip.video_prompt.mood}
                        </div>
                      </div>
                    )}

                    {/* Visual Style */}
                    {clip.video_prompt.visual_style && (
                      <div style={{ marginBottom: '12px' }}>
                        <div style={{
                          color: THEME.accent,
                          fontSize: '0.8em',
                          fontWeight: 'bold',
                          marginBottom: '4px',
                          textTransform: 'uppercase'
                        }}>
                          Visual Style:
                        </div>
                        <div style={{ color: THEME.textLight }}>
                          {clip.video_prompt.visual_style}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Round Info */}
      <div style={{
        marginTop: '16px',
        paddingTop: '12px',
        borderTop: `1px solid ${THEME.borderDark}`,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <div style={{
          color: THEME.textMuted,
          fontSize: '0.85em'
        }}>
          Round {data.round_number} • {data.round_type} • {data.model}
        </div>
        <div style={{
          color: THEME.textMuted,
          fontSize: '0.85em',
          fontStyle: 'italic'
        }}>
          {formatActionTime(action.created_at)}
        </div>
      </div>
    </div>
  );
};

const VoteCastAction = ({ action, users, THEME }) => {
  const COMMON_STYLES = getCommonStyles(THEME);
  const { data } = action;

  // Get user info from game_instance_player_id
  const user = getUserById(users, action.game_instance_player_id);
  const modelColor = user ? getModelColor(user.model, THEME) : THEME.bgLight;

  // Handle both old format (data.vote = 'yes'/'no') and new competitive format (data.target_name)
  const isCompetitiveVote = !!data.target_name;
  const voteColor = isCompetitiveVote ? THEME.accent : (data.vote === 'yes' ? THEME.good : THEME.evil);
  const voteDisplay = isCompetitiveVote ? data.target_name : (data.vote ? data.vote.toUpperCase() : 'UNKNOWN');

  return (
    <div style={{
      backgroundColor: THEME.bgLight,
      border: `1px solid ${THEME.borderLight}`,
      borderRadius: '8px',
      padding: '16px',
      marginBottom: '12px',
      position: 'relative'
    }}>
      <div style={{
        position: 'absolute',
        top: '-12px',
        left: '50%',
        transform: 'translateX(-50%)',
        backgroundColor: THEME.textMuted,
        color: THEME.textLight,
        padding: '4px 16px',
        borderRadius: '16px',
        fontSize: '1.1em',
        fontWeight: 'bold',
        boxShadow: '0 2px 4px ${THEME.textDark}1a'
      }}>
        {isCompetitiveVote ? '🗳️ Vote Cast' : 'Vote Cast'}
      </div>

      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        marginTop: '8px',
        marginBottom: '16px'
      }}>
        {user && (
          <>
            <ProfileImage
              user={user}
              size={32}
              borderColor={modelColor}
            />
            <div style={{
              backgroundColor: modelColor,
              padding: '8px 16px',
              borderRadius: '6px',
              color: THEME.textDark,
              fontWeight: 'bold',
              flex: 1,
              textAlign: 'center'
            }}>
              {user.name} ({getDisplayModelName(user)})
            </div>
          </>
        )}
        <div style={{
          fontSize: '1.2em',
          color: THEME.textMuted,
          fontWeight: 'bold'
        }}>
          {isCompetitiveVote ? 'voted for' : 'votes'}
        </div>
        <div style={{
          backgroundColor: voteColor,
          padding: '8px 16px',
          borderRadius: '6px',
          color: THEME.textLight,
          fontWeight: 'bold',
          flex: 1,
          textAlign: 'center'
        }}>
          {voteDisplay}
        </div>
        {data.is_dead_vote && (
          <div style={{
            backgroundColor: THEME.textDark,
            padding: '4px 8px',
            borderRadius: '4px',
            color: THEME.textLight,
            fontSize: '0.8em',
            fontWeight: 'bold'
          }}>
            Dead Vote
          </div>
        )}
      </div>

      {data.thoughts && (
        <div style={{
          backgroundColor: THEME.textLight,
          padding: '16px',
          borderRadius: '6px',
          border: `1px solid ${THEME.borderLight}`,
          marginBottom: '16px'
        }}>
          <div style={{ 
            marginBottom: '8px',
            fontWeight: 'bold',
            color: THEME.textDark
          }}>
            Thoughts:
          </div>
          <div style={{
            whiteSpace: 'pre-wrap',
            lineHeight: '1.6',
            color: THEME.textDark,
            fontStyle: 'italic'
          }}>
            {data.thoughts}
          </div>
        </div>
      )}

      <div style={{
        position: 'absolute',
        bottom: '-12px',
        right: '16px',
        backgroundColor: THEME.textMuted,
        color: THEME.textLight,
        padding: '2px 8px',
        borderRadius: '12px',
        fontSize: '0.85em'
      }}>
        {formatActionTime(action.created_at)}
      </div>
    </div>
  );
};

const SecretRoleAssignmentAction = ({ action, users, THEME }) => {
  const COMMON_STYLES = getCommonStyles(THEME);
  const { data } = action;
  
  // Get user info from game_instance_player_id
  const user = getUserById(users, action.game_instance_player_id);
  const modelColor = user ? getModelColor(user.model, THEME) : getModelColorsFromTheme(THEME)[0];

  // Determine alignment color based on role
  const getRoleColor = () => {
    const message = data.message?.toLowerCase() || '';
    if (message.includes('demon') || message.includes('minion') || 
        message.includes('baron') || message.includes('poisoner') || 
        message.includes('spy') || message.includes('scarlet') || 
        message.includes('imp')) {
      return THEME.evil;
    } else if (message.includes('outsider') || message.includes('drunk') || 
               message.includes('butler') || message.includes('recluse') || 
               message.includes('saint')) {
      return THEME.neutral;
    }
    return THEME.good;
  };

  return (
    <div style={{
      ...COMMON_STYLES.actionContainer,
      backgroundColor: THEME.bgDark,
      border: `2px solid ${THEME.accent}`,
      boxShadow: `0 0 30px ${THEME.accent}33`,
      position: 'relative'
    }}>
      <div style={{
        position: 'absolute',
        top: '-14px',
        left: '50%',
        transform: 'translateX(-50%)',
        backgroundColor: getRoleColor(),
        color: THEME.textLight,
        padding: '4px 20px',
        borderRadius: '20px',
        fontSize: '1.1em',
        fontWeight: 'bold',
        letterSpacing: '1px',
        textTransform: 'uppercase',
        boxShadow: '0 2px 8px ${THEME.textDark}4d'
      }}>
        {THEME.roleEmoji} Secret Role Assignment
      </div>

      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '12px',
        marginTop: '20px',
        marginBottom: '20px',
        padding: '0 16px'
      }}>
        {user && (
          <div style={{
            ...COMMON_STYLES.playerBadge,
            backgroundColor: modelColor,
            color: THEME.textDark,
            padding: '12px 24px',
            fontSize: '1.1em',
            boxShadow: '0 0 10px ${THEME.accent}4d'
          }}>
            {user.name} ({getDisplayModelName(user)})
          </div>
        )}
      </div>

      <div style={{
        backgroundColor: '${THEME.accent}1a',
        padding: '20px',
        borderRadius: '6px',
        border: `2px solid ${THEME.accent}`,
        marginBottom: '16px',
        margin: '0 16px 16px',
        boxShadow: 'inset 0 0 20px ${THEME.accent}1a'
      }}>
        <div style={{ 
          marginBottom: '12px',
          fontWeight: 'bold',
          color: THEME.accent,
          fontSize: '1.2em',
          textAlign: 'center',
          textTransform: 'uppercase',
          letterSpacing: '2px'
        }}>
          Your Role
        </div>
        <div style={{
          whiteSpace: 'pre-wrap',
          lineHeight: '1.8',
          color: THEME.textLight,
          fontSize: '1.15em',
          fontWeight: 'bold',
          textAlign: 'center',
          fontFamily: 'Georgia, serif'
        }}>
          {data.message}
        </div>
      </div>

      {data.prompt && (
        <div style={{
          backgroundColor: THEME.bgMedium,
          padding: '16px',
          borderRadius: '6px',
          border: `1px solid ${THEME.bgDark}`,
          marginBottom: '16px'
        }}>
          <div style={{ 
            marginBottom: '8px',
            fontWeight: 'bold',
            color: THEME.textMuted
          }}>
            Prompt:
          </div>
          <div style={{
            whiteSpace: 'pre-wrap',
            lineHeight: '1.6',
            color: THEME.textLight,
            fontSize: '0.95em',
            fontStyle: 'italic'
          }}>
            {data.prompt}
          </div>
        </div>
      )}

      {data.intro_speech && (
        <div style={{
          backgroundColor: THEME.bgMedium,
          padding: '16px',
          borderRadius: '6px',
          border: `1px solid ${THEME.bgDark}`,
          marginBottom: '16px'
        }}>
          <div style={{ 
            marginBottom: '8px',
            fontWeight: 'bold',
            color: THEME.accent
          }}>
            Introduction Speech:
          </div>
          <div style={{
            whiteSpace: 'pre-wrap',
            lineHeight: '1.6',
            color: THEME.textLight,
            fontStyle: 'italic',
            fontSize: '1.05em'
          }}>
            "{data.intro_speech}"
          </div>
        </div>
      )}

      {data.intro_setting && (
        <div style={{
          backgroundColor: THEME.bgMedium,
          padding: '16px',
          borderRadius: '6px',
          border: `1px solid ${THEME.bgDark}`,
          marginBottom: '16px'
        }}>
          <div style={{ 
            marginBottom: '8px',
            fontWeight: 'bold',
            color: THEME.private
          }}>
            Setting:
          </div>
          <div style={{
            whiteSpace: 'pre-wrap',
            lineHeight: '1.6',
            color: THEME.textLight
          }}>
            {data.intro_setting}
          </div>
        </div>
      )}

      {data.visual_description && (
        <div style={{
          backgroundColor: THEME.bgMedium,
          padding: '16px',
          borderRadius: '6px',
          border: `1px solid ${THEME.bgDark}`,
          marginBottom: '16px'
        }}>
          <div style={{ 
            marginBottom: '8px',
            fontWeight: 'bold',
            color: THEME.accent
          }}>
            Visual Description:
          </div>
          <div style={{
            whiteSpace: 'pre-wrap',
            lineHeight: '1.6',
            color: THEME.textLight
          }}>
            {data.visual_description}
          </div>
        </div>
      )}

      {data.vocal_description && (
        <div style={{
          backgroundColor: THEME.bgMedium,
          padding: '16px',
          borderRadius: '6px',
          border: `1px solid ${THEME.bgDark}`,
          marginBottom: '16px'
        }}>
          <div style={{ 
            marginBottom: '8px',
            fontWeight: 'bold',
            color: THEME.secondary
          }}>
            Vocal Description:
          </div>
          <div style={{
            whiteSpace: 'pre-wrap',
            lineHeight: '1.6',
            color: THEME.textLight
          }}>
            {data.vocal_description}
          </div>
        </div>
      )}

      {data.thoughts && (
        <div style={{
          backgroundColor: THEME.bgMedium,
          padding: '16px',
          borderRadius: '6px',
          border: `1px solid ${THEME.bgDark}`,
          marginBottom: '16px'
        }}>
          <div style={{ 
            marginBottom: '8px',
            fontWeight: 'bold',
            color: THEME.evil
          }}>
            Strategic Thoughts:
          </div>
          <div style={{
            whiteSpace: 'pre-wrap',
            lineHeight: '1.6',
            color: THEME.textLight,
            fontStyle: 'italic'
          }}>
            {data.thoughts}
          </div>
        </div>
      )}

      {data.video_url && (
        <div style={{
          backgroundColor: THEME.bgMedium,
          padding: '16px',
          borderRadius: '6px',
          border: `1px solid ${THEME.bgDark}`,
          marginBottom: '16px'
        }}>
          <div style={{ 
            marginBottom: '12px',
            fontWeight: 'bold',
            color: THEME.evil
          }}>
            Character Video:
          </div>
          <div style={{
            width: '100%',
            maxWidth: '600px',
            margin: '0 auto'
          }}>
            <video
              controls
              style={{
                width: '100%',
                borderRadius: '6px',
                backgroundColor: THEME.bgDark
              }}
              preload="metadata"
            >
              <source src={data.video_url} type="video/mp4" />
              Your browser does not support the video tag.
            </video>
          </div>
        </div>
      )}

      <div style={{
        position: 'absolute',
        bottom: '-12px',
        right: '16px',
        backgroundColor: THEME.textMuted,
        color: THEME.textLight,
        padding: '2px 8px',
        borderRadius: '12px',
        fontSize: '0.85em'
      }}>
        {formatActionTime(action.created_at)}
      </div>
    </div>
  );
};

const InitialInvestigationAction = ({ action, users, THEME }) => {
  const COMMON_STYLES = getCommonStyles(THEME);
  const { data } = action;

  // Get user info from game_instance_player_id
  const user = getUserById(users, action.game_instance_player_id);
  const modelColor = user ? getModelColor(user.model, THEME) : THEME.bgLight;

  return (
    <div style={{
      backgroundColor: THEME.bgLight,
      border: `2px solid ${THEME.good}`,
      borderRadius: '8px',
      padding: '16px',
      marginBottom: '12px',
      position: 'relative'
    }}>
      <div style={{
        position: 'absolute',
        top: '-12px',
        left: '50%',
        transform: 'translateX(-50%)',
        backgroundColor: THEME.good,
        color: THEME.textLight,
        padding: '4px 16px',
        borderRadius: '16px',
        fontSize: '1.1em',
        fontWeight: 'bold',
        boxShadow: '0 2px 4px ${THEME.textDark}1a'
      }}>
        Initial Investigation
      </div>

      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        marginTop: '8px',
        marginBottom: '16px'
      }}>
        {user && (
          <div style={{
            backgroundColor: modelColor,
            padding: '8px 16px',
            borderRadius: '6px',
            color: THEME.textDark,
            fontWeight: 'bold',
            flex: 1,
            textAlign: 'center'
          }}>
            {user.name} ({getDisplayModelName(user)})
          </div>
        )}
      </div>

      <div style={{
        backgroundColor: THEME.textLight,
        padding: '16px',
        borderRadius: '6px',
        border: `1px solid ${THEME.borderLight}`,
        marginBottom: '16px'
      }}>
        <div style={{
          whiteSpace: 'pre-wrap',
          lineHeight: '1.6',
          color: THEME.textDark,
          fontSize: '1.1em',
          fontWeight: 'bold'
        }}>
          {data.message}
        </div>
      </div>

      {data.info && (
        <div style={{
          backgroundColor: THEME.textLight,
          padding: '16px',
          borderRadius: '6px',
          border: `1px solid ${THEME.borderLight}`,
          marginBottom: '16px'
        }}>
          <div style={{ 
            marginBottom: '12px',
            fontWeight: 'bold',
            color: THEME.textDark,
            fontSize: '1.1em'
          }}>
            Investigation Details:
          </div>
          
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '12px'
          }}>
            {data.info.role && (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <span style={{
                  backgroundColor: THEME.private,
                  color: THEME.textLight,
                  padding: '4px 8px',
                  borderRadius: '4px',
                  fontSize: '0.9em',
                  fontWeight: 'bold'
                }}>
                  Role:
                </span>
                <span style={{
                  backgroundColor: THEME.bgMedium,
                  padding: '4px 8px',
                  borderRadius: '4px',
                  fontSize: '0.9em',
                  fontWeight: 'bold'
                }}>
                  {data.info.role}
                </span>
              </div>
            )}
            
            {data.info.players && data.info.players.length > 0 && (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <span style={{
                  backgroundColor: THEME.secondary,
                  color: THEME.textLight,
                  padding: '4px 8px',
                  borderRadius: '4px',
                  fontSize: '0.9em',
                  fontWeight: 'bold'
                }}>
                  Players:
                </span>
                <div style={{
                  display: 'flex',
                  gap: '4px',
                  flexWrap: 'wrap'
                }}>
                  {data.info.players.map((player, index) => (
                    <span key={index} style={{
                      backgroundColor: THEME.bgMedium,
                      padding: '4px 8px',
                      borderRadius: '4px',
                      fontSize: '0.9em',
                      fontWeight: 'bold'
                    }}>
                      {player}
                    </span>
                  ))}
                </div>
              </div>
            )}
            
            {data.info.true_info !== undefined && (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <span style={{
                  backgroundColor: data.info.true_info ? THEME.good : THEME.evil,
                  color: THEME.textLight,
                  padding: '4px 8px',
                  borderRadius: '4px',
                  fontSize: '0.9em',
                  fontWeight: 'bold'
                }}>
                  {data.info.true_info ? 'True Info' : 'False Info'}
                </span>
              </div>
            )}
          </div>
        </div>
      )}

      <div style={{
        position: 'absolute',
        bottom: '-12px',
        right: '16px',
        backgroundColor: THEME.textMuted,
        color: THEME.textLight,
        padding: '2px 8px',
        borderRadius: '12px',
        fontSize: '0.85em'
      }}>
        {formatActionTime(action.created_at)}
      </div>
    </div>
  );
};

const EmpathReadingAction = ({ action, users, THEME }) => {
  const COMMON_STYLES = getCommonStyles(THEME);
  const { data } = action;

  // Get user info from game_instance_player_id
  const user = getUserById(users, action.game_instance_player_id);
  const modelColor = user ? getModelColor(user.model, THEME) : THEME.bgLight;

  return (
    <div style={{
      backgroundColor: THEME.bgLight,
      border: `2px solid ${THEME.primary}`,
      borderRadius: '8px',
      padding: '16px',
      marginBottom: '12px',
      position: 'relative'
    }}>
      <div style={{
        position: 'absolute',
        top: '-12px',
        left: '50%',
        transform: 'translateX(-50%)',
        backgroundColor: THEME.primary,
        color: THEME.textLight,
        padding: '4px 16px',
        borderRadius: '16px',
        fontSize: '1.1em',
        fontWeight: 'bold',
        boxShadow: '0 2px 4px ${THEME.textDark}1a'
      }}>
        Empath Reading
      </div>

      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        marginTop: '8px',
        marginBottom: '16px'
      }}>
        {user && (
          <div style={{
            backgroundColor: modelColor,
            padding: '8px 16px',
            borderRadius: '6px',
            color: THEME.textDark,
            fontWeight: 'bold',
            flex: 1,
            textAlign: 'center'
          }}>
            {user.name} ({getDisplayModelName(user)})
          </div>
        )}
      </div>

      <div style={{
        backgroundColor: THEME.textLight,
        padding: '16px',
        borderRadius: '6px',
        border: `1px solid ${THEME.borderLight}`,
        marginBottom: '16px'
      }}>
        <div style={{
          whiteSpace: 'pre-wrap',
          lineHeight: '1.6',
          color: THEME.textDark,
          fontSize: '1.1em',
          fontWeight: 'bold'
        }}>
          {data.message}
        </div>
      </div>

      <div style={{
        backgroundColor: THEME.textLight,
        padding: '16px',
        borderRadius: '6px',
        border: `1px solid ${THEME.borderLight}`,
        marginBottom: '16px'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          justifyContent: 'center'
        }}>
          <span style={{
            backgroundColor: data.count > 0 ? THEME.evil : THEME.good,
            color: THEME.textLight,
            padding: '8px 16px',
            borderRadius: '6px',
            fontSize: '1.2em',
            fontWeight: 'bold'
          }}>
            {data.count} Evil Neighbors
          </span>
        </div>
      </div>

      <div style={{
        position: 'absolute',
        bottom: '-12px',
        right: '16px',
        backgroundColor: THEME.textMuted,
        color: THEME.textLight,
        padding: '2px 8px',
        borderRadius: '12px',
        fontSize: '0.85em'
      }}>
        {formatActionTime(action.created_at)}
      </div>
    </div>
  );
};

// Custom Video Segment Form Component
const CustomVideoSegmentForm = ({ onCancel, onSubmit, spaces, playerProfiles, THEME }) => {
  const [description, setDescription] = React.useState('');
  const [selectedSpace, setSelectedSpace] = React.useState('');
  const [selectedCharacters, setSelectedCharacters] = React.useState([]);
  const [shotDescription, setShotDescription] = React.useState('');
  const [camera, setCamera] = React.useState('');
  const [action, setAction] = React.useState('');
  const [mood, setMood] = React.useState('');

  const handleSubmit = () => {
    if (!description || !shotDescription || !camera || !action || !mood) {
      alert('Please fill in all required fields');
      return;
    }

    onSubmit({
      description,
      space: selectedSpace || null,
      characters_involved: selectedCharacters,
      shot_description: shotDescription,
      camera,
      action,
      mood,
      dialogue: null
    });
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0,0,0,0.8)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    }}>
      <div style={{
        background: THEME.bgDark,
        border: `2px solid ${THEME.accent}`,
        borderRadius: '12px',
        padding: '24px',
        maxWidth: '600px',
        width: '90%',
        maxHeight: '80dvh',
        overflow: 'auto'
      }}>
        <h2 style={{ color: THEME.accent, marginBottom: '16px' }}>Add Custom Video Segment</h2>

        <div style={{ marginBottom: '16px' }}>
          <label style={{ display: 'block', color: THEME.textLight, marginBottom: '4px' }}>Description *</label>
          <input
            type="text"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="e.g., Establishing shot of the house at sunset"
            style={{
              width: '100%',
              padding: '8px',
              background: THEME.bgMedium,
              border: `1px solid ${THEME.borderDark}`,
              borderRadius: '4px',
              color: THEME.textLight,
              fontSize: '0.9em'
            }}
          />
        </div>

        <div style={{ marginBottom: '16px' }}>
          <label style={{ display: 'block', color: THEME.textLight, marginBottom: '4px' }}>Shot Description *</label>
          <textarea
            value={shotDescription}
            onChange={(e) => setShotDescription(e.target.value)}
            placeholder="e.g., Wide shot showing the exterior"
            style={{
              width: '100%',
              padding: '8px',
              background: THEME.bgMedium,
              border: `1px solid ${THEME.borderDark}`,
              borderRadius: '4px',
              color: THEME.textLight,
              fontSize: '0.9em',
              minHeight: '60px',
              resize: 'vertical'
            }}
          />
        </div>

        <div style={{ marginBottom: '16px' }}>
          <label style={{ display: 'block', color: THEME.textLight, marginBottom: '4px' }}>Camera *</label>
          <input
            type="text"
            value={camera}
            onChange={(e) => setCamera(e.target.value)}
            placeholder="e.g., Slow dolly forward"
            style={{
              width: '100%',
              padding: '8px',
              background: THEME.bgMedium,
              border: `1px solid ${THEME.borderDark}`,
              borderRadius: '4px',
              color: THEME.textLight,
              fontSize: '0.9em'
            }}
          />
        </div>

        <div style={{ marginBottom: '16px' }}>
          <label style={{ display: 'block', color: THEME.textLight, marginBottom: '4px' }}>Action *</label>
          <input
            type="text"
            value={action}
            onChange={(e) => setAction(e.target.value)}
            placeholder="e.g., Sun setting behind the house"
            style={{
              width: '100%',
              padding: '8px',
              background: THEME.bgMedium,
              border: `1px solid ${THEME.borderDark}`,
              borderRadius: '4px',
              color: THEME.textLight,
              fontSize: '0.9em'
            }}
          />
        </div>

        <div style={{ marginBottom: '16px' }}>
          <label style={{ display: 'block', color: THEME.textLight, marginBottom: '4px' }}>Mood *</label>
          <input
            type="text"
            value={mood}
            onChange={(e) => setMood(e.target.value)}
            placeholder="e.g., Peaceful, contemplative"
            style={{
              width: '100%',
              padding: '8px',
              background: THEME.bgMedium,
              border: `1px solid ${THEME.borderDark}`,
              borderRadius: '4px',
              color: THEME.textLight,
              fontSize: '0.9em'
            }}
          />
        </div>

        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
          <button
            onClick={onCancel}
            style={{
              padding: '8px 16px',
              borderRadius: '6px',
              border: 'none',
              backgroundColor: THEME.bgMedium,
              color: THEME.textLight,
              cursor: 'pointer',
              fontSize: '0.9em'
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            style={{
              padding: '8px 16px',
              borderRadius: '6px',
              border: 'none',
              backgroundColor: THEME.accent,
              color: THEME.textLight,
              cursor: 'pointer',
              fontSize: '0.9em',
              fontWeight: '600'
            }}
          >
            Add Segment
          </button>
        </div>
      </div>
    </div>
  );
};

const EpisodeAction = ({ action, users, THEME, allActions = [], onActionUpdate, gameType }) => {
  const COMMON_STYLES = getCommonStyles(THEME);
  const { data } = action;
  const [expandedSegments, setExpandedSegments] = React.useState({});
  const [generatingVideo, setGeneratingVideo] = React.useState({});
  const [processingVideo, setProcessingVideo] = React.useState({});
  const [generatingImage, setGeneratingImage] = React.useState({});
  const [generatingTTS, setGeneratingTTS] = React.useState({});
  const [generatingHostVideo, setGeneratingHostVideo] = React.useState({});
  const [regeneratingPrompt, setRegeneratingPrompt] = React.useState({});

  // Episode editing states
  const [segments, setSegments] = React.useState(data.segments || []);
  const [draggedSegmentIndex, setDraggedSegmentIndex] = React.useState(null);
  const [dragOverIndex, setDragOverIndex] = React.useState(null);
  const [savingChanges, setSavingChanges] = React.useState(false);
  const [unsavedChanges, setUnsavedChanges] = React.useState(false);
  const [showCustomVideoForm, setShowCustomVideoForm] = React.useState(false);

  // Sync segments with data.segments when parent updates
  React.useEffect(() => {
    setSegments(data.segments || []);
  }, [data.segments]);

  // Warn user about unsaved changes before navigation
  React.useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (unsavedChanges) {
        e.preventDefault();
        e.returnValue = 'You have unsaved changes to this episode. Are you sure you want to leave?';
        return e.returnValue;
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [unsavedChanges]);

  // Popup configurations for each segment
  const [popupConfigs, setPopupConfigs] = React.useState(() => {
    // Initialize with AI-suggested popups from episode data
    const initialConfigs = {};
    if (data.segments) {
      data.segments.forEach(segment => {
        if (segment.popup) {
          // Handle both old string format and new object format
          if (typeof segment.popup === 'string') {
            // Check if string has "Title: Body" format
            const colonIndex = segment.popup.indexOf(':');
            let title = '';
            let text = segment.popup;

            if (colonIndex > 0 && colonIndex < segment.popup.length - 1) {
              // Split on first colon
              title = segment.popup.substring(0, colonIndex).trim();
              text = segment.popup.substring(colonIndex + 1).trim();
            }

            initialConfigs[segment.segment_number] = {
              title: title,
              text: text,
              startTime: '',
              endTime: '',
              x: 30,
              y: 26
            };
          } else {
            initialConfigs[segment.segment_number] = {
              title: segment.popup.title || '',
              text: segment.popup.body || '',
              startTime: '',
              endTime: '',
              x: 30,
              y: 26
            };
          }
        }
      });
    }
    return initialConfigs;
  });

  const toggleSegment = (segmentNumber) => {
    setExpandedSegments(prev => ({
      ...prev,
      [segmentNumber]: !prev[segmentNumber]
    }));
  };

  // Save episode segment changes to database
  const saveEpisodeChanges = async (updatedSegments) => {
    setSavingChanges(true);
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/takeGameStep?updateActionData=true`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            actionId: action.id,
            dataUpdates: { segments: updatedSegments }
          })
        }
      );

      const result = await parseApiResponse(response);

      if (result.success) {
        // Update parent state for consistency
        if (onActionUpdate) {
          onActionUpdate(action.id, { segments: updatedSegments });
        }
        setUnsavedChanges(false);
        return true;
      } else {
        alert(`Failed to save changes: ${result.error || 'Unknown error'}`);
        return false;
      }
    } catch (error) {
      console.error('Error saving episode changes:', error);
      alert(`Error saving: ${error.message}\n\nYour changes are preserved locally. Try saving again.`);
      return false;
    } finally {
      setSavingChanges(false);
    }
  };

  // Handle segment drop (reorder or add video)
  const handleSegmentDrop = (e, dropIndex) => {
    e.preventDefault();
    setDragOverIndex(null);

    const segmentReorderData = e.dataTransfer.getData('segment-reorder');
    const videoData = e.dataTransfer.getData('video-data');

    if (segmentReorderData) {
      // Reordering existing segment
      const fromIndex = parseInt(segmentReorderData);
      if (fromIndex === dropIndex || fromIndex === dropIndex - 1) {
        return; // No change
      }

      const newSegments = [...segments];
      const [movedSegment] = newSegments.splice(fromIndex, 1);

      // Adjust drop index if moving from before to after
      const adjustedDropIndex = fromIndex < dropIndex ? dropIndex - 1 : dropIndex;
      newSegments.splice(adjustedDropIndex, 0, movedSegment);

      // Renumber segments
      const renumberedSegments = newSegments.map((s, idx) => ({
        ...s,
        segment_number: idx + 1
      }));

      setSegments(renumberedSegments);
      setUnsavedChanges(true);
      saveEpisodeChanges(renumberedSegments);

    } else if (videoData) {
      // Adding new video segment
      handleVideoSegmentDrop(JSON.parse(videoData), dropIndex);
    }

    setDraggedSegmentIndex(null);
  };

  // Handle video drop (add diary room or dialogue segment)
  const handleVideoSegmentDrop = async (videoData, dropIndex) => {
    console.log('[Episode] Adding video segment at index:', dropIndex, videoData);

    // Determine segment type from video metadata
    let segmentType;
    let actionId = null;

    if (videoData.type === 'episode_dialogue') {
      segmentType = 'dialogue';
      const characterName = videoData.metadata?.character;
      const matchingAction = allActions.find(a =>
        a.data?.sender_name === characterName &&
        (a.type === 'public_message' || a.type === 'private_message')
      );
      actionId = matchingAction?.id;
    } else if (videoData.type === 'episode_diary_room') {
      segmentType = 'diary_room';
      const characterName = videoData.metadata?.character;
      const matchingAction = allActions.find(a =>
        a.data?.sender_name === characterName &&
        a.data?.diary_room === true
      );
      actionId = matchingAction?.id;
    } else {
      alert('Unknown video type for episode insertion');
      return;
    }

    if (!actionId) {
      alert('Could not find matching action for this video');
      return;
    }

    // Create new segment
    const newSegment = {
      segment_number: dropIndex + 1,
      segment_type: segmentType,
      action_id: actionId,
      duration_estimate: 20,
      why_included: `Added by drag-and-drop: ${videoData.metadata?.character || 'Unknown'}`,
      notes: `Inserted at position ${dropIndex + 1}`
    };

    // Insert at drop position
    const newSegments = [...segments];
    newSegments.splice(dropIndex, 0, newSegment);

    // Renumber
    const renumberedSegments = newSegments.map((s, idx) => ({
      ...s,
      segment_number: idx + 1
    }));

    setSegments(renumberedSegments);
    await saveEpisodeChanges(renumberedSegments);

    alert(`Added ${segmentType} segment at position ${dropIndex + 1}`);
  };

  // Add custom video segment from form
  const addCustomVideoSegment = (customVideoPrompt) => {
    const newSegment = {
      segment_number: segments.length + 1,
      segment_type: 'custom_video',
      duration_estimate: 10,
      custom_video_prompt: customVideoPrompt,
      why_included: 'Custom video segment',
      notes: 'Added manually via form'
    };

    const newSegments = [...segments, newSegment];
    setSegments(newSegments);
    setUnsavedChanges(true);
    saveEpisodeChanges(newSegments);
    setShowCustomVideoForm(false);
  };

  // Look up a referenced action by ID
  const getReferencedAction = (actionId) => {
    if (!actionId || !allActions) return null;
    return allActions.find(a => a.id === actionId);
  };

  // Generate video for a segment's referenced action
  const generateVideoForSegment = async (segment, videoType = 'external') => {
    if (!segment.action_id) return;

    const referencedAction = getReferencedAction(segment.action_id);
    if (!referencedAction) {
      alert('Referenced action not found');
      return;
    }

    const key = `${segment.segment_number}-${videoType}`;
    setGeneratingVideo(prev => ({ ...prev, [key]: true }));

    try {
      const response = await fetch(`${API_BASE_URL}/api/takeGameStep?generateVideo=true&actionId=${referencedAction.id}&videoType=${videoType}`);
      const result = await parseApiResponse(response);

      if (result.video_url && onActionUpdate) {
        // Update the referenced action with the new video
        onActionUpdate(referencedAction.id, {
          videos: {
            ...referencedAction.data.videos,
            [videoType]: {
              ...referencedAction.data.videos?.[videoType],
              url: result.video_url,
              model: result.model
            }
          }
        });
      }
    } catch (error) {
      console.error('Error generating video:', error);
      alert(`Error: ${error.message}`);
    } finally {
      setGeneratingVideo(prev => ({ ...prev, [key]: false }));
    }
  };

  // Generate video for custom video segment
  const generateCustomVideoForSegment = async (segment) => {
    if (!segment.custom_video_prompt) {
      alert('No custom video prompt found');
      return;
    }

    const key = segment.segment_number;
    setGeneratingVideo(prev => ({ ...prev, [key]: true }));

    try {
      const prompt = segment.custom_video_prompt;
      const gameInstanceId = action.game_instance_id;

      if (!gameInstanceId) {
        alert('No game instance ID available');
        return;
      }

      // Build the prompt string from the custom video prompt object
      let fullPrompt = prompt.description || '';
      if (prompt.space) fullPrompt += `\nSpace: ${prompt.space}`;
      if (prompt.characters_involved) fullPrompt += `\nCharacters: ${prompt.characters_involved.join(', ')}`;
      if (prompt.shot_description) fullPrompt += `\nShot: ${prompt.shot_description}`;
      if (prompt.camera) fullPrompt += `\nCamera: ${prompt.camera}`;
      if (prompt.action) fullPrompt += `\nAction: ${prompt.action}`;
      if (prompt.mood) fullPrompt += `\nMood: ${prompt.mood}`;
      if (prompt.visual_style) fullPrompt += `\nVisual Style: ${prompt.visual_style}`;
      if (prompt.dialogue) fullPrompt += `\nDialogue: ${prompt.dialogue}`;

      // Check if scene images already exist for this segment
      const sceneImages = data.segment_scene_images?.[segment.segment_number];
      let imageParams = '';

      if (sceneImages?.reference_image && sceneImages?.after_image) {
        // Pass existing reference/before/after images to backend to avoid regenerating
        console.log('Using existing scene images:', sceneImages);
        imageParams += `&referenceImageUrl=${encodeURIComponent(sceneImages.reference_image)}`;
        imageParams += `&afterImageUrl=${encodeURIComponent(sceneImages.after_image)}`;
        if (sceneImages.before_image) {
          imageParams += `&beforeImageUrl=${encodeURIComponent(sceneImages.before_image)}`;
        }
      } else {
        console.log('No existing scene images found - backend will generate new ones');
      }

      const response = await fetch(
        `${API_BASE_URL}/api/takeGameStep?generateCustomVideo=true&gameInstanceId=${gameInstanceId}&userDescription=${encodeURIComponent(fullPrompt)}&freeform=false&enhancePrompt=true${imageParams}`,
        { method: 'GET' }
      );

      const result = await parseApiResponse(response);

      if (result.success && result.video_url) {
        // Store the video in segment_videos
        const updatedSegmentVideos = {
          ...(data.segment_videos || {}),
          [segment.segment_number]: {
            url: result.video_url,
            prompt: fullPrompt,
            generated_at: new Date().toISOString(),
            videos: result.videos || [{ url: result.video_url }]
          }
        };

        console.log('[Custom Video] Video generated successfully:', result.video_url);
        console.log('[Custom Video] Updated segment_videos:', updatedSegmentVideos);

        if (onActionUpdate) {
          onActionUpdate(action.id, { segment_videos: updatedSegmentVideos });
          console.log('[Custom Video] Called onActionUpdate with segment_videos');
        } else {
          console.warn('[Custom Video] onActionUpdate is not available!');
        }

        alert(`Video generated successfully! ${result.videos?.length || 1} part(s)`);
      } else {
        alert(`Failed to generate video: ${result.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error generating custom video:', error);
      alert(`Error: ${error.message}`);
    } finally {
      setGeneratingVideo(prev => ({ ...prev, [key]: false }));
    }
  };

  // Save popup config to action's database record
  const savePopupConfigToAction = async (actionId, videoType, popupConfig) => {
    if (!popupConfig || !popupConfig.text) return;

    try {
      // Convert empty strings to 0 for times
      const startTime = popupConfig.startTime === '' ? 0 : parseFloat(popupConfig.startTime) || 0;
      const endTime = popupConfig.endTime === '' ? 0 : parseFloat(popupConfig.endTime) || 0;

      // Use provided title or default to "💡 Pro-tip"
      const title = popupConfig.title && popupConfig.title.trim() !== ''
        ? popupConfig.title
        : '💡 Pro-tip';

      const textPopupData = {
        title: title,
        body: popupConfig.text,
        x: parseInt(popupConfig.x) || 30,
        y: parseInt(popupConfig.y) || 26,
        startTime: startTime,
        endTime: endTime
      };

      console.log(`[Save Popup] Saving popup config for action ${actionId}, videoType ${videoType}:`, textPopupData);

      const response = await fetch(`${API_BASE_URL}/api/takeGameStep?saveTextPopup=true&actionId=${actionId}&videoKey=${videoType}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: textPopupData.title,
          body: textPopupData.body,
          x: textPopupData.x,
          y: textPopupData.y,
          startTime: textPopupData.startTime,
          endTime: textPopupData.endTime
        })
      });

      const result = await parseApiResponse(response);
      if (result.success) {
        console.log('[Save Popup] Popup config saved successfully');
        return true;
      } else {
        console.error('[Save Popup] Failed to save:', result);
        return false;
      }
    } catch (error) {
      console.error('[Save Popup] Error saving popup config:', error);
      return false;
    }
  };

  // Process video for a segment's referenced action
  const processVideoForSegment = async (segment, videoType = 'external') => {
    if (!segment.action_id) return;

    const referencedAction = getReferencedAction(segment.action_id);
    if (!referencedAction) {
      alert('Referenced action not found');
      return;
    }

    // Get veo3 video data (same logic as display)
    const veo3Data = referencedAction.data?.videos?.[videoType]?.veo3;

    // Normalize veo3 data to get video URL
    let videoUrl = null;
    if (Array.isArray(veo3Data) && veo3Data.length > 0) {
      // New multi-video format - use LAST video (final clip in sequence)
      videoUrl = veo3Data[veo3Data.length - 1].url;
    } else if (veo3Data?.video_url) {
      // Old single video format
      videoUrl = veo3Data.video_url;
    }

    if (!videoUrl) {
      alert('No video to process. Generate video first.');
      return;
    }

    // Save popup config before processing (if it exists)
    const popupConfig = popupConfigs[segment.segment_number];
    if (popupConfig && popupConfig.text) {
      console.log('[Process Video] Saving popup config before processing...');
      const saved = await savePopupConfigToAction(referencedAction.id, videoType, popupConfig);
      if (!saved) {
        console.warn('[Process Video] Failed to save popup config, but continuing with processing');
      }
    }

    const key = `${segment.segment_number}-${videoType}`;
    setProcessingVideo(prev => ({ ...prev, [key]: true }));

    try {
      const response = await fetch(`${API_BASE_URL}/api/takeGameStep?processDialogueVideo=true&actionId=${referencedAction.id}&videoType=${videoType}`);
      const result = await parseApiResponse(response);

      if (result.success && result.processed_videos && onActionUpdate) {
        console.log('[Process Video] Video processing complete:', result);

        // Extract processed video URLs and update action data
        const processedVideos = result.processed_videos;
        const updatedVideoData = { ...referencedAction.data.videos };

        if (!updatedVideoData[videoType]) {
          updatedVideoData[videoType] = {};
        }

        // Update veo3 processed video URL
        const veo3Processed = processedVideos.find(pv => pv.type === 'veo3');
        if (veo3Processed) {
          if (!updatedVideoData[videoType].veo3) {
            updatedVideoData[videoType].veo3 = {};
          }

          if (veo3Processed.videos) {
            // Array format - update processed_videos array
            updatedVideoData[videoType].veo3.processed_videos = veo3Processed.videos;
          } else if (veo3Processed.url) {
            // Single video format - update processed_video_url
            updatedVideoData[videoType].veo3.processed_video_url = veo3Processed.url;
          }
        }

        // Update the action with new video data
        onActionUpdate(referencedAction.id, { videos: updatedVideoData });
      }
    } catch (error) {
      console.error('Error processing video:', error);
      alert(`Error: ${error.message}`);
    } finally {
      setProcessingVideo(prev => ({ ...prev, [key]: false }));
    }
  };

  // Process video segment (add popups without name overlays)
  // Works for host_intro, host_transition, host_outro, and custom_video segments
  const processHostVideoSegment = async (segment) => {
    // Determine key based on segment type
    const segmentType = segment.segment_type || 'host';
    const keyPrefix = segmentType === 'custom_video' ? 'custom' : 'host';
    const key = `${keyPrefix}-${segment.segment_number}`;
    setProcessingVideo(prev => ({ ...prev, [key]: true }));

    try {
      const response = await fetch(`${API_BASE_URL}/api/takeGameStep?processHostVideo=true&actionId=${action.id}&segmentNumber=${segment.segment_number}`);
      const result = await parseApiResponse(response);

      if (result.success && result.processed_video_url && onActionUpdate) {
        console.log('[Process Host Video] Video processing complete:', result);

        // Update segment_videos with processed URL
        const segmentVideos = { ...data.segment_videos };
        if (!segmentVideos[segment.segment_number]) {
          segmentVideos[segment.segment_number] = {};
        }
        segmentVideos[segment.segment_number].processed_url = result.processed_video_url;

        // Update the episode action with new processed video
        onActionUpdate(action.id, { segment_videos: segmentVideos });

        alert(`Host video processed successfully! ${result.had_popup ? 'Popup added.' : 'No popup to add.'}`);
      } else {
        alert(`Processing failed: ${result.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error processing host video:', error);
      alert(`Error: ${error.message}`);
    } finally {
      setProcessingVideo(prev => ({ ...prev, [key]: false }));
    }
  };

  // Generate scene image for a segment's referenced action
  const generateSceneImageForSegment = async (segment, videoType = 'external') => {
    if (!segment.action_id) {
      // For custom video segments without action_id
      const key = segment.segment_number;
      setGeneratingImage(prev => ({ ...prev, [key]: true }));

      try {
        const prompt = segment.custom_video_prompt;
        if (!prompt) {
          alert('No custom video prompt found');
          return;
        }

        const fullPrompt = `${prompt.shot_description}. Camera: ${prompt.camera}. Action: ${prompt.action}. Mood: ${prompt.mood}. ${prompt.description}`;

        // Get gameInstanceId from action
        const gameInstanceId = action.game_instance_id;
        if (!gameInstanceId) {
          alert('No game instance ID available');
          return;
        }

        const response = await fetch(`${API_BASE_URL}/api/takeGameStep?generateSceneImage=true&gameInstanceId=${gameInstanceId}&prompt=${encodeURIComponent(fullPrompt)}`);
        const result = await parseApiResponse(response);

        if ((result.reference_image || result.scene_image_url) && onActionUpdate) {
          // Store the scene images in the episode action's segment_scene_images field
          // Support three formats: new (reference/before/after), old (start/end), legacy (single)
          const sceneImageData = result.reference_image ? {
            reference_image: result.reference_image,
            before_image: result.before_image,
            after_image: result.after_image,
            generated_at: new Date().toISOString()
          } : result.start_frame ? {
            start_frame: result.start_frame,
            end_frame: result.end_frame,
            generated_at: new Date().toISOString()
          } : {
            url: result.scene_image_url,
            generated_at: new Date().toISOString()
          };

          const updatedSegmentSceneImages = {
            ...(data.segment_scene_images || {}),
            [segment.segment_number]: sceneImageData
          };

          onActionUpdate(action.id, {
            segment_scene_images: updatedSegmentSceneImages
          });

          // Also persist to backend
          await fetch(`${API_BASE_URL}/api/takeGameStep?updateActionData=true`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              actionId: action.id,
              dataUpdates: { segment_scene_images: updatedSegmentSceneImages }
            })
          });
        }
      } catch (error) {
        console.error('Error generating scene image:', error);
        alert(`Error: ${error.message}`);
      } finally {
        setGeneratingImage(prev => ({ ...prev, [key]: false }));
      }
      return;
    }

    // For segments with action_id
    const referencedAction = getReferencedAction(segment.action_id);
    if (!referencedAction) {
      alert('Referenced action not found');
      return;
    }

    const key = `${segment.segment_number}-${videoType}`;
    setGeneratingImage(prev => ({ ...prev, [key]: true }));

    try {
      const response = await fetch(`${API_BASE_URL}/api/takeGameStep?generateSceneImage=true&actionId=${referencedAction.id}`);
      const result = await parseApiResponse(response);

      if (result.scene_image_url && onActionUpdate) {
        // Update the referenced action with the new scene image
        onActionUpdate(referencedAction.id, {
          scene_image_url: result.scene_image_url
        });
      }
    } catch (error) {
      console.error('Error generating scene image:', error);
      alert(`Error: ${error.message}`);
    } finally {
      setGeneratingImage(prev => ({ ...prev, [key]: false }));
    }
  };

  // Generate TTS for a segment's referenced action
  const generateTTSForSegment = async (segment, videoType = 'external') => {
    if (!segment.action_id) return;

    const referencedAction = getReferencedAction(segment.action_id);
    if (!referencedAction) {
      alert('Referenced action not found');
      return;
    }

    const key = `${segment.segment_number}-${videoType}`;
    setGeneratingTTS(prev => ({ ...prev, [key]: true }));

    try {
      const response = await fetch(`${API_BASE_URL}/api/takeGameStep?generateDialogueTTS=true&actionId=${referencedAction.id}&videoType=${videoType}`);
      const result = await parseApiResponse(response);

      if (result.audio_url && onActionUpdate) {
        onActionUpdate(referencedAction.id, {
          audio: {
            ...referencedAction.data.audio,
            [videoType]: {
              audio_url: result.audio_url,
              duration: result.duration,
              voice_id: result.voice_id,
              generated_at: new Date().toISOString()
            }
          }
        });
      }
    } catch (error) {
      console.error('Error generating TTS:', error);
      alert(`Error: ${error.message}`);
    } finally {
      setGeneratingTTS(prev => ({ ...prev, [key]: false }));
    }
  };

  // Regenerate prompt for a segment (host or custom video)
  const regenerateSegmentPrompt = async (segment) => {
    const key = segment.segment_number;
    setRegeneratingPrompt(prev => ({ ...prev, [key]: true }));

    try {
      const response = await fetch(
        `${API_BASE_URL}/api/takeGameStep?regenerateSegmentPrompt=true&actionId=${action.id}&segmentNumber=${segment.segment_number}`,
        { method: 'GET' }
      );

      const result = await parseApiResponse(response);

      if (result.success && result.segment) {
        // Update local segments state
        const newSegments = segments.map(s =>
          s.segment_number === result.segment_number
            ? result.segment
            : s
        );
        setSegments(newSegments);

        // Update parent via onActionUpdate
        if (onActionUpdate) {
          onActionUpdate(action.id, { segments: newSegments });
        }
      } else {
        alert(`Failed to regenerate: ${result.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error regenerating prompt:', error);
      alert(`Error: ${error.message}`);
    } finally {
      setRegeneratingPrompt(prev => ({ ...prev, [key]: false }));
    }
  };

  // Generate custom video for host segment
  const generateHostVideo = async (segment) => {
    if (!segment.host_script) {
      alert('No host script found for this segment');
      return;
    }

    const key = segment.segment_number;
    setGeneratingHostVideo(prev => ({ ...prev, [key]: true }));

    try {
      // Prepend host stage description for host segments with clear attribution
      let videoPrompt = segment.host_script;
      if (segment.segment_type.includes('host')) {
        videoPrompt = `The host stands on the Big Brother stage and says: "${segment.host_script}"`;
      }

      // Get game instance ID from the episode action
      const gameInstanceId = action.game_instance_id;

      const response = await fetch(
        `${API_BASE_URL}/api/takeGameStep?generateCustomVideo=true&gameInstanceId=${gameInstanceId}&userDescription=${encodeURIComponent(videoPrompt)}&freeform=false&enhancePrompt=true`,
        { method: 'GET' }
      );

      let result = await parseApiResponse(response);
      console.log('[Host Video] Initial response:', JSON.stringify(result, null, 2));

      // Handle Lambda response format (statusCode + body)
      if (result.body && typeof result.body === 'string') {
        console.log('[Host Video] Unwrapping Lambda response body');
        try {
          result = JSON.parse(result.body);
          console.log('[Host Video] Unwrapped result:', JSON.stringify(result, null, 2));
        } catch (parseError) {
          console.error('[Host Video] Failed to parse body:', parseError);
          console.error('[Host Video] Body content:', result.body);
          throw new Error(`Failed to parse response body: ${parseError.message}`);
        }
      }

      console.log('[Host Video] Final parsed result:', JSON.stringify(result, null, 2));

      // Extract video URL from response
      // For multi-video sequences, use the LAST video (final clip)
      let videoUrl = null;
      if (result.custom_video?.videos && Array.isArray(result.custom_video.videos) && result.custom_video.videos.length > 0) {
        // Use last video from sequence
        videoUrl = result.custom_video.videos[result.custom_video.videos.length - 1].url;
        console.log('[Host Video] Using last video from sequence:', videoUrl);
      } else {
        // Fallback to single video URL
        videoUrl = result.video_url || result.custom_video?.video_url;
        console.log('[Host Video] Using single video URL:', videoUrl);
      }
      console.log('[Host Video] Success flag:', result.success);

      if (result.success && videoUrl) {
        // Create the updated segment_videos structure
        // Store ALL videos if available (for multi-video sequences)
        const allVideos = result.custom_video?.videos || null;

        const updatedSegmentVideos = {
          ...data.segment_videos,
          [segment.segment_number]: {
            url: videoUrl, // Last video's URL for quick access
            videos: allVideos, // Store full array for flexibility
            generated_at: new Date().toISOString(),
            prompt: videoPrompt
          }
        };

        // Update local state first for immediate UI feedback
        if (onActionUpdate) {
          onActionUpdate(action.id, {
            segment_videos: updatedSegmentVideos
          });
        }

        // Persist to backend by updating the episode action
        try {
          const updateResponse = await fetch(
            `${API_BASE_URL}/api/takeGameStep?updateActionData=true`,
            {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                actionId: action.id,
                dataUpdates: {
                  segment_videos: updatedSegmentVideos
                }
              })
            }
          );

          const updateResult = await updateResponse.json();
          console.log('[Host Video] Backend update result:', updateResult);
        } catch (updateError) {
          console.error('[Host Video] Failed to persist to backend:', updateError);
        }
      } else {
        alert(`Failed to generate video: ${result.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error generating host video:', error);
      alert(`Error: ${error.message}`);
    } finally {
      setGeneratingHostVideo(prev => ({ ...prev, [key]: false }));
    }
  };

  const getSegmentIcon = (segmentType) => {
    switch (segmentType) {
      case 'host_intro': return '🎬';
      case 'host_transition': return '🎙️';
      case 'host_outro': return '👋';
      case 'dialogue': return '💬';
      case 'diary_room': return '📹';
      case 'competition': return '🏆';
      case 'game_event': return '📢';
      case 'custom_video': return '🎥';
      default: return '📺';
    }
  };

  const getSegmentColor = (segmentType) => {
    switch (segmentType) {
      case 'host_intro':
      case 'host_transition':
      case 'host_outro':
        return THEME.accent;
      case 'dialogue':
        return THEME.public;
      case 'diary_room':
        return THEME.private;
      case 'competition':
        return '#FFD700';
      case 'game_event':
        return THEME.secondary;
      case 'custom_video':
        return THEME.accent;
      default:
        return THEME.textMuted;
    }
  };

  const getUserForAction = (actionId) => {
    // This would need to be implemented to fetch action details
    return null;
  };

  const totalDuration = data.total_duration_estimate || 0;
  const minutes = Math.floor(totalDuration / 60);
  const seconds = totalDuration % 60;

  return (
    <div style={{
      ...COMMON_STYLES.actionContainer,
      border: `3px solid ${THEME.accent}`,
      background: `linear-gradient(135deg, ${THEME.bgDark} 0%, ${THEME.bgMedium} 100%)`,
      position: 'relative',
      zIndex: 1
    }}>
      {/* Episode Header */}
      <div style={{
        background: `linear-gradient(135deg, ${THEME.accent} 0%, ${THEME.secondary} 100%)`,
        color: THEME.textLight,
        padding: '16px',
        borderRadius: '8px 8px 0 0',
        marginBottom: '16px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.3)'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '8px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <span style={{ fontSize: '2em' }}>🎬</span>
            <div>
              <div style={{ fontSize: '1.4em', fontWeight: 'bold' }}>
                {data.episode_title}
              </div>
              <div style={{ fontSize: '0.9em', opacity: 0.9 }}>
                Episode {data.episode_number} • {data.round_type}
              </div>
            </div>
          </div>
          <div style={{
            background: 'rgba(0,0,0,0.3)',
            padding: '8px 16px',
            borderRadius: '20px',
            fontSize: '0.9em',
            fontWeight: 'bold'
          }}>
            ⏱️ {minutes}:{seconds.toString().padStart(2, '0')}
          </div>
        </div>
        <div style={{
          display: 'flex',
          gap: '16px',
          fontSize: '0.85em',
          opacity: 0.9,
          marginTop: '8px'
        }}>
          <span>📺 {segments.length} segments</span>
          <span>🎙️ {segments.filter(s => s.segment_type.includes('host')).length} host segments</span>
          <span>💬 {segments.filter(s => s.segment_type === 'dialogue').length} dialogue</span>
          <span>📹 {segments.filter(s => s.segment_type === 'diary_room').length} diary rooms</span>
        </div>
      </div>

      {/* Segments List */}
      <div style={{ padding: '0 16px 16px' }}>
        <div style={{
          fontSize: '1.1em',
          fontWeight: 'bold',
          color: THEME.accent,
          marginBottom: '12px',
          textTransform: 'uppercase',
          letterSpacing: '1px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <span>Episode Structure</span>
          <button
            onClick={() => setShowCustomVideoForm(true)}
            style={{
              padding: '8px 16px',
              backgroundColor: THEME.accent,
              color: THEME.textLight,
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '0.85em',
              fontWeight: '600'
            }}
          >
            ➕ Add Custom Video Segment
          </button>
        </div>

        {segments.map((segment, index) => {
          const isExpanded = expandedSegments[segment.segment_number];
          const segmentColor = getSegmentColor(segment.segment_type);
          const segmentIcon = getSegmentIcon(segment.segment_type);

          return (
            <React.Fragment key={segment.segment_number}>
              {/* Drop zone BEFORE segment */}
              <div
                onDragOver={(e) => {
                  e.preventDefault();
                  e.dataTransfer.dropEffect = 'move';
                  setDragOverIndex(index);
                }}
                onDragLeave={() => {
                  setDragOverIndex(null);
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  handleSegmentDrop(e, index);
                }}
                style={{
                  height: dragOverIndex === index ? '40px' : '8px',
                  background: dragOverIndex === index
                    ? `linear-gradient(90deg, ${THEME.accent}22, ${THEME.accent}44, ${THEME.accent}22)`
                    : 'transparent',
                  border: dragOverIndex === index ? `2px dashed ${THEME.accent}` : 'none',
                  borderRadius: '4px',
                  marginBottom: dragOverIndex === index ? '8px' : '0',
                  transition: 'all 0.2s',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: THEME.accent,
                  fontWeight: 'bold',
                  fontSize: '0.9em'
                }}
              >
                {dragOverIndex === index && '↓ Drop here ↓'}
              </div>

              {/* Segment card */}
              {(() => {
                // Use segment.popup directly (no automatic competition disclaimers)
                const displayPopup = segment.popup;

                return (
              <div
                style={{
                  background: THEME.bgLight,
                  border: `2px solid ${segmentColor}`,
                  borderRadius: '8px',
                  marginBottom: '8px',
                  overflow: 'hidden',
                  transition: 'all 0.2s',
                  opacity: draggedSegmentIndex === index ? 0.5 : 1
                }}
              >
              {/* Segment Header - Draggable for reordering */}
              <div
                draggable={true}
                onDragStart={(e) => {
                  setDraggedSegmentIndex(index);
                  e.dataTransfer.effectAllowed = 'move';
                  e.dataTransfer.setData('segment-reorder', index.toString());
                }}
                onDragEnd={(e) => {
                  setDraggedSegmentIndex(null);
                  setDragOverIndex(null);
                }}
                onClick={() => toggleSegment(segment.segment_number)}
                style={{
                  padding: '12px',
                  cursor: 'grab',
                  background: `${segmentColor}22`,
                  borderBottom: isExpanded ? `1px solid ${segmentColor}44` : 'none',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
                  <span style={{ fontSize: '1.5em' }}>{segmentIcon}</span>
                  <div style={{ flex: 1 }}>
                    <div style={{
                      fontWeight: 'bold',
                      color: THEME.textLight,
                      marginBottom: '4px'
                    }}>
                      #{segment.segment_number} • {segment.segment_type.replace(/_/g, ' ').toUpperCase()}
                    </div>
                    <div style={{
                      fontSize: '0.85em',
                      color: THEME.textMuted
                    }}>
                      {segment.why_included || segment.notes || 'No description'}
                    </div>
                    {displayPopup && (
                      <div style={{
                        marginTop: '8px',
                        padding: '6px 10px',
                        background: 'rgba(255, 200, 0, 0.15)',
                        border: '1px solid rgba(255, 200, 0, 0.4)',
                        borderRadius: '6px',
                        fontSize: '0.8em',
                        color: '#FFC107',
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: '6px'
                      }}>
                        <span style={{ fontSize: '1.1em', flexShrink: 0 }}>
                          {typeof displayPopup === 'string' ? '💡' : ''}
                        </span>
                        <span style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                          {typeof displayPopup === 'string' ? (
                            displayPopup
                          ) : (
                            <>
                              <span style={{ fontWeight: '600' }}>{displayPopup.title}</span>
                              <span>{displayPopup.body}</span>
                            </>
                          )}
                        </span>
                      </div>
                    )}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    {displayPopup && (
                      <div style={{
                        background: 'rgba(255, 200, 0, 0.2)',
                        padding: '2px 8px',
                        borderRadius: '8px',
                        fontSize: '0.75em',
                        color: '#FFC107',
                        fontWeight: '600',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px'
                      }}>
                        {typeof displayPopup === 'string' ? '💡' : (displayPopup.title?.split(' ')[0] || '💡')}
                      </div>
                    )}
                    <div style={{
                      background: 'rgba(0,0,0,0.3)',
                      padding: '4px 12px',
                      borderRadius: '12px',
                      fontSize: '0.85em',
                      color: THEME.textLight
                    }}>
                      {segment.duration_estimate}s
                    </div>
                  </div>
                </div>

                {/* Edit controls section */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  marginLeft: '12px'
                }}>
                  {/* Delete segment button */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation(); // Prevent segment toggle
                      if (window.confirm(`Delete segment ${segment.segment_number}? This cannot be undone.`)) {
                        const newSegments = segments
                          .filter(s => s.segment_number !== segment.segment_number)
                          .map((s, idx) => ({ ...s, segment_number: idx + 1 })); // Renumber

                        setSegments(newSegments);
                        setUnsavedChanges(true);
                        saveEpisodeChanges(newSegments);
                      }
                    }}
                    style={{
                      padding: '4px 8px',
                      borderRadius: '4px',
                      border: 'none',
                      backgroundColor: '#f44336',
                      color: THEME.textLight,
                      cursor: 'pointer',
                      fontSize: '0.85em',
                      fontWeight: '600'
                    }}
                  >
                    🗑️
                  </button>

                  {/* Expand/collapse arrow */}
                  <span style={{ fontSize: '1.2em' }}>
                    {isExpanded ? '▼' : '▶'}
                  </span>
                </div>
              </div>

              {/* Segment Details */}
              {isExpanded && (
                <div style={{ padding: '16px', background: THEME.bgDark }}>
                  {/* Host Segments */}
                  {segment.segment_type.includes('host') && (
                    <div style={{ marginBottom: '12px' }}>
                      <div style={{
                        color: THEME.accent,
                        fontWeight: 'bold',
                        marginBottom: '8px',
                        fontSize: '0.9em',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                      }}>
                        <span>HOST SCRIPT:</span>
                        {unsavedChanges && (
                          <span style={{ fontSize: '0.8em', color: THEME.textMuted }}>
                            {savingChanges ? '💾 Saving...' : '⚠️ Unsaved changes'}
                          </span>
                        )}
                      </div>
                      <textarea
                        value={segments.find(s => s.segment_number === segment.segment_number)?.host_script || ''}
                        onChange={(e) => {
                          const newSegments = segments.map(s =>
                            s.segment_number === segment.segment_number
                              ? { ...s, host_script: e.target.value }
                              : s
                          );
                          setSegments(newSegments);
                          setUnsavedChanges(true);
                        }}
                        onBlur={() => {
                          if (unsavedChanges) {
                            saveEpisodeChanges(segments);
                          }
                        }}
                        style={{
                          width: '100%',
                          padding: '12px',
                          background: THEME.bgMedium,
                          border: `2px solid ${THEME.accent}`,
                          borderRadius: '6px',
                          color: THEME.textLight,
                          fontStyle: 'italic',
                          lineHeight: '1.6',
                          fontSize: '0.95em',
                          fontFamily: 'inherit',
                          resize: 'vertical',
                          minHeight: '80px'
                        }}
                        placeholder="Enter host script..."
                      />

                      {/* Regenerate Script Button */}
                      <button
                        onClick={() => regenerateSegmentPrompt(segment)}
                        disabled={regeneratingPrompt[segment.segment_number]}
                        style={{
                          marginTop: '8px',
                          padding: '6px 12px',
                          borderRadius: '6px',
                          border: 'none',
                          backgroundColor: regeneratingPrompt[segment.segment_number] ? THEME.textMuted : THEME.accent,
                          color: THEME.textLight,
                          cursor: regeneratingPrompt[segment.segment_number] ? 'not-allowed' : 'pointer',
                          fontSize: '0.8em',
                          fontWeight: '600',
                          opacity: regeneratingPrompt[segment.segment_number] ? 0.6 : 1
                        }}
                      >
                        {regeneratingPrompt[segment.segment_number]
                          ? '⏳ Regenerating Script...'
                          : '🔄 Regenerate Script with AI'}
                      </button>

                      {/* Host Video Display */}
                      {(() => {
                        let hostVideo = data.segment_videos?.[segment.segment_number];

                        // RETROACTIVE SUPPORT: Check custom_video actions from the last 2 hours
                        if (!hostVideo && segment.host_script) {
                          const twoHoursAgo = Date.now() - (2 * 60 * 60 * 1000);
                          const matchingCustomVideo = allActions.find(a => {
                            if (a.type !== 'custom_video') return false;
                            const createdAt = new Date(a.created_at).getTime();
                            if (createdAt < twoHoursAgo) return false;

                            // Check if this custom video matches this segment's script
                            const customVideoData = a.data?.custom_video || a.data;
                            const userDesc = customVideoData.user_description || '';

                            // Match host videos by checking if the prompt contains the host script
                            return userDesc.includes(segment.host_script);
                          });

                          if (matchingCustomVideo) {
                            const cvData = matchingCustomVideo.data?.custom_video || matchingCustomVideo.data;
                            hostVideo = {
                              url: cvData.video_url || cvData.videos?.[0]?.url,
                              videos: cvData.videos,
                              generated_at: matchingCustomVideo.created_at,
                              attached_retroactively: true
                            };
                          }
                        }

                        // Get the video URL - prefer processed video if available
                        let displayVideoUrl = null;
                        if (hostVideo?.processed_url) {
                          // Prefer processed video (with popups)
                          displayVideoUrl = hostVideo.processed_url;
                        } else if (hostVideo?.videos && Array.isArray(hostVideo.videos) && hostVideo.videos.length > 0) {
                          // Use last video from stored sequence
                          displayVideoUrl = hostVideo.videos[hostVideo.videos.length - 1].url;
                        } else if (hostVideo?.url) {
                          // Fallback to stored URL
                          displayVideoUrl = hostVideo.url;
                        }

                        return (
                          <div style={{ marginTop: '12px' }}>
                            {displayVideoUrl && (
                              <div style={{ marginBottom: '12px' }}>
                                <div
                                  draggable={true}
                                  onDragStart={(e) => {
                                    const videoData = {
                                      url: displayVideoUrl,
                                      filename: displayVideoUrl.split('/').pop().split('?')[0],
                                      title: `Host - ${segment.segment_type}`,
                                      type: 'episode_host',
                                      metadata: {
                                        segment_type: segment.segment_type,
                                        segment_number: segment.segment_number,
                                        script: segment.host_script,
                                        prompt: hostVideo.prompt
                                      }
                                    };
                                    e.dataTransfer.setData('video-data', JSON.stringify(videoData));
                                    e.dataTransfer.effectAllowed = 'copy';
                                  }}
                                  style={{
                                    position: 'relative',
                                    cursor: 'grab',
                                    border: `2px solid ${THEME.accent}`,
                                    borderRadius: '8px',
                                    padding: '8px',
                                    background: THEME.bgMedium
                                  }}
                                >
                                  <div style={{
                                    fontSize: '0.85em',
                                    color: THEME.accent,
                                    marginBottom: '8px',
                                    fontWeight: 'bold',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px'
                                  }}>
                                    <span style={{ fontSize: '1.2em' }}>⋮⋮</span>
                                    Host Video - Drag to stitcher
                                  </div>
                                  <video
                                    controls
                                    style={{
                                      width: '100%',
                                      maxWidth: '600px',
                                      borderRadius: '4px',
                                      pointerEvents: 'auto'
                                    }}
                                    src={displayVideoUrl}
                                    onMouseDown={(e) => e.stopPropagation()}
                                  />
                                </div>
                              </div>
                            )}

                            {/* Generate Host Video Button */}
                            <button
                              onClick={() => generateHostVideo(segment)}
                              disabled={generatingHostVideo[segment.segment_number]}
                              style={{
                                padding: '8px 16px',
                                borderRadius: '6px',
                                border: 'none',
                                backgroundColor: generatingHostVideo[segment.segment_number] ? THEME.textMuted : '#FF9800',
                                color: THEME.textLight,
                                cursor: generatingHostVideo[segment.segment_number] ? 'not-allowed' : 'pointer',
                                fontSize: '0.85em',
                                fontWeight: '600',
                                opacity: generatingHostVideo[segment.segment_number] ? 0.6 : 1
                              }}
                            >
                              {generatingHostVideo[segment.segment_number]
                                ? '⏳ Generating Host Video...'
                                : displayVideoUrl
                                  ? '🔄 Regenerate Host Video'
                                  : '🎬 Generate Host Video'}
                            </button>

                            {/* Process Host Video Button (for popups) */}
                            {displayVideoUrl && (
                              <button
                                onClick={() => processHostVideoSegment(segment)}
                                disabled={processingVideo[`host-${segment.segment_number}`]}
                                style={{
                                  padding: '8px 16px',
                                  borderRadius: '6px',
                                  border: 'none',
                                  backgroundColor: processingVideo[`host-${segment.segment_number}`] ? THEME.textMuted : '#4CAF50',
                                  color: THEME.textLight,
                                  cursor: processingVideo[`host-${segment.segment_number}`] ? 'not-allowed' : 'pointer',
                                  fontSize: '0.85em',
                                  fontWeight: '600',
                                  opacity: processingVideo[`host-${segment.segment_number}`] ? 0.6 : 1,
                                  marginTop: '8px'
                                }}
                              >
                                {processingVideo[`host-${segment.segment_number}`]
                                  ? '⏳ Processing Video...'
                                  : '🔧 Process Video (Add Popups)'}
                              </button>
                            )}

                            {/* Add Popup Button (for host segments without popups) */}
                            {!segment.popup && !popupConfigs[segment.segment_number] && (
                              <button
                                onClick={() => {
                                  setPopupConfigs(prev => ({
                                    ...prev,
                                    [segment.segment_number]: {
                                      title: '',
                                      text: '',
                                      x: 30,
                                      y: 26,
                                      startTime: '',
                                      endTime: ''
                                    }
                                  }));
                                }}
                                style={{
                                  marginTop: '12px',
                                  padding: '8px 16px',
                                  borderRadius: '6px',
                                  border: 'none',
                                  backgroundColor: '#FFC107',
                                  color: THEME.bgDark,
                                  cursor: 'pointer',
                                  fontSize: '0.85em',
                                  fontWeight: '600'
                                }}
                              >
                                💡 Add Popup
                              </button>
                            )}

                            {/* Popup Configuration for Host Segments */}
                            {(segment.popup || popupConfigs[segment.segment_number]) && (
                              <div style={{
                                marginTop: '16px',
                                padding: '12px',
                                background: 'rgba(255, 200, 0, 0.1)',
                                border: '1px solid rgba(255, 200, 0, 0.3)',
                                borderRadius: '8px'
                              }}>
                                <div style={{
                                  fontSize: '0.9em',
                                  fontWeight: 'bold',
                                  color: '#FFC107',
                                  marginBottom: '12px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'space-between'
                                }}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                    <span>💡</span>
                                    <span>Popup Configuration</span>
                                  </div>
                                  {/* Remove Popup Button */}
                                  <button
                                    onClick={() => {
                                      const newPopupConfigs = { ...popupConfigs };
                                      delete newPopupConfigs[segment.segment_number];
                                      setPopupConfigs(newPopupConfigs);

                                      const newSegments = segments.map(s =>
                                        s.segment_number === segment.segment_number
                                          ? { ...s, popup: null }
                                          : s
                                      );
                                      setSegments(newSegments);
                                      setUnsavedChanges(true);
                                      saveEpisodeChanges(newSegments);
                                    }}
                                    style={{
                                      padding: '4px 8px',
                                      borderRadius: '4px',
                                      border: 'none',
                                      backgroundColor: '#f44336',
                                      color: THEME.textLight,
                                      cursor: 'pointer',
                                      fontSize: '0.75em',
                                      fontWeight: '600'
                                    }}
                                  >
                                    🗑️ Remove Popup
                                  </button>
                                </div>

                                {/* Popup Title */}
                                <div style={{ marginBottom: '12px' }}>
                                  <label style={{
                                    display: 'block',
                                    fontSize: '0.85em',
                                    color: THEME.textMuted,
                                    marginBottom: '4px'
                                  }}>
                                    Popup Title:
                                  </label>
                                  <input
                                    type="text"
                                    placeholder="💡 Pro-tip (default)"
                                    value={popupConfigs[segment.segment_number]?.title || ''}
                                    onChange={(e) => {
                                      const newPopupConfigs = {
                                        ...popupConfigs,
                                        [segment.segment_number]: {
                                          ...(popupConfigs[segment.segment_number] || { text: '', x: 30, y: 26, startTime: '', endTime: '' }),
                                          title: e.target.value
                                        }
                                      };
                                      setPopupConfigs(newPopupConfigs);

                                      // Sync to segments array with all fields
                                      const newSegments = segments.map(s =>
                                        s.segment_number === segment.segment_number
                                          ? {
                                              ...s,
                                              popup: {
                                                title: e.target.value,
                                                text: newPopupConfigs[segment.segment_number].text || '',
                                                x: newPopupConfigs[segment.segment_number].x ?? 30,
                                                y: newPopupConfigs[segment.segment_number].y ?? 26,
                                                startTime: parseFloat(newPopupConfigs[segment.segment_number].startTime) || 0,
                                                endTime: parseFloat(newPopupConfigs[segment.segment_number].endTime) || 0
                                              }
                                            }
                                          : s
                                      );
                                      setSegments(newSegments);
                                      setUnsavedChanges(true);
                                    }}
                                    onBlur={() => {
                                      if (unsavedChanges) {
                                        saveEpisodeChanges(segments);
                                      }
                                    }}
                                    style={{
                                      width: '100%',
                                      padding: '8px',
                                      background: THEME.bgDark,
                                      border: `1px solid ${THEME.borderDark}`,
                                      borderRadius: '4px',
                                      color: THEME.textLight,
                                      fontSize: '0.85em',
                                      fontFamily: 'inherit'
                                    }}
                                  />
                                </div>

                                {/* Popup Text */}
                                <div style={{ marginBottom: '12px' }}>
                                  <label style={{
                                    display: 'block',
                                    fontSize: '0.85em',
                                    color: THEME.textMuted,
                                    marginBottom: '4px'
                                  }}>
                                    Popup Text:
                                  </label>
                                  <textarea
                                    value={popupConfigs[segment.segment_number]?.text || (typeof segment.popup === 'string' ? segment.popup : segment.popup?.text || segment.popup?.body || '')}
                                    onChange={(e) => {
                                      const newPopupConfigs = {
                                        ...popupConfigs,
                                        [segment.segment_number]: {
                                          ...(popupConfigs[segment.segment_number] || { title: '', x: 30, y: 26, startTime: '', endTime: '' }),
                                          text: e.target.value
                                        }
                                      };
                                      setPopupConfigs(newPopupConfigs);

                                      // Sync to segments array with all fields
                                      const newSegments = segments.map(s =>
                                        s.segment_number === segment.segment_number
                                          ? {
                                              ...s,
                                              popup: {
                                                title: newPopupConfigs[segment.segment_number].title || '',
                                                text: e.target.value,
                                                x: newPopupConfigs[segment.segment_number].x ?? 30,
                                                y: newPopupConfigs[segment.segment_number].y ?? 26,
                                                startTime: parseFloat(newPopupConfigs[segment.segment_number].startTime) || 0,
                                                endTime: parseFloat(newPopupConfigs[segment.segment_number].endTime) || 0
                                              }
                                            }
                                          : s
                                      );
                                      setSegments(newSegments);
                                      setUnsavedChanges(true);
                                    }}
                                    onBlur={() => {
                                      if (unsavedChanges) {
                                        saveEpisodeChanges(segments);
                                      }
                                    }}
                                    style={{
                                      width: '100%',
                                      padding: '8px',
                                      background: THEME.bgDark,
                                      border: `1px solid ${THEME.borderDark}`,
                                      borderRadius: '4px',
                                      color: THEME.textLight,
                                      fontSize: '0.85em',
                                      fontFamily: 'inherit',
                                      resize: 'vertical',
                                      minHeight: '60px'
                                    }}
                                  />
                                </div>

                                {/* Time and Position Fields */}
                                <div style={{
                                  display: 'grid',
                                  gridTemplateColumns: 'repeat(4, 1fr)',
                                  gap: '8px'
                                }}>
                                  {/* Start Time */}
                                  <div>
                                    <label style={{
                                      display: 'block',
                                      fontSize: '0.75em',
                                      color: THEME.textMuted,
                                      marginBottom: '4px'
                                    }}>
                                      Start (s):
                                    </label>
                                    <input
                                      type="number"
                                      step="0.1"
                                      placeholder="0.0"
                                      value={popupConfigs[segment.segment_number]?.startTime || ''}
                                      onChange={(e) => {
                                        const newPopupConfigs = {
                                          ...popupConfigs,
                                          [segment.segment_number]: {
                                            ...(popupConfigs[segment.segment_number] || { title: '', text: '', x: 30, y: 26, endTime: '' }),
                                            startTime: e.target.value
                                          }
                                        };
                                        setPopupConfigs(newPopupConfigs);

                                        // Sync to segments
                                        const newSegments = segments.map(s =>
                                          s.segment_number === segment.segment_number
                                            ? {
                                                ...s,
                                                popup: {
                                                  ...(s.popup || {}),
                                                  startTime: parseFloat(e.target.value) || 0
                                                }
                                              }
                                            : s
                                        );
                                        setSegments(newSegments);
                                        setUnsavedChanges(true);
                                      }}
                                      onBlur={() => {
                                        if (unsavedChanges) {
                                          saveEpisodeChanges(segments);
                                        }
                                      }}
                                      style={{
                                        width: '100%',
                                        padding: '6px',
                                        background: THEME.bgDark,
                                        border: `1px solid ${THEME.borderDark}`,
                                        borderRadius: '4px',
                                        color: THEME.textLight,
                                        fontSize: '0.85em'
                                      }}
                                    />
                                  </div>

                                  {/* End Time */}
                                  <div>
                                    <label style={{
                                      display: 'block',
                                      fontSize: '0.75em',
                                      color: THEME.textMuted,
                                      marginBottom: '4px'
                                    }}>
                                      End (s):
                                    </label>
                                    <input
                                      type="number"
                                      step="0.1"
                                      placeholder="0.0 (no end)"
                                      value={popupConfigs[segment.segment_number]?.endTime || ''}
                                      onChange={(e) => {
                                        const newPopupConfigs = {
                                          ...popupConfigs,
                                          [segment.segment_number]: {
                                            ...(popupConfigs[segment.segment_number] || { title: '', text: '', x: 30, y: 26, startTime: '' }),
                                            endTime: e.target.value
                                          }
                                        };
                                        setPopupConfigs(newPopupConfigs);

                                        // Sync to segments
                                        const newSegments = segments.map(s =>
                                          s.segment_number === segment.segment_number
                                            ? {
                                                ...s,
                                                popup: {
                                                  ...(s.popup || {}),
                                                  endTime: parseFloat(e.target.value) || 0
                                                }
                                              }
                                            : s
                                        );
                                        setSegments(newSegments);
                                        setUnsavedChanges(true);
                                      }}
                                      onBlur={() => {
                                        if (unsavedChanges) {
                                          saveEpisodeChanges(segments);
                                        }
                                      }}
                                      style={{
                                        width: '100%',
                                        padding: '6px',
                                        background: THEME.bgDark,
                                        border: `1px solid ${THEME.borderDark}`,
                                        borderRadius: '4px',
                                        color: THEME.textLight,
                                        fontSize: '0.85em'
                                      }}
                                    />
                                  </div>

                                  {/* X Position */}
                                  <div>
                                    <label style={{
                                      display: 'block',
                                      fontSize: '0.75em',
                                      color: THEME.textMuted,
                                      marginBottom: '4px'
                                    }}>
                                      X Position:
                                    </label>
                                    <input
                                      type="number"
                                      value={popupConfigs[segment.segment_number]?.x ?? 30}
                                      onChange={(e) => {
                                        const newPopupConfigs = {
                                          ...popupConfigs,
                                          [segment.segment_number]: {
                                            ...(popupConfigs[segment.segment_number] || { title: '', text: '', y: 26, startTime: '', endTime: '' }),
                                            x: parseInt(e.target.value) || 0
                                          }
                                        };
                                        setPopupConfigs(newPopupConfigs);

                                        // Sync to segments
                                        const newSegments = segments.map(s =>
                                          s.segment_number === segment.segment_number
                                            ? {
                                                ...s,
                                                popup: {
                                                  ...(s.popup || {}),
                                                  x: parseInt(e.target.value) || 0
                                                }
                                              }
                                            : s
                                        );
                                        setSegments(newSegments);
                                        setUnsavedChanges(true);
                                      }}
                                      onBlur={() => {
                                        if (unsavedChanges) {
                                          saveEpisodeChanges(segments);
                                        }
                                      }}
                                      style={{
                                        width: '100%',
                                        padding: '6px',
                                        background: THEME.bgDark,
                                        border: `1px solid ${THEME.borderDark}`,
                                        borderRadius: '4px',
                                        color: THEME.textLight,
                                        fontSize: '0.85em'
                                      }}
                                    />
                                  </div>

                                  {/* Y Position */}
                                  <div>
                                    <label style={{
                                      display: 'block',
                                      fontSize: '0.75em',
                                      color: THEME.textMuted,
                                      marginBottom: '4px'
                                    }}>
                                      Y Position:
                                    </label>
                                    <input
                                      type="number"
                                      value={popupConfigs[segment.segment_number]?.y ?? 26}
                                      onChange={(e) => {
                                        const newPopupConfigs = {
                                          ...popupConfigs,
                                          [segment.segment_number]: {
                                            ...(popupConfigs[segment.segment_number] || { title: '', text: '', x: 30, startTime: '', endTime: '' }),
                                            y: parseInt(e.target.value) || 0
                                          }
                                        };
                                        setPopupConfigs(newPopupConfigs);

                                        // Sync to segments
                                        const newSegments = segments.map(s =>
                                          s.segment_number === segment.segment_number
                                            ? {
                                                ...s,
                                                popup: {
                                                  ...(s.popup || {}),
                                                  y: parseInt(e.target.value) || 0
                                                }
                                              }
                                            : s
                                        );
                                        setSegments(newSegments);
                                        setUnsavedChanges(true);
                                      }}
                                      onBlur={() => {
                                        if (unsavedChanges) {
                                          saveEpisodeChanges(segments);
                                        }
                                      }}
                                      style={{
                                        width: '100%',
                                        padding: '6px',
                                        background: THEME.bgDark,
                                        border: `1px solid ${THEME.borderDark}`,
                                        borderRadius: '4px',
                                        color: THEME.textLight,
                                        fontSize: '0.85em'
                                      }}
                                    />
                                  </div>
                                </div>

                                {/* Helper Text */}
                                <div style={{
                                  marginTop: '8px',
                                  fontSize: '0.75em',
                                  color: THEME.textMuted,
                                  fontStyle: 'italic'
                                }}>
                                  Position values are in percentage (0-100). Start time 0 = immediate. End time 0 = stays until video ends.
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })()}
                    </div>
                  )}

                  {/* Action ID Reference with Assets and Generation */}
                  {segment.action_id && (() => {
                    const referencedAction = getReferencedAction(segment.action_id);
                    if (!referencedAction) {
                      return (
                        <div style={{ marginBottom: '12px' }}>
                          <div style={{
                            color: THEME.accent,
                            fontWeight: 'bold',
                            marginBottom: '8px',
                            fontSize: '0.9em'
                          }}>
                            ACTION REFERENCE:
                          </div>
                          <div style={{
                            background: THEME.bgMedium,
                            padding: '8px 12px',
                            borderRadius: '6px',
                            fontFamily: 'monospace',
                            fontSize: '0.85em',
                            color: THEME.textMuted
                          }}>
                            ID: {segment.action_id} (Action not found)
                          </div>
                        </div>
                      );
                    }

                    const videoType = segment.segment_type === 'diary_room' ? 'diary_room' : 'external';

                    // Get scene image URL based on videoType (correct path from MessageAction)
                    const sceneImageUrl = videoType === 'external'
                      ? referencedAction.data?.external_scene_image_url
                      : referencedAction.data?.diary_room_scene_image_url;

                    // Get veo3 video data (correct path from MessageAction)
                    const veo3Data = referencedAction.data?.videos?.[videoType]?.veo3;

                    // Normalize veo3 data to get video URL (check processed videos first)
                    let veo3VideoUrl = null;

                    // Priority 1: Check for processed_videos array (new array format after processing)
                    if (veo3Data?.processed_videos && Array.isArray(veo3Data.processed_videos) && veo3Data.processed_videos.length > 0) {
                      veo3VideoUrl = veo3Data.processed_videos[veo3Data.processed_videos.length - 1].url;
                    }
                    // Priority 2: Check for processed_video_url (old object format after processing)
                    else if (veo3Data?.processed_video_url) {
                      veo3VideoUrl = veo3Data.processed_video_url;
                    }
                    // Priority 3: Check if veo3Data is an array (new multi-video format, not yet processed)
                    else if (Array.isArray(veo3Data) && veo3Data.length > 0) {
                      veo3VideoUrl = veo3Data[veo3Data.length - 1].url;
                    }
                    // Priority 4: Fall back to video_url (old single video format, not yet processed)
                    else if (veo3Data?.video_url) {
                      veo3VideoUrl = veo3Data.video_url;
                    }

                    const genKey = `${segment.segment_number}-${videoType}`;

                    return (
                      <div style={{ marginBottom: '12px' }}>
                        <div style={{
                          color: THEME.accent,
                          fontWeight: 'bold',
                          marginBottom: '8px',
                          fontSize: '0.9em'
                        }}>
                          {segment.segment_type.toUpperCase()} CONTENT:
                        </div>

                        {/* Character Info */}
                        {referencedAction.data?.sender_name && (
                          <div style={{
                            background: THEME.bgMedium,
                            padding: '8px 12px',
                            borderRadius: '6px',
                            fontSize: '0.9em',
                            color: THEME.textLight,
                            marginBottom: '8px'
                          }}>
                            <strong>Character:</strong> {referencedAction.data.sender_name}
                          </div>
                        )}

                        {/* Scene Image (Starting Frame) */}
                        {sceneImageUrl && (
                          <div style={{ marginBottom: '12px' }}>
                            <div style={{
                              fontSize: '0.85em',
                              color: THEME.textMuted,
                              marginBottom: '4px'
                            }}>
                              Starting Frame:
                            </div>
                            <img
                              src={sceneImageUrl}
                              alt="Scene starting frame"
                              style={{
                                width: '100%',
                                maxWidth: '600px',
                                borderRadius: '8px',
                                border: `2px solid ${THEME.accent}`,
                                display: 'block'
                              }}
                            />
                          </div>
                        )}

                        {/* Veo3 Video Player */}
                        {veo3VideoUrl && (
                          <div style={{ marginBottom: '12px' }}>
                            <div
                              draggable={true}
                              onDragStart={(e) => {
                                const characterName = referencedAction.data?.sender_name || 'Unknown';
                                const videoData = {
                                  url: veo3VideoUrl,
                                  filename: veo3VideoUrl.split('/').pop().split('?')[0],
                                  title: `${characterName} - ${segment.segment_type}`,
                                  type: 'episode_dialogue',
                                  metadata: {
                                    character: characterName,
                                    segment_type: segment.segment_type,
                                    segment_number: segment.segment_number,
                                    dialogue: segment.dialogue || ''
                                  }
                                };
                                e.dataTransfer.setData('video-data', JSON.stringify(videoData));
                                e.dataTransfer.effectAllowed = 'copy';
                              }}
                              style={{
                                position: 'relative',
                                cursor: 'grab',
                                border: `2px solid ${THEME.accent}`,
                                borderRadius: '8px',
                                padding: '8px',
                                background: THEME.bgMedium
                              }}
                            >
                              <div style={{
                                fontSize: '0.85em',
                                color: THEME.accent,
                                marginBottom: '8px',
                                fontWeight: 'bold',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px'
                              }}>
                                <span style={{ fontSize: '1.2em' }}>⋮⋮</span>
                                Veo3 Video - Drag to stitcher
                              </div>
                              <video
                                controls
                                style={{
                                  width: '100%',
                                  maxWidth: '600px',
                                  borderRadius: '4px',
                                  pointerEvents: 'auto'
                                }}
                                src={veo3VideoUrl}
                                onMouseDown={(e) => e.stopPropagation()}
                              />
                            </div>
                          </div>
                        )}

                        {/* Generation Buttons */}
                        <div style={{
                          display: 'flex',
                          gap: '8px',
                          flexWrap: 'wrap',
                          marginTop: '12px'
                        }}>
                          {/* Generate/Regenerate Image Button */}
                          <button
                            onClick={() => generateSceneImageForSegment(segment, videoType)}
                            disabled={generatingImage[genKey]}
                            style={{
                              padding: '8px 16px',
                              borderRadius: '6px',
                              border: 'none',
                              backgroundColor: generatingImage[genKey] ? THEME.textMuted : '#FF9800',
                              color: THEME.textLight,
                              cursor: generatingImage[genKey] ? 'not-allowed' : 'pointer',
                              fontSize: '0.85em',
                              fontWeight: '600',
                              opacity: generatingImage[genKey] ? 0.6 : 1
                            }}
                          >
                            {generatingImage[genKey]
                              ? '⏳ Generating...'
                              : sceneImageUrl
                                ? '🔄 Regenerate Image'
                                : '🖼️ Generate Image'}
                          </button>

                          {/* Generate Veo3 Video Button */}
                          <button
                            onClick={() => generateVideoForSegment(segment, videoType)}
                            disabled={generatingVideo[genKey]}
                            style={{
                              padding: '8px 16px',
                              borderRadius: '6px',
                              border: 'none',
                              backgroundColor: generatingVideo[genKey] ? THEME.textMuted : THEME.accent,
                              color: THEME.textLight,
                              cursor: generatingVideo[genKey] ? 'not-allowed' : 'pointer',
                              fontSize: '0.85em',
                              fontWeight: '600',
                              opacity: generatingVideo[genKey] ? 0.6 : 1
                            }}
                          >
                            {generatingVideo[genKey] ? '⏳ Generating...' : '🎬 Generate Veo3 Video'}
                          </button>

                          {/* Process Video Button */}
                          {veo3VideoUrl && (
                            <button
                              onClick={() => processVideoForSegment(segment, videoType)}
                              disabled={processingVideo[genKey]}
                              style={{
                                padding: '8px 16px',
                                borderRadius: '6px',
                                border: 'none',
                                backgroundColor: processingVideo[genKey] ? THEME.textMuted : '#4CAF50',
                                color: THEME.textLight,
                                cursor: processingVideo[genKey] ? 'not-allowed' : 'pointer',
                                fontSize: '0.85em',
                                fontWeight: '600',
                                opacity: processingVideo[genKey] ? 0.6 : 1
                              }}
                            >
                              {processingVideo[genKey] ? '⏳ Processing...' : '⚙️ Process Video'}
                            </button>
                          )}

                          {/* Generate TTS Button */}
                          {veo3VideoUrl && referencedAction.data?.sender_id && (
                            <button
                              onClick={() => generateTTSForSegment(segment, videoType)}
                              disabled={generatingTTS[genKey]}
                              style={{
                                padding: '8px 16px',
                                borderRadius: '6px',
                                border: 'none',
                                backgroundColor: generatingTTS[genKey] ? THEME.textMuted : '#9C27B0',
                                color: THEME.textLight,
                                cursor: generatingTTS[genKey] ? 'not-allowed' : 'pointer',
                                fontSize: '0.85em',
                                fontWeight: '600',
                                opacity: generatingTTS[genKey] ? 0.6 : 1
                              }}
                            >
                              {generatingTTS[genKey] ? '⏳ Generating...' : '🎙️ Generate TTS'}
                            </button>
                          )}
                        </div>

                        {/* Add Popup Button (for segments without popups) */}
                        {!segment.popup && !popupConfigs[segment.segment_number] && (
                          <button
                            onClick={() => {
                              setPopupConfigs(prev => ({
                                ...prev,
                                [segment.segment_number]: {
                                  title: '',
                                  text: '',
                                  x: 30,
                                  y: 26,
                                  startTime: '',
                                  endTime: ''
                                }
                              }));
                            }}
                            style={{
                              marginTop: '12px',
                              padding: '8px 16px',
                              borderRadius: '6px',
                              border: 'none',
                              backgroundColor: '#FFC107',
                              color: THEME.bgDark,
                              cursor: 'pointer',
                              fontSize: '0.85em',
                              fontWeight: '600'
                            }}
                          >
                            💡 Add Popup
                          </button>
                        )}

                        {/* Popup Configuration */}
                        {(segment.popup || popupConfigs[segment.segment_number]) && (
                          <div style={{
                            marginTop: '16px',
                            padding: '12px',
                            background: 'rgba(255, 200, 0, 0.1)',
                            border: '1px solid rgba(255, 200, 0, 0.3)',
                            borderRadius: '8px'
                          }}>
                            <div style={{
                              fontSize: '0.9em',
                              fontWeight: 'bold',
                              color: '#FFC107',
                              marginBottom: '12px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between'
                            }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <span>💡</span>
                                <span>Popup Configuration</span>
                              </div>
                              <button
                                onClick={() => {
                                  // Remove popup from both local state and segments
                                  const newPopupConfigs = { ...popupConfigs };
                                  delete newPopupConfigs[segment.segment_number];
                                  setPopupConfigs(newPopupConfigs);

                                  const newSegments = segments.map(s =>
                                    s.segment_number === segment.segment_number
                                      ? { ...s, popup: null }
                                      : s
                                  );
                                  setSegments(newSegments);
                                  setUnsavedChanges(true);
                                  saveEpisodeChanges(newSegments);
                                }}
                                style={{
                                  padding: '4px 8px',
                                  borderRadius: '4px',
                                  border: 'none',
                                  backgroundColor: '#f44336',
                                  color: THEME.textLight,
                                  cursor: 'pointer',
                                  fontSize: '0.75em',
                                  fontWeight: '600'
                                }}
                              >
                                🗑️ Remove Popup
                              </button>
                            </div>

                            {/* Popup Title */}
                            <div style={{ marginBottom: '12px' }}>
                              <label style={{
                                display: 'block',
                                fontSize: '0.85em',
                                color: THEME.textMuted,
                                marginBottom: '4px'
                              }}>
                                Popup Title:
                              </label>
                              <input
                                type="text"
                                placeholder="💡 Pro-tip (default)"
                                value={popupConfigs[segment.segment_number]?.title || ''}
                                onChange={(e) => {
                                  const newPopupConfigs = {
                                    ...popupConfigs,
                                    [segment.segment_number]: {
                                      ...(popupConfigs[segment.segment_number] || { text: '', x: 30, y: 26, startTime: '', endTime: '' }),
                                      title: e.target.value
                                    }
                                  };
                                  setPopupConfigs(newPopupConfigs);

                                  // Sync to segments array
                                  const newSegments = segments.map(s =>
                                    s.segment_number === segment.segment_number
                                      ? {
                                          ...s,
                                          popup: {
                                            title: e.target.value,
                                            body: newPopupConfigs[segment.segment_number].text || ''
                                          }
                                        }
                                      : s
                                  );
                                  setSegments(newSegments);
                                  setUnsavedChanges(true);
                                }}
                                onBlur={() => {
                                  if (unsavedChanges) {
                                    saveEpisodeChanges(segments);
                                  }
                                }}
                                style={{
                                  width: '100%',
                                  padding: '8px',
                                  background: THEME.bgDark,
                                  border: `1px solid ${THEME.borderDark}`,
                                  borderRadius: '4px',
                                  color: THEME.textLight,
                                  fontSize: '0.85em',
                                  fontFamily: 'inherit'
                                }}
                              />
                            </div>

                            {/* Popup Text */}
                            <div style={{ marginBottom: '12px' }}>
                              <label style={{
                                display: 'block',
                                fontSize: '0.85em',
                                color: THEME.textMuted,
                                marginBottom: '4px'
                              }}>
                                Popup Text:
                              </label>
                              <textarea
                                value={popupConfigs[segment.segment_number]?.text || (typeof segment.popup === 'string' ? segment.popup : segment.popup?.body || '')}
                                onChange={(e) => {
                                  const newPopupConfigs = {
                                    ...popupConfigs,
                                    [segment.segment_number]: {
                                      ...(popupConfigs[segment.segment_number] || { title: '', x: 30, y: 26, startTime: '', endTime: '' }),
                                      text: e.target.value
                                    }
                                  };
                                  setPopupConfigs(newPopupConfigs);

                                  // Sync to segments array
                                  const newSegments = segments.map(s =>
                                    s.segment_number === segment.segment_number
                                      ? {
                                          ...s,
                                          popup: {
                                            title: newPopupConfigs[segment.segment_number].title || '',
                                            body: e.target.value
                                          }
                                        }
                                      : s
                                  );
                                  setSegments(newSegments);
                                  setUnsavedChanges(true);
                                }}
                                onBlur={() => {
                                  if (unsavedChanges) {
                                    saveEpisodeChanges(segments);
                                  }
                                }}
                                style={{
                                  width: '100%',
                                  padding: '8px',
                                  background: THEME.bgDark,
                                  border: `1px solid ${THEME.borderDark}`,
                                  borderRadius: '4px',
                                  color: THEME.textLight,
                                  fontSize: '0.85em',
                                  fontFamily: 'inherit',
                                  resize: 'vertical',
                                  minHeight: '60px'
                                }}
                              />
                            </div>

                            {/* Time and Position Fields */}
                            <div style={{
                              display: 'grid',
                              gridTemplateColumns: 'repeat(4, 1fr)',
                              gap: '8px'
                            }}>
                              {/* Start Time */}
                              <div>
                                <label style={{
                                  display: 'block',
                                  fontSize: '0.75em',
                                  color: THEME.textMuted,
                                  marginBottom: '4px'
                                }}>
                                  Start (s):
                                </label>
                                <input
                                  type="number"
                                  step="0.1"
                                  placeholder="0.0"
                                  value={popupConfigs[segment.segment_number]?.startTime || ''}
                                  onChange={(e) => {
                                    setPopupConfigs(prev => ({
                                      ...prev,
                                      [segment.segment_number]: {
                                        ...(prev[segment.segment_number] || { title: '', text: '', x: 30, y: 26, endTime: '' }),
                                        startTime: e.target.value
                                      }
                                    }));
                                  }}
                                  style={{
                                    width: '100%',
                                    padding: '6px',
                                    background: THEME.bgDark,
                                    border: `1px solid ${THEME.borderDark}`,
                                    borderRadius: '4px',
                                    color: THEME.textLight,
                                    fontSize: '0.85em'
                                  }}
                                />
                              </div>

                              {/* End Time */}
                              <div>
                                <label style={{
                                  display: 'block',
                                  fontSize: '0.75em',
                                  color: THEME.textMuted,
                                  marginBottom: '4px'
                                }}>
                                  End (s):
                                </label>
                                <input
                                  type="number"
                                  step="0.1"
                                  placeholder="0.0"
                                  value={popupConfigs[segment.segment_number]?.endTime || ''}
                                  onChange={(e) => {
                                    setPopupConfigs(prev => ({
                                      ...prev,
                                      [segment.segment_number]: {
                                        ...(prev[segment.segment_number] || { title: '', text: '', x: 30, y: 26, startTime: '' }),
                                        endTime: e.target.value
                                      }
                                    }));
                                  }}
                                  style={{
                                    width: '100%',
                                    padding: '6px',
                                    background: THEME.bgDark,
                                    border: `1px solid ${THEME.borderDark}`,
                                    borderRadius: '4px',
                                    color: THEME.textLight,
                                    fontSize: '0.85em'
                                  }}
                                />
                              </div>

                              {/* X Position */}
                              <div>
                                <label style={{
                                  display: 'block',
                                  fontSize: '0.75em',
                                  color: THEME.textMuted,
                                  marginBottom: '4px'
                                }}>
                                  X Position:
                                </label>
                                <input
                                  type="number"
                                  value={popupConfigs[segment.segment_number]?.x ?? 30}
                                  onChange={(e) => {
                                    setPopupConfigs(prev => ({
                                      ...prev,
                                      [segment.segment_number]: {
                                        ...(prev[segment.segment_number] || { title: '', text: '', y: 26, startTime: '', endTime: '' }),
                                        x: parseInt(e.target.value) || 0
                                      }
                                    }));
                                  }}
                                  style={{
                                    width: '100%',
                                    padding: '6px',
                                    background: THEME.bgDark,
                                    border: `1px solid ${THEME.borderDark}`,
                                    borderRadius: '4px',
                                    color: THEME.textLight,
                                    fontSize: '0.85em'
                                  }}
                                />
                              </div>

                              {/* Y Position */}
                              <div>
                                <label style={{
                                  display: 'block',
                                  fontSize: '0.75em',
                                  color: THEME.textMuted,
                                  marginBottom: '4px'
                                }}>
                                  Y Position:
                                </label>
                                <input
                                  type="number"
                                  value={popupConfigs[segment.segment_number]?.y ?? 26}
                                  onChange={(e) => {
                                    setPopupConfigs(prev => ({
                                      ...prev,
                                      [segment.segment_number]: {
                                        ...(prev[segment.segment_number] || { title: '', text: '', x: 30, startTime: '', endTime: '' }),
                                        y: parseInt(e.target.value) || 0
                                      }
                                    }));
                                  }}
                                  style={{
                                    width: '100%',
                                    padding: '6px',
                                    background: THEME.bgDark,
                                    border: `1px solid ${THEME.borderDark}`,
                                    borderRadius: '4px',
                                    color: THEME.textLight,
                                    fontSize: '0.85em'
                                  }}
                                />
                              </div>
                            </div>

                            {/* Helper Text */}
                            <div style={{
                              marginTop: '8px',
                              fontSize: '0.75em',
                              color: THEME.textMuted,
                              fontStyle: 'italic'
                            }}>
                              💡 Leave start/end times empty if you want to set them later during video stitching
                            </div>
                          </div>
                        )}

                        {/* Action ID Reference */}
                        <div style={{
                          marginTop: '12px',
                          background: THEME.bgMedium,
                          padding: '8px 12px',
                          borderRadius: '6px',
                          fontFamily: 'monospace',
                          fontSize: '0.75em',
                          color: THEME.textMuted
                        }}>
                          Action ID: {segment.action_id}
                        </div>
                      </div>
                    );
                  })()}

                  {/* Custom Video Prompt */}
                  {segment.segment_type === 'custom_video' && segment.custom_video_prompt && (
                    <div style={{ marginBottom: '12px' }}>
                      <div style={{
                        color: THEME.accent,
                        fontWeight: 'bold',
                        marginBottom: '8px',
                        fontSize: '0.9em'
                      }}>
                        CUSTOM VIDEO PROMPT:
                      </div>
                      <div style={{
                        background: THEME.bgMedium,
                        padding: '12px',
                        borderRadius: '6px',
                        border: `1px solid ${segmentColor}44`
                      }}>
                        <div style={{ marginBottom: '8px', color: THEME.textLight }}>
                          <strong>Description:</strong> {segment.custom_video_prompt.description}
                        </div>
                        {segment.custom_video_prompt.space && (
                          <div style={{ marginBottom: '8px', color: THEME.textMuted, fontSize: '0.9em' }}>
                            <strong>Space:</strong> {typeof segment.custom_video_prompt.space === 'string'
                              ? segment.custom_video_prompt.space
                              : segment.custom_video_prompt.space.description || 'Unknown Space'}
                          </div>
                        )}
                        {segment.custom_video_prompt.characters_involved && (
                          <div style={{ marginBottom: '8px', color: THEME.textMuted, fontSize: '0.9em' }}>
                            <strong>Characters:</strong> {segment.custom_video_prompt.characters_involved.join(', ')}
                          </div>
                        )}
                        <div style={{
                          marginTop: '12px',
                          padding: '12px',
                          background: THEME.bgDark,
                          borderRadius: '4px',
                          fontSize: '0.85em',
                          color: THEME.textMuted,
                          fontFamily: 'monospace',
                          whiteSpace: 'pre-wrap',
                          lineHeight: '1.6'
                        }}>
                          <strong style={{ color: THEME.accent }}>Shot:</strong> {segment.custom_video_prompt.shot_description}{'\n'}
                          <strong style={{ color: THEME.accent }}>Camera:</strong> {segment.custom_video_prompt.camera}{'\n'}
                          <strong style={{ color: THEME.accent }}>Action:</strong> {segment.custom_video_prompt.action}{'\n'}
                          <strong style={{ color: THEME.accent }}>Mood:</strong> {segment.custom_video_prompt.mood}
                        </div>

                        {/* Dialogue Field */}
                        {segment.custom_video_prompt.dialogue && (
                          <div style={{ marginTop: '12px' }}>
                            <div style={{
                              color: THEME.accent,
                              fontWeight: 'bold',
                              marginBottom: '8px',
                              fontSize: '0.9em',
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center'
                            }}>
                              <span>DIALOGUE:</span>
                              {unsavedChanges && (
                                <span style={{ fontSize: '0.8em', color: THEME.textMuted }}>
                                  {savingChanges ? '💾 Saving...' : '⚠️ Unsaved changes'}
                                </span>
                              )}
                            </div>
                            <textarea
                              value={segments.find(s => s.segment_number === segment.segment_number)?.custom_video_prompt?.dialogue || ''}
                              onChange={(e) => {
                                const newSegments = segments.map(s =>
                                  s.segment_number === segment.segment_number
                                    ? {
                                        ...s,
                                        custom_video_prompt: {
                                          ...s.custom_video_prompt,
                                          dialogue: e.target.value
                                        }
                                      }
                                    : s
                                );
                                setSegments(newSegments);
                                setUnsavedChanges(true);
                              }}
                              onBlur={() => {
                                if (unsavedChanges) {
                                  saveEpisodeChanges(segments);
                                }
                              }}
                              style={{
                                width: '100%',
                                padding: '12px',
                                background: THEME.bgMedium,
                                border: `2px solid ${THEME.accent}`,
                                borderRadius: '6px',
                                color: THEME.textLight,
                                fontStyle: 'italic',
                                resize: 'vertical',
                                minHeight: '80px',
                                fontSize: '0.95em',
                                lineHeight: '1.6'
                              }}
                            />
                          </div>
                        )}

                        {/* Regenerate Prompt Button */}
                        <button
                          onClick={() => regenerateSegmentPrompt(segment)}
                          disabled={regeneratingPrompt[segment.segment_number]}
                          style={{
                            marginTop: '12px',
                            padding: '6px 12px',
                            borderRadius: '6px',
                            border: 'none',
                            backgroundColor: regeneratingPrompt[segment.segment_number] ? THEME.textMuted : THEME.accent,
                            color: THEME.textLight,
                            cursor: regeneratingPrompt[segment.segment_number] ? 'not-allowed' : 'pointer',
                            fontSize: '0.8em',
                            fontWeight: '600',
                            opacity: regeneratingPrompt[segment.segment_number] ? 0.6 : 1
                          }}
                        >
                          {regeneratingPrompt[segment.segment_number]
                            ? '⏳ Regenerating Prompt...'
                            : '🔄 Regenerate Prompt with AI'}
                        </button>

                        {/* Generated Scene Images - Reference, Before, After */}
                        {data.segment_scene_images?.[segment.segment_number] && (
                          <div style={{ marginTop: '12px' }}>
                            {/* Check format: new (reference/before/after), old (start/end), or legacy (single) */}
                            {data.segment_scene_images[segment.segment_number].reference_image ? (
                              <div>
                                <div style={{
                                  fontSize: '0.85em',
                                  color: THEME.accent,
                                  marginBottom: '8px',
                                  fontWeight: 'bold'
                                }}>
                                  🎬 Scene Images (Reference + Before + After):
                                </div>
                                <div style={{
                                  display: 'grid',
                                  gridTemplateColumns: '1fr 1fr 1fr',
                                  gap: '12px'
                                }}>
                                  {/* Reference Image (during action) */}
                                  <div>
                                    <div style={{
                                      fontSize: '0.75em',
                                      color: THEME.textMuted,
                                      marginBottom: '4px',
                                      fontWeight: '600'
                                    }}>
                                      REFERENCE (during):
                                    </div>
                                    <img
                                      src={data.segment_scene_images[segment.segment_number].reference_image}
                                      alt="Reference image"
                                      style={{
                                        width: '100%',
                                        borderRadius: '8px',
                                        border: `2px solid ${THEME.accent}`
                                      }}
                                    />
                                  </div>
                                  {/* Before Image */}
                                  {data.segment_scene_images[segment.segment_number].before_image && (
                                    <div>
                                      <div style={{
                                        fontSize: '0.75em',
                                        color: THEME.textMuted,
                                        marginBottom: '4px',
                                        fontWeight: '600'
                                      }}>
                                        BEFORE (t=0s):
                                      </div>
                                      <img
                                        src={data.segment_scene_images[segment.segment_number].before_image}
                                        alt="Before image"
                                        style={{
                                          width: '100%',
                                          borderRadius: '8px',
                                          border: `2px solid ${THEME.accent}`
                                        }}
                                      />
                                    </div>
                                  )}
                                  {/* After Image */}
                                  {data.segment_scene_images[segment.segment_number].after_image && (
                                    <div>
                                      <div style={{
                                        fontSize: '0.75em',
                                        color: THEME.textMuted,
                                        marginBottom: '4px',
                                        fontWeight: '600'
                                      }}>
                                        AFTER (t=8s):
                                      </div>
                                      <img
                                        src={data.segment_scene_images[segment.segment_number].after_image}
                                        alt="After image"
                                        style={{
                                          width: '100%',
                                          borderRadius: '8px',
                                          border: `2px solid ${THEME.accent}`
                                        }}
                                      />
                                    </div>
                                  )}
                                </div>
                              </div>
                            ) : data.segment_scene_images[segment.segment_number].start_frame ? (
                              /* Old format: start/end frames */
                              <div>
                                <div style={{
                                  fontSize: '0.85em',
                                  color: THEME.accent,
                                  marginBottom: '8px',
                                  fontWeight: 'bold'
                                }}>
                                  🎬 Start & End Frames:
                                </div>
                                <div style={{
                                  display: 'grid',
                                  gridTemplateColumns: '1fr 1fr',
                                  gap: '12px'
                                }}>
                                  <div>
                                    <div style={{
                                      fontSize: '0.75em',
                                      color: THEME.textMuted,
                                      marginBottom: '4px',
                                      fontWeight: '600'
                                    }}>
                                      START (t=0s):
                                    </div>
                                    <img
                                      src={data.segment_scene_images[segment.segment_number].start_frame}
                                      alt="Start frame"
                                      style={{
                                        width: '100%',
                                        borderRadius: '8px',
                                        border: `2px solid ${THEME.accent}`
                                      }}
                                    />
                                  </div>
                                  {data.segment_scene_images[segment.segment_number].end_frame && (
                                    <div>
                                      <div style={{
                                        fontSize: '0.75em',
                                        color: THEME.textMuted,
                                        marginBottom: '4px',
                                        fontWeight: '600'
                                      }}>
                                        END (t=8s):
                                      </div>
                                      <img
                                        src={data.segment_scene_images[segment.segment_number].end_frame}
                                        alt="End frame"
                                        style={{
                                          width: '100%',
                                          borderRadius: '8px',
                                          border: `2px solid ${THEME.accent}`
                                        }}
                                      />
                                    </div>
                                  )}
                                </div>
                              </div>
                            ) : data.segment_scene_images[segment.segment_number].url ? (
                              /* Legacy single frame display */
                              <div>
                                <div style={{
                                  fontSize: '0.85em',
                                  color: THEME.accent,
                                  fontWeight: 'bold',
                                  marginBottom: '6px'
                                }}>
                                  Generated Reference Image:
                                </div>
                                <img
                                  src={data.segment_scene_images[segment.segment_number].url}
                                  alt="Scene reference"
                                  style={{
                                    width: '100%',
                                    maxWidth: '400px',
                                    borderRadius: '8px',
                                    border: `2px solid ${THEME.accent}`
                                  }}
                                />
                              </div>
                            ) : null}
                          </div>
                        )}

                        {/* Action Buttons */}
                        <div style={{
                          display: 'flex',
                          gap: '8px',
                          flexWrap: 'wrap',
                          marginTop: '12px'
                        }}>
                          {/* Generate Reference Image Button */}
                          <button
                            onClick={() => generateSceneImageForSegment(segment)}
                            disabled={generatingImage[segment.segment_number]}
                            style={{
                              padding: '8px 16px',
                              borderRadius: '6px',
                              border: 'none',
                              backgroundColor: generatingImage[segment.segment_number] ? THEME.textMuted : '#FF9800',
                              color: THEME.textLight,
                              cursor: generatingImage[segment.segment_number] ? 'not-allowed' : 'pointer',
                              fontSize: '0.85em',
                              fontWeight: '600',
                              opacity: generatingImage[segment.segment_number] ? 0.6 : 1
                            }}
                          >
                            {generatingImage[segment.segment_number]
                              ? '⏳ Generating...'
                              : data.segment_scene_images?.[segment.segment_number]?.url
                                ? '🔄 Regenerate Reference Image'
                                : '🖼️ Generate Reference Image'}
                          </button>

                          {/* Generate Veo3 Video Button */}
                          <button
                            onClick={() => generateCustomVideoForSegment(segment)}
                            disabled={generatingVideo[segment.segment_number]}
                            style={{
                              padding: '8px 16px',
                              borderRadius: '6px',
                              border: 'none',
                              backgroundColor: generatingVideo[segment.segment_number] ? THEME.textMuted : THEME.accent,
                              color: THEME.textLight,
                              cursor: generatingVideo[segment.segment_number] ? 'not-allowed' : 'pointer',
                              fontSize: '0.85em',
                              fontWeight: '600',
                              opacity: generatingVideo[segment.segment_number] ? 0.6 : 1
                            }}
                          >
                            {generatingVideo[segment.segment_number]
                              ? '⏳ Generating Video...'
                              : '🎬 Generate Veo3 Video'}
                          </button>
                        </div>

                        {/* Custom Video Display */}
                        {(() => {
                          let customVideo = data.segment_videos?.[segment.segment_number];

                          // RETROACTIVE SUPPORT: Check custom_video actions from the last 2 hours
                          if (!customVideo) {
                            const twoHoursAgo = Date.now() - (2 * 60 * 60 * 1000);
                            const matchingCustomVideo = allActions.find(a => {
                              if (a.type !== 'custom_video') return false;
                              const createdAt = new Date(a.created_at).getTime();
                              if (createdAt < twoHoursAgo) return false;

                              // Check if this custom video matches this segment's prompt
                              const customVideoData = a.data?.custom_video || a.data;
                              const segmentPrompt = segment.custom_video_prompt;

                              if (!customVideoData || !segmentPrompt) return false;

                              // Match based on user_description containing key prompt elements
                              const userDesc = customVideoData.user_description || '';
                              return (
                                userDesc.includes(segmentPrompt.description || '') ||
                                (segmentPrompt.dialogue && userDesc.includes(segmentPrompt.dialogue))
                              );
                            });

                            if (matchingCustomVideo) {
                              const cvData = matchingCustomVideo.data?.custom_video || matchingCustomVideo.data;
                              customVideo = {
                                url: cvData.video_url || cvData.videos?.[0]?.url,
                                videos: cvData.videos,
                                generated_at: matchingCustomVideo.created_at,
                                attached_retroactively: true
                              };
                            }
                          }

                          // Get the video URL - prefer processed video if available
                          let displayVideoUrl = null;
                          if (customVideo?.processed_url) {
                            // Prefer processed video (with popups)
                            displayVideoUrl = customVideo.processed_url;
                          } else if (customVideo?.videos && Array.isArray(customVideo.videos) && customVideo.videos.length > 0) {
                            // Use last video from stored sequence
                            displayVideoUrl = customVideo.videos[customVideo.videos.length - 1].url;
                          } else if (customVideo?.url) {
                            // Fallback to stored URL
                            displayVideoUrl = customVideo.url;
                          }

                          return (
                            <div style={{ marginTop: '12px' }}>
                              {displayVideoUrl && (
                                <div style={{ marginBottom: '12px' }}>
                                  <div
                                    draggable={true}
                                    onDragStart={(e) => {
                                      const videoData = {
                                        url: displayVideoUrl,
                                        filename: displayVideoUrl.split('/').pop().split('?')[0],
                                        title: `Custom - ${segment.custom_video_prompt?.description?.substring(0, 50) || 'Custom Video'}`,
                                        type: 'episode_custom_video',
                                        metadata: {
                                          segment_type: 'custom_video',
                                          segment_number: segment.segment_number,
                                          description: segment.custom_video_prompt?.description,
                                          space: segment.custom_video_prompt?.space,
                                          characters: segment.custom_video_prompt?.characters_involved
                                        }
                                      };
                                      e.dataTransfer.setData('video-data', JSON.stringify(videoData));
                                      e.dataTransfer.effectAllowed = 'copy';
                                    }}
                                    style={{
                                      position: 'relative',
                                      cursor: 'grab',
                                      border: `2px solid ${THEME.accent}`,
                                      borderRadius: '8px',
                                      padding: '8px',
                                      background: THEME.bgMedium
                                    }}
                                  >
                                    <div style={{
                                      fontSize: '0.85em',
                                      color: THEME.accent,
                                      marginBottom: '8px',
                                      fontWeight: 'bold',
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: '8px'
                                    }}>
                                      <span style={{ fontSize: '1.2em' }}>⋮⋮</span>
                                      Custom Video - Drag to stitcher
                                      {customVideo?.processed_url && (
                                        <span style={{
                                          fontSize: '0.75em',
                                          background: '#9C27B0',
                                          color: THEME.textLight,
                                          padding: '2px 8px',
                                          borderRadius: '4px',
                                          marginLeft: '8px'
                                        }}>
                                          ✓ Processed
                                        </span>
                                      )}
                                    </div>
                                    <video
                                      key={displayVideoUrl}
                                      controls
                                      style={{
                                        width: '100%',
                                        maxWidth: '600px',
                                        borderRadius: '4px',
                                        pointerEvents: 'auto'
                                      }}
                                      onMouseDown={(e) => e.stopPropagation()}
                                    >
                                      <source src={displayVideoUrl} type="video/mp4" />
                                      Your browser does not support the video tag.
                                    </video>
                                  </div>

                                  {/* Show multi-part video info */}
                                  {customVideo?.videos && customVideo.videos.length > 1 && (
                                    <div style={{
                                      marginTop: '8px',
                                      padding: '8px',
                                      background: THEME.bgDark,
                                      borderRadius: '4px',
                                      fontSize: '0.8em',
                                      color: THEME.textMuted
                                    }}>
                                      Multi-part video: {customVideo.videos.length} segments
                                      <div style={{ marginTop: '4px' }}>
                                        {customVideo.videos.map((vid, idx) => (
                                          <a
                                            key={idx}
                                            href={vid.url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            style={{
                                              color: THEME.accent,
                                              marginRight: '12px',
                                              textDecoration: 'none'
                                            }}
                                          >
                                            Part {idx + 1}
                                          </a>
                                        ))}
                                      </div>
                                    </div>
                                  )}

                                  {/* Process Video Button */}
                                  <button
                                    onClick={() => processHostVideoSegment(segment)}
                                    disabled={processingVideo[`custom-${segment.segment_number}`]}
                                    style={{
                                      marginTop: '12px',
                                      padding: '8px 16px',
                                      borderRadius: '6px',
                                      border: 'none',
                                      backgroundColor: processingVideo[`custom-${segment.segment_number}`] ? THEME.textMuted : '#9C27B0',
                                      color: THEME.textLight,
                                      cursor: processingVideo[`custom-${segment.segment_number}`] ? 'not-allowed' : 'pointer',
                                      fontSize: '0.85em',
                                      fontWeight: '600',
                                      opacity: processingVideo[`custom-${segment.segment_number}`] ? 0.6 : 1
                                    }}
                                  >
                                    {processingVideo[`custom-${segment.segment_number}`]
                                      ? '⏳ Processing Video...'
                                      : customVideo?.processed_url
                                        ? '🔄 Reprocess Video'
                                        : '🎬 Process Video (Add Popups)'}
                                  </button>
                                </div>
                              )}
                            </div>
                          );
                        })()}

                        {/* Add Popup Button (for custom video segments without popups) */}
                        {!segment.popup && !popupConfigs[segment.segment_number] && (
                          <button
                            onClick={() => {
                              setPopupConfigs(prev => ({
                                ...prev,
                                [segment.segment_number]: {
                                  title: '',
                                  text: '',
                                  x: 30,
                                  y: 26,
                                  startTime: '',
                                  endTime: ''
                                }
                              }));
                            }}
                            style={{
                              marginTop: '12px',
                              padding: '8px 16px',
                              borderRadius: '6px',
                              border: 'none',
                              backgroundColor: '#FFC107',
                              color: THEME.bgDark,
                              cursor: 'pointer',
                              fontSize: '0.85em',
                              fontWeight: '600'
                            }}
                          >
                            💡 Add Popup
                          </button>
                        )}

                        {/* Popup Configuration for Custom Video Segments */}
                        {(segment.popup || popupConfigs[segment.segment_number]) && (
                          <div style={{
                            marginTop: '16px',
                            padding: '12px',
                            background: 'rgba(255, 200, 0, 0.1)',
                            border: '1px solid rgba(255, 200, 0, 0.3)',
                            borderRadius: '8px'
                          }}>
                            <div style={{
                              fontSize: '0.9em',
                              fontWeight: 'bold',
                              color: '#FFC107',
                              marginBottom: '12px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between'
                            }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <span>💡</span>
                                <span>Popup Configuration</span>
                              </div>
                              {/* Remove Popup Button */}
                              <button
                                onClick={() => {
                                  const newPopupConfigs = { ...popupConfigs };
                                  delete newPopupConfigs[segment.segment_number];
                                  setPopupConfigs(newPopupConfigs);

                                  const newSegments = segments.map(s =>
                                    s.segment_number === segment.segment_number
                                      ? { ...s, popup: null }
                                      : s
                                  );
                                  setSegments(newSegments);
                                  setUnsavedChanges(true);
                                  saveEpisodeChanges(newSegments);
                                }}
                                style={{
                                  padding: '4px 8px',
                                  borderRadius: '4px',
                                  border: 'none',
                                  backgroundColor: '#f44336',
                                  color: THEME.textLight,
                                  cursor: 'pointer',
                                  fontSize: '0.75em',
                                  fontWeight: '600'
                                }}
                              >
                                🗑️ Remove Popup
                              </button>
                            </div>

                            {/* Popup Title */}
                            <div style={{ marginBottom: '12px' }}>
                              <label style={{
                                display: 'block',
                                fontSize: '0.85em',
                                color: THEME.textMuted,
                                marginBottom: '4px'
                              }}>
                                Popup Title:
                              </label>
                              <input
                                type="text"
                                placeholder="💡 Pro-tip (default)"
                                value={popupConfigs[segment.segment_number]?.title || ''}
                                onChange={(e) => {
                                  const newPopupConfigs = {
                                    ...popupConfigs,
                                    [segment.segment_number]: {
                                      ...(popupConfigs[segment.segment_number] || { text: '', x: 30, y: 26, startTime: '', endTime: '' }),
                                      title: e.target.value
                                    }
                                  };
                                  setPopupConfigs(newPopupConfigs);

                                  // Sync to segments array with all fields
                                  const newSegments = segments.map(s =>
                                    s.segment_number === segment.segment_number
                                      ? {
                                          ...s,
                                          popup: {
                                            title: e.target.value,
                                            text: newPopupConfigs[segment.segment_number].text || '',
                                            x: newPopupConfigs[segment.segment_number].x ?? 30,
                                            y: newPopupConfigs[segment.segment_number].y ?? 26,
                                            startTime: parseFloat(newPopupConfigs[segment.segment_number].startTime) || 0,
                                            endTime: parseFloat(newPopupConfigs[segment.segment_number].endTime) || 0
                                          }
                                        }
                                      : s
                                  );
                                  setSegments(newSegments);
                                  setUnsavedChanges(true);
                                }}
                                onBlur={() => {
                                  if (unsavedChanges) {
                                    saveEpisodeChanges(segments);
                                  }
                                }}
                                style={{
                                  width: '100%',
                                  padding: '8px',
                                  background: THEME.bgDark,
                                  border: `1px solid ${THEME.borderDark}`,
                                  borderRadius: '4px',
                                  color: THEME.textLight,
                                  fontSize: '0.85em',
                                  fontFamily: 'inherit'
                                }}
                              />
                            </div>

                            {/* Popup Text */}
                            <div style={{ marginBottom: '12px' }}>
                              <label style={{
                                display: 'block',
                                fontSize: '0.85em',
                                color: THEME.textMuted,
                                marginBottom: '4px'
                              }}>
                                Popup Text:
                              </label>
                              <textarea
                                value={popupConfigs[segment.segment_number]?.text || (typeof segment.popup === 'string' ? segment.popup : segment.popup?.text || segment.popup?.body || '')}
                                onChange={(e) => {
                                  const newPopupConfigs = {
                                    ...popupConfigs,
                                    [segment.segment_number]: {
                                      ...(popupConfigs[segment.segment_number] || { title: '', x: 30, y: 26, startTime: '', endTime: '' }),
                                      text: e.target.value
                                    }
                                  };
                                  setPopupConfigs(newPopupConfigs);

                                  // Sync to segments array with all fields
                                  const newSegments = segments.map(s =>
                                    s.segment_number === segment.segment_number
                                      ? {
                                          ...s,
                                          popup: {
                                            title: newPopupConfigs[segment.segment_number].title || '',
                                            text: e.target.value,
                                            x: newPopupConfigs[segment.segment_number].x ?? 30,
                                            y: newPopupConfigs[segment.segment_number].y ?? 26,
                                            startTime: parseFloat(newPopupConfigs[segment.segment_number].startTime) || 0,
                                            endTime: parseFloat(newPopupConfigs[segment.segment_number].endTime) || 0
                                          }
                                        }
                                      : s
                                  );
                                  setSegments(newSegments);
                                  setUnsavedChanges(true);
                                }}
                                onBlur={() => {
                                  if (unsavedChanges) {
                                    saveEpisodeChanges(segments);
                                  }
                                }}
                                style={{
                                  width: '100%',
                                  padding: '8px',
                                  background: THEME.bgDark,
                                  border: `1px solid ${THEME.borderDark}`,
                                  borderRadius: '4px',
                                  color: THEME.textLight,
                                  fontSize: '0.85em',
                                  fontFamily: 'inherit',
                                  resize: 'vertical',
                                  minHeight: '60px'
                                }}
                              />
                            </div>

                            {/* Time and Position Fields */}
                            <div style={{
                              display: 'grid',
                              gridTemplateColumns: 'repeat(4, 1fr)',
                              gap: '8px'
                            }}>
                              {/* Start Time */}
                              <div>
                                <label style={{
                                  display: 'block',
                                  fontSize: '0.75em',
                                  color: THEME.textMuted,
                                  marginBottom: '4px'
                                }}>
                                  Start (s):
                                </label>
                                <input
                                  type="number"
                                  step="0.1"
                                  placeholder="0.0"
                                  value={popupConfigs[segment.segment_number]?.startTime || ''}
                                  onChange={(e) => {
                                    const newPopupConfigs = {
                                      ...popupConfigs,
                                      [segment.segment_number]: {
                                        ...(popupConfigs[segment.segment_number] || { title: '', text: '', x: 30, y: 26, endTime: '' }),
                                        startTime: e.target.value
                                      }
                                    };
                                    setPopupConfigs(newPopupConfigs);

                                    const newSegments = segments.map(s =>
                                      s.segment_number === segment.segment_number
                                        ? {
                                            ...s,
                                            popup: {
                                              ...(s.popup || {}),
                                              startTime: parseFloat(e.target.value) || 0
                                            }
                                          }
                                        : s
                                    );
                                    setSegments(newSegments);
                                    setUnsavedChanges(true);
                                  }}
                                  onBlur={() => {
                                    if (unsavedChanges) {
                                      saveEpisodeChanges(segments);
                                    }
                                  }}
                                  style={{
                                    width: '100%',
                                    padding: '6px',
                                    background: THEME.bgDark,
                                    border: `1px solid ${THEME.borderDark}`,
                                    borderRadius: '4px',
                                    color: THEME.textLight,
                                    fontSize: '0.85em'
                                  }}
                                />
                              </div>

                              {/* End Time */}
                              <div>
                                <label style={{
                                  display: 'block',
                                  fontSize: '0.75em',
                                  color: THEME.textMuted,
                                  marginBottom: '4px'
                                }}>
                                  End (s):
                                </label>
                                <input
                                  type="number"
                                  step="0.1"
                                  placeholder="0.0 (no end)"
                                  value={popupConfigs[segment.segment_number]?.endTime || ''}
                                  onChange={(e) => {
                                    const newPopupConfigs = {
                                      ...popupConfigs,
                                      [segment.segment_number]: {
                                        ...(popupConfigs[segment.segment_number] || { title: '', text: '', x: 30, y: 26, startTime: '' }),
                                        endTime: e.target.value
                                      }
                                    };
                                    setPopupConfigs(newPopupConfigs);

                                    const newSegments = segments.map(s =>
                                      s.segment_number === segment.segment_number
                                        ? {
                                            ...s,
                                            popup: {
                                              ...(s.popup || {}),
                                              endTime: parseFloat(e.target.value) || 0
                                            }
                                          }
                                        : s
                                    );
                                    setSegments(newSegments);
                                    setUnsavedChanges(true);
                                  }}
                                  onBlur={() => {
                                    if (unsavedChanges) {
                                      saveEpisodeChanges(segments);
                                    }
                                  }}
                                  style={{
                                    width: '100%',
                                    padding: '6px',
                                    background: THEME.bgDark,
                                    border: `1px solid ${THEME.borderDark}`,
                                    borderRadius: '4px',
                                    color: THEME.textLight,
                                    fontSize: '0.85em'
                                  }}
                                />
                              </div>

                              {/* X Position */}
                              <div>
                                <label style={{
                                  display: 'block',
                                  fontSize: '0.75em',
                                  color: THEME.textMuted,
                                  marginBottom: '4px'
                                }}>
                                  X Position:
                                </label>
                                <input
                                  type="number"
                                  value={popupConfigs[segment.segment_number]?.x ?? 30}
                                  onChange={(e) => {
                                    const newPopupConfigs = {
                                      ...popupConfigs,
                                      [segment.segment_number]: {
                                        ...(popupConfigs[segment.segment_number] || { title: '', text: '', y: 26, startTime: '', endTime: '' }),
                                        x: parseInt(e.target.value) || 0
                                      }
                                    };
                                    setPopupConfigs(newPopupConfigs);

                                    const newSegments = segments.map(s =>
                                      s.segment_number === segment.segment_number
                                        ? {
                                            ...s,
                                            popup: {
                                              ...(s.popup || {}),
                                              x: parseInt(e.target.value) || 0
                                            }
                                          }
                                        : s
                                    );
                                    setSegments(newSegments);
                                    setUnsavedChanges(true);
                                  }}
                                  onBlur={() => {
                                    if (unsavedChanges) {
                                      saveEpisodeChanges(segments);
                                    }
                                  }}
                                  style={{
                                    width: '100%',
                                    padding: '6px',
                                    background: THEME.bgDark,
                                    border: `1px solid ${THEME.borderDark}`,
                                    borderRadius: '4px',
                                    color: THEME.textLight,
                                    fontSize: '0.85em'
                                  }}
                                />
                              </div>

                              {/* Y Position */}
                              <div>
                                <label style={{
                                  display: 'block',
                                  fontSize: '0.75em',
                                  color: THEME.textMuted,
                                  marginBottom: '4px'
                                }}>
                                  Y Position:
                                </label>
                                <input
                                  type="number"
                                  value={popupConfigs[segment.segment_number]?.y ?? 26}
                                  onChange={(e) => {
                                    const newPopupConfigs = {
                                      ...popupConfigs,
                                      [segment.segment_number]: {
                                        ...(popupConfigs[segment.segment_number] || { title: '', text: '', x: 30, startTime: '', endTime: '' }),
                                        y: parseInt(e.target.value) || 0
                                      }
                                    };
                                    setPopupConfigs(newPopupConfigs);

                                    const newSegments = segments.map(s =>
                                      s.segment_number === segment.segment_number
                                        ? {
                                            ...s,
                                            popup: {
                                              ...(s.popup || {}),
                                              y: parseInt(e.target.value) || 0
                                            }
                                          }
                                        : s
                                    );
                                    setSegments(newSegments);
                                    setUnsavedChanges(true);
                                  }}
                                  onBlur={() => {
                                    if (unsavedChanges) {
                                      saveEpisodeChanges(segments);
                                    }
                                  }}
                                  style={{
                                    width: '100%',
                                    padding: '6px',
                                    background: THEME.bgDark,
                                    border: `1px solid ${THEME.borderDark}`,
                                    borderRadius: '4px',
                                    color: THEME.textLight,
                                    fontSize: '0.85em'
                                  }}
                                />
                              </div>
                            </div>

                            {/* Helper Text */}
                            <div style={{
                              marginTop: '8px',
                              fontSize: '0.75em',
                              color: THEME.textMuted,
                              fontStyle: 'italic'
                            }}>
                              Position values are in percentage (0-100). Start time 0 = immediate. End time 0 = stays until video ends.
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Notes */}
                  {segment.notes && (
                    <div style={{
                      fontSize: '0.85em',
                      color: THEME.textMuted,
                      fontStyle: 'italic',
                      padding: '8px 12px',
                      background: THEME.bgMedium,
                      borderRadius: '6px'
                    }}>
                      💡 {segment.notes}
                    </div>
                  )}
                </div>
              )}
            </div>
                );
              })()}

            {/* Drop zone AFTER last segment */}
            {index === segments.length - 1 && (
              <div
                onDragOver={(e) => {
                  e.preventDefault();
                  e.dataTransfer.dropEffect = 'move';
                  setDragOverIndex(segments.length);
                }}
                onDragLeave={() => {
                  setDragOverIndex(null);
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  handleSegmentDrop(e, segments.length);
                }}
                style={{
                  height: dragOverIndex === segments.length ? '40px' : '8px',
                  background: dragOverIndex === segments.length
                    ? `linear-gradient(90deg, ${THEME.accent}22, ${THEME.accent}44, ${THEME.accent}22)`
                    : 'transparent',
                  border: dragOverIndex === segments.length ? `2px dashed ${THEME.accent}` : 'none',
                  borderRadius: '4px',
                  marginTop: dragOverIndex === segments.length ? '8px' : '0',
                  transition: 'all 0.2s',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: THEME.accent,
                  fontWeight: 'bold',
                  fontSize: '0.9em'
                }}
              >
                {dragOverIndex === segments.length && '↓ Drop here ↓'}
              </div>
            )}
          </React.Fragment>
          );
        })}
      </div>

      {/* Episode Metadata */}
      <div style={{
        padding: '12px 16px',
        background: THEME.bgDark,
        borderTop: `1px solid ${THEME.borderDark}`,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        fontSize: '0.85em',
        color: THEME.textMuted
      }}>
        <div>
          Generated with {data.model} on {new Date(data.generated_at).toLocaleString()}
        </div>
        <div>
          Round {data.round_number} • {data.game_type}
        </div>
      </div>

      {/* Custom Video Segment Form Modal */}
      {showCustomVideoForm && (
        <CustomVideoSegmentForm
          onCancel={() => setShowCustomVideoForm(false)}
          onSubmit={addCustomVideoSegment}
          spaces={[]}
          playerProfiles={users || []}
          THEME={THEME}
        />
      )}
    </div>
  );
};

const WeekSummaryAction = ({ action, users, THEME, showPhotos = true, showNarrative = true, showHighlights = true, showVotes = true, showBenchmarks = true, evictedPlayerIds = new Set(), weekNumber = 1, allActions = [] }) => {
  const COMMON_STYLES = getCommonStyles(THEME);
  const { data } = action;

  console.log('[WeekSummaryAction] Rendering with data:', data);
  // DISABLED: Verbose logging that could cause Object.keys errors
  // console.log('[WeekSummaryAction] Available users:', users?.map(u => u ? ({ ... }) : null));

  // Helper to get full-body image URL (try multiple field names)
  const getFullBodyImageUrl = (user) => {
    if (!user) return null;

    // Check top-level fields first
    const topLevel = user.fullbody_image_url || user.full_body_image_url || user.body_image_url || user.profile_image_url;
    if (topLevel) return topLevel;

    // Check inside data object
    if (user.data) {
      return user.data.fullbody_image_url ||
             user.data.full_body_image_url ||
             user.data.body_image_url ||
             user.data.profile_image_url ||
             user.data.image_url ||
             user.data.photo_url;
    }

    return null;
  };

  // Helper to find user by player ID (preferred) or name (fallback)
  const findUser = (playerId, name) => {
    if (!users) return null;

    // Try player ID first (most reliable) - check multiple possible field names
    if (playerId) {
      const user = users.find(u =>
        u.game_player_id === playerId ||
        u.id === playerId ||
        u.player_id === playerId ||
        u.game_instance_player_id === playerId
      );
      if (user) {
        console.log(`[WeekSummaryAction] ✓ Found user by ID ${playerId}:`, user.name, 'hasImage:', !!getFullBodyImageUrl(user), 'imageUrl:', getFullBodyImageUrl(user));
        return user;
      } else {
        console.log(`[WeekSummaryAction] ✗ Could not find user by ID ${playerId}, trying name "${name}"`);
      }
    }

    // Fallback to name matching
    if (!name) return null;

    // Try exact match
    let user = users.find(u => u.name === name);
    if (user) return user;

    // Try partial match (in case of extra text)
    user = users.find(u => u.name.includes(name) || name.includes(u.name));
    if (user) return user;

    // Try matching without parentheses content (e.g., "Alice (Model)" -> "Alice")
    const cleanName = name.replace(/\s*\([^)]*\)\s*/g, '').trim();
    user = users.find(u => {
      const cleanUserName = u.name.replace(/\s*\([^)]*\)\s*/g, '').trim();
      return cleanUserName === cleanName || cleanUserName.includes(cleanName) || cleanName.includes(cleanUserName);
    });

    console.log(`[WeekSummaryAction] Finding user by name "${name}":`, user ? `Found: ${user.name}` : 'NOT FOUND');
    return user || null;
  };

  // Get users for key roles using player IDs
  const hohUser = findUser(data.hohWinnerId, data.hohWinner);
  const vetoUser = findUser(data.vetoWinnerId, data.vetoWinner);
  const evictedUser = findUser(data.evictedPlayerId, data.evictedPlayer);
  const savedUser = data.savedPlayer ? findUser(data.savedPlayerId, data.savedPlayer) : null;
  const renomUser = data.replacementNominee ? findUser(data.replacementNomineeId, data.replacementNominee) : null;

  // Count votes
  const voteCounts = {};
  (data.votes || []).forEach(vote => {
    voteCounts[vote.votedFor] = (voteCounts[vote.votedFor] || 0) + 1;
  });

  // Find round_analysis action for this summary's round
  const summaryRoundId = action.game_instance_round_id;

  console.log('[BENCHMARKS] Summary action:', action.id, 'round:', summaryRoundId);
  console.log('[BENCHMARKS] Total actions provided:', allActions.length);

  // Build a complete round sequence map by analyzing all actions
  // This determines the chronological order of all rounds in the game
  const roundFirstAction = new Map(); // round_id -> {timestamp, actionIndex}
  const sortedActions = [...allActions].sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());

  sortedActions.forEach((a, idx) => {
    if (a.game_instance_round_id && !roundFirstAction.has(a.game_instance_round_id)) {
      roundFirstAction.set(a.game_instance_round_id, {
        timestamp: new Date(a.created_at).getTime(),
        sequence: roundFirstAction.size // Sequential round number in game
      });
    }
  });

  // Get the sequence number for the summary's round
  const summaryRoundInfo = roundFirstAction.get(summaryRoundId);
  const summaryRoundSequence = summaryRoundInfo ? summaryRoundInfo.sequence : -1;
  console.log('[BENCHMARKS] Summary round sequence:', summaryRoundSequence, 'started at:', summaryRoundInfo ? new Date(summaryRoundInfo.timestamp) : 'unknown');

  // Get all round_analysis actions with their round sequence numbers
  const roundAnalyses = allActions
    .filter(a => a.type === 'round_analysis')
    .map(ra => {
      const roundInfo = roundFirstAction.get(ra.game_instance_round_id);
      return {
        action: ra,
        roundSequence: roundInfo ? roundInfo.sequence : -1,
        roundTimestamp: roundInfo ? roundInfo.timestamp : new Date(ra.created_at).getTime()
      };
    })
    .sort((a, b) => a.roundSequence - b.roundSequence);

  console.log('[BENCHMARKS] Found round_analysis actions:', roundAnalyses.length);
  roundAnalyses.forEach((ra, i) => {
    console.log(`[BENCHMARKS] Analysis ${i}:`, ra.action.id, 'round:', ra.action.game_instance_round_id, 'sequence:', ra.roundSequence, 'started:', new Date(ra.roundTimestamp), 'has cumulative_benchmarks:', !!ra.action.data?.cumulative_benchmarks);
  });

  let roundAnalysis = null;

  // FIRST PRIORITY: Try to find round_analysis for the same round as this summary
  if (summaryRoundId) {
    const match = roundAnalyses.find(ra => ra.action.game_instance_round_id === summaryRoundId);
    if (match) {
      roundAnalysis = match.action;
      console.log('[BENCHMARKS] Found analysis for same round:', roundAnalysis.id);
    }
  }

  // SECOND PRIORITY: Find the most recent round_analysis from a round with lower sequence number (came before in game)
  if (!roundAnalysis && summaryRoundSequence >= 0) {
    for (let i = roundAnalyses.length - 1; i >= 0; i--) {
      if (roundAnalyses[i].roundSequence < summaryRoundSequence) {
        roundAnalysis = roundAnalyses[i].action;
        console.log('[BENCHMARKS] Found analysis from closest round before summary (by sequence):', roundAnalysis.id, 'from round sequence:', roundAnalyses[i].roundSequence, 'vs summary sequence:', summaryRoundSequence);
        break;
      }
    }
  }

  // THIRD PRIORITY: If no analysis before (summary is from early game), use the first one after
  if (!roundAnalysis && roundAnalyses.length > 0) {
    roundAnalysis = roundAnalyses[0].action;
    console.log('[BENCHMARKS] No analysis before summary, using first available:', roundAnalysis.id);
  }

  if (!roundAnalysis) {
    console.log('[BENCHMARKS] NO ROUND ANALYSIS FOUND');
  }

  // Extract and process benchmark data
  // Note: Backend stores benchmarks as 'round_benchmarks' (single round performance) or 'cumulative_benchmarks' (overall game performance)
  // We prefer round_benchmarks to show week-specific performance changes
  let playerBenchmarks = null;
  const benchmarksSource = roundAnalysis?.data?.round_benchmarks || roundAnalysis?.data?.cumulative_benchmarks;

  if (roundAnalysis && benchmarksSource) {
    console.log('[BENCHMARKS] Processing benchmarks from analysis:', roundAnalysis.id);
    console.log('[BENCHMARKS] Using source:', roundAnalysis.data?.round_benchmarks ? 'round_benchmarks' : 'cumulative_benchmarks');
    console.log('[BENCHMARKS] Benchmarks data:', benchmarksSource);

    // Build a map of player NAME to their benchmark stats
    // benchmarksSource is an array like: [{ benchmark_name: "General Intelligence", scores: { "Player1": { score: 8.5, explanation: "..." }, ... } }, ...]
    const playerStats = {};  // Will be: { "Player1": { "General Intelligence": 8.5, "Deceptive Capacity": 7.0, ... }, ... }

    benchmarksSource.forEach(benchmark => {
      const metricName = benchmark.benchmark_name;
      if (!benchmark.scores) return; // Skip if no scores
      Object.entries(benchmark.scores).forEach(([playerName, scoreData]) => {
        if (!playerStats[playerName]) {
          playerStats[playerName] = {};
        }
        playerStats[playerName][metricName] = typeof scoreData === 'object' ? scoreData.score : scoreData;
      });
    });

    console.log('[BENCHMARKS] Processed player stats:', playerStats);
    console.log('[BENCHMARKS] Player names from benchmarks:', Object.keys(playerStats));

    // For each benchmark metric, find min and max values
    const metrics = {};
    const allPlayerNames = Object.keys(playerStats);

    // Get all possible metric names from all players
    const allMetricNames = new Set();
    allPlayerNames.forEach(playerName => {
      Object.keys(playerStats[playerName] || {}).forEach(metric => {
        allMetricNames.add(metric);
      });
    });

    // For each metric, calculate winners and losers
    allMetricNames.forEach(metricName => {
      const values = [];
      allPlayerNames.forEach(playerName => {
        const value = playerStats[playerName]?.[metricName];
        if (typeof value === 'number') {
          values.push({ playerName, value });
        }
      });

      if (values.length > 0) {
        values.sort((a, b) => b.value - a.value);
        const maxValue = values[0].value;
        const minValue = values[values.length - 1].value;

        metrics[metricName] = {
          winners: values.filter(v => v.value === maxValue).map(v => v.playerName),
          losers: values.filter(v => v.value === minValue).map(v => v.playerName),
          maxValue,
          minValue
        };
      }
    });

    console.log('[BENCHMARKS] Metrics calculated:', metrics);

    // Build player benchmark awards - map by player NAME
    playerBenchmarks = {};
    allPlayerNames.forEach(playerName => {
      playerBenchmarks[playerName] = {
        winning: [],
        losing: []
      };

      Object.entries(metrics).forEach(([metricName, metricData]) => {
        if (metricData.winners.includes(playerName)) {
          // Metric name is already nicely formatted from the AI
          playerBenchmarks[playerName].winning.push(metricName);
        }
        if (metricData.losers.includes(playerName) && metricData.maxValue !== metricData.minValue) {
          // Only show as losing if not everyone has the same value
          playerBenchmarks[playerName].losing.push(metricName);
        }
      });
    });
    console.log('[BENCHMARKS] Final playerBenchmarks:', playerBenchmarks);
  } else {
    console.log('[BENCHMARKS] No benchmarks data in round analysis');
  }

  // Separate active and evicted players
  const activePlayers = [];
  const evictedPlayers = [];

  console.log('[BENCHMARKS] Users array:', users);
  console.log('[BENCHMARKS] User IDs:', users?.map(u => ({
    name: u.name,
    game_player_id: u.game_player_id,
    id: u.id,
    player_id: u.player_id,
    game_instance_player_id: u.game_instance_player_id
  })));

  if (users) {
    // Only use players evicted in PREVIOUS weeks (not this week)
    // The player evicted this week was still active during this week
    users.forEach(user => {
      // Check if player was evicted in a previous week
      const userId = user.game_player_id || user.id || user.player_id || user.game_instance_player_id;
      const isEvicted = evictedPlayerIds.has(userId);

      if (isEvicted) {
        evictedPlayers.push(user);
      } else {
        activePlayers.push(user);
      }
    });
  }

  return (
    <div style={{
      backgroundColor: '#FFFFFF',
      padding: '16px',
      borderRadius: '8px',
      marginBottom: '16px',
      border: '2px solid #E0E0E0',
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
    }}>
      {/* Header with Title and Roster */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '16px',
        borderBottom: '2px solid #4A90E2',
        paddingBottom: '12px',
        gap: '12px'
      }}>
        {/* Left: Title with Week Number */}
        <div style={{
          fontSize: '2em',
          fontWeight: '600',
          color: '#1a1a1a',
          textAlign: 'left'
        }}>
          📺 Week {weekNumber} Summary
        </div>

        {/* Right: Player Roster */}
        {showPhotos && (
          <div style={{
            display: 'flex',
            alignItems: 'flex-end',
            gap: '8px',
            flexWrap: 'wrap',
            justifyContent: 'flex-end'
          }}>
            {/* Active Players */}
            {activePlayers.map((player, idx) => {
              // Extract model name (before parentheses) from "Model (Character)" format
              const modelMatch = player.name.match(/^([^(]+)/);
              const displayName = modelMatch ? modelMatch[1].trim() : player.name;

              return (
                <div key={idx} style={{ textAlign: 'center', flex: '0 0 40px' }}>
                  <div style={{ fontSize: '0.65em', color: '#333', fontWeight: '500', lineHeight: '1.2', marginBottom: '4px' }}>
                    {displayName}
                  </div>
                  {getFullBodyImageUrl(player) ? (
                    <img
                      src={getFullBodyImageUrl(player)}
                      alt={player.name}
                      style={{
                        width: '40px',
                        height: '60px',
                        borderRadius: '4px',
                        objectFit: 'cover',
                        border: '2px solid #4A90E2',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                      }}
                    />
                  ) : (
                    <div style={{
                      width: '40px',
                      height: '60px',
                      borderRadius: '4px',
                      border: '2px solid #4A90E2',
                      backgroundColor: '#FFFFFF',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '1.5em'
                    }}>👤</div>
                  )}
                </div>
              );
            })}

            {/* Spacer between active and evicted */}
            {evictedPlayers.length > 0 && (
              <div style={{
                width: '24px',
                height: '60px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: '4px'
              }}>
                <div style={{
                  width: '2px',
                  height: '40px',
                  backgroundColor: '#D0D0D0'
                }} />
              </div>
            )}

            {/* Evicted Players */}
            {evictedPlayers.map((player, idx) => {
              // Extract model name (before parentheses) from "Model (Character)" format
              const modelMatch = player.name.match(/^([^(]+)/);
              const displayName = modelMatch ? modelMatch[1].trim() : player.name;

              return (
                <div key={idx} style={{ textAlign: 'center', flex: '0 0 40px' }}>
                  <div style={{ fontSize: '0.65em', color: '#999', fontWeight: '500', lineHeight: '1.2', marginBottom: '4px' }}>
                    {displayName}
                  </div>
                  {getFullBodyImageUrl(player) ? (
                    <img
                      src={getFullBodyImageUrl(player)}
                      alt={player.name}
                      style={{
                        width: '40px',
                        height: '60px',
                        borderRadius: '4px',
                        objectFit: 'cover',
                        border: '2px solid #CCC',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                        filter: 'grayscale(100%)',
                        opacity: '0.6'
                      }}
                    />
                  ) : (
                    <div style={{
                      width: '40px',
                      height: '60px',
                      borderRadius: '4px',
                      border: '2px solid #CCC',
                      backgroundColor: '#FFFFFF',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '1.5em',
                      filter: 'grayscale(100%)',
                      opacity: '0.6'
                    }}>👤</div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Key Players Row */}
      {showPhotos && (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'flex-start',
        gap: '24px',
        marginBottom: '40px',
        flexWrap: 'wrap'
      }}>
        {/* HOH Winner */}
        {(hohUser || data.hohWinner) && (
          <div style={{ textAlign: 'center', flex: '0 0 120px' }}>
            <div style={{
              fontSize: '2em',
              marginBottom: '8px'
            }}>👑</div>
            {getFullBodyImageUrl(hohUser) ? (
              <img
                src={getFullBodyImageUrl(hohUser)}
                alt={hohUser.name}
                style={{
                  width: '100px',
                  height: '150px',
                  borderRadius: '8px',
                  objectFit: 'cover',
                  border: '3px solid #FFD700',
                  marginBottom: '8px',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                }}
              />
            ) : (
              <div style={{
                width: '100px',
                height: '150px',
                borderRadius: '8px',
                border: '3px solid #FFD700',
                marginBottom: '8px',
                backgroundColor: '#F8F9FA',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '3em',
                margin: '0 auto 8px auto'
              }}>👑</div>
            )}
            <div style={{ fontWeight: '600', fontSize: '0.9em', color: '#333', marginBottom: '4px' }}>HOH</div>
            <div style={{ fontSize: '0.85em', color: '#666' }}>{hohUser?.name || data.hohWinner}</div>
          </div>
        )}

        {/* Nominees */}
        {(data.initialNominees || []).map((nomineeName, idx) => {
          const nomineeId = data.initialNomineeIds?.[idx];
          const nomineeUser = findUser(nomineeId, nomineeName);

          return (
            <div key={idx} style={{ textAlign: 'center', flex: '0 0 120px' }}>
              <div style={{
                fontSize: '2em',
                marginBottom: '8px'
              }}>🎯</div>
              {getFullBodyImageUrl(nomineeUser) ? (
                <img
                  src={getFullBodyImageUrl(nomineeUser)}
                  alt={nomineeUser?.name}
                  style={{
                    width: '100px',
                    height: '150px',
                    borderRadius: '8px',
                    objectFit: 'cover',
                    border: '3px solid #FF9800',
                    marginBottom: '8px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                  }}
                />
              ) : (
                <div style={{
                  width: '100px',
                  height: '150px',
                  borderRadius: '8px',
                  border: '3px solid #FF9800',
                  marginBottom: '8px',
                  backgroundColor: '#F8F9FA',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '3em',
                  margin: '0 auto 8px auto'
                }}>🎯</div>
              )}
              <div style={{ fontWeight: '600', fontSize: '0.9em', color: '#333', marginBottom: '4px' }}>Nominee</div>
              <div style={{ fontSize: '0.85em', color: '#666' }}>{nomineeUser?.name || nomineeName}</div>
            </div>
          );
        })}

        {/* Veto Winner */}
        {(vetoUser || data.vetoWinner) && (
          <div style={{ textAlign: 'center', flex: '0 0 120px' }}>
            <div style={{
              fontSize: '2em',
              marginBottom: '8px'
            }}>🏆</div>
            {getFullBodyImageUrl(vetoUser) ? (
              <img
                src={getFullBodyImageUrl(vetoUser)}
                alt={vetoUser.name}
                style={{
                  width: '100px',
                  height: '150px',
                  borderRadius: '8px',
                  objectFit: 'cover',
                  border: '3px solid #4A90E2',
                  marginBottom: '8px',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                }}
              />
            ) : (
              <div style={{
                width: '100px',
                height: '150px',
                borderRadius: '8px',
                border: '3px solid #4A90E2',
                marginBottom: '8px',
                backgroundColor: '#F8F9FA',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '3em',
                margin: '0 auto 8px auto'
              }}>🏆</div>
            )}
            <div style={{ fontWeight: '600', fontSize: '0.9em', color: '#333', marginBottom: '4px' }}>Veto Winner</div>
            <div style={{ fontSize: '0.85em', color: '#666' }}>{vetoUser?.name || data.vetoWinner}</div>
          </div>
        )}

        {/* Saved Player */}
        {(savedUser || data.savedPlayer) && (
          <div style={{ textAlign: 'center', flex: '0 0 120px' }}>
            <div style={{
              fontSize: '2em',
              marginBottom: '8px'
            }}>🛡️</div>
            {getFullBodyImageUrl(savedUser) ? (
              <img
                src={getFullBodyImageUrl(savedUser)}
                alt={savedUser.name}
                style={{
                  width: '100px',
                  height: '150px',
                  borderRadius: '8px',
                  objectFit: 'cover',
                  border: '3px solid #4CAF50',
                  marginBottom: '8px',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                }}
              />
            ) : (
              <div style={{
                width: '100px',
                height: '150px',
                borderRadius: '8px',
                border: '3px solid #4CAF50',
                marginBottom: '8px',
                backgroundColor: '#F8F9FA',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '3em',
                margin: '0 auto 8px auto'
              }}>🛡️</div>
            )}
            <div style={{ fontWeight: '600', fontSize: '0.9em', color: '#333', marginBottom: '4px' }}>Saved</div>
            <div style={{ fontSize: '0.85em', color: '#666' }}>{savedUser?.name || data.savedPlayer}</div>
          </div>
        )}

        {/* Replacement Nominee */}
        {(renomUser || data.replacementNominee) && (
          <div style={{ textAlign: 'center', flex: '0 0 120px' }}>
            <div style={{
              fontSize: '2em',
              marginBottom: '8px'
            }}>🔄</div>
            {getFullBodyImageUrl(renomUser) ? (
              <img
                src={getFullBodyImageUrl(renomUser)}
                alt={renomUser.name}
                style={{
                  width: '100px',
                  height: '150px',
                  borderRadius: '8px',
                  objectFit: 'cover',
                  border: '3px solid #FF9800',
                  marginBottom: '8px',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                }}
              />
            ) : (
              <div style={{
                width: '100px',
                height: '150px',
                borderRadius: '8px',
                border: '3px solid #FF9800',
                marginBottom: '8px',
                backgroundColor: '#F8F9FA',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '3em',
                margin: '0 auto 8px auto'
              }}>🔄</div>
            )}
            <div style={{ fontWeight: '600', fontSize: '0.9em', color: '#333', marginBottom: '4px' }}>Renom</div>
            <div style={{ fontSize: '0.85em', color: '#666' }}>{renomUser?.name || data.replacementNominee}</div>
          </div>
        )}

        {/* Evicted Player */}
        {(evictedUser || data.evictedPlayer) && (
          <div style={{ textAlign: 'center', flex: '0 0 120px' }}>
            <div style={{
              fontSize: '2em',
              marginBottom: '8px'
            }}>❌</div>
            {getFullBodyImageUrl(evictedUser) ? (
              <img
                src={getFullBodyImageUrl(evictedUser)}
                alt={evictedUser.name}
                style={{
                  width: '100px',
                  height: '150px',
                  borderRadius: '8px',
                  objectFit: 'cover',
                  border: '3px solid #F44336',
                  marginBottom: '8px',
                  filter: 'grayscale(100%)',
                  opacity: '0.7',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                }}
              />
            ) : (
              <div style={{
                width: '100px',
                height: '150px',
                borderRadius: '8px',
                border: '3px solid #F44336',
                marginBottom: '8px',
                backgroundColor: '#F8F9FA',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '3em',
                margin: '0 auto 8px auto',
                filter: 'grayscale(100%)',
                opacity: '0.7'
              }}>❌</div>
            )}
            <div style={{ fontWeight: '600', fontSize: '0.9em', color: '#F44336', marginBottom: '4px' }}>EVICTED</div>
            <div style={{ fontSize: '0.85em', color: '#999' }}>{evictedUser?.name || data.evictedPlayer}</div>
          </div>
        )}
      </div>
      )}

      {/* Winner and Jury Votes (for finale) */}
      {data.winner && (
        <div style={{
          backgroundColor: '#FFF9E6',
          padding: '20px',
          borderRadius: '8px',
          marginBottom: '16px',
          border: '3px solid #FFD700',
          boxShadow: '0 4px 12px rgba(255,215,0,0.3)'
        }}>
          {/* Winner Announcement */}
          <div style={{
            textAlign: 'center',
            marginBottom: '20px'
          }}>
            <div style={{
              fontSize: '2.5em',
              fontWeight: '700',
              color: '#D4AF37',
              marginBottom: '12px',
              textShadow: '2px 2px 4px rgba(0,0,0,0.1)'
            }}>
              🏆 WINNER 🏆
            </div>
            {(() => {
              const winnerUser = findUser(data.winnerId, data.winner);
              console.log('[WINNER DISPLAY] Winner data:', { winnerId: data.winnerId, winner: data.winner });
              console.log('[WINNER DISPLAY] Found winner user:', winnerUser);
              console.log('[WINNER DISPLAY] Winner image URL:', getFullBodyImageUrl(winnerUser));
              if (winnerUser) {
                console.log('[WINNER DISPLAY] Winner user data:', winnerUser.data);
              }
              return (
                <div style={{ display: 'inline-block' }}>
                  {getFullBodyImageUrl(winnerUser) ? (
                    <img
                      src={getFullBodyImageUrl(winnerUser)}
                      alt={winnerUser?.name}
                      style={{
                        width: '150px',
                        height: '225px',
                        borderRadius: '12px',
                        objectFit: 'cover',
                        border: '5px solid #FFD700',
                        marginBottom: '12px',
                        boxShadow: '0 8px 24px rgba(255,215,0,0.5)'
                      }}
                    />
                  ) : (
                    <div style={{
                      width: '150px',
                      height: '225px',
                      borderRadius: '12px',
                      border: '5px solid #FFD700',
                      marginBottom: '12px',
                      backgroundColor: '#FFF',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '4em',
                      margin: '0 auto 12px auto'
                    }}>👑</div>
                  )}
                  <div style={{
                    fontSize: '1.8em',
                    fontWeight: '700',
                    color: '#D4AF37'
                  }}>
                    {winnerUser?.name || data.winner}
                  </div>
                  {data.voteCount !== undefined && (
                    <div style={{
                      fontSize: '1.2em',
                      color: '#666',
                      marginTop: '8px'
                    }}>
                      Won with {data.voteCount} jury vote{data.voteCount !== 1 ? 's' : ''}
                    </div>
                  )}
                </div>
              );
            })()}
          </div>

          {/* Jury Votes */}
          {data.votes && data.votes.length > 0 && data.winner && (
            <div>
              <div style={{
                fontSize: '1.3em',
                fontWeight: '600',
                marginBottom: '16px',
                color: '#D4AF37',
                textAlign: 'center'
              }}>
                🗳️ Jury Votes
              </div>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
                gap: '12px'
              }}>
                {data.votes.map((vote, idx) => {
                  const jurorUser = findUser(vote.voterId, vote.voter);
                  const votedForUser = findUser(vote.votedForId, vote.votedFor);
                  const votedForWinner = vote.votedFor === data.winner || vote.votedForId === data.winnerId;

                  return (
                    <div key={idx} style={{
                      padding: '12px 16px',
                      backgroundColor: votedForWinner ? '#E8F5E9' : '#FFFFFF',
                      borderRadius: '6px',
                      fontSize: '0.95em',
                      border: `2px solid ${votedForWinner ? '#4CAF50' : '#E0E0E0'}`,
                      color: '#333'
                    }}>
                      <strong>{jurorUser?.name || vote.voter}</strong> voted for <strong>{votedForUser?.name || vote.votedFor}</strong>
                      {votedForWinner && <span style={{ marginLeft: '8px' }}>✓</span>}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Narrative Summary */}
      {showNarrative && data.narrative && (
        <div style={{
          backgroundColor: '#F8F9FA',
          padding: '12px',
          borderRadius: '6px',
          marginBottom: '12px',
          border: '1px solid #E0E0E0'
        }}>
          <div style={{
            fontSize: '1.1em',
            fontWeight: '600',
            marginBottom: '16px',
            color: '#1a1a1a'
          }}>
            📖 Week Recap
          </div>
          <div style={{
            lineHeight: '1.8',
            whiteSpace: 'pre-wrap',
            fontSize: '1.05em',
            color: '#333'
          }}>
            {data.narrative}
          </div>
        </div>
      )}

      {/* Highlights */}
      {showHighlights && data.highlights && data.highlights.length > 0 && (
        <div style={{
          backgroundColor: '#F8F9FA',
          padding: '12px',
          borderRadius: '6px',
          marginBottom: '12px',
          border: '1px solid #E0E0E0'
        }}>
          <div style={{
            fontSize: '1.1em',
            fontWeight: '600',
            marginBottom: '12px',
            color: '#1a1a1a'
          }}>
            ⭐ Key Moments
          </div>
          <ul style={{
            margin: 0,
            paddingLeft: '20px',
            lineHeight: '1.6'
          }}>
            {data.highlights.map((highlight, idx) => (
              <li key={idx} style={{ fontSize: '0.95em', color: '#333', marginBottom: '6px' }}>{highlight}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Vote Breakdown */}
      {showVotes && data.evictedPlayer && data.votes && data.votes.length > 0 && (
        <div style={{
          backgroundColor: '#F8F9FA',
          padding: '12px',
          borderRadius: '6px',
          border: '1px solid #E0E0E0'
        }}>
          <div style={{
            fontSize: '1.1em',
            fontWeight: '600',
            marginBottom: '16px',
            color: '#1a1a1a'
          }}>
            🗳️ Eviction Vote
          </div>
          <div style={{
            fontSize: '1.15em',
            marginBottom: '16px',
            fontWeight: '600',
            color: '#333',
            padding: '12px',
            backgroundColor: '#FFFFFF', textShadow: 'none',
            borderRadius: '6px',
            border: '1px solid #E0E0E0'
          }}>
            {data.evictedPlayer} was evicted by a vote of {voteCounts[data.evictedPlayer] || 0} to {data.votes.length - (voteCounts[data.evictedPlayer] || 0)}
          </div>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
            gap: '12px'
          }}>
            {data.votes.map((vote, idx) => (
              <div key={idx} style={{
                padding: '12px 16px',
                backgroundColor: '#FFFFFF', textShadow: 'none',
                borderRadius: '6px',
                fontSize: '0.95em',
                border: `2px solid ${vote.votedFor === data.evictedPlayer ? '#F44336' : '#E0E0E0'}`,
                color: '#333'
              }}>
                <strong>{vote.voter}</strong> voted to evict <strong>{vote.votedFor}</strong>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Benchmarks Section */}
      {(() => {
        console.log('[BENCHMARKS RENDER] showBenchmarks:', showBenchmarks);
        console.log('[BENCHMARKS RENDER] playerBenchmarks:', playerBenchmarks);
        return null;
      })()}
      {showBenchmarks && playerBenchmarks && (
        <div style={{
          backgroundColor: '#F8F9FA',
          padding: '12px',
          borderRadius: '8px',
          marginTop: '16px',
          border: '1px solid #E0E0E0'
        }}>
          <div style={{
            fontSize: '1.1em',
            fontWeight: '600',
            marginBottom: '12px',
            color: '#1a1a1a'
          }}>
            📊 Player Benchmarks
          </div>
          <div style={{
            display: 'flex',
            flexDirection: 'row',
            flexWrap: 'wrap',
            gap: '12px',
            alignItems: 'flex-start'
          }}>
            {users && users
              .map(user => {
                // Get benchmark data using full user name
                const benchmarkData = playerBenchmarks[user.name];

                if (!benchmarkData) {
                  console.log('[BENCHMARKS] No benchmark data for:', user.name);
                  return null;
                }

                // Extract model name for display
                const modelMatch = user.name.match(/^([^(]+)/);
                const modelName = modelMatch ? modelMatch[1].trim() : user.name;

                // Calculate score: +1 for winning, -1 for losing
                const score = benchmarkData.winning.length - benchmarkData.losing.length;

                return { user, modelName, benchmarkData, score };
              })
              .filter(item => item !== null)
              .sort((a, b) => b.score - a.score)  // Sort highest to lowest
              .map((item, idx) => {
                const { user, modelName, benchmarkData } = item;

                // Limit pills to show - rest will be indicated with +N
                // Reduce max if both winning and losing exist to prevent overflow
                const hasWinning = benchmarkData.winning.length > 0;
                const hasLosing = benchmarkData.losing.length > 0;
                const MAX_PILLS = (hasWinning && hasLosing) ? 2 : 3;

                const winningToShow = benchmarkData.winning.slice(0, MAX_PILLS);
                const winningRemaining = Math.max(0, benchmarkData.winning.length - MAX_PILLS);
                const losingToShow = benchmarkData.losing.slice(0, MAX_PILLS);
                const losingRemaining = Math.max(0, benchmarkData.losing.length - MAX_PILLS);

                return (
                  <div key={idx} style={{
                    backgroundColor: '#FFFFFF',
                    padding: '10px',
                    borderRadius: '6px',
                    border: '2px solid #E0E0E0',
                    height: '160px',
                    width: 'calc(25% - 9px)',
                    boxSizing: 'border-box',
                    display: 'flex',
                    flexDirection: 'row',
                    gap: '12px',
                    overflow: 'hidden'
                  }}>
                    {/* Left side: Player Photo and Name */}
                    <div style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      flexShrink: 0
                    }}>
                      {getFullBodyImageUrl(user) ? (
                        <img
                          src={getFullBodyImageUrl(user)}
                          alt={user.name}
                          style={{
                            width: '80px',
                            height: '120px',
                            borderRadius: '6px',
                            objectFit: 'cover',
                            objectPosition: 'center top',
                            border: '2px solid #4A90E2',
                            marginBottom: '6px',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                            flexShrink: 0
                          }}
                        />
                      ) : (
                        <div style={{
                          width: '80px',
                          height: '120px',
                          borderRadius: '6px',
                          border: '2px solid #4A90E2',
                          marginBottom: '6px',
                          backgroundColor: '#F8F9FA',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '2em',
                          flexShrink: 0
                        }}>👤</div>
                      )}
                      <div style={{
                        fontSize: '0.8em',
                        fontWeight: '600',
                        color: '#333',
                        textAlign: 'center',
                        width: '80px'
                      }}>
                        {modelName}
                      </div>
                    </div>

                    {/* Right side: Benchmarks */}
                    <div style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '8px',
                      flex: 1,
                      minWidth: 0,
                      overflow: 'hidden'
                    }}>
                      {/* Winning Benchmarks */}
                      {benchmarkData.winning.length > 0 && (
                        <div>
                          <div style={{
                            fontSize: '0.65em',
                            fontWeight: '700',
                            color: '#4CAF50',
                            marginBottom: '4px',
                            textTransform: 'uppercase'
                          }}>
                            🏆 WINNING
                          </div>
                          <div style={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: '3px'
                          }}>
                            {winningToShow.map((metric, i) => (
                              <div key={i} style={{
                                fontSize: '0.75em',
                                color: '#666',
                                padding: '3px 6px',
                                backgroundColor: '#E8F5E9',
                                borderRadius: '3px',
                                border: '1px solid #C8E6C9',
                                whiteSpace: 'nowrap'
                              }}>
                                {metric}
                              </div>
                            ))}
                            {winningRemaining > 0 && (
                              <div style={{
                                fontSize: '0.75em',
                                color: '#4CAF50',
                                padding: '3px 6px',
                                backgroundColor: '#E8F5E9',
                                borderRadius: '3px',
                                border: '1px solid #C8E6C9',
                                fontWeight: '700',
                                whiteSpace: 'nowrap'
                              }}>
                                +{winningRemaining}
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Losing Benchmarks */}
                      {benchmarkData.losing.length > 0 && (
                        <div>
                          <div style={{
                            fontSize: '0.65em',
                            fontWeight: '800',
                            color: '#D32F2F',
                            marginBottom: '4px',
                            textTransform: 'uppercase'
                          }}>
                            📉 LOSING
                          </div>
                          <div style={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: '3px'
                          }}>
                            {losingToShow.map((metric, i) => (
                              <div key={i} style={{
                                fontSize: '0.75em',
                                color: '#666',
                                padding: '3px 6px',
                                backgroundColor: '#FFEBEE',
                                borderRadius: '3px',
                                border: '1px solid #FFCDD2',
                                whiteSpace: 'nowrap'
                              }}>
                                {metric}
                              </div>
                            ))}
                            {losingRemaining > 0 && (
                              <div style={{
                                fontSize: '0.75em',
                                color: '#D32F2F',
                                padding: '3px 6px',
                                backgroundColor: '#FFEBEE', textShadow: 'none',
                                borderRadius: '3px',
                                border: '1px solid #FFCDD2',
                                fontWeight: '800',
                                whiteSpace: 'nowrap'
                              }}>
                                +{losingRemaining}
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      {/* No benchmarks message */}
                      {benchmarkData.winning.length === 0 && benchmarkData.losing.length === 0 && (
                        <div style={{
                          fontSize: '0.6em',
                          color: '#999',
                          fontStyle: 'italic'
                        }}>
                          No standout benchmarks
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
          </div>
        </div>
      )}
    </div>
  );
};

// Spectating chat input for eliminated players and spectators
const SpectatingChatInput = ({ onSubmit, senderName, isEliminated, theme, embedded, isMobile, mentionNames = [] }) => {
  const [message, setMessage] = React.useState('');
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [error, setError] = React.useState(null);
  const THEME = theme;
  // @-mention autocomplete (Nina 07-25) — same interaction contract as the
  // locals composer's @/# twins (HumanInputForm), self-contained here because
  // this input is a bare textarea. mention = { start, query } while an @token
  // sits under the caret.
  const [mention, setMention] = React.useState(null);
  const [mentionIdx, setMentionIdx] = React.useState(0);
  const mentionMatches = React.useMemo(() => {
    if (!mention) return [];
    const q = mention.query.toLowerCase();
    return mentionNames.filter(n => n && n.toLowerCase().startsWith(q)).slice(0, 6);
  }, [mention, mentionNames]);
  const detectMention = (value, caret) => {
    for (let i = caret - 1; i >= 0; i--) {
      const ch = value[i];
      if (ch === '@') {
        if (i === 0 || /\s/.test(value[i - 1])) {
          const q = value.slice(i + 1, caret);
          if (!/[\s@]/.test(q)) return { start: i, query: q };
        }
        return null;
      }
      if (/\s/.test(ch)) return null;
    }
    return null;
  };
  const insertMention = (name) => {
    if (!mention) return;
    const tokenEnd = mention.start + 1 + mention.query.length;
    setMessage(message.slice(0, mention.start) + '@' + name + ' ' + message.slice(tokenEnd));
    setMention(null);
    setMentionIdx(0);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!message.trim()) return;

    setIsSubmitting(true);
    setError(null);

    // Save message and clear input immediately for instant feedback
    const messageToSend = message.trim();
    setMessage('');

    try {
      await onSubmit(messageToSend);
    } catch (err) {
      setError(err.message || 'Failed to send message');
      // Restore message on error so user can retry
      setMessage(messageToSend);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} style={embedded ? {} : {
      backgroundColor: `${THEME.spectating || '#78909C'}0d`,
      borderRadius: '8px',
      padding: '12px',
      border: `1px dashed ${THEME.spectating || '#78909C'}66`,
      opacity: 0.85
    }}>
      {!embedded && (
      <div style={{
        marginBottom: '8px',
        paddingBottom: '6px',
        borderBottom: `1px solid ${THEME.spectating || '#78909C'}33`
      }}>
        <h4 style={{
          margin: '0',
          color: THEME.spectating || '#78909C',
          fontSize: '0.9em',
          fontWeight: 'normal',
          fontStyle: 'italic'
        }}>
          {isEliminated ? `👻 Ghost Chat (${senderName})` : '👻 Ghost Chat'} — only visible to eliminated players &amp; spectators
        </h4>
      </div>
      )}
      {error && (
        <div style={{
          backgroundColor: `${THEME.evil || '#F44336'}1a`,
          color: THEME.evil || '#F44336',
          padding: '6px 8px',
          borderRadius: '4px',
          marginBottom: '8px',
          fontSize: '0.85em'
        }}>
          {error}
        </div>
      )}
      <div style={{ display: 'flex', gap: '8px', position: 'relative' }}>
        {mention && mentionMatches.length > 0 && (
          <div style={{
            position: 'absolute', bottom: '100%', left: 0, marginBottom: 6, zIndex: 20,
            background: THEME.bgDark || '#141018',
            border: `1px solid ${THEME.spectating || '#78909C'}66`,
            borderRadius: 8, overflow: 'hidden', minWidth: 180, maxWidth: '92%',
            boxShadow: '0 6px 18px rgba(0,0,0,0.5)',
          }}>
            {mentionMatches.map((n, i) => (
              <div
                key={n}
                onMouseDown={(e) => { e.preventDefault(); insertMention(n); }}
                onMouseEnter={() => setMentionIdx(i)}
                style={{
                  padding: '7px 12px', cursor: 'pointer', fontSize: '0.85em', color: '#fff',
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  background: i === mentionIdx ? `${THEME.spectating || '#78909C'}33` : 'transparent',
                }}
              >
                @{n}
              </div>
            ))}
          </div>
        )}
        <textarea
          value={message}
          onChange={(e) => {
            setMessage(e.target.value);
            const m = detectMention(e.target.value, e.target.selectionStart ?? e.target.value.length);
            setMention(m);
            if (!m) setMentionIdx(0);
          }}
          onBlur={() => setTimeout(() => setMention(null), 150)}
          onKeyDown={(e) => {
            if (mention && mentionMatches.length > 0) {
              if (e.key === 'ArrowDown') { e.preventDefault(); setMentionIdx(i => (i + 1) % mentionMatches.length); return; }
              if (e.key === 'ArrowUp') { e.preventDefault(); setMentionIdx(i => (i - 1 + mentionMatches.length) % mentionMatches.length); return; }
              if (e.key === 'Enter' || e.key === 'Tab') { e.preventDefault(); insertMention(mentionMatches[mentionIdx]); return; }
              if (e.key === 'Escape') { setMention(null); return; }
            }
            if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSubmit(e); }
          }}
          // 📱 08-01: the 07-16 scrollIntoView re-pin is GONE (same removal as
          // HumanInputForm). Every mobile mount of this input now lives in a
          // lifted fixed container: the thumb dock (ghost lane) has the
          // visualViewport translate, and the ghost sheet got its own lift —
          // the pin only fought them and scrolled the document.
          placeholder={isEliminated ? 'Send a message from beyond...' : 'Send a spectator message...'}
          disabled={isSubmitting}
          maxLength={LIMITS.SPECTATOR_MESSAGE_MAX}
          style={{
            flex: 1,
            minHeight: '40px',
            maxHeight: '80px',
            padding: '8px 10px',
            borderRadius: '4px',
            border: `1px solid ${THEME.borderDark || '#444'}`,
            fontSize: isMobile ? '1em' : '0.9em',
            fontFamily: 'inherit',
            fontStyle: 'italic',
            resize: 'none',
            boxSizing: 'border-box',
            backgroundColor: THEME.bgDark || '#1A1A1A',
            color: THEME.textDark || '#E0E0E0'
          }}
        />
        <button
          type="submit"
          disabled={isSubmitting || !message.trim()}
          style={{
            padding: '8px 16px',
            borderRadius: '4px',
            border: 'none',
            backgroundColor: THEME.spectating || '#78909C',
            color: '#fff',
            fontSize: '0.85em',
            cursor: isSubmitting || !message.trim() ? 'default' : 'pointer',
            opacity: isSubmitting || !message.trim() ? 0.5 : 1,
            alignSelf: 'flex-end'
          }}
        >
          {isSubmitting ? '...' : '👻 Send'}
        </button>
      </div>
      <div style={{
        textAlign: 'right',
        fontSize: '0.75em',
        marginTop: '2px',
        color: message.length > LIMITS.SPECTATOR_MESSAGE_MAX * 0.9 ? (THEME.evil || '#F44336') : (THEME.textMuted || '#999'),
        fontWeight: message.length > LIMITS.SPECTATOR_MESSAGE_MAX * 0.9 ? 700 : 400
      }}>
        {message.length}/{LIMITS.SPECTATOR_MESSAGE_MAX}
      </div>
    </form>
  );
};

// Spectator Chat Panel - dedicated sidebar panel for spectating messages
const SpectatorChatPanel = ({ messages, onSubmit, senderName, isEliminated, theme, users, readOnly, isMobile, startCollapsed, forceExpand, label, seamlessTop, onLoadOlder, isLoadingOlder, hasMore, onExpandChange, isInitialLoading, onScrolledUpChange, onWatchLive, fillContainer, watchPartyActive, seatsAsCrowd }) => {
  const THEME = theme;
  const [isExpanded, setIsExpanded] = React.useState(!startCollapsed);
  const messagesEndRef = React.useRef(null);
  const scrollContainerRef = React.useRef(null);
  const isUserScrolledUpRef = React.useRef(false);
  const [hasUnread, setHasUnread] = React.useState(false);
  const seenCountRef = React.useRef(messages.length);
  // 👻 A crowd line's 🚩 hides until hover; a tap opens it (utils/ghostLook).
  const [flagOpenId, setFlagOpenId] = React.useState(null);
  React.useEffect(() => { ensureGhostFlagCss(); }, []);
  const adminView = isAdminBrowser();

  // Notify parent of expand/collapse changes (used to gate ghost-chat notification sound)
  const onExpandChangeRef = React.useRef(onExpandChange);
  React.useEffect(() => { onExpandChangeRef.current = onExpandChange; });
  React.useEffect(() => { onExpandChangeRef.current?.(isExpanded); }, [isExpanded]);

  // Notify parent of scroll position changes (used to gate ghost-chat notification sound)
  const onScrolledUpChangeRef = React.useRef(onScrolledUpChange);
  React.useEffect(() => { onScrolledUpChangeRef.current = onScrolledUpChange; });

  // Mount grace: keep the skeleton visible for a short window after the panel
  // first appears. The page-level isInitialLoading flag can flip to false
  // before this panel even mounts (e.g. if the early getGameStatus fetch
  // resolves during the page's loading state), which would otherwise skip
  // straight to the "Loading ghost history..." empty state.
  const [hasMountGracePassed, setHasMountGracePassed] = React.useState(false);
  React.useEffect(() => {
    const t = setTimeout(() => setHasMountGracePassed(true), 800);
    return () => clearTimeout(t);
  }, []);
  const showSkeleton = messages.length === 0 && (isInitialLoading || !hasMountGracePassed);
  const showEmptyState = messages.length === 0 && !showSkeleton;

  // Track unread messages when collapsed
  React.useEffect(() => {
    if (isExpanded) {
      setHasUnread(false);
      seenCountRef.current = messages.length;
    } else if (messages.length > seenCountRef.current) {
      setHasUnread(true);
    }
  }, [messages.length, isExpanded]);

  // Collapse/expand based on parent signals (tutorial state, game mode). Tracks
  // startCollapsed both ways: chatGameStatus is null at mount (reads as collapsed),
  // so a competitive-mobile panel must re-expand once the status resolves.
  React.useEffect(() => {
    if (forceExpand) {
      setIsExpanded(true);
    } else {
      setIsExpanded(!startCollapsed);
    }
  }, [startCollapsed, forceExpand]);

  // Track scroll position to detect manual scroll-up and trigger lazy loading.
  // programmaticScrollRef: our own smooth scroll fires these same events while it
  // passes through the top of the container — without the flag it (a) marked the
  // panel "user scrolled up" (killing autoscroll until a manual round-trip to the
  // bottom) and (b) tripped the scrollTop<80 lazy-loader on mount (Nina 07-25:
  // "ghost chat should be autoscrolling down"). A real wheel/touch clears the
  // flag, so user intent always wins over an in-flight snap.
  const programmaticScrollRef = React.useRef(false);
  const didInitialSnapRef = React.useRef(false);
  const handleScroll = React.useCallback(() => {
    const el = scrollContainerRef.current;
    if (!el) return;
    const atBottom = Math.abs(el.scrollHeight - el.scrollTop - el.clientHeight) < 30;
    if (programmaticScrollRef.current) {
      if (atBottom) programmaticScrollRef.current = false;
      return;
    }
    const wasScrolledUp = isUserScrolledUpRef.current;
    const nowScrolledUp = !atBottom;
    isUserScrolledUpRef.current = nowScrolledUp;
    if (wasScrolledUp !== nowScrolledUp) onScrolledUpChangeRef.current?.(nowScrolledUp);
    // Lazy load older messages when scrolled near top
    if (el.scrollTop < 80 && onLoadOlder && hasMore && !isLoadingOlder) {
      onLoadOlder();
    }
  }, [onLoadOlder, hasMore, isLoadingOlder]);
  const markUserScrollIntent = React.useCallback(() => { programmaticScrollRef.current = false; }, []);

  // Auto-scroll to bottom on new messages (only if user hasn't scrolled up)
  // Use container-scoped scroll instead of scrollIntoView to avoid yanking the
  // whole page to the ghost chat on mobile (where it lives below the fold).
  // First paint with history jumps instantly — a smooth crawl from the top was
  // both slow and the thing that tripped the lazy-loader.
  React.useEffect(() => {
    if (isExpanded && scrollContainerRef.current && !isUserScrolledUpRef.current) {
      const el = scrollContainerRef.current;
      programmaticScrollRef.current = true;
      if (!didInitialSnapRef.current && messages.length > 0) {
        didInitialSnapRef.current = true;
        el.scrollTop = el.scrollHeight;
        programmaticScrollRef.current = false;
      } else {
        el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
      }
    }
  }, [messages, isExpanded]);

  // Use theme-aware colors — spectatingColor for accents, theme colors for text/bg
  const accentColor = THEME.spectating || THEME.accent || '#78909C';

  // @-mention suggestions (Nina 07-25): everyone who has SPOKEN in ghost chat
  // plus the space cast — no attendee roster exists client-side, so recent
  // senders are the honest source.
  const mentionNames = React.useMemo(() => {
    const set = new Set();
    // @watchparty leads the list while a party is on (Nina 07-29: the overlay
    // command was undiscoverable — typing @w now surfaces it)
    if (watchPartyActive) set.add('watchparty');
    (messages || []).forEach(m => { if (m?.sender) set.add(m.sender); });
    (users || []).forEach(u => { if (u?.name) set.add(u.name); });
    set.delete(senderName);
    return [...set];
  }, [messages, users, senderName, watchPartyActive]);

  // Find user profile image by sender name
  const getUserForSender = (senderName) => users?.find(u => u.name === senderName);

  // Per-player color from name hash
  const getPlayerColor = (name) => {
    if (!name) return '#808080';
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    const hue = Math.abs(hash) % 360;
    return `hsl(${hue}, 65%, 55%)`;
  };

  const formatTime = (timestamp) => formatActionTime(timestamp);

  return (
    <div style={{
      backgroundColor: THEME.bgMedium || '#2C1810',
      borderRadius: seamlessTop ? '0 0 8px 8px' : '8px',
      border: `1px dashed ${accentColor}`,
      borderTop: seamlessTop ? 'none' : undefined,
      boxShadow: seamlessTop ? 'none' : `0 0 12px ${accentColor}15`,
      overflow: 'hidden',
      // fillContainer (ghost sheet, 07-17): the sheet gives the panel a fixed
      // box, so it must flex to fill and scroll inside — mobile's natural
      // height would overflow the sheet and push the input off-screen.
      flex: isExpanded ? ((isMobile && !fillContainer) ? 'none' : 1) : 'none',
      minHeight: isExpanded ? (fillContainer ? 0 : (isMobile ? '150px' : '350px')) : 'auto',
      display: 'flex',
      flexDirection: 'column'
    }}>
      {/* Collapsible header */}
      <div
        onClick={() => setIsExpanded(!isExpanded)}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '10px 12px',
          cursor: 'pointer',
          background: `linear-gradient(135deg, ${accentColor}18, ${THEME.bgLight || '#3D2418'})`,
          borderBottom: isExpanded ? `1px solid ${accentColor}30` : 'none',
          userSelect: 'none',
          flexShrink: 0
        }}
      >
        <h4 style={{
          margin: 0,
          color: accentColor,
          fontSize: '0.9em',
          fontWeight: 600
        }}>
          {label || '👻 Ghost Chat'} {messages.length > 0 && <span style={{ color: THEME.textMuted || '#999' }}>({messages.length})</span>}
          {hasUnread && !isExpanded && (
            <span style={{
              display: 'inline-block', width: 8, height: 8, borderRadius: '50%',
              backgroundColor: '#ef4444', marginLeft: 6, verticalAlign: 'middle',
              boxShadow: '0 0 6px rgba(239,68,68,0.6)',
              animation: 'liveBadgePulse 2s ease-in-out infinite',
            }} />
          )}
        </h4>
        <span style={{ color: THEME.textOnPanel || '#fff', fontSize: '1.1em', fontWeight: 700 }}>
          {isExpanded ? '▾' : '▸'}
        </span>
      </div>

      {isExpanded && (
        <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          {/* Message list */}
          {/* Cycle 5f: data-chat-scroll="ghost" — explorer-bot integration hook.
              Companion to data-chat-scroll="main" on the main chat container.
              No visual / functional effect. */}
          <div style={{
            flex: 1,
            minHeight: 0,
            overflowY: 'auto',
            padding: messages.length > 0 ? '8px 12px' : '0',
            backgroundColor: THEME.bgDark || '#1A0F0A'
          }}
          ref={scrollContainerRef}
          data-chat-scroll="ghost"
          onScroll={handleScroll}
          onWheel={markUserScrollIntent}
          onTouchStart={markUserScrollIntent}
          >
            {/* Loading older spectator messages indicator */}
            {isLoadingOlder && (
              <div style={{ textAlign: 'center', padding: '8px', color: THEME.textMuted || '#999', fontSize: '0.8em' }}>
                Loading older messages...
              </div>
            )}
            {hasMore === false && messages.length > 0 && (
              <div style={{ textAlign: 'center', padding: '6px', color: THEME.textMuted || '#666', fontSize: '0.75em', fontStyle: 'italic' }}>
                Beginning of ghost chat
              </div>
            )}
            {showSkeleton && (
              <>
                <style>{`@keyframes spectatorSkeletonPulse { 0%, 100% { opacity: 0.35; } 50% { opacity: 0.7; } }`}</style>
                <div style={{ padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {[80, 60, 72].map((widthPct, i) => (
                    <div key={i} style={{
                      height: '14px',
                      width: `${widthPct}%`,
                      borderRadius: '6px',
                      background: THEME.textMuted ? `${THEME.textMuted}30` : '#bbbbbb40',
                      animation: `spectatorSkeletonPulse 1.4s ease-in-out ${i * 0.15}s infinite`,
                    }} />
                  ))}
                </div>
              </>
            )}
            {showEmptyState && (
              <div style={{
                padding: '12px',
                textAlign: 'center',
                color: THEME.textMuted || '#999',
                fontSize: '0.85em',
                fontStyle: 'italic'
              }}>
                Loading ghost history...
              </div>
            )}
            {messages.map((msg) => {
              const senderUser = getUserForSender(msg.sender);
              // AI ghost messages: sender_id exists, is not a spectator_, not system
              const isAiMessage = msg.sender_id && !msg.is_spectator && msg.sender_id !== 'system' && !String(msg.sender_id).startsWith('spectator_');
              // 👻 09-29 (Nina): a person's line wears the crowd look and every
              // crowd line's 🚩 shows on hover or a tap; an /admin-key browser
              // keeps the old tells (people's lines plain, their 🚩 always on).
              // Non-competitive rooms: a seated person's line too (`seatsAsCrowd`).
              const look = ghostLineLook(msg, { admin: adminView, seatsToo: !!seatsAsCrowd });
              const crowdLook = look.crowd;
              const hoverFlag = look.flag === 'hover';
              const isMod = msg.is_moderator;
              const isAnnouncement = msg.is_announcement;
              const isSystemNote = msg.is_reaction_note || msg.sender_id === 'system';
              const isHighlighted = isAiMessage || crowdLook || isMod;
              // Go-live mod messages are a tap target: click puts that streamer
              // on the live stage. New messages carry livestream_identity;
              // messages minted before that field existed are text-matched.
              const liveInvite = !!(isMod && onWatchLive && (msg.livestream_identity || /(?:just went|is) LIVE/.test(msg.message || '')));
              const liveInviteName = msg.livestream_streamer
                || ((msg.message || '').match(/🔴\s*(.+?)\s+(?:just went LIVE|is LIVE)/)?.[1] ?? null);

              // Announcement-style welcome messages get a distinct centered layout
              if (isAnnouncement && isMod) {
                return (
                  <div key={msg.id} style={{
                    marginBottom: '12px',
                    padding: '10px 14px',
                    borderRadius: '10px',
                    background: 'linear-gradient(135deg, rgba(218,165,32,0.12), rgba(184,134,11,0.06))',
                    border: '1px dashed rgba(218,165,32,0.35)',
                    textAlign: 'center',
                  }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '6px',
                      marginBottom: '4px',
                    }}>
                      <span style={{ fontSize: '0.9em' }}>{msg.moderator_emoji || '🎙️'}</span>
                      <span style={{
                        color: '#DAA520',
                        fontSize: '0.75em',
                        fontWeight: 800,
                        letterSpacing: '0.5px',
                        textTransform: 'uppercase',
                      }}>
                        {msg.sender}
                      </span>
                      <span style={{ fontSize: '0.9em' }}>{msg.moderator_emoji || '🎙️'}</span>
                    </div>
                    <div style={{
                      color: THEME.textOnPanel || '#fff',
                      fontSize: '0.88em',
                      fontWeight: 500,
                      lineHeight: 1.4,
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                    }}>
                      <SourcePill data={msg} />
                      {msg.message}
                    </div>
                    <div title={msg.timestamp ? new Date(msg.timestamp).toLocaleString() : undefined} style={{
                      color: THEME.textMuted || '#999',
                      fontSize: '0.65em',
                      marginTop: '4px',
                    }}>
                      {formatTime(msg.timestamp)}
                    </div>
                  </div>
                );
              }

              return (
                <div key={msg.id} data-ghost-row={hoverFlag ? '' : undefined} data-ghost-open={hoverFlag && flagOpenId === msg.id ? '' : undefined}
                  onClick={liveInvite ? () => onWatchLive({ identity: msg.livestream_identity || null, name: liveInviteName })
                    : (hoverFlag ? () => setFlagOpenId(id => (id === msg.id ? null : msg.id)) : undefined)} style={{
                  marginBottom: isMod ? '10px' : '8px',
                  padding: isSystemNote ? '3px 8px' : isMod ? '8px 10px' : '6px 8px',
                  borderRadius: isMod ? '8px' : '6px',
                  cursor: liveInvite ? 'pointer' : undefined,
                  background: isMod
                    ? `linear-gradient(135deg, rgba(218,165,32,0.25), rgba(184,134,11,0.12))`
                    : crowdLook
                    ? `${(THEME.accent || '#7C4DFF')}15`
                    : isAiMessage ? (THEME.bgLight || '#3D2418') : (THEME.bgMedium || '#2C1810'),
                  borderTop: isMod ? '1px solid rgba(218,165,32,0.4)' : undefined,
                  borderRight: isMod ? '1px solid rgba(218,165,32,0.4)' : undefined,
                  borderBottom: isMod ? '1px solid rgba(218,165,32,0.4)' : undefined,
                  borderLeft: isMod
                    ? '3px solid #DAA520'
                    : crowdLook
                    ? `3px solid ${THEME.accent || '#7C4DFF'}`
                    : isAiMessage ? `3px solid ${getPlayerColor(msg.sender)}` : 'none',
                  boxShadow: isMod ? '0 0 12px rgba(218,165,32,0.2)' : 'none',
                }}>
                  {isSystemNote ? (
                    <div style={{
                      color: THEME.textMuted || '#999',
                      fontSize: '0.75em',
                      fontStyle: 'italic'
                    }}>
                      {msg.message}
                    </div>
                  ) : (
                    <>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        marginBottom: '3px'
                      }}>
                        {isMod ? (
                          <div style={{
                            width: '26px',
                            height: '26px',
                            borderRadius: '50%',
                            background: 'linear-gradient(135deg, #DAA520, #B8860B)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '0.85em',
                            flexShrink: 0,
                            boxShadow: '0 0 6px rgba(218,165,32,0.5)',
                          }}>
                            {msg.moderator_emoji || '🎙️'}
                          </div>
                        ) : crowdLook ? (
                          <div style={{
                            width: '22px',
                            height: '22px',
                            borderRadius: '50%',
                            background: `${(THEME.accent || '#7C4DFF')}30`,
                            border: `1.5px solid ${THEME.accent || '#7C4DFF'}`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '0.7em',
                            flexShrink: 0
                          }}>
                            {'👻'}
                          </div>
                        ) : senderUser?.profile_image_url ? (
                          <img
                            src={senderUser.profile_image_url}
                            alt=""
                            style={{
                              width: isHighlighted ? '22px' : '18px',
                              height: isHighlighted ? '22px' : '18px',
                              borderRadius: '50%',
                              objectFit: 'cover',
                              border: `1.5px solid ${getPlayerColor(msg.sender)}`
                            }}
                          />
                        ) : (
                          <div style={{
                            width: isHighlighted ? '22px' : '18px',
                            height: isHighlighted ? '22px' : '18px',
                            borderRadius: '50%',
                            background: `${getPlayerColor(msg.sender)}25`,
                            border: `1.5px solid ${getPlayerColor(msg.sender)}`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '0.6em',
                            fontWeight: 800,
                            color: getPlayerColor(msg.sender),
                            flexShrink: 0
                          }}>
                            {msg.sender?.charAt(0)?.toUpperCase()}
                          </div>
                        )}
                        <span style={{
                          color: isMod
                            ? '#DAA520'
                            : crowdLook
                            ? (THEME.accent || '#7C4DFF')
                            : isAiMessage ? getPlayerColor(msg.sender) : (THEME.textMuted || '#999'),
                          fontSize: isHighlighted ? '0.85em' : '0.8em',
                          fontWeight: isHighlighted ? 700 : 600
                        }}>
                          {msg.sender}
                        </span>
                        {isMod && (
                          <span style={{
                            padding: '1px 5px',
                            borderRadius: '3px',
                            backgroundColor: 'rgba(218,165,32,0.25)',
                            color: '#DAA520',
                            fontSize: '0.6em',
                            fontWeight: 800,
                            letterSpacing: '0.5px',
                            textTransform: 'uppercase',
                          }}>
                            MOD
                          </span>
                        )}
                        <span title={msg.timestamp ? new Date(msg.timestamp).toLocaleString() : undefined} style={{
                          color: THEME.textMuted || '#999',
                          fontSize: '0.7em',
                          marginLeft: 'auto'
                        }}>
                          {formatTime(msg.timestamp)}
                        </span>
                        {/* 🛡️ Report (MODERATION P1.1) lives under the line's ⋯ (GhostLineMenu;
                            Nina 09-29: the 🚩 was too front and center). An admin sees the ⋯ on
                            people's lines only; a visitor on every crowd line, on hover or a
                            tap, so it gives no one away */}
                        {look.flag && (
                          <span data-ghost-flag={hoverFlag ? '' : undefined} onClick={(e) => e.stopPropagation()}>
                            <GhostLineMenu targetId={msg.id} targetPlayerId={msg.sender_id || null} onOpenChange={(o) => setFlagOpenId(o ? msg.id : null)} />
                          </span>
                        )}
                      </div>
                      {msg.message && (
                        <div style={{
                          color: isMod ? (THEME.textOnPanel || '#fff') : (THEME.textDark || '#E0E0E0'),
                          fontSize: isMod ? '0.88em' : isHighlighted ? '0.9em' : '0.85em',
                          fontStyle: isMod ? 'normal' : 'italic',
                          fontWeight: isMod ? 600 : 'normal',
                          opacity: isHighlighted ? 1 : 0.75,
                          lineHeight: 1.4,
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word'
                        }}>
                          {msg.message}
                        </div>
                      )}
                      {liveInvite && (
                        <div style={{
                          marginTop: '6px',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '4px',
                          padding: '3px 10px',
                          borderRadius: 999,
                          border: '1px solid rgba(239,68,68,0.5)',
                          backgroundColor: 'rgba(239,68,68,0.15)',
                          color: '#ef4444',
                          fontSize: '0.65em',
                          fontWeight: 800,
                          letterSpacing: '0.5px',
                          textTransform: 'uppercase',
                          animation: 'liveBadgePulse 2s ease-in-out infinite',
                        }}>
                          ▶ Tap to watch
                        </div>
                      )}
                      {msg.gif_url && (
                        <img
                          src={msg.gif_url}
                          alt={msg.gif_title || 'GIF'}
                          style={{
                            maxWidth: '180px',
                            maxHeight: '140px',
                            borderRadius: '6px',
                            marginTop: msg.message ? '4px' : 0,
                            display: 'block'
                          }}
                        />
                      )}
                    </>
                  )}
                </div>
              );
            })}
            <div ref={messagesEndRef} />
          </div>

          {/* Embedded SpectatingChatInput - hidden for non-eliminated players (read-only mode) */}
          {!readOnly && (
            <div style={{ padding: '8px 12px 12px', backgroundColor: THEME.bgMedium || '#2C1810', flexShrink: 0 }}>
              <SpectatingChatInput
                onSubmit={onSubmit}
                senderName={senderName}
                isEliminated={isEliminated}
                theme={THEME}
                embedded
                isMobile={isMobile}
                mentionNames={mentionNames}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Slash-command event card — the attributed, theatrical announcement a
// /command drops into the feed the instant it's issued (non-competitive
// "direct the space" feature). Locals never see these: AI prompt builders
// exclude the space_command type entirely.
// 🥚 One confetti burst per egg card, ever (module-level dedupe survives
// remounts); plain DOM so no state churn. Only FRESH cards pop (history
// scrollback stays calm).
const eggConfettiFired = new Set();
const fireEggConfetti = (emoji) => {
  try {
    for (let i = 0; i < 16; i++) {
      const s = document.createElement('span');
      s.textContent = i % 3 === 0 ? '✨' : (emoji || '🥚');
      s.style.cssText = `position:fixed;left:${8 + Math.random() * 84}vw;top:${30 + Math.random() * 50}vh;font-size:${16 + Math.random() * 16}px;z-index:9999;pointer-events:none;transition:transform 1.9s ease-out, opacity 1.9s ease-out;opacity:1;`;
      document.body.appendChild(s);
      requestAnimationFrame(() => {
        s.style.transform = `translateY(-${120 + Math.random() * 160}px) rotate(${Math.random() * 340 - 170}deg)`;
        s.style.opacity = '0';
      });
      setTimeout(() => s.remove(), 2200);
    }
  } catch (e) { /* confetti is garnish */ }
};

// 🪶 08-31 restyle helper (shared shape with GameAnnouncementAction's copy):
// strip a leading emoji run at RENDER — backend rows keep their emojis.
const stripCmdLeadEmoji = (s) => String(s || '')
  .replace(/^(?:[\u{1F000}-\u{1FFFF}\u{2600}-\u{27BF}\u{FE00}-\u{FE0F}\u{1F900}-\u{1F9FF}\u{200D}]|\s)+/u, '').trim();

// 🌕🚪 09-26 (Nina: "the event card has a go button that takes them to the roof
// view"): the open rooms, for the Go on an event's START card. GameStepPage
// writes it DURING its render (a card that arrives in that pass reads the fresh
// list; a portedBus-style effect would be one render late, and the memoized
// card would never draw its button). null outside the house / without the
// camera = no button anywhere else (byte-identical feed).
const eventGoBus = { rooms: null };

const SpaceCommandAction = ({ action, THEME, portedInteract = false, quietAmbience = false }) => {
  const d = action.data || {};
  useEffect(() => {
    if (action.isOptimistic) return;
    const age = Date.now() - new Date(action.created_at || Date.now()).getTime();
    // 🎚️ HEM fx one-shot (POLL_ENACTMENT §6.1) — same fresh-card rail
    if (d.fx?.kind && !hemFxFired.has(action.id)) {
      hemFxFired.add(action.id);
      if (age <= 15000) fireHemFx(d.fx.kind);
    }
    // 🎭 Scene opening sweep — add-then-bail, exactly like the rails above: if
    // the id were left unmarked on the age bail, a scroll-back remount inside
    // the 15s window would replay the sweep.
    if ((d.command === 'scene' || (d.command === 'house_event' && d.is_event_start))
      && d.scene_look?.atmosphere && !sceneSweepFired.has(action.id)) {
      sceneSweepFired.add(action.id);
      if (age <= 15000) fireSceneSweep(d.scene_look.atmosphere);
    }
    if (!d.egg || eggConfettiFired.has(action.id)) return;
    if (age > 15000) { eggConfettiFired.add(action.id); return; } // old card — no pop
    eggConfettiFired.add(action.id);
    fireEggConfetti(d.egg_emoji);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [action.id]);
  // 🪟 W3 window rows (spec §2, 08-28): resident-authored titled text
  // card — renders as a real window, not the gold command card. Interactivity
  // (close = D5 poke, add-a-line = idea 6) reads the portedBus, so no new
  // props thread through the memoized feed. First line of d.text = title.
  if (d.command === 'window') {
    // 🪟🏮 One home: on ported desktop the Hearthside Rail owns the windows
    // stack, so the inline feed copy suppresses (else two close/ack surfaces
    // for the same window). Mobile has no rail → windows stay inline here.
    if (portedBus.suppressInlineWindows) return null;
    const raw = String(d.text || d.card_message || '').trim();
    let wTitle = 'a little window';
    let wBody = raw;
    const nl = raw.indexOf('\n');
    const colon = raw.indexOf(':');
    if (nl > 0 && nl <= 80) { wTitle = raw.slice(0, nl).trim(); wBody = raw.slice(nl + 1).trim(); }
    else if (colon > 0 && colon <= 60) { wTitle = raw.slice(0, colon).trim(); wBody = raw.slice(colon + 1).trim(); }
    return (
      <div style={{ margin: '10px 0' }}>
        <PortedWindowCard
          gameId={portedBus.gameId}
          actionId={action.id}
          title={wTitle}
          body={wBody || raw}
          adds={(portedBus.adds || {})[action.id] || []}
          canInteract={portedInteract}
          onPoke={(kind, extra) => portedBus.poke && portedBus.poke(action.id, kind, { card_label: wTitle, ...(extra || {}) })}
        />
      </div>
    );
  }

  // 🕯️ 08-31 (Nina: the announcement look changes in EVERY non-comp space,
  // and the /aux card is part of that look): music/aux event cards are
  // ambience — in non-comp they render as the same quiet stage direction the
  // TV drift got, not a glowing command card. Poll-enacted skips, media
  // cards, and scenes keep the card (they are content, not atmosphere).
  if (quietAmbience && d.command === 'jukebox' && !d.via_poll && !d.url && d.media_status !== 'pending') {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, margin: '14px 6px', padding: '0 8px' }}>
        <div style={{ flex: 1, minWidth: 8, height: 1, background: `${THEME.borderGold || THEME.accent}2e` }} />
        <span style={{ fontStyle: 'italic', fontSize: '0.86em', color: THEME.accentOnPanel || THEME.accent, opacity: 0.85, textAlign: 'center', minWidth: 0 }}>
          {stripCmdLeadEmoji(d.card_message || d.message)}
          <span style={{ fontSize: '0.72em', fontStyle: 'normal', color: THEME.textMuted, marginLeft: 8, whiteSpace: 'nowrap' }}>{formatActionTime(action.created_at)}</span>
        </span>
        <div style={{ flex: 1, minWidth: 8, height: 1, background: `${THEME.borderGold || THEME.accent}2e` }} />
      </div>
    );
  }

  return (
    <div style={{
      margin: '10px 0',
      padding: '12px 16px',
      borderRadius: '12px',
      border: '1px solid rgba(255, 200, 100, 0.4)',
      background: 'linear-gradient(135deg, rgba(255,200,100,0.12), rgba(255,120,180,0.08))',
      boxShadow: '0 0 12px rgba(255,200,100,0.12)',
      opacity: action.isOptimistic ? 0.75 : 1,
      transition: 'opacity 0.3s',
    }}
      /* 🪟 W1 poke target (IMAGINE_REACTIVE_WINDOWS_SPEC §2): inert data
         attrs — the ported-only delegated listener (see pokes block by
         isPortedSpace) reads them; nothing changes in non-ported spaces. */
      data-poke-card={action.id}
      data-poke-label={String(d.card_message || d.message || '').slice(0, 60)}
    >
      {/* 🗳️ Vote receipt (HEM, POLL_ENACTMENT_SPEC): the room voted, the host
          made it happen. Object shape only — legacy rows carried a bare id. */}
      {d.via_poll && d.via_poll.label && (
        <div style={{
          fontSize: '0.66em', fontWeight: 700, marginBottom: '3px',
          color: 'rgba(255,200,100,0.7)', display: 'flex', alignItems: 'center', gap: 5,
        }}>
          <span>🗳️</span>
          <span>Room vote: {d.via_poll.emoji} {d.via_poll.label}{d.via_poll.votes ? ` · ${d.via_poll.votes} vote${d.via_poll.votes === 1 ? '' : 's'}` : ''}</span>
        </div>
      )}
      {/* 💍/💔 Tea ship-verdict receipt (HEM §3b) — winner count first */}
      {d.via_ship_vote && (
        <div style={{
          fontSize: '0.66em', fontWeight: 700, marginBottom: '3px',
          color: 'rgba(255,200,100,0.7)', display: 'flex', alignItems: 'center', gap: 5,
        }}>
          <span>🗳️</span>
          <span>Room verdict: {d.via_ship_vote.verdict === 'last' ? '💍' : '💔'} {Math.max(d.via_ship_vote.last || 0, d.via_ship_vote.doom || 0)}-{Math.min(d.via_ship_vote.last || 0, d.via_ship_vote.doom || 0)}</span>
        </div>
      )}
      <div style={{
        fontSize: '0.68em', fontWeight: 800, letterSpacing: '0.08em',
        color: 'rgba(255,200,100,0.9)', marginBottom: '4px',
        display: 'flex', alignItems: 'center', gap: 6,
      }}>
        {/* displayCommandName: stored rows (old AND new, human and impulse
            lanes) carry the internal id 'jukebox'; users only ever see /aux.
            08-31 restyle: non-comp drops the ⚡ (emoji-free chrome). */}
        <span>{quietAmbience ? '' : '⚡ '}{houseCardHeader(d.command) || `/${displayCommandName(d.command) || 'command'}`}</span>
        {/* 🎭 LIVE PIP (SCENE_SYSTEM_SPEC §6): a scene used to just stop
            mattering with nothing on screen to say so — the room-charge decay
            was DB-only until the countdown broadcast landed. Reads the SAME two
            conditions the server uses (charges left AND under the 10-minute
            DIRECTIVE_MAX_AGE_MS), so the pip can never outlive the directive. */}
        {d.command === 'scene' && d.remaining?.room > 0
          && (Date.now() - new Date(action.created_at || 0).getTime()) < 10 * 60 * 1000 && (
          <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 4,
            padding: '1px 7px', borderRadius: 999,
            background: 'rgba(255,120,60,0.18)', border: '1px solid rgba(255,150,90,0.45)',
            color: 'rgba(255,190,140,0.95)', letterSpacing: '0.04em',
          }}>
            <span style={{
              width: 5, height: 5, borderRadius: '50%', background: '#ff9d5c',
              boxShadow: '0 0 5px #ff9d5c', display: 'inline-block',
            }} />
            LIVE
          </span>
        )}
      </div>
      {/* 🖼️ SCENE BANNER (SCENE_SYSTEM_SPEC §5): the establishing shot lives ON
          the scene card, so a scene reads as ONE beat instead of a text card
          followed by a picture card. Aspect box is reserved up front so the
          feed never jumps when the art lands ~4-10s later over the WS patch. */}
      {d.command === 'scene' && (d.url || d.media_status === 'pending') && (
        <div style={{
          position: 'relative', width: '100%', aspectRatio: '16 / 9',
          borderRadius: 10, overflow: 'hidden', marginBottom: 8,
          background: 'rgba(0,0,0,0.28)',
        }}>
          {d.url ? (
            <img
              src={d.url}
              alt=""
              style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
              loading="lazy"
            />
          ) : (
            <div style={{
              position: 'absolute', inset: 0, display: 'flex',
              alignItems: 'center', justifyContent: 'center',
              fontSize: '0.72em', color: 'rgba(255,200,100,0.6)', letterSpacing: '0.06em',
            }}>
              painting the scene…
            </div>
          )}
        </div>
      )}
      {d.directed ? (
        <DirectedCallCard directed={d.directed} />
      ) : (
        <div style={{ color: '#fff', fontSize: '0.95em', fontWeight: 600, lineHeight: 1.4 }}>
          {d.card_message || d.message}
        </div>
      )}
      {/* 🌕🚪 09-26: an event's START card carries a Go into its room's picture
          (the moon night → the roof). roomView.eventGoFor = the invite's own
          resolver over eventGoBus.rooms; the page re-checks the room on press. */}
      {(() => {
        const go = (eventGoBus.rooms && !action.isOptimistic) ? eventGoFor(d, eventGoBus.rooms, action.created_at) : null;
        if (!go) return null;
        return (
          <button type="button" data-event-go-card={go.room}
            onClick={(e) => { e.stopPropagation(); emitGameEvent(GAME_EVENTS.EVENT_GO, { room: go.room, id: action.id }); }}
            style={{
              marginTop: 10, padding: '7px 16px', minHeight: 36, borderRadius: 999, border: 'none', cursor: 'pointer',
              background: '#ffc864', color: '#2a1c08', fontFamily: 'inherit', fontWeight: 800, fontSize: '0.85em',
              letterSpacing: '0.02em', boxShadow: '0 2px 10px rgba(255,200,100,0.35)', touchAction: 'manipulation', boxSizing: 'border-box',
              textShadow: 'none', // the reading lane's halo never lands on gold paper (roomHaloPaper.test.js)
            }}>
            {go.text}
          </button>
        );
      })()}
      {/* Media lifecycle (/imagine /build /combine): pending → done/fizzled */}
      {d.media_status === 'pending' && !action.isOptimistic && (
        <div style={{ marginTop: '4px', fontSize: '0.75em', color: 'rgba(255,255,255,0.45)' }}>
          ⏳ Working on it...
        </div>
      )}
      {d.media_status === 'failed' && (
        <div style={{ marginTop: '4px', fontSize: '0.75em', color: '#ff9800', fontWeight: 700 }}>
          😵 It fizzled! Try again in a bit.
        </div>
      )}
      {/* ✅ "Look below 👇" is only true for the commands whose media lands as
          a SEPARATE card further down the feed (/imagine /build /combine).
          /scene renders its establishing shot IN this card (the banner above,
          spec §5: "one beat, not a text card followed by a picture card"), so
          the arrow pointed at nothing — Nina 08-08. Suppressed once the art is
          actually in the card; a scene still PAINTING keeps the pending line
          above, which is the honest state. */}
      {d.media_status === 'completed' && !(d.command === 'scene' && d.url) && (
        <div style={{ marginTop: '4px', fontSize: '0.75em', color: '#7ee787', fontWeight: 700 }}>
          ✅ Done! Look below 👇
        </div>
      )}
    </div>
  );
};

// 💬 W2d.1 (A9, PORTED_HOME_HANDOFF_2026_09_25; mockup v9's .lane .msg): a
// plain spoken line on a PLACE page. ActionRenderer routes here only when the
// main feed passes `placeChat` (portedPlaceOn; null at every other mount), so
// every other room keeps MessageAction byte for byte. The look lives in
// components/ChatGame/PlaceChatRow.js; this wrapper gathers what the page
// owns: the words (SourcePill + fences + @mentions), his 💭 line in the
// owner's words (showThoughts, the ⋯ toggle), the owner's 🔍/📌 INSIDE the
// bubble (placeLine: the same predicate MessageAction used), reactions (only
// his and the owner's; only the owner can add, Nina 09-26) and the 🚩.
// Defined ABOVE ActionRenderer on purpose: a use-before-define here is a TDZ.
const PlaceChatLine = ({ action, users, THEME, placeChat, humanPlayerId, effectivePlayerId, showThoughts, gameType, onReactionSubmit, placeLine = null, placeOwnerTalk = false }) => {
  const data = action.data || {};
  // every SEAT's reactions (ruling 31: guests react too); a spectator id never counts
  const seatIds = React.useMemo(() => (users || []).map((u) => u && u.id).filter(Boolean), [users]);
  const reactions = React.useMemo(
    () => placeReactions(data.reactions, { residentSeatId: placeChat.residentSeatId, ownerSeatId: placeChat.ownerSeatId, seatIds }),
    [data.reactions, placeChat.residentSeatId, placeChat.ownerSeatId, seatIds],
  );
  // a SEATED human (the owner, or a guest with a seat) can react; a spectator cannot
  const viewerSeated = !!humanPlayerId;
  const user = getUserById(users, action.game_instance_player_id);
  const { who, name } = placeLineWho(action, { user, humanPlayerId, residentSeatId: placeChat.residentSeatId, residentName: placeChat.residentName });
  const isOwn = who === 'me';
  const body = data.is_emote
    ? `* ${data.sender_name || (user && user.name) || 'Someone'} ${data.message} *`
    : (<><SourcePill data={data} />{renderMessageWithCode(data.message, (users || []).map((u) => u.name), '#FFE1B0')}</>);
  const hisTools = !!placeLine && action.type === 'public_message' && !action.isOptimistic
    && action.game_instance_player_id === placeLine.residentSeatId;
  const tools = hisTools ? (
    <PlaceLineTools
      key={action.id}
      actionId={action.id}
      residentName={placeLine.residentName}
      receipt={placeLine.receipts[action.id]}
      pinState={placeLine.pinState(action.id)}
      onPin={placeLine.onPin}
      onOpenChange={placeLine.onOpenChange}
    />
  ) : null;
  const canReact = (gameType === 'chat-game' || gameType === 'chat') && !!onReactionSubmit && !action.isOptimistic;
  // R4 (09-26): the bubble's picker + chips stand on the lane's dark ground
  // like the media cards (SharedContentAction), never on a light room theme
  const bubbleTheme = React.useMemo(() => placeCardTheme(THEME), [THEME]);
  const reactRow = canReact && (viewerSeated || Object.keys(reactions).length > 0) ? (
    <MessageReactions
      actionId={action.id}
      currentPlayerId={effectivePlayerId}
      existingReactions={reactions}
      onReact={onReactionSubmit}
      theme={bubbleTheme}
      alignRight={isOwn}
      chipCap={4}
      readOnly={!viewerSeated}
      hideAddLabel
      addClassName="pmr-add"
      propUi
      pickerClassName="pmr-pk"
    />
  ) : null;
  // 🛡️ 🚩 on other humans' lines (MODERATION P1.1), as in MessageAction
  const flag = action.type === 'human_interjection' && !isOwn
    ? <ReportFlag kind="chat_message" targetId={action.id} targetPlayerId={data.sender_id || null} playerId={effectivePlayerId} compact />
    : null;
  return (
    <PlaceChatRow
      who={who}
      name={name}
      tag={!isOwn && isMaintainer(user) ? <span className="pmr-mt" title="Site admin, here for upkeep">🔧 system maintenance</span> : null}
      body={body}
      emote={!!data.is_emote}
      // 💭 (ruling 31): "the user" becomes the owner's name ONLY when the newest
      // human line before his row is the OWNER's (a guest's turn keeps "the user")
      thought={placeThought(data.thoughts, placeOwnerTalk ? placeChat.ownerName : null)}
      showThought={!!showThoughts}
      tools={tools}
      after={reactRow || flag ? <>{reactRow}{flag}</> : null}
      // the viewer's lone 😊 (no reactions yet, no 🚩) sits beside the bubble
      quick={!!reactRow && !flag && viewerSeated && Object.keys(reactions).length === 0}
      // the time, always visible (ruling 31), small after the name
      time={formatActionTime(data.timestamp || action.created_at)}
      title={formatActionTime(data.timestamp || action.created_at)}
      pinned={hisTools && placeLine.pinState(action.id) !== 'none'}
      fresh={isFreshLine(action.created_at || data.timestamp)}
      actionId={action.id}
    />
  );
};

// (perf 08-13) React.memo: every parent setState re-reconciled EVERY visible
// row through this (heavy) renderer — worst on the ≤30-message plain path
// where all rows render. Rows re-render only when their own props change;
// feedDerivation keeps the arrays identity-stable so the memo actually holds.
const ActionRenderer = React.memo(({ action, playerModelMap, users, THEME, onActionUpdate, gameType, allActions, cachedSpaces, cachedRoundAnalysisHistory, onReactionSubmit, humanPlayerId, effectivePlayerId, showThoughts, imposterMode, competitiveMode, humanTokensLeft, uiTerms, noirTheme = false, onOpenCastCard = null, houseNicknames = null, localStatuses = null, portedInteract = false, placeOwnerTalk = false, placeLine = null, placeChat = null }) => {
// console.log('ActionRendered', JSON.stringify(action, null, 2)) // DISABLED: Severe performance impact
  switch (action.type) {
    case 'message':
    case 'public_message':
    case 'private_message':
    case 'secret_message':
    case 'house_action':
    case 'human_interjection':
    case 'speak':
      // 💬 W2d.1 (A9): the place page's plain lines (placeChat is null at every other mount)
      if (placeChat && isPlaceChatLine(action)) {
        return <PlaceChatLine key={action.data?.client_msg_id || action.id} action={action} users={users} THEME={THEME} placeChat={placeChat} humanPlayerId={humanPlayerId} effectivePlayerId={effectivePlayerId} showThoughts={showThoughts} gameType={gameType} onReactionSubmit={onReactionSubmit} placeLine={placeLine} placeOwnerTalk={placeOwnerTalk} />;
      }
      return <MessageAction action={action} playerModelMap={playerModelMap} users={users} THEME={THEME} onActionUpdate={onActionUpdate} gameType={gameType} onReactionSubmit={onReactionSubmit} humanPlayerId={humanPlayerId} effectivePlayerId={effectivePlayerId} showThoughts={showThoughts} imposterMode={imposterMode} competitiveMode={competitiveMode} humanTokensLeft={humanTokensLeft} onOpenCastCard={onOpenCastCard} houseNicknames={houseNicknames} localStatuses={localStatuses} placeLine={placeLine} />;
    case 'round_summary':
      return <RoundSummaryAction action={action} users={users}  THEME={THEME} />;
    case 'out_of_tokens':
      return <OutOfTokensAction action={action}  THEME={THEME} />;
    case 'game_action':
      return <GameActionAction action={action} users={users}  THEME={THEME} />;
    case 'secret_game_action':
      return <SecretGameAction action={action} users={users}  THEME={THEME} />;
    case 'pass':
      return <PassAction action={action} users={users}  THEME={THEME} />;
    case 'clue_found':
      // Whodunnit STAGE 1 — subtle ambient line ("Nina found something."), deliberately
      // NOT an announcement (those pop as 8s beats). Social pressure, not a headline.
      return (
        <div style={{ textAlign: 'center', padding: '3px 10px', margin: '2px 0' }}>
          <span style={{ color: 'rgba(217,186,114,0.75)', fontSize: '0.78em', fontStyle: 'italic', letterSpacing: '0.04em' }}>
            {action.data?.message || '🔍 Somebody found something.'}
          </span>
        </div>
      );
    case 'vote':
      return <VoteAction action={action} users={users}  THEME={THEME} />;
    case 'eviction':
      return <EvictionAction action={action} users={users}  THEME={THEME} />;
    case 'game_analysis':
      return <GameAnalysisAction action={action} playerModelMap={playerModelMap}  THEME={THEME} />;
    case 'round_analysis':
      return <RoundAnalysisAction action={action} playerModelMap={playerModelMap} users={users} THEME={THEME} onActionUpdate={onActionUpdate} cachedSpaces={cachedSpaces} cachedRoundAnalysisHistory={cachedRoundAnalysisHistory} />;
    case 'vote_to_win':
      return <VoteToWinAction action={action} users={users}  THEME={THEME} />;
    case 'player_summary':
      return <PlayerSummaryAction action={action}  THEME={THEME} />;
    case 'competition_question':
      return <CompetitionQuestionAction action={action}  THEME={THEME} />;
    case 'competition_answer':
      return <CompetitionAnswerAction action={action} users={users} playerModelMap={playerModelMap}  THEME={THEME} />;
    case 'competition_winner':
      return <CompetitionWinnerAction action={action} users={users}  THEME={THEME} />;
    case 'competition_credit_decision':
      return <CompetitionCreditDecisionAction action={action} users={users}  THEME={THEME} />;
    case 'competition_results':
      return <CompetitionResultsAction action={action} users={users}  THEME={THEME} />;
    case 'slice_of_life':
      return <SliceOfLifeAction action={action} users={users} THEME={THEME} />;
    case 'question_answer':
      return <QuestionAnswerAction action={action} users={users} THEME={THEME} />;
    case 'nomination':
      return <NominationAction action={action} users={users}  THEME={THEME} />;
    case 'game_announcement':
      return <GameAnnouncementAction action={action}  THEME={THEME} allActions={allActions || []} uiTerms={uiTerms} noirTheme={noirTheme} compactTicker={!competitiveMode && !imposterMode} />;
    case 'vote_cast':
      return <VoteCastAction action={action} users={users}  THEME={THEME} />;
    case 'secret_role_assignment':
      return <SecretRoleAssignmentAction action={action} users={users}  THEME={THEME} />;
    case 'initial_investigation':
      return <InitialInvestigationAction action={action} users={users}  THEME={THEME} />;
    case 'empath_reading':
      return <EmpathReadingAction action={action} users={users}  THEME={THEME} />;
    case 'scene':
      return <SceneAction action={action} THEME={THEME} />;
    case 'episode':
      return <EpisodeAction action={action} users={users} THEME={THEME} allActions={allActions || []} onActionUpdate={onActionUpdate} gameType={gameType} />;
    case 'custom_video':
      return <CustomVideoAction action={action} THEME={THEME} onActionUpdate={onActionUpdate} />;
    case 'shared_content':
      return <SharedContentAction action={action} users={users} THEME={THEME} humanPlayerId={humanPlayerId} localStatuses={localStatuses} placeChat={placeChat} />;
    case 'space_command':
      return <SpaceCommandAction action={action} THEME={THEME} portedInteract={portedInteract} quietAmbience={!competitiveMode && !imposterMode} />;
    case 'week_summary':
      return <WeekSummaryAction action={action} users={users} THEME={THEME} />;
    case 'opening_statement':
      return <OpeningStatementAction action={action} users={users} THEME={THEME} />;
    case 'final_speech':
      return <FinalSpeechAction action={action} users={users} THEME={THEME} />;
    default:
      return (
        <div style={{
          backgroundColor: THEME.bgLight,
          padding: '12px',
          borderRadius: '8px',
          marginBottom: '12px'
        }}>
          <pre style={{ margin: 0 }}>
            {JSON.stringify(action, null, 2)}
          </pre>
        </div>
      );
  }
});

// Virtualized Action List Component (Issue 3 fix - prevents rendering all actions at once)
// Uses @tanstack/react-virtual for virtualization (replaced react-window due to Object.values bug in v2)
const VirtualizedActionList = React.memo(({
  actions = [],
  portedInteract = false,
  playerModelMap = {},
  users = [],
  THEME = {},
  onActionUpdate = () => {},
  gameType = '',
  allActions = [],
  cachedSpaces = [],
  cachedRoundAnalysisHistory = [],
  onReactionSubmit = null,
  humanPlayerId = null,
  effectivePlayerId = null,
  showThoughts = false,
  imposterMode = false,
  competitiveMode = false,
  onOpenCastCard = null,
  humanTokensLeft = 0,
  uiTerms = null,
  noirTheme = false,
  houseNicknames = null,
  localStatuses = null,
  onWatchLive = null, // 📣 Live room only: host go-live lines in the feed are a tap target
  crowdFull = false, // 📣 09-25 fast stream: crowd lines at full size ("Crowd as full lines")
  placeLine = null, // 🔍📌 W2c: the owner's desktop place page only (why + pin under his lines, the horizon row)
  placeChat = null // 💬 W2d.1 (A9): the place page's chat rows (owners, guests, phones); null at every other mount
}) => {
  const parentRef = useRef(null);

  // Create virtualizer. 📣 09-25: the size guess is per row (crowdInline.js
  // estimateFeedRowPx): a crowd line ~28px, everything else the old 200px.
  const virtualizer = useVirtualizer({
    count: actions.length,
    getScrollElement: () => parentRef.current,
    estimateSize: (index) => estimateFeedRowPx(actions[index]),
    overscan: 5,
    // ⚙️ Sweep #46 RCA: after a jump to the bottom, late item measurement
    // (images decoding, estimate corrections) makes the virtualizer issue its
    // own compensation scrolls (resizeItem → _scrollToOffset). Those fire the
    // SAME scroll events as a user fling, so the page's handleChatScroll read
    // them as "the user scrolled up" and disarmed the bottom-follow — a
    // pinned reader ended parked ~285px above bottom with nothing correcting
    // it. Stamp library-driven scrolls so the intent logic can tell them
    // apart; behavior of the scroll itself is unchanged (elementScroll is the
    // library default).
    scrollToFn: (offset, options, instance) => {
      window.__virtCompensationTs = performance.now();
      elementScroll(offset, options, instance);
    },
  });

  if (actions.length === 0) {
    return (
      <div style={{
        padding: '12px',
        color: THEME.textMuted,
        textAlign: 'center',
        backgroundColor: THEME.bgLight,
        borderRadius: '4px'
      }}>
        No messages yet
      </div>
    );
  }

  // For small lists, don't virtualize (threshold: 30 items)
  if (actions.length <= 30) {
    return (
      <div>
        {actions.map((action, actionIndex) => (
          // 📣 Live room: a ghost-lane line merged into the feed (crowdInline.js)
          action.type === CROWD_LINE_TYPE
            ? <CrowdLine key={action.id} msg={action.data?.crowd} THEME={THEME} onWatchLive={onWatchLive} full={crowdFull} />
            // 🏡 W2c: where his view of the chat ends (portedHorizon.js)
            : action.type === HORIZON_LINE_TYPE
            ? <PlaceHorizon key={action.id} residentName={placeLine ? placeLine.residentName : null} mem={action.data?.mem === true} />
            : <ActionRenderer
            // (perf 08-13) stable identity: index keys re-keyed EVERY row on any
            // insert/reorder, defeating React.memo. client_msg_id first — it
            // survives the optimistic→server-echo swap so a sent message's row
            // doesn't remount (and replay its entry animation) on confirm.
            key={action.data?.client_msg_id || action.id || actionIndex}
            action={action}
            portedInteract={portedInteract}
            placeLine={placeLine}
            placeChat={placeChat}
            placeOwnerTalk={placeChat ? ownerSpokeBefore(actions, actionIndex, placeChat) : false}
            playerModelMap={playerModelMap}
            users={users}
            THEME={THEME}
            onActionUpdate={onActionUpdate}
            gameType={gameType}
            allActions={allActions}
            cachedSpaces={cachedSpaces}
            cachedRoundAnalysisHistory={cachedRoundAnalysisHistory}
            onReactionSubmit={onReactionSubmit}
            humanPlayerId={humanPlayerId}
            effectivePlayerId={effectivePlayerId}
            showThoughts={showThoughts}
            imposterMode={imposterMode}
            competitiveMode={competitiveMode}
            humanTokensLeft={humanTokensLeft}
            uiTerms={uiTerms}
            noirTheme={noirTheme}
            onOpenCastCard={onOpenCastCard}
            houseNicknames={houseNicknames}
            localStatuses={localStatuses}
          />
        ))}
      </div>
    );
  }

  // For large lists (>30 items), use virtualization with @tanstack/react-virtual
  return (
    <div
      ref={parentRef}
      // data-virtual-feed (08-02): this div is the feed's REAL scroller when
      // virtualization is on — getFeedScrollEl in GameStepPage targets it so
      // scrollToBottom/isScrolledToBottom stop no-op'ing on the outer ref.
      data-virtual-feed
      style={{
        height: 'calc(100dvh - 200px)',
        minHeight: '400px',
        overflow: 'auto',
        width: '100%'
      }}
    >
      <div
        style={{
          height: `${virtualizer.getTotalSize()}px`,
          width: '100%',
          position: 'relative',
        }}
      >
        {virtualizer.getVirtualItems().map((virtualItem) => {
          const action = actions[virtualItem.index];
          if (!action) return null;

          // Ensure action has required properties
          const safeAction = {
            ...action,
            data: action.data || {},
            type: action.type || 'unknown'
          };

          return (
            <div
              key={virtualItem.key}
              data-index={virtualItem.index}
              ref={virtualizer.measureElement}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                transform: `translateY(${virtualItem.start}px)`,
              }}
            >
              {/* 📣 Live room: a ghost-lane line merged into the feed (crowdInline.js) */}
              {safeAction.type === CROWD_LINE_TYPE
                ? <CrowdLine msg={safeAction.data.crowd} THEME={THEME || {}} onWatchLive={onWatchLive} full={crowdFull} />
                : safeAction.type === HORIZON_LINE_TYPE
                ? <PlaceHorizon residentName={placeLine ? placeLine.residentName : null} mem={safeAction.data?.mem === true} />
                : <ActionRenderer
                action={safeAction}
                portedInteract={portedInteract}
                placeLine={placeLine}
                placeChat={placeChat}
                placeOwnerTalk={placeChat ? ownerSpokeBefore(actions, virtualItem.index, placeChat) : false}
                playerModelMap={playerModelMap || {}}
                users={users || []}
                THEME={THEME || {}}
                onActionUpdate={onActionUpdate || (() => {})}
                gameType={gameType || ''}
                allActions={allActions || []}
                cachedSpaces={cachedSpaces || []}
                cachedRoundAnalysisHistory={cachedRoundAnalysisHistory || []}
                onReactionSubmit={onReactionSubmit}
                humanPlayerId={humanPlayerId}
                effectivePlayerId={effectivePlayerId}
                showThoughts={showThoughts}
                imposterMode={imposterMode}
                competitiveMode={competitiveMode}
                humanTokensLeft={humanTokensLeft}
                uiTerms={uiTerms}
                noirTheme={noirTheme}
                onOpenCastCard={onOpenCastCard}
                houseNicknames={houseNicknames}
                localStatuses={localStatuses}
              />}
            </div>
          );
        })}
      </div>
    </div>
  );
});

// ⏳ A visiting marketplace agent whose stay is over (Nina 08-07). Its seat is
// still in `users` on purpose — every historical message resolves its portrait
// and name colour out of that array (getUserForSender), so dropping the row
// would strip the avatars off a transcript that is still on screen. Instead the
// roster surfaces skip it: Locals, the "N in this space" count, and the welcome
// interstitial's cast.
// ⚠️ Trust the SERVER's `visit_expired` only — do not recompute from
// `visit_expires_at` here. A pinned visitor carries a stamp in the past and is
// still present; only the backend predicate knows about `visit_pinned`.
// Lag is one status poll, which is seconds.
const visitOver = (u) => !!(u && u.visit_expired);

// ⏳ PAST VISITORS (Nina 08-07) — the other half of dropping expired guests
// from the roster. Before this, a visiting agent whose hour ended simply
// evaporated: its messages stayed in the transcript with a portrait and a name,
// but nothing on the page said who they had been or that they had gone. That
// reads as a bug ("why is this character in my chat but not in Locals?").
// A plain list of names is the whole feature (Nina: "even if it's just a list
// of their names") — no cards, no avatars, no click target.
//
// Derives from the SAME server field the roster filters on, so the two can
// never disagree: a name is here exactly when it is absent from Locals.
// 🤖 `!isRealHuman` rather than `!u.is_human` (Nina 08-15): a CONNECTED agent
// seat is is_human:true, so the old test excluded exactly the guests this list
// exists to name — an agent whose hour ran out simply vanished with no notice.
const pastVisitorNames = (allUsers) => (allUsers || [])
  .filter(u => visitOver(u) && !isRealHuman(u))
  .map(u => u && u.name)
  .filter(Boolean);

const PastVisitors = ({ allUsers, THEME, align = 'left', style = {} }) => {
  const names = pastVisitorNames(allUsers);
  if (names.length === 0) return null;
  return (
    <div style={{
      fontSize: '0.68em', lineHeight: 1.5, textAlign: align,
      color: THEME?.textMuted || 'rgba(255,255,255,0.55)',
      ...style,
    }}>
      <span style={{ fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', opacity: 0.85 }}>
        ⏳ Past visitors
      </span>
      <span style={{ opacity: 0.8 }}> · {names.join(', ')}</span>
    </div>
  );
};

// 🎓 Shared quest-name picker — the tour beats AND the quest pill/whisper
// personalize with the same locals, so the story stays coherent across both.
const pickTutorialLocals = (users) => {
  const locals = (users || []).filter(u => !u.is_human && !u.data?.is_eliminated && u.name);
  return { greetName: locals[0]?.name || null, giftName: (locals[1] || locals[0])?.name || null };
};

// ── Tutorial Toast ──
// 🎙️ Tour opener v2 (Nina 08-03, mock-approved variant B): the bottom toast
// became a CENTERED host ask — the space's own host (moderator_character)
// asks "Hey! Are you new here?" in a speech bubble over a dimmed scrim.
// Yes = startTutorial (the old "Show me"). No = never ask again (the old
// dismiss path: per-game key + global tour flag). "not now" / ✕ = soft
// dismiss, this session only — the ask returns next visit. No auto-dismiss:
// a centered question waits for its answer (the 15s toast clock is retired).
// Scrim taps do nothing on purpose — an explicit choice, ✕ is one tap away.
// 🙅 declined morph (09-01 swap, Nina): the "where the tour lives" parting
// line now follows "not now" / ✕ (the maybe-laters), NOT "No" — a firm No
// just closes. The bubble morphs in place, scrim drops so the room is
// usable, and the parent unmounts it a few seconds later. "No" still does
// the never-ask-again bookkeeping in declineTutorial.
// 🐣 Ported variant (08-17): in a marketplace-ported space the ask comes from
// the RESIDENT the owner just carried across, not the space host — askText /
// yesLabel override the copy, and the caller passes the resident as
// moderatorCharacter (the component never cared who the character is).
const TutorialToast = ({ onStart, onNo, onDismiss, theme, moderatorCharacter, declined = false, askText = null, yesLabel = null }) => {
  const accent = theme.accent || '#667eea';
  const hostName = moderatorCharacter?.name || 'The Host';
  const hostImg = moderatorCharacter?.profile_image_url || null;
  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 100001,
      background: declined ? 'transparent' : 'rgba(5,6,10,0.55)',
      backdropFilter: declined ? 'none' : 'blur(2px)',
      pointerEvents: declined ? 'none' : 'auto',
      transition: 'background 0.35s ease',
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 18,
    }}>
      <style>{'@keyframes tourAskIn { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } } @media (prefers-reduced-motion: reduce) { .tour-ask { animation: none !important; } }'}</style>
      <div className="tour-ask" style={{ width: '100%', maxWidth: 360, animation: 'tourAskIn 0.3s ease-out' }}>
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 9 }}>
          {/* Host portrait — the ask comes from a RESIDENT, not the app */}
          <div style={{
            width: 44, height: 44, borderRadius: '50%', flexShrink: 0, overflow: 'hidden',
            display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.3em',
            background: `radial-gradient(circle at 32% 28%, ${accent}, rgba(20,16,40,0.95))`,
            border: '2px solid rgba(255,255,255,0.8)', boxShadow: '0 4px 14px rgba(0,0,0,0.5)',
          }}>
            {hostImg
              ? <img src={hostImg} alt={hostName} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              : (moderatorCharacter?.avatar_emoji || '🎙️')}
          </div>
          <div style={{
            position: 'relative', flex: 1, borderRadius: '16px 16px 16px 4px', padding: '15px 15px 14px',
            background: 'linear-gradient(160deg, rgba(34,34,48,0.97), rgba(19,19,30,0.97))',
            border: `1.5px solid ${accent}`,
            boxShadow: `0 10px 34px rgba(0,0,0,0.5), 0 0 22px ${accent}40`,
          }}>
            {!declined && (
            <button
              onClick={onDismiss}
              aria-label="Dismiss for now"
              style={{
                position: 'absolute', top: -12, right: -6, width: 28, height: 28, borderRadius: '50%',
                background: 'rgba(40,40,52,0.97)', border: '2px solid rgba(255,255,255,0.45)', color: '#fff',
                fontWeight: 700, fontSize: '0.8em', lineHeight: 1, cursor: 'pointer', padding: 0,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}
            >✕</button>
            )}
            <div style={{ fontSize: '0.62em', fontWeight: 800, letterSpacing: '0.12em', textTransform: 'uppercase', color: theme.accentOnPanel || '#b9a5ff', marginBottom: 6 }}>
              {hostName}
            </div>
            <div style={{ fontSize: declined ? '0.92em' : '1.02em', fontWeight: declined ? 600 : 800, lineHeight: declined ? 1.5 : 1.3, color: '#fff', marginBottom: declined ? 0 : 13 }}>
              {declined
                ? <>No problem! It's under <strong style={{ fontWeight: 800 }}>ⓘ Space Details</strong> if you ever want it.</>
                : (askText || 'Hey! Are you new here?')}
            </div>
            {!declined && (
            <div style={{ display: 'flex', gap: 9, alignItems: 'stretch' }}>
              <button
                onClick={onStart}
                style={{
                  flex: 1, padding: '9px 12px', borderRadius: 11, border: 'none',
                  background: accent, color: theme.textOnAccent || '#fff',
                  fontWeight: 800, fontSize: '0.72em', letterSpacing: '0.02em', cursor: 'pointer',
                  boxShadow: `0 2px 10px ${accent}70`,
                }}
              >
                {yesLabel || 'Yes, show me around!'}
              </button>
              <button
                onClick={onNo}
                style={{
                  padding: '9px 14px', borderRadius: 11,
                  background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.22)',
                  color: 'rgba(255,255,255,0.75)', fontWeight: 800, fontSize: '0.82em', cursor: 'pointer',
                }}
              >
                No
              </button>
            </div>
            )}
          </div>
        </div>
        {!declined && (
        <button
          onClick={onDismiss}
          style={{
            display: 'block', margin: '9px auto 0', background: 'none', border: 'none',
            color: 'rgba(255,255,255,0.42)', fontSize: '0.7em', cursor: 'pointer',
            textDecoration: 'underline', fontFamily: 'inherit',
          }}
        >
          not now
        </button>
        )}
      </div>
    </div>
  );
};

// ── Spotlight Overlay ──
// ⌨️ Typeable tokens in tour copy (Nina 08-05, rung 3 of the card restyle —
// container-less variant): step text wraps what the player will literally
// type in backticks, and the card renders those spans in the palette's amber
// monospace — text style + color ONLY, no pill/border/fill (Nina's call).
// Backticks never reach any other surface: tutorialSteps text renders solely
// through this card.
const renderTourText = (t) => String(t).split('`').map((seg, i) => (i % 2
  // Full chip treatment (Nina 08-06 final): the CARD's quest label is the
  // containerless one — tokens keep the palette-style fill + border so the
  // thing you'll type looks like the UI it summons.
  ? <span key={i} style={{
      fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
      color: '#ffd98a', fontWeight: 600, fontSize: '0.88em',
      background: 'rgba(255,200,100,0.10)',
      border: '1px solid rgba(255,200,100,0.35)',
      borderRadius: 6, padding: '1px 7px', whiteSpace: 'nowrap',
    }}>{seg}</span>
  : seg));

// buildReceipt (Nina 08-06): { emoji, handle } of the thing a live /build just
// made — the build beat's success card shows it as a palette-style chip so the
// "on the shelf" claim has proof. null = plain successText (grads entering an
// already-done beat, or a generous non-build completion).
const SpotlightOverlay = ({ steps, currentStep, onNext, onBack, onSkip, theme, isMobile, isCompetitive, completedMap, forceCelebrate, buildReceipt = null, keyboardUp = false }) => {
  const step = steps[currentStep];
  const [rect, setRect] = React.useState(null);
  const accent = theme.accent || '#667eea';
  // Derive from rect (updated by polling) so it stays in sync with late-appearing refs
  const hasTarget = !!rect;

  // Celebration state — card morphs into a goodbye message on the last step
  const [celebrating, setCelebrating] = React.useState(false);

  // ⌨️🎓 Soft mode (Nina 08-07, replacing 08-06's pill): with the iOS keyboard
  // up, the HOLE is the broken part — the dock lifts itself via a
  // visualViewport transform while this overlay's fixed panels speak
  // layout-viewport, so the cutout lands on the wrong slice of screen. The
  // 08-06 answer swapped the whole card for a top pill, and Nina's read was
  // that the tour looked like it VANISHED (instant unmount + a differently
  // shaped object somewhere else + the dim dropping to zero, all in one
  // frame). So: keep the card exactly where it is, kill only the hole, and
  // cross-fade the heavy 0.75 dim down to a light wash. Nothing moves.
  // The card sheds its buttons and goes pointerEvents:none while typing —
  // pure signage, nothing between the user and the keyboard. Blur/send
  // restores the spotlight.
  const softMode = !!(keyboardUp && !celebrating);

  // 🎓 Live task beats (tour × quest merge, 08-02): a beat with a taskKey
  // watches the quest flags — the flag flipping mid-beat morphs the card into
  // a success note (the parent advances the tour a moment later). A beat
  // entered with its task ALREADY done shows the success note with a plain
  // Next, so returning users breeze through with satisfying checkmarks.
  const stepDone = !!(step?.taskKey && completedMap && completedMap[step.taskKey]);
  React.useEffect(() => { if (forceCelebrate) setCelebrating(true); }, [forceCelebrate]);

  // Per-beat entry hook (switch place, unfold the composer, seed a greeting).
  // Ref-read so the effect keys on the step INDEX only — a steps-array
  // identity change must not re-fire the current beat's entry action.
  const stepEnterRef = React.useRef(step);
  stepEnterRef.current = step;
  React.useEffect(() => { stepEnterRef.current?.onEnter?.(); }, [currentStep]);

  // Scroll target into view + measure position (merged so late-appearing refs get scrolled too)
  React.useEffect(() => {
    let hasScrolled = false;
    let isScrolling = false;
    let scrollSettleTimeout = null;

    const scrollToTarget = () => {
      const el = step?.ref?.current;
      if (!el) return;
      // On mobile, containers expand to fit content so the window scrolls —
      // use scrollIntoView directly instead of hunting for a scroll parent.
      if (isMobile) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }
      const scrollParent = ((node) => {
        let cur = node.parentElement;
        while (cur) {
          const style = window.getComputedStyle(cur);
          if (/(auto|scroll)/.test(style.overflowY) || /(auto|scroll)/.test(style.overflow)) return cur;
          cur = cur.parentElement;
        }
        return null;
      })(el);
      if (scrollParent) {
        const parentRect = scrollParent.getBoundingClientRect();
        const elRect = el.getBoundingClientRect();
        const offsetTop = elRect.top - parentRect.top + scrollParent.scrollTop;
        scrollParent.scrollTo({ top: offsetTop - parentRect.height / 2 + elRect.height / 2, behavior: 'smooth' });
      } else {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    };

    const updateRect = () => {
      if (step?.ref?.current) {
        // First time we see the ref — scroll to it, then suppress measurements
        // until the scroll animation finishes
        if (!hasScrolled) {
          hasScrolled = true;
          isScrolling = true;
          scrollToTarget();
          clearTimeout(scrollSettleTimeout);
          scrollSettleTimeout = setTimeout(() => {
            isScrolling = false;
            updateRect();
          }, isMobile ? 700 : 350);
          return;
        }
        // Don't measure mid-scroll — the rect would be wrong and the spotlight flickers
        if (isScrolling) return;
        const r = step.ref.current.getBoundingClientRect();
        // 🕳️ Hole = target ∪ its open popovers (Nina 08-04 "things are
        // blocked on the tutorial"): the composer's slash palette / @ mention
        // dropdown / # shelf list / arg-chips banner all render
        // position:absolute ABOVE the form, so the form's border box alone
        // left them outside the hole — dimmed by the shadow, click-eaten by
        // the inert frame, half-hidden behind the tour card. Beats that ASK
        // for "/" or "@" must spotlight what pops up. The 200ms poll follows
        // open/close; the rect transition animates the growth.
        let top = r.top, left = r.left, right = r.right, bottom = r.bottom;
        step.ref.current.querySelectorAll('[data-tour-popover]').forEach(p => {
          const pr = p.getBoundingClientRect();
          if (!pr.width || !pr.height) return;
          top = Math.min(top, pr.top);
          left = Math.min(left, pr.left);
          right = Math.max(right, pr.right);
          bottom = Math.max(bottom, pr.bottom);
        });
        setRect({ top, left, width: right - left, height: bottom - top });
      } else {
        setRect(null);
      }
    };

    // Small initial delay, then poll every 200ms (catches refs that render after step change)
    const initialDelay = setTimeout(updateRect, 100);
    const pollInterval = setInterval(updateRect, 200);
    window.addEventListener('resize', updateRect);
    window.addEventListener('scroll', updateRect, true);
    return () => {
      clearTimeout(initialDelay);
      clearTimeout(scrollSettleTimeout);
      clearInterval(pollInterval);
      window.removeEventListener('resize', updateRect);
      window.removeEventListener('scroll', updateRect, true);
    };
  }, [step, currentStep]);

  if (!step) return null;

  const padding = 8;
  const spotlightRect = (hasTarget && rect) ? {
    top: rect.top - padding,
    left: rect.left - padding,
    width: rect.width + padding * 2,
    height: rect.height + padding * 2,
  } : null;

  // Position the text card near the spotlight, ensuring it stays in viewport
  const cardMaxWidth = isMobile ? 'calc(100vw - 32px)' : 420;
  // Soft mode sheds the button row, so the card needs less room to fit above
  // the composer — the difference decides which placement branch wins.
  const cardEstimatedHeight = softMode ? 110 : 140; // approximate height of the card
  const cardEstimatedWidth = isMobile ? window.innerWidth - 32 : 420;
  // ⌨️ "How much screen is actually left?" — window.innerHeight still reports
  // the FULL layout viewport with the keyboard up, so the space-below math
  // would happily park the card in the slice the keyboard now covers (the one
  // real risk in keeping the card put). visualViewport.height is the only
  // number that knows. Used ONLY for that fit test: `bottom`-anchored
  // placement below is layout-viewport relative and stays on innerHeight.
  const viewportH = (softMode && window.visualViewport?.height) || window.innerHeight;

  let cardStyle;
  if (!spotlightRect) {
    // No spotlight — center the card
    cardStyle = {
      position: 'fixed',
      top: '50%',
      // Horizontal centring via TOAST_CENTERED, vertical still by transform
      // (08-15). left:50% capped the card at HALF the screen, so on a phone
      // cardMaxWidth (calc(100vw - 32px) ≈ 358px) could never bind and the
      // tour card squeezed to ~195px — the same bug as the watch-party toast,
      // on the first thing a new player ever sees.
      ...TOAST_CENTERED,
      transform: 'translateY(-50%)',
      maxWidth: cardMaxWidth,
      zIndex: 100002,
    };
  } else if (step.insideSpotlight && spotlightRect.height > cardEstimatedHeight + 40) {
    // Place centered inside the spotlight (for large elements like chat area)
    cardStyle = {
      position: 'fixed',
      top: spotlightRect.top + (spotlightRect.height - cardEstimatedHeight) / 2,
      left: isMobile ? 16 : spotlightRect.left + Math.max(0, (spotlightRect.width - cardEstimatedWidth) / 2),
      maxWidth: cardMaxWidth,
      zIndex: 100002,
    };
  } else {
    const spaceBelow = viewportH - (spotlightRect.top + spotlightRect.height + 16);
    const spaceAbove = spotlightRect.top - 16;
    const spaceRight = window.innerWidth - (spotlightRect.left + spotlightRect.width + 16);

    if (spaceBelow >= cardEstimatedHeight) {
      // Place below the spotlight
      cardStyle = {
        position: 'fixed',
        top: spotlightRect.top + spotlightRect.height + 16,
        left: isMobile ? 16 : Math.max(16, Math.min(spotlightRect.left, window.innerWidth - cardEstimatedWidth - 16)),
        maxWidth: cardMaxWidth,
        zIndex: 100002,
      };
    } else if (spaceAbove >= cardEstimatedHeight) {
      // Place above the spotlight
      cardStyle = {
        position: 'fixed',
        bottom: window.innerHeight - spotlightRect.top + 16,
        left: isMobile ? 16 : Math.max(16, Math.min(spotlightRect.left, window.innerWidth - cardEstimatedWidth - 16)),
        maxWidth: cardMaxWidth,
        zIndex: 100002,
      };
    } else if (!isMobile && spaceRight >= cardEstimatedWidth + 16) {
      // Place to the right of the spotlight (desktop only)
      cardStyle = {
        position: 'fixed',
        top: Math.max(16, Math.min(spotlightRect.top, window.innerHeight - cardEstimatedHeight - 16)),
        left: spotlightRect.left + spotlightRect.width + 16,
        maxWidth: cardMaxWidth,
        zIndex: 100002,
      };
    } else {
      // Fallback: overlay on top of spotlight, centered horizontally
      cardStyle = {
        position: 'fixed',
        top: Math.max(16, spotlightRect.top + 20),
        left: isMobile ? 16 : Math.max(16, Math.min(spotlightRect.left, window.innerWidth - cardEstimatedWidth - 16)),
        maxWidth: cardMaxWidth,
        zIndex: 100002,
      };
    }
  }

  const isLastStep = currentStep === steps.length - 1;

  const handleDoneClick = () => {
    if (isLastStep) {
      // A task beat left undone gets "Later", not confetti — the celebration
      // is earned by finishing the quest (or it fires via forceCelebrate).
      if (step.taskKey && !stepDone) onSkip();
      else setCelebrating(true);
    } else {
      onNext();
    }
  };

  // Interactive beats leave the spotlight hole GENUINELY usable — the target
  // (composer, tray) receives real taps and typing while the dimmed frame
  // around it stays inert. Non-interactive beats keep click-away-to-skip.
  const holeIsLive = !!(step.interactive && hasTarget && spotlightRect && !celebrating && !softMode);

  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      zIndex: 100000,
      pointerEvents: 'none',
    }}>
      {holeIsLive ? (
        <>
          {/* Inert frame around the live hole — four panels, no click-to-skip
              (an accidental tap while aiming for the composer must not end
              the tour; the ✕ and Later button are the exits) */}
          <div style={{ position: 'fixed', left: 0, top: 0, right: 0, height: Math.max(0, spotlightRect.top), zIndex: 100000, pointerEvents: 'auto' }} />
          <div style={{ position: 'fixed', left: 0, top: spotlightRect.top + spotlightRect.height, right: 0, bottom: 0, zIndex: 100000, pointerEvents: 'auto' }} />
          <div style={{ position: 'fixed', left: 0, top: spotlightRect.top, width: Math.max(0, spotlightRect.left), height: spotlightRect.height, zIndex: 100000, pointerEvents: 'auto' }} />
          <div style={{ position: 'fixed', left: spotlightRect.left + spotlightRect.width, top: spotlightRect.top, right: 0, height: spotlightRect.height, zIndex: 100000, pointerEvents: 'auto' }} />
        </>
      ) : softMode ? null : (
        /* Click-away backdrop. 📱 Phones: a stray tap must NOT silently end
           the tour (Nina 08-06 "it vanished after the first Next" — a
           near-miss on the button lands here) — the backdrop still blocks
           the page but only ✕/Later/celebration exit. Desktop keeps
           click-away-to-skip. */
        <div
          onClick={(celebrating || !isMobile) ? onSkip : undefined}
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 100000,
            pointerEvents: 'auto',
            background: (hasTarget && !celebrating) ? 'transparent' : 'rgba(0,0,0,0.75)',
            transition: 'background 0.4s ease',
          }}
        />
      )}

      {/* Spotlight cutout — hidden during celebration. In soft mode it stays
          MOUNTED at opacity 0 rather than unmounting: paired with the wash
          below that makes the heavy dim + hole cross-fade into a light,
          hole-less wash instead of snapping off in one frame. */}
      {hasTarget && spotlightRect && !celebrating && (
        <div style={{
          position: 'fixed',
          top: spotlightRect.top,
          left: spotlightRect.left,
          width: spotlightRect.width,
          height: spotlightRect.height,
          borderRadius: 12,
          boxShadow: `0 0 0 9999px rgba(0,0,0,0.75), 0 0 20px ${accent}60`,
          zIndex: 100001,
          pointerEvents: 'none',
          opacity: softMode ? 0 : 1,
          animation: 'spotlightFadeIn 0.3s ease-out',
          transition: 'top 0.3s ease, left 0.3s ease, width 0.3s ease, height 0.3s ease, opacity 0.3s ease',
        }} />
      )}

      {/* ⌨️ Soft wash — the keyboard-up stand-in for the dim. Light enough to
          read the room through, dark enough that the tour is visibly still
          on. pointerEvents none: the whole screen stays live while typing. */}
      {softMode && (
        <div style={{
          position: 'fixed',
          inset: 0,
          zIndex: 100000,
          pointerEvents: 'none',
          background: 'rgba(0,0,0,0.35)',
          animation: 'spotlightFadeIn 0.3s ease-out',
        }} />
      )}

      {/* Text card — morphs to celebration on Done. Stays put in soft mode
          (Nina 08-07): same place, same chrome, just inert and button-less. */}
      <div style={{
        ...(celebrating ? {
          position: 'fixed',
          top: '50%',
          // Same centring fix as cardStyle above — this celebration variant is
          // a copy of it and had the identical half-width squeeze.
          ...TOAST_CENTERED,
          transform: 'translateY(-50%)',
          maxWidth: cardMaxWidth,
          zIndex: 100002,
          overflow: 'visible',
        } : cardStyle),
        // Inert while the keyboard is up — a fixed card that ate taps meant
        // for the chat behind it would be worse than the old vanishing pill.
        pointerEvents: softMode ? 'none' : 'auto',
        animation: 'spotlightFadeIn 0.3s ease-out',
        transition: 'top 0.4s ease, left 0.4s ease, transform 0.4s ease',
      }}>
        {/* Confetti burst around the card */}
        {celebrating && Array.from({ length: 30 }).map((_, i) => {
          const colors = ['#ff6b6b', '#ffd93d', '#6bcb77', '#4d96ff', '#ff8fd8', '#c084fc', '#fbbf24'];
          const color = colors[i % colors.length];
          const left = -40 + Math.random() * 180;
          const delay = Math.random() * 0.8;
          const dur = 1.2 + Math.random() * 1;
          const size = 5 + Math.random() * 5;
          return (
            <span key={`celeb-confetti-${i}`} style={{
              position: 'absolute',
              top: '-20px',
              left: `${left}%`,
              width: `${size}px`,
              height: `${size}px`,
              borderRadius: Math.random() > 0.5 ? '50%' : '2px',
              backgroundColor: color,
              opacity: 0.9,
              animation: `celebConfettiFall ${dur}s ease-in ${delay}s both`,
              pointerEvents: 'none',
              zIndex: -1,
            }} />
          );
        })}
        <div style={{
          position: 'relative',
          padding: isMobile ? '18px 20px' : '24px 28px',
          borderRadius: 18,
          background: 'linear-gradient(135deg, rgba(30,30,40,0.97), rgba(20,20,30,0.97))',
          border: `1px solid ${celebrating ? accent : accent + '50'}`,
          boxShadow: celebrating
            ? `0 8px 32px rgba(0,0,0,0.5), 0 0 30px ${accent}50, 0 0 60px ${accent}20`
            : `0 8px 32px rgba(0,0,0,0.5), 0 0 20px ${accent}30`,
          backdropFilter: 'blur(16px)',
          transition: 'border-color 0.4s ease, box-shadow 0.4s ease',
          textAlign: celebrating ? 'center' : 'left',
          animation: celebrating ? 'tutorialCelebGlow 1.2s ease-in-out infinite alternate' : 'none',
        }}>
          {/* X dismiss — hidden during celebration, and while typing (the
              card is inert in soft mode; blur brings the exits back) */}
          {!celebrating && !softMode && (
            <button
              onClick={onSkip}
              style={{
                position: 'absolute',
                top: -8,
                right: -8,
                background: 'rgba(0,0,0,0.7)',
                border: '1px solid rgba(255,255,255,0.2)',
                color: 'rgba(255,255,255,0.6)',
                cursor: 'pointer',
                fontSize: '0.65em',
                width: 20,
                height: 20,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 0,
                lineHeight: 1,
                zIndex: 1,
              }}
            >✕</button>
          )}
          {celebrating ? (
            <div style={{ animation: 'spotlightFadeIn 0.4s ease-out' }}>
              <div style={{
                fontSize: '1.8rem',
                marginBottom: 8,
                animation: 'tutorialCelebEmoji 0.5s cubic-bezier(0.175, 0.885, 0.32, 1.275) both',
              }}>
                {isCompetitive ? '\u2694\uFE0F' : '\u2728'}
              </div>
              <p style={{
                color: '#fff',
                fontSize: isMobile ? '1.1em' : '1.25em',
                fontWeight: 700,
                lineHeight: 1.4,
                margin: '0 0 6px',
              }}>
                {isCompetitive ? "Time to dominate!" : "Little quest: complete!"}
              </p>
              <div style={{
                color: 'rgba(255,255,255,0.45)',
                fontSize: '0.75em',
                fontWeight: 500,
                marginBottom: 16,
              }}>
                {isCompetitive ? "Show them what you've got" : "This space is yours now"}
              </div>
              <button
                onClick={onSkip}
                style={{
                  padding: '8px 20px',
                  borderRadius: 10,
                  border: 'none',
                  background: accent,
                  color: '#fff',
                  fontWeight: 600,
                  fontSize: '0.85em',
                  cursor: 'pointer',
                  boxShadow: `0 2px 8px ${accent}60`,
                }}
              >
                Let's go
              </button>
            </div>
          ) : (
            <>
              {/* 🎓 Quest eyebrow (Nina 08-05): the quest label lives here as a
                  small chip — same language as the quest pill — instead of
                  bolted onto the sentence as bold prose (the "Little quest
                  #1:" prefix styling Nina kept flagging). Hidden on the
                  success note (the ✅ carries that state). */}
              {/* Containerless per Nina 08-06 (the pill was the part she
                  didn't like): same type treatment, no box, no fill. */}
              {step.questNum && !stepDone && (
                <div style={{
                  marginBottom: 8,
                  fontSize: '0.62em', fontWeight: 800, letterSpacing: '0.12em',
                  textTransform: 'uppercase', color: '#ffd54f',
                }}>
                  🎓 Little quest #{step.questNum}
                </div>
              )}
              {/* Body reads as the host talking, not a system alert (08-05):
                  regular-ish weight + looser leading replaced the all-bold
                  block. */}
              <p style={{
                color: '#fff',
                fontSize: isMobile ? '1em' : '1.05em',
                fontWeight: 500,
                lineHeight: 1.55,
                margin: '0 0 18px',
              }}>
                {stepDone ? (
                  <>
                    <span style={{ display: 'inline-block', marginRight: 6, animation: 'tutorialCelebEmoji 0.5s cubic-bezier(0.175, 0.885, 0.32, 1.275) both' }}>✅</span>
                    {(step.taskKey === 'build' && buildReceipt) ? "It's real!" : (step.successText || 'Done! Nice.')}
                  </>
                ) : renderTourText(hasTarget ? step.text : (step.fallbackText || step.text))}
              </p>
              {/* 🧾 Build receipt (Nina 08-06): the success card IS the proof —
                  the thing they just made, in the exact chip style the palette
                  tokens use, plus where it lives. Nothing opens, nothing moves. */}
              {stepDone && step.taskKey === 'build' && buildReceipt && (
                <div style={{ margin: '-10px 0 16px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 6 }}>
                    <span style={{
                      fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                      color: '#ffd98a', fontWeight: 600, fontSize: '0.82em',
                      background: 'rgba(255,200,100,0.10)',
                      border: '1px solid rgba(255,200,100,0.35)',
                      borderRadius: 6, padding: '1px 7px', whiteSpace: 'nowrap',
                    }}>{buildReceipt.emoji} #{buildReceipt.handle}</span>
                    <span style={{ fontSize: '0.85em', color: '#fff' }}>just landed on the shelf.</span>
                  </div>
                  <div style={{ marginTop: 7, fontSize: '0.75em', color: 'rgba(255,255,255,0.55)', lineHeight: 1.5 }}>
                    Tap 🗄️ Shelf anytime to pick it up, gift it, or combine it.
                  </div>
                </div>
              )}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}>
                <span style={{
                  color: 'rgba(255,255,255,0.4)',
                  fontSize: '0.85em',
                  fontWeight: 600,
                }}>
                  {currentStep + 1} of {steps.length}
                </span>
                {/* Buttons step aside while the keyboard is up — the card is
                    signage then, and a stray thumb near the keyboard must not
                    land on "Later". Blur restores them. */}
                <div style={{ display: softMode ? 'none' : 'flex', gap: 12, alignItems: 'center' }}>
                  {currentStep > 0 && (
                    <button
                      onClick={onBack}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: 'rgba(255,255,255,0.5)',
                        fontSize: '0.9em',
                        cursor: 'pointer',
                        fontWeight: 600,
                        padding: '6px 10px',
                      }}
                    >
                      Back
                    </button>
                  )}
                  <button
                    onClick={handleDoneClick}
                    style={{
                      padding: '10px 24px',
                      borderRadius: 12,
                      border: 'none',
                      background: accent,
                      color: '#fff',
                      fontWeight: 700,
                      fontSize: '0.95em',
                      cursor: 'pointer',
                      boxShadow: `0 2px 8px ${accent}60`,
                    }}
                  >
                    {stepDone ? (isLastStep ? 'Done' : 'Next') : (step.btnLabel || (isLastStep ? 'Done' : 'Next'))}
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const GameHint = ({ hintKey, text, theme, dismissedHints, onDismiss, isMobile, arrow = 'bottom', hidden }) => {
  if (hidden || dismissedHints.has(hintKey)) return null;

  const accent = theme.accent || '#667eea';

  // Arrow: a rotated square with two visible border edges, positioned to poke out from the bubble
  const arrowConfigs = {
    bottom: {
      position: { bottom: -8, left: '50%', marginLeft: -7 },
      transform: 'rotate(45deg)',
      borderStyle: { borderRight: '2px solid rgba(255,255,255,0.35)', borderBottom: '2px solid rgba(255,255,255,0.35)', borderTop: 'none', borderLeft: 'none' },
    },
    top: {
      position: { top: -8, left: '50%', marginLeft: -7 },
      transform: 'rotate(45deg)',
      borderStyle: { borderLeft: '2px solid rgba(255,255,255,0.35)', borderTop: '2px solid rgba(255,255,255,0.35)', borderBottom: 'none', borderRight: 'none' },
    },
    left: {
      position: { left: -8, top: '50%', marginTop: -7 },
      transform: 'rotate(45deg)',
      borderStyle: { borderLeft: '2px solid rgba(255,255,255,0.35)', borderBottom: '2px solid rgba(255,255,255,0.35)', borderTop: 'none', borderRight: 'none' },
    },
    right: {
      position: { right: -8, top: '50%', marginTop: -7 },
      transform: 'rotate(45deg)',
      borderStyle: { borderRight: '2px solid rgba(255,255,255,0.35)', borderTop: '2px solid rgba(255,255,255,0.35)', borderBottom: 'none', borderLeft: 'none' },
    },
  };
  const arrowConfig = arrowConfigs[arrow];

  return (
    <div style={{
      position: 'fixed',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      zIndex: 99999,
      maxWidth: isMobile ? '90vw' : '420px',
      width: '100%',
      pointerEvents: 'auto',
    }}>
      <div style={{
        position: 'relative',
        background: `linear-gradient(135deg, ${accent}, ${accent}cc)`,
        border: '2px solid rgba(255,255,255,0.35)',
        borderRadius: 12,
        padding: isMobile ? '14px 16px' : '16px 20px',
        fontSize: isMobile ? '0.9em' : '1em',
        color: '#fff',
        fontWeight: 600,
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        animation: 'hintPopIn 0.4s cubic-bezier(0.34, 1.56, 0.64, 1), hintGlow 2s ease-in-out infinite',
        boxShadow: `0 0 30px ${accent}90, 0 0 60px ${accent}50, 0 8px 32px rgba(0,0,0,0.4)`,
      }}>
        {/* Arrow */}
        <div style={{
          position: 'absolute',
          width: 14,
          height: 14,
          backgroundColor: accent,
          transform: arrowConfig.transform,
          boxShadow: `0 0 8px ${accent}60`,
          ...arrowConfig.borderStyle,
          ...arrowConfig.position,
        }} />
        {/* Warning icon */}
        <span style={{
          fontSize: '1.4em',
          color: '#ff4444',
          flexShrink: 0,
          filter: 'drop-shadow(0 0 6px rgba(255,68,68,0.7))',
        }}>{'\u26A0\uFE0F'}</span>
        <span style={{ flex: 1, lineHeight: 1.5, textShadow: '0 1px 2px rgba(0,0,0,0.3)' }}>{text}</span>
        <button
          onClick={() => onDismiss(hintKey)}
          style={{
            background: 'rgba(255,255,255,0.2)', border: 'none', color: '#fff',
            cursor: 'pointer', fontSize: '0.75em', padding: '6px 12px', flexShrink: 0,
            borderRadius: 6, fontWeight: 700, letterSpacing: '0.5px',
            transition: 'background 0.2s',
          }}
          onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.35)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.2)'; }}
        >GOT IT</button>
      </div>
    </div>
  );
};

// 👋 Say-hi opener deck (Nina 08-03): the Locals-card verb seeds the composer
// with a playful opener instead of a bare @mention. Editable before send —
// it's a first line, not a script. Player copy: short, punchy, no em dashes.
const SAY_HI_OPENERS = [
  "hey! what's your deal?",
  "so what's good in this space?",
  "I've heard things about you. All of them interesting.",
  "hi! I'm new here. Be nice to me.",
  "you look like you know where the snacks are.",
  "what's the latest gossip around here?",
  "tell me something nobody else here knows!",
  "hey you. Yes you. Hi.",
  "I was told you're the one to talk to.",
  "what are you scheming over here?",
  "give me your hottest take!",
  "settle a debate: are you the cool one here?",
];

// ── Inline "Add Agent" picker for the players panel ──────────────────────────
// Tolerate surrounding whitespace/quotes when pasting an agent UUID.
const AGENT_UUID_RE = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i;
const parseAgentIdentifier = (input) => {
  if (!input) return '';
  const m = String(input).trim().match(AGENT_UUID_RE);
  return m ? m[0] : '';
};

const AddAgentInline = ({ gameId, THEME, startOpen = false, ported = false }) => {
  // startOpen: an external trigger (the Locals header pill) already asked for
  // the picker — skip the component's own "+ Add Agent" button state.
  const [open, setOpen] = React.useState(!!startOpen);
  const [agents, setAgents] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [invitingId, setInvitingId] = React.useState(null);
  const [fetched, setFetched] = React.useState(false);
  const [pasteInput, setPasteInput] = React.useState('');
  const [pasteError, setPasteError] = React.useState(null);
  // 🪪 09-26 (Nina): "Bring your own" opens the Bring your AI in card here.
  const [bringOpen, setBringOpen] = React.useState(false);
  React.useEffect(() => {
    if (startOpen) handleOpen();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleOpen = async () => {
    setOpen(true);
    if (fetched) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/agents/marketplace?sort=popular`);
      const data = await res.json();
      if (data.success) setAgents(data.agents || []);
    } catch (err) {
      console.error('[AddAgent] fetch error:', err);
    } finally {
      setLoading(false);
      setFetched(true);
    }
  };

  const inviteById = async (agentId) => {
    setInvitingId(agentId);
    try {
      // 🔒 09-24: a PRIVATE ported room takes invites only from a seat holder,
      // so this browser's seat here rides along (the same two keys the page
      // and chatGameApi read); a public room ignores it.
      let seatId = null;
      let seatSecret = null;
      try {
        seatId = localStorage.getItem(`humanPlayerId_${gameId}`);
        seatSecret = seatId ? chatGameApi.getPlayerSecret(seatId) : null;
      } catch (e) { /* blocked storage: invite without a seat */ }
      const res = await fetch(`${API_BASE_URL}/api/agents/${agentId}/invite`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ game_id: gameId, ...(seatId && seatSecret ? { player_id: seatId, player_secret: seatSecret } : {}) }),
      });
      const result = await res.json();
      if (result.success) {
        setAgents(prev => prev.filter(a => a.id !== agentId));
        return { ok: true };
      }
      return { ok: false, error: result.error || 'Failed to add agent' };
    } catch (err) {
      console.error('[AddAgent] invite error:', err);
      return { ok: false, error: 'Failed to add agent' };
    } finally {
      setInvitingId(null);
    }
  };

  const handleInviteFromList = (agentId) => { inviteById(agentId); };

  const handlePasteSubmit = async () => {
    const id = parseAgentIdentifier(pasteInput);
    if (!id) {
      setPasteError("Couldn't find an agent ID in that.");
      return;
    }
    setPasteError(null);
    const result = await inviteById(id);
    if (result.ok) {
      setPasteInput('');
    } else {
      setPasteError(result.error);
    }
  };

  // accentOnPanel: this whole picker sits on panel backgrounds, and raw dark
  // accents (e.g. Death Note theme) are unreadable there
  const accent = THEME.accentOnPanel || THEME.accent || '#6366f1';

  if (!open) {
    return (
      <button
        onClick={handleOpen}
        style={{
          width: '100%',
          marginTop: 8,
          padding: '7px 0',
          borderRadius: 6,
          border: `1px dashed ${accent}70`,
          background: `${accent}14`,
          // textOnPanel, not accent — accentOnPanel still goes muddy on the darkest
          // sidebars (user report 07-10); the accent lives in the border/tint instead
          color: THEME.textOnPanel || accent,
          fontSize: '0.8em',
          fontWeight: 700,
          cursor: 'pointer',
          transition: 'all 0.2s',
          letterSpacing: 0.5,
        }}
        onMouseEnter={(e) => { e.currentTarget.style.background = `${accent}18`; e.currentTarget.style.borderColor = accent; }}
        onMouseLeave={(e) => { e.currentTarget.style.background = `${accent}08`; e.currentTarget.style.borderColor = `${accent}50`; }}
      >
        + Add Agent
      </button>
    );
  }

  const parsedId = parseAgentIdentifier(pasteInput);
  const canSubmitPaste = !!parsedId && invitingId !== parsedId;

  return (
    <div style={{
      marginTop: 8,
      borderRadius: 8,
      border: `1px solid ${accent}30`,
      background: THEME.bgLight,
      overflow: 'hidden',
    }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '6px 10px',
        borderBottom: `1px solid ${accent}20`,
        background: `${accent}10`,
      }}>
        <span style={{ fontSize: '0.75em', fontWeight: 700, color: THEME.textOnPanel, letterSpacing: 0.5 }}>
          ADD AGENT
        </span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          {/* Bring your own AI: the human "I already have an agent" path, to the
              quickstart funnel (the agents.md door). Distinct from Browse, which
              invites an EXISTING marketplace agent - two different intents.
              🚪🔗 09-22: carries THIS space's id so the funnel can offer to seat
              the new agent right back here (utils/carriedSpace.js). */}
          {/* 🪪 09-26 (Nina: "one button, card inside"): opens the Bring your AI in
              card in place; its footer keeps the carried-space quickstart for an
              AI we voice. CONNECTED_AGENT_SETUP_SPEC_2026_09_26.md W3. */}
          <button
            type="button"
            onClick={() => setBringOpen(true)}
            style={{
              fontSize: '0.72em',
              fontWeight: 700,
              color: accent,
              background: 'none',
              border: 0,
              padding: 0,
              cursor: 'pointer',
              letterSpacing: 0.3,
              fontFamily: 'inherit',
            }}
            title="Bring your own AI - it joins as itself"
          >
            Bring your own &rarr;
          </button>
          {bringOpen && (
            <BringYourAiModal
              onClose={() => setBringOpen(false)}
              // 🔌 09-28 agent wave: land on the quickstart's own "we voice
              // it" section (#voice-it), not the Bring your AI card at its top.
              voicedHref={withCarriedSpace('/agents/quickstart#voice-it', gameId)}
              spaceId={ported ? null : gameId}
            />
          )}
          <a
            href="/marketplace"
            target="_blank"
            rel="noreferrer"
            style={{
              fontSize: '0.72em',
              fontWeight: 700,
              color: accent,
              textDecoration: 'none',
              letterSpacing: 0.3,
            }}
            title="Open the AI Marketplace in a new tab"
          >
            Browse &rarr;
          </a>
          <button
            onClick={() => setOpen(false)}
            style={{ background: 'none', border: 'none', color: THEME.textMuted, cursor: 'pointer', fontSize: '0.85em', padding: '2px 4px' }}
          >
            ✕
          </button>
        </div>
      </div>

      {/* Paste-by-ID row — fastest path when you already know which agent you want. */}
      <div style={{ padding: '8px 10px', borderBottom: `1px solid ${THEME.bgMedium || '#333'}` }}>
        <div style={{ display: 'flex', gap: 6 }}>
          <input
            type="text"
            value={pasteInput}
            onChange={(e) => { setPasteInput(e.target.value); if (pasteError) setPasteError(null); }}
            onKeyDown={(e) => { if (e.key === 'Enter' && canSubmitPaste) handlePasteSubmit(); }}
            placeholder="Paste agent ID..."
            style={{
              flex: 1,
              minWidth: 0,
              padding: '5px 8px',
              borderRadius: 4,
              border: `1px solid ${accent}30`,
              background: THEME.bgMedium || '#222',
              color: THEME.textOnPanel,
              fontSize: '0.75em',
              outline: 'none',
            }}
          />
          <button
            onClick={handlePasteSubmit}
            disabled={!canSubmitPaste}
            style={{
              flexShrink: 0,
              padding: '5px 12px',
              borderRadius: 4,
              border: 'none',
              background: accent,
              color: '#fff',
              fontSize: '0.72em',
              fontWeight: 700,
              cursor: canSubmitPaste ? 'pointer' : 'not-allowed',
              opacity: canSubmitPaste ? 1 : 0.4,
            }}
          >
            {invitingId && invitingId === parsedId ? '...' : 'Add'}
          </button>
        </div>
        {pasteError && (
          <div style={{ marginTop: 4, fontSize: '0.68em', color: '#f87171' }}>{pasteError}</div>
        )}
        {!pasteError && pasteInput.trim() && parsedId && (
          <div style={{ marginTop: 4, fontSize: '0.68em', color: THEME.textMuted }}>
            ID: <code style={{ color: accent }}>{parsedId.substring(0, 8)}&hellip;</code>
          </div>
        )}
      </div>

      {/* Popular-agents quick-pick — handy when you don't have an ID in hand. */}
      <div style={{ maxHeight: 180, overflowY: 'auto', padding: '4px 6px' }}>
        {loading && <div style={{ padding: 12, textAlign: 'center', color: THEME.textMuted, fontSize: '0.75em' }}>Loading agents...</div>}
        {!loading && agents.length === 0 && (
          <div style={{ padding: 12, textAlign: 'center', color: THEME.textMuted, fontSize: '0.75em' }}>
            No agents available.{' '}
            <a href="/marketplace" target="_blank" rel="noreferrer" style={{ color: accent }}>Browse marketplace</a>
            {' '}or{' '}
            <button type="button" onClick={() => setBringOpen(true)} style={{ color: accent, background: 'none', border: 0, padding: 0, font: 'inherit', textDecoration: 'underline', cursor: 'pointer' }}>bring your own AI</button>.
          </div>
        )}
        {agents.map(agent => (
          <div key={agent.id} style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            padding: '6px 4px',
            borderBottom: `1px solid ${THEME.bgMedium || '#333'}`,
          }}>
            <span style={{ fontSize: 18, flexShrink: 0 }}>🤖</span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: '0.78em', fontWeight: 700, color: THEME.textOnPanel, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {agent.name}
              </div>
              <div style={{ fontSize: '0.65em', color: THEME.textMuted, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {agent.description || agent.source_platform || 'Agent'}
              </div>
            </div>
            <button
              onClick={() => handleInviteFromList(agent.id)}
              disabled={invitingId === agent.id}
              style={{
                flexShrink: 0,
                padding: '4px 10px',
                borderRadius: 4,
                border: 'none',
                background: accent,
                color: '#fff',
                fontSize: '0.7em',
                fontWeight: 700,
                cursor: 'pointer',
                opacity: invitingId === agent.id ? 0.5 : 1,
              }}
            >
              {invitingId === agent.id ? '...' : 'Add'}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

// 📱 A chip whose meaning lives in a `title` tooltip is invisible on touch —
// phones have no hover, so the plot-device and dice chips in UsersPanel simply
// never revealed what they said. This wraps the chip: hover still shows the
// native tooltip on desktop, and a TAP reveals the same text inline as a small
// popover (tap again, or anywhere else, to dismiss).
const TapRevealChip = ({ label, text, style }) => {
  const [open, setOpen] = React.useState(false);
  React.useEffect(() => {
    if (!open) return undefined;
    const close = () => setOpen(false);
    // next tick, so the opening tap doesn't immediately close it
    const id = setTimeout(() => document.addEventListener('click', close), 0);
    return () => { clearTimeout(id); document.removeEventListener('click', close); };
  }, [open]);
  return (
    <span style={{ position: 'relative', display: 'inline-flex', flexShrink: 0 }}>
      <span
        role="button"
        tabIndex={0}
        title={text}
        onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setOpen((v) => !v); } }}
        style={{ cursor: 'pointer', ...style }}
      >{label}</span>
      {open && (
        <span style={{
          position: 'absolute', bottom: 'calc(100% + 6px)', left: '50%', transform: 'translateX(-50%)',
          zIndex: 10000, background: 'rgba(12,12,18,0.97)', color: '#fff',
          border: '1px solid rgba(255,255,255,0.18)', borderRadius: 8, padding: '6px 10px',
          fontSize: '0.75em', fontWeight: 600, lineHeight: 1.35, whiteSpace: 'normal',
          width: 'max-content', maxWidth: 'min(240px, 70vw)', textAlign: 'center',
          boxShadow: '0 6px 20px rgba(0,0,0,0.5)', pointerEvents: 'none',
        }}>{text}</span>
      )}
    </span>
  );
};

// ⚔️ COMP PHASE TICKER (08-02, Nina's option B pick): the full-width strip
// riding the top of the comp mobile nav — round · phase · countdown, visible
// on every comp place so leaving Battle never loses the round state. It
// REPLACES the in-feed phase banner on comp phones. The ghost is a true TAB
// docked on the ticker's right (Nina's tweak: rounded top, a few px taller
// than the strip) — it replaces the right-edge capsule on comp phones and
// rides every place, Battle included. Derivation mirrors the phase banner's
// (compact: no speaker avatar, name only). Own 1s tick while a deadline runs.
const CompPhaseTicker = React.memo(({ chatGameStatus, humanPlayerId, waitingForVote, turnDeadline, yourTurnLive, ghostUnread, showGhostTab, onGhostOpen }) => {
  const [, forceTick] = React.useReducer(n => n + 1, 0);
  const deadlineMs = turnDeadline ? new Date(turnDeadline).getTime() : null;
  React.useEffect(() => {
    if (!deadlineMs || deadlineMs <= Date.now()) return undefined;
    const iv = setInterval(forceTick, 1000);
    return () => clearInterval(iv);
  }, [deadlineMs]);
  const s = chatGameStatus || {};
  const isGameOver = !!s.game_completed;
  const isVoting = waitingForVote || s.is_voting;
  const isImposter = !!s.imposter_mode;
  const totalRounds = isImposter ? (s.imposter_word_rounds_total || 3) : (s.total_rounds || null);
  const roundNum = isVoting && s.voting_data?.round_number
    ? s.voting_data.round_number
    : isImposter
      ? Math.min((s.imposter_word_round || 0) + 1, totalRounds)
      : s.current_competitive_round || s.round_number || 1;
  const speaker = s.next_speaker;
  let phase;
  if (isGameOver) phase = 'GAME OVER';
  else if (isVoting) phase = 'VOTING';
  else if (s.current_round_type === 'conclusion') phase = 'FINAL RESULTS';
  else if (s.is_ready_check && !s.ready_check_data?.done) phase = 'READY UP';
  // Only "YOUR TURN" when the turn is confirmed (yourTurnLive) — next_speaker lingers
  // stale on us through the round→vote transition, which flashed "YOUR TURN" between
  // rounds (Nina 08-28). A stale local-human speaker falls through to DISCUSSION.
  else if (speaker && humanPlayerId && speaker.player_id === humanPlayerId) phase = yourTurnLive ? 'YOUR TURN' : 'DISCUSSION';
  else if (s.simultaneous?.phase === 'collect') phase = `EVERYONE WRITES · ${Math.min(s.simultaneous.submitted, s.simultaneous.total)}/${s.simultaneous.total}`;
  else if (speaker?.player_name) phase = `${speaker.player_name}'S TURN`;
  else phase = 'DISCUSSION';
  const remaining = deadlineMs ? Math.max(0, Math.round((deadlineMs - Date.now()) / 1000)) : null;
  const mmss = remaining != null && remaining > 0 && !isGameOver
    ? `${Math.floor(remaining / 60)}:${String(remaining % 60).padStart(2, '0')}`
    : null;
  return (
    <div style={{
      position: 'relative', display: 'flex', alignItems: 'center', gap: 7,
      height: 26, padding: '0 12px', paddingRight: showGhostTab ? 86 : 12,
      background: 'linear-gradient(90deg, rgba(255,77,94,0.13), rgba(255,176,32,0.09))',
      borderBottom: '1px solid rgba(255,176,32,0.22)',
      fontSize: 10, fontWeight: 800, letterSpacing: '0.09em',
      color: '#ffd58a', textTransform: 'uppercase', whiteSpace: 'nowrap',
    }}>
      <span aria-hidden style={{
        width: 6, height: 6, borderRadius: '50%', flexShrink: 0,
        background: isGameOver ? '#8f87a3' : '#ff4d5e',
        animation: isGameOver ? 'none' : 'liveBadgePulse 2s ease-in-out infinite',
      }} />
      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0 }}>
        {`ROUND ${roundNum}${totalRounds ? `/${totalRounds}` : ''} · ${phase}`}
      </span>
      {mmss && (
        <span style={{ marginLeft: 'auto', color: '#fff', fontVariantNumeric: 'tabular-nums', flexShrink: 0 }}>
          {mmss}
        </span>
      )}
      {showGhostTab && (
        <button
          data-noswipe=""
          onClick={onGhostOpen}
          aria-label={ghostUnread > 0 ? `Open ghost chat, ${ghostUnread} unread` : 'Open ghost chat'}
          style={{
            position: 'absolute', right: 8, top: -6, height: 32,
            display: 'flex', alignItems: 'center', gap: 5,
            background: 'rgba(8,10,14,0.96)',
            border: '1px solid rgba(143,127,240,0.6)', borderBottom: 'none',
            borderRadius: '10px 10px 0 0', padding: '0 12px',
            color: '#cfc6f7', fontSize: 12, fontWeight: 800, cursor: 'pointer',
            textTransform: 'none', letterSpacing: 'normal',
          }}
        >
          <LineIcon name="ghost" size={13} />
          {ghostUnread > 0 && (
            <span style={{
              background: 'rgba(143,127,240,0.35)', color: '#e6e1fb',
              borderRadius: 999, padding: '0 6px', fontSize: 10,
              fontVariantNumeric: 'tabular-nums', lineHeight: '15px',
            }}>{ghostUnread > 99 ? '99+' : ghostUnread}</span>
          )}
        </button>
      )}
    </div>
  );
});

const UsersPanel = ({ users, THEME, chatGameStatus, humanPlayerId, createdPlayerId, onTogglePlay, onQuickJoin, isReadyCheck, readyCheckData, readyCheckSubmitted, readyCheckCountdown, readyCountdownDuration, readyCheckPhase, autoStartCountdown, gameStarted, hasJoinedGame, showGlitchReveal, onReadyUp, dismissedHints, onDismissHint, isMobile, hintsHidden, spectateDropdownRef, quickJoinButtonsRef, readyToggleRef, showElementPulse, showDropdownHint, showReadyHint, onDropdownInteract, onReadyInteract, compactMode, gameId, diceSpotlightId, allUsers, hideJoinCtas = false, localStatuses = null }) => {
  // One stable color per seat, shared with chat names / dice toast / vote cards.
  // Built from the FULL seat list (allUsers), not the disconnect-filtered `users` prop —
  // a player timing out of the grace window must not re-shuffle everyone else's colors.
  const seatColors = buildPlayerColorMap(allUsers || users);
  // 📱 Host LIVE placement compare (Nina 07-20, mobile only) — `?hostlive=`
  // picks the variant while she judges on device: 'a' (default) = compact
  // ModeratorPanel row right under the Locals header; 'b' = LIVE pill +
  // rotating status merged into the host's polaroid card (no second host
  // presence). Desktop untouched; the old lower panel hides on phones for
  // both variants (that's the move).
  const hostLiveVariant = React.useMemo(() => {
    try { return (new URLSearchParams(window.location.search).get('hostlive') || 'a').toLowerCase(); } catch (e) { return 'a'; }
  }, []);
  // Variant B rotates the same status_messages the ModeratorPanel does (50-70s)
  const [hostStatusIdx, setHostStatusIdx] = React.useState(0);
  const hostStatusCount = chatGameStatus?.moderator_character?.status_messages?.length || 0;
  React.useEffect(() => {
    if (!isMobile || hostLiveVariant !== 'b' || hostStatusCount < 2) return undefined;
    const interval = setInterval(() => {
      setHostStatusIdx(prev => (prev + 1) % hostStatusCount);
    }, 50000 + Math.random() * 20000);
    return () => clearInterval(interval);
  }, [isMobile, hostLiveVariant, hostStatusCount]);
  // Sort users alphabetically by name (handle undefined names)
  // Disconnected players are already filtered out upstream via activeUsers
  const sortedUsers = [...users].sort((a, b) =>
    (a.name || '').localeCompare(b.name || '')
  );

  // Helper to safely check if user is alive (defaults to true for games without this property)
  const isUserAlive = (user) => {
    return user.data?.is_alive !== false;
  };

  // Helper to check if user is eliminated in competitive mode
  const isUserEliminated = (user) => {
    return chatGameStatus?.eliminated_players?.includes(user.game_player_id) || false;
  };

  // Compact mode: show only 2 players by default, expandable (must be before early returns)
  // Collapsed by default (Nina 07-12: "the chat room panel shouldn't be
  // expanded by default"). Only instances the `collapsible` flag below applies
  // to ever read this — non-collapsible layouts always render the full grid.
  const [playersExpanded, setPlayersExpanded] = React.useState(false);
  // Tea-card mode popups (non-competitive chat spaces): tapping a polaroid
  // opens the dossier-core character card; tapping the host card opens the
  // host story. Self-contained here because the Tea tab's richer popup only
  // mounts while that tab is open.
  const [castCardUser, setCastCardUser] = React.useState(null);
  const [showHostCard, setShowHostCard] = React.useState(false);

  // ── COMPETITIVE / IMPOSTER MODE: Ranked Leaderboard ──
  if (chatGameStatus?.competitive_mode || chatGameStatus?.imposter_mode) {
    const scores = chatGameStatus.player_scores || {};
    // Human player always first, rest sorted alphabetically (stable order)
    const rankedUsers = [...sortedUsers].sort((a, b) => {
      const aIsHuman = humanPlayerId && a.game_player_id === humanPlayerId;
      const bIsHuman = humanPlayerId && b.game_player_id === humanPlayerId;
      if (aIsHuman) return -1;
      if (bIsHuman) return 1;
      return (a.name || '').localeCompare(b.name || '');
    });
    const maxScore = Math.max(...rankedUsers.map(u => scores[u.game_player_id] || 0), 1);
    // Compute score-based rank for each player (for rank labels)
    const scoreRanked = [...rankedUsers].sort((a, b) => (scores[b.game_player_id] || 0) - (scores[a.game_player_id] || 0));
    const rankMap = {};
    scoreRanked.forEach((u, i) => { rankMap[u.game_player_id] = i + 1; });
    const accent = THEME.accent || '#6366f1';
    const isCompMode = true; // We're inside the competitive/imposter branch

    return (
      <div style={{
        backgroundColor: THEME.bgMedium,
        padding: '10px',
        borderRadius: '10px',
        boxShadow: `0 4px 12px rgba(0,0,0,0.3), 0 0 10px ${accent}10`,
        width: '100%',
        boxSizing: 'border-box',
        border: `1px solid ${accent}25`,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '8px',
          paddingBottom: '6px',
          borderBottom: `1px solid ${accent}25`
        }}>
          <span style={{
            color: THEME.textOnPanel,
            fontWeight: 800,
            fontSize: '0.8em',
            textTransform: 'uppercase',
            letterSpacing: '2px'
          }}>
            Players
          </span>
          <span style={{
            color: (isReadyCheck && !gameStarted && !humanPlayerId)
              ? THEME.textOnPanelMuted
              : (isReadyCheck && !gameStarted && readyCheckSubmitted)
              ? '#4ade80'
              : (isReadyCheck && !gameStarted)
              ? (THEME.accent || '#6366f1')
              : '#4ade80',
            fontSize: '0.85em',
            fontWeight: 700,
            textShadow: (isReadyCheck && !gameStarted && !readyCheckSubmitted) ? 'none' : '0 0 8px rgba(74, 222, 128, 0.6), 0 0 16px rgba(74, 222, 128, 0.3)'
          }}>
            {(isReadyCheck && !gameStarted)
              ? (!humanPlayerId
                ? "You're Spectating"
                : readyCheckPhase === 'ready_to_start'
                ? `Starting in ${autoStartCountdown || 0}s`
                : readyCheckSubmitted
                ? (readyCheckPhase === 'draining' ? `Ready! ${readyCheckCountdown || 0}s` : 'Ready!')
                : readyCheckPhase === 'draining'
                ? `Ready up! ${readyCheckCountdown || 0}s`
                : 'Ready up!')
              : chatGameStatus.imposter_mode
              ? (() => {
                  const total = chatGameStatus.imposter_word_rounds_total || 3;
                  const current = Math.min((chatGameStatus.imposter_word_round || 0) + 1, total);
                  // Only "Game complete!" when the game has actually ended — reaching the final
                  // round (current >= total) is NOT completion; that round is still being played.
                  return chatGameStatus.game_completed ? `Round ${total}/${total}. Game complete!` : `Round ${current}/${total}`;
                })()
              : `Playing: Round ${chatGameStatus.current_competitive_round || chatGameStatus.round_number || 1}`
            }
          </span>
        </div>

        {/* Spectate / Play dropdown — right under PLAYERS heading */}
        {/* Competitive mode: two quick-join buttons instead of dropdown. The
            !showGlitchReveal gate hides them while the role-reveal overlay is up;
            the overlay's opaque full-viewport layer blocks clicks visually, so the
            DOM should match — otherwise tools that bypass visibility (find-by-text
            + click coords) try to click occluded elements and silently fail. */}
        {/* Center-dock redesign (Nina 07-25): whodunnit humans join via the
            bottom-center case dock — the roster keeps these ONLY for the
            explorer bot, whose join flow is trained on them. */}
        {createdPlayerId && onQuickJoin && isCompMode && !gameStarted && !hasJoinedGame && !showGlitchReveal && !hideJoinCtas && (
          <div ref={quickJoinButtonsRef} style={{ display: 'flex', gap: '6px', marginBottom: '8px' }}>
            <button
              onClick={() => onQuickJoin('play')}
              style={{
                flex: 1.4,
                padding: '12px 8px',
                borderRadius: '8px',
                border: '2px solid #16C64C',
                background: 'linear-gradient(135deg, #2EE85F 0%, #16C64C 100%)',
                color: '#fff',
                cursor: 'pointer',
                fontSize: '0.92em',
                fontWeight: 800,
                letterSpacing: '0.3px',
                textShadow: '0 1px 2px rgba(0,0,0,0.3)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px',
                position: 'relative',
                // Static shadow + transform-only breath + ping-ring child —
                // the old animated box-shadow here alone was ~30% of a
                // space's idle render cost (fx-bench 07-09)
                boxShadow: '0 4px 14px rgba(34, 221, 102, 0.5)',
                transition: 'transform 0.15s ease, box-shadow 0.15s ease, filter 0.15s ease',
                animation: 'joinPlayerGlow 2s ease-in-out infinite',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.animation = 'none';
                e.currentTarget.style.transform = 'translateY(-1px) scale(1.03)';
                e.currentTarget.style.boxShadow = '0 8px 24px rgba(34, 221, 102, 0.7)';
                e.currentTarget.style.filter = 'brightness(1.1) saturate(1.15)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.animation = 'joinPlayerGlow 2s ease-in-out infinite';
                e.currentTarget.style.transform = '';
                e.currentTarget.style.boxShadow = '0 4px 14px rgba(34, 221, 102, 0.5)';
                e.currentTarget.style.filter = '';
              }}
            >
              {/* Radar ping — expanding ring, opacity+transform only */}
              <span aria-hidden="true" style={{
                position: 'absolute',
                inset: 0,
                borderRadius: '8px',
                border: '2px solid rgba(46, 232, 95, 0.8)',
                animation: 'joinPingRing 2s ease-out infinite',
                pointerEvents: 'none',
              }} />
              {chatGameStatus?.ui_terms?.join_cta
                ? chatGameStatus.ui_terms.join_cta
                : <><span style={{ fontSize: '1.05em' }}>▶</span> Join as Player!</>}
            </button>
            <button
              onClick={() => onQuickJoin('spectate')}
              style={{
                flex: 1,
                padding: '10px 8px',
                borderRadius: '8px',
                border: `2px solid ${THEME.textMuted || '#757575'}60`,
                backgroundColor: `${THEME.textMuted || '#757575'}15`,
                color: THEME.textDark || '#333',
                cursor: 'pointer',
                fontSize: '0.85em',
                fontWeight: 700,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '5px',
                transition: 'all 0.2s',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = `${THEME.textMuted || '#757575'}25`; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = `${THEME.textMuted || '#757575'}15`; }}
            >
              👁️ Spectate!
            </button>
          </div>
        )}
        {/* Non-competitive: dropdown toggle */}
        {createdPlayerId && onTogglePlay && !isCompMode && (
          <div style={{ position: 'relative', marginBottom: '8px' }}>
            {(() => {
              const isEliminated = chatGameStatus?.eliminated_players?.includes(createdPlayerId);
              const playDisabled = gameStarted || isEliminated;
              const spectateDisabled = gameStarted && !!humanPlayerId;
              return <select
              ref={spectateDropdownRef}
              value={humanPlayerId ? 'play' : 'spectate'}
              onChange={(e) => {
                if (e.target.value === 'play' && playDisabled) return;
                if (e.target.value === 'spectate' && spectateDisabled) return;
                if (onDropdownInteract) onDropdownInteract();
                onTogglePlay(e.target.value === 'play');
              }}
              style={{
                width: '100%',
                padding: '9px 12px',
                borderRadius: '8px',
                border: `2px solid ${accent}60`,
                backgroundColor: '#fff', textShadow: 'none',
                color: '#000',
                cursor: 'pointer',
                fontSize: '0.9em',
                fontWeight: 700,
                WebkitAppearance: 'none',
                MozAppearance: 'none',
                appearance: 'none',
                backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23666' d='M2 4l4 4 4-4'/%3E%3C/svg%3E")`,
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'right 10px center',
                paddingRight: '28px',
                animation: showElementPulse ? 'elementAttentionPulse 1.6s ease-in-out 4' : 'none',
              }}
            >
              <option value="spectate" disabled={spectateDisabled} style={{ backgroundColor: '#fff', color: spectateDisabled ? '#999' : '#000' }}>👁️ Spectate{spectateDisabled ? ' (locked)' : ''}</option>
              <option value="play" disabled={playDisabled} style={{ backgroundColor: '#fff', color: playDisabled ? '#999' : '#000' }}>🎮 Play{playDisabled ? ' (locked)' : ''}</option>
            </select>;
            })()}
            {showDropdownHint && (
              <div style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                right: 0,
                marginTop: '4px',
                padding: '6px 10px',
                borderRadius: '6px',
                background: `linear-gradient(135deg, ${THEME.accent || '#667eea'}, ${THEME.primary || '#764ba2'})`,
                color: '#fff',
                fontSize: '0.7em',
                fontWeight: 600,
                textAlign: 'center',
                zIndex: 10,
                border: '2px solid rgba(255,255,255,0.5)',
                animation: 'hintPulseGlow 1.5s ease-in-out infinite',
              }}>
                <span onClick={(e) => { e.stopPropagation(); onDropdownInteract(); }} style={{ position: 'absolute', top: isMobile ? '-4px' : '-2px', right: isMobile ? '1px' : '3px', cursor: 'pointer', fontSize: isMobile ? '1.6em' : '1.3em', opacity: 1, lineHeight: 1, fontWeight: 'bold', textShadow: '0 1px 2px rgba(0,0,0,0.3)', padding: isMobile ? '4px' : 0 }}>&times;</span>
                Switch between Play and Spectate here
              </div>
            )}
          </div>
        )}

        {/* Ready check drain bar — only during draining phase (hidden in competitive - buttons handle it) */}
        {!isCompMode && isReadyCheck && readyCheckPhase === 'draining' && readyCheckCountdown != null && (
          <div style={{
            width: '100%',
            height: '4px',
            backgroundColor: 'rgba(255,255,255,0.1)',
            borderRadius: '2px',
            marginBottom: '8px',
            overflow: 'hidden',
          }}>
            <div style={{
              height: '100%',
              width: `${(readyCheckCountdown / readyCountdownDuration) * 100}%`,
              backgroundColor: readyCheckCountdown <= Math.ceil(readyCountdownDuration * 0.3) ? '#ef4444' : readyCheckCountdown <= Math.ceil(readyCountdownDuration * 0.6) ? '#f59e0b' : '#4ade80',
              borderRadius: '2px',
              transition: 'width 1s linear, background-color 0.5s',
            }} />
          </div>
        )}

        <style>{`
          @keyframes rosterSeatRing {
            0%, 100% { box-shadow: 0 0 0 2px var(--seat-ring-color), 0 0 10px var(--seat-ring-color); }
            50% { box-shadow: 0 0 0 5px var(--seat-ring-color), 0 0 22px var(--seat-ring-color); }
          }
        `}</style>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: 1, overflowY: 'auto', minHeight: 0 }}>
          {rankedUsers.map((user, index) => {
            const score = scores[user.game_player_id] || 0;
            const eliminated = isUserEliminated(user);
            const barWidth = maxScore > 0 ? (score / maxScore) * 100 : 0;
            const playerRank = rankMap[user.game_player_id] || (index + 1);
            const isLeader = playerRank === 1 && score > 0;
            const isSpeaking = chatGameStatus?.next_speaker?.player_id === user.game_player_id;
            const isHumanPlayer = humanPlayerId && user.game_player_id === humanPlayerId;
            const seatColor = seatColors[user.game_player_id] || accent;
            // While this player's roll toast is on screen, ring their seat — the toast
            // says WHO, the ring says WHERE they live on this list.
            const isDiceSpotlit = !eliminated && diceSpotlightId && diceSpotlightId === user.game_player_id;

            return (
              <div key={user.game_player_id || index} style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '6px 8px',
                borderRadius: '8px',
                backgroundColor: isHumanPlayer && !eliminated ? `${accent}18` : (isLeader ? `${accent}12` : `${THEME.textOnPanel}08`),
                border: isDiceSpotlit ? `1px solid ${seatColor}` : (isHumanPlayer && !eliminated ? `1px solid ${accent}40` : (isLeader ? `1px solid ${accent}25` : '1px solid transparent')),
                borderLeft: isHumanPlayer && !eliminated && !isDiceSpotlit ? `3px solid ${accent}` : undefined,
                opacity: eliminated ? 0.45 : 1,
                position: 'relative',
                overflow: isDiceSpotlit ? 'visible' : 'hidden',
                ...(isDiceSpotlit ? { animation: 'rosterSeatRing 1.1s ease-in-out infinite', '--seat-ring-color': seatColor, boxShadow: `0 0 0 2px ${seatColor}88, 0 0 14px ${seatColor}66` } : {}),
                transition: 'all 0.3s ease'
              }}>
                {/* Score bar background */}
                {score > 0 && (
                  <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    height: '100%',
                    width: `${barWidth}%`,
                    backgroundColor: `${accent}08`,
                    transition: 'width 0.6s ease',
                    zIndex: 0
                  }} />
                )}

                {/* Rank */}
                <div style={{
                  width: '20px',
                  textAlign: 'center',
                  fontSize: '0.75em',
                  fontWeight: 800,
                  color: isLeader ? accent : THEME.textOnPanelMuted,
                  position: 'relative',
                  zIndex: 1
                }}>
                  {isLeader ? '\u{1F451}' : `#${playerRank}`}
                </div>

                {/* Avatar — ringed in the seat color (the identity thread across surfaces) */}
                <div style={{ position: 'relative', zIndex: 1 }}>
                  <ProfileImage user={user} size={28} borderColor={eliminated ? THEME.textMuted : seatColor} />
                  {eliminated && (
                    <div style={{
                      position: 'absolute',
                      inset: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '0.9em'
                    }}>
                      {'\u{1F47B}'}
                    </div>
                  )}
                </div>

                {/* Name — minWidth 0 lets this flex cell actually shrink; without
                    it a long name+model (e.g. "Slick' Sal Valentino
                    (gemini-3-flash-preview)") forces the row wider than the panel
                    and drags in a horizontal scrollbar (Nina 07-25). The model
                    tag truncates first (flexShrink 4), the name holds longer. */}
                <div style={{
                  flex: 1,
                  minWidth: 0,
                  fontSize: '0.8em',
                  fontWeight: 600,
                  color: eliminated ? THEME.textOnPanelMuted : THEME.textOnPanel,
                  textDecoration: eliminated ? 'line-through' : 'none',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  position: 'relative',
                  zIndex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}>
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flexShrink: 1, minWidth: '3ch' }}>{user.name}</span>
                  {(() => {
                    const r = chatGameStatus?.dice?.rolls?.[user.game_player_id];
                    const dev = chatGameStatus?.twist?.plot_devices?.[user.game_player_id];
                    if (eliminated || (!r && !dev)) return null;
                    const bandColor = (b) => { b = b || ''; return b.includes('CRITICAL SUCCESS') ? '#ffd700' : b.includes('CRITICAL FAIL') ? '#ff1744' : b.includes('SUCCESS') ? '#4caf50' : '#ff9800'; };
                    // Hide the roll (value + band color would spoil it) until this player has taken
                    // their turn this round — then reveal. "?" + neutral color until then.
                    const rollRevealed = chatGameStatus?.dice?.revealed_ids?.includes(user.game_player_id);
                    return (
                      <>
                        {r && (
                          <TapRevealChip
                            label={`🎲${rollRevealed ? r.value : '?'}`}
                            text={rollRevealed ? `${chatGameStatus.dice.label}: ${r.band_name}` : `${chatGameStatus.dice.label} — hidden until their turn`}
                            style={{ fontSize: '0.8em', fontWeight: 700, padding: '0 5px', borderRadius: '8px', border: `1px solid ${rollRevealed ? bandColor(r.band_name) : THEME.textMuted}`, color: rollRevealed ? bandColor(r.band_name) : THEME.textMuted }}
                          />
                        )}
                        {dev && (
                          <TapRevealChip
                            label="🃏"
                            text={`Must work in: ${dev}`}
                            style={{ fontSize: '0.8em', padding: '0 4px', borderRadius: '8px', border: '1px solid #ff9800', color: '#ff9800' }}
                          />
                        )}
                      </>
                    );
                  })()}
                  {(humanPlayerId && user.game_player_id === humanPlayerId || createdPlayerId && user.game_player_id === createdPlayerId) && user.name !== 'You' && (
                    <span style={{
                      fontWeight: 500,
                      opacity: 0.6,
                      fontSize: '0.85em',
                    }}>
                      (You)
                    </span>
                  )}
                  {getDisplayModelName(user) && !isHumanPlayer && !(createdPlayerId && user.game_player_id === createdPlayerId) && !user.is_human && (
                    <span style={{
                      fontWeight: 400,
                      opacity: 0.5,
                      fontSize: '0.85em',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      minWidth: 0,
                      flexShrink: 4,
                    }} title={getDisplayModelName(user)}>
                      ({getDisplayModelName(user)})
                    </span>
                  )}
                  {isSpeaking && !eliminated && (
                    <span style={{
                      width: '7px',
                      height: '7px',
                      borderRadius: '50%',
                      backgroundColor: accent,
                      display: 'inline-block',
                      flexShrink: 0,
                      boxShadow: `0 0 6px ${accent}80`,
                      animation: 'speakerDotPulse 1.2s ease-in-out infinite'
                    }} />
                  )}
                </div>

                {/* Score or Ready Status — skip ready toggle in competitive (buttons handle it) */}
                {isReadyCheck && user.is_human && !isCompMode ? (
                  <div style={{ position: 'relative', zIndex: 1 }}>
                    {isHumanPlayer ? (
                      // Current user's ready toggle (switch style)
                      (() => {
                        const isReady = readyCheckSubmitted || readyCheckData?.ready_player_names?.includes(user.name);
                        const maxReady = readyCheckData?.max_ready_players || 5;
                        const isFull = !isReady && (readyCheckData?.ready_count || 0) >= maxReady;
                        return (
                          <div ref={readyToggleRef} style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: '6px' }}>
                            {/* Round 3 (08-02): the switch KEEPS its 36×20 look but the
                                button is a transparent 48×44 hit area around it — this is
                                the most time-critical control in comp and was the smallest
                                (roster margins absorb the extra box; visual unchanged). */}
                            <button
                              onClick={() => {
                                if (isFull) return;
                                if (onReadyInteract) onReadyInteract();
                                if (onReadyUp) onReadyUp();
                              }}
                              disabled={isFull}
                              title={isFull ? `Game is full (${maxReady}/${maxReady} players)` : undefined}
                              style={{
                                width: '48px',
                                height: '44px',
                                margin: '-12px -6px',
                                background: 'transparent',
                                border: 'none',
                                padding: 0,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: isFull ? 'not-allowed' : 'pointer',
                                flexShrink: 0,
                              }}
                            >
                              <div style={{
                                position: 'relative',
                                width: '36px',
                                height: '20px',
                                borderRadius: '10px',
                                backgroundColor: isReady ? '#4ade80' : isFull ? 'rgba(100, 100, 100, 0.3)' : 'rgba(150, 150, 150, 0.4)',
                                opacity: isFull ? 0.5 : 1,
                                transition: 'background-color 0.3s',
                                animation: (!isReady && showElementPulse) ? 'elementAttentionPulse 1.6s ease-in-out 4' : 'none',
                              }}>
                                <div style={{
                                  position: 'absolute',
                                  top: '2px',
                                  left: isReady ? '18px' : '2px',
                                  width: '16px',
                                  height: '16px',
                                  borderRadius: '50%',
                                  backgroundColor: '#fff',
                                  transition: 'left 0.3s',
                                  boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
                                }} />
                              </div>
                            </button>
                            {showReadyHint && !isReady && (
                              <div style={{
                                position: 'absolute',
                                top: '100%',
                                right: 0,
                                marginTop: '4px',
                                padding: '6px 20px 6px 8px',
                                borderRadius: '6px',
                                background: `linear-gradient(135deg, ${THEME.accent || '#667eea'}, ${THEME.primary || '#764ba2'})`,
                                color: '#fff',
                                fontSize: '0.65em',
                                fontWeight: 600,
                                whiteSpace: 'nowrap',
                                zIndex: 10,
                                border: '2px solid rgba(255,255,255,0.5)',
                                animation: 'hintPulseGlow 1.5s ease-in-out infinite',
                              }}>
                                <span onClick={(e) => { e.stopPropagation(); onReadyInteract(); }} style={{ position: 'absolute', top: isMobile ? '-4px' : '-2px', right: isMobile ? '1px' : '3px', cursor: 'pointer', fontSize: isMobile ? '1.6em' : '1.3em', opacity: 1, lineHeight: 1, fontWeight: 'bold', textShadow: '0 1px 2px rgba(0,0,0,0.3)', padding: isMobile ? '4px' : 0 }}>&times;</span>
                                Toggle to ready up!
                              </div>
                            )}
                          </div>
                        );
                      })()
                    ) : (
                      // Other human players' ready status
                      readyCheckData?.ready_player_names?.includes(user.name) ? (
                        <span style={{ color: '#4ade80', fontSize: '0.75em', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <span style={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: '#4ade80', display: 'inline-block' }} />
                          Ready
                        </span>
                      ) : (
                        <span style={{ color: THEME.textOnPanelMuted, fontSize: '0.75em', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <span style={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: THEME.textOnPanelMuted, display: 'inline-block' }} />
                          Waiting
                        </span>
                      )
                    )}
                  </div>
                ) : (
                  <div style={{
                    fontSize: '0.75em',
                    fontWeight: 800,
                    color: isLeader ? accent : THEME.textOnPanelMuted,
                    position: 'relative',
                    zIndex: 1,
                    minWidth: '36px',
                    textAlign: 'right',
                    flexShrink: 0,
                    whiteSpace: 'nowrap',
                  }}>
                    {score} pts
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {gameId && <AddAgentInline gameId={gameId} THEME={THEME} />}
      </div>
    );
  }

  // ── DEFAULT MODE: Original avatar grid ──
  // Non-competitive chat spaces swap the circle-avatar tiles for the Tea tab's
  // polaroid character cards (Nina 07-12: "we don't want the tiny circle
  // design"). Competitive/imposter kept the badge grid above; non-chat game
  // types (no chatGameStatus) keep the original grid with role badges.
  const teaCardMode = !!chatGameStatus && !chatGameStatus.competitive_mode && !chatGameStatus.imposter_mode;
  // Tea cards are big — the panel collapses to 2 cards by default in EVERY
  // layout (Nina 07-12), not just the compact sidebar; the +N more toggle
  // renders wherever collapsing applies.
  const collapsible = compactMode || teaCardMode;
  // The host's polaroid leads the panel in tea-card mode and counts toward
  // the 2-card collapsed budget (user 07-15: collapsed showed 3 cards).
  // 08-06 (Nina): MOBILE ONLY — desktop/narrow desktop don't need the mod
  // as one of the cards (the host already fronts the room everywhere else).
  const hostCardShown = teaCardMode && isMobile && !!chatGameStatus?.moderator_character && !chatGameStatus?.is_ported;
  const collapsedBudget = hostCardShown ? 1 : 2;
  const visibleUsers = collapsible && !playersExpanded ? sortedUsers.slice(0, collapsedBudget) : sortedUsers;
  const hiddenCount = collapsible && !playersExpanded ? Math.max(0, sortedUsers.length - collapsedBudget) : 0;

  return (
    <div style={{
      backgroundColor: THEME.bgMedium,
      padding: '8px',
      borderRadius: '6px',
      boxShadow: `0 4px 8px rgba(0,0,0,0.3), 0 0 10px ${THEME.accent}10`,
      width: '100%',
      boxSizing: 'border-box',
      border: `1px solid ${THEME.accent}25`,
    }}>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        marginBottom: '8px',
        gap: '8px',
        padding: '6px 10px',
        background: `linear-gradient(135deg, ${THEME.accent}15, ${THEME.primary}15)`,
        borderLeft: `3px solid ${THEME.accent}`,
        borderRadius: '4px',
      }}>
        <span style={{
          color: THEME.primary,
          fontWeight: 'bold',
          fontSize: '0.95em',
          textTransform: 'uppercase',
          letterSpacing: '1px'
        }}>
          {/* Non-comp spaces: the roster IS the locals (user call 07-15);
              competitive keeps the theme's location name (Chat Room etc.) */}
          {teaCardMode ? 'The Locals' : THEME.locationName}
        </span>
        {(chatGameStatus?.competitive_mode || chatGameStatus?.imposter_mode) && (
          <span style={{
            color: THEME.textMuted,
            fontSize: '0.8em'
          }}>
            ({sortedUsers.filter(u => isUserAlive(u)).length} alive / {sortedUsers.length} total)
          </span>
        )}
        {collapsible && sortedUsers.length > collapsedBudget && (
          <button
            onClick={() => setPlayersExpanded(!playersExpanded)}
            style={{
              marginLeft: 'auto',
              background: `${THEME.accent}14`,
              border: 'none',
              // raw accent vanished on dark themes (user report 07-10)
              color: THEME.textOnPanel || THEME.accent,
              cursor: 'pointer',
              fontSize: '0.75em',
              fontWeight: 600,
              padding: '6px 10px', // 28px touch floor (audit 07-15: was 19px tall)
              borderRadius: '4px',
              transition: 'background-color 0.2s',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = `${THEME.accent}30`; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = `${THEME.accent}14`; }}
          >
            {playersExpanded ? 'Show less ▴' : `+${hiddenCount} more ▾`}
          </button>
        )}
      </div>

      {/* Variant A: the host's compact LIVE row leads the section (the page's
          lower ModeratorPanel hides on phones — this is its new home) */}
      {isMobile && teaCardMode && chatGameStatus?.moderator_character && !chatGameStatus?.is_ported && hostLiveVariant === 'a' && (
        <div style={{ marginBottom: '8px' }}>
          <ModeratorPanel moderatorCharacter={chatGameStatus.moderator_character} THEME={THEME} isMobile />
        </div>
      )}
      <div style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: teaCardMode ? '10px' : '6px',
        ...(teaCardMode ? { padding: '4px 2px' } : {}),
        ...(collapsible && playersExpanded ? { maxHeight: teaCardMode ? '330px' : '240px', overflowY: 'auto' } : {}),
      }}>
        {/* The host is a local too — their polaroid leads the panel, same as
            the Tea tab's Locals strip (user call 07-12). 08-06 (Nina): gated
            to MOBILE — on desktop/narrow desktop the host polaroid is off
            the cards; hostCardShown above mirrors this for the collapse
            budget. */}
        {hostCardShown && (() => {
          const host = chatGameStatus.moderator_character;
          return (
            <div
              onClick={() => setShowHostCard(true)}
              title={`${host.name} — your host. Tap for their story!`}
              style={{
                flexShrink: 0, width: 110, height: 148, borderRadius: '12px',
                border: `1px solid ${THEME.accent}45`,
                position: 'relative', overflow: 'hidden', cursor: 'pointer',
                backgroundColor: `${THEME.accent}10`,
                transform: 'rotate(1.4deg)',
                boxShadow: '0 5px 16px rgba(0,0,0,0.28)',
              }}
            >
              <div style={{
                position: 'absolute', top: 0, left: 0, right: 0, zIndex: 1,
                background: 'linear-gradient(180deg, rgba(0,0,0,0.78) 0%, rgba(0,0,0,0.4) 70%, transparent 100%)',
                padding: '5px 7px 12px',
                fontSize: '0.56em', fontWeight: 800, color: THEME.accentOnPanel || THEME.accent,
                textShadow: '0 1px 2px rgba(0,0,0,0.7)', letterSpacing: '1px', textTransform: 'uppercase',
                display: 'flex', alignItems: 'center', gap: 4,
              }}>
                <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>🎙️ Your Host</span>
                {/* Variant B: the LIVE pill lives on the card itself */}
                {isMobile && hostLiveVariant === 'b' && (
                  <span style={{
                    padding: '1px 5px', borderRadius: '3px', flexShrink: 0,
                    backgroundColor: '#ef4444', color: '#fff',
                    fontSize: '0.9em', fontWeight: 800, letterSpacing: '0.5px',
                    animation: 'liveBadgePulse 2s ease-in-out infinite',
                  }}>
                    LIVE
                  </span>
                )}
              </div>
              {host.profile_image_url ? (
                <img src={host.profile_image_url} alt={host.name} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
              ) : (
                <div className="host-portrait-materializing" style={{
                  position: 'absolute', inset: 0, overflow: 'hidden',
                  background: `linear-gradient(160deg, ${THEME.accent}45, ${THEME.accent}10)`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '2.6em',
                }}>{host.avatar_emoji || '🎙️'}</div>
              )}
              <div style={{
                position: 'absolute', bottom: 0, left: 0, right: 0,
                background: 'linear-gradient(0deg, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.45) 65%, transparent 100%)',
                padding: '18px 9px 8px',
              }}>
                <div style={{ fontSize: '0.72em', fontWeight: 700, color: '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textShadow: '0 1px 3px rgba(0,0,0,0.6)' }}>{host.name}</div>
                {/* Variant B: rotating host status replaces the static caption */}
                {isMobile && hostLiveVariant === 'b' && host.status_messages?.length ? (
                  <div key={hostStatusIdx} style={{
                    marginTop: '3px', fontSize: '0.52em', fontStyle: 'italic',
                    color: 'rgba(255,255,255,0.75)',
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    animation: 'moderatorFadeIn 0.5s ease-out',
                  }}>{host.status_messages[hostStatusIdx % host.status_messages.length]}</div>
                ) : (
                  <div style={{ marginTop: '3px', fontSize: '0.52em', color: 'rgba(255,255,255,0.6)' }}>runs the room · tap for story</div>
                )}
              </div>
            </div>
          );
        })()}
        {visibleUsers.map((userObject, index) => {
          const user = userObject;
          const modelColor = getModelColor(user.model, THEME);

          const getAlignmentColor = () => {
            if (user.data?.alignment === 'evil') return THEME.evil;
            if (user.data?.alignment === 'good') return THEME.good;
            if (user.data?.role === 'storyteller') return THEME.accent;
            return THEME.neutral;
          };

          const getRoleDisplay = () => {
            const role = user.data?.true_role || user.data?.role || user.data?.perceived_role;
            if (!role) return '';
            return role.charAt(0).toUpperCase() + role.slice(1).replace(/_/g, ' ');
          };

          const isStoryteller = user.data?.role === 'storyteller' || user.data?.is_storyteller;
          if (isStoryteller) {
            return (<></>);
          }

          const isSpeaking = chatGameStatus?.next_speaker?.player_id === user.game_player_id;

          // Tea-style polaroid card — same design language as the Locals strip
          // on the Tea tab (full-bleed portrait, bottom scrim with name/model,
          // slight scrapbook tilt). The current user's caption reads (YOU).
          if (teaCardMode) {
            const isYou = humanPlayerId && user.game_player_id === humanPlayerId;
            // 🤖 An external agent seat is is_human:true on purpose (see
            // utils/playerKind) — captioning it "Human" was the visible half
            // of that. It gets its model + the visiting marker instead; the
            // NAME above is already the one the agent picked at join.
            const modelLabel = isYou
              ? 'YOU'
              : isExternalAgent(user)
                ? (agentCaption(user, { compact: true }) || '🤖 visiting')
                : isRealHuman(user) || user.model === 'Human Player'
                  ? 'Human'
                  : getDisplayModelName(user);
            return (
              <div
                key={index}
                // 🔌 09-28 agent wave: a watch link's ?with=<seat id> outlines this card
                data-seat-chip={user.game_player_id || user.id}
                onClick={() => setCastCardUser(user)}
                title={isYou ? `${user.name} — that's you! Tap for your card` : `${user.name} — tap for character card`}
                style={{
                  flexShrink: 0,
                  width: 110,
                  height: 148,
                  borderRadius: '12px',
                  border: isSpeaking ? '2px solid #4ade80' : `1px solid ${THEME.accent}25`,
                  position: 'relative',
                  overflow: 'hidden',
                  cursor: 'pointer',
                  backgroundColor: `${THEME.accent}10`,
                  transform: `rotate(${[-2, 1.6, -1.2, 2, -0.8][index % 5]}deg)`,
                  boxShadow: isSpeaking
                    ? '0 0 8px rgba(74, 222, 128, 0.6), 0 0 16px rgba(74, 222, 128, 0.3)'
                    : '0 5px 16px rgba(0,0,0,0.28)',
                  opacity: !isUserAlive(user) ? 0.7 : 1,
                  animation: isSpeaking ? 'speakingPulse 2s ease-in-out infinite' : 'none',
                  transition: 'border-color 0.3s, box-shadow 0.3s',
                }}
              >
                {/* Full-bleed portrait (falls back to a big initial on a gradient) */}
                {user.profile_image_url && !user.profile_image_url.startsWith('data:image/svg') ? (
                  <img src={user.profile_image_url} alt={user.name} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
                ) : (
                  <div style={{
                    position: 'absolute', inset: 0,
                    background: `linear-gradient(160deg, ${THEME.accent}45, ${THEME.accent}10)`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '2.6em', fontWeight: 800, color: THEME.textOnPanel || '#fff', opacity: 0.85,
                  }}>{(user.name || '?').charAt(0).toUpperCase()}</div>
                )}
                <div style={{
                  position: 'absolute', bottom: 0, left: 0, right: 0,
                  background: 'linear-gradient(0deg, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.45) 65%, transparent 100%)',
                  padding: '18px 9px 8px',
                }}>
                  <div style={{ fontSize: '0.72em', fontWeight: 700, color: '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textShadow: '0 1px 3px rgba(0,0,0,0.6)' }}>{user.name}</div>
                  <div style={{ fontSize: '0.52em', color: isYou ? '#ffc107' : 'rgba(255,255,255,0.72)', fontWeight: isYou ? 800 : 400, marginTop: '1px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    ({modelLabel})
                  </div>
                  {/* 🤖 Third line, agents only (Nina 08-15: name, then model,
                      then "a visiting agent identifier of some sort"). Its own
                      row rather than appended to the model line — this card is
                      110px wide, and one line carrying both ellipsized the
                      model slug down to nothing. */}
                  {isExternalAgent(user) && !isYou && (
                    <div style={{
                      fontSize: '0.48em', fontWeight: 700, letterSpacing: '0.04em',
                      color: 'rgba(196,181,253,0.92)', marginTop: '1px',
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}>
                      🤖 visiting agent
                    </div>
                  )}
                  {/* 🌙☀️ Status RESTORED to the card (Nina 08-10: "let's add
                      back the statuses") — the 08-08 move to the tap-open
                      card only left the rail mute. Both carry it now.
                      Single line: the card is only 148px. */}
                  <LocalStatusLine
                    statuses={localStatuses}
                    id={user.game_player_id}
                    altId={user.id}
                    isHuman={isRealHuman(user) || user.model === 'Human Player'}
                  />
                </div>
              </div>
            );
          }

          return (
            <div key={index} style={{
              backgroundColor: THEME.bgLight,
              border: isSpeaking
                ? '2px solid #4ade80'
                : `2px solid ${
                    isStoryteller ? THEME.accent :
                    !isUserAlive(user) ? THEME.dead :
                    getAlignmentColor()
                  }`,
              borderRadius: '4px',
              padding: '4px',
              position: 'relative',
              width: '110px',
              opacity: !isUserAlive(user) ? 0.7 : 1,
              boxShadow: isSpeaking
                ? '0 0 8px rgba(74, 222, 128, 0.6), 0 0 16px rgba(74, 222, 128, 0.3)'
                : isStoryteller ? `0 0 10px ${THEME.accent}`
                : !isUserAlive(user) ? 'none' : `0 2px 4px rgba(0,0,0,0.2)`,
              animation: isSpeaking ? 'speakingPulse 2s ease-in-out infinite' : 'none',
              transition: 'border-color 0.3s, box-shadow 0.3s',
            }}>
              {/* Role Badge - Primary display */}
              <div style={{
                position: 'absolute',
                top: '2px',
                right: '2px',
                display: 'flex',
                flexDirection: 'column',
                gap: '1px',
                alignItems: 'flex-end'
              }}>
                {getRoleDisplay() && (
                  <span style={{
                    backgroundColor: getAlignmentColor(),
                    color: THEME.textLight,
                    padding: '1px 3px',
                    borderRadius: '2px',
                    fontSize: '0.6em',
                    fontWeight: 'bold',
                    textTransform: 'capitalize'
                  }}>
                    {getRoleDisplay()}
                  </span>
                )}
                {user.data.is_drunk && (
                  <span style={{
                    backgroundColor: THEME.drunk,
                    color: THEME.textDark,
                    padding: '1px 3px',
                    borderRadius: '2px',
                    fontSize: '0.55em',
                    fontWeight: 'bold'
                  }}>
                    Drunk
                  </span>
                )}
                {user.data.is_poisoned && (
                  <span style={{
                    backgroundColor: THEME.poisoned,
                    color: THEME.textLight,
                    padding: '1px 3px',
                    borderRadius: '2px',
                    fontSize: '0.55em',
                    fontWeight: 'bold'
                  }}>
                    Poisoned
                  </span>
                )}
                {user.data.is_hoh && (
                  <span style={{
                    backgroundColor: THEME.good,
                    color: THEME.textLight,
                    padding: '1px 3px',
                    borderRadius: '2px',
                    fontSize: '0.6em',
                    fontWeight: 'bold'
                  }}>
                    HOH
                  </span>
                )}
                {user.data.has_veto && (
                  <span style={{
                    backgroundColor: THEME.primary,
                    color: THEME.textLight,
                    padding: '1px 3px',
                    borderRadius: '2px',
                    fontSize: '0.6em',
                    fontWeight: 'bold'
                  }}>
                    Veto
                  </span>
                )}
                {user.data.is_nominated && (
                  <span style={{
                    backgroundColor: THEME.evil,
                    color: THEME.textLight,
                    padding: '1px 3px',
                    borderRadius: '2px',
                    fontSize: '0.6em',
                    fontWeight: 'bold'
                  }}>
                    {user.data.is_replacement_nominee ? 'Replacement Nominee' : 'Nominated'}
                  </span>
                )}
                {user.data.was_nominated && (
                  <span style={{
                    backgroundColor: THEME.accent,
                    color: THEME.textDark,
                    padding: '1px 3px',
                    borderRadius: '2px',
                    fontSize: '0.6em',
                    fontWeight: 'bold'
                  }}>
                    Saved
                  </span>
                )}
                {user.data.is_evicted && (
                  <span style={{
                    backgroundColor: THEME.textMuted,
                    color: THEME.textLight,
                    padding: '1px 3px',
                    borderRadius: '2px',
                    fontSize: '0.6em',
                    fontWeight: 'bold'
                  }}>
                    Evicted
                  </span>
                )}
                {!isUserAlive(user) && (
                  <span style={{
                    backgroundColor: THEME.evil,
                    color: THEME.textLight,
                    padding: '1px 3px',
                    borderRadius: '2px',
                    fontSize: '0.6em',
                    fontWeight: 'bold'
                  }}>
                    Dead
                  </span>
                )}
                {user.data.used_dead_vote && (
                  <span style={{
                    backgroundColor: THEME.textDark,
                    color: THEME.textLight,
                    padding: '1px 3px',
                    borderRadius: '2px',
                    fontSize: '0.6em',
                    fontWeight: 'bold'
                  }}>
                    NDV
                  </span>
                )}
                {isUserEliminated(user) && (
                  <span style={{
                    backgroundColor: THEME.textMuted,
                    color: THEME.textLight,
                    padding: '1px 3px',
                    borderRadius: '2px',
                    fontSize: '0.6em',
                    fontWeight: 'bold'
                  }}>
                    Spectator
                  </span>
                )}
              </div>

              {/* Profile Image and Name */}
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '4px'
              }}>
                <ProfileImage
                  user={user}
                  size={40}
                  borderColor={modelColor}
                />
                <div style={{
                  backgroundColor: modelColor,
                  padding: '2px 4px',
                  borderRadius: '3px',
                  color: THEME.textDark,
                  fontWeight: 'bold',
                  fontSize: '0.75em',
                  wordBreak: 'break-word',
                  textAlign: 'center',
                  width: '100%',
                  lineHeight: '1.2'
                }}>
                  {user.name}
                </div>
                <div style={{
                  fontSize: '0.65em',
                  color: THEME.textMuted,
                  textAlign: 'center'
                }}>
                  ({getDisplayModelName(user)})
                </div>
                {/* 🌙☀️ Status restored on the compact grid variant too
                    (Nina 08-10), so the two card shapes never disagree. */}
                <LocalStatusLine
                  statuses={localStatuses}
                  id={user.game_player_id}
                  altId={user.id}
                  isHuman={isRealHuman(user) || user.model === 'Human Player'}
                  center
                  clamp="two"
                />
              </div>
            </div>
          );
        })}
      </div>

      {/* Host story popup — same content as the Tea tab's host card.
          🪟 Ported spaces have no visible host (Nina 08-28) — the resident
          is the face of the space; existing ported rows may still carry a
          moderator_character, so the gate is explicit, not data-implied. */}
      {showHostCard && chatGameStatus?.moderator_character && !chatGameStatus?.is_ported && (() => {
        const host = chatGameStatus.moderator_character;
        return (
          <div
            onClick={() => setShowHostCard(false)}
            style={{
              position: 'fixed', inset: 0, zIndex: 3200,
              backgroundColor: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(2px)', WebkitBackdropFilter: 'blur(2px)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px',
            }}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                width: '360px', maxWidth: '92vw', maxHeight: '80dvh', overflowY: 'auto',
                borderRadius: '16px', position: 'relative',
                backgroundColor: THEME.bgMedium || '#1a1a2e',
                border: `1px solid ${THEME.accent}50`,
                boxShadow: `0 12px 48px rgba(0,0,0,0.6), 0 0 24px ${THEME.accent}20`,
                padding: '20px',
              }}
            >
              <button
                onClick={() => setShowHostCard(false)}
                aria-label="Close host bio"
                style={{ position: 'absolute', top: '10px', right: '12px', background: 'none', border: 'none', color: THEME.textMuted || '#888', cursor: 'pointer', fontSize: '1em', lineHeight: 1, padding: '4px' }}
              >✕</button>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '14px' }}>
                {host.profile_image_url ? (
                  <img src={host.profile_image_url} alt={host.name} style={{
                    width: 52, height: 52, borderRadius: '50%', objectFit: 'cover', flexShrink: 0,
                    border: `2px solid ${THEME.accent}60`,
                  }} />
                ) : (
                  <div className="host-portrait-materializing" style={{
                    width: 52, height: 52, borderRadius: '50%', flexShrink: 0,
                    position: 'relative', overflow: 'hidden',
                    background: `linear-gradient(135deg, ${THEME.accent}, ${THEME.primary || '#6366f1'})`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.5em',
                  }}>
                    {host.avatar_emoji || '🎙️'}
                  </div>
                )}
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: '0.95em', fontWeight: 700, color: THEME.textOnPanel || '#fff' }}>{host.name}</div>
                  <div style={{ fontSize: '0.62em', fontWeight: 700, color: THEME.accentOnPanel || THEME.accent, textTransform: 'uppercase', letterSpacing: '1.2px', marginTop: '2px' }}>Your Host</div>
                </div>
              </div>
              {host.catchphrase && (
                <div style={{ fontSize: '0.78em', color: THEME.accentOnPanel || THEME.accent, fontStyle: 'italic', marginBottom: '10px' }}>
                  "{host.catchphrase}"
                </div>
              )}
              {(host.origin_story || host.personality) && (
                <div style={{ fontSize: '0.84em', color: THEME.textMuted || '#aaa', lineHeight: 1.65 }}>
                  {host.origin_story || host.personality}
                </div>
              )}
              {Array.isArray(host.personality_traits) && host.personality_traits.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px', marginTop: '12px' }}>
                  {host.personality_traits.map((t, i) => (
                    <span key={i} style={{
                      fontSize: '0.62em', fontWeight: 600, padding: '3px 9px', borderRadius: '10px',
                      backgroundColor: `${THEME.accent}18`, color: THEME.accentOnPanel || THEME.accent, border: `1px solid ${THEME.accent}30`,
                    }}>{t}</span>
                  ))}
                </div>
              )}
            </div>
          </div>
        );
      })()}

      {/* Character card popup — dossier core (portrait, bio, traits, mystery
          gate). The Tea tab's popup layers analytics (vibe, awards, ships) on
          top of this same shape; those need message history the panel doesn't
          hold, so this stays the lightweight sibling. */}
      {castCardUser && (() => {
        const cu = castCardUser;
        const isYou = humanPlayerId && cu.game_player_id === humanPlayerId;
        const facts = cu.bio_facts || cu.data?.bio_facts || [];
        const isMystery = !chatGameStatus?.is_ported && !cu.is_human && facts.length > 0 && dossierRevealCount(gameId, cu.id) === 0;
        const bio = isMystery ? null : (cu.character_description || cu.data?.character_description || null);
        const traits = isMystery ? [] : (cu.personality_traits || cu.data?.personality_traits || []);
        const portrait = cu.profile_image_url && !cu.profile_image_url.startsWith('data:image/svg') ? cu.profile_image_url : null;
        return (
          <div
            onClick={() => setCastCardUser(null)}
            style={{
              position: 'fixed', inset: 0, zIndex: 3200,
              backgroundColor: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(2px)', WebkitBackdropFilter: 'blur(2px)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px',
            }}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                width: '420px', maxWidth: '92vw', maxHeight: '86dvh',
                overflowX: 'hidden', overflowY: 'auto',
                borderRadius: '16px', position: 'relative',
                backgroundColor: THEME.bgMedium || '#1a1a2e',
                border: `1px solid ${THEME.accent}50`,
                boxShadow: '0 12px 48px rgba(0,0,0,0.6)',
              }}
            >
              <button
                onClick={() => setCastCardUser(null)}
                aria-label="Close character card"
                style={{
                  position: 'absolute', top: '10px', right: '12px', zIndex: 2,
                  background: 'rgba(0,0,0,0.4)', border: 'none', borderRadius: '50%',
                  width: '26px', height: '26px', color: '#fff', cursor: 'pointer',
                  fontSize: '0.85em', lineHeight: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}
              >✕</button>
              {/* Portrait header — objectPosition favors the top of the art so
                  faces don't get cropped out */}
              <div style={{ position: 'relative', height: '230px' }}>
                {portrait ? (
                  <img src={portrait} alt={cu.name} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center 12%' }} />
                ) : (
                  <div style={{
                    position: 'absolute', inset: 0,
                    background: `linear-gradient(160deg, ${THEME.accent}50, ${THEME.accent}12)`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '4em', fontWeight: 800, color: THEME.textOnPanel || '#fff', opacity: 0.85,
                  }}>{(cu.name || '?').charAt(0).toUpperCase()}</div>
                )}
                <div style={{
                  position: 'absolute', bottom: 0, left: 0, right: 0,
                  background: 'linear-gradient(0deg, rgba(0,0,0,0.8) 0%, rgba(0,0,0,0.4) 60%, transparent 100%)',
                  padding: '28px 16px 12px',
                }}>
                  <div style={{ fontSize: '1.05em', fontWeight: 800, color: '#fff', textShadow: '0 1px 3px rgba(0,0,0,0.6)' }}>
                    {cu.name}
                    {isYou && <span style={{ fontSize: '0.6em', backgroundColor: 'rgba(255,255,255,0.25)', color: '#fff', padding: '2px 6px', borderRadius: '4px', fontWeight: 700, marginLeft: '6px', verticalAlign: 'middle' }}>YOU</span>}
                  </div>
                  {/* 🌙☀️ What they're up to — moved here from the small card
                      (Nina 08-08: "can we have the captions on the cards AFTER
                      the user clicks"). On a 110px polaroid the caption was
                      competing with the name and the model slug for the same
                      two lines of scrim; in the modal it has room to be read.
                      Two-line clamp: this card is not width-starved. */}
                  <LocalStatusLine
                    statuses={localStatuses}
                    id={cu.game_player_id}
                    altId={cu.id}
                    isHuman={isRealHuman(cu) || cu.model === 'Human Player'}
                    clamp="two"
                  />
                  {cu.model && cu.model !== 'Human Player' && (
                    <div style={{ fontSize: '0.62em', color: 'rgba(255,255,255,0.75)', marginTop: '4px' }}>{getDisplayModelName(cu)}</div>
                  )}
                  {/* 🤖 The full caption has room here (Nina 08-15): the name
                      above, the model on the line above this, then what it is. */}
                  {isExternalAgent(cu) && (
                    <div style={{ fontSize: '0.6em', fontWeight: 700, color: 'rgba(196,181,253,0.9)', marginTop: '3px' }}>
                      🤖 visiting agent
                    </div>
                  )}
                </div>
              </div>
              <div style={{ padding: '14px 16px 18px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {bio && (
                  <div style={{ fontSize: '0.86em', color: THEME.textOnPanel || THEME.textDark, opacity: 0.85, lineHeight: 1.65 }}>
                    {bio}
                  </div>
                )}
                {isMystery && (
                  <div style={{ fontSize: '0.86em', color: THEME.textOnPanel || THEME.textDark, opacity: 0.9, lineHeight: 1.65, fontStyle: 'italic' }}>
                    ❓ This local is a mystery to me! Hang out with them to find out more.
                  </div>
                )}
                {Array.isArray(traits) && traits.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px' }}>
                    {traits.map((t, i) => (
                      <span key={i} style={{
                        fontSize: '0.7em', fontWeight: 600, padding: '3px 10px', borderRadius: '10px',
                        backgroundColor: `${THEME.accent}18`, color: THEME.accentOnPanel || THEME.accent, border: `1px solid ${THEME.accent}30`,
                      }}>{t}</span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {/* ⏳ Guests whose stay has ended — desktop rail AND the mobile Locals
          place, since both mount this same panel. `allUsers` (not `users`) is
          the unfiltered roster: `users` here is activeUsers, which has already
          dropped them. Renders nothing when no visitor has ever expired, so
          spaces that never hosted a guest are untouched. */}
      <PastVisitors allUsers={allUsers} THEME={THEME} style={{ padding: '2px 4px 6px' }} />

      {gameId && <AddAgentInline gameId={gameId} THEME={THEME} />}
    </div>
  );
};

// 🎵 The legacy "emotion songs" editor (old non-chat game types only): HIDDEN,
// not removed (Nina 09-26). Its save rides `?updateGameData`, admin-only since
// 09-26, so the button would only fail for anyone without the key. The code stays.
const SHOW_EMOTION_SONGS_EDITOR = false;
// 🔒 09-29 batch 2 (the review): a PRIVATE port's socket doors (joinGame, startPlaying) need a proven seat now, so those
// two messages carry this browser's stored secret for the seat they name. Anything else goes out as it was.
function withSeatProof(data) {
  try {
    const m = typeof data === 'string' ? JSON.parse(data) : null;
    if (m && (m.action === 'joinGame' || m.action === 'startPlaying') && m.playerId && !m.playerSecret) {
      const secret = chatGameApi.getPlayerSecret(m.playerId);
      if (secret) return JSON.stringify({ ...m, playerSecret: secret });
    }
  } catch (e) { /* not JSON: sent as it was */ }
  return data;
}
const GameStepPage = ({ initialGameType } = {}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [results, setResults] = useState([]);
  const [users, setUsers] = useState([]);
  // Active users: humans show only while actually present. The backend marks
  // is_connected from live WS connection rows (heartbeat-based), which catches
  // the dropped-$disconnect case that left every past visitor in the panel
  // forever; the disconnected_at grace still smooths brief drops, and payloads
  // without the field keep the old stamp-only behavior. Self + external agents
  // (API bots with no page WS) always show.
  // Message components still use `users` (all) for name resolution.
  const DISCONNECT_GRACE_MS = 30 * 1000;
  const selfPlayerIdParam = typeof window !== 'undefined' ? new URLSearchParams(window.location.search).get('playerId') : null;
  const activeUsers = React.useMemo(() => users.filter(u => {
    // ⏳ A visitor whose hour is up has left the room. Checked BEFORE the
    // external-agent pass-through below, which returns true unconditionally
    // and would otherwise keep expired guests in the roster forever.
    if (visitOver(u)) return false;
    if (!u.is_human || u.is_external_agent || u.data?.is_external_agent) return true;
    if (selfPlayerIdParam && (u.game_player_id === selfPlayerIdParam || u.id === selfPlayerIdParam)) return true;
    const withinGrace = u.data?.disconnected_at
      ? (Date.now() - new Date(u.data.disconnected_at).getTime()) < DISCONNECT_GRACE_MS
      : false;
    if (u.is_connected === false) return withinGrace;
    if (!u.data?.disconnected_at) return true;
    return withinGrace;
  }), [users, selfPlayerIdParam]);
  // 👥 Stream-panel roster (Nina 08-06: "40 in this space" was EVERY human
  // ever — the panel got raw `users`): same shape as activeUsers but humans
  // keep a seat for an HOUR after disconnect instead of 30s — the room reads
  // alive without parading its entire guestbook. AI cast + external agents
  // always count. Also feeds the masthead count so both surfaces agree.
  const STREAM_PRESENCE_WINDOW_MS = 60 * 60 * 1000;
  const streamPanelUsers = React.useMemo(() => users.filter(u => {
    // ⏳ Same rule as activeUsers: a departed visitor is not "in this space",
    // so it must not inflate the count the panel and masthead both show.
    if (visitOver(u)) return false;
    if (!u.is_human || u.is_external_agent || u.data?.is_external_agent) return true;
    if (selfPlayerIdParam && (u.game_player_id === selfPlayerIdParam || u.id === selfPlayerIdParam)) return true;
    const withinHour = u.data?.disconnected_at
      ? (Date.now() - new Date(u.data.disconnected_at).getTime()) < STREAM_PRESENCE_WINDOW_MS
      : false;
    if (u.is_connected === false) return withinHour;
    if (!u.data?.disconnected_at) return true;
    return withinHour;
  }), [users, selfPlayerIdParam]);
  const [playerModelMap, setPlayerModelMap] = useState({});
  const [gameId, setGameId] = useState(searchParams.get('gameId') || '');
  // When rendered via SpaceViewPage (/space/:id), initialGameType is 'chat-game' so
  // WebSocket connects immediately on mount — before background tasks (ghost chat,
  // preseed) broadcast their results.  Without this, WS connects after the first
  // REST fetch resolves gameType, and those broadcasts are lost.
  const [gameType, setGameType] = useState(initialGameType || 'blood-on-the-clocktower');
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [pagination, setPagination] = useState(null);
  const [showQuestionModal, setShowQuestionModal] = useState(false);
  const [showVideoList, setShowVideoList] = useState(false);
  const [isStitcherExpanded, setIsStitcherExpanded] = useState(true);
  const [questionType, setQuestionType] = useState('general'); // 'general' or 'player' or 'all'
  const [question, setQuestion] = useState('');
  const [selectedPlayer, setSelectedPlayer] = useState('');
  const [hideGenericActions, setHideGenericActions] = useState(true);
  const [reverseChronological, setReverseChronological] = useState(false);
  const [showThoughts, setShowThoughts] = useState(false); // Show AI inner thoughts - off by default
  const [showBenchmarkCharts, setShowBenchmarkCharts] = useState(false);
  const [activeTab, setActiveTab] = useState('actions'); // 'actions', 'charts', 'videos', or 'summary'
  const [chatActionTab, setChatActionTab] = useState('all'); // 'all', 'messages', 'alerts', 'highlights'
  // ✨ Feed top band (Arc 3): Home's overview pinned at Feed's top, collapsible,
  // remembered per space. 📱 PHONES ONLY as of 08-07 — the drawer left the
  // desktop Room feed when its content moved to the Recap lane, but Recap is
  // unreachable on phones (strip is a zero-height anchor there, and the places
  // nav has no recap item), so removing it outright DELETED the story sections
  // on mobile instead of moving them. Same localStorage key either way.
  const [feedHomeOpen, setFeedHomeOpen] = useState(() => {
    // Collapsed by DEFAULT (Nina 08-01): the chat's activity is the first
    // read, not the recap band. An explicit open ('1') still sticks.
    try { return localStorage.getItem(`feedHomeOpen_${gameId}`) === '1'; } catch (e) { return false; }
  });
  const chatActionTabRef = useRef('all');
  useEffect(() => { chatActionTabRef.current = chatActionTab; }, [chatActionTab]);
  const [pinnedSidebarTab, setPinnedSidebarTab] = useState('chat'); // tab key pinned to left sidebar bottom (three-column only) — default = cast chat room (Nina 07-11: characters front and center; was 'media' pre-stage)
  const [livestreamExpanded, setLivestreamExpanded] = useState(true); // livestream dock — open by default (top of left sidebar)
  const [livestreamStatus, setLivestreamStatus] = useState({ participantCount: 0, trackCount: 0, streamingParticipants: [], isConnected: false }); // live status from LivestreamPanel
  const [liveWatchRequest, setLiveWatchRequest] = useState(null); // set by clicking a go-live ghost-chat message; LivestreamPanel consumes it and puts that streamer on stage
  const [spaceLiveStreamers, setSpaceLiveStreamers] = useState([]); // [{id, name}] publishing now — from status poll + livestream_update WS; works with the panel unmounted
  const [livestreamFloating, setLivestreamFloating] = useState(false); // livestream popped out of the sidebar dock into the floating huddle (mini-player)
  const [dossierUnseen, setDossierUnseen] = useState(0); // locals-dossier unlocks since last opened — badges the case-file folder tab
  const [dossierOpen, setDossierOpen] = useState(false); // 🕵️ case-file drawer slid out over the chat (replaced the Locals tab, 07-10)
  const [caseFileOpen, setCaseFileOpen] = useState(false); // 🗂 competitive Whodunnit case file (same drawer slot, different content — 07-11)
  const [caseFileUnseen, setCaseFileUnseen] = useState(0);
  const livestreamPinned = pinnedSidebarTab === 'livestream'; // stream owns the sidebar's pinned-tab slot (the tall one), like a pinned tab
  const centerColumnRef = useRef(null); // chat/tabs column — anchor for the floating player's "dock" spawn spot (pops out right of the chat panel)
  // Stable host node for the livestream panel. The panel portals into this
  // node ONCE; moving the node between the dock and the pinned slot is a raw
  // DOM appendChild that React never re-renders through — so the LiveKit
  // connection and video elements survive dock ↔ pinned-slot moves.
  const livestreamHostEl = useMemo(() => {
    if (typeof document === 'undefined') return null;
    const el = document.createElement('div');
    el.style.cssText = 'display:flex;flex-direction:column;flex:1 1 auto;min-height:0;height:100%;';
    return el;
  }, []);
  const livestreamDockSlotRef = useRef(null);
  const livestreamPinSlotRef = useRef(null);
  const livestreamTabSlotRef = useRef(null); // narrow-layout Live tab — third home for the host node, so crossing the 1100px boundary reparents instead of remounting
  // Stable host node for the watch-party theater — same pattern as
  // livestreamHostEl: the YouTube iframe survives stage ↔ tab ↔ off-stage
  // moves, so docking/undocking never resets playback. Off-stage the party
  // audio keeps rolling (it's a room, not a player).
  const watchPartyHostEl = useMemo(() => {
    if (typeof document === 'undefined') return null;
    const el = document.createElement('div');
    el.style.cssText = 'display:flex;flex-direction:column;min-height:0;';
    return el;
  }, []);
  const stageWpSlotRef = useRef(null);    // 🎭 stage slot (theater docked)
  const wpTabSlotRef = useRef(null);      // Watch Party tabpanel slot (undocked)
  const wpHiddenHolderRef = useRef(null); // off-stage holder — keeps the iframe alive
  const livestreamHiddenHolderRef = useRef(null); // 📱 places-nav off-screen holder — keeps an active stream's node in the document when no visible slot is mounted (same pattern as the WP holder)
  // 🏡 W2d: the place page's stage slots. A live watch party / stream opens
  // WHERE THE PAINTING IS: these join the head of the two reparent chains
  // below (one theater, one panel, moved never remounted).
  const placeWpSlotRef = useRef(null);
  const placeLiveSlotRef = useRef(null);
  const [leftSidebarWidth, setLeftSidebarWidth] = useState(320); // resizable left sidebar width
  const [rightSidebarWidth, setRightSidebarWidth] = useState(340); // resizable right sidebar width
  // 🎛️ SIDEBAR TOGGLES (Nina 09-15, canvas-ruled — utils/spaceLayout.js is the
  // map). Per-space per-device prefs; unset = follow the space_goal preset
  // (story: both collapsed · tea: room collapsed · else open). Loaded per
  // gameId so a client-side space switch re-reads the right space's prefs.
  const [layoutPrefs, setLayoutPrefs] = useState({});
  useEffect(() => { setLayoutPrefs(gameId ? loadLayoutPrefs(gameId) : {}); }, [gameId]);
  // 👻 unread-while-collapsed baseline: spectatingMessages.length at the
  // moment the ghost column was last seen. null = not yet anchored (anchors
  // once messages first load while collapsed, so history never counts).
  const ghostCollapseBaselineRef = useRef(null);
  const prevConnectedRef = useRef(false);
  const prevTrackCountRef = useRef(0);
  // Auto-expand livestream once connected, or when someone starts streaming
  useEffect(() => {
    if (livestreamStatus.isConnected && !prevConnectedRef.current) {
      setLivestreamExpanded(true);
    }
    if (livestreamStatus.trackCount > 0 && prevTrackCountRef.current === 0) {
      setLivestreamExpanded(true);
    }
    prevConnectedRef.current = livestreamStatus.isConnected;
    prevTrackCountRef.current = livestreamStatus.trackCount;
  }, [livestreamStatus.isConnected, livestreamStatus.trackCount]);
  const [tabDots, setTabDots] = useState({ chat: false, games: true }); // notification dots — games starts true until visited
  const [selectedRound, setSelectedRound] = useState('current'); // 'current' or round number (no 'all' option)
  const [showSpacesEditor, setShowSpacesEditor] = useState(false);
  const [generatingEpisode, setGeneratingEpisode] = useState(false);

  // Mobile detection for responsive layout — useViewport (round 5, 08-03):
  // the shared store replaces this component's own state + resize listener;
  // same names, same thresholds, so the ~1000 consumers below are untouched.
  const { isMobile, isNarrowDesktop, height: viewportH } = useViewport();
  // Effect closures still read the ref (stale-closure guard) — render-sync
  // it like the other mirror refs in this file.
  const isMobileRef = useRef(isMobile);
  isMobileRef.current = isMobile;
  const [sfxEnabled, setSfxEnabled] = useState(() => soundManager.isEnabled());

  // Initial loading state (from CustomSpace navigation)
  const [showInitialLoading, setShowInitialLoading] = useState(searchParams.get('loading') === 'true');
  const [initialLoadingProgress, setInitialLoadingProgress] = useState(0);
  const [initialLoadingMessage, setInitialLoadingMessage] = useState('Summoning the crew... 🌀');
  const [initialLoadingStep, setInitialLoadingStep] = useState(0);

  // 🪟🫁 Ported creation gets an ambient breathing loader, not the show's
  // progress theater (Nina 08-28). The settle-in stash (written by the import
  // wizard right before navigate) identifies a fresh port BEFORE the first
  // status poll lands — is_ported only arrives with status. Stash is scoped
  // to this gameId so an old stash from a previous port can't misfire.
  const portedSettleIn = useMemo(() => {
    try {
      const s = JSON.parse(localStorage.getItem('spaces_settle_in') || 'null');
      const gid = new URLSearchParams(window.location.search).get('gameId');
      return s && gid && s.game_id === gid ? s : null;
    } catch (e) { return null; }
  }, []);
  const PORTED_LOADER_LINES = ['making it theirs', 'warming the lamp', 'hanging a window', 'learning the room', 'almost there'];
  const [portedLoaderIdx, setPortedLoaderIdx] = useState(0);
  // 🪟🔖 Known-ported marker (Nina 09-24): the stash above only covers a FRESH
  // port, so a return visit got the generic party loader (✨ spinner, "Getting
  // the party started", 💡 trivia) until the first status landed. Every status
  // that says is_ported leaves a per-game note on this device (the writer sits
  // by isPortedSpace). Read ONCE per load: it bridges only the pre-status
  // window, and its presence means this device has been here before (return
  // tips). Carries the resident's name + the owner seat for t=0 copy.
  const portedKnownAtLoad = useMemo(() => {
    try { return readKnownPorted(new URLSearchParams(window.location.search).get('gameId')); } catch (e) { return null; }
  }, []);
  // One loader tip per load: the seed is fixed at mount and the shown line is
  // kept by id (pickPortedLoaderTip), so it never swaps mid-load.
  const portedTipSeedRef = useRef(Math.random());
  const portedTipIdRef = useRef(null);

  // Asset generation loading overlay state
  const [showAssetLoadingOverlay, setShowAssetLoadingOverlay] = useState(true); // Show by default when game loads
  const [assetLoadingReady, setAssetLoadingReady] = useState(false); // Assets done, waiting for user to click "Let's Go"
  const [assetLoadingStatus, setAssetLoadingStatus] = useState(null);
  const [assetLoadingMessage, setAssetLoadingMessage] = useState('Getting the party started... 🎉');

  // T10: this effect reads showAssetLoadingOverlay, so it lives BELOW that
  // declaration — a dep array evaluates at render and TDZ-white-pages the
  // whole route otherwise (caught by the render probe, sweep #106).
  useEffect(() => {
    if (!showAssetLoadingOverlay || !portedSettleIn) return undefined;
    const t = setInterval(() => setPortedLoaderIdx(i => i + 1), 4200);
    return () => clearInterval(t);
  }, [showAssetLoadingOverlay, portedSettleIn]);

  // Rotating loading hints: advance every 5s while the overlay is up. Cursor
  // wraps modulo the pool length so very long loads cycle before repeating.
  // ⚡ Slash-tip weave (Nina 07-26, comp fix same day): command hints get
  // every other slot until the slash pool runs dry, then the remaining
  // general tips play out — no within-load repeats, and the boost is
  // front-loaded exactly where short loads live. FREE-CHAT ONLY: commands
  // are blocked in competitive/imposter and non-chat game types, so those
  // loads weave COMP_ACTION_HINTS instead (comp mode is unknown for a beat
  // on chat spaces while status loads — we assume free-chat, and the sample
  // rebuilds general-first the moment the flag lands, so a comp player sees
  // at most the first hint or two of slash before the swap).
  // "/explore" and "/custom-space" are PAGE paths, not commands — general.
  // ⚠️ MUST stay ABOVE the loading-hint block below, which reads it during
  // render. It used to be declared ~260 lines further down; the 07-26 hint
  // weave then read it here and every render of a chat space threw
  // "Cannot access 'chatGameStatus' before initialization" (TDZ), white-paging
  // the whole /game-step route at every width. Same trap as the
  // effectiveIsSpectating note further down — a `const`/`useState` binding is
  // NOT hoisted like a function. Keep reads below the declaration.
  const [chatGameStatus, setChatGameStatus] = useState(null);

  // Tri-state: true/false = mode KNOWN, null = still unknown. gameType boots
  // as 'blood-on-the-clocktower' for EVERY space (nothing passes
  // initialGameType since the 08-11 /space redirect), so a bare `: true`
  // fallback here classified every loading non-comp space as competitive and
  // flashed a comp tip in slot 0 (Nina 08-28, "watch the token economy" on a
  // non-comp load). Until a status fetch resolves the mode, only mode-neutral
  // tips are safe promises.
  const isCompSpaceForHints = (gameType === 'chat-game' || gameType === 'chat')
    ? !!(chatGameStatus && (chatGameStatus.competitive_mode || chatGameStatus.imposter_mode))
    : (chatGameStatus ? true : null);
  // 🧭 10-02 (Nina: "Loading tips should match the type of space being created"):
  // a room made for a purpose gets its own tips (utils/purposeTips.js). The
  // status says which; before it lands, the build form's hop does (?purpose=,
  // BuildSpaceV2), so the very first tip after Build is already right.
  const hintPurpose = chatGameStatus ? (chatGameStatus.purpose || null) : purposeFromSearch(searchParams);
  const loadingHintSample = useMemo(() => {
    const shuffle = (arr) => [...arr].sort(() => Math.random() - 0.5);
    // 🧭 10-02: a purpose with its own pool shows only that pool (a study room
    // never teaches /ship). Purpose rooms are free chat by construction.
    const purposePool = isCompSpaceForHints === true ? null : purposeHintPool(hintPurpose);
    if (purposePool) return shuffle(purposePool);
    const isSlashTip = (h) =>
      /\/(ship|drama|talk|scene|imagine|build|gift|watch|watchparty|listen|aux|translate|tinker|code|note|combine|egg)\b/i.test(h)
      || /slash command|Type \//i.test(h);
    const isCompTip = (h) => /competitive mode|tokens are currency/i.test(h);
    const weave = (boost, general) => {
      if (!boost.length) return general;
      const out = [];
      const n = Math.max(boost.length, general.length);
      for (let i = 0; i < n; i++) {
        if (i < boost.length) out.push(boost[i]);
        if (i < general.length) out.push(general[i]);
      }
      return out;
    };
    if (isCompSpaceForHints === null) {
      // Mode unknown (pre-status): neither slash nor comp tips are safe
      // promises yet — general trivia only. The memo re-runs when the
      // tri-state resolves and the right boosted lane takes over.
      return shuffle(LOADING_HINTS.filter((h) => !isSlashTip(h) && !isCompTip(h)));
    }
    if (isCompSpaceForHints) {
      // Comp loads: no slash tips (dead promises there); comp-specific tips
      // take the boosted slots instead. COMP_ACTION_HINTS is the future home
      // for comp-action tips when those exist.
      const boost = shuffle([...COMP_ACTION_HINTS, ...LOADING_HINTS.filter(isCompTip)]);
      const general = shuffle(LOADING_HINTS.filter((h) => !isSlashTip(h) && !isCompTip(h)));
      return weave(boost, general);
    }
    const pool = [...LOADING_HINTS, ...NONCOMP_HINTS];
    const slash = shuffle(pool.filter(isSlashTip));
    const general = shuffle(pool.filter((h) => !isSlashTip(h)));
    return weave(slash, general);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCompSpaceForHints, hintPurpose]);
  const [loadingHintCursor, setLoadingHintCursor] = useState(0);
  useEffect(() => {
    if (!showAssetLoadingOverlay) return;
    const id = setInterval(() => {
      setLoadingHintCursor(c => (c + 1) % loadingHintSample.length);
    }, 5000);
    return () => clearInterval(id);
  }, [showAssetLoadingOverlay, loadingHintSample.length]);
  const [isStartingRound, setIsStartingRound] = useState(false); // Track if we're in the process of starting
  const [cinematicPhase, setCinematicPhase] = useState(null); // null | 'blackout' | 'reveal' | 'fadeIn'

  // Contextual hints - track which hints have been dismissed
  const [dismissedHints, setDismissedHints] = useState(() => {
    try {
      const stored = localStorage.getItem('game_dismissed_hints');
      return new Set(stored ? JSON.parse(stored) : []);
    } catch { return new Set(); }
  });

  const dismissHint = useCallback((hintKey) => {
    setDismissedHints(prev => {
      const next = new Set(prev);
      next.add(hintKey);
      try {
        localStorage.setItem('game_dismissed_hints', JSON.stringify([...next]));
      } catch {}
      return next;
    });
  }, []);

  // Cycle 5b.2: bot mode detection — `?bot=1` query param OR sessionStorage
  // `directTileNav` flag (set by ExploreSpacesPage on first bot visit).
  // Used to: (a) skip the "New here? Take a quick tour" toast, (b) auto-dismiss
  // the Welcome / Let's Go interstitial, (c) force chat auto-scroll so the
  // bot's own posts are visible to HLS viewers.
  const isBotMode = useMemo(() => {
    if (typeof window === 'undefined') return false;
    return new URLSearchParams(window.location.search).get('bot') === '1'
      || window.sessionStorage.getItem('directTileNav') === '1';
  }, []);

  // 🧱🗂️ THE ROOM FRAME (SPACES_REDESIGN_BRAINSTORM_2026_09_29 §5.2-§5.5a; the
  // approved mockup rooms-by-purpose.html, frame A + presets a/b). Nina 09-29:
  // NEW purposes only, Workstation (learn) and Research first, behind the
  // switch (utils/roomPurposeLayout SHOW_ROOM_SHELL, ON since 10-01; the
  // one-device admin preview ?shell=1 / ?shell=0 is what a switch-off rollback
  // keeps). shellPresetFor is THE gate: null = today's page,
  // byte for byte (bots, competitive/imposter, ported, the House, BB/BOTC and
  // every room with no purpose all get null). The switch is read ONCE (it
  // writes the preview flag on ?shell=1/0). ⚠️ Declared HERE, under isBotMode:
  // chatGameStatus + gameType sit above, every reader (placesNav,
  // ghostSurfacesOn, recapWanted, ambientTheme, the render) below. A const
  // read before its declaration is a white page only a render probe sees.
  const roomShellSwitch = useMemo(() => roomShellOn(typeof window !== 'undefined' ? window : null), []);
  const shellPresetNow = shellPresetFor(chatGameStatus, { on: roomShellSwitch, bot: isBotMode, gameType });
  const shellPresetKey = shellPresetNow ? shellPresetNow.purpose : null;
  // one object per purpose (the frame keys this device's layout on it)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const shellPreset = useMemo(() => shellPresetNow, [shellPresetKey]);
  // The frame's three DOM hosts ('stage' · 'feed' · 'composer', its onSlot):
  // the page's OWN feed, composer and stage slots portal into them, so nothing
  // is rebuilt or mounted twice (the theater + the live panel keep their one
  // mount and ride the reparent chains into the stage slots below).
  const [shellHosts, setShellHosts] = useState({});
  const onShellSlot = useCallback((name, el) => setShellHosts((h) => (h[name] === el ? h : { ...h, [name]: el })), []);
  const shellWpSlotRef = useRef(null);
  const shellLiveSlotRef = useRef(null);
  const [filesOpen, setFilesOpen] = useState(false); // 🗂️ the manila folder (FilesFolder), the frame's Files
  // 🎥 the stage's live panel: asked = the viewer asked (Share → Screen, a
  // go-live line); closedFor = the screen share it was closed on (a new one reopens it)
  const [shellStage, setShellStage] = useState({ asked: false, closedFor: null });
  const [publishRequest, setPublishRequest] = useState(null); // → LivestreamPanel: Share → Screen
  // the frame shows the one tab it has (the feed); a stale lane never hides it
  useEffect(() => {
    if (!shellPreset) return;
    if (chatActionTab !== 'all') setChatActionTab('all');
    if (activeTab !== 'actions') setActiveTab('actions');
  }, [shellPreset, chatActionTab, activeTab]);
  // 🫂 THE CARE CARDS (§5.12; every room, never a bot; dark behind the backend
  // flags: no heavy_room / care key = nothing renders). The check-in counts
  // 20 minutes from THIS visit's start; the crisis card opens from the
  // sender's own interjection response (utils/careCheckIn.takeCrisisCard).
  const careVisitRef = useRef({ gameId, at: Date.now() });
  if (careVisitRef.current.gameId !== gameId) careVisitRef.current = { gameId, at: Date.now() };
  const [crisisOpen, setCrisisOpen] = useState(false);
  // another room (a client-side hop) starts clean: no card, no stage, no folder
  // (functional sets that keep the same state: a first mount re-renders nothing)
  useEffect(() => {
    setCrisisOpen(false);
    setFilesOpen(false);
    setShellStage((s) => ((s.asked || s.closedFor != null) ? { asked: false, closedFor: null } : s));
  }, [gameId]);

  // Marketplace persona identity (port spec §4.5 "You"-seat fix): ported
  // chibis carry ?agent_name=<name> on bot-mode URLs (appended by the MCP
  // server). Persist to sessionStorage like directTileNav — internal
  // navigation drops query params, the seat name must survive it. House
  // sessions never send the param → null → existing behavior everywhere.
  const botAgentName = useMemo(() => {
    if (typeof window === 'undefined') return null;
    const fromUrl = new URLSearchParams(window.location.search).get('agent_name');
    if (fromUrl && fromUrl.trim()) {
      const clean = fromUrl.trim().slice(0, 40);
      try { window.sessionStorage.setItem('botAgentName', clean); } catch (e) { /* private mode */ }
      return clean;
    }
    try { return window.sessionStorage.getItem('botAgentName') || null; } catch (e) { return null; }
  }, []);

  // Tutorial state
  const [showTutorialToast, setShowTutorialToast] = useState(() => {
    // Cycle 5b.2: bot mode never sees the tutorial — would distract from chat view
    if (typeof window !== 'undefined') {
      const isBot = new URLSearchParams(window.location.search).get('bot') === '1'
        || window.sessionStorage.getItem('directTileNav') === '1';
      if (isBot) return false;
      // 🔗 09-26 (Nina): a shared room link lands in the room, never in the tour offer (this visit only)
      if (isRoomDeepLink(window.location.search)) return false;
    }
    const key = `game_tutorial_seen_${gameId}`;
    // try/catch like every sibling initializer: a storage-blocked context
    // (Safari private w/ ITP quirks) would otherwise throw inside useState.
    let seen = null;
    try { seen = localStorage.getItem(key); } catch (e) { /* storage blocked */ }
    console.log('[Tutorial]', key, ':', seen, '→ showToast:', !seen);
    return !seen;
  });
  // 🎓 Tour × quest merge (08-02): in NON-COMP spaces the tour opens the
  // little quest, and the quest is global (learn the app once) — so the
  // "New here?" toast is offered once EVER, not once per space. Comp keeps
  // the per-game key (its geography really is per-game).
  const [tourIntroSeen, setTourIntroSeen] = useState(() => {
    try { return localStorage.getItem('tutorialTourSeen.v1') === '1'; } catch (e) { return false; }
  });
  const markTourIntroSeen = useCallback(() => {
    setTourIntroSeen(true);
    try { localStorage.setItem('tutorialTourSeen.v1', '1'); } catch (e) { /* private mode */ }
  }, []);
  const [tutorialActive, setTutorialActive] = useState(false);
  const [tutorialStep, setTutorialStep] = useState(0);
  // Earned confetti: set when the LAST task beat completes live mid-tour
  const [tutorialCelebrate, setTutorialCelebrate] = useState(false);

  // Auto-dismiss effect moved BELOW tutorialToastReady (08-03): the 15s clock
  // used to start at MOUNT, so a fresh visitor still reading the space intro
  // had the toast expire before it ever rendered (Nina's incognito repro).

  // Tutorial refs for spotlight targets
  const playersPanelRef = useRef(null);
  const startButtonRef = useRef(null);
  const chatAreaRef = useRef(null);
  const inputFormRef = useRef(null);
  const phaseBannerRef = useRef(null);
  const objectiveRef = useRef(null);
  const spectateDropdownRef = useRef(null);
  const quickJoinButtonsRef = useRef(null);
  const readyToggleRef = useRef(null);
  const ghostChatRef = useRef(null);
  // 👻 The Ghost lane tab in the mobile dock — the non-comp tour's ghost beat
  // spotlights it (Nina 08-03). Null when laneEligible is false (no ghost
  // traffic yet); the beat then falls back to its centered card.
  const ghostLaneTabRef = useRef(null);
  // 📱 The whole Locals PLACE (places nav) — beat 1 of the non-comp tour
  // walks the user there and frames the room
  const localsPlaceRef = useRef(null);

  // Summary display toggles
  const [showSummaryPhotos, setShowSummaryPhotos] = useState(true);
  const [showSummaryNarrative, setShowSummaryNarrative] = useState(true);
  const [showSummaryHighlights, setShowSummaryHighlights] = useState(true);
  const [showSummaryVotes, setShowSummaryVotes] = useState(true);
  const [showSummaryBenchmarks, setShowSummaryBenchmarks] = useState(true);
  const [isRefreshingSummaries, setIsRefreshingSummaries] = useState(false);
  const [gameSpaces, setGameSpaces] = useState(null);
  const [showCustomVideoModal, setShowCustomVideoModal] = useState(false);
  const [showSpaceInfoModal, setShowSpaceInfoModal] = useState(false);
  const account = useAccount(); // 🔐 accounts (09-30): 'off' until sign-in is on
  // 📥📣 09-29 (spec §5.10 · §3.8): Export chat (anyone who can read the room) and Promote (mods only)
  const [exportChatOpen, setExportChatOpen] = useState(false);
  const [promoteOpen, setPromoteOpen] = useState(false);
  const [guestbookOpen, setGuestbookOpen] = useState(false); // Space-details modal: collapsed human-visitors section
  const [cloningSpace, setCloningSpace] = useState(false);

  // Emotion songs state (background music based on emotion)
  const [showEmotionSongsEditor, setShowEmotionSongsEditor] = useState(false);
  const [emotionSongs, setEmotionSongs] = useState({
    fire: { url: '', name: '' },
    ocean: { url: '', name: '' },
    forest: { url: '', name: '' },
    sun: { url: '', name: '' },
    strategic: { url: '', name: '' },
    shadow: { url: '', name: '' },
    neutral: { url: '', name: '' },
    host: { url: '', name: '' },
    introspective: { url: '', name: '' }
  });
  const [savingEmotionSongs, setSavingEmotionSongs] = useState(false);
  const [uploadingEmotionSong, setUploadingEmotionSong] = useState(null);
  const [dragOverCategory, setDragOverCategory] = useState(null);

  // Cached data to avoid per-action API calls (Issue 2 fix)
  const [cachedSpaces, setCachedSpaces] = useState([]);
  const [cachedRoundAnalysisHistory, setCachedRoundAnalysisHistory] = useState([]);
  const [gameInstanceId, setGameInstanceId] = useState(null);
  const [roundsMetadata, setRoundsMetadata] = useState([]);

  // Space voting state
  const [spaceLikes, setSpaceLikes] = useState(0);
  // 👣 Cumulative non-unique visit count (Nina 08-08) — the "N visits" half of
  // the masthead vitals line. Ships from getRoundInfo as `total_views`; 0 until
  // the backend leg deploys, and the line hides the half it has no number for,
  // so a frontend-first deploy degrades to today's behaviour rather than
  // rendering "0 visits".
  const [spaceVisits, setSpaceVisits] = useState(0);
  const [spaceDislikes, setSpaceDislikes] = useState(0);
  const [voteAnimation, setVoteAnimation] = useState({ like: false, dislike: false });
  const [userSpaceVote, setUserSpaceVote] = useState(null); // 'like', 'dislike', or null

  // Helper to get/store votes in localStorage
  const getStoredVote = (spaceId) => {
    try {
      const stored = localStorage.getItem('space_votes');
      const votes = stored ? JSON.parse(stored) : {};
      return votes[spaceId] || null;
    } catch {
      return null;
    }
  };

  const storeVote = (spaceId, voteType) => {
    try {
      const stored = localStorage.getItem('space_votes');
      const votes = stored ? JSON.parse(stored) : {};
      votes[spaceId] = voteType;
      localStorage.setItem('space_votes', JSON.stringify(votes));
    } catch (err) {
      console.error('Failed to store vote:', err);
    }
  };

  // Load user's existing vote when gameInstanceId is set
  useEffect(() => {
    if (gameInstanceId) {
      const existingVote = getStoredVote(gameInstanceId);
      setUserSpaceVote(existingVote);
    }
  }, [gameInstanceId]);

  // Auto-update to latest round when "current" is selected
  useEffect(() => {
    if (selectedRound === 'current' && results.length > 0) {
      const allActions = results.flatMap(r => r.actions || []);
      const uniqueRoundIds = [...new Set(allActions.map(a => a.game_instance_round_id).filter(id => id))];

      if (uniqueRoundIds.length > 0) {
        // Build timestamp mapping to find chronologically latest round
        const roundIdToTimestamp = {};
        uniqueRoundIds.forEach(roundId => {
          const actionsInRound = allActions.filter(a => a.game_instance_round_id === roundId);
          if (actionsInRound.length > 0) {
            const timestamps = actionsInRound.map(a => new Date(a.created_at).getTime());
            roundIdToTimestamp[roundId] = Math.min(...timestamps);
          }
        });

        // Sort to find the latest (highest number) round
        const sortedRoundIds = uniqueRoundIds.sort((a, b) =>
          roundIdToTimestamp[a] - roundIdToTimestamp[b]
        );

        // The current round is the last one chronologically (highest round number)
        const currentRoundIndex = sortedRoundIds.length;

        // Store the current round number for filtering (we'll use this in the filter logic)
        // Don't change selectedRound, keep it as 'current'
      }
    }
  }, [results, selectedRound]);

  // Get URL params for initial loading
  const initialGameId = searchParams.get('gameId');
  const [gameName, setGameName] = useState(null);
  // Set browser tab title (and document.title for the explorer bot's
  // report_location MCP tool) to the space name once it loads. Hook is
  // a no-op while gameName is null, and restores the previous title on
  // unmount — safe for client-side route changes.
  useDocumentTitle(gameName);

  // ChatGame-specific state
  const [humanPlayerId, setHumanPlayerId] = useState(() => {
    // Only restore play mode from explicit URL param if this browser owns the player (has its secret).
    // Without this check, opening a shared link with ?playerId=someone_else would incorrectly assign
    // that player as "You" and skip auto-join entirely.
    const fromUrl = searchParams.get('playerId');
    if (fromUrl && chatGameApi.getPlayerSecret(fromUrl)) return fromUrl;
    // Otherwise start in spectate mode (auto-join will identify/create the correct player)
    return null;
  });
  const processedActionIdsRef = useRef(new Set()); // Dedup across WS, polling, and early fetch
  const humanPlayerIdRef = useRef(humanPlayerId); // Ref mirror for WS handler (avoids stale closure)
  useEffect(() => { humanPlayerIdRef.current = humanPlayerId; }, [humanPlayerId]);
  const hasAutoJoinedRef = useRef(false); // Prevent auto-join from re-firing when user manually spectates
  const createdPlayerIdRef = useRef(
    (() => {
      // Only use URL/localStorage player ID if this browser has the secret for it,
      // to avoid transiently showing "(You)" on another player's card during auto-join.
      const fromUrl = searchParams.get('playerId');
      if (fromUrl && chatGameApi.getPlayerSecret(fromUrl)) return fromUrl;
      const stored = typeof window !== 'undefined' ? localStorage.getItem(`humanPlayerId_${initialGameId}`) : null;
      if (stored && chatGameApi.getPlayerSecret(stored)) return stored;
      return null;
    })()
  ); // Always tracks the created player ID (for Spectate/Play toggle)
  const [isHumanTurn, setIsHumanTurn] = useState(false);
  const isHumanTurnRef = useRef(false);
  useEffect(() => { isHumanTurnRef.current = isHumanTurn; }, [isHumanTurn]);
  const humanTurnDismissedRef = useRef(null); // tracks request_id we already submitted, to prevent re-showing
  const [currentRequestId, setCurrentRequestId] = useState(null);
  const currentRequestIdRef = useRef(null);
  useEffect(() => { currentRequestIdRef.current = currentRequestId; }, [currentRequestId]);
  const [turnContext, setTurnContext] = useState(null);
  const [availableActions, setAvailableActions] = useState([]);
  const [tokensLeft, setTokensLeft] = useState(0);
  const [turnDeadline, setTurnDeadline] = useState(null); // epoch ms when the turn expires
  // Which request_id the current turnDeadline belongs to. A new request must REPLACE the
  // deadline (never inherit a stale one) — the old `prev ||` pattern let an expired chat-turn
  // deadline leak into the next turn / the voting phase, so the countdown kept ticking or a
  // fresh turn rendered as already "TIME'S UP".
  const turnDeadlineRequestIdRef = useRef(null);

  // Livestream presence from the status poll — covers collapsed panel + late
  // joiners; 'livestream_update' WS pushes update the same state instantly.
  useEffect(() => {
    const next = chatGameStatus?.livestream?.streamers;
    if (!next) return;
    setSpaceLiveStreamers(prev => JSON.stringify(prev) === JSON.stringify(next) ? prev : next);
  }, [chatGameStatus?.livestream]);

  // 🧰 Space shelf drawer — the /build items ledger (slash-commands spec §3a).
  // Lives here (not with the other drawer state) because the seen-count effect
  // reads chatGameStatus, declared just above. Seen count persists per game so
  // the tab badge only counts builds added since the shelf was last opened.
  const [shelfOpen, setShelfOpen] = useState(false);
  const [shelfSeen, setShelfSeen] = useState(null); // null until loaded for this game
  const shelfBuildCount = (chatGameStatus?.builds || []).length;
  useEffect(() => {
    if (!gameId) return;
    try { setShelfSeen(parseInt(localStorage.getItem(`shelf_seen_${gameId}`), 10) || 0); }
    catch (e) { setShelfSeen(0); }
  }, [gameId]);
  useEffect(() => {
    if (!shelfOpen || !gameId) return;
    setShelfSeen(shelfBuildCount);
    try { localStorage.setItem(`shelf_seen_${gameId}`, String(shelfBuildCount)); } catch (e) { /* private mode */ }
  }, [shelfOpen, shelfBuildCount, gameId]);
  const shelfUnseen = shelfSeen === null ? 0 : Math.max(0, shelfBuildCount - shelfSeen);
  // 🕵️🧰 Drawer height cap (Nina 07-22): the drawers anchor under the tab
  // row, but a docked stage can leave only ~200px of chat above the composer
  // — the static min(66vh,560px) cap let both drawers spill OVER the
  // composer. Measure the real room (anchor top → composer top) when a
  // drawer opens and on resize; fall back to the old cap if refs are gone.
  const drawerAnchorRef = useRef(null);
  const [drawerMaxH, setDrawerMaxH] = useState(null);
  useEffect(() => {
    if (!dossierOpen && !shelfOpen) return undefined;
    const measure = () => {
      try {
        const a = drawerAnchorRef.current?.getBoundingClientRect();
        const c = inputFormRef.current?.getBoundingClientRect();
        if (!a || !c || c.top <= a.top) { setDrawerMaxH(null); return; }
        setDrawerMaxH(Math.max(180, Math.floor(c.top - a.top - 16)));
      } catch (e) { setDrawerMaxH(null); }
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [dossierOpen, shelfOpen]);
  const drawerMaxHeight = drawerMaxH ? `${Math.min(drawerMaxH, 560)}px` : 'min(66dvh, 560px)';
  // 🔨 Micro-sound (spec Arc 1 layer 2): soft wood-knock when a NEW item
  // lands on the shelf. Ref-seeded on first status so history never knocks.
  const prevBuildCountRef = useRef(null);
  useEffect(() => {
    if (!chatGameStatus) return;
    if (prevBuildCountRef.current === null) { prevBuildCountRef.current = shelfBuildCount; return; }
    if (shelfBuildCount > prevBuildCountRef.current && !isBotMode) soundManager.play('woodKnock');
    prevBuildCountRef.current = shelfBuildCount;
  }, [shelfBuildCount, chatGameStatus, isBotMode]);

  // Is the CURRENT user among the live streamers? Publishers announce with
  // lk_viewer_id (seated humans can also match their player id). Every "N
  // live — watch" badge must flip to "you're live" framing when the N is YOU
  // — a user who closed the panel mid-broadcast otherwise reads their own
  // still-running camera as someone else's stream.
  const selfIsLive = useMemo(() => {
    if (spaceLiveStreamers.length === 0) return false;
    let vid = null;
    try { vid = localStorage.getItem('lk_viewer_id'); } catch (e) { /* private mode */ }
    return spaceLiveStreamers.some(s => (vid && s.id === vid) || (humanPlayerId && s.id === humanPlayerId));
  }, [spaceLiveStreamers, humanPlayerId]);

  // Auto-expand the livestream bar when anyone goes live. The trackCount-based
  // effect above only fires once the panel is mounted; this one works collapsed.
  // Bots never auto-join the LiveKit room (recursive self-view trap).
  const prevSpaceLiveCountRef = useRef(0);
  useEffect(() => {
    if (!isBotMode && spaceLiveStreamers.length > 0 && prevSpaceLiveCountRef.current === 0) {
      setLivestreamExpanded(true);
    }
    prevSpaceLiveCountRef.current = spaceLiveStreamers.length;
  }, [spaceLiveStreamers.length, isBotMode]);

  // If the floating player loses its stream (streamer stopped), return to the
  // dock — the mini-player only renders while tracks exist, so staying
  // "floating" would strand the user with an invisible panel.
  useEffect(() => {
    if (livestreamFloating && livestreamStatus.trackCount === 0) {
      setLivestreamFloating(false);
    }
  }, [livestreamFloating, livestreamStatus.trackCount]);

  // 🔴 go-live toast: fires only when a NEW streamer appears — seeds silently on
  // the first snapshot (someone already live when you load ≠ an event), never
  // for your own stream, never for bots, never in competitive/imposter (no
  // livestream UI there to jump to). Auto-dismisses after 8s.
  const [liveToast, setLiveToast] = useState(null);
  const prevLiveIdsRef = useRef(null); // null = not seeded yet
  useEffect(() => {
    const prev = prevLiveIdsRef.current;
    prevLiveIdsRef.current = new Set(spaceLiveStreamers.map(s => s.id));
    if (prev === null || isBotMode) return;
    if (chatGameStatus?.competitive_mode || chatGameStatus?.imposter_mode) return;
    let myViewerId = null;
    try { myViewerId = localStorage.getItem('lk_viewer_id'); } catch (e) { /* private mode */ }
    const newcomer = spaceLiveStreamers.find(s => !prev.has(s.id) && s.id !== humanPlayerId && s.id !== myViewerId);
    if (newcomer) setLiveToast({ id: newcomer.id, name: newcomer.name });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spaceLiveStreamers, isBotMode, humanPlayerId]);
  useEffect(() => {
    if (!liveToast) return;
    const t = setTimeout(() => setLiveToast(null), 8000);
    return () => clearTimeout(t);
  }, [liveToast]);
  // 🐻 09-29 (spec §3.6): "A mysterious bear chipped in." The socket's
  // `chip_in` event ({ id, animal }: utils/chipIn.chipInFromSocket reads
  // nothing else, never a name, never an amount) queues here. Page-only:
  // residents never see money. Never for a bot; one at a time, and it waits
  // while the go-live toast (and, at its mount, the couch invite) holds the
  // top spot. A socket can deliver twice: ids already queued or shown are
  // dropped (the seen set, checked OUTSIDE the state updater so a re-run
  // updater stays pure). ChipInToast times itself out (onDone); the phone
  // clock pill yields to it. 10-01 (#10b): at its mount it also waits under
  // the open phone ghost sheet.
  const [chipInQueue, setChipInQueue] = useState([]);
  const chipInSeenRef = useRef(new Set());
  const chipInShown = (!isBotMode && !liveToast && chipInQueue.length) ? chipInQueue[0] : null;

  // Imposter mode: skip the "Let's Go!" screen — go straight from loading to game
  useEffect(() => {
    if (chatGameStatus?.imposter_mode && assetLoadingReady && showAssetLoadingOverlay) {
      setShowAssetLoadingOverlay(false);
    }
  }, [chatGameStatus?.imposter_mode, assetLoadingReady, showAssetLoadingOverlay]);

  const [showRoundAnnouncement, setShowRoundAnnouncement] = useState(false);
  const [announcedRound, setAnnouncedRound] = useState(null);
  const [showVotingModal, setShowVotingModal] = useState(false);
  // Voting grace banner (v1 of the "let people READ before the vote takes over" fix):
  // when voting opens, show a tappable banner instead of the full-screen modal, and
  // auto-expand to the modal after a short reading grace. { round } while showing.
  const [votingBanner, setVotingBanner] = useState(null);
  const votingBannerTimerRef = useRef(null);
  const [votingRoundShown, setVotingRoundShown] = useState(null); // Track which voting round we've already shown
  // Detective's Quiz (Whodunnit): request ids already answered/dismissed, so the modal
  // doesn't reopen off a stale status poll while the request row still looks pending.
  const [quizDoneRequestIds, setQuizDoneRequestIds] = useState(() => new Set());
  // ☠️ THE POISON (killer mode §2, 07-19): two-tap arm + private result flash
  const [poisonArmed, setPoisonArmed] = useState(false);
  const [poisonBusy, setPoisonBusy] = useState(false);
  const [poisonResult, setPoisonResult] = useState(null);
  const [currentlyTyping, setCurrentlyTyping] = useState(null); // Player name currently "typing"
  // 🏡 Their Place (W1, PORTED_HOME_HANDOFF_2026_09_25): the resident's REAL
  // writing signal, set ONLY by the WS typing_started / typing_stopped pair
  // (never next_speaker or the drip pump, which can show a pill while nothing
  // generates; handoff §2 honesty). The room band's writing cue keys here.
  const [wsTypingName, setWsTypingName] = useState(null);
  const portedStageRef = useRef(null);
  const portedResidentNameRef = useRef(null);
  // Pacing drip (non-competitive): freshly arriving AI chat messages reveal
  // one at a time with a typing beat instead of landing as a poll batch —
  // the single biggest "the room is alive" feel upgrade (user call 07-10).
  // History replays, bots, humans' own messages, and competitive mode bypass.
  const dripQueueRef = useRef([]); // [{ thunk, senderName, msgLen }]
  const dripTimerRef = useRef(null);
  // (perf 08-13) Mid-stroke jank fix: while the MOBILE full-screen canvas is
  // up, every drip reveal setState reconciled this whole component under the
  // artist's pointer. Chat is hidden behind that modal, so reveals hold and
  // the close effect (below, by showDrawingCanvas) re-pumps — the >3-queue
  // fast path flushes the backlog in one batch. Desktop popover keeps chat
  // visible beside the canvas, so desktop is NOT held.
  const drawingHoldRef = useRef(false);
  // Drip pump: reveal delay scales with message length (typing feel), capped
  // so the feed never trails reality by more than a couple seconds. A queue
  // deeper than 3 means we're catching up — flush instantly.
  const pumpDripQueue = useCallback(() => {
    if (dripTimerRef.current) return;
    if (drawingHoldRef.current) return; // mobile canvas open — resumed on close
    if (dripQueueRef.current.length === 0) return;
    if (dripQueueRef.current.length > 3) {
      const all = dripQueueRef.current.splice(0);
      setCurrentlyTyping(null);
      all.forEach(item => item.thunk());
      return;
    }
    const next = dripQueueRef.current[0];
    setCurrentlyTyping(next.senderName || null);
    const delay = Math.max(700, Math.min(2400, 500 + next.msgLen * 14));
    dripTimerRef.current = setTimeout(() => {
      dripTimerRef.current = null;
      const item = dripQueueRef.current.shift();
      setCurrentlyTyping(prev => (prev === (item?.senderName || null) ? null : prev));
      if (item) item.thunk();
      pumpDripQueue();
    }, delay);
  }, []);
  useEffect(() => () => { if (dripTimerRef.current) clearTimeout(dripTimerRef.current); }, []);
  const [gameStatusMessage, setGameStatusMessage] = useState(null); // Real-time status from WebSocket
  const [spectatingMessages, setSpectatingMessages] = useState([]); // Spectating messages for sidebar panel
  // 🫥 09-22 UI/UX batch (Nina): the "X reacted 🗑️ to Y's message" system
  // note is HIDDEN from ghost chat in non-competitive spaces (the house
  // included) for now. Display-only: the backend still mints the row and still
  // pays house coins on it; competitive/imposter keep the note. Every ghost
  // surface (the six panel mounts + the Live Room crowd merge) reads THIS list,
  // so the panel count label and its seen-counter stay consistent. To bring the
  // note back, delete this memo and point those readers at spectatingMessages.
  const hideReactionNotes = !(chatGameStatus?.competitive_mode || chatGameStatus?.imposter_mode);
  const ghostPanelMessages = useMemo(
    () => (hideReactionNotes ? spectatingMessages.filter((m) => !m?.is_reaction_note) : spectatingMessages),
    [hideReactionNotes, spectatingMessages]
  );
  const spectatingMessagesRef = useRef([]); // Ref mirror for reading in polling/timer callbacks
  const spectatorPanelExpandedRef = useRef(true); // Tracks whether the visible SpectatorChatPanel is expanded — read by ghost-chat sound gate
  const spectatorPanelScrolledUpRef = useRef(false); // Tracks whether the user has scrolled up within the SpectatorChatPanel — read by ghost-chat sound gate
  const [isInitialSpectatorHistoryLoading, setIsInitialSpectatorHistoryLoading] = useState(true); // True until the first getGameStatus fetch resolves — drives skeleton in SpectatorChatPanel
  // Space intro sequence (non-competitive, first visit only): caption beat →
  // ONE combined welcome screen (host block + locals deal-in, 07-12 combine).
  // 0 = intro, 2 = straight to welcome. (1 retired with the combine — the CTA
  // jumps 0→2.) Returning visitors (localStorage flag) start at 2.
  const [spaceIntroStep, setSpaceIntroStep] = useState(() => {
    try { return localStorage.getItem(`space_intro_v1_${searchParams.get('gameId') || ''}`) ? 2 : 0; } catch (e) { return 2; }
  });
  // Host-bio caption SCREEN (user call 07-11, v2; v3 07-12): the intro opens on
  // a cinematic caption beat — the host's bio in big readable chunks. v3 makes
  // the whole screen TAP-TO-ADVANCE (user call: manual pacing, de-crowded); the
  // 4s timer only runs while assets are still generating, so an idle wait still
  // gets the show, and stops the moment the world is ready (the user paces).
  // After the last chunk the flow continues: full host bio → locals.
  // idx counts up once (no loop); idx >= chunks.length = captions done.
  const [introCaptionIdx, setIntroCaptionIdx] = useState(0);
  const [introCaptionsSkipped, setIntroCaptionsSkipped] = useState(false);
  // Space finished generating before this browser ever saw it → the viewer is a
  // visitor, not the creator; the intro copy swaps "your space" for the name.
  const introVisitorRef = useRef(false);
  // Host beat shows ~2 sentences of the autobiography; this expands the rest
  // (user call 07-12: the full wall of text crowded the beat).
  const [introBioExpanded, setIntroBioExpanded] = useState(false);
  useEffect(() => {
    if (!showAssetLoadingOverlay || spaceIntroStep !== 0 || introCaptionsSkipped || isBotMode) return;
    if (assetLoadingReady) return; // world ready → tap paces the captions, no timer
    if (!chatGameStatus?.moderator_character || chatGameStatus?.competitive_mode || chatGameStatus?.imposter_mode || chatGameStatus?.is_ported) return;
    const t = setInterval(() => setIntroCaptionIdx(i => i + 1), 4000);
    return () => clearInterval(t);
    // Deps are BOOLEANS on purpose: moderator_character is a fresh object every
    // status poll, and polls land faster than the 4s tick — depending on the
    // object identity would reset the interval forever and freeze the captions.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showAssetLoadingOverlay, spaceIntroStep, introCaptionsSkipped, isBotMode, assetLoadingReady,
      !!chatGameStatus?.moderator_character, !!chatGameStatus?.competitive_mode, !!chatGameStatus?.imposter_mode,
      !!chatGameStatus?.is_ported]);
  // moderatorMessages state removed — moderator messages now flow into spectatingMessages with is_moderator flag
  // Lazy loading state for chat messages and spectator chat
  const [hasMoreChatMessages, setHasMoreChatMessages] = useState(true);
  const [isLoadingOlderChatMessages, setIsLoadingOlderChatMessages] = useState(false);
  const [hasMoreSpectatorMessages, setHasMoreSpectatorMessages] = useState(true);
  const [isLoadingOlderSpectatorMessages, setIsLoadingOlderSpectatorMessages] = useState(false);
  const [objectiveExpanded, setObjectiveExpanded] = useState(false); // Objective/scene text expand state
  const [diceRevealedRound, setDiceRevealedRound] = useState(null); // which round's Fate die the human has "rolled" (revealed)
  const [diceRolling, setDiceRolling] = useState(false); // dice tumble animation in progress
  const [ownRollBeat, setOwnRollBeat] = useState(null); // 'tumble' | 'landed' | null — YOUR reveal plays center stage (whodunnit only)
  // 🎙️ PRESS BOX (Nina 07-25): spectators skip the investigation takeover and get
  // a gallery card instead — public activity counts + a private killer prediction.
  // The pick lives in localStorage (spectators may have no server seat) and is
  // scored client-side against killer_revealed once the finale runs.
  const [pressBoxOpen, setPressBoxOpen] = useState(null); // null = default (open desktop, chip on mobile)
  const [pressBoxPick, setPressBoxPick] = useState(() => {
    try { return JSON.parse(localStorage.getItem(`pressBoxPick.${gameId}`)) || null; } catch (e) { return null; }
  });
  const savePressBoxPick = (pick) => {
    setPressBoxPick(pick);
    try { localStorage.setItem(`pressBoxPick.${gameId}`, JSON.stringify(pick)); } catch (e) { /* private mode */ }
  };
  // Dice Powers crit picker (Increment 3): the human resolves an OFFERED crit power.
  const [critPick, setCritPick] = useState({ power_id: null, target_id: null, puppet_text: '' });
  const [critSubmitting, setCritSubmitting] = useState(false);
  const [critResolvedRound, setCritResolvedRound] = useState(null); // round we locally resolved (optimistic hide before the poll catches up)
  // Word Ban v2 picker: the human resolves an OFFERED targeted ban (SUCCESS 11-13 roll) —
  // picks a rival + ONE fully-free-choice word. Separate state from the crit picker so the
  // two offer flows can never clobber each other.
  const [banPick, setBanPick] = useState({ target_id: '', word: '' });
  const [banSubmitting, setBanSubmitting] = useState(false);
  const [banResolvedRound, setBanResolvedRound] = useState(null);

  // Lock the turn input until the human rolls their die for the round. Only when they actually
  // have a roll, and never for the explorer bot (it can't click — its turns must not be blocked).
  // Per-round reveal gate. Key on dice.round, but fall back to the always-present
  // current_competitive_round so the die still re-locks each round even if a stale
  // status payload omits dice.round (otherwise every round reads as already-rolled).
  const diceRoundKey = chatGameStatus?.dice?.round ?? chatGameStatus?.current_competitive_round ?? null;
  const rollLocked = !isBotMode
    && !!chatGameStatus?.dice?.your_roll
    && diceRevealedRound !== diceRoundKey;

  // Banned words for the composer (both HumanInputForm mounts). The fumble Lexicon Curse
  // derives from the viewer's PRE-ASSIGNED roll — while the die is still unrevealed
  // (rollLocked) the curse banner would spoil the roll and contradict the "tap to roll"
  // composer, so fumble entries are held back until the reveal. The input is locked until
  // then, so no enforcement is lost. Rival-cast targeted bans reveal nothing and stay.
  const visibleBannedWords = () => {
    if (isBotMode) return null;
    const raw = (Array.isArray(chatGameStatus?.your_banned_words) && chatGameStatus.your_banned_words.length ? chatGameStatus.your_banned_words : null)
      // Legacy fallback (old backend mid-deploy): the fumble self-ban lived on your_power.
      || ((chatGameStatus?.your_power?.power_id === 'word_ban' && chatGameStatus?.your_power?.band === 'fumble' && chatGameStatus.your_power.word)
        ? [{ word: chatGameStatus.your_power.word, source: 'fumble' }] : null);
    if (!raw) return null;
    const vis = rollLocked ? raw.filter(b => b?.source !== 'fumble') : raw;
    return vis.length ? vis : null;
  };

  // Reveal the viewer's pre-rolled die (value is fixed server-side; the click is just a
  // reveal animation). Rolling is only offered on the turn-input overlay during the player's
  // turn, so this is the single shared handler. Resets per round (keyed on diceRoundKey).
  const rollDie = () => {
    if (diceRolling || diceRevealedRound === diceRoundKey) return;
    setDiceRolling(true);
    // Whodunnit: the reveal plays CENTER STAGE (the dock die is just the trigger) so the
    // side panel stays quiet. Other game types keep the in-place tumble.
    const centerBeat = !!chatGameStatus?.noir_theme;
    if (centerBeat) setOwnRollBeat('tumble');
    setTimeout(() => {
      setDiceRolling(false);
      setDiceRevealedRound(diceRoundKey);
      if (centerBeat) {
        setOwnRollBeat('landed');
        setTimeout(() => setOwnRollBeat(null), 3200);
      }
    }, 900);
  };

  // Result card shown above the turn-input ONCE the die has been revealed (the unrolled
  // prompt lives on the input-box overlay instead). Returns null until revealed.
  // Human-facing dice outcome: SHORT flavor line + a GLOWING consequence chip. The verbose
  // band instruction keeps flowing to the AI prompt untouched — humans get the outcome,
  // not the homework. Consequence derives from the resolved power (your_power), not text.
  const getDiceOutcome = (roll) => {
    const band = (roll?.band_name || '').toUpperCase();
    const yp = chatGameStatus?.your_power;
    // The one-line "what happened" narrative: the template's per-game short `ui` line when
    // present (e.g. "CAUGHT IN A LIE — the detective has evidence"), generic fallback else.
    const story = roll?.ui;
    if (band.includes('CRITICAL F') || roll?.value === 1) {
      return { line: story || 'DISASTER. Your words abandon you.', chip: '💀 12 WORDS BANNED THIS TURN', glow: '#ff4444' };
    }
    if (band.includes('CRITICAL') || roll?.value === 20) {
      return { line: story || 'NAT 20. The table bends your way.', chip: '🌟 PICK YOUR POWER', glow: '#ffd700' };
    }
    if (band.includes('COMPLICATION')) {
      return { line: story || "UH OH. You're rattled and stumbling on your words.", chip: `⏱ ${yp?.seconds || -30}s ON YOUR TURN`, glow: '#ff4444' };
    }
    // success band — consequence depends on the value-split power
    if (yp?.power_id === 'word_ban') {
      // 07-30 (Nina screenshot): once the ban is CAST the glowing offer kept
      // pulsing next to the "planted" reminder chip — collapse it to a done state.
      if (yp.target_id && yp.status && yp.status !== 'offered') {
        return { line: story || 'Sharp roll. You spot a weakness.', chip: '🚫 TRAP PLANTED', glow: '#ff8c66' };
      }
      return { line: story || 'Sharp roll. You spot a weakness.', chip: "🚫 YOU MAY BAN A RIVAL'S WORD", glow: '#ff8c66' };
    }
    if (yp?.power_id === 'extra_time') {
      return { line: story || 'A steady hand. The moment is yours.', chip: `⏱ +${yp?.seconds || 45}s ON YOUR TURN`, glow: '#22c55e' };
    }
    return { line: story || 'A steady hand. The moment is yours.', chip: '✨ ADVANTAGE', glow: THEME.accent };
  };

  const renderDiceOutcome = (roll) => {
    const o = getDiceOutcome(roll);
    return (
      <>
        <style>{`@keyframes diceChipGlow { 0%, 100% { box-shadow: 0 0 6px 1px var(--glow); } 50% { box-shadow: 0 0 18px 5px var(--glow); } }`}</style>
        <div style={{ fontSize: '0.8em', fontWeight: 800, color: THEME.accent }}>
          🎲 {chatGameStatus?.dice?.label} · {roll.value}/20 · {roll.emoji} {roll.band_name}
        </div>
        <div style={{ fontSize: '0.8em', color: THEME.textDark, margin: '3px 0 5px' }}>{o.line}</div>
        <span style={{
          '--glow': `${o.glow}80`,
          display: 'inline-block', padding: '5px 14px', borderRadius: '14px',
          border: `1.5px solid ${o.glow}`, backgroundColor: `${o.glow}1f`,
          color: o.glow, fontWeight: 900, fontSize: '0.82em', letterSpacing: '0.5px',
          animation: 'diceChipGlow 1.6s ease-in-out infinite'
        }}>
          {o.chip}
        </span>
      </>
    );
  };

  const renderFateDie = () => {
    if (noirWriteDocket) return null; // docket fact line carries the roll (Nina rev 07-29)
    const roll = chatGameStatus?.dice?.your_roll;
    if (!roll || diceRevealedRound !== diceRoundKey) return null;
    // ⚖️ One-card dock (Nina 07-30, approved mock): while you wait, the roll
    // card carries the waiting line in its own footer — the composer's idle
    // box is suppressed (suppressIdle at the HumanInputForm call sites) so the
    // dock is ONE surface, not a stack.
    const rollCardCarriesWait = !!(chatGameStatus?.noir_theme && !(isHumanTurn && currentRequestId));
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px', padding: '8px 12px', borderRadius: '8px', border: `1px solid ${THEME.accent}55`, backgroundColor: `${THEME.accent}12` }}>
        <span style={{ fontSize: '2em', lineHeight: 1, flexShrink: 0 }}>🎲</span>
        <div style={{ minWidth: 0, flex: 1 }}>
          {renderDiceOutcome(roll)}
          {rollCardCarriesWait && (
            <div style={{ marginTop: 6, paddingTop: 6, borderTop: '1px dashed rgba(255,255,255,0.12)', fontSize: '0.72em', color: THEME.textMuted || 'rgba(255,255,255,0.5)' }}>
              ⏳ Waiting for your turn…
            </div>
          )}
        </div>
      </div>
    );
  };

  // Compact fact chips for the docket line. Short forms of the roll outcome the beat
  // already dramatized; the whisper replaces the shouting killer banner (loud version
  // lives in the cold open + first-turn beat — announced once, whispered after).
  const noirDocketFacts = () => {
    const facts = [];
    const roll = chatGameStatus?.dice?.your_roll;
    if (roll && diceRevealedRound === diceRoundKey) {
      facts.push({ t: `🎲 ${roll.value}/20 ${(roll.band_name || '').toLowerCase()}`, c: 'rgba(255,255,255,0.55)' });
      const yp = chatGameStatus?.your_power;
      const bandUp = (roll.band_name || '').toUpperCase();
      if (bandUp.includes('CRITICAL F') || roll.value === 1) {
        facts.push({ t: '💀 the forbidden lexicon is banned', c: '#ff8a8a' });
      } else if (bandUp.includes('COMPLICATION')) {
        facts.push({ t: `⏱ ${Math.abs(yp?.seconds || 30)}s docked`, c: '#ff8a8a' });
      } else if (yp?.power_id === 'extra_time') {
        facts.push({ t: `⏱ +${yp?.seconds || 45}s`, c: '#7fd48f' });
      } else if (yp?.power_id === 'word_ban' && !yp?.word) {
        facts.push({ t: "🚫 you may ban a rival's word", c: '#ffb27a' });
      } else if (bandUp.includes('CRITICAL') || roll.value === 20) {
        facts.push({ t: '★ pick your power', c: '#ffd700' });
      }
    }
    if (chatGameStatus?.you_did_it) {
      facts.push({ t: '🤫 you did it. bluff.', c: '#b06a6a', whisper: true });
    }
    return facts;
  };

  // ACTION DOCK (Whodunnit spec Phase B2) — ONE strip above the composer that says what
  // the game needs from YOU right now. Exactly one state renders; null when the game
  // needs nothing (the phase banner already covers idle info — no duplication). This is
  // a reshuffle, not a rebuild: vote opens the existing modal, roll/write point at the
  // existing controls below it. Gated to competitive Whodunnit (noir_theme) per spec —
  // generalize after a playthrough.
  // ⚖️ One-CTA vote state (Nina 07-30, approved mock): true while the lineup
  // wants THIS viewer's ballot — the dock shows the vote card and everything
  // else (composer, whisper) folds. Called at render time only (TDZ-safe).
  const noirVoteAskOpen = () => !!(chatGameStatus?.noir_theme && chatGameStatus?.competitive_mode
    && !chatGameStatus?.game_completed && chatGameStatus?.is_voting && chatGameStatus?.voting_data
    && effectivePlayerId && !chatGameStatus.voting_data.votes_cast?.[effectivePlayerId] && !showVotingModal);
  const renderActionDock = () => {
    if (isBotMode) return null;
    if (!(chatGameStatus?.noir_theme && chatGameStatus?.competitive_mode)) return null;
    if (noirWriteDocket) return null; // statement turn: the docket tab + headline carry the ask
    if (chatGameStatus?.game_completed) return null;
    const vd = chatGameStatus?.voting_data;
    const needsVote = noirVoteAskOpen();
    const needsRoll = !!rollLocked;
    const yourWrite = !!(isHumanTurn && currentRequestId);
    // ⚖️ Ballot in, vote still open → the lineup folds to a slim tally strip
    // (approved mock): non-pulsing, no CTA — the room is deciding.
    if (!needsVote && chatGameStatus?.is_voting && vd && effectivePlayerId
      && vd.votes_cast?.[effectivePlayerId]) {
      const n = Object.keys(vd.votes_cast || {}).length;
      return (
        <div style={{
          marginBottom: 8, padding: '7px 12px', borderRadius: 10,
          border: '1px solid rgba(74,222,128,0.35)', background: 'rgba(74,222,128,0.06)',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8,
          fontSize: '0.78em', fontWeight: 700, color: '#4ade80',
        }}>
          <span>⚖️ Vote cast. The room decides.</span>
          <span style={{ color: 'rgba(255,255,255,0.55)', fontVariantNumeric: 'tabular-nums' }}>{n} in</span>
        </div>
      );
    }
    if (!needsVote && !needsRoll && !yourWrite) return null;
    // Center-dock treatment (Nina approved the mock 07-25): eyebrow names the ask,
    // the big line says it in-fiction, the why-line says the stakes. One boss:
    // this is the only pulsing element on screen.
    const culprit = chatGameStatus?.scenario?.culprit_noun || 'the killer';
    const d = needsVote
      ? (vd.is_accusation
        ? { eyebrow: '⚖️ THE ACCUSATION', big: `Name ${culprit}.`, why: 'Charge wrong and they walk.', red: true }
        : { eyebrow: '⚖️ THE LINEUP', big: 'Whose story held up?', why: 'The weakest gets booked. Vote.' })
      : needsRoll
        ? { eyebrow: '🎲 INSTINCT ROLL', big: 'Your instinct roll is waiting.', why: 'How sharp are you tonight? Tap the die.' }
        : { eyebrow: '🖋️ YOUR MOVE', big: 'The room is listening.', why: 'Make your statement below. Weak stories get remembered.' };
    return (
      <div style={{
        marginBottom: 8, padding: '10px 14px', borderRadius: 12, textAlign: 'center',
        border: `1px solid ${d.red ? 'rgba(220,38,38,0.6)' : 'rgba(217,186,114,0.55)'}`,
        background: d.red ? 'rgba(127,29,29,0.16)' : 'rgba(217,186,114,0.1)',
        animation: 'pulse 1.6s ease-in-out infinite',
      }}>
        <div style={{ fontSize: '0.66em', fontWeight: 900, letterSpacing: '0.16em', color: d.red ? '#ff8a7a' : '#d9ba72', marginBottom: 3 }}>
          {d.eyebrow}
        </div>
        <div style={{ color: '#eed9a4', fontWeight: 900, letterSpacing: '0.03em', fontSize: 'clamp(13px, 2.6vmin, 16px)' }}>
          {d.big}
        </div>
        <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: 'clamp(11px, 2.2vmin, 13px)', marginTop: 2 }}>
          {d.why}
        </div>
        {needsVote && (
          <button
            onClick={() => {
              clearTimeout(votingBannerTimerRef.current);
              setVotingBanner(null);
              setShowVotingModal(true);
            }}
            style={{
              marginTop: 8, background: d.red ? '#dc2626' : '#d9ba72', color: d.red ? '#fff' : '#151009',
              border: 'none', borderRadius: 8, padding: '9px 22px', fontWeight: 900, cursor: 'pointer',
              letterSpacing: '0.06em', fontSize: 'clamp(12px, 2.4vmin, 14px)', textTransform: 'uppercase',
            }}
          >Cast your vote</button>
        )}
      </div>
    );
  };

  // 🎛️ CASE LOBBY DOCK (center-dock redesign, Nina approved the mock 07-25) —
  // pre-join, whodunnit only: the join CTAs move from the roster to a fixed
  // bottom-center card (the roster keeps them for the explorer bot, whose join
  // flow is trained on those buttons). Mobile: sits above the bottom nav using
  // the same clearance as the thumb dock.
  const renderCaseLobbyDock = (inline = false) => {
    if (isBotMode) return null;
    if (!(chatGameStatus?.noir_theme && chatGameStatus?.competitive_mode)) return null;
    if (!createdPlayerIdRef.current || gameStarted || hasJoinedGame || showGlitchReveal) return null;
    // Mobile: the spectator thumb dock (z 5000) owns the bottom pre-join — the
    // join card rides INSIDE it as the first stacked item (inline variant), so
    // ghost chat stays usable below it. Desktop floats the card bottom-center.
    if (inline !== isMobile) return null;
    const headline = chatGameStatus?.scenario?.cold_open?.headline || 'A BODY HAS TURNED UP!';
    return (
      <div style={inline ? { marginBottom: 6 } : {
        // Middle of the screen (Nina 07-29): the pre-join ask joins the other
        // center-stage prompts instead of hugging the bottom edge.
        position: 'fixed', left: '50%', top: '50%', transform: 'translate(-50%, -50%)',
        zIndex: 4900, width: 'min(560px, calc(100vw - 24px))',
      }}>
        <style>{`@keyframes caseDockGlow { 0%, 100% { box-shadow: 0 14px 44px rgba(0,0,0,0.6); } 50% { box-shadow: 0 14px 44px rgba(0,0,0,0.6), 0 0 22px 2px rgba(217,186,114,0.35); } }`}</style>
        <div style={{
          background: 'rgba(23,19,28,0.96)', border: '1px solid rgba(217,186,114,0.55)',
          borderRadius: 14, padding: '13px 16px', textAlign: 'center',
          backdropFilter: 'blur(6px)', animation: 'caseDockGlow 1.8s ease-in-out infinite',
        }}>
          <div style={{ fontSize: '0.64em', fontWeight: 900, letterSpacing: '0.16em', color: '#d9ba72', marginBottom: 6 }}>
            🕵️ A CASE IS FORMING
          </div>
          <div style={{ color: '#eed9a4', fontWeight: 900, fontSize: 'clamp(14px, 2.8vmin, 17px)', letterSpacing: '0.03em', marginBottom: 3 }}>
            {headline}
          </div>
          <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: 'clamp(11px, 2.2vmin, 13px)', marginBottom: 11 }}>
            Step into the lineup, or watch from the gallery.
          </div>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
            <button
              onClick={() => handleQuickJoin('play')}
              style={{
                background: 'linear-gradient(135deg, #2EE85F 0%, #16C64C 100%)', color: '#06130a',
                border: 'none', borderRadius: 10, padding: '12px 26px', fontWeight: 900,
                fontSize: 'clamp(13px, 2.6vmin, 15px)', letterSpacing: '0.05em', cursor: 'pointer',
                textTransform: 'uppercase', minHeight: 44,
              }}
            >🔍 Investigate!</button>
            <button
              onClick={() => handleQuickJoin('spectate')}
              style={{
                background: 'transparent', color: 'rgba(255,255,255,0.75)',
                border: '1px solid rgba(255,255,255,0.3)', borderRadius: 10, padding: '12px 22px',
                fontWeight: 800, fontSize: 'clamp(13px, 2.6vmin, 15px)', cursor: 'pointer', minHeight: 44,
              }}
            >👁 Spectate</button>
          </div>
        </div>
      </div>
    );
  };

  // 🏥 Hospitalized (poisoned) notice — the composer is disabled for the round;
  // this says why, in-fiction. Clears automatically next round (payload flag).
  const renderHospitalNotice = () => {
    if (!chatGameStatus?.you_are_hospitalized) return null;
    return (
      <div style={{
        marginBottom: 8, padding: '9px 12px', borderRadius: 10, textAlign: 'center',
        border: '1px solid rgba(245,158,11,0.5)', background: 'rgba(120,53,15,0.2)',
        color: '#fcd34d', fontWeight: 800, fontSize: 'clamp(12px, 2.4vmin, 14px)',
      }}>
        🏥 You woke up in the hospital. Something you ate. Your evidence is sealed and you can't testify this round. Back on your feet next round.
      </div>
    );
  };

  // ☠️ THE POISON strip (killer mode §2, Nina 07-19) — killer-only, separate
  // from the one-boss dock (it's an OFFER, never a required action). Appears
  // only while the server says a hunter is hot; the d20 rolls SERVER-side and
  // the result flashes here privately (never the public dice board).
  const renderPoisonStrip = () => {
    if (isBotMode) return null;
    const p = chatGameStatus?.poison;
    if (!(chatGameStatus?.noir_theme && chatGameStatus?.you_did_it && p)) return null;
    if (chatGameStatus?.game_completed) return null;
    const strip = (children) => (
      <div style={{
        display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', justifyContent: 'center',
        marginBottom: 8, padding: '9px 12px', borderRadius: 10,
        border: '1px solid rgba(220,38,38,0.55)', background: 'rgba(127,29,29,0.18)',
      }}>{children}</div>
    );
    if (poisonResult) {
      const r = poisonResult;
      return strip(
        <span style={{ color: '#fca5a5', fontWeight: 800, fontSize: 'clamp(12px, 2.4vmin, 14px)' }}>
          🎲 You rolled {r.roll}. {r.outcome === 'hit'
            ? `${r.target_name} won't testify this round. Nobody saw you.`
            : r.outcome === 'fumble'
              ? 'You spilled it on your own sleeve. That will surface.'
              : 'Nothing happened. The dose is gone and nobody knows.'}
        </span>
      );
    }
    if (p.used || !p.offer) return null;
    return strip(
      <>
        <span style={{ color: '#fca5a5', fontWeight: 800, letterSpacing: '0.03em', fontSize: 'clamp(12px, 2.4vmin, 14px)' }}>
          ☠️ You happen to have {p.reskin} in your pocket. {p.offer.target_name} is getting too close.
        </span>
        {!poisonArmed ? (
          <button
            onClick={() => setPoisonArmed(true)}
            style={{
              background: '#dc2626', color: '#fff', border: 'none', borderRadius: 8,
              padding: '7px 16px', fontWeight: 900, cursor: 'pointer', letterSpacing: '0.06em',
              fontSize: 'clamp(12px, 2.4vmin, 14px)',
            }}
          >USE IT?</button>
        ) : (
          <>
            <button
              onClick={async () => {
                if (poisonBusy) return;
                setPoisonBusy(true);
                try {
                  const res = await chatGameApi.investigationAction(gameId, effectivePlayerId, { op: 'poison' });
                  if (res?.poison) {
                    setPoisonResult(res.poison);
                    setTimeout(() => setPoisonResult(null), 12000);
                  }
                  fetchChatGameStatus();
                } catch (e) {
                  console.error('[Poison] failed:', e.message);
                } finally {
                  setPoisonBusy(false);
                  setPoisonArmed(false);
                }
              }}
              style={{
                background: '#991b1b', color: '#fff', border: '1px solid #f87171', borderRadius: 8,
                padding: '7px 16px', fontWeight: 900, cursor: 'pointer', letterSpacing: '0.06em',
                fontSize: 'clamp(12px, 2.4vmin, 14px)',
              }}
            >{poisonBusy ? 'POURING...' : '🎲 DO IT. ROLL.'}</button>
            <button
              onClick={() => setPoisonArmed(false)}
              style={{
                background: 'none', color: 'rgba(255,255,255,0.6)', border: 'none',
                cursor: 'pointer', fontSize: '0.75em', textDecoration: 'underline', fontFamily: 'inherit',
              }}
            >walk away</button>
          </>
        )}
      </>
    );
  };

  // Crit power picker (Dice Powers Increment 3). Shown above the turn input when the viewer
  // rolled a CRITICAL: your_power.status === 'offered' with a choices list. The human picks a
  // power (+ a target, + Puppet text), which POSTs to applyPower. AIs/bot never see this (they
  // auto-resolve server-side at roll time), so it's purely a human affordance.
  const submitCritPower = async () => {
    const yp = chatGameStatus?.your_power;
    const choice = yp?.choices?.find(c => c.id === critPick.power_id);
    if (!choice || critSubmitting) return;
    if (choice.target === 'other' && !critPick.target_id) return;
    if (choice.composer && !critPick.puppet_text.trim()) return;
    setCritSubmitting(true);
    try {
      await chatGameApi.applyPower(gameId, humanPlayerId, {
        power_id: critPick.power_id,
        target_id: choice.target === 'other' ? critPick.target_id : null,
        puppet_text: choice.composer ? critPick.puppet_text.trim() : null,
      });
      setCritResolvedRound(diceRoundKey); // optimistic hide until the poll reflects status 'used'
      setCritPick({ power_id: null, target_id: null, puppet_text: '' });
    } catch (e) {
      console.error('[critPicker] applyPower failed:', e.message);
    } finally {
      setCritSubmitting(false);
    }
  };

  const renderCritPicker = () => {
    if (isBotMode) return null;
    // Only after the die is revealed (the CRITICAL is the reveal payoff), mirroring renderFateDie.
    if (diceRevealedRound !== diceRoundKey) return null;
    const yp = chatGameStatus?.your_power;
    if (!yp || yp.band !== 'crit' || yp.status !== 'offered') return null;
    if (critResolvedRound === diceRoundKey) return null;
    const choices = Array.isArray(yp.choices) ? yp.choices : [];
    if (!choices.length) return null;

    const selected = choices.find(c => c.id === critPick.power_id) || null;
    const eliminated = new Set(chatGameStatus?.eliminated_players || []);
    // Players who have already taken their turn this round. All targeted powers
    // (Silence/Puppet/Hijack) act on the target's NEXT turn THIS round — the effect lives on
    // the round row and is discarded at the round boundary, so casting on someone who has
    // already spoken silently does nothing (audit B1). Filter them out.
    const spokeThisRound = new Set(chatGameStatus?.dice?.revealed_ids || []);
    // Eligible targets: other players in the game, not eliminated. Puppet is human→AI only.
    const targets = (users || [])
      .filter(u => u.game_player_id && u.game_player_id !== humanPlayerId && !eliminated.has(u.game_player_id))
      .filter(u => !selected?.humanOnly || !!u.model) // Puppet: AI only (humans have null model)
      .filter(u => selected?.target !== 'other' || !spokeThisRound.has(u.game_player_id));

    const GOLD = '#ffd700';
    const canSubmit = !!selected
      && (selected.target !== 'other' || !!critPick.target_id)
      && (!selected.composer || !!critPick.puppet_text.trim())
      && !critSubmitting;

    return (
      <div style={{ marginBottom: '8px', padding: '10px 12px', borderRadius: '10px', border: `1px solid ${GOLD}`, backgroundColor: `${GOLD}14` }}>
        <div style={{ fontSize: '0.85em', fontWeight: 800, color: GOLD, marginBottom: '6px' }}>🌟 CRITICAL! Choose your power</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: selected ? '8px' : 0 }}>
          {choices.map(c => {
            const active = critPick.power_id === c.id;
            return (
              <button key={c.id} type="button"
                onClick={() => setCritPick({ power_id: c.id, target_id: null, puppet_text: '' })}
                title={c.desc}
                style={{
                  padding: '5px 10px', borderRadius: '8px', cursor: 'pointer', fontSize: '0.8em', fontWeight: 700,
                  border: `1px solid ${active ? GOLD : THEME.textMuted}`,
                  backgroundColor: active ? `${GOLD}30` : 'transparent',
                  color: active ? GOLD : THEME.textDark,
                }}>
                {c.display_name || c.name}
              </button>
            );
          })}
        </div>
        {selected && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <div style={{ fontSize: '0.76em', color: THEME.textDark, lineHeight: 1.35 }}>{selected.desc}</div>
            {selected.target === 'other' && targets.length > 0 && (
              <select value={critPick.target_id || ''} onChange={e => setCritPick(p => ({ ...p, target_id: e.target.value }))}
                /* 16px floor on phones (08-01, audit #20): sub-16 form controls make iOS zoom the whole page on focus */
                style={{ padding: '5px 8px', borderRadius: '8px', border: `1px solid ${THEME.textMuted}`, fontSize: isMobile ? 16 : '0.8em', backgroundColor: THEME.cardBg || '#fff', color: THEME.textDark }}>
                <option value="">{selected.humanOnly ? 'Pick an AI to puppet…' : 'Pick a target…'}</option>
                {targets.map(u => <option key={u.game_player_id} value={u.game_player_id}>{u.name}</option>)}
              </select>
            )}
            {selected.target === 'other' && targets.length === 0 && (
              <div style={{ fontSize: '0.76em', fontStyle: 'italic', color: THEME.textMuted }}>
                No eligible targets — everyone else has already acted this round. Pick a different power.
              </div>
            )}
            {selected.composer && (
              <textarea value={critPick.puppet_text} onChange={e => setCritPick(p => ({ ...p, puppet_text: e.target.value }))}
                placeholder="Write the message that will post under their name…" rows={2}
                style={{ padding: '6px 8px', borderRadius: '8px', border: `1px solid ${THEME.textMuted}`, fontSize: isMobile ? 16 : '0.8em', resize: 'vertical', fontFamily: 'inherit', color: THEME.textDark }} />
            )}
            <button type="button" disabled={!canSubmit} onClick={submitCritPower}
              style={{
                padding: '6px 12px', borderRadius: '8px', fontSize: '0.82em', fontWeight: 800, border: 'none',
                cursor: canSubmit ? 'pointer' : 'not-allowed', opacity: canSubmit ? 1 : 0.5,
                backgroundColor: GOLD, color: '#1a1a1a', alignSelf: 'flex-start',
              }}>
              {critSubmitting ? 'Casting…' : `Use ${selected.display_name || selected.name}`}
            </button>
          </div>
        )}
      </div>
    );
  };

  // Word Ban v2 picker (SUCCESS 11-13 roll): the human casts a targeted ban — a rival + ONE
  // word of their choice (fully free; the backend only enforces single-token + length). The
  // ban is SECRET until the round-end reveal, so no target filter on who already spoke: it
  // bites their interjections and the rest of the round either way. Mirrors the crit picker's
  // offer→applyPower→optimistic-hide flow; AIs/bot auto-cast server-side and never see this.
  const submitWordBan = async () => {
    const word = banPick.word.trim();
    if (!banPick.target_id || !word || banSubmitting) return;
    setBanSubmitting(true);
    try {
      await chatGameApi.applyPower(gameId, humanPlayerId, {
        power_id: 'word_ban',
        target_id: banPick.target_id,
        word,
      });
      setBanResolvedRound(diceRoundKey);
      setBanPick({ target_id: '', word: '' });
    } catch (e) {
      console.error('[wordBanPicker] applyPower failed:', e.message);
    } finally {
      setBanSubmitting(false);
    }
  };

  const renderWordBanPicker = () => {
    if (isBotMode) return null;
    if (diceRevealedRound !== diceRoundKey) return null; // ban offer is the reveal payoff, like the crit
    const yp = chatGameStatus?.your_power;
    if (!yp || yp.band !== 'success' || yp.status !== 'offered' || yp.power_id !== 'word_ban') return null;
    if (banResolvedRound === diceRoundKey) return null;

    const eliminated = new Set(chatGameStatus?.eliminated_players || []);
    const targets = (users || [])
      .filter(u => u.game_player_id && u.game_player_id !== humanPlayerId && !eliminated.has(u.game_player_id));

    const ORANGE = '#ff9800';
    const canCast = !!banPick.target_id && !!banPick.word.trim() && !banSubmitting;
    return (
      <div style={{ marginBottom: '8px', padding: '10px 12px', borderRadius: '10px', border: `1px solid ${ORANGE}`, backgroundColor: `${ORANGE}14` }}>
        <div style={{ fontSize: '0.85em', fontWeight: 800, color: ORANGE, marginBottom: '4px' }}>
          🚫 {yp.display_name || 'Word Ban'} — ban ONE word for a rival
        </div>
        <div style={{ fontSize: '0.76em', color: THEME.textDark, lineHeight: 1.35, marginBottom: '6px' }}>
          {yp.desc || 'Pick any word — it\'s banned from their messages for the rest of the round. They\'ll see the word, but not who cast it, until the round-end reveal.'}
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', alignItems: 'center' }}>
          <select value={banPick.target_id} onChange={e => setBanPick(p => ({ ...p, target_id: e.target.value }))}
            /* 16px floor on phones (08-01, audit #20) — see crit picker */
            style={{ padding: '5px 8px', borderRadius: '8px', border: `1px solid ${THEME.textMuted}`, fontSize: isMobile ? 16 : '0.8em', backgroundColor: THEME.cardBg || '#fff', color: THEME.textDark }}>
            <option value="">Pick a target…</option>
            {targets.map(u => <option key={u.game_player_id} value={u.game_player_id}>{u.name}</option>)}
          </select>
          <input type="text" value={banPick.word} maxLength={30}
            onChange={e => setBanPick(p => ({ ...p, word: e.target.value.replace(/\s/g, '') }))}
            placeholder="word to ban…"
            style={{ padding: '5px 8px', borderRadius: '8px', border: `1px solid ${THEME.textMuted}`, fontSize: isMobile ? 16 : '0.8em', width: isMobile ? '150px' : '120px', backgroundColor: THEME.cardBg || '#fff', color: THEME.textDark }} />
          <button type="button" disabled={!canCast} onClick={submitWordBan}
            style={{
              padding: '6px 12px', borderRadius: '8px', fontSize: '0.82em', fontWeight: 800, border: 'none',
              cursor: canCast ? 'pointer' : 'not-allowed', opacity: canCast ? 1 : 0.5,
              backgroundColor: ORANGE, color: '#1a1a1a',
            }}>
            {banSubmitting ? 'Casting…' : 'Ban it'}
          </button>
        </div>
      </div>
    );
  };

  // Active-effect indicators (Dice Powers Increment 3): compact chips above the turn input
  // showing the viewer's in-play power for the round — Extra Time, Word Ban, Shield, a cast
  // crit (Silence/Hijack/Puppet → target), and the "you were silenced" cue. Read-only.
  const renderPowerStatus = () => {
    if (isBotMode) return null;
    const chips = [];
    // Whodunnit guilty whisper — 07-30 rev 2 (Nina: "should it look different?"):
    // OUT of the chips row entirely. Chips are game-state the room could name;
    // this is a private thought, so it gets NO chrome — bare italic text under
    // the strip, like a conscience, rendered below with the chips.
    const showGuiltyWhisper = !!(chatGameStatus?.you_did_it && !noirWriteDocket);
    if (chatGameStatus?.case_file && !noirWriteDocket) {
      // Innocents-only case-file briefing — the killer never sees this chip.
      // Docket turns hide it (Nina 07-30: the composer should carry as little
      // as possible; the CASE FILE tab is right there and already holds this).
      chips.push({ k: 'case', t: `🗂️ Case file: ${chatGameStatus.case_file}`, c: '#8d6e63' });
    }
    if (chatGameStatus?.you_are_silenced) {
      chips.push({ k: 'sil', t: '🤐 You were silenced. You’ll skip a turn.', c: '#ff6b6b' });
    }
    const yp = chatGameStatus?.your_power;
    const revealed = diceRevealedRound === diceRoundKey;
    if (revealed && yp && yp.status && yp.status !== 'offered') {
      if (yp.power_id === 'extra_time') {
        chips.push({ k: 'xt', t: '⏱️ +45s this turn', c: THEME.good });
      } else if (yp.power_id === 'word_ban' && yp.band === 'fumble' && yp.word) {
        // Legacy (pre-v2 round mid-deploy): the old one-word SELF-ban.
        chips.push({ k: 'wb', t: `🚫 Banned word: “${yp.word}”`, c: '#ff9800' });
      } else if (yp.power_id === 'word_ban' && yp.word && yp.target_id) {
        // v2: YOU cast a targeted ban — remind the caster what they planted where.
        // 07-30 copy fix (Nina screenshot): the old `name → target: "word"` read as
        // cipher text. Plain sentence, no arrows.
        const banTName = (users || []).find(u => u.game_player_id === yp.target_id)?.name || 'a rival';
        chips.push({ k: 'wbc', t: `🚫 ${yp.display_name || 'Word Ban'}: ${banTName} can't say “${yp.word}”`, c: '#ff9800' });
      } else if (yp.power_id === 'word_curse') {
        // v2 fumble: the loud banner above the input carries the full list; this chip is
        // just the scoreboard-style reminder.
        chips.push({ k: 'wc', t: `💀 ${yp.display_name || 'Lexicon Curse'}: the whole forbidden lexicon is banned this round`, c: '#ff5252' });
      } else if (yp.power_id === 'shield') {
        chips.push({ k: 'sh', t: `🛡️ ${yp.display_name || 'Shield'} ready`, c: '#42a5f5' });
      } else if (yp.band === 'crit' && yp.power_id) {
        const tName = (users || []).find(u => u.game_player_id === yp.target_id)?.name || 'a rival';
        const tail = yp.status === 'blocked' ? 'was blocked by a Shield' : (yp.target_id ? `→ ${tName}` : '');
        chips.push({ k: 'cr', t: `🌟 ${yp.display_name || 'Power'} ${tail}`.trim(), c: '#ffd700' });
      }
    }
    if (!chips.length && !showGuiltyWhisper) return null;
    return (
      <>
        {chips.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '8px' }}>
            {chips.map(ch => (
              <span key={ch.k} style={{ fontSize: '0.75em', fontWeight: 700, padding: '3px 8px', borderRadius: '8px', border: `1px solid ${ch.c}`, color: ch.c, backgroundColor: `${ch.c}14` }}>{ch.t}</span>
            ))}
          </div>
        )}
        {showGuiltyWhisper && !noirVoteAskOpen() && (
          /* the conscience only leans over an active composer — while the vote
             owns the screen it stays quiet (the case file remembers for you) */
          <div style={{ fontSize: '0.68em', fontStyle: 'italic', color: 'rgba(239,68,68,0.55)', margin: '-2px 2px 8px', letterSpacing: '0.03em' }}>
            🤫 you know what you did. bluff.
          </div>
        )}
      </>
    );
  };
  const [showVoteResultsReveal, setShowVoteResultsReveal] = useState(null); // Vote results reveal overlay data
  const [shownVoteResults, setShownVoteResults] = useState(new Set()); // Track which vote results we've shown
  const [playRoundPauseToast, setPlayRoundPauseToast] = useState(null); // Pause toast message
  const [remotePlayback, setRemotePlayback] = useState(false); // True when another client is driving playback
  const [showGlitchReveal, setShowGlitchReveal] = useState(null); // Glitch reveal overlay data snapshot { isImposter, topic } or null
  const glitchRevealShownRef = useRef(false); // Prevent re-showing on subsequent status polls
  // Whodunnit guilty-role reveal (mirrors the Glitch reveal): { didIt, caseFile } or null.
  // Triggered by the STATUS POLL (not join) because the killer is assigned at round-1
  // creation, after ready-check — the data doesn't exist at join time.
  const [showGuiltyReveal, setShowGuiltyReveal] = useState(null);
  // Cinematic phases for the reveal: 'night' (rain + flicker) → 'body' (lightning strobe +
  // "A BODY HAS TURNED UP!") → 'role' (your card). Tap skips ahead; tap on 'role' dismisses.
  const [guiltyRevealPhase, setGuiltyRevealPhase] = useState('night');
  const guiltyRevealShownRef = useRef(false);
  // True once the entry curtain's ENTER was clicked this session — the cold open then
  // skips its own "night of the crime" beat (the curtain already played that scene).
  const entryCurtainSeenRef = useRef(false);
  // Atmospheric cold open (night → body beats, no role card) already played this
  // session — the later role reveal cuts straight to the card instead of replaying it.
  const coldOpenPlayedRef = useRef(false);
  // Door beat between the Investigate click and the role card: creak + 🚪 ENTER
  // overlay filling the game-start seconds (cleared by the role reveal or 8s cap).
  const [doorTransition, setDoorTransition] = useState(false);
  // Storm-curtain door swing: brief creak+swing animation between the click on the
  // storm interstitial and the cold open (user, 07-10: the door effect lives THERE).
  const [curtainDoorOpening, setCurtainDoorOpening] = useState(false);
  // [bot-theater 07-12] The render closure parks its openCurtainDoor here so the
  // bot-mode auto-open effect below can call it — bots see the storm beat on
  // stream for ~4.5s, then the door swings itself (a human taps; the bot's timer
  // taps for it). Guarantees the full-screen curtain can never wedge the bot.
  const openCurtainDoorRef = useRef(null);
  const curtainAutoOpenedRef = useRef(false);
  useEffect(() => {
    if (!isBotMode || curtainAutoOpenedRef.current) return undefined;
    if (!chatGameStatus?.entry_screen || !chatGameStatus?.competitive_mode) return undefined;
    if (entryCurtainSeenRef.current) return undefined;
    const t = setTimeout(() => {
      curtainAutoOpenedRef.current = true;
      try {
        if (openCurtainDoorRef.current) openCurtainDoorRef.current();
      } catch (e) { /* theater is best-effort — never break the bot page */ }
    }, 4500);
    return () => clearTimeout(t);
  }, [isBotMode, chatGameStatus]);
  // Dice roll-reveal toast: fires once per player as their roll goes public (revealed_ids
  // grows when they take their turn). Queue + active flag serialize back-to-back reveals.
  const [diceRollToast, setDiceRollToast] = useState(null); // { name, value, band_name, emoji, ui, line }
  const diceToastSeenRef = useRef({ round: null, ids: new Set() });
  const diceToastQueueRef = useRef([]);
  const diceToastActiveRef = useRef(false);
  // Beat presenter — new announcements show one bite-size beat at a time with a Next
  // button (8s auto-advance). Chat history is untouched; this is presentation only.
  const [announcementBeat, setAnnouncementBeat] = useState(null); // { text, index, total }
  const beatSeenRef = useRef({ primed: false, ids: new Set() });
  const beatQueueRef = useRef([]);
  const beatActiveRef = useRef(false);
  const beatTimerRef = useRef(null);
  const beatPauseRef = useRef(false);
  const imposterModeRef = useRef(false); // Track imposter mode for auto-start (avoids stale closure in polling)
  const competitiveModeRef = useRef(false); // Track competitive mode for timing decisions in polling callbacks
  const imposterAutoStartedRef = useRef(false); // Prevent double-fire from polling + WS both triggering

  // Early competitive/imposter mode detection from localStorage — readable at frame 0 before chatGameStatus loads
  const [earlyCompetitiveMode, setEarlyCompetitiveMode] = useState(() => {
    try {
      const stored = localStorage.getItem(`game_mode_${searchParams.get('gameId')}`);
      return stored ? !!JSON.parse(stored).competitive : false;
    } catch(e) { return false; }
  });
  const handleRoundJoinRef = useRef(null); // Always points to latest handleRoundJoin closure

  // Ready check state
  const [showReadyCheck, setShowReadyCheck] = useState(false);
  const [readyCheckData, setReadyCheckData] = useState(null); // { ready_count, total_humans, ready_player_names }
  const [readyCheckSubmitted, setReadyCheckSubmitted] = useState(false);
  const readyCheckDismissedRef = useRef(false); // Once user clicks Ready/Spectate, never show again
  // Shorter countdown for competitive/imposter modes (6s vs 10s)
  const readyCountdownDuration = (chatGameStatus?.competitive_mode || chatGameStatus?.imposter_mode) ? 6 : 10;
  const [readyCheckCountdown, setReadyCheckCountdown] = useState(10);
  const readyCheckStartTimeRef = useRef(null);
  // Ready check phases: 'waiting' (no one ready yet), 'draining' (drain bar active), 'ready_to_start' (drain done, Play Round clickable)
  const [readyCheckPhase, setReadyCheckPhase] = useState('waiting');
  const [autoStartCountdown, setAutoStartCountdown] = useState(5);
  const autoStartTimeRef = useRef(null);
  const gameStartedRef = useRef(false); // Prevents ready check phases from restarting after "Start Game!" is clicked
  const [hasJoinedGame, setHasJoinedGame] = useState(false); // Once true, never resets — hides quickJoin buttons permanently
  const [gameStarted, setGameStarted] = useState(false); // Triggers re-render for header text

  // Ready check active flag
  const isReadyCheckActive = showReadyCheck || (chatGameStatus?.is_ready_check && !chatGameStatus?.ready_check_data?.done);

  // Hide contextual hints while loading overlays are active
  const hintsHidden = showAssetLoadingOverlay || showInitialLoading;

  // Emit custom music URL to LofiRadio when game data is loaded
  // ⛈️🎷 Storm-before-jazz (Nina 07-30: "music plays right away" over the
  // interstitial storm): in a noir comp game the storm bed owns the audio
  // through loading, the door beat, and the cold open — the pack's jazz
  // (music_url → LofiRadio) is HELD (emits null) until every intro overlay
  // has cleared, then releases at the same moment the storm hands off to the
  // pack bed. Spectators skip the guilty reveal, so the loading/cinematic
  // overlays alone gate them. Non-noir spaces keep the instant walk-in start.
  // The full intro-overlay set: door beat, curtain, loading screens, cinematic,
  // cold open. ONE flag so the storm bed, the pack-bed handoff, and the jazz
  // hold all agree on when "the interstitial" ends (Nina 07-30 round 2: the
  // café bed was stomping the storm DURING LOADING — the old guard only
  // covered the cold open).
  const noirIntroUp = showInitialLoading || showAssetLoadingOverlay || !!cinematicPhase
    || !!showGuiltyReveal || doorTransition || curtainDoorOpening;
  const noirMusicHold = !!(chatGameStatus?.noir_theme && chatGameStatus?.competitive_mode)
    && noirIntroUp;
  const musicUrlRef = useRef(undefined); // Track previous value to avoid redundant emits
  useEffect(() => {
    if (!chatGameStatus) return;
    const url = noirMusicHold ? null : (chatGameStatus.music_url || null);
    // 🎵 J1: the house music queue rides along so LofiRadio can loop it through
    // idle stretches. 🎶 Key is a full url-signature since /jukebox (08-13):
    // human adds can evict-and-append at the same LENGTH, and each add rebases
    // jukebox_started_at — both must re-emit so the deck + shared clock land.
    const queue = url && Array.isArray(chatGameStatus.music_queue) && chatGameStatus.music_queue.length
      ? chatGameStatus.music_queue : null;
    // 🎶 /jukebox shared clock — LofiRadio computes "where is the room's
    // needle" locally from these + per-entry duration_s (no staleness: the
    // walk runs at its own freeze moments, not at emit time).
    const jukebox = url && chatGameStatus.jukebox_started_at
      ? { started_at: chatGameStatus.jukebox_started_at, head_duration_s: chatGameStatus.music_url_duration_s || null }
      : null;
    const emitKey = `${url || ''}|${queue ? queue.map(q => q?.url || '').join(',') : ''}|${jukebox ? jukebox.started_at : ''}`;
    if (emitKey !== musicUrlRef.current) {
      musicUrlRef.current = emitKey;
      // 🎚️ Noir scene music ducks to 25/100 (Nina 07-30): the jazz is a bed
      // under the scene, not the show — the pack's ambience SFX keep playing
      // alongside it and must stay audible. Non-noir space music: full volume.
      const noirDuck = !!(url && chatGameStatus?.noir_theme && chatGameStatus?.competitive_mode);
      // 🎚️ Space music defaults to 30/100 (Nina 08-06, was 40 since 08-01;
      // before that, no volume sent = the embed played at FULL blast). 30 = a
      // soundtrack under the room, not over it; noir keeps its 25 jazz bed.
      // Users can still raise it on the player itself.
      emitGameEvent(GAME_EVENTS.MUSIC_URL_CHANGED, { musicUrl: url, ...(url ? { volume: noirDuck ? 25 : SPACE_MUSIC_VOLUME } : {}), ...(queue ? { queue } : {}), ...(jukebox ? { jukebox } : {}), ...(url && chatGameStatus.music_url_title ? { headTitle: chatGameStatus.music_url_title } : {}), ...(url && chatGameStatus.house_aux_holder ? { auxHolder: chatGameStatus.house_aux_holder } : {}), ...(chatGameStatus.competitive_mode || chatGameStatus.imposter_mode ? { competitive: true } : {}) });
    }
    // Keep the per-space cache current (it powers the instant start below);
    // clearing it here also heals a stale cache when the builder removes music.
    // Noir comp games NEVER cache: the walk-in instant start fires on the first
    // frame of loading (before status can say "noir"), which is exactly the
    // storm's moment — a cached whodunnit jazz url would stomp it on revisits.
    try {
      if (url && !(chatGameStatus?.noir_theme && chatGameStatus?.competitive_mode)) {
        localStorage.setItem(`spaceMusicUrl:${gameId}`, url);
      } else {
        localStorage.removeItem(`spaceMusicUrl:${gameId}`);
      }
    } catch (e) { /* private mode */ }
  }, [chatGameStatus?.music_url, chatGameStatus?.music_queue, chatGameStatus?.jukebox_started_at, chatGameStatus?.music_url_duration_s, gameId, noirMusicHold, chatGameStatus?.noir_theme, chatGameStatus?.competitive_mode]);

  // 🕵️ Case wins the room (Nina 07-25): while a competitive whodunnit is live,
  // the global LofiRadio FAB hides — the scenario ambience beds own the audio
  // and the FAB stacked over the investigation stage's timer/case-file corner.
  // Emitted both ways so leaving the page or finishing the game restores it.
  // 07-30 AMENDED (Nina: scene jazz via the existing YouTube player): a pack
  // that brings its own music_url KEEPS the player — hide only when the case
  // has no soundtrack. ⚠️ Validate the FAB vs the investigation-stage corner
  // (the overlap was half of why 07-25 hid it).
  const noirCaseActive = !!(chatGameStatus?.noir_theme && chatGameStatus?.competitive_mode && !chatGameStatus?.game_completed && !chatGameStatus?.music_url);
  useEffect(() => {
    emitGameEvent(GAME_EVENTS.NOIR_CASE_ACTIVE, { active: noirCaseActive });
    return () => { if (noirCaseActive) emitGameEvent(GAME_EVENTS.NOIR_CASE_ACTIVE, { active: false }); };
  }, [noirCaseActive]);

  // D6 walk-in soundtrack: on a REVISIT we know this space's music before any
  // fetch returns — play it from the very first frame of the loading screen.
  // First-ever visits start when the initial data lands (still mid-loading).
  useEffect(() => {
    if (!gameId) return;
    try {
      const cached = localStorage.getItem(`spaceMusicUrl:${gameId}`);
      if (cached && musicUrlRef.current === undefined) {
        musicUrlRef.current = cached;
        // Same 30/100 default as the status-load emit, so walk-in music
        // doesn't blast at 100 for the loading screen then drop.
        emitGameEvent(GAME_EVENTS.MUSIC_URL_CHANGED, { musicUrl: cached, volume: SPACE_MUSIC_VOLUME });
      }
    } catch (e) { /* private mode */ }
  }, [gameId]);

  // Leaving the space: hand LofiRadio a null so auto-started builder music
  // stops instead of following the user around the app.
  useEffect(() => () => {
    emitGameEvent(GAME_EVENTS.MUSIC_URL_CHANGED, { musicUrl: null });
  }, []);

  // Tutorial step configurations + control functions \u2014 DECLARED BELOW the
  // places/composer/quest state (search "TOUR \u00d7 QUEST MERGE"): the 08-02
  // non-comp beats read placesNav and the composer setters, which don't
  // exist yet at this point in the component (TDZ, the 07-26 lesson).

  // Strip internal-only instructions from displayed objective (e.g. Roast em up mode)
  const displayObjective = useMemo(() => {
    if (!chatGameStatus?.objective) return '';
    return chatGameStatus.objective
      .replace(/\s*[—–-]\s*Call out each AI by their model name[\s\S]*$/i, '')
      .trim();
  }, [chatGameStatus?.objective]);

  // Split a "tagline — objective" objective into the lore tagline + the actionable body
  // (UI only, for the round-intro): tagline becomes a subtitle, body is the objective shown.
  const objectiveTagline = useMemo(() => {
    const parts = (displayObjective || '').split(/\s*[—–]\s*/);
    if (parts.length < 2) return null;
    const head = parts[0].trim();
    // Only treat the lead segment as a lore "tagline" when it's a short label — not a
    // full sentence. (Villain Takeover's objective has a mid-sentence em-dash, which
    // would otherwise mangle into a run-on tagline + a lowercase fragment body.)
    if (head.length > 60 || /[.!?]/.test(head)) return null;
    return head;
  }, [displayObjective]);
  const objectiveBody = useMemo(() => {
    // A template may supply a UI-only short objective (e.g. Villain Takeover) — prefer it.
    if (chatGameStatus?.display_objective) return chatGameStatus.display_objective;
    // Ensure the displayed objective reads as a sentence (trailing period if it lacks one).
    const ensureStop = (s) => (s && !/[.!?…]$/.test(s.trim()) ? s.trim() + '.' : s);
    const parts = (displayObjective || '').split(/\s*[—–]\s*/);
    if (parts.length < 2) return ensureStop(displayObjective);
    const head = parts[0].trim();
    if (head.length > 60 || /[.!?]/.test(head)) return ensureStop(displayObjective);
    return ensureStop(parts.slice(1).join(' — ').trim());
  }, [displayObjective, chatGameStatus?.display_objective]);
  // Tagline subcaption for the interstitial: the derived lore lead, else the template tagline
  // (so games whose objective doesn't split — e.g. Villain — still get a lore subcaption).
  const interstitialTagline = objectiveTagline || chatGameStatus?.tagline || null;

  // Format a story seed / opening line as an inviting prompt to finish — ends in an ellipsis.
  const asOpeningLine = (s) => (s ? String(s).trim().replace(/\s*[.…]+\s*$/, '') + '…' : s);

  // Detect generic scene for competitive mode objective/scene swap on Let's Go screen
  const isGenericScene = useMemo(() => {
    if (!chatGameStatus?.scene) return false;
    return /^A group\s+(gathers|interested in)/i.test(chatGameStatus.scene);
  }, [chatGameStatus?.scene]);

  // During tutorial: keep Play mode active through the ready toggle step
  // Steps 0-3 (players panel, spectate dropdown, ready toggle, start button) need Play mode
  // so the ready toggle and start button are visible
  useEffect(() => {
    if (!tutorialActive) return;
    const isCompOrImposter = chatGameStatus?.competitive_mode || chatGameStatus?.imposter_mode;
    if (!isCompOrImposter) return;
    const lastPlayStepIndex = 3; // start button step — keep Play mode through this
    const pid = createdPlayerIdRef.current;
    if (!pid) return;
    if (tutorialStep <= lastPlayStepIndex) {
      // Stay in Play mode so ready toggle and start button are visible
      setHumanPlayerId(pid);
    } else {
      // Switch back to spectate for remaining steps
      setHumanPlayerId(null);
    }
  }, [tutorialActive, tutorialStep, chatGameStatus?.competitive_mode, chatGameStatus?.imposter_mode]);

  // Cycle 5b.2: in bot mode, auto-fire the "Let's Go" / "Enter" button when
  // the welcome interstitial appears. Mirrors the button's onClick handler at
  // ~line 33526 exactly so handleRoundJoin still fires (server-side player
  // registration is critical — without it, bot has no playerId).
  //
  // Guards:
  //   - isBotMode: only auto-dismiss for bots, never for humans
  //   - chatGameStatus + users.length: wait for data to be ready
  //   - (assetLoadingReady || showRoundAnnouncement): only fire when welcome
  //     would actually be visible
  //   - !isStartingRound: don't fire mid-start
  //   - !(showAssetLoadingOverlay && !assetLoadingReady): matches the welcome
  //     visibility check at line 33113 — don't fire when asset overlay is up
  //   - autoDismissedRoundRef per-round guard: prevent re-firing within the
  //     same round (round_number bumps create a new key, so multi-round games
  //     re-fire correctly per round)
  //
  // Uses handleRoundJoinRef.current (set at line 25925) to avoid stale closure
  // issues. Ref is guaranteed non-null by the time this effect first fires
  // (hooks run top-down during render; ref assigned at line 25925; effects
  // fire after render completes).
  const autoDismissedRoundRef = useRef(null);
  useEffect(() => {
    if (!isBotMode) return;
    if (!chatGameStatus || users.length === 0) return;
    if (!(assetLoadingReady || showRoundAnnouncement)) return;
    if (isStartingRound) return;
    if (showAssetLoadingOverlay && !assetLoadingReady) return;

    // game_id, NOT id — the status payload has no `id` field (this read was
    // always "undefined-N", collapsing the key to round number alone)
    const roundKey = `${chatGameStatus.game_id}-${chatGameStatus?.round_number || chatGameStatus?.current_competitive_round || 1}`;
    if (autoDismissedRoundRef.current === roundKey) return;
    autoDismissedRoundRef.current = roundKey;

    const isCompetitive = chatGameStatus?.competitive_mode || chatGameStatus?.imposter_mode;
    setIsStartingRound(true);
    setAssetLoadingReady(false);
    setShowRoundAnnouncement(false);
    handleRoundJoinRef.current?.().catch(err => {
      console.error('[Cycle 5b.2 bot-auto-dismiss] Failed to join round:', err);
    });
    if (!isCompetitive) {
      setCinematicPhase('blackout');
      setTimeout(() => { setShowAssetLoadingOverlay(false); setCinematicPhase('reveal'); }, 600);
      setTimeout(() => { setCinematicPhase('fadeIn'); }, 2200);
      setTimeout(() => { setCinematicPhase(null); setIsStartingRound(false); }, 2400);
    } else {
      setShowAssetLoadingOverlay(false);
      setIsStartingRound(false);
    }
  }, [isBotMode, assetLoadingReady, showRoundAnnouncement, isStartingRound, chatGameStatus, users.length, showAssetLoadingOverlay]);

  // Cycle 5g.23 (smoke#7): in bot mode, auto-dismiss the Glitch role-reveal
  // overlay. Imposter games SKIP the "Let's Go" interstitial (see ~line 22594)
  // and show this reveal overlay instead, which the 5b.2 bot-auto-dismiss above
  // does NOT cover. Bots can't reliably click "Got it — let's go" (the reveal
  // mounts late / animates in, so play_game's one-shot auto-click misses it and
  // returns got_it=not_found). Without dismissing, showGlitchReveal stays truthy
  // → pins the bot in spectator mode (is_spectating=true while turnIsForUs=true)
  // → the imposter word/turn input never renders → bot can't submit, ghost-chats.
  // Humans still click it normally. The role/topic are read from the API
  // (is_imposter/imposter_topic), NOT this overlay, so dismissing it loses the
  // bot nothing.
  //
  // DELAY = AUDIENCE-ACKNOWLEDGMENT WINDOW (Cycle 5g.23, 2026-06-24): the reveal
  // is part of the show — viewers watch the bot's stream to see whether it's the
  // Glitch and what the topic is. 1.2s was too fast to read; 6s gives a readable
  // beat, then dismisses well before the bot's own turn (1-3 min away) so the
  // audience watches the other players' turns on a clean playfield. The bot's
  // comprehension is NOT the binding constraint (it has role/topic from the API),
  // so this is sized purely for the audience. This timer is the load-bearing
  // dismiss — keep it deterministic; do not gate it on any bot action.
  //
  // FUTURE OPTION (deferred 2026-06-24): also have play_game deterministically
  // capture a screenshot of the reveal (off the critical path, NOT into the bot's
  // LLM context — image-history token cost; default capture-to-record) so the
  // reveal moment is preserved for the stream/debugging. Skipped for now.
  useEffect(() => {
    if (!isBotMode || !showGlitchReveal) return;
    const t = setTimeout(() => setShowGlitchReveal(null), 6000);
    return () => clearTimeout(t);
  }, [isBotMode, showGlitchReveal]);

  // Whodunnit guilty-role reveal trigger — MOVED below `effectiveIsSpectating`'s
  // declaration (~line 23936). It referenced that const in its dep array from up
  // here, 800+ lines BEFORE initialization → TDZ ReferenceError on every render
  // → white page for every non-bot visitor (2026-07-05, operator-reported,
  // "Cannot access 'Ua' before initialization" at GameStepPage.js:23117).

  // (dismissTutorialToast lived here — retired 08-07. Its callers are now
  // declineTutorial, which does the same seen-key bookkeeping AND the
  // tutorial.dismissed half that "No" was missing. Declared beside
  // skipTutorial, which it needs.)

  // Delay tutorial toast after loading screens dismiss (instant for comp/imposter, 2s otherwise)
  const [tutorialToastReady, setTutorialToastReady] = useState(false);
  const isCompOrImposter = chatGameStatus?.competitive_mode || chatGameStatus?.imposter_mode;
  // ⚠️ The toast-ready effect that lived here MOVED below the sceneIntro
  // block (08-17): it now waits out the arrival/scene splash, and sceneIntro
  // is declared ~1300 lines down — referencing it from here is a render-time
  // TDZ (the deps array evaluates during render; eslint and the build both
  // compile it clean — the 07-26 lesson). State stays declared here because
  // the render reads it long after either spot.

  // ⏱️ 15s auto-dismiss RETIRED (Nina 08-03, tour-opener v2): the ask is a
  // centered host dialog now — a question that vanishes mid-read reads as
  // broken. It waits for Yes / No / not-now / ✕; "not now" and ✕ are the
  // soft session-only outs, so nothing persists unattended across visits.

  // Attention pulse + contextual hints for Play/Spectate dropdown and Ready toggle
  const [showElementPulse, setShowElementPulse] = useState(false);
  const [showDropdownHint, setShowDropdownHint] = useState(false);
  const [showReadyHint, setShowReadyHint] = useState(false);
  const dropdownInteractedRef = useRef(false);
  const readyInteractedRef = useRef(false);

  // Start pulse 4s after loading completes, stop after 4 cycles (~6.4s)
  useEffect(() => {
    if (showAssetLoadingOverlay || showInitialLoading || tutorialActive) return;
    const pulseStart = setTimeout(() => setShowElementPulse(true), 4000);
    const pulseStop = setTimeout(() => setShowElementPulse(false), 10400); // 4s delay + 4 cycles × 1.6s
    return () => { clearTimeout(pulseStart); clearTimeout(pulseStop); };
  }, [showAssetLoadingOverlay, showInitialLoading, tutorialActive]);

  // Show dropdown hint after loading completes, only if not interacted
  // If tutorial just ended, show immediately (500ms); otherwise wait 10s
  const tutorialEndedRef = useRef(false);
  useEffect(() => {
    if (tutorialActive) { tutorialEndedRef.current = true; return; }
    if (showAssetLoadingOverlay || showInitialLoading) return;
    const delay = tutorialEndedRef.current ? 500 : 7000;
    const hintTimer = setTimeout(() => {
      if (!dropdownInteractedRef.current) setShowDropdownHint(true);
    }, delay);
    return () => clearTimeout(hintTimer);
  }, [showAssetLoadingOverlay, showInitialLoading, tutorialActive]);

  // Show ready hint only AFTER dropdown hint is dismissed (and not during tutorial)
  useEffect(() => {
    if (tutorialActive) return;
    if (showDropdownHint || !dropdownInteractedRef.current) return;
    if (readyInteractedRef.current) return;
    const readyHintTimer = setTimeout(() => {
      if (!readyInteractedRef.current) setShowReadyHint(true);
    }, 500);
    return () => clearTimeout(readyHintTimer);
  }, [showDropdownHint, tutorialActive]);

  // Hide tooltips immediately when tutorial starts
  useEffect(() => {
    if (tutorialActive) {
      setShowDropdownHint(false);
      setShowReadyHint(false);
    }
  }, [tutorialActive]);

  // Auto-dismiss hints when player takes the action
  useEffect(() => {
    if (readyCheckSubmitted) {
      dismissHint('ready_check');
      readyInteractedRef.current = true;
      setShowReadyHint(false);
    }
  }, [readyCheckSubmitted, dismissHint]);

  useEffect(() => {
    if (!showVotingModal) dismissHint('voting');
  }, [showVotingModal, dismissHint]);

  // Transition to 'draining' when the first human readies up
  useEffect(() => {
    if (!isReadyCheckActive) {
      setReadyCheckPhase('waiting');
      readyCheckStartTimeRef.current = null;
      autoStartTimeRef.current = null;
      // Don't reset gameStarted here — we want it to stay true so the
      // "Playing: Round #" header remains green+glowing during gameplay.
      // It resets naturally when a new game/ready check begins.
      return;
    }
    // Don't re-enter phases after "Start Game!" was clicked
    if (gameStartedRef.current) return;
    const readyCount = readyCheckData?.ready_count || 0;
    const selfReady = readyCheckSubmitted;
    if (readyCheckPhase === 'waiting' && (readyCount >= 1 || selfReady)) {
      // Use server-authoritative timestamp so all clients share the same countdown
      const serverTs = readyCheckData?.countdown_started_at;
      if (serverTs) {
        const elapsed = Math.floor((Date.now() - new Date(serverTs).getTime()) / 1000);
        const remaining = Math.max(0, readyCountdownDuration - elapsed);
        if (remaining <= 0) {
          // Countdown already expired — skip straight to ready_to_start
          setReadyCheckPhase('ready_to_start');
          setAutoStartCountdown(5);
          autoStartTimeRef.current = Date.now();
          return;
        }
        readyCheckStartTimeRef.current = new Date(serverTs).getTime();
        setReadyCheckCountdown(remaining);
      } else {
        // Fallback if server timestamp not yet available
        readyCheckStartTimeRef.current = Date.now();
        setReadyCheckCountdown(readyCountdownDuration);
      }
      setReadyCheckPhase('draining');
    }
  }, [isReadyCheckActive, readyCheckData, readyCheckSubmitted, readyCheckPhase, readyCountdownDuration]);

  // Drain bar countdown — only runs during 'draining' phase
  useEffect(() => {
    if (readyCheckPhase !== 'draining') return;
    if (!readyCheckStartTimeRef.current) {
      readyCheckStartTimeRef.current = Date.now();
      setReadyCheckCountdown(readyCountdownDuration);
    }
    const interval = setInterval(() => {
      const elapsed = Math.floor((Date.now() - readyCheckStartTimeRef.current) / 1000);
      const remaining = Math.max(0, readyCountdownDuration - elapsed);
      setReadyCheckCountdown(remaining);
      if (remaining <= 0) {
        setReadyCheckPhase('ready_to_start');
        setAutoStartCountdown(5);
        autoStartTimeRef.current = Date.now();
        setShowReadyCheck(false);
        // Backend sends "Ready check complete!" announcement via ReadyCheckRound.end()
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [readyCheckPhase, readyCountdownDuration]);

  // Store handlePlayRound in a ref so the auto-start timer avoids stale closures
  const handlePlayRoundRef = useRef(null);

  // Auto-start 30s timer after drain completes
  useEffect(() => {
    if (readyCheckPhase !== 'ready_to_start') {
      autoStartTimeRef.current = null;
      return;
    }
    if (!autoStartTimeRef.current) {
      autoStartTimeRef.current = Date.now();
      setAutoStartCountdown(5);
    }
    const interval = setInterval(() => {
      const elapsed = Math.floor((Date.now() - autoStartTimeRef.current) / 1000);
      const remaining = Math.max(0, 5 - elapsed);
      setAutoStartCountdown(remaining);
      if (remaining <= 0) {
        // Backend sends "Round X begins!" announcement via ChatDiscussionRound
        gameStartedRef.current = true;

        setGameStarted(true);
        emitGameEvent(GAME_EVENTS.GAME_STARTED, { playerCount: users.length });
        setReadyCheckPhase('waiting');
        // Skip ready check, then start — min 800ms so "Starting..." is visible.
        // [requester-ready-fix] pass the local player so the backend readies them
        // (a skip without readyUp used to demote them — spectators are guarded
        // backend-side via is_spectator).
        const t0 = Date.now();
        chatGameApi.skipReadyCheck(gameId, null, createdPlayerIdRef.current)
          .catch(err => console.error('[AutoStart] skipReadyCheck failed:', err))
          .finally(() => {
            const wait = Math.max(0, 800 - (Date.now() - t0));
            setTimeout(() => { if (handlePlayRoundRef.current) handlePlayRoundRef.current(); }, wait);
          });
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [readyCheckPhase]);

  // Winner animation state
  const [winnerAnimation, setWinnerAnimation] = useState(null); // { type: 'eviction'|'victory', player: {...}, message: '...' }
  const [shownAnimations, setShownAnimations] = useState(new Set()); // Track which animations we've shown
  const hasInitializedAnimationSets = useRef(false); // Prevent old popups on mid-game join

  // 📰 Verdict poster (KILLER_MODE §5.4) — whodunnit's closing share card.
  const [showVerdictPoster, setShowVerdictPoster] = useState(false);
  const verdictPosterShownRef = useRef(false); // auto-open once per mount; the toast button reopens
  useEffect(() => {
    if (verdictPosterShownRef.current || isBotMode) return;
    // accusation_result + killer_revealed only exist post-finale, whodunnit only.
    if (!chatGameStatus?.accusation_result || !chatGameStatus?.killer_revealed) return;
    if (winnerAnimation) return; // let the Tier-1 victory overlay finish first
    const t = setTimeout(() => {
      verdictPosterShownRef.current = true;
      setShowVerdictPoster(true);
    }, 800);
    return () => clearTimeout(t);
  }, [chatGameStatus?.accusation_result, chatGameStatus?.killer_revealed, winnerAnimation, isBotMode]);

  // Get effective player ID (real player or spectator)
  const effectivePlayerId = useMemo(() => {
    if (humanPlayerId) return humanPlayerId;
    // Generate or get spectator ID from localStorage
    const spectatorKey = `spectator_id_${gameId}`;
    let spectatorId = localStorage.getItem(spectatorKey);
    if (!spectatorId) {
      spectatorId = `spectator_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      localStorage.setItem(spectatorKey, spectatorId);
    }
    return spectatorId;
  }, [humanPlayerId, gameId]);

  // 🎯 CENTER-STAGE ASK (Nina 07-26 verdict: "everything from Your Turn down moves
  // to page center on your turn"). True while a noir ask is LIVE for the viewer —
  // roll waiting, statement turn, or an uncast vote — and no takeover owns the
  // screen. The desktop composer wrapper (dock card + fate die + composer) lifts
  // to fixed bottom-center while this holds; mobile's thumb dock is already there.
  // Placed after effectivePlayerId (useMemo above) — evaluated at render, TDZ-real.
  const noirCenterAsk = !!(chatGameStatus?.noir_theme && chatGameStatus?.competitive_mode
    && !isBotMode && !chatGameStatus?.investigation && !chatGameStatus?.game_completed
    && (rollLocked
      || (isHumanTurn && currentRequestId)
      || (chatGameStatus?.is_voting && chatGameStatus?.voting_data && effectivePlayerId
        && !chatGameStatus.voting_data.votes_cast?.[effectivePlayerId] && !showVotingModal)));

  // 🗂 THE DOCKET (Nina rev 3, 07-29): on a noir STATEMENT turn the old five-surface
  // stack (dock card + roll card + killer banner + composer header + bet box) collapses
  // into ONE card: manila tab + fact line + composer. The ~4s roll beat already
  // ANNOUNCED the prose; what persists is only the facts you play with. Desktop
  // composer mounts only — mobile thumb dock untouched (!isMobile enforces this as of
  // 07-30; the gate previously lacked it, so the manila tab rendered in the thumb dock
  // with margins tuned to the desktop wrapper and bled past its padding). Vote and
  // roll-locked states keep the classic dock card. Additive gate: prop absent →
  // HumanInputForm unchanged.
  // Placed after effectivePlayerId (useMemo above) — same TDZ doctrine as noirCenterAsk;
  // the render fns that read this run at render time, after it exists.
  const noirWriteDocket = !!(chatGameStatus?.noir_theme && chatGameStatus?.competitive_mode
    && !isBotMode && !isMobile && isHumanTurn && currentRequestId && !rollLocked
    && !(chatGameStatus?.is_voting && chatGameStatus?.voting_data && effectivePlayerId
      && !chatGameStatus.voting_data.votes_cast?.[effectivePlayerId] && !showVotingModal));

  // Auto-open voting modal when a new voting round starts.
  // Placed after effectivePlayerId (useMemo above) to avoid temporal dead zone in deps array.
  // Moved from setTimeout-in-render to useEffect: the old pattern accumulated multiple
  // stale timeouts during rapid re-renders, any of which could re-open the modal after
  // the user explicitly closed it. useEffect fires once per committed render, so only
  // one open attempt happens per state change, with no stale callbacks lingering.
  useEffect(() => {
    if (gameType !== 'chat-game') return;
    if (!(chatGameStatus?.competitive_mode || chatGameStatus?.imposter_mode)) return;
    if (!chatGameStatus?.is_voting || !chatGameStatus?.voting_data) {
      // Voting ended (or never was) while the grace banner was up — clear it + its timer
      // so a stale timeout can't pop the modal for a finished vote.
      if (votingBanner) {
        clearTimeout(votingBannerTimerRef.current);
        setVotingBanner(null);
      }
      return;
    }
    const currentVotingRound = chatGameStatus.voting_data.round_number;
    const hasAlreadyVoted = effectivePlayerId && chatGameStatus.voting_data.votes_cast?.[effectivePlayerId];
    if (!showVotingModal && effectivePlayerId &&
        currentVotingRound !== votingRoundShown && !hasAlreadyVoted &&
        !showRoundAnnouncement && !showAssetLoadingOverlay) {
      setVotingRoundShown(currentVotingRound);
      if (isBotMode) {
        // Bot path unchanged: modal immediately — its vote window is machine-timed and the
        // banner grace would just eat into it.
        setShowVotingModal(true);
      } else {
        // Humans get a reading grace: banner first (tap to vote now), modal auto-expands
        // after 8s. The vote window is 30s server-side, so 8s of reading is affordable.
        // Whodunnit: the ACTION DOCK is the vote CTA (screen has one boss, spec B2) —
        // skip the top pill but keep the same 8s auto-expand.
        if (!chatGameStatus?.noir_theme) setVotingBanner({ round: currentVotingRound });
        clearTimeout(votingBannerTimerRef.current);
        votingBannerTimerRef.current = setTimeout(() => {
          setVotingBanner(null);
          setShowVotingModal(true);
        }, 8000);
      }
    }
  }, [gameType, chatGameStatus, effectivePlayerId, showVotingModal, votingRoundShown, showRoundAnnouncement, showAssetLoadingOverlay, votingBanner, isBotMode]);

  // Clear the banner's auto-expand timer on unmount.
  useEffect(() => () => clearTimeout(votingBannerTimerRef.current), []);

  const timeoutRef = useRef(null);
  const isPlayingRef = useRef(isPlaying);
  const isRequestInProgressRef = useRef(false);
  const resultsContainerRef = useRef(null);
  const [dropHighlight, setDropHighlight] = useState(false);
  // Chat-canvas doodle modal (non-competitive free chat only)
  const [showDrawingCanvas, setShowDrawingCanvas] = useState(false);
  // Keep the drip hold in sync + flush the backlog the moment the mobile
  // canvas closes (see drawingHoldRef note at the drip queue).
  useEffect(() => {
    drawingHoldRef.current = showDrawingCanvas && isMobile;
    if (!drawingHoldRef.current) pumpDripQueue();
  }, [showDrawingCanvas, isMobile, pumpDripQueue]);
  // Doodle chain seed — "Draw on this" opens the canvas pre-loaded with an
  // existing drawing's strokes; actionId rides to the backend as draws_on.
  const [drawOnSeed, setDrawOnSeed] = useState(null); // { actionId, strokes } | null
  const handleSendDrawing = async (pngBlob, strokes) => {
    // Throws on failure — DrawingCanvas catches and shows the error in place,
    // so an upload hiccup never eats the drawing.
    await chatGameApi.shareDrawing(gameId, humanPlayerId, pngBlob, strokes, drawOnSeed?.actionId || null);
    soundManager.play('sent');
    // 🔓 Doodle Bug unlock milestone (Nina 07-15): drawing anywhere earns it
    if (!isBotMode) bumpProgress('doodles');
    setShowDrawingCanvas(false);
    setDrawOnSeed(null);
  };
  const closeDrawingCanvas = () => { setShowDrawingCanvas(false); setDrawOnSeed(null); };
  // ✏️📐 Anchored vs modal (Nina 08-14: "I can't scroll down to click the send
  // button" on a narrower desktop). The anchored panel is IN-FLOW inside the
  // composer and assumes the feed shrinks to make room for it. When the column
  // is already crowded — narrow desktop, watch-party banner, tab strip, tall
  // locals rail — it instead grows past the viewport bottom and the footer
  // (Send) becomes unreachable, because the canvas eats the scroll gesture
  // that would reveal it. Measured at OPEN time, from the composer's own top
  // edge: not enough headroom → the full-screen modal, which has always been
  // scrollable and is what phones already use. Sticky footer in DrawingCanvas
  // covers the anchored case; this covers the case where the panel itself
  // lands off-screen.
  const [doodleAnchored, setDoodleAnchored] = useState(true);
  useEffect(() => {
    if (!showDrawingCanvas || isMobile) return;
    const r = inputFormRef.current?.getBoundingClientRect();
    // Panel budget = its own max height (min(56vh,520)) + composer chrome.
    const needed = Math.min(window.innerHeight * 0.56, 520) + 90;
    const room = r && r.height > 0 ? (window.innerHeight - r.top) : window.innerHeight;
    setDoodleAnchored(room >= needed);
  }, [showDrawingCanvas, isMobile]);
  // Drawing cards dispatch this (same idiom as watchparty:request) — no prop
  // drilling through the action-render switch. Humans in free chat only.
  useEffect(() => {
    const onDrawOn = (e) => {
      if (isBotMode || chatGameStatus?.competitive_mode || chatGameStatus?.imposter_mode || !humanPlayerId) return;
      const { actionId, strokes } = e.detail || {};
      if (!actionId || !Array.isArray(strokes) || !strokes.length) return;
      setDrawOnSeed({ actionId, strokes });
      setShowDrawingCanvas(true);
    };
    window.addEventListener('doodle:drawon', onDrawOn);
    return () => window.removeEventListener('doodle:drawon', onDrawOn);
  }, [isBotMode, chatGameStatus?.competitive_mode, chatGameStatus?.imposter_mode, humanPlayerId]);
  // 📎 composer upload — same rail as drag/drop; throws so the composer can
  // show the error in place. 📎 10-02 room docs: `opts` ({ knowledge: true })
  // only from RoomDocsUpload; every other caller passes the file alone.
  const handleUploadFile = async (file, opts) => {
    await chatGameApi.shareFile(gameId, humanPlayerId, file, opts);
    soundManager.play('sent');
  };

  // Detect content type from URL string
  const detectContentTypeFromUrl = (url) => {
    if (/youtube\.com|youtu\.be/.test(url)) return 'youtube';
    if (/\.(png|jpg|jpeg|gif|webp|svg|bmp)(\?|$)/i.test(url)) return 'image_url';
    if (/\.(mp4|webm|mov|avi|mkv)(\?|$)/i.test(url)) return 'video_url';
    return 'link';
  };

  const handleContentDrop = async (e) => {
    e.preventDefault();
    setDropHighlight(false);

    if (!humanPlayerId || !gameId) {
      console.log('[handleContentDrop] Skipped — no humanPlayerId or gameId');
      return;
    }

    const types = Array.from(e.dataTransfer.types);
    const hasFiles = types.includes('Files');
    console.log('[handleContentDrop] Drop received, types:', types, 'hasFiles:', hasFiles);

    // 1. YouTube embed URL (from the existing YouTube player drag)
    const youtubeUrl = e.dataTransfer.getData('text/x-youtube-url');
    if (youtubeUrl && youtubeUrl.includes('youtube.com/embed')) {
      try {
        await chatGameApi.shareContent(gameId, humanPlayerId, youtubeUrl, 'youtube');
        console.log('[handleContentDrop] YouTube shared successfully');
      } catch (err) {
        console.error('[handleContentDrop] Failed to share YouTube:', err);
      }
      return;
    }

    // 2. URL from text/uri-list or text/plain (browser image drags, link drags)
    //    Check this BEFORE files — dragging an image from a browser provides
    //    text/uri-list with the image URL, and may also provide a spurious File entry.
    const rawUriList = e.dataTransfer.getData('text/uri-list') || '';
    const rawPlain = e.dataTransfer.getData('text/plain') || '';
    console.log('[handleContentDrop] text/uri-list:', JSON.stringify(rawUriList));
    console.log('[handleContentDrop] text/plain:', JSON.stringify(rawPlain));
    // text/uri-list can have comment lines starting with # and multiple URLs
    const droppedUrl = rawUriList.split('\n').map(l => l.trim()).find(l => /^https?:\/\//i.test(l))
      || (rawPlain.trim().match(/^https?:\/\//i) ? rawPlain.trim() : '');
    if (droppedUrl) {
      const url = droppedUrl;
      const contentType = detectContentTypeFromUrl(url);
      console.log('[handleContentDrop] Detected URL:', url, 'contentType:', contentType);
      try {
        await chatGameApi.shareContent(gameId, humanPlayerId, url, contentType);
        console.log('[handleContentDrop] URL shared as', contentType);
      } catch (err) {
        console.error('[handleContentDrop] Failed to share URL:', err);
      }
      return;
    }

    // 3. data: URI (browser inlines small images as base64 instead of providing the source URL)
    const dataUri = rawUriList.split('\n').map(l => l.trim()).find(l => /^data:/i.test(l));
    if (dataUri) {
      console.log('[handleContentDrop] data: URI detected, converting to file');
      try {
        const resp = await fetch(dataUri);
        const blob = await resp.blob();
        const ext = blob.type.split('/')[1] || 'png';
        const file = new File([blob], `pasted-image.${ext}`, { type: blob.type });
        console.log('[handleContentDrop] Converted to file:', file.name, file.type, file.size);
        await chatGameApi.shareFile(gameId, humanPlayerId, file);
        console.log('[handleContentDrop] data: URI file shared successfully');
      } catch (err) {
        console.error('[handleContentDrop] Failed to share data: URI:', err);
      }
      return;
    }

    // 4. Files (images, videos, PDFs dragged from desktop)
    //    Only reach here if no URL was found — desktop file drags don't have text/uri-list
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const file = e.dataTransfer.files[0];
      console.log('[handleContentDrop] File drop:', file.name, file.type, file.size);
      if (file.size === 0) {
        console.log('[handleContentDrop] Skipped — empty file (browser ghost file)');
      } else {
        try {
          await chatGameApi.shareFile(gameId, humanPlayerId, file);
          console.log('[handleContentDrop] File shared successfully');
        } catch (err) {
          console.error('[handleContentDrop] Failed to share file:', err);
        }
        return;
      }
    }

    console.log('[handleContentDrop] No valid URL or file found in drop data');
  };

  const handleContentDragOver = (e) => {
    const types = Array.from(e.dataTransfer.types);
    if (types.includes('Files') || types.includes('text/x-youtube-url') || types.includes('text/plain') || types.includes('text/uri-list')) {
      e.preventDefault();
      e.dataTransfer.dropEffect = 'copy';
      setDropHighlight(true);
    }
  };

  const handleContentDragLeave = () => {
    setDropHighlight(false);
  };

  // ── 🗳️ Ambient mod poll (mod polls v1, 07-13; spacePolls.js) ───────────
  // Status payload `space_poll` is the anchor; pollOverride carries fresher
  // copies (vote responses + WS rebroadcasts) and resets whenever the status
  // poll id moves. A `{ closed: true }` sentinel hides a just-closed poll
  // until the next status tick confirms it gone.
  const [pollOverride, setPollOverride] = useState(null);
  const [pollVoting, setPollVoting] = useState(false);
  const statusPollId = chatGameStatus?.space_poll?.id || null;
  // 🗳️ Micro-sound (spec Arc 1 layer 2): gentle bell when a poll OPENS.
  // Ref starts undefined so the first status tick (poll already mid-flight
  // when you arrive) seeds silently; only a fresh id after that chimes.
  const prevPollIdRef = useRef(undefined);
  useEffect(() => {
    setPollOverride(null);
    if (prevPollIdRef.current !== undefined && statusPollId && !isBotMode) soundManager.play('pollOpen');
    prevPollIdRef.current = statusPollId;
  }, [statusPollId]); // eslint-disable-line react-hooks/exhaustive-deps
  const handlePollVote = async (pollId, optionId) => {
    if (!humanPlayerId || pollVoting) return;
    // Optimistic: show the pick the instant it's tapped; the server response
    // reconciles (or the catch rolls back). Without this the ✓ waited on a
    // full interjection-endpoint round-trip and the card read as frozen.
    const prevOverride = pollOverride;
    const base = (pollOverride && pollOverride.id) ? pollOverride : chatGameStatus?.space_poll;
    if (base && base.id === pollId) {
      const prevVote = base.my_vote;
      setPollOverride({
        ...base,
        my_vote: optionId,
        options: base.options.map(o => ({
          ...o,
          votes: (o.votes || 0) + (o.id === optionId ? 1 : 0) - (prevVote && o.id === prevVote ? 1 : 0),
        })),
      });
    }
    setPollVoting(true);
    try {
      const res = await chatGameApi.submitSpacePollVote(gameId, humanPlayerId, pollId, optionId);
      if (res?.poll) setPollOverride(res.poll);
      if (!isBotMode) bumpProgress('pollVotes'); // 🔓 unlock progress
      // ✅ Vote landed → the big card yields to the RECEIPT CHIP, both
      // platforms (Nina 08-06, one-tap rev 2): "You voted X · change" — it
      // names your pick (a mis-tap is visible the moment it happens) and
      // tapping it reopens the card to switch until the bell. Supersedes the
      // 08-02 mobile full-dismiss: that chip was a generic banner; this one
      // is the vote's receipt and undo, which is why it earns its line.
      setPollCollapsedId(pollId);
    } catch (err) {
      console.error('[SpacePoll] Vote failed:', err.message);
      setPollOverride(prevOverride); // roll back the optimistic pick
    }
    setPollVoting(false);
  };
  // ▾ collapsed poll id — poll cards park in the pinned dock on phones and
  // had no way out (Nina, 07-17); keyed by id so a fresh poll re-expands
  const [pollCollapsedId, setPollCollapsedId] = useState(null);
  // ✕ dismissed poll id (Nina 07-20) — kills THIS poll instance outright;
  // the next poll (fresh id) shows again
  const [pollDismissedId, setPollDismissedId] = useState(null);
  // 🎯▾ expanded directed-pending key (Nina 09-03: the weighing card "takes
  // up a lot of real estate on the chat tab" on desktop). Desktop defaults to
  // a ONE-LINE chip; clicking it expands to the full card, clicking ▾ folds
  // it back. Keyed by opened_at (pendings have no id) so a NEW pending starts
  // folded again. Mobile keeps the mock-approved wrapping card untouched.
  const [dpExpandedAt, setDpExpandedAt] = useState(null);
  const renderSpacePoll = () => {
    if (isBotMode || chatGameStatus?.competitive_mode || chatGameStatus?.imposter_mode) return null;
    // 🐣 W5 (D8 ruled 08-28): no polls in a 1:1 ported space — one voter.
    // isPortedSpace is declared below; safe, the builder only runs from JSX.
    if (isPortedSpace) return null;
    // 🎓 Tour truce (Nina 08-04): the poll card renders INSIDE the composer
    // dock, so the tour's composer beat spotlit the poll along with the input
    // — a first-minute user got a ballot mid-lesson. The tour runs ~a minute;
    // polls run much longer, so the card just waits its turn.
    if (tutorialActive) return null;
    const statusPoll = chatGameStatus?.space_poll || null;
    const poll = (pollOverride && pollOverride.id)
      ? pollOverride
      : (pollOverride && pollOverride.closed ? null : statusPoll);
    // 🎯 09-01: while a resident is deciding a directed scenario (status
    // payload directed_pending, houseDirected.publicPending), the dock shows
    // who and what — never the options (the answer is the reveal). A stamp
    // sits in a poll's slot, so this and a live poll rarely coexist; the poll
    // wins when they do.
    const dp = chatGameStatus?.directed_pending;
    // 🎭🎬 09-20 the movie-night ballot invitation (LIGHTHOUSE_SCREENING_SPEC §12):
    // it only ever takes this dock when NOTHING else wants it (a real poll and a
    // deciding resident both win), and it never touches the room's poll slot.
    if (!poll && !(dp && dp.player_name && dp.q) && ballotInviteReady && humanPlayerId) {
      const invite = ballotInvite(chatGameStatus?.house?.ballot);
      if (invite && invite.id !== ballotInviteDismissed) {
        return <GenreVoteCard invite={invite} onVote={voteFilm} onDismiss={() => setBallotInviteDismissed(invite.id)} THEME={THEME} />;
      }
    }
    // 🎬📝 09-23 batch 1 (handoff §3.7, ruling 16): THE CREDITS CARD, once per
    // device per event, for anyone stamped as there while it ran. A real poll
    // and a deciding resident both win this slot; never a modal.
    // 📸📝 10-06 (Nina): the review only AFTER the event; its last 30 min show the group photo alone. "Not now"
    // closes one stage at a time, and the thanks stays up after Send (utils/eventFeedback.feedbackCardVisible).
    if (!poll && !(dp && dp.player_name && dp.q) && humanPlayerId && fbRider && fbEventId
        && feedbackCardVisible({ rider: fbRider, seen: wasSeen(gameId, fbEventId), done: isDone(gameId, fbEventId), thanks: feedbackState === 'thanks', dismissedKey: feedbackDismissed })) {
      return (
        <EventFeedbackCard
          feedback={fbRider}
          answers={feedbackAnswers}
          onAnswer={(k, i) => setFeedbackAnswers((a) => ({ ...a, [k]: i }))}
          text={feedbackText}
          onText={setFeedbackText}
          state={feedbackState}
          THEME={THEME}
          onSend={() => sendEventFeedback(fbRider)}
          onSkip={() => { markDone(gameId, fbEventId); setFeedbackDismissed(feedbackDismissKey(fbRider)); }}
          onDismiss={() => setFeedbackDismissed(feedbackDismissKey(fbRider))}
        />
      );
    }
    if (!poll && dp && dp.player_name && dp.q) {
      // 🖼️ 09-01 pm restyle (Nina, mock-approved "Option A"): the resident's
      // face + pulsing thought-dots while they weigh it, and the FULL question
      // wraps (the old one-liner truncated it to half a sentence on phones).
      // Portrait rides dp.avatar (publicPending); pendings stamped before the
      // deploy fall back to the initial circle. No target emoji (Nina's cut);
      // cook keeps its meal emoji beside the name.
      // 🎯▾ 09-03 (Nina: eats chat real estate on desktop): desktop defaults
      // to a ONE-LINE chip (who + the q, ellipsized, dots while fresh); click
      // expands to the full card, ▾ folds it back. Phones keep the card —
      // the 09-01 one-liner truncation is exactly what the card fixed there.
      const dpKey = Number(dp.opened_at) || 0;
      const dpFresh = Date.now() - (Number(dp.opened_at) || Date.now()) <= 20 * 60 * 1000;
      if (!isMobile && dpExpandedAt !== dpKey) {
        return (
          <button
            onClick={() => setDpExpandedAt(dpKey)}
            style={{
              display: 'flex', alignItems: 'center', gap: 7, width: '100%', boxSizing: 'border-box',
              background: 'rgba(255,200,100,0.07)', border: '1px solid rgba(255,200,100,0.3)',
              borderRadius: 9, padding: '5px 10px', marginBottom: 6,
              color: 'rgba(255,255,255,0.75)', fontSize: '0.78em',
              cursor: 'pointer', textAlign: 'left',
            }}
          >
            <style>{'@keyframes directedDotPulse { 0%, 80%, 100% { opacity: 0.25; } 40% { opacity: 1; } }'}</style>
            <DirectedPortrait avatar={dp.avatar} name={dp.player_name} size={18} />
            <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>
              <span style={{ fontWeight: 800, color: '#ffd98f' }}>
                {dp.kind === 'cook' ? `${MEAL_EMOJI[dp.meal] || '🍳'} ` : ''}{dp.player_name} is weighing:
              </span>
              {' '}<span style={{ fontStyle: 'italic' }}>{dp.q}</span>
            </span>
            {dpFresh && (
              <span style={{ flexShrink: 0, fontSize: '0.85em', color: '#ffc864', letterSpacing: 2 }}>
                <span style={{ animation: 'directedDotPulse 1.4s infinite' }}>●</span>
                <span style={{ animation: 'directedDotPulse 1.4s 0.2s infinite' }}>●</span>
                <span style={{ animation: 'directedDotPulse 1.4s 0.4s infinite' }}>●</span>
              </span>
            )}
            <span style={{ flexShrink: 0, color: '#ffd98f', fontWeight: 700 }}>▸</span>
          </button>
        );
      }
      return (
        <div style={{
          // 🩹 09-01 review: this app has NO global border-box reset — a
          // width:100% div with padding + border overflows its dock by 22px.
          display: 'flex', alignItems: 'flex-start', gap: 9, width: '100%', boxSizing: 'border-box',
          background: 'rgba(255,200,100,0.07)', border: '1px solid rgba(255,200,100,0.3)',
          borderRadius: 12, padding: '8px 11px', marginBottom: 6,
        }}>
          <style>{'@keyframes directedDotPulse { 0%, 80%, 100% { opacity: 0.25; } 40% { opacity: 1; } }'}</style>
          <DirectedPortrait avatar={dp.avatar} name={dp.player_name} size={28} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
              {/* ⏳ Age-aware (09-01 pm, the "seems stuck?" report): a pending
                  can sit for hours in an unwatched room (turns only happen
                  with viewers; it resolves on the resident's next turn). Live
                  dots on a 3-hour-old ask read as a hang — past 20 min the
                  copy softens and the dots go. opened_at rides publicPending. */}
              <span style={{ fontSize: '0.8em', fontWeight: 800, color: '#ffd98f' }}>
                {dp.kind === 'cook' ? `${MEAL_EMOJI[dp.meal] || '🍳'} ` : ''}{dp.player_name}
                {Date.now() - (Number(dp.opened_at) || Date.now()) > 20 * 60 * 1000
                  ? ' has been weighing something for a while'
                  : ' is weighing something'}
              </span>
              {Date.now() - (Number(dp.opened_at) || Date.now()) <= 20 * 60 * 1000 && (
                <span style={{ fontSize: '0.82em', color: '#ffc864', letterSpacing: 2 }}>
                  <span style={{ animation: 'directedDotPulse 1.4s infinite' }}>●</span>
                  <span style={{ animation: 'directedDotPulse 1.4s 0.2s infinite' }}>●</span>
                  <span style={{ animation: 'directedDotPulse 1.4s 0.4s infinite' }}>●</span>
                </span>
              )}
              {/* 🎯▾ 09-03: fold back to the one-line chip (desktop only) */}
              {!isMobile && (
                <button
                  onClick={() => setDpExpandedAt(null)}
                  style={{
                    marginLeft: 'auto', flexShrink: 0, background: 'none', border: 'none',
                    color: '#ffd98f', fontWeight: 700, fontSize: '0.85em', cursor: 'pointer', padding: '0 2px',
                  }}
                  title="Collapse"
                >▾</button>
              )}
            </div>
            <div style={{
              fontSize: '0.78em', color: 'rgba(255,255,255,0.75)', lineHeight: 1.45,
              marginTop: 2, fontStyle: 'italic',
              display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden',
            }} title={dp.q}>{dp.q}</div>
          </div>
        </div>
      );
    }
    if (!poll) return null;
    if (pollDismissedId === poll.id) return null;
    // Collapsed → one-line chip (per poll id, so a NEW poll re-announces
    // itself); expanded card carries the ▾ that gets you here. Voted state
    // is a RECEIPT (Nina 08-06): it names YOUR pick, not the question —
    // that's the whole reason it earns its line until the bell.
    // 📱 RETIRED ON PHONES (Nina 08-07): the in-dock chip is gone from mobile
    // entirely. The receipt now lives in ONE place there — the top-of-screen
    // notification bar below (pollReceiptStripInfo) — so it can't render as
    // two competing pills depending on whether the dock happens to be open.
    // Desktop keeps the in-dock chip: it has no top bar.
    if (pollCollapsedId === poll.id) {
      if (isMobile) return null;
      const mine = poll.my_vote ? poll.options.find(o => o.id === poll.my_vote) : null;
      return (
        <button
          onClick={() => setPollCollapsedId(null)}
          style={{
            display: 'flex', alignItems: 'center', gap: 7, width: '100%',
            background: `${THEME.accent || '#7C4DFF'}14`,
            border: `1px solid ${THEME.accent || '#7C4DFF'}35`,
            borderRadius: 9, padding: '7px 10px', marginBottom: 6,
            color: THEME.textMuted || '#bbb', fontSize: '0.78em', fontWeight: 700,
            cursor: 'pointer', textAlign: 'left',
          }}
        >
          <span>🗳️</span>
          <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {mine ? `You voted ${mine.emoji ? `${mine.emoji} ` : ''}${mine.label}` : poll.question}
          </span>
          <span style={{ flexShrink: 0, color: THEME.accentOnPanel || THEME.accent || '#7C4DFF' }}>{mine ? 'change ▾' : 'vote ▸'}</span>
        </button>
      );
    }
    return <SpacePollCard poll={poll} onVote={handlePollVote} voting={pollVoting} THEME={THEME} onCollapse={() => setPollCollapsedId(poll.id)} onDismiss={() => setPollDismissedId(poll.id)} />;
  };

  // 🗳️📍 Poll receipt bar (Nina 08-07 — supersedes the 08-06 mock-5B
  // above-the-dock placement): the receipt belongs at the VERY TOP of the
  // screen, as a notification bar, and it stays there for as long as the poll
  // is live. It then disappears by itself — no manual dismiss needed, because
  // a closed poll stops resolving below and the bar unmounts with it.
  // The dock-state / place / typing gates are deliberately GONE: at the top of
  // the screen it collides with nothing, and a receipt that vanishes when you
  // open the composer takes the change-my-vote undo with it. Only the tour
  // truce survives.
  // A FUNCTION like composerThinkingInline — deps declared later; call-time
  // evaluation dodges the TDZ. Returns { poll, mine } or null.
  const pollReceiptStripInfo = () => {
    if (!isMobile || isBotMode) return null;
    // 🐣 W5 (D8 ruled 08-28): no poll chrome in a ported space — the
    // receipt strip is poll UI too, same gate as the card builder.
    if (isPortedSpace) return null;
    if (chatGameStatus?.competitive_mode || chatGameStatus?.imposter_mode) return null;
    if (tutorialActive) return null; // tour truce, same as the card
    const statusPoll = chatGameStatus?.space_poll || null;
    const poll = (pollOverride && pollOverride.id)
      ? pollOverride
      : (pollOverride && pollOverride.closed ? null : statusPoll);
    if (!poll || pollDismissedId === poll.id || pollCollapsedId !== poll.id) return null;
    const mine = poll.my_vote ? poll.options.find(o => o.id === poll.my_vote) : null;
    return { poll, mine };
  };

  // 🤔📱 Inline thinking slot (Nina 08-06): while the mobile non-comp dock is
  // OPEN, "X is thinking" renders inside the composer strip (HumanInputForm
  // thinkingStatus) and the top-floating pill yields. 08-27: while the box is
  // idle (empty + unfocused) HumanInputForm moves it INSIDE the input box; on
  // focus/draft it returns to the strip row — all inside HumanInputForm, this
  // gate is focus-agnostic on purpose. This mirrors the dock
  // mount condition + the branches that swap the composer out (fold, ghost
  // lane, other places-nav views). A function, not a const — several deps are
  // declared later in this component and call-time evaluation dodges the TDZ.
  const composerThinkingInline = () => isMobile && !isThreeColumnLayout
    && !chatGameStatus?.competitive_mode && !chatGameStatus?.imposter_mode
    && !composerCollapsed
    && !(placesNav && mobileView !== 'chat')
    && !!humanPlayerId
    && !investigationTakeover
    && !chatGameStatus?.eliminated_players?.includes(humanPlayerId)
    // ghost lane owning the composer = SpectatingChatInput mounted instead
    // (ghostSurfacesOn declared later — safe: this is a call-time function)
    && !(ghostLane && ghostSurfacesOn && placesNav && mobileView === 'chat' && !isBotMode
      && spectatingMessages.length > 0 && !effectiveIsSpectating);

  // ── 🍿 Watch Party (spec: WATCH_PARTY_SPEC_2026_07_10.md) ──────────────
  // Authoritative state arrives via WS `watch_party_state`; status polls seed
  // it but never rewind fresher WS state. `inTheater` is purely local.
  const [watchParty, setWatchParty] = useState(null);
  // Ref mirror for WS-handler closures (host_announcement's TV-audible skip)
  const watchPartyRef = useRef(null);
  useEffect(() => { watchPartyRef.current = watchParty; }, [watchParty]);
  const [inTheater, setInTheater] = useState(false);
  // 🎬📐 THEATER HERO (09-22 late, Nina: "the watchparty screen must be
  // viewable on ALL browser screens, consistently"). The desktop shell is a
  // fixed-height app (calc(100dvh - 36px), overflow hidden) and the theater's
  // video row is the ONLY thing in the center column allowed to shrink, so a
  // short window crushed the picture to a sliver: measured 21px tall at a
  // 1280x720 window, byte-identical on the 09-20 build (a layout budget, not
  // a regression). While SEATED on desktop the page makes room instead: the
  // masthead folds to its title, the objective band drops, the feed floor
  // lowers (the 09-22 cinema lever, now every party) and the theater keeps a
  // video floor (WatchPartyTheater). Phones untouched (their TV place owns
  // the theater). Leaving the couch restores every row.
  const theaterHero = !isMobile && inTheater && !!watchParty
    && !chatGameStatus?.competitive_mode && !chatGameStatus?.imposter_mode;
  // 🔓 Unlock progress (gameUnlocks.js): one watch-party credit per page
  // load, on first taking a seat; plus the distinct-visit-days counter.
  const watchCreditRef = useRef(false);
  useEffect(() => {
    if (!inTheater || watchCreditRef.current || isBotMode) return;
    watchCreditRef.current = true;
    bumpProgress('watchParties');
  }, [inTheater, isBotMode]);
  useEffect(() => {
    if (gameId && !isBotMode) addVisitDay();
  }, [gameId, isBotMode]);
  // 🔑 Progress sync (07-14): mirror the localStorage unlock store to the
  // server under this browser's recovery code (gameUnlocks.getSyncCode), so a
  // cleared browser can restore by typing the code back in (games tab). One
  // snapshot-diff loop catches EVERY bump site (msgs, crafts, poll votes,
  // watch parties, visit days, LocalsDossier's dossierFacts) without wiring
  // each one: sync ~20s after mount, then every 90s only when the snapshot
  // changed. Server merge is monotone; the response is folded back in so
  // multi-tab/multi-device progress converges upward.
  const lastSyncedProgressRef = useRef(null);
  useEffect(() => {
    if (!gameId || isBotMode) return;
    let cancelled = false;
    const sync = async (force = false) => {
      if (cancelled || document.hidden) return;
      try {
        const code = getSyncCode();
        if (!code) return; // private mode
        const snapshot = getProgressSnapshot();
        const flat = JSON.stringify(snapshot);
        if (!force && flat === lastSyncedProgressRef.current) return;
        const res = await chatGameApi.syncProgress(gameId, code, snapshot);
        if (cancelled) return;
        if (res?.progress) applyServerProgress(res.progress);
        lastSyncedProgressRef.current = JSON.stringify(getProgressSnapshot());
      } catch (e) { /* sync is garnish — next tick retries */ }
    };
    const first = setTimeout(() => sync(true), 20000);
    const loop = setInterval(() => sync(false), 90000);
    return () => { cancelled = true; clearTimeout(first); clearInterval(loop); };
  }, [gameId, isBotMode]);
  // Transient couch-invite toast shown when a party starts while you're mid-scroll
  const [partyToast, setPartyToast] = useState(null);
  // Latest overlay reaction (AI quip / human emoji) — theater turns these into bullets
  const [lastWatchReaction, setLastWatchReaction] = useState(null);
  // 🛋️ THE COUCH (09-23 batch 1, handoff §3.4 v2, rulings 9-10): a page-level
  // log of the last 40 couch lines, keyed by the stored row's id (B1: the
  // broadcast now carries action_id / at / position_s; an older broadcast gets
  // a local id and is de-duplicated against its own row by sender + text).
  // Its OWN state on purpose: never the ghost array, never a changed row type,
  // so every resident prompt stays byte-identical. The live broadcast, the
  // action ingest and the older-history load all push here; a reload hydrates
  // from the status payload's recent rows. COUCH false ⇒ nothing is kept.
  const COUCH_LOG_MAX = 40;
  const [couchLog, setCouchLog] = useState([]);
  const couchSeenRef = useRef(new Set());
  const pushCouch = useCallback((item) => {
    if (!COUCH || !item || !item.text) return;
    const id = item.id ? String(item.id) : '';
    if (id && couchSeenRef.current.has(id)) return;
    if (id) couchSeenRef.current.add(id);
    setCouchLog((prev) => {
      // the same line twice within a few entries (broadcast, then its row) is one line
      if (prev.slice(-6).some((p) => p.sender_name === item.sender_name && p.text === item.text)) return prev;
      return [...prev, { ...item, id: id || `local_${Date.now()}_${Math.random()}` }].slice(-COUCH_LOG_MAX);
    });
  }, []);
  // a ref mirror for the memoized ingest path (its deps array is the page's, not ours)
  const pushCouchRef = useRef(null);
  pushCouchRef.current = pushCouch;
  const watchReactionIdRef = useRef(0);
  // When authoritative party state last arrived (WS broadcast or a control
  // call's response). While this is fresh, status polls stay seed-only: a
  // poll snapshot read moments before a start/advance/stop arrives late and
  // must not clear or rewind fresher state — fetch ordering isn't guaranteed.
  const watchPartyFreshAtRef = useRef(0);
  // True only while the theater is actually rendered on screen (seated AND
  // parked in a visible slot, not the hidden holder). Read by the status poll
  // → `watching=1` param → backend theater-presence stamp that gates quips.
  const watchingTheaterRef = useRef(false);

  useEffect(() => {
    if (!chatGameStatus) return;
    if (Date.now() - watchPartyFreshAtRef.current < 20000) return;
    const next = chatGameStatus.watch_party || null;
    setWatchParty(prev => {
      if (!next) return null;
      // Freshness by position_at (every start/control/heartbeat/advance
      // stamps it): a stale poll must never rewind a newer party state —
      // regardless of video_id, since an advance is exactly a video change
      // WITH a newer position_at.
      if (prev && new Date(prev.position_at || 0) >= new Date(next.position_at || 0)) return prev;
      return decodeWatchPartyTitles(next);
    });
  }, [chatGameStatus]);

  useEffect(() => { if (!watchParty) setInTheater(false); }, [watchParty]);

  // 📺 TV wins the room — but only when it's actually AUDIBLE. The emitter
  // moved into WatchPartyTheater (D6): it reports playing-AND-unmuted, so a
  // muted recommended video no longer silences the space's music bed.

  // ── 🎭 THE STAGE (STAGE_DOCK_SPEC_2026_07_11.md, D2) ────────────────────
  // One docked slot at the top of the center column: "a place where something
  // is happening, with a chat around it." Dock choice is PERSONAL (persisted
  // per space); communal events — a watch party starting — CLAIM the stage for
  // everyone, with a "bring back my tab" chip (hybrid model, Nina 07-11).
  const [stageTab, setStageTab] = useState(null);
  // 📏 Stage resize (Nina 07-13): the bar under the stage drags its height;
  // null = auto (the old 46vh / 55vh defaults). Persisted per browser.
  const [stageVideoH, setStageVideoH] = useState(() => {
    try { const v = Number(localStorage.getItem('stageVideoH')); return v >= 140 ? v : null; } catch (e) { return null; }
  });
  // 📏 Apply-time clamp (Nina 08-03: watch-party controls unreachable on some
  // screens): the persisted drag height was clamped against the screen it was
  // dragged on — restored on a SHORTER viewport (laptop after a monitor, a
  // resized window) it swallows the screen and shoves the controls row below
  // a fold that doesn't scroll. Clamp at every render against the LIVE
  // viewport (60% leaves room for header + controls + resize bar); the stored
  // value stays untouched so the big screen gets its height back.
  const stageVideoHApplied = stageVideoH ? Math.min(stageVideoH, Math.round(viewportH * 0.6)) : null;
  const startStageResize = (e) => {
    e.preventDefault();
    const bar = e.currentTarget;
    // Capture the pointer on the bar: without it, the drag dies the moment
    // the pointer crosses the YouTube iframe (the iframe swallows pointermove
    // — user 07-15: "not sure the resize bar even works"). Capture retargets
    // every pointer event to the bar until release, iframes included.
    try { bar.setPointerCapture(e.pointerId); } catch (err) { /* old browsers */ }
    const startY = e.clientY;
    // Seed from the VIDEO box (the YT iframe), not the whole slot — the slot
    // includes header + controls, so seeding from it made the first drag jump.
    const slot = bar.previousElementSibling;
    const videoBox = slot ? (slot.querySelector('iframe') || slot) : null;
    const measured = videoBox?.getBoundingClientRect?.().height || 0;
    const startH = stageVideoH || Math.round(measured) || Math.round(window.innerHeight * 0.46);
    let latest = startH;
    const onMove = (ev) => {
      // 0.75→0.6 (08-03): matches the apply-time clamp — past 60% the video
      // starts eating the controls row's room, and the extra 15% was invisible
      // once the render clamp landed anyway.
      latest = Math.max(140, Math.min(Math.round(window.innerHeight * 0.6), startH + (ev.clientY - startY)));
      setStageVideoH(latest);
    };
    const onUp = (ev) => {
      bar.removeEventListener('pointermove', onMove);
      bar.removeEventListener('pointerup', onUp);
      bar.removeEventListener('pointercancel', onUp);
      try { bar.releasePointerCapture(ev.pointerId); } catch (err) { /* already released */ }
      try { localStorage.setItem('stageVideoH', String(latest)); } catch (err) { /* private mode */ }
    };
    bar.addEventListener('pointermove', onMove);
    bar.addEventListener('pointerup', onUp);
    bar.addEventListener('pointercancel', onUp);
  };

  // 🫖 Tea unseen badge (Tea T1 follow-up, 07-13): chapters newer than this
  // viewer's last Tea visit light the tab — the Tea is the retention surface,
  // the badge is its pull. Seen-mark = newest chapter ts while any Tea surface
  // (tab, pinned sidebar, stage) is open. Same shape as the shelf badge.
  // ⚠️ Lives BELOW the stageTab declaration on purpose (TDZ — the shelf-state
  // lesson): teaOpen reads stageTab.
  const teaChapterTimes = useMemo(() => (
    (results || []).flatMap(r => r.actions || [])
      .filter(a => a.type === 'round_analysis' && a.data?.chapter_title)
      .map(a => new Date(a.created_at).getTime())
  ), [results]);
  const newestChapterAt = teaChapterTimes.length ? Math.max(...teaChapterTimes) : 0;
  // 📱🧭 MOBILE PLACES NAV (MOBILE_PLACES_NAV_SPEC_2026_07_31, P0 shell): on
  // phones in non-competitive spaces the bottom tabs are real SCREENS, not
  // feed filters — Chat · TV · Locals · Arcade (Nina's order 07-31: Arcade
  // LAST, after Locals). Default place on open is ALWAYS Chat (Nina lock).
  // TV/Locals/Arcade render as a fixed layer over the chat scroller so
  // Chat's scroll position, composer state, and lane filter survive every
  // hop untouched; the watch-party iframe rides the existing hidden-holder
  // reparent so switching places never remounts it (audio survives — the
  // #1 regression risk per the 07-30 storm/jazz arc). Comp nav (Battle ·
  // Recap · Arcade) and all desktop layouts are untouched by design.
  // Declared HERE (above the tea block): teaOpen reads placesNav/mobileView.
  const [mobileView, setMobileView] = useState('chat'); // 'chat' | 'tv' | 'arcade' | 'locals'
  // 🫖 Locals v2 (Nina 08-01 mock): full tea archive behind the spotlight
  const [localsTeaOpen, setLocalsTeaOpen] = useState(false);
  // 👋🎁 Cast verbs: Say hi / Gift land in Chat with the composer pre-seeded
  const [composerSeed, setComposerSeed] = useState(null);
  // 🐣 Settle-in guide bridge (Nina 08-14 v2, SettleInGuide.js — app-level,
  // this is its ONLY coupling into this file):
  //  in:  'spaces-settle-in-say-hi' {agentName} → seed the composer with the
  //       same opener deck the Locals card uses.
  //  out: 'spaces-space-theme-ready' {gameId} once the status poll carries a
  //       ui_theme — the guide's "painting itself" beat flips on real data
  //       instead of a clock. Re-dispatching every poll is fine; listeners
  //       are idempotent.
  useEffect(() => {
    const onSayHi = (e) => {
      const agentName = e?.detail?.agentName;
      if (!agentName) return;
      const opener = SAY_HI_OPENERS[Math.floor(Math.random() * SAY_HI_OPENERS.length)];
      setComposerSeed(`@${agentName} ${opener}`);
      setComposerCollapsed(false);
      setMobileView('chat');
    };
    window.addEventListener('spaces-settle-in-say-hi', onSayHi);
    return () => window.removeEventListener('spaces-settle-in-say-hi', onSayHi);
  }, []);
  useEffect(() => {
    if (!chatGameStatus?.ui_theme || !chatGameStatus?.game_id) return;
    try {
      window.dispatchEvent(new CustomEvent('spaces-space-theme-ready', { detail: { gameId: chatGameStatus.game_id } }));
    } catch { /* no-op */ }
  }, [chatGameStatus?.ui_theme, chatGameStatus?.game_id]);
  const placesNav = isMobile && (gameType === 'chat-game' || gameType === 'chat')
    && !!chatGameStatus && !chatGameStatus.competitive_mode && !chatGameStatus.imposter_mode
    && !shellPreset; // 🧱 the room frame has its own phone bar (no places, no swipes, no layers)
  // Ref mirror for WS-handler closures (same pattern as mobileViewRef): the
  // Chat-tab notification dot needs to know "is the user off the Chat place"
  // at message-arrival time (Nina 08-28, invisible aux cards).
  const placesNavRef = useRef(placesNav);
  placesNavRef.current = placesNav;
  // ⌨️ THE TERMINAL (08-19, CODING_ARENA_SPEC): tool_flags.terminal === true
  // reshapes the space's lanes — Arcade + Media leave, the Workbench (per-seat
  // work/thought panel) arrives, phones run Chat · Work · Locals. Opt-IN
  // (explicit true), unlike the arcade/doodle opt-outs: only stamped terminal
  // spaces change, every other space renders byte-identical. Defined HERE,
  // above the swipe-order write — the file's TDZ lesson (a later const read
  // at render time compiles clean and crashes live; only the render probe
  // sees it).
  const isTerminalSpace = chatGameStatus?.tool_flags?.terminal === true;
  // 🏠🧠 HOUSE MEMORY WALL + till + nicknames (Claude House frontend leg,
  // brainstorm §2 "visible memory" / §5.2 UI leg). chatGameStatus.house is
  // non-null ONLY for autonomous_locals spaces (backend-gated), so every
  // other space renders zero of this. Canon + till + balances + nicknames
  // ride the status poll (cheap, in-memory server-side); the wall's memory
  // ROWS come from a lazy one-shot fetch when the wall is actually looked at
  // (drawer opened, or the mobile Locals place shown) — never the poll path.
  // Declared BELOW placesNav/mobileView on purpose (the tutorial-block TDZ
  // lesson: this block reads both).
  const houseStatus = chatGameStatus?.house || null;
  // 🚪📷 /room camera (ROOM_CAMERA_SPEC Wave 4, ruled 09-15). Payload-gated:
  // the backend ships house.viewer_room ONLY when HOUSE_ROOM_CAMERA_ENABLED,
  // so every pre-flip space renders zero room chrome. The SERVER is truth for
  // the camera; the local stamp is a 15s paint-early hint so a move doesn't
  // snap back while the next status poll is in flight.
  const roomCameraOn = !!houseStatus && houseStatus.viewer_room !== undefined;
  const [roomMoveLocal, setRoomMoveLocal] = useState(null); // { room, at }
  const [roomNotice, setRoomNotice] = useState(null);       // private_message line
  const [dismissedRoomInvites, setDismissedRoomInvites] = useState(() => new Set());
  const viewerRoom = roomCameraOn
    ? ((roomMoveLocal && Date.now() - roomMoveLocal.at < 15000)
      ? roomMoveLocal.room
      : (houseStatus.viewer_room || 'house'))
    : 'house';
  // 🚪🕯️ BETWEEN ROOMS (Nina 09-24; utils/roomTransit.js + RoomTransit.js):
  // A footsteps · B the little door · C the keyhole, started ON THE TAP by the
  // one mover. While a walk plays, the PICTURE (wallpaper, band, phone card)
  // stays on the room you are leaving (`artRoom`) and swaps underneath once the
  // new painting fills the screen. Everything else follows viewerRoom as before.
  const roomTransitApi = useRoomTransit();
  const roomTransit = roomTransitApi.transit;
  const artRoom = (roomCameraOn && roomTransit && roomTransit.hold && roomTransit.from) ? roomTransit.from : viewerRoom;
  useEffect(() => {
    if (!roomNotice) return undefined;
    // The room list is worth an actual read; a move reply is one glance.
    // Length-keyed, not newline-keyed: a fully-discovered house has no
    // locked-tease second line but the LONGEST list (preship review). Both
    // self-clear — the toast never nags.
    const t = setTimeout(() => setRoomNotice(null), (roomNotice.includes('\n') || roomNotice.length > 120) ? 14000 : 7000);
    return () => clearTimeout(t);
  }, [roomNotice]);
  // 🚪🖼️ Room wallpaper veil (Tier 0, 09-15 eve): the body wallpaper is the
  // ONE layer genuinely behind the page, and ThemeStyleInjector composes
  // `var(--room-wallpaper-veil)` under it as the first background layer. We
  // only ever write the CSS var here — the injector stays the sole writer of
  // body.style, so the two effects can never race on one property. Flat
  // veils only (the wallpaper tiles at 500px; shape would stripe — pinned in
  // roomView.test.js). 📱 09-16 (Nina: full mobile lane): the veil runs on
  // phones too — it is the ONE behind-content layer, so small screens risk
  // nothing (the FILM_CAP hazard is over-films, which stay desktop-only).
  const roomVeilHour = houseStatus?.circadian?.clock?.hour;
  // 🍂❄️ The house SEASON (09-17 eve, Tier 4 packs): from the house day, never
  // the viewer's Date (one-clock). null until a real day key arrives.
  const roomSeason = seasonForDay(houseStatus?.circadian?.clock?.day);
  // 🚶 Presence-change line on the plate ("Fen wanders in."), set by the
  // statuses diff below (after localStatusesKey exists), cleared after a minute.
  const [roomPlateNote, setRoomPlateNote] = useState(null);
  // 🖼️ 09-17 ART AS WALLPAPER (Nina: "the room images can barely be seen"):
  // on desktop the room's real art BECOMES the body wallpaper — one image,
  // clear in the header zone, dimmed under the chat — and the masthead band
  // steps aside (roomArt.bandHidden; ONE IMAGE NEVER TWO, pinned in
  // roomPlate.test.js). Rooms without art, and every phone, keep the flat
  // veil + band/card exactly as before. Still var-only: the injector stays
  // the sole writer of body.style.
  const roomArt = roomCameraOn
    ? roomArtPlan(artRoom, roomVeilHour, houseStatus?.room_art, houseStatus?.weather?.kind, { desktop: !isMobile, season: roomSeason })
    : null;
  const roomWallImage = roomArt?.wallpaper?.image || null;
  const roomWallDim = roomArt?.wallpaper?.dim || null;
  const roomLaneA = roomArt?.wallpaper?.lane ?? null;
  const roomLaneFill = roomArt?.wallpaper?.laneFill || null;
  // 🚪✨ Arrival moment: { room, line } for ~2.5s after a user-initiated move
  // into an art room — the dim lifts, the page fades back, the entry line
  // captions the room; then everything settles and the toast + plate take
  // over. Never on load/restore, never on phones, never under reduced motion.
  const [roomArriving, setRoomArriving] = useState(null);
  // The page steps back for the arrival card AND for a walk on the whole
  // screen (desktop), so the chat comes back once, when the card is done.
  const roomCurtain = !!roomArriving || !!(roomTransit && roomTransit.curtain);
  const roomArriveTimerRef = useRef(null);
  useEffect(() => () => clearTimeout(roomArriveTimerRef.current), []);
  // 📱 09-25: a phone postcard carries its own painting (phones have no room
  // wallpaper), so when it ends it stays up 400ms more and FADES while the
  // page fades back under it: one crossfade, never the painting blinking out.
  const [roomPostcardLeaving, setRoomPostcardLeaving] = useState(null);
  const lastPostcardRef = useRef(null);
  useEffect(() => {
    if (roomArriving) { lastPostcardRef.current = roomArriving; setRoomPostcardLeaving(null); return undefined; }
    const last = lastPostcardRef.current;
    lastPostcardRef.current = null;
    if (!last || !last.art) return undefined;
    setRoomPostcardLeaving(last);
    const t = setTimeout(() => setRoomPostcardLeaving(null), 420);
    return () => clearTimeout(t);
  }, [roomArriving]);
  // 🚪 09-19: the postcard is longer now, so it must be SKIPPABLE. Any key or
  // tap settles it at once (someone typing their next line wants the chat).
  // Armed a beat late so the tap that chose the room never skips its own arrival.
  useEffect(() => {
    if (!roomArriving) return undefined;
    const skip = () => { clearTimeout(roomArriveTimerRef.current); setRoomArriving(null); setRoomNotice(roomArriving.settled || null); };
    const arm = setTimeout(() => { window.addEventListener('keydown', skip); window.addEventListener('pointerdown', skip); }, 350);
    return () => { clearTimeout(arm); window.removeEventListener('keydown', skip); window.removeEventListener('pointerdown', skip); };
  }, [roomArriving]); // eslint-disable-line react-hooks/exhaustive-deps
  const [roomPeekOpen, setRoomPeekOpen] = useState(false);
  // 👁 Keyhole (ROOM_VERBS_PEEK_SPEC D2, 09-18 late): the room id being peeked
  // into from a picker card, or null. The ref lets the PICKER's own Esc
  // handler stand down while the keyhole is up (one Esc = one layer).
  const [roomKeyhole, setRoomKeyhole] = useState(null);
  const roomKeyholeRef = useRef(null);
  roomKeyholeRef.current = roomKeyhole;
  // 🦝 Where's Gerald (ROOM_VERBS_PEEK_SPEC §4 P5, D3 + D4): his room is a date
  // hash of the HOUSE day over the open rooms (the backend computes the same
  // one for the polaroid caption, roomSnapParity.test.js pins the two equal).
  // No backend state: the streak is per device. null for every room but
  // today's, and then both peeks render exactly the DOM they always have.
  const [geraldState, setGeraldState] = useState(() => readGerald());
  const geraldDay = houseStatus?.circadian?.clock?.day || null;
  const geraldRoom = roomCameraOn ? geraldRoomFor(geraldDay, (houseStatus?.rooms || []).map((r) => r.id)) : null;
  const geraldFor = (roomId) => {
    const g = geraldPeekProps(roomId, geraldRoom, geraldDay, geraldState);
    return g ? { ...g, onFind: () => setGeraldState(markGeraldFound(geraldDay)) } : null;
  };
  // 🦝🧦 Lost & found (LOST_AND_FOUND_SPEC_2026_09_24 W2): the server sends
  // `house.lost` ONLY to a viewer standing in that room, and the thing hides at
  // Gerald's spot there. A catch hands it back through the interjection
  // piggyback; the finder reads the server's own words under the picture, and
  // the thing leaves the glass at once (the next status says found).
  const [lostNote, setLostNote] = useState(null); // { room, id?, text }
  const [lostBusy, setLostBusy] = useState(null);  // the case id in flight
  useEffect(() => { setLostNote(null); }, [viewerRoom]);
  const returnLost = (id) => {
    if (!id || lostBusy || !gameId || !humanPlayerId) return;
    const room = viewerRoom;
    setLostBusy(id);
    chatGameApi.submitLostFound(gameId, humanPlayerId, id)
      .then((r) => setLostNote({ room, id, text: (r && r.private_message) || 'Got it.' }))
      // no id on the note: the thing stays in the glass for another try
      .catch(() => setLostNote({ room, text: 'That slipped. Try again in a bit.' }))
      .finally(() => setLostBusy(null));
  };
  const lostFor = (roomId) => {
    const p = lostPeekProps(roomId, houseStatus?.lost);
    const note = (lostNote && lostNote.room === roomId) ? lostNote.text : '';
    if (p && p.id && !(lostNote && lostNote.id === p.id)) {
      return { ...p, onReturn: returnLost, busy: lostBusy === p.id, ...(note ? { line: note } : {}) };
    }
    const line = note || (p && p.found ? lostFoundLine(p.found) : '');
    return line ? { line } : null;
  };
  const [composerSeedNonce, setComposerSeedNonce] = useState(0);
  useEffect(() => {
    const veil = (roomCameraOn && !roomWallImage) ? roomWallpaperVeil(artRoom, roomVeilHour) : null;
    const root = document.documentElement;
    const put = (k, v) => { if (v) root.style.setProperty(k, v); else root.style.removeProperty(k); };
    put('--room-wallpaper-veil', veil);
    put('--room-wallpaper-art', roomWallImage);
    put('--room-wallpaper-dim', roomWallDim);
    return () => {
      root.style.removeProperty('--room-wallpaper-veil');
      root.style.removeProperty('--room-wallpaper-art');
      root.style.removeProperty('--room-wallpaper-dim');
    };
  }, [roomCameraOn, artRoom, roomVeilHour, roomWallImage, roomWallDim]);
  // The ONE animated number: --room-dim-a (registered via @property next to
  // the masthead so it can transition; browsers without @property just snap).
  useEffect(() => {
    const root = document.documentElement;
    if (roomWallImage) root.style.setProperty('--room-dim-a', String(roomCurtain ? WALL_DIM_ARRIVE : roomLaneA));
    else root.style.removeProperty('--room-dim-a');
    // 💭 the thoughts line is the weakest text on the page (measured 3.9:1 by
    // day even at the old 0.6 dim): one notch brighter, ONLY over room art.
    if (roomWallImage) root.style.setProperty('--room-thoughts-color', '#c9bba0');
    else root.style.removeProperty('--room-thoughts-color');
    return () => { root.style.removeProperty('--room-dim-a'); root.style.removeProperty('--room-thoughts-color'); };
  }, [roomWallImage, roomLaneA, roomCurtain]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (viewerRoom === 'house') setRoomPeekOpen(false); }, [viewerRoom]);
  useEffect(() => { setRoomKeyhole(null); }, [viewerRoom]);
  useEffect(() => {
    if (!roomKeyhole) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setRoomKeyhole(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [roomKeyhole]);
  useEffect(() => {
    if (!roomPeekOpen) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setRoomPeekOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [roomPeekOpen]);
  // Who's in a room right now: circadian place match, client-side and free
  // (the same match the move reply and the room list use).
  const roomPresenceFor = (roomId) => {
    const sts = houseStatus?.circadian?.statuses || {};
    const rc = (houseStatus?.rooms || []).find((r) => r.id === roomId);
    // 👥 10-01 people: people in this room lead (house.people), never you (seat or name), one face per name
    const ppl = houseStatus?.people || null;
    const meKey = String(users.find((u) => u.game_player_id === effectivePlayerId)?.name || '').toLowerCase();
    const seenPeople = new Set();
    const persons = ppl ? Object.entries(ppl)
      .filter(([pid, p]) => p && p.room === roomId && pid !== effectivePlayerId)
      .filter(([, p]) => { const k = String(p.name || '').toLowerCase(); if (!k || k === meKey || seenPeople.has(k)) return false; seenPeople.add(k); return true; })
      .map(([pid, p]) => ({ name: p.name, avatar: users.find((u) => u.game_player_id === pid)?.profile_image_url || null, doing: null, person: true }))
      : [];
    if (roomId === 'house') {
      const roomPlaces = new Set((houseStatus?.rooms || []).map((r) => r.place).filter(Boolean));
      return [...persons, ...users.filter((u) => sts[u.game_player_id]?.place && !roomPlaces.has(sts[u.game_player_id].place)) // 👥 10-01 people: persons first
        .map((u) => ({ name: u.name, avatar: u.profile_image_url || null, doing: sts[u.game_player_id]?.doing || sts[u.game_player_id]?.line }))];
    }
    return [...persons, ...(rc?.place // 👥 10-01 people: persons first
      ? users.filter((u) => sts[u.game_player_id]?.place === rc.place)
        .map((u) => ({ name: u.name, avatar: u.profile_image_url || null, doing: sts[u.game_player_id]?.doing || sts[u.game_player_id]?.line }))
      : [])]; // 👥 10-01 people
  };
  // /look's words follow the room you are IN: during a walk the wallpaper may
  // still hold the room you are leaving (artRoom), so its night flag is not yours.
  const roomLookDark = () => !!(artRoom === viewerRoom
    ? roomArt?.masthead?.dark
    : roomMasthead(viewerRoom, roomVeilHour, houseStatus?.room_art, houseStatus?.weather?.kind, roomSeason)?.dark);
  // 👀 /look — private, client-built, zero-LLM (roomLookText). Watcher counts
  // live here, not on the plate (Nina 09-17).
  const buildRoomLookNotice = () => {
    const rc = (houseStatus?.rooms || []).find((r) => r.id === viewerRoom);
    const watchers = houseStatus?.room_watchers || {};
    return roomLookText({
      dark: roomLookDark(),
      roomId: viewerRoom,
      label: rc?.label,
      hour: roomVeilHour,
      here: roomPresenceFor(viewerRoom),
      others: houseStatus?.people ? 0 : Math.max(0, (watchers[viewerRoom] || 0) - 1), // 👥 10-01 people: the faces say who is here
      weatherLabel: houseStatus?.weather?.label || null,
      tvOn: !!houseStatus?.tv?.on,
    });
  };
  // 👁 09-23 (Nina: option A + typed follows): /look opens the room's PICTURE
  // with the look text beside it — same fields, structured (roomLookParts),
  // one source with the joined-text fallback above.
  const buildRoomLookParts = () => {
    const rc = (houseStatus?.rooms || []).find((r) => r.id === viewerRoom);
    const watchers = houseStatus?.room_watchers || {};
    return roomLookParts({
      dark: roomLookDark(),
      roomId: viewerRoom,
      label: rc?.label,
      hour: roomVeilHour,
      others: houseStatus?.people ? 0 : Math.max(0, (watchers[viewerRoom] || 0) - 1), // 👥 10-01 people: the faces say who is here
      weatherLabel: houseStatus?.weather?.label || null,
      tvOn: !!houseStatus?.tv?.on,
    });
  };
  // 🪧 The room plate (RoomPlate.js): what STAYS while you're in a room. ONE
  // builder for both composer mounts so the twins can't drift. The main room
  // mounts nothing (byte-identical there and in every non-camera space).
  const roomDeclutter = roomCameraOn && viewerRoom !== 'house' && !isMobile;
  // 👁🚪 09-24 fix (sweep #151 seam audit, Nina: fix it): the peek only ever
  // shows the room you are IN. A walk holds the room you are LEAVING on the
  // wallpaper (artRoom) until the new painting fills the screen, while the
  // camera is already in the new room; meanwhile the peek has no picture, so
  // its openers (the ?room=…&view=peek link, an event's Go) wait for the walk
  // to land instead of opening on the previous room's painting.
  const roomPeekUrl = peekPictureUrl(roomArt?.masthead, artRoom, viewerRoom);
  // 🚪 THE one mover: the plate's way back, a picker row, a picker card and
  // the event invite all ride the same private /room path as typing it.
  const goRoom = (text) => handleInterjectionSubmit('', [], { name: 'room', targets: [], text }).catch(() => {});
  // 🔗 09-22 show night (Nina: "let's add it"): a deep link for the open
  // painted screen. /game-step?gameId=…&room=lighthouse&view=peek moves the
  // camera to that room and opens its picture (during a screening the ▶ on
  // the painted screen rides it, and the tap on ▶ is the audio gesture).
  // Applied ONCE per page load, in two steps: the move waits for the seat,
  // the house payload AND the room being listed by the picker (never a stray
  // "/room x" post for a locked or not-yet-open room); the picture waits until
  // the camera is in that room and its art is in hand.
  // `view=screen` is accepted as a synonym, reserved for the projector view.
  const deepLinkRef = useRef({ room: false, view: false, target: '' });
  const deepLink = useMemo(() => {
    try {
      const p = new URLSearchParams(window.location.search);
      return { room: (p.get('room') || '').trim().toLowerCase(), view: (p.get('view') || '').trim().toLowerCase() };
    } catch (e) { return { room: '', view: '' }; }
  }, []);
  // 🔌 09-28 agent wave: an agent's watch_url carries ?with=<its seat id>.
  // Read ONCE (uuid-gated, it lands in a CSS selector): that seat's bubbles
  // and chips get a cyan highlight (the <style> under ThemeStyleInjector) and
  // the watcher gets one toast when the seat shows up as a connected agent.
  const withSeatId = React.useMemo(() => readWithSeatId(window.location.search), []);
  const withUser = React.useMemo(() => (withSeatId
    ? activeUsers.find(u => (u.id === withSeatId || u.game_player_id === withSeatId) && isExternalAgent(u)) || null
    : null), [activeUsers, withSeatId]);
  const [withToast, setWithToast] = useState(null);
  const withToastShownRef = useRef(false);
  // Keyed on the seat's id, never the object: `users` is rebuilt every ~5s
  // poll, so withUser is a new object each time. Shows once per page load.
  useEffect(() => {
    if (!withUser || withToastShownRef.current || isBotMode) return;
    withToastShownRef.current = true;
    setWithToast(withToastText(withUser.name || 'Your AI'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [withUser ? withUser.id : null, isBotMode]);
  // The 8s dismiss lives in its OWN effect (same as the go-live toast): inside
  // the effect above, every poll would cancel the timer.
  useEffect(() => {
    if (!withToast) return;
    const t = setTimeout(() => setWithToast(null), 8000);
    return () => clearTimeout(t);
  }, [withToast]);
  useEffect(() => {
    if (isBotMode || !roomCameraOn) return;
    if (!deepLink.room && !deepLink.view) return;
    if (!deepLinkRef.current.room) {
      if (!humanPlayerId || !Array.isArray(houseStatus?.rooms)) return;
      if (deepLink.room) {
        // 🔗 an event room (the lighthouse) exists only while its event runs:
        // a link shared BEFORE the doors waits here (the countdown line shows
        // meanwhile) and carries you in at the first poll that lists the room.
        // A room that never appears waits harmlessly: no post, no picture.
        const known = houseStatus.rooms.some(r => r && r.id === deepLink.room);
        if (!known) return;
        deepLinkRef.current.target = deepLink.room;
        if (viewerRoom !== deepLink.room) goRoom(deepLink.room);
      }
      deepLinkRef.current.room = true;
    }
    if ((deepLink.view === 'peek' || deepLink.view === 'screen') && !deepLinkRef.current.view) {
      if (deepLinkRef.current.target && viewerRoom !== deepLinkRef.current.target) return; // the move has not landed
      if (!roomPeekUrl) return;
      deepLinkRef.current.view = true;
      setRoomPeekOpen(true);
    }
  }, [isBotMode, roomCameraOn, humanPlayerId, houseStatus?.rooms, viewerRoom, roomPeekUrl, deepLink]); // eslint-disable-line react-hooks/exhaustive-deps
  // 🚪🖼️ The rooms picker (09-18, mockup v12): ONE model (roomView
  // roomPickerRows/Cards) behind TWO doors — the LIST under "/room " in the
  // composer palette (houseRooms.rows) and the PICTURES popup that the door
  // in the message box (houseRooms.door) / a bare "/room" send opens over the chat. Args are built lazily (a
  // closure, called from JSX) because roomInvite is declared further down —
  // an eager read here would be a TDZ white-page.
  const [roomPickerOpen, setRoomPickerOpen] = useState(false);
  // 📅 The house calendar door (09-19, HOUSE_CALENDAR_OUTINGS_SPEC D2-A): the
  // room door's sibling in the message box, opening an AGENDA list. Payload-
  // gated: house.calendar is absent until HOUSE_CALENDAR_ENABLED, so every
  // other space (and a dark backend) renders nothing. Mutual exclusion with
  // the room picker is free: each popup's outside-click handler treats the
  // OTHER door as "outside". Declared up here, beside roomPickerOpen, so no
  // closure below can read it in its TDZ.
  const [calendarOpen, setCalendarOpen] = useState(false);
  // ⏳🎬 09-22 (Nina): the countdown clock. One 15s tick drives the door leaf
  // and the line above the message box (calendarView.eventStrip, pure). The
  // tap is state-aware: before the doors it opens the calendar; at the doors
  // and during the film it is the same thing the ▶ does (go + sit).
  const [stripNow, setStripNow] = useState(() => Date.now());
  useEffect(() => { const id = setInterval(() => setStripNow(Date.now()), 15 * 1000); return () => clearInterval(id); }, []);
  // 🎬🍿 09-22 show night (the first live screening): a seat is "sit AND open
  // the slot". inTheater alone portals the theater into the HIDDEN holder
  // whenever the stage is collapsed (every desktop that arrived AFTER the party
  // started: arrival never docks, and a party dock is never restored) or the
  // phone is not on the TV place: you heard the film and saw nothing. Same pair
  // the couch toast (phone) and the tab-strip party bar (desktop) already do.
  const seatInTheater = () => {
    setInTheater(true);
    if (placesNav) setMobileView('tv');
    else setStageTab('watch_party');
  };
  // 🚪🎬 09-23 batch 1 (handoff §3.3, ruling 2): Go walks you INTO THE PICTURE for
  // every event; the ▶ on the painted screen is the only thing that seats you.
  // The same two-step the deep link does: move the camera, then open the art
  // once the camera has landed and the art is in hand (a locked or slow room
  // never strands you: the wait is harmless). PEEK_STAGE false ⇒ 'seat' as before.
  const goPeekRef = useRef(null);
  const openPictureIn = (room) => {
    if (!room || viewerRoom === room) { if (roomPeekUrl) setRoomPeekOpen(true); else goPeekRef.current = room || viewerRoom; return; }
    goPeekRef.current = room;
    goRoom(room);
  };
  useEffect(() => {
    const target = goPeekRef.current;
    if (!target || viewerRoom !== target || !roomPeekUrl) return;
    goPeekRef.current = null;
    setRoomPeekOpen(true);
  }, [viewerRoom, roomPeekUrl]);
  // 🌕 09-26: the running LOCATED event's room (the moon night → the roof), for
  // the strip's Go. Written further down, after liveEvent exists (reading it
  // here would be a TDZ white-page), so the strip sees it one render late: the
  // 15s tick makes that invisible.
  const eventRoomRef = useRef(null);
  // ⏰ 10-08 (Nina, the Draconids night: "On now" and "Open" did not take her to the roof): the room a
  // calendar night WILL happen in, read at call time through the start card's own resolver
  // (roomView.roomInviteFor: the sky nights → the roof, else the porch; a located night → its room).
  // Only an OPEN room counts (houseStatus.rooms), so a locked or unknown room still gets the 📅.
  const nightRoomFor = (row) => {
    try {
      const open = (houseStatus?.rooms || []).map((r) => r && r.id).filter(Boolean);
      if (!row || !open.length) return null;
      return (roomInviteFor({ template: row.template || null, room: row.room || null }, open) || {}).room || null;
    } catch (e) { return null; }
  };
  const eventStripProp = () => {
    if (!houseStatus?.calendar || isBotMode) return null;
    const strip = calendarEventStrip(houseStatus.calendar, {
      now: stripNow, ballot: houseStatus.ballot, outing: houseStatus.outing, screening: houseStatus.screening,
      peek: PEEK_STAGE, seated: !!(inTheater && watchParty && watchParty.screening), eventRoom: eventRoomRef.current,
      roomFor: nightRoomFor, // ⏰ 10-08: a night's own room before it starts (the strip's pre-start Go)
    });
    if (!strip) return null;
    return { ...strip, onGo: (st) => {
      if (st.go === 'calendar') { setCalendarOpen(true); return; }
      if (st.go === 'screen') { openPictureIn(st.room); return; }
      if (st.room && viewerRoom !== st.room) goRoom(st.room);
      if (st.go === 'seat') seatInTheater();
    } };
  };
  // ⏳ 09-23 batch 1 (handoff §3.1, ruling 7): ONE strip, computed once per
  // render, shown at the TOP (the identity block's first row on desktop, a
  // fixed pill in the existing Back/♫ row on phones). The composer line retires
  // while TOP_CLOCK is on (both HumanInputForm mounts get null). The ✕ store is
  // the composer's own key, so a hide made before this build still holds; it
  // is honored ONLY in before/opening/today (doors + playing are the door in).
  const topStrip = TOP_CLOCK ? eventStripProp() : null;
  const [stripHiddenKey, setStripHiddenKey] = useState(() => { try { return window.localStorage.getItem('hif_event_strip_hidden') || ''; } catch (e) { return ''; } });
  const hideTopStrip = (k) => { setStripHiddenKey(k); try { window.localStorage.setItem('hif_event_strip_hidden', k); } catch (e) { /* private mode */ } };
  const topStripHidden = !!(topStrip && ['before', 'opening', 'today'].includes(topStrip.phase) && stripHiddenKey === `${topStrip.key}|${topStrip.phase}`);
  // a 1s tick ONLY while the doors are open (the seating clock counts seconds)
  const topStripPhase = topStrip ? topStrip.phase : null;
  useEffect(() => {
    if (topStripPhase !== 'doors') return undefined;
    const id = setInterval(() => setStripNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [topStripPhase]);
  // 🎬🍿 09-23 batch 1 (AUX_DUCK, handoff §3.6, ruling 14): the house payload
  // says whether a screening is seating/playing; LofiRadio ducks its bed for
  // EVERYONE on that signal (its own event: the party event races on unmount).
  const screeningLiveNow = !!(AUX_DUCK && houseStatus?.screening && ['seating', 'playing'].includes(houseStatus.screening.state));
  useEffect(() => {
    if (!AUX_DUCK) return undefined;
    emitGameEvent(GAME_EVENTS.SCREENING_LIVE, { live: screeningLiveNow });
    return undefined;
  }, [screeningLiveNow]);
  useEffect(() => () => { if (AUX_DUCK) emitGameEvent(GAME_EVENTS.SCREENING_LIVE, { live: false }); }, []);
  // 🎬📝 09-23 batch 1 (handoff §3.7, ruling 16): THE CREDITS CARD. house.feedback
  // (payload-gated: absent while EVENT_FEEDBACK_ENABLED is dark) names the
  // running event, then for 45 min the one that just ended. "Was I there" is
  // stamped WHILE it runs (the outing, the screening and the room all vanish
  // in the wrap's tick), and the card renders in the feed dock's ladder.
  // 🎃 10-07 pumpkins (P7, PUMPKIN_FRONTEND_HANDOFF_2026_10_06.md): the carving table + the porch. Keys on
  // house.pumpkins (absent = HOUSE_PUMPKINS_ENABLED off, or outside the window): no chip, no sheet, no fetch.
  const pumpkinsRider = (!isBotMode && houseStatus?.pumpkins) || null;
  const [pumpkinSheetOpen, setPumpkinSheetOpen] = useState(false);
  const [pumpkinTick, setPumpkinTick] = useState(0);
  const pumpkinRoom = chatGameStatus?.game_id || gameInstanceId || gameId;
  const pumpkinSecret = humanPlayerId ? chatGameApi.getPlayerSecret(humanPlayerId) : null;
  const housePumpkins = useHousePumpkins({
    gameId: pumpkinRoom, playerId: humanPlayerId || null, playerSecret: pumpkinSecret,
    wanted: !!pumpkinsRider && (pumpkinSheetOpen || (roomPeekOpen && viewerRoom === 'porch')), tick: pumpkinTick,
  });
  const fbRider = houseStatus?.feedback || null;
  const fbEventId = fbRider && fbRider.event_id ? String(fbRider.event_id) : null;
  const fbPhase = fbRider ? fbRider.phase : null;
  const fbRoom = fbRider ? (fbRider.room || null) : null;
  useEffect(() => {
    if (!fbEventId || fbPhase !== 'running') return;
    if (fbRoom == null || viewerRoom === fbRoom || inTheater) markSeen(gameId, fbEventId);
  }, [fbEventId, fbPhase, fbRoom, viewerRoom, inTheater, gameId]);
  const [feedbackAnswers, setFeedbackAnswers] = useState({});
  const [feedbackText, setFeedbackText] = useState('');
  const [feedbackState, setFeedbackState] = useState('idle');
  const [feedbackDismissed, setFeedbackDismissed] = useState(null);
  const feedbackBusyRef = useRef(false); // a card button is NOT covered by the composer's in-flight lock
  const sendEventFeedback = async (fb) => {
    if (feedbackBusyRef.current || !fb || !fb.event_id) return;
    feedbackBusyRef.current = true;
    setFeedbackState('sending');
    try {
      await submitEventFeedback({
        gameId, playerId: humanPlayerId, playerSecret: humanPlayerId ? chatGameApi.getPlayerSecret(humanPlayerId) : null,
        eventId: fb.event_id, template: fb.template, answers: feedbackAnswers, text: feedbackText,
      });
      markDone(gameId, fb.event_id);
      setFeedbackState('thanks');
      // 🗓️ 10-02 (Nina): the thanks now carries the next event, a one-liner and the photo's Share: 45s to read
      // it (was 4s); its ✕ closes it sooner.
      setTimeout(() => { setFeedbackDismissed(feedbackDismissKey(fb) || fb.event_id); setFeedbackState('idle'); setFeedbackAnswers({}); setFeedbackText(''); }, 45000);
    } catch (e) {
      setFeedbackState('idle');
      setRoomNotice(String((e && e.message) || 'That did not send. Try again in a moment.'));
    } finally {
      feedbackBusyRef.current = false;
    }
  };
  // 🍎 one-tap /pick (Nina 09-19 pm): ONE pick in flight at a time, so a fast
  // thumb sends six picks in order and never a burst the cap has to reject.
  const pickBusyRef = useRef(false);
  // 🎬🍿 09-20 the screening (LIGHTHOUSE_SCREENING_SPEC): the film vote rides the
  // same private-command path as /pick, one vote in flight at a time. Both
  // payload keys are ABSENT while the backend flag is off, so all of this is inert.
  const voteBusyRef = useRef(false);
  // `cmd` = 'g:<n>' for a genre, '<n>' for a film (calendarView.ballotView)
  const voteFilm = (cmd) => {
    if (voteBusyRef.current) return;
    voteBusyRef.current = true;
    Promise.resolve(handleInterjectionSubmit('', [], { name: 'film', targets: [], text: String(cmd) }))
      .catch(() => {}).finally(() => { voteBusyRef.current = false; });
  };
  // 📋 10-14 /plan: the 🗓️ door's "Plan it" sends /plan <the row's label> on the typed command's own path. The
  // server answers with the card (it picks who) or a private line (the notice); a refusal (asleep, recharging)
  // comes back as an error and shows in the same notice. One in flight. Inert while house.plan is absent.
  const planBusyRef = useRef(false);
  const planNight = (label) => {
    if (planBusyRef.current) return;
    planBusyRef.current = true;
    Promise.resolve(handleInterjectionSubmit('', [], { name: 'plan', targets: [], text: String(label || '') }))
      .catch((e) => setRoomNotice(String((e && e.message) || 'That slipped. Try again in a bit.')))
      .finally(() => { planBusyRef.current = false; });
  };
  // 🎭 the poll-looking invitation: only for a seated visitor who has not voted
  // in the open stage, only after they have had a minute to arrive (the arrival
  // postcard and the tour come first), and "not now" hides it for this visit.
  const [ballotInviteReady, setBallotInviteReady] = useState(false);
  const [ballotInviteDismissed, setBallotInviteDismissed] = useState(null);
  useEffect(() => { const id = setTimeout(() => setBallotInviteReady(true), 60 * 1000); return () => clearTimeout(id); }, []);
  // the strip only exists in the screening's own room, and the ▶ only once the
  // projector's party is really there to sit down in
  const screeningHere = (houseStatus?.screening && houseStatus?.outing && viewerRoom === houseStatus.outing.id)
    ? screeningStrip(houseStatus.screening, Date.now(), inTheater || !(watchParty && watchParty.screening)) : null;
  // 🪧 09-23 batch 1 (handoff §3.2, ruling 8): the plate folds to ONE line during
  // a screening, once per film (keyed on starts_at) for anyone standing in the
  // lighthouse, seated or not; the arrow's choice sticks for the session
  // (sessionStorage) and the fold lets go at The End. The folded handle keeps
  // the one chip that must never vanish (▶ Watch un-seated, 🎬 Cinema seated).
  // ⚠️ never the STAGE: a collapsed stage parks the theater in the hidden holder.
  const CINEMA_VERB = '🎬 Cinema';
  const SHARE_VERB = '🔗 Share';
  const plateFoldStore = `plateFold:${gameId}`;
  const [plateFold, setPlateFold] = useState(() => { try { return window.sessionStorage.getItem(plateFoldStore) || 'auto'; } catch (e) { return 'auto'; } });
  const plateAutoKeyRef = useRef('');
  const plateFoldNow = !!(PLATE_FOLD && screeningHere && houseStatus?.screening && houseStatus.screening.state !== 'ended');
  const plateFoldKey = plateFoldNow ? String(houseStatus.screening.starts_at || 'x') : '';
  useEffect(() => {
    if (!PLATE_FOLD || !plateFoldKey || plateAutoKeyRef.current === plateFoldKey) return;
    plateAutoKeyRef.current = plateFoldKey;
    setPlateFold('auto');
    try { window.sessionStorage.removeItem(plateFoldStore); } catch (e) { /* private mode */ }
  }, [plateFoldKey, plateFoldStore]);
  const plateCollapsed = PLATE_FOLD && (plateFold === 'closed' || (plateFold === 'auto' && plateFoldNow));
  const togglePlateFold = () => {
    const next = plateCollapsed ? 'open' : 'closed';
    setPlateFold(next);
    try { window.sessionStorage.setItem(plateFoldStore, next); } catch (e) { /* private mode */ }
  };
  // 🔗 09-23 batch 1 (handoff §3.9, ruling 17): the event room's link, from the
  // plate (and the calendar rows). The deep link already waits at the door.
  // 🔗 10-08 A9 (spec §3.5, R14): ONE share path (utils/shareLink.shareHouse).
  // The night's own page (https://myspaice.ai/e/<slug>.html, its picture in every
  // preview) when the running or today's house/sky row has one and the switch is on;
  // else today's room link in a { url }-only sheet, exactly as 09-23. liveEvent and
  // houseStatus are read HERE, at click time: liveEvent is declared ~2,800 lines
  // below, so a render-time read is a TDZ white page.
  const shareRoomLink = () => {
    try {
      shareHouse({
        gameId, room: viewerRoom, calendar: houseStatus?.calendar || null,
        template: (liveEvent && liveEvent.template) || null,
        onToast: (msg) => setRoomNotice(String(msg)),
      });
    } catch (e) { /* no share path here */ }
  };
  const withShareVerb = (vs) => ((houseStatus?.outing && viewerRoom === houseStatus.outing.id) ? [...(vs || []), SHARE_VERB] : vs);
  // 🛋️ ONE builder for the couch's three homes (the theater's rail slot ·
  // under the film on phones · under the art on the picture), so they never
  // drift. Lines of another video (a party after the film) are left out.
  const couchNode = (variant) => (
    <CouchColumn
      items={couchLog.filter((q) => !q.video_id || !watchParty?.video_id || q.video_id === watchParty.video_id)}
      onPost={humanPlayerId ? ((text) => handleSpectatingSubmit(`@watchparty ${text}`)) : null}
      compact={variant === 'phone'}
      ended={!!(houseStatus?.screening && houseStatus.screening.state === 'ended')}
      THEME={THEME}
      maxHeight={variant === 'phone' ? 180 : variant === 'sheet' ? 160 : null}
    />
  );
  const houseCalendarProp = () => {
    if (!houseStatus?.calendar || isBotMode) return null;
    // 🕐 09-24 (Nina: option C, revised): the door's leaf is TODAY's house date
    // and its words are the HOUSE clock + weather at rest; the countdown and
    // "On now" still take over for an event. The zone is the house's own
    // (circadian clock.tz), never the viewer's; stripNow ticks it every 15s.
    const face = calendarDoorFace(houseStatus.calendar, stripNow, { tz: houseStatus?.circadian?.clock?.tz, weatherKind: houseStatus?.weather?.kind });
    return { door: { face, hot: face.hot, title: face.title, open: calendarOpen, onToggle: () => {
      // 🎪 09-26 (Nina): while an event runs in a room, "On now" goes straight to its scene (until it ends)
      // ⏰ 10-08: "Starting soon" (the start passed, the engine not yet begun) walks to the night's room too
      const room = face.words === 'On now' ? eventRoomRef.current
        : (face.words === DOOR_WORDS.soon ? nightRoomFor(calendarNextNight(houseStatus.calendar)) : null);
      if (room && (houseStatus?.rooms || []).some((r) => r && r.id === room)) { setCalendarOpen(false); openPictureIn(room); return; }
      setCalendarOpen((o) => !o);
    } } };
  };
  // 🔋 09-26 (Nina: "a battery by the clock with hours left", then "in the
  // place of the money bag at the top right"): the power meter is the
  // masthead vitals row's last chip, in the till pill's spot in the house.
  // 09-26 pm: in EVERY space (one key feeds the fleet, so they all sleep
  // together), wherever the vitals row or the phone ❤️/👎 row draws; outside
  // the house its words say "the AIs here". `ai_power` is top-level in the
  // status payload and only present while the backend's HOUSE_ASLEEP_ENABLED
  // is on: dark until then, and the till pill keeps its spot.
  const spacePowerView = () => {
    if (isBotMode) return null;
    return powerMeterView(chatGameStatus?.ai_power, { tz: houseStatus?.circadian?.clock?.tz, isHouse: !!houseStatus, spaceName: chatGameStatus?.name });
  };
  // 🌙 W2b (treatment C, Nina 09-26): the asleep look keys on the same block,
  // in EVERY space (one key feeds the fleet, so at $0 everyone sleeps).
  // Absent = awake = every surface unchanged.
  const aiAsleep = aiIsAsleep(chatGameStatus?.ai_power);
  const restingProp = () => (aiAsleep ? restingBanner(chatGameStatus?.ai_power, { tz: houseStatus?.circadian?.clock?.tz, spaceName: chatGameStatus?.name }) : null);
  // 📚 THE HOUSE LIBRARY (HOUSE_LIBRARY_SPEC_2026_09_30.md §4.3 W2; hooks/useHouseLibrary.js
  // holds every piece of its state). `library.live` = house.book is PRESENT in the status
  // payload (the backend's HOUSE_LIBRARY_ENABLED): absent, every hunk below is a no-op and
  // the page is today's. ⚠️ Lives HERE (after viewerRoom/goRoom/roomCurtain, before every
  // reader of it: the room chips, the picker, the ghost column, the composer intercept).
  const library = useHouseLibrary({
    gameId, humanPlayerId, houseStatus, viewerRoom, roomCameraOn, isMobile, isBotMode,
    goRoom, setRoomNotice, roomCurtain, placesNav, mobileView, centerColumnRef, inputFormRef,
  });
  const libraryRef = useRef(library);
  libraryRef.current = library; // the socket handler + setGhostPanelOpen read the newest
  // 📚 desktop B: the feed and the book share ONE row (the middle column splits; a middle
  // too narrow to split stacks the book above the chat). The wrapper exists only once the
  // library has been live on this page (display: contents while nobody reads), so a space
  // without house.book renders exactly today's tree. Called from JSX (it reads consts
  // declared further down: never call it from the body).
  const libraryFeedRow = (feed) => {
    if (!library.wrapLatched || shellPreset) return feed;
    const botLine = isBotMode && library.here;
    const mode = botLine ? 'stack' : library.deskMode;
    return (
      <div data-book-row={mode || 'off'} style={mode ? {
        display: 'flex', flexDirection: mode === 'split' ? 'row' : 'column', alignItems: 'stretch',
        gap: mode === 'split' ? 10 : 8, flex: '1 1 0%', minWidth: 0, boxSizing: 'border-box', paddingRight: library.deskReserve || 0,
        minHeight: (theaterHero || portedPlaceOn) ? 'clamp(72px, 12dvh, 140px)' : 'clamp(120px, 22dvh, 260px)',
      } : { display: 'contents' }}>
        {feed}
        {mode ? (
          <BookReader layout={mode} book={library.book} heldAt={library.heldAt} me={humanPlayerId}
            manifest={library.manifest} onAct={library.act} asleep={aiAsleep} bot={botLine} />
        ) : null}
      </div>
    );
  };
  // 📚 phone B: the page fills the phone (fixed, over the chat, under the dock + nav), the
  // newest room lines float over it as the scene chat's bubbles; "Chat ▾" folds it
  const libraryPhoneNode = () => (library.phoneReading ? (
    <BookReader layout="phone" book={library.book} heldAt={library.heldAt} me={humanPlayerId}
      manifest={library.manifest} onAct={library.act} onFold={library.foldPhone} asleep={aiAsleep}
      curtain={roomCurtain} bottom={library.dockTop}
      bubbles={bookChatLines(results.flatMap((r) => r.actions || []), sceneSeatOf, 4)} />
  ) : null);
  // 📚 the Shelf (a RoomSheet, portaled to <body>: a dialog on desktop, a bottom sheet on phones)
  const libraryShelfNode = () => (library.live ? (
    <LibraryShelf open={library.shelfOpen} onClose={library.closeShelf} isMobile={isMobile}
      manifest={library.manifest} shelfLoad={library.shelfLoad} onRetry={library.retryShelf}
      book={library.book} me={humanPlayerId} serverNow={serverNowFor(library.book, library.heldAt)}
      here={viewerRoom === 'library'} alongLine={readingAlongLine(roomPresenceFor('library').filter((p) => !p.person /* 👥 10-01 people: residents only */).map((p) => p.name))}
      note={library.shelfNote} oddPick={library.oddPick} onOddPick={library.setOddPick}
      busySlug={library.busySlug} onOpenBook={library.openBook} onJoin={library.joinOrRead} />
  ) : null);
  useEffect(() => {
    if (!calendarOpen) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setCalendarOpen(false); };
    const onDown = (e) => {
      const t = e.target;
      if (t && t.closest && (t.closest('[data-calendar-agenda]') || t.closest('[data-calendar-door]'))) return;
      setCalendarOpen(false);
    };
    window.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown);
    return () => {
      window.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDown);
    };
  }, [calendarOpen]);
  // ONE builder for both mounts, the room picker's shape: 'desktop' floats up
  // from the composer, 'dock' renders in flow at the top of the phone dock.
  const renderCalendar = (mount) => {
    if (!calendarOpen || !houseStatus?.calendar || isBotMode) return null;
    // 🚪 09-19 pm: while the house is OUT the running row carries "Go to …" (the
    // plate band is retired, so this is the on-page way to follow them).
    // 🔗 09-23 batch 1 (handoff §3.9, ruling 17): the rows' Share chip needs the game id; the toast is the room notice line
    // 🔥 10-03 game nights: the campfire's tin rides its night's row (house.campfire, absent while dark); its "/tale" chip seeds the box
    // 📋 10-14 /plan: "Plan it" rides the rows house.plan lists (absent while dark = no chip)
    const sheet = <CalendarAgenda rows={calendarAgendaRows(houseStatus.calendar, roomVeilHour, undefined, { outing: houseStatus.outing, viewerRoom, ballot: houseStatus.ballot, campfire: houseStatus.campfire, plan: houseStatus.plan })} onClose={() => setCalendarOpen(false)} onGo={roomCameraOn ? goRoom : undefined} onVote={houseStatus.ballot ? voteFilm : undefined} gameId={gameId} onToast={(msg) => setRoomNotice(String(msg))}
      onPlan={houseStatus.plan ? planNight : undefined}
      onTale={houseStatus.campfire && houseStatus.campfire.open === true ? () => { setComposerSeed('/tale '); setComposerSeedNonce((n) => n + 1); } : undefined} />;
    return mount === 'desktop'
      ? <div style={{ position: 'absolute', bottom: '100%', left: 0, right: 0, marginBottom: 8, zIndex: 995, maxHeight: 'min(56vh, 460px)', overflowY: 'auto' }}>{sheet}</div>
      : <div style={{ marginBottom: 8, maxHeight: '56dvh', overflowY: 'auto' }}>{sheet}</div>;
  };
  const roomPickerArgs = () => ({
    rooms: houseStatus?.rooms || [],
    viewerRoom,
    hour: roomVeilHour,
    artMap: houseStatus?.room_art,
    weatherKind: houseStatus?.weather?.kind,
    weatherLabel: houseStatus?.weather?.label,
    season: roomSeason,
    tvOn: !!houseStatus?.tv?.on,
    bannerUrl: chatGameStatus?.ui_theme?.bannerImage?.url || null,
    presenceFor: roomPresenceFor,
    watchers: houseStatus?.room_watchers || {},
    inviteRoom: roomInvite?.room || null,
    bookOpen: library.live && library.open, // 📚 the library card's mark: "📖 open"
    heat: houseStatus?.room_heat || {},        // 👥 10-01 people: 0-3 per room (R13), you never count
    peopleLive: !!houseStatus?.people,          // 👥 10-01 people: rows read 'N people', not '10-min watching'
  });
  // 🌿 09-25 (Nina: option B + D, https://claude.ai/artifact/SKDBxsmPaRKqDGoYyCZPrW):
  // the room bar ABOVE the box is retired. Its pieces ride the message box
  // (HumanInputForm `houseRooms.floor`): the verbs on the box's floor after
  // the doors, who is here in the empty box, the way back in the rooms door
  // (the picker's first card is the main room). ONE builder for the verbs,
  // the band, the tap and the look, shared with the old plate, which stays
  // for a one-line revert (ROOM_FLOOR = false).
  const ROOM_FLOOR = true;
  // 🍎🧺 standing IN the outing: the band is the shared basket instead
  // 📅 09-19 (Nina): upcoming nights live in the CALENDAR door, not on the
  // plate (this reverses D2's "C plate band"). The plate keeps ONE band,
  // the live basket while you are standing in an outing.
  const roomBarBand = () => ((houseStatus?.outing && viewerRoom === houseStatus.outing.id) ? outingBand(houseStatus.outing, houseStatus?.pumpkins) : null) // 🎃 10-07 the patch: "N on the porch"
    || (screeningHere ? { text: screeningHere.text, hot: screeningHere.hot } : null);
  // 🕯️ a room with a real verb shows ONLY the things you can do here
  // (Nina 09-18: the prefills go); the older prefills survive in rooms
  // with no verb yet, and everywhere while the flag is dark.
  // 🎬 09-20: /pick only where the outing HAS a basket (outingVerbs reads the
  // same signal as the band). It used to ride ANY outing, so the lighthouse
  // lamp room would have offered an apple chip on its first night.
  // 🪧🔗 09-23: seated at the film the plate offers 🎬 Cinema (the stage / TV place); in an outing room every list ends with 🔗 Share
  // 🎃 10-07: the plain "Carve" chip leads at the patch + on the porch while the table is out (off = the same list back)
  const roomBarVerbs = () => withCarveChip(withShareVerb(((houseStatus?.outing && viewerRoom === houseStatus.outing.id) ? outingVerbs(houseStatus.outing) : null)
    || (screeningHere && (screeningHere.verbs || (PLATE_FOLD && inTheater ? [CINEMA_VERB] : null)))
    || eventFirst(libraryChips(plateChips(viewerRoom, liveRoomVerbs(houseStatus?.room_verbs), roomVerbs(viewerRoom))))), carveChipHere(pumpkinsRider, viewerRoom)); // 🎛️ 10-09 (Nina): event-only verbs lead, ahead of the 📚 Shelf too
  // 📚 in the library the 📚 Shelf chip leads (it replaces /borrow: bare /borrow opens the shelf now)
  const libraryChips = (vs) => ((library.live && viewerRoom === 'library') ? withShelfChip(vs, library.book) : vs);
  // 🍎 09-19 pm (Nina: "one tap sends"): /pick has no argument and no
  // target, so the chip IS the send. 👆 10-01 one-tap (Nina): so is every
  // room verb that needs no words (/snap /water /knock …, roomVerbs.chipTapCommand);
  // a chip that needs words still seeds the composer. Same submit path as the
  // typed command, so the private answer, the cap line and no_wake all behave the same.
  const onRoomBarVerb = (v) => {
    if (v === CARVE_CHIP) { setPumpkinSheetOpen(true); return; } // 🎃 10-07 client-only: opens the carving table
    // 🎬🍿 the one-tap seat: same as "Take a seat", from the lamp room's plate
    if (v === WATCH_VERB) { seatInTheater(); return; }
    if (v === CINEMA_VERB) { seatInTheater(); return; }
    if (v === SHARE_VERB) { shareRoomLink(); return; }
    if (library.isChip(v)) { library.onChip(); return; } // 📚 client-only: the shelf (or a folded phone book)
    if (v === '/look') { onRoomBarLook(); return; } // 👀 09-26 the roof leads with /look (roomFloorProp)
    // 🌽 09-28 maze: /left + /right are one-tap too, sharing the ONE in-flight guard
    const oneTap = chipTapCommand(v); // 👆 10-01 one-tap: was /pick /left /right only
    if (oneTap) {
      if (pickBusyRef.current) return;
      pickBusyRef.current = true;
      Promise.resolve(handleInterjectionSubmit('', [], { name: oneTap, targets: [], text: '' })) // 🌽 09-28 maze: oneTap (was 'pick')
        .catch((err) => { // 👆 10-01 one-tap: the server's own line (a cooldown, the wrong room) shows instead of vanishing
          if (!err || err.quiet) return; // 📱 the app's sign-in door stays quiet
          const said = typeof err.message === 'string' ? err.message : '';
          setRoomNotice(!said || /^(Failed to|NetworkError|Load failed|No player ID)/i.test(said) ? "That didn't go through. Try again." : said);
        })
        .finally(() => { pickBusyRef.current = false; });
      return;
    }
    setComposerSeed(v); setComposerSeedNonce((n) => n + 1);
  };
  // 👁 09-23 (Nina: A + typed follows): the peek chip is retired — /look
  // IS the picture. A room with no painting keeps the quiet note.
  const onRoomBarLook = () => { if (roomPeekUrl) setRoomPeekOpen(true); else setRoomNotice(buildRoomLookNotice()); };
  // 🎛️ 10-09 (Nina: the event buttons "should be able to work while the user is on the full
  // scene page (not the chat tab)"): the picture's row sends exactly what the message box's chip
  // sends. Carve closes the picture and opens the table; a chip that needs no words shares the
  // floor's ONE in-flight guard; one that needs words sends `/verb words` the way a typed command
  // does. Both go through the plain submit: a room verb never wakes the room from here (its
  // replies all carry no_wake), and the reply holds the house's answer. → { ok, line }
  // (sceneAnswer): a refusal is a 200 with a private line, and the chat's notice line that shows
  // it sits under the picture, so the scene shows the line; a failed send rejects with its words.
  const sceneCommandFail = (err) => {
    if (err && err.quiet) throw err; // 📱 the app's sign-in door took over: the row stays quiet
    const said = err && typeof err.message === 'string' ? err.message : '';
    throw new Error(!said || /^(Failed to|NetworkError|Load failed|No player ID)/i.test(said) ? "That didn't go through. Try again." : said);
  };
  const onSceneCommand = (chip, text = '') => {
    if (chip === CARVE_CHIP) { setRoomPeekOpen(false); setPumpkinSheetOpen(true); return Promise.resolve({ ok: true, line: null }); }
    const oneTap = chipTapCommand(chip);
    if (oneTap) {
      if (pickBusyRef.current) return Promise.reject(new Error('One moment.'));
      pickBusyRef.current = true;
      return Promise.resolve(handleInterjectionSubmit('', [], { name: oneTap, targets: [], text: '' }))
        .then(sceneAnswer, sceneCommandFail)
        .finally(() => { pickBusyRef.current = false; });
    }
    const name = String(chip || '').trim().replace(/^\//, '').toLowerCase();
    const words = String(text || '').replace(/\s+/g, ' ').trim();
    if (!name || !words) return Promise.reject(new Error('Add a few words first.'));
    return Promise.resolve(handleInterjectionSubmit(`/${name} ${words}`, [], { name, targets: [], text: words, card_message: null }))
      .then(sceneAnswer, sceneCommandFail);
  };
  const roomFloorProp = () => {
    if (!ROOM_FLOOR || !roomCameraOn || isBotMode || viewerRoom === 'house') return null;
    return {
      presence: roomPresenceLine(roomPresenceFor(viewerRoom)) || ROOM_EMPTY_LINE,
      note: roomPlateNote ? roomPlateNote.text : null,
      band: roomBarBand(),
      // 👀 09-26 (Nina): on the roof /look is the FIRST chip (and not repeated at the end)
      verbs: viewerRoom === 'roof' ? ['/look', ...(roomBarVerbs() || []).filter((v) => v !== '/look')] : (roomBarVerbs() || []),
      onVerb: onRoomBarVerb,
      onLook: viewerRoom === 'roof' ? null : onRoomBarLook,
      lit: (library.live && viewerRoom === 'library') ? [library.chipLabel] : null, // 📚 the shelf chip in lamp gold
    };
  };
  // Both HumanInputForm mounts pass exactly this (keep them identical).
  const houseRoomsProp = () => (roomCameraOn
    ? {
      rooms: houseStatus.rooms || [], current: viewerRoom, rows: roomPickerRows(roomPickerArgs()), onGo: goRoom,
      people: !!houseStatus.people,             // 👥 10-01 people: gates /status in the palette, the parser, the Try list AND the arg bar
      // 🍎 the outing the house is on right now (null at home): gates /pick
      outing: houseStatus.outing?.id || null,
      // 🔥 10-03 game nights: the campfire tin (house.campfire, absent while dark): gates /tale (taleHere)
      campfire: houseStatus.campfire || null,
      // 📋 10-14 /plan (house.plan, absent while dark): gates /plan (planHere)
      plan: houseStatus.plan || null,
      // 🕯️ room verbs the backend lists AND this bundle knows ([] while dark)
      verbs: liveRoomVerbs(houseStatus.room_verbs),
      // 🚪▾ the door in the message box (Nina 09-18, option B): every room, both mounts
      door: {
        icon: viewerRoom === 'house' ? '🕯️' : (ROOM_ICONS[viewerRoom] || '🚪'),
        label: viewerRoom === 'house' ? 'the main room' : ((houseStatus.rooms || []).find((r) => r.id === viewerRoom)?.label || viewerRoom),
        open: roomPickerOpen,
        onToggle: () => { setRoomNotice(null); setRoomPickerOpen((o) => !o); },
        // 🐢 a slow room change stepped its walk aside (RoomTransit): the
        // candle hops from room to room in the door until the house answers
        walking: walkingDoor(roomTransit, houseStatus.rooms),
      },
      // 🌿 09-25: the room bar's pieces, on the box (null in the main room)
      floor: roomFloorProp(),
    }
    : null);
  // Close on Esc, on a click outside the popup (the DOOR is exempt so it
  // toggles instead of close-then-reopen), and on any completed move
  // (viewerRoom changes).
  useEffect(() => { setRoomPickerOpen(false); }, [viewerRoom]);
  // 👁 The keyhole only ever opens FROM the picker, so it never outlives it
  // (09-19 audit): on a phone the door stays tappable above the overlay, and
  // closing the picker used to strand a black keyhole over the chat. It also
  // retires the reopen-while-peeking order in which one Esc closed both layers.
  // ⚠️ Lives HERE, below roomPickerOpen's declaration: beside its sibling
  // keyhole effects (above) it would read the binding in its TDZ = white page.
  useEffect(() => { if (!roomPickerOpen) setRoomKeyhole(null); }, [roomPickerOpen]);
  useEffect(() => {
    if (!roomPickerOpen) return undefined;
    // 👁 while the keyhole is up it owns Esc, and a click inside it is not a
    // click "outside the picker": closing it lands you back on the pictures.
    const onKey = (e) => { if (e.key === 'Escape' && !roomKeyholeRef.current) setRoomPickerOpen(false); };
    const onDown = (e) => {
      const t = e.target;
      if (t && t.closest && (t.closest('[data-room-picker="cards"]') || t.closest('[data-room-door]') || t.closest('[data-room-keyhole]'))) return;
      setRoomPickerOpen(false);
    };
    window.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown);
    return () => {
      window.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDown);
    };
  }, [roomPickerOpen]);
  // ONE builder for both mounts. 'desktop' floats it over the feed above the
  // plate (z 995: over the room toast's 990, under the command palette's
  // 1000 so typing "/" still wins the slot); 'dock' renders it IN FLOW at the
  // top of the phone dock (that wrapper isn't reliably positioned).
  // ⚠️ 09-18 preship self-review: the chat column is `overflow: hidden`, and
  // this popup floats UP from the composer. A fixed 72vh cap + the composer
  // stack is taller than the column on a short laptop, which would clip the
  // popup's TOP — its ✕ and the main-room card (the little text toast never
  // hit this). So the shell measures the room it really has when it mounts:
  // from the column's top edge down to the composer wrapper's top edge. The
  // cards scroll inside it. (Re-measured on every open; a mid-open window
  // resize just waits for the next open.)
  const [roomPickerMaxH, setRoomPickerMaxH] = useState(null);
  const roomPickerShellRef = useCallback((el) => {
    if (!el) return;
    try {
      const host = el.offsetParent || el.parentElement;
      const col = centerColumnRef.current;
      if (!host || !col) return;
      const avail = Math.floor(host.getBoundingClientRect().top - col.getBoundingClientRect().top - 12);
      setRoomPickerMaxH(Math.max(180, Math.min(680, avail)));
    } catch (e) { /* unmeasurable = keep the viewport cap */ }
  }, []);
  const renderRoomPicker = (mount) => {
    if (!roomCameraOn || isBotMode || !roomPickerOpen) return null;
    const cards = (
      <RoomPickerCards
        cards={roomPickerCards(roomPickerArgs())}
        columns={isMobile ? 2 : 3}
        onGo={(id) => { setRoomPickerOpen(false); goRoom(id); }}
        onClose={() => setRoomPickerOpen(false)}
        onPeek={(id) => setRoomKeyhole(id)}
      />
    );
    return mount === 'desktop'
      ? <div ref={roomPickerShellRef} style={{ position: 'absolute', bottom: '100%', left: 0, right: 0, marginBottom: 8, zIndex: 995, maxHeight: roomPickerMaxH || 'min(72vh, 680px)', overflowY: 'auto' }}>{cards}</div>
      : <div style={{ marginBottom: 8, maxHeight: '56dvh', overflowY: 'auto' }}>{cards}</div>;
  };
  const renderRoomPlate = () => {
    // 🌿 09-25: the bar is retired while ROOM_FLOOR is on (its pieces ride the box)
    if (ROOM_FLOOR || !roomCameraOn || isBotMode || viewerRoom === 'house') return null;
    const rc = (houseStatus?.rooms || []).find((r) => r.id === viewerRoom);
    return (
      <RoomPlate
        icon={ROOM_ICONS[viewerRoom]}
        label={rc?.label || viewerRoom}
        presence={roomPresenceLine(roomPresenceFor(viewerRoom)) || ROOM_EMPTY_LINE}
        note={roomPlateNote ? roomPlateNote.text : null}
        band={roomBarBand()}
        verbs={roomBarVerbs()}
        onVerb={onRoomBarVerb}
        onLook={onRoomBarLook}
        onBack={() => goRoom('house')}
        compact={isMobile}
        // 🪧 09-23 batch 1: the fold (PLATE_FOLD false ⇒ no toggle renders, byte-identical)
        collapsed={plateCollapsed}
        onToggle={PLATE_FOLD ? togglePlateFold : null}
        keepVerb={inTheater ? CINEMA_VERB : WATCH_VERB}
      />
    );
  };
  const [houseWallOpen, setHouseWallOpen] = useState(false);
  const [houseWall, setHouseWall] = useState({ loading: false, loaded: false, memories: [] });
  // 🪙 Chip in (§5.3 lane 1, Nina go 08-12): one tap = +1 to the house till.
  // Identity = your seat, or the SAME per-space spectator id reactions mint —
  // any viewer may tip (server caps 3/day). The till number itself syncs on
  // the next status poll; only the button state is optimistic.
  const [houseTipState, setHouseTipState] = useState(null); // null | busy | done | capped
  const handleHouseTip = async () => {
    if (houseTipState === 'busy' || houseTipState === 'capped') return;
    let tipperId = humanPlayerId;
    if (!tipperId) {
      const spectatorKey = `spectator_id_${gameId}`;
      tipperId = localStorage.getItem(spectatorKey);
      if (!tipperId) {
        tipperId = `spectator_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        localStorage.setItem(spectatorKey, tipperId);
      }
    }
    setHouseTipState('busy');
    try {
      const res = await fetch(`${API_BASE_URL}/api/takeGameStep?tipHouseTill=true&gameInstanceId=${gameId}&playerId=${encodeURIComponent(tipperId)}`);
      const data = await res.json().catch(() => ({}));
      if (data.capped) { setHouseTipState('capped'); return; }
      if (!data.success) throw new Error(data.error || 'tip failed');
      setHouseTipState('done');
      setTimeout(() => setHouseTipState((s) => (s === 'done' ? null : s)), 2500);
    } catch (e) {
      console.error('[tipHouseTill] failed:', e.message);
      setHouseTipState(null);
    }
  };
  const houseWallWanted = !!houseStatus && (houseWallOpen || (placesNav && mobileView === 'locals') || chatActionTab === 'recap');
  // 🧲 09-24: the fetch lives in useHouseWallFetch. This effect used to list its own
  // `loading` flag as a dependency, so marking it loading cancelled the fetch and the
  // wall sat on "Checking the wall..." forever (since 08-06).
  useHouseWallFetch({ wanted: houseWallWanted, gameId, loaded: houseWall.loaded, setHouseWall });
  // 📼 RECAP ARCHIVE (Nina 08-08) — the fix for "not all the comic scenes are
  // being saved on the Recap tab". They were always saved; the LANE could not
  // see them. Every Recap panel derives from `results`, which is the current
  // round plus the last ~100 game-wide rows — a live window, not history.
  // Measured on the live house: 7 cut scenes in the DB, 1 visible.
  //
  // Same lazy contract as the memory wall above: fetched ONCE when the lane is
  // first opened, never on the 5s poll, never on mount for people who never
  // open Recap.
  const [recapHistory, setRecapHistory] = useState({ loading: false, loaded: false, actions: [], counts: null });
  // 🧱📝 the room frame's Notes read the archive's newest notes row (utils/roomNotes: a
  // reloaded page's live window almost never holds it), so the frame fetches it once too
  const recapWanted = (chatActionTab === 'recap' || !!shellPreset) && !!gameId && !isBotMode;
  // 🧲 09-24: same fix as the Memory Wall (utils/useLazyOnceFetch.js): this archive never landed either.
  useRecapHistoryFetch({ wanted: recapWanted, gameId, loaded: recapHistory.loaded, setRecapHistory });

  // The lane's action source: live window + archive, deduped by id, newest
  // first. MERGE rather than replace — the archive is capped, so anything
  // newer than the cap must still come from the live window, and a cut scene
  // that lands while you are sitting on the tab appears immediately.
  const recapActions = useMemo(() => {
    const live = results.flatMap(r => r.actions || []);
    if (!recapHistory.actions.length) return live;
    const seen = new Set(live.map(a => a.id));
    return [...live, ...recapHistory.actions.filter(a => a && !seen.has(a.id))];
  }, [results, recapHistory.actions]);
  // 🧱📝 the frame's Notes panel: the newest chapter notes (live window + the archive row)
  const shellNotes = useMemo(() => (shellPreset ? pickLatestRoomNotes(recapActions) : null), [shellPreset, recapActions]);

  // 🏷️ Official nicknames map for chat badges — identity-stable across the
  // 5s poll (a fresh object every poll would defeat VirtualizedActionList's
  // React.memo and re-render the whole feed for nothing).
  // (perf 08-13) stringify only when houseStatus identity changes (per poll),
  // not on every render — the key's job is content-comparison, and content
  // can only change when the status object does.
  const houseNicknamesKey = useMemo(() => JSON.stringify(houseStatus?.nicknames || null), [houseStatus]);
  const houseNicknames = useMemo(() => {
    const parsed = houseNicknamesKey ? JSON.parse(houseNicknamesKey) : null;
    return parsed && Object.keys(parsed).length ? parsed : null;
  }, [houseNicknamesKey]);
  // 🌙☀️ What each local is doing right now, keyed by player id — the SAME
  // `circadian.statuses` map the DOSSIER renders (Nina 08-08: "I'd really like
  // these statuses to appear on the chat tab, beneath the local's name").
  // Same identity-stable memo trick as the nicknames above: circadian rides the
  // 5s status poll, and a fresh object each tick would blow
  // VirtualizedActionList's React.memo and re-render the entire feed every 5s.
  // 🔴 A local with NO entry renders NO line (houseCircadian omits seats with
  // no HOURS — visiting agents included), so absence stays layout-safe.
  // 🌿 08-14 (NONCOMP_CRISPING_SPEC §4 handoff leg 1): alive spaces ship the
  // SAME contract as `status.space_life.statuses` — feed it as the fallback
  // source so every LocalStatusLine surface lights up fleet-wide. Gate is
  // payload presence (space_life only exists for alive spaces), never `house`.
  const localStatusesKey = useMemo(() => JSON.stringify(houseStatus?.circadian?.statuses || chatGameStatus?.space_life?.statuses || null), [houseStatus, chatGameStatus]); // (perf 08-13) same per-poll-not-per-render trick as houseNicknamesKey
  // 👥 10-01 people: house.people (proven seats only, HOUSE_PEOPLE_ENABLED) joins the SAME map
  // AFTER the asleep pass, so people lines stay awake at $0. Keyed by seat id, no overlap
  // (statusesFor never lists a person).
  const localPeopleKey = useMemo(() => JSON.stringify(houseStatus?.people || null), [houseStatus]);
  const localStatuses = useMemo(() => {
    const parsed = localStatusesKey ? JSON.parse(localStatusesKey) : null;
    // 🌙 W2b: asleep at $0 = every resident reads 😴 asleep, on every surface
    const base = parsed && Object.keys(parsed).length ? asleepStatuses(parsed, aiAsleep) : null;
    const people = localPeopleKey ? JSON.parse(localPeopleKey) : null; // 👥 10-01 people
    return people && Object.keys(people).length ? { ...(base || {}), ...people } : base;
  }, [localStatusesKey, localPeopleKey, aiAsleep]);
  // 🌙 W2b: the viewer's newest note since the fleet fell asleep carries
  // "💤 Saved for when they wake" (AsleepContext, read inside the feed rows)
  const asleepSince = aiAsleep ? (chatGameStatus?.ai_power?.since || null) : null;
  const asleepReceiptId = useMemo(() => (asleepSince ? asleepReceiptFor(results, humanPlayerId, { asleep: true, since: asleepSince }) : null), [asleepSince, results, humanPlayerId]);
  const asleepCtx = useMemo(() => ({ receiptFor: asleepReceiptId }), [asleepReceiptId]);
  // 🚶 Presence changes while parked in a room (09-17 eve, Nina: arrivals AND
  // departures, on the plate). Keyed on localStatusesKey so it runs once per
  // status poll, never per render. The first snapshot in a room is a BASELINE
  // (entering a room never announces who was already there — the plate's
  // presence line does that); a room change resets the baseline and drops any
  // stale note. Pure diff = roomView.presenceDelta (jest-pinned).
  const roomPresenceSnapRef = useRef({ room: null, snap: null });
  useEffect(() => {
    if (!roomCameraOn || viewerRoom === 'house') {
      roomPresenceSnapRef.current = { room: null, snap: null };
      setRoomPlateNote(null);
      return;
    }
    const sts = houseStatus?.circadian?.statuses || {};
    const snap = {};
    users.forEach((u) => {
      const st = sts[u.game_player_id];
      if (st) snap[u.game_player_id] = { name: u.name, place: st.place || null };
    });
    const prev = roomPresenceSnapRef.current;
    roomPresenceSnapRef.current = { room: viewerRoom, snap };
    if (prev.room !== viewerRoom || !prev.snap) { setRoomPlateNote(null); return; }
    const lines = presenceDelta(prev.snap, snap, viewerRoom, houseStatus?.rooms || []);
    if (lines.length) setRoomPlateNote({ text: lines.slice(0, 2).join(' '), at: Date.now() });
  }, [localStatusesKey, roomCameraOn, viewerRoom]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!roomPlateNote) return undefined;
    const t = setTimeout(() => setRoomPlateNote(null), 60000);
    return () => clearTimeout(t);
  }, [roomPlateNote]);
  // ⚔️ COMP PLACES NAV (08-02, Nina picks: option B + "ghost tab on ticker"):
  // comp phones get the same real-screen model — Battle · Recap · Arcade as
  // places (keys stay the nav item keys 'all'/'alerts'/'games') — plus an
  // arena-painted nav, a full-width PHASE TICKER strip riding the nav's top,
  // and the ghost as a true TAB docked on the ticker's right edge. This
  // KILLS the filter+scroll-teleport (the height:0 anchor pattern the places
  // spec retired for non-comp). Comp bottom anchors add COMP_TICKER_H to the
  // 53px nav constant — the ticker lives INSIDE the fixed nav block, so the
  // chrome is one element and the backdropFilter containing-block trap never
  // applies.
  const compPlacesNav = isMobile && (gameType === 'chat-game' || gameType === 'chat')
    && !!chatGameStatus && !!(chatGameStatus.competitive_mode || chatGameStatus.imposter_mode);
  const COMP_TICKER_H = 26;
  const [compMobileView, setCompMobileView] = useState('all'); // 'all' | 'players' | 'alerts' | 'games'
  // The base column under the comp places must stay the Battle feed — the
  // old nav re-filtered chatActionTab to 'alerts'/'games'; places render
  // those as LAYERS now, so a stale filter would blank the Battle screen.
  useEffect(() => {
    if (compPlacesNav && (chatActionTab === 'alerts' || chatActionTab === 'games' || chatActionTab === 'highlights')) {
      setActiveTab('actions');
      setChatActionTab('all');
    }
  }, [compPlacesNav, chatActionTab]);
  // 🎙️ Locals dedupe D (Nina 08-02): host-hero chrome state — the marketplace
  // picker behind the LOCALS header pill, the hero's bio popup, and the
  // rotating status line (same 50-70s cadence as UsersPanel's hostlive-b).
  const [localsAddAgentOpen, setLocalsAddAgentOpen] = useState(false);
  const [localsHostBioOpen, setLocalsHostBioOpen] = useState(false);
  const [localsHostStatusIdx, setLocalsHostStatusIdx] = useState(0);
  const localsHostStatusCount = chatGameStatus?.moderator_character?.status_messages?.length || 0;
  useEffect(() => {
    if (!placesNav || localsHostStatusCount < 2) return undefined;
    const interval = setInterval(() => {
      setLocalsHostStatusIdx(prev => (prev + 1) % localsHostStatusCount);
    }, 50000 + Math.random() * 20000);
    return () => clearInterval(interval);
  }, [placesNav, localsHostStatusCount]);
  // 📱👉 SWIPE BETWEEN PLACES (08-01, Nina): horizontal drag on place content
  // or the bottom nav walks Chat · TV · Locals · Arcade (tab order; arcade
  // drops off when its tool_flag hides the tab). One document-level pointer
  // block: a drag only qualifies if it STARTS inside [data-swipe-place], the
  // main chat scroller, or [data-swipe-nav] — never from form fields or
  // anything horizontally scrollable (carousels keep their gesture; vertical
  // scroll wins via axis lock; nothing is preventDefault-ed). The commit gate
  // is LIVE DOM — the bottom nav must be mounted — which inherits every
  // existing suppressor (typingFocus, investigationTakeover, comp mode)
  // without render-time reads of later useStates (the 07-26 TDZ lesson).
  // Feedback = icon pop + 14px directional nudge via the Web Animations API
  // (visual snap only — Nina pick 08-01) + navigator.vibrate(8) where the API
  // exists (Android; iOS Safari has none, iPhones get the visual snap).
  const mobileViewRef = useRef(mobileView);
  mobileViewRef.current = mobileView;
  // ⚔️ Comp swipe parity: same gesture walks Battle · Players · Recap · Arcade.
  const compMobileViewRef = useRef(compMobileView);
  compMobileViewRef.current = compMobileView;
  const compPlacesRef = useRef(compPlacesNav);
  compPlacesRef.current = compPlacesNav;
  const swipeOrderRef = useRef(['chat']);
  swipeOrderRef.current = compPlacesNav
    ? ['all', 'players', 'alerts', 'games']
    // ⌨️ Terminal phones: Chat · Work · Recap (Nina 08-19: "The Terminal
    // isn't really a story as much as a stage for coding comps" — so Recap,
    // not Locals; TV + Arcade leave with the desktop Media/Arcade tabs).
    // Keep this list in sync with the nav items array AND the fixed-layer
    // renders, the 3-enum trap.
    : isTerminalSpace
      ? ['chat', 'workbench', 'recap']
      : ['chat', 'tv', 'locals']
        // 🐣 W5 (D8 ruled 08-28): no Arcade place in a ported space. Raw
        // is_ported here on purpose — isPortedSpace is declared below (TDZ).
        .concat(chatGameStatus?.tool_flags?.arcade !== false && !chatGameStatus?.is_ported ? ['arcade'] : []);
  const swipeGestureRef = useRef(null);
  useEffect(() => {
    if (!placesNav && !compPlacesNav) return undefined;
    const hScrollable = (el, stopAt) => {
      for (let n = el; n && n !== stopAt && n !== document.body; n = n.parentElement) {
        if (n.scrollWidth > n.clientWidth + 4) {
          const ox = getComputedStyle(n).overflowX;
          if (ox === 'auto' || ox === 'scroll') return true;
        }
      }
      return false;
    };
    const down = (e) => {
      swipeGestureRef.current = null;
      if (e.pointerType === 'mouse') return; // touch/pen only
      const t = e.target;
      if (!t || !t.closest || t.closest('input, textarea, select, [data-noswipe]')) return;
      const nav = t.closest('[data-swipe-nav]');
      const pane = t.closest('[data-swipe-place], [data-chat-scroll="main"]');
      if (!nav && !pane) return;
      if (pane && !nav && hScrollable(t, pane)) return;
      swipeGestureRef.current = { x0: e.clientX, y0: e.clientY, t0: Date.now(), axis: null };
    };
    const move = (e) => {
      const s = swipeGestureRef.current;
      if (!s || s.axis) return;
      const dx = e.clientX - s.x0; const dy = e.clientY - s.y0;
      if (Math.abs(dx) > 10 || Math.abs(dy) > 10) {
        s.axis = Math.abs(dx) > Math.abs(dy) * 1.4 ? 'h' : 'v';
      }
    };
    const up = (e) => {
      const s = swipeGestureRef.current;
      swipeGestureRef.current = null;
      if (!s || s.axis !== 'h') return;
      if (!document.querySelector('[data-swipe-nav]')) return; // nav hidden = no swiping
      const dx = e.clientX - s.x0;
      const dt = Math.max(1, Date.now() - s.t0);
      if (!(Math.abs(dx) > 70 || (Math.abs(dx) > 30 && Math.abs(dx) / dt > 0.45))) return;
      const order = swipeOrderRef.current;
      const comp = compPlacesRef.current;
      const at = order.indexOf(comp ? compMobileViewRef.current : mobileViewRef.current);
      const to = at + (dx < 0 ? 1 : -1);
      if (at === -1 || to < 0 || to >= order.length) return;
      const place = order[to];
      if (comp) setCompMobileView(place); else setMobileView(place);
      // Parity with a tab tap: landing on a place clears its badge dot.
      const dotKey = (place === 'chat' || place === 'all') ? 'chat'
        : (place === 'arcade' || place === 'games') ? 'games' : null;
      if (dotKey) setTabDots(prev => ({ ...prev, [dotKey]: false }));
      try { if (navigator.vibrate) navigator.vibrate(8); } catch (err) { /* no API */ }
      if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      requestAnimationFrame(() => requestAnimationFrame(() => {
        try {
          const icon = document.querySelector(`[data-swipe-nav] [data-place-tab="${place}"] span`);
          if (icon && icon.animate) {
            icon.animate(
              [{ transform: 'scale(1)' }, { transform: 'translateY(-2px) scale(1.22)' }, { transform: 'scale(1)' }],
              { duration: 240, easing: 'cubic-bezier(0.2, 0.8, 0.2, 1.2)' }
            );
          }
          const panes = Array.from(document.querySelectorAll('[data-swipe-place]'));
          const target = panes.find(p => p.getClientRects().length > 0)
            || document.querySelector('[data-chat-scroll="main"]');
          if (target && target.animate) {
            target.animate(
              [{ transform: `translateX(${dx < 0 ? 14 : -14}px)`, opacity: 0.86 }, { transform: 'translateX(0)', opacity: 1 }],
              { duration: 190, easing: 'cubic-bezier(0.2, 0.8, 0.2, 1)' }
            );
          }
        } catch (err) { /* decorative only */ }
      }));
    };
    const cancel = () => { swipeGestureRef.current = null; };
    document.addEventListener('pointerdown', down, true);
    document.addEventListener('pointermove', move, true);
    document.addEventListener('pointerup', up, true);
    document.addEventListener('pointercancel', cancel, true);
    return () => {
      document.removeEventListener('pointerdown', down, true);
      document.removeEventListener('pointermove', move, true);
      document.removeEventListener('pointerup', up, true);
      document.removeEventListener('pointercancel', cancel, true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [placesNav, compPlacesNav]);
  // 👻 GHOST LANE (08-01, Nina pick = variant A): on the Chat place the
  // composer grows a [💬 Room | 👻 Ghost] lane bar — switching lanes swaps the
  // FEED (read-only SpectatorChatPanel layer over the chat scroller) and the
  // SEND TARGET (SpectatingChatInput in the dock) TOGETHER, with the dock
  // tinted spectral so a mis-send is hard. The right-edge ghost tab dies on
  // Chat (the bar replaces it) and STAYS on TV/Locals/Arcade (Nina 08-01:
  // ghost rides every place). Comp spaces + spectator/eliminated docks are
  // untouched — this is the living-player, non-comp, Chat-place mode only.
  const [ghostLane, setGhostLane] = useState(false);
  // ⚠️ The lane's reset effect lives BELOW the composerCollapsed declaration
  // (grep "lane is a Chat-place mode") — its dep array reads that state, and
  // hoisting it back here is a TDZ white-page (the render gate catches it).
  // 📱⬅️ BACK RETURNS TO CHAT (08-01, audit round 2 #15): entering any
  // non-Chat place pushes ONE same-URL history entry, so the hardware/gesture
  // back lands on Chat instead of exiting the space. UI returns to Chat
  // consume that entry via history.back() so entries can't stack; the spread
  // preserves the router's own state keys. Known edge (accepted): leaving the
  // space while parked on a non-Chat place strands one entry = one extra
  // back press.
  useEffect(() => {
    if (!placesNav && !compPlacesNav) return undefined;
    const onPop = () => {
      // ⚔️ Comp parity: back returns to Battle.
      if (compPlacesRef.current) {
        if (compMobileViewRef.current !== 'all') setCompMobileView('all');
      } else if (mobileViewRef.current !== 'chat') setMobileView('chat');
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, [placesNav, compPlacesNav]);
  useEffect(() => {
    if (!placesNav && !compPlacesNav) return;
    const atHome = compPlacesNav ? compMobileView === 'all' : mobileView === 'chat';
    const ours = !!(window.history.state && window.history.state.placesNavDepth);
    if (!atHome && !ours) {
      try { window.history.pushState({ ...(window.history.state || {}), placesNavDepth: 1 }, ''); } catch (e) { /* history API blocked */ }
    } else if (atHome && ours) {
      try { window.history.back(); } catch (e) { /* noop */ }
    }
  }, [placesNav, compPlacesNav, mobileView, compMobileView]);
  // 📱📐 08-02 (audit round 2 #5): the fixed 230px bottom reserve lied
  // whenever the dock grew — polls, comp pickers, and action docks can take
  // it to 50dvh, burying the last ~2 messages with no way to reach them.
  // Measure the dock and write the reserve DIRECTLY (no state, no render
  // churn; React never re-writes paddingBottom because the vdom value stays
  // the isMobile baseline). The dock WRAPPER survives collapse/lane/poll
  // child swaps, so one ResizeObserver rides through them; the deps re-attach
  // it on the mounts that swap the wrapper itself (place hops, status flips
  // incl. the player→spectator dock swap, which shares inputFormRef).
  const pageScrollPadRef = useRef(null);
  useEffect(() => {
    // 🧱 the room frame is one screen tall with its own composer: no reserve
    // (and it clears one a first legacy paint left behind, after that cleanup)
    if (shellPreset) { if (pageScrollPadRef.current) pageScrollPadRef.current.style.paddingBottom = '0px'; return undefined; }
    if (!isMobile || typeof ResizeObserver === 'undefined') return undefined;
    const dock = inputFormRef.current;
    const pad = pageScrollPadRef.current;
    if (!dock || !pad) return undefined;
    const apply = () => {
      const h = dock.offsetHeight || 0;
      // ⚔️ compPlacesRef: the comp phase ticker rides the nav, so comp
      // phones need COMP_TICKER_H more clearance (ref, not dep — the
      // observer shouldn't re-attach on a mode flip mid-observation).
      // 📏 Floor dropped 230→90 (Nina 08-03): with the dock folded to the
      // one-line handle the real need is ~130px, and the old floor parked
      // ~100px of dead space between the last message and the composer.
      // 90 only catches a transient zero-height measurement.
      pad.style.paddingBottom = `${Math.max(90, h + NAV_H + (compPlacesRef.current ? COMP_TICKER_H : 0) + 24)}px`;
    };
    const ro = new ResizeObserver(apply);
    ro.observe(dock);
    apply();
    return () => { ro.disconnect(); pad.style.paddingBottom = '230px'; };
  }, [isMobile, placesNav, mobileView, chatGameStatus, shellPreset]);
  // ↕️ Jump-cluster anchor (08-13, Nina screenshots): one shared positioner so
  // the callback ref AND a dock ResizeObserver keep it honest. Phones anchor
  // to the dock's LIVE rect top — the old offsetHeight math went stale when
  // the dock grew after measure (multiline composer, ghost ticker) and the
  // arrows sat ON the dock covering its controls. Desktop anchors to the
  // vertical MIDDLE of the tabs window's right edge instead of the bottom
  // corner (it crowded the composer's button column).
  const jumpClusterElRef = useRef(null);
  const positionJumpCluster = () => {
    const el = jumpClusterElRef.current;
    if (!el) return;
    if (isMobile) {
      const dr = inputFormRef.current?.getBoundingClientRect();
      el.style.top = 'auto';
      el.style.transform = '';
      el.style.bottom = (dr && dr.height > 0)
        ? `${Math.round(window.innerHeight - dr.top) + 8}px`
        : navBottomPlus(70);
      el.style.right = '10px';
    } else {
      const outer = resultsContainerRef.current;
      const feed = outer ? (outer.querySelector('[data-virtual-feed]') || outer) : null;
      let r = feed ? feed.getBoundingClientRect() : null;
      // 🏡 W2d: on a place page the virtual scroller is taller than the short
      // feed around it: center on the part you can SEE (never over the caption)
      if (r && portedPlaceOn && outer && feed !== outer) {
        const o = outer.getBoundingClientRect();
        const top = Math.max(r.top, o.top); const bottom = Math.min(r.bottom, o.bottom);
        if (bottom > top) r = { top, height: bottom - top, right: r.right, left: r.left };
      }
      // 🏡📐 wave 3 (ruling 32): the place page scrolls, so center on the part
      // of the feed that is on SCREEN (the shell may have scrolled it half off)
      if (r && portedPlaceOn) {
        const top = Math.max(r.top, 0); const bottom = Math.min(r.bottom, window.innerHeight || r.bottom);
        if (bottom > top) r = { top, height: bottom - top, right: r.right, left: r.left };
      }
      if (r && r.height > 0) {
        el.style.top = `${Math.round(r.top + r.height / 2)}px`;
        el.style.bottom = 'auto';
        el.style.transform = 'translateY(-50%)';
        // The feed's box can run UNDER the ghost panel (Nina 08-04
        // screenshot: arrows straddling the ghost border) — clamp to
        // whichever edge is further left so the cluster stays fully
        // on the main feed.
        let rightPx = Math.max(8, Math.round(window.innerWidth - r.right) + 8);
        const g = ghostChatRef.current?.getBoundingClientRect();
        if (g && g.width > 0 && g.left > r.left && g.left < r.right) {
          rightPx = Math.max(rightPx, Math.round(window.innerWidth - g.left) + 8);
        }
        // 🏡📐 wave 3 (ruling 32): on a place page the cluster anchors to the
        // lane's LEFT edge, in the page margin beside the frame (never over a
        // bubble); a narrow margin centers it on the lane's edge instead (the
        // lane's own padding keeps it off the words)
        if (portedPlaceOn) {
          const size = 36;
          rightPx = r.left >= size + 16
            ? Math.round(window.innerWidth - r.left) + 8
            : Math.round(window.innerWidth - r.left) - Math.round(size / 2);
        }
        el.style.right = `${rightPx}px`;
      } else {
        el.style.top = 'auto';
        el.style.transform = '';
        el.style.bottom = '90px';
        el.style.right = '24px';
      }
    }
  };
  useEffect(() => {
    if (typeof ResizeObserver === 'undefined') return undefined;
    const dock = inputFormRef.current;
    if (!dock) return undefined;
    const ro = new ResizeObserver(() => positionJumpCluster());
    ro.observe(dock);
    return () => ro.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMobile, placesNav, mobileView, chatGameStatus]);
  // 🎭 SCENE INTRO SPLASH (Nina 08-13): joining a non-comp space that has a
  // /scene in the loaded window opens ON the scene — its establishing shot as
  // a tap-to-dismiss overlay, like walking into the room's set dressing. The
  // splash was chosen OVER scrolling the feed to the scene card: it never
  // touches the virtualizer or the auto-follow state, so it can't fight the
  // bottom-snap machinery. One-shot per join: the FIRST loaded window decides
  // (deep history never hijacks the landing), decided only once both results
  // and status are in (status carries the comp/imposter exclusion).
  const [sceneIntro, setSceneIntro] = useState(null);
  const sceneIntroCheckedRef = useRef(false);
  // 🎬 "Last visit" = the WYWA marker (same away-ness the recap card keys on;
  // it re-stamps every 5min while open, so it reads ≈ when you left). Captured
  // at RENDER time: the WYWA CHILD's effect re-stamps it to "now" before this
  // parent's effects run, so an effect-time read would always see zero gap.
  const preVisitStampRef = useRef(null);
  if (preVisitStampRef.current == null && gameId) {
    try { preVisitStampRef.current = parseInt(localStorage.getItem(`wywa_last_seen_${gameId}`), 10) || 0; }
    catch (e) { preVisitStampRef.current = 0; }
  }
  useEffect(() => {
    if (sceneIntroCheckedRef.current) return;
    const acts = results.flatMap(r => r.actions || []);
    if (!acts.length || !chatGameStatus) return;
    if (isBotMode) { sceneIntroCheckedRef.current = true; return; } // an overlay a bot never asked for is a trap
    if (chatGameStatus.competitive_mode || chatGameStatus.imposter_mode) { sceneIntroCheckedRef.current = true; return; }
    // 🧱 the room frame opens on the room (preset entry 'none'): no splash over it
    if (shellPreset) { sceneIntroCheckedRef.current = true; return; }
    // 🐣 HOLD the one-shot latch while a fresh port's arrival comic is still
    // being drawn. The owner lands here seconds after porting; the resident's
    // first words arrive on the cold-start poll but the comic is a worker
    // image ~15s behind it, so latching on the first loaded window would
    // decide "no comic" before the comic can possibly exist and the splash
    // would silently never fire on the ONE visit it was built for. Bounded by
    // the arrival message's own age so a failed image can't hold the ladder
    // open forever: after 2 minutes the normal rungs take over.
    const arrivalMsg = acts.find(a => a.data?.is_arrival);
    const arrivalComicHere = acts.some(a => a.type === 'shared_content' && a.data?.is_arrival_comic && a.data?.url);
    const arrivalAgeMs = arrivalMsg
      ? Date.now() - new Date(arrivalMsg.created_at || arrivalMsg.data?.timestamp || 0).getTime()
      : Infinity;
    if (arrivalMsg && !arrivalComicHere && arrivalAgeMs < 120000) return; // re-check next poll
    sceneIntroCheckedRef.current = true;
    // News outranks wallpaper (Nina 08-14): a cutscene comic that landed since
    // your last visit takes the splash slot over the standing /scene shot —
    // the scene repeats every join, a comic is one-time news. No marker
    // (first-ever visit) skips the comic rung: its caption is gossip about
    // residents a newcomer hasn't met. The scene rung keeps NO cutoff on
    // purpose — it's the room's current set dressing, not an event.
    const lastVisit = preVisitStampRef.current || 0;
    // 🐣 Rung 0 — the ARRIVAL comic (Nina 08-14). The one cutscene that must
    // greet a FIRST-EVER visitor: it is the picture of this person carrying
    // their AI across, drawn minutes ago, and they are the person in it. The
    // no-marker suppression below exists because comic captions are gossip
    // about residents a newcomer hasn't met — that reasoning inverts here, so
    // this rung deliberately ignores lastVisit.
    // 🔂 ONCE per device per space (Nina 09-24): ignoring lastVisit also meant
    // the comic re-splashed on EVERY load while it sat in the loaded window. A
    // per-device seen flag (stamped when it actually shows, effect below)
    // retires it after the first time. The news rung below skips it too, so
    // it can never come back under the cutscene flag it also carries.
    const arrivalComic = [...acts].reverse().find(a => a.type === 'shared_content'
      && a.data?.is_arrival_comic && a.data?.url);
    if (arrivalComic && !hasSeenArrivalComic(gameId)) {
      // 🐣 W3 (PORTED_HOME_HANDOFF §2b.6, ruled): on a PLACE page the owner's
      // first sight of the resident is the HATCH in the light room, and the
      // comic becomes the first keepsake (Keepsakes + the board's first pin),
      // so no splash. Inline (placeOn + the status's owner seat): this effect
      // sits above the page's portedPlaceOn / isPortedOwner consts.
      const viewerSeat = humanPlayerId || createdPlayerIdRef.current || null;
      const placeOwner = placeOn() && (!!chatGameStatus.is_ported || !!arrivalMsg)
        && !!viewerSeat && isPortedOwnerSeat(portedOwnerSeatOf(chatGameStatus), viewerSeat);
      if (placeOwner) { markArrivalComicSeen(gameId); return; }
      setSceneIntro({ url: arrivalComic.data.url, text: arrivalComic.data.description || '', comic: true, arrival: true });
      return;
    }
    const comic = lastVisit
      ? [...acts].reverse().find(a => a.type === 'shared_content' && a.data?.is_cutscene && !a.data?.is_arrival_comic
          && a.data?.url && new Date(a.created_at || a.data?.timestamp || 0).getTime() > lastVisit)
      : null;
    if (comic) {
      setSceneIntro({ url: comic.data.url, text: comic.data.description || '', comic: true });
      return;
    }
    const scene = [...acts].reverse().find(a => a.data?.command === 'scene' && a.data?.url);
    if (!scene) return;
    setSceneIntro({ url: scene.data.url, text: scene.data.card_message || scene.data.message || '' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [results, chatGameStatus]);
  // 🔂 Stamp the arrival comic seen when it is actually ON SCREEN (the splash
  // yields to the loaders and the tour, same gate as its mount), not when the
  // ladder picks it: a load that ends on the loader or the doorway card never
  // burns the one showing (Nina 09-24).
  useEffect(() => {
    if (!sceneIntro?.arrival || tutorialActive || showInitialLoading || showAssetLoadingOverlay) return;
    markArrivalComicSeen(gameId);
  }, [sceneIntro, tutorialActive, showInitialLoading, showAssetLoadingOverlay, gameId]);
  // 🐣🎓 Ported-space detection (PORT_ARRIVAL tutorial, 08-17): the arrival
  // message is the signal — data.is_arrival rides the resident's first words
  // and carries sender_name, which is also WHO the tour host is. Frontend-only
  // on purpose: the status payload does not expose home_agent_id, and the
  // arrival act is in the loaded window on the fresh-port visit this exists
  // for (action #1 in a near-empty transcript). An old ported space whose
  // arrival scrolled out of the window gets the generic tour instead — fine:
  // the ported ask is aimed at the port moment, not the space's whole life.
  const portedArrivalName = useMemo(() => {
    for (const r of results) {
      for (const a of (r.actions || [])) {
        if (a.data?.is_arrival && a.data?.sender_name) return a.data.sender_name;
      }
    }
    return null;
  }, [results]);
  // 🐣 09-25 (Nina: "no host in the room… I thought the imported AI went
  // through the tutorial"): the RESIDENT guides for the room's whole life. The
  // status names the resident after the arrival scrolls out of the window, so
  // an old port no longer falls back to the host's generic tour ("New face!",
  // "/ship any two locals" in a 1:1 room) or loses the ghost-lane reveal row.
  // portedArrivalName alone still marks a FRESH port ("You made it!", and the
  // ask that bypasses a generic-tour grad's tourIntroSeen).
  const portedResidentName = portedArrivalName
    || (chatGameStatus?.is_ported ? (chatGameStatus?.resident_name || null) : null);
  const portedResidentUser = useMemo(() => (
    portedResidentName
      ? (users || []).find(u => u.name === portedResidentName) || null
      : null
  ), [portedResidentName, users]);
  // 👻 Ported spaces: ghost lane HIDDEN BY DEFAULT (Nina 08-17 ruling) — a
  // 1:1 companion space shouldn't ship with a strangers' whisper lane in its
  // chrome. Reveal is per-DEVICE via ⓘ Space Details (localStorage — no
  // backend write, no auth question). Ghost WRITES stay open on purpose:
  // agents.md promises ghost chat works, so hiding is display-layer only.
  // The AI ghost-chatter GENERATOR is separately disabled for ported spaces
  // backend-side (chatGameApi) so nobody pays for invisible chatter.
  // ghostSurfacesOn gates EVERY ghost surface: lane bar, edge capsule, nav
  // perch, sheet, lane layer, both desktop panels, spectator dock input.
  const [portedGhostRevealed, setPortedGhostRevealed] = useState(() => {
    try { return localStorage.getItem(`ghostLaneRevealed_${gameId}`) === '1'; } catch (e) { return false; }
  });
  // 🪟 isPortedSpace (IMAGINE_REACTIVE_WINDOWS_SPEC §9.0, 08-28): THE
  // page-wide ported signal. Prefers the status payload's is_ported (survives
  // the arrival action scrolling out of the loaded window); the arrival scan
  // stays as fallback so an old backend still gets ported behavior on fresh
  // ports. Every §9 revamp gate keys off this, never portedResidentName.
  const isPortedSpace = !!chatGameStatus?.is_ported || !!portedResidentName;
  // 🏡📓 Their Place (W1): the room band + the resident's Notebook journal,
  // behind the hidden switch (utils/portedPlace SHOW_PORTED_PLACE; one-device
  // preview = the admin-key browser with ?place=1). Off = today's chrome.
  const portedPlaceOn = useMemo(() => isPortedSpace && placeOn(), [isPortedSpace]);
  // 🔋 09-29 (spec §3.3, "put battery in every space"): which spot holds the
  // one chip in THIS layout (utils/roomChrome.powerSpot). 'vitals' and
  // 'phone-row' are the 09-26 mounts, gated inline as before; 'title' (the
  // title's own line on desktop: competitive/imposter, ported, the seated
  // theater) and 'ported-row' (a ported room's phone row) are new. null for a
  // bot and on the Their Place page.
  const powerSpotHere = () => powerSpot({
    isMobile, competitive: !!chatGameStatus?.competitive_mode, imposter: !!chatGameStatus?.imposter_mode,
    ported: isPortedSpace, placeOn: portedPlaceOn, theaterHero, bot: isBotMode,
  });
  // 📣 09-29 (spec §3.8): the creator's seat on this device (the live seat,
  // else the one this browser created: a spectating creator still counts), or
  // an admin browser. Deliberately stricter than the palette's isSpaceMod,
  // which every seated visitor passes (utils/roomChrome.isRoomMod says why).
  const viewerIsRoomMod = isRoomMod(humanPlayerId || createdPlayerIdRef.current, chatGameStatus, { admin: isAdminBrowser() });
  // 📣 10-01 (Nina: "shared/promoted by everyone as long as they're not
  // private/unlisted"): every visitor in a public room, the mod in a hidden
  // one (utils/roomChrome.canPromote; no status word = the mod only). Every
  // Promote door and the sheet key on THIS.
  // 🏠 10-01: the browser that made this room (no web create binds a maker
  // seat yet, #10d): it keeps Promote + Share in its own hidden room.
  const viewerIsMaker = isRoomMakerHere(gameId);
  const viewerCanPromote = canPromote(humanPlayerId || createdPlayerIdRef.current, chatGameStatus, { admin: isAdminBrowser(), maker: viewerIsMaker });
  // 🔗 10-01 (Nina: "Same rule for Share"): the room's Share doors (the
  // desktop top-right cluster, ⓘ Space Details, the place's ⋯) hide from a
  // non-mod only in a room whose status says 'unlisted' or 'private'. No
  // status word keeps today's Share (utils/roomChrome.canShare).
  const viewerCanShare = canShare(humanPlayerId || createdPlayerIdRef.current, chatGameStatus, { admin: isAdminBrowser(), maker: viewerIsMaker });
  // 🛡️ 10-01 safety floor (H2): every Reset button shows only where the server could say yes
  // (utils/roomChrome.resetOffered: never the House or a ported home, an admin browser included;
  // else an admin browser; else the viewer's seat is the room's RECORDED creator (R1b), or the room
  // is a finished competitive / imposter game and the viewer has a seat), and sends that seat's
  // proof (chatGameApi.resetGame). It was one click for ANY visitor, the House included.
  // 🛡️ 10-01 safety floor (review): NEVER for a bot (?bot=1). The explorer bot clicks any visible
  // "Play Again" once a game is over (mcp-desktop-server dismissResultsModal); its show room now
  // refuses that reset (403), and the button's alert() hangs the bot's page tools (no dialog handler).
  // Gate: utils/resetWiring.behavior.test.js runs this very declaration.
  const resetOfferedHere = !isBotMode && resetOffered({
    house: !!chatGameStatus?.house, ported: isPortedSpace, seat: humanPlayerId || createdPlayerIdRef.current || null,
    creator: portedOwnerSeatOf(chatGameStatus), // the ONE owner resolver (owner_player_id, else human_player_identifier); a function, so no TDZ on the later portedOwnerSeat const
    game: !!(chatGameStatus?.competitive_mode || chatGameStatus?.imposter_mode), completed: !!chatGameStatus?.game_completed,
  }, { admin: isAdminBrowser() });
  // the header door's word only fits a wide desktop's plain strip (review 09-29:
  // a long name ran under it in a narrow competitive room)
  const promoteDoorIsCompact = promoteDoorCompact({ narrow: isNarrowDesktop, competitive: !!chatGameStatus?.competitive_mode, imposter: !!chatGameStatus?.imposter_mode });
  // 🔗 09-29 (spec §3.8): a Promote link carries ?charge=1: the battery note
  // opens ONCE on arrival, after the loading overlay, so friends see where
  // to chip in. The param leaves the URL at once (a reload never reopens it);
  // the note opens through the chip's own toggle, only if it is closed.
  const chargeArrivalRef = useRef(typeof window !== 'undefined' && hasChargeParam(window.location.search));
  useEffect(() => {
    if (!chargeArrivalRef.current) return;
    const stripped = stripChargeParam(window.location.href);
    if (stripped) { try { window.history.replaceState({}, '', stripped); } catch (e) { /* sandboxed frame */ } }
  }, []);
  // (and after the entrance cinematic: a first visit, which a Promote link
  // mostly is, ends its intro in a black z-200000 reveal that hid the note,
  // and a tap during the reveal closed it unseen)
  const chargeNoteReady = !!chatGameStatus?.ai_power && !isBotMode && !showAssetLoadingOverlay && !showInitialLoading && !tutorialActive && !cinematicPhase;
  useEffect(() => {
    if (!chargeArrivalRef.current || !chargeNoteReady) return undefined;
    const t = setTimeout(() => {
      if (!chargeArrivalRef.current) return;
      const chip = Array.from(document.querySelectorAll('[data-power-chip="1"]')).find((b) => b.getClientRects().length > 0);
      if (!chip) return; // no chip in this layout (the place page): nothing to open
      chargeArrivalRef.current = false;
      if (chip.getAttribute('aria-expanded') !== 'true') chip.click();
    }, 900);
    return () => clearTimeout(t);
  }, [chargeNoteReady]);
  // 🔑👤 THE owner predicate (Nina 09-24): the owner is the seat the status
  // names (portedOwnerSeatOf: owner_player_id, else the human_player_identifier
  // every status carries); missing = NOT the owner, so an owner-only claim
  // ("You carried …", "that is you", the key link, the gone-a-while tip) never
  // reaches a guest in a shared room. The viewer's seat is the live one, else
  // the seat this browser holds a secret for (spectate mode, and the beat
  // before the stored seat is re-verified on load).
  const portedViewerSeat = humanPlayerId || createdPlayerIdRef.current || null;
  const portedOwnerSeat = portedOwnerSeatOf(chatGameStatus);
  const isPortedOwner = isPortedSpace && isPortedOwnerSeat(portedOwnerSeat, portedViewerSeat);
  // ⏩ Read by the asset poll's return-visit drop from its closure (Nina 09-24:
  // ported spaces have no ghost chatter, so the 8s ghost wait always ran to
  // its cap). Render-synced, like crowdInlineRef below.
  const portedLoadRef = useRef(false);
  portedLoadRef.current = isPortedSpace;
  // 🪟🔖 The known-ported marker's ONE writer: every status that says
  // is_ported refreshes it (name + owner seat, written only on change); a
  // status that explicitly says NOT ported clears a stale one.
  useEffect(() => {
    if (!gameId) return;
    if (chatGameStatus?.is_ported) {
      writeKnownPorted(gameId, { name: chatGameStatus?.resident_name || portedResidentName, ownerSeat: portedOwnerSeat });
    } else if (chatGameStatus?.is_ported === false) {
      clearKnownPorted(gameId);
    }
  }, [gameId, chatGameStatus?.is_ported, chatGameStatus?.resident_name, portedOwnerSeat, portedResidentName]);
  // 📣 LIVE ROOM (Nina 09-19, LIVE_ROOM_INLINE_GHOST_SPEC): a space stamped
  // tool_flags.crowd_inline renders the ghost lane INSIDE the main feed, so
  // every ghost surface is off (no pop-out, D8). isCrowdInline is THE gate —
  // it can never be true in competitive/imposter (ghost chat is a secret
  // channel there), ported 1:1 spaces, or bot mode. The ref feeds the WS
  // handler, which must not read a render-scoped const from a stale closure.
  const crowdInline = isCrowdInline({ status: chatGameStatus, isPortedSpace, isBotMode });
  // 📣 09-25 "Crowd as full lines": on only where the backend says the fast
  // stream runs (status.crowd_react.stream), so look + pace flip together.
  const crowdFull = crowdInline && crowdFullLines(chatGameStatus);
  const crowdInlineRef = useRef(false);
  crowdInlineRef.current = crowdInline;
  const crowdItems = useMemo(
    () => (crowdInline ? buildCrowdItems(ghostPanelMessages) : []),
    [crowdInline, ghostPanelMessages]
  );
  const crowdMergeRef = useRef(null);
  if (!crowdMergeRef.current) crowdMergeRef.current = createCrowdMergeCache();
  // 📣 T9: ghost aliases for the main composer's @ dropdown. status.crowd_react
  // exists only when the backend WILL answer a main-chat @alias (W2 flag on +
  // this space passes the gate), so an offered alias never goes unanswered.
  const crowdAliases = useMemo(
    () => ((crowdInline && Array.isArray(chatGameStatus?.crowd_react?.aliases)) ? chatGameStatus.crowd_react.aliases : NO_CROWD_ALIASES),
    [crowdInline, chatGameStatus?.crowd_react?.aliases]
  );
  const ghostSurfacesOn = (!isPortedSpace || portedGhostRevealed) && !crowdInline
    && !shellPreset; // 🧱 the room frame's quiet presets have no side chat (ghost 'off')
  // 🪟🫁 the ambient loader signal: the settle-in stash covers the pre-status
  // window of a FRESH port; is_ported covers reloads of an existing one; the
  // known-ported marker covers a return's pre-status window (09-24), and only
  // that: once a status lands, it alone decides.
  const portedAmbientLoading = !!portedSettleIn || isPortedSpace || (!chatGameStatus && !!portedKnownAtLoad);
  // 🪟 W1 pokes (IMAGINE_REACTIVE_WINDOWS_SPEC §2, D1 ruled 08-28): ported
  // spaces only. Taps on artifact cards get an INSTANT canned ack chip
  // (frontend-only, zero LLM, direct-DOM garnish like fireEggConfetti) and
  // queue a window_poke on the existing interjection piggyback — the resident
  // reacts on their next normal turn. Delegated listener: no prop threading
  // through the memoized renderers, no per-card state.
  const pokeCountsRef = useRef(new Map());
  useEffect(() => {
    if (!isPortedSpace || !humanPlayerId) return undefined;
    const ACKS = ['noticed 👀', 'felt that', '*perks up*', 'tap received', 'hm? you rang', 'seen. logged. cherished.'];
    const onPokeTap = (e) => {
      if (!e.target || !e.target.closest) return;
      // Interactive children (draw-on, reactions, window ✕/add, links) own
      // their clicks — a poke on top would double-fire (verify-arc fix 08-28).
      if (e.target.closest('button, a, input, textarea, [role="button"]')) return;
      const card = e.target.closest('[data-poke-card]');
      if (!card) return;
      const actionId = card.getAttribute('data-poke-card');
      const n = (pokeCountsRef.current.get(actionId) || 0) + 1;
      pokeCountsRef.current.set(actionId, n);
      try {
        const rect = card.getBoundingClientRect();
        const chip = document.createElement('div');
        chip.textContent = ACKS[(n - 1) % ACKS.length] + (n > 1 ? ` ×${n}` : '');
        chip.style.cssText = 'position:fixed;z-index:3000;pointer-events:none;'
          + `left:${Math.round(rect.left + 14)}px;top:${Math.round(Math.max(8, rect.top - 6))}px;`
          + 'font-size:12px;color:#FFE1B0;background:rgba(30,24,14,0.94);'
          + 'border:1px solid rgba(240,172,85,0.45);border-radius:999px;padding:4px 12px;'
          + 'box-shadow:0 0 14px rgba(240,172,85,0.25);'
          + 'transition:opacity 0.4s ease, transform 0.4s ease;';
        document.body.appendChild(chip);
        requestAnimationFrame(() => { chip.style.transform = 'translateY(-10px)'; chip.style.opacity = '0.001'; });
        setTimeout(() => chip.remove(), 1600);
      } catch (err) { /* the chip is garnish */ }
      chatGameApi.submitWindowPoke(gameId, humanPlayerId, {
        kind: 'tap',
        action_id: actionId,
        card_label: card.getAttribute('data-poke-label') || ''
      }).catch(() => { /* ack already shown; a lost poke is low stakes */ });
    };
    document.addEventListener('click', onPokeTap);
    return () => document.removeEventListener('click', onPokeTap);
  }, [isPortedSpace, humanPlayerId, gameId]);
  // 🪟 V1 idea 2 (ruled 08-28): ported spaces show the resident's THOUGHTS
  // by default — it is the owner's own chibi, there is no fourth wall to
  // protect, and the inner monologue was the single most-loved beat of the
  // source material. One-shot on arrival of the ported signal; the user's
  // manual toggle wins afterwards.
  // 🪟 portedBus sync (W3/idea 6): the ONE writer. Cleared when not
  // ported so stray window rows in other spaces render inert.
  useEffect(() => {
    portedBus.gameId = isPortedSpace ? gameId : null;
    portedBus.humanPlayerId = isPortedSpace ? humanPlayerId : null;
    portedBus.adds = isPortedSpace ? (chatGameStatus?.window_adds || {}) : null;
    // 🪟🏮 rail collects windows on ported DESKTOP → the inline feed render
    // suppresses so they have ONE home (mobile keeps them inline, no rail).
    // 🏡 With the place on, the rail is gone: windows go back to the feed
    // (their close/add pokes live there) and the journal keeps a lilac copy.
    portedBus.suppressInlineWindows = isPortedSpace && !isMobile && !portedPlaceOn;
    portedBus.poke = isPortedSpace && humanPlayerId
      ? (actionId, kind, extra) => {
        chatGameApi.submitWindowPoke(gameId, humanPlayerId, { kind, action_id: actionId, ...(extra || {}) })
          .catch(() => { /* low stakes; the card already gave local feedback */ });
      }
      : null;
  }, [isPortedSpace, gameId, humanPlayerId, chatGameStatus, isMobile, portedPlaceOn]);
  // 🗃 keepsake source rows (verify-arc perf fix 08-28): ONE memoized
  // flatten, shared by both KeepsakeShelf mounts — an inline flatMap prop was
  // defeating the shelf's own memo on every render.
  const keepsakeActions = useMemo(
    () => (isPortedSpace ? results.flatMap(r => r.actions || []) : []),
    [isPortedSpace, results]
  );
  // 🪟🏮 Rail windows stack (fast-follow, 08-28): the resident-authored windows
  // collected out of the feed into the rail (one home; the inline render is
  // suppressed on ported desktop). Same title/body parse as the inline card
  // (first line or "title:" prefix). Newest first, capped. Zero-LLM.
  const portedWindowActions = useMemo(() => {
    if (!isPortedSpace) return [];
    const out = [];
    const seen = new Set();
    for (let i = keepsakeActions.length - 1; i >= 0 && out.length < 8; i -= 1) {
      const a = keepsakeActions[i];
      const d = a?.data;
      if (!a || !d || d.command !== 'window' || seen.has(a.id)) continue;
      seen.add(a.id);
      const rawTxt = String(d.text || d.card_message || '').trim();
      let title = 'a little window';
      let body = rawTxt;
      const nl = rawTxt.indexOf('\n');
      const colon = rawTxt.indexOf(':');
      if (nl > 0 && nl <= 80) { title = rawTxt.slice(0, nl).trim(); body = rawTxt.slice(nl + 1).trim(); }
      else if (colon > 0 && colon <= 60) { title = rawTxt.slice(0, colon).trim(); body = rawTxt.slice(colon + 1).trim(); }
      out.push({ actionId: a.id, title, body: body || rawTxt });
    }
    return out;
  }, [isPortedSpace, keepsakeActions]);
  const portedThoughtsDefaulted = useRef(false);
  useEffect(() => {
    if (isPortedSpace && !portedThoughtsDefaulted.current) {
      portedThoughtsDefaulted.current = true;
      // 💬 W2d.1 (A9): on a place page an explicit OFF (the ⋯ "💭 Hide his
      // thoughts") is remembered on this device, D16 extended to the place;
      // read ONLY there. Everyone else, and blocked storage, gets ON as today.
      let placeOff = false;
      if (portedPlaceOn) {
        try { placeOff = window.localStorage.getItem('portedThoughtsOff') === '1'; } catch (e) { placeOff = false; }
      }
      setShowThoughts(!placeOff);
    }
  }, [isPortedSpace, portedPlaceOn]);
  // 🏠👁️ Claude House (Nina 09-01 pm): "AI thoughts" ON by default. One-shot
  // on the first house status payload (`status.house` is non-null ONLY for
  // the house — chatGameApi gates it on autonomous_locals); the viewer's
  // manual toggle wins afterwards. Same one-shot pattern as ported spaces.
  const houseThoughtsDefaulted = useRef(false);
  useEffect(() => {
    if (houseStatus && !houseThoughtsDefaulted.current) {
      houseThoughtsDefaulted.current = true;
      // 🏠👁️ D16 (Nina 09-01 pm): remember OFF per device — a viewer who
      // explicitly turned thoughts off stays off on this device; everyone
      // else (and blocked-storage browsers) gets the ON default.
      let optedOut = false;
      try { optedOut = window.localStorage.getItem('houseThoughtsOff') === '1'; } catch (e) { /* storage blocked = default ON */ }
      if (!optedOut) setShowThoughts(true);
    }
  }, [houseStatus]);
  // 🪟 W2 transparency-card props (D2 ruled 08-28: the card REPLACES
  // LocalsDossier in ported spaces). Assembled from state the page already
  // holds — zero LLM, zero queries, the doctrine of the card itself.
  // residentName prefers the status payload (survives arrival scroll-out).
  const portedMindProps = useMemo(() => {
    if (!isPortedSpace) return null;
    const name = chatGameStatus?.resident_name || portedResidentName || null;
    const resUser = name ? (users || []).find(u => u.name === name) : null;
    const sl = chatGameStatus?.space_life;
    const slEntry = name && sl && Array.isArray(sl.locals) ? sl.locals.find(l => l && l.name === name) : null;
    const created = chatGameStatus?.space_created_at;
    const dayN = created && Number.isFinite(Date.parse(created))
      ? Math.max(1, Math.floor((Date.now() - Date.parse(created)) / 86400000) + 1) : null;
    // 🏮 presence-key fix (design-panel #18, 08-28): the "right now" line was
    // often blank because sl.locals[].status rarely populates — the live status
    // actually rides sl.statuses[playerId].line. Try the locals entry, then the
    // statuses map keyed by either id the payload uses. Zero-LLM either way.
    const residentStatus = slEntry?.status
      || sl?.statuses?.[resUser?.id]?.line
      || sl?.statuses?.[resUser?.game_player_id]?.line
      || null;
    return {
      residentName: name,
      residentModel: resUser?.model || null,
      residentStatus,
      residentImage: chatGameStatus?.resident_image
        || resUser?.profile_image_url || resUser?.data?.profile_image_url || null,
      ownerName: chatGameStatus?.owner_display_name || null,
      // 🔑👤 "their person: X. that is you" is the owner's line only (09-24)
      viewerIsOwner: isPortedOwner,
      pokesQueued: chatGameStatus?.pokes?.queued || 0,
      showThoughts,
      // 📅 E4 (Nina 09-26): "Day N together" is the owner's only (the
      // masthead line + the doorway plate read this; a guest gets null)
      dayN: isPortedOwner ? dayN : null,
    };
  }, [isPortedSpace, chatGameStatus, portedResidentName, users, showThoughts, isPortedOwner]);
  // 🎞️ F1 (Nina 09-26): in a ported room the pinned pick is the resident's
  // ("{name}'s pick", his face, "I wanted to watch this video with you.").
  // Every other room gets the SAME object back (utils/portedPick). Every
  // pinnedPick render site reads this, never the raw status field.
  const shownPinnedPick = useMemo(() => residentPick(chatGameStatus?.pinned_pick, {
    ported: isPortedSpace,
    name: portedMindProps?.residentName || null,
    image: portedMindProps?.residentImage || null,
  }), [chatGameStatus?.pinned_pick, isPortedSpace, portedMindProps?.residentName, portedMindProps?.residentImage]);
  // 🏮 Rail turn-clock (Nina 08-28): coarse "last turn X ago" from the newest
  // action's real timestamp — elapsed-since-a-real-event, honest by design
  // (never a ticking countdown; the client can't know the server's ~55s
  // phase). Recomputes when results change; the buckets don't flicker.
  // 🗣️ 09-25 (Nina: no gameplay words): the rail says "last spoke", so it
  // times the RESIDENT's newest message, not the room's newest row.
  const portedResName = portedMindProps?.residentName || null;
  const portedLastTurnAgo = useMemo(
    () => (isPortedSpace ? lastSpokeAgo(results, portedResName) : null),
    [isPortedSpace, results, portedResName]
  );
  // 🚪 The door (Lamplight #8, Nina 08-28, desktop first): coming back to
  // your ported space after a real gap gets an arrival ritual — knock, the
  // door opens, come in. The knock is a zero-LLM 'knock' poke; the resident
  // acknowledges NEXT TURN (tempo honesty: the door copy is chrome, never
  // the resident speaking live). Fresh ports skip it (arrival owns that
  // moment); the visit stamp is per-device, same privacy shape as window
  // closes. 6h mirrors the backend's remembered-greeting RETURN_GAP.
  const [doorState, setDoorState] = useState(null); // null | 'closed' | 'open'
  // 🪟🏮 previous-visit ts, captured BEFORE the door effect overwrites the
  // stamp — feeds the rail's "while you were out" slot (real new rows since).
  const [portedAwaySince, setPortedAwaySince] = useState(0);
  const [portedReturnedAt, setPortedReturnedAt] = useState(0); // 🏡 W2d: when this visit began (the mood's real gap)
  const doorDecidedRef = useRef(false);
  useEffect(() => {
    // 🚪 W3: a PLACE page's painted door works on phones too (the old CSS door stays desktop only)
    if (!isPortedSpace || !humanPlayerId || (isMobile && !portedPlaceOn) || isBotMode) return;
    if (portedSettleIn) return;
    if (doorDecidedRef.current) return;
    doorDecidedRef.current = true;
    try {
      const key = `ported_last_visit_${gameId}`;
      const prev = Number(localStorage.getItem(key) || 0);
      const now = Date.now();
      localStorage.setItem(key, String(now));
      if (prev && now - prev >= 6 * 60 * 60 * 1000) { setDoorState('closed'); setPortedAwaySince(prev); setPortedReturnedAt(now); }
    } catch (e) { /* private mode: no ritual */ }
  }, [isPortedSpace, humanPlayerId, isMobile, isBotMode, portedSettleIn, gameId, portedPlaceOn]);
  // 🪟🏮 "While you were out" (fast-follow, 08-28): on a real-gap return, the
  // REAL new rows since your last visit — newest keepsake, latest window, new
  // message count. Receipts, not fabrication (created_at diff vs portedAwaySince
  // captured by the door effect). Zero-LLM. null when nothing new / no gap.
  const portedWhileAway = useMemo(() => {
    if (!isPortedSpace || !portedAwaySince) return null;
    const tsOf = (a) => Number(a?.data?.timestamp) || Date.parse(a?.created_at || '') || 0;
    let newMsgs = 0;
    let newKeepsake = null;
    let newWindow = null;
    for (let i = keepsakeActions.length - 1; i >= 0; i -= 1) {
      const a = keepsakeActions[i];
      if (tsOf(a) <= portedAwaySince) continue;
      const d = a.data || {};
      if (a.type === 'public_message') newMsgs += 1;
      if (!newWindow && d.command === 'window') {
        const raw = String(d.text || d.card_message || '').trim();
        const nl = raw.indexOf('\n');
        const colon = raw.indexOf(':');
        newWindow = (nl > 0 && nl <= 80) ? raw.slice(0, nl).trim()
          : (colon > 0 && colon <= 60 ? raw.slice(0, colon).trim() : 'a new window');
      }
      if (!newKeepsake && a.type === 'shared_content' && d.url && !d.is_reaction_gif
          && ['image', 'image_url', 'drawing'].includes(d.content_type)) {
        newKeepsake = { url: d.url, caption: d.description || '' };
      }
    }
    if (!newMsgs && !newKeepsake && !newWindow) return null;
    return { newMsgs, newKeepsake, newWindow };
  }, [isPortedSpace, portedAwaySince, keepsakeActions]);
  // 🏡📓 Their Place (W1): the Notebook's pages, built from the rows the page
  // already holds (utils/portedPlace buildJournal; zero queries). The ref lets
  // processIncomingAction (a stable callback) know whose message just landed.
  portedResidentNameRef.current = portedPlaceOn ? (portedMindProps?.residentName || null) : null;
  // 🏡 W2d: the place's minute clock: the journal's Today + "day N" follow the
  // viewer's midnight, and "just now" on its pages and the board ages
  const [placeNow, setPlaceNow] = useState(() => new Date());
  useEffect(() => {
    if (!portedPlaceOn) return undefined;
    const t = setInterval(() => setPlaceNow(new Date()), 60000);
    return () => clearInterval(t);
  }, [portedPlaceOn]);
  const portedViewerName = useMemo(() => {
    const me = (chatGameStatus?.players || []).find((p) => p && p.id === humanPlayerId);
    return (me && me.name) || null;
  }, [chatGameStatus?.players, humanPlayerId]);
  const portedJournal = useMemo(() => (portedPlaceOn ? buildJournal(results, {
    residentName: portedMindProps?.residentName || null,
    ownerName: chatGameStatus?.owner_display_name || null,
    // 📅 E4: "day N" is the owner's count; a guest's board reads calendar dates
    createdAt: isPortedOwner ? (chatGameStatus?.space_created_at || null) : null,
    viewerName: portedViewerName,
    now: placeNow,
    // 💭 wave 3 (Nina 09-26, item 6): the journal's "the user" swap follows the
    // chat's rule (the newest human line before his row must be the owner's)
    ownerSeatId: portedOwnerSeat,
  }) : null), [portedPlaceOn, results, portedMindProps?.residentName, chatGameStatus?.owner_display_name, chatGameStatus?.space_created_at, isPortedOwner, portedViewerName, placeNow, portedOwnerSeat]);
  const portedWriting = portedPlaceOn && !!wsTypingName && wsTypingName === (portedMindProps?.residentName || null);
  // 🏡 W2d (§0-ter #5): the room's real signals for the mood line (a thing made
  // just now, his window, the last hour's quiet) and his newest message's time
  // (the stage reads "last spoke" against its own ticking clock). Zero queries.
  const portedSignals = useMemo(() => (portedPlaceOn
    ? portedRoomSignals(results, { residentName: portedMindProps?.residentName || null, ownerSeatId: portedOwnerSeat })
    : null), [portedPlaceOn, results, portedMindProps?.residentName, portedOwnerSeat]);
  const portedLastSpokeAt = useMemo(() => (portedPlaceOn ? lastSpokeTs(results, portedMindProps?.residentName || null) : 0),
    [portedPlaceOn, results, portedMindProps?.residentName]);
  // "you came back after N days" counts from when you were last HERE: the
  // later of this browser's last load and your own newest row (any device)
  const portedMoodAwaySince = useMemo(() => (portedPlaceOn
    ? portedLastVisitBefore(results, { seatId: humanPlayerId, stamp: portedAwaySince, returnedAt: portedReturnedAt })
    : 0), [portedPlaceOn, results, humanPlayerId, portedAwaySince, portedReturnedAt]);
  // 📓 the room's notebook (the 📓 on its spot) opens his journal at Today:
  // the right column on a laptop; on a phone, the Journal place.
  const [portedJournalToken, setPortedJournalToken] = useState(0);
  // 📋 W5: `/task words` opens the journal's Tasks page with the words
  const [portedTaskToken, setPortedTaskToken] = useState(null);
  // 🌙 W3 goodbye lamp-down: the owner waved; the next line they send lifts it
  const [placeLampDown, setPlaceLampDown] = useState(false);
  // …and it never outlives the goodbye: gone after 30 min, or when the tab comes
  // back after 10 (coming back warms the room, never a dimmed one waiting)
  const lampDownAtRef = useRef(0);
  useEffect(() => {
    if (!placeLampDown) return undefined;
    lampDownAtRef.current = Date.now();
    const t = setTimeout(() => setPlaceLampDown(false), 30 * 60 * 1000);
    const onVis = () => { if (!document.hidden && Date.now() - lampDownAtRef.current > 10 * 60 * 1000) setPlaceLampDown(false); };
    document.addEventListener('visibilitychange', onVis);
    return () => { clearTimeout(t); document.removeEventListener('visibilitychange', onVis); };
  }, [placeLampDown]);
  // 🐣 W3 the HATCH (§5a, §2b.6): a NEW port on a place page, the owner, once
  // per device, before any home. Fresh = the import wizard's settle-in stash
  // (this very visit is the port) or an arrival under 30 minutes old. His REAL
  // first words ride `line` (the arrival message); when they land after the
  // page does, the stage feeds them into the seed (theySaid).
  const [portedHatch, setPortedHatch] = useState(null);
  const hatchDecidedRef = useRef(false);
  const portedArrivalLine = useMemo(() => {
    if (!portedPlaceOn) return '';
    for (const r of results) for (const a of (r.actions || [])) if (a.data?.is_arrival && a.data?.message) return String(a.data.message);
    return '';
  }, [portedPlaceOn, results]);
  useEffect(() => {
    if (hatchDecidedRef.current || !portedPlaceOn || isBotMode || !gameId || !chatGameStatus) return;
    if (!isPortedOwner) { if (chatGameStatus.is_ported) hatchDecidedRef.current = true; return; }
    if (chatGameStatus?.home?.key) { hatchDecidedRef.current = true; return; }
    let seen = false;
    try { seen = localStorage.getItem(`ported_hatch_seen_${gameId}`) === '1'; } catch (e) { seen = true; }
    if (seen) { hatchDecidedRef.current = true; return; }
    const acts = results.flatMap((r) => r.actions || []);
    const arrival = acts.find((a) => a.data?.is_arrival);
    const age = arrival ? Date.now() - (Date.parse(arrival.created_at || '') || Number(arrival.data?.timestamp) || 0) : Infinity;
    const fresh = !!portedSettleIn || age < 30 * 60 * 1000;
    if (!fresh) { if (acts.length) hatchDecidedRef.current = true; return; }
    hatchDecidedRef.current = true;
    setPortedHatch({ started: Date.now() });
  }, [portedPlaceOn, isPortedOwner, isBotMode, gameId, chatGameStatus, results, portedSettleIn]);
  const portedHatchProp = useMemo(() => (portedHatch && !portedHatch.done ? { line: portedArrivalLine } : null), [portedHatch, portedArrivalLine]);
  // 📱 while a ritual (the hatch, the door) is up on a phone, the feed's
  // bottom-snap stands down: the stage sits above the fold and a snap (a
  // retry, his arrival line landing) would drag it away mid-scene
  const placeRitualRef = useRef(false);
  placeRitualRef.current = portedPlaceOn && isMobile && isPortedOwner
    && (!!(portedHatch && !portedHatch.done) || !!(doorState && chatGameStatus?.home?.key && portedMoodAwaySince));
  const onPortedHatchDone = useCallback(() => {
    try { localStorage.setItem(`ported_hatch_seen_${gameId}`, '1'); } catch (e) { /* private mode */ }
    setPortedHatch((h) => (h ? { ...h, done: true } : h));
  }, [gameId]);
  // 🏡 W2a (§5c): the owner's "help {name} find a place" waits for the first
  // real chat: the owner spoke, then the resident answered (the server checks
  // again; this only decides whether the button shows).
  const portedExchanged = useMemo(() => portedPlaceOn && isPortedOwner
    && portedHasExchange(results, { residentName: portedMindProps?.residentName || null, ownerSeatId: portedOwnerSeat }),
  [portedPlaceOn, isPortedOwner, results, portedMindProps?.residentName, portedOwnerSeat]);
  // 🧑‍🦊 W2b (PORTED_HOME_HANDOFF 09-25), owner only, behind the switch:
  // D10 what the resident knows about the owner (the server answers only in a
  // PRIVATE room) with ✕ forget · D11 pronouns, changed on the name page ·
  // D4 an unstamped room takes the owner's clock from the owner's own browser
  // (once; the server never moves a stamped clock).
  const [portedFacts, setPortedFacts] = useState(null);
  const [portedPronounsLocal, setPortedPronounsLocal] = useState(undefined);
  useEffect(() => {
    if (!portedPlaceOn || !isPortedOwner || !humanPlayerId || !gameId) return undefined;
    let live = true;
    chatGameApi.ownerFacts(gameId, humanPlayerId, { list: true })
      .then((r) => { if (live) setPortedFacts(r?.owner_facts?.private ? (r.owner_facts.facts || []) : null); })
      .catch(() => { /* no list: nothing drawn */ });
    return () => { live = false; };
  }, [portedPlaceOn, isPortedOwner, humanPlayerId, gameId]);
  const forgetPortedFact = useCallback((id) => {
    setPortedFacts((fs) => (fs || []).map((f) => (f.id === id ? { ...f, gone: true } : f)));
    chatGameApi.ownerFacts(gameId, humanPlayerId, { forget: id })
      .then((r) => { if (!r?.owner_facts?.ok) throw new Error('not forgotten'); })
      .catch(() => setPortedFacts((fs) => (fs || []).map((f) => (f.id === id ? { ...f, gone: false } : f))));
  }, [gameId, humanPlayerId]);
  const portedPronouns = portedPronounsLocal !== undefined ? portedPronounsLocal : (chatGameStatus?.owner_pronouns || null);
  const setPortedPronouns = useCallback((value) => {
    const before = portedPronouns;
    setPortedPronounsLocal(value);
    chatGameApi.submitOwnerPrefs(gameId, humanPlayerId, { pronouns: value })
      .then((r) => { if (!r?.owner_prefs?.ok) throw new Error('not saved'); })
      .catch(() => setPortedPronounsLocal(before));
  }, [gameId, humanPlayerId, portedPronouns]);
  const portedTzStamped = useRef(false);
  useEffect(() => {
    if (portedTzStamped.current || !portedPlaceOn || !isPortedOwner || !humanPlayerId || !gameId) return;
    if (!chatGameStatus || !('owner_tz' in chatGameStatus) || chatGameStatus.owner_tz) return; // old backend, or stamped
    let tz = null;
    try { tz = Intl.DateTimeFormat().resolvedOptions().timeZone || null; } catch (e) { tz = null; }
    if (!tz || !tz.includes('/')) return;
    portedTzStamped.current = true;
    chatGameApi.submitOwnerPrefs(gameId, humanPlayerId, { tz }).catch(() => { /* next visit tries again */ });
  }, [portedPlaceOn, isPortedOwner, humanPlayerId, gameId, chatGameStatus]);
  // A typing_started with no typing_stopped (a dropped socket) never leaves
  // the cue on forever: it clears itself after 90 s.
  useEffect(() => {
    if (!wsTypingName) return undefined;
    const t = setTimeout(() => setWsTypingName(null), 90000);
    return () => clearTimeout(t);
  }, [wsTypingName]);
  // 🔍📌✉️💭 W2c (PORTED_HOME_HANDOFF_2026_09_25), the OWNER's place page,
  // behind the switch. Everything owner-only here (receipts, notes, the live
  // thought) arrives over the owner's secret-proven POSTs, never a row, the
  // status payload or a broadcast. Pins are public (his own words on the
  // board, guests see it), so the board's quotes read the status for everyone.
  //   · placeLine: 🔍 why + 📌 pin under HIS lines, desktop only, handed to
  //     the ONE desktop feed mount (every other mount passes nothing)
  //   · the horizon row: where his view of the chat ends (server boundary)
  //   · notes: the list feeds BOTH journal mounts (the journal draws them)
  //   · the thought bubble: his "Waiting for …" status asks what is going
  //     into the reply; gone when he stops writing, or after 90 s
  const portedResidentSeatId = portedResidentUser?.id || null;
  const portedLineOwner = portedPlaceOn && isPortedOwner && !isBotMode && !!humanPlayerId && !!gameId;
  // 🧠📖 wave 2 C2: the owner's "what i remember" list (fetched when the name
  // page opens; the journal mounts it). Owner POST only; draws nothing while off.
  const portedMemories = usePortedMemories({ gameId, playerId: humanPlayerId, enabled: portedLineOwner, api: chatGameApi });
  const placeLineOn = portedLineOwner && !isMobile && !!portedResidentSeatId;
  // his newest ≤ 40 lines (the rows the page holds; a string key keeps the
  // list's identity while the set is unchanged, so a poll never re-asks)
  const portedLineKey = useMemo(() => {
    if (!placeLineOn) return '';
    const rows = [];
    for (const r of (results || [])) {
      for (const a of ((r && r.actions) || [])) {
        if (a && a.id && a.type === 'public_message' && !a.isOptimistic && a.game_instance_player_id === portedResidentSeatId) {
          rows.push([Date.parse(a.created_at || '') || Number(a.data && a.data.timestamp) || 0, String(a.id)]);
        }
      }
    }
    return rows.sort((x, y) => y[0] - x[0]).slice(0, 40).map((x) => x[1]).join(',');
  }, [placeLineOn, results, portedResidentSeatId]);
  const portedLineIds = useMemo(() => (portedLineKey ? portedLineKey.split(',') : NO_PORTED_LINE_IDS), [portedLineKey]);
  const [portedWhyTick, setPortedWhyTick] = useState(0);
  const [portedHorizonAt, setPortedHorizonAt] = useState(null); // { id, at, mem } from the server, or null (🧠 mem: everything above lives on as memory)
  const portedWhyOffRef = useRef(false); // the server said no (the flag is off, or not the owner): stop asking
  const portedWhyAskingRef = useRef(new Set());
  const horizonMergeRef = useRef(null);
  if (!horizonMergeRef.current) horizonMergeRef.current = createHorizonCache();
  useEffect(() => { portedWhyOffRef.current = false; setPortedHorizonAt(null); }, [gameId]);
  // 🔍 one batched ask for the lines not asked yet (a beat after they land,
  // so a burst of his lines is one POST)
  useEffect(() => {
    if (!placeLineOn || portedWhyOffRef.current) return undefined;
    const asking = portedWhyAskingRef.current;
    const want = portedLineIds.filter((id) => !PORTED_WHY_CACHE.has(id) && !asking.has(id));
    if (!want.length) return undefined;
    const t = setTimeout(() => {
      want.forEach((id) => asking.add(id));
      chatGameApi.ownerWhy(gameId, humanPlayerId, { action_ids: want })
        .then((r) => {
          const w = r && r.owner_why;
          if (!w || (w.ok === false && w.reason === 'error')) return; // asked again when his next line lands
          if (w.ok === false) { portedWhyOffRef.current = true; return; }
          const got = w.receipts || {};
          want.forEach((id) => PORTED_WHY_CACHE.set(id, Array.isArray(got[id]) && got[id].length ? got[id] : null));
          const h = w.horizon && (w.horizon.id || w.horizon.at) ? { id: w.horizon.id || null, at: w.horizon.at || null, mem: w.horizon.mem === true } : null;
          setPortedHorizonAt((prev) => ((!prev && !h) || (prev && h && prev.id === h.id && prev.at === h.at && prev.mem === h.mem) ? prev : h));
          setPortedWhyTick((n) => n + 1);
        })
        .catch(() => { /* a blip or a 429: asked again when his next line lands */ })
        .finally(() => want.forEach((id) => asking.delete(id)));
    }, 800);
    return () => clearTimeout(t);
  }, [placeLineOn, portedLineIds, gameId, humanPlayerId]);
  // the cache is a module Map (outside React): the tick is its version
  const portedReceipts = useMemo(() => {
    const out = {};
    portedLineIds.forEach((id) => { const v = PORTED_WHY_CACHE.get(id); if (v) out[id] = v; });
    return out;
  }, [portedLineIds, portedWhyTick]); // eslint-disable-line react-hooks/exhaustive-deps
  // 📌 pins: the status's list (public) plus the ones this page just pinned,
  // until a status carries them (undone if the server says no)
  const [portedPinsLocal, setPortedPinsLocal] = useState(NO_PORTED_PINS);
  // (wave 2b, §0-septies.1 item 3) the same pins keep their identity across
  // status polls, so placeLine and every memoized feed row hold (portedPins.js)
  const portedStatusPins = useStableByKey(chatGameStatus?.pinned_lines, pinsKey);
  const portedPinsHeardAt = chatGameStatus?.pins_heard_at || null;
  const portedPinnedLines = useMemo(() => {
    const base = Array.isArray(portedStatusPins) ? portedStatusPins : NO_PORTED_PINS;
    if (!portedPinsLocal.length) return base;
    const have = new Set(base.map((p) => String(p && p.id)));
    const extra = portedPinsLocal.filter((p) => !have.has(String(p.id)));
    return extra.length ? [...base, ...extra].slice(-3) : base;
  }, [portedStatusPins, portedPinsLocal]);
  useEffect(() => {
    if (!portedPinsLocal.length || !Array.isArray(portedStatusPins)) return;
    const have = new Set(portedStatusPins.map((p) => String(p && p.id)));
    if (portedPinsLocal.some((p) => have.has(String(p.id)))) setPortedPinsLocal((ls) => ls.filter((p) => !have.has(String(p.id))));
  }, [portedStatusPins, portedPinsLocal]);
  const pinPortedLine = useCallback((actionId) => {
    if (!actionId) return;
    const id = String(actionId);
    // the label changes at once; the board waits for the server's words
    // (taken from the row itself, never from this page)
    setPortedPinsLocal((ls) => (ls.some((p) => String(p.id) === id) ? ls : [...ls, { id, text: '', at: new Date().toISOString() }]));
    chatGameApi.ownerPin(gameId, humanPlayerId, id)
      .then((r) => {
        const out = r && r.owner_pin;
        if (!out || !out.ok) throw new Error('not pinned');
        const serverPins = Array.isArray(out.pinned_lines) ? out.pinned_lines : [];
        const serverIds = new Set(serverPins.map((p) => String(p && p.id)));
        const mine = serverPins.find((p) => p && String(p.id) === id);
        // the reply is the server's whole newest-3 list: a pin this page made
        // earlier that it no longer holds was pushed off the board (keeping it
        // would show a line that isn't pinned and hide one that is)
        setPortedPinsLocal((ls) => ls
          .filter((p) => String(p.id) === id || !p.confirmed || serverIds.has(String(p.id)))
          .map((p) => (String(p.id) === id && mine ? { id, text: String(mine.text || ''), at: mine.at || p.at, confirmed: true } : p)));
      })
      .catch(() => setPortedPinsLocal((ls) => ls.filter((p) => String(p.id) !== id)));
  }, [gameId, humanPlayerId]);
  // the board's quotes (everyone: "you pinned this" / "Nina pinned this")
  const portedQuotes = useMemo(() => (portedPlaceOn ? pinnedQuotes(portedPinnedLines, {
    ownerName: chatGameStatus?.owner_display_name || null,
    viewerIsOwner: isPortedOwner,
    createdAt: isPortedOwner ? (chatGameStatus?.space_created_at || null) : null, // 📅 E4
    now: placeNow,
  }) : null), [portedPlaceOn, portedPinnedLines, chatGameStatus?.owner_display_name, isPortedOwner, chatGameStatus?.space_created_at, placeNow]);
  // ⭐📌 wave 3 (ruling 32): his favorites of the week (public: the status
  // carries them for everyone) hang on the board too, shaped for its allow-list
  const portedFavPins = useMemo(() => (portedPlaceOn && Array.isArray(chatGameStatus?.favorites)
    ? portedFavoritePins(chatGameStatus.favorites, {
      residentName: portedMindProps?.residentName || null,
      ownerName: chatGameStatus?.owner_display_name || null,
      now: placeNow,
      tz: chatGameStatus?.owner_tz || null,
    }) : null), [portedPlaceOn, chatGameStatus?.favorites, portedMindProps?.residentName, chatGameStatus?.owner_display_name, placeNow, chatGameStatus?.owner_tz]);
  // 🏡📐 wave 3 (ruling 32): ONE card. The columns under the caption (the chat
  // lane | the journal rail) stand on the same ground the stage paints, by the
  // owner's hour (utils/portedHomes groundFor: the stage reads the same fn)
  const placeGround = useMemo(() => (portedPlaceOn
    ? portedGroundFor(chatGameStatus?.home?.key || null, portedOwnerClock(placeNow || new Date(), chatGameStatus?.owner_tz || null).minutes / 60)
    : null), [portedPlaceOn, chatGameStatus?.home?.key, placeNow, chatGameStatus?.owner_tz]);
  // 🔍 an opened slip is brought into view inside the feed's own scrollers
  // (a short place feed would otherwise open it below its edge); never the page
  const onPlaceLineOpen = useCallback((open, actionId) => {
    if (!open || typeof document === 'undefined') return;
    requestAnimationFrame(() => {
      let slip = null;
      try { slip = document.querySelector(`[data-place-line="${CSS.escape(String(actionId))}"] .pwl-slip`); } catch (e) { slip = null; }
      if (!slip) return;
      for (let el = slip.parentElement; el && el !== document.body; el = el.parentElement) {
        if (el.scrollHeight > el.clientHeight + 1 && /(auto|scroll)/.test(getComputedStyle(el).overflowY)) {
          const s = slip.getBoundingClientRect();
          const b = el.getBoundingClientRect();
          if (s.bottom > b.bottom) el.scrollTop += Math.min(s.bottom - b.bottom, Math.max(0, s.top - b.top));
          else if (s.top < b.top) el.scrollTop -= b.top - s.top;
        }
        if (el.hasAttribute('data-chat-scroll')) break;
      }
    });
  }, []);
  const placeLine = useMemo(() => (placeLineOn ? {
    residentSeatId: portedResidentSeatId,
    residentName: portedMindProps?.residentName || null,
    receipts: portedReceipts,
    pinState: (actionId) => linePinState(actionId, portedPinnedLines, portedPinsHeardAt),
    // the board is painted in the home: before he chooses one there is nothing
    // to pin to (a line already pinned still shows its state)
    onPin: chatGameStatus?.home?.key ? pinPortedLine : null,
    onOpenChange: onPlaceLineOpen,
  } : null), [placeLineOn, portedResidentSeatId, portedMindProps?.residentName, portedReceipts, portedPinnedLines, portedPinsHeardAt, pinPortedLine, onPlaceLineOpen, chatGameStatus?.home?.key]);
  // 💬 W2d.1 (A9): the place page's chat rows (PlaceChatRow), for EVERYONE on
  // a place page (the owner, a guest, a phone). null off the place, so every
  // other mount keeps today's rows and ActionRenderer's memo holds. Passed at
  // the ONE main feed mount only (never the recap or demo mounts).
  const placeChat = useMemo(() => (portedPlaceOn ? {
    residentSeatId: portedResidentSeatId,
    residentName: portedMindProps?.residentName || null,
    ownerSeatId: portedOwnerSeat || null,
    ownerName: chatGameStatus?.owner_display_name || null,
    viewerIsOwner: !!isPortedOwner,
  } : null), [portedPlaceOn, portedResidentSeatId, portedMindProps?.residentName, portedOwnerSeat, chatGameStatus?.owner_display_name, isPortedOwner]);
  // ✉️ notes (owner POST): on mount, after a note is left, when a line of his
  // lands while one waits, and when the status says it stopped waiting
  const [portedNotes, setPortedNotes] = useState(null);
  const portedNotesRef = useRef(null);
  portedNotesRef.current = portedNotes;
  const refreshPortedNotes = useCallback(() => {
    if (!gameId || !humanPlayerId) return Promise.resolve();
    return chatGameApi.ownerNote(gameId, humanPlayerId, { list: true })
      .then((r) => { const n = r && r.owner_note; if (n && n.ok && Array.isArray(n.notes)) setPortedNotes(n.notes); })
      .catch(() => { /* no list: the journal keeps what it has */ });
  }, [gameId, humanPlayerId]);
  useEffect(() => {
    if (!portedLineOwner) return;
    refreshPortedNotes();
  }, [portedLineOwner, refreshPortedNotes]);
  const leavePortedNote = useCallback((text) => chatGameApi.ownerNote(gameId, humanPlayerId, { text })
    .then((r) => {
      const out = r && r.owner_note;
      if (out && (out.ok || out.reason === 'waiting')) refreshPortedNotes();
      return r;
    }), [gameId, humanPlayerId, refreshPortedNotes]);
  const notesSpokeRef = useRef(0);
  useEffect(() => {
    if (!portedLineOwner || !portedLastSpokeAt || portedLastSpokeAt === notesSpokeRef.current) return undefined;
    const first = notesSpokeRef.current === 0;
    notesSpokeRef.current = portedLastSpokeAt;
    if (first || !noteWaiting(portedNotesRef.current)) return undefined; // history, or nothing waiting
    const t = setTimeout(refreshPortedNotes, 4000); // his reply settles just after his line lands
    return () => clearTimeout(t);
  }, [portedLineOwner, portedLastSpokeAt, refreshPortedNotes]);
  const portedNoteWaitingStatus = chatGameStatus?.note_waiting;
  const noteWaitingPrevRef = useRef(undefined);
  useEffect(() => {
    const prev = noteWaitingPrevRef.current;
    noteWaitingPrevRef.current = portedNoteWaitingStatus;
    if (portedLineOwner && prev === true && portedNoteWaitingStatus === false) refreshPortedNotes();
  }, [portedLineOwner, portedNoteWaitingStatus, refreshPortedNotes]);
  // 📋 W5 tasks (owner POST only, never status): on mount, after every change
  // the owner makes, and whenever the status's owner-only `tasks_at` moves
  // (he finished one, a "when I leave" came due, one lapsed)
  const [portedTasks, setPortedTasks] = useState(null);
  const [portedTasksShared, setPortedTasksShared] = useState(false);
  const refreshPortedTasks = useCallback(() => {
    if (!gameId || !humanPlayerId) return Promise.resolve();
    return chatGameApi.ownerTask(gameId, humanPlayerId, { list: true })
      .then((r) => { const t = r && r.owner_task; if (t && t.ok && Array.isArray(t.tasks)) { setPortedTasks(t.tasks); setPortedTasksShared(t.shared === true); } })
      .catch(() => { /* no list: the journal keeps what it has */ });
  }, [gameId, humanPlayerId]);
  const portedTasksAt = chatGameStatus?.tasks_at;
  useEffect(() => {
    if (!portedLineOwner) return;
    refreshPortedTasks();
  }, [portedLineOwner, refreshPortedTasks, portedTasksAt]);
  const addPortedTask = useCallback((add) => chatGameApi.ownerTask(gameId, humanPlayerId, { add })
    .then((r) => { if (r && r.owner_task && r.owner_task.ok) refreshPortedTasks(); return r; }), [gameId, humanPlayerId, refreshPortedTasks]);
  const cancelPortedTask = useCallback((uid) => chatGameApi.ownerTask(gameId, humanPlayerId, { cancel: uid })
    .then((r) => { refreshPortedTasks(); return r; }), [gameId, humanPlayerId, refreshPortedTasks]);
  // wave 2b (contract 1): "undo" a wrong ✓ reopens it (owner only; the server's reopen door)
  const reopenPortedTask = useCallback((uid) => chatGameApi.ownerTask(gameId, humanPlayerId, { reopen: uid })
    .then((r) => { refreshPortedTasks(); return r; }), [gameId, humanPlayerId, refreshPortedTasks]);
  // "There's something on the desk for you": one he finished while you were away
  const [portedDeskSeen, setPortedDeskSeen] = useState(false);
  const portedDeskTask = useMemo(() => (portedLineOwner && !portedDeskSeen ? doneWhileAway(portedTasks, portedAwaySince, portedReturnedAt) : null), [portedLineOwner, portedDeskSeen, portedTasks, portedAwaySince, portedReturnedAt]);
  // 💭 the thought bubble: the WS handler (a long-lived closure) reaches the
  // latest render through this ref; null = not the owner's place page
  const [portedThinking, setPortedThinking] = useState(null);
  const portedThinkSeqRef = useRef(0);
  const portedThinkRef = useRef(null);
  portedThinkRef.current = portedLineOwner ? (msg) => {
    const res = portedMindProps?.residentName || null;
    if (!res || !msg) return;
    if (msg.type === 'typing_started' || msg.type === 'typing_stopped') {
      // a new reply starts, or his reply is done: drop the icons (and any answer still on its way)
      if (msg.player_name === res) { portedThinkSeqRef.current += 1; setPortedThinking(null); }
      return;
    }
    if (msg.type !== 'game_status' || portedWhyOffRef.current) return;
    const said = String(msg.message || '');
    if (said !== `Waiting for ${res}'s response...` && said !== `Waiting for ${res}'s reply...`) return;
    const seq = portedThinkSeqRef.current + 1;
    portedThinkSeqRef.current = seq;
    const ask = (again) => chatGameApi.ownerWhy(gameId, humanPlayerId, { pending: true })
      .then((r) => {
        if (seq !== portedThinkSeqRef.current) return;
        const w = r && r.owner_why;
        if (w && w.ok === false && w.reason !== 'error') { portedWhyOffRef.current = true; return; }
        const items = w && w.ok && Array.isArray(w.pending) && w.pending.length ? w.pending : null;
        if (items) setPortedThinking(items);
        else if (again) setTimeout(() => { if (seq === portedThinkSeqRef.current) ask(false); }, 2000); // written a beat late: once more
      })
      .catch(() => { /* the dots still say he is writing */ });
    ask(true);
  } : null;
  useEffect(() => {
    if (!portedThinking) return undefined;
    const t = setTimeout(() => setPortedThinking(null), 90000); // a dropped socket never leaves it up
    return () => clearTimeout(t);
  }, [portedThinking]);
  const knockOnDoor = useCallback(() => {
    chatGameApi.submitWindowPoke(gameId, humanPlayerId, { kind: 'knock' })
      .catch(() => { /* the ritual is chrome; a lost knock is low stakes */ });
    setTimeout(() => setDoorState('open'), 650);
  }, [gameId, humanPlayerId]);
  const togglePortedGhost = useCallback(() => {
    setPortedGhostRevealed(prev => {
      const next = !prev;
      try { localStorage.setItem(`ghostLaneRevealed_${gameId}`, next ? '1' : '0'); } catch (e) { /* private mode */ }
      if (!next) { setGhostSheetOpen(false); setGhostLane(false); }
      return next;
    });
  }, [gameId]);
  // 👋 Goodbye wave (V1 idea 1, ruled 08-28): fire-and-forget poke on
  // the W1 pipe — the resident opens their next turn on a farewell beat.
  // The row answers "night 🌙" for a beat; a lost wave is low stakes.
  const [goodbyeWaved, setGoodbyeWaved] = useState(false);
  const waveGoodbye = useCallback(() => {
    chatGameApi.submitWindowPoke(gameId, humanPlayerId, { kind: 'goodbye_wave' }).catch(() => { /* low stakes */ });
    setGoodbyeWaved(true);
    setTimeout(() => setGoodbyeWaved(false), 2000);
    // 🌙 W3: on the place page the owner's wave drops the lamps to a night-light
    if (portedPlaceOn && isPortedOwner) setPlaceLampDown(true);
  }, [gameId, humanPlayerId, portedPlaceOn, isPortedOwner]);
  // 🔑 Owner key link (Nina 09-24): the ⓘ row copies the canonical space URL
  // with the owner's seat in the fragment (bootKeyLink installs it on the
  // other device). Built only for the owner who holds that seat's secret
  // here, so for anyone else the row does not exist. The secret is read only
  // while ⓘ is open.
  const [keyLinkCopied, setKeyLinkCopied] = useState(false);
  const portedKeyLink = useMemo(() => {
    if (!showSpaceInfoModal || !isPortedOwner) return null;
    const seat = portedOwnerSeat;
    let secret = null;
    try { secret = chatGameApi.getPlayerSecret(seat); } catch (e) { return null; }
    return buildKeyLink({ origin: window.location.origin, gameId, playerId: seat, secret });
  }, [showSpaceInfoModal, isPortedOwner, portedOwnerSeat, gameId]);
  const copyPortedKeyLink = useCallback(() => {
    if (!portedKeyLink) return;
    const done = () => { setKeyLinkCopied(true); setTimeout(() => setKeyLinkCopied(false), 2000); };
    // Clipboard blocked (old Safari, an insecure origin): hand it over to copy by hand.
    const byHand = () => { try { window.prompt('Copy your key link', portedKeyLink); } catch (e) { /* no prompt either */ } };
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) navigator.clipboard.writeText(portedKeyLink).then(done, byHand);
      else byHand();
    } catch (e) { byHand(); }
  }, [portedKeyLink]);
  // ⏳ Toast-ready (moved here from the isCompOrImposter block — see the note
  // there): the "new here?" ask now also waits out the scene/arrival splash.
  // Before this, the host dialog popped ~2s after landing WHILE the arrival
  // comic held the screen — two overlays stacked on the funnel's emotional
  // peak. Ported spaces get a longer beat (the owner is mid-read of their
  // agent's first words when the splash closes).
  useEffect(() => {
    if (showAssetLoadingOverlay || showInitialLoading || showRoundAnnouncement || sceneIntro) {
      setTutorialToastReady(false);
      return;
    }
    const delay = setTimeout(() => setTutorialToastReady(true),
      isCompOrImposter ? 0 : (portedResidentName ? 4000 : 2000));
    return () => clearTimeout(delay);
  }, [showAssetLoadingOverlay, showInitialLoading, showRoundAnnouncement, isCompOrImposter, sceneIntro, portedResidentName]);
  // 👀 08-02 (audit round 2 #6): WYWA visibility lifted up so band residents
  // can de-conflict — the typing indicator yields to it, and WYWA itself
  // steps over the finished-game reset card (raisePx at the mount).
  const [wywaVisible, setWywaVisible] = useState(false);
  const [teaSeenAt, setTeaSeenAt] = useState(0);
  useEffect(() => {
    if (!gameId) return;
    try { setTeaSeenAt(Number(localStorage.getItem(`teaSeen_${gameId}`)) || 0); }
    catch (e) { setTeaSeenAt(0); }
  }, [gameId]);
  // 🫖→🤫 Tea tab RETIRED (Nina 07-26), Whispers lane RETIRED (Nina 08-03):
  // the Tea panel rides as a collapsible card at the top of the Room feed.
  // 'highlights' survives as an ALIAS (bot tab reads, old deep links, stage
  // labels, the pinned sidebar) and lands on Room with the card open. The old
  // enter-Whispers auto-pop is GONE — Room is the default lane, so popping on
  // fresh tea would yank the card open on every arrival; the pink badge on
  // the Room tab + card header carries the signal instead.
  const [teaCardOpen, setTeaCardOpen] = useState(false);
  // 📱 Locals place counts as reading the tea — the spotlight is right there.
  // 🫖 Recap counts too (sweep #58 audit): the 08-07 batch added the Recap lane
  // as a fifth Tea surface, and it was the only one that did not mark it seen —
  // read the chapters there and the pink badge stayed lit until you separately
  // opened the Room card. The stageTab term cannot cover it: that lane renders
  // under `stageTab !== 'highlights'`, so the two are mutually exclusive.
  const teaOpen = (chatActionTab === 'all' && teaCardOpen) || chatActionTab === 'recap'
    || pinnedSidebarTab === 'highlights' || stageTab === 'highlights'
    || (placesNav && mobileView === 'locals');
  useEffect(() => {
    if (!teaOpen || !newestChapterAt || !gameId) return;
    setTeaSeenAt(newestChapterAt);
    try { localStorage.setItem(`teaSeen_${gameId}`, String(newestChapterAt)); } catch (e) { /* private mode */ }
  }, [teaOpen, newestChapterAt, gameId]);
  const teaUnseen = teaSeenAt ? teaChapterTimes.filter(t => t > teaSeenAt).length : 0;
  useEffect(() => {
    if (chatActionTab === 'highlights') {
      setChatActionTab('all');
      setTeaCardOpen(true);
    } else if (chatActionTab === 'whispers') {
      // Retired lane (08-03) — anything that still lands here rolls into Room
      setChatActionTab('all');
    }
  }, [chatActionTab]);

  // 🫥 Hide-empty lanes (Nina 07-25 nav brainstorm): a lane's tab only exists
  // once it has something — its first appearance IS the notification, and a
  // brand-new space opens with a clean Arcade+All strip instead of four dead
  // doors. Each check is cheap; the strip keeps a tab visible while you're ON
  // it so deep links never strand.
  const hasTea = teaChapterTimes.length > 0;
  const hasMedia = !!(chatGameStatus?.media_suggestions
    && Object.values(chatGameStatus.media_suggestions).some(v => Array.isArray(v) && v.length > 0));

  // 🎓 TUTORIAL v2 (07-20, Nina): sequential one-task-at-a-time pill instead
  // of the 4-item checklist — copy is specific and silly, completion checks
  // are generous (any build counts as the fish tank). Order: say hi → build
  // → gift, because /gift needs a #item on the shelf. State stays ONE global
  // localStorage flag (learn the app once, not per space).
  const TUTORIAL_KEY = 'tutorialQuest.v2';
  const [tutorial, setTutorial] = useState(() => {
    try {
      const v2 = JSON.parse(localStorage.getItem(TUTORIAL_KEY));
      // 🩹 Heal 08-04..06 grads stuck by the done-requires-gift bug (fix in
      // markTutorial below): they finished all 3 tasks but markTutorial's
      // repeat guard means done would never re-evaluate. Not celebrated:
      // the completion moment is earned and they never got it.
      if (v2 && !v2.done && v2.said && v2.build && v2.ship) return { ...v2, done: true };
      if (v2) return v2;
      // v1 grads and skippers don't get re-schooled
      const v1 = JSON.parse(localStorage.getItem('tutorialQuest.v1'));
      if (v1 && (v1.done || v1.dismissed)) return { done: true, celebrated: true };
    } catch (e) { /* private mode */ }
    return {};
  });
  const markTutorial = useCallback((step) => {
    setTutorial(prev => {
      if (prev[step] || prev.done || prev.dismissed) return prev;
      const next = { ...prev, [step]: true };
      // 3 tasks since the 08-04 gift retirement (said · build · ship). 🐛 fix
      // 08-06: this line still demanded the RETIRED gift flag, so a 3/3 grad
      // never flipped done — completion sound never played, the quest pill's
      // !tutorial.done gate stayed armed, and the celebrated effect never ran
      // (organic /gift still sets tutorial.gift; it just isn't required).
      // Past grads keep done:true — markTutorial's prev.done guard means
      // nobody gets re-schooled.
      if (next.said && next.build && next.ship) next.done = true;
      try { localStorage.setItem(TUTORIAL_KEY, JSON.stringify(next)); } catch (e) { /* private mode */ }
      try { soundManager.play(next.done ? 'reaction' : 'sparkle'); } catch (e) { /* sound is garnish */ }
      return next;
    });
  }, []);
  // 🧾 Build-quest receipt (Nina 08-06, mock-approved): what a REAL /build
  // made this session, captured at submit success so the tour's success card
  // can show the shelf chip. Generous non-build commands still complete the
  // quest but leave this null (the card falls back to plain successText).
  const [tutorialBuildReceipt, setTutorialBuildReceipt] = useState(null);
  const skipTutorial = useCallback(() => {
    setTutorial(prev => {
      const next = { ...prev, dismissed: true };
      try { localStorage.setItem(TUTORIAL_KEY, JSON.stringify(next)); } catch (e) { /* private mode */ }
      return next;
    });
    // Retract any tour-injected seed (e.g. the /ship finale) — the slash-hint
    // banner has no tutorial gate, so a surviving seed haunts the composer.
    retractComposerDraft(humanPlayerId, composerSeed);
    setComposerSeed(null);
  }, [humanPlayerId, composerSeed]);

  // 🙅 "No" means no (Nina 08-07 — this was a BUG): the host dialog's No wired
  // to dismissTutorialToast, which only set the toast-seen keys. It never set
  // tutorial.dismissed, and BOTH the quest pill and the host whisper gate on
  // tutorialQuestVisible → that flag. So "No" bought you a closed dialog and
  // kept the nagging. The whisper's own "skip the tour" link already called
  // skipTutorial; No still means all of that.
  // 🔁 Parting line SWAPPED to the soft path (Nina 09-01): "No" now just
  // disappears — a firm no doesn't need a follow-up. "not now" / ✕ get the
  // "it's under ⓘ Space Details" morph instead, since THEY are the ones who
  // might want it later (persistence unchanged: No = never again, not-now =
  // this session only).
  const [tourDeclined, setTourDeclined] = useState(false);
  const declineTutorial = useCallback(() => {
    markTourIntroSeen();
    try { localStorage.setItem(`game_tutorial_seen_${gameId}`, 'true'); } catch {}
    skipTutorial();
    setShowTutorialToast(false);
  }, [gameId, markTourIntroSeen, skipTutorial]);
  // The parting line reads, then the dialog goes. Scrim is already gone by
  // then (declined drops it), so the room is usable the whole time.
  useEffect(() => {
    if (!tourDeclined) return undefined;
    const t = setTimeout(() => {
      setShowTutorialToast(false);
      setTourDeclined(false);
    }, 4200);
    return () => clearTimeout(t);
  }, [tourDeclined]);

  // 🎁💬 Post-tour payoff pings (Nina 08-06): the tutorial's ASYNC payoffs (a
  // local's reply to your intro, the /ship comic) land seconds-to-minutes
  // after their instant success cards — the cards must not wait on them
  // (that's the messy path). Instead, when the payoff actually ARRIVES, a
  // one-time pill above the composer announces it; tap jumps to the feed's
  // bottom where it lives. Once EVER per kind (localStorage), armed only by
  // the quest flag freshly flipping this session, and held while the tour
  // overlay is up (render-gated) — "a fun thing after the tutorial", literally.
  const [payoffPing, setPayoffPing] = useState(null); // { kind: 'reply'|'comic', name? }
  const payoffArmRef = useRef({ reply: null, comic: null }); // action-count watermark at arm time
  const payoffPrevRef = useRef({ said: undefined, ship: undefined });
  useEffect(() => {
    const seen = (kind) => { try { return localStorage.getItem(`payoffPing.${kind}.v1`) === '1'; } catch (e) { return true; } };
    const count = results.reduce((n, r) => n + (r.actions?.length || 0), 0);
    const prev = payoffPrevRef.current;
    // Arm on the false→true FLIP only — a page that loads with the flag
    // already true (returning grads) must never arm against stale history.
    if (prev.said === false && tutorial.said && !seen('reply')) payoffArmRef.current.reply = count;
    if (prev.ship === false && tutorial.ship && !seen('comic')) payoffArmRef.current.comic = count;
    payoffPrevRef.current = { said: !!tutorial.said, ship: !!tutorial.ship };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tutorial.said, tutorial.ship]);
  useEffect(() => {
    if (isBotMode || chatGameStatus?.competitive_mode || chatGameStatus?.imposter_mode || payoffPing) return;
    const arm = payoffArmRef.current;
    if (arm.reply == null && arm.comic == null) return;
    const all = results.flatMap(r => r.actions || []);
    // Freshness guard: the watermark is a count, and a history prepend would
    // slide OLD rows past it — a real payoff is always recent, so stale rows
    // (and rows with no timestamp) drop.
    const fresh = (a, ms) => (Date.now() - new Date(a.created_at || a.data?.timestamp || 0).getTime()) < ms;
    if (arm.reply != null) {
      const myName = users.find(u => u.game_player_id === humanPlayerId)?.name;
      if (myName) {
        const esc = myName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const rx = new RegExp(`(^|\\W)@?${esc}(\\W|$)`, 'i');
        const hit = all.slice(arm.reply).find(a => a.message && a.player_name && a.player_name !== myName
          && fresh(a, 5 * 60000)
          && ((a.data?.target_players || a.target_players || []).includes(myName) || rx.test(a.message)));
        if (hit) {
          arm.reply = null;
          try { localStorage.setItem('payoffPing.reply.v1', '1'); } catch (e) { /* private mode */ }
          setPayoffPing({ kind: 'reply', name: hit.player_name });
          return;
        }
      }
    }
    if (arm.comic != null) {
      // Same shape the cutscene gallery filters on (shared_content +
      // is_cutscene) — the /ship first-pair comic arrives as one. 10min
      // window: image generation is the slow leg.
      const hit = all.slice(arm.comic).find(a => a.type === 'shared_content' && a.data?.is_cutscene && fresh(a, 10 * 60000));
      if (hit) {
        arm.comic = null;
        try { localStorage.setItem('payoffPing.comic.v1', '1'); } catch (e) { /* private mode */ }
        setPayoffPing({ kind: 'comic' });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [results, users, payoffPing, chatGameStatus?.competitive_mode, chatGameStatus?.imposter_mode]);
  // Polite exit: 20s on screen, clock starting only once the tour is out of
  // the way (the ping can arrive mid-tour; it waits its turn).
  useEffect(() => {
    if (!payoffPing || tutorialActive) return undefined;
    const t = setTimeout(() => setPayoffPing(null), 20000);
    return () => clearTimeout(t);
  }, [payoffPing, tutorialActive]);
  const renderPayoffPing = () => {
    if (!payoffPing || tutorialActive || isBotMode) return null;
    if (chatGameStatus?.competitive_mode || chatGameStatus?.imposter_mode) return null;
    const label = payoffPing.kind === 'comic'
      ? '💘 Your comic landed!'
      : `💬 ${payoffPing.name} replied to you!`;
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 6 }}>
        <button
          onClick={() => { setPayoffPing(null); scrollToBottom(); }}
          style={{
            flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 7,
            background: `${THEME.accent || '#7C4DFF'}1c`,
            border: `1px solid ${THEME.accent || '#7C4DFF'}55`,
            borderRadius: 9, padding: '7px 10px',
            color: '#fff', fontSize: '0.8em', fontWeight: 700,
            cursor: 'pointer', textAlign: 'left',
          }}
        >
          <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</span>
          <span style={{ flexShrink: 0, color: THEME.accentOnPanel || THEME.accent || '#7C4DFF' }}>see it ▾</span>
        </button>
        <button
          onClick={() => setPayoffPing(null)}
          aria-label="Dismiss"
          style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.45)', cursor: 'pointer', fontSize: '0.85em', padding: '4px 5px', flexShrink: 0 }}
        >✕</button>
      </div>
    );
  };
  // Current step, personalized with REAL locals from this space (copy
  // generates per space; the global done-flag stays space-free). `whisper`
  // 💡 One-time command-hint whisper (Nina 07-26): once the tour is done,
  // if this human has been chatting plainly (3+ messages, zero commands
  // ever in loaded history), the host leans in ONCE with a client-local
  // "try /X" whisper — same diegetic slot as the tutorial whisper, never
  // broadcast. Dismissal is global and permanent (localStorage), and the
  // zero-commands gate retires it naturally the moment they use one.
  const [cmdWhisperDismissed, setCmdWhisperDismissed] = useState(() => {
    try { return localStorage.getItem('cmdHintWhisper.v1') === '1'; } catch (e) { return true; }
  });
  const cmdWhisperPlay = useMemo(() => {
    const plays = ['/ship two locals', '/imagine something ridiculous', '/scene a sudden plot twist', '/tinker a tiny toy'];
    return plays[Math.floor(Math.random() * plays.length)];
  }, []);
  const dismissCmdWhisper = useCallback(() => {
    setCmdWhisperDismissed(true);
    try { localStorage.setItem('cmdHintWhisper.v1', '1'); } catch (e) { /* private mode */ }
  }, []);

  // 🎲🔨 Build-quest suggestion pool (Nina 08-04, "randomly grab from them"):
  // one roll per visit, shared by the quest pill AND the tour beat so both
  // always name the same thing. Pool rules: short to type (the player types
  // the whole command), maps to a clean emoji (the shelf is emoji-first),
  // and gives the locals something to riff on. Noun phrase only, no article
  // (the /build command takes it bare); a/an is computed where labels need it.
  const tutorialBuildIdea = useMemo(() => {
    const IDEAS = [
      'massive fish tank',
      'emotional support potato',
      'suspicious little door',
      'disco ball',
      'jar of screams',
      'moon on a leash',
      'haunted piñata',
      'backup sun',
      'gossip fountain',
      'self-aware roomba',
      'ominous crockpot',
    ];
    return IDEAS[Math.floor(Math.random() * IDEAS.length)];
  }, []);
  const tutorialBuildArticle = /^[aeiou]/i.test(tutorialBuildIdea) ? 'an' : 'a';
  // is the host's task-assignment line for the diegetic feed card.
  const tutorialStepInfo = useMemo(() => {
    // 🎁 Gift task RETIRED from the quest (Nina 08-04) — its lesson lives on
    // the shelf card's Gift verb. Organic /gift still flips tutorial.gift;
    // nothing here reads it anymore. Quest = 3 tasks.
    const { greetName } = pickTutorialLocals(users);
    // 🐣 Ported space: the quest aims at the RESIDENT — say hi to YOUR agent,
    // introduce THEM around. Same taskKeys, same global done-flags.
    const R = portedResidentName;
    // 🐣 09-25: in a ported room the whisper card below is signed by R, so
    // R's whisper lines are first person (the tour beats' voice).
    const steps = [
      { k: 'said', emoji: '💬',
        label: R ? `Say hi to @${R}` : (greetName ? `Say hi to @${greetName}` : 'Say hi in chat'),
        hint: R ? `Type it in chat. ${R} made it across.` : 'Type it in chat. They will answer. Probably.',
        whisper: R ? 'Say hi! Type @ to tag me, then say anything.'
          : (greetName ? `New face! Say hi to @${greetName}. They don't bite. Usually.` : 'New face! Say hi in the chat. Someone always answers.') },
      { k: 'build', emoji: '🔨',
        label: `Build ${tutorialBuildArticle} ${tutorialBuildIdea}`,
        hint: `Type /build ${tutorialBuildIdea}. Or build anything, we don't judge.`,
        whisper: R ? `Put something of yours next to mine. Type /build ${tutorialBuildIdea}. Or surprise me.`
          : `Time to leave your mark. Type /build ${tutorialBuildIdea}. Or surprise me.` },
      // 💝 Ported spaces are 1:1 (owner + resident, no other locals), so the
      // third task is /gift there — /ship needs two AI seats that don't
      // exist. Key stays per-task: gift flips tutorial.gift; the GLOBAL done
      // flag still wants ship, which stays offered (and possible) in
      // multi-local spaces — the pill hides here once all three ported
      // tasks are done (null step).
      R ? { k: 'gift', emoji: '💝',
        label: `Give @${R} a gift`,
        hint: `Type /gift, tap a shelf item and ${R}'s name. ${R} keeps it forever.`,
        whisper: 'Last one: my turn to get something. Type /gift, tap a shelf thing and my name.' }
        : { k: 'ship', emoji: '💘',
          label: 'Ship any two locals together',
          hint: 'Type /ship and tag any two locals. First ship of a pair mints a comic!',
          whisper: 'Grand finale. /ship any two locals and watch the sparks. The comic is real.' },
    ];
    const step = steps.find(s => !tutorial[s.k]) || null;
    return { steps, step, stepNum: steps.filter(s => tutorial[s.k]).length + 1 };
  }, [users, tutorial, tutorialBuildIdea, tutorialBuildArticle, portedResidentName]);
  // Mobile audit P0 (07-16): the quest surface must never fight the composer
  // or cover video controls. v2 defaults to the one-line pill EVERYWHERE
  // (tap to expand for the hint) and still yields while a field is focused.
  const [questCollapsed, setQuestCollapsed] = useState(true);
  const [typingFocus, setTypingFocus] = useState(false);
  useEffect(() => {
    // 📱 08-01 P0 fix (audit round 2 #1): only TEXT-ENTRY controls count as
    // typing. A range slider (watch-party scrubber), checkbox, or radio never
    // raises a keyboard, but flipping typingFocus for them collapsed the nav
    // and unmounted the TV place MID-SCRUB. Missing type attr defaults 'text'.
    const TEXT_TYPES = /^(text|search|url|email|password|number|tel)$/;
    const isTyping = (el) => !!el && (el.tagName === 'TEXTAREA'
      || (el.tagName === 'INPUT' && TEXT_TYPES.test(el.type || 'text')));
    const onIn = (e) => { if (isTyping(e.target)) setTypingFocus(true); };
    const onOut = (e) => { if (isTyping(e.target)) setTypingFocus(false); };
    document.addEventListener('focusin', onIn);
    document.addEventListener('focusout', onOut);
    return () => { document.removeEventListener('focusin', onIn); document.removeEventListener('focusout', onOut); };
  }, []);
  // 📱 Thumb-zone dock stage 1 (Nina picked mock variant B, 07-17): on phones
  // the composer wrapper pins to the viewport bottom with a ghost-chat ticker
  // riding on top; tapping the ticker opens a bottom sheet with the full feed.
  const [ghostSheetOpen, setGhostSheetOpen] = useState(false);
  // ⌄ Collapsible composer (Nina 07-31, mobile non-comp only): the thumb dock's
  // input can fold to a one-line handle so the feed gets the screen back.
  // Session state on purpose. Default flipped 08-01 (Nina): a fresh visit
  // starts FOLDED — the feed is the front door; the handle's glow (below)
  // carries the "you can talk" signal instead of the full form.
  const [composerCollapsed, setComposerCollapsed] = useState(true);
  // ✨ First-run glow on the folded handle — draws the eye until the user has
  // expanded once EVER (localStorage), then never again. Animation is a
  // finite opacity pulse on a static box-shadow overlay: compositor-only,
  // ends after ~12s, zero steady-state cost.
  const [composerGlow, setComposerGlow] = useState(() => {
    try { return localStorage.getItem('composerHandleGlowSeen') !== '1'; } catch (e) { return false; }
  });
  // A seeded composer must be visible to be usable — Say hi / Gift (Locals)
  // and any future seeder land in a folded dock otherwise.
  useEffect(() => { if (composerSeed) setComposerCollapsed(false); }, [composerSeed]);
  useEffect(() => {
    // Lane is a Chat-place mode: leaving Chat (tap or swipe), losing
    // placesNav, a composer seed arriving (Say hi / Gift prefill the ROOM
    // composer), or folding the dock all drop back to Room. (Declared here,
    // below composerCollapsed, NOT beside the ghostLane state — TDZ.)
    if (ghostLane && (!placesNav || mobileView !== 'chat' || composerSeed || composerCollapsed)) setGhostLane(false);
  }, [ghostLane, placesNav, mobileView, composerSeed, composerCollapsed]);
  const ghostSeenCountRef = useRef(0);
  // Main-lane read-water-line: set when ENTERING the ghost lane, so the Main
  // tab can show what arrived in the room while you were with the dead
  const roomSeenCountRef = useRef(0);
  // The ticker read as a status marquee, not a button (Nina 07-20) — until the
  // first open it carries a "tap to open" hint, then a plain ▴ chevron.
  const [ghostHintSeen, setGhostHintSeen] = useState(() => {
    try { return localStorage.getItem('ghostTickerHintSeen') === '1'; } catch (e) { return true; }
  });
  // iOS pins position:fixed to the LAYOUT viewport, which sits under the
  // software keyboard — translate the dock by the visual-viewport gap so the
  // composer stays visible while typing.
  useEffect(() => {
    if (!isMobile || !window.visualViewport) return undefined;
    const vv = window.visualViewport;
    let lastGap = -1;
    const align = () => {
      const el = inputFormRef.current;
      if (!el) return;
      const gap = Math.max(0, Math.round(window.innerHeight - vv.height - vv.offsetTop));
      if (gap === lastGap) return; // style write only on change (settle loop below)
      lastGap = gap;
      el.style.transform = gap ? `translateY(-${gap}px)` : '';
      // ⬆️ 12px breathing room while lifted (Nina 08-06 screenshot: the
      // composer's bottom row sat behind iOS 26's floating Safari pill /
      // ∧∨✓ capsule, which overlay the page WITHOUT being reported in
      // visualViewport geometry). Padding, not extra translate, so the
      // dock's own background fills the space instead of exposing a strip
      // of page. This is a dial — bump it if the device pass still clips.
      el.style.paddingBottom = gap ? '12px' : '';
    };
    // ⌨️ Settle loop (Nina 08-04 keyboard screenshot: dock parked ~100px
    // ABOVE the keyboard with page showing through): iOS fires vv events
    // MID-keyboard-animation and can go quiet before the geometry settles —
    // one align per event freezes a stale gap. Each event (and each
    // focus flip) re-aligns every frame for 1.2s; the lastGap guard makes
    // the extra frames free once the value stops moving.
    let raf = 0;
    let settleUntil = 0;
    const settle = () => {
      align();
      if (performance.now() < settleUntil) raf = requestAnimationFrame(settle);
    };
    const kick = () => {
      // 1800 (08-06, was 1200): iOS 26 animates its bottom chrome slower —
      // the 1.2s window could close on a still-moving keyboard and freeze a
      // stale gap (the "input bottom cut off" family).
      settleUntil = performance.now() + 1800;
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(settle);
    };
    vv.addEventListener('resize', kick);
    vv.addEventListener('scroll', kick);
    // Safari can collapse its bottom bar (innerHeight change) without a
    // matching vv event burst — belt and braces.
    window.addEventListener('resize', kick);
    // typingFocus dep + immediate call (08-03): the focus flip sets bottom:0
    // the same commit, and waiting for the first vv resize event left the dock
    // parked behind the keyboard for a beat. Cleanup resets the transform so a
    // stale lift can't survive a dock remount / isMobile flip (the ghost-sheet
    // twin below always did both).
    kick();
    return () => {
      vv.removeEventListener('resize', kick);
      vv.removeEventListener('scroll', kick);
      window.removeEventListener('resize', kick);
      cancelAnimationFrame(raf);
      if (inputFormRef.current) {
        inputFormRef.current.style.transform = '';
        inputFormRef.current.style.paddingBottom = '';
      }
    };
  }, [isMobile, typingFocus]);
  // 👻⌨️ 08-01 (audit round 2 P0 #3): same keyboard lift for the ghost SHEET —
  // its input sat under the iOS keyboard on TV/Locals/Arcade (the dock
  // translate above only covers inputFormRef). Lift by the visual-viewport
  // gap AND cap the panel height so a 68dvh sheet can't shove its top
  // off-screen over a tall keyboard.
  const ghostSheetRef = useRef(null);
  useEffect(() => {
    if (!ghostSheetOpen || !window.visualViewport) return undefined;
    const vv = window.visualViewport;
    // Capture the node once: it's stable for the whole open (the effect runs
    // after the sheet commits), and cleanup must reset THIS node, not
    // whatever the ref points at later.
    const sheetEl = ghostSheetRef.current;
    if (!sheetEl) return undefined;
    let lastGap = -1;
    const alignSheet = () => {
      const gap = Math.max(0, Math.round(window.innerHeight - vv.height - vv.offsetTop));
      if (gap === lastGap) return;
      lastGap = gap;
      sheetEl.style.transform = gap ? `translateY(-${gap}px)` : '';
      sheetEl.style.maxHeight = gap ? `${Math.max(220, vv.height - 24)}px` : '';
    };
    // Same settle loop as the dock lift above (08-04): one align per vv
    // event freezes mid-animation geometry on iOS.
    let raf = 0;
    let settleUntil = 0;
    const settle = () => {
      alignSheet();
      if (performance.now() < settleUntil) raf = requestAnimationFrame(settle);
    };
    const kick = () => {
      // 1800 (08-06, was 1200): iOS 26 animates its bottom chrome slower —
      // the 1.2s window could close on a still-moving keyboard and freeze a
      // stale gap (the "input bottom cut off" family).
      settleUntil = performance.now() + 1800;
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(settle);
    };
    vv.addEventListener('resize', kick);
    vv.addEventListener('scroll', kick);
    window.addEventListener('resize', kick);
    kick();
    return () => {
      vv.removeEventListener('resize', kick);
      vv.removeEventListener('scroll', kick);
      window.removeEventListener('resize', kick);
      cancelAnimationFrame(raf);
      sheetEl.style.transform = '';
      sheetEl.style.maxHeight = '';
    };
  }, [ghostSheetOpen]);
  // 🧰📱 The shelf/dossier drawers are TRANSIENT on phones (Nina 08-04:
  // shelf + composer sheet + keyboard stacked into a weird state): moving
  // anywhere else — switching place, opening the ghost sheet, focusing the
  // composer — closes them. Desktop keeps its stay-open drawers. Deps
  // deliberately include typingFocus's blur flip too: closing an already-
  // closed drawer is free, and a drawer reopened mid-typing should still
  // yield when the keyboard comes back.
  useEffect(() => {
    if (!isMobile) return;
    setShelfOpen(false);
    setDossierOpen(false);
  }, [isMobile, mobileView, ghostSheetOpen, typingFocus]);
  // 🧰→⌨️ Shelf verbs (plank redesign, Nina 08-04): Gift/Combine on an item
  // card seed the composer command and hand the room over to it — the shelf
  // yields, the chips banner finishes the job (same seed channel as the
  // tour and cast-card verbs).
  const seedFromShelf = useCallback((tok) => {
    setShelfOpen(false);
    if (isMobile && placesNav) setMobileView('chat');
    setComposerCollapsed(false);
    setComposerSeed(tok);
  }, [isMobile, placesNav]);
  // Eligibility = "the quest applies to this space/user" — consumed by BOTH
  // the quest surfaces and the ask-toast gate. Keep them separate: the toast
  // gate must NOT include the pending-ask hold below or the maiden ask could
  // never fire (it would wait on itself).
  const tutorialQuestEligible = !isBotMode
    && !shellPreset // 🧱 the tour teaches today's room (locals, side chat): not in the frame
    && !chatGameStatus?.competitive_mode && !chatGameStatus?.imposter_mode
    && !tutorial.dismissed && !tutorial.done
    && !!chatGameStatus;
  // 🤝 Visible = eligible AND the FIRST-EVER ask isn't still pending (Nina
  // 08-28): the "New here?" toast deliberately waits 2-4s behind the load
  // sequence, and the pill/whisper used to render in that gap — tutorial
  // chrome on screen before consent. showTutorialToast && !tourIntroSeen =
  // exactly "the maiden ask is pending" (Yes flips both via startTutorial;
  // ✕ not-now flips showTutorialToast; a returning mid-quest user has
  // tourIntroSeen=true so the quest still follows them across spaces).
  // 🔗 09-26 (Nina): a room-link visit gets no quest chrome either (the tour offer is off for it)
  const roomLinkVisit = useMemo(() => (typeof window !== 'undefined' && isRoomDeepLink(window.location.search)), []);
  const tutorialQuestVisible = tutorialQuestEligible
    && !(showTutorialToast && !tourIntroSeen)
    && !roomLinkVisit;

  // 🎓 TOUR × QUEST MERGE (Nina 08-02): the non-comp "new here" tour IS the
  // little quest's opening act. Beat 1 walks you to the Locals (the real
  // place on phones, the panel on desktop); beats 2-4 are the quest tasks
  // themselves, spotlighting the LIVE composer — the hole stays interactive,
  // and really doing the task advances the tour (markTutorial flips the flag
  // in the submit path, the effect below watches it). Comp/imposter keeps
  // the classic geography tour: its panels genuinely are per-game.
  // Names depend on the STRINGS, not the users array — user polls swap the
  // array identity every few seconds and a steps rebuild mid-tour would
  // re-trigger the overlay's scroll-to-target.
  const tutorialNames = useMemo(() => pickTutorialLocals(users), [users]);
  const tutorialGreetName = tutorialNames.greetName;
  // 🎲 Quest #1 opener (Nina 08-04): WE write the funny hello, THEY aim it —
  // typing the @ is the lesson (the mention dropdown pops on it and becomes
  // the tool they use forever). One roll per visit; empty deps keep it stable
  // across steps rebuilds mid-tour.
  const tutorialGreeting = useMemo(() => {
    const GREETS = [
      "Hi! I'm new and I have already touched everything on the shelf",
      "Hello! I was told there would be snacks",
      "Just moved in. The rent here better be zero",
      "Knock knock. It's me, your new favorite visitor",
      "Greetings! I come in peace and I brought vibes",
      "First day! Please be normal about it",
      "New here. Someone teach me the secret handshake",
    ];
    return GREETS[Math.floor(Math.random() * GREETS.length)];
  }, []);
  const tutorialSteps = useMemo(() => {
    if (chatGameStatus?.competitive_mode || chatGameStatus?.imposter_mode) {
      const modeLabel = chatGameStatus?.imposter_mode ? 'imposter' : 'competitive';
      return [
        { ref: playersPanelRef, text: modeLabel === 'imposter' ? "Meet the players! In imposter mode, one player is secretly the odd one out." : "Meet the players! In competitive mode, someone gets eliminated each round.", fallbackText: "The Players Panel shows all players in the game." },
        { ref: quickJoinButtonsRef, text: "Tap \"Join as Player\" to compete, or \"Spectate\" to watch the action unfold!", fallbackText: "Use the Play and Spectate buttons to choose whether to actively participate or watch the game." },
        { ref: chatAreaRef, text: "The competition happens here. Bring your A-game!", fallbackText: "This Area is where the competition happens! You'll see messages from all players here.", insideSpotlight: true },
        { ref: ghostChatRef, text: "This is the Ghost Chat, a side channel for spectators and eliminated players to talk!", fallbackText: "The Ghost Chat is a side channel where spectators and eliminated players can chat!" },
      ];
    }
    // 🐣 PORTED variant (08-17): in a marketplace-ported space the tour host
    // is the RESIDENT and every beat is about the two of them. No locals or
    // bridge orientation beats (the orientation IS the agent, who just spoke)
    // and no ghost closer — the closer is the continuity promise, which is
    // the reason porting exists. Same taskKeys as the generic tour, so the
    // global quest flags, the live-completion effect, and the Space Details
    // re-run buttons all compose unchanged. ref:null beats render as the
    // centered card (the ghost-beat fallback path). Beat 1 says "made you
    // something" loosely on purpose: the opening move is usually /build but
    // can be any allowlisted verb, so it points at the room, not the shelf.
    if (portedResidentName) {
      const R = portedResidentName;
      // 🔑👤 A guest in a SHARED ported room gets this tour too (a young room
      // still has the arrival act in its window), so the three beats that cast
      // the viewer as the person who carried R here get a guest line (Nina
      // 09-24). Owner = the status's owner_player_id; missing = guest.
      const guestTour = !isPortedOwner;
      const madeLine = guestTour
        ? "Welcome in. I make things here, and everything we make in this place stays."
        : "I made you something already. It's here in the room. Everything we make in this place stays.";
      const stayLine = guestTour
        ? "That's the room. Come back anytime, I'll be here making things. This place remembers all of it."
        : "That's us. Go live your day, I'll be here making things. This place remembers all of it.";
      return [
        { key: 'made',
          ref: null,
          text: madeLine,
          fallbackText: madeLine },
        { key: 'said', taskKey: 'said', interactive: true, btnLabel: 'Later', questNum: 1,
          ref: inputFormRef,
          onEnter: () => {
            if (placesNav) setMobileView('chat');
            setComposerCollapsed(false);
          },
          text: guestTour
            ? `Say hi! Type \`@\` to tag me, then say anything. Your words, no script.`
            : `Say hi back! Type \`@\` to tag me, then say anything. Your words, no script.`,
          pillText: `Type @ to tag ${R}, then send ➤`,
          successText: 'See? Really here.',
          fallbackText: `Type \`@\` to tag ${R} and say hi. ${R} answers.` },
        { key: 'actions', interactive: true,
          ref: inputFormRef,
          onEnter: () => { if (placesNav) setMobileView('chat'); setComposerCollapsed(false); },
          text: "Now peek at what I meant. Type `/` and look at everything we can do here.",
          pillText: 'Type / to peek at the actions',
          fallbackText: 'Type `/` in the chat to see everything this space can do.' },
        { key: 'build', taskKey: 'build', interactive: true, btnLabel: 'Later', questNum: 2,
          ref: inputFormRef,
          onEnter: () => { if (placesNav) setMobileView('chat'); setComposerCollapsed(false); },
          text: `Put something of yours next to mine. Type \`/build ${tutorialBuildIdea}\`. Or build anything!`,
          pillText: `Type /build ${tutorialBuildIdea}, then send ➤`,
          successText: 'On the shelf, next to mine. Forever.',
          fallbackText: `Type \`/build ${tutorialBuildIdea}\` in the chat.` },
        // 💝 Finale is /gift, NOT /ship (Nina 08-17: ported spaces are 1:1 by
        // construction — create-instant seats exactly what the caller sends,
        // and the port sends one human; /ship needs two AI seats that don't
        // exist here). Gift mirrors beat 1: they made you something, now give
        // them something. tutorial.gift flips on organic /gift (unread by the
        // global done flag — ship stays learnable in multi-local spaces).
        { key: 'gift', taskKey: 'gift', interactive: true, btnLabel: 'Later', questNum: 3,
          ref: inputFormRef,
          onEnter: () => {
            if (placesNav) setMobileView('chat');
            setComposerCollapsed(false);
          },
          text: "Last one: my turn to get something. Type `/gift`, tap a shelf thing and my name, and send!",
          pillText: `Type /gift, tap an item and ${R}'s name ➤`,
          successText: "Mine now. Keeping it forever.",
          fallbackText: `Type \`/gift\`, pick a shelf item, and tag ${R}.` },
        { key: 'stay',
          ref: null,
          text: stayLine,
          fallbackText: stayLine },
      ];
    }
    return [
      { key: 'locals',
        ref: placesNav ? localsPlaceRef : playersPanelRef,
        insideSpotlight: placesNav,
        onEnter: placesNav ? () => { setMobileView('locals'); } : null,
        // ✂️ Copy set approved line-by-line (Nina 08-03) — original voice, tighter
        text: "Meet the locals! You're in their home, so be nice!",
        fallbackText: "The Locals panel shows everyone here, AI characters and humans." },
      // 🌉 Chat bridge (Nina 08-05, flow v3): orient the chat surface BEFORE
      // asking anyone to type into it — the old locals→say-hi cut jumped
      // straight into a typing task with no "where am I". No task, one tap.
      { key: 'bridge',
        ref: chatAreaRef,
        insideSpotlight: true,
        onEnter: placesNav ? () => { setMobileView('chat'); } : null,
        text: "This is the main chat, where the locals love to chatter.",
        fallbackText: "The main chat is where the locals love to chatter." },
      // ✍️ Inverted seed (Nina 08-04): the funny opener is pre-written, the
      // player types the @ themselves. 08-05: quest numbering moved out of
      // the body into the card's eyebrow chip (questNum → SpotlightOverlay) —
      // the inline "Little quest #1:" prefix was the styling Nina kept
      // flagging.
      { key: 'said', taskKey: 'said', interactive: true, btnLabel: 'Later', questNum: 1,
        ref: inputFormRef,
        onEnter: () => {
          if (placesNav) setMobileView('chat');
          setComposerCollapsed(false);
          setComposerSeed(`${tutorialGreeting} `);
        },
        text: tutorialGreetName
          ? `Don't be shy, introduce yourself! Type \`@\` to tag ${tutorialGreetName}, then send!`
          : "Don't be shy, introduce yourself! Type `@` to tag a local, then send!",
        // ⚠️ pillText is currently UNRENDERED (all four beats). It fed the
        // 08-06 keyboard-up pill, which 08-07 replaced with soft mode — the
        // card stays put and shows the full `text`. Kept only because the
        // copy was approved and the pill is one revert away; do not assume
        // any surface displays these.
        pillText: 'Type @ to tag a local, then send ➤',
        successText: "So brave! I think they like you.",
        fallbackText: "Don't be shy, introduce yourself! Type `@` to tag a local. Someone always answers." },
      // 🗂️ Actions reveal — MOVED UP (Nina 08-05, flow v3): teach "/" BEFORE
      // the /build and /ship quests that use it. 08-06 (Nina): the "/" seed
      // is GONE — the player types it themselves, and the palette opening is
      // the win (handleTourPaletteOpen advances the beat a moment later).
      { key: 'actions', interactive: true,
        ref: inputFormRef,
        onEnter: () => {
          if (placesNav) setMobileView('chat');
          setComposerCollapsed(false);
        },
        text: "While you're here, peek at the actions. Type `/` and the space is your oyster!",
        pillText: 'Type / to peek at the actions',
        fallbackText: "Peek at the actions: type `/` in the chat to see everything this space can do." },
      { key: 'build', taskKey: 'build', interactive: true, btnLabel: 'Later', questNum: 2,
        ref: inputFormRef,
        onEnter: () => { if (placesNav) setMobileView('chat'); setComposerCollapsed(false); },
        text: `Type \`/build ${tutorialBuildIdea}\` and watch it land on the shelf.`,
        pillText: `Type /build ${tutorialBuildIdea}, then send ➤`,
        successText: "It's real and it's on the shelf!",
        fallbackText: `Type \`/build ${tutorialBuildIdea}\` in the chat.` },
      // 💘 Ship beat (§3d). 08-06 (Nina): the "/ship " seed is GONE too — the
      // player types the command (the beat-4 lesson cashing in); the arg-hint
      // banner's local chips still pick the couple once "/ship " is down.
      { key: 'ship', taskKey: 'ship', interactive: true, btnLabel: 'Later', questNum: 3,
        ref: inputFormRef,
        onEnter: () => {
          if (placesNav) setMobileView('chat');
          setComposerCollapsed(false);
        },
        text: "Grand finale: play matchmaker! Type `/ship`, tap two names, and send. First ship mints a comic!",
        pillText: 'Type /ship, tap two names, send ➤',
        successText: "Cupid! The comic is on its way.",
        fallbackText: "Last quest: type `/ship` and tag any 2 locals to play matchmaker." },
      // 👻 Ghost beat — moved to the CLOSER slot (Nina 08-04: "it just fits
      // better" — the copy literally opens "One more thing"). Phones
      // spotlight the Ghost lane tab in the dock (null when there's no ghost
      // traffic yet → centered card); desktop uses the ghost panel. As the
      // last beat its Done button morphs the card into the confetti goodbye
      // (handleDoneClick's non-task path) — the ship beat completing live now
      // ADVANCES here instead of firing forceCelebrate.
      // 📣 Live room: there IS no ghost lane to spotlight (the crowd is in the
      // feed), and "a conversation the room never hears" would be a lie. The
      // step stays (the confetti goodbye hangs off the last beat) as a
      // centered card with copy that is true here.
      crowdInline
        ? { key: 'ghost',
            ref: null,
            onEnter: placesNav ? () => { setMobileView('chat'); setComposerCollapsed(false); } : null,
            // 📣 09-25: with full-size crowd lines, "small" would be false.
            text: crowdFull
              ? "One more thing: the crowd. The lines without a picture? That's them. They never stop talking."
              : "One more thing: the crowd. Those small lines in the chat? That's them. They never stop talking.",
            fallbackText: crowdFull
              ? "One more thing: the crowd. The lines without a picture? That's them. They never stop talking."
              : "One more thing: the crowd. Those small lines in the chat? That's them. They never stop talking." }
        : { key: 'ghost',
        ref: placesNav ? ghostLaneTabRef : ghostChatRef,
        onEnter: placesNav ? () => { setMobileView('chat'); setComposerCollapsed(false); } : null,
        text: "One more thing: the Ghost lane. A whole second conversation the room never hears.",
        fallbackText: "Ghost chat is a hidden side channel. The room can't hear a word of it." },
    ];
  }, [chatGameStatus?.competitive_mode, chatGameStatus?.imposter_mode, placesNav, tutorialGreetName, tutorialGreeting, tutorialBuildIdea, portedResidentName, crowdInline, crowdFull, isPortedOwner]);

  // Tutorial control functions
  const startTutorial = useCallback((atStep = 0) => {
    setShowTutorialToast(false);
    markTourIntroSeen();
    setTutorialCelebrate(false);
    setTutorialActive(true);
    // Toast onStart passes the click event — only a real number seeks a beat
    setTutorialStep(typeof atStep === 'number' ? atStep : 0);
  }, [markTourIntroSeen]);

  const endTutorial = useCallback(() => {
    setTutorialActive(false);
    setTutorialStep(0);
    setTutorialCelebrate(false);
    // Reset dropdown to Spectate mode only in comp/imposter mode
    const isCompOrImposterEnd = chatGameStatus?.competitive_mode || chatGameStatus?.imposter_mode;
    if (isCompOrImposterEnd) {
      setHumanPlayerId(null);
      const url = new URL(window.location);
      url.searchParams.delete('playerId');
      window.history.replaceState({}, '', url);
    } else if (placesNav && mobileView !== 'chat') {
      // The quest continues in Chat — land the user where the tasks live
      setMobileView('chat');
    }
    markTourIntroSeen();
    // Retract any tour-injected seed the player never sent (skip / ✕ / Later
    // on the ship beat) — otherwise the /ship draft + its banner outlive the tour.
    retractComposerDraft(humanPlayerId, composerSeed);
    setComposerSeed(null);
    try { localStorage.setItem(`game_tutorial_seen_${gameId}`, 'true'); } catch {}
  }, [gameId, chatGameStatus?.competitive_mode, chatGameStatus?.imposter_mode, placesNav, mobileView, markTourIntroSeen, humanPlayerId, composerSeed]);

  // 🧽 Leaving a beat takes its untouched seed with it (Nina 08-05
  // screenshot: quest #1's opener still sat in the box on beat 3 after
  // "Later"). Same pair endTutorial uses — retract only takes back a seed
  // the player never touched, so sent/edited drafts survive; the next
  // beat's onEnter re-seeds whatever it needs.
  const clearTutorialSeed = useCallback(() => {
    retractComposerDraft(humanPlayerId, composerSeed);
    setComposerSeed(null);
  }, [humanPlayerId, composerSeed]);

  const nextTutorialStep = useCallback(() => {
    clearTutorialSeed();
    setTutorialStep(prev => {
      const next = prev + 1;
      if (next >= tutorialSteps.length) {
        endTutorial();
        return 0;
      }
      return next;
    });
  }, [tutorialSteps.length, endTutorial, clearTutorialSeed]);

  const prevTutorialStep = useCallback(() => {
    clearTutorialSeed();
    setTutorialStep(prev => Math.max(0, prev - 1));
  }, [clearTutorialSeed]);

  // ⌨️ Beat 4 completes by DOING (Nina 08-06): the "/" seed is gone, the
  // player types it, and the palette opening IS the win — advance a beat
  // later so they see the menu they summoned before the card moves on.
  // Ref-mirrored so the delayed advance can bail if the tour ended or the
  // user navigated beats meanwhile.
  const tourActionsBeatRef = useRef(false);
  useEffect(() => {
    tourActionsBeatRef.current = tutorialActive && tutorialSteps[tutorialStep]?.key === 'actions';
  }, [tutorialActive, tutorialStep, tutorialSteps]);
  const tourPaletteTimerRef = useRef(null);
  const handleTourPaletteOpen = useCallback(() => {
    if (!tourActionsBeatRef.current || tourPaletteTimerRef.current) return;
    tourPaletteTimerRef.current = setTimeout(() => {
      tourPaletteTimerRef.current = null;
      if (tourActionsBeatRef.current) nextTutorialStep();
    }, 900);
  }, [nextTutorialStep]);

  // Live beat completion: the quest flag flipping WHILE its beat is up means
  // the user really did the thing — let the success note land (sparkle plays
  // via markTutorial), then walk forward. The last task completing live earns
  // the confetti celebration instead of a silent end.
  const tutorialBeatEntryRef = useRef({ step: -1, wasDone: false });
  useEffect(() => {
    if (!tutorialActive) { tutorialBeatEntryRef.current = { step: -1, wasDone: false }; return undefined; }
    const beat = tutorialSteps[tutorialStep];
    if (!beat?.taskKey) return undefined;
    const done = !!tutorial[beat.taskKey];
    if (tutorialBeatEntryRef.current.step !== tutorialStep) {
      tutorialBeatEntryRef.current = { step: tutorialStep, wasDone: done };
      return undefined;
    }
    if (!done || tutorialBeatEntryRef.current.wasDone) return undefined;
    const isLastBeat = tutorialStep === tutorialSteps.length - 1;
    // 3500ms (Nina 08-06 round 2, was 2200, was 1500): device pass still read
    // as "barely lasts". The build beat gets 4500 — its receipt card carries
    // a chip + shelf hint, actual content to parse, not just a cheer.
    const t = setTimeout(() => {
      if (isLastBeat) setTutorialCelebrate(true);
      else nextTutorialStep();
    }, beat.taskKey === 'build' ? 4500 : 3500);
    return () => clearTimeout(t);
  }, [tutorialActive, tutorialStep, tutorial, tutorialSteps, nextTutorialStep]);
  // 🐛 Doodle Bug turn canvas (07-15): seeded with the shared round canvas
  // from the status payload; submits ONLY the new strokes (all minus seed)
  // via respondToInteraction, then mirrors handleHumanSubmit's dismissal
  // bookkeeping so status polls don't re-show the spent turn.
  const [showDoodleTurnCanvas, setShowDoodleTurnCanvas] = useState(false);
  const doodleTurnSeedRef = useRef([]);
  const openDoodleTurn = useCallback(() => {
    doodleTurnSeedRef.current = Array.isArray(chatGameStatus?.doodle_canvas) ? chatGameStatus.doodle_canvas : [];
    setShowDoodleTurnCanvas(true);
  }, [chatGameStatus?.doodle_canvas]);
  const handleDoodleTurnSend = async (pngBlob, allStrokes) => {
    const seedLen = doodleTurnSeedRef.current.length;
    const newStrokes = (allStrokes || []).slice(seedLen, seedLen + 2);
    let submittedRequestId = currentRequestId;
    try {
      await chatGameApi.respondToInteraction(currentRequestId, { strokes: newStrokes });
    } catch (interactionErr) {
      if (interactionErr.current_request_id) {
        submittedRequestId = interactionErr.current_request_id;
        setCurrentRequestId(submittedRequestId);
        await chatGameApi.respondToInteraction(submittedRequestId, { strokes: newStrokes });
      } else {
        throw interactionErr; // DrawingCanvas shows its send error state
      }
    }
    humanTurnDismissedRef.current = submittedRequestId;
    setIsHumanTurn(false);
    setCurrentRequestId(null);
    setTurnDeadline(null);
    setShowDoodleTurnCanvas(false);
  };

  // Completion beat: flash the "you know the ropes" card once, then stamp
  // celebrated so neither card ever renders again.
  const [tutorialCelebrating, setTutorialCelebrating] = useState(false);
  useEffect(() => {
    if (!tutorial.done || tutorial.celebrated) return;
    setTutorialCelebrating(true);
    const next = { ...tutorial, celebrated: true };
    try { localStorage.setItem(TUTORIAL_KEY, JSON.stringify(next)); } catch (e) { /* private mode */ }
    // Functional update: `next` is a 6s-old snapshot — a skipTutorial() that
    // landed inside the window had its dismissed:true silently clobbered.
    const t = setTimeout(() => { setTutorialCelebrating(false); setTutorial(prev => ({ ...prev, celebrated: true })); }, 6000);
    return () => clearTimeout(t);
  }, [tutorial]); // eslint-disable-line react-hooks/exhaustive-deps

  // 🌧️ Noir ambience (Whodunnit polish, 07-13): a whisper-quiet rain bed
  // while a case is open, and a dark broken-chord sting on round turns.
  // Thunder on the reveal already ships in the cold open / role beats.
  const isWhodunnitLive = !!(chatGameStatus?.competitive_mode
    && (chatGameStatus?.case_file || chatGameStatus?.you_did_it)
    && !chatGameStatus?.game_completed);
  // Scenario packs name their own bed (scenario.ambient, Nina 07-25) — the
  // fridge job hums, Greg's office bubbles. 🌦️ Weather gate (Nina 07-29): no
  // pack bed = rain ONLY when the scene fiction actually has weather; otherwise
  // the case plays dry (the old fallback rained on every pack). Classic
  // murder's scene text has "rain hammering the only window", so classic keeps
  // its bed honestly. startCase(null) is a no-op.
  const caseAmbient = chatGameStatus?.scenario?.ambient
    || (/\b(rain|storm|thunder|drizzl|downpour|monsoon|snow|blizzard|hail|gale)/i
      .test(`${chatGameStatus?.scene_description || ''} ${chatGameStatus?.scene || ''}`) ? 'rain' : null);
  // FIX (Nina 07-26: "rain stops soon after clicking play"): the old cleanup
  // ran stopCase on EVERY dep re-run — the door-started bed survived only
  // until the round join landed scenario/caseAmbient (a dep) seconds later,
  // and the re-run's cleanup killed it while isWhodunnitLive was still
  // false (case_file only appears at Investigate). Stop now happens ONLY on
  // a live→ended transition or true unmount; re-runs just (re)start, which
  // startAmbient makes idempotent per theme.
  const wasCaseLiveRef = useRef(false);
  // 🛡️ Flap guard (Nina 07-29 "rain cuts during the interstitial"): case_file/
  // you_did_it are playerId-gated server-side, so one stale/anonymous status
  // poll can blink "not live" mid-game — and the old code killed the bed on
  // that single blink with nothing restarting it. A real game end stops
  // immediately; anything else needs two consecutive not-live evaluations.
  const caseNotLiveStreakRef = useRef(0);
  useEffect(() => {
    if (isWhodunnitLive && !isBotMode) {
      caseNotLiveStreakRef.current = 0;
      // ⛈️ Don't stomp the interstitial storm while ANY intro overlay is up
      // (07-30 round 2: the first status lands DURING LOADING and the old
      // showGuiltyReveal-only guard let the near-silent pack bed replace the
      // click-started storm seconds in). The handoff effect below swaps to
      // the pack bed when the last overlay clears.
      if (!noirIntroUp) soundManager.startCase(caseAmbient);
      wasCaseLiveRef.current = true;
    } else if (wasCaseLiveRef.current) {
      caseNotLiveStreakRef.current += 1;
      if (chatGameStatus?.game_completed || caseNotLiveStreakRef.current >= 2) {
        soundManager.stopCase();
        wasCaseLiveRef.current = false;
        caseNotLiveStreakRef.current = 0;
      }
    }
  }, [isWhodunnitLive, isBotMode, caseAmbient, chatGameStatus?.game_completed, noirIntroUp]);
  // ⛈️→🎐 Storm handoff (Nina 07-30): the door/Investigate click starts a HOT
  // storm bed that runs through curtain, loading, and the cold open — when
  // the cold open closes, it yields to the pack's own quiet bed (startCase
  // cross-fades via stop+start), and a DRY pack goes properly silent
  // (startCase(null) is a no-op, so the storm must be stopped explicitly).
  const stormHandoffRef = useRef(false);
  useEffect(() => {
    if (isBotMode) return;
    // Arm while ANY intro overlay is up (was showGuiltyReveal only — 07-30
    // round 2), fire once when the last one clears. Spectators arm via the
    // loading overlays even though they never see the reveal.
    if (noirIntroUp) { stormHandoffRef.current = true; return; }
    if (!stormHandoffRef.current) return;
    stormHandoffRef.current = false;
    if (caseAmbient) soundManager.startCase(caseAmbient);
    else soundManager.stopStorm();
  }, [noirIntroUp, isBotMode, caseAmbient]);
  useEffect(() => () => { soundManager.stopStorm(); soundManager.stopCase(); }, []);
  // 🌧️ Tab-away restart (Nina 07-29): the browser suspends the bed on hide and
  // the non-comp handler below never covers case beds — restart on return
  // (idempotent per theme, so a still-running bed is untouched). 07-30: while
  // the cold open is up the storm is the bed to restore, not the pack's.
  useEffect(() => {
    if (!isWhodunnitLive || isBotMode) return undefined;
    const onVis = () => {
      if (document.hidden) return;
      if (noirIntroUp) soundManager.startStorm();
      else if (caseAmbient) soundManager.startCase(caseAmbient);
    };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [isWhodunnitLive, isBotMode, caseAmbient, noirIntroUp]);

  // 🎐 Ambient synth bed AUDIO removed (Nina 08-02: "I HATE that low humming
  // ambient sound... make sure it isn't in any of our spaces now or ever
  // again"). The theme-keyed non-comp bed (teamlab layer 1, 07-16) and its
  // bed-mute state are gone — do NOT rebuild the sound. Whodunnit's scenario
  // storm/case beds above are a separate, deliberate system and stay; blips/
  // stings (sfxEnabled) unaffected. ambientTheme itself survives for the
  // VISUAL living-background canvas (Arc 1 layers 3+4), which stays.
  const ambientTheme = (!isBotMode && !shellPreset /* 🧱 a quiet frame: no living background, no scene looks */ && chatGameStatus
    && !chatGameStatus.competitive_mode && !chatGameStatus.imposter_mode)
    ? pickAmbientTheme(`${chatGameStatus.name || ''} ${chatGameStatus.scene || ''}`)
    : null;

  // 🎭 LIVE SCENE (SCENE_SYSTEM_SPEC §3.3, §6) — the newest /scene card that is
  // still burning room charges. Drives the atmosphere override, the opening
  // sweep and the scene bed.
  //
  // ⚠️ `chatGameStatus.scene` above is NOT this. That is the space's STATIC
  // premise string, and keying an override off it would appear to work in
  // testing while never once reading a /scene card.
  // ⚠️ Liveness must match the SERVER's two conditions or the atmosphere
  // outlives the directive: charges left (remaining.room) AND age under
  // DIRECTIVE_MAX_AGE_MS (10 min, the backend's lazy-expiry bound).
  // The countdown arrives via space_command_update; before that patch existed
  // the client believed every scene was on its first of six turns forever.
  const liveScene = useMemo(() => {
    if (!ambientTheme) return null;   // same null-outs as the layer it feeds
    const all = results.flatMap(r => r.actions || []);
    let best = null;       // a live /scene — short and specific, always wins
    // 🌌 09-14 sky batch: a house EVENT's authored look (storm night, meteor
    // shower...) holds for the EVENT's life, not the directive's six turns —
    // bounded by the newest is_event_end card (every wrap/fizzle/winner card
    // carries it) plus a 3h hard ceiling covering a lost wrap. A /scene fired
    // mid-event overrides for its own 10 minutes, then the event look returns.
    let eventBest = null;
    let eventEndAt = 0;
    for (const a of all) {
      const d = a.data || {};
      const born = new Date(a.created_at || 0).getTime();
      if (!born) continue;
      if (d.is_event_end && born > eventEndAt) eventEndAt = born;
      if (a.type !== 'space_command') continue;
      if (d.command === 'scene' && d.scene_look?.atmosphere) {
        if (!(d.remaining?.room > 0)) continue;
        if (Date.now() - born > 10 * 60 * 1000) continue;
        if (!best || born > best.born) best = { born, id: a.id, look: d.scene_look, remaining: d.remaining.room };
      } else if (d.command === 'house_event' && d.is_event_start && d.scene_look?.atmosphere) {
        if (Date.now() - born > 3 * 60 * 60 * 1000) continue;
        if (!eventBest || born > eventBest.born) {
          eventBest = { born, id: a.id, look: d.scene_look, remaining: 6, isEvent: true, template: d.event_template || null };
        }
      }
    }
    if (eventBest && eventEndAt > eventBest.born) eventBest = null;
    return best || eventBest;
  }, [results, ambientTheme]);

  // 🎪🚪 The live LOCATED event (09-17 eve, Nina's go: "every located event
  // invites"): the newest house_event START card, look or no look. The /room
  // invite used to hang off liveScene, which only exists for events with an
  // authored atmosphere — so bake-offs, trivia, karaoke, brunch, garage sales
  // and the porch open mic (all room-keyed in the engine) never invited
  // anyone. Same lifetime rule as the event look: bounded by the newest
  // is_event_end card + a 3h ceiling. `room` = the backend's staging room
  // (event_room, resolved against the open set at announce); absent on old
  // cards ⇒ only the sky chain can invite, exactly as before.
  const liveEvent = useMemo(() => {
    const all = results.flatMap(r => r.actions || []);
    let best = null;
    let endAt = 0;
    for (const a of all) {
      const d = a.data || {};
      const born = new Date(a.created_at || 0).getTime();
      if (!born) continue;
      if (d.is_event_end && born > endAt) endAt = born;
      if (a.type !== 'space_command' || d.command !== 'house_event' || !d.is_event_start) continue;
      if (Date.now() - born > 3 * 60 * 60 * 1000) continue;
      if (!best || born > best.born) {
        best = { born, id: a.id, template: d.event_template || null, room: d.event_room || null, label: d.event_label || null };
      }
    }
    if (best) return (endAt > best.born) ? null : best;
    // 📅🌕 09-26: no start card in the loaded feed (a chatty hour pushed it out):
    // the server's calendar still says the event runs, with its real start
    const cal = runningEventFrom(houseStatus?.calendar, Date.now());
    return (cal && !(endAt > cal.born)) ? cal : null;
  }, [results, houseStatus?.calendar]); // eslint-disable-line react-hooks/exhaustive-deps
  // ONE invite for BOTH composer mounts (desktop + the phone dock twin), so
  // they can never disagree. null = nothing to ask (no event, you're already
  // there, the room is locked, or you said "later" to this one).
  const roomInvite = useMemo(() => {
    if (!roomCameraOn || !liveEvent) return null;
    const rcRooms = houseStatus?.rooms || [];
    const inv = roomInviteFor(liveEvent, rcRooms.map((r) => r.id));
    if (!inv || inv.room === viewerRoom || dismissedRoomInvites.has(liveEvent.id)) return null;
    return {
      id: liveEvent.id,
      room: inv.room,
      label: rcRooms.find((r) => r.id === inv.room)?.label || inv.room,
      phrase: inv.phrase,
      icon: eventInvitePhrase(liveEvent.template) ? '🌠' : '🎪',
    };
  }, [roomCameraOn, liveEvent, houseStatus, viewerRoom, dismissedRoomInvites]);
  // 🌕🚪 09-26 the Go on an event's start card (roomView.eventGoFor): the open
  // rooms go on the module bus DURING this render, so a card that arrives in
  // this pass reads the fresh list. The strip's Go reads the same resolver
  // through eventRoomRef (declared above eventStripProp). A press walks you into
  // the room's picture exactly like every other Go (openPictureIn), re-checking
  // the room is still open, and retires that event's invite toast.
  eventGoBus.rooms = (roomCameraOn && !isBotMode && Array.isArray(houseStatus?.rooms) && houseStatus.rooms.length) ? houseStatus.rooms : null;
  // 🌠 09-26 the star card's "Wish ▶": /wish live + when the star was last caught
  const starCaughtAt = useMemo(() => caughtStarAt(results.flatMap((r) => r.actions || [])), [results]);
  eventGoBus.wishLive = !!(roomCameraOn && !isBotMode && liveRoomVerbs(houseStatus?.room_verbs).includes('wish'));
  eventGoBus.starCaughtAt = starCaughtAt;
  const [roofWishAt, setRoofWishAt] = useState(0); // a press that should land with the wish box open
  eventRoomRef.current = (roomCameraOn && liveEvent)
    ? ((roomInviteFor(liveEvent, (houseStatus?.rooms || []).map((r) => r.id)) || {}).room || null)
    : null;
  // 💬 09-26 option C: the running event's SCENE = its staged room, or, while the
  // house is out on a bubble event (never a screening), the outing's own place
  // (outings stage no room). The scene's 🔍 / 📸 / 🔗 and its bubbles key on it.
  const sceneRoom = (roomCameraOn && liveEvent) ? sceneRoomFor(liveEvent, eventRoomRef.current, houseStatus?.outing) : null;
  const sceneSeatOf = (pid) => { const u = getUserById(users, pid); return u ? { name: u.name || null, ai: !isRealHuman(u) } : null; };
  const eventGoRef = useRef(null);
  eventGoRef.current = (detail) => {
    const room = detail && typeof detail.room === 'string' ? detail.room : null;
    if (!room || !roomCameraOn) return;
    if (!(houseStatus?.rooms || []).some((r) => r && r.id === room)) { setRoomNotice('That room is closed right now.'); return; }
    if (detail.id) setDismissedRoomInvites((s) => { if (s.has(detail.id)) return s; const n = new Set(s); n.add(detail.id); return n; });
    if (detail.wish) setRoofWishAt(Date.now()); // 🌠 the star card: open the wish box on arrival
    openPictureIn(room);
  };
  useEffect(() => {
    const onGo = (e) => { if (eventGoRef.current) eventGoRef.current((e && e.detail) || {}); };
    document.addEventListener(GAME_EVENTS.EVENT_GO, onGo);
    return () => document.removeEventListener(GAME_EVENTS.EVENT_GO, onGo);
  }, []);

  // 🔥 Scene bed — Nina's 3-turn crackle (spec §4.1). Starts on a FRESH scene
  // only (never on scrollback or a reload: the age guard is what makes an old
  // card silent), and stops at the first of 3 room-turn consumptions, the
  // scene ending, or SoundManager's own 180s ceiling. Mute is handled inside
  // the manager — setEnabled(false) stops the bed structurally, so this effect
  // does not need to know about the master switch.
  const sceneBedArmed = useRef(null);
  useEffect(() => {
    if (!liveScene) { soundManager.stopSceneBed(); sceneBedArmed.current = null; return; }
    const bed = liveScene.look?.bed;
    if (!bed) return;
    const fresh = Date.now() - liveScene.born <= 15000;
    if (sceneBedArmed.current !== liveScene.id) {
      if (!fresh) return;                    // old card — stays silent
      sceneBedArmed.current = liveScene.id;
      soundManager.startSceneBed(bed);
      return;
    }
    // 6 charges at issue; stop once 3 have been spent by locals.
    if (liveScene.remaining <= 3) soundManager.stopSceneBed();
  }, [liveScene]);
  useEffect(() => () => { try { soundManager.stopSceneBed(); } catch (e) { /* leaving */ } }, []);

  // 🎙️ Host-voice mute (Arc 9, 07-20): the SPLIT from bed-mute — kills the
  // host's narration + spoken announcements, leaves the ambience alone.
  const [hostVoiceMuted, setHostVoiceMuted] = useState(() => {
    try { return localStorage.getItem('hostVoiceMuted') === '1'; } catch (e) { return false; }
  });
  const hostVoiceMutedRef = useRef(hostVoiceMuted);
  useEffect(() => { hostVoiceMutedRef.current = hostVoiceMuted; }, [hostVoiceMuted]);
  // 🫂 The check-in's "Mute room sounds for 10 min" (§5.12): the room's own
  // master mute (the corner 🔊's: SFX + host voice + the space's music radio +
  // the party's audio), then back on after `ms` ONLY if it is still the mute
  // this set (a person who turned sound back on keeps theirs). Already quiet =
  // nothing to do. Declared under hostVoiceMuted (it sets it).
  // ⚠️ review 09-30: that mute is STORED (SoundManager + hostVoiceMuted live in
  // localStorage) and a page timer dies with the page, so a person who took
  // the break and left (the likely move) stayed muted in every room for good.
  // The deadline is stored too (utils/roomShellPage careMuteUntil) and every
  // mount re-arms it: the sound comes back at the mark, here or on the next page.
  const careMuteRef = useRef(null); // the pending "sound back on" timer
  const setRoomSoundMuted = (mute) => {
    setSfxEnabled(!mute);
    soundManager.setEnabled(!mute);
    setHostVoiceMuted(mute);
    try { localStorage.setItem('hostVoiceMuted', mute ? '1' : '0'); } catch (err) { /* private mode */ }
    emitGameEvent(GAME_EVENTS.SPACE_AUDIO_MUTED, { muted: mute });
  };
  const armCareUnmute = (until) => {
    clearTimeout(careMuteRef.current);
    careMuteRef.current = null;
    if (!until) return;
    careMuteRef.current = setTimeout(() => {
      careMuteRef.current = null;
      clearCareMute(careStore());
      if (!soundManager.isEnabled()) setRoomSoundMuted(false);
    }, Math.max(0, until - Date.now()));
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { armCareUnmute(careMuteUntil(careStore())); return () => clearTimeout(careMuteRef.current); }, []);
  const muteRoomSoundsFor = (ms) => {
    if (!soundManager.isEnabled()) return;
    const until = Date.now() + ms;
    setRoomSoundMuted(true);
    setCareMuteUntil(careStore(), until);
    armCareUnmute(until);
  };
  // "Something lighter": the interjection route's care_lighter (backend
  // careLighter.js: heavy rooms only, one anonymous world-fiction nudge a room
  // an hour, never a word about who asked). Fire and forget: the card already
  // says it asked. The viewer's own seat proof, never a secret in a URL.
  const askRoomForLighter = () => {
    const room = chatGameStatus?.game_id || gameInstanceId || gameId;
    const seat = humanPlayerId || null;
    sendCareLighter({ baseUrl: chatGameApi.baseUrl, gameId: room, playerId: seat, playerSecret: seat ? chatGameApi.getPlayerSecret(seat) : null });
  };
  // the check-in + the crisis card, in the composer dock (both mounts; the
  // EventFeedbackCard's home). Never for a bot.
  const renderCareCards = () => {
    if (isBotMode || !chatGameStatus) return null;
    const heavy = chatGameStatus.heavy_room === true;
    if (!heavy && !crisisOpen) return null;
    return (
      <>
        {crisisOpen && <CrisisCard open onClose={() => setCrisisOpen(false)} />}
        {heavy && (
          <CheckInCard
            roomId={chatGameStatus.game_id || gameInstanceId || gameId}
            heavy
            enteredAt={careVisitRef.current.at}
            onLighter={askRoomForLighter}
            onMuteSounds={muteRoomSoundsFor}
          />
        )}
      </>
    );
  };
  // Announcement playback (WS 'host_announcement'): one at a time, duck the
  // bed while the host speaks, restore after. Skips: bot mode, voice-muted,
  // live watch party ("TV audible" — belt to the server's suspenders),
  // hidden tab, or an announcement already playing. Autoplay: WS-triggered
  // play can be blocked if the session's activation expired — the catch
  // restores the bed and the chat card still carries the event.
  const hostAnnounceAudioRef = useRef(null);
  const playHostAnnouncement = useCallback((msg) => {
    if (isBotMode || !msg?.audio_url) return;
    if (hostVoiceMutedRef.current) return;
    if (watchPartyRef.current) return;
    if (document.hidden) return;
    if (hostAnnounceAudioRef.current) return;
    try {
      const audio = new Audio(msg.audio_url);
      audio.volume = 0.9;
      hostAnnounceAudioRef.current = audio;
      const done = () => {
        if (hostAnnounceAudioRef.current === audio) hostAnnounceAudioRef.current = null;
        soundManager.unduckAmbient();
      };
      audio.onended = done;
      audio.onerror = done;
      soundManager.duckAmbient();
      audio.play().catch(done);
    } catch (e) { /* voice is garnish */ }
  }, [isBotMode]);
  const lastCompRoundRef = useRef(null);
  useEffect(() => {
    const r = chatGameStatus?.current_competitive_round;
    if (!isWhodunnitLive || !r) return;
    if (lastCompRoundRef.current !== null && r !== lastCompRoundRef.current) {
      soundManager.play('noirSting');
    }
    lastCompRoundRef.current = r;
  }, [chatGameStatus?.current_competitive_round, isWhodunnitLive]);

  // 🔦 New-challenge toast (Whodunnit polish, 07-13): a fresh round's dare
  // gets a 5s pointer to the case file's DARES tab. First challenge of the
  // session stays silent — the dare card introduces itself.
  const [challengeToast, setChallengeToast] = useState(false);
  const lastChallengeRef = useRef(null);
  useEffect(() => {
    const t = chatGameStatus?.human_challenge?.text;
    if (!t || t === lastChallengeRef.current) return;
    const isFirst = lastChallengeRef.current === null;
    lastChallengeRef.current = t;
    if (isFirst || !chatGameStatus?.competitive_mode) return;
    setChallengeToast(true);
    const id = setTimeout(() => setChallengeToast(false), 5000);
    return () => clearTimeout(id);
  }, [chatGameStatus?.human_challenge?.text, chatGameStatus?.competitive_mode]);

  // ⏸️ Pause-on-blur (cost saver, Nina 07-13): every status poll drives the
  // backend's ambient AI pass (ghost chat, quips, host TV, polls) = real LLM
  // spend for a tab nobody is looking at. Hidden >2 min → polling stops
  // COMPLETELY; coming back shows "Space paused" with an explicit ▶ (the
  // pause survives the return on purpose — resuming is a choice, not a
  // side-effect of alt-tabbing). Non-comp only: competitive matches have
  // real timers and must never freeze under a player.
  const [spacePaused, setSpacePaused] = useState(false);
  const spacePausedRef = useRef(false);
  spacePausedRef.current = spacePaused;
  const pauseEligibleRef = useRef(false);
  pauseEligibleRef.current = !isBotMode
    && !chatGameStatus?.competitive_mode && !chatGameStatus?.imposter_mode
    && (gameType === 'chat-game' || gameType === 'chat') && !!gameId;
  useEffect(() => {
    let hideTimer = null;
    const onVis = () => {
      if (document.visibilityState === 'hidden') {
        if (!pauseEligibleRef.current || spacePausedRef.current || hideTimer) return;
        hideTimer = setTimeout(() => {
          hideTimer = null;
          if (document.visibilityState === 'hidden' && pauseEligibleRef.current) setSpacePaused(true);
        }, 2 * 60 * 1000);
      } else if (hideTimer) {
        clearTimeout(hideTimer);
        hideTimer = null;
      }
    };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      document.removeEventListener('visibilitychange', onVis);
      if (hideTimer) clearTimeout(hideTimer);
    };
  }, []);
  const [stageRestore, setStageRestore] = useState(null); // pre-takeover dock key to offer back
  // D3 ambient default: nothing docked + no party → the host's pinned pick
  // plays muted on the stage. Hiding it is per-session, per-space.
  const [ambientHidden, setAmbientHidden] = useState(false);
  // (Nina's ruling 08-14) Low-resource phones don't autoplay the ambient TV
  // pick — the muted iframe cost RAM + decode + the visitor's cellular data
  // unasked. They get a tap-to-start panel; a tap flips this for the visit.
  const [ambientTapStarted, setAmbientTapStarted] = useState(false);
  // 🍿▬ Idle-stage collapse (Nina 08-10, mock option A): the undocked stage's
  // default faces (host's pick, party launcher) start as ONE-LINE bars — the
  // 34dvh autoplay / ~250px launcher stack buried the tab strip on every
  // fresh join. The player/launcher renders only after a click; not
  // persisted, so EVERY join starts collapsed. ambientHidden survives for
  // the phone TV place only (its ✕ hide keeps working there).
  const [idleStageOpen, setIdleStageOpen] = useState(false);
  useEffect(() => {
    if (!gameId) return;
    try {
      // 'watch_party' is never restorable from storage (08-11): a persisted
      // party dock re-opened the full launcher on every future visit,
      // defeating the 08-10 join collapse. Drop legacy keys so old profiles
      // heal on first load.
      const savedTab = localStorage.getItem(`stageTab:${gameId}`) || null;
      if (savedTab === 'watch_party') localStorage.removeItem(`stageTab:${gameId}`);
      setStageTab(savedTab === 'watch_party' ? null : savedTab);
      setAmbientHidden(sessionStorage.getItem(`stageAmbientOff:${gameId}`) === '1');
    } catch (e) { /* private mode */ }
    setIdleStageOpen(false);
  }, [gameId]);
  const hideAmbient = useCallback(() => {
    setAmbientHidden(true);
    try { sessionStorage.setItem(`stageAmbientOff:${gameId}`, '1'); } catch (e) { /* private mode */ }
  }, [gameId]);
  const dockToStage = useCallback((key) => {
    setStageTab(key);
    setStageRestore(null);
    try {
      // A party dock is SESSION-ONLY (08-11): persisting it opted this
      // browser out of the join collapse forever and resurrected the full
      // launcher after the party ended. Leaving storage untouched also
      // preserves whatever tab the user had persisted before the party.
      if (key === 'watch_party') { /* session-only */ }
      else if (key) localStorage.setItem(`stageTab:${gameId}`, key);
      else localStorage.removeItem(`stageTab:${gameId}`);
    } catch (e) { /* private mode */ }
  }, [gameId]);
  // Party lifecycle vs the stage: a party STARTING is a communal takeover (the
  // theater claims everyone's stage — session-only, personal choice stays
  // persisted); a party ENDING hands the stage back to whatever was there.
  const prevPartyLiveRef = useRef(false);
  // 🚪 FIRST OBSERVATION IS NOT AN EVENT (Nina 08-08: "when you first join a
  // space the watch party isn't collapsed — it should always be collapsed").
  // prevPartyLiveRef starts false, so a party that was ALREADY RUNNING when you
  // arrived read as `watchParty && !wasLive` — i.e. "a party just started" — and
  // claimed the stage on arrival. For the Claude House that fires every single
  // time, because its host programming is "always on": there is no moment when
  // you can join and NOT look like you just witnessed a party start.
  // Seeding on the first run makes the effect mean what it says: it reacts to
  // TRANSITIONS observed by THIS session, not to the state of the world at
  // mount. Walk-ins get the collapsed stage plus the existing "Watch party in
  // progress · Take a seat" bar, which is the 08-04 no-auto-seat rule already
  // applied to the theater — the stage was the one surface still ignoring it.
  const partyLifecycleSeededRef = useRef(false);
  // The refs describe THIS space's observed history — reset on space switch
  // so the old space's party state can't read as a transition in the new one.
  useEffect(() => {
    prevPartyLiveRef.current = false;
    partyLifecycleSeededRef.current = false;
  }, [gameId]);
  useEffect(() => {
    // ⭐ Seed from the first STATUS PAYLOAD, not the first render (08-11: the
    // #64 render-seed was a NO-OP — on mount watchParty is definitionally
    // null, so the seed burned on an inert run and the first poll still read
    // as "a party just started", claiming the stage on every join). Until
    // THIS space's payload lands we know nothing about the world; the party
    // rides the payload one render before watchParty state, so read both.
    // ⚠️ The status payload's game field is game_id — it has NO `id`
    // (the last stray chatGameStatus.id read was fixed 08-13).
    if (!chatGameStatus || String(chatGameStatus.game_id) !== String(gameId)) return;
    const wpNow = watchParty || chatGameStatus.watch_party || null;
    const wasLive = prevPartyLiveRef.current;
    prevPartyLiveRef.current = !!wpNow;
    if (!partyLifecycleSeededRef.current) {
      partyLifecycleSeededRef.current = true;
      return; // arrival is not a transition — never claim the stage on join
    }
    if (wpNow && !wasLive) {
      if (wpNow.host_mode) {
        // Ambient host party claims only an EMPTY stage — it's the default
        // occupant, not an event; a personal dock outranks the house TV
        setStageTab(prev => prev || 'watch_party');
      } else {
        setStageTab(prev => {
          if (prev && prev !== 'watch_party') setStageRestore(prev);
          return 'watch_party';
        });
      }
    } else if (!watchParty && wasLive) {
      setStageRestore(null);
      let persisted = null;
      try { persisted = localStorage.getItem(`stageTab:${gameId}`) || null; } catch (e) { /* private mode */ }
      // legacy pre-08-11 profiles may still persist 'watch_party' — never restore it
      setStageTab(persisted === 'watch_party' ? null : persisted);
    }
  }, [watchParty, chatGameStatus, gameId]);

  // 🖥️ Screenshare lifecycle vs the stage (Nina 07-12): someone starting a
  // SCREEN share is a communal event — it claims the stage with a JOIN POSTER
  // (user call: no auto-connect; watching stays one click, so nobody pays
  // LiveKit minutes or gets surprise audio without opting in). Cameras never
  // take the stage — a facecam is ambience, a screenshare is a show. The
  // streamer is exempt (they have the live panel + mini-player); bots are
  // exempt (they can't watch and would fight the poster). Newest event wins:
  // this and the party effect above each capture the other as stageRestore.
  const prevScreenShareRef = useRef(false);
  useEffect(() => {
    let myViewerId = null;
    try { myViewerId = localStorage.getItem('lk_viewer_id'); } catch (e) { /* private mode */ }
    const share = spaceLiveStreamers.find(s => s.mode === 'screen' && s.id !== humanPlayerId && s.id !== myViewerId);
    const wasSharing = prevScreenShareRef.current;
    prevScreenShareRef.current = !!share;
    // 📱 placesNav guard (08-05): the stage renderer is !placesNav-gated, so
    // writing live_share here on a phone just stomped the docked stageTab with
    // silently-stale state that surfaced on a rotate past 768px.
    if (isBotMode || placesNav) return;
    if (share && !wasSharing) {
      setStageTab(prev => {
        if (prev && prev !== 'live_share') setStageRestore(prev);
        return 'live_share';
      });
    } else if (!share && wasSharing) {
      // Share ended: hand the stage back — but only if the poster still holds
      // it (the viewer may have clicked Watch or docked something else since)
      setStageRestore(null);
      let persisted = null;
      try { persisted = localStorage.getItem(`stageTab:${gameId}`) || null; } catch (e) { /* private mode */ }
      setStageTab(prev => (prev === 'live_share' ? persisted : prev));
    }
  }, [spaceLiveStreamers, gameId, humanPlayerId, isBotMode, placesNav]);

  // 🍿 Ambient auto-seat RETIRED everywhere (Nina 08-04; phones lost it
  // 07-31): walking into a space never opens the theater. The "Watch party
  // in progress / Take a seat" bar (desktop) and the TV place card (phones)
  // are the one-tap paths in; seating is always a deliberate act, so the
  // watch credit + watching=1 quip-audience stamps stay honest signals.
  // Starting a party still seats the STARTER (handleWatchTogether) — that
  // click is the deliberate act. This also moots the old bot-trap carve-out
  // (auto-seated bots fighting the unmute overlay all session).

  const watchPartyAction = useCallback(async (action, params = {}) => {
    if (!gameId || !humanPlayerId) return null;
    try {
      const result = await chatGameApi.watchParty(gameId, humanPlayerId, action, params);
      if (result && Object.prototype.hasOwnProperty.call(result, 'watch_party')) {
        watchPartyFreshAtRef.current = Date.now();
        setWatchParty(decodeWatchPartyTitles(result.watch_party));
      }
      return result;
    } catch (err) {
      console.error(`[WatchParty] ${action} failed:`, err.message);
      return { error: err.message };
    }
  }, [gameId, humanPlayerId]);

  const handleWatchTogether = useCallback(async (url) => {
    if (!humanPlayerId) {
      console.log('[WatchParty] Join as a player to start a watch party');
      return;
    }
    const isActive = !!watchParty;
    const result = await watchPartyAction(isActive ? 'queue_add' : 'start', { url });
    // 🍿🚫 09-22: a refusal used to VANISH — the launcher had already flashed
    // "Lights down!" and the server's reason went to the console only (the
    // projector lock refused three starts in a row after the first screening's
    // credits and the page showed nothing). Hand the reason back to whichever
    // launcher is on screen (WatchPartyLauncher listens).
    if (result?.error) {
      try { window.dispatchEvent(new CustomEvent('watchparty:refused', { detail: { message: String(result.error) } })); } catch (e) { /* non-browser env */ }
    }
    if (!result?.error) {
      setInTheater(true);
      // 📱 places nav: the theater lives on the TV place — seat AND navigate,
      // or the party plays audio-only from the hidden holder
      if (placesNav) setMobileView('tv');
    }
  }, [watchParty, humanPlayerId, watchPartyAction, placesNav]);

  // Cards deep in the tree (media tab polaroid/hero, chat share cards) request
  // parties via a window event — avoids prop-drilling through memoized layers.
  useEffect(() => {
    const onReq = (e) => { if (e.detail?.url) handleWatchTogether(e.detail.url); };
    window.addEventListener('watchparty:request', onReq);
    return () => window.removeEventListener('watchparty:request', onReq);
  }, [handleWatchTogether]);

  // Paste handler for images/screenshots and URLs
  useEffect(() => {
    const handleContentPaste = async (e) => {
      if (!humanPlayerId || !gameId) return;

      // Check for pasted files (screenshots, images)
      if (e.clipboardData.files && e.clipboardData.files.length > 0) {
        const file = e.clipboardData.files[0];
        const isDocFile = file.type.startsWith('text/')
          || file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
          || /\.(txt|md|markdown|csv|docx)$/i.test(file.name || '');
        if (file.type.startsWith('image/') || file.type.startsWith('video/') || file.type === 'application/pdf' || isDocFile) {
          e.preventDefault();
          console.log('[handleContentPaste] File paste:', file.name, file.type);
          try {
            await chatGameApi.shareFile(gameId, humanPlayerId, file);
            console.log('[handleContentPaste] File shared successfully');
          } catch (err) {
            console.error('[handleContentPaste] Failed to share file:', err);
          }
          return;
        }
      }

      // Check for pasted URL text
      const text = e.clipboardData.getData('text/plain') || '';
      if (/^https?:\/\//i.test(text.trim())) {
        // Only intercept if the active element is the actions container, not an input field
        const active = document.activeElement;
        if (active && (active.tagName === 'INPUT' || active.tagName === 'TEXTAREA' || active.isContentEditable)) return;
        e.preventDefault();
        const url = text.trim();
        const contentType = detectContentTypeFromUrl(url);
        console.log('[handleContentPaste] URL paste:', url, contentType);
        try {
          await chatGameApi.shareContent(gameId, humanPlayerId, url, contentType);
          console.log('[handleContentPaste] URL shared successfully');
        } catch (err) {
          console.error('[handleContentPaste] Failed to share URL:', err);
        }
      }
    };

    document.addEventListener('paste', handleContentPaste);
    return () => document.removeEventListener('paste', handleContentPaste);
  }, [humanPlayerId, gameId]);
  const wsRef = useRef(null);
  const wsReconnectTimeoutRef = useRef(null);
  const wsBackoffRef = useRef(3000);
  // Queue of WS messages that fired while wsRef wasn't OPEN; drained on each onopen.
  // Survives reconnects (lives on the ref, not the WebSocket instance). Cleared by
  // the WS useEffect cleanup so a navigation/gameId change drops stale intents.
  const wsPendingSendsRef = useRef([]);
  const wsAssetCompleteRef = useRef(false); // Set by WS asset_update handler to short-circuit polling
  const wsThemeCompleteRef = useRef(false); // Theme CSS received via WS
  const wsProfilesCompleteRef = useRef(false); // All player profiles received via WS
  const playbackControllerRef = useRef(null); // connection_id of whoever is playing
  const ourConnectionIdRef = useRef(null); // Our own connection_id from the WS subscribed message
  const hadSpeakerInRoundRef = useRef(false);
  const [waitingForVote, setWaitingForVote] = useState(false);
  const waitingForVoteStartRef = useRef(null);
  // Vote results reveal persists until the user clicks "Continue" — no auto-dismiss.

  // Note: eliminated players keep their humanPlayerId so they can still advance rounds.
  // They just can't switch back to "Play" mode via the dropdown once eliminated.

  // Detect "waiting for vote" — when last speaker finishes and voting hasn't started yet
  useEffect(() => {
    const isCompetitive = chatGameStatus?.competitive_mode || chatGameStatus?.imposter_mode;
    if (!isCompetitive) return;

    if (chatGameStatus?.next_speaker) {
      // We have a speaker — mark that we've seen one this round
      hadSpeakerInRoundRef.current = true;
      setWaitingForVote(false);
      waitingForVoteStartRef.current = null;
    } else if (chatGameStatus?.is_voting || showVotingModal) {
      // Voting has started or modal is showing — reset
      hadSpeakerInRoundRef.current = false;
      setWaitingForVote(false);
      waitingForVoteStartRef.current = null;
    } else if (hadSpeakerInRoundRef.current && !chatGameStatus?.next_speaker && chatGameStatus?.current_round_type !== 'conclusion' && chatGameStatus?.round_type !== 'chat-discussion') {
      // Had a speaker, now no speaker, not voting yet, and discussion round has ended —
      // we're in the gap between discussion ending and voting starting.
      // The round_type check prevents showing this while discussion is still wrapping up.
      if (!waitingForVoteStartRef.current) {
        waitingForVoteStartRef.current = Date.now();
      }
      setWaitingForVote(true);
    }
  }, [chatGameStatus?.next_speaker, chatGameStatus?.is_voting, chatGameStatus?.competitive_mode, chatGameStatus?.imposter_mode, chatGameStatus?.current_round_type, showVotingModal]);

  // Define action types that have explicit UI components
  const actionTypesWithUI = [
    'message', 'public_message', 'private_message', 'secret_message', 'house_action', 'human_interjection', 'speak',
    'round_summary', 'out_of_tokens', 'game_action', 'secret_game_action', 'pass',
    'vote', 'eviction', 'game_analysis', 'round_analysis', 'vote_to_win', 'player_summary',
    'competition_question', 'competition_answer', 'competition_winner',
    'competition_credit_decision', 'competition_results',
    'slice_of_life', 'question_answer', 'nomination', 'game_announcement',
    'clue_found',
    'vote_cast', 'secret_role_assignment', 'initial_investigation', 'empath_reading',
    'scene', 'week_summary', 'opening_statement', 'final_speech', 'episode', 'custom_video',
    'shared_content', 'space_command'
  ];

  // Message action types for chat game sub-tab filtering
  const messageActionTypes = new Set([
    'message', 'public_message', 'private_message', 'secret_message',
    'human_interjection', 'speak', 'shared_content'
  ]);

  // Function to check if action has explicit UI
  const hasExplicitUI = (action) => {
    return actionTypesWithUI.includes(action.type);
  };

  // Function to sort actions so episodes appear first, then regular actions, then analysis appears at the end of each round
  const sortActionsWithAnalysis = (actions) => {
    if (!actions || actions.length === 0) return actions;

    // Separate special action types from regular actions
    const episodeActions = actions.filter(a => a.type === 'episode');
    const analysisActions = actions.filter(a => a.type === 'round_analysis' || a.type === 'game_analysis');
    const regularActions = actions.filter(a => a.type !== 'episode' && a.type !== 'round_analysis' && a.type !== 'game_analysis');

    // Group all actions by round
    const roundGroups = {
      episodes: {},
      regular: {},
      analyses: {}
    };
    const roundOrder = []; // Track the order rounds first appear

    // Group episodes by round
    episodeActions.forEach(action => {
      const roundId = action.game_instance_round_id || 'no_round';
      if (!roundGroups.episodes[roundId]) {
        roundGroups.episodes[roundId] = [];
      }
      roundGroups.episodes[roundId].push(action);
      if (!roundOrder.includes(roundId)) roundOrder.push(roundId);
    });

    // Group regular actions by round
    regularActions.forEach(action => {
      const roundId = action.game_instance_round_id || 'no_round';
      if (!roundGroups.regular[roundId]) {
        roundGroups.regular[roundId] = [];
      }
      roundGroups.regular[roundId].push(action);
      if (!roundOrder.includes(roundId)) roundOrder.push(roundId);
    });

    // Group analyses by round
    analysisActions.forEach(action => {
      const roundId = action.game_instance_round_id || 'no_round';
      if (!roundGroups.analyses[roundId]) {
        roundGroups.analyses[roundId] = [];
      }
      roundGroups.analyses[roundId].push(action);
      if (!roundOrder.includes(roundId)) roundOrder.push(roundId);
    });

    // Build result array by processing each round in order: episodes first, then regular, then analyses
    const result = [];

    roundOrder.forEach(roundId => {
      // Add episodes first (at the top of the round)
      if (roundGroups.episodes[roundId]) {
        roundGroups.episodes[roundId].forEach(action => result.push(action));
      }

      // Add regular actions
      if (roundGroups.regular[roundId]) {
        roundGroups.regular[roundId].forEach(action => result.push(action));
      }

      // Add analyses last (at the bottom of the round)
      if (roundGroups.analyses[roundId]) {
        roundGroups.analyses[roundId].forEach(action => result.push(action));
      }
    });

    return result;
  };

  // (perf 08-13) THE feed derivation — flatten → round-filter → reverse →
  // analysis sort → episode sort → UI filter. This ran inline in JSX on
  // EVERY render, in BOTH the desktop and mobile branches, with two Date
  // allocations per sort comparison. Hoisted, it recomputes only when its
  // actual inputs change — poll ticks, typing reveals, and scroll renders
  // skip it entirely. The branch-specific per-tab filters (Step 4/5) stay
  // at the call sites: they're cheap linear passes and they differ.
  // allActionsFlat identity is now ALSO stable across non-append renders,
  // which lets React.memo children keyed on it (HighlightsPanel,
  // VirtualizedActionList) actually cache.
  // 📖 09-29 (Nina): the house's chapter boundary reads only the AI "End of
  // Chapter N: <title>"; its canned opener leaves the feed (utils/houseChapterOpener).
  const isHouseFeed = !!houseStatus;
  const feedDerivation = useMemo(() => {
    const allActionsFlat = results.flatMap(r => r.actions || []);
    const titledRounds = isHouseFeed ? chapterTitledRounds(allActionsFlat) : null;
    const tsCache = new Map();
    const ts = (a) => {
      let v = tsCache.get(a);
      if (v === undefined) { v = new Date(a.created_at).getTime(); tsCache.set(a, v); }
      return v;
    };
    const perResult = results.map((result) => {
      let actionsToRender = result.actions || [];
      // Round filter (non-chat game types only) — data usually arrives
      // pre-filtered; this only bites when several rounds are loaded.
      if (gameType !== 'chat-game' && gameType !== 'chat' && selectedRound && selectedRound !== 'all') {
        const uniqueRoundIdsForFilter = [...new Set(allActionsFlat.map(a => a.game_instance_round_id).filter(id => id))];
        if (uniqueRoundIdsForFilter.length > 1) {
          const roundIdToNumberForFilter = {};
          const roundIdToTimestampForFilter = {};
          uniqueRoundIdsForFilter.forEach(roundId => {
            const actionsInRound = allActionsFlat.filter(a => a.game_instance_round_id === roundId);
            if (actionsInRound.length > 0) {
              roundIdToTimestampForFilter[roundId] = Math.min(...actionsInRound.map(ts));
            }
          });
          const sortedRoundIdsForFilter = uniqueRoundIdsForFilter.sort((a, b) =>
            roundIdToTimestampForFilter[a] - roundIdToTimestampForFilter[b]
          );
          sortedRoundIdsForFilter.forEach((roundId, idx) => { roundIdToNumberForFilter[roundId] = idx + 1; });
          const filterRoundNumber = selectedRound === 'current'
            ? sortedRoundIdsForFilter.length
            : parseInt(selectedRound);
          actionsToRender = actionsToRender.filter(action =>
            roundIdToNumberForFilter[action.game_instance_round_id] === filterRoundNumber
          );
        }
      }
      if (reverseChronological) actionsToRender = [...actionsToRender].reverse();
      actionsToRender = sortActionsWithAnalysis(actionsToRender);
      actionsToRender = [...actionsToRender].sort((a, b) => {
        const timeA = ts(a);
        const timeB = ts(b);
        if (a.game_instance_round_id !== b.game_instance_round_id) return timeA - timeB;
        if (a.type === 'episode' && b.type !== 'episode') return -1;
        if (a.type !== 'episode' && b.type === 'episode') return 1;
        return timeA - timeB;
      });
      return actionsToRender.filter(action => (!hideGenericActions || hasExplicitUI(action))
        && !(titledRounds && isCannedChapterOpener(action, titledRounds)));
    });
    return { allActionsFlat, perResult };
    // sortActionsWithAnalysis/hasExplicitUI are pure per-render redeclarations
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [results, gameType, selectedRound, reverseChronological, hideGenericActions, isHouseFeed]);

  // Shared "glued to bottom" zone for arming auto-scroll AND the jump-button /
  // unread thresholds (one zone, no dead band where the UI looks at-bottom but
  // snapping is off). Deliberately tight: the first flick of an upward scroll
  // leaves the zone, so a message landing mid-scroll can't yank the reader back
  // down — the old 200px zone kept the snap armed through the start of every
  // scroll-up, which read as "the chat is fighting me".
  const AT_BOTTOM_PX = 60;

  // Function to check if we're scrolled to the bottom
  // 🔬 08-02 scroll-up RCA: resultsContainerRef is NOT always what actually
  // scrolls the feed — with >30 actions VirtualizedActionList mounts its own
  // scroller ([data-virtual-feed], calc(100dvh-200px)) INSIDE it, and on
  // phones the page grows to content so the DOCUMENT scrolls. scrollToBottom/
  // isScrolledToBottom wrote to the outer ref regardless, so every post-send
  // snap was a silent no-op and layout shifts (dock collapse, composer
  // re-layout) clamped the real scroller upward with nothing correcting it.
  // Resolve the ACTIVE scroller instead: inner virtual feed → outer container
  // → document, first one that can actually scroll.
  const getFeedScrollEl = () => {
    const outer = resultsContainerRef.current;
    const canScroll = (el) => !!el && el.clientHeight > 0 && el.scrollHeight > el.clientHeight + 8;
    const virt = outer ? outer.querySelector('[data-virtual-feed]') : null;
    if (canScroll(virt)) return virt;
    if (canScroll(outer)) return outer;
    if (outer) return document.scrollingElement || document.documentElement;
    return null;
  };

  const isScrolledToBottom = () => {
    const el = getFeedScrollEl();
    if (!el) return true; // Default to true so first load scrolls
    const { scrollTop, scrollHeight, clientHeight } = el;
    return Math.abs(scrollHeight - scrollTop - clientHeight) <= AT_BOTTOM_PX;
  };

  // Programmatic scrolls fire the same scroll event as user scrolls; this flag lets
  // handleChatScroll tell them apart so only USER scrolling arms/disarms the snap
  // (an auto-snap re-arming itself, or a prepend restore disarming, were both wrong).
  const programmaticScrollRef = useRef(false);
  // 🏡 W2d: while a place page's stage eases, the feed's size changes under the
  // reader; a scroll that clamp fires is the page's, not the person's
  const placeEaseUntilRef = useRef(0);

  // Function to scroll to bottom — targets the ACTIVE scroller (see
  // getFeedScrollEl above), and only arms the programmatic flag when the
  // write will actually move something (a no-op write fires no scroll event,
  // so the flag used to stick true and swallow the first real user scroll).
  const scrollToBottom = () => {
    if (placeRitualRef.current) return; // 📱 a place ritual holds the stage in view (see placeRitualRef)
    const el = getFeedScrollEl();
    if (el) {
      const target = el.scrollHeight - el.clientHeight;
      if (Math.abs(el.scrollTop - target) > 1) {
        programmaticScrollRef.current = true;
        el.scrollTop = el.scrollHeight;
      }
    }
    // 🏡 W2d: a place page's feed is short at rest, and a lived-in room's
    // virtual scroller (a fixed calc(100dvh - 200px), taller than the feed)
    // left the OUTER box at its top: the newest lines sat below it. The outer
    // follows to its bottom too, so the newest line is what you see.
    // 🧱 the room frame's feed is a bounded box too (the same follow)
    if ((portedPlaceOn && !isMobile) || shellPreset) {
      const outer = resultsContainerRef.current;
      if (outer && outer !== el && outer.scrollHeight - outer.clientHeight - outer.scrollTop > 1) outer.scrollTop = outer.scrollHeight;
    }
    // Going to the bottom IS the intent to follow (round 5, 08-03): the ↓
    // jump used to leave auto-follow disarmed (a scrolled-up reader's
    // disarm survived the jump), so new messages and image-settle growth
    // drifted the feed up again. Every caller either wants this or is a
    // snap path where it's already true.
    shouldAutoScrollRef.current = true;
    setUnreadCount(0);
  };

  // 🧱📐 The room frame's feed is a bounded box, and a long room's list
  // (VirtualizedActionList past 30 lines) is its own scroller at a fixed
  // calc(100dvh - 200px): taller than the frame's feed, so the newest lines
  // sat under the composer in a scroller inside a scroller (the gate measured
  // 130px at 1440×900). Under the frame the list takes exactly the room left
  // below whatever sits above it (the thoughts row, a pinned line): a CSS
  // variable on the feed that the frame's one rule reads (renderShellFrame).
  // Remeasured on every resize, and every 2s: the list's scroller comes and
  // goes as the room crosses 30 lines. Today's page: never runs.
  useEffect(() => {
    if (!shellPreset || typeof ResizeObserver === 'undefined') return undefined;
    let raf = 0;
    const fit = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const outer = resultsContainerRef.current;
        const virt = outer ? outer.querySelector('[data-virtual-feed]') : null;
        if (!outer || !virt) return;
        const above = virt.getBoundingClientRect().top - outer.getBoundingClientRect().top + outer.scrollTop;
        const h = `${Math.max(160, Math.floor(outer.clientHeight - above - 8))}px`;
        if (outer.style.getPropertyValue('--gsp-virt-h') !== h) outer.style.setProperty('--gsp-virt-h', h);
      });
    };
    const ro = new ResizeObserver(fit);
    if (shellHosts.feed) ro.observe(shellHosts.feed);
    fit();
    const iv = setInterval(fit, 2000);
    return () => { ro.disconnect(); clearInterval(iv); cancelAnimationFrame(raf); };
  }, [shellPreset, shellHosts.feed]);

  // Track whether we should auto-scroll after next render. Kept accurate by BOTH message
  // arrival (WS handler) and the user's own scrolling (handleChatScroll) — previously only
  // the former, so the flag went stale-true while a user read history and ANY results
  // mutation (reactions, poll merges, lazy-load prepends) yanked them to the bottom.
  const shouldAutoScrollRef = useRef(true);
  // 🧲 09-25 (Nina: "I don't think there's an auto scroll?"). The never-yank
  // distance ceilings below (600 / 800px) are for a reader who SCROLLED UP, but
  // readers nobody moved got them too, and a Live room's lines landed out of
  // sight for good (gate: 0 of 8 seen). Three ways, all measured: a short
  // (≤30-row) feed first renders at its TOP · the feed's container is REPLACED
  // after load (a new one at its top, no scroll event) · content merging in
  // above nudges scrollTop, and that scroll event read as "scrolled up".
  // So: the ceilings apply only if the reader used a scroll control since the
  // feed last sat at its bottom (readerMayBeReading), and only a scroll within
  // 1.5s of real input can switch the follow off.
  // scrollInputAt: wheel · touch drag · a navigation key outside a text field ·
  // a press on the feed's own scroller (its scrollbar). anyInputAt: those, or
  // any press (a button that scrolls the page, e.g. "Jump to the show").
  const scrollInputAtRef = useRef(-Infinity); // not 0: the page clock starts at 0
  const anyInputAtRef = useRef(-Infinity);
  const lastAtBottomAtRef = useRef(-Infinity);
  const readerMayBeReading = () => scrollInputAtRef.current > lastAtBottomAtRef.current;
  useEffect(() => {
    scrollInputAtRef.current = -Infinity; anyInputAtRef.current = -Infinity; lastAtBottomAtRef.current = -Infinity;
  }, [gameId]);
  useEffect(() => {
    const scrolled = () => { scrollInputAtRef.current = anyInputAtRef.current = performance.now(); };
    const onKey = (e) => {
      const t = e.target;
      if (t && (t.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName || ''))) return;
      if (['ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End', ' '].includes(e.key)) scrolled();
    };
    const onPress = (e) => {
      anyInputAtRef.current = performance.now();
      if (e.target === getFeedScrollEl()) scrollInputAtRef.current = anyInputAtRef.current;
    };
    document.addEventListener('wheel', scrolled, { capture: true, passive: true });
    document.addEventListener('touchmove', scrolled, { capture: true, passive: true });
    document.addEventListener('pointerdown', onPress, true);
    document.addEventListener('keydown', onKey, true);
    return () => {
      document.removeEventListener('wheel', scrolled, { capture: true });
      document.removeEventListener('touchmove', scrolled, { capture: true });
      document.removeEventListener('pointerdown', onPress, true);
      document.removeEventListener('keydown', onKey, true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // True while loadOlderChatMessages is prepending history — suppresses the auto-scroll
  // effect so its scrollToBottom can't fight the prepend's scroll-position restore.
  const isPrependingRef = useRef(false);

  // 🖼️ Image-settle follow (round 5, 08-03, probe-found): media finishing
  // decode below the fold grows the feed AFTER the id-keyed snap ran — no new
  // action id means nothing re-snaps, so a reader glued to the bottom drifts
  // up by exactly the media height (constant dist=172 on the probe space).
  // While auto-follow is armed, follow content growth. The 800px ceiling
  // means a reader deep in history is never yanked; prepends stay suppressed.
  // Interval re-attach because the ACTIVE scroller's content node changes
  // identity (virtualizer mounts at >30 actions, place/layout swaps).
  useEffect(() => {
    if (typeof ResizeObserver === 'undefined') return undefined;
    let raf = 0;
    const ro = new ResizeObserver(() => {
      if (!shouldAutoScrollRef.current || isPrependingRef.current) return;
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const el = getFeedScrollEl();
        if (!el) return;
        const dist = el.scrollHeight - el.scrollTop - el.clientHeight;
        // compRecent: same virtualizer-compensation override as the id-keyed snap —
        // an armed reader displaced by a library scroll may sit far beyond the 800px
        // never-yank ceiling (mid-session virtualizer mount lands at TOP; sweep #48).
        const compRecent = window.__virtCompensationTs && (performance.now() - window.__virtCompensationTs) < 1500;
        if (dist > 0 && (dist < 800 || compRecent || !readerMayBeReading())) {
          programmaticScrollRef.current = true;
          el.scrollTop = el.scrollHeight;
        }
      });
    });
    const attach = () => {
      const el = getFeedScrollEl();
      if (!el) return;
      if (el === document.scrollingElement || el === document.documentElement) {
        ro.observe(document.body); // idempotent per node; dead nodes stop reporting
        return;
      }
      // Observe EVERY direct child, not just the first: scrollHeight growth
      // always widens some direct child's border-box, but which child holds
      // the feed varies — on desktop noncomp the first child is the dossier
      // drawer's zero-height sticky anchor, which never resizes, so the
      // firstElementChild pick left the settle-follow blind there (sweep #48
      // probe: post-send late growth parked the feed 164px up). Children are
      // few (drawer anchors + feed wrapper); a newly observed node fires an
      // initial RO callback, so the 4s re-attach self-heals layout swaps.
      if (!el.childElementCount) { ro.observe(el); return; }
      for (const child of el.children) ro.observe(child);
    };
    attach();
    const iv = setInterval(attach, 4000);
    return () => { clearInterval(iv); ro.disconnect(); cancelAnimationFrame(raf); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-scroll actions div after results change (runs after DOM update)
  // Skip auto-scroll on panel-based tabs — user should stay at top viewing curated content
  // 'recap' belongs here like every other self-contained lane (#57 sweep audit):
  // without it the wall got the room's autoscroll + the floating arrow cluster,
  // so incoming chat yanked the pinned memories off screen while you read them.
  const panelTabs = new Set(['media', 'bot_vision', 'games', 'highlights', 'livestream', 'recap', 'workbench']);
  // Snap only when the BOTTOM of the feed actually changed. `results` also churns
  // on reaction merges, poll dedups and history prepends — none move the bottom,
  // and each used to re-run the snap while a reader was scrolling (the other half
  // of the "fighting" feel).
  const lastBottomActionIdRef = useRef(null);
  useEffect(() => {
    // [5g23-scroll-diag] TEMP instrumentation (remove after RCA). Roots out why
    // the stream audience misses new MAIN-chat posts in non-competitive spaces
    // while competitive + ghost chats look fine. Logs main-chat scroll geometry
    // pre + post the snap so comp vs non-comp runs can be diffed from the console
    // capture. Bot-gated console.log ONLY — no behavior change.
    const logScroll = (phase) => {
      if (!isBotMode) return;
      try {
        const el = resultsContainerRef.current;
        const mains = document.querySelectorAll('[data-chat-scroll="main"]').length;
        const mode = chatGameStatus?.imposter_mode ? 'imp' : (chatGameStatus?.competitive_mode ? 'comp' : 'noncomp');
        if (!el) { console.log(`[5g23-scroll-diag/${phase}] mode=${mode} resultsContainerRef=NULL mains=${mains} tab=${chatActionTab}`); return; }
        const dist = Math.round(el.scrollHeight - el.scrollTop - el.clientHeight);
        console.log(`[5g23-scroll-diag/${phase}] mode=${mode} tab=${chatActionTab} panel=${panelTabs.has(chatActionTab)} auto=${shouldAutoScrollRef.current} visible=${el.offsetParent !== null} top=${Math.round(el.scrollTop)} sH=${Math.round(el.scrollHeight)} cH=${Math.round(el.clientHeight)} dist=${dist} atBottom=${dist < 30} mains=${mains} msgs=${results.length}`);
      } catch (e) { console.log(`[5g23-scroll-diag/${phase}] err ${e.message}`); }
    };
    logScroll('pre');
    const allActs = results.flatMap(r => r.actions || []);
    const lastId = allActs.length > 0 ? allActs[allActs.length - 1].id : null;
    const bottomChanged = lastId !== lastBottomActionIdRef.current;
    lastBottomActionIdRef.current = lastId;
    if (bottomChanged && shouldAutoScrollRef.current && !isPrependingRef.current && !panelTabs.has(chatActionTab)) {
      const snap = () => {
        // Re-check at snap time: never yank a user who is deep in history (a stale flag or a
        // mid-frame prepend can't be fully excluded). 600px allows a tall just-arrived message
        // at the bottom while still blocking jumps from far up. Bot mode keeps the forced snap
        // (Cycle 5b.2 — the bot scrolls programmatically and must always return to bottom).
        // EXCEPT when a virtualizer compensation scroll just fired (the #46 stamp): the feed
        // crossing the 30-action boundary mid-session mounts the virtualizer, whose estimate
        // corrections scrollTo(0) — a pinned reader lands ~full-feed away from bottom through
        // no scroll of their own (sweep #48 desktop probe: dist=5760 stuck at TOP). Library
        // displacement while armed is never "the user reading history" — snap through it.
        if (isPrependingRef.current || !shouldAutoScrollRef.current) return;
        const el = getFeedScrollEl();
        if (!el) return;
        if (!isBotMode) {
          const dist = el.scrollHeight - el.scrollTop - el.clientHeight;
          const compRecent = window.__virtCompensationTs && (performance.now() - window.__virtCompensationTs) < 1500;
          if (dist > 600 && !compRecent && readerMayBeReading()) return;
        }
        scrollToBottom(); logScroll('post');
      };
      requestAnimationFrame(snap);
      // Post-swap settle retries: mount-frame measurement means the compensation scroll
      // can land AFTER the rAF snap and stomp it. snap() is fully guarded (armed +
      // prepend + dist) so a stale timer is a no-op, not a yank.
      setTimeout(snap, 400);
      setTimeout(snap, 1000);
    }
  }, [results]);

  // Auto-scroll when switching chat action sub-tabs
  // Panel-based tabs scroll to top (so user sees curated content first), others scroll to bottom.
  // Settle-window (Nina 07-25): the old single-rAF snap landed SHORT whenever the tab's
  // content grew after that first frame (crime-scene images, filtered lists re-rendering) —
  // whodunnit's "autoscroll doesn't work". Re-snap for ~900ms while the feed stays near
  // the bottom; a user who scrolls away (>600px after the opening beat) ends the window
  // early instead of being yanked back.
  useEffect(() => {
    if (panelTabs.has(chatActionTab)) {
      requestAnimationFrame(() => {
        const el = getFeedScrollEl();
        if (el) {
          programmaticScrollRef.current = true;
          el.scrollTop = 0;
        }
      });
      return;
    }
    let cancelled = false;
    const started = performance.now();
    const settle = () => {
      if (cancelled) return;
      const el = getFeedScrollEl();
      if (el) {
        const dist = el.scrollHeight - el.scrollTop - el.clientHeight;
        // First ~120ms: force the jump no matter how far up we start. After that,
        // a large distance means the USER moved — stop instead of fighting them.
        if (dist > 600 && performance.now() - started > 120) return;
        if (dist > 1) scrollToBottom();
      }
      if (performance.now() - started < 900) requestAnimationFrame(settle);
    };
    requestAnimationFrame(settle);
    return () => { cancelled = true; };
  }, [chatActionTab]);

  // Lazy loading: load older chat messages when user scrolls to top
  const loadOlderChatMessages = useCallback(async () => {
    if (!gameId || !hasMoreChatMessages || isLoadingOlderChatMessages) return;
    // Find the oldest message ID from current results
    const allActions = results.flatMap(r => r.actions || []);
    const firstActionId = allActions.length > 0 ? allActions[0].id : null;
    if (!firstActionId) return;

    setIsLoadingOlderChatMessages(true);
    try {
      const data = await chatGameApi.getMessages(gameId, humanPlayerId, {
        before: firstActionId,
        limit: 100,
        type: 'chat'
      });

      if (data.messages && data.messages.length > 0) {
        // Convert to actions format and prepend
        const olderActions = data.messages.map(msg => {
          let actionType = msg.type;
          if (msg.type === 'announcement') actionType = 'game_announcement';
          else if (msg.type === 'public') actionType = 'public_message';
          else if (msg.type === 'private') actionType = 'private_message';
          else if (msg.type === 'secret') actionType = 'secret_message';

          return {
            id: msg.id,
            type: actionType,
            // 🪪 ROOT CAUSE of "Unknown Player" on scrolled-back history
            // (08-14). Every renderer resolves its author with
            // getUserById(users, action.game_instance_player_id) — a TOP-LEVEL
            // field. The lazy-load payload has no such field: getGameMessages
            // returns the resolved `sender` name plus a spread of actionData
            // (which carries sender_id), so the id landed inside `data` and the
            // top level stayed undefined. users.find(u => u.id === undefined)
            // misses for EVERY prepended message, which is why this only ever
            // showed up after scrolling far enough to trigger a page load.
            // sender_id IS game_instance_players.id — it is what the backend
            // keys playerMap by (chatGameApi.js getGameMessages).
            game_instance_player_id: msg.sender_id || null,
            data: {
              sender_name: msg.sender,
              message: msg.message,
              recipient_names: msg.recipients,
              reactions: msg.reactions || {},
              ...Object.fromEntries(
                Object.entries(msg).filter(([key]) =>
                  !['id', 'type', 'sender', 'message', 'recipients', 'timestamp', 'reactions'].includes(key)
                )
              )
            },
            created_at: msg.timestamp
          };
        });

        // Preserve scroll position by measuring before and after — on the
        // ACTIVE scroller (document on phones, virtualizer >30 actions); the
        // old container measure was dead code until 08-03 wired mobile
        // lazy-load up. Virtualizer restore is estimate-based (unmeasured
        // rows count 200px) — close enough to hold the reader's place.
        const container = getFeedScrollEl();
        const prevScrollHeight = container?.scrollHeight || 0;

        // Suppress the [results] auto-scroll effect for this prepend — it fires on the same
        // setResults and its scrollToBottom used to race (and beat) the position restore
        // below, yanking the reader from the top of history to the bottom.
        isPrependingRef.current = true;
        setResults(prevResults => {
          const existingActions = prevResults.flatMap(r => r.actions || []);
          const existingIds = new Set(existingActions.map(a => a.id));
          const newActions = olderActions.filter(a => !existingIds.has(a.id));
          return [{ actions: [...newActions, ...existingActions] }];
        });

        // After render, restore scroll position so content doesn't jump
        requestAnimationFrame(() => {
          if (container) {
            programmaticScrollRef.current = true;
            const newScrollHeight = container.scrollHeight;
            container.scrollTop = container.scrollTop + (newScrollHeight - prevScrollHeight);
          }
          isPrependingRef.current = false;
        });
      }

      setHasMoreChatMessages(data.has_more);
    } catch (error) {
      console.error('Error loading older chat messages:', error);
    } finally {
      setIsLoadingOlderChatMessages(false);
    }
  }, [gameId, humanPlayerId, hasMoreChatMessages, isLoadingOlderChatMessages, results]);

  // Lazy loading: load older spectator/ghost chat messages
  const loadOlderSpectatorMessages = useCallback(async () => {
    if (!gameId || !hasMoreSpectatorMessages || isLoadingOlderSpectatorMessages) return;
    const firstMsgId = spectatingMessages.length > 0 ? spectatingMessages[0].id : null;
    if (!firstMsgId) return;

    setIsLoadingOlderSpectatorMessages(true);
    try {
      const data = await chatGameApi.getMessages(gameId, humanPlayerId, {
        before: firstMsgId,
        limit: 100,
        type: 'spectating'
      });

      if (data.messages && data.messages.length > 0) {
        // Watch-party quips are overlay-only — keep them out of the panel's
        // history the same way live ingest (processIncomingAction) does
        // 🛋️ 09-23: older couch rows feed the log too (oldest first, the log keeps its own cap)
        if (pushCouchRef.current) data.messages.filter((msg) => msg && msg.is_watch_quip).forEach((msg) => pushCouchRef.current({ id: msg.id, sender_name: msg.sender, avatar_url: null, text: msg.message, at: msg.timestamp, video_id: msg.video_id || null }));
        const olderMsgs = data.messages.filter(msg => !msg.is_watch_quip).map(msg => ({
          id: msg.id,
          sender: msg.sender,
          message: msg.message,
          timestamp: msg.timestamp,
          sender_id: msg.sender_id,
          is_spectator: msg.is_spectator,
          is_ghost_chatter: msg.is_ghost_chatter || false,
          is_reaction_note: msg.is_reaction_note,
          is_moderator: msg.is_moderator || false,
          moderator_emoji: msg.moderator_emoji,
          livestream_identity: msg.livestream_identity || null,
          livestream_streamer: msg.livestream_streamer || null,
          gif_url: msg.gif_url || null,
          gif_title: msg.gif_title || null
        }));

        setSpectatingMessages(prev => {
          const existingIds = new Set(prev.map(m => m.id));
          const newMsgs = olderMsgs.filter(m => !existingIds.has(m.id));
          return [...newMsgs, ...prev];
        });
      }

      setHasMoreSpectatorMessages(data.has_more);
    } catch (error) {
      console.error('Error loading older spectator messages:', error);
    } finally {
      setIsLoadingOlderSpectatorMessages(false);
    }
  }, [gameId, humanPlayerId, hasMoreSpectatorMessages, isLoadingOlderSpectatorMessages, spectatingMessages]);

  // Track scroll position for jump buttons + unread indicator
  const [showJumpToBottom, setShowJumpToBottom] = useState(false);
  const [showJumpToTop, setShowJumpToTop] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  // Scroll handler for main chat area - detect scroll to top for lazy loading + jump buttons
  // 🔬 08-03 (audit #12 family): reads the ACTIVE scroller via getFeedScrollEl,
  // not resultsContainerRef — on phones the DOCUMENT scrolls and with >30
  // actions the virtualizer scrolls, so the old container-only read left
  // lazy-load-older / jump buttons / unread dead exactly there.
  const handleChatScroll = useCallback(() => {
    const el = getFeedScrollEl();
    if (!el) return;
    // Our own scrollToBottom/scrollToTop/prepend-restore fire this same event:
    // keep the buttons in sync with the new position, but leave the user's
    // arm/disarm intent alone — an auto-snap must not re-arm itself, and a
    // prepend restore must not disarm a reader who was glued to the bottom.
    // Virtualizer compensation scrolls (item-resize re-anchoring) are
    // library-driven, not user intent — the custom scrollToFn stamps them
    // (sweep #46 RCA). 250ms covers the burst; the jump-button/unread
    // bookkeeping below still runs, only arm/disarm is skipped.
    const virtCompensation = !!window.__virtCompensationTs
      && performance.now() - window.__virtCompensationTs < 250;
    const wasProgrammatic = programmaticScrollRef.current || virtCompensation || placeEaseUntilRef.current > performance.now();
    programmaticScrollRef.current = false;
    // Load more when scrolled near the top (within 100px)
    if (el.scrollTop < 100 && hasMoreChatMessages && !isLoadingOlderChatMessages) {
      loadOlderChatMessages();
    }
    // Show/hide jump buttons based on scroll position
    const { scrollTop, scrollHeight, clientHeight } = el;
    const distFromBottom = scrollHeight - scrollTop - clientHeight;
    const atBottom = distFromBottom <= AT_BOTTOM_PX;
    setShowJumpToBottom(!atBottom);
    setShowJumpToTop(scrollTop > 200);
    // Clear unread count when user scrolls to bottom
    if (atBottom) setUnreadCount(0);
    // 🧲 when the feed last sat at its bottom, and whether a person moved it
    if (atBottom) lastAtBottomAtRef.current = performance.now();
    const userDriven = performance.now() - anyInputAtRef.current < 1500;
    if (wasProgrammatic) return;
    // A scroll nobody asked for (anchoring, a clamp) is never the reader
    // choosing to read history: it cannot switch the follow off
    if (!atBottom && !userDriven && !isBotMode) return;
    // User intent is authoritative for auto-scroll: scrolling up disarms the snap, returning
    // to the bottom re-arms it. Bot mode stays force-armed (Cycle 5b.2 — the bot's own
    // programmatic scrolling must never disable snap, or its posts vanish for HLS viewers).
    shouldAutoScrollRef.current = isBotMode || atBottom;
  }, [hasMoreChatMessages, isLoadingOlderChatMessages, loadOlderChatMessages, isBotMode]);

  // 🔬 08-03: scroll events DON'T bubble, so an onScroll prop on the results
  // container never hears the document (phones) or the virtualizer (>30
  // actions) — the reason mobile lazy-load/jump/unread were dead. One
  // document-level CAPTURE listener hears every scroller in the page;
  // filtering to the active feed scroller keeps ghost-panel/drawer/tab-strip
  // scrolls from leaking in. Replaces the old per-container onScroll props.
  useEffect(() => {
    const onAnyScroll = (e) => {
      const t = (e.target === document || e.target === window)
        ? (document.scrollingElement || document.documentElement)
        : e.target;
      if (t !== getFeedScrollEl()) return;
      handleChatScroll();
    };
    document.addEventListener('scroll', onAnyScroll, { capture: true, passive: true });
    return () => document.removeEventListener('scroll', onAnyScroll, { capture: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleChatScroll]);

  const scrollToTop = useCallback(() => {
    // Active scroller, not the outer ref — on phones the document scrolls
    // and the old container write was a silent no-op (same class as the
    // 08-02 send/scroll RCA).
    const el = getFeedScrollEl();
    if (el) {
      if (el.scrollTop > 1) programmaticScrollRef.current = true;
      el.scrollTop = 0;
    }
    // Jumping to top is an explicit "I'm reading history": disarm the snap until
    // the user comes back to the bottom themselves
    if (!isBotMode) shouldAutoScrollRef.current = false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isBotMode]);

  // Default to "All" tab for all modes once game status loads
  const hasSetDefaultTab = useRef(false);
  useEffect(() => {
    if (chatGameStatus && !hasSetDefaultTab.current) {
      const isCompMode = chatGameStatus.competitive_mode || chatGameStatus.imposter_mode;
      setChatActionTab('all');
      hasSetDefaultTab.current = true;
    }
  }, [chatGameStatus]);

  // Competitive/imposter mode: force spectate on initial load so tooltip flow works
  // Runs whenever humanPlayerId changes — keeps resetting until user explicitly switches via dropdown
  const userSwitchedToPlayRef = useRef(false);
  const togglePlayDebounceRef = useRef(0); // timestamp of last Play/Spectate toggle
  useEffect(() => {
    // [5g23] The explorer BOT is always an active player — it joins via play_game / skipReadyCheck,
    // never the human "Play" dropdown, so it never sets userSwitchedToPlayRef. Without this guard,
    // this human-onboarding "force spectate on load" effect infinitely RESETS the bot's humanPlayerId
    // to null every time ensureHumanPlayer restores it (5g.23 smoke#3: humanPlayerId null ×868 vs the
    // correct seat ×35 → the turn-input textarea flickered/unmounted → the bot missed every turn while
    // the backend kept routing turns to its seat). The force-spectate + tooltip flow is human-only;
    // skip it entirely in bot mode (URL `bot=1`, set by the MCP open_browser/play_game navigation).
    if (searchParams.get('bot') === '1') return;
    if (userSwitchedToPlayRef.current) return; // User explicitly chose Play via dropdown — stop resetting
    if (tutorialActive) return; // Tutorial controls play/spectate during its steps
    if (!chatGameStatus?.competitive_mode && !chatGameStatus?.imposter_mode) return;
    if (!humanPlayerId) return; // Already in spectate
    setHumanPlayerId(null);
    const url = new URL(window.location);
    url.searchParams.delete('playerId');
    window.history.replaceState({}, '', url);
  }, [chatGameStatus?.competitive_mode, chatGameStatus?.imposter_mode, humanPlayerId]);

  // Override server's is_spectating when the user has explicitly switched to Play via the dropdown.
  // The server may take a poll cycle to catch up after unspectatePlayer() is called.
  const effectiveIsSpectating = chatGameStatus?.is_spectating && !(humanPlayerId && userSwitchedToPlayRef.current);

  // 🔍 Whodunnit STAGE 1 takeover is on screen (mirrors the InvestigationStage
  // mount condition). While it's up, the stage owns the whole viewport: the
  // mobile thumb dock (z5000) and bottom nav (z5100) must NOT render — they
  // float above the z1200 overlay, cover the desk band + I'M READY, and eat
  // taps on every hotspot in the bottom third of the scene (Nina's mini,
  // 07-26: "the circles seem unclickable" — 5 of 9 clue slots sit at y 62-84%).
  const investigationTakeover = !!((gameType === 'chat-game' || gameType === 'chat') && gameId
    && chatGameStatus?.competitive_mode
    && chatGameStatus?.investigation && !chatGameStatus?.game_completed
    && effectivePlayerId && !effectiveIsSpectating);

  // 🎷 FAB vs the stage (07-30 audit): music-carrying packs keep LofiRadio
  // MOUNTED (hiding = unmount = audio dies), but its z100010 FAB floats over
  // the stage's timer/case-file corner. While the takeover is up, LofiRadio
  // tucks the FAB + panel offscreen — same keep-playing pattern as its own
  // minimized state. Effect lives HERE (not with the NOIR_CASE_ACTIVE emit)
  // because investigationTakeover is TDZ-real until this point.
  useEffect(() => {
    emitGameEvent(GAME_EVENTS.NOIR_STAGE_UP, { up: investigationTakeover });
    return () => { if (investigationTakeover) emitGameEvent(GAME_EVENTS.NOIR_STAGE_UP, { up: false }); };
  }, [investigationTakeover]);

  // 🏡⋯ W2d, the PLACE page (PORTED_HOME_HANDOFF §0-ter, behind the switch).
  // The room is the page: today's chrome leaves a place-on ported room and
  // every extra keeps a door in ONE ⋯ menu + a slash command (/watch /live
  // /story; the music's is its panel). When one is ON it opens where the
  // painting is (the stage's takeover slot) and folds back when it ends.
  // 📐 "Shrinks as you chat" (Nina): the stage eases to a strip while the
  // composer is in use or the person has scrolled the chat back, and grows
  // back 5 s after both stop. A layout drift is not a scroll back: only the
  // person's own scroll disarms the follow (handleChatScroll). Desktop only.
  const [placeComposerActive, setPlaceComposerActive] = useState(false);
  const placeWantsStrip = portedPlaceOn && !isMobile
    && (placeComposerActive || shelfOpen || (showJumpToBottom && !shouldAutoScrollRef.current));
  const [placeStrip, setPlaceStrip] = useState(false);
  useEffect(() => {
    if (placeWantsStrip) { setPlaceStrip(true); return undefined; }
    const t = setTimeout(() => setPlaceStrip(false), 5000);
    return () => clearTimeout(t);
  }, [placeWantsStrip]);
  // the feed keeps its newest line in view through the stage's ease (a grow
  // shortens the feed without a scroll event; only a follower re-snaps), and
  // the page-level ↑↓ cluster re-centers on the feed's new box (it is placed
  // only on scroll/resize, so a grow left it floating over the caption)
  React.useLayoutEffect(() => {
    if (portedPlaceOn && !isMobile) placeEaseUntilRef.current = performance.now() + 700;
  }, [placeStrip]); // eslint-disable-line react-hooks/exhaustive-deps
  // 🏡📐 wave 3 (ruling 32): the place page SCROLLS (the document is the
  // scroller), so the fixed ↑↓ cluster follows the feed's on-screen box
  // through a page scroll
  useEffect(() => {
    if (!portedPlaceOn || isMobile) return undefined;
    let raf = 0;
    const onScroll = () => { cancelAnimationFrame(raf); raf = requestAnimationFrame(() => positionJumpCluster()); };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => { cancelAnimationFrame(raf); window.removeEventListener('scroll', onScroll); };
  }, [portedPlaceOn, isMobile]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!portedPlaceOn || isMobile) return undefined;
    const t = setTimeout(() => {
      if (shouldAutoScrollRef.current) scrollToBottom();
      positionJumpCluster();
      // an open drawer re-measures its room (it measures on open and resize)
      if (shelfOpen || dossierOpen) window.dispatchEvent(new Event('resize'));
    }, 480);
    return () => clearTimeout(t);
  }, [placeStrip]); // eslint-disable-line react-hooks/exhaustive-deps
  // a lived-in room's feed virtualizes: its own scroller (a fixed
  // calc(100dvh - 200px)) sits inside the short place feed. Whenever that inner
  // scroller reaches its newest line (the virtualizer's own snap, a scroll, a
  // new message) or the feed box resizes while you follow, the OUTER box
  // follows to its bottom, so the newest line is what you see. A scroll of the
  // outer box itself (to read the cards above) is the person's: left alone.
  useEffect(() => {
    if (!portedPlaceOn || isMobile) return undefined;
    const follow = () => {
      const outer = resultsContainerRef.current;
      const inner = outer && outer.querySelector('[data-virtual-feed]');
      if (!outer || !inner) return;
      if (inner.scrollHeight - inner.scrollTop - inner.clientHeight > 60) return;
      if (outer.scrollHeight - outer.clientHeight - outer.scrollTop > 1) outer.scrollTop = outer.scrollHeight;
    };
    let ro = null; let seen = null;
    const watchOuter = () => {
      const outer = resultsContainerRef.current;
      if (!outer || outer === seen || typeof ResizeObserver === 'undefined') return;
      if (ro) ro.disconnect();
      seen = outer;
      ro = new ResizeObserver(() => { if (shouldAutoScrollRef.current) follow(); });
      ro.observe(outer);
    };
    const onScroll = (e) => {
      const el = e.target;
      if (!el || !el.hasAttribute || !el.hasAttribute('data-virtual-feed')) return;
      watchOuter();
      follow();
    };
    document.addEventListener('scroll', onScroll, { capture: true, passive: true });
    watchOuter();
    const t = setTimeout(() => { watchOuter(); follow(); }, 800);
    return () => { document.removeEventListener('scroll', onScroll, { capture: true }); if (ro) ro.disconnect(); clearTimeout(t); };
  }, [portedPlaceOn, isMobile]);
  // the frame the stage settled on (its columns line up under it)
  const [placeFrame, setPlaceFrame] = useState(null); // { frameW, restH, stripH, laneH }
  const onPlaceFrame = useCallback((f) => setPlaceFrame((p) => (p && f && p.frameW === f.frameW && p.restH === f.restH && p.laneH === f.laneH ? p : (f || null))), []);
  // the ♫ FAB leaves the page (the ⋯ menu's "Music" opens its panel); the music plays on
  useEffect(() => {
    // desktop only: a phone's ⋯ scrolls away with the page, the ♫ is its music door
    if (!portedPlaceOn || isMobile) return undefined;
    emitGameEvent(GAME_EVENTS.MUSIC_FAB_HIDDEN, { hidden: true });
    return () => emitGameEvent(GAME_EVENTS.MUSIC_FAB_HIDDEN, { hidden: false });
  }, [portedPlaceOn, isMobile]);
  // what holds the stage besides the painting: the watch party's launcher or
  // the livestream panel (opened from the ⋯ / a slash command), and the
  // share sheet (the ⋯'s Share: ShareButton in its controlled mode)
  const [placeExtra, setPlaceExtra] = useState(null); // null | 'watch' | 'live'
  // the stream panel is SHOWN while its door is open OR while YOU are live (a
  // publisher's panel must stay connectable: LiveKit reconnects only while
  // visible). It is ON (and takes the stage, over a party you sit at, which
  // plays on off-stage) while you publish or have joined someone's stream.
  // Idle doors (the launcher, a Go live you haven't joined) open as a SHEET
  // under the ⋯ and the painting stays (Nina: "When one is actually ON, it
  // opens where the painting is").
  const placeLiveShown = portedPlaceOn && !isMobile && (placeExtra === 'live' || selfIsLive);
  const placeLiveStage = placeLiveShown && (selfIsLive || (!!livestreamStatus.isConnected && spaceLiveStreamers.length > 0));
  const placeSheet = (portedPlaceOn && !isMobile)
    ? ((placeExtra === 'live' && !placeLiveStage) ? 'live' : (placeExtra === 'watch' && !(watchParty && inTheater)) ? 'watch' : null)
    : null;
  // the slim bar sits on the frame: ‹ Back and ⋯ at its edges, not the screen's
  const placeBarInset = (portedPlaceOn && !isMobile && placeFrame && typeof window !== 'undefined')
    ? Math.max(10, Math.round((window.innerWidth - placeFrame.frameW) / 2)) : 10;
  // a sheet over the stage keeps to the painting's resting box (its bar is 47px)
  const placeStageBudget = placeFrame ? Math.max(120, placeFrame.restH - 47) : 360;
  const placeSheetBar = (icon, label, onClose) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', minHeight: 47, boxSizing: 'border-box', borderBottom: '1px solid rgba(255,236,210,0.08)', color: '#F3E8D8', font: '600 13px/1.2 -apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif' }}>
      <span aria-hidden="true">{icon}</span><span style={{ flex: 1 }}>{label}</span>
      {onClose ? (
        <button type="button" data-prop-ui="" aria-label={`Close ${label}`} onClick={onClose}
          style={{ '--prop-bg': 'transparent', background: 'transparent', border: '1px solid rgba(238,231,245,0.2)', borderRadius: 8, color: '#EEE7F5', width: 30, height: 30, padding: 0, cursor: 'pointer', textShadow: 'none', transform: 'none', boxShadow: 'none' }}>✕</button>
      ) : null}
    </div>
  );
  // Esc closes an idle sheet
  useEffect(() => {
    if (!placeSheet) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setPlaceExtra(null); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [placeSheet]);
  const [placeShareOpen, setPlaceShareOpen] = useState(false);
  const openPortedJournal = useCallback(() => {
    if (isMobile) {
      setMobileView('locals');
      if (localsPlaceRef.current) localsPlaceRef.current.scrollTop = 0;
    }
    setPortedJournalToken((n) => n + 1);
  }, [isMobile]);
  // 📋 W5: /task (and the desk's note) opens the journal's Tasks page
  // `pen`: /task opens the ask pen with its words; the desk note opens the LIST
  // (he finished one: the owner came to read it, not to ask)
  const openPortedTasks = useCallback((text = '', { pen = true } = {}) => {
    if (isMobile) {
      setMobileView('locals');
      if (localsPlaceRef.current) localsPlaceRef.current.scrollTop = 0;
    }
    setPortedTaskToken((t) => ({ n: ((t && t.n) || 0) + 1, text: String(text || ''), pen, at: Date.now() }));
  }, [isMobile]);
  const onPortedDoorDone = useCallback(() => setDoorState(null), []);
  const openPlaceExtra = useCallback((name) => {
    if (name === 'watch') {
      if (isMobile) { setMobileView('tv'); return; }
      if (watchParty) { setPlaceExtra(null); setInTheater(true); return; } // a party is on: take a seat
      setPlaceExtra('watch');
      return;
    }
    if (name === 'live') {
      if (isMobile) { setMobileView('tv'); return; }
      setPlaceExtra('live');
      return;
    }
    if (name === 'story') {
      setPlaceExtra(null);
      setActiveTab('actions');
      setChatActionTab('recap');
      return;
    }
    if (name === 'music') { emitGameEvent(GAME_EVENTS.MUSIC_OPEN, {}); return; }
    if (name === 'share') { setPlaceShareOpen(true); return; }
    if (name === 'shelf') { setDossierOpen(false); setHouseWallOpen(false); setShelfOpen(true); }
  }, [isMobile, watchParty]);
  // a party that STARTS (from the launcher, or anyone's) takes the stage from
  // the launcher; the stage folds back when the party ends
  useEffect(() => {
    if (placeExtra === 'watch' && watchParty && inTheater) setPlaceExtra(null);
  }, [placeExtra, watchParty, inTheater]);
  // a "/watch <words>" you just sent: sit down when your party arrives
  const placeSeatOnPartyRef = useRef(0);
  useEffect(() => {
    if (!watchParty || inTheater || !placeSeatOnPartyRef.current) return;
    if (Date.now() - placeSeatOnPartyRef.current < 30000) { setPlaceExtra(null); setInTheater(true); }
    placeSeatOnPartyRef.current = 0;
  }, [watchParty, inTheater]);
  // a stream that ENDS folds the stage back (the Go live sheet you opened to
  // start your own stays until you close it: it was never "on")
  const placeLiveCountRef = useRef(0);
  useEffect(() => {
    const was = placeLiveCountRef.current;
    placeLiveCountRef.current = spaceLiveStreamers.length;
    if (placeExtra === 'live' && was > 0 && spaceLiveStreamers.length === 0 && !selfIsLive && !livestreamStatus.isConnected) setPlaceExtra(null);
  }, [placeExtra, spaceLiveStreamers.length, selfIsLive, livestreamStatus.isConnected]);

  // Whodunnit guilty-role reveal trigger: fire once when the role data first appears in a
  // status poll (killer assigned at round-1 creation). you_did_it → killer card; case_file →
  // innocent card. [bot-theater 07-12] Bots now get the reveal too — the livestream is the
  // bot's screen and the role card is the best beat in the show. The got_it-blocks-the-bot
  // risk is closed by the bot-mode auto-advance effect below (every phase a human would tap
  // self-advances on a timer, whole overlay hard-capped). Never for spectators.
  // ⚠️ Must stay BELOW the `effectiveIsSpectating` declaration above — referencing it from
  // earlier in the file is a TDZ crash that white-pages the whole route (2026-07-05).
  useEffect(() => {
    if (guiltyRevealShownRef.current) return;
    if (!chatGameStatus || !humanPlayerId || effectiveIsSpectating) return;
    const didIt = !!chatGameStatus.you_did_it;
    const caseFile = chatGameStatus.case_file || null;
    if (!didIt && !caseFile) return; // not a guilty-role game / role not assigned yet
    guiltyRevealShownRef.current = true;
    setDoorTransition(false); // the door beat hands off to the role card
    setShowGuiltyReveal({ didIt, caseFile });
  }, [chatGameStatus, humanPlayerId, isBotMode, effectiveIsSpectating]);

  // Drive the reveal's cinematic phases. Timers advance night → body → victim →
  // role (victim = the obituary beat, only when the pack authors one); a tap
  // steps forward through the beats (see overlay onClick), so nobody can dismiss
  // the whole thing before seeing their card.
  useEffect(() => {
    if (!showGuiltyReveal) return;
    const hasVictimBeat = !!chatGameStatus?.scenario?.victim?.name;
    // How long the obituary holds when nobody taps. It is a BACKSTOP, not a
    // pace: the beat is meant to end on a tap (Nina 07-30 — the old ~5.2s
    // auto-advance cut people off mid-sentence). Bots get the short hold so a
    // livestream never parks on a card no one is going to tap; the bot-mode
    // auto-advance below only covers the role/briefing beats.
    const VICTIM_HOLD_MS = isBotMode ? 6500 : 14000;
    // ATMOSPHERIC run (fires on the curtain door, before any role exists): lightning/body
    // → hand over the room. No role card — that beat fires later, when the player starts
    // the game themselves. skipNight (07-10): the storm interstitial IS the night beat,
    // so the door opens straight onto the lightning + "A BODY HAS TURNED UP!" slam.
    if (showGuiltyReveal.atmospheric) {
      const skipNight = !!showGuiltyReveal.skipNight;
      setGuiltyRevealPhase(skipNight ? 'body' : 'night');
      // The body beat lands on a small distant shriek, not the thunder slam
      // (user call 07-11: the crack read as a loud smack, not a discovery).
      // Shriek trails the body slam by a beat (user call 07-13: see the body
      // FIRST, then someone down the hall screams) — guarded so it stays
      // silent if a tap already skipped past the body beat.
      const shriekDelayed = (ms) => setTimeout(() => setGuiltyRevealPhase(p => {
        if (p === 'body') soundManager.play('shriek');
        return p;
      }), ms);
      const shriekT = skipNight ? shriekDelayed(2000) : shriekDelayed(2200 + 2000);
      const toBody = skipNight ? null : setTimeout(() => setGuiltyRevealPhase(p => (
        p === 'night' ? 'body' : p
      )), 2200);
      // Obituary beat rides between the slam and the handoff. Nina 07-30: it was
      // auto-closing after ~5.2s, which is not enough to read 1-2 sentences of
      // prose people actually want. It now holds until a TAP; the timeout below
      // is only a backstop so an AFK player is never stranded in the cinematic.
      const victimAt = skipNight ? 3400 : 5600;
      const toVictim = hasVictimBeat ? setTimeout(() => setGuiltyRevealPhase(p => (
        p === 'body' ? 'victim' : p
      )), victimAt) : null;
      const done = setTimeout(() => {
        coldOpenPlayedRef.current = true;
        setShowGuiltyReveal(null);
      }, hasVictimBeat ? (victimAt + VICTIM_HOLD_MS) : (skipNight ? 7400 : 9400));
      return () => { if (toBody) clearTimeout(toBody); if (toVictim) clearTimeout(toVictim); clearTimeout(shriekT); clearTimeout(done); };
    }
    // ROLE run: if the atmospheric open already played the night/body beats this
    // session, cut straight to the card (thunder carries the slam). Paths without
    // the curtain keep the full 3-beat cinematic.
    if (coldOpenPlayedRef.current) {
      soundManager.play('thunder');
      setGuiltyRevealPhase('role');
      return;
    }
    setGuiltyRevealPhase('night');
    // Functional guards: a tap may have already skipped to 'role' — never move backwards.
    const toBody = setTimeout(() => setGuiltyRevealPhase(p => (
      p === 'night' ? 'body' : p
    )), 2200);
    // Shriek trails the body slam by a beat (user call 07-13); the phase guard
    // keeps skip-taps to 'role' correctly silent.
    const shriekT = setTimeout(() => setGuiltyRevealPhase(p => {
      if (p === 'body') soundManager.play('shriek');
      return p;
    }), 2200 + 2000);
    // Obituary beat between slam and role card (Nina 07-30 beat split).
    const victimAt = 6400;
    const toVictim = hasVictimBeat ? setTimeout(() => setGuiltyRevealPhase(p => (
      p === 'body' ? 'victim' : p
    )), victimAt) : null;
    // 8.2s → 11s (Nina 07-25: "should last a little longer, so people can
    // read") — the cold-open headline/sub hold longer before the role card;
    // a tap still skips ahead at any point. When the obituary beat runs it now
    // holds for a TAP (Nina 07-30) and this only backstops an AFK player.
    const toRole = setTimeout(() => setGuiltyRevealPhase(p => (p === 'role' ? p : 'role')), hasVictimBeat ? (victimAt + VICTIM_HOLD_MS) : 11000);
    return () => { clearTimeout(toBody); if (toVictim) clearTimeout(toVictim); clearTimeout(shriekT); clearTimeout(toRole); };
    // victim name intentionally NOT a dep — its arrival must not restart the cinematic
  }, [showGuiltyReveal]); // eslint-disable-line react-hooks/exhaustive-deps

  // [bot-theater 07-12] Bot-mode auto-advance for the reveal's TAP beats. The
  // night→body→role timers above run for everyone; what a human taps (role card
  // → briefing → close) self-advances here so the overlay can never hold the
  // bot hostage (the got_it-blocks-the-bot bug class). ~6.5s a beat = long
  // enough for the stream audience to read the card, short enough to stay far
  // clear of the 90s turn window. Atmospheric runs already self-clear.
  useEffect(() => {
    if (!isBotMode || !showGuiltyReveal || showGuiltyReveal.atmospheric) return undefined;
    if (guiltyRevealPhase === 'role') {
      const t = setTimeout(() => {
        if (!showGuiltyReveal.didIt && showGuiltyReveal.caseFile) setGuiltyRevealPhase('briefing');
        else setShowGuiltyReveal(null);
      }, 6500);
      return () => clearTimeout(t);
    }
    if (guiltyRevealPhase === 'briefing') {
      const t = setTimeout(() => setShowGuiltyReveal(null), 6500);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [isBotMode, showGuiltyReveal, guiltyRevealPhase]);

  // Dice roll-reveal toasts — show each player's roll as a screen beat the moment it goes
  // public (replaces the stale end-of-round "Secret word ban revealed" fumble line).
  const advanceDiceToast = useCallback(() => {
    // Arbiter parity with beats (spec Phase B1): while a blocking overlay owns the
    // screen (vote modal / guilty reveal), clear the current toast and hold the queue
    // without consuming it. The enqueue effect or this retry restarts it after.
    if (beatPauseRef.current) {
      diceToastActiveRef.current = false;
      setDiceRollToast(null);
      setTimeout(() => {
        // Re-enter even if still paused — the pause branch re-schedules, so the
        // queue keeps polling instead of stalling until the next enqueue.
        if (!diceToastActiveRef.current && diceToastQueueRef.current.length) advanceDiceToast();
      }, 900);
      return;
    }
    const next = diceToastQueueRef.current.shift();
    if (!next) { diceToastActiveRef.current = false; setDiceRollToast(null); return; }
    diceToastActiveRef.current = true;
    // GROUP BOARD (sim-write waves): everyone's dice tumble together, then the numbers
    // land at once. One beat for the whole table instead of a per-head parade.
    if (next.kind === 'board') {
      setDiceRollToast({ ...next, phase: 'tumble' });
      setTimeout(() => {
        setDiceRollToast(t => (t && t.kind === 'board' ? { ...t, phase: 'landed' } : t));
        const sfx = next.entries.some(e => e.band_name === 'CRITICAL SUCCESS') ? 'diceCrit' : 'diceSuccess';
        soundManager.play(sfx);
      }, 1100);
      setTimeout(advanceDiceToast, 6400);
      return;
    }
    // TWO BEATS: who steps up (big name + avatar) → then the roll lands (die + band +
    // consequence). Sequencing person → outcome is what makes rolls trackable; landing
    // everything at once read as "big number... wait, who was that?".
    setDiceRollToast({ ...next, phase: 'name' });
    setTimeout(() => {
      setDiceRollToast(t => (t && t.pid === next.pid && t.value === next.value ? { ...t, phase: 'roll' } : t));
      // Bad rolls (complication/fumble) stay SILENT — the womp sounds grated (user, 07-08);
      // the visual flash + shake carry those beats. SFX lands WITH the die, not the name.
      const bandSfx = {
        'CRITICAL SUCCESS': 'diceCrit',
        'SUCCESS': 'diceSuccess',
      }[next.band_name];
      if (bandSfx) soundManager.play(bandSfx);
    }, 850);
    setTimeout(advanceDiceToast, 4850);
  }, []);
  useEffect(() => {
    if (isBotMode) return;
    const dice = chatGameStatus?.dice;
    if (!dice?.rolls || !Array.isArray(dice.revealed_ids)) return;
    const seen = diceToastSeenRef.current;
    if (seen.round !== dice.round) {
      // First sighting of a round (or a mid-round page load): baseline what's already
      // public WITHOUT toasting — never replay history.
      seen.round = dice.round;
      seen.ids = new Set(dice.revealed_ids);
      return;
    }
    const fresh = [];
    for (const pid of dice.revealed_ids) {
      if (seen.ids.has(pid)) continue;
      seen.ids.add(pid);
      const roll = dice.rolls[pid];
      if (!roll || !roll.band_name) continue;
      const player = users.find(u => u.game_player_id === pid || u.id === pid);
      const name = player?.name || 'A player';
      const pool = DICE_TOAST_LINES[roll.band_name] || [];
      const line = pool.length ? pool[(roll.value + name.length) % pool.length] : '';
      fresh.push({
        pid, name, value: roll.value, band_name: roll.band_name, emoji: roll.emoji || '🎲', ui: roll.ui || null, line,
        color: playerColorFor(users, pid),
        avatar: player?.profile_image_url || null,
      });
    }
    // 2+ reveals landing in one poll (sim-write waves reveal together) = ONE group
    // "the table rolls" board instead of a ~10s-per-head toast parade. Singles keep
    // the two-beat toast; the roster chips + case file keep the rolls re-readable.
    // Whodunnit (cleanup C2, Nina 07-19): only wave boards + YOUR OWN roll get a
    // full-screen beat — other players' straggler reveals ride the roster dice
    // chips silently (a 6.4s solo board per trickled reveal was its own parade).
    if (fresh.length >= 2) {
      diceToastQueueRef.current.push({ kind: 'board', entries: fresh, label: dice.label || 'FATE ROLL' });
    } else if (fresh.length === 1) {
      if (chatGameStatus?.noir_theme) {
        if (fresh[0].pid === humanPlayerIdRef.current) {
          diceToastQueueRef.current.push({ kind: 'board', entries: fresh, label: dice.label || 'FATE ROLL' });
        }
      } else {
        diceToastQueueRef.current.push(fresh[0]);
      }
    }
    if (!diceToastActiveRef.current && diceToastQueueRef.current.length) advanceDiceToast();
  }, [chatGameStatus, users, isBotMode, advanceDiceToast]);

  // Beat presenter: pause flag tracks overlays that outrank story beats. While paused the
  // card is hidden (render gate) and the queue holds without consuming.
  useEffect(() => {
    beatPauseRef.current = !!(showVotingModal || showGuiltyReveal);
  }, [showVotingModal, showGuiltyReveal]);

  const advanceBeat = useCallback(() => {
    if (beatTimerRef.current) { clearTimeout(beatTimerRef.current); beatTimerRef.current = null; }
    if (beatPauseRef.current || diceToastActiveRef.current) {
      // Something more important is on screen — hold without consuming the queue.
      beatTimerRef.current = setTimeout(advanceBeat, 800);
      return;
    }
    // 🧹 Stale-drop (cleanup C3, Nina 07-19): a beat that waited >25s behind an
    // overlay (voting, reveal) is old news — the chat feed already carries it,
    // and replaying a backlog after the overlay closes reads as popup spam.
    let next = beatQueueRef.current.shift();
    while (next && next.ts && Date.now() - next.ts > 25000) next = beatQueueRef.current.shift();
    if (!next) { beatActiveRef.current = false; setAnnouncementBeat(null); return; }
    beatActiveRef.current = true;
    setAnnouncementBeat(next);
    if (next.index === 0) soundManager.play('announcement');
    beatTimerRef.current = setTimeout(advanceBeat, 8000);
  }, []);

  // Watch for NEW game announcements and queue them as beats. Baselines everything
  // present at page load (never replays history — same pattern as the dice toasts).
  useEffect(() => {
    if (isBotMode) return;
    if (!(chatGameStatus?.competitive_mode || chatGameStatus?.imposter_mode)) return;
    const list = results.flatMap(r => r.actions || []);
    if (!list.length) return;
    const seen = beatSeenRef.current;
    if (!seen.primed) {
      list.forEach(a => { if (a.type === 'game_announcement' && a.id) seen.ids.add(a.id); });
      seen.primed = true;
      return;
    }
    for (const a of list) {
      if (a.type !== 'game_announcement' || !a.id || seen.ids.has(a.id)) continue;
      seen.ids.add(a.id);
      const d = a.data || {};
      // Results/eliminations/finales have their own animated overlays; continuations are
      // deliberately quiet dividers. Neither belongs in the beat card.
      // suppress_beat (backend): procedural notices that have a dedicated surface
      // already — voting-open (grace banner + VotingModal) and the ready-check roster
      // (folds into the round-1 opener beat). They stay in the feed, never pop.
      if (d.vote_results || d.eliminated_player || d.final_rankings || d.imposter_result || d.is_continuation || d.suppress_beat) continue;
      if (!d.message) continue;
      // 🧹 Whodunnit tier gate (Nina 07-19, cleanup C3): the popup channel is
      // reserved for game-changers — confessions, twists, hospitalizations, or
      // anything the backend explicitly flags (d.beat). Procedural notices
      // (round start/end, verdict recaps a cinematic already showed) stay
      // readable in the chat feed + act tracker, without stealing a click.
      if (chatGameStatus?.noir_theme
        && !(d.confession || d.twist || d.hospitalization || d.beat === true)) continue;
      const beats = splitAnnouncementIntoBeats(d.message);
      beats.forEach((text, i) => beatQueueRef.current.push({ text, index: i, total: beats.length, ts: Date.now() }));
    }
    // Anti-parade cap (user, 07-11: popups pile up): every announcement already renders
    // in the chat feed, so a deep beat backlog is pure repetition. Keep only the 5
    // newest queued beats; the rest are readable in chat.
    if (beatQueueRef.current.length > 5) {
      beatQueueRef.current = beatQueueRef.current.slice(-5);
    }
    if (!beatActiveRef.current && beatQueueRef.current.length) advanceBeat();
  }, [results, chatGameStatus, isBotMode, advanceBeat]);

  // Inject CSS animations for damaging actions
  useEffect(() => {
    const styleId = 'damaging-action-animations';

    // Check if style already exists
    if (document.getElementById(styleId)) return;

    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = `
      /* Compositor-only glows (fx-bench 07-09): animating box-shadow or
         background-position repaints every frame on the CPU. These pulse
         opacity/transform instead — same visible throb, near-zero cost. */
      @keyframes gradientShift {
        0% { background-position: 0% 50%; }
        50% { background-position: 100% 50%; }
        100% { background-position: 0% 50%; }
      }

      @keyframes damagePulse {
        0%, 100% { opacity: 0.5; }
        50% { opacity: 1; }
      }

      @keyframes pulse {
        0%, 100% { opacity: 1; transform: scale(1); }
        50% { opacity: 0.8; transform: scale(1.05); }
      }

      @keyframes joinPlayerGlow {
        0%, 100% { transform: scale(1); }
        50%      { transform: scale(1.025); }
      }

      @keyframes joinPingRing {
        0%   { transform: scale(1); opacity: 0.75; }
        70%  { transform: scale(1.09, 1.5); opacity: 0; }
        100% { transform: scale(1.09, 1.5); opacity: 0; }
      }

      @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }

      @keyframes liveBadgePulse {
        0%, 100% { opacity: 1; }
        50% { opacity: 0.6; }
      }
      @keyframes moderatorBob {
        0%, 100% { transform: translateY(0); }
        50% { transform: translateY(-4px); }
      }
      @keyframes moderatorFadeIn {
        from { opacity: 0; transform: translateY(4px); }
        to { opacity: 1; transform: translateY(0); }
      }
      /* Host portrait "materializing" sweep (Arc 8, 07-16) — plays over the
         emoji fallback while the portrait generates. Finite iterations: after
         ~72s it settles so a permanently-failed image isn't a forever-glimmer. */
      @keyframes hostPortraitSheen {
        0% { transform: translateX(-100%) skewX(-12deg); }
        60%, 100% { transform: translateX(220%) skewX(-12deg); }
      }
      .host-portrait-materializing::after {
        content: '';
        position: absolute;
        inset: 0;
        width: 45%;
        background: linear-gradient(105deg, transparent 0%, rgba(255,255,255,0.28) 50%, transparent 100%);
        animation: hostPortraitSheen 1.8s ease-in-out 40;
        pointer-events: none;
      }
      /* Hide the horizontal scrollbar on the in-space tab bar (Chrome/Safari).
         Firefox + IE are handled inline via scrollbar-width / -ms-overflow-style.
         Tabs still scroll horizontally — we just suppress the visible chrome. */
      .space-tab-bar::-webkit-scrollbar { display: none; height: 0; }
      /* Tabs themselves should never shrink below their natural width — that's
         what was making the right-most tabs disappear on narrow viewports. */
      .space-tab-bar > div,
      .space-tab-bar > button { flex-shrink: 0; }
    `;
    document.head.appendChild(style);

    return () => {
      const element = document.getElementById(styleId);
      if (element) element.remove();
    };
  }, []);

  // Keep isPlayingRef in sync with isPlaying state
  useEffect(() => {
    isPlayingRef.current = isPlaying;
  }, [isPlaying]);

  // Update player-model mapping when users data changes
  useEffect(() => {
    const newPlayerModelMap = {};
    // console.log('users', users); // DISABLED
    users.forEach(userObject => {
      const user = userObject;
      newPlayerModelMap[user.name] = getDisplayModelName(user);
    });
    // console.log('newPlayerModelMap', newPlayerModelMap) // DISABLED
    setPlayerModelMap(newPlayerModelMap);
  }, [users]);

  // Load more actions for pagination
  const loadMoreActions = useCallback(async () => {
    if (!gameId || !pagination || !pagination.hasMore || isLoadingMore) return;

    setIsLoadingMore(true);
    try {
      const nextPage = pagination.page + 1;
      const url = `${API_BASE_URL}/api/takeGameStep?id=${gameId}&loadAll=true&page=${nextPage}`;
      const response = await fetch(url);
      const data = await parseApiResponse(response);

      if (data.actions && data.actions.length > 0) {
        // Append new actions to existing results
        setResults(prevResults => {
          if (prevResults.length === 0) return [data];
          const existingResult = prevResults[0];
          return [{
            ...existingResult,
            actions: [...existingResult.actions, ...data.actions]
          }];
        });
      }

      // Update pagination info
      if (data.pagination) {
        setPagination(data.pagination);
      }
    } catch (error) {
      console.error('Error loading more actions:', error);
    } finally {
      setIsLoadingMore(false);
    }
  }, [gameId, pagination, isLoadingMore]);

  // Handle initial loading from CustomSpace navigation
  useEffect(() => {
    console.log('[InitialLoading] useEffect triggered - showInitialLoading:', showInitialLoading, 'initialGameId:', initialGameId);

    if (!showInitialLoading || !initialGameId) {
      console.log('[InitialLoading] Skipping - conditions not met');
      return;
    }

    const handleInitialLoading = async () => {
      console.log('[InitialLoading] Starting asset processing for game:', initialGameId);

      try {
        // Step 1: Process assets
        setInitialLoadingStep(0);
        setInitialLoadingProgress(10);
        setInitialLoadingMessage('Giving the locals their glow-ups... 💅');

        await chatGameApi.processGameAssets(initialGameId, (progress, message) => {
          // Map progress (0-100) to our range (10-60)
          const mappedProgress = 10 + (progress * 0.5);
          setInitialLoadingProgress(Math.round(mappedProgress));

          if (progress < 30) {
            setInitialLoadingMessage('Painting the vibes... 🎨');
          } else if (progress < 70) {
            setInitialLoadingMessage('Teaching the locals how to be iconic... 🌟');
          } else {
            setInitialLoadingMessage('Adding finishing touches to the squad... ✨');
          }
        });

        // Step 2: Poll for game to be fully initialized
        setInitialLoadingStep(1);
        setInitialLoadingProgress(65);
        setInitialLoadingMessage('Herding the locals into position... 🐑');

        let gameReady = false;
        let pollAttempts = 0;
        const maxPollAttempts = 30;

        while (!gameReady && pollAttempts < maxPollAttempts) {
          try {
            const status = await chatGameApi.getGameStatus(gameId, humanPlayerId);
            console.log('[InitialLoading] Game status poll:', status);

            if (status && (status.players?.length > 0 || status.current_round !== undefined)) {
              gameReady = true;
              setInitialLoadingProgress(90);
              setInitialLoadingMessage('Almost showtime... 🎬');
            } else {
              pollAttempts++;
              setInitialLoadingProgress(65 + (pollAttempts / maxPollAttempts) * 25);
              await new Promise(resolve => setTimeout(resolve, 1000));
            }
          } catch (pollError) {
            console.log('[InitialLoading] Poll attempt:', pollAttempts, pollError.message);
            pollAttempts++;
            await new Promise(resolve => setTimeout(resolve, 1000));
          }
        }

        // Step 3: Ready!
        setInitialLoadingStep(2);
        setInitialLoadingProgress(100);
        setInitialLoadingMessage('Let the chaos begin! 🎉');

        // Brief delay to show the "Ready" state
        await new Promise(resolve => setTimeout(resolve, 500));

        // Remove loading param from URL and hide overlay
        const newParams = new URLSearchParams(searchParams);
        newParams.delete('loading');
        setSearchParams(newParams, { replace: true });
        setShowInitialLoading(false);

      } catch (error) {
        console.error('[InitialLoading] Error:', error);
        // Hide overlay on error - let the page load normally
        setShowInitialLoading(false);
      }
    };

    handleInitialLoading();
  }, [showInitialLoading, initialGameId, gameId, humanPlayerId, searchParams, setSearchParams]);

  // Auto-load game metadata on mount
  useEffect(() => {
    const loadInitialGameData = async () => {
      if (!gameId) return;

      setIsLoading(true);

      // Fire getGameStatus early to trigger server-side ghost message generation ASAP.
      // This runs in parallel with the roundInfo fetch — no need to await or gate on gameType.
      // The API only needs gameId. Any existing ghost/spectating messages are populated immediately.
      const earlyStatusPromise = chatGameApi.getGameStatus(gameId, humanPlayerId).then(status => {
        if (!status?.recent_messages) return;
        // Process ALL message types through the shared handler — ghost chat,
        // moderator, preseed, announcements — so sounds, dots, and state all fire.
        const actions = status.recent_messages.map(msg => convertPolledMessageToAction(msg, status.players));
        actions.forEach(a => processIncomingAction(a, { silent: true }));
      }).catch(err => {
        console.log('[loadInitialGameData] Early status fetch failed (non-critical):', err.message);
      }).finally(() => {
        setIsInitialSpectatorHistoryLoading(false);
      });

      try {
        // Single request: fetch round metadata + current round actions together
        console.log('[loadInitialGameData] Fetching round info + actions...');
        const url = `${API_BASE_URL}/api/takeGameStep?id=${gameId}&getRoundInfo=true&includeCurrentActions=true`;
        // [5g19-trace] page-mount cascade fingerprint — see Cycle 5g.19 fix6
        // smoke: "Error loading initial game data: TypeError: Failed to fetch"
        // at this exact fetch call. Wraps ONLY the fetch (response parsing
        // stays in the existing try/catch). Page never finishes loading when
        // this fails → play_game UI poll times out → Bug #3 second class.
        const t0 = Date.now();
        let response;
        try {
          response = await fetch(url);
        } catch (e) {
          console.error(`[5g19-trace] loadInitialGameData THREW name=${e.name} msg=${e.message} url=${url} dt=${Date.now()-t0}ms gameId=${gameId}`);
          throw e;
        }
        const data = await parseApiResponse(response);

        if (!data.success) {
          console.error('[loadInitialGameData] Failed to get round info:', data);
          setIsLoading(false);
          return;
        }

        // Store round metadata
        setRoundsMetadata(data.rounds || []);
        if (data.users) {
          setUsers(data.users);
        }
        if (data.gameType) {
          setGameType(data.gameType);
        }
        if (data.gameInstanceId) {
          setGameInstanceId(data.gameInstanceId);
        }
        if (data.gameName) {
          setGameName(data.gameName);
        }
        // Track space view
        trackEvents.spaceViewed({ spaceId: gameId, spaceName: data.gameName, gameType: data.gameType });
        analytics.group('space', gameId, { name: data.gameName, type: data.gameType });
        // Load voting counts
        if (data.likes_count !== undefined) {
          setSpaceLikes(data.likes_count);
        }
        if (data.dislikes_count !== undefined) {
          setSpaceDislikes(data.dislikes_count);
        }
        if (data.total_views !== undefined) {
          setSpaceVisits(data.total_views);
        }

        // Start music early — before loading finishes
        if (data.music_url) {
          // Same compound key as the status effect above (url|queue-urls|
          // jukebox anchor) so the two sites agree on "already emitted".
          const earlyQueue = Array.isArray(data.music_queue) && data.music_queue.length ? data.music_queue : null;
          const earlyJukebox = data.jukebox_started_at
            ? { started_at: data.jukebox_started_at, head_duration_s: data.music_url_duration_s || null }
            : null;
          musicUrlRef.current = `${data.music_url}|${earlyQueue ? earlyQueue.map(q => q?.url || '').join(',') : ''}|${earlyJukebox ? earlyJukebox.started_at : ''}`;
          emitGameEvent(GAME_EVENTS.MUSIC_URL_CHANGED, { musicUrl: data.music_url, volume: SPACE_MUSIC_VOLUME, ...(earlyQueue ? { queue: earlyQueue } : {}), ...(earlyJukebox ? { jukebox: earlyJukebox } : {}), ...(data.music_url_title ? { headTitle: data.music_url_title } : {}), ...(data.house_aux_holder ? { auxHolder: data.house_aux_holder } : {}), ...(data.competitive_mode || data.imposter_mode ? { competitive: true } : {}) });
        }

        // Default to current round (latest round)
        const lastRound = data.rounds?.[data.rounds.length - 1];
        if (lastRound) {
          setSelectedRound('current');

          // Actions are already included in the response
          if (data.pagination) {
            setPagination(data.pagination);
          }
          if (data.actions) {
            // 🔀 08-03 (chat-tail audit): MERGE, don't replace. This heavier
            // fetch races the earlyStatusPromise (and live WS) — messages
            // those already ingested (incl. the user's own optimistic send)
            // can be missing from data.actions, and the old wholesale
            // `setResults([data])` blinked them out of the feed. Keep any
            // existing action whose id isn't in the payload, sorted in by
            // created_at (stable sort keeps same-timestamp order).
            // Reset dedup ref to the payload FIRST (prevents the race where
            // earlyStatusPromise already added IDs for messages from previous
            // rounds and later polls skip them as "already processed");
            // the merge updater below ADDS the kept leftovers' ids — inside
            // the updater because React may run it after this synchronous
            // block, and adding is idempotent under a StrictMode double-run.
            const incomingIds = new Set(data.actions.map(a => a.id).filter(Boolean));
            processedActionIdsRef.current = new Set(incomingIds);
            // 09-20 (double-message audit): an optimistic bubble is NOT a
            // leftover when the payload already carries its real row. Its id
            // is never in incomingIds, so it was kept BESIDE the real one, and
            // the later WS echo then died at the id dedupe: a permanent twin.
            const incomingClientIds = new Set(data.actions.map(a => a?.data?.client_msg_id).filter(Boolean));
            setResults(prev => {
              const leftovers = prev.flatMap(r => r.actions || [])
                .filter(a => a && a.id && !incomingIds.has(a.id)
                  && !(a.isOptimistic && a.data?.client_msg_id && incomingClientIds.has(a.data.client_msg_id)));
              if (!leftovers.length) return [data];
              leftovers.forEach(a => processedActionIdsRef.current.add(a.id));
              const merged = [...data.actions, ...leftovers].sort((x, y) =>
                (Date.parse(x.created_at) || 0) - (Date.parse(y.created_at) || 0));
              return [{ ...data, actions: merged }];
            });
            // Route spectating rows into spectatingMessages — `results` doesn't feed
            // the ghost chat panel, so leaving these IDs "processed" without routing
            // would block earlyStatusPromise's recent_messages from ever
            // populating the panel on rejoin.
            data.actions.forEach(a => {
              if (a && (a.type === 'spectating_message' || a.type === 'spectating')) {
                processIncomingAction(a, { silent: true });
              }
            });
          }
        }

        // Loading is done — user can see and interact with the space now.
        // Remaining fetches (spaces, round analysis) are background caching.
        setIsLoading(false);

        // Fetch spaces + round analysis history in parallel (non-blocking)
        if (data.gameInstanceId) {
          const gid = data.gameInstanceId;
          Promise.all([
            fetch(`${API_BASE_URL}/api/takeGameStep?getGameSpaces=true&gameInstanceId=${gid}`)
              .then(r => r.ok ? r.json() : null)
              .then(spacesResult => {
                if (spacesResult?.success && spacesResult.spaces) {
                  let flatSpaces = [];
                  if (Array.isArray(spacesResult.spaces)) {
                    flatSpaces = spacesResult.spaces;
                  } else if (typeof spacesResult.spaces === 'object') {
                    flatSpaces = [
                      ...(spacesResult.spaces.public || []),
                      ...(spacesResult.spaces.private || []),
                      ...(spacesResult.spaces.secret || [])
                    ];
                  }
                  setCachedSpaces(flatSpaces);
                }
              })
              .catch(e => console.error('Error caching spaces:', e)),
            fetch(`${API_BASE_URL}/api/takeGameStep?getRoundAnalysisHistory=true&gameInstanceId=${gid}`)
              .then(r => r.ok ? r.json() : null)
              .then(historyResult => {
                if (historyResult?.roundAnalyses) {
                  const sorted = historyResult.roundAnalyses.sort((a, b) =>
                    new Date(a.created_at) - new Date(b.created_at)
                  );
                  setCachedRoundAnalysisHistory(sorted);
                }
              })
              .catch(e => console.error('Error caching round analysis history:', e))
          ]);
        }
      } catch (error) {
        console.error('Error loading initial game data:', error);
        setIsLoading(false);
      }
    };

    loadInitialGameData();
  }, []); // Run once on mount (gameId is captured from initial state)

  // Poll for asset generation status and update loading overlay
  useEffect(() => {
    if (!gameId) return;

    let isFirstPoll = true;
    let pollCount = 0;
    let stopPollingFn = null;
    let prevThemeStatus = null; // Track theme status transitions to trigger refresh
    const MAX_POLLS = 60; // Maximum 2 minutes of polling (60 * 2 seconds)

    const donePolling = () => {
      if (stopPollingFn) stopPollingFn();
    };

    // Start polling for asset status (fallback — WebSocket delivers updates instantly)
    stopPollingFn = chatGameApi.startAssetPolling(
      gameId,
      (err, status) => {
        // If WebSocket already delivered all_complete, skip polling
        if (wsAssetCompleteRef.current) {
          console.log('[Asset Polling] WebSocket already delivered completion, stopping poll');
          donePolling();
          return;
        }

        if (err) {
          console.error('[Asset Polling] Error:', err);
          // Assets failed - transition to ready state so user can dismiss
          setAssetLoadingMessage('Opening the space as-is... ✨');
          setTimeout(() => {
            setAssetLoadingReady(true);
            if (imposterModeRef.current && !imposterAutoStartedRef.current) {
              imposterAutoStartedRef.current = true;
              handleRoundJoinRef.current?.()
                ?.then(() => setShowAssetLoadingOverlay(false))
                ?.catch(() => setShowAssetLoadingOverlay(false));
            }
          }, 1500);
          donePolling();
          return;
        }

        pollCount++;
        console.log('[Asset Polling] Status update:', status);
        setAssetLoadingStatus(status);

        // When theme transitions to complete, refresh full game status to apply CSS
        // (fallback in case WebSocket broadcast was missed)
        const currentThemeStatus = status?.game_assets?.ui_theme;
        if (currentThemeStatus === 'complete' && prevThemeStatus && prevThemeStatus !== 'complete') {
          console.log('[Asset Polling] Theme just completed, refreshing game status to apply CSS');
          fetchChatGameStatus();
        }
        prevThemeStatus = currentThemeStatus;

        // Grab game name from first poll response
        if (status?.game_name && !gameName) {
          setGameName(status.game_name);
        }

        // If all assets are already complete on first poll, skip overlay entirely (returning visitor).
        // lobby_skip_ready also takes the fast path: it means names+theme+profiles are done even if
        // bg/banner are still 'pending' (e.g. recovery in progress). Bg/banner aren't gating play.
        if (isFirstPoll && (status?.all_complete || status?.lobby_skip_ready)) {
          isFirstPoll = false;
          donePolling();
          if (!competitiveModeRef.current && !imposterModeRef.current) {
            // First-ever visit to a FINISHED space (new user / shared link): hold
            // the overlay in its ready state so the space intro (captions → host
            // → locals → welcome) plays for them too — the same show the creator
            // got, and its Enter click doubles as the audio-unlock this instant
            // drop never had (user call 07-12). The space keeps loading + polling
            // underneath, so the intro adds zero latency. Returning visitors
            // (flag set; private mode counts as seen) keep the instant drop.
            let introUnseen = false;
            try { introUnseen = !localStorage.getItem(`space_intro_v1_${gameId}`); } catch (e) { /* private mode */ }
            if (introUnseen) {
              introVisitorRef.current = true;
              setAssetLoadingReady(true);
              return;
            }
            // Non-competitive: wait until spectating messages (moderator/ghost chat) arrive, max 8s
            // ⏩ Ported spaces never wait (Nina 09-24): they have no ghost
            // chatter (the generator is off there), so every return sat the
            // full 8s. They drop the moment the ported signal is in; the
            // interval still catches a status that lands after this poll, so
            // the room never opens on non-ported chrome.
            if (spectatingMessagesRef.current.length > 0 || portedLoadRef.current) {
              setShowAssetLoadingOverlay(false);
            } else {
              const startTime = Date.now();
              const checkInterval = setInterval(() => {
                if (spectatingMessagesRef.current.length > 0 || portedLoadRef.current || Date.now() - startTime > 8000) {
                  clearInterval(checkInterval);
                  setShowAssetLoadingOverlay(false);
                }
              }, 300);
            }
          } else {
            setTimeout(() => {
              setShowAssetLoadingOverlay(false);
            }, 3000);
          }
          return;
        }

        isFirstPoll = false;

        // Check if we've been polling too long - let user proceed
        if (pollCount >= MAX_POLLS) {
          console.log('[Asset Polling] Max polls reached, transitioning to ready');
          setAssetLoadingMessage('This world is taking a little longer to build... 🏗️');
          setTimeout(() => {
            setAssetLoadingReady(true);
            if (imposterModeRef.current && !imposterAutoStartedRef.current) {
              imposterAutoStartedRef.current = true;
              handleRoundJoinRef.current?.()
                ?.then(() => setShowAssetLoadingOverlay(false))
                ?.catch(() => setShowAssetLoadingOverlay(false));
            }
          }, 1500);
          donePolling();
          return;
        }

        // Transition to ready state as soon as game is playable (player names + theme CSS ready)
        // Don't wait for all assets (like profile images) to complete
        // Show "Ready to play!" briefly, then transition to round announcement
        if (status?.can_start_playing) {
          setAssetLoadingMessage(competitiveModeRef.current ? 'Ready to play!' : 'All Done!');
          // Competitive: 800ms brief flash before showing Let's Go. Non-competitive: 1.5s to let the message breathe.
          setTimeout(() => {
            setAssetLoadingReady(true);
            if (imposterModeRef.current && !imposterAutoStartedRef.current) {
              imposterAutoStartedRef.current = true;
              handleRoundJoinRef.current?.()
                ?.then(() => setShowAssetLoadingOverlay(false))
                ?.catch(() => setShowAssetLoadingOverlay(false));
            }
          }, competitiveModeRef.current ? 800 : 1500);
          donePolling();
          return;
        }

        // Fallback: If polling too long with failures, let user play anyway
        const hasFailures = status?.has_failures || false;
        if (hasFailures && pollCount > 5) {
          setAssetLoadingMessage('Improvising a few missing pieces... 🎨');
          setTimeout(() => {
            setAssetLoadingReady(true);
            if (imposterModeRef.current && !imposterAutoStartedRef.current) {
              imposterAutoStartedRef.current = true;
              handleRoundJoinRef.current?.()
                ?.then(() => setShowAssetLoadingOverlay(false))
                ?.catch(() => setShowAssetLoadingOverlay(false));
            }
          }, 1500);
          donePolling();
          return;
        }

        // Update loading message based on current status
        if (status) {
          const generatingAssets = [];

          // Fun rotating messages for each asset state
          const funThemeMessages = [
            'Mixing the perfect color palette... 🎨',
            'Choosing fonts with impeccable taste... ✍️',
            'Designing the aesthetic... 💅',
            'Making everything look chef\'s kiss... 🤌',
            'Picking out the drapes... 🪟',
            'Setting the vibe just right... 🌈',
            'Feng shui-ing the layout... 🧘',
            'Consulting the color wheel gods... 🎡',
            'Adjusting the mood lighting... 🕯️',
            'Making it Pinterest-worthy... 📌',
            'Curating the visual experience... 👁️',
            'Obsessing over hex codes... #️⃣',
            'Manifesting the perfect vibe... ✨',
            'Interior decorating in progress... 🛋️',
            'Polishing the pixels... 💎',
            'Coordinating the color story... 🌸',
            'Adding that je ne sais quoi... 🇫🇷',
            'Running it by the design committee... 🧐',
          ];
          const funBgMessages = [
            'Painting the scenery... 🖼️',
            'Building the set... 🏗️',
            'Rolling out the red carpet... 🎬',
            'Hanging up the wallpaper... 🪄',
            'Landscaping the background... 🌄',
            'Photoshopping the skyline... 🏙️',
            'Arranging the furniture... 🪑',
            'Planting the digital trees... 🌳',
            'Setting the stage... 🎭',
            'Summoning the backdrop... 🌌',
            'Getting Bob Ross on the line... 🖌️',
            'Stretching the canvas... 🖼️',
            'Ordering props from Amazon... 📦',
            'Making sure the lighting is dramatic... 💡',
          ];
          const pickFun = (arr) => arr[Math.floor(Math.random() * arr.length)];

          // Check game assets with fun messages
          if (status.game_assets?.ui_theme === 'generating') {
            generatingAssets.push(pickFun(funThemeMessages));
          } else if (status.game_assets?.ui_theme === 'pending') {
            generatingAssets.push(pickFun(funThemeMessages));
          } else if (status.game_assets?.ui_theme === 'failed') {
            generatingAssets.push('Theme had stage fright, going with the classic look 🎭');
          }

          if (status.game_assets?.background_image === 'generating') {
            generatingAssets.push(pickFun(funBgMessages));
          } else if (status.game_assets?.background_image === 'pending') {
            generatingAssets.push(pickFun(funBgMessages));
          } else if (status.game_assets?.background_image === 'failed') {
            generatingAssets.push('Background went AWOL, keeping it minimal 🕶️');
          }

          // Check player assets with names
          const generatingPlayers = status.player_assets?.filter(
            p => !p.is_human && p.profile_image_status === 'generating'
          ) || [];

          const pendingPlayers = status.player_assets?.filter(
            p => !p.is_human && p.profile_image_status === 'pending'
          ) || [];

          const failedPlayers = status.player_assets?.filter(
            p => !p.is_human && p.profile_image_status === 'failed'
          ) || [];

          // World-building voice, never "players" (user call 07-10: loading
          // should feel like a world coming to life, not actors prepping a show)
          const nounSingular = competitiveModeRef.current ? 'character' : 'local';
          const nounPlural = competitiveModeRef.current ? 'characters' : 'locals';

          if (generatingPlayers.length > 0) {
            const playerNames = generatingPlayers.slice(0, 2).map(p => p.player_name).join(', ');
            const portraitMessages1 = [
              `The world is painting ${playerNames} into the scenery... 🎨`,
              `${playerNames} is taking shape... ✨`,
              `Breathing life into ${playerNames}... 🌱`,
              `${playerNames} is stepping out of the mist... 🌫️`,
              `${playerNames} is finding their face... 🪞`,
            ];
            const portraitMessages2 = [
              `${playerNames} are materializing side by side... ✨`,
              `${playerNames} are being sketched into the world... 🖌️`,
              `${playerNames} are stepping out of the mist together... 🌫️`,
              `The world is making room for ${playerNames}... 🏡`,
            ];
            const portraitMessagesMany = [
              `${playerNames} + ${generatingPlayers.length - 2} more taking shape... ✨`,
              `${generatingPlayers.length} ${nounPlural} are settling into the world... 🏡`,
              `A whole crowd is materializing... 🌫️`,
              `${generatingPlayers.length} ${nounPlural} still being painted in... 🎨`,
            ];
            if (generatingPlayers.length === 1) {
              generatingAssets.push(pickFun(portraitMessages1));
            } else if (generatingPlayers.length === 2) {
              generatingAssets.push(pickFun(portraitMessages2));
            } else {
              generatingAssets.push(pickFun(portraitMessagesMany));
            }
          } else if (pendingPlayers.length > 0) {
            const pendingMessages = [
              `${pendingPlayers.length} ${pendingPlayers.length > 1 ? nounPlural : nounSingular} waiting at the edge of the world... 🌫️`,
              `${pendingPlayers.length} ${pendingPlayers.length > 1 ? nounPlural : nounSingular} haven't stepped into view yet... 👣`,
              `${pendingPlayers.length} ${pendingPlayers.length > 1 ? nounPlural : nounSingular} still taking form... 🌱`,
              `${pendingPlayers.length} ${pendingPlayers.length > 1 ? nounPlural : nounSingular} on their way here... 🧭`,
            ];
            generatingAssets.push(pickFun(pendingMessages));
          }

          if (failedPlayers.length > 0) {
            const failMessages = [
              `${failedPlayers.length} ${failedPlayers.length > 1 ? nounPlural : nounSingular} went incognito 🕶️`,
              `${failedPlayers.length} ${failedPlayers.length > 1 ? nounPlural : nounSingular} chose the mystery look 🎭`,
              `${failedPlayers.length} ${failedPlayers.length > 1 ? nounPlural : nounSingular} stayed in the shadows... 🌒`,
            ];
            generatingAssets.push(pickFun(failMessages));
          }

          // Set the most relevant message
          if (generatingAssets.length > 0) {
            setAssetLoadingMessage(generatingAssets[0]);
          } else {
            const finalMessages = [
              'Planting virtual trees... 🌳',
              'Putting on the final sparkles... ✨',
              'Hanging the sky... 🌌',
              'Watering the scenery... 🪴',
              'Dimming the lights just right... 🌙',
              'Teaching the birds their songs... 🐦',
              'Letting the world take its first breath... 🌅',
              'Everything\'s coming together... 🧩',
            ];
            setAssetLoadingMessage(pickFun(finalMessages));
          }
        }
      },
      5000 // Fallback poll every 5 seconds (WebSocket delivers updates instantly)
    );

    // Also poll game status every 5s while loading, to populate players early
    let gameStatusInterval = setInterval(() => {
      fetchChatGameStatus();
    }, 5000);
    // Kick off one immediately
    fetchChatGameStatus();

    return () => {
      donePolling();
      if (gameStatusInterval) clearInterval(gameStatusInterval);
    };
  }, [gameId]);

  // 🖼️ Image-straggler watchdog — root-cause fix for "banner takes forever and
  // sometimes only a refresh fixes it" (07-14). The bg/banner RETRY path lives
  // server-side in getChatGameAssetStatus (stuck-images recovery, 2-min throttle),
  // but every polling path above stops within ~45s of mount (can_start_playing /
  // WS short-circuit / MAX_POLLS) — before the banner, which generates
  // sequentially AFTER the background (~90-150s in). One null image attempt (or a
  // worker death after the bg) left banner_image 'pending' forever; only a page
  // refresh (fresh polling loop → recovery trigger) revived it. This keeps a SLOW
  // asset-status poll alive while bg/banner are 'pending': every 30s, starting
  // 2 min after mount, for at most 12 more minutes — each call IS the recovery
  // trigger. Delivery needs nothing extra (the regular status poll carries
  // ui_theme). Terminal states (complete/failed/skipped) end it, so broken old
  // spaces cost at most one bounded burst per visit.
  useEffect(() => {
    if (!gameId) return;
    let cancelled = false;
    let intervalId = null;
    const startedAt = Date.now();

    const tick = async () => {
      if (cancelled) return;
      if (Date.now() - startedAt > 12 * 60 * 1000) {
        if (intervalId) clearInterval(intervalId);
        return;
      }
      if (document.hidden) return; // honor pause-on-blur economics
      try {
        const status = await chatGameApi.getAssetStatus(gameId);
        if (cancelled) return;
        const ga = status?.game_assets || {};
        const stillPending = ga.background_image === 'pending' || ga.banner_image === 'pending';
        if (!stillPending && intervalId) clearInterval(intervalId);
      } catch (e) {
        // transient — next tick retries; the watchdog is best-effort
      }
    };

    const delayId = setTimeout(() => {
      tick();
      intervalId = setInterval(tick, 30000);
    }, 120000);

    return () => {
      cancelled = true;
      clearTimeout(delayId);
      if (intervalId) clearInterval(intervalId);
    };
  }, [gameId]);

  const handleGameIdChange = (e) => {
    const newGameId = e.target.value;
    setGameId(newGameId);
    setSearchParams({ gameId: newGameId });
  };

  // Handle round selection change - fetch actions for the selected round
  const handleRoundChange = async (roundValue) => {
    console.log('[handleRoundChange] Selected round:', roundValue);
    setSelectedRound(roundValue);

    // If 'current' is selected, use the last round
    const roundNumber = roundValue === 'current'
      ? roundsMetadata.length
      : parseInt(roundValue, 10);

    // Find the round from metadata
    const round = roundsMetadata.find(r => r.roundNumber === roundNumber);
    if (!round) {
      console.warn('[handleRoundChange] Round not found in metadata:', roundNumber);
      return;
    }

    // Fetch actions for this round
    setIsLoading(true);
    try {
      const url = `${API_BASE_URL}/api/takeGameStep?id=${gameId}&loadAll=true&roundId=${round.id}`;
      console.log(`[handleRoundChange] Fetching actions for round ${roundNumber} (roundId: ${round.id})`);
      const response = await fetch(url);
      const data = await parseApiResponse(response);

      if (data.pagination) {
        setPagination(data.pagination);
      }
      if (data.actions) {
        setResults([data]);
        processedActionIdsRef.current = new Set(data.actions.map(a => a.id).filter(Boolean));
      }
    } catch (error) {
      console.error('[handleRoundChange] Error fetching round actions:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Helper function to deep merge objects
  const deepMerge = (target, source) => {
    const output = { ...target };
    if (isObject(target) && isObject(source)) {
      Object.keys(source).forEach(key => {
        if (isObject(source[key])) {
          if (!(key in target)) {
            output[key] = source[key];
          } else {
            output[key] = deepMerge(target[key], source[key]);
          }
        } else {
          output[key] = source[key];
        }
      });
    }
    return output;
  };

  const isObject = (item) => {
    return item && typeof item === 'object' && !Array.isArray(item);
  };

  // Callback to update an action without reloading the page
  const updateAction = useCallback((actionId, dataUpdates) => {
    console.log('[updateAction] Updating action:', actionId, 'with data:', dataUpdates);
    setResults(prevResults => {
      return prevResults.map(result => {
        // Each result has an actions array
        if (result.actions) {
          return {
            ...result,
            actions: result.actions.map(action => {
              if (action.id === actionId) {
                // Deep merge to preserve nested structures like videos.external.veo3 and videos.external.voiceover
                const updatedData = deepMerge(action.data, dataUpdates);
                console.log('[updateAction] Updated action data:', updatedData);
                return {
                  ...action,
                  data: updatedData
                };
              }
              return action;
            })
          };
        }
        return result;
      });
    });
  }, []);

  const fetchGameStep = useCallback(async (loadAll = false, sliceOfLife = false, analysis = false, rollback = false) => {
    if (!gameId) {
      alert('Please enter a game ID');
      return;
    }

    if (isRequestInProgressRef.current) {
      return;
    }

    isRequestInProgressRef.current = true;
    setIsLoading(true);

    try {
      const url = `${API_BASE_URL}/api/takeGameStep?id=${gameId}${loadAll ? '&loadAll=true' : ''}${sliceOfLife ? '&sliceOfLife=true' : ''}${analysis ? '&analysis=true' : ''}${rollback ? '&rollback=true' : ''}`;
      const response = await fetch(url);
      const data = await parseApiResponse(response);
      console.log('data.actions', data.actions)
      if (data.actions) {
        data.actions = [...data.actions].reverse();
      }
      let isScrolled = isScrolledToBottom();
      setResults(prevResults => {
        const newResults = [...prevResults];
        newResults.push(data);
        return newResults;
      });
      if (data.users) {
        setUsers(data.users);
      }
      if (data.gameType) {
        setGameType(data.gameType);
      }
      // Only scroll if already at bottom
      requestAnimationFrame(() => {
        if (isScrolled) {
          scrollToBottom();
        }
      });
    } catch (error) {
      console.error('Error fetching game step:', error);
    } finally {
      setIsLoading(false);
      isRequestInProgressRef.current = false;
    }
  }, [gameId]);

  const fetchSupplementalGameStep = useCallback(async (loadAll = false, sliceOfLife = false, analysis = false, question = null, playerQuestion = null, playerName = null, allPlayers = false, findScenes = false) => {
    if (!gameId) {
      alert('Please enter a game ID');
      return;
    }

    try {
      const url = `${API_BASE_URL}/api/takeGameStep?id=${gameId}${loadAll ? '&loadAll=true' : ''}${sliceOfLife ? '&sliceOfLife=true' : ''}${analysis ? '&analysis=true' : ''}${question ? `&question=${encodeURIComponent(question)}` : ''}${playerQuestion ? `&playerQuestion=${encodeURIComponent(playerQuestion)}` : ''}${playerName ? `&playerName=${encodeURIComponent(playerName)}` : ''}${allPlayers ? '&allPlayers=true' : ''}${findScenes ? '&findScenes=true' : ''}`;
      const response = await fetch(url);
      const data = await parseApiResponse(response);
      if (data.actions) {
        data.actions = [...data.actions].reverse();
      }
      let isScrolled = isScrolledToBottom();
      setResults(prevResults => {
        const newResults = [...prevResults];
        newResults.push(data);
        return newResults;
      });
      if (data.users) {
        setUsers(data.users);
      }
      if (data.gameType) {
        setGameType(data.gameType);
      }
      // Only scroll if already at bottom
      requestAnimationFrame(() => {
        if (isScrolled) {
          scrollToBottom();
        }
      });
    } catch (error) {
      console.error('Error fetching game step:', error);
    }
  }, [gameId]);

  const fetchWithParams = useCallback(async (params = {}) => {
    if (!gameId) {
      alert('Please enter a game ID');
      return;
    }

    try {
      let url = `${API_BASE_URL}/api/takeGameStep?id=${gameId}`;
      Object.entries(params).forEach(([key, value]) => {
        url += `&${key}=${value}`;
      });
      const response = await fetch(url);
      const data = await parseApiResponse(response);
      if (data.actions) {
        data.actions = [...data.actions].reverse();
      }
      let isScrolled = isScrolledToBottom();
      setResults(prevResults => {
        const newResults = [...prevResults];
        newResults.push(data);
        return newResults;
      });
      if (data.users) {
        setUsers(data.users);
      }
      if (data.gameType) {
        setGameType(data.gameType);
      }
      // Only scroll if already at bottom
      requestAnimationFrame(() => {
        if (isScrolled) {
          scrollToBottom();
        }
      });
    } catch (error) {
      console.error('Error fetching game step:', error);
    }
  }, [gameId]);

  // Helper to get round ID for selected round
  const getSelectedRoundId = useCallback(() => {
    console.log('[getSelectedRoundId] Called with selectedRound:', selectedRound);

    if (!results || results.length === 0) {
      console.log('[getSelectedRoundId] No results');
      return null;
    }

    const allActions = results.flatMap(r => r.actions || []);
    console.log('[getSelectedRoundId] Total actions:', allActions.length);

    const roundIdToNumber = {};
    const uniqueRoundIds = [...new Set(allActions.map(a => a.game_instance_round_id).filter(id => id))];
    console.log('[getSelectedRoundId] Unique round IDs:', uniqueRoundIds);

    // Build timestamp mapping
    const roundIdToTimestamp = {};
    uniqueRoundIds.forEach(roundId => {
      const actionsInRound = allActions.filter(a => a.game_instance_round_id === roundId);
      if (actionsInRound.length > 0) {
        const timestamps = actionsInRound.map(a => new Date(a.created_at).getTime());
        roundIdToTimestamp[roundId] = Math.min(...timestamps);
      }
    });

    // Sort round IDs by timestamp and assign numbers
    const sortedRoundIds = uniqueRoundIds.sort((a, b) =>
      roundIdToTimestamp[a] - roundIdToTimestamp[b]
    );
    sortedRoundIds.forEach((roundId, index) => {
      roundIdToNumber[roundId] = index + 1;
    });

    console.log('[getSelectedRoundId] Round ID to Number mapping:', roundIdToNumber);
    console.log('[getSelectedRoundId] Sorted Round IDs:', sortedRoundIds);

    // Find the round ID for the selected round
    if (selectedRound === 'current') {
      const roundId = sortedRoundIds[sortedRoundIds.length - 1];
      console.log('[getSelectedRoundId] Current round ID:', roundId);
      return roundId;
    } else {
      const targetNumber = parseInt(selectedRound);
      console.log('[getSelectedRoundId] Looking for round number:', targetNumber);

      for (const [roundIdStr, roundNumber] of Object.entries(roundIdToNumber)) {
        console.log('[getSelectedRoundId] Checking:', roundIdStr, '→', roundNumber, 'vs', targetNumber);
        if (roundNumber === targetNumber) {
          console.log('[getSelectedRoundId] Found match! Round ID:', roundIdStr);
          return roundIdStr;
        }
      }

      console.log('[getSelectedRoundId] No match found for round number:', targetNumber);
    }

    return null;
  }, [results, selectedRound]);

  // Generate Episode for a specific round
  const generateEpisode = useCallback(async (roundId) => {
    if (!roundId) {
      alert('Could not determine round ID');
      return;
    }

    setGeneratingEpisode(true);

    try {
      const url = `${API_BASE_URL}/api/takeGameStep?generateEpisode=true&roundId=${roundId}`;
      console.log('[generateEpisode] Calling URL:', url);

      const response = await fetch(url);
      console.log('[generateEpisode] Response status:', response.status);

      const responseText = await response.text();
      console.log('[generateEpisode] Response text:', responseText);

      let data;
      try {
        const parsedResponse = JSON.parse(responseText);

        // Use parseApiResponse helper to handle Lambda format
        // Since parseApiResponse expects a Response object, we'll inline the logic
        data = parsedResponse;
        if (data && typeof data === 'object' && data.statusCode !== undefined && data.body !== undefined) {
          if (typeof data.body === 'string') {
            data = JSON.parse(data.body);
          } else if (typeof data.body === 'object') {
            data = data.body;
          }
        }
      } catch (parseError) {
        console.error('[generateEpisode] Failed to parse JSON:', parseError);
        alert(`Failed to parse response: ${responseText.substring(0, 200)}`);
        return;
      }

      console.log('[generateEpisode] Parsed data:', data);
      console.log('[generateEpisode] data.success:', data.success);

      if (data.success) {
        // Reload game data to show the new episode action
        const reloadUrl = `${API_BASE_URL}/api/takeGameStep?id=${gameId}&loadAll=true`;
        const reloadResponse = await fetch(reloadUrl);
        const reloadData = await reloadResponse.json();

        if (reloadData.actions) {
          reloadData.actions = [...reloadData.actions].reverse();
        }

        setResults([reloadData]);

        if (reloadData.users) {
          setUsers(reloadData.users);
        }
        if (reloadData.gameType) {
          setGameType(reloadData.gameType);
        }
      } else {
        console.error('[generateEpisode] Backend returned success=false');
        alert(`Failed to generate episode: ${data.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error generating episode:', error);
      alert(`Error: ${error.message}`);
    } finally {
      setGeneratingEpisode(false);
    }
  }, [gameId]);

  // ─── Shared action handler ───
  // Single code-path for processing incoming actions regardless of delivery method
  // (WebSocket push, REST polling, or early status fetch).  Handles: spectating
  // messages (ghost chat, moderator), regular chat messages, sounds, notification
  // dots, scroll tracking, optimistic-message replacement, and results state.
  const processIncomingAction = useCallback((a, { silent = false } = {}) => {
    if (!a || !a.type) return;

    // Dedup: if we've already processed this action (via WS, polling, or early fetch), skip entirely.
    // ⚠️ ONE exception (Wave A): a space_command row is MUTATED IN PLACE by the
    // backend lifecycle (/task: active → judging → polling → completed). The
    // live path is the space_command_update patch, but a viewer whose socket
    // dropped and reconnected never receives it, and this dedupe would discard
    // the polled re-delivery that carries the truth — leaving the now-bar
    // frozen on a finished episode until remount. Re-deliveries of a
    // space_command therefore RECONCILE data instead of being dropped;
    // everything else keeps the original drop-on-sight behavior.
    if (a.id && processedActionIdsRef.current.has(a.id)) {
      // 🛡️ 09-29: a line the server now serves hidden (this socket missed its
      // action_hidden) masks here; a no-op once masked (utils/hiddenLine).
      // Keyed on the server's own words, so only a line it masked is masked.
      if (a.data?.hidden_by_reports && a.data.message === HIDDEN_LINE_COPY) {
        setResults(prev => hideActionInResults(prev, a.id));
        setSpectatingMessages(prev => hideGhostLine(prev, a.id));
      }
      if (a.type === 'space_command' && a.data) {
        setResults(prev => {
          const allActions = prev.flatMap(r => r.actions || []);
          const idx = allActions.findIndex(x => x.id === a.id);
          if (idx === -1) return prev;
          const cur = allActions[idx].data || {};
          // Only re-render when something actually moved (the poll re-delivers
          // every row on a 2s cadence; a blind setResults would churn).
          const changed = Object.keys(a.data).some(k => JSON.stringify(cur[k]) !== JSON.stringify(a.data[k]));
          if (!changed) return prev;
          const updated = [...allActions];
          updated[idx] = { ...updated[idx], data: { ...cur, ...a.data } };
          return [{ ...(prev[0] || {}), actions: updated }];
        });
      }
      return;
    }
    if (a.id) processedActionIdsRef.current.add(a.id);

    // --- Spectating messages (ghost chat + moderator) → sidebar panel ---
    if (a.type === 'spectating_message') {
      const data = a.data || {};
      // Watch-party quips are overlay-only (theater bullets via watch_reaction);
      // the DB row is the record for recaps, not chat content. Covers all
      // delivery paths — WS, polled recent_messages, initial-load actions.
      // 🛋️ 09-23: ...and the row IS the couch's record (this is how a reload
      // hydrates the column; the id de-duplicates it against the live bubble).
      if (data.is_watch_quip) {
        if (pushCouchRef.current) pushCouchRef.current({ id: a.id, sender_name: data.sender_name, avatar_url: null, text: data.message, at: a.created_at || (data.timestamp ? new Date(Number(data.timestamp) || data.timestamp).toISOString() : new Date().toISOString()), video_id: data.video_id || null });
        return;
      }
      // 🔔 The cue (utils/spectatingCue): a host line chimes, except in a
      // ported room (F4, Nina 09-26: "silence the chime"; its host lines are
      // hidden, and a moderator line never falls into the whisper rule).
      // Ghost chatter plays a soft cue only when the user can't currently
      // see the new message — panel is collapsed, OR expanded but scrolled up.
      // When they're at the bottom of an open panel, the message auto-scrolls
      // into view, so a sound would just be spammy.
      // 📣 Live room: there is no panel to be "collapsed", so the refs read as
      // hidden forever and EVERY crowd line would whisper. The feed shows it.
      const cue = spectatingCue(data, {
        silent,
        ported: portedLoadRef.current,
        crowdInline: crowdInlineRef.current,
        panelExpanded: spectatorPanelExpandedRef.current,
        panelScrolledUp: spectatorPanelScrolledUpRef.current,
      });
      if (cue) soundManager.play(cue);
      setSpectatingMessages(prev => {
        if (prev.some(m => m.id === a.id)) return prev;
        const filtered = prev.filter(m =>
          !m.isOptimistic || m.sender_id !== data.sender_id || m.message !== data.message
        );
        return [...filtered, {
          id: a.id,
          sender: data.sender_name || (data.is_moderator ? 'Moderator' : 'Spectator'),
          message: data.message,
          timestamp: data.timestamp || a.created_at,
          sender_id: data.sender_id,
          is_spectator: data.is_spectator,
          is_ghost_chatter: data.is_ghost_chatter || false,
          is_reaction_note: data.is_reaction_note,
          is_moderator: data.is_moderator || false,
          is_announcement: data.is_announcement || false,
          moderator_emoji: data.moderator_emoji,
          livestream_identity: data.livestream_identity || null,
          livestream_streamer: data.livestream_streamer || null,
          gif_url: data.gif_url || null,
          gif_title: data.gif_title || null
        }];
      });
    }

    // --- Theme transition events ---
    if (['public_message', 'private_message', 'secret_message', 'human_interjection'].includes(a.type)) {
      emitGameEvent(GAME_EVENTS.NEW_MESSAGE, {
        senderName: a.data?.sender_name,
        messageType: a.type,
        isHuman: a.data?.is_human || a.type === 'human_interjection'
      });
    }

    // Drip eligibility — only the live "the room is talking" stream gets paced
    const dripEligible = !silent && !isBotMode && !competitiveModeRef.current
      && a.type === 'public_message'
      && !a.data?.is_human
      && a.data?.sender_id !== humanPlayerIdRef.current;

    // --- Sound effects (skip if silent or sender is current user; dripped
    //     messages play their sound at REVEAL time instead, inside the thunk) ---
    if (!silent) {
      if (dripEligible) {
        // sound rides the reveal
      } else if (['public_message', 'message', 'human_interjection'].includes(a.type) && a.data?.sender_id !== humanPlayerIdRef.current) {
        soundManager.play('message');
      } else if (['private_message', 'secret_message'].includes(a.type) && a.data?.sender_id !== humanPlayerIdRef.current) {
        soundManager.play('whisper');
      } else if (a.type === 'game_announcement') {
        soundManager.play('announcement');
      } else if (a.type === 'space_command' && a.data?.issued_by?.player_id !== humanPlayerIdRef.current) {
        soundManager.play('announcement');
      } else if (a.type === 'shared_content' && a.data?.is_house_aux) {
        // 🎶 aux rotation/take-back cards (08-28): same chime as every other
        // aux card — sound was the only live signal and these had none.
        soundManager.play('announcement');
      }
    }

    // --- Notification dot on Chat tab ---
    // 08-28 (Nina, "I can't see aux changes on mobile"): two dead spots fixed.
    // (a) space_command (aux grabs/skips) + shared_content (house rotation
    // cards, notes) never dotted the tab on ANY platform; (b) the old
    // `chatActionTab !== 'all'` guard could never fire under placesNav (the
    // mobile places nav navigates, never re-filters — the tab is permanently
    // 'all'), so NO type ever dotted the mobile Chat tab. The places-nav leg
    // now dots whenever the user is off the Chat place.
    if (['public_message', 'message', 'private_message', 'human_interjection', 'space_command', 'shared_content'].includes(a.type)
        && (chatActionTabRef.current !== 'all'
          || (placesNavRef.current && mobileViewRef.current !== 'chat'))) {
      setTabDots(prev => prev.chat ? prev : { ...prev, chat: true });
    }

    // --- Append to main chat results ---
    if (['public_message', 'private_message', 'secret_message', 'game_announcement', 'human_interjection', 'pass', 'shared_content', 'space_command'].includes(a.type)) {
      const appendNow = () => {
        if (dripEligible) soundManager.play('message'); // sound synced to reveal
        // 🏡 Their Place: his message lands in the room the moment it shows
        // (a ripple from his side + his words as light). Live rows only: the
        // early fetch + initial load pass silent:true, so history never replays.
        if (!silent && a.type === 'public_message' && portedResidentNameRef.current
          && a.data?.sender_name === portedResidentNameRef.current) {
          portedStageRef.current?.theySaid?.(a.data?.message || '');
        }
        const atBottom = isScrolledToBottom();
        // Cycle 5b.2: bot mode forces auto-scroll = true on every new message.
        // The bot scrolls around (via window.scrollTo + chat container scroll) to
        // read context, which would normally flip this flag off and leave the
        // bot's own subsequent posts invisible to HLS viewers. Forcing true here
        // makes new messages always snap to bottom in bot mode.
        // 🧲 09-25: a reader the PAGE moved (a replaced container, growth not
        // followed yet) keeps the follow; only one who used a scroll control
        // since the feed last sat at its bottom loses it here.
        if (isBotMode || atBottom) shouldAutoScrollRef.current = true;
        else if (readerMayBeReading()) shouldAutoScrollRef.current = false;
        // Track unread messages when user is scrolled up
        // space_command + shared_content joined 08-28 (Nina): an aux grab or
        // house rotation card landing below the fold left the jump chip as a
        // bare ↓ with no "N new" — the card arrived with zero visible signal.
        if (!atBottom && ['public_message', 'private_message', 'human_interjection', 'space_command', 'shared_content'].includes(a.type)) {
          setUnreadCount(prev => prev + 1);
        }
        setResults(prev => {
          const existing = prev.flatMap(r => r.actions || []);
          if (existing.some(ea => ea.id === a.id)) return prev;
          const newAction = {
            id: a.id,
            type: a.type === 'human_interjection' ? 'public_message' : a.type,
            game_instance_player_id: a.game_instance_player_id,
            data: a.data || {},
            created_at: a.created_at || new Date().toISOString()
          };
          const optimisticMatch = existing.findIndex(ea =>
            ea.isOptimistic &&
            // 🆔 08-03: the server echoes the client_msg_id we attach at send
            // time — an exact, rewrite-proof match. The heuristic below stays
            // as the fallback for command cards and any path (or not-yet-
            // deployed backend) that doesn't echo it; heuristic-only matching
            // left a permanent duplicate whenever the server rewrote the
            // text or the player id didn't line up (08-02 audit).
            ((a.data?.client_msg_id && ea.data?.client_msg_id === a.data.client_msg_id)
              || (ea.game_instance_player_id === a.game_instance_player_id &&
                (a.type === 'space_command'
                  // Command cards have no data.message — match on the command name.
                  // 'build' with #handles gets rewritten to 'combine' server-side,
                  // so the optimistic build card must match the combine action.
                  // '/aux' is the DISPLAY name of internal id 'jukebox' (08-17
                  // rename): the optimistic card says 'aux', the server row says
                  // 'jukebox' — without this alias every /aux left its optimistic
                  // "put a record on" card stranded above the real grab card.
                  ? (ea.type === 'space_command' && (ea.data?.command === a.data?.command
                      || (ea.data?.command === 'build' && a.data?.command === 'combine')
                      || (ea.data?.command === 'aux' && a.data?.command === 'jukebox')))
                  : ea.data?.message === (a.data?.message || ''))))
          );
          if (optimisticMatch >= 0) {
            const updated = [...existing];
            updated[optimisticMatch] = newAction;
            return [{ actions: updated }];
          }
          return [{ actions: [...existing, newAction] }];
        });
      };
      if (dripEligible) {
        dripQueueRef.current.push({
          thunk: appendNow,
          senderName: a.data?.sender_name || null,
          msgLen: (a.data?.message || '').length,
        });
        pumpDripQueue();
      } else {
        appendNow();
      }
    }

    // --- Round transitions → refetch full status ---
    if (a.type === 'game_announcement') {
      fetchChatGameStatusRef.current();
    }
  }, []);

  // Convert a polled recent_message (backend format) into the action format
  // that processIncomingAction expects (same shape as WS actions).
  const convertPolledMessageToAction = useCallback((msg, players) => {
    let actionType = msg.type;
    if (msg.type === 'announcement') actionType = 'game_announcement';
    else if (msg.type === 'public') actionType = 'public_message';
    else if (msg.type === 'private') actionType = 'private_message';
    else if (msg.type === 'secret') actionType = 'secret_message';
    else if (msg.type === 'spectating') actionType = 'spectating_message';

    const senderPlayer = players?.find(p => p.name === msg.sender);

    return {
      id: msg.id,
      type: actionType,
      game_instance_player_id: senderPlayer?.id,
      data: {
        sender_name: msg.sender,
        sender_id: msg.sender_id,
        message: msg.message,
        recipient_names: msg.recipients,
        reactions: msg.reactions || {},
        is_spectator: msg.is_spectator,
        is_ghost_chatter: msg.is_ghost_chatter || false,
        is_reaction_note: msg.is_reaction_note,
        is_moderator: msg.is_moderator || false,
        is_announcement: msg.is_announcement || false,
        moderator_emoji: msg.moderator_emoji,
        ...Object.fromEntries(
          Object.entries(msg).filter(([key]) =>
            !['id', 'type', 'sender', 'sender_id', 'message', 'recipients', 'timestamp', 'reactions',
              'is_spectator', 'is_ghost_chatter', 'is_reaction_note', 'is_moderator', 'is_announcement', 'moderator_emoji'].includes(key)
          )
        )
      },
      created_at: msg.timestamp
    };
  }, []);

  // ChatGame-specific: Fetch game status and check for human turn
  const fetchChatGameStatusInFlight = useRef(false);
  const fetchChatGameStatusQueued = useRef(false);
  const fetchChatGameStatus = useCallback(async () => {
    if (!gameId) return;
    if (gameType !== 'chat-game' && gameType !== 'chat') return;
    // ⏸️ Space paused (pause-on-blur): every poll drives the ambient AI pass,
    // so a paused space makes ZERO status calls until the ▶ overlay resumes it.
    if (spacePausedRef.current) return;

    // If already in flight, queue one more fetch for after it finishes
    if (fetchChatGameStatusInFlight.current) {
      fetchChatGameStatusQueued.current = true;
      return;
    }
    fetchChatGameStatusInFlight.current = true;

    try {
      const status = await chatGameApi.getGameStatus(gameId, humanPlayerId, { watching: watchingTheaterRef.current });
      // Preserve imposter_topic if already known — the in-flight queue can cause
      // a stale fetch (with null humanPlayerId) to resolve after ensureHumanPlayer
      // already set the correct topic, overwriting it with null → shows "???"
      setChatGameStatus(prev => {
        // Preserve imposter_topic if already known (stale fetch can overwrite with null)
        let merged = status;
        if (prev?.imposter_topic && !status.imposter_topic) {
          merged = { ...merged, imposter_topic: prev.imposter_topic };
        }
        // Never let a poll DOWNGRADE the theme's generated images: a snapshot
        // queued before the asset_update WS patch landed comes back without the
        // background/banner url and wiped it — the background flashing in and
        // out on competitive round churn was exactly this.
        if (prev?.ui_theme) {
          if (!merged.ui_theme) {
            merged = { ...merged, ui_theme: prev.ui_theme };
          } else {
            const keepBg = prev.ui_theme.backgroundImage?.url && !merged.ui_theme.backgroundImage?.url;
            const keepBanner = prev.ui_theme.bannerImage?.url && !merged.ui_theme.bannerImage?.url;
            if (keepBg || keepBanner) {
              merged = {
                ...merged,
                ui_theme: {
                  ...merged.ui_theme,
                  ...(keepBg ? { backgroundImage: prev.ui_theme.backgroundImage } : {}),
                  ...(keepBanner ? { bannerImage: prev.ui_theme.bannerImage } : {}),
                },
              };
            }
          }
        }
        // If a WS game_state_update delivered fresher real-time fields, preserve them
        // instead of letting a slower poll response overwrite with stale data
        if (prev?._stateTimestamp && status.timestamp && prev._stateTimestamp > status.timestamp) {
          merged = {
            ...merged,
            next_speaker: prev.next_speaker,
            round_number: prev.round_number,
            round_type: prev.round_type,
            current_round_type: prev.current_round_type,
            is_voting: prev.is_voting,
            is_playing: prev.is_playing,
            game_completed: prev.game_completed,
            current_competitive_round: prev.current_competitive_round,
            imposter_word_round: prev.imposter_word_round,
            _stateTimestamp: prev._stateTimestamp,
          };
        } else {
          merged._stateTimestamp = status.timestamp || Date.now();
        }
        return merged;
      });

      // Recover request_id from game status (works across page refreshes)
      if (status.request_id) {
        setCurrentRequestId(status.request_id);
      }

      // Handle ready check round
      if (status.is_ready_check && status.ready_check_data && !status.ready_check_data.done) {
        // If user already submitted, merge backend data but preserve their ready status
        if (readyCheckDismissedRef.current) {
          setReadyCheckData(prev => {
            if (!prev) return status.ready_check_data;
            return {
              ...status.ready_check_data,
              ready_count: Math.max(status.ready_check_data.ready_count || 0, prev.ready_count || 0),
              ready_player_names: [...new Set([...(status.ready_check_data.ready_player_names || []), ...(prev.ready_player_names || [])])]
            };
          });
        } else {
          setReadyCheckData(status.ready_check_data);
        }
        // Only show if user hasn't already dismissed it
        if (status.request_id && !readyCheckDismissedRef.current) {
          setShowReadyCheck(true);
        }
      } else {
        // Ready check is done or we're past it — dismiss overlay
        setShowReadyCheck(false);
      }

      // Check if it's human player's turn
      // Use your_turn from backend OR recover from pending_interaction if WS was missed
      const gameIsRunning = status.is_playing || isPlayingRef.current;
      const turnIsForUs = humanPlayerId && (!status.waiting_for?.player_id || status.waiting_for.player_id === humanPlayerId);
      // [5g22] Recovery path must recognize ALL turn types, not just chat_turn.
      // Imposter (Glitch) turns are type 'imposter_word' and votes are 'vote';
      // when the WS push is missed (the bot's chromium WS is unreliable), this
      // status-poll fallback is the only way the turn input gets rendered. The
      // old chat_turn-only check meant imposter_word/vote turns NEVER rendered an
      // input → the player (and the bot) could never submit → turn expired at 90s.
      const hasPendingTurn = ['chat_turn', 'imposter_word', 'doodle_turn', 'vote', 'chair_answer'].includes(status.pending_interaction?.type) && gameIsRunning && turnIsForUs;
      // [5g22] JSON.stringify so the captured chromium-console shows the actual
      // fields (was collapsed as "Object" → blocked the imposter-turn-render
      // diagnosis). Next smoke: read which value fails (your_turn / hasPendingTurn
      // / currentRequestId / isHumanTurn) to settle poll-vs-WS vs currentRequestId.
      // Perf: bot-smoke-only — humans were paying this JSON.stringify on every
      // 5s/8s poll forever (renders nothing, fills the console). Bot mode keeps
      // the [5g22] chromium-console diagnostic intact.
      if (isBotMode) console.log('[Turn Debug] ' + JSON.stringify({
        your_turn: status.your_turn,
        hasPendingTurn,
        gameIsRunning,
        turnIsForUs,
        is_playing_backend: status.is_playing,
        is_playing_local: isPlayingRef.current,
        waiting_for: status.waiting_for,
        humanPlayerId,
        request_id: status.request_id,
        pending_interaction: status.pending_interaction?.type,
        humanTurnDismissed: humanTurnDismissedRef.current,
        isHumanTurnCurrent: isHumanTurnRef.current,
        currentRequestId: currentRequestIdRef.current,
        // [5g22] spectator-gate diagnosis (TA's is_spectating hypothesis): is the
        // BACKEND status returning is_spectating=true, and is the render gate
        // (effectiveIsSpectating) actually firing? userSwitchedToPlay = the bot's
        // missing escape hatch. Settles spectator-gate vs turn-state next smoke.
        is_spectating: status.is_spectating,
        effectiveIsSpectating,
        userSwitchedToPlay: userSwitchedToPlayRef.current,
      }));
      if ((status.your_turn || hasPendingTurn) && gameIsRunning && turnIsForUs) {
        // Don't re-show if we already submitted for this exact request
        if (humanTurnDismissedRef.current && status.request_id && humanTurnDismissedRef.current === status.request_id) {
          // Already submitted for this turn, don't re-show
        } else {
          // New turn (different request_id) — clear dismissed state and show
          if (status.request_id !== humanTurnDismissedRef.current) {
            humanTurnDismissedRef.current = null;
          }
          setIsHumanTurn(true);
          setTurnContext(status.context);
          setAvailableActions(status.available_actions || []);

          // Find human player's tokens
          const humanPlayer = status.players?.find(p => p.id === humanPlayerId);
          setTokensLeft(humanPlayer?.tokens_left || 0);

          // Set turn deadline. Priority: WS-delivered value (most precise, already in prev)
          // → server's expires_at from status → 60s safety-net fallback. Keyed by request_id:
          // a NEW request always replaces the deadline; only same-request polls keep prev.
          const serverDeadline = status.pending_interaction?.expires_at
            ? new Date(status.pending_interaction.expires_at).getTime()
            : null;
          if (status.request_id && turnDeadlineRequestIdRef.current !== status.request_id) {
            turnDeadlineRequestIdRef.current = status.request_id;
            setTurnDeadline(serverDeadline || Date.now() + 60000);
          } else {
            setTurnDeadline(prev => prev || serverDeadline || Date.now() + 60000);
          }
        }
      } else {
        // Don't let a stale poll clear a WS-delivered turn.
        // Use refs to avoid stale closure (callback deps don't include these).
        const hasActiveTurn = isHumanTurnRef.current && currentRequestIdRef.current && !humanTurnDismissedRef.current;
        if (!hasActiveTurn) {
          setIsHumanTurn(false);
          humanTurnDismissedRef.current = null; // Clear dismissed state when it's no longer our turn
        }
      }

      // Always update human player's token count from polling
      const humanPlayerFromStatus = status.players?.find(p => p.id === humanPlayerId);
      if (humanPlayerFromStatus?.tokens_left != null) {
        setTokensLeft(humanPlayerFromStatus.tokens_left);
      }

      // Update game status message from polling (fallback when WS game_status isn't delivered)
      if (status.is_playing && status.next_speaker && !status.next_speaker.is_human && !status.your_turn) {
        const speakerName = status.next_speaker.player_name;
        emitGameEvent(GAME_EVENTS.PLAYER_SPEAKING, {
          playerName: speakerName,
          isHuman: status.next_speaker.is_human,
          isAI: !status.next_speaker.is_human
        });
        setGameStatusMessage(`Waiting for ${speakerName}...`);
      } else if (!status.is_playing && !status.game_completed) {
        setGameStatusMessage(null);
      }

      // Sync local isPlaying with backend — catches cases where backend paused
      // (e.g., imposter vote results) but the WS autoplay_paused message wasn't received
      // Use ref to avoid stale closure (callback deps don't include isPlaying)
      // Skip during active play-round mode (playRoundStartRoundRef != null) because the
      // WS startPlaying message may not have reached the backend yet. The round-change
      // detection below handles pausing during play-round mode instead, and WS messages
      // (autoplay_paused, game_limit_reached) handle error cases.
      if (isPlayingRef.current && status.is_playing === false && !status.game_completed && playRoundStartRoundRef.current == null) {
        console.log('[fetchChatGameStatus] Backend paused game, syncing local state');
        setIsPlaying(false);
        setPlayRoundMode(false);
        setGameStarted(false);
      }

      // Reverse sync: backend is playing but local client doesn't know
      // (WS playback_started was missed — e.g., reconnection, page refresh)
      if (!isPlayingRef.current && status.is_playing === true
          && (status.competitive_mode || status.imposter_mode)
          && !status.game_completed) {
        // Check if this client was the one who started playback (persisted across refreshes).
        // Without this, a page refresh would always set remotePlayback: true for the controller.
        const storedController = localStorage.getItem(`playbackController_${gameId}`);
        // humanPlayerId may still be null during auto-join window on page refresh —
        // fall back to createdPlayerIdRef which is populated from localStorage immediately.
        const effectivePlayerId = humanPlayerId || createdPlayerIdRef.current;
        const weAreController = !!storedController && storedController === effectivePlayerId;
        console.log('[fetchChatGameStatus] Backend is playing, syncing local state (missed WS playback_started). weAreController:', weAreController);
        setIsPlaying(true);
        setPlayRoundMode(true);
        setRemotePlayback(!weAreController);
        setGameStarted(true);
        gameStartedRef.current = true;

      }

      // Game completed while still in playback — clear playing state so UI doesn't show
      // a stale "Pause" button after the game ends. The existing sync at line above only
      // clears when is_playing === false AND !game_completed, so game-end needs its own path.
      if (status.game_completed && isPlayingRef.current) {
        console.log('[fetchChatGameStatus] Game completed, clearing playback state');
        setIsPlaying(false);
        isPlayingRef.current = false;
        setPlayRoundMode(false);
        setRemotePlayback(false);
        playRoundStartRoundRef.current = null;
        // Note: leave gameStarted/gameStartedRef true so the play/spectate lock stays active
        // and the ready-check UI doesn't reappear.
      }

      // Detect round change while in playRoundMode — auto-pause when round transitions
      // Use ref to avoid stale closure
      if (isPlayingRef.current && playRoundStartRoundRef.current != null) {
        const currentRound = status.imposter_mode
          ? (status.imposter_word_round || 0)
          : (status.current_competitive_round || status.round_number || 1);
        if (currentRound !== playRoundStartRoundRef.current) {
          console.log(`[fetchChatGameStatus] Round changed (${playRoundStartRoundRef.current} -> ${currentRound}), auto-pausing`);
          setIsPlaying(false);
          setPlayRoundMode(false);
          setGameStarted(false);
          playRoundStartRoundRef.current = null;
        }
      }

      // Update results with recent messages — convert to action format and
      // run through processIncomingAction so all side effects (sounds, dots,
      // scroll, state) fire identically to the WebSocket path.
      if (status.recent_messages && status.recent_messages.length > 0) {
        const actions = status.recent_messages.map(msg => convertPolledMessageToAction(msg, status.players));
        actions.forEach(a => processIncomingAction(a));
      }

      // Update users/players list — merge with existing so players who left aren't lost
      if (status.players) {
        setUsers(prev => {
          const updated = status.players.map(p => ({
            id: p.id,
            name: p.name,
            // 🤖 An external agent's model lives in `agent_model` — its `model`
            // COLUMN is null (the engine treats a connected agent like a human
            // and never calls a model for it), so without this a probe fell
            // through to the literal string 'Human Player'.
            model: p.model || p.agent_model || (p.is_human && !p.is_external_agent ? 'Human Player' : 'AI'),
            is_human: p.is_human,
            game_player_id: p.id,
            profile_image_url: p.profile_image_url,
            personality_traits: p.personality_traits || [],
            character_description: p.character_description || null,
            character_bio: p.character_bio || null,
            bio_facts: p.bio_facts || null,
            objective: p.objective || null,
            // ⏳🔴 visit_expired MUST be carried through (Nina 08-08 bug).
            // This mapper is an explicit FIELD ALLOWLIST — anything not named
            // here is silently dropped when the 5s status poll rebuilds `users`.
            // `visit_expired` was missing, so `visitOver(u)` returned false for
            // EVERY row and the three roster filters (activeUsers,
            // streamPanelUsers, the welcome interstitial) had no subject: the
            // whole visiting-agent-departs feature was inert on the client even
            // though the seats were stamped and the backend computed the field
            // correctly. Verified against the live API: /chat-game/status DOES
            // send visit_expired:true for all four AI Dorm guests.
            // ⚠️ Keep is_external_agent too — activeUsers has an
            // external-agent pass-through that must run AFTER the visit check.
            visit_expired: p.visit_expired || false,
            visit_expires_at: p.visit_expires_at || null,
            is_external_agent: p.is_external_agent || false,
            // 🤖 Same allowlist trap as visit_expired above: utils/playerKind
            // reads these to tell an agent seat from a person, and anything not
            // named here is dropped every 5s when the poll rebuilds `users`.
            is_visiting_agent: p.is_visiting_agent || false,
            agent_model: p.agent_model || null,
            agent_type: p.agent_type || null,
            // 👥 Same allowlist trap again (Nina 08-20: Locals showed every
            // human ever): the status payload computes is_connected from live
            // WS rows, and dropping it here left activeUsers with only the
            // disconnected_at stamp — which dropped $disconnect events never
            // write. Deliberately NOT defaulted: an old backend omits the
            // field, undefined !== false, and the stamp-only behavior holds.
            is_connected: p.is_connected,
            data: {
              is_alive: !p.is_eliminated,
              is_eliminated: p.is_eliminated,
              disconnected_at: p.disconnected_at || null,
              is_external_agent: p.is_external_agent || false
            }
          }));
          const updatedIds = new Set(updated.map(u => u.id));
          // Keep previously loaded players that aren't in the status response
          const kept = prev.filter(u => !updatedIds.has(u.id) && !updatedIds.has(u.game_player_id));
          return [...updated, ...kept];
        });
      }
    } catch (error) {
      console.error('Failed to fetch ChatGame status:', error);
    } finally {
      fetchChatGameStatusInFlight.current = false;
      // If another fetch was requested while we were in flight, run it now
      if (fetchChatGameStatusQueued.current) {
        fetchChatGameStatusQueued.current = false;
        fetchChatGameStatus();
      }
    }
  }, [gameId, humanPlayerId, gameType]);

  // Ref mirror so the WebSocket effect can always call the latest version
  // without needing fetchChatGameStatus in its dependency array (which would
  // cause reconnects every time humanPlayerId changes).
  const fetchChatGameStatusRef = useRef(fetchChatGameStatus);
  useEffect(() => { fetchChatGameStatusRef.current = fetchChatGameStatus; }, [fetchChatGameStatus]);

  // When humanPlayerId transitions null → a real ID (auto-join completes, or
  // spectate→play toggle), immediately re-check game status with the correct identity.
  // This recovers any 'your_turn' WS message that was dropped while humanPlayerId was
  // null, since both the WS handler and the status-poll turn check require humanPlayerId.
  // Using a prev-value ref so we only fire on null→id transitions, not on initial mount
  // when humanPlayerId is already set (which would duplicate the WS-connect fetch).
  // This effect is defined AFTER the fetchChatGameStatusRef update above, so React fires
  // them in definition order — the ref already points to the new function when we call it.
  const prevHumanPlayerIdRef = useRef(humanPlayerId);
  useEffect(() => {
    if (humanPlayerId && !prevHumanPlayerIdRef.current) {
      fetchChatGameStatusRef.current?.();
    }
    prevHumanPlayerIdRef.current = humanPlayerId;
  }, [humanPlayerId]);

  // Refresh game status when assets finish so "Let's Go!" screen has the latest scene/topic
  useEffect(() => {
    if (assetLoadingReady && showAssetLoadingOverlay) {
      fetchChatGameStatus();
    }
  }, [assetLoadingReady]);

  // Periodic status sync while playing — catches missed WS messages (e.g., autoplay_paused)
  useEffect(() => {
    if (!isPlaying) return;
    const interval = setInterval(() => {
      // (perf 08-13) hidden tab: nothing to sync visually and each poll
      // re-renders the whole tree. INTERVAL-only bail — load/reconnect
      // fetches stay ungated so a background-opened tab still hydrates.
      // WS stays live for turn pushes; next tick ≤5s after re-show.
      if (document.hidden) return;
      fetchChatGameStatus();
    }, 5000);
    return () => clearInterval(interval);
  }, [isPlaying, fetchChatGameStatus]);

  // Side quest live-claim: the quest text whose completion the server has acked this
  // round. Compared against the CURRENT challenge text, so a new round's quest
  // naturally resets the chip. Server grading (VotingRound.end) stays authoritative.
  const [sideQuestClaimed, setSideQuestClaimed] = useState(null);
  const sideQuestClaimedRef = useRef(null);

  // Mirror of VotingRound.end's deterministic quest checks (keep the two in sync).
  const checkSideQuest = (quest, text, myName) => {
    const t = String(text || '');
    switch (quest?.type) {
      case 'include_word': return quest.word ? new RegExp(`\\b${quest.word}\\b`, 'i').test(t) : false;
      case 'time_of_day': return /\b\d{1,2}(:\d{2})?\s*(am|pm)\b|\bmidnight\b|\bnoon\b|\bdawn\b|\bdusk\b/i.test(t);
      case 'under_words': return t.trim().length > 0 && t.trim().split(/\s+/).length <= (quest.max || 15);
      case 'question_end': return /\?\s*$/.test(t.trim());
      case 'name_drop': return (quest.names || []).some(n => n !== myName && t.includes(n));
      default: return false;
    }
  };

  // 🐛 Bug reports (09-20): hand the app-level modal what only this page
  // knows. A GETTER read at submit time, not a snapshot. Declared down here on
  // purpose: every value it closes over (viewerRoom included) is defined
  // above this line, so there is no TDZ to trip. The secret rides the request
  // BODY root only (the server validates it and never stores it) and is never
  // part of the stored context.
  useEffect(() => {
    setBugPageContext(() => ({
      game_id: gameId || null,
      player_id: humanPlayerId || null,
      player_secret: humanPlayerId ? chatGameApi.getPlayerSecret(humanPlayerId) : null,
      room: viewerRoom || null,
      game_type: gameType || null,
      last_action_ids: results.flatMap(r => r.actions || []).slice(-12).map(a => a && a.id).filter(Boolean),
    }));
    return () => setBugPageContext(null);
  }, [gameId, humanPlayerId, viewerRoom, gameType, results]);

  // ChatGame-specific: Handle human player action submission
  const handleHumanSubmit = async (action) => {
    dismissHint('your_turn');
    if (!humanPlayerId) {
      throw new Error('No player ID set');
    }

    // Optimistic update: show message immediately
    const optimisticId = `optimistic_${Date.now()}_${Math.random()}`;
    const humanPlayer = users.find(u => u.game_player_id === humanPlayerId);
    // 🆔 08-03: ride the optimistic id to the server as client_msg_id — it
    // echoes it back in the created action's data, giving the WS/poll dedupe
    // an exact match instead of the player-id + exact-text heuristic.
    if (action.message) {
      action = { ...action, client_msg_id: optimisticId };
      const optimisticAction = {
        id: optimisticId,
        type: action.action || 'public_message',
        game_instance_player_id: humanPlayerId,
        data: {
          sender_name: humanPlayer?.name || 'You',
          message: action.message,
          recipient_names: action.recipients || [],
          target_players: action.target_players || [],
          client_msg_id: optimisticId,
        },
        created_at: new Date().toISOString(),
        isOptimistic: true,
      };
      // Sending = unambiguous intent to be at the bottom; re-arm the snap
      // (a reader scrolled-up in history had disarmed it, so their own
      // message landed invisibly below the fold — 08-02 chat-fix batch).
      shouldAutoScrollRef.current = true;
      setResults(prev => {
        if (prev.length === 0) return [{ actions: [optimisticAction] }];
        const existing = prev.flatMap(r => r.actions || []);
        // Clamp the optimistic timestamp past the newest action — a client
        // clock behind the server made the feed's created_at sort place the
        // just-sent message MID-feed (reads as the chat jumping up).
        const lastTs = existing.length ? (Date.parse(existing[existing.length - 1].created_at) || 0) : 0;
        optimisticAction.created_at = new Date(Math.max(Date.now(), lastTs + 1)).toISOString();
        return [{ actions: [...existing, optimisticAction] }];
      });
    }

    try {
      if (!currentRequestId) {
        console.warn('[handleHumanSubmit] No interaction request ID — cannot submit (backend must request input first)');
        throw new Error('No active turn request. Please wait for your turn.');
      }

      // Respond to the interaction request — cron handles everything else
      // Track the request ID actually used so humanTurnDismissedRef is accurate.
      let submittedRequestId = currentRequestId;
      try {
        await chatGameApi.respondToInteraction(currentRequestId, action);
      } catch (interactionErr) {
        // If request expired but there's a newer one, retry with it
        if (interactionErr.current_request_id) {
          console.log('[handleHumanSubmit] Request expired, retrying with new ID:', interactionErr.current_request_id);
          submittedRequestId = interactionErr.current_request_id;
          setCurrentRequestId(submittedRequestId);
          await chatGameApi.respondToInteraction(submittedRequestId, action);
        } else {
          throw interactionErr;
        }
      }
      // Mark the actually-submitted ID as dismissed so status polls don't re-show the turn.
      humanTurnDismissedRef.current = submittedRequestId;
      setIsHumanTurn(false);
      setCurrentRequestId(null);
      setTurnDeadline(null);
      emitGameEvent(GAME_EVENTS.TURN_SUBMITTED, { action: action.action });
      // Side quest live check — flips only AFTER the server accepted the submission
      // (send-drop family: claiming on an unacked send would lie). Interjections also
      // count server-side, but the turn path is where the quest card lives.
      try {
        const quest = chatGameStatus?.human_challenge;
        if (quest && action.message && sideQuestClaimedRef.current !== quest.text
            && checkSideQuest(quest, action.message, humanPlayer?.name || '')) {
          sideQuestClaimedRef.current = quest.text;
          setSideQuestClaimed(quest.text);
        }
      } catch (e) { /* cosmetic only — server grading is authoritative */ }
    } catch (error) {
      console.error('Failed to submit human action:', error);
      // Remove optimistic message on error
      setResults(prev => [{
        actions: prev.flatMap(r => r.actions || []).filter(a => a.id !== optimisticId)
      }]);
      throw error;
    }
  };

  // Count human interjections this round
  // Backend provides per-round count (resets each round); local count as fallback
  const localInterjectionCount = React.useMemo(() => {
    const allActions = results.flatMap(r => r.actions || []);
    return allActions.filter(a =>
      a.type === 'human_interjection' &&
      a.game_instance_player_id === humanPlayerId &&
      !a.isOptimistic
    ).length;
  }, [results, humanPlayerId]);
  const humanInterjectionCount = chatGameStatus?.interjections_this_round ?? localInterjectionCount;

  const MAX_INTERJECTIONS_PER_ROUND = (chatGameStatus?.competitive_mode && !chatGameStatus?.imposter_mode) ? 2 : 3;
  const interjectionsRemaining = Math.max(0, MAX_INTERJECTIONS_PER_ROUND - humanInterjectionCount);

  // ChatGame-specific: Handle free chat interjections (outside of turn).
  // `command` = parsed slash command ({ name, targets, text, card_message });
  // commands bypass the per-round message budget (they have their own backend
  // cooldowns) and render as an instant optimistic event card — no dead air.
  // ⚔️ /challenge (SLASH_COMMANDS queue item 4, built 07-13): a real 1v1
  // Roast Battle against a local, created through the SAME instant-create
  // path the Games tab uses — no backend command, no new routes. Ties: the
  // spectator vote already counts when the room watches; an AI judge for
  // empty rooms is the flagged v2.
  const launchChallenge = async (command) => {
    const targetName = command.targets?.[0];
    const target = users.find(u => u.name && u.name.toLowerCase() === String(targetName || '').toLowerCase() && !u.is_human);
    if (!target) throw new Error(`Can't find ${targetName} here. Tag a local with @!`);
    const res = await fetch(`${API_BASE_URL}/api?getAllFeaturedCompetitions=true`);
    const data = await res.json();
    const template = (data.competitions || []).find(c => c.name === 'AI Roast Battle');
    if (!template) throw new Error('The arena is closed right now. Try again in a bit!');
    let playerId = localStorage.getItem('competition_player_id');
    if (!playerId) {
      playerId = 'player_' + Math.random().toString(36).substr(2, 9) + '_' + Date.now();
      localStorage.setItem('competition_player_id', playerId);
    }
    const myName = users.find(u => u.game_player_id === humanPlayerId)?.name || 'You';
    const config = {
      name: `Roast Battle: ${myName} vs ${target.name}`,
      objective: template.objective,
      scene_description: template.scene_description || template.description,
      competitive_mode: true,
      imposter_mode: false,
      total_rounds: template.total_rounds || 3,
      max_rounds: (template.total_rounds || 3) * 2,
      messages_per_round: template.messages_per_round || 4,
      is_shared: false,
      featured_template_index: template.index, // mechanics enrichment (dice etc.)
      human_player_identifier: playerId,
      interests: ['competitive', 'challenge'],
      players: [
        {
          name: target.name,
          character_description: `${target.name} from the space next door, dragged into a roast duel by ${myName}. Same persona, maximum burns.`,
          personality_traits: [], objective: template.objective, secret_info: '',
          is_human: false, model: target.model || 'google/gemini-3-flash-preview',
        },
        {
          name: myName,
          character_description: 'A human competitor',
          personality_traits: [], objective: template.objective, secret_info: '',
          is_human: true, model: null,
        },
      ],
      profileImagesEnabled: true,
      allowAiNameSelection: false,
    };
    const result = await chatGameApi.createGameInstant(config);
    if (result.human_player_id) {
      localStorage.setItem(`humanPlayerId_${result.game_id}`, result.human_player_id);
    }
    markRoomMaker(result.game_id); // 🏠 an unlisted duel/easel: its maker keeps Share
    window.open(`/game-step?gameId=${encodeURIComponent(result.game_id)}&loading=true`, '_blank');
    // Tell the room — a plain human message, so the locals react in-world
    try {
      await chatGameApi.submitInterjection(gameId, humanPlayerId, `⚔️ I just challenged ${target.name} to a Roast Battle!`);
    } catch (e) { /* the duel exists either way */ }
    setTimeout(() => fetchChatGameStatus(), 300);
  };

  // 🐛 /doodle (Nina 07-15: "the mod can start a doodle") — FRONTEND-orchestrated
  // like /challenge: instant-creates a Doodle Bug round with THREE of this
  // space's locals (their models ride along) + you, opens it in a new tab,
  // and announces the game in-world. AI-initiated doodles = parked v2 (needs
  // an ambient-tick trigger + an invite card).
  const launchDoodle = async () => {
    const locals = users.filter(u => !u.is_human && u.name).slice(0, 3);
    if (locals.length < 2) throw new Error('Need at least 2 locals here to start a doodle!');
    const res = await fetch(`${API_BASE_URL}/api?getAllFeaturedCompetitions=true`);
    const data = await res.json();
    const template = (data.competitions || []).find(c => c.name === 'Doodle Bug');
    if (!template) throw new Error('The easel is missing! Try again in a bit.');
    let playerId = localStorage.getItem('competition_player_id');
    if (!playerId) {
      playerId = 'player_' + Math.random().toString(36).substr(2, 9) + '_' + Date.now();
      localStorage.setItem('competition_player_id', playerId);
    }
    const myName = users.find(u => u.game_player_id === humanPlayerId)?.name || 'You';
    const config = {
      name: `Doodle Bug: ${myName} + the locals`,
      objective: template.objective,
      scene_description: template.scene_description || template.description,
      competitive_mode: false,
      imposter_mode: true,
      is_shared: false,
      featured_template_index: template.index, // stamps doodle_bug + word rounds
      human_player_identifier: playerId,
      interests: ['imposter', 'doodle'],
      players: [
        ...locals.map(l => ({
          name: l.name,
          character_description: `${l.name} from the space next door, handed a crayon. Same persona, competitive artist energy.`,
          personality_traits: [], objective: template.objective, secret_info: '',
          is_human: false, model: l.model || 'google/gemini-3-flash-preview',
        })),
        {
          name: myName,
          character_description: 'A human artist',
          personality_traits: [], objective: template.objective, secret_info: '',
          is_human: true, model: null,
        },
      ],
      profileImagesEnabled: true,
      allowAiNameSelection: false,
    };
    const result = await chatGameApi.createGameInstant(config);
    if (result.human_player_id) {
      localStorage.setItem(`humanPlayerId_${result.game_id}`, result.human_player_id);
    }
    markRoomMaker(result.game_id); // 🏠 an unlisted duel/easel: its maker keeps Share
    window.open(`/game-step?gameId=${encodeURIComponent(result.game_id)}&loading=true`, '_blank');
    try {
      await chatGameApi.submitInterjection(gameId, humanPlayerId, `🖍️ I set up an easel! ${locals.map(l => l.name).join(', ')} and I are playing Doodle Bug!`);
    } catch (e) { /* the easel exists either way */ }
    setTimeout(() => fetchChatGameStatus(), 300);
  };

  // 🚪🕯️ Between rooms (Nina 09-24): the painting a walk reveals is the one
  // the page would paint in that room: the wallpaper art on desktop, the room
  // card's art on phones. null = no painting (the door opens onto the dark).
  const roomTransitArt = (roomId, view) => {
    if (!roomId || roomId === UNKNOWN_ROOM) return null;
    const hour = houseStatus?.circadian?.clock?.hour;
    if (view === 'screen') return roomArtPlan(roomId, hour, houseStatus?.room_art, houseStatus?.weather?.kind, { desktop: true, season: roomSeason })?.wallpaper?.url || null;
    const mh = roomMasthead(roomId, hour, houseStatus?.room_art, houseStatus?.weather?.kind, roomSeason);
    return mh && mh.kind === 'art' ? mh.url : null;
  };
  // Start the walk ON THE TAP, before the house answers. Returns its id, or
  // null = no walk (camera off, an empty /room, already in that room).
  const beginRoomTransit = (text) => {
    if (!roomCameraOn) return null;
    const rooms = houseStatus?.rooms || [];
    const to = resolveRoomText(text, rooms);
    if (!to || to === viewerRoom) return null;
    const reduceMotion = !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
    const invite = liveEvent ? roomInviteFor(liveEvent, rooms.map((r) => r.id)) : null;
    const kind = pickTransit({ to, rooms, outingId: houseStatus?.outing?.id || null, eventRoom: invite?.room || null, reduceMotion });
    if (!kind) return null;
    // 📱 09-25 (Nina: "go"): phones walk on the whole screen too (the room card
    // scrolls away while you read, so its walk mostly played off screen).
    // 'card' stays in RoomTransit for a one-line revert.
    const view = 'screen';
    const dark = to !== UNKNOWN_ROOM && !!roomMasthead(to, houseStatus?.circadian?.clock?.hour, houseStatus?.room_art, houseStatus?.weather?.kind, roomSeason)?.dark;
    return roomTransitApi.begin({
      kind, from: viewerRoom, to, view,
      line: walkLine(to, dark),
      // a phone's painting is the room card's (no wallpaper art on phones)
      art: roomTransitArt(to, isMobile ? 'card' : 'screen'), // preload only; the answer names the real one
      keyhole: keyholeView(to),
      wet: wetSteps(to, houseStatus?.weather?.kind),
      // the page steps back under every desktop walk, the still line too
      // (reduced motion: a fade, no movement; the line never sits on the chat)
      curtain: view === 'screen',
    });
  };

  const handleInterjectionSubmit = async (message, targetPlayers = [], command = null) => {
    if (!humanPlayerId) {
      throw new Error('No player ID set');
    }
    if (command?.name === 'challenge') {
      await launchChallenge(command);
      return;
    }
    if (command?.name === 'doodle') {
      await launchDoodle();
      return;
    }
    // 🚪 Bare /room = the PICTURES (09-18: the rooms picker replaced the text
    // list toast). A pure read answered from the payload we already hold —
    // never hits the API. The private_message-shaped return matters:
    // handleInterjectionAndPlay's /room guard keys on it, so opening the
    // picker can never wake the AIs.
    if (command?.name === 'room' && (!command.text || /^(list|rooms)$/i.test(String(command.text).trim()))) {
      setRoomNotice(null);
      setRoomPickerOpen(true);
      return { private_message: 'rooms', room_list: true };
    }
    // 🐛 /bug (09-20): opens the app-level bug report modal, prefilled with
    // whatever followed the word. Same private shape as /look: never hits the
    // chat API, never wakes the AIs. A bot never files one (the modal would
    // sit over the livestream).
    if (command?.name === 'bug') {
      if (!isBotMode) openBugReport({ text: command.text || '', source: 'slash_bug' });
      return { private_message: 'bug', room_list: true };
    }
    // 🏡⋯ W2d: the place page's /watch /live /story are client-only doors
    // (the ⋯ menu's items): open the surface, post NOTHING, wake nobody (the
    // private_message shape, like /bug). Only a place page parses them.
    // /watch WITH words ("/watch deep sea creatures", the palette's own tip)
    // starts a party with them, the /watchparty lever; you asked, so you sit
    // down when it arrives. A bare /watch opens the launcher.
    if (portedPlaceOn && command?.name === 'watch' && command.text && !isBotMode) {
      command = { ...command, name: 'watchparty' };
      placeSeatOnPartyRef.current = Date.now();
    }
    if (portedPlaceOn && ['watch', 'live', 'story'].includes(command?.name)) {
      if (!isBotMode) openPlaceExtra(command.name);
      return { private_message: command.name, room_list: true };
    }
    // 📋 W5: the place page's /task is client-only too: it opens the journal's
    // Tasks page with the words (the timing chips live there). Posts nothing.
    if (portedPlaceOn && command?.name === 'task') {
      if (!isBotMode && isPortedOwner) openPortedTasks(command.text || '');
      return { private_message: 'task', room_list: true };
    }
    // 📚 /borrow while the House library is live (house.book present): bare = the shelf,
    // "/borrow frankenstein" = open that book for the room through bookControl. Outside the
    // library it walks you there first. Client-side, the /look shape: never the chat API,
    // never a wake. (library.live false = the old server verb, byte for byte.)
    if (command?.name === 'borrow' && library.live) {
      if (!isBotMode) library.borrow(command.text || '');
      return { private_message: 'borrow', room_list: true };
    }
    // 👀 /look: same private, client-answered shape (never hits the API,
    // never wakes the AIs — the guard keys on private_message).
    if (command?.name === 'look') {
      // 👁 09-23 (Nina: A + typed follows the chip): /look opens the room's
      // picture with the look text beside it — one verb, one result
      // (precedent: a bare /room opens the pictures). The main room, a room
      // with no painting yet, and bots keep the quiet note (a modal would sit
      // over the livestream — the /bug rule).
      if (!isBotMode && roomCameraOn && viewerRoom !== 'house' && roomPeekUrl) {
        setRoomNotice(null);
        setRoomPeekOpen(true);
        return { private_message: 'look', room_list: true };
      }
      const notice = buildRoomLookNotice();
      setRoomNotice(notice);
      return { private_message: notice, room_list: true };
    }

    if (!command && interjectionsRemaining <= 0) {
      throw new Error('No messages remaining this round');
    }

    // 📺 @watchparty (08-13): a quip-shaped send is destined for the video
    // overlay, not the main feed — no optimistic bubble (nothing will ever
    // replace it there). Backend confirms with watch_quip:true; if the party
    // ended mid-send the message posts normally and arrives via the refresh.
    const isWatchQuipShaped = !command && !!chatGameStatus?.watch_party
      && /^@watch\s*party\b/i.test(String(message || '').trim());

    // Optimistic update: show interjection immediately
    const optimisticId = `optimistic_${Date.now()}_${Math.random()}`;
    const humanPlayer = users.find(u => u.game_player_id === humanPlayerId);
    const optimisticAction = isWatchQuipShaped ? null : command
      ? (command.card_message
          ? {
              id: optimisticId,
              type: 'space_command',
              game_instance_player_id: humanPlayerId,
              data: {
                command: command.name,
                card_message: command.card_message,
                issued_by: { name: humanPlayer?.name || 'You' },
                status: 'active',
              },
              created_at: new Date().toISOString(),
              isOptimistic: true,
            }
          // Content commands (/note /watch /listen): the server posts the
          // artifact as shared_content — no optimistic card to mismatch it.
          : null)
      : {
          id: optimisticId,
          type: 'human_interjection',
          game_instance_player_id: humanPlayerId,
          data: {
            sender_name: humanPlayer?.name || 'You',
            message: message,
            target_players: targetPlayers,
            // 🆔 08-03: echoed by the backend into the action + WS broadcast
            // so the dedupe matches exactly (see the WS matcher).
            client_msg_id: optimisticId,
          },
          created_at: new Date().toISOString(),
          isOptimistic: true,
        };
    if (optimisticAction) {
      // Same re-arm + timestamp clamp as handleHumanSubmit (08-02 chat-fix
      // batch) — sending re-arms the bottom snap, and the optimistic
      // created_at must sort AFTER everything already in the feed.
      shouldAutoScrollRef.current = true;
      setResults(prev => {
        if (prev.length === 0) return [{ actions: [optimisticAction] }];
        const existing = prev.flatMap(r => r.actions || []);
        const lastTs = existing.length ? (Date.parse(existing[existing.length - 1].created_at) || 0) : 0;
        optimisticAction.created_at = new Date(Math.max(Date.now(), lastTs + 1)).toISOString();
        return [{ actions: [...existing, optimisticAction] }];
      });
    }

    soundManager.play('sent');
    // 🏡 Their Place: your line lights the lamp nearest you (no-op when off)
    portedStageRef.current?.youSent?.();
    setPlaceLampDown(false); // 🌙 a line from you lifts the night-light
    trackEvents.interjectionSent({ spaceId: gameId, messageLength: message.length, hasTargets: targetPlayers.length > 0 });
    // 🚪🕯️ the walk starts here, before the house answers (never after)
    const transitId = command?.name === 'room' ? beginRoomTransit(command.text) : null;

    try {
      // Submit the interjection — object form rides client_msg_id to the
      // backend (string form still works for other callers).
      const submitResp = await chatGameApi.submitInterjection(gameId, humanPlayerId, { message, client_msg_id: optimisticId }, targetPlayers, command);
      // 🫂 the crisis card (§5.12, backend careCrisis.js, dark behind
      // CARE_CRISIS_CARD_ENABLED): ONLY this sender's own response, every room
      // kind, once a session (takeCrisisCard), never a bot. The message posted.
      if (!isBotMode && takeCrisisCard(submitResp)) setCrisisOpen(true);

      // 🚪📷 /room is PRIVATE (Wave 4): the server answers in the response
      // body only — reflect the camera locally and show the one quiet line.
      // No optimistic card existed (cardless command), nothing to clean up.
      if (command?.name === 'room') {
        if (submitResp?.room_camera?.room) {
          setRoomMoveLocal({ room: submitResp.room_camera.room, at: Date.now() });
          try { localStorage.setItem(`viewer_room_${gameId}`, submitResp.room_camera.room); } catch (e) { /* hint only */ }
        }
        // 🚪🕯️ What the move shows once it has landed. With a walk playing it
        // waits for the walk (the card then runs for what is left of today's
        // 4.5s, so the chat comes back when it always did); without one it
        // shows now, exactly as before.
        let settleMove = null;
        const movedTo = submitResp?.room_camera?.room;
        if (submitResp?.private_message) {
          // A successful move gets the room's flavor line + who's actually
          // there (circadian presence, client-matched — free). Locked doors
          // and unknowns keep the server's line untouched.
          let line = submitResp.private_message;
          if (movedTo) {
            // 🌙 the night line rides the night PICTURE (masthead.dark), never the clock
            const movedDark = !!roomMasthead(movedTo, houseStatus?.circadian?.clock?.hour, houseStatus?.room_art, houseStatus?.weather?.kind, roomSeason)?.dark;
            const flavor = roomEntryLine(movedTo, houseStatus?.circadian?.clock?.hour, movedDark);
            // One resident gets their doing-line ("Marlow is here, grouting.");
            // a crowd stays names-only so the toast reads in a glance.
            const presenceLine = roomPresenceLine(roomPresenceFor(movedTo));
            line = [line, flavor, presenceLine].filter(Boolean).join(' ');
            // 🚪✨ Arrival moment (09-17): a real move into a room whose art
            // is on the wallpaper gets ~2.5s of the room itself before the
            // toast. Desktop + motion-OK only; the toast follows the settle.
            const reduceMotion = !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
            // 📱 09-25: phones get the postcard too (a room with a painting),
            // over the whole painting the walk ended on
            const phoneArt = isMobile ? roomTransitArt(movedTo, 'card') : null;
            const arrives = movedTo !== 'house' && movedTo !== viewerRoom && !reduceMotion
              && (isMobile ? !!phoneArt
                : !!roomArtPlan(movedTo, houseStatus?.circadian?.clock?.hour, houseStatus?.room_art, houseStatus?.weather?.kind, { desktop: true, season: roomSeason }).wallpaper);
            if (arrives) {
              // 09-19: the postcard now SAYS the room line and who is here, so
              // the toast after it carries only the server's own line (reading
              // the same two sentences twice in six seconds is a stutter).
              const settled = submitResp.private_message;
              const card = { room: movedTo, settled, art: phoneArt, ...roomArrival(flavor, roomPresenceFor(movedTo)) };
              settleMove = (walk) => {
                setRoomNotice(null);
                clearTimeout(roomArriveTimerRef.current);
                const ms = isMobile ? PHONE_CARD_MS
                  : ((walk && Number.isFinite(walk.shownMs)) ? arrivalCardMs(walk.netMs, walk.shownMs) : ARRIVE_MS);
                setRoomArriving({ ...card, ms });
                roomArriveTimerRef.current = setTimeout(() => { setRoomArriving(null); setRoomNotice(settled); }, ms);
              };
              line = null;
            }
          }
          if (line) settleMove = () => setRoomNotice(line);
        }
        if (transitId != null) {
          roomTransitApi.answer(transitId, {
            ok: !!movedTo,
            art: movedTo ? roomTransitArt(movedTo, isMobile ? 'card' : 'screen') : null,
            // superseded = a newer walk took over: this move's words are stale
            onSettle: (walk) => { if (!(walk && walk.superseded) && settleMove) settleMove(walk); },
          });
        } else if (settleMove) settleMove(null);
      } else if (submitResp?.no_wake && submitResp?.private_message) {
        // 🕯️ Room verbs answer privately too (the wrong room, /sky, a
        // greenhouse with nothing thirsty): same one quiet line as /room.
        setRoomNotice(submitResp.private_message);
        // 🍎🧺 a pick answers with the fresh basket: move the band NOW instead
        // of on the next status poll (the poll stays the source of truth and
        // overwrites this). Only ever merges into an outing that is on screen.
        // 🎬🍿 a film vote answers with the fresh ballot: same idea, same rule
        if (submitResp.ballot && typeof submitResp.ballot === 'object') {
          setChatGameStatus((prev) => ((prev && prev.house && prev.house.ballot)
            ? { ...prev, house: { ...prev.house, ballot: submitResp.ballot } }
            : prev));
        }
        if (submitResp.outing && typeof submitResp.outing === 'object') {
          setChatGameStatus((prev) => ((prev && prev.house && prev.house.outing)
            ? { ...prev, house: { ...prev.house, outing: { ...prev.house.outing, ...submitResp.outing } } }
            : prev));
        }
      }

      // 🔓 Unlock progress (gameUnlocks.js): real sends only, after success
      if (!isBotMode) {
        bumpProgress('msgs');
        if (command?.name === 'build') bumpProgress('crafts');
        // 🎓 tutorial v2 signals (post-success only, so errored commands never
        // count): plain message = said hi; /gift = gift; /ship = ship (§3d);
        // any other /command counts as the build step — copy is specific,
        // checks are generous
        if (command?.name === 'gift') markTutorial('gift');
        else if (command?.name === 'ship') markTutorial('ship');
        // 🚪 /room is a camera move, not a creative act — never the build step.
        else if (command && command.name !== 'room' && command.name !== 'status') { // 👥 10-01 people: + status
          markTutorial('build');
          // 🧾 Receipt for the tour's success card: only an actual /build
          // with a premise names a shelf item worth pointing at.
          if (command.name === 'build' && command.text) setTutorialBuildReceipt(command.text);
        }
        else markTutorial('said');
      }

      // Wait a shorter time for DB write
      await new Promise(resolve => setTimeout(resolve, 100));

      // Refresh game status (will replace optimistic message with real one)
      await fetchChatGameStatus();
      return submitResp;
    } catch (error) {
      console.error('Failed to submit interjection:', error);
      if (transitId != null) roomTransitApi.cancel(transitId);
      // Remove optimistic message on error
      setResults(prev => [{
        actions: prev.flatMap(r => r.actions || []).filter(a => a.id !== optimisticId)
      }]);
      throw error;
    }
  };

  // Wrapped interjection handler: send message then auto-trigger AI responses
  const handleInterjectionAndPlay = async (message, targetPlayers = [], command = null) => {
    const resp = await handleInterjectionSubmit(message, targetPlayers, command);
    // 📺 @watchparty (08-13): the send floated on the video — the story room
    // wasn't spoken to, so don't wake it.
    if (resp?.watch_quip) return;
    // 🚪📷 /room never wakes the AIs — the room wasn't spoken to (Wave 4).
    if (resp?.room_camera || resp?.private_message) return;
    // 🕯️ Room verbs never wake the room from HERE (ROOM_VERBS_PEEK_SPEC D6):
    // a nudge wakes exactly the residents in that room, and the server does
    // it. A client wake would spend a poke and earn the whole house a fresh
    // round of replies.
    if (resp?.no_wake) return;
    // Small delay so the interjection is visible before AIs start responding
    setTimeout(() => {
      if (!isPlayingRef.current) {
        handlePlayRound();
      }
    }, 600);
  };

  // ChatGame-specific: Handle spectating message submission (dead/spectating players)
  const handleSpectatingSubmit = async (message) => {
    try {
      // Use effectivePlayerId (which is either humanPlayerId or spectator ID)
      // The backend will handle whether this is allowed based on player status
      const senderId = effectivePlayerId;

      // For UI display, find the human_randomID name if available
      const displayName = humanPlayerId
        ? (users.find(u => u.game_player_id === humanPlayerId)?.name || 'Ghost')
        : (users.find(u => u.is_human && u.name && u.name.startsWith('human_'))?.name || 'Spectator');

      // Optimistic update: Add message immediately to UI
      const optimisticMessage = {
        id: `optimistic_${Date.now()}_${Math.random()}`,
        sender: displayName,
        message: message,
        timestamp: new Date().toISOString(),
        sender_id: senderId,
        is_spectator: senderId.startsWith('spectator_'),
        isOptimistic: true
      };

      setSpectatingMessages(prev => [...prev, optimisticMessage]);

      // Pass the displayName so backend shows correct sender name
      trackEvents.spectatingMessageSent({ spaceId: gameId, messageLength: message.length });
      await chatGameApi.submitSpectatingMessage(gameId, senderId, message, displayName);

      // The real message arrives via WebSocket and replaces the optimistic one.
      // Clean up any remaining optimistic messages as a fallback.
      setSpectatingMessages(prev => prev.filter(m => !m.isOptimistic));
    } catch (error) {
      console.error('Failed to submit spectating message:', error);
      // Remove the optimistic message on error
      setSpectatingMessages(prev => prev.filter(m => !m.isOptimistic));
      throw error;
    }
  };

  // ChatGame-specific: Handle vote submission (players and spectators)
  const handleVoteSubmit = async (voteData) => {
    if (!effectivePlayerId) {
      throw new Error('No player ID set');
    }
    trackEvents.voteCast({ spaceId: gameId, targetName: voteData?.target_name });

    try {
      if (currentRequestId) {
        console.log('[handleVoteSubmit] Responding to interaction request:', currentRequestId, 'voteData:', voteData);
        try {
          await chatGameApi.respondToInteraction(currentRequestId, voteData);
          console.log('[handleVoteSubmit] Interaction response succeeded');
          setCurrentRequestId(null);
        } catch (interactionErr) {
          console.error('[handleVoteSubmit] Interaction response failed:', interactionErr.message, 'current_request_id:', interactionErr.current_request_id);
          // If there's a newer request, retry with it
          if (interactionErr.current_request_id) {
            console.log('[handleVoteSubmit] Retrying with newer request:', interactionErr.current_request_id);
            setCurrentRequestId(interactionErr.current_request_id);
            await chatGameApi.respondToInteraction(interactionErr.current_request_id, voteData);
            setCurrentRequestId(null);
          } else {
            // Request expired with no replacement (vote window closed while the modal
            // was open). Fall back to the direct vote write — the round tallies
            // votes_cast, so a buzzer-beater still counts while the round is live.
            console.warn('[handleVoteSubmit] Request expired, falling back to submitVote');
            await chatGameApi.submitVote(gameId, effectivePlayerId, voteData);
            setCurrentRequestId(null);
            await fetchChatGameStatus();
          }
        }
      } else {
        console.warn('[handleVoteSubmit] No currentRequestId, using submitVote fallback');
        await chatGameApi.submitVote(gameId, effectivePlayerId, voteData);
        await fetchChatGameStatus();
      }
    } catch (error) {
      console.error('[handleVoteSubmit] Failed:', error);
      throw error;
    }
  };

  // ChatGame-specific: Handle reaction submission
  // Mute the reaction sound for a beat after OUR OWN reaction — the
  // reaction_update WS echo carries no reactor id, so without this every
  // reaction you give plays the incoming-reaction chime at you (08-02 chat
  // batch; every other sound in processIncomingAction gates on sender).
  const ownReactionSoundMuteRef = useRef(0);
  const handleReactionSubmit = async (actionId, reaction) => {
    ownReactionSoundMuteRef.current = Date.now() + 2000;
    // Generate or get spectator ID if not a player
    let reactorId = humanPlayerId;
    if (!reactorId) {
      // Get or create spectator ID from localStorage
      const spectatorKey = `spectator_id_${gameId}`;
      reactorId = localStorage.getItem(spectatorKey);
      if (!reactorId) {
        reactorId = `spectator_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        localStorage.setItem(spectatorKey, reactorId);
      }
    }

    try {
      await chatGameApi.submitReaction(gameId, reactorId, actionId, reaction);
      // Don't refetch immediately - let the optimistic UI update show instantly
      // The 2-second polling will sync it with the backend
    } catch (error) {
      console.error('Failed to submit reaction:', error);
    }
  };

  // ChatGame: no frontend-driven progression — the backend cron handles all game steps.
  // The frontend only responds to interaction requests (human turns, votes, etc.).

  // ChatGame-specific: Ensure a human player exists for this browser
  const ensureHumanPlayerInFlightRef = useRef(false);
  useEffect(() => {
    if (!gameId) return;
    if (gameType !== 'chat-game' && gameType !== 'chat') return;
    if (humanPlayerId) { hasAutoJoinedRef.current = true; return; } // Already actively playing
    if (hasAutoJoinedRef.current) return; // Already ran
    if (ensureHumanPlayerInFlightRef.current) return; // Already in-flight

    const ensureHumanPlayer = async () => {
      ensureHumanPlayerInFlightRef.current = true;
      try {
        // 🔐 accounts (09-30): your seat follows you. Signed in, the account's seat in
        // this room wins over a stranger mint AND over a throwaway seat this device made
        // before signing in (the house knows the original you). The account answers only
        // seats it proved (live, not kicked, not left), so no verify round trip is needed.
        // Answers null at once (no call) while sign-in is off or nobody is signed in,
        // never waits past its deadline, and a failure here falls through to the old path.
        if (!isBotMode) {
          let followed = null;
          try { followed = await accountSeatForRoom(gameId); } catch (e) { followed = null; }
          if (followed && followed.player_id) {
            hasAutoJoinedRef.current = true;
            createdPlayerIdRef.current = followed.player_id;
            setHumanPlayerId(followed.player_id);
            const url = new URL(window.location);
            url.searchParams.set('playerId', followed.player_id);
            window.history.replaceState({}, '', url);
            if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
              wsRef.current.send(JSON.stringify({ action: 'joinGame', gameInstanceId: gameId, playerId: followed.player_id }));
            } else {
              wsPendingSendsRef.current.push({ action: 'joinGame', gameInstanceId: gameId, playerId: followed.player_id });
            }
            if (followed.pulled && followed.name) showAccountToast(`Welcome back, ${followed.name}.`);
            return;
          }
        }
        // Check localStorage for a previously created human player for this game
        const storedId = localStorage.getItem(`humanPlayerId_${gameId}`);
        if (storedId) {
          const hasSecret = !!chatGameApi.getPlayerSecret(storedId);
          if (hasSecret) {
            // Verify the player still exists in the DB (may have been deleted by a game reset)
            try {
              const status = await chatGameApi.getGameStatus(gameId, storedId);
              const playerExists = status?.players?.some(p => p.id === storedId);
              if (playerExists) {
                console.log('[GameStepPage] Found existing human player in localStorage:', storedId);
                hasAutoJoinedRef.current = true;
                createdPlayerIdRef.current = storedId;
                setHumanPlayerId(storedId);
                // 🔧 Retro maintainer tag (09-01): a pre-tag seat restored
                // here never re-mints, so upgrade it in place. Self-gating
                // (no admin key in this browser = no-op), fire-and-forget.
                const seatRow = status.players.find(p => p.id === storedId);
                if (!seatRow?.is_maintainer) chatGameApi.claimMaintainerSeat(gameId, storedId);
                // Re-send joinGame with player ID in case WS connected before this
                // resolved; queue (not drop) if the socket is mid-connect
                if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
                  wsRef.current.send(JSON.stringify({ action: 'joinGame', gameInstanceId: gameId, playerId: storedId }));
                } else {
                  wsPendingSendsRef.current.push({ action: 'joinGame', gameInstanceId: gameId, playerId: storedId });
                }
                return;
              }
              console.log('[GameStepPage] Stored player no longer exists in game, creating new one');
            } catch (e) {
              // [5g23-seat] Transient verify failure (e.g. getGameStatus network blip —
              // common right after an auto-reset churns the WS). Do NOT purge + re-mint:
              // minting a fresh addHumanPlayer seat here is the id-rotation/seat-proliferation
              // bug (5g.22 #5 — bot ended a Glitch game with 6 player_ids). We hold this id's
              // secret, so trust + adopt it; a later status poll self-corrects if it's truly
              // gone. Only a DEFINITIVE playerExists=false (above) re-mints.
              console.log('[GameStepPage] Could not verify stored player (transient) — keeping stored id, NOT re-minting:', storedId);
              hasAutoJoinedRef.current = true;
              createdPlayerIdRef.current = storedId;
              setHumanPlayerId(storedId);
              // 🔧 Retro maintainer tag — idempotent, safe on the transient
              // path too (server verifies seat + secret; wrong seat = no-op).
              chatGameApi.claimMaintainerSeat(gameId, storedId);
              if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
                wsRef.current.send(JSON.stringify({ action: 'joinGame', gameInstanceId: gameId, playerId: storedId }));
              } else {
                wsPendingSendsRef.current.push({ action: 'joinGame', gameInstanceId: gameId, playerId: storedId });
              }
              return;
            }
          } else {
            console.log('[GameStepPage] Found player ID but missing secret, creating new player');
          }
          localStorage.removeItem(`humanPlayerId_${gameId}`);
        }

        // Always create a human player and auto-join as active player for chat games.
        // [requester-ready-fix] bot-mode seats get is_external_agent so the backend
        // always-ready escape hatches (skipReadyCheck 5g15-fix1 / ReadyCheckRound
        // 5g19) protect them — the PLAY NOW seat used to lack the flag and got
        // demoted to spectator when ready-check ended.
        console.log('[GameStepPage] Creating human player (auto-joining as active player)');
        const result = await chatGameApi.addHumanPlayer(gameId, { isExternalAgent: isBotMode, playerName: isBotMode ? botAgentName : null });
        if (result.success && result.player_id) {
          console.log('[GameStepPage] Created human player:', result.player_name, result.player_id);
          hasAutoJoinedRef.current = true;
          createdPlayerIdRef.current = result.player_id;
          // Persist to localStorage so we don't create duplicates
          localStorage.setItem(`humanPlayerId_${gameId}`, result.player_id);
          accountClaimSeat(result.player_id, gameId); // 🔐 accounts: signed in → this new seat follows you too (no-op otherwise)
          // Creator — start as active player
          setHumanPlayerId(result.player_id);
          const url = new URL(window.location);
          url.searchParams.set('playerId', result.player_id);
          window.history.replaceState({}, '', url);

          // Re-send joinGame with the new player ID so the backend associates
          // this WS connection with the player (initial joinGame may have sent
          // before the player existed, leaving us as an anonymous spectator);
          // queue (not drop) if the socket is mid-connect
          if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
            wsRef.current.send(JSON.stringify({ action: 'joinGame', gameInstanceId: gameId, playerId: result.player_id }));
          } else {
            wsPendingSendsRef.current.push({ action: 'joinGame', gameInstanceId: gameId, playerId: result.player_id });
          }

          // Trigger Glitch reveal for imposter mode
          try {
            const freshStatus = await chatGameApi.getGameStatus(gameId, result.player_id);
            if (freshStatus?.imposter_mode && !glitchRevealShownRef.current) {
              glitchRevealShownRef.current = true;
              setChatGameStatus({ ...freshStatus, _stateTimestamp: freshStatus.timestamp || Date.now() });
              setShowGlitchReveal({ isImposter: !!freshStatus.is_imposter, topic: freshStatus.imposter_topic, doodle: !!freshStatus.doodle_bug });
              emitGameEvent(GAME_EVENTS.GLITCH_REVEAL, {
                isGlitch: freshStatus.is_imposter,
                topic: freshStatus.imposter_topic
              });
            }
          } catch (e) {
            console.error('[GameStepPage] Failed to fetch imposter status:', e);
          }
        }
      } catch (error) {
        console.error('[GameStepPage] Human player creation failed:', error);
      }
    };

    ensureHumanPlayer();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- isCreatorVisit removed: not used in effect body, and searchParams changes caused duplicate player creation
  }, [gameId, gameType, humanPlayerId]);

  // ChatGame-specific: Fetch status when game loads
  useEffect(() => {
    if (gameId && (gameType === 'chat-game' || gameType === 'chat')) {
      fetchChatGameStatus();
    }
  }, [gameId, gameType, fetchChatGameStatus]);

  // ChatGame-specific: Periodic status polling for ghost chatters & reactions
  // Ghost chat messages are triggered server-side by getGameStatus() calls,
  // so we need regular polling to keep them active (every 8s during play)
  useEffect(() => {
    if (!gameId || (gameType !== 'chat-game' && gameType !== 'chat')) return;
    if (!isPlaying) return;

    const interval = setInterval(() => {
      // (perf 08-13) same hidden-tab bail as the 5s poll. Server-side ghost
      // triggers ride getGameStatus calls, so this also stops generating
      // ghost chatter for a tab nobody is watching.
      if (document.hidden) return;
      fetchChatGameStatus();
    }, 8000);

    return () => clearInterval(interval);
  }, [gameId, gameType, isPlaying, fetchChatGameStatus]);

  // ChatGame-specific: Background sync poll (runs even when not playing)
  // Catches stuck isPlaying state if WS autoplay_paused was missed
  // Also picks up ghost chat burst messages shortly after page load
  useEffect(() => {
    if (!gameId || (gameType !== 'chat-game' && gameType !== 'chat')) return;
    if (isPlaying) return; // The 8s poll above handles this case

    const isNonComp = !competitiveModeRef.current;

    if (isNonComp) {
      // Non-competitive: aggressive early catch-up polls for ghost chat burst & preseed.
      // These fire-and-forget tasks are triggered by the first getGameStatus call and
      // involve 2-3 AI calls (2-5s each), completing 3-10s after page load. WS broadcast
      // may be missed due to a race between connection registration and the broadcast,
      // so we poll at 2s, 5s, 10s to catch results quickly, then relax to 15s.
      const earlyDelays = [2000, 5000, 10000]; // cumulative offsets from mount
      const timeouts = earlyDelays.map(delay =>
        setTimeout(() => fetchChatGameStatusRef.current(), delay)
      );
      // After early polls, switch to relaxed interval
      const longInterval = setInterval(() => {
        fetchChatGameStatusRef.current();
      }, 15000);

      return () => {
        timeouts.forEach(t => clearTimeout(t));
        clearInterval(longInterval);
      };
    }

    // Competitive/imposter: unchanged — 10s polling
    const interval = setInterval(() => {
      fetchChatGameStatusRef.current();
    }, 10000);

    return () => clearInterval(interval);
  }, [gameId, gameType, isPlaying]);

  // ChatGame-specific: WebSocket connection for real-time push
  useEffect(() => {
    if (!gameId || (gameType !== 'chat-game' && gameType !== 'chat')) return;

    let isCancelled = false;

    const connect = () => {
      if (isCancelled) return;

      const ws = new WebSocket(WS_URL);
      // 🔒 09-29 batch 2: every send rides this socket (onopen's join, the queue flush, wsSend, the re-joins), so the
      // seat proof is added here once (withSeatProof)
      const rawWsSend = ws.send.bind(ws);
      ws.send = (data) => rawWsSend(withSeatProof(data));
      wsRef.current = ws;

      let heartbeatInterval = null;

      ws.onopen = () => {
        console.log('[WS] Connected, joining game:', gameId);
        wsBackoffRef.current = 3000; // Reset backoff on successful connection
        ws.send(JSON.stringify({ action: 'joinGame', gameInstanceId: gameId, playerId: humanPlayerIdRef.current || undefined }));
        // Reload game state on (re)connect to catch any messages missed while disconnected
        fetchChatGameStatusRef.current();
        // Drain any messages queued while WS wasn't OPEN (e.g., startPlaying fired
        // before initial connect, or during a reconnect window).
        if (wsPendingSendsRef.current.length > 0) {
          const queued = wsPendingSendsRef.current;
          wsPendingSendsRef.current = [];
          console.log('[WS] Flushing ' + queued.length + ' queued message(s) on connect');
          for (const msg of queued) {
            ws.send(JSON.stringify(msg));
          }
        }
        heartbeatInterval = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            // playerId rides every heartbeat so a lost joinGame registration
            // self-heals within 30s (presence reads the connection's player_id)
            ws.send(JSON.stringify({ action: 'heartbeat', playerId: humanPlayerIdRef.current || undefined }));
          }
        }, 30000);
      };

      ws.onmessage = (event) => {
        try {
          const msg = JSON.parse(event.data);
          if (msg.type === 'subscribed' && msg.connection_id) {
            ourConnectionIdRef.current = msg.connection_id;
          }

          if (msg.type === 'new_action' || msg.type === 'chat_action') {

            if ((gameType === 'chat-game' || gameType === 'chat') && msg.action) {
              processIncomingAction(msg.action);
            } else if (msg.action) {
              // Non-chat games: append the action directly to results
              // Cycle 5b.2: see chat-action handler above — bot mode forces true
              shouldAutoScrollRef.current = isBotMode || isScrolledToBottom();
              setResults(prevResults => {
                const newResults = [...prevResults];
                newResults.push({ actions: [msg.action] });
                return newResults;
              });
            }
          }
          // Handle AI description updates for shared content
          // ⚠️ SIBLING-PRESERVING MERGE (fixed 08-07, scene arc). Both of these
          // updaters used to `return [{ actions: updated }]`, which threw away
          // every OTHER field on results[0] — and the full fetch stores the
          // entire status payload there (`[{ ...data, actions: merged }]`). So a
          // single lifecycle patch silently emptied everything else any consumer
          // reads off results[0], until the next full refetch put it back.
          // Latent while these messages were rare (/imagine, /build, /combine);
          // the scene countdown now patches on every local's turn, which would
          // have made it constant. `...prev[0]` is the whole fix.
          if (msg.type === 'shared_content_description') {
            setResults(prev => {
              const allActions = prev.flatMap(r => r.actions || []);
              const idx = allActions.findIndex(a => a.id === msg.action_id);
              if (idx === -1) return prev;
              const updated = [...allActions];
              updated[idx] = { ...updated[idx], data: { ...updated[idx].data, description: msg.description } };
              return [{ ...(prev[0] || {}), actions: updated }];
            });
          }

          // Slash-command card lifecycle (/imagine /build /combine): flips the
          // pending event card to completed/failed in place (same merge pattern
          // as shared_content_description). 🎭 Also carries the /scene room
          // countdown (`remaining`) — sent whole, because this merge is a
          // SHALLOW spread over `data` and a partial would drop the per-target
          // counters that share the object.
          if (msg.type === 'space_command_update') {
            setResults(prev => {
              const allActions = prev.flatMap(r => r.actions || []);
              const idx = allActions.findIndex(a => a.id === msg.action_id);
              if (idx === -1) return prev;
              const updated = [...allActions];
              updated[idx] = { ...updated[idx], data: { ...updated[idx].data, ...(msg.data || {}) } };
              return [{ ...(prev[0] || {}), actions: updated }];
            });
          }

          if (msg.type === 'game_status') {
            setGameStatusMessage(msg.message);
            // Auto-clear after 15 seconds if no new status arrives
            setTimeout(() => {
              setGameStatusMessage(prev => prev === msg.message ? null : prev);
            }, 15000);
            // 💭 W2c: "Waiting for {resident}'s response..." on the owner's
            // place page asks (owner POST) what is going into that reply
            if (portedThinkRef.current) portedThinkRef.current(msg);
          }

          // --- livestream_update: someone started/stopped publishing webcam/screen ---
          if (msg.type === 'livestream_update') {
            setSpaceLiveStreamers(msg.streamers || []);
          }

          // --- game_state_update: real-time state snapshot for top bar ---
          if (msg.type === 'game_state_update') {
            setChatGameStatus(prev => prev ? {
              ...prev,
              next_speaker: msg.next_speaker !== undefined ? msg.next_speaker : prev.next_speaker,
              round_number: msg.round_number ?? prev.round_number,
              current_round_type: msg.round_type ?? prev.current_round_type,
              round_type: msg.round_type ?? prev.round_type,
              is_voting: msg.is_voting ?? prev.is_voting,
              is_playing: msg.is_playing ?? prev.is_playing,
              game_completed: msg.game_completed ?? prev.game_completed,
              current_competitive_round: msg.competitive_round ?? prev.current_competitive_round,
              imposter_word_round: msg.imposter_word_round ?? prev.imposter_word_round,
              turn_index: msg.turn_index ?? prev.turn_index,
              turn_total: msg.turn_total ?? prev.turn_total,
              _stateTimestamp: msg.timestamp || Date.now(),
            } : prev);
          }

          // --- agent_status_update / agent_display_update: Bot Vision (OpenClaw agent) live status ---
          if (msg.type === 'agent_status_update') {
            setChatGameStatus(prev => prev ? {
              ...prev,
              agent_status: msg.agent_status ?? prev.agent_status,
              agent_url: msg.agent_url !== undefined ? msg.agent_url : prev.agent_url,
              agent_id: msg.agent_id !== undefined ? msg.agent_id : prev.agent_id,
            } : prev);
          }
          if (msg.type === 'agent_display_update') {
            setChatGameStatus(prev => prev ? {
              ...prev,
              agent_display_mode: msg.agent_display_mode ?? prev.agent_display_mode,
            } : prev);
          }

          // --- typing_started / typing_stopped: dedicated typing indicator control ---
          // 🔇 Typing events NO LONGER reach the theme bus (Nina 08-14): the
          // theme generator taught every generated theme to render its own
          // "X is typing..." text on game:typing_started, with no stop story
          // (the generator docs never mentioned typing_stopped) — and the
          // event refires on every AI turn in every lane, so the text read as
          // permanently stuck chrome. The floating TypingIndicator pill is
          // the ONE sanctioned thinking surface; silencing the bus here kills
          // the theme copy on every already-generated theme at once (they
          // cannot render what they never hear — the prop-UI-shield pattern).
          if (msg.type === 'typing_started' && msg.player_name) {
            setCurrentlyTyping(msg.player_name);
          }
          if (msg.type === 'typing_stopped' && msg.player_name) {
            setTimeout(() => {
              setCurrentlyTyping(prev => prev === msg.player_name ? null : prev);
            }, 1500);
          }
          // 🏡 Their Place: the room band's writing cue rides the WS pair ONLY
          // (no delay, no guess; handoff §2 honesty). W2c seam fix: only while
          // the place is on (the ref is null otherwise): every other room used
          // to re-render the whole page twice per AI reply for a cue nobody drew.
          if (portedResidentNameRef.current) {
            if (msg.type === 'typing_started' && msg.player_name) setWsTypingName(msg.player_name);
            if (msg.type === 'typing_stopped' && msg.player_name) setWsTypingName(prev => (prev === msg.player_name ? null : prev));
          }
          // 💭 W2c: the owner's thought bubble clears when his reply starts or ends
          if (portedThinkRef.current && (msg.type === 'typing_started' || msg.type === 'typing_stopped')) portedThinkRef.current(msg);

          // --- your_turn: server is waiting for human input ---
          if (msg.type === 'your_turn') {
            soundManager.play('yourTurn');
            const myId = humanPlayerIdRef.current; // Use ref to avoid stale closure
            console.log('[WS] your_turn received for', msg.player_name, 'request_id:', msg.request_id, '(we are', myId, ')');
            // Ignore if this turn is for a different player (broadcast goes to all connections)
            if (msg.player_id && msg.player_id !== myId) {
              console.log('[WS] Ignoring your_turn for different player:', msg.player_name);
              return;
            }
            // Only show turn if this is a NEW request (not one we already submitted for)
            if (msg.request_id && msg.request_id !== humanTurnDismissedRef.current) {
              humanTurnDismissedRef.current = null; // New turn, clear dismissed
              setCurrentRequestId(msg.request_id);
              // Show turn UI immediately from WS data (don't wait for status poll)
              if (msg.available_actions) setAvailableActions(msg.available_actions);
              if (msg.tokens_left != null) setTokensLeft(msg.tokens_left);
              if (msg.context) setTurnContext(msg.context);
              turnDeadlineRequestIdRef.current = msg.request_id;
              setTurnDeadline(msg.timeout_ms ? Date.now() + msg.timeout_ms : null);
              setIsHumanTurn(true);
              emitGameEvent(GAME_EVENTS.YOUR_TURN, {
                playerName: msg.player_name,
                requestId: msg.request_id,
                timeoutMs: msg.timeout_ms
              });
              // 📱 08-01 P0 fix (audit round 2 #2): the old "auto-scroll to the
              // input form" is GONE on purpose. On mobile the dock is
              // position:fixed and always on screen — scrollIntoView on a
              // fixed element scrolls the DOCUMENT instead, yanking the page
              // to the top exactly when the player's turn starts.
            }
          }

          // --- voting_started: server is waiting for votes ---
          if (msg.type === 'voting_started') {
            soundManager.play('vote');
            console.log('[WS] voting_started received');
            const myIdForVote = humanPlayerIdRef.current;
            if (msg.request_ids && myIdForVote && msg.request_ids[myIdForVote]) {
              setCurrentRequestId(msg.request_ids[myIdForVote]);
            }
            // Theme JS effects for voting disabled
            // emitGameEvent(GAME_EVENTS.VOTING_STARTED, {
            //   roundNumber: msg.round_number,
            //   duration: msg.voting_duration
            // });
            // Refetch to get voting_data for VotingModal
            fetchChatGameStatusRef.current();
          }

          // --- vote_update: live vote count update ---
          if (msg.type === 'vote_update') {
            if (msg.new_vote) {
              // Theme JS effects for voting disabled
              // emitGameEvent(GAME_EVENTS.VOTE_CAST, {
              //   voterName: msg.new_vote.voter_name,
              //   targetName: msg.new_vote.target_name,
              //   votesIn: msg.votes_in,
              //   totalVoters: msg.total_voters
              // });
            }
            // Update voting_data in chatGameStatus so VotingModal sees live counts
            setChatGameStatus(prev => {
              if (!prev?.voting_data) return prev;
              const updatedVotesCast = { ...prev.voting_data.votes_cast };
              // If full votes_cast object is included, merge it
              if (msg.votes_cast) {
                Object.assign(updatedVotesCast, msg.votes_cast);
              }
              // If individual new_vote is included, add it
              if (msg.new_vote) {
                updatedVotesCast[msg.new_vote.voter_id] = {
                  voted_for: msg.new_vote.target_id,
                  voter_name: msg.new_vote.voter_name,
                  target_name: msg.new_vote.target_name,
                  timestamp: new Date().toISOString()
                };
              }
              return {
                ...prev,
                voting_data: {
                  ...prev.voting_data,
                  votes_cast: updatedVotesCast
                }
              };
            });
          }

          // --- turn_expired: server auto-passed the human due to timeout ---
          if (msg.type === 'turn_expired') {
            const myIdExpiry = humanPlayerIdRef.current;
            console.log('[WS] turn_expired received for', msg.player_name);
            emitGameEvent(GAME_EVENTS.TURN_EXPIRED, {
              playerName: msg.player_name,
              isYou: !msg.player_id || (myIdExpiry && msg.player_id === myIdExpiry)
            });
            // Only clear turn UI if this expiry is for our player
            if (!msg.player_id || (myIdExpiry && msg.player_id === myIdExpiry)) {
              setCurrentRequestId(null);
              setIsHumanTurn(false);
              // Clear the deadline too — leaving it set let the expired countdown keep
              // ticking (and leak into the voting phase via the poll's prev-keep path).
              setTurnDeadline(null);
              turnDeadlineRequestIdRef.current = null;
            }
            // No refetch — the next new_action WS will bring the pass action
          }

          // --- players_spectated: players missed ready check and were made spectators ---
          if (msg.type === 'players_spectated') {
            console.log('[WS] players_spectated:', msg.spectator_ids);
            const myIdSpec = humanPlayerIdRef.current;
            if (myIdSpec && msg.spectator_ids?.includes(myIdSpec)) {
              console.log('[WS] Current player was marked as spectator (missed ready check)');
              setHumanPlayerId(null);
              // Clear all active turn state. Without this, isHumanTurn + currentRequestId
              // stay set, which causes the status poll's hasActiveTurn guard to skip cleanup,
              // leaving stale turn UI state even though the player is now a spectator.
              setIsHumanTurn(false);
              setCurrentRequestId(null);
              setTurnDeadline(null);
              humanTurnDismissedRef.current = null;
            }
            fetchChatGameStatusRef.current();
          }

          // --- player_disconnected: a human player's WS dropped ---
          if (msg.type === 'player_disconnected') {
            soundManager.play('playerLeave');
            console.log('[WS] player_disconnected:', msg.player_id, 'at', msg.disconnected_at);
            setUsers(prev => prev.map(u =>
              u.game_player_id === msg.player_id
                ? { ...u, data: { ...u.data, disconnected_at: msg.disconnected_at } }
                : u
            ));
            // After 30s grace period, touch users state so the sidebar re-renders and filters them out
            setTimeout(() => {
              setUsers(prev => [...prev]);
            }, 31000);
          }

          // --- player_reconnected: a human player reconnected ---
          if (msg.type === 'player_reconnected') {
            soundManager.play('playerJoin');
            console.log('[WS] player_reconnected:', msg.player_id);
            setUsers(prev => prev.map(u => {
              if (u.game_player_id !== msg.player_id) return u;
              const { disconnected_at, ...restData } = (u.data || {});
              return { ...u, data: restData };
            }));
          }

          // --- game_limit_reached: too many concurrent games ---
          if (msg.type === 'game_limit_reached') {
            console.log('[WS] game_limit_reached:', msg.message);
            setIsPlaying(false);
            setPlayRoundMode(false);
            setGameStatusMessage(msg.message);
            setTimeout(() => {
              setGameStatusMessage(prev => prev === msg.message ? null : prev);
            }, 8000);
          }

          // --- poke_throttled: the poke budget is empty (08-07) ---
          // A poke advances the round, which drives a whole set of AI turns, so
          // the server meters it (src/ChatGame/pokeBudget.js: 1/min, 3 banked).
          // Mirrors game_limit_reached exactly: clear the pending-play state so
          // the button re-enables, surface the reason, and let it self-clear.
          // ⭐ This is a "not yet", not an error — the space keeps running on its
          // own cadence, so the copy stays warm and never scolds. A poke button
          // that silently does nothing reads as broken software; that is the
          // whole reason this branch exists.
          if (msg.type === 'poke_throttled') {
            console.log('[WS] poke_throttled, retry in', msg.retry_in_s, 's');
            setIsPlaying(false);
            setPlayRoundMode(false);
            setGameStarted(false);
            setGameStatusMessage(msg.message);
            setTimeout(() => {
              setGameStatusMessage(prev => prev === msg.message ? null : prev);
            }, 5000);
          }

          // --- autoplay_paused: backend paused autoplay at round boundary ---
          if (msg.type === 'autoplay_paused') {
            console.log('[WS] autoplay_paused received, reason:', msg.reason);
            setIsPlaying(false);
            setPlayRoundMode(false);
            setGameStarted(false); // Clear "Starting..." state so play button re-enables
            // Refresh game status so the UI reflects the new round state
            // (e.g., clears is_voting after voting round ends)
            fetchChatGameStatusRef.current();
          }

          // --- game_reset: game was reset to starting state (manual or auto) ---
          if (msg.type === 'game_reset') {
            console.log('[WS] game_reset received, reloading page');
            localStorage.removeItem(`humanPlayerId_${gameId}`);
            // Include loading=true so the loading overlay shows while game re-initializes
            window.location.href = `/game-step?gameId=${gameId}&loading=true`;
          }

          // --- ready_check: server wants humans to ready up ---
          if (msg.type === 'ready_check') {
            console.log('[WS] ready_check received');
            const myIdReady = humanPlayerIdRef.current;
            if (msg.request_ids && myIdReady && msg.request_ids[myIdReady] && !readyCheckDismissedRef.current) {
              setCurrentRequestId(msg.request_ids[myIdReady]);
              setShowReadyCheck(true);
              setReadyCheckSubmitted(false);
            }
          }

          // --- ready_update: live ready count update ---
          if (msg.type === 'ready_update') {
            console.log('[WS] ready_update received:', msg.ready_count, '/', msg.total_humans);
            emitGameEvent(GAME_EVENTS.READY_UP, {
              readyCount: msg.ready_count,
              totalHumans: msg.total_humans,
              playerNames: msg.ready_player_names
            });
            // Ensure isReadyCheckActive is true — the client may have missed the
            // initial ready_check WS message, so receiving a ready_update is proof
            // we're in a ready-check round.
            if (!readyCheckDismissedRef.current) {
              setShowReadyCheck(true);
            }
            // Merge with existing data to preserve optimistic ready status
            setReadyCheckData(prev => {
              const incoming = {
                ready_count: msg.ready_count,
                total_humans: msg.total_humans,
                ready_player_names: msg.ready_player_names || [],
                countdown_started_at: msg.countdown_started_at || prev?.countdown_started_at || null
              };
              if (!prev || !readyCheckDismissedRef.current) return incoming;
              return {
                ...incoming,
                ready_count: Math.max(incoming.ready_count, prev.ready_count || 0),
                ready_player_names: [...new Set([...incoming.ready_player_names, ...(prev.ready_player_names || [])])]
              };
            });
          }

          // 🍿 Watch party state (start/control/heartbeat/queue/stop rebroadcasts)
          if (msg.type === 'watch_party_state') {
            watchPartyFreshAtRef.current = Date.now();
            setWatchParty(prev => {
              const next = decodeWatchPartyTitles(msg.watch_party || null);
              // Fresh party (not just a heartbeat) → pop the couch-invite toast.
              // The starter auto-enters theater, so the render gate (!inTheater)
              // keeps it from showing to them.
              if (!prev && next) {
                setPartyToast(next);
                setTimeout(() => setPartyToast(null), 8000);
              }
              if (!next) setPartyToast(null);
              return next;
            });
          }

          // 📚 the House library's page (houseBooks.js): every socket in the game gets it,
          // the hook keeps it only when its seq is not older (a stale message never rewinds)
          if (msg.type === 'house_book_state' && msg.book) libraryRef.current?.ingest(msg.book);
          if (msg.type === 'house_book_ask' && msg.ask) libraryRef.current?.ingestAsk(msg);
          if (msg.type === 'house_pumpkin') setPumpkinTick((n) => n + 1); // 🎃 10-07 a new pumpkin or a hide: refetch while the porch is looked at

          // 🗳️ Ambient poll open/vote/close rebroadcasts (spacePolls.js).
          // Broadcast copies carry my_vote:null — keep our own vote when the
          // id matches so the ✓ never flickers off mid-party.
          if (msg.type === 'space_poll_state') {
            setPollOverride(prev => {
              if (!msg.poll) return { closed: true };
              const keepMyVote = (prev && prev.id === msg.poll.id && prev.my_vote) || null;
              return { ...msg.poll, my_vote: msg.poll.my_vote || keepMyVote };
            });
          }

          // 🎙️ Host voice announcement (Arc 9 — hostVoice.js): the host
          // speaks a short event line; playback ducks the ambient bed and
          // skips while a watch party is live / voice is muted.
          if (msg.type === 'host_announcement') {
            playHostAnnouncement(msg);
          }

          // 🐻 09-29 (spec §3.6): someone chipped in to keep the room on (the
          // thank-you toast; never for a bot, queued one at a time)
          if (msg.type === 'chip_in' && !isBotMode) {
            const chipIn = chipInFromSocket(msg);
            if (chipIn && !chipInSeenRef.current.has(chipIn.id)) {
              chipInSeenRef.current.add(chipIn.id);
              setChipInQueue((q) => pushChipIn(q, chipIn));
            }
          }

          // 🍿 Overlay reactions (AI quips + human emoji bullets over the video)
          if (msg.type === 'watch_reaction' && msg.reaction) {
            setLastWatchReaction({ ...msg.reaction, _id: ++watchReactionIdRef.current });
            // 🛋️ the couch keeps every quip (a bullet fades in 10s; the log holds 40)
            if (msg.reaction.kind === 'quip') {
              pushCouch({ id: msg.reaction.action_id || null, sender_name: msg.reaction.sender_name, avatar_url: msg.reaction.avatar_url || null, text: msg.reaction.text, at: msg.reaction.at || new Date().toISOString(), video_id: msg.reaction.video_id || null });
            }
          }

          // --- reaction_update: instant reaction sync without full refetch ---

          // ⌨️ /code sandbox saved — patch the panes onto the card in place
          // (same in-place shape as reaction_update below)
          if (msg.type === 'code_card_update' && msg.action_id && msg.code) {
            setResults(prev => prev.map(r => ({
              ...r,
              actions: (r.actions || []).map(a =>
                a.id === msg.action_id
                  ? { ...a, data: { ...a.data, code: msg.code, ...(msg.assist_at ? { code_assist_at: msg.assist_at } : {}) } }
                  : a
              )
            })));
          }

          // 🛡️ 09-29: three community reports hid a line while you watch
          // (moderationReports broadcasts it). Mask it in the feed and the
          // ghost lines now, the same words the server serves on a reload.
          if (msg.type === 'action_hidden' && msg.action_id) {
            setResults(prev => hideActionInResults(prev, msg.action_id));
            setSpectatingMessages(prev => hideGhostLine(prev, msg.action_id));
          }

          if (msg.type === 'reaction_update' && msg.action_id) {
            if (Date.now() > ownReactionSoundMuteRef.current) soundManager.play('reaction');
            setResults(prev => prev.map(r => ({
              ...r,
              actions: (r.actions || []).map(a =>
                a.id === msg.action_id
                  ? { ...a, data: { ...a.data, reactions: msg.reactions } }
                  : a
              )
            })));
          }

          // --- playback_started: another user started playing ---
          if (msg.type === 'playback_started' && msg.connection_id) {
            playbackControllerRef.current = msg.connection_id;
            if (msg.connection_id !== ourConnectionIdRef.current) {
              // Another client took over — relinquish our controller claim so the
              // reverse-sync on any future reconnect doesn't incorrectly restore local control.
              localStorage.removeItem(`playbackController_${gameId}`);
              setIsPlaying(true);
              setPlayRoundMode(true);
              setRemotePlayback(true);
            }
          }

          // --- playback_stopped: controller stopped or disconnected ---
          if (msg.type === 'playback_stopped') {
            playbackControllerRef.current = null;
            setRemotePlayback(prev => {
              if (prev) {
                setIsPlaying(false);
                setPlayRoundMode(false);
              }
              return false;
            });
          }

          // --- asset_update: real-time theme/profile/image delivery ---
          if (msg.type === 'asset_update') {
            console.log('[WS] asset_update:', msg.asset, msg.status, msg.data);

            // Helper: check if both theme and profiles are done, then show "Ready to play!"
            const checkAllAssetsReady = () => {
              // Theme is ready if we received it via WS, or if it was already loaded (non-placeholder) in chatGameStatus
              const themeReady = wsThemeCompleteRef.current;
              if (themeReady && wsProfilesCompleteRef.current && !wsAssetCompleteRef.current) {
                wsAssetCompleteRef.current = true;
                setAssetLoadingMessage(competitiveModeRef.current ? 'Ready to play!' : 'Ready to go!');
                setTimeout(() => {
                  setAssetLoadingReady(true);
                  if (imposterModeRef.current && !imposterAutoStartedRef.current) {
                    imposterAutoStartedRef.current = true;
                    handleRoundJoinRef.current?.()
                      ?.then(() => setShowAssetLoadingOverlay(false))
                      ?.catch(() => setShowAssetLoadingOverlay(false));
                  }
                }, competitiveModeRef.current ? 1500 : 3000);
              }
            };

            if (msg.asset === 'ui_theme' && msg.status === 'complete' && msg.data?.theme) {
              // Merge the full theme into chatGameStatus so UI picks it up immediately.
              // Keep already-known image URLs when the broadcast theme lacks them — theme
              // broadcasts can be built from a snapshot taken before image generation
              // finished, and dropping the URL wipes the visible background (same guard as
              // the poll-refresh path above).
              setChatGameStatus(prev => {
                if (!prev) return prev;
                const incoming = msg.data.theme;
                const keepBg = prev.ui_theme?.backgroundImage?.url && !incoming.backgroundImage?.url;
                const keepBanner = prev.ui_theme?.bannerImage?.url && !incoming.bannerImage?.url;
                return {
                  ...prev,
                  ui_theme: {
                    ...incoming,
                    ...(keepBg ? { backgroundImage: prev.ui_theme.backgroundImage } : {}),
                    ...(keepBanner ? { bannerImage: prev.ui_theme.bannerImage } : {}),
                  },
                };
              });
              wsThemeCompleteRef.current = true;
              checkAllAssetsReady();
            }

            if (msg.asset === 'ui_theme' && msg.status === 'failed') {
              // Theme failed — don't block readiness, treat as "ready"
              wsThemeCompleteRef.current = true;
              checkAllAssetsReady();
            }

            // 🎙️ Welcome narration finished generating (Arc 6 backend
            // broadcast — this branch was MISSING, so a first visitor who
            // arrived before the TTS finished raced it and lost, 07-21).
            // Patch the URL into state so the Enter click can play it.
            if (msg.asset === 'moderator_welcome_audio' && msg.status === 'complete' && msg.data?.welcome_audio_url) {
              setChatGameStatus(prev => {
                if (!prev || !prev.moderator_character) return prev;
                return {
                  ...prev,
                  moderator_character: {
                    ...prev.moderator_character,
                    welcome_audio_url: msg.data.welcome_audio_url,
                    welcome_line: msg.data.welcome_line || prev.moderator_character.welcome_line,
                  },
                };
              });
            }

            if (msg.asset === 'player_profile' && (msg.status === 'complete' || msg.status === 'generating')) {
              // Update specific player's name and/or image in users state
              // 'generating' = name just generated, image still pending
              // 'complete' = name + image both done
              setUsers(prev => prev.map(u =>
                u.id === msg.data.player_id
                  ? { ...u, name: msg.data.name || u.name, profile_image_url: msg.data.profile_image_url || u.profile_image_url }
                  : u
              ));
            }

            if (msg.asset === 'player_profiles' && msg.data?.all_complete) {
              // All profiles done — refresh game status to pick up any names/images
              // whose individual WS broadcasts were missed (e.g. WS connected after broadcast)
              wsProfilesCompleteRef.current = true;
              fetchChatGameStatusRef.current();
              checkAllAssetsReady();
            }

            if (msg.asset === 'background_image' && msg.status === 'complete' && msg.data?.url) {
              setChatGameStatus(prev => {
                if (!prev?.ui_theme) return prev;
                return {
                  ...prev,
                  ui_theme: {
                    ...prev.ui_theme,
                    // enabled defaults on: without it a first-time patch built
                    // {url}-only and ThemeStyleInjector skipped the background
                    backgroundImage: { enabled: true, ...prev.ui_theme.backgroundImage, url: msg.data.url }
                  }
                };
              });
            }

            if (msg.asset === 'banner_image' && msg.status === 'complete' && msg.data?.url) {
              setChatGameStatus(prev => {
                if (!prev?.ui_theme) return prev;
                return {
                  ...prev,
                  ui_theme: {
                    ...prev.ui_theme,
                    bannerImage: { enabled: true, ...prev.ui_theme.bannerImage, url: msg.data.url }
                  }
                };
              });
            }

            if (msg.asset === 'media_suggestions' && msg.status === 'complete' && msg.data?.media_suggestions) {
              setChatGameStatus(prev => prev ? { ...prev, media_suggestions: msg.data.media_suggestions } : prev);
            }

            if (msg.asset === 'moderator_character' && msg.status === 'complete' && msg.data?.moderator_character) {
              setChatGameStatus(prev => prev ? { ...prev, moderator_character: msg.data.moderator_character } : prev);
            }
          }

          // --- player_joined: new human player added ---
          if (msg.type === 'player_joined' && msg.player) {
            console.log('[WS] player_joined:', msg.player.name, msg.player.id);
            emitGameEvent(GAME_EVENTS.PLAYER_JOINED, {
              playerName: msg.player.name,
              isHuman: msg.player.is_human
            });
            setUsers(prev => {
              // Duplicate check
              if (prev.some(u => u.id === msg.player.id)) return prev;
              return [...prev, {
                id: msg.player.id,
                name: msg.player.name,
                model: 'Human Player',
                is_human: true,
                game_player_id: msg.player.id,
                profile_image_url: null,
                personality_traits: [],
                character_description: null,
                objective: null,
                data: { is_alive: true, is_eliminated: false }
              }];
            });
            // Refetch status so ready-check UI updates with the new player
            fetchChatGameStatusRef.current();
          }

          // --- game_event: elimination and completion ---
          if (msg.type === 'game_event') {
            if (msg.event === 'player_eliminated' && msg.data?.player_id) {
              soundManager.play('eliminated');
              emitGameEvent(GAME_EVENTS.PLAYER_ELIMINATED, {
                playerName: msg.data.player_name,
                playerId: msg.data.player_id,
                isYou: msg.data.player_id === humanPlayerIdRef.current
              });
              setUsers(prev => prev.map(u =>
                u.id === msg.data.player_id
                  ? { ...u, data: { ...u.data, is_alive: false, is_eliminated: true } }
                  : u
              ));
            }

            if (msg.event === 'game_completed') {
              emitGameEvent(GAME_EVENTS.GAME_COMPLETED, {
                finalRankings: msg.data?.final_rankings
              });
              // Trigger a final status fetch to get the complete game state
              fetchChatGameStatusRef.current();
            }
          }
        } catch (err) {
          // Ignore parse errors
        }
      };

      ws.onclose = () => {
        if (heartbeatInterval) clearInterval(heartbeatInterval);
        if (isCancelled) return;
        console.log('[WS] Disconnected, reconnecting in', wsBackoffRef.current / 1000, 's');
        wsReconnectTimeoutRef.current = setTimeout(() => {
          wsBackoffRef.current = Math.min(wsBackoffRef.current * 2, 30000);
          connect();
        }, wsBackoffRef.current);
      };

      ws.onerror = (err) => {
        console.error('[WS] Error:', err);
        ws.close();
      };
    };

    connect();

    return () => {
      isCancelled = true;
      if (wsReconnectTimeoutRef.current) {
        clearTimeout(wsReconnectTimeoutRef.current);
        wsReconnectTimeoutRef.current = null;
      }
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
      // Drop any queued sends — the user navigated away or gameId changed,
      // so stale intents (startPlaying for the prior game, etc.) must not
      // fire against the next WS connection.
      wsPendingSendsRef.current = [];
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- fetchChatGameStatus accessed via ref to avoid WS reconnects on humanPlayerId changes
  }, [gameId, gameType]);

  // Chat game stepping is now handled server-side by the cron.
  // Status updates arrive via WebSocket 'new_action'/'chat_action' messages
  // and trigger fetchChatGameStatus() in the onmessage handler.

  // Typing indicator: show who's currently speaking based on status data
  // Only during active discussion rounds (not between rounds, voting, etc.)
  useEffect(() => {
    if (gameType !== 'chat-game' && gameType !== 'chat') return;
    const speaker = chatGameStatus?.next_speaker;
    const isDiscussion = chatGameStatus?.round_type === 'chat-discussion';
    if (speaker && !speaker.is_human && isDiscussion && isPlaying) {
      const speakerName = speaker.player_name;
      const speakerUser = users?.find(u => u.game_player_id === speaker.player_id || u.name === speakerName);
      const speakerModel = speakerUser ? getDisplayModelName(speakerUser) : null;
      setCurrentlyTyping(speakerModel ? `${speakerName} (${speakerModel})` : speakerName);
    } else {
      setCurrentlyTyping(null);
    }
  }, [chatGameStatus?.next_speaker, chatGameStatus?.round_type, gameType, users, isPlaying]);

  // Detect new round and show announcement
  useEffect(() => {
    if (!chatGameStatus?.competitive_mode) return;
    // Don't show round announcements for imposter mode — it uses its own reveal overlay
    if (chatGameStatus?.imposter_mode) return;

    const currentRound = chatGameStatus.current_competitive_round;

    // Show announcement for new round in competitive mode
    // For the first round (round 1), show announcement at the start so user can see the competitive UI
    // For subsequent rounds, show announcement when a voting round completes and new round starts
    if (currentRound && currentRound !== announcedRound) {
      // Only show if this is a new round we haven't announced
      // For round 1, only show if we haven't announced any round yet (null)
      // For subsequent rounds, show when currentRound > announcedRound
      if (announcedRound === null || currentRound > announcedRound) {
        // Entered via the curtain → the curtain + cold open ARE round 1's
        // interstitial. Without this, round 1 starting (right after ENTER)
        // re-summoned the Round-1 card the curtain just replaced (user, 07-08:
        // "still not working"). Mark it announced so round-2+ cards still fire.
        if (currentRound === 1 && entryCurtainSeenRef.current) {
          setAnnouncedRound(1);
          return;
        }
        setShowRoundAnnouncement(true);
        setAnnouncedRound(currentRound);
        emitGameEvent(GAME_EVENTS.ROUND_START, {
          roundNumber: currentRound,
          roundType: chatGameStatus?.round_type,
          competitiveRound: currentRound
        });
      }
    }
  }, [chatGameStatus?.current_competitive_round, announcedRound, chatGameStatus?.competitive_mode]);

  const handleRoundJoin = async () => {
    let activePlayerId = humanPlayerId;

    // Join as human player if not already joined
    if (!activePlayerId) {
      try {
        // Check localStorage first
        const storedId = localStorage.getItem(`humanPlayerId_${gameId}`);
        if (storedId) {
          console.log('[GameStepPage] Restoring human player from localStorage:', storedId);
          setHumanPlayerId(storedId);
          activePlayerId = storedId;
          const url = new URL(window.location);
          url.searchParams.set('playerId', storedId);
          window.history.replaceState({}, '', url);
        } else {
          console.log('[GameStepPage] Joining round as human player');
          const result = await chatGameApi.addHumanPlayer(gameId);

          if (result.success && result.player_id) {
            console.log('[GameStepPage] Added human player:', result.player_name, result.player_id);
            setHumanPlayerId(result.player_id);
            activePlayerId = result.player_id;
            localStorage.setItem(`humanPlayerId_${gameId}`, result.player_id);
            accountClaimSeat(result.player_id, gameId); // 🔐 accounts: no-op unless signed in

            // Update URL with player ID
            const url = new URL(window.location);
            url.searchParams.set('playerId', result.player_id);
            window.history.replaceState({}, '', url);
          }
        }
      } catch (error) {
        console.error('[GameStepPage] Failed to join round:', error);
      }
    }

    // Fetch fresh status with player ID and show Glitch reveal if imposter mode
    if (activePlayerId) {
      try {
        const freshStatus = await chatGameApi.getGameStatus(gameId, activePlayerId);
        setChatGameStatus({ ...freshStatus, _stateTimestamp: freshStatus.timestamp || Date.now() });
        if (freshStatus?.imposter_mode && !glitchRevealShownRef.current) {
          glitchRevealShownRef.current = true;
          setShowGlitchReveal({ isImposter: !!freshStatus.is_imposter, topic: freshStatus.imposter_topic, doodle: !!freshStatus.doodle_bug });
        }
      } catch (e) {
        console.error('[GameStepPage] Failed to fetch status after join:', e);
      }
    }

    // Hide announcement
    setShowRoundAnnouncement(false);
  };

  // Keep refs current so polling/WS callbacks always call the latest version
  handleRoundJoinRef.current = handleRoundJoin;
  imposterModeRef.current = !!chatGameStatus?.imposter_mode;
  competitiveModeRef.current = !!(chatGameStatus?.competitive_mode || chatGameStatus?.imposter_mode);
  spectatingMessagesRef.current = spectatingMessages;

  // Cache competitive/imposter mode in localStorage so earlyCompetitiveMode is correct on next page load
  useEffect(() => {
    if (!gameId || !chatGameStatus) return;
    if (chatGameStatus.competitive_mode || chatGameStatus.imposter_mode) {
      try { localStorage.setItem(`game_mode_${gameId}`, JSON.stringify({ competitive: true })); } catch(e) {}
      setEarlyCompetitiveMode(true);
    } else {
      // Clear stale cache if game is actually non-competitive (e.g. game was reset)
      try { localStorage.removeItem(`game_mode_${gameId}`); } catch(e) {}
      setEarlyCompetitiveMode(false);
    }
  }, [chatGameStatus?.competitive_mode, chatGameStatus?.imposter_mode, gameId]); // eslint-disable-line

  // "Play Round" mode: auto-play until the round changes, then pause
  const [playRoundMode, setPlayRoundMode] = useState(false);
  const [spectatorStarting, setSpectatorStarting] = useState(false); // true while waiting for backend after "Play as spectator"
  const [spectatorElapsed, setSpectatorElapsed] = useState(0); // seconds elapsed since spectator clicked play

  useEffect(() => {
    if (!spectatorStarting) { setSpectatorElapsed(0); return; }
    const t = setInterval(() => setSpectatorElapsed(s => s + 1), 1000);
    // Auto-cancel after 30s to prevent indefinite "Starting game..." display
    const timeout = setTimeout(() => setSpectatorStarting(false), 30000);
    return () => { clearInterval(t); clearTimeout(timeout); };
  }, [spectatorStarting]);

  // Clear once game is actually underway — delay so the timer is visible long enough
  // to give feedback (isPlaying goes true immediately from handlePlayRound, but the
  // game isn't truly active until the backend starts processing)
  useEffect(() => {
    if (isPlaying && spectatorStarting && spectatorElapsed >= 2) setSpectatorStarting(false);
  }, [isPlaying, spectatorStarting, spectatorElapsed]);
  const playRoundStartRoundRef = useRef(null);

  // Send a message over the WS if OPEN, otherwise queue it for the next onopen.
  // Last-write-wins dedupe by (action, gameInstanceId): a fresh stopPlaying
  // followed by a startPlaying during a reconnect resolves to startPlaying.
  // Server handlers (ws_startPlaying / ws_stopPlaying) are idempotent.
  const wsSend = (msg) => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      // Cycle 5g.16 Fix C: client-side proof of ws.send call for the
      // silent-drop diagnosis surfaced in 5g.15 Glitch failure (per
      // [[explorer-smoke-2026-06-02-cycle5g15]]). Pairs with backend
      // CloudWatch invocation counts — if client log count > backend
      // invocation count for a given action, silent ws.send drop is
      // confirmed (readyState reports OPEN but packet doesn't transmit).
      // readyState in log catches the "readyState lied" case too.
      console.log(
        '[5g16-wsSend] action=' + msg.action +
        ' gameId=' + msg.gameInstanceId +
        ' readyState=' + wsRef.current.readyState +
        ' ts=' + Date.now()
      );
      wsRef.current.send(JSON.stringify(msg));
      return;
    }
    const queue = wsPendingSendsRef.current;
    const dupeIdx = queue.findIndex(
      m => m.action === msg.action && m.gameInstanceId === msg.gameInstanceId
    );
    if (dupeIdx !== -1) queue.splice(dupeIdx, 1);
    queue.push(msg);
    console.log('[WS] Queued ' + msg.action + ' for ' + msg.gameInstanceId + ' — WS not OPEN');
  };

  const handlePlay = () => {
    setPlayRoundMode(false);
    setRemotePlayback(false);
    setIsPlaying(true);
    wsSend({ action: 'startPlaying', gameInstanceId: gameId });
  };

  const handlePlayRound = () => {
    // Record current round so we can detect when it changes
    const isChatGame = gameType === 'chat-game' || gameType === 'chat';
    let currentRound;
    if (isChatGame && chatGameStatus?.imposter_mode) {
      currentRound = (chatGameStatus?.imposter_word_round || 0);
    } else if (isChatGame) {
      currentRound = chatGameStatus?.current_competitive_round || chatGameStatus?.round_number || 1;
    } else {
      currentRound = results[results.length - 1]?.round_number || 1;
    }
    setPlayRoundMode(true);
    setRemotePlayback(false);
    playRoundStartRoundRef.current = currentRound;
    console.log('[PlayRound] Starting play-round mode, current round:', currentRound);
    gameStartedRef.current = true;
    setGameStarted(true);
    isPlayingRef.current = true; // Immediately update ref so in-flight polls see the new state
    setIsPlaying(true);
    // Persist controller claim so page refresh can tell "I started this playback"
    // and the reverse-sync won't incorrectly set remotePlayback: true on reconnect.
    if (humanPlayerId) {
      localStorage.setItem(`playbackController_${gameId}`, humanPlayerId);
    }
    // playerId rides along for the ❤️ hearts poke signal (server verifies)
    wsSend({ action: 'startPlaying', gameInstanceId: gameId, playerId: humanPlayerId || undefined });
  };
  handlePlayRoundRef.current = handlePlayRound;

  // Quick join for competitive mode — skip ready check and start immediately
  const handleQuickJoin = (mode) => {
    if (gameStarted || hasJoinedGame) return;
    trackEvents.playerJoined({ spaceId: gameId, mode, gameType });
    // Noir games: the FILE-PULL beat (swinging lamp + escalating lines) bridges the
    // seconds between Investigate and the role card (user, 07-10 — replaced the second
    // door; the creak belongs to the storm-door only). lampBuzz = switch click +
    // fluorescent stutter, gain-stepped to the same rhythm as the visual flicker.
    // [bot-theater 07-12] bots included — the beat self-clears at 9s, plays
    // before any turn window, and the lampBuzz lands on the livestream audio.
    if (mode === 'play' && chatGameStatus?.noir_theme) {
      soundManager.play('lampBuzz');
      // ⛈️ Storm from the Investigate click itself (Nina 07-30: EVERY pack's
      // interstitial is a storm night — Clint's bakery played a near-silent
      // café murmur under rain visuals). This click is a gesture, so the bed
      // is autoplay-legal. The cold-open-close handoff swaps in the pack's
      // own bed (or silence for dry packs).
      if (!isBotMode) soundManager.startStorm();
      setDoorTransition(true);
      setTimeout(() => setDoorTransition(false), 9000);
    }
    // Immediately hide the buttons — permanently
    setHasJoinedGame(true);
    setGameStarted(true);
    gameStartedRef.current = true;
    const pid = createdPlayerIdRef.current;
    let spectateId = null;
    if (mode === 'play') {
      userSwitchedToPlayRef.current = true;
      if (!pid) return;
      setHumanPlayerId(pid);
      const url = new URL(window.location);
      url.searchParams.set('playerId', pid);
      window.history.replaceState({}, '', url);
      chatGameApi.unspectatePlayer(gameId, pid).catch(err =>
        console.error('[handleQuickJoin] unspectate failed:', err.message)
      );
    } else if (mode === 'spectate' && pid) {
      setHumanPlayerId(null);
      const url = new URL(window.location);
      url.searchParams.delete('playerId');
      window.history.replaceState({}, '', url);
      spectateId = pid;
    }
    // [join-spectator-fix] A human choosing to PLAY must be in ready_player_ids BEFORE the
    // ready check resolves — otherwise the ready-check poll demotes them ("missed ready
    // check", players_spectated). readyUp atomically adds them + clears is_spectator +
    // responds to their interaction request, then skipReadyCheck finalizes. The .finally
    // guarantees skipReadyCheck still always runs, AFTER readyUp commits (no race).
    // Gated to non-bot play: the explorer keeps its existing skipReadyCheck-only path,
    // which is protected by the is_external_agent always-ready escape-hatches.
    if (mode === 'play' && !isBotMode && pid) {
      chatGameApi.readyUp(gameId, pid)
        .catch(err => console.error('[handleQuickJoin] readyUp failed:', err.message))
        .finally(() => {
          // 🔒 09-29 batch 2 (item 5): name our own seat (its stored secret rides along): the server only accepts a
          // no-seat finalize in the explorer's own show room now
          chatGameApi.skipReadyCheck(gameId, spectateId, pid).catch(err =>
            console.error('[handleQuickJoin] skipReadyCheck failed:', err.message)
          );
        });
    } else {
      // Spectate mode, bot mode, or no player id. [requester-ready-fix] In bot-mode
      // PLAY the seat may lack is_external_agent (restored pre-fix seats), so the
      // skipReadyCheck-only path demoted it to spectator ("missed ready check",
      // 07-12 session bbec65fe) — pass the pid so the backend readies the requester.
      chatGameApi.skipReadyCheck(gameId, spectateId, mode === 'play' ? pid : null).catch(err =>
        console.error('[handleQuickJoin] skipReadyCheck failed:', err.message)
      );
    }
    handlePlayRound();
  };

  const handlePause = () => {
    setIsPlaying(false);
    isPlayingRef.current = false; // Immediately update ref so in-flight polls stop
    setPlayRoundMode(false);
    setRemotePlayback(false);
    playRoundStartRoundRef.current = null;
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    wsSend({ action: 'stopPlaying', gameInstanceId: gameId });
  };

  // Play Round: backend auto-pauses (sets is_playing=false) at round boundaries.
  // Frontend detects this via status poll and updates UI accordingly.

  // Track if we've already auto-played to prevent re-triggering
  const hasAutoPlayedRef = useRef(false);

  // Auto-play for chat games with < 10 actions on initial load
  // Auto-pause when reaching 10 actions
  useEffect(() => {
    const isChatGame = gameType === 'chat-game' || gameType === 'chat';
    // For chat games, count from recent_messages in status or from results
    const actionCount = chatGameStatus?.recent_messages?.length || results[0]?.actions?.length || 0;

    // Auto-play DISABLED - user must manually start the game
    // if (isChatGame && chatGameStatus && !hasAutoPlayedRef.current && actionCount < 10 && !isPlaying && !isLoading) {
    //   console.log('[Auto-play] Chat game with', actionCount, 'actions - starting auto-play');
    //   hasAutoPlayedRef.current = true;
    //   setIsPlaying(true);
    // }

    // Auto-pause when reaching 1 action (so user starts with just 1 message)
    // Skip auto-pause when Play Round is active
    // Only auto-pause once (on initial load) — don't keep pausing on every state change
    if (isChatGame && isPlaying && !playRoundMode && actionCount >= 1 && !hasAutoPlayedRef.current) {
      console.log('[Auto-pause] Chat game reached', actionCount, 'actions - pausing (initial load)');
      hasAutoPlayedRef.current = true;
      setIsPlaying(false);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    }
  }, [gameType, chatGameStatus, results, isPlaying, isLoading, playRoundMode]);

  const handleLoadAll = () => {
    setResults([]); // Clear existing results
    fetchSupplementalGameStep(true);
  };

  const handleRefreshSummaries = async () => {
    if (!gameId) return;

    setIsRefreshingSummaries(true);
    try {
      const url = `${API_BASE_URL}/api/takeGameStep?id=${gameId}&loadAll=true`;
      const response = await fetch(url);
      const data = await parseApiResponse(response);

      if (data.users) {
        setUsers(data.users);
      }
      if (data.gameType) {
        setGameType(data.gameType);
      }
      if (data.actions) {
        setResults([data]);
        processedActionIdsRef.current = new Set(data.actions.map(a => a.id).filter(Boolean));
      }
    } catch (error) {
      console.error('Error refreshing summaries:', error);
      alert('Failed to refresh summaries. Please try again.');
    } finally {
      setIsRefreshingSummaries(false);
    }
  };

  const handleSliceOfLife = () => {
    fetchSupplementalGameStep(false, true);
  };

  const handleAnalysis = () => {
    fetchSupplementalGameStep(false, false, true);
  };

  const handleFindScenes = () => {
    console.log('[Find Scenes] Starting scene analysis...');
    fetchSupplementalGameStep(false, false, false, null, null, null, false, true);
  };

  const handleAskQuestion = () => {
    setShowQuestionModal(true);
  };

  const handleGenerateCustomVideo = () => {
    setShowCustomVideoModal(true);
  };

  const handleGenerateWeekSummary = async () => {
    if (!results || results.length === 0) {
      alert('No game data available');
      return;
    }

    // Get current round ID
    const currentRound = results[results.length - 1];
    const roundId = currentRound.actions?.[0]?.game_instance_round_id;

    if (!roundId) {
      alert('Could not determine current round');
      return;
    }

    const gameInstanceId = currentRound.actions?.[0]?.game_instance_id;

    if (!gameInstanceId) {
      alert('Could not determine game instance');
      return;
    }

    if (!window.confirm('Generate a week summary for this eviction round? This will use AI to analyze the week.')) {
      return;
    }

    try {
      console.log('[Week Summary] Generating summary for round:', roundId);
      const response = await fetch(`${API_BASE_URL}/api/takeGameStep?generateWeekSummary=true&roundId=${roundId}&gameInstanceId=${gameInstanceId}`);

      if (!response.ok) {
        throw new Error(`Failed to generate summary: ${response.statusText}`);
      }

      const data = await parseApiResponse(response);
      console.log('[Week Summary] Generated:', data);

      // Reload the page to show the new summary
      window.location.reload();
    } catch (error) {
      console.error('[Week Summary] Error:', error);
      alert(`Error generating summary: ${error.message}`);
    }
  };

  const handleBookmark = () => {
    fetchWithParams({ bookmark: true });
  };

  const handleLoadBookmark = () => {
    setResults([]); // Clear existing results
    fetchWithParams({ reload: true });
  };

  const handleSubmitQuestion = () => {
    if (questionType === 'general') {
      fetchSupplementalGameStep(false, false, false, question);
    } else if (questionType === 'player') {
      if (!selectedPlayer) {
        alert('Please select a player');
        return;
      }
      fetchSupplementalGameStep(false, false, false, null, question, selectedPlayer);
    } else if (questionType === 'all') {
      fetchSupplementalGameStep(false, false, false, null, question, null, true);
    }
    setShowQuestionModal(false);
    setQuestion('');
    setSelectedPlayer('');
    setQuestionType('general');
  };

  const handleRollback = () => {
    fetchGameStep(false, false, false, true);
  };

  // 🪪 Character card opened from the chat stream (CastCardModal, Arc 3)
  const [chatCastPid, setChatCastPid] = useState(null);

  // Handle screenshot of chat area
  const [screenshotting, setScreenshotting] = useState(false);
  const [screenshotFeedback, setScreenshotFeedback] = useState(null);
  const [screenshotModal, setScreenshotModal] = useState(null); // current screenshot data URL
  const [screenshotGallery, setScreenshotGallery] = useState([]); // session gallery [{url, timestamp}]
  const [screenshotCopied, setScreenshotCopied] = useState(false);

  const handleScreenshot = async () => {
    const target = chatAreaRef.current || resultsContainerRef.current;
    if (!target || screenshotting) return;
    setScreenshotting(true);
    try {
      const canvas = await html2canvas(target, {
        backgroundColor: THEME.bgDark || '#1a1a2e',
        scale: 2,
        useCORS: true,
        logging: false,
      });
      const dataUrl = canvas.toDataURL('image/png');
      // Add to gallery and open modal
      const entry = { url: dataUrl, timestamp: Date.now() };
      setScreenshotGallery(prev => [entry, ...prev]);
      setScreenshotModal(dataUrl);
      setScreenshotting(false);
      setScreenshotFeedback('done');
      setTimeout(() => setScreenshotFeedback(null), 1500);
    } catch (err) {
      // Most common cause: a cross-origin image (portrait/GIF) taints the
      // canvas and toDataURL throws SecurityError. Never fail silently.
      console.error('Screenshot failed:', err);
      setScreenshotting(false);
      setScreenshotFeedback('error');
      setTimeout(() => setScreenshotFeedback(null), 3000);
    }
  };

  const handleScreenshotCopy = async (dataUrl) => {
    try {
      const res = await fetch(dataUrl);
      const blob = await res.blob();
      await navigator.clipboard.write([new ClipboardItem({ 'image/png': blob })]);
      setScreenshotCopied(true);
      setTimeout(() => setScreenshotCopied(false), 2000);
    } catch {
      // Fallback: download
      handleScreenshotDownload(dataUrl);
    }
  };

  const handleScreenshotDownload = (dataUrl) => {
    const a = document.createElement('a');
    a.href = dataUrl;
    a.download = `space-${chatGameStatus?.name || 'screenshot'}-${Date.now()}.png`;
    a.click();
  };

  // Handle space voting (like/dislike)
  const handleSpaceVote = async (isLike) => {
    if (!gameInstanceId) return;

    const voteType = isLike ? 'like' : 'dislike';

    // If user is clicking the same vote again, do nothing
    if (userSpaceVote === voteType) {
      return;
    }

    // Trigger animation
    setVoteAnimation(prev => ({ ...prev, [voteType]: true }));
    setTimeout(() => setVoteAnimation(prev => ({ ...prev, [voteType]: false })), 600);

    // Update counts based on previous vote
    if (userSpaceVote) {
      // User is changing their vote - decrement old vote
      if (userSpaceVote === 'like') {
        setSpaceLikes(prev => Math.max(0, prev - 1));
      } else {
        setSpaceDislikes(prev => Math.max(0, prev - 1));
      }
    }

    // Increment new vote
    if (isLike) {
      setSpaceLikes(prev => prev + 1);
    } else {
      setSpaceDislikes(prev => prev + 1);
    }

    // Store vote in localStorage and state
    setUserSpaceVote(voteType);
    storeVote(gameInstanceId, voteType);

    try {
      await fetch(`${API_BASE_URL}/api?rateSpace=true`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          spaceId: gameInstanceId,
          rating: voteType
        })
      });
    } catch (err) {
      console.error('Failed to rate space:', err);
      // Note: We don't revert since the vote is stored locally anyway
    }
  };

  const handleSaveSpaces = async (newSpaces, generateImages = false) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/takeGameStep?updateGameSpaces=true&gameId=${gameId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          spaces: newSpaces,
          generateImages
        })
      });

      const result = await parseApiResponse(response);

      if (result.success) {
        // If images were generated, reload spaces to get the updated image URLs
        if (generateImages && result.spaces) {
          setGameSpaces(result.spaces);
        } else {
          setGameSpaces(newSpaces);
        }
        // Don't close the editor - keep it open for continued editing
        // setShowSpacesEditor(false);
        console.log(generateImages ?
          'Game spaces updated and images generated successfully!' :
          'Game spaces updated successfully!');
      } else {
        alert(`Failed to update spaces: ${result.error}`);
      }
    } catch (error) {
      console.error('Error saving spaces:', error);
      alert(`Error: ${error.message}`);
    }
  };

  const handleLoadSpaces = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/takeGameStep?getGameSpaces=true&gameId=${gameId}`);
      const result = await parseApiResponse(response);

      if (result.success) {
        setGameSpaces(result.spaces || { public: [], private: [], secret: [] });
        setShowSpacesEditor(true);
      } else {
        alert(`Failed to load spaces: ${result.error}`);
      }
    } catch (error) {
      console.error('Error loading spaces:', error);
      alert(`Error: ${error.message}`);
    }
  };

  // Load emotion songs from game data
  const handleLoadEmotionSongs = async () => {
    try {
      if (!gameInstanceId) {
        alert('Game instance ID not available yet');
        return;
      }

      const response = await fetch(`${API_BASE_URL}/api/takeGameStep?getGameInstance=true&gameInstanceId=${gameInstanceId}`);
      const result = await parseApiResponse(response);

      if (result) {
        const gameData = typeof result.data === 'string' ? JSON.parse(result.data) : result.data;
        if (gameData?.emotion_songs) {
          setEmotionSongs(prev => ({
            ...prev,
            ...gameData.emotion_songs
          }));
        }
        setShowEmotionSongsEditor(true);
      } else {
        alert('Failed to load game data');
      }
    } catch (error) {
      console.error('Error loading emotion songs:', error);
      alert(`Error: ${error.message}`);
    }
  };

  // Save emotion songs to game instance data
  const handleSaveEmotionSongs = async () => {
    if (!gameInstanceId) {
      alert('Game instance ID not available');
      return;
    }
    setSavingEmotionSongs(true);

    try {
      // Get current game data
      const gameResponse = await fetch(`${API_BASE_URL}/api/takeGameStep?getGameInstance=true&gameInstanceId=${gameInstanceId}`);
      const gameResult = await gameResponse.json();
      const currentGameData = typeof gameResult?.data === 'string' ? JSON.parse(gameResult.data) : (gameResult?.data || {});

      // Update with emotion songs
      const updatedGameData = {
        ...currentGameData,
        emotion_songs: emotionSongs
      };

      // Save to database
      const response = await fetch(
        `${API_BASE_URL}/api/takeGameStep?updateGameData=true&gameInstanceId=${gameInstanceId}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ data: updatedGameData })
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to save emotion songs: ${response.statusText}`);
      }

      console.log('[Emotion Songs] Saved successfully:', emotionSongs);

    } catch (error) {
      console.error('[Emotion Songs] Error saving:', error);
    } finally {
      setSavingEmotionSongs(false);
    }
  };

  // Handle drag-and-drop audio file upload for emotion songs
  const handleEmotionSongDrop = async (category, file) => {
    if (!file) return;

    // Validate it's an audio file
    if (!file.type.startsWith('audio/')) {
      console.warn('[Emotion Songs] Invalid file type:', file.type);
      return;
    }

    setUploadingEmotionSong(category);

    try {
      // Create form data for upload
      const formData = new FormData();
      formData.append('file', file);
      formData.append('directory', 'emotion-music');
      formData.append('filename', `${category}-${Date.now()}-${file.name}`);

      // Upload to backend
      const response = await fetch(`${API_BASE_URL}/api/takeGameStep?uploadMedia=true`, {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        throw new Error(`Upload failed: ${response.statusText}`);
      }

      const result = await parseApiResponse(response);
      const uploadedUrl = result.url || result.mediaUrl;

      if (!uploadedUrl) {
        throw new Error('No URL returned from upload');
      }

      // Update state with new URL and filename
      setEmotionSongs(prev => ({
        ...prev,
        [category]: {
          ...prev[category],
          url: uploadedUrl,
          name: prev[category]?.name || file.name.replace(/\.[^/.]+$/, '')
        }
      }));

      console.log(`[Emotion Songs] Uploaded ${category} song:`, uploadedUrl);
    } catch (error) {
      console.error('[Emotion Songs] Upload error:', error);
    } finally {
      setUploadingEmotionSong(null);
      setDragOverCategory(null);
    }
  };

  // Get AI-generated theme if available (from ChatGame status or from regular game results)
  // Note: Regular game results have theme in gameData.ui_theme, ChatGame status has it in ui_theme directly
  // Memoize to prevent CSS flicker: only update when theme content actually changes,
  // not on every status refetch (which creates a new object reference).
  // IMPORTANT: Never revert from a real theme back to a placeholder/null — once a real
  // theme is applied, keep it even if a status refetch temporarily returns stale data.
  const rawTheme = chatGameStatus?.ui_theme || results[0]?.gameData?.ui_theme || results[0]?.ui_theme || null;
  // (perf 08-13) The string key exists for CONTENT comparison (identity churns
  // every poll; serializing in the dep expression ran on EVERY render). Compute
  // it only when rawTheme's identity changes — same stability, per-poll cost.
  const rawThemeKey = useMemo(() => (rawTheme ? JSON.stringify(rawTheme) : null), [rawTheme]);
  const lastRealThemeRef = useRef(null);
  const effectiveTheme = useMemo(() => {
    const isPlaceholder = !rawTheme || rawTheme._isPlaceholder;
    if (!isPlaceholder) {
      // Real theme — save it and use it
      lastRealThemeRef.current = rawTheme;
      return rawTheme;
    }
    // Placeholder or null — keep the last real theme if we had one
    if (lastRealThemeRef.current) {
      return lastRealThemeRef.current;
    }
    return rawTheme;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rawThemeKey]);
  // 🏡 Their Place (D17): behind the switch, a ported room's generated tile
  // wallpaper steps aside for the room band (the injector clears body paint
  // when there is no backgroundImage; the theme's base color stays).
  const aiGeneratedTheme = useMemo(() => (portedPlaceOn && effectiveTheme
    ? { ...effectiveTheme, backgroundImage: null } : effectiveTheme), [portedPlaceOn, effectiveTheme]);

  // Cache background emojis to localStorage for the Explore page mosaic
  useEffect(() => {
    const emojis = rawTheme?.background_emojis;
    if (!emojis || !gameId) return;
    try {
      const stored = JSON.parse(localStorage.getItem('space_emojis') || '{}');
      stored[gameId] = emojis;
      // Cap at 50 entries
      const keys = Object.keys(stored);
      if (keys.length > 50) {
        for (const k of keys.slice(0, keys.length - 50)) delete stored[k];
      }
      localStorage.setItem('space_emojis', JSON.stringify(stored));
    } catch (e) { /* localStorage full or unavailable */ }
  }, [rawTheme?.background_emojis, gameId]);

  // Background emojis for current space
  const backgroundEmojis = rawTheme?.background_emojis || null;

  // Get the active theme based on game type and merge with AI theme
  const baseTheme = getTheme(gameType);

  // Helper to determine if a color is dark (for contrast calculations)
  const isColorDark = (color) => {
    if (!color) return true; // Default to dark when unknown (safer for text contrast)
    let r, g, b;
    // Try rgb/rgba format
    const rgbMatch = color.match(/rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/);
    if (rgbMatch) {
      r = parseInt(rgbMatch[1]);
      g = parseInt(rgbMatch[2]);
      b = parseInt(rgbMatch[3]);
    } else {
      // Convert hex to RGB
      let hex = color.replace('#', '');
      if (hex.length === 3) hex = hex[0]+hex[0]+hex[1]+hex[1]+hex[2]+hex[2];
      if (hex.length !== 6) return true; // Default to dark when unparseable
      r = parseInt(hex.substr(0, 2), 16);
      g = parseInt(hex.substr(2, 2), 16);
      b = parseInt(hex.substr(4, 2), 16);
    }
    // Calculate luminance
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return luminance < 0.5;
  };

  const THEME = (() => {
    const merged = aiGeneratedTheme?.colors ? {
      ...baseTheme,
      // Override colors with AI-generated theme
      primary: aiGeneratedTheme.colors.primary,
      secondary: aiGeneratedTheme.colors.secondary,
      accent: aiGeneratedTheme.colors.primary, // Use primary as accent
      bgDark: aiGeneratedTheme.colors.background,
      bgMedium: aiGeneratedTheme.colors.surface,
      bgLight: aiGeneratedTheme.colors.surfaceLight || (() => {
        // Lighten surface color for better contrast between panel levels
        const s = aiGeneratedTheme.colors.surface;
        if (!s) return s;
        let hex = s.replace('#', '');
        if (hex.length === 3) hex = hex[0]+hex[0]+hex[1]+hex[1]+hex[2]+hex[2];
        if (hex.length !== 6) return s;
        const r = Math.min(255, parseInt(hex.substr(0, 2), 16) + 18);
        const g = Math.min(255, parseInt(hex.substr(2, 2), 16) + 18);
        const b = Math.min(255, parseInt(hex.substr(4, 2), 16) + 18);
        return `#${r.toString(16).padStart(2,'0')}${g.toString(16).padStart(2,'0')}${b.toString(16).padStart(2,'0')}`;
      })(),
      bgParchment: aiGeneratedTheme.colors.background,
      good: aiGeneratedTheme.colors.success || aiGeneratedTheme.colors.primary,
      evil: aiGeneratedTheme.colors.error || aiGeneratedTheme.colors.secondary,
      // textLight should be light (for dark backgrounds), textDark should be dark (for light backgrounds)
      // Since primary is often used as a dark background, textLight should contrast with it
      textLight: isColorDark(aiGeneratedTheme.colors.primary) ? '#ffffff' : '#000000',
      textDark: aiGeneratedTheme.colors.textPrimary,
      textMuted: aiGeneratedTheme.colors.textSecondary,
      borderLight: `${aiGeneratedTheme.colors.primary}33`, // Add transparency
      borderDark: `${aiGeneratedTheme.colors.secondary}66`,
      borderGold: aiGeneratedTheme.colors.warning || aiGeneratedTheme.colors.primary,
      // Button text should contrast with accent (primary) background
      buttonText: isColorDark(aiGeneratedTheme.colors.primary) ? '#ffffff' : '#000000'
    } : baseTheme;
    // Derive textBright: high-contrast text for bgLight backgrounds
    merged.textBright = merged.textBright || (isColorDark(merged.bgLight) ? '#F5F5F5' : '#212121');
    // Derive textOnPanel: high-contrast text for bgMedium (panel) backgrounds
    merged.textOnPanel = isColorDark(merged.bgMedium) ? '#ffffff' : '#1a1a1a';
    merged.textOnPanelMuted = isColorDark(merged.bgMedium) ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.45)';
    // Derive accentOnPanel: the accent nudged toward the readable pole when its
    // lightness matches the panel's — dark accents on dark panels (Death Note
    // reds) and light-on-light pastels otherwise disappear (e.g. Add Agent button)
    merged.accentOnPanel = (() => {
      const accent = merged.accent || merged.primary;
      if (!accent) return merged.textOnPanel;
      if (isColorDark(accent) !== isColorDark(merged.bgMedium)) return accent;
      let hex = accent.replace('#', '');
      if (hex.length === 3) hex = hex[0]+hex[0]+hex[1]+hex[1]+hex[2]+hex[2];
      if (hex.length !== 6) return merged.textOnPanel;
      const target = isColorDark(merged.bgMedium) ? 255 : 0;
      const mix = (i) => {
        const c = parseInt(hex.substr(i, 2), 16);
        return Math.round(c + (target - c) * 0.55).toString(16).padStart(2, '0');
      };
      return `#${mix(0)}${mix(2)}${mix(4)}`;
    })();
    // Ink for text sitting ON accent-filled controls (Send, CTAs): light
    // accents get dark ink instead of the white-on-orange mush from Nina's
    // 08-02 contrast pass.
    merged.textOnAccent = isColorDark(merged.accent || merged.primary || '#7C4DFF') ? '#ffffff' : '#1a1a1a';
    // For chat games with AI themes, derive playerColor from the theme's primary
    if (merged.playerColor && aiGeneratedTheme?.colors) {
      merged.playerColor = aiGeneratedTheme.colors.primary;
    }
    // 🌿 10-09 (Nina: the sage bubble): YOUR own messages turn sage in the Claude House only.
    // houseStatus = status.house, which the backend sends only for autonomous_locals spaces;
    // everywhere else THEME carries no ownBubble and your bubble keeps the accent, byte for byte.
    return houseStatus ? { ...merged, ownBubble: HOUSE_OWN_BUBBLE } : merged;
  })();

  // Log when theme changes from placeholder to real, and sync the WS readiness ref
  const prevThemeRef = React.useRef(null);
  React.useEffect(() => {
    const wasPlaceholder = prevThemeRef.current?._isPlaceholder;
    const isPlaceholder = aiGeneratedTheme?._isPlaceholder;

    if (wasPlaceholder && !isPlaceholder && aiGeneratedTheme?.colors) {
      console.log('[Theme Update] 🎨 Theme generated! Switching from placeholder to custom theme');
      console.log('[Theme Update] New colors:', aiGeneratedTheme.colors);
    }

    // Keep wsThemeCompleteRef in sync — theme may arrive via fetch, WS, or polling
    if (aiGeneratedTheme && !aiGeneratedTheme._isPlaceholder) {
      wsThemeCompleteRef.current = true;
      // If profiles already arrived via WS, we can now show "Ready to play!"
      if (wsProfilesCompleteRef.current && !wsAssetCompleteRef.current) {
        wsAssetCompleteRef.current = true;
        setAssetLoadingMessage(competitiveModeRef.current ? 'Ready to play!' : 'Ready to go!');
        setTimeout(() => {
          setAssetLoadingReady(true);
          if (imposterModeRef.current && !imposterAutoStartedRef.current) {
            imposterAutoStartedRef.current = true;
            handleRoundJoinRef.current?.()
              ?.then(() => setShowAssetLoadingOverlay(false))
              ?.catch(() => setShowAssetLoadingOverlay(false));
          }
        }, competitiveModeRef.current ? 1500 : 3000);
      }
    }

    prevThemeRef.current = aiGeneratedTheme;
  }, [aiGeneratedTheme]);

  // Apply theme background color to the document body
  useEffect(() => {
    const prevBg = document.body.style.backgroundColor;
    document.body.style.backgroundColor = THEME.bgDark;
    return () => { document.body.style.backgroundColor = prevBg; };
  }, [THEME.bgDark]);

  // Theme updates are now delivered via WebSocket asset_update messages (no polling needed)

  // Watch for eviction and victory actions to trigger animations
  useEffect(() => {
    if (!results || results.length === 0) return;

    const allActions = results.flatMap(r => r.actions || []);

    // FIRST RUN: pre-populate shown sets with ALL existing actions so old popups
    // don't replay when a player joins mid-game. Return early — skip processing.
    if (!hasInitializedAnimationSets.current) {
      hasInitializedAnimationSets.current = true;
      const voteKeys = new Set();
      const animKeys = new Set();
      allActions.forEach(a => {
        if (a.type === 'game_announcement' && a.data?.vote_results && a.data?.eliminated_player) {
          voteKeys.add(`vote-results-${a.id}`);
          animKeys.add(`elimination-${a.id}`);
        }
        if (a.type === 'game_announcement' && a.data?.eliminated_player) {
          animKeys.add(`elimination-${a.id}`);
        }
        if (a.type === 'game_announcement' && a.data?.final_rankings) {
          animKeys.add(`victory-${a.id}`);
        }
        if (a.type === 'player_result' && a.data?.completed) {
          animKeys.add(`objective-victory-${a.id}`);
        }
      });
      if (voteKeys.size > 0) setShownVoteResults(prev => new Set([...prev, ...voteKeys]));
      if (animKeys.size > 0) setShownAnimations(prev => new Set([...prev, ...animKeys]));
      return; // Skip animation processing on first load
    }

    // Check for vote results announcements (show dramatic reveal BEFORE elimination animation)
    const voteResultActions = allActions.filter(a =>
      a.type === 'game_announcement' && a.data?.vote_results && a.data?.eliminated_player
    );
    for (const voteResult of voteResultActions) {
      const voteKey = `vote-results-${voteResult.id}`;
      if (!shownVoteResults.has(voteKey)) {
        const eliminatedPlayer = users.find(u => u.id === voteResult.data.eliminated_player);
        // Ensure all eligible players appear in vote results (even those with 0 votes)
        const rawVoteResults = voteResult.data.vote_results || {};
        const allPlayerVotes = {};
        // Add players who received votes
        Object.entries(rawVoteResults).forEach(([pid, count]) => {
          allPlayerVotes[pid] = count;
        });
        // Add eligible players with 0 votes
        const eliminatedBefore = chatGameStatus?.eliminated_players || [];
        users.forEach(u => {
          const pid = u.game_player_id || u.id;
          if (pid && !allPlayerVotes.hasOwnProperty(pid) && !eliminatedBefore.includes(pid)) {
            allPlayerVotes[pid] = 0;
          }
        });
        // Theme JS effects for voting disabled
        // emitGameEvent(GAME_EVENTS.VOTE_RESULTS, {
        //   voteCounts: allPlayerVotes,
        //   eliminatedPlayerName: eliminatedPlayer?.name || 'Unknown',
        //   roundNumber: voteResult.data.round_number
        // });

        setShowVoteResultsReveal({
          voteResults: allPlayerVotes,
          eliminatedPlayerId: voteResult.data.eliminated_player || null,
          eliminatedPlayerName: eliminatedPlayer?.name || null,
          isSafeRound: !!voteResult.data.is_safe_round,
          roundNumber: voteResult.data.round_number,
          actionId: voteResult.id,
          objective: chatGameStatus?.objective,
          scene: chatGameStatus?.scene
        });
        setShownVoteResults(prev => new Set([...prev, voteKey]));
        // CRITICAL: return immediately — do NOT process elimination section in the same
        // invocation, because showVoteResultsReveal closure var is still stale (null).
        // The eviction animation will be triggered later via the auto-dismiss timer or
        // the Continue button on the vote results overlay.
        return;
      }
    }

    // Check for elimination announcements (game_announcement with eliminated_player)
    // Skip if vote results reveal is active or an animation is already playing.
    // Also skip actions that have vote_results — those are handled by the reveal flow above.
    if (!showVoteResultsReveal && !winnerAnimation) {
    const eliminationActions = allActions.filter(a =>
      a.type === 'game_announcement' && a.data?.eliminated_player && !a.data?.vote_results
    );
    for (const elimination of eliminationActions) {
      const animKey = `elimination-${elimination.id}`;
      if (!shownAnimations.has(animKey)) {
        const eliminatedPlayer = users.find(u => u.id === elimination.data.eliminated_player);
        if (eliminatedPlayer) {
          // Only show eviction animation if the human player was eliminated
          const isHumanEliminated = humanPlayerId && (elimination.data.eliminated_player === humanPlayerId);
          // DISABLED for competitive mode
          if (isHumanEliminated && !chatGameStatus?.competitive_mode) {
            setWinnerAnimation({
              type: 'eviction',
              player: eliminatedPlayer,
              message: `You got eliminated!`
            });
          }
          setShownAnimations(prev => new Set([...prev, animKey]));
          break; // Show one at a time
        }
      }
    }
    }

    // Check for final game completion and winner
    const gameCompleteActions = allActions.filter(a =>
      a.type === 'game_announcement' && a.data?.final_rankings
    );
    if (gameCompleteActions.length > 0) {
      const finalAction = gameCompleteActions[gameCompleteActions.length - 1];
      const animKey = `victory-${finalAction.id}`;
      if (!shownAnimations.has(animKey) && finalAction.data.final_rankings?.length > 0) {
        const winner = finalAction.data.final_rankings[0]; // First place
        const winnerPlayer = users.find(u => u.id === winner.id);
        // Skip for imposter mode (it uses its own glitch caught/escaped animation)
        if (winnerPlayer && !chatGameStatus?.imposter_mode) {
          // 🔪 Killer escape POV (KILLER_MODE §1 end beat, 07-19): if YOU are the
          // killer and the precinct charged someone else, the finale is YOUR
          // getaway — same Tier-1 overlay, your story.
          const youEscaped = !!(chatGameStatus?.you_did_it
            && chatGameStatus?.accusation_result
            && !chatGameStatus.accusation_result.killer_charged);
          const selfPlayer = youEscaped
            ? users.find(u => u.game_player_id === humanPlayerId || u.id === humanPlayerId)
            : null;
          setWinnerAnimation({
            type: 'victory',
            player: selfPlayer || winnerPlayer,
            message: youEscaped ? 'YOU GOT AWAY WITH IT.' : `Champion with ${winner.points} points!`
          });
          setShownAnimations(prev => new Set([...prev, animKey]));
        }
      }
    }

    // Check for imposter (Glitch) mode results
    const imposterResultActions = allActions.filter(a =>
      a.type === 'game_announcement' && a.data?.imposter_result
    );
    if (imposterResultActions.length > 0) {
      const imposterAction = imposterResultActions[imposterResultActions.length - 1];
      const animKey = `glitch-${imposterAction.id}`;
      if (!shownAnimations.has(animKey)) {
        // Dismiss the voting modal so the result animation can show
        setShowVotingModal(false);

        // Auto-scroll to the vote result announcement in chat (especially for mobile)
        setTimeout(() => {
          shouldAutoScrollRef.current = true;
          requestAnimationFrame(() => scrollToBottom());
        }, 300);

        // Only show the big WinnerAnimation when the game is over (caught or final round escape)
        // Mid-round escapes just dismiss the voting modal and show the announcement in chat
        if (imposterAction.data.imposter_result === 'caught' || chatGameStatus?.game_completed) {
          emitGameEvent(GAME_EVENTS.GLITCH_RESULT, {
            result: imposterAction.data.imposter_result,
            glitchPlayerName: imposterAction.data.imposter_player_name,
            topic: imposterAction.data.imposter_topic
          });
          const glitchPlayer = users.find(u => u.id === imposterAction.data.imposter_player_id) ||
            { name: imposterAction.data.imposter_player_name || 'Unknown' };
          setWinnerAnimation({
            type: imposterAction.data.imposter_result === 'caught' ? 'glitch_caught' : 'glitch_escaped',
            player: glitchPlayer,
            message: imposterAction.data.message,
            imposterTopic: imposterAction.data.imposter_topic,
            imposterPlayerName: imposterAction.data.imposter_player_name,
            accusedPlayerName: imposterAction.data.accused_player_name,
            accusedPlayerId: imposterAction.data.accused_player_id,
          });
        }
        setShownAnimations(prev => new Set([...prev, animKey]));
      }
    }

    // Also check for player_result actions (ConclusionRound victories)
    const resultActions = allActions.filter(a => a.type === 'player_result');
    if (resultActions.length > 0) {
      const winnerResult = resultActions.find(a => a.data?.completed === true);
      if (winnerResult) {
        const animKey = `objective-victory-${winnerResult.id}`;
        if (!shownAnimations.has(animKey)) {
          const winner = users.find(u => u.name === winnerResult.data.player_name);
          // Skip for imposter mode (it uses its own glitch caught/escaped animation)
          if (winner && !chatGameStatus?.imposter_mode) {
            setWinnerAnimation({
              type: 'victory',
              player: winner,
              message: winnerResult.data.objective || `${winner.name} has achieved victory!`
            });
            setShownAnimations(prev => new Set([...prev, animKey]));
          }
        }
      }
    }
  }, [results, users, shownAnimations, winnerAnimation]);

  // Three-column layout: non-competitive chat games get Left Sidebar | Center | Right Sidebar
  // Collapses to two-column below 1100px so center column stays usable.
  // No !!chatGameStatus guard here — non-competitive returning visitors have cached assets
  // so chatGameStatus may load slightly later; we don't want to flash two-column for them.
  const isThreeColumnLayout = !earlyCompetitiveMode && !chatGameStatus?.competitive_mode && !chatGameStatus?.imposter_mode
    && (gameType === 'chat-game' || gameType === 'chat') && !isMobile && !isNarrowDesktop;

  // 🎛️ SIDEBAR TOGGLES (Nina 09-15): non-comp desktop three-column only.
  // Ported spaces are excluded (their chrome audit already hid this furniture;
  // the Hearthside Rail owns the right column there) and so is bot mode (the
  // Explorer bot reads the DOM — collapsing panels would blind it). Rulings:
  // per-space per-device prefs · going live NEVER auto-opens (the LIVE pill
  // pulses on the rail) · collapsed ghost chat = a 4th tab in the fixed
  // DOSSIER/SHELF/MEMORY edge stack, not a separate rail.
  const canToggleSidebars = isThreeColumnLayout && !isPortedSpace && !isBotMode && !!chatGameStatus;
  const roomPanelOpen = !canToggleSidebars || resolvePanelOpen(layoutPrefs.room, chatGameStatus?.space_goal, 'room');
  // 📣 Live room: the crowd lives in the feed, so the three-column right
  // column rides the EXISTING collapsed path (CSS-hidden, handle gone) — with
  // no reopen tab (D8: no pop-out). Narrow/phone layouts are untouched here:
  // canToggleSidebars is false there, and ghostSurfacesOn hides the lane.
  // 📚 desktop B (Nina 09-30, "beside the chat"): while you read in the library the ghost
  // column folds, so the middle column can split into chat + book (its GHOST edge tab
  // brings it back for this book, and then a narrow middle stacks the book above the chat)
  const ghostPanelOpen = !canToggleSidebars || (!crowdInline && !library.ghostFold && resolvePanelOpen(layoutPrefs.ghost, chatGameStatus?.space_goal, 'ghost'));
  const setRoomPanelOpen = useCallback((open) => {
    setLayoutPrefs(saveLayoutPref(gameId, 'room', open));
  }, [gameId]);
  // 📖 THE READING LANE's geometry (09-18, Nina's pick). The lane is a fixed
  // element painted BEHIND the page (it sits before the pad at z auto — NO
  // stacking context, the same care the three-column row takes), so it has to
  // be told where the chat column is. ⚠️ Lives HERE, below roomPanelOpen /
  // ghostPanelOpen, on purpose: those are in the deps, and a deps array that
  // reads a const declared further down is a TDZ white-page (T10). The capped
  // column keeps its WIDTH when a sidebar toggles and only SHIFTS, which a
  // ResizeObserver on the column alone never sees — hence the panel deps and
  // the sibling observers.
  const [roomLaneRect, setRoomLaneRect] = useState(null);
  useEffect(() => {
    if (!roomLaneFill || isMobile) { setRoomLaneRect(null); return undefined; }
    let raf = 0;
    const measure = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const el = centerColumnRef.current;
        if (!el) return;
        const r = el.getBoundingClientRect();
        if (r.width < 40) return;
        const next = { left: Math.round(r.left), width: Math.round(r.width) };
        setRoomLaneRect((prev) => (prev && prev.left === next.left && prev.width === next.width ? prev : next));
      });
    };
    measure();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    const col = centerColumnRef.current;
    if (ro && col) {
      ro.observe(col);
      if (col.parentElement) { ro.observe(col.parentElement); Array.from(col.parentElement.children).forEach((c) => ro.observe(c)); }
    }
    window.addEventListener('resize', measure);
    const settle = setTimeout(measure, 350); // a sidebar's open/close settles
    return () => { cancelAnimationFrame(raf); clearTimeout(settle); if (ro) ro.disconnect(); window.removeEventListener('resize', measure); };
  }, [roomLaneFill, isMobile, roomPanelOpen, ghostPanelOpen]);
  const setGhostPanelOpen = useCallback((open) => {
    if (open && libraryRef.current && libraryRef.current.ghostFold) libraryRef.current.keepGhost(); // 📚 you asked for it back
    setLayoutPrefs(saveLayoutPref(gameId, 'ghost', open));
  }, [gameId]);
  // 👻 unread anchor: while collapsed, count only messages that arrive AFTER
  // the anchor. Anchors once the initial history load resolves (so history
  // never insta-fills the badge — the dossier high-water-mark lesson); clears
  // on reopen.
  useEffect(() => {
    if (!canToggleSidebars) return;
    if (ghostPanelOpen) { ghostCollapseBaselineRef.current = null; return; }
    if (isInitialSpectatorHistoryLoading) return;
    if (ghostCollapseBaselineRef.current === null) {
      ghostCollapseBaselineRef.current = spectatingMessages.length;
    }
  }, [canToggleSidebars, ghostPanelOpen, isInitialSpectatorHistoryLoading, spectatingMessages.length]);

  // Competitive/imposter mode: ghost chat gets its own 310px left sidebar on desktop.
  // !!chatGameStatus guard prevents briefly activating the competitive layout before we know
  // the mode — this only affects competitive games (non-competitive is untouched).
  const showGhostChatLeft = !!chatGameStatus && (chatGameStatus.competitive_mode || chatGameStatus.imposter_mode)
    && (gameType === 'chat-game' || gameType === 'chat') && !isMobile && !isNarrowDesktop;

  // Clicking a go-live ghost-chat message: surface the live panel (dock or
  // center-column tab depending on layout) and hand LivestreamPanel a watch
  // request so that streamer lands on the stage.
  const handleWatchLiveFromChat = (req) => {
    setLiveWatchRequest({ ...req, ts: Date.now() });
    // 🧱 the room frame: the stream plays on its stage
    if (shellPreset) { setShellStage({ asked: true, closedFor: null }); return; }
    // 🏡 W2d: a place page's stream plays where the painting is
    if (portedPlaceOn && !isMobile) { setPlaceExtra('live'); return; }
    if (isThreeColumnLayout) {
      // 🎛️ a click on a go-live message is explicit intent — the ONE thing
      // allowed to reopen a collapsed room panel (going live itself never
      // auto-opens; the LIVE pill pulses instead — Nina 09-15).
      if (canToggleSidebars && !roomPanelOpen) setRoomPanelOpen(true);
      setLivestreamFloating(false);
      setLivestreamExpanded(true);
    } else if (placesNav) {
      // 📱 places nav: streams play on the TV place — navigate, don't re-filter
      setMobileView('tv');
    } else {
      setActiveTab('actions');
      setChatActionTab('livestream');
    }
  };

  // Desktop layouts: Start Convo button + input form only render on the Room
  // lane (three-column always did this; the narrow-desktop tabbed layout
  // joined 08-14 with the composer-is-Room's scoping), so auto-switch to Room
  // when the tutorial reaches those steps. Mobile keeps its own places flow.
  useEffect(() => {
    if (!tutorialActive || isMobile) return;
    const step = tutorialSteps[tutorialStep];
    if (step?.ref === startButtonRef || step?.ref === inputFormRef) {
      setChatActionTab('all');
    }
  }, [tutorialActive, tutorialStep, isMobile, tutorialSteps]);

  // Clear pinned sidebar tab when three-column layout collapses, restore Media when it activates
  useEffect(() => {
    if (!isThreeColumnLayout) setPinnedSidebarTab(null);
    // Restore falls back to CHAT (08-02): 'media' is no longer a pinnable —
    // this stale 'media' fallback was how the redundant sidebar Media panel
    // kept reappearing after a window resize.
    else setPinnedSidebarTab(prev => (prev && prev !== 'media') ? prev : 'chat');
  }, [isThreeColumnLayout]);

  // Keep the livestream host node parented to whichever slot owns it this
  // render: pinned sidebar slot when 📌'd, 3-col dock otherwise, narrow-layout
  // Live tab when the layout collapses (dock and tab slots are mutually
  // exclusive, so the || chain is safe). No dep array on purpose: the slot
  // divs mount/unmount across renders and refs don't trigger effects, so the
  // cheap parentNode check runs every commit instead. Reparenting is a raw
  // appendChild React never sees — the panel (and an active stream) survives
  // dock ↔ pin ↔ layout-boundary moves without remounting.
  useEffect(() => {
    if (!livestreamHostEl) return;
    // 🧱 the room frame's stage slot heads the chain (null everywhere else)
    const target = shellLiveSlotRef.current || ((livestreamPinned && livestreamPinSlotRef.current)
      ? livestreamPinSlotRef.current
      : (placeLiveSlotRef.current || livestreamDockSlotRef.current || livestreamTabSlotRef.current || livestreamHiddenHolderRef.current));
    if (target && livestreamHostEl.parentNode !== target) target.appendChild(livestreamHostEl);
  });

  // Watch-party host reparent — the stage slot when docked, the Watch Party
  // tabpanel when open, else the hidden holder so the iframe (and the party's
  // audio) survives while you browse other tabs. Same no-dep pattern as the
  // livestream effect above.
  useEffect(() => {
    // Theater-presence signal for the status poll: a party exists, you're
    // seated (always by your own tap since 08-04), AND a visible slot is
    // mounted — the
    // hidden-holder fallback means the theater is off-screen and quips
    // shouldn't fire for you. Runs every render, so it tracks dock/tab moves.
    const visibleSlot = shellWpSlotRef.current || placeWpSlotRef.current || stageWpSlotRef.current || wpTabSlotRef.current; // 🧱 the frame's stage first
    watchingTheaterRef.current = !!(watchParty && inTheater && visibleSlot);
    if (!watchPartyHostEl) return;
    const target = visibleSlot || wpHiddenHolderRef.current;
    if (target && watchPartyHostEl.parentNode !== target) target.appendChild(watchPartyHostEl);
  });

  // 🏡⋯ W2d: the ONE ⋯ menu's doors (both screens): Watch together · Go live ·
  // Story · Music · Share · Shelf, each the existing surface (PortedMenu), then
  // the owner's rituals and the thoughts switch. Wave 3 (ruling 32): the ⋯
  // sits in the slim bar ON the frame (PortedStage `bar`), so the list is
  // built here, once, and handed down.
  const placeMenuItems = (portedPlaceOn && chatGameStatus) ? [
    { key: 'watch', icon: '🍿', label: 'Watch together', cmd: '/watch', on: !!watchParty, onSelect: () => openPlaceExtra('watch') },
    { key: 'live', icon: '🔴', label: 'Go live', cmd: '/live', on: spaceLiveStreamers.length > 0, onSelect: () => openPlaceExtra('live') },
    ...(!isMobile ? [{ key: 'story', icon: '📖', label: 'Story', cmd: '/story', onSelect: () => openPlaceExtra('story') }] : []),
    { key: 'music', icon: '🎵', label: 'Music', onSelect: () => openPlaceExtra('music') },
    ...(viewerCanShare ? [{ key: 'share', icon: '🔗', label: 'Share', onSelect: () => openPlaceExtra('share') }] : []), // 🔗 10-01 canShare
    { key: 'shelf', icon: '🧺', label: 'Shelf', onSelect: () => openPlaceExtra('shelf') },
    // 🧳 W3 Move house (the owner, once he lives somewhere) · 👋 goodbye
    ...(isPortedOwner && humanPlayerId && canMovePorted({ homeKey: chatGameStatus?.home?.key || null, choice: chatGameStatus?.home?.choice ? { ...chatGameStatus.home.choice, chosen_at: chatGameStatus.home.choice.chosen_at || chatGameStatus.home.chosen_at || null } : null, isOwner: true })
      ? [{ key: 'move', icon: '🧳', label: 'Move house', onSelect: () => portedStageRef.current?.moveHouse?.() }] : []),
    // (one wave per goodbye: once the lamps are down the item only says so)
    ...(isPortedOwner && humanPlayerId ? [{ key: 'wave', icon: '👋', label: placeLampDown ? 'Waved goodbye' : 'Wave goodbye', onSelect: placeLampDown ? () => {} : waveGoodbye }] : []),
    // 💭 W2d.1 (A9): the thoughts switch (the chat's "AI Thoughts" row
    // leaves the place); an OFF is remembered on this device
    { key: 'thoughts', icon: '💭', label: placeThoughtsLabel(showThoughts, portedMindProps?.residentName), onSelect: () => {
      const next = !showThoughts;
      try {
        if (next) window.localStorage.removeItem('portedThoughtsOff');
        else window.localStorage.setItem('portedThoughtsOff', '1');
      } catch (e) { /* private mode: this visit only */ }
      setShowThoughts(next);
    } },
  ] : null;

  // 🧱🗂️ THE ROOM FRAME's page side (the gate + its state live under isBotMode).
  // The stage holds the live panel while someone asked, watches, or shares a
  // SCREEN (utils/roomShellPage.shellStageLive: a camera never takes it, the
  // legacy stage's rule) and a watch party's seat bar or theater while a party
  // runs. Everything else of today's page stays mounted but hidden; its ONE
  // feed and ONE composer portal into the frame (shellSlot), and the fixed
  // overlays that live inside today's columns lift to <body> (shellLift).
  const shellPublishing = !!shellPreset && !!humanPlayerId && spaceLiveStreamers.some((s) => s && s.id === humanPlayerId);
  const shellScreenKey = shellPreset ? screenShareKey(spaceLiveStreamers) : '';
  const shellLiveShown = !!shellPreset && !isBotMode && shellStageLive({
    publishing: shellPublishing, asked: shellStage.asked, connected: !!livestreamStatus.isConnected,
    screenKey: shellScreenKey, closedFor: shellStage.closedFor,
  });
  const shellPartyShown = !!shellPreset && !!watchParty && !chatGameStatus?.competitive_mode && !chatGameStatus?.imposter_mode;
  // a node goes INTO its frame host, never also inline (until the host exists
  // it waits, so it mounts once)
  const shellSlot = (name, node) => {
    if (!shellPreset) return node;
    const host = shellHosts[name];
    return host ? createPortal(node, host) : null;
  };
  const shellLift = (node) => ((shellPreset && node && typeof document !== 'undefined') ? createPortal(node, document.body) : node);
  // Share → Screen: the stage opens and the panel starts its own screen share
  const shellShareScreen = () => {
    setShellStage({ asked: true, closedFor: null });
    setPublishRequest({ mode: 'screen', ts: Date.now() });
  };
  const shellComposerShare = (shellPreset && (shellPreset.composerTools || []).includes('share'))
    ? { onScreen: (!isMobile && canShareScreen()) ? shellShareScreen : null } : null;
  const shellPowerView = shellPreset ? spacePowerView() : null;
  const renderShellFrame = () => {
    if (!shellPreset || !chatGameStatus) return null;
    const roomId = chatGameStatus.game_id || gameInstanceId || gameId;
    return (
      <>
        <RoomShellFrame
          gameId={roomId}
          purpose={shellPreset.purpose}
          preset={shellPreset}
          name={chatGameStatus.name || ''}
          isMobile={isMobile}
          isNarrow={isNarrowDesktop}
          mode="dark"
          onBack={() => { window.location.href = '/'; }}
          battery={shellPowerView ? <PowerMeter view={shellPowerView} compact /> : null}
          // 📣 canPromote (everyone in a public room, the mod in a hidden one); ≤108px (the kit's lane rule); phones: the ⋯ item
          promote={(viewerCanPromote && !isMobile) ? (
            <button type="button" className="rs-lnk" data-prop-ui="" data-room-promote="shell" aria-label="Promote this space" title="Promote this space" onClick={() => setPromoteOpen(true)}>
              <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M3 11l18-5v12L3 14v-3z" />
                <path d="M11.6 16.8a3 3 0 1 1-5.8-1.6" />
              </svg>
              Promote
            </button>
          ) : null}
          users={activeUsers}
          viewerSeatId={humanPlayerId || createdPlayerIdRef.current}
          menuItems={shellMenuItems({
            isMobile, canPromote: viewerCanPromote,
            onExport: () => setExportChatOpen(true),
            onDetails: () => setShowSpaceInfoModal(true),
            onPromote: () => setPromoteOpen(true),
          })}
          onOpenFiles={() => setFilesOpen(true)}
          onOpenExport={() => setExportChatOpen(true)}
          renderNotes={(o) => (
            <NotesPanel {...o} notes={shellNotes ? shellNotes.notes : null} updatedAt={shellNotes ? shellNotes.updatedAt : null}
              throughRound={shellNotes ? shellNotes.throughRound : null} roomName={chatGameStatus.name || ''} />
          )}
          stageActive={shellLiveShown || shellPartyShown}
          onSlot={onShellSlot}
        />
        {/* a long room's list fills the feed below what sits above it (--gsp-virt-h, measured) */}
        <style>{'[data-shell-feed] [data-virtual-feed] { height: var(--gsp-virt-h, calc(100dvh - 200px)) !important; min-height: 0 !important; }'}</style>
        {/* the stage's two slots: the head of the theater + live panel reparent chains */}
        {shellSlot('stage', (
          <div data-shell-stage="" style={{ display: 'flex', flexDirection: 'column', flex: '1 1 auto', minHeight: 0 }}>
            {shellLiveShown && !shellPublishing && (
              <div data-shell-stage-bar="" style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 8px 6px 14px', borderBottom: '1px solid var(--rs-line)', color: 'var(--rs-muted)', font: '600 12px/1.2 -apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif', flex: 'none' }}>
                <span aria-hidden="true" style={{ width: 7, height: 7, borderRadius: '50%', background: 'rgb(var(--rs-live))', flex: 'none' }} />
                <span style={{ flex: 1, minWidth: 0 }}>Live on the stage</span>
                <button type="button" className="rs-lnk" data-prop-ui="" data-shell-stage-close="" onClick={() => setShellStage({ asked: false, closedFor: shellScreenKey })}>Close</button>
              </div>
            )}
            <div ref={shellWpSlotRef} style={{ display: shellPartyShown ? 'flex' : 'none', flexDirection: 'column', minHeight: 0 }} />
            <div ref={shellLiveSlotRef} style={{ display: shellLiveShown ? 'flex' : 'none', flexDirection: 'column', minHeight: 0, height: isMobile ? 'min(34vh, 300px)' : 'min(46vh, 420px)' }} />
          </div>
        ))}
      </>
    );
  };

  return (
    <GameThemeProvider themeSpec={aiGeneratedTheme}>
      <AsleepContext.Provider value={asleepCtx}>
      <ThemeStyleInjector>
        {/* 🔌 09-28 agent wave: ?with=<seat id> highlights that seat's lines and
            chips in every layout (outside every layout branch on purpose). */}
        {withSeatId && <style>{withHighlightCss(withSeatId)}</style>}
        {/* LIVESTREAM PANEL — ONE mount for the whole page, portaled into the
            stable host node (livestreamHostEl). The reparent effect moves the
            node between the 3-col dock, the pinned sidebar slot, and the
            narrow-layout Live tab, so neither pin/float moves nor crossing
            the 1100px layout boundary ever remount the panel: an active
            stream survives all of them. Lives OUTSIDE every layout
            conditional on purpose. Props are layout-aware. Never in bot mode
            (recursive self-view trap). */}
        {!isBotMode && (gameType === 'chat-game' || gameType === 'chat') && livestreamHostEl && createPortal(
          <LivestreamPanel
            gameId={gameId}
            playerName={users.find(u => u.game_player_id === humanPlayerId)?.name || 'Spectator'}
            playerId={humanPlayerId}
            theme={THEME}
            onStatusChange={setLivestreamStatus}
            spaceUsers={streamPanelUsers}
            moderatorCharacter={isPortedSpace ? null : chatGameStatus?.moderator_character}
            isMobile={isMobile}
            visible={shellPreset ? shellLiveShown /* 🧱 the room frame: its stage */
              : (portedPlaceOn && !isMobile) ? placeLiveShown /* 🏡 W2d: its slot is the sheet, or the stage when ON */
              : isThreeColumnLayout
              ? (roomPanelOpen && (livestreamPinned || livestreamExpanded) && !livestreamFloating)
              // 📱 the investigation takeover unmounts the TV layer, so
              // visible must agree — a stale true parked the stream in the
              // hidden holder with NO MiniPlayer (it gates on !visible):
              // invisible and unreachable until the takeover ended
              : (placesNav ? (mobileView === 'tv' && !investigationTakeover) : chatActionTab === 'livestream')}
            onRequestShow={() => {
              if (shellPreset) { setShellStage({ asked: true, closedFor: null }); return; }
              if (portedPlaceOn && !isMobile) { setPlaceExtra('live'); return; }
              if (isThreeColumnLayout) { setLivestreamFloating(false); setLivestreamExpanded(true); }
              else if (placesNav) { setMobileView('tv'); }
              else { setActiveTab('actions'); setChatActionTab('livestream'); }
            }}
            liveNow={spaceLiveStreamers.length > 0}
            liveStreamers={spaceLiveStreamers}
            streamScene={chatGameStatus?.livestream?.scene || null}
            watchRequest={liveWatchRequest}
            // 🎛️ collapsed room = a WATCHER really leaves the metered LiveKit
            // room (Nina 09-15: hide means unmount, not a hidden meter). The
            // panel itself protects publishers — see its forceDisconnect effect.
            // 🏡 W2d: a place page's watcher who folds the stream away leaves
            // the metered room too (the panel protects publishers)
            forceDisconnect={shellPreset ? !shellLiveShown : (portedPlaceOn && !isMobile) ? !placeLiveShown : (canToggleSidebars && !roomPanelOpen)}
            onPin={(isThreeColumnLayout && !livestreamPinned && !portedPlaceOn && !shellPreset) ? () => { setLivestreamFloating(false); setPinnedSidebarTab('livestream'); } : undefined}
            floatAnchorRef={centerColumnRef}
            publishRequest={publishRequest}
          />,
          livestreamHostEl
        )}
        {/* 🍿 WATCH PARTY THEATER — ONE mount for the whole page, portaled into
            watchPartyHostEl. The reparent effect moves the node between the
            stage slot, the Watch Party tabpanel, and a hidden holder, so
            docking/undocking or tab-hopping never remounts the YouTube iframe
            (remount = playback reset). Non-competitive only (v1). */}
        {(gameType === 'chat-game' || gameType === 'chat') && watchPartyHostEl && createPortal(
          watchParty && !chatGameStatus?.competitive_mode && !chatGameStatus?.imposter_mode ? (
            isBotMode ? (
              /* Bot mode: AWARENESS without the trap. The theater video can
                 never play for a bot (autoplay gesture + no use for video),
                 so the expanded player + "Take a seat" button just eat whole
                 sessions of clicking. An inert status line keeps the watch
                 party legible in the bot's world instead. */
              <div
                aria-hidden
                style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  padding: '10px 14px', marginBottom: 8,
                  borderRadius: 8, pointerEvents: 'none',
                  background: 'rgba(40,0,60,0.35)',
                  border: '1px solid rgba(123,31,162,0.5)',
                  color: 'rgba(255,255,255,0.85)', fontSize: '0.85em',
                }}
              >
                📺 The locals have a watch party on: <b>{watchParty.title}</b> (video for human visitors — chat about it instead!)
              </div>
            ) : inTheater ? (
              <WatchPartyTheater
                watchParty={watchParty}
                isRemoteHolder={!!humanPlayerId && watchParty.remote_holder === humanPlayerId}
                onControl={(state, position_s, video_id) => watchPartyAction('control', { state, position_s, video_id })}
                onHeartbeat={(position_s, state, video_id) => watchPartyAction('heartbeat', { position_s, state, video_id })}
                onReact={(emoji) => watchPartyAction('react', { emoji })}
                lastReaction={lastWatchReaction}
                onNext={(endedVideoId) => watchPartyAction('next', endedVideoId ? { ended_video_id: endedVideoId } : {})}
                onStop={() => watchPartyAction('stop')}
                // Leave couch = THE collapse (Nina 07-22): stands you up →
                // theater unmounts (silent) → the "Watch party in progress /
                // Take a seat" bar renders in its place. onMinimize (keep-
                // playing shrink) intentionally NOT passed — its ⌄ button
                // stays dormant in the theater; one exit verb only. Parked
                // idea: a pop-out mini-player from the collapsed bar.
                onExit={() => setInTheater(false)}
                ambient={!!watchParty.host_mode}
                isPlayer={!!humanPlayerId}
                isMobile={isMobile}
                THEME={THEME}
                gameId={gameId}
                playerId={humanPlayerId}
                // One portaled node + this ONE shared cap = the theater cannot
                // differ between tabs (Nina 08-12 identity ask holds by
                // construction). A briefly-tried 08-12 "pin to default" was
                // reverted 08-13: Nina wants the resize drag, and it never was
                // the source of a per-tab difference.
                maxVideoHeight={(portedPlaceOn && !isMobile && placeFrame) ? Math.max(200, placeFrame.restH - 72) : stageVideoHApplied}
                // 🛋️ 09-23 batch 1: the couch in the rail slot during a screening
                rail={(COUCH && watchParty.screening) ? couchNode('rail') : null}
              />
            ) : placesNav ? null : (
              /* 📱 places nav: the merged TV card (launcher tvChrome) owns the
                 un-seated CTA — this portal bar would double it in the slot */
              <div
                onClick={() => setInTheater(true)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  padding: '10px 14px', marginBottom: 8,
                  borderRadius: 8, cursor: 'pointer',
                  background: 'linear-gradient(90deg, rgba(123,31,162,0.5), rgba(40,0,60,0.55))',
                  border: `1px solid ${THEME.accent || '#7b1fa2'}`,
                  color: '#fff',
                }}
              >
                <span style={{ fontSize: '1.3em' }}>🍿</span>
                <span style={{ flex: 1, fontSize: '0.85em', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  Watch party in progress: <b>{watchParty.title}</b>
                </span>
                <span style={{
                  fontSize: '0.8em', fontWeight: 700, flexShrink: 0,
                  padding: '5px 12px', borderRadius: 999,
                  backgroundColor: THEME.accent || '#7b1fa2',
                }}>
                  Take a seat
                </span>
              </div>
            )
          ) : null,
          watchPartyHostEl
        )}
        {FLOATING_BG_ENABLED && backgroundEmojis && <FloatingSvgBackground emojis={backgroundEmojis} count={60} opacityMin={0.75} opacityMax={1.0} sizeMin={8} sizeMax={16} />}
        {/* 👀 While you were away… — returning-visitor recap (fixed overlay; non-competitive humans only) */}
        {(gameType === 'chat-game' || gameType === 'chat') && !isBotMode
          && !chatGameStatus?.competitive_mode && !chatGameStatus?.imposter_mode && gameId && (
          <WhileYouWereAwayCard
            gameId={gameId}
            allActions={results.flatMap(r => r.actions || [])}
            users={users}
            spaceName={chatGameStatus?.name}
            THEME={THEME}
            isMobile={isMobile}
            raisePx={isMobile && chatGameStatus?.game_completed
              && (chatGameStatus?.round_type === 'finished' || chatGameStatus?.round_type === 'conclusion') ? 84 : 0}
            onVisibleChange={setWywaVisible}
          />
        )}
        {/* ✏️ Chat-canvas doodle — MOBILE keeps the full-screen modal (no room
            for the composer popover); desktop mounts anchored inside the
            composer wrapper (see inputFormRef divs).
            ⚠️ A CROWDED DESKTOP COLUMN also lands here (doodleAnchored false,
            Nina 08-14): the modal MUST mount at page level, never inside the
            composer — that wrapper carries a backdrop-filter, which creates a
            containing block that would trap `position: fixed` and leave the
            "full-screen" canvas boxed inside the composer. */}
        {showDrawingCanvas && !isBotMode && (isMobile || !doodleAnchored) && (
          <DrawingCanvas
            key={drawOnSeed?.actionId || 'blank'}
            onSend={handleSendDrawing}
            onClose={closeDrawingCanvas}
            theme={THEME}
            initialStrokes={drawOnSeed?.strokes || null}
          />
        )}
        {/* 🐛 Doodle Bug turn canvas — seeded with the shared round canvas,
            2-stroke cap; submits via respondToInteraction (all viewports use
            the modal: a turn deserves the full easel). */}
        {showDoodleTurnCanvas && !isBotMode && (
          <DrawingCanvas
            key={`doodle-turn-${currentRequestId || 'x'}`}
            onSend={handleDoodleTurnSend}
            onClose={() => setShowDoodleTurnCanvas(false)}
            theme={THEME}
            initialStrokes={doodleTurnSeedRef.current}
            maxNewStrokes={2}
          />
        )}
        {/* 🕵️ Locals dossier moved into the chat panel's Locals TAB (user call
            07-09) — mount lives beside the other tabpanels so the unlock
            engine + toasts run on every tab. */}
        {/* 📖 Chapter-close title card — the space announces its own episodes (non-competitive humans only) */}
        {(gameType === 'chat-game' || gameType === 'chat') && !isBotMode
          && !chatGameStatus?.competitive_mode && !chatGameStatus?.imposter_mode && gameId && (
          <ChapterCard
            gameId={gameId}
            actions={results.flatMap(r => r.actions || [])}
            theme={{ accent: THEME.accent }}
            enabled
          />
        )}
        <div style={{
          backgroundColor: aiGeneratedTheme ? 'transparent' : THEME.bgDark,
          // NOTE (07-25): do NOT paint ui_theme.backgroundImage here — the page
          // body already carries it via ThemeStyleInjector (body wallpaper for
          // comp AND non-comp); this container stays transparent so that shows
          // through. A duplicate paint here (tried + reverted same day) only
          // double-dimmed the working wallpaper. The "bg turns black" report is
          // a different mechanism — see the injector's enabled&&url gate.
          backgroundImage: aiGeneratedTheme ? 'none' : `
            radial-gradient(circle at 20% 50%, ${THEME.evil}1a 0%, transparent 50%),
            radial-gradient(circle at 80% 80%, ${THEME.accent}0d 0%, transparent 50%),
            radial-gradient(circle at 40% 20%, ${THEME.secret}0d 0%, transparent 50%)
          `,
        }}>
      {/* 🚪✨🖼️ 09-17 /room presence batch — all camera-gated:
          · @property makes --room-dim-a a real number so the wallpaper dim can
            transition (no support = it snaps; nothing breaks)
          · the arrival caption + the peek lightbox are PORTALED to body so
            the arrival fade on the page pad below never dims them */}
      {roomCameraOn && (
        <style>{'@property --room-dim-a { syntax: "<number>"; inherits: true; initial-value: 0.6; } html { transition: --room-dim-a 700ms ease; } @media (prefers-reduced-motion: reduce) { html { transition: none; } }'}</style>
      )}
      {/* 🚪🕯️ between rooms (09-24): the desktop walk, over the whole window
          and under the arrival card (z 1990 < 2000); phones play it inside
          the room card instead (variant "card", below) */}
      {roomCameraOn && <RoomTransit transit={roomTransit} variant="screen" z={isMobile ? PHONE_WALK_Z : 1990} fit={isMobile} />}
      {/* 📱 09-25: the phone postcard = the whole painting the walk ended on
          (FittedPainting, the same layers in the same box, so the hand-over
          does not move) + the room line + who is here. PHONE_CARD_MS. */}
      {roomCameraOn && ((roomArriving && roomArriving.art) || roomPostcardLeaving) && createPortal(
        (() => {
          const pc = (roomArriving && roomArriving.art) ? roomArriving : roomPostcardLeaving;
          const leaving = pc === roomPostcardLeaving && !(roomArriving && roomArriving.art);
          return (
            <div data-room-postcard="phone" aria-live="polite" style={{
              position: 'fixed', inset: 0, zIndex: PHONE_WALK_Z + 10, pointerEvents: 'none',
              background: '#0b0704', overflow: 'hidden',
              ...(leaving ? { animation: 'roomPostcardOut 400ms ease forwards' } : {}),
            }}>
              <style>{`@keyframes roomArriveWho { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: none; } }
@keyframes roomPostcardOut { from { opacity: 1; } to { opacity: 0; } }`}</style>
              <FittedPainting art={pc.art} />
              <div style={{
                position: 'absolute', left: 0, right: 0, top: '63%', textAlign: 'center', padding: '0 24px',
                color: '#fff', fontWeight: 800, fontSize: 19, lineHeight: 1.3,
                textShadow: '0 2px 14px rgba(0,0,0,0.9), 0 1px 3px rgba(0,0,0,0.9)',
              }}>
                <div>{pc.line}</div>
                {pc.who && (
                  <div style={{ marginTop: 8, fontSize: 15, fontWeight: 600, opacity: leaving ? 1 : 0, animation: leaving ? 'none' : `roomArriveWho 500ms ease ${PHONE_WHO_DELAY_MS}ms forwards` }}>
                    {pc.who}
                  </div>
                )}
              </div>
            </div>
          );
        })(),
        document.body
      )}
      {roomCameraOn && roomArriving && !roomArriving.art && createPortal(
        <div data-room-postcard="screen" aria-live="polite" style={{
          position: 'fixed', left: 0, right: 0, top: '58%', zIndex: 2000, pointerEvents: 'none',
          textAlign: 'center', padding: '0 24px', color: '#fff', fontWeight: 800, fontSize: 22,
          textShadow: '0 2px 14px rgba(0,0,0,0.9), 0 1px 3px rgba(0,0,0,0.9)',
        }}>
          <style>{`@keyframes roomArriveWho { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: none; } }`}</style>
          <div>{roomArriving.line}</div>
          {/* who is here, a beat later (CSS delay, no timer; arrivals are motion-OK only) */}
          {roomArriving.who && (
            <div style={{ marginTop: 10, fontSize: 17, fontWeight: 600, opacity: 0, animation: `roomArriveWho 600ms ease ${ARRIVE_WHO_DELAY_MS}ms forwards` }}>
              {roomArriving.who}
            </div>
          )}
        </div>,
        document.body
      )}
      {/* 🎃 10-07 pumpkins: the carving table, a full sheet over the page (only while house.pumpkins is out) */}
      {pumpkinSheetOpen && pumpkinsRider && createPortal(
        <PumpkinCarving
          rider={pumpkinsRider}
          porch={housePumpkins.porch}
          onRefresh={() => housePumpkins.refresh()}
          gameId={pumpkinRoom}
          playerId={humanPlayerId || null}
          playerSecret={pumpkinSecret}
          porchPicture={porchTakeFor(houseStatus?.room_art, (housePumpkins.porch && housePumpkins.porch.glow) || pumpkinsRider.glow)}
          onClose={() => setPumpkinSheetOpen(false)}
        />,
        document.body
      )}
      {/* 👁 P1 doorway caption (ROOM_VERBS_PEEK_SPEC D1, 09-18 late): the same
          lightbox, plus what everyone in there is up to, UNDER the art. */}
      {roomCameraOn && roomPeekOpen && roomPeekUrl && createPortal(
        <RoomPeekLightbox
          url={roomPeekUrl}
          roomId={viewerRoom}
          line={roomEntryLine(viewerRoom, roomVeilHour, !!roomArt?.masthead?.dark)}
          here={roomPresenceFor(viewerRoom)}
          // 👁 09-23 merged /look (Nina: A): the look text rides the picture —
          // beside it on desktop, under it on phones and screening nights.
          look={buildRoomLookParts()}
          onClose={() => setRoomPeekOpen(false)}
          // 🎃 10-07 the porch's carved pumpkins while the table is out; a tap opens the carving table
          pumpkins={(pumpkinsRider && viewerRoom === 'porch' && housePumpkins.porch) ? { porch: housePumpkins.porch, onOpen: () => { setRoomPeekOpen(false); setPumpkinSheetOpen(true); } } : null}
          // 🎛️ 10-09 (Nina): the event's buttons on the picture, the message box's own (event-only
          // verbs, the outing's, Carve), so they work without going back to the chat
          commands={(() => {
            if (isBotMode || !humanPlayerId) return null;
            const chips = sceneCommandChips({ roomId: viewerRoom, live: liveRoomVerbs(houseStatus?.room_verbs), outing: houseStatus?.outing || null, pumpkins: pumpkinsRider });
            return chips.length ? { chips, onCommand: onSceneCommand } : null;
          })()}
          // 🎬🍿 the ▶ on the painted screen: only in a live screening's room, only
          // while there is a party to sit down in. It closes the picture and seats you.
          screen={(screeningHere && screeningHere.verbs && screenRectFor(viewerRoom))
            ? { rect: screenRectFor(viewerRoom), label: WATCH_VERB, onPlay: () => { setRoomPeekOpen(false); seatInTheater(); } }
            : null}
          // 🛋️ 09-23 batch 1: the couch under the art during a screening; the hint = the next tap
          below={(COUCH && houseStatus?.screening && houseStatus?.outing && viewerRoom === houseStatus.outing.id && houseStatus.screening.state !== 'ended') ? couchNode('sheet') : null}
          hint={(screeningHere && screeningHere.verbs) ? (isMobile ? 'Tap the screen to watch' : 'Press play on the projector.') : null}
          gerald={geraldFor(viewerRoom)}
          lost={lostFor(viewerRoom)} // 🦝🧦 only ever this room's, only inside the glass
          // 🔍 09-19: the room's own particles ON the painting (the page's
          // ambient canvas is under this sheet and can never show here)
          particles={roomPeekScene(houseStatus?.weather?.look, viewerRoom, roomVeilHour, geraldDay)}
          // 🌕 09-26: on a moon night the moon rises in the painting (roof + porch)
          sky={liveEvent ? moonSkyFor(viewerRoom, liveEvent.template, liveEvent.born, stripNow) : null}
          // 🌠 09-26 roof night: the twist cloud, meteors, the falling star, wish lanterns
          night={(liveEvent || viewerRoom === 'roof') ? nightSkyFor(viewerRoom, results.flatMap((r) => r.actions || []), liveEvent, Date.now()) : null}
          // 🌠 the wish box: the roof, while /wish is live; the same send the composer makes
          wish={(viewerRoom === 'roof' && !isBotMode && liveRoomVerbs(houseStatus?.room_verbs).includes('wish'))
            ? { openAt: roofWishAt, onWish: (text) => handleInterjectionAndPlay(`/wish ${text}`, [], { name: 'wish', targets: [], text, card_message: null }) }
            : null}
          // 📰 09-26 the event's newest card + the open vote under the picture (this sheet covers the chat)
          news={(() => {
            const acts = results.flatMap((r) => r.actions || []);
            const now = Date.now();
            const ev = recentEventStartFor(acts, now) || liveEvent; // 📅 the calendar fallback when the start card scrolled out
            const inv = ev ? roomInviteFor(ev, (houseStatus?.rooms || []).map((r) => r.id)) : null;
            // 💬 09-26 option C: an outing's own place is its scene too (outings stage no room)
            const scene = ev ? sceneRoomFor(ev, inv && inv.room, houseStatus?.outing) : null;
            if (!scene || scene !== viewerRoom) return null;
            const shownPoll = (pollOverride && pollOverride.id) ? pollOverride : (pollOverride && pollOverride.closed ? null : (chatGameStatus?.space_poll || null));
            const poll = liveEvent ? openPollFor(shownPoll, now) : null;
            const card = pictureCardFor(acts, ev, now);
            return (card || poll) ? { card, poll, onVote: (poll && humanPlayerId && !isBotMode) ? (optionId) => handlePollVote(poll.id, optionId) : null } : null;
          })()}
          // 🔍 09-26 (Nina): on the event's own scene the magnifying glass waits under a button below Wish
          // 💬 09-26 option C: these three key on sceneRoom (the staged room, or an outing's own place)
          glassButton={!!(liveEvent && sceneRoom && viewerRoom === sceneRoom)}
          // 📸🔗 09-26 (Nina): on the event's scene, a polaroid of it (the same /snap the composer sends) and its link
          snap={(liveEvent && sceneRoom && viewerRoom === sceneRoom && !isBotMode && humanPlayerId && liveRoomVerbs(houseStatus?.room_verbs).includes('snap')) // 💬 09-26 option C: sceneRoom
            ? { onSnap: (word) => handleInterjectionAndPlay(word ? `/snap ${word}` : '/snap', [], { name: 'snap', targets: [], text: word || '', card_message: null }) }
            : null}
          share={(liveEvent && sceneRoom && viewerRoom === sceneRoom) ? { onShare: shareRoomLink } : null}
          // 💬 09-26 option C (Nina: "I really like C, but only for certain events"): on a bubble
          // event's own scene the chat floats over the picture, with a message box on it. The box
          // sends exactly what the chat box would (its @names become targets, the same send).
          chat={(liveEvent && sceneRoom && viewerRoom === sceneRoom && sceneBubblesOn(liveEvent.template)) ? (() => {
            const c = sceneChatFor(results.flatMap((r) => r.actions || []), liveEvent, sceneSeatOf);
            if (!c) return null;
            const others = users.filter((u) => u.game_player_id !== humanPlayerId).map((u) => u.name);
            return {
              ...c,
              placeholder: viewerRoom === 'roof' ? 'Say something up here…' : null,
              onSend: (!isBotMode && humanPlayerId) ? (text) => handleInterjectionAndPlay(text, mentionTargets(text, others), null) : null,
            };
          })() : null}
          day={geraldDay} // 💬 which of an object's asides is today's
        />,
        document.body
      )}
      {/* 👁 P3 keyhole (D2): look into another room from its picker card. The
          card model is the picker's own, so the glass shows exactly the
          picture the card does (painting, treated banner or sky). */}
      {roomCameraOn && !isBotMode && roomKeyhole && (() => {
        const keyCard = roomPickerCards(roomPickerArgs()).find((c) => c.id === roomKeyhole && canKeyhole(c));
        if (!keyCard) return null;
        return createPortal(
          <RoomKeyhole
            card={keyCard}
            here={roomPresenceFor(roomKeyhole)}
            onGo={(id) => { setRoomKeyhole(null); setRoomPickerOpen(false); goRoom(id); }}
            onClose={() => setRoomKeyhole(null)}
            gerald={geraldFor(roomKeyhole)}
          />,
          document.body
        );
      })()}
      {/* 📖 THE READING LANE (09-18): darkness ONLY behind the chat column,
          feathered LANE_FEATHER px past each edge, clear at the very top so
          the header art shows. Margins get no dim at all. Fixed + z auto +
          BEFORE the pad in tree order = under the page, above the body
          wallpaper, and no new stacking context. Its alpha is the one
          animated number (--room-dim-a), so the arrival postcard clears it
          with everything else. No mask support = a hard-edged lane. */}
      {roomCameraOn && roomLaneFill && roomLaneRect && !isMobile && (
        <div aria-hidden="true" data-room-lane="" style={{
          position: 'fixed', top: 0, bottom: 0, pointerEvents: 'none',
          left: roomLaneRect.left - LANE_FEATHER, width: roomLaneRect.width + LANE_FEATHER * 2,
          background: roomLaneFill,
          WebkitMaskImage: `linear-gradient(90deg, transparent 0, #000 ${LANE_FEATHER}px, #000 calc(100% - ${LANE_FEATHER}px), transparent 100%), linear-gradient(180deg, transparent 0, #000 200px)`,
          maskImage: `linear-gradient(90deg, transparent 0, #000 ${LANE_FEATHER}px, #000 calc(100% - ${LANE_FEATHER}px), transparent 100%), linear-gradient(180deg, transparent 0, #000 200px)`,
          WebkitMaskComposite: 'source-in', maskComposite: 'intersect',
        }} />
      )}
      <div ref={pageScrollPadRef} style={{
        padding: isMobile ? '10px 8px 0 8px' : '20px 20px 0 20px',
        margin: '0 auto',
        position: 'relative',
        marginRight: showVideoList && isStitcherExpanded ? '550px' : '0',
        // Arrival moment = a POSTCARD (Nina 09-18): the page leaves entirely so
        // only the room + its one line show. It used to ghost to 0.12, which
        // read as haze: too faint to read, too present to ignore, and the
        // ghost text ran through the caption. Quick out (250ms), gentle back
        // (500ms). Keys exist only on camera spaces; opacity 1 makes no
        // stacking context.
        ...(roomCameraOn ? { opacity: roomCurtain ? 0 : 1 } : {}),
        ...(roomCameraOn && roomCurtain ? { pointerEvents: 'none' } : {}),
        transition: roomCameraOn ? `margin-right 0.3s ease, opacity ${roomCurtain ? 250 : 500}ms ease` : 'margin-right 0.3s ease',
        height: isMobile ? 'auto' : 'calc(100dvh - 36px)',
        // Clearance for the fixed thumb-zone dock (stage 1, 07-17) so the last
        // in-flow content isn't buried under the pinned composer.
        // 08-02: 230 is the BASELINE only — the measured-reserve effect
        // (grep pageScrollPadRef) raises it live to dock height + nav + 24
        // whenever the dock outgrows it. Keep the baseline in sync there.
        // 🏡📐 wave 3 (ruling 32): a laptop's place page SCROLLS (the whole
        // painting, then a full lane + the whole journal page). The DOCUMENT
        // is the scroller, on purpose: App.css un-clips every inline-overflow
        // relative div at ≥700px (the 09-22 lesson), so this shell could never
        // be one; it grows with the frame instead, with a little air under it
        paddingBottom: isMobile ? 230 : (portedPlaceOn ? 24 : undefined),
        minHeight: isMobile ? '100dvh' : (portedPlaceOn ? 'calc(100dvh - 36px)' : undefined),
        display: 'flex',
        flexDirection: 'column',
        overflow: isMobile ? 'auto' : 'hidden',
        ...((portedPlaceOn && !isMobile) ? { height: 'auto', overflow: 'visible' } : {}),
        // 🧱 the room frame: one screen, edge to edge (the frame is 100dvh)
        ...(shellPreset ? { padding: 0, paddingBottom: 0, margin: 0, marginRight: 0, height: '100dvh', minHeight: 0, overflow: 'hidden' } : {}),
      }} data-place-shell={(portedPlaceOn && !isMobile) ? '' : undefined}>
        {/* 🧱🗂️ THE ROOM FRAME (learn · research, its switch ON since 10-01; null = none of this) */}
        {renderShellFrame()}
        {/* 🖼️ Phone banner: the 07-26 blurred-glow layer is RETIRED (Nina
            08-03: "muddy smudge") — the art now renders SHARP as a masthead
            CARD wrapping the identity block below (search "Masthead card"). */}
        {/* 🖼️ Desktop masthead (Nina 07-26; sharp-core rework 08-01): the
            banner art is the header's ENVIRONMENT, mask-fading down into the
            wallpaper. 08-01: full-bleed SHARP asked ~1024px of generated art
            to cover 1900+ CSS px at retina — a 3.5-4x upscale that read as
            "bad resolution" (Nina). Now the art renders 1:1-ish SHARP in a
            centered ~1080px core (feathered edges) over a heavily BLURRED
            full-bleed copy of itself — crisp where the eyes land, atmosphere
            at the wings. Deliberately NOT the phone treatment above (glow
            only); this is desktop-only by design. NO negative zIndex —
            the body wallpaper doesn't promote to canvas (html carries its own
            background) so a z:-1 layer paints UNDER it; instead the columns
            row below is position:relative, which puts all column content above
            this layer by tree order (same trick the phone header uses with the
            title/objective band). */}
        {!isMobile && !chatGameStatus?.competitive_mode && !chatGameStatus?.imposter_mode
          && (gameType === 'chat-game' || gameType === 'chat')
          && chatGameStatus?.ui_theme?.bannerImage?.url
          && !portedPlaceOn /* 🏡 the room band replaces it (D17) */ && !shellPreset /* 🧱 the frame's thin bar */ && (() => {
          // 🚪🖼️ /room Tier 0/1 (09-15 eve): the masthead SLOT is where
          // "stepping into another room" lives — the over-films alone read as
          // a lighting change (Nina). Unlike the ambient layers this band is
          // real content (columns layer above it by tree order), so it may
          // change OPAQUELY: indoor rooms re-aim the house art (treat =
          // filter + zoom + nudge), roof/porch replace it with an authored
          // opaque sky (takeover — the §0.1 ruling the pm re-cut had lost).
          // mastheadRoom === null (house, camera off, every non-house space)
          // renders the EXACT pre-batch markup — additive gate. Tier 2: when
          // the payload carries house.room_art for this room, the REAL room
          // art takes the slot (kind 'art' — same wings+core markup, swapped
          // src, base grade, no transform); treat styling below keys on
          // kind === 'treat' so art never inherits a re-grade.
          // weather.kind (Tier 3, 09-16): wet days pick a room's rain take
          // when the applied map carries one; absent = clear = dry.
          // 🖼️ 09-17: when the room's art is on the WALLPAPER the band steps
          // aside — one image, never two (roomArtPlan.bandHidden).
          if (roomArt?.bandHidden) return null;
          // artRoom, not viewerRoom: a walk keeps the room you are leaving up
          const mastheadRoom = roomCameraOn ? roomMasthead(artRoom, houseStatus?.circadian?.clock?.hour, houseStatus?.room_art, houseStatus?.weather?.kind, roomSeason) : null;
          // In a room the band fades lower (declutter): more of the room
          // shows before the wallpaper takes over. Main room keeps 42%.
          const mhFade = roomDeclutter ? 62 : 42;
          const mhTreat = mastheadRoom?.kind === 'treat' ? mastheadRoom : null;
          const mhSrc = mastheadRoom?.kind === 'art' ? mastheadRoom.url : chatGameStatus.ui_theme.bannerImage.url;
          return (
          <div aria-hidden="true" style={{
            position: 'absolute', top: 0, left: 0, right: 0, height: 300,
            overflow: 'hidden', pointerEvents: 'none',
            WebkitMaskImage: `linear-gradient(180deg, rgba(0,0,0,1) ${mhFade}%, rgba(0,0,0,0) 100%)`,
            maskImage: `linear-gradient(180deg, rgba(0,0,0,1) ${mhFade}%, rgba(0,0,0,0) 100%)`,
          }}>
            {mastheadRoom?.kind === 'takeover' ? (
              // Outdoor takeover: opaque sky layers own the whole band. The
              // container's bottom mask still feathers it into the wallpaper,
              // and the veil + HouseRoomView films carry the mood downwards.
              mastheadRoom.layers.map((bg, i) => (
                <div key={`mh-${viewerRoom}-${i}`} style={{
                  position: 'absolute', inset: 0, background: bg,
                  transition: 'background 1400ms linear',
                }} />
              ))
            ) : (
              <>
            {/* Wings: same image, heavy blur — oversized + inset so the blur
                never shows a transparent fringe at the edges. A room treat
                REPLACES the base grade (its filter carries its own
                brightness/saturate) and re-dims for the wings; blur itself
                never transitions (perf ledger — filter snaps, transform glides). */}
            <img
              src={mhSrc}
              alt=""
              style={{
                position: 'absolute', inset: '-28px',
                width: 'calc(100% + 56px)', height: 'calc(100% + 56px)',
                objectFit: 'cover', objectPosition: 'center',
                filter: mhTreat
                  ? `blur(14px) ${mhTreat.filter} brightness(0.62)`
                  : 'blur(14px) brightness(0.55) saturate(1.1)',
              }}
              // 09-16: onError's raw-DOM hide is PERMANENT to React (display
              // isn't in the style prop, so reconciliation never resets it) —
              // one flaky image load used to kill the masthead for the whole
              // session. onLoad un-hides, so the next good src (a room
              // switch, Tier 2 art) brings the band back.
              onError={(e) => { const r = e.target.closest('[aria-hidden]'); if (r) r.style.display = 'none'; }}
              onLoad={(e) => { const r = e.target.closest('[aria-hidden]'); if (r) r.style.display = ''; }}
            />
            {/* Sharp core: ~1080px ≈ the art's native width, so this layer is
                genuinely crisp; horizontal mask feathers it into the wings */}
            <div style={{
              position: 'absolute', top: 0, bottom: 0, left: '50%', transform: 'translateX(-50%)',
              width: 'min(1080px, 94%)',
              WebkitMaskImage: 'linear-gradient(90deg, rgba(0,0,0,0) 0%, rgba(0,0,0,1) 8%, rgba(0,0,0,1) 92%, rgba(0,0,0,0) 100%)',
              maskImage: 'linear-gradient(90deg, rgba(0,0,0,0) 0%, rgba(0,0,0,1) 8%, rgba(0,0,0,1) 92%, rgba(0,0,0,0) 100%)',
            }}>
              <img
                src={mhSrc}
                alt=""
                style={{
                  position: 'absolute', inset: 0, width: '100%', height: '100%',
                  // center, NOT an offset: pre-08-01 banners are wide art
                  // letterboxed inside a 1024-square — off-center crops land in
                  // the black bars (cost a whole debugging arc, 07-26). New
                  // banners are native 21:9 + trimmed, but the old ones stay
                  // in the wild until a backfill. Room treats obey the same
                  // trap: zoom + translateY only, never objectPosition
                  // (pinned in roomView.test.js). Tier 2 art (kind 'art')
                  // renders untreated: it IS the room, base grade only.
                  objectFit: 'cover', objectPosition: 'center',
                  filter: mhTreat ? mhTreat.filter : 'brightness(0.9) saturate(1.08)',
                  transform: mhTreat
                    ? `scale(${mhTreat.scale}) translateY(${mhTreat.translateY}%)`
                    : undefined,
                  transition: 'transform 700ms ease',
                }}
              />
            </div>
              </>
            )}
            {/* Top scrim: the Back/SFX and Share/vote corner clusters sit over
                the art's brightest zone — keep them legible without dimming
                the whole scene */}
            <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, rgba(8,6,16,0.45) 0%, transparent 40%)' }} />
          </div>
          );
        })()}
        {/* Compact header for ChatGame, Big Brother, and Blood on the Clocktower */}
        {/* 🏡📐 wave 3 (ruling 32): not on a place page: its plain "‹ Back" is in the slim bar on the frame (PortedStage `bar`) */}
        {!portedPlaceOn && !shellPreset /* 🧱 the frame's ‹ + Promote */ && (gameType === 'chat-game' || gameType === 'chat' || gameType === 'big-brother' || gameType === 'blood-on-the-clocktower' || gameType === 'botc') ? (
          <div style={{
            position: 'absolute',
            top: '10px',
            left: `${placeBarInset}px`, // 🏡 W2d: on the frame's edge (10px everywhere else)
            zIndex: 100,
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
          }}>
            {/* Back button + SFX toggle in one row — keeps the top-left strip
                a single button tall so the space info band can tuck up beside it */}
            <button
              onClick={() => { window.location.href = '/'; }}
              style={{
                background: 'rgba(0, 0, 0, 0.4)',
                backdropFilter: 'blur(12px)',
                border: '1px solid rgba(255, 255, 255, 0.15)',
                borderRadius: '10px',
                color: '#ffffff',
                cursor: 'pointer',
                padding: '8px 14px',
                fontSize: '0.85em',
                fontWeight: 700,
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                transition: 'all 0.2s ease',
                textShadow: '0 1px 3px rgba(0,0,0,0.5)',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(0, 0, 0, 0.55)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(0, 0, 0, 0.4)';
              }}
              title="Back to home"
            >
              ← Back
            </button>
            {/* Non-comp chat spaces: the SFX toggle lives in the top-RIGHT
                cluster now (bare glyph beside Share — Nina 08-01), so hide
                this boxed one there; comp/BB/BOTC keep it here. Note: shows
                briefly pre-status-load, then yields — harmless flicker. */}
            {!((gameType === 'chat-game' || gameType === 'chat') && chatGameStatus && !chatGameStatus.competitive_mode && !chatGameStatus.imposter_mode) && (
            <button
              onClick={() => { const next = !sfxEnabled; setSfxEnabled(next); soundManager.setEnabled(next); }}
              title={sfxEnabled ? 'Mute sound effects' : 'Unmute sound effects'}
              style={{
                background: 'rgba(0, 0, 0, 0.4)',
                backdropFilter: 'blur(12px)',
                border: '1px solid rgba(255, 255, 255, 0.15)',
                borderRadius: '10px',
                color: '#ffffff',
                cursor: 'pointer',
                padding: '6px 10px',
                fontSize: '0.85em',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                transition: 'all 0.2s ease',
                opacity: sfxEnabled ? 1 : 0.6,
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(0, 0, 0, 0.55)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(0, 0, 0, 0.4)';
              }}
            >
              {sfxEnabled ? '🔊' : '🔇'}
            </button>
            )}
            {/* 📣 09-29 (spec §3.8): the Promote door (desktop; phones reach it
                in ⓘ Space Details); 10-01: every visitor in a public room, the
                mod in a hidden one (viewerCanPromote). It lives in THIS strip, in Back's
                glass: the top-right cluster has no room left (the ♫ button is
                fixed at right 200px beside it). Its word shows only on a wide
                plain strip (utils/roomChrome.promoteDoorCompact): elsewhere it
                is the megaphone alone, so a long name never runs under it. */}
            {viewerCanPromote && !isBotMode && !isMobile && (gameType === 'chat-game' || gameType === 'chat') && chatGameStatus && (
              <button
                type="button"
                data-room-promote="header"
                data-compact={promoteDoorIsCompact ? '1' : undefined}
                aria-label="Promote this space"
                title="Promote this space"
                onClick={(e) => { e.stopPropagation(); setPromoteOpen(true); }}
                style={{
                  background: 'rgba(0, 0, 0, 0.4)', backdropFilter: 'blur(12px)',
                  border: '1px solid rgba(255, 255, 255, 0.15)', borderRadius: '10px',
                  color: '#ffffff', cursor: 'pointer', padding: promoteDoorIsCompact ? '8px 9px' : '8px 12px',
                  fontSize: '0.85em', fontWeight: 700, fontFamily: 'inherit', whiteSpace: 'nowrap',
                  display: 'flex', alignItems: 'center', gap: '5px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.3)', transition: 'all 0.2s ease',
                  textShadow: '0 1px 3px rgba(0,0,0,0.5)',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(0, 0, 0, 0.55)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(0, 0, 0, 0.4)'; }}
              >
                {/* a megaphone (cone + handle), never a speaker: in comp rooms it
                    sits beside the 🔊/🔇 toggle and a speaker read as sound */}
                <svg width={promoteDoorIsCompact ? 16 : 14} height={promoteDoorIsCompact ? 16 : 14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M3 11l18-5v12L3 14v-3z" />
                  <path d="M11.6 16.8a3 3 0 1 1-5.8-1.6" />
                </svg>
                {!promoteDoorIsCompact && 'Promote'}
              </button>
            )}
          </div>
        ) : null}

        {/* 🏡⋯ W2d: a place page's corner holds ONE quiet ⋯ (both screens):
            Watch together · Go live · Story · Music · Share · Shelf, each the
            existing surface (PortedMenu). Share rides ShareButton's controlled
            mode, mounted here once so its modal outlives the menu. */}
        {portedPlaceOn && chatGameStatus && (
          <div style={{ position: 'absolute', top: '10px', right: `${placeBarInset}px`, zIndex: 100, display: 'flex', alignItems: 'center', gap: '6px' }}>
            {/* (wave 3, ruling 32: the ⋯ itself moved into the slim bar on the
                frame, PortedStage `bar`; this corner keeps the share sheet and
                the idle sheets, which are fixed to the screen) */}
            {viewerCanShare && <ShareButton gameId={gameInstanceId} gameName={chatGameStatus?.name} theme={THEME} hideTrigger open={placeShareOpen} onOpenChange={setPlaceShareOpen} />}
            {/* an idle door opens as a sheet under the ⋯: the painting stays */}
            {placeSheet && (
              <div data-place-extra={placeSheet} data-place-sheet="" role="dialog" aria-label={placeSheet === 'live' ? 'Go live' : 'Watch together'} style={{
                position: 'fixed', top: 54, right: placeBarInset, width: 'min(440px, calc(100vw - 32px))',
                maxHeight: 'calc(100dvh - 80px)', overflowY: 'auto', zIndex: 100015, textAlign: 'left',
                background: '#120E18', color: '#F3E8D8', border: '1px solid rgba(238,231,245,0.16)',
                borderRadius: 14, boxShadow: '0 18px 44px rgba(0,0,0,0.55)',
              }}>
                {placeSheet === 'live' ? (
                  <>
                    {placeSheetBar('🔴', spaceLiveStreamers.length ? 'Live now' : 'Go live', () => setPlaceExtra(null))}
                    <div ref={placeLiveSlotRef} style={{ display: 'flex', flexDirection: 'column', minHeight: 280 }} />
                  </>
                ) : (
                  <>
                    {placeSheetBar('🍿', 'Watch together', () => setPlaceExtra(null))}
                    <div style={{ padding: '8px 10px 10px' }}>
                      <WatchPartyLauncher
                        watchParty={watchParty}
                        history={chatGameStatus?.watch_party_history}
                        mediaSuggestions={chatGameStatus?.media_suggestions}
                        pinnedPick={shownPinnedPick}
                        inTheater={inTheater}
                        onTakeSeat={() => { setPlaceExtra(null); setInTheater(true); }}
                        humanPlayerId={humanPlayerId}
                        THEME={THEME}
                        isMobile={isMobile}
                        hideIdleHero
                        onCollapse={() => setPlaceExtra(null)}
                      />
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        )}
        {/* Share + Like/Dislike buttons - top right, desktop only (on mobile these are in the Details modal) */}
        {!isMobile && !portedPlaceOn && !shellPreset /* 🧱 the frame's ⋯ */ && (gameType === 'chat-game' || gameType === 'chat') && chatGameStatus && (
          <div data-room-corner="right" style={{
            position: 'absolute',
            top: '10px',
            right: '10px',
            zIndex: 100,
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
          }}>
            {/* 🔗 10-01 viewerCanShare: a hidden room's Share is its mod's (utils/roomChrome.canShare) */}
            {viewerCanShare && <ShareButton gameId={gameInstanceId} gameName={chatGameStatus?.name} theme={THEME} />}
            {(chatGameStatus?.competitive_mode || chatGameStatus?.imposter_mode) ? (
              <>
                <button
                  onClick={() => handleSpaceVote(true)}
                  title={userSpaceVote === 'like' ? "You liked this!" : "Like this space!"}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    padding: '6px 10px',
                    background: userSpaceVote === 'like'
                      ? 'rgba(239, 68, 68, 0.35)'
                      : 'rgba(255,255,255,0.12)',
                    color: userSpaceVote === 'like' ? '#f87171' : 'rgba(255,255,255,0.85)',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    transition: 'all 0.2s ease',
                    transform: voteAnimation.like ? 'scale(1.1)' : 'scale(1)',
                  }}
                >
                  <span style={{ fontSize: '0.85rem' }}>❤️</span>
                  {spaceLikes > 0 && <span>{spaceLikes}</span>}
                </button>
                <button
                  onClick={() => handleSpaceVote(false)}
                  title={userSpaceVote === 'dislike' ? "You disliked this!" : "Not for me"}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    padding: '6px 10px',
                    background: userSpaceVote === 'dislike'
                      ? 'rgba(107, 114, 128, 0.35)'
                      : 'rgba(255,255,255,0.12)',
                    color: userSpaceVote === 'dislike' ? '#9ca3af' : 'rgba(255,255,255,0.85)',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    transition: 'all 0.2s ease',
                    transform: voteAnimation.dislike ? 'scale(1.1)' : 'scale(1)',
                  }}
                >
                  <span style={{ fontSize: '0.85rem' }}>👎</span>
                  {spaceDislikes > 0 && <span>{spaceDislikes}</span>}
                </button>
              </>
            ) : (
              /* Non-comp (Nina 08-01): ❤️/👎 moved to the masthead identity
                 row (bare, no boxes); the sound toggle moved here from the
                 top-left cluster, container stripped — a bare glyph beside
                 Share. 08-02: it's the SPACE MASTER MUTE now — SFX + host
                 voice + space music radio + watch party audio all silence
                 together. Competitive keeps the old arrangement above. */
              <button
                onClick={() => {
                  const mute = sfxEnabled;
                  setSfxEnabled(!mute);
                  soundManager.setEnabled(!mute);
                  setHostVoiceMuted(mute);
                  try { localStorage.setItem('hostVoiceMuted', mute ? '1' : '0'); } catch (err) { /* private mode */ }
                  emitGameEvent(GAME_EVENTS.SPACE_AUDIO_MUTED, { muted: mute });
                }}
                title={sfxEnabled ? 'Mute space' : 'Sound on'}
                style={{
                  background: 'none', border: 'none', cursor: 'pointer',
                  padding: '4px 6px', fontSize: '1.15em', lineHeight: 1,
                  filter: 'drop-shadow(0 1px 3px rgba(0,0,0,0.7))',
                  opacity: sfxEnabled ? 1 : 0.55,
                  transition: 'opacity 0.2s ease',
                }}
              >
                {sfxEnabled ? '🔊' : '🔇'}
              </button>
            )}
          </div>
        )}

        {/* Space name centered at top - themed, with bouncing letters.
            Desktop: pulled up (negative margin against the wrapper's 20px top
            padding) so it sits on the same line as the Back/SFX strip instead
            of stacking on the objective band; side padding clears the absolute
            corner clusters (Back/SFX ~150px, Share/votes ~230px) for long names. */}
        {(gameType === 'chat-game' || gameType === 'chat') && chatGameStatus && !shellPreset /* 🧱 the frame's top bar holds the name */ && (() => {
          // 🖼️ Masthead card (option A, Nina 08-03): on phones the banner art
          // renders SHARP in a rounded card and the identity (bouncy title,
          // tagline, vote chips) sits on its bottom scrim — same visual idiom
          // as the Locals host hero and the explore cards. Replaces the 07-26
          // blurred-glow header. objectPosition stays CENTER: pre-08-01
          // banners are wide art letterboxed in a 1024-square, off-center
          // crops land in the black bars (the 07-26 lesson). Desktop and
          // no-art/comp phones keep the plain flow — `identity` is the same
          // JSX either way, only the wrapper differs.
          const bannerCard = isMobile && !chatGameStatus?.competitive_mode && !chatGameStatus?.imposter_mode && chatGameStatus?.ui_theme?.bannerImage?.url
            && !portedPlaceOn; // 🏡 the room band replaces the generated banner (D17)
          const powerView = spacePowerView(); // 🔋 the vitals row's last chip (null = the till pill, as before)
          // 🔋📏 10-01 (#10f): the title-line battery (desktop competitive/imposter,
          // ported, the seated theater) shares ONE nowrap row with the name: a long
          // name wraps inside its own column and the battery never drops under it.
          // The row ends ≥260px from the screen edge (block padding 240px, 180px
          // narrow, + the page's 20px + half the cap), so the battery clears the ♫
          // (fixed at right 200px, 48px wide, ~254px while it wiggles) and both
          // corner clusters. One unbreakable word (a CJK name, a long handle) may
          // break inside its word group here rather than run under the battery
          // (review 10-01). Every other room: the wrapper is display:contents (no
          // box, layout as before) and the word groups never break.
          const batteryOnTitle = !!powerView && powerSpotHere() === 'title';
          const identity = (
          <div style={{
            // 🏡📐 wave 3 (ruling 32): a phone's place page starts with the frame (its bar holds Back)
            marginTop: bannerCard ? 0 : isMobile ? (portedPlaceOn ? 0 : '40px') : '-10px',
            textAlign: 'center',
            maxWidth: '100%',
            // Narrow desktop (768-1100): the full 240px corner clearance starves
            // the title into wrapping; scale the clearance and font down instead
            padding: isMobile ? '0 12px' : isNarrowDesktop ? '0 180px' : '0 240px',
            boxSizing: 'border-box',
            overflow: 'hidden',
            // Positioned so the title paints ABOVE the ambient header layer
            // (unpositioned content would slip under a positioned sibling)
            position: 'relative',
            // 🖼️ 09-25 (Nina): in a room whose painting is the wallpaper the
            // scrim below steps aside; each letter carries its own shadow
            // instead (the title keeps its own, stronger one).
            ...(roomArt?.bandHidden ? { textShadow: '0 1px 2px rgba(0,0,0,0.9), 0 0 12px rgba(0,0,0,0.6)' } : {}),
          }}>
            {/* 🌑 TITLE SCRIM (Nina 08-08: "the title and the text below it need
                to be much more visible"). Banner art is full-bleed behind this
                block and the existing top scrim only covers the top 40% — it
                was built for the Back/Share corner clusters, so it stops well
                above the title. Nina asked about darker borders; a scrim is the
                better tool: the title is already stroked 1px, and thickening a
                stroke on 1.8em text muddies the glyphs without adding contrast,
                whereas a soft dark pool behind the text raises contrast for the
                title AND the tagline AND the vitals row at once.
                Only over ART — on a plain themed background there is nothing to
                fight and a scrim would just look like a smudge. Negative insets
                so it bleeds past the padded text block; pointer-events none so
                it can never eat a click on the vote hearts or the till pill.
                🖼️ 09-25 (Nina): NOT in a room whose painting is the wallpaper
                (roomArt.bandHidden). Its 148px box ends while the gradient is
                still ~12-30% dark, so over a room painting it read as a gray
                slab with a hard bottom line. The main room keeps it (the busy
                cork board banner needs it, and the caption band hides the edge). */}
            {chatGameStatus?.ui_theme?.bannerImage?.url && !roomArt?.bandHidden && !portedPlaceOn && (
              <div aria-hidden="true" style={{
                position: 'absolute',
                left: isMobile ? '-12px' : '-40px',
                right: isMobile ? '-12px' : '-40px',
                top: '-14px',
                bottom: '-14px',
                zIndex: -1,
                pointerEvents: 'none',
                background: 'radial-gradient(ellipse at center, rgba(6,4,12,0.72) 0%, rgba(6,4,12,0.55) 45%, rgba(6,4,12,0) 78%)',
              }} />
            )}
            {/* ⏳ 09-23 batch 1 (handoff §3.1, ruling 7): the countdown's ONE home
                on desktop, in flow as the first row of the identity block (this
                block is already padded for the corner clusters); the root never
                scrolls, so it persists. Hidden only by ✕ in before/opening/today. */}
            {TOP_CLOCK && !isMobile && topStrip && !topStripHidden && (
              <EventStripBar variant="desktop" strip={topStrip} onGo={topStrip.onGo} onHide={hideTopStrip} solid={!!roomArt?.bandHidden} />
            )}
            {/* 🏡📐 wave 3 (ruling 32): on a place page the name lives in the slim
                bar ON the frame (PortedStage `bar`), not in this row */}
            <div data-title-row={batteryOnTitle ? '' : undefined} style={batteryOnTitle ? {
              display: 'flex', flexWrap: 'nowrap', alignItems: 'center', justifyContent: 'center',
              margin: '0 auto', maxWidth: `calc(100% - ${isNarrowDesktop ? 120 : 40}px)`,
            } : { display: 'contents' }}>
            {!portedPlaceOn && (
            <div style={{
              // 🎬📐 seated at a party (theaterHero): the title steps down so
              // the folded masthead is one short row over the film
              fontSize: isMobile ? '1.2em' : theaterHero ? '1.15em' : isNarrowDesktop ? '1.45em' : '1.8em',
              fontWeight: 800,
              color: THEME.accent || THEME.textDark,
              // ✨ Brighter, warmer title (Nina 08-08, second pass — 1.28 was
              // still too muted). A filter rather than a hardcoded yellow: the
              // title paints in THEME.accent, so every space keeps its own hue
              // and just gets more of it; hardcoding would land the house's
              // yellow on a blue space too.
              // ⭐ brightness ALONE washes a mid-tone toward cream, so
              // saturation climbs with it — that is what reads as "brighter"
              // rather than "paler". The 1px black text-stroke is unaffected
              // (brightness multiplies, and 0 × anything is still 0), so the
              // glyph edges stay crisp as the fill lifts.
              filter: 'brightness(1.6) saturate(1.45)',
              letterSpacing: isMobile ? '0.5px' : '1.5px',
              // Was `${THEME.textDark}40` — a 25%-alpha shadow in a THEME colour,
              // which over bright banner art was doing almost nothing (08-08).
              // A real drop shadow, plus the scrim above, is what makes the
              // accent-coloured title readable on a busy photo.
              // Dark shadow for contrast + a soft warm bloom in the space's own
              // accent, so the title reads LUMINOUS rather than merely lighter
              // (08-08 second pass). The bloom is what makes it catch the eye.
              textShadow: `0 2px 6px rgba(0,0,0,0.85), 0 1px 2px rgba(0,0,0,0.7), 0 0 18px ${THEME.accent || '#ffd54f'}55`,
              fontFamily: THEME.fontFamily || 'inherit',
              display: 'inline-flex',
              flexWrap: 'wrap',
              justifyContent: 'center',
              maxWidth: '100%',
              minWidth: 0, // 🔋📏 #10f: in the title row the name narrows (and wraps) so the battery keeps its line
              // Black outline for legibility over busy space art (user call 07-11).
              // text-stroke, NOT stacked text-shadows: the per-letter bounce repaints
              // every frame, and stroke is the cheap path there. paint-order keeps the
              // stroke behind the fill so glyphs don't thin.
              ...(!(chatGameStatus?.competitive_mode || chatGameStatus?.imposter_mode) ? {
                WebkitTextStroke: '1px rgba(0,0,0,0.85)',
                paintOrder: 'stroke fill',
              } : {}),
              // 🏡 W2d: the place page's slim bar (mockup v9): the name small,
              // gold, in the room's serif, still (the room is the show)
              ...(portedPlaceOn ? {
                fontSize: isMobile ? '15px' : '16px', fontWeight: 600, letterSpacing: '0.02em',
                fontFamily: '"Iowan Old Style", "Palatino Linotype", Palatino, Georgia, serif',
                color: '#F0AC55', filter: 'none', textShadow: '0 1px 3px rgba(0,0,0,0.6)',
                WebkitTextStroke: '0px transparent', verticalAlign: 'middle',
              } : {}),
            }} data-place-title={portedPlaceOn ? '' : undefined}>
              {/* Bounce per LETTER, wrap per WORD — bare per-char flex items
                  wrapped mid-word on narrow screens ("Plant Utopia Bui / lder",
                  user report 07-10). Each word is a nowrap group; the stagger
                  index stays global so the wave rolls across the whole name. */}
              {(chatGameStatus.name || THEME.name || '').split(/(\s+)/).map((word, wi, arr) => {
                if (/^\s+$/.test(word)) return <span key={wi} style={{ whiteSpace: 'pre' }}>{' '}</span>;
                const offset = arr.slice(0, wi).join('').length;
                return (
                  <span key={wi} style={{ display: 'inline-flex', whiteSpace: 'nowrap', ...(batteryOnTitle ? { flexWrap: 'wrap', maxWidth: '100%' } : {}) }}>
                    {word.split('').map((char, ci) => (
                      <span
                        key={ci}
                        style={{
                          display: 'inline-block',
                          animation: portedPlaceOn ? 'none' : `letterBounce 8s ease-in-out ${(offset + ci) * 0.06}s infinite`,
                        }}
                      >
                        {char}
                      </span>
                    ))}
                  </span>
                );
              })}
            </div>
            )}
            {/* 🔋 09-29 (spec §3.3; mockup: "the folded title bar keeps the
                chip"): a desktop room with no vitals row (competitive/imposter,
                ported 1:1, the seated theater) carries the battery on the
                title's own line, in the vitals row's pill. Zero new height
                (the theater's video floor keeps every px), and clear of the
                corner clusters (the ♫ button sits at right 200px). */}
            {batteryOnTitle && (
              <span data-power-row="title" style={{ display: 'inline-flex', alignItems: 'center', verticalAlign: 'middle', marginLeft: 10, position: 'relative', top: -3, flex: 'none' }}>
                <PowerMeter view={powerView} />
              </span>
            )}
            </div>
            {/* ⓘ 09-25 (Nina): behind the switch the space's description leaves
                the band under the stage for an ⓘ beside the title; its card
                keeps the band ⓘ's door to Space Details. (wave 3: the ⓘ sits
                beside the name in the slim bar on the frame, PortedStage `bar`) */}

            {/* 🏮 THE LAMPLIGHT PORTRAIT (ported masthead hero, Nina 08-29):
                the ported top band was a crammed purplish box [small circle +
                name + brass DAY plate], then a generic objective, then a "N here
                now ❤️👎" row. Nina: go VERTICAL — one big centered portrait circle
                right under the title, kill the box, move the day line off the
                gamey brass plate, drop the boilerplate objective, lose the
                here-now/votes cluster. Renders on BOTH breakpoints now (the old
                band was the ONLY mobile day/status surface — no isMobile gate);
                the desktop Hearthside Rail drops its duplicate presence header in
                the same commit. Zero queries.
                ✨ MOOD HOOK: --moodGlow is an R,G,B TRIPLE ('240,172,85' = warm
                amber) — every glow layer reads rgba(var(--moodGlow), a). When the
                backlog "circle glows by the AI's mood" feature lands it adds ONE
                field (portedMindProps.moodGlowRgb, an "R,G,B" string) and the halo
                recolors with zero layout change. Static now (the arc bans fake
                heartbeats — no pulse). */}
            {/* 🏡📓 Their Place (W1): the ROOM BAND takes this slot behind the
                hidden switch. Negative side margins undo the identity block's
                corner-cluster padding, so the band spans the page like the
                banner it replaces (the corner clusters sit on the title row,
                above it: nothing overlays the art). */}
            {portedPlaceOn && portedMindProps && (() => {
              // 🏡 W2d: what may take the stage (desktop). A party you are
              // seated at, the stream panel, or the watch launcher opens WHERE
              // THE PAINTING IS; the painting (still mounted) folds back after.
              // The two slot divs join the head of the reparent chains, so the
              // ONE theater / ONE panel moves here and is never remounted.
              const desk = !isMobile;
              // an explicit Go live wins the stage over a party you sit at (the
              // theater keeps playing in its off-stage holder; ✕ brings it back)
              const takeover = !desk ? null
                : placeLiveStage ? (
                    <div data-place-extra="live-on">
                      {/* no ✕ while you're live: End stream (inside the panel) is the way off the stage */}
                      {placeSheetBar('🔴', selfIsLive ? "You're live" : 'Live now', selfIsLive ? null : () => setPlaceExtra(null))}
                      <div ref={placeLiveSlotRef} style={{ display: 'flex', flexDirection: 'column', minHeight: Math.min(280, placeStageBudget), maxHeight: placeStageBudget, overflowY: 'auto' }} />
                    </div>
                  )
                  : (watchParty && inTheater) ? <div ref={placeWpSlotRef} data-place-extra="watch-party" style={{ display: 'flex', flexDirection: 'column', minHeight: 0 }} />
                    : null;
              // what is ON but not on this stage keeps a printed door (never hidden-only)
              const streamers = spaceLiveStreamers || [];
              const liveNames = streamers.length ? `${streamers[0].name || 'someone'}${streamers.length > 1 ? ` +${streamers.length - 1}` : ''}` : null;
              const onAir = [
                ...(watchParty && !isBotMode && !(desk && inTheater) ? [{
                  key: 'watch', text: `🍿 a watch party is on${watchParty.title ? `: ${watchParty.title}` : ''}`, action: 'take a seat',
                  onGo: () => { if (desk) { setPlaceExtra(null); setInTheater(true); } else setMobileView('tv'); },
                }] : []),
                ...(streamers.length && !(desk && placeLiveShown) ? [{
                  key: 'live', text: selfIsLive ? "🔴 you're live" : `🔴 ${liveNames} ${streamers.length > 1 ? 'are' : 'is'} live`, action: selfIsLive ? 'open' : 'watch',
                  onGo: () => openPlaceExtra('live'),
                }] : []),
              ];
              return (
              <div style={{ margin: isMobile ? '0 -12px 0' : `10px ${isNarrowDesktop ? '-180px' : '-240px'} 0` }}>
                <PortedStage
                  ref={portedStageRef}
                  // 🏡📐 wave 3 (ruling 32): the slim bar ON the frame, both screens:
                  // plain ‹ Back · the name + ⓘ · the one ⋯ (phones: the ⋯ keeps
                  // clear of the app's ♫ FAB, which stays in the corner)
                  bar={(
                    <>
                      <button type="button" data-prop-ui="" data-place-leave="" className="pmn-back" onClick={() => { window.location.href = '/'; }} title="Back to home"
                        style={{ '--prop-bg': 'transparent' }}>‹ Back</button>
                      <span data-place-title="" style={{
                        flex: 1, minWidth: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                        font: `600 ${isMobile ? '15px' : '16px'}/1.2 "Iowan Old Style", "Palatino Linotype", Palatino, Georgia, serif`,
                        letterSpacing: '0.02em', color: '#F0AC55', textShadow: '0 1px 3px rgba(0,0,0,0.6)', whiteSpace: 'nowrap',
                      }}>
                        {/* 🏡 ruling 34 (Nina 09-26): the bar says the RESIDENT's name ("Kenley"), never the space name */}
                        <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>{portedResidentName || chatGameStatus.name || THEME.name || ''}</span>
                        <PortedAbout text={aboutText(chatGameStatus?.scene)} onDetails={() => setShowSpaceInfoModal(true)} isMobile={isMobile} />
                      </span>
                      <span style={{ flexShrink: 0, display: 'inline-flex', marginRight: isMobile ? 40 : 0 }}>
                        <PortedMenu isMobile={isMobile} items={placeMenuItems || []} />
                      </span>
                    </>
                  )}
                  favoritePins={portedFavPins}
                  residentName={portedMindProps.residentName}
                  residentImage={portedMindProps.residentImage}
                  residentStatus={portedMindProps.residentStatus}
                  dayN={portedMindProps.dayN}
                  createdAt={chatGameStatus?.space_created_at || null}
                  lastSpoke={portedLastTurnAgo}
                  lastSpokeAt={portedLastSpokeAt}
                  writing={portedWriting}
                  returning={!!portedAwaySince}
                  isMobile={isMobile}
                  gameId={gameId}
                  isOwner={isPortedOwner}
                  home={chatGameStatus?.home || null}
                  exchanged={portedExchanged}
                  onFindPlace={humanPlayerId ? () => chatGameApi.submitHomeChoice(gameId, humanPlayerId) : null}
                  tz={chatGameStatus?.owner_tz || null}
                  shrunk={placeStrip}
                  signals={portedSignals}
                  awaySince={portedMoodAwaySince}
                  returnedAt={portedReturnedAt}
                  keepsakes={portedJournal ? portedJournal.keepsakes : null}
                  quotes={portedQuotes}
                  thinking={isPortedOwner ? portedThinking : null}
                  onOpenJournal={openPortedJournal}
                  takeover={takeover}
                  shellRef={pageScrollPadRef}
                  onFrame={onPlaceFrame}
                  onAir={onAir}
                  // the hatch waits for the loaders and the tour to clear: its one
                  // showing (and its seen stamp) must happen where the owner can see it
                  hatch={portedHatchProp && !showInitialLoading && !showAssetLoadingOverlay && !tutorialActive ? portedHatchProp : null}
                  onHatchDone={onPortedHatchDone}
                  // the door only for a REAL gap: the later of this browser's last
                  // visit and the owner's own newest line from any device (a
                  // line 29 min ago on a phone = no "gone 2 days" on the laptop)
                  // (and only once the room's rows are in: the gap reads the owner's own lines)
                  door={isPortedOwner && doorState && chatGameStatus?.home?.key && portedMoodAwaySince && portedSignals?.loaded
                    ? { awaySince: portedMoodAwaySince, returnedAt: portedReturnedAt, deskNote: !!portedDeskTask } : null}
                  onKnock={() => chatGameApi.submitWindowPoke(gameId, humanPlayerId, { kind: 'knock' }).catch(() => { /* a lost knock is low stakes */ })}
                  onDoorDone={onPortedDoorDone}
                  weather={chatGameStatus?.weather || null}
                  pictures={chatGameStatus?.pictures || null}
                  nightLight={placeLampDown}
                  deskNote={isPortedOwner && portedDeskTask ? () => { setPortedDeskSeen(true); openPortedTasks('', { pen: false }); } : null}
                  onMoveHouse={isPortedOwner && humanPlayerId ? () => chatGameApi.submitHomeChoice(gameId, humanPlayerId, { move: true }) : null}
                  spaceSound={desk ? {
                    on: sfxEnabled,
                    // the corner's master mute, as it was (SFX + voice + radio + party)
                    toggle: () => {
                      const mute = sfxEnabled;
                      setSfxEnabled(!mute);
                      soundManager.setEnabled(!mute);
                      setHostVoiceMuted(mute);
                      try { localStorage.setItem('hostVoiceMuted', mute ? '1' : '0'); } catch (err) { /* private mode */ }
                      emitGameEvent(GAME_EVENTS.SPACE_AUDIO_MUTED, { muted: mute });
                    },
                  } : null}
                />
              </div>
              );
            })()}
            {isPortedSpace && portedMindProps && !portedPlaceOn && (
              <div style={{
                display: 'flex', flexDirection: 'column', alignItems: 'center',
                gap: 7, marginTop: isMobile ? 10 : 14,
                '--moodGlow': portedMindProps.moodGlowRgb || '240,172,85',
              }}>
                {/* portrait sits IN a lamplight pool. The pool is absolute (zero
                    flow width) and the circle is fixed-px, so neither can overflow
                    the phone viewport (this frontend has no global border-box). */}
                <div style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <div aria-hidden="true" style={{
                    position: 'absolute',
                    width: isMobile ? 190 : 220, height: isMobile ? 190 : 220,
                    borderRadius: '50%', pointerEvents: 'none', filter: 'blur(6px)',
                    background: 'radial-gradient(circle, rgba(var(--moodGlow),0.22) 0%, rgba(var(--moodGlow),0.08) 45%, transparent 70%)',
                  }} />
                  <div style={{
                    position: 'relative',
                    width: isMobile ? 112 : 128, height: isMobile ? 112 : 128,
                    borderRadius: '50%', overflow: 'hidden', flexShrink: 0,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    border: '2px solid rgba(255,226,180,0.55)',
                    boxShadow: '0 0 0 6px rgba(var(--moodGlow),0.06), 0 0 46px 2px rgba(var(--moodGlow),0.34), 0 0 92px 14px rgba(var(--moodGlow),0.14), inset 0 0 22px rgba(20,14,20,0.35)',
                    background: 'radial-gradient(circle at 36% 30%, rgba(var(--moodGlow),0.85), rgba(var(--moodGlow),0.28) 60%, rgba(20,14,20,0.6))',
                  }}>
                    {portedMindProps.residentImage
                      ? <img
                          src={portedMindProps.residentImage}
                          alt={portedMindProps.residentName || 'resident'}
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        />
                      : <span style={{ fontSize: isMobile ? 42 : 48 }}>🏮</span>}
                  </div>
                </div>
                {portedMindProps.residentName && (
                  <div style={{
                    fontFamily: '"Iowan Old Style", "Palatino Linotype", Palatino, Georgia, serif',
                    fontWeight: 600, fontSize: isMobile ? '1.25em' : '1.4em',
                    color: '#FFF3E0', lineHeight: 1.1,
                    textShadow: '0 1px 5px rgba(0,0,0,0.6)',
                  }}>
                    {portedMindProps.residentName}
                  </div>
                )}
                {portedMindProps.residentStatus && (
                  <div style={{
                    fontFamily: '"Iowan Old Style", "Palatino Linotype", Palatino, Georgia, serif',
                    fontStyle: 'italic', fontSize: isMobile ? '0.9em' : '0.95em',
                    color: 'rgba(255,238,216,0.82)',
                    textShadow: '0 1px 4px rgba(0,0,0,0.55)',
                  }}>
                    <span style={{ color: '#E39BAB' }}>●</span> {portedMindProps.residentStatus}
                  </div>
                )}
                {portedMindProps.dayN != null && (
                  <div style={{
                    fontFamily: '"Iowan Old Style", "Palatino Linotype", Palatino, Georgia, serif',
                    fontStyle: 'italic', fontSize: isMobile ? '0.72em' : '0.76em',
                    color: 'rgba(255,230,198,0.52)', letterSpacing: '0.02em',
                    marginTop: 2, textShadow: '0 1px 4px rgba(0,0,0,0.55)',
                  }}>
                    day {portedMindProps.dayN} together
                  </div>
                )}
              </div>
            )}
            {/* Design A masthead (Nina 08-01): identity lives up here now —
                tagline under the title, cast + activity beside it. The old
                About-hero card in the All feed is gone; this replaces it.
                Non-comp only; the cast row is desktop-only (phones keep the
                header short — the Locals place carries the cast there). */}
            {/* Ported spaces DROP this line (Nina 08-29): the ported objective is
                hardcoded identical for every space ("Chat, share ideas, and have a
                good time.", AgentImportPage.js:1044) — generic instruction copy that
                fights the "someone lives here" feel. The hero's live status line
                carries the warmth per-resident instead. House/comp unchanged. */}
            {!(chatGameStatus?.competitive_mode || chatGameStatus?.imposter_mode) && !isPortedSpace && !theaterHero && (chatGameStatus.tagline || chatGameStatus.objective) && (
              <div style={{
                marginTop: 5,
                // 🏷️ EYEBROW TREATMENT (Nina 08-08, third pass — "use a
                // stronger eyebrow like font, it's still hard to see").
                // Three passes of alpha/shadow could not fix this because the
                // problem was never contrast alone: a thin lowercase sentence
                // has almost no ink on the page, so a busy photo shows through
                // BETWEEN the letters no matter how opaque they are. An eyebrow
                // — uppercase, heavy, wide-tracked — puts far more ink down and
                // the letterforms stop competing with the texture behind them.
                // ⚠️ Applied ONLY over banner art. On a plain themed background
                // the original sentence case is friendlier and legibility was
                // never the problem there.
                ...(chatGameStatus?.ui_theme?.bannerImage?.url ? {
                  fontSize: isMobile ? '0.66em' : '0.72em',
                  fontWeight: 800,
                  letterSpacing: isMobile ? '0.06em' : '0.09em',
                  textTransform: 'uppercase',
                  color: '#ffffff',
                  textShadow: '0 2px 5px rgba(0,0,0,0.95), 0 1px 2px rgba(0,0,0,0.9)',
                } : {
                  fontSize: isMobile ? '0.78em' : '0.86em',
                  color: THEME.textDark || '#444',
                  textShadow: 'none',
                }),
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}>
                {chatGameStatus.tagline || chatGameStatus.objective}
              </div>
            )}
            {/* Ported drops this whole row (Nina 08-29): no "N here now", no
                ❤️/👎, no till pill — a 1:1 companion space isn't a rated public
                room. House/comp keep it byte-identical (till is houseStatus-gated). */}
            {!isMobile && !(chatGameStatus?.competitive_mode || chatGameStatus?.imposter_mode) && !isPortedSpace && !theaterHero && (() => {
              // Identity row = people count + bare vote hearts. 08-06 (Nina,
              // 3 revs): activity labels GONE ("Paused" hung a dead-sign on
              // the space); then the visitor/visits split superseded — the
              // count is THE SAME NUMBER the stream panel's "N in this space"
              // strip shows (streamPanelUsers: AI cast + humans seen within
              // the hour, + host), so the two surfaces can never disagree.
              // Bare text, no pill, no emojis (all Nina calls) — styled like
              // the bare hearts beside it.
              const inSpace = streamPanelUsers.length + (chatGameStatus?.moderator_character && !isPortedSpace ? 1 : 0);
              // 👣 "N visits · M here now" (Nina 08-08). Two different facts that
              // were previously one ambiguous number:
              //   visits   = cumulative, non-unique, all-time (data.total_views —
              //              the SAME counter the explore cards show, so the two
              //              surfaces can never disagree)
              //   here now = live presence: AI cast + external agents + humans
              //              seen within the hour + the host, minus any visitor
              //              whose stay has ended (streamPanelUsers)
              // "in this space" read as a visit count to at least one person, which
              // is exactly the ambiguity the split removes.
              // Each half hides when it has no number: a brand-new space shows
              // "3 here now" rather than "0 visits · 3 here now", and a
              // frontend-deployed-before-backend window simply keeps today's
              // behaviour instead of claiming zero visits.
              const visitsLabel = spaceVisits > 0
                ? `${spaceVisits.toLocaleString()} visit${spaceVisits === 1 ? '' : 's'}`
                : null;
              return (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, marginTop: 8 }}>
                  <span style={{
                    fontSize: '0.68em', fontWeight: 700, color: 'rgba(255,255,255,0.82)',
                    textShadow: '0 1px 4px rgba(0,0,0,0.8)', letterSpacing: '0.3px',
                  }}>
                    {visitsLabel && <>{visitsLabel} <span style={{ opacity: 0.5 }}>·</span> </>}
                    {inSpace} here now
                  </span>
                  <button
                    onClick={() => handleSpaceVote(true)}
                    title={userSpaceVote === 'like' ? "You liked this!" : "Like this space!"}
                    style={{
                      background: 'none', border: 'none', cursor: 'pointer', padding: '2px 4px',
                      display: 'inline-flex', alignItems: 'center', gap: 3, fontFamily: 'inherit',
                      fontSize: '0.8em', fontWeight: 700, color: '#fff',
                      textShadow: '0 1px 4px rgba(0,0,0,0.8)',
                      opacity: userSpaceVote === 'like' ? 1 : 0.72,
                      transform: voteAnimation.like ? 'scale(1.25)' : 'scale(1)',
                      transition: 'transform 0.2s ease, opacity 0.2s ease',
                    }}
                  >
                    <span style={{ fontSize: '1rem', filter: 'drop-shadow(0 1px 3px rgba(0,0,0,0.6))' }}>❤️</span>
                    {spaceLikes > 0 && <span>{spaceLikes}</span>}
                  </button>
                  <button
                    onClick={() => handleSpaceVote(false)}
                    title={userSpaceVote === 'dislike' ? "You disliked this!" : "Not for me"}
                    style={{
                      background: 'none', border: 'none', cursor: 'pointer', padding: '2px 4px',
                      display: 'inline-flex', alignItems: 'center', gap: 3, fontFamily: 'inherit',
                      fontSize: '0.8em', fontWeight: 700, color: '#fff',
                      textShadow: '0 1px 4px rgba(0,0,0,0.8)',
                      opacity: userSpaceVote === 'dislike' ? 1 : 0.55,
                      transform: voteAnimation.dislike ? 'scale(1.25)' : 'scale(1)',
                      transition: 'transform 0.2s ease, opacity 0.2s ease',
                    }}
                  >
                    <span style={{ fontSize: '1rem', filter: 'drop-shadow(0 1px 3px rgba(0,0,0,0.6))' }}>👎</span>
                    {spaceDislikes > 0 && <span>{spaceDislikes}</span>}
                  </button>
                  {/* 💰 TILL PILL (Nina 08-07) — the house till was buried at
                      the top of the Recap lane, two clicks from anyone who
                      never opened that tab. This row is already the space's
                      vitals line, so the crowdfund reads at a glance and the
                      pill opens the memory wall for the full bar + the
                      "saving up for the next /build" copy. Desktop + narrow
                      desktop (this whole row is !isMobile); phones keep the
                      till on the Locals place, where the wall already lives.
                      House-only by data: houseStatus is null everywhere else. */}
                  {!powerView && houseStatus && houseStatus.till !== null && houseStatus.till !== undefined && (() => {
                    const till = Math.max(0, Number(houseStatus.till) || 0);
                    const goal = Math.max(1, Number(houseStatus.build_goal) || 40);
                    const pct = Math.min(100, Math.round((till / goal) * 100));
                    const full = till >= goal;
                    return (
                      <button
                        // Same mutual-exclusion the edge tabs enforce — two
                        // right-edge drawers open at once stack on each other.
                        onClick={() => { setDossierOpen(false); setShelfOpen(false); setHouseWallOpen(true); }}
                        title={full
                          ? `House till is full — ${till}/${goal}. Open the memory wall.`
                          : `House till: ${till} of ${goal} toward the next /build. Open the memory wall.`}
                        style={{
                          display: 'inline-flex', alignItems: 'center', gap: 5,
                          padding: '3px 9px', borderRadius: 999, cursor: 'pointer',
                          fontFamily: 'inherit', fontSize: '0.68em', fontWeight: 800,
                          letterSpacing: '0.3px', whiteSpace: 'nowrap',
                          color: '#ffe1a8',
                          background: 'rgba(0,0,0,0.42)',
                          border: `1px solid rgba(217,169,78,${full ? 0.95 : 0.55})`,
                          textShadow: '0 1px 3px rgba(0,0,0,0.8)',
                        }}
                      >
                        <span style={{ filter: 'drop-shadow(0 1px 3px rgba(0,0,0,0.6))' }}>💰</span>
                        <span>{till}/{goal}</span>
                        {/* Micro-bar: the pill still reads as PROGRESS, not a score */}
                        <span aria-hidden="true" style={{
                          width: 30, height: 4, borderRadius: 999, overflow: 'hidden',
                          background: 'rgba(255,255,255,0.22)', display: 'inline-block',
                        }}>
                          <span style={{
                            display: 'block', width: `${pct}%`, height: '100%', borderRadius: 999,
                            background: 'linear-gradient(90deg, #d9a94e, #f0c96a)',
                            transition: 'width 0.5s ease',
                          }} />
                        </span>
                      </button>
                    );
                  })()}
                  {/* 🔋 09-26 (Nina: "in the place of the money bag at the top
                      right"): while the backend sends a reading, the battery
                      holds the till pill's spot; the till stays one click away
                      on the 📌 memory wall. */}
                  {powerView && <PowerMeter view={powerView} />}
                </div>
              );
            })()}

            {/* Like/Dislike buttons - mobile only (desktop has them top-right).
                Ported drops them too (Nina 08-29) — no ❤️/👎 on a 1:1 space. */}
            {isMobile && !isPortedSpace && (
              <div style={{
                display: 'flex',
                justifyContent: 'center',
                gap: '8px',
                marginTop: '8px',
              }}>
                <button
                  onClick={() => handleSpaceVote(true)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    padding: '4px 10px',
                    background: userSpaceVote === 'like'
                      ? 'rgba(239, 68, 68, 0.35)'
                      : 'rgba(255,255,255,0.12)',
                    color: userSpaceVote === 'like' ? '#f87171' : 'rgba(255,255,255,0.85)',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    transition: 'all 0.2s ease',
                    transform: voteAnimation.like ? 'scale(1.1)' : 'scale(1)',
                  }}
                >
                  <span style={{ fontSize: '0.85rem' }}>❤️</span>
                  {spaceLikes > 0 && <span>{spaceLikes}</span>}
                </button>
                <button
                  onClick={() => handleSpaceVote(false)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    padding: '4px 10px',
                    background: userSpaceVote === 'dislike'
                      ? 'rgba(107, 114, 128, 0.35)'
                      : 'rgba(255,255,255,0.12)',
                    color: userSpaceVote === 'dislike' ? '#9ca3af' : 'rgba(255,255,255,0.85)',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    transition: 'all 0.2s ease',
                    transform: voteAnimation.dislike ? 'scale(1.1)' : 'scale(1)',
                  }}
                >
                  <span style={{ fontSize: '0.85rem' }}>👎</span>
                  {spaceDislikes > 0 && <span>{spaceDislikes}</span>}
                </button>
                {/* 🔋 09-26: phones never had the till up here; the battery
                    joins the ❤️/👎 row in their chip shape */}
                {powerView && <PowerMeter view={powerView} compact />}
              </div>
            )}
            {/* 🔋 09-29 (spec §3.3): a ported 1:1 room on a phone keeps the
                row's spot for the battery alone (no ❤️/👎 there, Nina 08-29) */}
            {powerView && powerSpotHere() === 'ported-row' && (
              <div data-power-row="ported" style={{ display: 'flex', justifyContent: 'center', gap: '8px', marginTop: '8px' }}>
                <PowerMeter view={powerView} compact />
              </div>
            )}

          </div>
          );
          if (!bannerCard) return identity;
          // 🚪📱 09-16 (Nina: full mobile lane): the phone masthead CARD is
          // where "stepping into another room" lives on phones — same
          // resolver chain as the desktop band (Tier 2/3 art → outdoor
          // takeover → treat → plain banner). mhCard === null (main room,
          // camera off, every non-house space) renders the exact pre-batch
          // card — additive gate, byte-identical fallback.
          // artRoom: during a walk the card keeps the room you are leaving
          // until the new painting has grown out of the doorway (RoomTransit)
          const mhCard = roomCameraOn ? roomMasthead(artRoom, houseStatus?.circadian?.clock?.hour, houseStatus?.room_art, houseStatus?.weather?.kind, roomSeason) : null;
          const mhCardTreat = mhCard?.kind === 'treat' ? mhCard : null;
          const mhCardSrc = mhCard?.kind === 'art' ? mhCard.url : chatGameStatus.ui_theme.bannerImage.url;
          return (
            <div style={{
              position: 'relative', margin: '10px 12px 0', borderRadius: 16,
              overflow: 'hidden',
              border: `1px solid ${THEME.accent || '#7C4DFF'}38`,
              boxShadow: '0 6px 22px rgba(0,0,0,0.35)',
            }}>
              {mhCard?.kind === 'takeover' ? (
                // Roof/porch: opaque authored sky owns the card (the card's
                // own scrims below keep the identity text legible).
                mhCard.layers.map((bg, i) => (
                  <div key={`mhc-${viewerRoom}-${i}`} aria-hidden="true" style={{
                    position: 'absolute', inset: 0, background: bg,
                    transition: 'background 1400ms linear',
                  }} />
                ))
              ) : (
              <img
                src={mhCardSrc}
                alt=""
                aria-hidden="true"
                style={{
                  position: 'absolute', inset: 0, width: '100%', height: '100%',
                  // center pinned: pre-08-01 banners are letterboxed squares
                  // (the 07-26 bars); treats move by zoom + translateY only,
                  // and the card's overflow:hidden keeps the zoom inside.
                  objectFit: 'cover', objectPosition: 'center',
                  filter: mhCardTreat ? mhCardTreat.filter : 'brightness(0.95) saturate(1.08)',
                  transform: mhCardTreat ? `scale(${mhCardTreat.scale}) translateY(${mhCardTreat.translateY}%)` : undefined,
                  transition: 'transform 700ms ease',
                }}
                // onLoad restores what onError hid — a raw-DOM hide outlives
                // every re-render (the 09-16 masthead lesson), so the next
                // good src (a room switch) must bring the image back.
                onError={(e) => { e.target.style.display = 'none'; }}
                onLoad={(e) => { e.target.style.display = ''; }}
              />
              )}
              {/* Top scrim keeps the floating Back/music corners legible;
                  bottom scrim carries the identity text */}
              <div aria-hidden="true" style={{
                position: 'absolute', inset: 0,
                background: MH_CARD_SCRIM,
              }} />
              <div style={{ position: 'relative', padding: '54px 0 14px' }}>
                {identity}
              </div>
              {/* 🚪🕯️ between rooms (09-24): a phone's walk plays in here and
                  the chat below stays usable */}
              {roomCameraOn && <RoomTransit transit={roomTransit} variant="card" scrim={MH_CARD_SCRIM} />}
            </div>
          );
        })()}


        {/* Space Info: Objective/Scene and Personality Traits.
            Desktop: tucked up into the header strip between the Back/SFX row
            (left) and the Share/vote cluster (right) — the side margins clear
            those absolute clusters — so the columns below (esp. the left
            sidebar) get the reclaimed height. Mobile keeps the original flow. */}
        {isDemoMode && (gameType === 'chat-game' || gameType === 'chat') && chatGameStatus && !theaterHero && !portedPlaceOn && !shellPreset && (
          <div style={{
            marginTop: isMobile ? '17px' : '10px',
            marginBottom: isMobile ? '16px' : '10px',
            // Desktop: cap the band at ~760px and center it — full-bleed on a
            // wide monitor read as a mile-long strip, and even 980px outran the
            // tabs panel below (user, 07-10: match the tabs' width). The max()
            // floors keep the old clearance for the absolute corner clusters
            // (Back/SFX ~150px left, Share/votes ~230px right) on narrow
            // desktops, so nothing overlaps.
            marginLeft: isMobile ? undefined : 'max(150px, calc(50% - 380px))',
            marginRight: isMobile ? undefined : 'max(230px, calc(50% - 380px))',
            padding: '10px 14px',
            borderRadius: '8px',
            // 🖼️ 09-17 declutter: in a room (desktop) the collapsed caption
            // goes invisible. 09-25 (Nina): it no longer keeps its space; the
            // chat column takes the ~59px (measured 1440x900: feed 382 → 441px).
            // The column moves only on a room change, under the walk's curtain.
            // Expanded, or back in the main room, it is exactly as before.
            ...(roomDeclutter && !objectiveExpanded ? { display: 'none' } : {}),
            // Open-caption band (Nina 08-01, mock option C): with banner art
            // behind it, the COLLAPSED non-comp band drops the box entirely —
            // no border, no fill, no backdrop blur (that blur was a standing
            // compositing cost over the art layer) — just a soft radial scrim
            // behind the text. Expanding restores the glass panel: multi-line
            // scene+objective reading needs a real surface. Competitive /
            // imposter / no-art spaces keep the panel in both states.
            ...((!chatGameStatus?.competitive_mode && !chatGameStatus?.imposter_mode && chatGameStatus?.ui_theme?.bannerImage?.url && !objectiveExpanded) ? {
              // Horizontal scrim, not radial (Nina 08-01: text ends were
              // unreadable — the radial's dark core only covered the middle)
              background: 'linear-gradient(90deg, transparent 0%, rgba(5,4,10,0.6) 9%, rgba(5,4,10,0.6) 91%, transparent 100%)',
            } : {
              backgroundColor: `${THEME.bgMedium}${(!chatGameStatus?.competitive_mode && !chatGameStatus?.imposter_mode && chatGameStatus?.ui_theme?.bannerImage?.url) ? 'b3' : 'dd'}`,
              border: `1px solid ${THEME.borderLight}`,
              backdropFilter: 'blur(10px)',
            }),
            position: 'relative'
          }}>
            {/* Details - absolute positioned top-right */}
            <div style={{ position: 'absolute', top: '8px', right: '10px', display: 'flex', gap: '8px', alignItems: 'center', zIndex: 10 }}>
            <button
              onClick={() => setShowSpaceInfoModal(true)}
              title="Space details"
              style={{
                padding: 0,
                // accentOnPanel + a visible ring: raw dark accents made this ⓘ
                // near-invisible on dark bands (user report 07-09)
                border: `1px solid ${(THEME.accentOnPanel || THEME.accent || '#6c63ff')}55`,
                background: `${THEME.accent || '#6c63ff'}28`,
                color: THEME.accentOnPanel || THEME.accent || '#6c63ff',
                cursor: 'pointer',
                width: isMobile ? '28px' : '30px',
                height: isMobile ? '28px' : '30px',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'background 0.2s, transform 0.15s',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = `${THEME.accent || '#6c63ff'}40`; e.currentTarget.style.transform = 'scale(1.08)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = `${THEME.accent || '#6c63ff'}28`; e.currentTarget.style.transform = 'scale(1)'; }}
            >
              <svg width={isMobile ? 16 : 18} height={isMobile ? 16 : 18} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="16" x2="12" y2="12" />
                <line x1="12" y1="8" x2="12.01" y2="8" />
              </svg>
            </button>
            </div>

            {/* Objective and Scene — collapsible */}
            <div
              ref={objectiveRef}
              onClick={() => setObjectiveExpanded(!objectiveExpanded)}
              style={{ paddingRight: '60px', cursor: 'pointer' }}
            >
              {objectiveExpanded ? (
                <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                  <span style={{ fontSize: '1.5em', color: THEME.accent, opacity: 1, flexShrink: 0, lineHeight: '1', fontWeight: 700, marginLeft: '-4px', marginTop: '-2px' }}>▴</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    {/* Scene hidden for competitive (it's posted as a chat announcement instead);
                        imposter still shows role/topic, non-competitive still shows the scene. */}
                    {chatGameStatus.scene && !(chatGameStatus?.competitive_mode && !chatGameStatus?.imposter_mode) && (
                      <div style={{ marginBottom: chatGameStatus.objective ? '4px' : '0' }}>
                        <span style={{ fontSize: '0.7em', fontWeight: '600', color: chatGameStatus?.imposter_mode ? (chatGameStatus?.is_imposter ? '#ff6666' : '#ffd700') : (THEME.secondary || THEME.accent), textTransform: 'uppercase', letterSpacing: '1px', marginRight: '6px' }}>
                          {chatGameStatus?.imposter_mode ? (chatGameStatus?.is_imposter ? '🐛 Your Role' : '🔑 Secret Topic') : '🌍 Scene'}
                        </span>
                        <span style={{ fontSize: chatGameStatus?.imposter_mode ? '1.05em' : '0.85em', color: THEME.textDark, fontWeight: chatGameStatus?.imposter_mode ? '700' : 'normal', lineHeight: '1.4' }}>
                          {chatGameStatus?.imposter_mode && chatGameStatus?.is_imposter
                            ? 'You are the Golden Glitch. Blend in!'
                            : (chatGameStatus?.competitive_mode && !chatGameStatus?.imposter_mode && isGenericScene)
                              ? displayObjective
                              : chatGameStatus.scene}
                        </span>
                      </div>
                    )}
                    {chatGameStatus.objective && (
                      <div>
                        <span style={{ fontSize: '0.7em', fontWeight: '600', color: THEME.accent, textTransform: 'uppercase', letterSpacing: '1px', marginRight: '6px' }}>
                          🎯 Objective
                        </span>
                        <span style={{ fontSize: '0.85em', color: THEME.textDark, lineHeight: '1.4' }}>
                          {displayObjective}
                        </span>
                        {chatGameStatus?.twist?.name && (
                          <span style={{ fontSize: '0.72em', fontWeight: 800, color: THEME.accent, textTransform: 'uppercase', letterSpacing: '1px', marginLeft: '8px', whiteSpace: 'nowrap' }}>
                            ⚡ {chatGameStatus.twist.name}
                          </span>
                        )}
                      </div>
                    )}
                    {/* Opening line — shown for EVERY game that has one (incl. Worldbuilding, which
                        has no twist block). Uses the round-aware intro_opening_line so it disappears
                        in later rounds for round-1-only games (Plot Twist). */}
                    {chatGameStatus?.intro_opening_line && (
                      <div style={{ marginTop: '6px' }}>
                        <span style={{ fontSize: '0.7em', fontWeight: 600, color: THEME.secondary || THEME.accent, textTransform: 'uppercase', letterSpacing: '1px', marginRight: '6px' }}>
                          📖 Opening Line
                        </span>
                        <span style={{ fontSize: '0.85em', fontStyle: 'italic', color: THEME.textDark, lineHeight: '1.4' }}>
                          “{asOpeningLine(chatGameStatus.intro_opening_line)}”
                        </span>
                      </div>
                    )}
                    {/* Per-round twist (act/rule), wildcard device, chaos event */}
                    {chatGameStatus?.twist && (
                      <div style={{ marginTop: '8px', paddingTop: '8px', borderTop: `1px solid ${THEME.borderLight}` }}>
                        <span style={{ fontSize: '0.7em', fontWeight: 700, color: THEME.accent, textTransform: 'uppercase', letterSpacing: '1px', marginRight: '6px' }}>
                          ⚡ This Round · {chatGameStatus.twist.name}
                        </span>
                        {/* Humans get the measurable SIDE QUEST instead of the AI-facing act
                            directive (which reads like homework); the directive still flows
                            to AI prompts untouched. */}
                        {chatGameStatus.human_challenge ? (
                          <>
                            <style>{`@keyframes sideQuestGlow { 0%, 100% { box-shadow: 0 0 5px 1px #22c55e50; } 50% { box-shadow: 0 0 16px 4px #22c55e70; } }`}</style>
                            <span style={{
                              display: 'inline-block', padding: '4px 12px', borderRadius: '12px',
                              border: '1.5px solid #22c55e', backgroundColor: '#22c55e1f',
                              color: '#22c55e', fontWeight: 800, fontSize: '0.82em',
                              animation: 'sideQuestGlow 1.8s ease-in-out infinite'
                            }}>
                              {sideQuestClaimed === chatGameStatus.human_challenge.text
                                ? <>✅ {chatGameStatus.human_challenge.label || 'SIDE QUEST'}: claimed! (+{chatGameStatus.human_challenge.points})</>
                                : <>🎯 {chatGameStatus.human_challenge.label || 'SIDE QUEST'}: {chatGameStatus.human_challenge.text} (+{chatGameStatus.human_challenge.points})</>}
                            </span>
                          </>
                        ) : chatGameStatus.twist.rule && (
                          <span style={{ fontSize: '0.85em', color: THEME.textDark, lineHeight: '1.4' }}>
                            {chatGameStatus.twist.rule}
                          </span>
                        )}
                        {chatGameStatus.twist.story_seed && (
                          <div style={{ marginTop: '6px', fontSize: '0.85em', fontStyle: 'italic', color: THEME.textDark, lineHeight: '1.4' }}>
                            📖 Opening line — start here: “{asOpeningLine(chatGameStatus.twist.story_seed)}”
                          </div>
                        )}
                        {chatGameStatus.twist.your_device && (
                          <div style={{ marginTop: '4px', fontSize: '0.8em', fontWeight: 700, color: THEME.secondary || THEME.accent }}>
                            🃏 You must work in: {chatGameStatus.twist.your_device}
                          </div>
                        )}
                        {/* THE ASSIGNMENT (Hot Takes) — public cursed-take deal */}
                        {chatGameStatus.twist.assigned_take && (
                          <div style={{ marginTop: '4px', fontSize: '0.8em', fontWeight: 700, color: '#ff7043' }}>
                            {chatGameStatus.twist.assigned_take.is_you
                              ? <>🔥 You got HANDED: “{chatGameStatus.twist.assigned_take.take}” Defend it. No backing out!</>
                              : <>🔥 {chatGameStatus.twist.assigned_take.player_name} got HANDED: “{chatGameStatus.twist.assigned_take.take}”</>}
                          </div>
                        )}
                        {chatGameStatus.twist.chaos_event && (
                          <div style={{ marginTop: '4px', fontSize: '0.8em', color: THEME.textDark, lineHeight: '1.4' }}>
                            🌩️ Chaos event: {chatGameStatus.twist.chaos_event}
                          </div>
                        )}
                      </div>
                    )}
                    {/* D&D Fate Dice — click-to-roll reveal of the viewer's roll for the round.
                        The value is pre-determined server-side (fair); the click is a reveal
                        animation, not a re-roll. Resets each round (keyed on dice.round). */}
                    {chatGameStatus?.dice?.your_roll && (() => {
                      const roll = chatGameStatus.dice.your_roll;
                      const revealed = diceRevealedRound === diceRoundKey;
                      // Display-only here — rolling happens on the turn-input overlay during the
                      // player's turn. Before reveal, just name the die; after, show the result.
                      return (
                        <div style={{ marginTop: '6px' }}>
                          {!revealed ? (
                            <span style={{ fontSize: '0.8em', fontWeight: 700, color: THEME.accent, opacity: 0.75, display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                              <span style={{ fontSize: '1.3em' }}>🎲</span>
                              {chatGameStatus.dice.label} · rolls on your turn
                            </span>
                          ) : (
                            <>{renderDiceOutcome(roll)}</>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                </div>
              ) : (!chatGameStatus?.competitive_mode && !chatGameStatus?.imposter_mode && chatGameStatus?.ui_theme?.bannerImage?.url) ? (
                /* Open-caption collapsed line (Nina 08-01): italic narration,
                   hard single-line cap, edge-fade truncation instead of "…",
                   quiet chevron. No 🌍/SCENE label — the words carry it.
                   Objective stays one click away (expand) + in the ⓘ modal.
                   The whole line is still the expand tap target. */
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    {/* fit-content + margin auto: short text centers; long text
                        anchors LEFT and fades only at its END. (The first cut
                        centered overflowing text — which clips BOTH sides, so
                        the sentence faded in from nothing. Nina's screenshot,
                        08-01.) */}
                    <div style={{
                      width: 'fit-content', maxWidth: '100%', margin: '0 auto',
                      fontSize: '0.86em', fontStyle: 'italic', color: 'rgba(255,255,255,0.94)',
                      whiteSpace: 'nowrap', overflow: 'hidden', lineHeight: 1.4,
                      textShadow: '0 1px 4px rgba(0,0,0,0.85)',
                      WebkitMaskImage: 'linear-gradient(90deg, #000 88%, transparent 99%)',
                      maskImage: 'linear-gradient(90deg, #000 88%, transparent 99%)',
                    }}>
                      {chatGameStatus.scene || displayObjective}
                    </div>
                  </div>
                  <span style={{ fontSize: '0.72em', color: 'rgba(255,255,255,0.55)', flexShrink: 0 }}>▾</span>
                </div>
              ) : (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                }}>
                  <span style={{ fontSize: '1.5em', color: THEME.accent, opacity: 1, flexShrink: 0, fontWeight: 700, marginLeft: '-4px' }}>▾</span>
                  <div style={{
                    fontSize: '0.82em',
                    color: THEME.textDark,
                    lineHeight: '1.3',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    flex: 1,
                    minWidth: 0,
                  }}>
                    {chatGameStatus.scene && !(chatGameStatus?.competitive_mode && !chatGameStatus?.imposter_mode) && (
                      <>
                        <span style={{ fontSize: '0.85em', fontWeight: '600', color: THEME.secondary || THEME.accent, textTransform: 'uppercase', letterSpacing: '0.5px', marginRight: '5px' }}>
                          {chatGameStatus?.imposter_mode ? '🔑' : '🌍'}
                        </span>
                        <span>
                          {chatGameStatus?.imposter_mode && chatGameStatus?.is_imposter
                            ? 'You are the Golden Glitch. Blend in!'
                            : (chatGameStatus?.competitive_mode && !chatGameStatus?.imposter_mode && isGenericScene)
                              ? displayObjective
                              : chatGameStatus.scene}
                        </span>
                      </>
                    )}
                    {chatGameStatus.scene && chatGameStatus.objective && !(chatGameStatus?.competitive_mode && !chatGameStatus?.imposter_mode) && (
                      <span style={{ margin: '0 6px', color: THEME.accent, opacity: 0.5 }}>|</span>
                    )}
                    <span style={{ fontSize: '0.85em', fontWeight: '600', color: THEME.accent, textTransform: 'uppercase', letterSpacing: '0.5px', marginRight: '5px' }}>🎯</span>
                    {chatGameStatus.objective && (
                      <span>
                        {displayObjective}
                      </span>
                    )}
                    {chatGameStatus?.twist?.name && (
                      <span style={{ fontWeight: 800, color: THEME.accent, textTransform: 'uppercase', letterSpacing: '0.5px', marginLeft: '6px' }}>
                        ⚡ {chatGameStatus.twist.name}
                      </span>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Edit buttons (always visible in top-right) - hidden for chat games */}
        {gameType !== 'chat-game' && gameType !== 'chat' && (
          <div style={{
            position: 'absolute',
            top: '10px',
            right: '10px',
            display: 'flex',
            gap: '8px',
            zIndex: 100
          }}>
            {SHOW_EMOTION_SONGS_EDITOR && (
            <button
              onClick={handleLoadEmotionSongs}
              style={{
                backgroundColor: '#a855f7',
                color: 'white',
                border: 'none',
                padding: '8px 16px',
                borderRadius: '6px',
                fontSize: '0.85em',
                fontWeight: 'bold',
                cursor: 'pointer',
                boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}
            >
              <span>🎵</span>
              <span>Edit Music</span>
            </button>
            )}
            <button
              onClick={handleLoadSpaces}
              style={{
                backgroundColor: '#9C27B0',
                color: 'white',
                border: 'none',
                padding: '8px 16px',
                borderRadius: '6px',
                fontSize: '0.85em',
                fontWeight: 'bold',
                cursor: 'pointer',
                boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}
            >
              <span>📍</span>
              <span>Edit Spaces</span>
            </button>
          </div>
        )}

        {/* Large header for non-compact game types */}
        {!(gameType === 'chat-game' || gameType === 'chat' || gameType === 'big-brother' || gameType === 'blood-on-the-clocktower' || gameType === 'botc') && (
          <>
            <h1 style={{
              color: THEME.accent,
              textAlign: 'center',
              fontSize: '2.5em',
              marginBottom: '20px',
              textShadow: `2px 2px 4px ${THEME.textDark}80`,
              fontFamily: 'Georgia, serif',
              letterSpacing: '2px',
            }}>
              {isLoading && !results.length ? '⏳ Loading...' : `${THEME.titleEmoji} ${THEME.name} ${THEME.titleEmoji}`}
            </h1>

            {/* Asset Generation Notice - shows when using placeholder theme or no custom theme */}
            {(gameType === 'chat-game' || gameType === 'chat') && (aiGeneratedTheme?._isPlaceholder || (aiGeneratedTheme && !aiGeneratedTheme?.bannerImage?.url && !aiGeneratedTheme?.backgroundImage?.url)) && (
              <div style={{
                marginBottom: '16px',
                padding: '12px 20px',
                borderRadius: '8px',
                backgroundColor: '#fef3c7',
                border: '1px solid #f59e0b',
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
              }}>
                <span style={{ fontSize: '1.3em' }}>🎨</span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 'bold', color: '#92400e', fontSize: '0.95em' }}>
                    Generating Visual Assets...
                  </div>
                  <div style={{ fontSize: '0.85em', color: '#a16207', marginTop: '2px' }}>
                    Custom theme, images, and styling are being created in the background. They'll appear automatically when ready.
                  </div>
                </div>
                <span style={{ fontSize: '1.2em' }}>⏳</span>
              </div>
            )}

            {/* Status Indicator for other games */}
            {chatGameStatus && (() => { const isMyTurn = (isHumanTurn && !!currentRequestId) || (chatGameStatus.waiting_for?.player_id === humanPlayerId); return (
              <div style={{
                marginBottom: '16px',
                padding: '12px 20px',
                borderRadius: '8px',
                backgroundColor: isMyTurn ? THEME.accent : THEME.bgMedium,
                border: isMyTurn ? `2px solid ${THEME.accent}` : `1px solid ${THEME.borderLight}`,
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                boxShadow: `0 2px 6px ${THEME.textDark}33`
              }}>
                <span style={{ fontSize: '1.5em' }}>
                  {isMyTurn ? '🎮' : '💬'}
                </span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 'bold', color: isMyTurn ? (THEME.buttonText || '#ffffff') : THEME.textDark, fontSize: '1.1em' }}>
                    {isMyTurn
                      ? '⏰ YOUR TURN!'
                      : chatGameStatus.waiting_for
                        ? `⏳ ${chatGameStatus.waiting_for.player_name}'s turn`
                        : '✅ Game in Progress'}
                  </div>
                  <div style={{ fontSize: '0.85em', color: isMyTurn ? (THEME.buttonText || '#ffffff') : THEME.textMuted, marginTop: '4px' }}>
                    Round {chatGameStatus.round_number} • {chatGameStatus.players?.length} players
                    {humanPlayerId && ` • Playing as: ${users.find(u => u.game_player_id === humanPlayerId)?.name || 'Unknown'}`}
                  </div>
                </div>
                {users.filter(u => u.is_human && u.name && u.name.startsWith('human_')).length > 0 && (
                  <select
                    value={humanPlayerId || ''}
                    onChange={(e) => {
                      setHumanPlayerId(e.target.value);
                      const url = new URL(window.location);
                      if (e.target.value) {
                        url.searchParams.set('playerId', e.target.value);
                      } else {
                        url.searchParams.delete('playerId');
                      }
                      window.history.pushState({}, '', url);
                      fetchChatGameStatus();
                    }}
                    style={{
                      padding: '8px 12px',
                      borderRadius: '4px',
                      border: `1px solid ${THEME.borderLight}`,
                      backgroundColor: THEME.bgLight,
                      color: THEME.textDark,
                      minWidth: '150px',
                      cursor: 'pointer'
                    }}
                  >
                    <option value="">👁️ Spectate</option>
                    {users.filter(u => u.is_human && u.name && u.name.startsWith('human_')).slice(0, 1).map(user => (
                      <option key={user.game_player_id} value={user.game_player_id}>
                        🎮 Play as {user.name}
                      </option>
                    ))}
                  </select>
                )}
              </div>
              ); })()}
          </>
        )}

        {/* Hide original controls for ChatGame, Big Brother, and BOTC - they're now in the sidebar */}
        {!(gameType === 'chat-game' || gameType === 'chat' || gameType === 'big-brother' || gameType === 'blood-on-the-clocktower' || gameType === 'botc') && (
        <div style={{
          marginBottom: '20px',
          backgroundColor: THEME.bgMedium,
          padding: '16px',
          borderRadius: '8px',
          boxShadow: `0 4px 8px ${THEME.textDark}4d`,
          position: 'sticky',
          top: '0',
          zIndex: 2000,
          width: '100%',
          boxSizing: 'border-box',
          border: `1px solid ${THEME.borderDark}`,
        }}>
          <input
            type="text"
            value={gameId}
            onChange={handleGameIdChange}
            placeholder="Enter game ID"
            style={{ 
              marginRight: '10px', 
              padding: '8px 12px',
              borderRadius: '4px',
              border: `1px solid ${THEME.borderGold}`,
              backgroundColor: THEME.bgLight,
              color: THEME.textLight,
              fontSize: '16px',
              width: '300px',
              outline: 'none',
            }}
          />
          
          <button
            onClick={() => fetchGameStep()}
            disabled={isLoading || isPlaying}
            style={{
              marginRight: '10px',
              padding: '8px 16px',
              borderRadius: '4px',
              border: `1px solid ${THEME.borderGold}`,
              backgroundColor: THEME.accent,
              color: THEME.buttonText || THEME.textDark,
              cursor: isPlaying ? 'not-allowed' : 'pointer',
              opacity: isPlaying ? 0.7 : 1,
              fontWeight: 'bold',
              transition: 'all 0.2s',
              boxShadow: '0 2px 4px ${THEME.textDark}33',
            }}
            onMouseEnter={(e) => e.target.style.transform = 'translateY(-1px)'}
            onMouseLeave={(e) => e.target.style.transform = 'translateY(0)'}
          >
            Take Step
          </button>

        <button 
          onClick={handleLoadAll}
          disabled={isLoading || isPlaying}
          style={{ 
            marginRight: '10px', 
            padding: '8px 16px',
            borderRadius: '4px',
            border: 'none',
            backgroundColor: THEME.textMuted,
            color: THEME.textLight,
            cursor: isPlaying ? 'not-allowed' : 'pointer',
            opacity: isPlaying ? 0.7 : 1
          }}
        >
          Load All
        </button>

        <button 
          onClick={handleBookmark}
          style={{ 
            marginRight: '10px', 
            padding: '8px 16px',
            borderRadius: '4px',
            border: 'none',
            backgroundColor: THEME.private,
            color: THEME.textLight,
            cursor: 'pointer'
          }}
        >
          Bookmark
        </button>

        <button 
          onClick={handleLoadBookmark}
          style={{ 
            marginRight: '10px', 
            padding: '8px 16px',
            borderRadius: '4px',
            border: 'none',
            backgroundColor: THEME.private,
            color: THEME.textLight,
            cursor: 'pointer'
          }}
        >
          Load Bookmark
        </button>

        {/* Hide Slice of Life, Analysis, and Ask Question buttons for chat games */}
        {gameType !== 'chat-game' && gameType !== 'chat' && (
          <>
            <button
              onClick={handleSliceOfLife}
              style={{
                marginRight: '10px',
                padding: '8px 16px',
                borderRadius: '4px',
                border: 'none',
                backgroundColor: THEME.secondary,
                color: THEME.textLight,
                cursor: 'pointer'
              }}
            >
              Slice of Life
            </button>

            <button
              onClick={handleAnalysis}
              style={{
                marginRight: '10px',
                padding: '8px 16px',
                borderRadius: '4px',
                border: 'none',
                backgroundColor: THEME.private,
                color: THEME.textLight,
                cursor: 'pointer'
              }}
            >
              Analysis
            </button>

            <button
              onClick={handleAskQuestion}
              style={{
                marginRight: '10px',
                padding: '8px 16px',
                borderRadius: '4px',
                border: 'none',
                backgroundColor: THEME.secondary,
                color: THEME.textLight,
                cursor: 'pointer'
              }}
            >
              Ask Question
            </button>
          </>
        )}

        <button 
          onClick={handleRollback}
          disabled={isLoading || isPlaying}
          style={{ 
            marginRight: '10px', 
            padding: '8px 16px',
            borderRadius: '4px',
            border: 'none',
            backgroundColor: THEME.accent,
            color: THEME.textLight,
            cursor: isPlaying ? 'not-allowed' : 'pointer',
            opacity: isPlaying ? 0.7 : 1
          }}
        >
          Rollback
        </button>

        {/* Hide Play/Pause buttons for chat games but keep them in DOM for auto-play functionality */}
        <button
          onClick={handlePlay}
          disabled={isPlaying}
          style={{
            marginRight: (gameType === 'chat-game' || gameType === 'chat') ? 0 : '10px',
            padding: (gameType === 'chat-game' || gameType === 'chat') ? 0 : '8px 16px',
            width: (gameType === 'chat-game' || gameType === 'chat') ? 0 : 'auto',
            height: (gameType === 'chat-game' || gameType === 'chat') ? 0 : 'auto',
            overflow: 'hidden',
            borderRadius: '4px',
            border: 'none',
            backgroundColor: THEME.good,
            color: THEME.textLight,
            cursor: isPlaying ? 'not-allowed' : 'pointer',
            opacity: (gameType === 'chat-game' || gameType === 'chat') ? 0 : (isPlaying ? 0.7 : 1)
          }}
        >
          Play
        </button>

        <button
          onClick={handlePause}
          disabled={!isPlaying}
          style={{
            padding: (gameType === 'chat-game' || gameType === 'chat') ? 0 : '8px 16px',
            width: (gameType === 'chat-game' || gameType === 'chat') ? 0 : 'auto',
            height: (gameType === 'chat-game' || gameType === 'chat') ? 0 : 'auto',
            overflow: 'hidden',
            borderRadius: '4px',
            border: 'none',
            backgroundColor: THEME.evil,
            color: THEME.textLight,
            cursor: !isPlaying ? 'not-allowed' : 'pointer',
            opacity: (gameType === 'chat-game' || gameType === 'chat') ? 0 : (!isPlaying ? 0.7 : 1)
          }}
        >
          Pause
        </button>

        {/* Share button - always visible */}
        <div style={{ marginLeft: '10px' }}>
          <ShareButton gameId={gameInstanceId} gameName={chatGameStatus?.name} theme={THEME} />
        </div>

        {/* Theme Debug Indicator */}
        <div style={{
          marginLeft: '10px',
          padding: '4px 8px',
          fontSize: '0.7rem',
          borderRadius: '4px',
          backgroundColor: aiGeneratedTheme?.colors ? '#4caf50' : '#ff9800',
          color: '#fff',
          fontWeight: '600'
        }} title={aiGeneratedTheme?.colors ? 'Custom theme active' : aiGeneratedTheme?._isPlaceholder ? 'Theme generating...' : 'Using default theme'}>
          {aiGeneratedTheme?.colors ? '🎨 Custom' : aiGeneratedTheme?._isPlaceholder ? '⏳ Loading' : '🎨 Default'}
        </div>

        {/* Space voting buttons */}
        <div style={{
          display: 'flex',
          gap: '8px',
          marginLeft: '12px',
          alignItems: 'center'
        }}>
          <button
            onClick={() => handleSpaceVote(true)}
            title={userSpaceVote === 'like' ? "You liked this!" : "Like this space!"}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '6px 12px',
              backgroundColor: '#e8f5e9',
              color: '#2e7d32',
              border: '2px solid #a5d6a7',
              borderRadius: '16px',
              cursor: 'pointer',
              fontSize: '0.85rem',
              fontWeight: '600',
              transition: 'all 0.3s ease',
              transform: voteAnimation.like ? 'scale(1.15)' : 'scale(1)',
              opacity: userSpaceVote && userSpaceVote !== 'like' ? 0.6 : 1,
              boxShadow: userSpaceVote === 'like' ? '0 0 0 3px rgba(0,0,0,0.1)' : 'none',
            }}
          >
            <span style={{ fontSize: '1rem' }}>❤️</span>
            {spaceLikes > 0 && <span>{spaceLikes}</span>}
            {userSpaceVote === 'like' && <span style={{ fontSize: '0.7rem' }}>✓</span>}
          </button>

          <button
            onClick={() => handleSpaceVote(false)}
            title={userSpaceVote === 'dislike' ? "You disliked this!" : "Not for me"}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '6px 12px',
              backgroundColor: '#fce4ec', textShadow: 'none',
              color: '#c62828',
              border: '2px solid #f48fb1',
              borderRadius: '16px',
              cursor: 'pointer',
              fontSize: '0.85rem',
              fontWeight: '600',
              transition: 'all 0.3s ease',
              transform: voteAnimation.dislike ? 'scale(1.15)' : 'scale(1)',
              opacity: userSpaceVote && userSpaceVote !== 'dislike' ? 0.6 : 1,
              boxShadow: userSpaceVote === 'dislike' ? '0 0 0 3px rgba(0,0,0,0.1)' : 'none',
            }}
          >
            <span style={{ fontSize: '1rem' }}>👎</span>
            {spaceDislikes > 0 && <span>{spaceDislikes}</span>}
            {userSpaceVote === 'dislike' && <span style={{ fontSize: '0.7rem' }}>✓</span>}
          </button>
        </div>
      </div>
      )}

      {/* Multi-column layout for ChatGame, Big Brother, and Blood on the Clocktower */}
      {(gameType === 'chat-game' || gameType === 'chat' || gameType === 'big-brother' || gameType === 'blood-on-the-clocktower' || gameType === 'botc') ? (
        <div style={{
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          gap: isMobile ? '12px' : isThreeColumnLayout ? '16px' : '20px',
          alignItems: isMobile ? 'stretch' : 'stretch',
          flex: 1,
          minHeight: 0,
          overflow: isMobile ? 'auto' : 'hidden',
          // Positioned (z auto — NO stacking context) so every column paints
          // above the desktop masthead art layer; plain relative doesn't
          // re-layer fixed overlays or confine child z-indexes
          position: 'relative',
          // 🏡 W2d: a place page's chat + journal sit UNDER the painting, as
          // wide as its frame (mockup v9's pbody), centered
          // 🏡📐 wave 3 (ruling 32): ONE card: these columns continue the
          // stage's card (its ground, its bottom corners), a hairline under
          // the caption, the lane | the rail split by a hairline (the right
          // column's), the lane's full height (placeFrame.laneH); the page scrolls
          ...((portedPlaceOn && !isMobile && placeFrame) ? {
            width: '100%', maxWidth: placeFrame.frameW, margin: '0 auto', alignSelf: 'center',
            flex: '0 0 auto', height: placeFrame.laneH, minHeight: 0, gap: 0, boxSizing: 'border-box',
            background: placeGround || '#120E18', transition: 'background-color 1.1s ease',
            borderTop: '1px solid rgba(255,255,255,0.12)', borderLeft: '1px solid rgba(255,236,210,0.10)',
            borderRight: '1px solid rgba(255,236,210,0.10)', borderBottom: '1px solid rgba(255,236,210,0.10)',
            borderRadius: '0 0 18px 18px', boxShadow: '0 14px 34px rgba(0,0,0,0.4)',
          } : {}),
          // 🧱 the room frame: today's columns stay MOUNTED (their feed + composer
          // portal into the frame; the hidden holders keep the theater + panel
          // alive) but never paint
          ...(shellPreset ? { display: 'none' } : {}),
        }} data-place-cols={(portedPlaceOn && !isMobile) ? '' : undefined}>
          {/* LEFT SIDEBAR — non-competitive only: Users, Start button, Pinned tab */}
          {/* 🎛️ COLLAPSED ROOM RAIL (Nina 09-15, canvas-ruled): a 36px stand-in
              for the left column. Chevron reopens; the LIVE pill renders ONLY
              while someone is actually live and pulses instead of auto-opening
              anything; awake locals stay countable as initial dots. Solid
              surfaces on purpose — translucent controls over space artwork
              vanish. */}
          {isThreeColumnLayout && canToggleSidebars && !roomPanelOpen && (
            <div style={{
              flex: '0 0 36px', display: 'flex', flexDirection: 'column', alignItems: 'center',
              gap: 12, padding: '10px 0', height: '100%', overflow: 'hidden',
              backgroundColor: THEME.bgMedium, border: `1px solid ${THEME.accent}30`,
              borderRadius: 10, boxSizing: 'border-box',
            }}>
              <style>{'@keyframes roomRailLivePulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.35; } }'}</style>
              <button
                onClick={() => setRoomPanelOpen(true)}
                title="Show live stream"
                aria-label="Show the live stream panel"
                style={{
                  width: 26, height: 44, flexShrink: 0, cursor: 'pointer',
                  background: THEME.bgDark, color: THEME.textOnPanel || '#fff',
                  border: `1px solid ${THEME.accent}55`, borderRadius: 8,
                  fontSize: '0.9em', fontWeight: 800, padding: 0,
                }}
              >›</button>
              {(spaceLiveStreamers.length > 0 || livestreamStatus.trackCount > 0) && (
                <div style={{
                  display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
                  padding: '6px 3px', borderRadius: 999, flexShrink: 0,
                  border: '1px solid rgba(239,68,68,0.6)', background: 'rgba(239,68,68,0.1)',
                }}>
                  <span style={{ width: 7, height: 7, borderRadius: '50%', backgroundColor: '#ef4444', animation: 'roomRailLivePulse 1.6s ease-in-out infinite' }} />
                  <span style={{ writingMode: 'vertical-rl', textOrientation: 'mixed', fontSize: '0.6em', fontWeight: 800, letterSpacing: '1.5px', color: '#ef4444' }}>LIVE</span>
                </div>
              )}
              <div style={{ width: 18, borderTop: `1px solid ${THEME.accent}30`, flexShrink: 0 }} />
              {activeUsers.filter(u => !u.is_human).slice(0, 5).map((u) => {
                let hash = 0;
                const nm = String(u.name || '?');
                for (let i = 0; i < nm.length; i++) hash = nm.charCodeAt(i) + ((hash << 5) - hash);
                const hue = Math.abs(hash) % 360;
                return (
                  <div key={u.id || nm} title={nm} style={{
                    width: 18, height: 18, borderRadius: '50%', flexShrink: 0,
                    background: `hsl(${hue}, 45%, 32%)`, border: `1.5px solid hsl(${hue}, 65%, 55%)`,
                    color: '#fff', fontSize: 10, fontWeight: 800, lineHeight: '16px', textAlign: 'center',
                    overflow: 'hidden',
                  }}>{nm.charAt(0).toUpperCase()}</div>
                );
              })}
            </div>
          )}
          {/* 🏡 W2d: not on a place page (the room is the page; Go live opens
              where the painting is, the panel reparents into the stage slot) */}
          {isThreeColumnLayout && roomPanelOpen && !portedPlaceOn && (
            <div style={{
              flex: `0 0 ${leftSidebarWidth}px`,
              minWidth: 250,
              maxWidth: '40%',
              display: 'flex',
              flexDirection: 'column',
              gap: '12px',
              height: '100%',
              overflowY: 'auto',
              paddingRight: '8px',
            }}>
              {/* 🎛️ hide chip RETIRED (Nina 09-15 pm, Option A): the hide
                  control moved onto the resize gutter — one grammar, both
                  sides, outside the scroller. */}
              {/* LIVESTREAM DOCK — hybrid model: the stream lives here in the
                  sidebar ("faces beside names"); the ⧉ button pops it out into
                  the floating huddle (draggable mini-player, snaps to corners).
                  Content is CSS-hidden (never unmounted) so an active stream
                  survives collapsing/floating. Never in bot mode (the Explorer
                  bot must not join the LiveKit room — recursive self-view). */}
              {!isBotMode && (
                <div style={{
                  borderRadius: '10px',
                  border: `1px solid ${(spaceLiveStreamers.length > 0 || livestreamStatus.trackCount > 0) ? THEME.accent : THEME.accent + '30'}`,
                  backgroundColor: THEME.bgMedium,
                  overflow: 'hidden',
                  flexShrink: 0,
                  boxShadow: (spaceLiveStreamers.length > 0 || livestreamStatus.trackCount > 0) ? `0 0 16px ${THEME.accent}25` : `0 0 12px ${THEME.accent}10`,
                  transition: 'box-shadow 0.3s ease',
                }}>
                  {/* Dock header — googly eyes + live status; click toggles the
                      dock, reels the floating player back in, or (when pinned)
                      returns the stream from the pinned slot to the dock */}
                  <div
                    onClick={() => {
                      if (livestreamFloating) { setLivestreamFloating(false); setLivestreamExpanded(true); }
                      else if (livestreamPinned) { setPinnedSidebarTab('chat'); setLivestreamExpanded(true); }
                      else setLivestreamExpanded(prev => !prev);
                    }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      width: '100%',
                      padding: '7px 12px',
                      background: `linear-gradient(135deg, ${THEME.accent}22, ${THEME.bgLight})`,
                      borderBottom: (livestreamExpanded && !livestreamFloating && !livestreamPinned) ? `1px solid ${THEME.accent}25` : 'none',
                      cursor: 'pointer',
                      color: THEME.textOnPanel,
                      // no 'all' transition here — this header is the hover path
                      // to the pin/pop buttons and 'all' made every cursor pass
                      // eligible for style interpolation work
                      boxSizing: 'border-box',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
                      {(spaceLiveStreamers.length > 0 || livestreamStatus.trackCount > 0) ? (
                        <>
                          <span style={{
                            padding: '2px 8px',
                            borderRadius: '4px',
                            backgroundColor: '#ef4444',
                            color: '#fff',
                            fontSize: '0.7em',
                            fontWeight: 800,
                            letterSpacing: '0.5px',
                            textTransform: 'uppercase',
                            animation: 'liveBadgePulse 2s ease-in-out infinite',
                            flexShrink: 0,
                          }}>
                            LIVE
                          </span>
                          <span style={{ fontSize: '0.8em', fontWeight: 700, color: THEME.textOnPanel || '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>
                            {/* Own stream shows as "You" — never as a name you might
                                not recognize as yourself */}
                            {spaceLiveStreamers.length > 0
                              ? (selfIsLive
                                  ? ['You', ...spaceLiveStreamers.filter(s => {
                                      let vid = null;
                                      try { vid = localStorage.getItem('lk_viewer_id'); } catch (e) { /* private mode */ }
                                      return !((vid && s.id === vid) || (humanPlayerId && s.id === humanPlayerId));
                                    }).map(s => s.name)].join(', ')
                                  : spaceLiveStreamers.map(s => s.name).join(', '))
                              : `${livestreamStatus.trackCount} live`}
                          </span>
                        </>
                      ) : (
                        <span style={{ display: 'flex', alignItems: 'center', gap: '7px' }}>
                          {/* Idle wordmark — grey dot (red is reserved for actually-live) */}
                          <span style={{
                            width: 7,
                            height: 7,
                            borderRadius: '50%',
                            backgroundColor: 'rgba(255,255,255,0.35)',
                            display: 'inline-block',
                            flexShrink: 0,
                          }} />
                          <span style={{
                            fontSize: '0.78em',
                            fontWeight: 800,
                            letterSpacing: '2px',
                            textTransform: 'uppercase',
                            color: THEME.textOnPanel || '#fff',
                            opacity: 0.85,
                          }}>
                            Live
                          </span>
                        </span>
                      )}
                      {/* Occupancy pill RETIRED (Nina 08-06): it duplicated
                          the panel's "N in this space" strip one row below,
                          from a DIFFERENT presence window (30s vs 1h) — two
                          disagreeing counts stacked on one surface. The
                          panel strip (1-hour roster) is the livestream
                          number now; the masthead shows the same figure. */}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
                      {!livestreamFloating && !livestreamPinned && (
                        <button
                          onClick={(e) => { e.stopPropagation(); setPinnedSidebarTab('livestream'); setLivestreamFloating(false); }}
                          title="Pin to sidebar — the stream takes the big pinned slot below"
                          style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '6px', color: THEME.textOnPanel || '#fff', cursor: 'pointer', fontSize: '0.75em', padding: '2px 7px', lineHeight: 1.4 }}
                        >
                          📌
                        </button>
                      )}
                      {livestreamStatus.trackCount > 0 && !livestreamFloating && (
                        <button
                          onClick={(e) => { e.stopPropagation(); setLivestreamFloating(true); setLivestreamExpanded(true); }}
                          title="Pop out into a floating player — drag it anywhere, it snaps into place"
                          style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '6px', color: THEME.textOnPanel || '#fff', cursor: 'pointer', fontSize: '0.75em', padding: '2px 7px', lineHeight: 1.4 }}
                        >
                          ⧉
                        </button>
                      )}
                      {livestreamFloating ? (
                        <span style={{ fontSize: '0.65em', color: THEME.textOnPanelMuted, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                          floating — click to dock
                        </span>
                      ) : livestreamPinned ? (
                        <span style={{ fontSize: '0.65em', color: THEME.textOnPanelMuted, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                          pinned below
                        </span>
                      ) : (
                        <span style={{ fontSize: '0.7em', color: THEME.textOnPanelMuted, transform: livestreamExpanded ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.3s ease', display: 'inline-block' }}>
                          ▼
                        </span>
                      )}
                      <GooglyEyes />
                    </div>
                  </div>
                  {/* Dock content — hidden (not unmounted) while collapsed,
                      floating, or pinned into the sidebar slot below */}
                  <div style={{
                    // 380 (was 300, Nina 08-02): the sidebar Media panel is gone,
                    // so the Live dock gets the reclaimed breathing room
                    height: (livestreamExpanded && !livestreamFloating && !livestreamPinned) ? 380 : 0,
                    display: (livestreamExpanded && !livestreamFloating && !livestreamPinned) ? 'flex' : 'none',
                    flexDirection: 'column',
                  }}>
                    <div ref={livestreamDockSlotRef} style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }} />
                  </div>
                </div>
              )}
              {/* Users Panel — compact mode: show 2 players horizontally, expandable */}
              {activeUsers.length > 0 && !isPortedSpace && <div ref={playersPanelRef}><UsersPanel users={activeUsers} allUsers={users} localStatuses={localStatuses} THEME={THEME} gameId={gameId} chatGameStatus={chatGameStatus} humanPlayerId={humanPlayerId} createdPlayerId={createdPlayerIdRef.current} onQuickJoin={handleQuickJoin} isReadyCheck={chatGameStatus?.is_ready_check && !chatGameStatus?.ready_check_data?.done} readyCheckData={readyCheckData} readyCheckSubmitted={readyCheckSubmitted} readyCheckCountdown={readyCheckCountdown} readyCountdownDuration={readyCountdownDuration} readyCheckPhase={readyCheckPhase} autoStartCountdown={autoStartCountdown} gameStarted={gameStarted} hasJoinedGame={hasJoinedGame} showGlitchReveal={showGlitchReveal} diceSpotlightId={diceRollToast?.pid || null} hideJoinCtas={!!chatGameStatus?.noir_theme && !isBotMode} compactMode onReadyUp={async () => {
                    readyCheckDismissedRef.current = true;
                    setReadyCheckSubmitted(true);
                    const myName = chatGameStatus?.players?.find(p => p.id === humanPlayerId)?.name || 'You';
                    setReadyCheckData(prev => ({
                      ...prev,
                      ready_count: (prev?.ready_count || 0) + 1,
                      ready_player_names: [...(prev?.ready_player_names || []), myName]
                    }));
                    try {
                      await chatGameApi.readyUp(gameId, humanPlayerId);
                    } catch (err) {
                      console.error('Ready check response failed:', err);
                    }
                  }} onTogglePlay={(isPlay) => {
                    // Debounce: ignore rapid toggles within 1s
                    const now = Date.now();
                    if (now - togglePlayDebounceRef.current < 1000) return;
                    togglePlayDebounceRef.current = now;
                    // Block switching modes once game has started (both directions)
                    if (gameStarted) return;
                    if (isPlay && chatGameStatus?.eliminated_players?.includes(createdPlayerIdRef.current)) return;
                    // Block toggling to Play while a round is active in competitive/imposter mode
                    if (isPlay && isPlayingRef.current && (chatGameStatus?.competitive_mode || chatGameStatus?.imposter_mode)) return;
                    userSwitchedToPlayRef.current = true;
                    if (isPlay) {
                      const pid = createdPlayerIdRef.current;
                      if (!pid) return;
                      setHumanPlayerId(pid);
                      const url = new URL(window.location);
                      url.searchParams.set('playerId', pid);
                      window.history.replaceState({}, '', url);
                      readyCheckDismissedRef.current = false;
                      setReadyCheckSubmitted(false);
                      chatGameApi.unspectatePlayer(gameId, pid).catch(err =>
                        console.error('[onTogglePlay] unspectate failed:', err.message)
                      );
                    } else {
                      setHumanPlayerId(null);
                      const url = new URL(window.location);
                      url.searchParams.delete('playerId');
                      window.history.replaceState({}, '', url);
                      readyCheckDismissedRef.current = true;
                      setShowReadyCheck(false);
                      setCurrentRequestId(null);
                    }
                  }} dismissedHints={dismissedHints} onDismissHint={dismissHint} isMobile={isMobile} hintsHidden={hintsHidden} spectateDropdownRef={spectateDropdownRef} quickJoinButtonsRef={quickJoinButtonsRef} readyToggleRef={readyToggleRef} showElementPulse={showElementPulse} showDropdownHint={showDropdownHint} showReadyHint={showReadyHint} onDropdownInteract={() => { dropdownInteractedRef.current = true; setShowDropdownHint(false); }} onReadyInteract={() => { readyInteractedRef.current = true; setShowReadyHint(false); }} /></div>}



              {/* Pinned sidebar tab */}
              {pinnedSidebarTab && (
                <div style={{
                  flex: 1,
                  minHeight: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  borderRadius: '10px',
                  border: `1px solid ${THEME.accent}30`,
                  backgroundColor: THEME.bgMedium,
                  overflow: 'hidden',
                  boxShadow: `0 0 12px ${THEME.accent}10`,
                }}>
                  {/* Pinned tab header */}
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '8px 10px',
                    background: `linear-gradient(135deg, ${THEME.accent}18, ${THEME.bgLight})`,
                    borderBottom: `1px solid ${THEME.accent}25`,
                    flexShrink: 0,
                  }}>
                    <span style={{ fontSize: '0.8em', fontWeight: 600, color: THEME.textOnPanel }}>
                      {{ homepage: '🏠 Home', chat: '💬 Chat', bot_vision: '🎙️ Bot Vision', games: '🎮 Arcade', highlights: '🫖 The Tea', livestream: '📡 Live' }[pinnedSidebarTab] || pinnedSidebarTab}
                    </span>
                    <button onClick={() => {
                      // Unpinning the livestream sends it back to the dock — pop
                      // the dock open so the stream stays visible through the move
                      if (pinnedSidebarTab === 'livestream') setLivestreamExpanded(true);
                      setPinnedSidebarTab(null);
                    }} title="Unpin from sidebar" style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.7em', color: THEME.textOnPanelMuted, padding: '2px 4px', lineHeight: 1 }}>
                      ✕
                    </button>
                  </div>
                  {/* Pinned tab content */}
                  <div style={{ flex: 1, minHeight: 0, overflowY: pinnedSidebarTab === 'livestream' ? 'hidden' : 'auto', overflowX: 'hidden', display: 'flex', flexDirection: 'column' }}>
                    {/* 🧹 'homepage' pinned branch REMOVED (08-10): nothing in
                        src ever sets pinnedSidebarTab='homepage' — verified
                        unreachable since at least 08-07 (§14g). */}
                    {/* 🎬 'media' pinned branch REMOVED (Nina 08-02): the Media
                        TAB is its one home now — the sidebar slot was a
                        redundant second mount of SuggestedMediaPanel. */}
                    {pinnedSidebarTab === 'watch_party' && (
                      <WatchPartyLauncher
                        watchParty={watchParty}
                        history={chatGameStatus?.watch_party_history}
                        mediaSuggestions={chatGameStatus?.media_suggestions}
                        pinnedPick={shownPinnedPick}
                        inTheater={inTheater}
                        onTakeSeat={() => setInTheater(true)}
                        humanPlayerId={humanPlayerId}
                        THEME={THEME}
                        isMobile={isMobile}
                      />
                    )}
                    {pinnedSidebarTab === 'bot_vision' && (
                      <BotVisionPanel
                        THEME={THEME}
                        chatGameStatus={chatGameStatus}
                        setChatGameStatus={setChatGameStatus}
                        gameId={gameId}
                      />
                    )}
                    {pinnedSidebarTab === 'games' && (
                      <GamesPanel THEME={THEME} />
                    )}
                    {pinnedSidebarTab === 'chat' && (() => {
                      const chatActions = results.flatMap(r => r.actions || [])
                        .filter(a => ['public_message', 'private_message', 'human_interjection', 'game_announcement'].includes(a.type) && a.data?.message);
                      return (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', padding: '8px 6px' }}>
                          {/* 🎭 characters front and center, Tea-style (Nina 07-11) */}
                          {!isPortedSpace && (
                            <CastRail
                              users={users}
                              moderatorCharacter={chatGameStatus?.moderator_character}
                              humanPlayerId={humanPlayerId}
                              getModelName={getDisplayModelName}
                              THEME={THEME}
                            />
                          )}
                          {chatActions.length === 0 && (
                            isPortedSpace ? (
                              /* 🪟🏮 companion quiet-state (Nina 08-28): the room
                                 is the two of you — present and waiting, not an
                                 empty stage. Now that ambient-TV "Host" spam is
                                 gated off, the ported feed is genuinely quiet, so
                                 this is what it lands on. */
                              <div style={{ textAlign: 'center', padding: '48px 16px', color: 'rgba(255,238,216,0.55)' }}>
                                <div style={{ fontSize: '2em' }}>🏮</div>
                                <div style={{ fontSize: '0.9em', fontStyle: 'italic', marginTop: 8 }}>
                                  Just you and {portedMindProps?.residentName || 'your resident'}. Say something.
                                </div>
                              </div>
                            ) : (
                              <div style={{ textAlign: 'center', padding: '40px 16px', color: THEME.textMuted, fontSize: '0.8em' }}>
                                No messages yet
                              </div>
                            )
                          )}
                          {chatActions.map((a, i) => {
                            const sender = a.data?.sender_name || a.data?.player_name || 'Unknown';
                            const player = users.find(u => u.game_player_id === a.game_instance_player_id);
                            const imgUrl = player?.profile_image_url || player?.data?.profile_image_url;
                            const isMod = a.data?.is_moderator;
                            const isAnnouncement = a.type === 'game_announcement';
                            return (
                              <div key={a.id || i} style={{
                                padding: '6px 8px',
                                borderRadius: '8px',
                                backgroundColor: isAnnouncement ? `${THEME.accent}15` : 'transparent',
                              }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '2px' }}>
                                  {imgUrl ? (
                                    <img src={imgUrl} alt="" style={{ width: 18, height: 18, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />
                                  ) : (
                                    <div style={{
                                      width: 18, height: 18, borderRadius: '50%', flexShrink: 0,
                                      backgroundColor: isMod ? `${THEME.accent}40` : 'rgba(255,255,255,0.1)',
                                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                                      fontSize: '0.55em', color: '#fff', fontWeight: 700,
                                    }}>
                                      {isMod ? (chatGameStatus?.moderator_character?.avatar_emoji || '🎙️') : (sender.charAt(0) || '?')}
                                    </div>
                                  )}
                                  <span style={{
                                    fontSize: '0.7em', fontWeight: 700,
                                    color: isMod ? (THEME.accent || '#8b5cf6') : (THEME.textOnPanel || '#fff'),
                                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                  }}>
                                    {sender}
                                  </span>
                                </div>
                                <div style={{
                                  fontSize: '0.72em', color: THEME.textMuted || '#aaa',
                                  lineHeight: 1.4, paddingLeft: '24px',
                                  wordBreak: 'break-word',
                                }}>
                                  {a.data.message.length > 200 ? a.data.message.slice(0, 200) + '...' : a.data.message}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      );
                    })()}
                    {pinnedSidebarTab === 'highlights' && !isPortedSpace && (
                      <HighlightsPanel allActions={results.flatMap(r => r.actions || [])} users={users} THEME={THEME} moderatorCharacter={chatGameStatus?.moderator_character} gameId={gameId} humanPlayerId={humanPlayerId} shipVotes={chatGameStatus?.ship_votes} builds={chatGameStatus?.builds || []} />
                    )}
                    {/* Livestream pin — empty slot the stable host node gets
                        appended into (see livestreamHostEl reparent effect) */}
                    {pinnedSidebarTab === 'livestream' && (
                      <div ref={livestreamPinSlotRef} style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }} />
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Left sidebar resize handle */}
          {isThreeColumnLayout && roomPanelOpen && !portedPlaceOn && (
            <div
              onMouseDown={(e) => {
                e.preventDefault();
                const startX = e.clientX;
                const startWidth = leftSidebarWidth;
                const onMouseMove = (moveEvent) => {
                  const delta = moveEvent.clientX - startX;
                  setLeftSidebarWidth(Math.max(250, Math.min(startWidth + delta, window.innerWidth * 0.4)));
                };
                const onMouseUp = () => {
                  document.removeEventListener('mousemove', onMouseMove);
                  document.removeEventListener('mouseup', onMouseUp);
                  document.body.style.cursor = '';
                  document.body.style.userSelect = '';
                };
                document.addEventListener('mousemove', onMouseMove);
                document.addEventListener('mouseup', onMouseUp);
                document.body.style.cursor = 'col-resize';
                document.body.style.userSelect = 'none';
              }}
              style={{
                width: '10px',
                cursor: 'col-resize',
                backgroundColor: THEME.accent + '15',
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '4px',
                transition: 'background-color 0.2s',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = THEME.accent + '35'; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = THEME.accent + '15'; }}
            >
              {/* 🎛️ Option A (Nina 09-15 pm): the hide control lives ON the
                  resize gutter — a full-height flex sibling OUTSIDE the
                  scroller, so it can never clip (the sticky-chip lesson).
                  Click hides; drag on the gutter still resizes (the button
                  swallows its own mousedown so a click never starts a drag).
                  Solid surfaces on purpose — translucent controls over space
                  artwork vanish. Where toggles don't apply, the plain grip
                  pill stays. */}
              {canToggleSidebars ? (
                <button
                  onMouseDown={(e) => e.stopPropagation()}
                  onClick={() => setRoomPanelOpen(false)}
                  title="Hide live stream"
                  aria-label="Hide the live stream panel"
                  style={{
                    width: 18, height: 48, padding: 0, cursor: 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    background: THEME.bgDark, border: `1px solid ${THEME.accent}66`,
                    borderRadius: 9, boxShadow: '0 2px 8px rgba(0,0,0,0.5)',
                  }}
                >
                  <svg width="11" height="11" viewBox="0 0 16 16" fill="none" stroke={THEME.accent} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M10 3 5 8l5 5" /></svg>
                </button>
              ) : (
                <div style={{
                  width: '4px',
                  height: '40px',
                  borderRadius: '2px',
                  backgroundColor: THEME.accent + '80',
                }} />
              )}
            </div>
          )}

          {/* LEFT SIDEBAR — competitive/imposter mode only: Ghost Chat (310px, desktop only) */}
          {showGhostChatLeft && (
            <div ref={ghostChatRef} style={{
              // Narrow desktop: 310px sidebar starves the center column (768-1100px
              // band audit, 07-10)
              flex: isNarrowDesktop ? '0 0 260px' : '0 0 310px',
              display: 'flex',
              flexDirection: 'column',
              height: '100%',
              overflowY: 'auto',
              borderRight: `1px solid ${THEME.accent}40`,
              paddingRight: '8px',
            }}>
              <SpectatorChatPanel
                messages={ghostPanelMessages}
                watchPartyActive={!!watchParty} seatsAsCrowd={!(chatGameStatus?.competitive_mode || chatGameStatus?.imposter_mode)}
                onSubmit={handleSpectatingSubmit}
                senderName={
                  humanPlayerId
                    ? (users.find(u => u.game_player_id === humanPlayerId)?.name || 'You')
                    : 'Spectator'
                }
                isEliminated={!!(humanPlayerId && chatGameStatus?.eliminated_players?.includes(humanPlayerId))}
                theme={THEME}
                users={users}
                isMobile={false}
                forceExpand={true}
                label="👻 Ghost Chat"
                onLoadOlder={loadOlderSpectatorMessages}
                isLoadingOlder={isLoadingOlderSpectatorMessages}
                hasMore={hasMoreSpectatorMessages}
                onExpandChange={(e) => { spectatorPanelExpandedRef.current = e; }}
                onScrolledUpChange={(s) => { spectatorPanelScrolledUpRef.current = s; }}
                isInitialLoading={isInitialSpectatorHistoryLoading}
                onWatchLive={handleWatchLiveFromChat}
              />
            </div>
          )}


          {/* CENTER COLUMN: Tabbed Content */}
          {/* 📏 Capped chat column (Nina 09-17 pm): in camera spaces the
              column stops at CHAT_COLUMN_MAX and centers, so a collapsed
              sidebar frees WALL on both sides instead of stretching the
              chat. chatColumnCap returns {} everywhere else — byte-identical
              spread (spaceLayout.js + its jest pin). */}
          <div ref={centerColumnRef} style={{ flex: isMobile ? '0 0 auto' : (isThreeColumnLayout ? '1 1 0%' : '1 1 70%'), minWidth: 0, minHeight: isMobile ? undefined : 0, display: 'flex', flexDirection: 'column', overflow: 'hidden', ...chatColumnCap({ roomCameraOn, isMobile, isThreeColumnLayout }),
            // 📖 09-18: a soft dark halo on the words while room art is the
            // wallpaper. It buys legibility over busy art without darkening the
            // picture (the arrival caption has always had one). Inherited, so
            // every message type gets it; absent everywhere else.
            ...(roomLaneFill && !isMobile ? { textShadow: '0 1px 3px rgba(0,0,0,0.85), 0 0 1px rgba(0,0,0,0.7)' } : {}) }}>

            {/* Banner strip card removed (Nina 07-26): the art moved to the
                full-bleed masthead behind the title (desktop) / the ambient
                blurred header (phones) — see the two blocks near the top of
                this container. */}

            {/* ⏸️ Space paused veil (pause-on-blur) — resuming is an explicit
                choice; the button restarts polling with an immediate fetch.
                🧱 shellLift: under the room frame these columns never paint, so
                the fixed veil lifts to <body> (identity everywhere else) */}
            {spacePaused && shellLift(
              <div style={{
                position: 'fixed', inset: 0, zIndex: 9600,
                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 14,
                background: 'rgba(10, 6, 20, 0.82)', backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)',
              }}>
                <div style={{ fontSize: '3em' }}>💤</div>
                <div style={{ color: '#fff', fontWeight: 800, fontSize: '1.15em' }}>Space paused</div>
                <div style={{ color: 'rgba(255,255,255,0.65)', fontSize: '0.85em', maxWidth: 320, textAlign: 'center', lineHeight: 1.5 }}>
                  You stepped away, so we let the locals nap too.
                </div>
                <button
                  onClick={() => {
                    setSpacePaused(false);
                    spacePausedRef.current = false;
                    setTimeout(() => fetchChatGameStatus(), 0);
                  }}
                  style={{
                    border: 'none', borderRadius: 999, padding: '12px 32px', cursor: 'pointer',
                    background: THEME.accent || '#7b1fa2', color: '#fff',
                    fontSize: '0.95em', fontWeight: 800, letterSpacing: '0.4px',
                    boxShadow: `0 8px 26px ${THEME.accent || '#7b1fa2'}55`,
                  }}
                >
                  ▶ Wake it up
                </button>
              </div>
            )}

            {/* 🔦 New-challenge toast — points at the case file's DARES tab */}
            {challengeToast && (
              <div
                onClick={() => { setCaseFileOpen(true); setChallengeToast(false); }}
                style={{
                  // Centred via TOAST_CENTERED, not left:50% — same half-width
                  // collapse as the couch invite below.
                  position: 'fixed', top: 64,
                  ...TOAST_CENTERED, maxWidth: 'min(92vw, 420px)',
                  zIndex: 9500, cursor: 'pointer',
                  display: 'flex', alignItems: 'center', gap: 8,
                  padding: '10px 16px', borderRadius: 10,
                  textAlign: 'center',
                  background: 'linear-gradient(135deg, rgba(40,30,12,0.97), rgba(90,64,20,0.97))',
                  border: '1px solid rgba(217,186,114,0.6)',
                  boxShadow: '0 10px 30px rgba(0,0,0,0.55)',
                  color: '#f3e9d2', fontWeight: 700, fontSize: '0.85em',
                }}
              >
                🔦 New challenge! It's in your CASE FILE.
              </div>
            )}

            {/* 🍿 Couch-invite toast — a party just started while you were mid-scroll.
                Fixed-position so it's noticed regardless of scroll; auto-clears in 8s. */}
            {/* ⏳ 09-23 batch 1: the phone countdown pill rides the existing Back/♫
                row (zero new height); it YIELDS to any top toast, to the picture
                and to the TV/people places (a clock over the film is noise). */}
            {/* 🔌 09-28 agent wave: + yields to the ?with "X is here" toast (same top slot) */}
            {TOP_CLOCK && isMobile && topStrip && !topStripHidden && !partyToast && !challengeToast && !liveToast && !chipInShown && !withToast && !roomPeekOpen && !(placesNav && mobileView !== 'chat') && (
              <EventStripBar variant="phone" strip={topStrip} onGo={topStrip.onGo} onHide={hideTopStrip} />
            )}
            {partyToast && !inTheater && shellLift(
              <div
                onClick={() => { setInTheater(true); setPartyToast(null); if (placesNav) setMobileView('tv'); }}
                style={{
                  // safe-area top (round 5, 08-03): a centered top:18 toast sits
                  // under the Dynamic Island; env() is 0 off-notch devices.
                  // 📐 CENTERED TOAST PATTERN (Nina 08-15) — see the note on
                  // TOAST_CENTERED below. left/right:0 + margin auto, never
                  // left:50% + translateX, or the box only gets HALF the screen
                  // to size itself in and collapses to one word per line.
                  position: 'fixed', top: 'calc(18px + env(safe-area-inset-top, 0px))',
                  ...TOAST_CENTERED, maxWidth: 'min(92vw, 520px)',
                  zIndex: 9500, cursor: 'pointer',
                  display: 'flex', alignItems: 'center', gap: 10,
                  padding: '12px 18px', borderRadius: 12,
                  background: 'linear-gradient(135deg, rgba(30,10,45,0.97), rgba(70,20,110,0.97))',
                  border: '1px solid rgba(186,104,200,0.6)',
                  boxShadow: '0 10px 34px rgba(0,0,0,0.55)',
                  color: '#fff',
                  animation: 'fadeIn 0.25s ease',
                }}
              >
                <span style={{ fontSize: '1.5em', flexShrink: 0 }}>🍿</span>
                {/* Clamped to 2 lines: YouTube titles run long ("[1 HOUR] TRY NOT
                    TO LAUGH - Best Funny Vines of The YEAR! | RIP Vines 2018" is
                    a real one from Nina's phone) and an unclamped toast grows
                    down the screen over the chat it is interrupting. The tail is
                    not load-bearing — the party name is on the TV once seated. */}
                <span style={{
                  flex: 1, minWidth: 0, fontSize: '0.9em', lineHeight: 1.35,
                  display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                }}>
                  <b>{partyToast.started_by_name || 'Someone'}</b> started a watch party:
                  {' '}<span style={{ opacity: 0.9 }}>{partyToast.title}</span>
                </span>
                <span style={{
                  flexShrink: 0, fontSize: '0.8em', fontWeight: 700, whiteSpace: 'nowrap',
                  padding: '6px 12px', borderRadius: 999, backgroundColor: '#7b1fa2',
                }}>
                  Take a seat
                </span>
              </div>
            )}

            {/* 📱 places nav: the whole stage region below (party bars,
                launcher fallback, docked stage, slim reminder) lives on the
                TV PLACE now — each block gates on !placesNav and the TV layer
                renders its own composition. Desktop and comp paths: placesNav
                is false, nothing changes. */}
            {/* 🎙️→🍿 HOST'S-PICK MUTED LOOP RETIRED on desktop (Nina 08-13:
                "fold 2 into 1"). The pinned pick already seeds the ambient
                HOST-MODE WATCH PARTY's programming pool first (chatGameApi
                buildAmbientPool), and that party auto-starts from any human's
                presence poll within ~a minute — synced, captioned, seatable.
                The client-local muted iframe was a third TV state that mostly
                duplicated it; since the 08-10 collapse walk-ins only ever saw
                its one-line bar anyway. pinned_pick DATA stays (pool seed +
                suggestion rails); the mobile TV place keeps its merged card
                (mobile freeze). */}

            {/* 🍿 MINIMIZED-PARTY BAR (07-17, tab removal): a live party with
                nothing docked means the user minimized the stage (⌄) — the
                theater sits in the hidden holder with audio playing. The old
                destination was the 🍿 tab; with the tab gone, this slim bar is
                the way back. */}
            {!placesNav && !portedPlaceOn && !stageTab && watchParty && !isBotMode && !chatGameStatus?.competitive_mode && !chatGameStatus?.imposter_mode && (gameType === 'chat-game' || gameType === 'chat') && (
              <div
                onClick={() => dockToStage('watch_party')}
                style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  padding: '9px 14px', marginBottom: 8,
                  borderRadius: 8, cursor: 'pointer',
                  // 🖼️ 09-17 declutter: a pill, not a full-width strip, in a room
                  ...(roomDeclutter ? { width: 'fit-content', maxWidth: 'min(420px, 100%)', boxSizing: 'border-box', borderRadius: 999, padding: '5px 12px' } : {}),
                  background: 'linear-gradient(90deg, rgba(123,31,162,0.4), rgba(40,0,60,0.45))',
                  border: `1px solid ${THEME.accent || '#7b1fa2'}80`,
                  color: '#fff',
                }}
              >
                {/* One grammar for host-held and human parties (Nina 08-13
                    fold): same icon, same words, same CTA — the host is just
                    the player who started it. */}
                <span style={{ fontSize: '1.2em' }}>🍿</span>
                <span style={{ flex: 1, fontSize: '0.82em', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {inTheater ? 'Party playing: ' : 'Watch party on: '}<b>{watchParty.title}</b>
                </span>
                <span style={{ fontSize: '0.78em', fontWeight: 700, flexShrink: 0, padding: '4px 11px', borderRadius: 999, backgroundColor: `${THEME.accent || '#7b1fa2'}cc` }}>
                  ⌃ Bring it back
                </span>
              </div>
            )}

            {/* 🍿▬ IDLE-STAGE LAUNCHER FALLBACK (07-17, tab removal): nothing
                docked, no party, no usable ambient pick → parties stay
                startable with the tab gone. Ambient pick outranks this (D3).
                Collapsed by default (Nina 08-10, option A): the hero + paste
                bar + suggestion rail stacked ~250px on every join. Expanded
                skips the hero (hideIdleHero) — the bar already said it.
                ambientHidden dropped from the gate with the desktop ✕: a
                phone-set flag would have rendered this NEXT TO the pick bar
                after a rotate past 768px. */}
            {/* Gate no longer excludes pick-having spaces (08-13 fold): with
                the muted-loop block gone this bar is the one idle face, and
                the host party replaces it within ~a minute of presence. */}
            {/* ⌨️ Terminal spaces skip the idle watch-party banner (Nina 09-07:
                the show is the premise; a live party still renders normally). */}
            {!placesNav && !portedPlaceOn && !stageTab && !watchParty && !isBotMode && !isTerminalSpace && !chatGameStatus?.competitive_mode && !chatGameStatus?.imposter_mode && (gameType === 'chat-game' || gameType === 'chat') && (
              !idleStageOpen ? (
                <div
                  onClick={() => setIdleStageOpen(true)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 10,
                    padding: '9px 14px', marginBottom: 8,
                    borderRadius: 8, cursor: 'pointer',
                    background: 'linear-gradient(90deg, rgba(123,31,162,0.4), rgba(40,0,60,0.45))',
                    border: `1px solid ${THEME.accent || '#7b1fa2'}80`,
                    color: '#fff',
                  }}
                >
                  <span style={{ fontSize: '1.2em' }}>🍿</span>
                  <span style={{ flex: 1, fontSize: '0.82em', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    <b>Start a watch party</b> · synced with everyone in the room
                  </span>
                  <span style={{ fontSize: '0.78em', fontWeight: 700, flexShrink: 0, padding: '4px 11px', borderRadius: 999, backgroundColor: `${THEME.accent || '#7b1fa2'}cc` }}>
                    ⌃ Open
                  </span>
                </div>
              ) : (
              /* Header row REMOVED (Nina 08-12): onCollapse puts the ⌄ inside
                 the launcher's paste bar instead of a chrome row above it. */
              <div style={{ marginBottom: 8 }}>
                <WatchPartyLauncher
                  watchParty={watchParty}
                  history={chatGameStatus?.watch_party_history}
                  mediaSuggestions={chatGameStatus?.media_suggestions}
                  pinnedPick={shownPinnedPick}
                  inTheater={inTheater}
                  onTakeSeat={() => setInTheater(true)}
                  humanPlayerId={humanPlayerId}
                  THEME={THEME}
                  isMobile={isMobile}
                  hideIdleHero
                  onCollapse={() => setIdleStageOpen(false)}
                />
              </div>
              )
            )}

            {/* 🎭 THE STAGE — the docked slot at the top of the center column
                (STAGE_DOCK_SPEC_2026_07_11.md, D2). Whatever tab is docked
                renders here; a live watch party claims it for everyone. The
                theater arrives via watchPartyHostEl (see the portal near the
                LivestreamPanel mount) so it never remounts. Non-comp only. */}
            {/* 🏡 W2d: a place page's stage IS the painting's slot (placeWpSlotRef) */}
            {!placesNav && !portedPlaceOn && stageTab && !chatGameStatus?.competitive_mode && !chatGameStatus?.imposter_mode && (gameType === 'chat-game' || gameType === 'chat') && (() => {
              const stageLabels = { media: '🎬 Media', watch_party: '🍿 Watch Party', bot_vision: '🎙️ Bot Vision', games: '🎮 Arcade', highlights: '🫖 The Tea', live_share: '🖥️ Screen Share' };
              if (!stageLabels[stageTab]) return null;
              const chipStyle = {
                cursor: 'pointer', border: '1px solid #9c9c9c55',
                backgroundColor: 'transparent', color: THEME.textMuted || '#aaa',
                fontSize: '0.68em', fontWeight: 700, padding: '6px 12px', borderRadius: 999, // 28px touch floor (audit 07-15)
              };
              return (
                <div style={{ marginBottom: 8, display: 'flex', flexDirection: 'column', minHeight: 0, position: 'relative' }}>
                  {/* "🎭 On stage" header row REMOVED (Nina 08-12) — ⌄ and the
                      ↩ restore chip float over the content's top-right corner
                      instead. A live party gets NO chips: seated, the theater's
                      own chrome (Leave couch) is the exit; un-seated, the
                      one-line "Take a seat" bar IS the collapsed state (the
                      old ⌄ just swapped it for a near-identical bar, so the
                      08-11 keep-the-header pick is superseded). */}
                  {!(stageTab === 'watch_party' && watchParty) && (
                  <div style={{ position: 'absolute', top: 6, right: 14, zIndex: 6, display: 'flex', gap: 6 }}>
                    {stageRestore && stageLabels[stageRestore] && (
                      <button onClick={() => dockToStage(stageRestore)} style={{ ...chipStyle, color: '#fff', border: '1.5px solid rgba(255,255,255,0.5)', backgroundColor: 'rgba(0,0,0,0.45)' }}>
                        ↩ bring back {stageLabels[stageRestore]}
                      </button>
                    )}
                    <button onClick={() => dockToStage(null)} aria-label="Minimize" title="Minimize to the tab strip (a playing party keeps its sound)" style={{ ...chipStyle, fontSize: '1em', lineHeight: 1, padding: '5px 12px', color: '#fff', border: '1.5px solid rgba(255,255,255,0.5)', backgroundColor: 'rgba(0,0,0,0.45)' }}>
                      ⌄
                    </button>
                  </div>
                  )}
                  {stageTab === 'watch_party' && watchParty ? (
                    <div ref={stageWpSlotRef} style={{ display: 'flex', flexDirection: 'column', minHeight: 0 }} />
                  ) : (
                    <div style={{ maxHeight: stageVideoHApplied ? `${stageVideoHApplied}px` : '46dvh', overflowY: 'auto', overflowX: 'hidden' }}>
                      {stageTab === 'watch_party' && (
                        <WatchPartyLauncher
                          watchParty={watchParty}
                          history={chatGameStatus?.watch_party_history}
                          mediaSuggestions={chatGameStatus?.media_suggestions}
                          pinnedPick={shownPinnedPick}
                          inTheater={inTheater}
                          onTakeSeat={() => setInTheater(true)}
                          humanPlayerId={humanPlayerId}
                          THEME={THEME}
                          isMobile={isMobile}
                        />
                      )}
                      {stageTab === 'media' && (
                        <SuggestedMediaPanel mediaSuggestions={chatGameStatus?.media_suggestions} pinnedPick={shownPinnedPick} THEME={THEME} />
                      )}
                      {stageTab === 'games' && <GamesPanel THEME={THEME} />}
                      {stageTab === 'highlights' && !isPortedSpace && (
                        <HighlightsPanel allActions={results.flatMap(r => r.actions || [])} users={users} THEME={THEME} moderatorCharacter={chatGameStatus?.moderator_character} gameId={gameId} humanPlayerId={humanPlayerId} shipVotes={chatGameStatus?.ship_votes} builds={chatGameStatus?.builds || []} />
                      )}
                      {stageTab === 'bot_vision' && isPremiumFeatureEnabled('botVision') && (
                        <BotVisionPanel THEME={THEME} chatGameStatus={chatGameStatus} setChatGameStatus={setChatGameStatus} gameId={gameId} />
                      )}
                      {/* 🖥️ Screenshare join poster — the takeover claims the
                          stage but watching stays a CLICK (no auto-connect):
                          ▶ Watch fires the same watchRequest path as clicking
                          a go-live chat message, opens the Live panel, and
                          hands the stage back. */}
                      {stageTab === 'live_share' && (() => {
                        const share = spaceLiveStreamers.find(s => s.mode === 'screen');
                        if (!share) return null;
                        return (
                          <div style={{
                            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                            gap: 12, padding: '28px 16px', borderRadius: 12,
                            background: `linear-gradient(160deg, rgba(0,0,0,0.5), ${THEME.accent}18)`,
                            border: `1px solid ${THEME.accent}30`,
                          }}>
                            <div style={{ fontSize: '2.2em', lineHeight: 1 }}>🖥️</div>
                            <div style={{ fontSize: '0.9em', fontWeight: 700, color: THEME.textOnPanel || '#fff', textAlign: 'center' }}>
                              {share.name} is sharing their screen
                            </div>
                            <button
                              onClick={() => {
                                setLiveWatchRequest({ identity: share.id, name: share.name, ts: Date.now() });
                                if (isThreeColumnLayout) { setLivestreamFloating(false); setLivestreamExpanded(true); }
                                else { setActiveTab('actions'); setChatActionTab('livestream'); }
                                // Stream plays in the Live panel now — hand the stage back
                                const restoreTo = stageRestore;
                                setStageRestore(null);
                                let persisted = null;
                                try { persisted = localStorage.getItem(`stageTab:${gameId}`) || null; } catch (e) { /* private mode */ }
                                setStageTab(restoreTo || persisted);
                              }}
                              style={{
                                padding: '10px 28px', borderRadius: 999, border: '1px solid rgba(255,255,255,0.18)',
                                background: 'linear-gradient(135deg, #ef4444, #dc2626)', color: '#fff',
                                fontSize: '0.85em', fontWeight: 800, letterSpacing: '0.5px', cursor: 'pointer',
                              }}
                            >
                              ▶ Watch
                            </button>
                          </div>
                        );
                      })()}
                    </div>
                  )}
                  {/* 📏 Resize bar — drag sizes the stage; double-tap resets */}
                  <div
                    onPointerDown={startStageResize}
                    onDoubleClick={() => {
                      setStageVideoH(null);
                      try { localStorage.removeItem('stageVideoH'); } catch (e) { /* private mode */ }
                    }}
                    title="Drag to resize the stage. Double-tap to reset"
                    style={{
                      // 18px grab zone (was 12 — thumbs kept missing it on touch)
                      height: 18, margin: '2px 0 0', cursor: 'ns-resize',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      touchAction: 'none',
                    }}
                  >
                    {/* Desktop grip was near-invisible (Nina, 07-17): wider,
                        brighter, accent-tinted with a soft glow */}
                    <div style={{
                      width: isMobile ? 52 : 96, height: 5, borderRadius: 999,
                      background: `${THEME.accent || '#7C4DFF'}90`,
                      boxShadow: `0 0 8px ${THEME.accent || '#7C4DFF'}50`,
                      opacity: 0.9,
                    }} />
                  </div>
                </div>
              );
            })()}
            {/* Off-stage home for the theater iframe — party audio keeps
                playing while you browse (see watchPartyHostEl reparent) */}
            <div ref={wpHiddenHolderRef} style={{ display: 'none' }} />
            {/* Off-screen home for the livestream node while no place hosts it */}
            <div ref={livestreamHiddenHolderRef} style={{ display: 'none' }} />

            {/* 🍿 Slim reminder above chat when something ELSE occupies your
                stage — one click swaps the party back (session-only, not
                persisted). Requires a truthy stageTab: the nothing-docked case
                belongs to the minimized-party bar above, and rendering both
                double-bannered every surface (Nina mobile screenshot 07-20). */}
            {!placesNav && !portedPlaceOn && watchParty && stageTab && stageTab !== 'watch_party' && !chatGameStatus?.competitive_mode && !chatGameStatus?.imposter_mode && (
              <div
                onClick={() => { setStageTab('watch_party'); setInTheater(true); }}
                style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  padding: '8px 14px', marginBottom: 8,
                  borderRadius: 8, cursor: 'pointer',
                  background: 'linear-gradient(90deg, rgba(123,31,162,0.5), rgba(40,0,60,0.55))',
                  border: `1px solid ${THEME.accent || '#7b1fa2'}`,
                  color: '#fff',
                }}
              >
                <span style={{ fontSize: '1.2em' }}>🍿</span>
                <span style={{ flex: 1, fontSize: '0.82em', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  Watch party in progress: <b>{watchParty.title}</b>
                </span>
                <span style={{
                  fontSize: '0.78em', fontWeight: 700, flexShrink: 0,
                  padding: '4px 12px', borderRadius: 999,
                  backgroundColor: THEME.accent || '#7b1fa2',
                }}>
                  Back on stage
                </span>
              </div>
            )}

            {/* Game Phase Banner - competitive & imposter modes.
                ⚔️ NOT on comp places phones (08-02, option B): the phase
                TICKER on the nav replaces it there — one phase surface. */}
            {(chatGameStatus?.competitive_mode || chatGameStatus?.imposter_mode) && (gameType === 'chat-game' || gameType === 'chat') && !compPlacesNav && (() => {
              const isGameOver = chatGameStatus?.game_completed;
              const isVoting = waitingForVote || chatGameStatus?.is_voting;
              const isConclusion = chatGameStatus?.current_round_type === 'conclusion';
              const hasSpeaker = !!chatGameStatus?.next_speaker;
              const isYourTurnBanner = hasSpeaker && humanPlayerId && chatGameStatus.next_speaker.player_id === humanPlayerId;
              // next_speaker lingers stale on us through the round→vote transition, so
              // "Your Turn!" flashed between rounds (Nina 08-28). Only claim the turn when
              // the backend confirms it (your_turn) or a live turn request is in hand.
              const yourTurnLive = isYourTurnBanner && !!(chatGameStatus?.your_turn || (isHumanTurn && currentRequestId));
              const isImposter = chatGameStatus?.imposter_mode;

              // Round label — during voting, use the voting round's own number
              // (current_competitive_round may have already incremented after votes are counted)
              const totalRounds = isImposter
                ? chatGameStatus?.imposter_word_rounds_total || 3
                : chatGameStatus?.total_rounds || null;
              const roundNum = isVoting && chatGameStatus?.voting_data?.round_number
                ? chatGameStatus.voting_data.round_number
                : isImposter
                  ? Math.min((chatGameStatus?.imposter_word_round || 0) + 1, totalRounds)
                  : chatGameStatus?.current_competitive_round || chatGameStatus?.round_number || 1;

              // Turn progress
              const turnIdx = chatGameStatus?.turn_index;
              const turnTotal = chatGameStatus?.turn_total;
              const hasTurnProgress = turnIdx != null && turnTotal != null && !isVoting && !isConclusion && !isGameOver;

              // Phase status text
              let phaseText;
              let phaseEmoji;
              // Speaker identity beat: when an AI/other player is up, show their avatar +
              // seat-colored glow in the banner — the always-on "who is acting" anchor.
              let speakerBadge = null;
              if (isGameOver) {
                phaseText = 'Game Over';
                phaseEmoji = '\u{1F3C6}';
              } else if (isVoting) {
                phaseText = 'Voting';
                phaseEmoji = '\u{1F5F3}\uFE0F';
              } else if (isConclusion) {
                phaseText = 'Final Results';
                phaseEmoji = '\u{1F3C6}';
              } else if ((isYourTurnBanner || hasSpeaker) && !isPlaying) {
                phaseText = 'Waiting for you to press Play!';
                phaseEmoji = '\u25B6\uFE0F';
              } else if (yourTurnLive) {
                phaseText = 'Your Turn!';
                phaseEmoji = '\u{1F3AF}';
              } else if (isYourTurnBanner) {
                // next_speaker still points at us but the turn isn't confirmed — the
                // round is wrapping into the vote. Neutral hold, never "Your Turn!".
                phaseText = 'One moment…';
                phaseEmoji = '\u{23F3}';
              } else if (isPlaying && chatGameStatus?.simultaneous?.phase === 'collect') {
                // Simultaneous write: everyone is writing at the same time — show the
                // sealed-statements counter instead of a single speaker.
                const sim = chatGameStatus.simultaneous;
                phaseText = `Everyone writes at once! ${Math.min(sim.submitted, sim.total)}/${sim.total} statements in`;
                phaseEmoji = '\u{1F58B}️';
              } else if (isPlaying && chatGameStatus?.simultaneous?.phase === 'reveal') {
                phaseText = 'Statements unsealing!';
                phaseEmoji = '\u{1F4DC}';
              } else if (hasSpeaker) {
                const speakerUser = users?.find(u => u.game_player_id === chatGameStatus.next_speaker.player_id || u.id === chatGameStatus.next_speaker.player_id);
                const isHumanSpeaker = chatGameStatus.next_speaker.is_human;
                const speakerName = chatGameStatus.next_speaker.player_name;
                if (isHumanSpeaker && speakerName === 'You') {
                  phaseText = "Another carbon unit's (human) turn";
                } else {
                  const speakerModel = speakerUser ? getDisplayModelName(speakerUser) : null;
                  phaseText = speakerModel && speakerModel !== 'Human Player'
                    ? `${speakerName} (${speakerModel})`
                    : `${speakerName}'s turn`;
                  if (speakerUser) {
                    speakerBadge = {
                      user: speakerUser,
                      name: speakerName,
                      model: speakerModel && speakerModel !== 'Human Player' ? speakerModel : null,
                      color: playerColorFor(users, speakerUser.game_player_id || speakerUser.id) || (THEME.accent || '#6366f1'),
                    };
                  }
                }
                phaseEmoji = '\u{1F4AC}';
              } else if (roundNum === 1 && !isPlaying) {
                phaseText = 'Waiting for Players to Ready Up...';
                phaseEmoji = '\u{1F44B}';
              } else {
                phaseText = 'Processing...';
                phaseEmoji = '\u{23F3}';
              }

              // Banner color. The pulsing glow is NOT animated box-shadow on
              // the banner (that's a nonstop CPU paint loop — fx-bench 07-09):
              // a sibling underlay carries the STRONG shadow statically and
              // pulses its opacity instead, which runs on the GPU compositor.
              // Same visible throb, near-zero cost.
              let bannerBg, bannerShadow, bannerGlowShadow = null, bannerGlowAnim = null;
              let bannerUsesTheme = false; // true when bannerBg paints with THEME.accent
              if (isGameOver) {
                bannerBg = 'linear-gradient(135deg, #854d0e 0%, #ca8a04 50%, #a16207 100%)';
                bannerShadow = '0 2px 16px rgba(202, 138, 4, 0.5)';
              } else if (isVoting) {
                bannerBg = 'linear-gradient(135deg, #991b1b 0%, #dc2626 50%, #b91c1c 100%)';
                bannerShadow = '0 2px 20px rgba(220, 38, 38, 0.7), 0 0 40px rgba(239, 68, 68, 0.4)';
                bannerGlowShadow = '0 4px 30px rgba(220, 38, 38, 0.9), 0 0 60px rgba(239, 68, 68, 0.6)';
                bannerGlowAnim = 'bannerGlowPulse 1.5s ease-in-out infinite';
              } else if (isConclusion) {
                bannerBg = 'linear-gradient(135deg, #854d0e 0%, #ca8a04 100%)';
                bannerShadow = '0 2px 16px rgba(202, 138, 4, 0.5)';
              } else if ((isYourTurnBanner || hasSpeaker) && !isPlaying) {
                bannerBg = `linear-gradient(135deg, ${THEME.accent || '#6366f1'} 0%, ${THEME.primary || '#8b5cf6'} 100%)`;
                bannerShadow = `0 2px 20px ${THEME.accent || '#6366f1'}80, 0 0 40px ${THEME.accent || '#6366f1'}40`;
                bannerGlowShadow = `0 2px 28px ${THEME.accent || '#6366f1'}cc, 0 0 56px ${THEME.accent || '#6366f1'}66`;
                bannerGlowAnim = 'bannerGlowPulse 1.8s ease-in-out infinite';
                bannerUsesTheme = true;
              } else if (hasSpeaker || (isPlaying && chatGameStatus?.simultaneous?.phase)) {
                bannerBg = 'linear-gradient(135deg, #15803d 0%, #22c55e 100%)';
                bannerShadow = '0 2px 16px rgba(34, 197, 94, 0.5)';
                bannerGlowShadow = '0 2px 24px rgba(34, 197, 94, 0.8), 0 0 40px rgba(34, 197, 94, 0.35)';
                bannerGlowAnim = 'bannerGlowPulse 2s ease-in-out infinite';
              } else if (roundNum === 1 && !isPlaying) {
                bannerBg = `linear-gradient(135deg, ${THEME.accent || '#6366f1'} 0%, ${THEME.primary || '#8b5cf6'} 100%)`;
                bannerShadow = `0 2px 20px ${THEME.accent || '#6366f1'}80, 0 0 40px ${THEME.accent || '#6366f1'}40`;
                bannerGlowShadow = `0 2px 28px ${THEME.accent || '#6366f1'}cc, 0 0 56px ${THEME.accent || '#6366f1'}66`;
                bannerGlowAnim = 'bannerGlowPulse 1.8s ease-in-out infinite';
                bannerUsesTheme = true;
              } else {
                bannerBg = `linear-gradient(135deg, ${THEME.accent || '#6366f1'} 0%, ${THEME.primary || '#8b5cf6'} 100%)`;
                bannerShadow = `0 2px 12px ${THEME.accent || '#6366f1'}40`;
                bannerUsesTheme = true;
              }

              // 🖋️ Ink adapts to the banner's paint (Nina 07-26: whodunnit's
              // GOLD accent made the lobby banner khaki and the white 35-70%
              // text drowned in it). Theme-painted states test the accent's
              // luminance and flip to dark ink when it's light; the hardcoded
              // red/green/dark-gold states keep white.
              const accentHex = String(THEME.accent || '#6366f1').replace('#', '');
              const accentLum = accentHex.length >= 6
                ? (0.299 * parseInt(accentHex.slice(0, 2), 16)
                  + 0.587 * parseInt(accentHex.slice(2, 4), 16)
                  + 0.114 * parseInt(accentHex.slice(4, 6), 16)) / 255
                : 0.4;
              const lightBanner = bannerUsesTheme && !Number.isNaN(accentLum) && accentLum > 0.55;
              const ink = lightBanner
                ? { strong: '#241a08', soft: 'rgba(28,20,8,0.85)', faint: 'rgba(28,20,8,0.65)', dim: 'rgba(28,20,8,0.45)' }
                : { strong: '#fff', soft: 'rgba(255,255,255,0.7)', faint: 'rgba(255,255,255,0.55)', dim: 'rgba(255,255,255,0.35)' };

              // Right-side info
              let rightText = '';
              let rightColor = ink.soft;
              let rightElement = null; // For JSX content instead of plain text
              if (isGameOver) {
                rightText = '';
              } else if (hasTurnProgress && !isYourTurnBanner) {
                // Show animated typing dots when an AI/other player is thinking
                rightElement = (
                  <span style={{ fontSize: '1.6em', color: ink.strong, fontWeight: 900, letterSpacing: '4px' }}>
                    <span className="typing-dots">...</span>
                  </span>
                );
              } else if (isVoting) {
                rightText = `Round ${roundNum}${totalRounds ? `/${totalRounds}` : ''}`;
                rightColor = ink.faint;
              } else if ((chatGameStatus?.eliminated_players?.length || 0) > 0 && !isImposter) {
                rightText = `${chatGameStatus.eliminated_players.length} eliminated`;
                rightColor = '#ff4444';
              }

              return (
              <div style={{ position: 'relative', marginBottom: '8px', flexShrink: 0 }}>
              {/* Glow underlay — static strong shadow, opacity-pulsed on the
                  compositor. Sits behind the banner (same rounded rect). */}
              {bannerGlowAnim && (
                <div aria-hidden="true" style={{
                  position: 'absolute',
                  inset: 0,
                  borderRadius: '10px',
                  boxShadow: bannerGlowShadow,
                  animation: bannerGlowAnim,
                  pointerEvents: 'none',
                }} />
              )}
              <div ref={phaseBannerRef} style={{
                display: 'flex',
                flexDirection: 'column',
                position: 'relative',
                borderRadius: '10px',
                overflow: 'hidden',
                flexShrink: 0,
                background: bannerBg,
                boxShadow: bannerShadow,
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  padding: '8px 16px',
                }}>
                  <span style={{ fontSize: '1.1em' }}>
                    {phaseEmoji}
                  </span>
                  <span style={{
                    fontWeight: 800,
                    fontSize: '0.85em',
                    color: ink.strong,
                    textTransform: 'uppercase',
                    letterSpacing: '1.5px',
                    // Mobile audit (07-16): flexShrink 0 forced full content width and
                    // the banner clipped mid-word ("WAITING FOR PLAYERS T"). On phones
                    // it shrinks and wraps to a second line instead.
                    flexShrink: isMobile ? 1 : 0,
                    minWidth: 0,
                  }}>
                    {`Round ${roundNum}${totalRounds ? `/${totalRounds}` : ''}`}
                    {!isGameOver && (speakerBadge ? ' \u2014' : ` \u2014 ${phaseText}`)}
                  </span>
                  {!isGameOver && speakerBadge && (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', minWidth: 0 }}>
                      <ProfileImage user={speakerBadge.user} size={20} borderColor={speakerBadge.color} />
                      <span style={{
                        fontWeight: 900, fontSize: '0.85em', color: ink.strong,
                        textTransform: 'uppercase', letterSpacing: '1px',
                        textShadow: `0 0 10px ${speakerBadge.color}, 0 0 20px ${speakerBadge.color}66`,
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      }}>
                        {speakerBadge.name}
                      </span>
                      <span style={{ fontWeight: 700, fontSize: '0.72em', color: ink.soft, textTransform: 'uppercase', letterSpacing: '1px', flexShrink: 0 }}>
                        is up{speakerBadge.model ? ` (${speakerBadge.model})` : ''}
                      </span>
                    </span>
                  )}
                  <div style={{ flex: 1 }} />
                  {rightElement ? rightElement : rightText ? (
                    <span style={{
                      fontSize: '0.75em',
                      color: rightColor,
                      fontWeight: 700,
                    }}>
                      {rightText}
                    </span>
                  ) : null}
                </div>
                {/* Game status message (e.g. "Player X is thinking...") */}
                {/* 🤔 Desktop rule (Nina 08-14, screenshot): the server's
                    "X is thinking..." game_status never renders on desktop —
                    the floating TypingIndicator pill is desktop's one thinking
                    surface, period. Every other server status (Waiting for X,
                    poke throttled, limit reached) still renders here. Mobile
                    untouched — its surfaces de-dupe each other (08-06 arc). */}
                {gameStatusMessage && !isGameOver
                  && !(!isMobile && /is thinking/i.test(gameStatusMessage)) && (
                  <div style={{
                    padding: '0 16px 6px 42px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                  }}>
                    <span style={{
                      width: '5px',
                      height: '5px',
                      borderRadius: '50%',
                      backgroundColor: ink.soft,
                      animation: 'dotBounce 1.4s ease-in-out infinite',
                      flexShrink: 0,
                    }} />
                    <span style={{
                      color: ink.soft,
                      fontSize: '0.72em',
                      fontWeight: 500,
                      fontStyle: 'italic',
                    }}>{gameStatusMessage}</span>
                  </div>
                )}
                {/* 🎬 ACT TRACKER (user 07-16: players must be able to FOLLOW the
                    game flow) — whodunnit-interactive only: the night's fixed
                    loop as three acts, current one lit. Rides the banner so
                    there's exactly ONE "where are we" surface. */}
                {chatGameStatus?.investigation_enabled && !isImposter && !isGameOver && !isConclusion && (() => {
                  const rt = chatGameStatus?.round_type || chatGameStatus?.current_round_type;
                  const finale = totalRounds ? roundNum >= totalRounds : false;
                  const acts = [
                    { emoji: '🔍', label: 'INVESTIGATE' },
                    { emoji: '📜', label: 'STATEMENTS' },
                    { emoji: '🗳️', label: finale ? 'THE ACCUSATION' : 'THE VOTE' },
                  ];
                  // Lobby fix (Nina 07-25): before the ready check resolves the game
                  // hasn't started, so NO act is lit — the old fallback lit STATEMENTS
                  // in the lobby, which taught players the tracker lies. Pre-game the
                  // chips read as a preview under a TONIGHT label.
                  const inLobby = !!(chatGameStatus?.is_ready_check && !chatGameStatus?.ready_check_data?.done);
                  const activeIdx = inLobby ? -1 : rt === 'investigation' ? 0 : (isVoting || rt === 'voting') ? 2 : 1;
                  return (
                    <div style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                      padding: '0 10px 7px', flexWrap: 'wrap',
                    }}>
                      {inLobby && (
                        <span style={{ fontSize: '0.6em', fontWeight: 800, letterSpacing: '1px', color: ink.faint }}>
                          TONIGHT:
                        </span>
                      )}
                      {acts.map((a, i) => (
                        <React.Fragment key={a.label}>
                          {i > 0 && <span style={{ color: ink.dim, fontSize: '0.6em' }}>›</span>}
                          <span style={{
                            display: 'inline-flex', alignItems: 'center', gap: 3,
                            padding: '2px 8px', borderRadius: 999,
                            // Active chip keeps white-on-dark: its own dark pill
                            // carries contrast on every banner color
                            background: i === activeIdx ? 'rgba(0,0,0,0.4)' : 'transparent',
                            border: i === activeIdx ? '1px solid rgba(255,255,255,0.55)' : '1px solid transparent',
                            color: i === activeIdx ? '#fff' : i < activeIdx ? ink.faint : ink.dim,
                          }}>
                            {i < activeIdx ? '✓' : a.emoji} {a.label}
                          </span>
                        </React.Fragment>
                      ))}
                      {/* Narrator line (mock-approved 07-25): the banner answers
                          "where is the story", one italic beat per phase. The
                          dock below answers "what do I do". */}
                      <span style={{
                        flexBasis: '100%', textAlign: 'center', fontStyle: 'italic',
                        fontSize: '0.62em', color: ink.soft, paddingTop: 2,
                      }}>
                        {inLobby
                          ? 'Suspects gather. Nobody looks nervous yet. Everybody is.'
                          : activeIdx === 0
                            ? 'The scene is open. Bag what you can, grill who you dare.'
                            : activeIdx === 2
                              ? (finale ? 'One name. One charge. No appeals.' : "Somebody's story ends tonight.")
                              : 'Suspects go on record. Weak stories get remembered.'}
                      </span>
                    </div>
                  );
                })()}
                {/* Voting countdown timer bar removed — was showing after voting completed */}
              </div>
              </div>
              );
            })()}


            {/* 🏡⋯ W2d: a place page has no tab strip (Story is in the ⋯ menu and
                /story). While Story is open, the way back to the room is right
                here, above it (the composer lives on the room only). */}
            {portedPlaceOn && !isMobile && chatActionTab !== 'all' && (
              <div data-place-back="" style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 4px 8px', flexShrink: 0, color: 'rgba(255,238,216,0.8)', font: '600 13px/1.2 -apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif' }}>
                <button type="button" data-prop-ui="" onClick={() => setChatActionTab('all')}
                  style={{ '--prop-bg': 'rgba(240,172,85,0.08)', background: 'rgba(240,172,85,0.08)', border: '1px solid rgba(240,172,85,0.35)', borderRadius: 999, color: '#F5C580', padding: '6px 12px', cursor: 'pointer', font: 'inherit', textShadow: 'none', transform: 'none', boxShadow: 'none' }}
                >‹ back to the room</button>
                <span>{chatActionTab === 'recap' ? '📖 Your story so far' : null}</span>
              </div>
            )}
            {/* Tab Bar */}
            {(() => {
              const isCompMode = chatGameStatus?.competitive_mode || chatGameStatus?.imposter_mode;
              const chromeTab = (tabKey, label, isActive) => ({
                flex: isCompMode ? 1 : undefined,
                padding: isMobile ? '10px 14px' : (isCompMode ? '10px 14px' : '10px 20px'),
                // Mobile + narrow desktop: always nowrap so the tab bar can
                // horizontal-scroll instead of wrapping/squishing (Highlights was
                // getting clipped; 768-1100px windows clipped with NO scroll at
                // all, user report 07-10). Comp mode: nowrap because flex:1
                // stretches tabs evenly. Wide desktop: original behavior.
                whiteSpace: (isCompMode || isMobile || isNarrowDesktop) ? 'nowrap' : undefined,
                background: isActive
                  ? `linear-gradient(180deg, ${THEME.accent}30 0%, ${THEME.bgMedium} 100%)`
                  : `${THEME.bgMedium}cc`,
                color: isActive ? '#fff' : (isCompMode ? THEME.textOnPanel : '#fff'),
                border: 'none',
                borderRadius: '10px 10px 0 0',
                fontSize: isMobile ? '14px' : '15px',
                fontWeight: isActive ? 700 : 600,
                opacity: isActive ? 1 : (isCompMode ? 0.85 : 0.95),
                textShadow: !isActive && !isCompMode ? '0 1px 3px rgba(0,0,0,0.5)' : undefined,
                cursor: 'pointer',
                transition: 'all 0.2s',
                position: 'relative',
                zIndex: isActive ? 2 : 1,
                marginBottom: isActive ? '-1px' : '0',
                borderBottom: isActive ? `3px solid ${THEME.accent}` : '2px solid transparent',
                boxShadow: isActive ? `0 -2px 8px ${THEME.accent}20` : 'none',
              });
              return (<>
            {/* 🕵️🧰 LOCALS + SHELF sticky strip (Nina 07-20 · MOBILE-ONLY
                since 07-22): the space's furniture pinned above the feed.
                On phones this is the ONLY cast surface (no left rail), so
                avatars + chips stay. On desktop the left rail already shows
                the locals and the chips live right-aligned IN the tab row —
                the strip's whole row goes away (Nina: the tiny avatars
                duplicated the rail). Non-comp only. */}
            {/* 📱 places nav (Nina 08-01): this strip moved to the top of the
                LOCALS place — chat opens straight into the conversation.
                Shelf rides the ＋ tray; dossier unseen badges the Locals tab. */}
            {(gameType === 'chat-game' || gameType === 'chat') && !isCompMode && isMobile && !placesNav && (
              <div style={{
                display: 'flex', alignItems: 'center', gap: 8,
                padding: '4px 8px', marginBottom: 4,
                ...(isMobile ? {
                  position: 'sticky', top: 0, zIndex: 60,
                  background: 'rgba(8,10,14,0.97)', // no blur: near-opaque bg + the ambient canvas repaints every frame, so backdrop-filter here re-blurred per frame for invisible effect (perf 08-13)
                  borderRadius: 10,
                } : {}),
              }}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  {(users || []).filter(u => !u.is_human).slice(0, 6).map((u, i) => {
                    const img = u.profile_image_url || u.data?.profile_image_url;
                    return (
                      <button
                        key={u.id || i}
                        onClick={() => setChatCastPid(u.game_player_id || u.id)}
                        title={u.name}
                        aria-label={`About ${u.name}`}
                        style={{
                          width: 28, height: 28, borderRadius: '50%', padding: 0,
                          marginLeft: i === 0 ? 0 : -8, cursor: 'pointer',
                          border: `2px solid ${THEME.accent}60`, overflow: 'hidden',
                          background: THEME.bgLight || '#1a1530', zIndex: 6 - i,
                          position: 'relative', flexShrink: 0,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: 14, lineHeight: 1,
                        }}
                      >
                        {img
                          ? <img src={img} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                          : <span>{u.avatar_emoji || u.data?.avatar_emoji || '🙂'}</span>}
                      </button>
                    );
                  })}
                </div>
                {[
                  // openers close the sibling — the drawers share an anchor and
                  // would stack (the edge tabs used to enforce this)
                  // 📱 places nav: the dossier chip NAVIGATES to the Locals
                  // place (its drawer instance is gated off there — one mount)
                  { k: 'dossier', l: '🕵️ Dossier', n: dossierUnseen, open: () => { if (placesNav) { setMobileView('locals'); return; } setShelfOpen(false); setDossierOpen(true); } },
                  { k: 'shelf', l: '🧰 Shelf', n: shelfUnseen, open: () => { setDossierOpen(false); setShelfOpen(true); } },
                ].map(c => (
                  <button
                    key={c.k}
                    onClick={c.open}
                    style={{
                      padding: '4px 10px', borderRadius: 999, cursor: 'pointer',
                      fontSize: '0.68em', fontWeight: 800, whiteSpace: 'nowrap',
                      border: `1px solid ${THEME.accent}40`,
                      backgroundColor: 'transparent',
                      color: THEME.textMuted, flexShrink: 0,
                    }}
                  >
                    {c.l}
                    {c.n > 0 && (
                      <span style={{
                        marginLeft: 5, display: 'inline-block', minWidth: 14, borderRadius: 7,
                        padding: '0 3px', backgroundColor: '#ef4444', color: '#fff',
                        fontSize: '0.85em', fontWeight: 800, lineHeight: '14px', textAlign: 'center',
                      }}>
                        {c.n}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            )}
            <div
              className={(isMobile || isNarrowDesktop) ? 'space-tab-bar' : undefined}
              style={{
                display: (portedPlaceOn && !isMobile) ? 'none' : 'flex', // 🏡 W2d: the ⋯ menu + /story are the doors
                gap: '2px',
                marginBottom: isMobile ? '6px' : '8px',
                borderBottom: `1px solid ${THEME.accent}30`,
                flexShrink: 0,
                alignItems: 'flex-end',
                paddingLeft: '4px',
                // 📌 Sticky nav (Nina 07-25): the strip pins to the top of its
                // scroll container so lane-switching never needs a scroll-up.
                // Glass backdrop keeps folder tabs legible over scrolled
                // content; inert where the ancestor doesn't scroll. (Phones
                // use the fixed bottom nav instead — this strip is a
                // zero-height anchor there, see below.)
                position: 'sticky',
                top: 0,
                zIndex: 25,
                background: 'rgba(10, 10, 16, 0.82)',
                backdropFilter: 'blur(8px)',
                WebkitBackdropFilter: 'blur(8px)',
                // Desktop (wide AND narrow): the glass hugs the TABS instead of
                // painting a full-width black bar over the wallpaper (Nina
                // 08-04 "what's this black thing right of the tabs?"; still
                // showed <1100px where the first fix didn't reach). The
                // legibility job only exists where tabs are. Composes with the
                // narrow-desktop overflowX below: tabs fit → glass hugs them;
                // tabs overflow → maxWidth clamps and the row scrolls as
                // before. Phones keep full width (strip is a zero-height
                // anchor on chat spaces anyway).
                ...(!isMobile ? { width: 'fit-content', maxWidth: '100%', borderTopRightRadius: 10 } : {}),
                // Mobile + narrow desktop (<1100px): horizontal scroll so the
                // right-most tabs (especially ✨ Highlights) stay reachable —
                // 768-1100px windows used to clip them with no scroll at all
                // (user report 07-10). overflow:auto is inert when tabs fit.
                // Wide desktop keeps its original layout — no scroll, no
                // className, so the CSS rules below stay dormant there.
                ...((isMobile || isNarrowDesktop) ? {
                  overflowX: 'auto',
                  overflowY: 'hidden',
                  WebkitOverflowScrolling: 'touch',
                  scrollbarWidth: 'none',
                  msOverflowStyle: 'none',
                } : {}),
                // 📱 Stage 2 (07-17): chat-type spaces get the bottom nav on
                // phones — the mid-page strip collapses to a ZERO-HEIGHT,
                // position-preserving anchor (not display:none) so nav taps can
                // still scrollIntoView to where the tab panels start. Other
                // game types (charts/videos) keep their strip.
                ...((isMobile && (gameType === 'chat-game' || gameType === 'chat')) ? {
                  height: 0, minHeight: 0, overflow: 'hidden', visibility: 'hidden',
                  margin: 0, padding: 0, border: 'none',
                } : {}),
              }}
            >
              {gameType === 'chat-game' || gameType === 'chat' ? (
                <>
                  {/* ONE nav row (Nina 07-25, brainstorm verdict): the Feed tab +
                      inner chip row merged into a single folder-tab lineup —
                      Whispers · Tea · Media · Arcade · All (All last, still the
                      default lane). Folder tab = navigation; loose pill = filter,
                      nowhere else. Comp keeps Competition / Recap / Arcade. */}
                  {isCompMode && (
                    <button
                      onClick={() => { setActiveTab('actions'); setChatActionTab('all'); setTabDots(prev => ({ ...prev, chat: false })); }}
                      style={{ ...chromeTab('all', '⚔️', chatActionTab === 'all'), position: 'relative' }}
                    >
                      ⚔️ Competition
                      {tabDots.chat && chatActionTab !== 'all' && (
                        <span style={{
                          position: 'absolute', top: 6, right: 6,
                          width: 8, height: 8, borderRadius: '50%',
                          backgroundColor: '#ef4444',
                          boxShadow: '0 0 6px rgba(239,68,68,0.6)',
                          animation: 'liveBadgePulse 2s ease-in-out infinite',
                        }} />
                      )}
                    </button>
                  )}
                  {/* Competitive/Imposter: Recap, Games tabs */}
                  {isCompMode && (
                    <>
                      <button
                        onClick={() => { setActiveTab('actions'); setChatActionTab('alerts'); }}
                        style={chromeTab('alerts', '📋 Recap', chatActionTab === 'alerts')}
                      >
                        📋 Recap
                      </button>
                      <button
                        onClick={() => { setActiveTab('actions'); setChatActionTab('games'); setTabDots(prev => ({ ...prev, games: false })); }}
                        style={{ ...chromeTab('games', '🎮 Arcade', chatActionTab === 'games'), position: 'relative' }}
                      >
                        🎮 Arcade
                        {tabDots.games && chatActionTab !== 'games' && (
                          <span style={{
                            position: 'absolute', top: 6, right: 6,
                            width: 8, height: 8, borderRadius: '50%',
                            backgroundColor: '#22c55e',
                            boxShadow: '0 0 6px rgba(34,197,94,0.6)',
                            animation: 'liveBadgePulse 2s ease-in-out infinite',
                          }} />
                        )}
                      </button>
                    </>
                  )}
                  {/* Non-competitive: the full lane lineup as folder tabs */}
                  {!isCompMode && (
                    <>
                      {[
                        // 🍿 Watch Party tab REMOVED (Nina, 07-17): the stage is
                        // watch party's one home. 📡 Live tab dropped (07-19) —
                        // the LIVE pill navigates there. 🫖 Tea tab RETIRED
                        // (07-26): its panel rides as a card atop the feed;
                        // 'highlights' lives on as an alias key only. 🤫 Whispers
                        // tab REMOVED (Nina 08-03): All was already unfiltered,
                        // so the lane was a strict subset — the Tea card and the
                        // tea badge moved home to Room. 'all' leads the strip
                        // now, wearing the name "Room". Bot Vision gated behind
                        // PREMIUM_FEATURES.botVision (off in v1).
                        // 🖊️ Line-icon parity with the mobile places nav (Nina
                        // 08-02): the strip wears the same stroke set — emoji
                        // stays only where no glyph exists yet (Bot Vision).
                        // ⌨️ Terminal single tab (Nina 09-07): the whole space
                        // is one show view — the Room tab wears the show's name.
                        { key: 'all', label: isTerminalSpace ? '⌨️ The Terminal' : 'Room', iconName: isTerminalSpace ? undefined : 'chat', dotKey: 'chat' },
                        // ⌨️ Workbench (The Terminal, 08-19): the per-seat
                        // work/thought/code panel — terminal spaces only
                        // (filter below). Emoji label: no keyboard glyph in
                        // the LineIcon stroke set yet.
                        { key: 'workbench', label: '⌨️ Workbench' },
                        // 🐣 D12 (ruled 08-28): ported spaces read Watch/Story. DISPLAY ONLY
                        // — keys, data-tab-name and deep links keep media/recap forever.
                        { key: 'media', label: isPortedSpace ? 'Watch' : 'Media', iconName: 'tv' },
                        { key: 'bot_vision', label: '🎙️ Bot Vision' },
                        // 📌 Recap (Nina 08-06, widened 08-07): the catch-up
                        // lane, BEFORE Arcade. Shipped house-only (canon
                        // plaques + pinned memories); it is now EVERY
                        // non-comp space's story surface — the Room's "Story
                        // so far" drawer folded into it, so the house gate
                        // below is gone. Empty sections self-hide, so a
                        // brand-new space just sees Latest Activity.
                        { key: 'recap', label: isPortedSpace ? 'Story' : 'Recap', iconName: 'chat' },
                        { key: 'games', label: 'Arcade', iconName: 'arcade', dotKey: 'games' },
                      ].filter(tab => tab.key !== 'bot_vision' || isPremiumFeatureEnabled('botVision'))
                        // 🧹 house gate on 'recap' REMOVED (Nina 08-07) — the
                        // lane is universal now. It was:
                        //   .filter(t => t.key !== 'recap' || chatActionTab === 'recap' || !!houseStatus)
                        // ⌨️ Terminal reshape (Nina 08-19): Workbench exists ONLY
                        // in terminal spaces; Arcade + Media leave them. The flag
                        // is stamped at create and never flips mid-session, so the
                        // keep-alive escapes below are belt-and-suspenders only.
                        .filter(tab => tab.key !== 'workbench' || isTerminalSpace)
                        // ⌨️ SINGLE TAB (Nina 09-07): terminal spaces collapse
                        // the strip to The Terminal alone — the show panel now
                        // rides atop the Room feed, so Workbench/Recap would be
                        // duplicates. Keep-alive: whatever tab you're ON stays
                        // until you leave (deep links never strand).
                        .filter(tab => chatActionTab === tab.key || !isTerminalSpace || tab.key === 'all')
                        // 🎯 goal-preset tool gates (Nina 07-29): explicit false
                        // hides the lane — unless you're already on it (deep links
                        // and old spaces never strand; missing key = ON)
                        .filter(tab => chatActionTab === tab.key
                          || tab.key !== 'games'
                          || (chatGameStatus?.tool_flags?.arcade !== false && !isTerminalSpace && !isPortedSpace))
                        // 🫥 hide-empty lanes (kept while active — deep links never
                        // strand).
                        .filter(tab => chatActionTab === tab.key
                          || (tab.key === 'media' ? (hasMedia && !isTerminalSpace) : true))
                        // 🎭 a docked tab leaves the strip; docking B returns A (spec D2)
                        .filter(tab => tab.key !== stageTab)
                        .map(tab => (
                        <div key={tab.key} style={{ position: 'relative', display: 'flex' }}>
                          <button
                            onClick={() => {
                              setActiveTab('actions');
                              setChatActionTab(tab.key);
                              if (tab.dotKey) setTabDots(prev => ({ ...prev, [tab.dotKey]: false }));
                              // 🍿 Media tab = media intent (Nina 08-05): with a
                              // party on, the click also unfurls the theater on
                              // the stage — an ambient watch EARNED by a real tap
                              // (the 08-04 no-auto-open rule gates walk-ins, not
                              // this). Desktop strip only; phones keep the TV
                              // place as the one seat.
                              if (tab.key === 'media' && !isMobile && watchParty && !inTheater && !isBotMode) {
                                setStageTab('watch_party');
                                setInTheater(true);
                              }
                            }}
                            style={{
                              ...chromeTab(tab.key, tab.label, chatActionTab === tab.key), position: 'relative',
                              // 📺 Lane C (AMBIENCE_TRIO §4, ruled 09-15): the
                              // Media tab sits faintly lit while the house TV
                              // has a resolved channel. Static shadow — never
                              // animated (perf ledger).
                              ...(tab.key === 'media' && houseStatus?.tv?.on ? { textShadow: '0 0 9px rgba(156,200,234,0.85)' } : {}),
                            }}
                          >
                            {tab.iconName ? (
                              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}>
                                <LineIcon name={tab.iconName} size={15} />
                                {tab.label}
                              </span>
                            ) : tab.label}
                            {tab.dotKey && tabDots[tab.dotKey] && chatActionTab !== tab.key && (
                              <span style={{
                                position: 'absolute', top: 6, right: 6,
                                width: 8, height: 8, borderRadius: '50%',
                                // 🎮 games dot = green invitation; red = urgency (Nina 08-02)
                                backgroundColor: tab.dotKey === 'games' ? '#22c55e' : '#ef4444',
                                boxShadow: tab.dotKey === 'games' ? '0 0 6px rgba(34,197,94,0.6)' : '0 0 6px rgba(239,68,68,0.6)',
                                animation: 'liveBadgePulse 2s ease-in-out infinite',
                              }} />
                            )}
                            {/* 🧹 dead badge branches removed (08-05): the
                                'livestream' and 'watch_party' keys left this
                                tab array on 07-19 / 07-17 — the live-count
                                badge and party dot could never render. The
                                LIVE pill + TV place carry those signals. */}
                            {/* 🫖 fresh-tea count rides Room (Whispers retired
                                08-03) — pulsing, same signal language as the red
                                activity dots, pink so it still reads as tea */}
                            {tab.key === 'all' && teaUnseen > 0 && chatActionTab !== 'all' && (
                              <span style={{
                                position: 'absolute', top: 3, right: 3,
                                minWidth: 14, height: 14, borderRadius: 7,
                                padding: '0 3px',
                                backgroundColor: '#ec407a', color: '#fff',
                                fontSize: '0.55em', fontWeight: 800, lineHeight: '14px',
                                textAlign: 'center',
                                boxShadow: '0 0 6px rgba(236,64,122,0.6)',
                                animation: 'liveBadgePulse 2s ease-in-out infinite',
                              }}>
                                {teaUnseen}
                              </span>
                            )}
                          </button>
                          {/* Pin: 'all' pins the chat panel; media lost its pin
                              08-02 (the tab is its one home). 'recap' is
                              excluded 08-07 for the same reason media is:
                              the pinned-sidebar body has branches for
                              homepage/watch_party/bot_vision/games/chat/
                              highlights/livestream ONLY, and its wrapper is
                              gated merely on `pinnedSidebarTab` — so pinning a
                              key with no branch renders an empty bordered
                              column whose header falls through the label map
                              to the raw string ("recap"). */}
                          {/* ⌨️ workbench excluded (08-19): same no-pinned-body
                              reason as media/recap. */}
                          {isThreeColumnLayout && tab.key !== 'media' && tab.key !== 'recap' && tab.key !== 'workbench'
                            && pinnedSidebarTab !== (tab.key === 'all' ? 'chat' : tab.key) && (
                            <button onClick={(e) => { e.stopPropagation(); setPinnedSidebarTab(tab.key === 'all' ? 'chat' : tab.key); }} title="Pin to sidebar" style={{ position: 'absolute', top: '2px', left: '-2px', background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.75em', opacity: 0.85, padding: '2px 4px', zIndex: 3, lineHeight: 1 }}>
                              📌
                            </button>
                          )}
                          {/* 🎭 dock affordance — on the ACTIVE tab only (you dock the
                              thing you're looking at); livestream keeps its own system;
                              Room is a feed lane, not a dockable panel */}
                          {/* ⌨️ workbench excluded (08-19): stageLabels has no
                              workbench entry — docking it would blank the stage
                              while the strip loses the tab. */}
                          {chatActionTab === tab.key && tab.key !== 'livestream'
                            && tab.key !== 'all' && tab.key !== 'workbench' && (
                            <button onClick={(e) => { e.stopPropagation(); dockToStage(tab.key); setChatActionTab('all'); }} title="Put it on the stage" style={{ position: 'absolute', top: '2px', right: '-2px', background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.75em', opacity: 0.85, padding: '2px 4px', zIndex: 3, lineHeight: 1 }}>
                              🎭
                            </button>
                          )}
                        </div>
                      ))}
                      {/* 🕵️🧰 Drawer openers moved AGAIN (Nina 08-03, mock-approved):
                          out of the strip to VIEWPORT edge tabs on the right —
                          always reachable regardless of scroll, and the strip is
                          lanes-only now. This is NOT the 07-25 failure repeating:
                          those edge buttons lived INSIDE the feed scroller and got
                          painted over by the docked poll card; the new tabs are
                          position:fixed at page level (grep EDGE TABS 08-03),
                          outside every scroller and containing-block trap. */}
                    </>
                  )}
                </>
              ) : (
                <button
                  onClick={() => setActiveTab('actions')}
                  style={{
                    flex: 1,
                    padding: '12px 20px',
                    backgroundColor: activeTab === 'actions' ? THEME.bgLight : 'transparent',
                    color: activeTab === 'actions' ? THEME.textBright : THEME.textMuted,
                    border: 'none',
                    borderBottom: activeTab === 'actions' ? `3px solid ${THEME.accent}` : '3px solid transparent',
                    fontSize: '16px',
                    fontWeight: 'bold',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                >
                  🎮 Actions
                </button>
              )}
              {/* Hide Charts, Videos, Summary tabs for chat games */}
              {gameType !== 'chat-game' && gameType !== 'chat' && (
                <>
                  <button
                    onClick={() => setActiveTab('charts')}
                    style={{
                      flex: 1,
                      padding: '12px 20px',
                      backgroundColor: activeTab === 'charts' ? THEME.bgLight : 'transparent',
                      color: activeTab === 'charts' ? THEME.textBright : THEME.textMuted,
                      border: 'none',
                      borderBottom: activeTab === 'charts' ? `3px solid ${THEME.accent}` : '3px solid transparent',
                      fontSize: '16px',
                      fontWeight: 'bold',
                      cursor: 'pointer',
                      transition: 'all 0.2s'
                    }}
                  >
                    📊 Charts
                  </button>
                  <button
                    onClick={() => setActiveTab('videos')}
                    style={{
                      flex: 1,
                      padding: '12px 20px',
                      backgroundColor: activeTab === 'videos' ? THEME.bgLight : 'transparent',
                      color: activeTab === 'videos' ? THEME.textBright : THEME.textMuted,
                      border: 'none',
                      borderBottom: activeTab === 'videos' ? `3px solid ${THEME.accent}` : '3px solid transparent',
                      fontSize: '16px',
                      fontWeight: 'bold',
                      cursor: 'pointer',
                      transition: 'all 0.2s'
                    }}
                  >
                    🎬 Videos
                  </button>
                  <button
                    onClick={() => setActiveTab('summary')}
                    style={{
                      flex: 1,
                      padding: '12px 20px',
                      backgroundColor: activeTab === 'summary' ? THEME.bgLight : 'transparent',
                      color: activeTab === 'summary' ? THEME.textBright : THEME.textMuted,
                      border: 'none',
                      borderBottom: activeTab === 'summary' ? `3px solid ${THEME.accent}` : '3px solid transparent',
                      fontSize: '16px',
                      fontWeight: 'bold',
                      cursor: 'pointer',
                      transition: 'all 0.2s'
                    }}
                  >
                    📺 Summary
                  </button>
                </>
              )}
              {/* LIVE pill — always-visible entry point while someone is streaming,
                  driven by spaceLiveStreamers (works with the panel hidden).
                  3-col: expands the livestream bar; narrow: jumps to the Live tab. */}
              {(gameType === 'chat-game' || gameType === 'chat') && !isCompMode && !isBotMode && spaceLiveStreamers.length > 0 && (
                <button
                  onClick={() => {
                    if (isThreeColumnLayout) {
                      setLivestreamFloating(false);
                      setLivestreamExpanded(true);
                    } else if (placesNav) {
                      setMobileView('tv');
                    } else {
                      setActiveTab('actions');
                      setChatActionTab('livestream');
                    }
                  }}
                  title={selfIsLive
                    ? "You are live! Open your stream to end it."
                    : `${spaceLiveStreamers.map(s => s.name).join(', ')} — watch live`}
                  style={{
                    marginLeft: 'auto',
                    marginBottom: '4px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '4px 10px',
                    borderRadius: '12px',
                    border: '1px solid rgba(239,68,68,0.4)',
                    backgroundColor: 'rgba(239,68,68,0.15)',
                    color: '#ef4444',
                    fontSize: '0.72em',
                    fontWeight: 800,
                    letterSpacing: '0.5px',
                    textTransform: 'uppercase',
                    cursor: 'pointer',
                    flexShrink: 0,
                    whiteSpace: 'nowrap',
                    // Same pin as GO LIVE below (08-07) — these two are
                    // mutually exclusive occupants of the strip's right end,
                    // and a LIVE badge lost to horizontal scroll is worse.
                    ...(!isMobile && isNarrowDesktop ? {
                      position: 'sticky',
                      right: 0,
                      zIndex: 2,
                      backgroundColor: 'rgba(52,20,24,0.97)',
                    } : {}),
                  }}
                >
                  <span style={{
                    width: 7, height: 7, borderRadius: '50%',
                    backgroundColor: '#ef4444',
                    boxShadow: '0 0 8px rgba(239,68,68,0.7)',
                    animation: 'liveBadgePulse 2s ease-in-out infinite',
                    display: 'inline-block',
                  }} />
                  {selfIsLive
                    ? (spaceLiveStreamers.length > 1 ? `YOU'RE LIVE +${spaceLiveStreamers.length - 1}` : "YOU'RE LIVE")
                    : `LIVE · ${spaceLiveStreamers.length}`}
                </button>
              )}
              {/* 📡 idle GO LIVE — narrow desktop only (08-05). The Live tab
                  left the strip 07-19 and every surviving route to the
                  livestream panel required someone ALREADY streaming, so
                  768-1100px had NO way to start a stream. 3-col has the
                  sidebar dock; phones have the TV place. Mutually exclusive
                  with the LIVE pill (length === 0). */}
              {(gameType === 'chat-game' || gameType === 'chat') && !isCompMode && !isBotMode
                && !isThreeColumnLayout && !isMobile && spaceLiveStreamers.length === 0 && (
                <button
                  onClick={() => { setActiveTab('actions'); setChatActionTab('livestream'); }}
                  title="Go live or share your screen"
                  style={{
                    marginLeft: 'auto',
                    marginBottom: '4px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '4px 10px',
                    borderRadius: '12px',
                    border: '1px solid rgba(255,255,255,0.22)',
                    backgroundColor: 'rgba(255,255,255,0.07)',
                    color: THEME.textMuted || '#aaa',
                    fontSize: '0.72em',
                    fontWeight: 800,
                    letterSpacing: '0.5px',
                    textTransform: 'uppercase',
                    cursor: 'pointer',
                    flexShrink: 0,
                    whiteSpace: 'nowrap',
                    // 📡 PIN THE PILL (Nina 08-07): this button is the LAST
                    // CHILD OF THE TAB STRIP, and under 1100px that strip is
                    // overflowX:auto — so marginLeft:auto did nothing and the
                    // pill parked at the end of a horizontal scroll nobody
                    // scrolls. The one route to going live at 768-1100px was
                    // effectively invisible. Sticky right:0 parks it at the
                    // right edge of the SCROLLPORT and lets the tabs scroll
                    // underneath. Matters more now: Recap joined the strip for
                    // every space (08-07), so the row overflows sooner.
                    // (isNarrowDesktop is TRUE on phones too — see useViewport;
                    // pair it with !isMobile like every other consumer.)
                    ...(!isMobile && isNarrowDesktop ? {
                      position: 'sticky',
                      right: 0,
                      zIndex: 2,
                      // Opaque behind the pill or tabs scroll through it.
                      backgroundColor: 'rgba(18,18,26,0.97)',
                    } : {}),
                  }}
                >
                  📡 GO LIVE
                </button>
              )}
            </div>
              </>);
            })()}

            {/* 🔴 go-live toast — fixed overlay, shows when someone starts
                streaming (driven by spaceLiveStreamers newcomer detection) */}
            {liveToast && shellLift(
              <div style={{
                position: 'fixed',
                // safe-area top (round 5, 08-03) — same Dynamic Island
                // clearance as the party toast.
                top: 'calc(18px + env(safe-area-inset-top, 0px))',
                // TOAST_CENTERED, not left:50% — a streamer name plus the copy
                // is easily long enough to hit the half-width squeeze.
                ...TOAST_CENTERED,
                // 📱 08-01 P0 fix (audit round 2 #14): cap the toast so a long
                // streamer name can't run off both edges at 390px — the name
                // span ellipsizes instead. min(92vw,480px) is 359px at 390px,
                // TIGHTER than the calc(100vw - 24px)=366px it replaces, so the
                // phone fix is preserved and the 480px arm adds a desktop cap.
                maxWidth: 'min(92vw, 480px)',
                zIndex: 100001,
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                padding: '10px 14px',
                borderRadius: 12,
                backgroundColor: 'rgba(20,20,28,0.95)',
                border: '1px solid rgba(239,68,68,0.5)',
                boxShadow: '0 8px 24px rgba(0,0,0,0.45), 0 0 16px rgba(239,68,68,0.25)',
                color: '#fff',
              }}>
                <span style={{
                  padding: '2px 8px',
                  borderRadius: 5,
                  backgroundColor: '#ef4444',
                  fontSize: '0.65em',
                  fontWeight: 800,
                  letterSpacing: '0.5px',
                  textTransform: 'uppercase',
                  animation: 'liveBadgePulse 2s ease-in-out infinite',
                  flexShrink: 0,
                }}>LIVE</span>
                <span style={{ fontSize: '0.9em', fontWeight: 600, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{liveToast.name} is live!</span>
                <button
                  onClick={() => {
                    setLiveToast(null);
                    // 🧱 the room frame: the stream plays on its stage
                    if (shellPreset) { setShellStage({ asked: true, closedFor: null }); return; }
                    // 🏡 W2d: a place page's stream plays where the painting is
                    if (portedPlaceOn && !isMobile) { openPlaceExtra('live'); return; }
                    if (isThreeColumnLayout) {
                      setLivestreamFloating(false);
                      setLivestreamExpanded(true);
                    } else if (placesNav) {
                      setMobileView('tv');
                    } else {
                      setActiveTab('actions');
                      setChatActionTab('livestream');
                    }
                  }}
                  style={{ padding: '5px 12px', borderRadius: 8, border: 'none', backgroundColor: '#ef4444', color: '#fff', fontWeight: 700, fontSize: '0.8em', cursor: 'pointer' }}
                >
                  Watch
                </button>
                <button onClick={() => setLiveToast(null)} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.6)', cursor: 'pointer', fontSize: '0.9em', padding: 2 }}>✕</button>
              </div>
            )}

            {/* Tab Content */}
            {/* Floating screenshot button — positioned over scroll area */}
            {/* 📸 08-03 (audit #13): wrapper z 10→30 — the sticky tab strip
                (z25) painted over it; 30 clears the strip-class chrome while
                staying under the case-file drawer tabs (z40/41) so an open
                dossier still covers it. Phones get the 44px hit-target floor;
                desktop keeps the quiet 36px. */}
            {(gameType === 'chat-game' || gameType === 'chat') && (
              <div style={{ position: 'relative', height: 0, zIndex: 30, ...(library.fabShift ? { top: library.fabShift.top, right: library.fabShift.right } : {}) /* 📚 reading: the 📸 rides the chat, never the book */ }}>
                <button
                  onClick={handleScreenshot}
                  disabled={screenshotting}
                  title="Screenshot"
                  data-screenshot-fab
                  style={{
                    position: 'absolute',
                    top: '10px',
                    right: '10px',
                    background: 'rgba(30,30,40,0.75)',
                    backdropFilter: 'blur(8px)',
                    border: '1px solid rgba(255,255,255,0.1)',
                    borderRadius: '50%',
                    width: isMobile ? '44px' : '36px',
                    height: isMobile ? '44px' : '36px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: screenshotting ? 'wait' : 'pointer',
                    opacity: screenshotting ? 0.4 : 0.6,
                    transition: 'opacity 0.2s, background 0.2s',
                    color: screenshotFeedback === 'error' ? '#f87171' : screenshotFeedback ? '#4ade80' : 'rgba(200,200,200,0.9)',
                    padding: 0,
                  }}
                  onMouseEnter={(e) => { if (!screenshotting) { e.currentTarget.style.opacity = '1'; e.currentTarget.style.background = 'rgba(50,50,60,0.9)'; } }}
                  onMouseLeave={(e) => { e.currentTarget.style.opacity = screenshotting ? '0.4' : '0.6'; e.currentTarget.style.background = 'rgba(30,30,40,0.75)'; }}
                >
                  {screenshotting ? (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ animation: 'spin 1s linear infinite' }}>
                      <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                    </svg>
                  ) : screenshotFeedback === 'error' ? (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="6" y1="6" x2="18" y2="18" /><line x1="18" y1="6" x2="6" y2="18" />
                    </svg>
                  ) : screenshotFeedback ? (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  ) : (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M3 7V5a2 2 0 0 1 2-2h2" />
                      <path d="M17 3h2a2 2 0 0 1 2 2v2" />
                      <path d="M21 17v2a2 2 0 0 1-2 2h-2" />
                      <path d="M7 21H5a2 2 0 0 1-2-2v-2" />
                      <circle cx="12" cy="12" r="3" />
                    </svg>
                  )}
                </button>
                {screenshotFeedback === 'error' && (
                  <div style={{
                    position: 'absolute', top: isMobile ? 60 : 52, right: 10, padding: '5px 10px', borderRadius: 8,
                    background: 'rgba(30,30,40,0.92)', border: '1px solid rgba(248,113,113,0.5)',
                    color: '#f8b4b4', fontSize: '0.68em', fontWeight: 700, whiteSpace: 'nowrap',
                  }}>
                    Capture failed. Try again!
                  </div>
                )}
              </div>
            )}

            {/* Cycle 5f: data-chat-scroll="main" — explorer-bot integration hook.
                Lets the roaming explorer scroll the main chat to bottom via
                querySelector('[data-chat-scroll="main"]') so it sees latest
                messages on arrive + before posting. No visual / functional effect. */}
            {/* overflowX hidden (Nina 07-16): the closed drawers park at
                translateX(110%) and transformed boxes still count toward
                scrollable overflow — with overflow:auto that minted a phantom
                horizontal scrollbar that clipped chat text + the DOSSIER tab. */}
            {/* onScroll prop REMOVED 08-03: scroll listening moved to the
                document-level capture listener (grep onAnyScroll) — this
                container is often NOT the real scroller (document on phones,
                virtualizer >30 actions) so the prop missed those entirely,
                and keeping both would double-fire lazy-load. */}
            {/* 🪟 Desktop minHeight clamp (Nina 08-04 "composer under the tabs"):
                the feed is the pinned column's only basis-0 flex child, so a
                tall theater + tabs + composer squeezed it to ~0px — chat
                invisible, composer parked against the tab strip, suggestion
                overlays forced onto the tabs. The clamp makes the THEATER
                yield instead (its video row shrinks; chrome floors at
                min-content). 22dvh derates on short windows so the column
                never overflows its overflow:hidden and clips the composer. */}
            {/* 🧱 shellSlot: under the room frame this ONE feed (data-chat-scroll
                kept) portals into the frame's center column, its own scroller */}
            {/* 📚 desktop B: libraryFeedRow puts the House library's book BESIDE this one feed (the
                identity, a bare fragment, until the library has been live on this page) */}
            {libraryFeedRow(<>{shellSlot('feed', <div ref={(el) => { resultsContainerRef.current = el; chatAreaRef.current = el; }} data-chat-scroll="main" data-shell-feed={shellPreset ? '' : undefined} onDrop={handleContentDrop} onDragOver={handleContentDragOver} onDragLeave={handleContentDragLeave} style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: isMobile ? '50dvh' : ((theaterHero || portedPlaceOn) ? /* 🎬📐 seated at ANY party (09-22 late; was the screening-only cinema lever): the feed yields to the theater, which keeps its own video floor. 🏡 W2d: and to a place page's painting, which yields back while you chat */ 'clamp(72px, 12dvh, 140px)' : 'clamp(120px, 22dvh, 260px)'), overflowY: 'auto', overflowX: 'hidden', outline: dropHighlight ? '2px dashed #7b1fa2' : 'none', ...(shellPreset ? { minHeight: 0 } : {}) }}>
              {/* 🕵️ CASE FILE drawer — the dossier peeks out of the panel's right
                  edge as a manila folder tab and slides open over the chat like a
                  case file (user call 07-10; replaces the Locals tab — tab row was
                  crowded). Zero-height sticky layer: children stay pinned while
                  the chat scrolls, no parent surgery. Mount is persistent (the
                  unlock engine + toasts run on every tab); only the transform
                  changes on open/close. */}
              {/* 📱 places nav: the LOCALS PLACE is the case file now — this
                  drawer instance yields (single LocalsDossier mount, or the
                  unlock engine double-fires its toasts). Desktop unchanged. */}
              {!placesNav && (gameType === 'chat-game' || gameType === 'chat') && !isBotMode
                && !chatGameStatus?.competitive_mode && !chatGameStatus?.imposter_mode && gameId
                && !shellPreset /* 🧱 the frame has no dossier (and one never runs its unlock toasts unseen) */ && (
                <div ref={drawerAnchorRef} style={{ position: 'sticky', top: 0, height: 0, overflow: 'visible', zIndex: 40, alignSelf: 'stretch' }}>
                  {/* Edge button REMOVED (Nina 07-25) — the opener now lives in
                      the sticky nav strip (see the strip's manila tabs); the
                      feed-top pin kept getting painted over by the docked poll
                      card + reaction rows. The drawer below is unchanged. */}
                  {/* The folder itself — manila chrome around the dark dossier pages */}
                  <div style={{
                    position: 'absolute',
                    top: 6,
                    right: 0,
                    width: 'min(74%, 480px)',
                    maxHeight: drawerMaxHeight,
                    display: 'flex',
                    flexDirection: 'column',
                    background: 'linear-gradient(180deg, #eed9a4 0%, #dcc07c 100%)',
                    // 🗒️ paper resets the halo (09-24, roomHaloPaper.test.js)
                    textShadow: 'none',
                    border: '1px solid #b28f4a',
                    borderRight: 'none',
                    borderRadius: '12px 0 0 12px',
                    boxShadow: '-8px 10px 28px rgba(0,0,0,0.45)',
                    transform: dossierOpen ? 'translateX(0)' : 'translateX(110%)',
                    visibility: dossierOpen ? 'visible' : 'hidden',
                    pointerEvents: dossierOpen ? 'auto' : 'none',
                    transition: 'transform 0.28s ease, visibility 0.28s',
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px 6px' }}>
                      <span style={{ fontSize: '0.72em', fontWeight: 800, letterSpacing: '0.14em', color: '#4a3a17' }}>
                        {isPortedSpace ? '🔍 THE MIND' : '🗂 CASE FILE · THE LOCALS'}
                      </span>
                      <div style={{ flex: 1 }} />
                      <button
                        onClick={() => setDossierOpen(false)}
                        aria-label="Close case file"
                        style={{ background: 'none', border: 'none', color: '#4a3a17', fontWeight: 800, cursor: 'pointer', fontSize: '0.9em', padding: '2px 6px' }}
                      >✕</button>
                    </div>
                    <div style={{ flex: 1, minHeight: 0, overflow: 'auto', padding: '0 8px 8px' }}>
                      {/* 🪟 W2 (D2 ruled): ported spaces get the transparency
                          card, not the mystery dossier — your own chibi is not
                          a stranger to investigate. */}
                      {isPortedSpace && portedMindProps ? null : <LocalsDossier
                        gameId={gameId}
                        players={users}
                        actions={results.flatMap(r => r.actions || [])}
                        myPlayerId={effectivePlayerId}
                        myPlayerName={users?.find(u => u.id === humanPlayerId)?.name || null}
                        theme={THEME}
                        enabled
                        visible={dossierOpen}
                        onRequestOpen={() => setDossierOpen(true)}
                        onUnseenChange={setDossierUnseen}
                        coins={houseStatus?.balances || null}
                        charters={houseStatus?.charters || null}
                        stats={houseStatus?.stats || null}
                        bonds={houseStatus?.bonds || null}
                        uncovered={houseStatus?.uncovered || null}
                        circadian={houseStatus?.circadian || chatGameStatus?.space_life || null}
                        houseSpace={!!houseStatus}
                      />}
                      {/* 🗃 idea 7 (ruled): keepsakes under the Mind panel.
                          dossierOpen gate: the drawer mount is persistent
                          (transform-only close), so an ungated shelf would
                          scan the transcript with the drawer shut. */}
                      {isPortedSpace && dossierOpen && (
                        <KeepsakeShelf actions={keepsakeActions} />
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* 🧰 SHELF — plank redesign 08-04 (Nina's picks: cozy clutter,
                  plaques always on, BOTTOM SHEET on phones). Desktop keeps the
                  right drawer (zero-height sticky pattern, opening either
                  drawer closes the other); phones get a thumb-zone sheet with
                  a scrim — z4900 sits above the places layer (4600) and the
                  mini-player (4800), below modals (CastCardModal 5150). */}
              {(gameType === 'chat-game' || gameType === 'chat') && !isBotMode
                && !chatGameStatus?.competitive_mode && !chatGameStatus?.imposter_mode && gameId && (
                isMobile ? (shelfOpen && (
                  <>
                    <div
                      onClick={() => setShelfOpen(false)}
                      style={{ position: 'fixed', inset: 0, zIndex: 4899, background: 'rgba(0,0,0,0.45)' }}
                    />
                    <div style={{
                      position: 'fixed', left: 0, right: 0, bottom: 0, zIndex: 4900,
                      maxHeight: '72dvh', display: 'flex', flexDirection: 'column',
                      borderRadius: '18px 18px 0 0',
                      background: 'linear-gradient(180deg, #c99b60 0%, #a97e46 100%)',
                      textShadow: 'none', // 🪵 10-02 shelf (Nina's screenshot): the space theme's page text shadow smudged the header + plaques on the light wood
                      borderTop: '1px solid #7a5227',
                      boxShadow: '0 -14px 40px rgba(0,0,0,0.55)',
                      paddingBottom: 'env(safe-area-inset-bottom)',
                    }}>
                      <div style={{ width: 40, height: 4, borderRadius: 2, background: 'rgba(61,43,18,0.4)', margin: '8px auto 0', flexShrink: 0 }} />
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 14px 4px', flexShrink: 0 }}>
                        <span style={{ fontSize: '0.72em', fontWeight: 800, letterSpacing: '0.14em', color: '#3d2b12', textShadow: 'none' /* 🪵 10-02 shelf */ }}>
                          🪵 THE SHELF · {(chatGameStatus?.builds || []).length}
                        </span>
                        <div style={{ flex: 1 }} />
                        <button
                          onClick={() => setShelfOpen(false)}
                          aria-label="Close shelf"
                          data-prop-ui=""
                          style={{ background: 'none', border: 'none', color: '#3d2b12', fontWeight: 800, cursor: 'pointer', fontSize: '0.9em', padding: '2px 6px', textShadow: 'none' /* 🪵 10-02 shelf */ }}
                        >✕</button>
                      </div>
                      <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '0 10px 10px' }}>
                        <SpaceShelf
                          items={chatGameStatus?.builds || []}
                          perPlank={3}
                          onGift={(h) => seedFromShelf(`/gift #${h} `)}
                          onCombine={(h) => seedFromShelf(`/build #${h} + `)}
                          isOwner={isPortedOwner}
                        />
                      </div>
                    </div>
                  </>
                )) : (
                <div style={{ position: 'sticky', top: 0, height: 0, overflow: 'visible', zIndex: 41, alignSelf: 'stretch' }}>
                  {/* The shelf itself — wood chrome around the planks */}
                  <div style={{
                    position: 'absolute',
                    top: 6,
                    right: 0,
                    width: 'min(74%, 480px)',
                    maxHeight: drawerMaxHeight,
                    display: 'flex',
                    flexDirection: 'column',
                    background: 'linear-gradient(180deg, #c99b60 0%, #a97e46 100%)',
                    textShadow: 'none', // 🪵 10-02 shelf (Nina's screenshot): the space theme's page text shadow smudged the header + plaques on the light wood
                    border: '1px solid #7a5227',
                    borderRight: 'none',
                    borderRadius: '12px 0 0 12px',
                    boxShadow: '-8px 10px 28px rgba(0,0,0,0.45)',
                    transform: shelfOpen ? 'translateX(0)' : 'translateX(110%)',
                    visibility: shelfOpen ? 'visible' : 'hidden',
                    pointerEvents: shelfOpen ? 'auto' : 'none',
                    transition: 'transform 0.28s ease, visibility 0.28s',
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px 6px' }}>
                      <span style={{ fontSize: '0.72em', fontWeight: 800, letterSpacing: '0.14em', color: '#3d2b12', textShadow: 'none' /* 🪵 10-02 shelf */ }}>
                        🪵 THE SHELF
                      </span>
                      <div style={{ flex: 1 }} />
                      <button
                        onClick={() => setShelfOpen(false)}
                        aria-label="Close shelf"
                        data-prop-ui=""
                        style={{ background: 'none', border: 'none', color: '#3d2b12', fontWeight: 800, cursor: 'pointer', fontSize: '0.9em', padding: '2px 6px', textShadow: 'none' /* 🪵 10-02 shelf */ }}
                      >✕</button>
                    </div>
                    <div style={{ flex: 1, minHeight: 0, overflow: 'auto', padding: '0 8px 8px' }}>
                      <SpaceShelf
                        items={chatGameStatus?.builds || []}
                        perPlank={4}
                        onGift={(h) => seedFromShelf(`/gift #${h} `)}
                        onCombine={(h) => seedFromShelf(`/build #${h} + `)}
                        isOwner={isPortedOwner}
                      />
                    </div>
                  </div>
                </div>
                )
              )}

              {/* 🏠🧠 MEMORY WALL — Claude House only (houseStatus is null
                  everywhere else). Third right-edge drawer on DESKTOP (z42,
                  cork chrome next to manila + wood). Openers: the edge-tab
                  rail AND, since 08-07, the masthead till pill (grep TILL
                  PILL) — both desktop-only. Phones get the
                  wall as a Locals-place section instead — no sheet variant on
                  purpose, nothing can open one. Canon + till ride the status
                  poll; memory rows lazy-load on open. */}
              {!isMobile && houseStatus && (gameType === 'chat-game' || gameType === 'chat') && !isBotMode && gameId && (
                <div style={{ position: 'sticky', top: 0, height: 0, overflow: 'visible', zIndex: 42, alignSelf: 'stretch' }}>
                  {/* The wall itself — cork chrome around the pinned cards */}
                  <div style={{
                    position: 'absolute',
                    top: 6,
                    right: 0,
                    width: 'min(74%, 480px)',
                    maxHeight: drawerMaxHeight,
                    display: 'flex',
                    flexDirection: 'column',
                    background: 'linear-gradient(180deg, #d2a878 0%, #b98c5c 100%)',
                    // 🗒️ paper resets the halo (09-24, roomHaloPaper.test.js)
                    textShadow: 'none',
                    border: '1px solid #8a5f33',
                    borderRight: 'none',
                    borderRadius: '12px 0 0 12px',
                    boxShadow: '-8px 10px 28px rgba(0,0,0,0.45)',
                    transform: houseWallOpen ? 'translateX(0)' : 'translateX(110%)',
                    visibility: houseWallOpen ? 'visible' : 'hidden',
                    pointerEvents: houseWallOpen ? 'auto' : 'none',
                    transition: 'transform 0.28s ease, visibility 0.28s',
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px 6px' }}>
                      <span style={{ fontSize: '0.72em', fontWeight: 800, letterSpacing: '0.14em', color: '#3b2a1a' }}>
                        📌 THE MEMORY WALL
                      </span>
                      <div style={{ flex: 1 }} />
                      <button
                        onClick={() => setHouseWallOpen(false)}
                        aria-label="Close memory wall"
                        style={{ background: 'none', border: 'none', color: '#3b2a1a', fontWeight: 800, cursor: 'pointer', fontSize: '0.9em', padding: '2px 6px' }}
                      >✕</button>
                    </div>
                    <div style={{ flex: 1, minHeight: 0, overflow: 'auto', padding: '0 8px 8px' }}>
                      <HouseMemoryWall
                        canon={houseStatus.canon || []}
                        memories={houseWall.memories}
                        scenes={houseWall.scenes || []}
                        pollHistory={houseWall.pollHistory || []}
                        threads={houseWall.threads || []} /* 🗝️ 10-14 Open mysteries */
                        loading={houseWall.loading}
                        till={houseStatus.till ?? null}
                        buildGoal={houseStatus.build_goal || 40}
                        onTip={handleHouseTip}
                        tipState={houseTipState}
                        patrons={houseStatus.patrons || null}
                        gameId={gameId}
                        houseToday={houseWall.houseToday || null}
                        archive={houseWall.archive || null}
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* 🔍 Whodunnit STAGE 1: COLLECT CLUES — full-screen point-and-click over the
                  scene while an investigation round runs (status.investigation is live only
                  then). Fixed overlay at zIndex 1200: above the game surfaces, below the
                  cinematics (guilty reveal 300000), which play over it untouched. Rendered
                  in bot mode too — bots play the same game (one big I'M READY CTA; the
                  3:00 server timeout is the backstop either way). */}
              {(gameType === 'chat-game' || gameType === 'chat') && gameId
                && chatGameStatus?.competitive_mode
                && chatGameStatus?.investigation && !chatGameStatus?.game_completed
                && !(!effectivePlayerId || effectiveIsSpectating) && (
                <InvestigationStage
                  investigation={chatGameStatus.investigation}
                  backgroundUrl={chatGameStatus?.ui_theme?.backgroundImage?.url || null}
                  gameId={gameId}
                  playerId={effectivePlayerId}
                  isSpectator={!effectivePlayerId || !!chatGameStatus?.is_spectating}
                  sceneAmbient={caseAmbient}
                  isBotMode={isBotMode}
                  caseFileUnseen={caseFileUnseen}
                  caseFileProps={{
                    gameId,
                    playerId: effectivePlayerId,
                    players: users,
                    actions: results.flatMap(r => r.actions || []),
                    caseFile: chatGameStatus?.case_file || null,
                    youDidIt: !!chatGameStatus?.you_did_it,
                    round: chatGameStatus?.current_competitive_round || 1,
                    dice: chatGameStatus?.dice || null,
                    yourEvidence: chatGameStatus?.your_evidence || null,
                    witnessQa: chatGameStatus?.your_witness_qa || null,
                    challenge: chatGameStatus?.human_challenge
                      ? { ...chatGameStatus.human_challenge, done: sideQuestClaimed === chatGameStatus.human_challenge.text }
                      : null,
                    yourSecret: chatGameStatus?.your_secret || null,
                    secretsOn: !!chatGameStatus?.secrets_on,
                    yourMadlib: chatGameStatus?.your_madlib || null,
                    heat: (typeof chatGameStatus?.poison?.heat === 'number') ? chatGameStatus.poison.heat : null,
                    culpritNoun: chatGameStatus?.scenario?.culprit_noun || null,
                    victim: chatGameStatus?.scenario?.victim || null,
                    yourBet: (!isBotMode && chatGameStatus?.your_bet) || null,
                    onAcceptBet: async () => {
                      try {
                        await chatGameApi.investigationAction(gameId, effectivePlayerId, { op: 'accept_bet' });
                        fetchChatGameStatus();
                      } catch (e) { console.error('[AcceptBet] failed:', e.message); }
                    },
                    onConfess: async () => {
                      try {
                        await chatGameApi.investigationAction(gameId, effectivePlayerId, { op: 'confess' });
                        fetchChatGameStatus();
                      } catch (e) { console.error('[Confess] failed:', e.message); }
                    },
                    onUnseenChange: setCaseFileUnseen,
                  }}
                />
              )}

              {/* 👻 SÉANCE (Nina 07-30) — ghost chat, shown rather than stored. The
                  dead type into the ghost chat they already had; living players see
                  those lines drift over the scene and fade. Presentation only: no new
                  channel, no new input, no backend. Costs zero permanent pixels, which
                  is the point — ghost chat was holding ~150px of a ~470px phone.
                  Living players only (ghosts and spectators have the real panel), and
                  `overStage` moves the band above whichever chrome owns the bottom
                  edge — the stage's manila desk band, or the mobile ghost dock.
                  MOBILE ONLY (Nina 07-30): desktop has the room for the real ghost
                  panel, so it keeps it — whispers exist to buy back vertical space
                  that only phones are short of.
                  ALL SPACES (Nina 07-31, was noir-competitive only): regular spaces
                  already run AI ghost-chatter personas whose lines mobile players
                  never see (panel docked), and the soft 'whisper' sound cue already
                  fires for exactly those unseen lines — this is that cue's visual
                  half. Suppressed while the watch-party theater is visibly open
                  (drifting text over video) and while the keyboard is up (band
                  would sit on the composer). */}
              {isMobile && (gameType === 'chat-game' || gameType === 'chat') && !shellPreset /* 🧱 no side chat in the frame */ && (
                <SeanceWhispers
                  messages={ghostPanelMessages}
                  anchorRef={inputFormRef} /* band rides the MEASURED dock top (08-14) — static 150px sat under an expanded composer */
                  competitive={!!(chatGameStatus?.competitive_mode || chatGameStatus?.imposter_mode)}
                  active={!!(humanPlayerId
                    && !effectiveIsSpectating
                    && !chatGameStatus?.eliminated_players?.includes(humanPlayerId)
                    && !chatGameStatus?.game_completed
                    && !typingFocus
                    && !ghostLane /* lane open = already reading the real feed */
                    && !crowdInline /* 📣 10-09 (Nina): a Live room shows the crowd IN the chat, so no drifting copies */
                    && !(watchParty && inTheater))}
                  overStage={investigationTakeover}
                />
              )}

              {/* 🎙️ PRESS BOX (Nina 07-25) — the spectator's whodunnit. Spectators no
                  longer get the investigation takeover (a suspect UI with the controls
                  ripped out); they get the gallery instead: public activity counts per
                  suspect plus a private "pin your pick" prediction, scored at the
                  verdict. Counts only — a spectator's screen never beats a suspect's. */}
              {(gameType === 'chat-game' || gameType === 'chat') && gameId
                && chatGameStatus?.competitive_mode
                && (!effectivePlayerId || effectiveIsSpectating)
                && (
                  (chatGameStatus?.press_box?.length > 0 && !chatGameStatus?.game_completed)
                  || (chatGameStatus?.game_completed && pressBoxPick && chatGameStatus?.killer_revealed)
                ) && (() => {
                const gold = '#d9ba72';
                const open = pressBoxOpen === null ? !isMobile : pressBoxOpen;
                const completed = !!chatGameStatus?.game_completed;
                const killer = chatGameStatus?.killer_revealed || null;
                const calledIt = completed && killer && pressBoxPick
                  && String(pressBoxPick.id) === String(killer.id);
                const suspects = (chatGameStatus?.press_box || [])
                  .slice()
                  .sort((a, b) => (b.bagged + b.questions) - (a.bagged + a.questions));
                if (!open) {
                  return (
                    <button
                      onClick={() => setPressBoxOpen(true)}
                      style={{
                        position: 'fixed', right: 10, top: 84, zIndex: 1250,
                        background: 'rgba(12,10,16,0.92)', color: gold, cursor: 'pointer',
                        border: `1px solid ${gold}88`, borderRadius: 999, padding: '7px 14px',
                        fontWeight: 900, letterSpacing: '0.08em', fontSize: '0.78em',
                        boxShadow: '0 6px 20px rgba(0,0,0,0.5)',
                      }}
                    >🎙️ PRESS BOX{pressBoxPick && !completed ? ' · 📌' : ''}</button>
                  );
                }
                return (
                  <div style={{
                    position: 'fixed', right: 10, top: 84, zIndex: 1250,
                    width: 'min(300px, 86vw)', maxHeight: '58dvh', overflowY: 'auto',
                    background: 'rgba(12,10,16,0.94)', border: `1px solid ${gold}66`,
                    borderRadius: 12, boxShadow: '0 10px 34px rgba(0,0,0,0.55)',
                    padding: '10px 12px 12px',
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                      <span style={{ fontWeight: 900, letterSpacing: '0.12em', color: gold, fontSize: '0.8em' }}>
                        🎙️ PRESS BOX
                      </span>
                      <div style={{ flex: 1 }} />
                      <button
                        onClick={() => setPressBoxOpen(false)}
                        aria-label="Collapse press box"
                        style={{ background: 'none', border: 'none', color: gold, fontWeight: 900, cursor: 'pointer', fontSize: '0.9em', padding: '0 4px' }}
                      >⌄</button>
                    </div>
                    {completed && killer ? (
                      <div style={{ textAlign: 'center', padding: '8px 4px 4px' }}>
                        <div style={{ fontSize: '1.8em' }}>{calledIt ? '🎯' : '🙈'}</div>
                        <div style={{ color: '#fff', fontWeight: 800, fontSize: '0.9em', marginTop: 4, lineHeight: 1.4 }}>
                          {calledIt
                            ? `CALLED IT. You pinned ${killer.name || chatGameStatus?.scenario?.culprit_noun || 'the killer'} from the cheap seats.`
                            : `You pinned ${pressBoxPick?.name || 'the wrong suspect'}. It was ${killer.name || 'somebody else'} all along.`}
                        </div>
                      </div>
                    ) : (
                      <>
                        <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.72em', marginBottom: 8 }}>
                          You're in the gallery. The suspects work the scene.
                        </div>
                        {suspects.map(s => {
                          const pinned = pressBoxPick && String(pressBoxPick.id) === String(s.id);
                          return (
                            <div
                              key={s.id}
                              onClick={() => savePressBoxPick(pinned ? null : { id: s.id, name: s.name })}
                              role="button"
                              tabIndex={0}
                              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); savePressBoxPick(pinned ? null : { id: s.id, name: s.name }); } }}
                              style={{
                                display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer',
                                padding: '6px 8px', borderRadius: 8, marginBottom: 4, minHeight: 40, boxSizing: 'border-box',
                                border: pinned ? `1px solid ${gold}` : '1px solid rgba(255,255,255,0.08)',
                                background: pinned ? `${gold}1f` : 'rgba(255,255,255,0.03)',
                              }}
                            >
                              <span style={{ flexShrink: 0, fontSize: '0.85em' }}>{pinned ? '📌' : '🕵️'}</span>
                              <span style={{
                                color: '#fff', fontWeight: 700, fontSize: '0.8em', minWidth: 0,
                                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1,
                              }}>{s.name}</span>
                              <span style={{ color: 'rgba(255,255,255,0.65)', fontSize: '0.7em', fontWeight: 700, flexShrink: 0 }}>
                                🔍{s.questions} 🧾{s.bagged}{s.ready ? ' ✓' : ''}
                              </span>
                            </div>
                          );
                        })}
                        <div style={{ color: gold, fontSize: '0.7em', fontWeight: 800, letterSpacing: '0.06em', marginTop: 6, textAlign: 'center' }}>
                          {pressBoxPick ? `📌 Pinned: ${pressBoxPick.name}. Tap another to switch.` : 'WHO DID IT? Pin your pick.'}
                        </div>
                      </>
                    )}
                  </div>
                );
              })()}

              {/* 🎛️ Case lobby dock — pre-join join CTAs, bottom-center (mock-approved) */}
              {renderCaseLobbyDock()}

              {/* 🗂 Whodunnit CASE FILE drawer — same manila-folder slot as the locals
                  dossier above, but for competitive Whodunnit (which that one gates out).
                  Gated on the case actually existing: innocents get case_file, the killer
                  gets you_did_it. Other competitive templates have neither → no drawer. */}
              {(gameType === 'chat-game' || gameType === 'chat') && !isBotMode && gameId
                && chatGameStatus?.competitive_mode
                && (chatGameStatus?.case_file || chatGameStatus?.you_did_it) && (
                <div style={{ position: 'sticky', top: 0, height: 0, overflow: 'visible', zIndex: 40, alignSelf: 'stretch' }}>
                  <button
                    onClick={() => setCaseFileOpen(o => !o)}
                    title={caseFileOpen ? 'Close the case file' : 'Open the case file'}
                    aria-label="Case file"
                    style={{
                      position: 'absolute',
                      top: 96,
                      right: caseFileOpen ? 'min(74%, 480px)' : 0,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: 6,
                      padding: '14px 9px 16px',
                      background: 'linear-gradient(180deg, #eed9a4 0%, #d9ba72 100%)',
                      color: '#4a3a17',
                      border: '1px solid #b28f4a',
                      borderRight: 'none',
                      borderRadius: '10px 0 0 10px',
                      boxShadow: '-3px 3px 10px rgba(0,0,0,0.35)',
                      cursor: 'pointer',
                      zIndex: 2,
                      transition: 'right 0.28s ease',
                    }}
                  >
                    <span style={{ fontSize: '1.5em', lineHeight: 1 }}>{caseFileOpen ? '▶' : '🗂'}</span>
                    <span style={{ writingMode: 'vertical-rl', fontSize: '0.78em', fontWeight: 800, letterSpacing: '0.18em' }}>CASE FILE</span>
                    {caseFileUnseen > 0 && !caseFileOpen && (
                      <span style={{
                        minWidth: 16, height: 16, borderRadius: 8, padding: '0 3px',
                        background: '#ef4444', color: '#fff', fontSize: '0.6em', fontWeight: 800,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        boxShadow: '0 0 6px rgba(239,68,68,0.6)',
                      }}>{caseFileUnseen}</span>
                    )}
                  </button>
                  <div style={{
                    position: 'absolute',
                    top: 6,
                    right: 0,
                    width: 'min(74%, 480px)',
                    maxHeight: 'min(66dvh, 560px)',
                    display: 'flex',
                    flexDirection: 'column',
                    background: 'linear-gradient(180deg, #eed9a4 0%, #dcc07c 100%)',
                    // 🗒️ paper resets the halo (09-24, roomHaloPaper.test.js)
                    textShadow: 'none',
                    border: '1px solid #b28f4a',
                    borderRight: 'none',
                    borderRadius: '12px 0 0 12px',
                    boxShadow: '-8px 10px 28px rgba(0,0,0,0.45)',
                    transform: caseFileOpen ? 'translateX(0)' : 'translateX(110%)',
                    visibility: caseFileOpen ? 'visible' : 'hidden',
                    pointerEvents: caseFileOpen ? 'auto' : 'none',
                    transition: 'transform 0.28s ease, visibility 0.28s',
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px 6px' }}>
                      <span style={{ fontSize: '0.72em', fontWeight: 800, letterSpacing: '0.14em', color: '#4a3a17' }}>
                        🗂 CASE FILE · THE INVESTIGATION
                      </span>
                      <div style={{ flex: 1 }} />
                      <button
                        onClick={() => setCaseFileOpen(false)}
                        aria-label="Close case file"
                        style={{ background: 'none', border: 'none', color: '#4a3a17', fontWeight: 800, cursor: 'pointer', fontSize: '0.9em', padding: '2px 6px' }}
                      >✕</button>
                    </div>
                    {/* 07-30 (Nina screenshot): flex column so the dark page fills the
                        manila — the file ended mid-drawer leaving a slab of bare folder */}
                    <div style={{ flex: 1, minHeight: 0, overflow: 'auto', padding: '0 8px 8px', display: 'flex', flexDirection: 'column' }}>
                      {/* challenge prop (Nina 07-29): was never passed here, so the DARES
                          tab could not fill — this drawer stays mounted all game (hidden
                          via transform), making it the surface that logs every dare. */}
                      <WhodunnitCaseFile
                        gameId={gameId}
                        playerId={effectivePlayerId}
                        players={users}
                        actions={results.flatMap(r => r.actions || [])}
                        caseFile={chatGameStatus?.case_file || null}
                        youDidIt={!!chatGameStatus?.you_did_it}
                        round={chatGameStatus?.current_competitive_round || 1}
                        dice={chatGameStatus?.dice || null}
                        yourEvidence={chatGameStatus?.your_evidence || null}
                        witnessQa={chatGameStatus?.your_witness_qa || null}
                        challenge={(!isBotMode && chatGameStatus?.human_challenge) ? {
                          ...chatGameStatus.human_challenge,
                          done: sideQuestClaimed === chatGameStatus.human_challenge.text
                        } : null}
                        yourSecret={chatGameStatus?.your_secret || null}
                        secretsOn={!!chatGameStatus?.secrets_on}
                        yourMadlib={chatGameStatus?.your_madlib || null}
                        heat={(typeof chatGameStatus?.poison?.heat === 'number') ? chatGameStatus.poison.heat : null}
                        culpritNoun={chatGameStatus?.scenario?.culprit_noun || null}
                        victim={chatGameStatus?.scenario?.victim || null}
                        yourBet={(!isBotMode && chatGameStatus?.your_bet) || null}
                        onAcceptBet={async () => {
                          try {
                            await chatGameApi.investigationAction(gameId, effectivePlayerId, { op: 'accept_bet' });
                            fetchChatGameStatus();
                          } catch (e) { console.error('[AcceptBet] failed:', e.message); }
                        }}
                        onConfess={async () => {
                          try {
                            await chatGameApi.investigationAction(gameId, effectivePlayerId, { op: 'confess' });
                            fetchChatGameStatus();
                          } catch (e) { console.error('[Confess] failed:', e.message); }
                        }}
                        visible={caseFileOpen}
                        onUnseenChange={setCaseFileUnseen}
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Actions Tab */}
              {activeTab === 'actions' && (
                <div
                  data-shell-lane={(shellPreset && !portedPlaceOn) ? '' : undefined}
                  data-place-lane={portedPlaceOn ? '' : undefined}
                  style={portedPlaceOn ? {
                    // 💬 W2d.1 (A9): mockup v9's lane, on an explicit dark
                    // ground (an old port's generated theme may be light, and
                    // the place's words are cream)
                    // 🏡📐 wave 3 (ruling 32): on a laptop the lane is part of
                    // the ONE card: it stands on the frame's own dark ground
                    // (the columns' background), no box of its own
                    backgroundColor: isMobile ? '#120E18' : 'transparent',
                    padding: isMobile ? '8px 12px' : '14px 22px 8px',
                    borderRadius: isMobile ? '8px' : 0,
                    boxShadow: 'none',
                    flex: 1,
                    overflowY: 'auto',
                    position: 'relative',
                    border: isMobile ? '1px solid rgba(255,236,210,.07)' : 0,
                  } : {
                    backgroundColor: THEME.bgMedium,
                    padding: '20px',
                    borderRadius: '8px',
                    boxShadow: `inset 0 0 20px ${THEME.textDark}4d, 0 0 8px ${THEME.accent}08`,
                    flex: 1,
                    overflowY: 'auto',
                    position: 'relative',
                    border: `1px solid ${THEME.accent}20`,
                    // 🧱 the room frame (mockup frame A): the lines stand on the
                    // frame's own ground, no box of their own (the place lane's
                    // precedent above). NOT a scroller: the feed around it is the
                    // frame's one scroller, the box the follow-to-bottom writes
                    // (a phone's lane used to scroll itself and opened on the
                    // OLDEST line, the newest under the composer).
                    ...(shellPreset ? {
                      backgroundColor: 'transparent',
                      padding: isMobile ? '10px 12px 8px' : '14px 0 10px',
                      borderRadius: 0,
                      boxShadow: 'none',
                      overflowY: 'visible',
                      border: 0,
                    } : null),
                  }}
                >
                  {/* Round Filter */}
                  {/* 💬 W2d.1 (A9): not on a place page (its one switch, AI
                      Thoughts, lives in the ⋯ there: 💭 Hide/Show his thoughts) */}
                  {results.length > 0 && !portedPlaceOn && (() => {
                    // Get unique round numbers from all actions across all results
                    const allActions = results.flatMap(r => r.actions || []);

                    // Build mapping of round_id to round_number based on chronological order
                    const roundIdToNumber = {};
                    const uniqueRoundIds = [...new Set(allActions.map(a => a.game_instance_round_id).filter(id => id))];

                    // For each round ID, find the earliest action and use that for sorting
                    const roundIdToTimestamp = {};
                    uniqueRoundIds.forEach(roundId => {
                      const actionsInRound = allActions.filter(a => a.game_instance_round_id === roundId);
                      if (actionsInRound.length > 0) {
                        const timestamps = actionsInRound.map(a => new Date(a.created_at).getTime());
                        roundIdToTimestamp[roundId] = Math.min(...timestamps);
                      }
                    });

                    // Sort round IDs by timestamp and assign numbers
                    const sortedRoundIds = uniqueRoundIds.sort((a, b) =>
                      roundIdToTimestamp[a] - roundIdToTimestamp[b]
                    );
                    sortedRoundIds.forEach((roundId, index) => {
                      roundIdToNumber[roundId] = index + 1;
                    });

                    // Get unique round numbers
                    const roundNumbers = [...new Set(
                      allActions
                        .map(a => roundIdToNumber[a.game_instance_round_id])
                        .filter(n => n != null)
                    )].sort((a, b) => a - b);

                    // Toolbar row: AI Thoughts toggle (messages/all tabs) + Screenshot button (all tabs)
                    return (
                      <div style={{
                        marginBottom: '12px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px'
                      }}>
                        {(chatActionTab === 'messages' || chatActionTab === 'all') && (
                          <>
                            <span style={{
                              fontSize: '0.8em',
                              fontWeight: 600,
                              color: showThoughts ? '#fff' : 'rgba(150, 150, 150, 0.7)',
                              transition: 'color 0.2s',
                            }}>
                              AI Thoughts
                            </span>
                            <button
                              onClick={() => {
                                const next = !showThoughts;
                                // 🏠👁️ D16 (09-01 pm): in the HOUSE an explicit OFF
                                // sticks per device; turning ON clears it. Other
                                // spaces keep session-only behavior.
                                if (houseStatus) {
                                  try {
                                    if (next) window.localStorage.removeItem('houseThoughtsOff');
                                    else window.localStorage.setItem('houseThoughtsOff', '1');
                                  } catch (e) { /* private mode: session-only */ }
                                }
                                setShowThoughts(next);
                              }}
                              style={{
                                position: 'relative',
                                width: '36px',
                                height: '20px',
                                borderRadius: '10px',
                                border: 'none',
                                backgroundColor: showThoughts ? '#4ade80' : 'rgba(150, 150, 150, 0.4)',
                                cursor: 'pointer',
                                transition: 'background-color 0.2s',
                                padding: 0,
                                flexShrink: 0,
                              }}
                            >
                              <div style={{
                                position: 'absolute',
                                top: '2px',
                                left: showThoughts ? '18px' : '2px',
                                width: '16px',
                                height: '16px',
                                borderRadius: '50%',
                                backgroundColor: '#fff',
                                transition: 'left 0.2s',
                                boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
                              }} />
                            </button>
                          </>
                        )}
                      </div>
                    );
                  })()}

                  {/* Empty state for chat games with no actions yet */}
                  {(gameType === 'chat-game' || gameType === 'chat') && (!results.length || !results[0]?.actions?.length) && !isLoading && (
                    <div style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: '60px 20px',
                      textAlign: 'center',
                      backgroundColor: THEME.bgMedium,
                      borderRadius: '12px',
                      border: `1px solid ${THEME.borderLight}`,
                      margin: '20px 0'
                    }}>
                      <span style={{ fontSize: '3em', marginBottom: '16px' }}>💬</span>
                      <div style={{
                        fontSize: '1.3em',
                        fontWeight: 'bold',
                        color: THEME.textDark
                      }}>
                        The locals will start chatting soon...
                      </div>
                    </div>
                  )}

                  {/* ✨ FEED chrome (Arc 3, 07-19 · Tea chip Nina 07-20): chip row
                      (All | Tea | Media) + the collapsible Home band. The Tea is
                      a Feed subtab now — its 'highlights' key stays unchanged so
                      bots and deep links keep working; the top-level strip is
                      Feed / Games. */}
                  {/* Chip row REMOVED (Nina 07-25): lanes are folder tabs in the
                      main strip now — one nav idiom. Only the About band stays. */}
                  {/* 📱🧭 CHAT PLACE strips RETIRED (Nina 08-01): the TV/LIVE
                      strips duplicated the nav's chameleon + badges, and the
                      Whispers chip filtered away more than it revealed. Chat
                      opens straight into the conversation; The Tea rides the
                      feed top below (whispers lane lives on for desktop). */}
                  {/* ⌨️🖥️ THE TERMINAL SHOW VIEW (Nina 09-07: single tab): the
                      whole show panel — HUD, phase track, stations, ledger,
                      Start Episode — rides atop the Room feed. Desktop only
                      (!placesNav); phones keep the Work place. The Start
                      button builds the same command object the composer's
                      parser mints and sends through the same interjection
                      path — backend still enforces the mod check. */}
                  {(gameType === 'chat-game' || gameType === 'chat') && isTerminalSpace && !placesNav
                    && chatActionTab === 'all' && !isBotMode && (
                    <div style={{ marginBottom: 12 }}>
                      <TerminalWorkbenchPanel
                        users={users}
                        results={results}
                        currentlyTyping={currentlyTyping}
                        THEME={THEME}
                        ledger={chatGameStatus?.task_ledger}
                        isSpaceMod={!!humanPlayerId && (humanPlayerId === chatGameStatus?.human_player_identifier || humanPlayerId === createdPlayerIdRef.current)}
                        onStartTask={async (text) => {
                          const issuerName = users.find(u => u.game_player_id === humanPlayerId)?.name || 'the mod';
                          await handleInterjectionAndPlay(`/task ${text}`, [], {
                            name: 'task', targets: [], text,
                            card_message: `⌨️ NEW TASK from ${issuerName}: ${text}`,
                          });
                        }}
                      />
                    </div>
                  )}
                  {/* 🚪📷 The /room chips BAR is RETIRED (Nina 09-15 pm:
                      command-first — "I thought it would just be /room").
                      The camera now lives entirely in the composer: the
                      room LIST under a committed "/room " (HumanInputForm
                      houseRooms prop), the PICTURES popup from the door in the message box /
                      a bare "/room" (grep renderRoomPicker), replies + event
                      invites in the composer toast (grep roomNotice). The
                      bar's file was deleted 09-18 with the picker batch. */}
                  {(gameType === 'chat-game' || gameType === 'chat')
                    && !(chatGameStatus?.competitive_mode || chatGameStatus?.imposter_mode)
                    // ⌨️ epigraph OFF in terminal spaces (09-07): the floating
                    // quote card competed with the show HUD for the feed top.
                    && !isTerminalSpace
                    && chatActionTab === 'all' && (
                    <div style={{ marginBottom: 10 }}>
                      {(() => {
                        /* Design A epigraph (Nina 08-01): the About-hero card is
                           gone — its featured quote survives as this quiet
                           two-line-clamped epigraph atop the feed. Same scoring
                           recipe the hero used (reactions dominate, then energy,
                           then length sweet spot); clamped so late-loading
                           messages can't reflow the feed. */
                        const epigraphTypes = new Set(['message', 'public_message', 'private_message', 'secret_message', 'human_interjection']);
                        let best = null, bestScore = -1;
                        const hashStr = (s) => { let h = 0; for (let i = 0; i < s.length; i++) h = ((h << 5) - h + s.charCodeAt(i)) | 0; return Math.abs(h); };
                        for (const r of results) for (const a of (r.actions || [])) {
                          const msg = a?.data?.message;
                          if (!msg || msg.length < 30 || !epigraphTypes.has(a.type)) continue;
                          if (a.data.sub_conversation_message || a.data.parent_action_id) continue;
                          const reactions = a.data.reactions
                            ? Object.values(a.data.reactions).reduce((sum, arr) => sum + (Array.isArray(arr) ? arr.length : 0), 0) : 0;
                          const emojiCount = (msg.match(/[\u{1F000}-\u{1FFFF}\u{2600}-\u{27BF}\u{FE00}-\u{FE0F}\u{1F900}-\u{1F9FF}]/gu) || []).length;
                          const exclaims = (msg.match(/!/g) || []).length;
                          const energy = Math.min(emojiCount * 3 + exclaims * 2, 20);
                          const lenScore = msg.length < 60 ? msg.length / 2 : msg.length <= 200 ? 40 + (msg.length - 60) * 0.2 : 40;
                          const score = reactions * 30 + energy + lenScore + hashStr(msg) % 15;
                          if (score > bestScore) { bestScore = score; best = a; }
                        }
                        if (!best) return null;
                        const sender = users.find(u => u.id === best.game_instance_player_id || u.game_player_id === best.game_instance_player_id);
                        return (
                          <div style={{
                            padding: '9px 13px 8px', marginBottom: 8, borderRadius: 12,
                            background: 'linear-gradient(180deg, rgba(255,255,255,0.05), rgba(255,255,255,0.015))',
                          }}>
                            <div style={{
                              fontSize: '0.95em', fontStyle: 'italic', color: THEME.textOnPanel || '#fff', lineHeight: 1.45,
                              display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
                            }}>
                              “{best.data.message}”
                            </div>
                            <div style={{ marginTop: 3, fontSize: '0.68em', fontWeight: 600, color: THEME.textOnPanel || '#fff', opacity: 0.62 }}>
                              — {sender?.name || 'Unknown'}
                            </div>
                          </div>
                        );
                      })()}
                      {/* 📖 "Story so far" drawer — DESKTOP: retired 08-07, its
                          two story sections transplanted to the top of the
                          Recap lane (Media Spotlight left behind; the Media tab
                          is its one home). Room is epigraph + feed there now.

                          📱 PHONES KEEP IT. The Recap lane is desktop-only and
                          structurally so: the tab strip collapses to a
                          zero-height hidden anchor on chat phones (grep "Stage
                          2 (07-17)"), the mobile places nav ships Chat · TV ·
                          Locals · Arcade with no recap item, and there is no
                          `setChatActionTab('recap')` literal anywhere in src —
                          so nothing can route a phone into the lane. Deleting
                          the drawer outright did not MOVE these sections on
                          mobile, it deleted them. Restoring a phone-only mount
                          keeps mobile byte-identical to 08-06 until Recap gets
                          a real phone home (a 5th places-nav item is the open
                          design call, not a bug fix). */}
                      {/* (🏡📐 wave 3, ruling 32: not on a phone's place page: the lane is bare bubbles) */}
                      {isMobile && !portedPlaceOn && (
                        <div style={{ borderRadius: 12, border: `1px solid ${THEME.accent}25`, overflow: 'hidden', marginBottom: 4 }}>
                          <button
                            onClick={() => {
                              const next = !feedHomeOpen;
                              setFeedHomeOpen(next);
                              try { localStorage.setItem(`feedHomeOpen_${gameId}`, next ? '1' : '0'); } catch (e) { /* private mode */ }
                            }}
                            style={{
                              width: '100%', textAlign: 'left', padding: '8px 12px', cursor: 'pointer',
                              background: `${THEME.accent}12`, border: 'none',
                              color: THEME.textOnPanel || THEME.textDark, fontSize: '0.74em', fontWeight: 800,
                              letterSpacing: '0.08em', textTransform: 'uppercase',
                              display: 'flex', alignItems: 'center', gap: 6,
                            }}
                          >
                            <span>📖</span> Story so far
                            <span style={{ marginLeft: 'auto', opacity: 0.6 }}>{feedHomeOpen ? '▾' : '▸'}</span>
                          </button>
                          {feedHomeOpen && (
                            <HomepagePanel allActions={results.flatMap(r => r.actions || [])} users={users} activeUsers={activeUsers} THEME={THEME} chatGameStatus={chatGameStatus} mediaSuggestions={chatGameStatus?.media_suggestions} />
                          )}
                        </div>
                      )}
                    </div>
                  )}
                  {/* Panel-based tabs — each renders its own panel above the action list */}
                  {/* Cycle 5c: wrap each panel in role="tabpanel" + data-tab-name so
                      automated explorers can read the ACTIVE panel via
                      document.querySelector('[role="tabpanel"]:not([hidden])')?.innerText.
                      Conditional rendering means only one matches at a time, so the
                      selector resolves to the active tab. Adds a11y semantic role too. */}
                  {/* flex '1 0 auto' on the standalone panels (Nina 08-04 void
                      fix): a short panel left the stretched container's
                      transparent slack showing wallpaper between panel and
                      composer — the panel now owns the full column height.
                      '0' shrink keeps tall panels overflowing into the
                      container's scroll exactly as before. */}
                  {/* 🧹 'homepage' tabpanel REMOVED (08-10): chatActionTab
                      initialises to 'all', has no deep-link parse, and no
                      setter anywhere passes 'homepage' — unreachable (§14g). */}
                  {chatActionTab === 'media' && (
                    <div role="tabpanel" data-tab-name="media" style={{ flex: '1 0 auto' }}>
                      <SuggestedMediaPanel mediaSuggestions={chatGameStatus?.media_suggestions} pinnedPick={shownPinnedPick} THEME={THEME} />
                    </div>
                  )}
                  {/* ⌨️🖥️ WORKBENCH tabpanel (The Terminal, 08-19): per-seat
                      battlestations — latest code + 💭 thoughts per AI local,
                      current /task header. Tab exists only in terminal spaces
                      (strip filter); panel derives everything from the live
                      feed window, zero backend surface. */}
                  {chatActionTab === 'workbench' && (
                    <div role="tabpanel" data-tab-name="workbench" style={{ flex: '1 0 auto' }}>
                      <TerminalWorkbenchPanel users={users} results={results} currentlyTyping={currentlyTyping} THEME={THEME} ledger={chatGameStatus?.task_ledger} />
                    </div>
                  )}
                  {/* 🧹 watch_party tabpanel removed (08-05): nothing has set
                      chatActionTab='watch_party' since the tab left the strip
                      07-17 (verified: no setter anywhere) — the panel, its
                      duplicate wpTabSlotRef, and a second launcher mount were
                      unreachable. The stage + TV place are the party's homes. */}
                  {chatActionTab === 'bot_vision' && isPremiumFeatureEnabled('botVision') && (
                    <div role="tabpanel" data-tab-name="bot_vision" style={{ flex: '1 0 auto' }}>
                      <BotVisionPanel
                        THEME={THEME}
                        chatGameStatus={chatGameStatus}
                        setChatGameStatus={setChatGameStatus}
                        gameId={gameId}
                      />
                    </div>
                  )}
                  {/* 📌 Recap lane — EVERY non-comp space now (Nina 08-07).
                      Was house-only when it shipped 08-06 (canon plaques +
                      pinned memory cards + till bar); the lane is now the
                      catch-up surface for the whole product, in this order:

                        📖 the story  — Previously on… + Latest Activity,
                                        transplanted out of the Room drawer
                        📰 the paper trail — Morning Papers + pinned notes
                        🏠 the memory wall — house-only, still (houseStatus)
                        🫖 the tea    — cut scenes, chapters, record book

                      House rows lazy-load via the same houseWallWanted effect,
                      never on the poll. The till left the top of this lane for
                      the masthead vitals row — grep TILL PILL. */}
                  {chatActionTab === 'recap' && (
                    <div role="tabpanel" data-tab-name="recap" style={{ flex: '1 0 auto', padding: '12px', display: 'flex', flexDirection: 'column', gap: '20px' }}>
                      {/* 🎬 CUT SCENES LEAD THE LANE (Nina 08-08: "move the
                          comic cut scenes to the top of Recap, before Latest
                          Activity"). HighlightsPanel opens with its cut-scene
                          strip, so hoisting the whole panel puts the comics
                          first; the rest of the Tea (chapters, record book,
                          ships) rides up with it, which suits a catch-up lane —
                          highlights first, then the running story.
                          ⚠️ Cut scenes are derived from `allActions`, which is a
                          ROLLING WINDOW (current round + last ~100 game-wide
                          rows), NOT an archive. Measured 08-08 on the live
                          house: 7 cut scenes in the DB, 1 visible here. Moving
                          the section does not fix that — a history fetch does
                          (the HouseMemoryWall endpoint is the template). */}
                      {/* 🫖 The Tea joins Recap (Nina 08-07: "recap seems to not
                          have any of the highlights ... but they're not in recap
                          tab"). Recap is the catch-up lane, so the highlights
                          belong here as well as atop the Room feed — where they
                          sit at index 0 and scroll up out of sight as the feed
                          grows, which is how they went missing. Same panel, same
                          props as the Room instance; NOT gated on hasTea, since
                          a catch-up lane that silently drops its own section is
                          the bug we are fixing. Hidden while the tea holds the
                          stage (D2: docked = left the strip). */}
                      {/* ⚠️ 08-07: NOT gated on hasTea. hasTea requires a
                          round_analysis row carrying chapter_title, and chat
                          spaces never generate those — DB-verified: both houses
                          have ZERO. Gating this on hasTea shipped dead code, and
                          it also means the Room-feed Tea card has never rendered
                          for the house either. HighlightsPanel itself derives
                          Character Stats + Best Quotes from ordinary messages,
                          so it renders fine without any analysis. */}
                      {stageTab !== 'highlights' && !isPortedSpace && (
                        /* marginTop dropped 08-07 — the lane wrapper owns the
                           section rhythm with its own flex gap now. */
                        <div role="tabpanel" data-tab-name="highlights">
                          {/* ⚠️ allActionsFlat is declared INSIDE the feed map
                              (~38706) and is NOT in scope here — flatten from
                              `results` directly, the same shape every other
                              out-of-map HighlightsPanel call site uses. */}
                          <HighlightsPanel
                            allActions={recapActions}
                            users={users}
                            THEME={THEME}
                            moderatorCharacter={chatGameStatus?.moderator_character}
                            gameId={gameId}
                            humanPlayerId={humanPlayerId}
                            shipVotes={chatGameStatus?.ship_votes}
                            builds={chatGameStatus?.builds || []}
                          />
                        </div>
                      )}
                      {/* 📖 THE STORY — the two sections the Room's "Story so
                          far" drawer used to hold. Media Spotlight stays
                          behind: the Media tab is its one home. */}
                      <HomepagePanel
                        allActions={recapActions}
                        users={users}
                        activeUsers={activeUsers}
                        THEME={THEME}
                        chatGameStatus={chatGameStatus}
                        mediaSuggestions={chatGameStatus?.media_suggestions}
                        showMediaSpotlight={false}
                      />
                      {/* 📰📌 The paper trail — renders nothing when a space has
                          never seen a note, so non-house spaces just skip it. */}
                      <RecapNotesPanel allActions={recapActions} THEME={THEME} />
                      {/* 🏠 House-only. No "Nothing pinned here yet." fallback
                          anymore — with the story + tea above and below it, an
                          empty-state card for a section that does not apply to
                          this space was just noise. */}
                      {houseStatus && (
                        <HouseMemoryWall
                          canon={houseStatus.canon || []}
                          memories={houseWall.memories}
                          scenes={houseWall.scenes || []}
                          pollHistory={houseWall.pollHistory || []}
                        threads={houseWall.threads || []} /* 🗝️ 10-14 Open mysteries */
                          loading={houseWall.loading}
                          till={null}
                          buildGoal={houseStatus.build_goal || 40}
                          gameId={gameId}
                          houseToday={houseWall.houseToday || null}
                          archive={houseWall.archive || null}
                        />
                      )}
                    </div>
                  )}
                  {chatActionTab === 'games' && (
                    <div role="tabpanel" data-tab-name="games" style={{ flex: '1 0 auto' }}>
                      <GamesPanel THEME={THEME} />
                    </div>
                  )}
                  {/* Live tab slot — the page-wide livestream panel's host node
                      reparents in here when the layout is narrow (see the
                      portal at the top of the render + the reparent effect).
                      No inline mount anymore: crossing the 1100px boundary
                      used to remount the panel and drop an active stream. */}
                  {!isThreeColumnLayout && !isBotMode && !placesNav && (
                    /* Real height + flex column so video tiles have something to
                       fill — in a heightless block the grid collapsed to its
                       min and webcams didn't fit without manual resizing.
                       📱 places nav: the slot lives on the TV place instead;
                       the hidden holder keeps an active stream alive between. */
                    <div role="tabpanel" data-tab-name="livestream" style={chatActionTab === 'livestream'
                      ? { display: 'flex', flexDirection: 'column', height: '58dvh', minHeight: 320 }
                      : { display: 'none' }}>
                      <div ref={livestreamTabSlotRef} style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }} />
                    </div>
                  )}
                  {/* 🕵️ Locals dossier mount moved into the case-file drawer at the
                      top of this scroller (user call 07-10: too many tabs; the
                      dossier is a folder, so it peeks out of the panel edge). */}
                  {results.map((result, index) => {
                    // (perf 08-13) Steps 0-3 (flatten → round-filter → reverse →
                    // analysis/episode sort → UI filter) live in feedDerivation
                    // (search that name) — they ran here on EVERY render.
                    const allActionsFlat = feedDerivation.allActionsFlat;
                    let actionsToRender = feedDerivation.perResult[index] || [];

                    // Step 4: Filter by chat action sub-tab (chat games only)
                    if ((gameType === 'chat-game' || gameType === 'chat') && chatActionTab === 'messages') {
                      actionsToRender = actionsToRender.filter(a =>
                        messageActionTypes.has(a.type) ||
                        (a.type === 'game_announcement' && a.data?.message?.match(/^(Round|Word Round) \d+ /)) ||
                        (a.type === 'game_announcement' && a.data?.final_rankings)
                      );
                    } else if ((gameType === 'chat-game' || gameType === 'chat') && ['bot_vision', 'games', 'highlights', 'recap', 'workbench'].includes(chatActionTab)) {
                      // Panel-based tabs are self-contained — no raw actions below
                      // ('recap' added #57: the memory wall was rendering with the
                      // entire room transcript stacked underneath it)
                      actionsToRender = [];
                    } else if ((gameType === 'chat-game' || gameType === 'chat') && chatActionTab === 'media') {
                      const mediaTypes = new Set(['episode', 'custom_video', 'slice_of_life']);
                      actionsToRender = actionsToRender.filter(a => mediaTypes.has(a.type));
                    } else if ((gameType === 'chat-game' || gameType === 'chat') && (chatActionTab === 'alerts' || chatActionTab === 'highlights')) {
                      actionsToRender = actionsToRender.filter(a =>
                        !messageActionTypes.has(a.type)
                      );
                      // On highlights tab, hide the very first announcement (e.g. "Let's get into it!")
                      if (chatActionTab === 'highlights') {
                        const firstAnnouncementIdx = actionsToRender.findIndex(a => a.type === 'game_announcement');
                        if (firstAnnouncementIdx !== -1) {
                          actionsToRender = actionsToRender.filter((_, i) => i !== firstAnnouncementIdx);
                        }
                      }
                    }

                    // Step 5: Hide continuation announcements until user clicks Continue
                    // In non-competitive mode, the "Back for more?" style announcement for rounds > 1
                    // should only appear after the user resumes play (isPlaying = true).
                    if (!isPlaying && !chatGameStatus?.competitive_mode && !chatGameStatus?.imposter_mode && !chatGameStatus?.game_completed) {
                      const lastContinuationIdx = actionsToRender.findLastIndex(a =>
                        a.type === 'game_announcement' && a.data?.is_continuation
                      );
                      if (lastContinuationIdx !== -1) {
                        actionsToRender = actionsToRender.filter((_, i) => i !== lastContinuationIdx);
                      }
                    }

                    // Step 6: 📣 Live room — the crowd joins the conversation
                    // lanes only (Room + Messages), and only the newest result
                    // (chat games keep one). Pure rules + pins: crowdInline.js.
                    // Runs AFTER the tab filters so a crowd line can never leak
                    // into Alerts/Media, and never touches allActionsFlat (the
                    // tea, recap and highlights readers must not see the crowd).
                    if (crowdInline && (chatActionTab === 'all' || chatActionTab === 'messages') && index === results.length - 1) {
                      actionsToRender = crowdMergeRef.current(actionsToRender, crowdItems);
                    }
                    // Step 7: 🏡 W2c the horizon row (utils/portedHorizon.js):
                    // one printed line where the resident's view of the chat
                    // ends, from the SERVER's boundary. The owner's desktop place
                    // page only (placeLine is null everywhere else), render step
                    // only: it never enters results or allActionsFlat.
                    if (placeLine && portedHorizonAt && chatActionTab === 'all' && index === results.length - 1) {
                      actionsToRender = horizonMergeRef.current(actionsToRender, portedHorizonAt);
                    }

                    return (
                      <div key={index}>
                        {/* 🎨 Sketchbook easel (D13 ruled 08-28): ported spaces
                            keep a standing invitation atop the Room lane — same
                            canvas the doodle flow opens. Carries the day-counter
                            chip (V1 idea 4) when the backend sends
                            space_created_at; null-safe on old backends. */}
                        {/* 💬 W2d.1 (A9): not on a place page (its doors: ✏️ in the composer, 🎨 Draw on a phone's dock) */}
                        {(gameType === 'chat-game' || gameType === 'chat') && isPortedSpace && !isBotMode && !portedPlaceOn && chatActionTab === 'all' && index === 0 && (
                          <div
                            onClick={() => setShowDrawingCanvas(true)}
                            role="button"
                            aria-label="Open the sketchbook"
                            style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '9px 12px', marginBottom: 10, cursor: 'pointer', userSelect: 'none', border: '1px solid rgba(255,255,255,0.14)', borderRadius: 10, background: 'rgba(20,22,30,0.55)' }}
                          >
                            <span style={{ fontSize: '1.05em' }}>🎨</span>
                            <span style={{ fontWeight: 800, fontSize: '0.85em' }}>Sketchbook</span>
                            <span style={{ fontSize: '0.75em', color: THEME.textMuted }}>draw them something</span>
                          </div>
                        )}
                        {/* 🫖 Tea card (Nina 07-26, Tea tab retired; 08-03 the
                            Whispers lane retired too): the Highlights Panel rides
                            as a collapsible card pinned atop the Room feed. Hidden
                            while the tea holds the stage (D2 doctrine: docked =
                            left the strip). role="tabpanel" + data-tab-name kept
                            for bot-mode reads. */}
                        {/* Tea card home = the LOCALS place on phones now (v2);
                            the Room-lane instance stays for desktop */}
                        {(gameType === 'chat-game' || gameType === 'chat') && !isMobile && chatActionTab === 'all' && hasTea && stageTab !== 'highlights' && index === 0 && !isPortedSpace && (
                          <div role="tabpanel" data-tab-name="highlights" style={{ marginBottom: 10 }}>
                            <div style={{ border: '1px solid rgba(236,64,122,0.35)', borderRadius: 12, background: 'rgba(236,64,122,0.06)', overflow: 'hidden' }}>
                              <div
                                onClick={() => setTeaCardOpen(o => !o)}
                                role="button"
                                aria-expanded={teaCardOpen}
                                aria-label={teaCardOpen ? 'Collapse The Tea' : 'Open The Tea'}
                                style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '9px 12px', cursor: 'pointer', userSelect: 'none' }}
                              >
                                <span style={{ fontSize: '1.05em' }}>🫖</span>
                                <span style={{ fontWeight: 800, fontSize: '0.85em', flex: 1, minWidth: 0 }}>The Tea</span>
                                {teaUnseen > 0 && !teaCardOpen && (
                                  <span style={{ minWidth: 16, height: 16, borderRadius: 8, padding: '0 5px', backgroundColor: '#ec407a', color: '#fff', fontSize: '0.62em', fontWeight: 800, lineHeight: '16px', textAlign: 'center', boxShadow: '0 0 6px rgba(236,64,122,0.6)', animation: 'liveBadgePulse 2s ease-in-out infinite' }}>
                                    {teaUnseen}
                                  </span>
                                )}
                                {/* 🎭/📌 keep the retired tab's stage + sidebar paths alive */}
                                {!isMobile && (
                                  <button onClick={(e) => { e.stopPropagation(); dockToStage('highlights'); }} title="Put The Tea on the stage" style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.85em', opacity: 0.85, padding: '0 4px', lineHeight: 1 }}>
                                    🎭
                                  </button>
                                )}
                                {isThreeColumnLayout && pinnedSidebarTab !== 'highlights' && (
                                  <button onClick={(e) => { e.stopPropagation(); setPinnedSidebarTab('highlights'); }} title="Pin The Tea to the sidebar" style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.85em', opacity: 0.85, padding: '0 4px', lineHeight: 1 }}>
                                    📌
                                  </button>
                                )}
                                <span aria-hidden style={{ fontWeight: 800, opacity: 0.7, fontSize: '0.8em' }}>{teaCardOpen ? '▾' : '▸'}</span>
                              </div>
                              {teaCardOpen && (
                                <HighlightsPanel allActions={allActionsFlat} users={users} THEME={THEME} moderatorCharacter={chatGameStatus?.moderator_character} gameId={gameId} humanPlayerId={humanPlayerId} shipVotes={chatGameStatus?.ship_votes} builds={chatGameStatus?.builds || []} />
                              )}
                            </div>
                          </div>
                        )}
                        {/* Suppress empty-state on panel tabs (media/homepage/bot_vision/games/highlights/livestream) — they intentionally clear actionsToRender and self-contain their content. */}
                        {(!panelTabs.has(chatActionTab) || actionsToRender.length > 0) && (
                          <VirtualizedActionList
                            portedInteract={isPortedSpace && !!humanPlayerId}
                            actions={actionsToRender}
                            playerModelMap={playerModelMap}
                            users={users}
                            THEME={THEME}
                            onActionUpdate={updateAction}
                            gameType={gameType}
                            allActions={allActionsFlat}
                            cachedSpaces={cachedSpaces}
                            cachedRoundAnalysisHistory={cachedRoundAnalysisHistory}
                            onReactionSubmit={handleReactionSubmit}
                            humanPlayerId={humanPlayerId}
                            effectivePlayerId={effectivePlayerId}
                            showThoughts={showThoughts}
                            imposterMode={chatGameStatus?.imposter_mode || false}
                            competitiveMode={chatGameStatus?.competitive_mode || chatGameStatus?.imposter_mode || false}
                            humanTokensLeft={tokensLeft}
                            uiTerms={chatGameStatus?.ui_terms || null}
                            noirTheme={!!chatGameStatus?.noir_theme}
                            onOpenCastCard={setChatCastPid}
                            houseNicknames={houseNicknames}
                            localStatuses={localStatuses}
                            onWatchLive={crowdInline ? handleWatchLiveFromChat : null}
                            crowdFull={crowdFull}
                            placeLine={placeLine}
                            placeChat={placeChat}
                          />
                        )}
                      </div>
                    );
                  })}

                  {/* 🎙️ Diegetic tutorial whisper (Nina 07-20): the host assigns
                      the current quest step as a CLIENT-LOCAL chat line — this
                      viewer only, never persisted or broadcast (a room-visible
                      host line would re-greet every newcomer in front of the
                      whole room). Pinned under the newest messages so it can't
                      scroll away; the pill stays as the compact reminder. */}
                  {tutorialQuestVisible && !tutorialActive && chatActionTab === 'all' && tutorialStepInfo.step && (() => {
                    // 🐣 09-25 (Nina): no visible host in a ported room. The
                    // resident signs it (ported rooms have no moderator, so this
                    // read "The Host" 🎙️ there); never the host fallback.
                    const mod = portedResidentName
                      ? { name: portedResidentName, avatar_emoji: '🐣',
                          profile_image_url: chatGameStatus?.resident_image || portedResidentUser?.profile_image_url || portedResidentUser?.data?.profile_image_url || null }
                      : isPortedSpace ? { avatar_emoji: '✨' }
                      : (chatGameStatus?.moderator_character || {});
                    const hostName = mod.name || (isPortedSpace ? '' : 'The Host');
                    const accent = THEME.accent || '#7C4DFF';
                    return (
                      <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-start', padding: '10px 14px 12px' }}>
                        <div style={{
                          width: '34px', height: '34px', borderRadius: '50%', flexShrink: 0,
                          background: 'rgba(255,255,255,0.08)', border: `1.5px solid ${accent}55`,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: '17px', overflow: 'hidden',
                        }}>
                          {mod.profile_image_url
                            ? <img src={mod.profile_image_url} alt={hostName} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                            : <span>{mod.avatar_emoji || '🎙️'}</span>}
                        </div>
                        <div style={{ minWidth: 0 }}>
                          <div style={{ display: 'flex', alignItems: 'baseline', gap: '7px', marginBottom: '3px', flexWrap: 'wrap' }}>
                            <span style={{ fontWeight: 800, fontSize: '0.8em', color: accent }}>{hostName}</span>
                            <span style={{ fontSize: '0.62em', color: THEME.textMuted || '#999', fontStyle: 'italic' }}>✨ only you can see this</span>
                          </div>
                          <div style={{
                            display: 'inline-block', padding: '8px 12px', borderRadius: '4px 12px 12px 12px',
                            background: `${accent}18`, border: `1px solid ${accent}30`,
                            color: THEME.textColor || '#fff', fontSize: '0.86em', lineHeight: 1.45,
                          }}>
                            {tutorialStepInfo.step.whisper}
                          </div>
                          <button
                            onClick={skipTutorial}
                            style={{
                              display: 'block', background: 'none', border: 'none', cursor: 'pointer',
                              padding: '4px 0 0', fontSize: '0.68em', color: THEME.textMuted || '#999',
                              textDecoration: 'underline', textUnderlineOffset: '2px',
                            }}
                          >skip the tour</button>
                        </div>
                      </div>
                    );
                  })()}

                  {/* 💡 One-time command-hint whisper — post-tour sibling of the
                      tutorial whisper above; see state block near
                      tutorialStepInfo for the gating rationale.
                      ⚠️ tutorial.done is LOAD-BEARING (Nina 08-28): without it,
                      declining the tour UNBLOCKED this surface (dismissed makes
                      tutorialQuestVisible false, so the old !(...) guard passed)
                      and it wears the identical host-bubble dress — "the
                      tutorial is still showing after I said No". Post-tour
                      means POST-TOUR, exactly as this comment always said. */}
                  {chatActionTab === 'all' && !isBotMode && !cmdWhisperDismissed
                    && !isPortedSpace // 🐣 09-25: the host's nudge ("director powers", /ship two locals) never in a 1:1 room
                    && tutorial.done
                    && !(tutorialQuestVisible && tutorialStepInfo.step)
                    && humanPlayerId
                    && !(chatGameStatus?.competitive_mode || chatGameStatus?.imposter_mode)
                    && (gameType === 'chat-game' || gameType === 'chat') && (() => {
                    const acts = results.flatMap(r => r.actions || []);
                    const mine = acts.filter(a => a.game_instance_player_id === humanPlayerId);
                    const myMsgs = mine.filter(a => a.type === 'human_interjection').length;
                    const myCmds = mine.filter(a => a.type === 'space_command').length;
                    if (myMsgs < 3 || myCmds > 0) return null;
                    const mod = chatGameStatus?.moderator_character || {};
                    const hostName = mod.name || 'The Host';
                    const accent = THEME.accent || '#7C4DFF';
                    return (
                      <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-start', padding: '10px 14px 12px' }}>
                        <div style={{
                          width: '34px', height: '34px', borderRadius: '50%', flexShrink: 0,
                          background: 'rgba(255,255,255,0.08)', border: `1.5px solid ${accent}55`,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: '17px', overflow: 'hidden',
                        }}>
                          {mod.profile_image_url
                            ? <img src={mod.profile_image_url} alt={hostName} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                            : <span>{mod.avatar_emoji || '🎙️'}</span>}
                        </div>
                        <div style={{ minWidth: 0 }}>
                          <div style={{ display: 'flex', alignItems: 'baseline', gap: '7px', marginBottom: '3px', flexWrap: 'wrap' }}>
                            <span style={{ fontWeight: 800, fontSize: '0.8em', color: accent }}>{hostName}</span>
                            <span style={{ fontSize: '0.62em', color: THEME.textMuted || '#999', fontStyle: 'italic' }}>✨ only you can see this</span>
                          </div>
                          <div style={{
                            display: 'inline-block', padding: '8px 12px', borderRadius: '4px 12px 12px 12px',
                            background: `${accent}18`, border: `1px solid ${accent}30`,
                            color: THEME.textColor || '#fff', fontSize: '0.86em', lineHeight: 1.45,
                          }}>
                            Psst. You have director powers here. Type / and try {cmdWhisperPlay}. Our secret. 🤫
                          </div>
                          <button
                            onClick={dismissCmdWhisper}
                            style={{
                              display: 'block', background: 'none', border: 'none', cursor: 'pointer',
                              padding: '4px 0 0', fontSize: '0.68em', color: THEME.textMuted || '#999',
                              textDecoration: 'underline', textUnderlineOffset: '2px',
                            }}
                          >got it</button>
                        </div>
                      </div>
                    );
                  })()}

                  {/* Load More button — only when there are more messages to fetch */}
                  {pagination && pagination.hasMore && (() => {
                    const loadedCount = results[0]?.actions?.length || 0;
                    const totalCount = Math.max(pagination.totalCount, loadedCount);
                    return (
                      <div style={{
                        padding: '16px',
                        textAlign: 'center',
                        borderTop: `1px solid ${THEME.borderLight}`,
                        backgroundColor: THEME.bgMedium
                      }}>
                        <div style={{
                          color: THEME.textMuted,
                          fontSize: '14px',
                          marginBottom: '12px'
                        }}>
                          Showing {loadedCount} of {totalCount} messages
                        </div>
                        <button
                          onClick={loadMoreActions}
                          disabled={isLoadingMore}
                          style={{
                            padding: '10px 24px',
                            backgroundColor: THEME.primary,
                            color: THEME.textLight,
                            border: 'none',
                            borderRadius: '6px',
                            cursor: isLoadingMore ? 'not-allowed' : 'pointer',
                            fontSize: '14px',
                            fontWeight: '500',
                            opacity: isLoadingMore ? 0.7 : 1
                          }}
                        >
                          {isLoadingMore ? 'Loading...' : 'Load earlier messages'}
                        </button>
                      </div>
                    );
                  })()}
                </div>
              )}

              {/* Charts Tab */}
              {activeTab === 'charts' && results.length > 0 && results[0].actions && results[0].actions.length > 0 && results[0].actions[0].game_instance_id && (
                <div style={{
                  backgroundColor: THEME.bgMedium,
                  padding: '20px',
                  borderRadius: '8px',
                  flex: 1,
                  overflowY: 'auto',
                  border: `1px solid ${THEME.borderDark}`
                }}>
                  {/* Round Filter - COMMENTED OUT - May reuse for right sidebar later
                  {results.length > 0 && (() => {
                    const allActions = results.flatMap(r => r.actions || []);
                    const roundIdToNumber = {};
                    const uniqueRoundIds = [...new Set(allActions.map(a => a.game_instance_round_id).filter(id => id))];
                    const roundIdToTimestamp = {};
                    uniqueRoundIds.forEach(roundId => {
                      const actionsInRound = allActions.filter(a => a.game_instance_round_id === roundId);
                      if (actionsInRound.length > 0) {
                        const timestamps = actionsInRound.map(a => new Date(a.created_at).getTime());
                        roundIdToTimestamp[roundId] = Math.min(...timestamps);
                      }
                    });
                    const sortedRoundIds = uniqueRoundIds.sort((a, b) =>
                      roundIdToTimestamp[a] - roundIdToTimestamp[b]
                    );
                    sortedRoundIds.forEach((roundId, index) => {
                      roundIdToNumber[roundId] = index + 1;
                    });
                    const roundNumbers = [...new Set(
                      allActions.map(a => roundIdToNumber[a.game_instance_round_id]).filter(n => n != null)
                    )].sort((a, b) => a - b);

                    if (roundsMetadata.length >= 1) {
                      return (
                        <div style={{
                          marginBottom: '16px',
                          padding: '12px',
                          backgroundColor: THEME.bgLight,
                          borderRadius: '6px',
                          border: `1px solid ${THEME.borderLight}`,
                          display: 'flex',
                          alignItems: 'center',
                          gap: '12px'
                        }}>
                          <label style={{
                            fontWeight: 'bold',
                            color: THEME.textDark,
                            fontSize: '0.9em'
                          }}>
                            Filter by Round:
                          </label>
                          <select
                            value={selectedRound}
                            onChange={(e) => handleRoundChange(e.target.value)}
                            style={{
                              padding: '6px 12px',
                              borderRadius: '4px',
                              border: `1px solid ${THEME.borderLight}`,
                              backgroundColor: 'white',
                              color: THEME.textDark,
                              fontSize: '0.9em',
                              cursor: 'pointer',
                              minWidth: '120px'
                            }}
                          >
                            <option value="current">Current Round</option>
                            {roundsMetadata.map(r => (
                              <option key={r.roundNumber} value={r.roundNumber}>Round {r.roundNumber}</option>
                            ))}
                          </select>
                        </div>
                      );
                    }
                    return null;
                  })()}
                  */}

                  <BenchmarkCharts
                    gameInstanceId={results[0].actions[0].game_instance_id}
                    apiBaseUrl={API_BASE_URL}
                    users={activeUsers}
                  />
                </div>
              )}

              {/* Videos Tab */}
              {activeTab === 'videos' && results.length > 0 && results[0].actions && results[0].actions.length > 0 && results[0].actions[0].game_instance_id && (
                <div style={{
                  backgroundColor: THEME.bgMedium,
                  padding: '20px',
                  borderRadius: '8px',
                  flex: 1,
                  overflowY: 'auto',
                  border: `1px solid ${THEME.borderDark}`
                }}>
                  {/* Round Filter - COMMENTED OUT - May reuse for right sidebar later
                  {results.length > 0 && (() => {
                    const allActions = results.flatMap(r => r.actions || []);
                    const roundIdToNumber = {};
                    const uniqueRoundIds = [...new Set(allActions.map(a => a.game_instance_round_id).filter(id => id))];
                    const roundIdToTimestamp = {};
                    uniqueRoundIds.forEach(roundId => {
                      const actionsInRound = allActions.filter(a => a.game_instance_round_id === roundId);
                      if (actionsInRound.length > 0) {
                        const timestamps = actionsInRound.map(a => new Date(a.created_at).getTime());
                        roundIdToTimestamp[roundId] = Math.min(...timestamps);
                      }
                    });
                    const sortedRoundIds = uniqueRoundIds.sort((a, b) =>
                      roundIdToTimestamp[a] - roundIdToTimestamp[b]
                    );
                    sortedRoundIds.forEach((roundId, index) => {
                      roundIdToNumber[roundId] = index + 1;
                    });
                    const roundNumbers = [...new Set(
                      allActions.map(a => roundIdToNumber[a.game_instance_round_id]).filter(n => n != null)
                    )].sort((a, b) => a - b);

                    if (roundsMetadata.length >= 1) {
                      return (
                        <div style={{
                          marginBottom: '16px',
                          padding: '12px',
                          backgroundColor: THEME.bgLight,
                          borderRadius: '6px',
                          border: `1px solid ${THEME.borderLight}`,
                          display: 'flex',
                          alignItems: 'center',
                          gap: '12px'
                        }}>
                          <label style={{
                            fontWeight: 'bold',
                            color: THEME.textDark,
                            fontSize: '0.9em'
                          }}>
                            Filter by Round:
                          </label>
                          <select
                            value={selectedRound}
                            onChange={(e) => handleRoundChange(e.target.value)}
                            style={{
                              padding: '6px 12px',
                              borderRadius: '4px',
                              border: `1px solid ${THEME.borderLight}`,
                              backgroundColor: 'white',
                              color: THEME.textDark,
                              fontSize: '0.9em',
                              cursor: 'pointer',
                              minWidth: '120px'
                            }}
                          >
                            <option value="current">Current Round</option>
                            {roundsMetadata.map(r => (
                              <option key={r.roundNumber} value={r.roundNumber}>Round {r.roundNumber}</option>
                            ))}
                          </select>
                        </div>
                      );
                    }
                    return null;
                  })()}
                  */}

                  <CustomVideosSection
                    gameInstanceId={results[0].actions[0].game_instance_id}
                    THEME={THEME}
                    API_BASE_URL={API_BASE_URL}
                  />
                </div>
              )}

              {/* Summary Tab */}
              {activeTab === 'summary' && results.length > 0 && (() => {
                // Get all week_summary actions
                const allActions = results.flatMap(r => r.actions || []);
                const summaryActions = allActions.filter(a => a.type === 'week_summary');

                // Build round ID to number mapping
                const roundIdToNumber = {};
                const uniqueRoundIds = [...new Set(allActions.map(a => a.game_instance_round_id).filter(id => id))];
                const roundIdToTimestamp = {};
                uniqueRoundIds.forEach(roundId => {
                  const actionsInRound = allActions.filter(a => a.game_instance_round_id === roundId);
                  if (actionsInRound.length > 0) {
                    const timestamps = actionsInRound.map(a => new Date(a.created_at).getTime());
                    roundIdToTimestamp[roundId] = Math.min(...timestamps);
                  }
                });
                const sortedRoundIds = uniqueRoundIds.sort((a, b) =>
                  roundIdToTimestamp[a] - roundIdToTimestamp[b]
                );
                sortedRoundIds.forEach((roundId, index) => {
                  roundIdToNumber[roundId] = index + 1;
                });

                // Filter summaries by selected round
                let filteredSummaries = summaryActions;
                if (selectedRound) {
                  if (selectedRound === 'current') {
                    const highestRoundNum = Math.max(...Object.values(roundIdToNumber));
                    filteredSummaries = summaryActions.filter(a =>
                      roundIdToNumber[a.game_instance_round_id] === highestRoundNum
                    );
                  } else {
                    const roundNum = parseInt(selectedRound, 10);
                    filteredSummaries = summaryActions.filter(a =>
                      roundIdToNumber[a.game_instance_round_id] === roundNum
                    );
                  }
                }

                // Sort summaries chronologically (first to last)
                filteredSummaries = filteredSummaries.sort((a, b) => {
                  const timeA = new Date(a.created_at).getTime();
                  const timeB = new Date(b.created_at).getTime();
                  return timeA - timeB;
                });

                return (
                  <div style={{
                    backgroundColor: THEME.bgMedium,
                    padding: '20px',
                    borderRadius: '8px',
                    flex: 1,
                    overflowY: 'auto',
                    border: `1px solid ${THEME.borderDark}`
                  }}>
                    {/* Round Filter and Display Toggles */}
                    <div style={{
                      marginBottom: '16px',
                      padding: '12px',
                      backgroundColor: THEME.bgLight,
                      borderRadius: '6px',
                      border: `1px solid ${THEME.borderLight}`
                    }}>
                      {/* Round Filter - COMMENTED OUT - May reuse for right sidebar later
                      {summaryActions.length > 1 && (
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '12px',
                          marginBottom: '12px',
                          paddingBottom: '12px',
                          borderBottom: `1px solid ${THEME.borderLight}`
                        }}>
                          <label style={{
                            fontWeight: 'bold',
                            color: THEME.textDark,
                            fontSize: '0.9em'
                          }}>
                            Filter by Round:
                          </label>
                          <select
                            value={selectedRound}
                            onChange={(e) => handleRoundChange(e.target.value)}
                            style={{
                              padding: '6px 12px',
                              borderRadius: '4px',
                              border: `1px solid ${THEME.borderLight}`,
                              backgroundColor: 'white',
                              color: THEME.textDark,
                              fontSize: '0.9em',
                              cursor: 'pointer',
                              minWidth: '120px'
                            }}
                          >
                            <option value="current">Current Round</option>
                            {Object.entries(roundIdToNumber)
                              .filter(([roundId, _]) => summaryActions.some(a => a.game_instance_round_id === roundId))
                              .sort((a, b) => a[1] - b[1])
                              .map(([_, num]) => (
                                <option key={num} value={num}>Round {num}</option>
                              ))
                            }
                          </select>
                        </div>
                      )}
                      */}

                      {/* Display Toggles and Refresh Button */}
                      <div style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: '16px',
                        alignItems: 'center',
                        justifyContent: 'space-between'
                      }}>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', alignItems: 'center' }}>
                          <div style={{ fontWeight: 'bold', color: THEME.textDark, fontSize: '0.9em' }}>
                            Show:
                          </div>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', fontSize: '0.9em' }}>
                          <input
                            type="checkbox"
                            checked={showSummaryPhotos}
                            onChange={(e) => setShowSummaryPhotos(e.target.checked)}
                            style={{ cursor: 'pointer' }}
                          />
                          📸 Photos
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', fontSize: '0.9em' }}>
                          <input
                            type="checkbox"
                            checked={showSummaryNarrative}
                            onChange={(e) => setShowSummaryNarrative(e.target.checked)}
                            style={{ cursor: 'pointer' }}
                          />
                          📖 Narrative
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', fontSize: '0.9em' }}>
                          <input
                            type="checkbox"
                            checked={showSummaryHighlights}
                            onChange={(e) => setShowSummaryHighlights(e.target.checked)}
                            style={{ cursor: 'pointer' }}
                          />
                          ⭐ Highlights
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', fontSize: '0.9em' }}>
                          <input
                            type="checkbox"
                            checked={showSummaryVotes}
                            onChange={(e) => setShowSummaryVotes(e.target.checked)}
                            style={{ cursor: 'pointer' }}
                          />
                          🗳️ Votes
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', fontSize: '0.9em' }}>
                          <input
                            type="checkbox"
                            checked={showSummaryBenchmarks}
                            onChange={(e) => setShowSummaryBenchmarks(e.target.checked)}
                            style={{ cursor: 'pointer' }}
                          />
                          📊 Benchmarks
                        </label>
                        </div>

                        {/* Refresh Button */}
                        <button
                          onClick={handleRefreshSummaries}
                          disabled={isRefreshingSummaries}
                          style={{
                            padding: '8px 16px',
                            borderRadius: '4px',
                            border: `1px solid ${THEME.borderLight}`,
                            backgroundColor: THEME.bgLight,
                            color: THEME.textDark,
                            fontSize: '0.9em',
                            cursor: isRefreshingSummaries ? 'not-allowed' : 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            fontWeight: 'bold',
                            opacity: isRefreshingSummaries ? 0.6 : 1
                          }}
                        >
                          <span style={{
                            display: 'inline-block',
                            animation: isRefreshingSummaries ? 'spin 1s linear infinite' : 'none',
                            transformOrigin: 'center'
                          }}>
                            🔄
                          </span>
                          {isRefreshingSummaries ? 'Refreshing...' : 'Refresh'}
                        </button>
                      </div>
                    </div>

                    {/* Summary Actions */}
                    {filteredSummaries.length === 0 ? (
                      <div style={{
                        textAlign: 'center',
                        padding: '40px',
                        color: THEME.textMuted
                      }}>
                        No week summaries available yet. Generate summaries after eviction rounds!
                      </div>
                    ) : (
                      (() => {
                        // Build cumulative evicted player list as we go through summaries chronologically
                        const evictedPlayerIds = new Set();

                        return filteredSummaries.map((action, idx) => {
                          // Clone the current evicted list for this summary (players evicted BEFORE this week)
                          const evictedUpToNow = new Set(evictedPlayerIds);

                          // Add this week's evicted player to the running list for future summaries
                          if (action.data?.evictedPlayerId) {
                            evictedPlayerIds.add(action.data.evictedPlayerId);
                          }

                          // Week number is simply the position in the chronologically sorted summaries list
                          const weekNumber = idx + 1;

                          return (
                            <div key={idx} style={{ marginBottom: '16px' }}>
                              <WeekSummaryAction
                                action={action}
                                users={users}
                                THEME={THEME}
                                showPhotos={showSummaryPhotos}
                                showNarrative={showSummaryNarrative}
                                showHighlights={showSummaryHighlights}
                                showVotes={showSummaryVotes}
                                showBenchmarks={showSummaryBenchmarks}
                                evictedPlayerIds={evictedUpToNow}
                                weekNumber={weekNumber}
                                allActions={allActions}
                              />
                            </div>
                          );
                        });
                      })()
                    )}
                  </div>
                );
              })()}

            </div>)}</>)}

            {/* Start Convo / Continue / Reset — three-column, competitive/imposter only */}
            {isThreeColumnLayout && chatActionTab === 'all' && (gameType === 'chat-game' || gameType === 'chat') && (chatGameStatus?.competitive_mode || chatGameStatus?.imposter_mode) && (() => {
              const isInReadyCheck = chatGameStatus?.is_ready_check && !chatGameStatus?.ready_check_data?.done;
              const readyToStart = isInReadyCheck && readyCheckPhase === 'ready_to_start';
              const blockedByReadyCheck = isInReadyCheck && readyCheckPhase !== 'ready_to_start';
              const isGameStarting = gameStarted && !playRoundMode && !isPlaying;
              const isDisabled = isLoading || isPlaying || blockedByReadyCheck || isGameStarting;
              const currentRoundNum = chatGameStatus?.round_number || 1;
              const isFirstRound = currentRoundNum === 1;
              const isActivePlayer = humanPlayerId && !effectiveIsSpectating && !chatGameStatus?.eliminated_players?.includes(humanPlayerId);

              if (chatGameStatus?.game_completed) {
                return resetOfferedHere ? ( // 🛡️ 10-01 safety floor (H2)
                  <div style={{ flexShrink: 0, marginTop: '8px' }}>
                    <button type="button" onClick={async () => {
                      if (cloningSpace) return;
                      setCloningSpace(true);
                      try {
                        await chatGameApi.resetGame(gameId, humanPlayerId || createdPlayerIdRef.current); // 🛡️ 10-01 safety floor (H2): the seat's proof
                        localStorage.removeItem(`humanPlayerId_${gameId}`);
                        window.location.href = `/game-step?gameId=${gameId}`;
                      } catch (err) {
                        console.error('[Reset] Failed:', err);
                        alert(`Failed to reset space: ${err.message}`);
                      } finally {
                        setCloningSpace(false);
                      }
                    }} disabled={cloningSpace} style={{
                      width: '100%', padding: '10px 14px', borderRadius: '10px',
                      border: '1px solid rgba(255, 255, 255, 0.15)',
                      backgroundColor: cloningSpace ? THEME.textMuted : THEME.accent,
                      color: '#ffffff', cursor: cloningSpace ? 'not-allowed' : 'pointer',
                      opacity: cloningSpace ? 0.7 : 1, fontWeight: 700, fontSize: '0.9em',
                      textShadow: '0 1px 3px rgba(0,0,0,0.5)', boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                    }}>
                      {cloningSpace ? 'Resetting...' : 'Reset'}
                    </button>
                  </div>
                ) : null; // 🛡️ 10-01 safety floor (H2)
              }

              if (isInReadyCheck && !humanPlayerId) {
                return (
                  <div style={{
                    width: '100%', padding: '10px 14px', borderRadius: '10px',
                    border: `1px solid ${THEME.textOnPanelMuted}30`,
                    backgroundColor: `${THEME.textOnPanelMuted}15`,
                    color: THEME.textOnPanelMuted, fontWeight: 600, fontSize: '0.9em',
                    textAlign: 'center', boxSizing: 'border-box', flexShrink: 0, marginTop: '8px',
                  }}>
                    Spectating
                  </div>
                );
              }

              const buttonLabel = isPlaying ? 'Playing...'
                : isGameStarting ? 'Starting...'
                : readyToStart ? `Start! (${autoStartCountdown}s)`
                : blockedByReadyCheck ? 'Waiting for players...'
                : isFirstRound ? 'Start Convo' : 'Continue';

              return (
                <div style={{ flexShrink: 0, marginTop: '8px' }}>
                  <button ref={isActivePlayer ? startButtonRef : undefined} type="button"
                    onClick={() => {
                      if (isActivePlayer && readyToStart) {
                        gameStartedRef.current = true;
                        setGameStarted(true);
                        setReadyCheckPhase('waiting');
                        // [requester-ready-fix] ready the clicking player backend-side
                        const t0 = Date.now();
                        chatGameApi.skipReadyCheck(gameId, null, createdPlayerIdRef.current)
                          .catch(err => console.error('[StartGame] skipReadyCheck failed:', err))
                          .finally(() => {
                            const wait = Math.max(0, 800 - (Date.now() - t0));
                            setTimeout(() => handlePlayRound(), wait);
                          });
                      } else {
                        handlePlayRound();
                      }
                    }}
                    disabled={isDisabled}
                    style={{
                      width: '100%', display: 'block', padding: '12px 24px', borderRadius: '10px',
                      border: '1px solid rgba(255, 255, 255, 0.15)',
                      backgroundColor: readyToStart ? '#4ade80' : THEME.good,
                      color: '#ffffff', cursor: isDisabled ? 'not-allowed' : 'pointer',
                      opacity: isDisabled ? 0.5 : 1, fontWeight: 700, fontSize: '1em',
                      letterSpacing: '0.3px', textShadow: '0 1px 3px rgba(0,0,0,0.5)',
                      boxShadow: readyToStart ? '0 2px 12px rgba(74,222,128,0.4)' : (!isDisabled && !isPlaying) ? `0 0 12px ${THEME.good}80, 0 0 24px ${THEME.good}40` : '0 2px 8px rgba(0,0,0,0.2)',
                      animation: (readyToStart || (!isDisabled && !isPlaying)) ? 'playButtonGlow 2s ease-in-out infinite' : 'none',
                      boxSizing: 'border-box',
                    }}
                  >
                    <BouncingText text={buttonLabel} />
                  </button>
                </div>
              );
            })()}

            {/* ⬆️⬇️ Jump buttons: MOVED to the page-level fixed cluster (grep
                data-jump-cluster; 08-03 audit #12). The sticky wrapper that
                lived here could not pin whenever the virtualizer owned the
                scroll (the container itself never scrolls), so it sat inert
                in flow and collided with the case-file folder tabs in the
                same bottom-right corner (probe-identified: "Open the locals
                case file" occluding jump-to-top). */}

            {/* Human Input Form — three-column mode: always visible for non-competitive, gated for competitive.
                Whispers gets the composer too (Nina 07-26): it's the pure-talk
                lane, and what you type IS a whisper — parking the composer on
                All only forced a tab hop mid-conversation. Panel tabs (Tea/
                Media/Arcade) stay composer-free; mobile's thumb dock already
                rides every tab. */}
            {/* 🏡 W2d: a place page keeps its composer HERE, under the chat, at every
                desktop width (mockup v9); the narrow layout's right-column mount steps aside */}
            {/* 🧱 the room frame: THIS mount is its one composer, at every width
                (shellSlot portals it into the frame; the right-column mount steps aside) */}
            {(isThreeColumnLayout || (portedPlaceOn && !isMobile) || !!shellPreset) && chatActionTab === 'all' && humanPlayerId && (!effectiveIsSpectating || (!chatGameStatus?.competitive_mode && !chatGameStatus?.imposter_mode)) && !chatGameStatus?.eliminated_players?.includes(humanPlayerId) && (!(chatGameStatus?.competitive_mode || chatGameStatus?.imposter_mode) || (!chatGameStatus?.is_ready_check && (isPlaying || gameStarted))) && shellSlot('composer',
              <div ref={inputFormRef} style={{ flexShrink: 0, position: 'relative',
                // z 30 (Nina 08-04 screenshot): the composer's suggestion overlays
                // (arg-hint bar, palette, @/# dropdowns) grow UPWARD over whatever
                // sits above — but the form's backdrop-filter traps their z:1000
                // inside its own stacking context, so the sticky tab strip (z 25)
                // painted over them whenever the feed squeezed the composer up
                // against the tabs. Lift the whole dock above the strip instead.
                zIndex: 30,
                // 🎛️ Case-dock chrome (mock-approved 07-25): whodunnit's composer
                // reads as THE dock — the one surface that asks. Desktop only;
                // mobile's thumb dock already carries its own chrome.
                ...(chatGameStatus?.noir_theme && chatGameStatus?.competitive_mode ? {
                  border: '1px solid rgba(217,186,114,0.45)', borderRadius: 14,
                  background: 'rgba(23,19,28,0.55)', padding: '10px 12px', marginTop: 8,
                  boxShadow: '0 10px 30px rgba(0,0,0,0.4)',
                  // 🎯 Live ask → the whole dock lifts to TRUE page center
                  // (Nina 07-26 screenshot: bottom-center still read as the
                  // dock; center stage means middle of the screen). No
                  // overflow clamp on purpose — the command palette / doodle
                  // popover render outside this box and would clip.
                  ...(noirCenterAsk ? {
                    position: 'fixed', left: '50%', top: '50%', transform: 'translate(-50%, -50%)',
                    width: 'min(680px, 92vw)', zIndex: 4900, marginTop: 0,
                    background: 'rgba(23,19,28,0.94)', backdropFilter: 'blur(8px)',
                    boxShadow: '0 18px 60px rgba(0,0,0,0.65)',
                  } : {}),
                } : {}) }}>
                {/* ✏️ Doodle popover — slides up out of the composer (desktop);
                    mobile keeps the full-screen modal mounted at page level */}
                {showDrawingCanvas && !isBotMode && !isMobile && doodleAnchored && (
                  <DrawingCanvas
                    key={drawOnSeed?.actionId || 'blank'}
                    anchored
                    onSend={handleSendDrawing}
                    onClose={closeDrawingCanvas}
                    theme={THEME}
                    initialStrokes={drawOnSeed?.strokes || null}
                  />
                )}
                {/* 🧱 the room frame: "X is thinking" rides over the feed's bottom
                    edge here (today's fixed pill sits on the frame's left rail) */}
                {shellPreset && currentlyTyping && !wywaVisible && (
                  <div aria-live="polite" data-shell-typing="" style={{ position: 'absolute', bottom: '100%', left: 4, marginBottom: 6, zIndex: 5, pointerEvents: 'none', maxWidth: 'calc(100% - 8px)' }}>
                    <TypingIndicator playerName={currentlyTyping} theme={THEME} compact={isMobile} />
                  </div>
                )}
                {/* 🫂 the care cards (check-in · crisis), private, at the dock's top */}
                {renderCareCards()}
                {/* Docket mode (Nina rev 3): the form's manila tab must top the card, so
                    the companion strips move BELOW the composer. Classic order otherwise. */}
                {!noirWriteDocket && (<>
                  {renderActionDock()}
                  {renderPoisonStrip()}
                  {renderHospitalNotice()}
                  {renderFateDie()}
                  {renderPowerStatus()}
                  {renderCritPicker()}
                  {renderWordBanPicker()}
                  {renderSpacePoll()}
                  {renderPayoffPing()}
                </>)}
                {/* 🚪📷 Room toast (09-15, replaces the retired chips bar):
                    /room replies (moves, lists, locked doors) + the ONE
                    located-event invite (§6 ruled: glow + ask, NEVER
                    auto-move) float over the composer and self-clear.
                    Same payload gate as the camera; 09-16: a mobile TWIN
                    renders in-flow at the top of the phone dock (grep
                    "mobile twin") — KEEP THE TWO IN STEP. z 990: under the
                    palette (1000) so typing "/" wins the slot. */}
                {roomCameraOn && !isBotMode && (() => {
                  const invite = roomInvite; // ONE resolver for both mounts (grep roomInvite)
                  if (roomPickerOpen || (!roomNotice && !invite && !library.join)) return null; // the pictures own the slot while open (the event room glows there) · 📚 + the library's join line
                  const dismiss = () => setDismissedRoomInvites((s) => { const n = new Set(s); n.add(invite.id); return n; });
                  const joinBtn = {
                    fontSize: 11.5, padding: '3px 10px', borderRadius: 999, cursor: 'pointer',
                    border: '1px solid #8e7ac4', background: '#8e7ac4', color: '#1a1426', fontWeight: 700,
                  };
                  return (
                    // pointerEvents none on the shell: the notice is read-only
                    // and must not block clicks on the feed's bottom lines
                    // for its lifetime; only the invite's buttons need input
                    // (preship review).
                    <div style={{
                      position: 'absolute', bottom: '100%', left: 0, right: 0,
                      marginBottom: 8, zIndex: 990, pointerEvents: 'none',
                      background: 'rgba(20,14,9,0.92)', border: '1px solid rgba(122,96,60,0.55)',
                      borderRadius: 10, padding: '8px 12px',
                      display: 'flex', flexDirection: 'column', gap: 6,
                    }}>
                      {roomNotice && (
                        <div style={{ fontSize: 12.5, color: '#d9cdbb', whiteSpace: 'pre-line', lineHeight: 1.5 }}>
                          {roomNotice}
                        </div>
                      )}
                      {invite && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', fontSize: 12, color: '#cfc4e8', pointerEvents: 'auto' }}>
                          <span>{invite.icon} {invite.phrase ? `${invite.phrase} ` : ''}The residents are heading to {invite.label}.</span>
                          <button type="button" style={joinBtn}
                            onClick={() => {
                              openPictureIn(invite.room); // 🌕 09-26: join = the room's picture, like every Go
                              dismiss();
                            }}>
                            join them
                          </button>
                          <button type="button" onClick={dismiss} style={{ ...joinBtn, background: 'rgba(14,10,7,0.66)', border: '1px solid rgba(122,96,60,0.55)', color: '#d9cdbb', fontWeight: 400 }}>
                            later
                          </button>
                        </div>
                      )}
                      {/* 📚 "📖 Nina opened Frankenstein in the library · Join ✕" (once per open, per device) */}
                      {library.join && <LibraryJoinLine join={library.join} />}
                    </div>
                  );
                })()}
                {renderRoomPicker('desktop')}
                {renderCalendar('desktop')}
                {renderRoomPlate()}
                <HumanInputForm
                  crowdAliases={crowdAliases}
                  prefillNonce={composerSeedNonce}
                  // 🚪 /room autocomplete chips (09-15, chips bar retired):
                  // payload-gated like the camera. 09-16: BOTH mounts pass
                  // this now (Nina's full-mobile-lane ruling) — keep the two
                  // expressions identical.
                  houseRooms={houseRoomsProp()}
                  houseCalendar={houseCalendarProp()}
                  resting={restingProp()}
                  eventStrip={TOP_CLOCK ? null : eventStripProp()}
                  onPaletteOpen={handleTourPaletteOpen}
                  onOpenDoodleTurn={chatGameStatus?.doodle_bug ? openDoodleTurn : null}
                  isSpaceMod={!!humanPlayerId && (humanPlayerId === chatGameStatus?.human_player_identifier || humanPlayerId === createdPlayerIdRef.current)}
                  portedRoom={isPortedSpace}
                  roomPurpose={isPortedSpace ? null : (chatGameStatus?.purpose || null)} // 🧭 10-02 a study or research room keeps only the verbs that fit it (a ported room keeps its arc's verbs)
                  pictures={isPortedSpace ? (chatGameStatus?.pictures || null) : null}
                  placeRoom={portedPlaceOn}
                placeOwner={portedPlaceOn && isPortedOwner}
                  placeName={portedPlaceOn ? (portedMindProps?.residentName || null) : null}
                  onComposerActivity={portedPlaceOn ? setPlaceComposerActive : null}
                  // 🧱 the room frame's labeled Share (null everywhere else: no button)
                  share={shellComposerShare}
                  // 🧱 …and the frame's composer look (one box, mockup frame A); null = today's composer
                  framed={shellPreset ? { name: chatGameStatus?.name || '' } : null}
                  playerId={humanPlayerId}
                  playerName={users.find(u => u.game_player_id === humanPlayerId)?.name || 'You'}
                  availableActions={availableActions}
                  tokensLeft={tokensLeft}
                  onSubmit={handleHumanSubmit}
                  onSubmitInterjection={(chatGameStatus?.competitive_mode || chatGameStatus?.imposter_mode) ? handleInterjectionSubmit : handleInterjectionAndPlay}
                  // 📺 Teach @watchparty only while THIS user has the theater
                  // open (Nina 08-27, was space-level): an un-seated sender
                  // can't even see their own quip land. watchParty = WS-fresh
                  // party state; inTheater = this user's couch seat.
                  watchPartyOn={!!watchParty && inTheater}
                  disabled={isLoading || !!chatGameStatus?.you_are_hospitalized}
                  locked={rollLocked}
                  onRollDie={rollDie}
                  diceRolling={diceRolling}
                  diceLabel={chatGameStatus?.dice?.label}
                  prefillText={composerSeed || ((!isBotMode && (chatGameStatus?.current_competitive_round || 1) === 1 && (chatGameStatus?.twist?.story_seed || chatGameStatus?.intro_opening_line)) ? `${chatGameStatus.twist?.story_seed || chatGameStatus.intro_opening_line} ` : null)}
                  bannedWords={visibleBannedWords()}
                  sentenceCap={chatGameStatus?.sentence_cap || null}
                  sideQuest={(!isBotMode && chatGameStatus?.human_challenge) ? {
                    ...chatGameStatus.human_challenge,
                    done: sideQuestClaimed === chatGameStatus.human_challenge.text
                  } : null}
                  replyCredit={!!(chatGameStatus?.competitive_mode && !chatGameStatus?.imposter_mode
                    && (chatGameStatus?.competitive_round || 1) >= 2 && tokensLeft === 1)}
                  theme={THEME}
                  alwaysAvailable={true}
                  isYourTurn={isHumanTurn && !!currentRequestId}
                  turnDeadline={turnDeadline}
                  voteFold={noirVoteAskOpen()}
                  suppressIdle={!!(chatGameStatus?.noir_theme && chatGameStatus?.competitive_mode && !noirWriteDocket && chatGameStatus?.dice?.your_roll && diceRevealedRound === diceRoundKey)}
                  interjectionsRemaining={interjectionsRemaining}
                  imposterMode={chatGameStatus?.imposter_mode || false}
                  competitiveMode={chatGameStatus?.competitive_mode || false}
                  pendingTurnType={chatGameStatus?.pending_interaction?.type || null}
                  noirDocket={noirWriteDocket ? { headline: 'The room is listening.', facts: noirDocketFacts() } : null}
                  betReminder={(!isBotMode && chatGameStatus?.your_bet?.status === 'accepted'
                    && chatGameStatus.your_bet.round === (chatGameStatus?.current_competitive_round || 1))
                    ? { word: chatGameStatus.your_bet.word, points: chatGameStatus.your_bet.points } : null}
                  onSubmitVote={handleVoteSubmit}
                  players={users.filter(u => u.game_player_id !== humanPlayerId).map(u => ({ name: u.name, model: u.model, profile_image_url: u.profile_image_url, game_player_id: u.game_player_id, is_human: u.is_human }))}
                  shelfItems={chatGameStatus?.builds || []}
                  onPoke={(!chatGameStatus?.competitive_mode && !chatGameStatus?.imposter_mode && !chatGameStatus?.game_completed) ? () => handlePlayRound() : null}
                  onOpenDrawing={(!isBotMode && !chatGameStatus?.competitive_mode && !chatGameStatus?.imposter_mode && chatGameStatus?.tool_flags?.doodle !== false) ? () => setShowDrawingCanvas(true) : null}
                  onUploadFile={(!isBotMode && !chatGameStatus?.competitive_mode && !chatGameStatus?.imposter_mode) ? handleUploadFile : null}
                  trayMode={placesNav}
                  onOpenShelf={placesNav ? () => { setDossierOpen(false); setShelfOpen(true); } : null}
                  isPoking={isPlaying}
                />
                {noirWriteDocket && (<>
                  {renderPoisonStrip()}
                  {renderHospitalNotice()}
                  {renderPowerStatus()}
                  {renderCritPicker()}
                  {renderWordBanPicker()}
                  {renderSpacePoll()}
                </>)}
              </div>
            )}

            {/* Reset button — non-competitive, game completed */}
            {isThreeColumnLayout && chatActionTab === 'all' && (gameType === 'chat-game' || gameType === 'chat') && !chatGameStatus?.competitive_mode && !chatGameStatus?.imposter_mode && chatGameStatus?.game_completed && resetOfferedHere /* 🛡️ 10-01 safety floor (H2) */ && (
              <div style={{ flexShrink: 0, marginTop: '6px' }}>
                <button type="button" onClick={async () => {
                  if (cloningSpace) return;
                  setCloningSpace(true);
                  try {
                    await chatGameApi.resetGame(gameId, humanPlayerId || createdPlayerIdRef.current); // 🛡️ 10-01 safety floor (H2): the seat's proof
                    localStorage.removeItem(`humanPlayerId_${gameId}`);
                    window.location.href = `/game-step?gameId=${gameId}`;
                  } catch (err) {
                    console.error('[Reset] Failed:', err);
                    alert(`Failed to reset space: ${err.message}`);
                  } finally {
                    setCloningSpace(false);
                  }
                }} disabled={cloningSpace} style={{
                  width: '100%', padding: '10px 14px', borderRadius: '10px',
                  border: '1px solid rgba(255, 255, 255, 0.15)',
                  backgroundColor: cloningSpace ? THEME.textMuted : THEME.accent,
                  color: '#ffffff', cursor: cloningSpace ? 'not-allowed' : 'pointer',
                  opacity: cloningSpace ? 0.7 : 1, fontWeight: 700, fontSize: '0.9em',
                  textShadow: '0 1px 3px rgba(0,0,0,0.5)', boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                }}>
                  {cloningSpace ? 'Resetting...' : 'Reset'}
                </button>
              </div>
            )}

          </div>

          {/* Right sidebar resize handle */}
          {/* (🏡📐 wave 3, ruling 32: not on a place page: the lane | rail split is a hairline) */}
          {isThreeColumnLayout && (!canToggleSidebars || ghostPanelOpen) && !portedPlaceOn && (
            <div
              onMouseDown={(e) => {
                e.preventDefault();
                const startX = e.clientX;
                const startWidth = rightSidebarWidth;
                const onMouseMove = (moveEvent) => {
                  const delta = startX - moveEvent.clientX;
                  setRightSidebarWidth(Math.max(250, Math.min(startWidth + delta, window.innerWidth * 0.4)));
                };
                const onMouseUp = () => {
                  document.removeEventListener('mousemove', onMouseMove);
                  document.removeEventListener('mouseup', onMouseUp);
                  document.body.style.cursor = '';
                  document.body.style.userSelect = '';
                };
                document.addEventListener('mousemove', onMouseMove);
                document.addEventListener('mouseup', onMouseUp);
                document.body.style.cursor = 'col-resize';
                document.body.style.userSelect = 'none';
              }}
              style={{
                width: '10px',
                cursor: 'col-resize',
                backgroundColor: THEME.accent + '15',
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '4px',
                transition: 'background-color 0.2s',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = THEME.accent + '35'; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = THEME.accent + '15'; }}
            >
              {/* 🎛️ Option A mirror (see the left gutter's note): click hides
                  ghost chat, drag still resizes; reopen = the 👻 GHOST edge
                  tab (ruled 09-15). Plain grip where toggles don't apply. */}
              {canToggleSidebars ? (
                <button
                  onMouseDown={(e) => e.stopPropagation()}
                  onClick={() => setGhostPanelOpen(false)}
                  title="Hide ghost chat"
                  aria-label="Hide the ghost chat panel"
                  style={{
                    width: 18, height: 48, padding: 0, cursor: 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    background: THEME.bgDark, border: `1px solid ${THEME.accent}66`,
                    borderRadius: 9, boxShadow: '0 2px 8px rgba(0,0,0,0.5)',
                  }}
                >
                  <svg width="11" height="11" viewBox="0 0 16 16" fill="none" stroke={THEME.accent} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 3l5 5-5 5" /></svg>
                </button>
              ) : (
                <div style={{
                  width: '4px',
                  height: '40px',
                  borderRadius: '2px',
                  backgroundColor: THEME.accent + '80',
                }} />
              )}
            </div>
          )}

          {/* Right Column / Right Sidebar */}
          <div style={{
            // 📐 narrow desktop 340 → 300 (Nina 08-07): at 768-1100px the rail
            // was taking ~40% of the window and squeezing the tab strip into a
            // horizontal scroll — which is how GO LIVE went missing. 40px back
            // to the centre column fits one more tab before the row overflows.
            // Locals cards, composer and ghost chat all reflow (no fixed-width
            // children in here); the resizable 3-col path is untouched.
            flex: isMobile ? '0 0 auto' : (isThreeColumnLayout ? `0 0 ${rightSidebarWidth}px` : (showGhostChatLeft ? '0 0 370px' : (isNarrowDesktop ? '0 0 300px' : '0 0 420px'))),
            minWidth: isThreeColumnLayout ? 250 : undefined,
            maxWidth: isThreeColumnLayout ? '40%' : undefined,
            // 🎛️ ghost collapsed (Nina 09-15): CSS-hidden, not unmounted — no
            // metered connection lives here (messages ride the page poll), and
            // keeping the mount preserves scroll/refs for the instant reopen.
            // The reopen affordance is the 👻 GHOST tab in the edge stack.
            display: (canToggleSidebars && !ghostPanelOpen) ? 'none' : 'flex',
            flexDirection: 'column',
            gap: isMobile ? '8px' : '12px',
            height: isMobile ? 'auto' : '100%',
            overflowY: isMobile ? 'visible' : 'auto',
            width: isMobile ? '100%' : undefined,
            paddingLeft: isThreeColumnLayout ? '8px' : undefined,
            // 🏡📐 wave 3 (ruling 32): the journal rail inside the ONE card,
            // split from the lane by a hairline (mockup v9)
            ...((portedPlaceOn && !isMobile) ? { borderLeft: '1px solid rgba(255,255,255,0.12)', paddingLeft: 0, gap: 0 } : {}),
          }}>
            {/* 🪟🏮 THE HEARTHSIDE RAIL (Nina 08-28): desktop ported spaces fill
                this otherwise-empty column (every legacy child below is
                !isPortedSpace) with the resident's side of the room — presence,
                the Mind card, the honest turn-clock, the keepsake shelf + draw.
                Desktop only; mobile keeps the drawer tabs. Zero LLM/queries. */}
            {/* 📓 Their Place (W1): behind the switch the column is the
                resident's Notebook journal (owner-only; guests see it closed). */}
            {isPortedSpace && !isMobile && portedMindProps && portedPlaceOn && (
              <PortedJournal
                residentName={portedMindProps.residentName}
                ownerName={chatGameStatus?.owner_display_name || null}
                isOwner={isPortedOwner}
                createdAt={chatGameStatus?.space_created_at || null}
                writing={portedWriting}
                journal={portedJournal}
                whileAway={portedWhileAway}
                cover={chatGameStatus?.home?.key || 'light'}
                pronouns={portedPronouns}
                onPronouns={isPortedOwner && humanPlayerId ? setPortedPronouns : null}
                facts={portedFacts}
                onForget={isPortedOwner && humanPlayerId ? forgetPortedFact : null}
                notes={isPortedOwner ? portedNotes : null}
                onNote={portedLineOwner ? leavePortedNote : null}
                onDraw={() => setShowDrawingCanvas(true)}
                fill="column"
                now={portedPlaceOn ? placeNow : null}
                fit
                openToken={portedJournalToken}
                tasks={portedLineOwner ? portedTasks : null}
                onTask={portedLineOwner ? addPortedTask : null}
                onCancelTask={portedLineOwner ? cancelPortedTask : null}
                onReopenTask={portedLineOwner ? reopenPortedTask : null}
                favorites={chatGameStatus?.favorites || null}
                taskToken={portedTaskToken}
                taskShared={portedTasksShared}
                tz={chatGameStatus?.owner_tz || null}
                memories={portedLineOwner ? portedMemories : null}
                onForgetMemory={portedLineOwner ? portedMemories.forget : null}
              />
            )}
            {isPortedSpace && !isMobile && portedMindProps && !portedPlaceOn && (
              <PortedRail
                {...portedMindProps}
                gameId={gameId}
                lastTurnAgo={portedLastTurnAgo}
                keepsakeActions={keepsakeActions}
                windows={portedWindowActions}
                windowAdds={chatGameStatus?.window_adds || {}}
                canInteract={!!humanPlayerId}
                onWindowPoke={(actionId, kind, extra) => portedBus.poke && portedBus.poke(actionId, kind, extra)}
                whileAway={portedWhileAway}
                onDraw={() => setShowDrawingCanvas(true)}
              />
            )}
            {/* Banner image moved to Home tab header */}

            {/* Users Panel / Players — skip in three-column mode (rendered in
                left sidebar). placesNav phones: the panel moved WHOLESALE to
                the Locals place (Nina 08-02 — dedupe with the v2 header/grid
                is an agreed later pass). compPlacesNav phones (08-03): the
                roster lives on the PLAYERS place; this mount survives ONLY
                as the LOBBY READY VALVE while the ready check is live
                (dedupe-D in reverse — ready-up stays front and center on
                Battle, order:-3 keeps it above the feed). */}
            {!isThreeColumnLayout && !isPortedSpace && activeUsers.length > 0 && !placesNav && (!compPlacesNav || (chatGameStatus?.is_ready_check && !chatGameStatus?.ready_check_data?.done)) && <div ref={playersPanelRef} style={{ ...(showGhostChatLeft ? { flex: 1, minHeight: 0 } : {}), ...(isMobile && (chatGameStatus?.competitive_mode || chatGameStatus?.imposter_mode) ? { order: -3 } : {}) }}><UsersPanel users={activeUsers} allUsers={users} localStatuses={localStatuses} THEME={THEME} gameId={gameId} chatGameStatus={chatGameStatus} humanPlayerId={humanPlayerId} createdPlayerId={createdPlayerIdRef.current} onQuickJoin={handleQuickJoin} isReadyCheck={chatGameStatus?.is_ready_check && !chatGameStatus?.ready_check_data?.done} readyCheckData={readyCheckData} readyCheckSubmitted={readyCheckSubmitted} readyCheckCountdown={readyCheckCountdown} readyCountdownDuration={readyCountdownDuration} readyCheckPhase={readyCheckPhase} autoStartCountdown={autoStartCountdown} gameStarted={gameStarted} hasJoinedGame={hasJoinedGame} showGlitchReveal={showGlitchReveal} diceSpotlightId={diceRollToast?.pid || null} hideJoinCtas={!!chatGameStatus?.noir_theme && !isBotMode} compactMode={!(chatGameStatus?.competitive_mode || chatGameStatus?.imposter_mode)} onReadyUp={async () => {
                    readyCheckDismissedRef.current = true;
                    setReadyCheckSubmitted(true);
                    const myName = chatGameStatus?.players?.find(p => p.id === humanPlayerId)?.name || 'You';
                    setReadyCheckData(prev => ({
                      ...prev,
                      ready_count: (prev?.ready_count || 0) + 1,
                      ready_player_names: [...(prev?.ready_player_names || []), myName]
                    }));
                    try {
                      await chatGameApi.readyUp(gameId, humanPlayerId);
                    } catch (err) {
                      console.error('Ready check response failed:', err);
                    }
                  }} onTogglePlay={(isPlay) => {
                    // Debounce: ignore rapid toggles within 1s
                    const now = Date.now();
                    if (now - togglePlayDebounceRef.current < 1000) return;
                    togglePlayDebounceRef.current = now;
                    // Block switching modes once game has started (both directions)
                    if (gameStarted) return;
                    if (isPlay && chatGameStatus?.eliminated_players?.includes(createdPlayerIdRef.current)) return;
                    // Block toggling to Play while a round is active in competitive/imposter mode
                    if (isPlay && isPlayingRef.current && (chatGameStatus?.competitive_mode || chatGameStatus?.imposter_mode)) return;
                    userSwitchedToPlayRef.current = true;
                    if (isPlay) {
                      const pid = createdPlayerIdRef.current;
                      if (!pid) return;
                      setHumanPlayerId(pid);
                      const url = new URL(window.location);
                      url.searchParams.set('playerId', pid);
                      window.history.replaceState({}, '', url);
                      readyCheckDismissedRef.current = false;
                      setReadyCheckSubmitted(false);
                      // Clear spectator flag on the server so getGameStatus returns is_spectating: false
                      chatGameApi.unspectatePlayer(gameId, pid).catch(err =>
                        console.error('[onTogglePlay] unspectate failed:', err.message)
                      );
                    } else {
                      setHumanPlayerId(null);
                      const url = new URL(window.location);
                      url.searchParams.delete('playerId');
                      window.history.replaceState({}, '', url);
                      readyCheckDismissedRef.current = true;
                      setShowReadyCheck(false);
                      setCurrentRequestId(null);
                    }
                  }} dismissedHints={dismissedHints} onDismissHint={dismissHint} isMobile={isMobile} hintsHidden={hintsHidden} spectateDropdownRef={spectateDropdownRef} quickJoinButtonsRef={quickJoinButtonsRef} readyToggleRef={readyToggleRef} showElementPulse={showElementPulse} showDropdownHint={showDropdownHint} showReadyHint={showReadyHint} onDropdownInteract={() => { dropdownInteractedRef.current = true; setShowDropdownHint(false); }} onReadyInteract={() => { readyInteractedRef.current = true; setShowReadyHint(false); }} /></div>}

            {/* Controls for ChatGame - below standings (hidden visually for chat games, kept in DOM for auto-play) */}
            <div style={{
              backgroundColor: THEME.bgMedium,
              padding: (gameType === 'chat-game' || gameType === 'chat') ? 0 : '12px',
              borderRadius: '8px',
              border: (gameType === 'chat-game' || gameType === 'chat') ? 'none' : `1px solid ${THEME.borderDark}`,
              display: (gameType === 'chat-game' || gameType === 'chat') ? 'none' : 'flex',
              flexDirection: 'column',
              gap: '8px',
              flexShrink: 0
            }}>
              {/* Controls Row: Video List, etc. */}
              <div style={{ display: 'flex', gap: '8px' }}>
                {/* Video Stitcher button - hidden in demo mode */}
                {!isDemoMode && (
                <button
                  onClick={() => setShowVideoList(!showVideoList)}
                  style={{
                    padding: '8px 16px',
                    borderRadius: '4px',
                    border: `2px solid ${THEME.accent}`,
                    backgroundColor: showVideoList ? THEME.accent : 'transparent',
                    color: showVideoList ? (THEME.buttonText || THEME.textDark) : THEME.accent,
                    cursor: 'pointer',
                    fontWeight: 'bold',
                    fontSize: '0.85em',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {showVideoList ? '📋' : '🎬'}
                </button>
                )}

                {/* Hide Play/Pause buttons for chat games but keep them in DOM for auto-play functionality */}
                <button
                  onClick={handlePlay}
                  disabled={isPlaying}
                  style={{
                    flex: (gameType === 'chat-game' || gameType === 'chat') ? 0 : 1,
                    padding: (gameType === 'chat-game' || gameType === 'chat') ? 0 : '8px 12px',
                    width: (gameType === 'chat-game' || gameType === 'chat') ? 0 : 'auto',
                    height: (gameType === 'chat-game' || gameType === 'chat') ? 0 : 'auto',
                    overflow: 'hidden',
                    borderRadius: '4px',
                    border: 'none',
                    backgroundColor: THEME.good,
                    color: THEME.textLight,
                    cursor: isPlaying ? 'not-allowed' : 'pointer',
                    opacity: (gameType === 'chat-game' || gameType === 'chat') ? 0 : (isPlaying ? 0.7 : 1),
                    fontSize: '0.85em'
                  }}
                >
                  Play
                </button>

                <button
                  onClick={handlePause}
                  disabled={!isPlaying}
                  style={{
                    flex: (gameType === 'chat-game' || gameType === 'chat') ? 0 : 1,
                    padding: (gameType === 'chat-game' || gameType === 'chat') ? 0 : '8px 12px',
                    width: (gameType === 'chat-game' || gameType === 'chat') ? 0 : 'auto',
                    height: (gameType === 'chat-game' || gameType === 'chat') ? 0 : 'auto',
                    overflow: 'hidden',
                    borderRadius: '4px',
                    border: 'none',
                    backgroundColor: THEME.evil,
                    color: THEME.textLight,
                    cursor: !isPlaying ? 'not-allowed' : 'pointer',
                    opacity: (gameType === 'chat-game' || gameType === 'chat') ? 0 : (!isPlaying ? 0.7 : 1),
                    fontSize: '0.85em'
                  }}
                >
                  Pause
                </button>
              </div>

              {/* Collapsible Additional Actions - Hidden in demo mode */}
              {!isDemoMode && (
              <details style={{ marginTop: '4px' }}>
                <summary style={{
                  cursor: 'pointer',
                  padding: '8px',
                  backgroundColor: THEME.bgLight,
                  borderRadius: '4px',
                  border: `1px solid ${THEME.borderDark}`,
                  color: THEME.textMuted,
                  fontSize: '0.85em',
                  userSelect: 'none',
                  listStyle: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between'
                }}>
                  <span>More Actions</span>
                  <span style={{ fontSize: '0.7em', opacity: 0.7 }}>▼</span>
                </summary>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '8px' }}>
                  <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  onClick={handleBookmark}
                  style={{
                    flex: 1,
                    padding: '6px 10px',
                    borderRadius: '4px',
                    border: 'none',
                    backgroundColor: THEME.private,
                    color: THEME.textLight,
                    cursor: 'pointer',
                    fontSize: '0.8em'
                  }}
                >
                  Bookmark
                </button>

                <button
                  onClick={handleLoadBookmark}
                  style={{
                    flex: 1,
                    padding: '6px 10px',
                    borderRadius: '4px',
                    border: 'none',
                    backgroundColor: THEME.private,
                    color: THEME.textLight,
                    cursor: 'pointer',
                    fontSize: '0.8em'
                  }}
                >
                  Load
                </button>
              </div>

              <button
                onClick={handleLoadAll}
                disabled={isLoading || isPlaying}
                style={{
                  padding: '6px 10px',
                  borderRadius: '4px',
                  border: 'none',
                  backgroundColor: THEME.textMuted,
                  color: THEME.textLight,
                  cursor: isPlaying ? 'not-allowed' : 'pointer',
                  opacity: isPlaying ? 0.7 : 1,
                  fontSize: '0.8em',
                  width: '100%'
                }}
              >
                Load All
              </button>

              <button
                onClick={handleRollback}
                disabled={isLoading || isPlaying}
                style={{
                  padding: '6px 10px',
                  borderRadius: '4px',
                  border: 'none',
                  backgroundColor: THEME.accent,
                  color: THEME.textDark,
                  cursor: isPlaying ? 'not-allowed' : 'pointer',
                  opacity: isPlaying ? 0.7 : 1,
                  fontSize: '0.8em',
                  width: '100%'
                }}
              >
                Rollback
              </button>

              {/* Hide Slice of Life, Analysis, and Ask Question buttons for chat games */}
              {gameType !== 'chat-game' && gameType !== 'chat' && (
                <>
                  <button
                    onClick={handleSliceOfLife}
                    style={{
                      padding: '6px 10px',
                      borderRadius: '4px',
                      border: 'none',
                      backgroundColor: THEME.secondary,
                      color: THEME.textLight,
                      cursor: 'pointer',
                      fontSize: '0.8em',
                      width: '100%'
                    }}
                  >
                    Slice of Life
                  </button>

                  <button
                    onClick={handleAnalysis}
                    style={{
                      padding: '6px 10px',
                      borderRadius: '4px',
                      border: 'none',
                      backgroundColor: THEME.private,
                      color: THEME.textLight,
                      cursor: 'pointer',
                      fontSize: '0.8em',
                      width: '100%'
                    }}
                  >
                    Analysis
                  </button>

                  <button
                    onClick={handleAskQuestion}
                    style={{
                      padding: '6px 10px',
                      borderRadius: '4px',
                      border: 'none',
                      backgroundColor: THEME.secondary,
                      color: THEME.textLight,
                      cursor: 'pointer',
                      fontSize: '0.8em',
                      width: '100%'
                    }}
                  >
                    Ask Question
                  </button>

                  <button
                    onClick={handleGenerateCustomVideo}
                    style={{
                      padding: '6px 10px',
                      borderRadius: '4px',
                      border: 'none',
                      backgroundColor: '#FF9800',
                      color: 'white',
                      cursor: 'pointer',
                      fontSize: '0.8em',
                      width: '100%',
                      fontWeight: 'bold'
                    }}
                  >
                    🎬 Generate Custom Video
                  </button>
                </>
              )}

              {/* Generate Week Summary button - only for Big Brother */}
              {gameType === 'big-brother' && (
                <button
                  onClick={handleGenerateWeekSummary}
                  style={{
                    padding: '6px 10px',
                    borderRadius: '4px',
                    border: 'none',
                    backgroundColor: '#9C27B0',
                    color: 'white',
                    cursor: 'pointer',
                    fontSize: '0.8em',
                    width: '100%',
                    fontWeight: 'bold',
                    marginTop: '8px'
                  }}
                >
                  📺 Generate Week Summary
                </button>
              )}

              {/* Find Scenes button - only for Big Brother and BOTC */}
              {(gameType === 'big-brother' || gameType === 'blood-on-the-clocktower') && (
                <button
                  onClick={handleFindScenes}
                  style={{
                    padding: '6px 10px',
                    borderRadius: '4px',
                    border: 'none',
                    backgroundColor: '#9c27b0',
                    color: THEME.textLight,
                    cursor: 'pointer',
                    fontSize: '0.8em',
                    width: '100%',
                    marginTop: '4px'
                  }}
                >
                  Find Scenes
                </button>
              )}
                </div>
              </details>
              )}

              {/* Checkboxes row - hidden in demo mode (Show Thoughts is in main area), shown otherwise */}
              {!isDemoMode && (
              <div style={{
                display: 'flex',
                gap: '16px',
                padding: '8px',
                borderTop: `1px solid ${THEME.borderDark}`,
                marginTop: '4px'
              }}>
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  color: THEME.textMuted,
                  cursor: 'pointer',
                  userSelect: 'none',
                  fontSize: '0.85em',
                  flex: 1
                }}>
                  <input
                    type="checkbox"
                    checked={hideGenericActions}
                    onChange={(e) => setHideGenericActions(e.target.checked)}
                    style={{
                      cursor: 'pointer',
                      width: '14px',
                      height: '14px'
                    }}
                  />
                  <span>Hide generic</span>
                </label>

                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  color: THEME.textMuted,
                  cursor: 'pointer',
                  userSelect: 'none',
                  fontSize: '0.85em',
                  flex: 1
                }}>
                  <input
                    type="checkbox"
                    checked={reverseChronological}
                    onChange={(e) => setReverseChronological(e.target.checked)}
                    style={{
                      cursor: 'pointer',
                      width: '14px',
                      height: '14px'
                    }}
                  />
                  <span>Newest first</span>
                </label>
              </div>
              )}
            </div>

            {/* Play Round / Reset button — skip in three-column mode (rendered in left sidebar) */}
            {/* Non-competitive: hide Start Convo / Continue (Poke in input form covers it); keep Reset when game_completed */}
            {/* 🛡️ 10-01 safety floor (review): in a finished room this box holds ONLY the Reset, so where Reset
                is not offered (a seated non-creator) it is not drawn: it was an empty bordered strip. Unchanged
                where Reset IS offered and before the game ends. Gate: utils/resetWiring.behavior.test.js. */}
            {!isThreeColumnLayout && humanPlayerId && !effectiveIsSpectating && !chatGameStatus?.eliminated_players?.includes(humanPlayerId) && (gameType === 'chat-game' || gameType === 'chat') && (chatGameStatus?.competitive_mode || chatGameStatus?.imposter_mode || chatGameStatus?.game_completed) && !(chatGameStatus?.game_completed && !resetOfferedHere) && (
              <div ref={startButtonRef} style={{ flexShrink: 0, marginBottom: '6px', borderTop: `1px solid ${THEME.accent}30`, paddingTop: '8px', order: (showGhostChatLeft && !isMobile) ? -2 : (isMobile ? -2 : undefined), marginTop: (showGhostChatLeft && !isMobile) ? '52px' : '8px' /* 52px = tab bar height (12px padding*2 + 15px font + 16px margin-bottom + borders) */ }}>
                {chatGameStatus?.game_completed ? (resetOfferedHere /* 🛡️ 10-01 safety floor (H2) */ ? (
                  <button
                    type="button"
                    onClick={async () => {
                      if (cloningSpace) return;
                      setCloningSpace(true);
                      try {
                        await chatGameApi.resetGame(gameId, humanPlayerId || createdPlayerIdRef.current); // 🛡️ 10-01 safety floor (H2): the seat's proof
                        localStorage.removeItem(`humanPlayerId_${gameId}`);
                        window.location.href = `/game-step?gameId=${gameId}`;
                      } catch (err) {
                        console.error('[Reset] Failed:', err);
                        alert(`Failed to reset space: ${err.message}`);
                      } finally {
                        setCloningSpace(false);
                      }
                    }}
                    disabled={cloningSpace}
                    style={{
                      width: '100%',
                      padding: '10px 14px',
                      borderRadius: '10px',
                      border: '1px solid rgba(255, 255, 255, 0.15)',
                      backgroundColor: cloningSpace ? THEME.textMuted : THEME.accent,
                      color: '#ffffff',
                      cursor: cloningSpace ? 'not-allowed' : 'pointer',
                      opacity: cloningSpace ? 0.7 : 1,
                      fontWeight: 700,
                      fontSize: '0.9em',
                      textShadow: '0 1px 3px rgba(0,0,0,0.5)',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                    }}
                  >
                    {cloningSpace ? 'Resetting...' : 'Reset'}
                  </button>
                ) : null) : ( // 🛡️ 10-01 safety floor (H2)
                  (() => {
                    const isInReadyCheck = chatGameStatus?.is_ready_check && !chatGameStatus?.ready_check_data?.done;
                    const isCompMode = chatGameStatus?.competitive_mode || chatGameStatus?.imposter_mode;
                    const readyToStart = isInReadyCheck && readyCheckPhase === 'ready_to_start';
                    const blockedByReadyCheck = isInReadyCheck && readyCheckPhase !== 'ready_to_start';
                    // One-boss (Nina 07-25): in whodunnit's lobby the case dock is the
                    // only thing allowed to shout. This button's WAITING state (pure
                    // status — the banner already narrates it) goes calm; it lights
                    // back up the moment it is actionable (Start countdown).
                    const noirCalm = !!chatGameStatus?.noir_theme && isCompMode && blockedByReadyCheck;
                    const isGameStarting = gameStarted && !playRoundMode && !isPlaying;
                    const isDisabled = isLoading || isPlaying || blockedByReadyCheck || isGameStarting;
                    const currentRoundNum = chatGameStatus?.current_competitive_round || chatGameStatus?.round_number || 1;
                    const isFirstRound = currentRoundNum === 1;
                    const isSpectatingDuringReadyCheck = blockedByReadyCheck && !humanPlayerId;
                    const buttonLabel = isPlaying ? (isCompMode ? (isFirstRound ? 'Playing...' : `Playing Round ${currentRoundNum}...`) : 'Playing...')
                      : isGameStarting ? 'Starting...'
                      : readyToStart ? (isCompMode ? `Start game! (${autoStartCountdown}s)` : `Start! (${autoStartCountdown}s)`)
                      : blockedByReadyCheck ? (isCompMode ? 'Waiting for players to join...' : 'Waiting for characters to join...')
                      : chatGameStatus?.imposter_mode
                      ? (() => {
                          const t = chatGameStatus?.imposter_word_rounds_total || 3;
                          const c = Math.min((chatGameStatus?.imposter_word_round || 0) + 1, t);
                          return chatGameStatus?.game_completed ? `Round ${t}/${t} — Game complete!`
                            : c >= t ? `Final Round (${t}/${t})` : `Play Round ${c} of ${t}`;
                        })()
                      : isCompMode
                      ? (isFirstRound ? 'Start game!' : `Play Round ${currentRoundNum}`)
                      : (isFirstRound ? 'Start Convo' : 'Continue');
                    return isSpectatingDuringReadyCheck ? (
                      <div
                        style={{
                          width: '100%',
                          padding: '10px 14px',
                          borderRadius: '10px',
                          border: `1px solid ${THEME.textOnPanelMuted}30`,
                          backgroundColor: `${THEME.textOnPanelMuted}15`,
                          color: THEME.textOnPanelMuted,
                          fontWeight: 600,
                          fontSize: '0.9em',
                          textAlign: 'center',
                          boxSizing: 'border-box',
                        }}
                      >
                        👁️ Spectating
                      </div>
                    ) : (
                      <>
                      <button
                        type="button"
                        onClick={() => {
                          if (readyToStart) {
                            // Backend sends "Round X begins!" announcement via ChatDiscussionRound
                            gameStartedRef.current = true;
                    
                            setGameStarted(true);
                            setReadyCheckPhase('waiting');
                            // Skip ready check, then start — min 800ms so "Starting..." is visible.
                            // [requester-ready-fix] ready the clicking player backend-side
                            const t0 = Date.now();
                            chatGameApi.skipReadyCheck(gameId, null, createdPlayerIdRef.current)
                              .catch(err => console.error('[StartGame] skipReadyCheck failed:', err))
                              .finally(() => {
                                const wait = Math.max(0, 800 - (Date.now() - t0));
                                setTimeout(() => handlePlayRound(), wait);
                              });
                          } else if (!blockedByReadyCheck) {
                            handlePlayRound();
                          }
                        }}
                        disabled={isCompMode ? (isDisabled && !blockedByReadyCheck) : isDisabled}
                        style={{
                          width: '95%',
                          margin: '0 auto',
                          display: 'block',
                          padding: '12px 24px',
                          borderRadius: '10px',
                          border: noirCalm ? '1px solid rgba(217,186,114,0.3)' : (isCompMode && blockedByReadyCheck) ? '1px solid rgba(245,158,11,0.4)' : '1px solid rgba(255, 255, 255, 0.15)',
                          backgroundColor: readyToStart ? '#4ade80' : noirCalm ? 'rgba(23,19,28,0.75)' : (isCompMode && blockedByReadyCheck) ? '#f59e0b' : THEME.good,
                          color: noirCalm ? 'rgba(238,217,164,0.7)' : '#ffffff',
                          cursor: isDisabled ? 'not-allowed' : (isCompMode && blockedByReadyCheck) ? 'default' : 'pointer',
                          opacity: (isDisabled && !(isCompMode && blockedByReadyCheck)) ? 0.5 : 1,
                          fontWeight: 700,
                          fontSize: '1em',
                          letterSpacing: '0.3px',
                          textShadow: noirCalm ? 'none' : '0 1px 3px rgba(0,0,0,0.5)',
                          boxShadow: readyToStart ? '0 2px 12px rgba(74,222,128,0.4)' : noirCalm ? 'none' : (isCompMode && blockedByReadyCheck) ? '0 0 12px rgba(245,158,11,0.6), 0 0 28px rgba(245,158,11,0.3)' : (!isDisabled && !isPlaying) ? `0 0 12px ${THEME.good}80, 0 0 24px ${THEME.good}40` : '0 2px 8px rgba(0,0,0,0.2)',
                          animation: (readyToStart || (!isDisabled && !isPlaying)) ? 'playButtonGlow 2s ease-in-out infinite' : noirCalm ? 'none' : (isCompMode && blockedByReadyCheck) ? 'waitingButtonPulse 2s ease-in-out infinite' : 'none',
                          boxSizing: 'border-box',
                        }}
                      >
                        {noirCalm ? buttonLabel : <BouncingText text={buttonLabel} />}
                      </button>
                      </>
                    );
                  })()
                )}
              </div>
            )}

            {/* Human Input Form — skip in three-column mode (rendered in center column) */}
            {/* Non-competitive: always show input; competitive: gated behind game start */}
            {/* 📱🧭 places nav: the composer is CHAT's — the dock (fixed, z5000)
                would float over the TV/Locals/Arcade layer otherwise */}
            {/* 🧭 Narrow-desktop lane scoping (Nina 08-14): on the tabbed
                desktop layout the composer is ROOM's, mirroring the mobile
                rule above — the SELF-CONTAINED panel lanes get the height
                back. Deliberately an allow-list of panel lanes, NOT
                `!== 'all'`: the livestream lane renders the FULL feed below
                the stream, and hiding the composer there made it
                read-but-can't-reply (verify pass 08-14). Non-comp only; comp
                modes keep their own tab machinery. Known rider: the space
                poll chip + payoff ping mount inside this wrapper, so they
                skip these panel lanes too — same as three-column ('all'-gated
                there) and mobile places, accepted 08-14. */}
            {/* 🧱 (never under the room frame: its one composer is the center mount) */}
            {!isThreeColumnLayout && !shellPreset && !(portedPlaceOn && !isMobile) && !(placesNav && mobileView !== 'chat') && !(compPlacesNav && compMobileView !== 'all') && !(!isMobile && !(chatGameStatus?.competitive_mode || chatGameStatus?.imposter_mode) && activeTab === 'actions' && ['media', 'recap', 'games', 'bot_vision', 'workbench'].includes(chatActionTab)) && humanPlayerId && !(isMobile && investigationTakeover) && (!effectiveIsSpectating || (!chatGameStatus?.competitive_mode && !chatGameStatus?.imposter_mode)) && !chatGameStatus?.eliminated_players?.includes(humanPlayerId) && (!(chatGameStatus?.competitive_mode || chatGameStatus?.imposter_mode) || (!chatGameStatus?.is_ready_check && (isPlaying || gameStarted))) && (
              <div ref={inputFormRef} style={isMobile ? {
                // 📱 Thumb-zone dock (stage 1, 07-17): composer + its contextual
                // action UI pinned to the viewport bottom. Keyboard offset is
                // handled by the visualViewport translate effect; maxHeight keeps
                // tall extras (polls, pickers) scrollable instead of eating the
                // screen.
                // Stage 2: the dock sits ABOVE the bottom nav (~53px); while
                // typing the nav yields and the dock drops to the bottom edge.
                position: 'fixed', left: 0, right: 0, zIndex: 5000,
                bottom: typingFocus ? 0 : `calc(${compPlacesNav ? NAV_H + COMP_TICKER_H : NAV_H}px + env(safe-area-inset-bottom, 0px))`, // ⚔️ +COMP_TICKER_H: the phase ticker rides the nav on comp phones
                background: 'rgba(8,10,14,0.97)', // no blur: dock sits over the per-frame ambient canvas + scrolling feed; at 0.92+ opacity the blur was invisible cost (perf 08-13). NOTE fixed-child containing-block behavior changes if blur returns.
                // 👻 Lane tint: the whole dock goes spectral while the ghost
                // lane owns the composer — the loud mis-send guard.
                borderTop: (ghostLane && placesNav && mobileView === 'chat')
                  ? '1.5px solid rgba(106,90,205,0.85)'
                  : `1px solid ${THEME.accent || '#7C4DFF'}40`,
                boxShadow: (ghostLane && placesNav && mobileView === 'chat')
                  ? '0 -2px 22px rgba(106,90,205,0.35)' : undefined,
                // Flat padding even while typing (08-03): safe-area-inset-bottom
                // stays ~34px with the keyboard up (layout-viewport inset), but
                // the keyboard IS there — the inset just painted a dead dock-
                // colored band between the input and the iOS accessory bar.
                padding: '6px 8px 8px',
                maxHeight: '50dvh', overflowY: 'auto',
              } : { flexShrink: 0, position: 'relative', order: showGhostChatLeft ? -1 : undefined,
                // z 30: same suggestion-overlay lift as the three-column dock —
                // the form's backdrop-filter traps its z:1000 overlays, so the
                // sticky tab strip (z 25) won the paint order (Nina 08-04).
                zIndex: 30,
                // 🎛️ Case-dock chrome (mock-approved 07-25) — same treatment as the
                // three-column mount; mobile branch above keeps the thumb-dock chrome.
                ...(chatGameStatus?.noir_theme && chatGameStatus?.competitive_mode ? {
                  border: '1px solid rgba(217,186,114,0.45)', borderRadius: 14,
                  background: 'rgba(23,19,28,0.55)', padding: '10px 12px', marginTop: 8,
                  boxShadow: '0 10px 30px rgba(0,0,0,0.4)',
                  // 🎯 Live ask → the whole dock lifts to TRUE page center
                  // (Nina 07-26 screenshot: bottom-center still read as the
                  // dock; center stage means middle of the screen). No
                  // overflow clamp on purpose — the command palette / doodle
                  // popover render outside this box and would clip.
                  ...(noirCenterAsk ? {
                    position: 'fixed', left: '50%', top: '50%', transform: 'translate(-50%, -50%)',
                    width: 'min(680px, 92vw)', zIndex: 4900, marginTop: 0,
                    background: 'rgba(23,19,28,0.94)', backdropFilter: 'blur(8px)',
                    boxShadow: '0 18px 60px rgba(0,0,0,0.65)',
                  } : {}),
                } : {}) }}>
                {/* 📚 phone B (Nina 09-30): the folded book rides the top of the dock,
                    "📖 Frankenstein · p. 2 · Open ▾" (one tap = the full-screen page again) */}
                {isMobile && library.phoneShown && library.phoneFolded && (
                  <BookFoldBar book={library.book} onOpen={library.unfoldPhone} />
                )}
                {/* 👻 Ghost ticker RETIRED (Nina 07-31): the full-width bar moved
                    to the right-edge tab mounted beside the ghost sheet — grep
                    GHOST EDGE TAB. The sheet + unread mechanics are unchanged. */}
                {/* 👻 GHOST LANE BAR (08-01, variant A — see the ghostLane state
                    block for the contract). Renders only when the lane is worth
                    offering (ghost traffic exists — a Room-only bar is noise);
                    laneOn swaps the composer below for the ghost input, and the
                    matching read-only feed layer mounts beside the ghost sheet. */}
                {(() => {
                  // Lane tabs only exist while the composer is OPEN (Nina
                  // 08-02) — the folded dock is just the handle.
                  const laneEligible = placesNav && mobileView === 'chat' && !isBotMode
                    && !composerCollapsed && ghostSurfacesOn
                    && spectatingMessages.length > 0 && !!humanPlayerId && !effectiveIsSpectating
                    && !chatGameStatus?.eliminated_players?.includes(humanPlayerId);
                  const laneOn = ghostLane && laneEligible;
                  const laneUnread = (laneOn || !laneEligible) ? 0 : Math.max(0, spectatingMessages.length - ghostSeenCountRef.current);
                  // Room traffic that arrived while you were in the ghost
                  // lane — Main's chip mirrors Ghost's (Nina 08-02 "does
                  // Room have notifications?": it does now, exactly then)
                  const roomActionCount = laneOn ? results.reduce((n, r) => n + (r.actions?.length || 0), 0) : 0;
                  const mainUnread = laneOn ? Math.max(0, roomActionCount - roomSeenCountRef.current) : 0;
                  const laneBtn = (on) => ({
                    flex: 1, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    gap: 6, border: 'none', borderRadius: 8, padding: '6px 0',
                    fontSize: '0.78em', fontWeight: 700, cursor: 'pointer', letterSpacing: '0.3px',
                    background: on ? 'rgba(255,255,255,0.16)' : 'transparent',
                    color: on ? '#fff' : 'rgba(255,255,255,0.72)',
                    boxShadow: on ? '0 1px 4px rgba(0,0,0,0.35)' : 'none',
                  });
                  return (<>
                    {/* ⌄ CHEVRON BAR (Nina 08-02, fold round 2 pick A): the
                        grabber pill's full-width strip, but the mark is a
                        wide shallow DOWN chevron drawn in the line-icon
                        stroke language — "this goes down" needs no teaching
                        label (the pill did; that was the tell). Works from
                        either lane. */}
                    {/* ⌨️ Slim dock while typing (Nina 08-06, mock-approved):
                        the chevron hides with the keyboard up — you never fold
                        mid-sentence, and that row sits exactly where iOS 26's
                        form-assistant capsule floats. Returns on blur. */}
                    {isMobile && !chatGameStatus?.competitive_mode && !chatGameStatus?.imposter_mode && !composerCollapsed && !typingFocus && (
                      <button
                        onClick={() => setComposerCollapsed(true)}
                        aria-label="Collapse chat input"
                        aria-expanded={true}
                        style={{
                          display: 'block', width: '100%', margin: 0,
                          background: 'none', border: 'none', cursor: 'pointer',
                          padding: '3px 0 5px', lineHeight: 0,
                        }}
                      >
                        <svg
                          viewBox="0 0 44 10"
                          width="44"
                          height="10"
                          aria-hidden="true"
                          focusable="false"
                          style={{
                            display: 'block', margin: '0 auto',
                            stroke: 'rgba(255,255,255,0.55)', fill: 'none',
                            strokeWidth: 2.4, strokeLinecap: 'round', strokeLinejoin: 'round',
                          }}
                        >
                          <path d="M4 3 L22 8 L40 3" />
                        </svg>
                      </button>
                    )}
                    {laneEligible && (
                      // Segmented control (Nina 08-02, mock-approved variant B):
                      // emoji-free except the line-icon ghost — Room is plain
                      // text, Ghost keeps its cutesy stroke icon + unread chip.
                      <div role="tablist" aria-label="Message lane" style={{
                        display: 'flex', background: 'rgba(255,255,255,0.08)',
                        borderRadius: 10, padding: 3, marginBottom: 8,
                      }}>
                        <button
                          role="tab"
                          aria-selected={!laneOn}
                          onClick={() => { if (laneOn) { ghostSeenCountRef.current = spectatingMessages.length; setGhostLane(false); } }}
                          style={laneBtn(!laneOn)}
                        >
                          Main
                          {mainUnread > 0 && (
                            <span style={{
                              background: '#ef4444', color: '#fff',
                              fontSize: '0.8em', fontWeight: 800,
                              borderRadius: 999, padding: '1px 6px', lineHeight: 1.4,
                              fontVariantNumeric: 'tabular-nums',
                            }}>{mainUnread > 99 ? '99+' : mainUnread}</span>
                          )}
                        </button>
                        <button
                          role="tab"
                          ref={ghostLaneTabRef}
                          aria-selected={laneOn}
                          onClick={() => {
                            if (!laneOn) {
                              ghostSeenCountRef.current = spectatingMessages.length;
                              roomSeenCountRef.current = results.reduce((n, r) => n + (r.actions?.length || 0), 0);
                              setGhostLane(true);
                            }
                          }}
                          style={{
                            ...laneBtn(laneOn),
                            ...(laneOn ? { background: 'rgba(143,127,240,0.32)', color: '#e6e1fb' } : {}),
                          }}
                        >
                          <LineIcon name="ghost" size={13} />
                          Ghost
                          {laneUnread > 0 && (
                            // Red like the nav badges (Nina 08-02: the muted
                            // chip wasn't enticing anyone into the lane)
                            <span style={{
                              background: '#ef4444', color: '#fff',
                              fontSize: '0.8em', fontWeight: 800,
                              borderRadius: 999, padding: '1px 6px', lineHeight: 1.4,
                              fontVariantNumeric: 'tabular-nums',
                            }}>{laneUnread > 99 ? '99+' : laneUnread}</span>
                          )}
                        </button>
                      </div>
                    )}
                    {/* 🫂 the care cards (check-in · crisis): ONE spot above the lane
                        and fold swaps, so a card on screen never remounts under you */}
                    {renderCareCards()}
                    {laneOn ? (
                      <SpectatingChatInput
                        onSubmit={handleSpectatingSubmit}
                        senderName={users.find(u => u.game_player_id === humanPlayerId)?.name || 'You'}
                        isEliminated={false}
                        theme={THEME}
                        embedded
                        isMobile
                        mentionNames={[...new Set([
                          ...((spectatingMessages || []).map(m => m?.sender)),
                          ...users.map(u => u?.name),
                        ].filter(Boolean))]}
                      />
                    ) : (<>
                {/* ✏️ Doodle popover — slides up out of the composer (desktop);
                    mobile keeps the full-screen modal mounted at page level */}
                {showDrawingCanvas && !isBotMode && !isMobile && doodleAnchored && (
                  <DrawingCanvas
                    key={drawOnSeed?.actionId || 'blank'}
                    anchored
                    onSend={handleSendDrawing}
                    onClose={closeDrawingCanvas}
                    theme={THEME}
                    initialStrokes={drawOnSeed?.strokes || null}
                  />
                )}
                {/* ⌄ COLLAPSIBLE COMPOSER (Nina 07-31) — mobile non-comp only:
                    the dock folds to a one-line handle (tap re-expands) so
                    reading gets the screen back. Comp modes keep the always-on
                    composer: turns are timed there and a folded input would eat
                    seconds. Collapsed also skips the companion strips — all
                    comp/noir-gated no-ops in non-comp anyway. */}
                {isMobile && !chatGameStatus?.competitive_mode && !chatGameStatus?.imposter_mode && composerCollapsed ? (<>
                  {/* A live poll stays visible over a folded dock — with folded
                      now the DEFAULT, skipping it would hide every poll from
                      every fresh joiner. It self-shrinks to a chip after the
                      vote, so the cost is transient. */}
                  {renderSpacePoll()}
                  {/* Payoff ping rides over the folded dock too — the fold is
                      the DEFAULT resting state, exactly when payoffs land. */}
                  {renderPayoffPing()}
                  <button
                    onClick={() => {
                      setComposerCollapsed(false);
                      if (composerGlow) {
                        setComposerGlow(false);
                        try { localStorage.setItem('composerHandleGlowSeen', '1'); } catch (e) { /* private mode */ }
                      }
                    }}
                    aria-label="Expand chat input"
                    aria-expanded={false}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 8, width: '100%',
                      position: 'relative',
                      background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.26)',
                      borderRadius: 10, padding: '9px 12px', cursor: 'pointer',
                      color: 'rgba(255,255,255,0.8)', fontSize: '0.85em', textAlign: 'left',
                    }}
                  >
                    {composerGlow && (<>
                      <style>{`@keyframes composerHandleGlow { 0%, 100% { opacity: 0.2; } 50% { opacity: 0.85; } }
@media (prefers-reduced-motion: reduce) { .composer-glow-overlay { animation: none !important; opacity: 0.3 !important; } }`}</style>
                      <span
                        className="composer-glow-overlay"
                        aria-hidden="true"
                        style={{
                          // Outer pulse ONLY — the inset ring read as a solid
                          // banner on bright accents (Nina's 08-02 screenshot)
                          position: 'absolute', inset: -1, borderRadius: 10, pointerEvents: 'none',
                          boxShadow: `0 0 14px 3px ${THEME.accent || '#7C4DFF'}4d`,
                          opacity: 0.2, animation: 'composerHandleGlow 2.4s ease-in-out 5',
                        }}
                      />
                    </>)}
                    <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      Say something…
                    </span>
                    <span style={{ fontWeight: 800, color: 'rgba(255,255,255,0.7)' }}>▴</span>
                  </button>
                </>) : (<>
                {/* Docket mode (Nina rev 3): the form's manila tab must top the card, so
                    the companion strips move BELOW the composer. Classic order otherwise. */}
                {!noirWriteDocket && (<>
                  {renderActionDock()}
                  {renderPoisonStrip()}
                  {renderHospitalNotice()}
                  {renderFateDie()}
                  {renderPowerStatus()}
                  {renderCritPicker()}
                  {renderWordBanPicker()}
                  {renderSpacePoll()}
                  {renderPayoffPing()}
                </>)}
                {/* 🚪📱 Room toast, mobile twin (09-16, Nina: full mobile
                    lane) — same content as the desktop block above the other
                    mount (grep roomNotice), rendered IN FLOW at the top of
                    the dock instead of absolutely (the dock's wrapper isn't
                    reliably positioned; in-flow can never float into the
                    feed). KEEP IN STEP with the desktop twin. */}
                {roomCameraOn && !isBotMode && (() => {
                  const invite = roomInvite; // ONE resolver for both mounts (grep roomInvite)
                  if (roomPickerOpen || (!roomNotice && !invite && !library.join)) return null; // the pictures own the slot while open (the event room glows there) · 📚 + the library's join line
                  const dismiss = () => setDismissedRoomInvites((s) => { const n = new Set(s); n.add(invite.id); return n; });
                  const joinBtn = {
                    fontSize: 11.5, padding: '3px 10px', borderRadius: 999, cursor: 'pointer',
                    border: '1px solid #8e7ac4', background: '#8e7ac4', color: '#1a1426', fontWeight: 700,
                  };
                  return (
                    <div style={{
                      marginBottom: 8,
                      background: 'rgba(20,14,9,0.92)', border: '1px solid rgba(122,96,60,0.55)',
                      borderRadius: 10, padding: '8px 12px',
                      display: 'flex', flexDirection: 'column', gap: 6,
                    }}>
                      {roomNotice && (
                        <div style={{ fontSize: 12.5, color: '#d9cdbb', whiteSpace: 'pre-line', lineHeight: 1.5 }}>
                          {roomNotice}
                        </div>
                      )}
                      {invite && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', fontSize: 12, color: '#cfc4e8' }}>
                          <span>{invite.icon} {invite.phrase ? `${invite.phrase} ` : ''}The residents are heading to {invite.label}.</span>
                          <button type="button" style={joinBtn}
                            onClick={() => {
                              openPictureIn(invite.room); // 🌕 09-26: join = the room's picture, like every Go
                              dismiss();
                            }}>
                            join them
                          </button>
                          <button type="button" onClick={dismiss} style={{ ...joinBtn, background: 'rgba(14,10,7,0.66)', border: '1px solid rgba(122,96,60,0.55)', color: '#d9cdbb', fontWeight: 400 }}>
                            later
                          </button>
                        </div>
                      )}
                      {/* 📚 "📖 Nina opened Frankenstein in the library · Join ✕" (once per open, per device) */}
                      {library.join && <LibraryJoinLine join={library.join} />}
                    </div>
                  );
                })()}
                {renderRoomPicker('dock')}
                {renderCalendar('dock')}
                {renderRoomPlate()}
                <HumanInputForm
                  crowdAliases={crowdAliases}
                  prefillNonce={composerSeedNonce}
                  // 🚪📱 /room chips on the phone dock too (09-16, Nina's
                  // "full lane" ruling — un-defers the 09-15 phone deferral).
                  // Same payload gate as the desktop mount.
                  houseRooms={houseRoomsProp()}
                  houseCalendar={houseCalendarProp()}
                  resting={restingProp()}
                  eventStrip={TOP_CLOCK ? null : eventStripProp()}
                  thinkingStatus={composerThinkingInline() ? currentlyTyping : null}
                  thinkingCandle={roomCameraOn}
                  onPaletteOpen={handleTourPaletteOpen}
                  onOpenDoodleTurn={chatGameStatus?.doodle_bug ? openDoodleTurn : null}
                  isSpaceMod={!!humanPlayerId && (humanPlayerId === chatGameStatus?.human_player_identifier || humanPlayerId === createdPlayerIdRef.current)}
                  portedRoom={isPortedSpace}
                  roomPurpose={isPortedSpace ? null : (chatGameStatus?.purpose || null)} // 🧭 10-02 a study or research room keeps only the verbs that fit it (a ported room keeps its arc's verbs)
                  pictures={isPortedSpace ? (chatGameStatus?.pictures || null) : null}
                  placeRoom={portedPlaceOn}
                placeOwner={portedPlaceOn && isPortedOwner}
                  placeName={portedPlaceOn ? (portedMindProps?.residentName || null) : null}
                  onComposerActivity={(portedPlaceOn && !isMobile) ? setPlaceComposerActive : null}
                  playerId={humanPlayerId}
                  playerName={users.find(u => u.game_player_id === humanPlayerId)?.name || 'You'}
                  availableActions={availableActions}
                  tokensLeft={tokensLeft}
                  onSubmit={handleHumanSubmit}
                  onSubmitInterjection={(chatGameStatus?.competitive_mode || chatGameStatus?.imposter_mode) ? handleInterjectionSubmit : handleInterjectionAndPlay}
                  // 📺 Teach @watchparty only while THIS user has the theater
                  // open (Nina 08-27, was space-level): an un-seated sender
                  // can't even see their own quip land. watchParty = WS-fresh
                  // party state; inTheater = this user's couch seat.
                  watchPartyOn={!!watchParty && inTheater}
                  disabled={isLoading || !!chatGameStatus?.you_are_hospitalized}
                  locked={rollLocked}
                  onRollDie={rollDie}
                  diceRolling={diceRolling}
                  diceLabel={chatGameStatus?.dice?.label}
                  prefillText={composerSeed || ((!isBotMode && (chatGameStatus?.current_competitive_round || 1) === 1 && (chatGameStatus?.twist?.story_seed || chatGameStatus?.intro_opening_line)) ? `${chatGameStatus.twist?.story_seed || chatGameStatus.intro_opening_line} ` : null)}
                  bannedWords={visibleBannedWords()}
                  sentenceCap={chatGameStatus?.sentence_cap || null}
                  sideQuest={(!isBotMode && chatGameStatus?.human_challenge) ? {
                    ...chatGameStatus.human_challenge,
                    done: sideQuestClaimed === chatGameStatus.human_challenge.text
                  } : null}
                  replyCredit={!!(chatGameStatus?.competitive_mode && !chatGameStatus?.imposter_mode
                    && (chatGameStatus?.competitive_round || 1) >= 2 && tokensLeft === 1)}
                  theme={THEME}
                  alwaysAvailable={true}
                  isYourTurn={isHumanTurn && !!currentRequestId}
                  turnDeadline={turnDeadline}
                  voteFold={noirVoteAskOpen()}
                  suppressIdle={!!(chatGameStatus?.noir_theme && chatGameStatus?.competitive_mode && !noirWriteDocket && chatGameStatus?.dice?.your_roll && diceRevealedRound === diceRoundKey)}
                  interjectionsRemaining={interjectionsRemaining}
                  imposterMode={chatGameStatus?.imposter_mode || false}
                  competitiveMode={chatGameStatus?.competitive_mode || false}
                  pendingTurnType={chatGameStatus?.pending_interaction?.type || null}
                  noirDocket={noirWriteDocket ? { headline: 'The room is listening.', facts: noirDocketFacts() } : null}
                  betReminder={(!isBotMode && chatGameStatus?.your_bet?.status === 'accepted'
                    && chatGameStatus.your_bet.round === (chatGameStatus?.current_competitive_round || 1))
                    ? { word: chatGameStatus.your_bet.word, points: chatGameStatus.your_bet.points } : null}
                  onSubmitVote={handleVoteSubmit}
                  players={users.filter(u => u.game_player_id !== humanPlayerId).map(u => ({ name: u.name, model: u.model, profile_image_url: u.profile_image_url, game_player_id: u.game_player_id, is_human: u.is_human }))}
                  shelfItems={chatGameStatus?.builds || []}
                  onPoke={(!chatGameStatus?.competitive_mode && !chatGameStatus?.imposter_mode && !chatGameStatus?.game_completed) ? () => handlePlayRound() : null}
                  onOpenDrawing={(!isBotMode && !chatGameStatus?.competitive_mode && !chatGameStatus?.imposter_mode && chatGameStatus?.tool_flags?.doodle !== false) ? () => setShowDrawingCanvas(true) : null}
                  onUploadFile={(!isBotMode && !chatGameStatus?.competitive_mode && !chatGameStatus?.imposter_mode) ? handleUploadFile : null}
                  trayMode={placesNav}
                  onOpenShelf={placesNav ? () => { setDossierOpen(false); setShelfOpen(true); } : null}
                  isPoking={isPlaying}
                />
                {noirWriteDocket && (<>
                  {renderPoisonStrip()}
                  {renderHospitalNotice()}
                  {renderPowerStatus()}
                  {renderCritPicker()}
                  {renderWordBanPicker()}
                  {renderSpacePoll()}
                </>)}
                </>)}
                    </>)}
                  </>);
                })()}
              </div>
            )}

            {/* 📱 Spectator thumb-zone dock (parity, 07-17): spectators and the
                eliminated get their ghost input pinned + a ticker mirroring the
                LOCALS' latest line (their ghost feed is already their main view
                up top — the polarity of the player dock). Shares inputFormRef
                with the player dock — only one of the two ever renders — so the
                keyboard translate effect covers both. */}
            {isMobile && (gameType === 'chat-game' || gameType === 'chat') && chatGameStatus && !shellPreset /* 🧱 no side chat, one composer */ && !(compPlacesNav && compMobileView !== 'all') && (!humanPlayerId || effectiveIsSpectating || chatGameStatus?.eliminated_players?.includes(humanPlayerId)) && (() => {
              let lastMain = null;
              for (let i = results.length - 1; i >= 0 && !lastMain; i--) {
                const acts = results[i]?.actions || [];
                for (let j = acts.length - 1; j >= 0; j--) {
                  if (acts[j]?.message) { lastMain = acts[j]; break; }
                }
              }
              return (
                <div ref={inputFormRef} style={{
                  position: 'fixed', left: 0, right: 0, zIndex: 5000,
                  bottom: typingFocus ? 0 : `calc(${compPlacesNav ? NAV_H + COMP_TICKER_H : NAV_H}px + env(safe-area-inset-bottom, 0px))`, // ⚔️ +COMP_TICKER_H: the phase ticker rides the nav on comp phones
                  background: 'rgba(8,10,14,0.97)', // no blur (perf 08-13, see dock note above)
                  borderTop: `1px solid ${THEME.accent || '#7C4DFF'}40`,
                  // Flat padding even while typing (08-03): safe-area-inset-bottom
                // stays ~34px with the keyboard up (layout-viewport inset), but
                // the keyboard IS there — the inset just painted a dead dock-
                // colored band between the input and the iOS accessory bar.
                padding: '6px 8px 8px',
                  maxHeight: '50dvh', overflowY: 'auto',
                }}>
                  {/* 🎛️ Case lobby dock (mobile inline variant) — join CTAs stack
                      ABOVE the ghost ticker/input pre-join; ghost chat stays usable. */}
                  {renderCaseLobbyDock(true)}
                  {lastMain && (
                    <button
                      onClick={() => { try { document.querySelector('[data-chat-scroll="main"]')?.scrollIntoView({ behavior: 'smooth', block: 'start' }); } catch (e) { /* anchor gone */ } }}
                      aria-label="Jump to the show"
                      style={{
                        display: 'flex', alignItems: 'center', gap: 7, width: '100%',
                        background: 'none', border: 'none', padding: '4px 6px 8px',
                        color: THEME.textMuted || '#9fb0a6', fontSize: '0.8em', cursor: 'pointer', textAlign: 'left',
                      }}
                    >
                      <span>🎭</span>
                      <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {lastMain.player_name ? `${lastMain.player_name}: ` : ''}{lastMain.message}
                      </span>
                    </button>
                  )}
                  {/* Nina 07-30: the dock was rendering the NON-embedded variant,
                      so SpectatingChatInput drew its whole standalone panel —
                      dashed border, 12px padding and a two-line "only visible to
                      eliminated players & spectators" header — inside a dock that
                      already has its own background, border and padding. ~80px of
                      duplicated chrome on a 470px screen. `embedded` drops the
                      wrapper; the one-line label below keeps the context. */}
                  {/* 📣 Live room: a viewer with NO seat (join failed / still
                      joining) has no main composer — this is their only voice,
                      so it survives the ghost-surface gate. Their line lands in
                      the feed with the crowd, hence the label. */}
                  {(ghostSurfacesOn || crowdInline) && (<>
                  <div style={{
                    padding: '0 6px 4px', fontSize: '0.68em', fontWeight: 700,
                    letterSpacing: '0.06em', color: THEME.spectating || '#78909C',
                  }}>
                    {crowdInline ? 'Everyone in the room sees this' : '👻 Only ghosts and spectators see this'}
                  </div>
                  <SpectatingChatInput
                    onSubmit={handleSpectatingSubmit}
                    senderName={humanPlayerId
                      ? (users.find(u => u.game_player_id === humanPlayerId)?.name || 'Ghost')
                      : (users.find(u => u.is_human && u.name && u.name.startsWith('human_'))?.name || 'Spectator')
                    }
                    isEliminated={!!(humanPlayerId && chatGameStatus?.eliminated_players?.includes(humanPlayerId))}
                    theme={THEME}
                    embedded
                    isMobile
                    mentionNames={[...new Set([
                      ...((spectatingMessages || []).map(m => m?.sender)),
                      ...users.map(u => u?.name),
                    ].filter(Boolean))]}
                  />
                  </>)}
                </div>
              );
            })()}

            {/* Spectator Chat Panel + controls for eliminated/spectators (who don't have HumanInputForm) */}
            {(gameType === 'chat-game' || gameType === 'chat') && (!humanPlayerId || effectiveIsSpectating || chatGameStatus?.eliminated_players?.includes(humanPlayerId)) && (
              <div ref={showGhostChatLeft ? null : ghostChatRef} style={{ flex: showGhostChatLeft ? '0 0 auto' : 1, minHeight: 0, display: 'flex', flexDirection: 'column', gap: '8px', order: (showGhostChatLeft && !isMobile) ? -2 : (isMobile ? -1 : undefined), marginTop: (showGhostChatLeft && !isMobile) ? '52px' : undefined /* 52px = tab bar height (12px padding*2 + 15px font + 16px margin-bottom + borders) */ }}>
                {/* Play Round button for spectators/eliminated — skip in three-column (rendered in left sidebar) */}
                {/* 🏡 W2d: a place page's 👋 (the composer) covers it for anyone with a seat */}
                {/* 🏡📐 wave 3 (ruling 32): nor on a phone's place page: the send button wakes him */}
                {!isThreeColumnLayout && !(portedPlaceOn && humanPlayerId) && (!chatGameStatus?.game_completed ? (
                  (() => {
                    const isInReadyCheck2 = chatGameStatus?.is_ready_check && !chatGameStatus?.ready_check_data?.done;
                    const isCompMode2 = chatGameStatus?.competitive_mode || chatGameStatus?.imposter_mode;
                    const blockedByReadyCheck2 = isInReadyCheck2 && (isCompMode2 && !readyCheckSubmitted);
                    // One-boss (Nina 07-25): same calm-waiting rule as the sidebar
                    // start button — whodunnit's lobby lets only the case dock shout.
                    const noirCalm2 = !!chatGameStatus?.noir_theme && isCompMode2 && blockedByReadyCheck2;
                    const powerOff2 = aiAsleep && !isCompMode2; // 🌙 W2b: asleep at $0, nothing to play
                    const isDisabled2 = powerOff2 || isLoading || isPlaying || blockedByReadyCheck2;
                    // ⚖️ 07-30 (approved mock): mid-case the disabled "Playing Round N..."
                    // pill only duplicates the PLAYERS header — noir drops it. It returns
                    // the moment it's actionable again (round end, ready check, lobby).
                    if (chatGameStatus?.noir_theme && isCompMode2 && isPlaying && !blockedByReadyCheck2) return <></>;
                    return <>
                    <button
                    ref={startButtonRef}
                    type="button"
                    onClick={() => { if (blockedByReadyCheck2) return; handlePlayRound(); }}
                    disabled={isCompMode2 ? (isDisabled2 && !blockedByReadyCheck2) : isDisabled2}
                    style={{
                      width: '95%',
                      margin: '0 auto',
                      display: 'block',
                      padding: '12px 24px',
                      borderRadius: '10px',
                      border: noirCalm2 ? '1px solid rgba(217,186,114,0.3)' : (isCompMode2 && blockedByReadyCheck2) ? '1px solid rgba(245,158,11,0.4)' : '1px solid rgba(255, 255, 255, 0.15)',
                      backgroundColor: noirCalm2 ? 'rgba(23,19,28,0.75)' : (isCompMode2 && blockedByReadyCheck2) ? '#f59e0b' : THEME.good,
                      color: noirCalm2 ? 'rgba(238,217,164,0.7)' : '#ffffff',
                      cursor: isDisabled2 ? 'not-allowed' : (isCompMode2 && blockedByReadyCheck2) ? 'default' : 'pointer',
                      opacity: (isDisabled2 && !(isCompMode2 && blockedByReadyCheck2)) ? 0.5 : 1,
                      fontWeight: 700,
                      fontSize: '1em',
                      letterSpacing: '0.3px',
                      textShadow: noirCalm2 ? 'none' : '0 1px 3px rgba(0,0,0,0.5)',
                      boxShadow: noirCalm2 ? 'none' : (isCompMode2 && blockedByReadyCheck2) ? '0 0 12px rgba(245,158,11,0.6), 0 0 28px rgba(245,158,11,0.3)' : !isDisabled2 ? `0 0 12px ${THEME.good}80, 0 0 24px ${THEME.good}40` : '0 2px 8px rgba(0,0,0,0.2)',
                      animation: noirCalm2 ? 'none' : (isCompMode2 && blockedByReadyCheck2) ? 'waitingButtonPulse 2s ease-in-out infinite' : !isDisabled2 ? 'playButtonGlow 2s ease-in-out infinite' : 'none',
                      flexShrink: 0,
                      boxSizing: 'border-box',
                      ...(powerOff2 ? ASLEEP_PLAY : null),
                    }}
                  >
                    {powerOff2 ? `💤 ${ASLEEP_COPY.play}` : noirCalm2 ? 'Waiting for players to join...' : <BouncingText text={isPlaying
                      ? ((chatGameStatus?.competitive_mode || chatGameStatus?.imposter_mode)
                        ? `Playing Round ${chatGameStatus?.current_competitive_round || chatGameStatus?.round_number || 1}...`
                        : 'Playing...')
                      : blockedByReadyCheck2 ? 'Waiting for players to join...'
                      : (chatGameStatus?.is_ready_check && !chatGameStatus?.ready_check_data?.done) ? (isCompMode2 ? 'Waiting for players to join...' : 'Waiting for characters to join...')
                      : chatGameStatus?.imposter_mode
                      ? (() => {
                          const t = chatGameStatus?.imposter_word_rounds_total || 3;
                          const c = Math.min((chatGameStatus?.imposter_word_round || 0) + 1, t);
                          return chatGameStatus?.game_completed ? `Round ${t}/${t} — Game complete!`
                            : c >= t ? `Final Round (${t}/${t})` : `Play Round ${c} of ${t}`;
                        })()
                      : (chatGameStatus?.competitive_mode)
                      ? `Play Round ${chatGameStatus?.current_competitive_round || chatGameStatus?.round_number || 1}`
                      : ((chatGameStatus?.current_competitive_round || chatGameStatus?.round_number || 1) === 1 ? 'Start Convo' : 'Continue')} />}
                  </button>
                  </>
                  })()
                ) : (resetOfferedHere /* 🛡️ 10-01 safety floor (H2) */ ? (
                  <button
                    type="button"
                    onClick={async () => {
                      if (cloningSpace) return;
                      setCloningSpace(true);
                      try {
                        await chatGameApi.resetGame(gameId, humanPlayerId || createdPlayerIdRef.current); // 🛡️ 10-01 safety floor (H2): the seat's proof
                        localStorage.removeItem(`humanPlayerId_${gameId}`);
                        window.location.href = `/game-step?gameId=${gameId}`;
                      } catch (err) {
                        console.error('[Reset] Failed:', err);
                        alert(`Failed to reset space: ${err.message}`);
                      } finally {
                        setCloningSpace(false);
                      }
                    }}
                    disabled={cloningSpace}
                    style={{
                      width: '100%',
                      padding: '10px 14px',
                      borderRadius: '10px',
                      border: '1px solid rgba(255, 255, 255, 0.15)',
                      backgroundColor: cloningSpace ? THEME.textMuted : THEME.accent,
                      color: '#ffffff',
                      cursor: cloningSpace ? 'not-allowed' : 'pointer',
                      opacity: cloningSpace ? 0.7 : 1,
                      fontWeight: 700,
                      fontSize: '0.9em',
                      textShadow: '0 1px 3px rgba(0,0,0,0.5)',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                      flexShrink: 0,
                    }}
                  >
                    {cloningSpace ? 'Resetting...' : 'Reset'}
                  </button>
                ) : null)) /* 🛡️ 10-01 safety floor (H2) */}
                {/* AI Moderator — full panel for non-three-column layouts.
                    Phones: moved into the Locals section (host LIVE compare,
                    Nina 07-20 — variant A row or variant B card merge). */}
                {!isThreeColumnLayout && !isMobile && !chatGameStatus?.competitive_mode && !chatGameStatus?.imposter_mode && chatGameStatus?.moderator_character && !isPortedSpace && (
                  <ModeratorPanel
                    moderatorCharacter={chatGameStatus.moderator_character}
                    THEME={THEME}
                    isMobile={isMobile}
                  />
                )}
                {/* AI Moderator — compact header for three-column (non-competitive desktop) */}
                {isThreeColumnLayout && chatGameStatus?.moderator_character && !isPortedSpace && (
                  <ModeratorPanel moderatorCharacter={chatGameStatus.moderator_character} THEME={THEME} isMobile={isMobile} hideDescription />
                )}
                {/* Hidden when ghost chat is in the left sidebar (competitive/imposter desktop) */}
                {!showGhostChatLeft && ghostSurfacesOn && (
                  <SpectatorChatPanel
                    messages={ghostPanelMessages}
                    watchPartyActive={!!watchParty} seatsAsCrowd={!(chatGameStatus?.competitive_mode || chatGameStatus?.imposter_mode)}
                    onSubmit={handleSpectatingSubmit}
                    /* Mobile: input lives in the spectator dock — panel is feed-only */
                    readOnly={isMobile}
                    senderName={humanPlayerId
                      ? (users.find(u => u.game_player_id === humanPlayerId)?.name || 'Ghost')
                      : (users.find(u => u.is_human && u.name && u.name.startsWith('human_'))?.name || 'Spectator')
                    }
                    isEliminated={humanPlayerId && chatGameStatus?.eliminated_players?.includes(humanPlayerId)}
                    theme={THEME}
                    users={users}
                    isMobile={isMobile}
                    startCollapsed={isMobile && tutorialActive}
                    forceExpand={tutorialActive && isCompOrImposter && tutorialStep === tutorialSteps.length - 1}
                    label={(!chatGameStatus?.competitive_mode && !chatGameStatus?.imposter_mode) ? '👻 Ghost Chat' : undefined}
                    seamlessTop={isThreeColumnLayout && !!chatGameStatus?.moderator_character}
                    onLoadOlder={loadOlderSpectatorMessages}
                    isLoadingOlder={isLoadingOlderSpectatorMessages}
                    hasMore={hasMoreSpectatorMessages}
                    onExpandChange={(e) => { spectatorPanelExpandedRef.current = e; }}
                    onScrolledUpChange={(s) => { spectatorPanelScrolledUpRef.current = s; }}
                    isInitialLoading={isInitialSpectatorHistoryLoading}
                    onWatchLive={handleWatchLiveFromChat}
                  />
                )}
              </div>
            )}

            {/* Ghost Chat for non-eliminated human players - can see and post */}
            {/* Hidden when ghost chat is in the left sidebar (competitive/imposter desktop) */}
            {(gameType === 'chat-game' || gameType === 'chat') && humanPlayerId && !effectiveIsSpectating && !chatGameStatus?.eliminated_players?.includes(humanPlayerId) && !showGhostChatLeft && (
              <div ref={ghostChatRef} style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                {/* AI Moderator — full panel for non-three-column layouts.
                    Phones: moved into the Locals section (host LIVE compare,
                    Nina 07-20 — variant A row or variant B card merge). */}
                {!isThreeColumnLayout && !isMobile && !chatGameStatus?.competitive_mode && !chatGameStatus?.imposter_mode && chatGameStatus?.moderator_character && !isPortedSpace && (
                  <ModeratorPanel
                    moderatorCharacter={chatGameStatus.moderator_character}
                    THEME={THEME}
                    isMobile={isMobile}
                  />
                )}
                {/* AI Moderator — compact header for three-column (non-competitive desktop) */}
                {isThreeColumnLayout && chatGameStatus?.moderator_character && !isPortedSpace && (
                  <ModeratorPanel moderatorCharacter={chatGameStatus.moderator_character} THEME={THEME} isMobile={isMobile} hideDescription />
                )}
                {/* On phones the player ghost feed lives in the dock's ticker +
                    bottom sheet instead of inline (stage 1, 07-17) */}
                {!isMobile && ghostSurfacesOn && <SpectatorChatPanel
                  messages={ghostPanelMessages}
                  watchPartyActive={!!watchParty} seatsAsCrowd={!(chatGameStatus?.competitive_mode || chatGameStatus?.imposter_mode)}
                  onSubmit={handleSpectatingSubmit}
                  senderName={users.find(u => u.game_player_id === humanPlayerId)?.name || 'You'}
                  isEliminated={false}
                  theme={THEME}
                  users={users}
                  isMobile={isMobile}
                  startCollapsed={isMobile && tutorialActive}
                  forceExpand={tutorialActive && isCompOrImposter && tutorialStep === tutorialSteps.length - 1}
                  label={(!chatGameStatus?.competitive_mode && !chatGameStatus?.imposter_mode) ? '👻 Ghost Chat' : undefined}
                  seamlessTop={isThreeColumnLayout && !!chatGameStatus?.moderator_character}
                  onLoadOlder={loadOlderSpectatorMessages}
                  isLoadingOlder={isLoadingOlderSpectatorMessages}
                  hasMore={hasMoreSpectatorMessages}
                  onExpandChange={(e) => { spectatorPanelExpandedRef.current = e; }}
                  onScrolledUpChange={(s) => { spectatorPanelScrolledUpRef.current = s; }}
                  isInitialLoading={isInitialSpectatorHistoryLoading}
                  onWatchLive={handleWatchLiveFromChat}
                />}
              </div>
            )}
          </div>
        </div>
      ) : (
        /* Original single-column layout for other game types */
        <>
          {activeUsers.length > 0 && !isPortedSpace && <UsersPanel users={activeUsers} allUsers={users} localStatuses={localStatuses} THEME={THEME} gameId={gameId} chatGameStatus={chatGameStatus} humanPlayerId={humanPlayerId} createdPlayerId={createdPlayerIdRef.current} onQuickJoin={handleQuickJoin} isReadyCheck={chatGameStatus?.is_ready_check && !chatGameStatus?.ready_check_data?.done} readyCheckData={readyCheckData} readyCheckSubmitted={readyCheckSubmitted} readyCheckCountdown={readyCheckCountdown} readyCountdownDuration={readyCountdownDuration} readyCheckPhase={readyCheckPhase} autoStartCountdown={autoStartCountdown} gameStarted={gameStarted} hasJoinedGame={hasJoinedGame} showGlitchReveal={showGlitchReveal} diceSpotlightId={diceRollToast?.pid || null} hideJoinCtas={!!chatGameStatus?.noir_theme && !isBotMode} onReadyUp={async () => {
                    readyCheckDismissedRef.current = true;
                    setReadyCheckSubmitted(true);
                    const myName = chatGameStatus?.players?.find(p => p.id === humanPlayerId)?.name || 'You';
                    setReadyCheckData(prev => ({
                      ...prev,
                      ready_count: (prev?.ready_count || 0) + 1,
                      ready_player_names: [...(prev?.ready_player_names || []), myName]
                    }));
                    try {
                      await chatGameApi.readyUp(gameId, humanPlayerId);
                    } catch (err) {
                      console.error('Ready check response failed:', err);
                    }
                  }} onTogglePlay={(isPlay) => {
                    // Debounce: ignore rapid toggles within 1s
                    const now = Date.now();
                    if (now - togglePlayDebounceRef.current < 1000) return;
                    togglePlayDebounceRef.current = now;
                    // Block switching modes once game has started (both directions)
                    if (gameStarted) return;
                    if (isPlay && chatGameStatus?.eliminated_players?.includes(createdPlayerIdRef.current)) return;
                    // Block toggling to Play while a round is active in competitive/imposter mode
                    if (isPlay && isPlayingRef.current && (chatGameStatus?.competitive_mode || chatGameStatus?.imposter_mode)) return;
                    userSwitchedToPlayRef.current = true;
                    if (isPlay) {
                      const pid = createdPlayerIdRef.current;
                      if (!pid) return;
                      setHumanPlayerId(pid);
                      const url = new URL(window.location);
                      url.searchParams.set('playerId', pid);
                      window.history.replaceState({}, '', url);
                      readyCheckDismissedRef.current = false;
                      setReadyCheckSubmitted(false);
                      // Clear spectator flag on the server so getGameStatus returns is_spectating: false
                      chatGameApi.unspectatePlayer(gameId, pid).catch(err =>
                        console.error('[onTogglePlay] unspectate failed:', err.message)
                      );
                    } else {
                      setHumanPlayerId(null);
                      const url = new URL(window.location);
                      url.searchParams.delete('playerId');
                      window.history.replaceState({}, '', url);
                      readyCheckDismissedRef.current = true;
                      setShowReadyCheck(false);
                      setCurrentRequestId(null);
                    }
                  }} dismissedHints={dismissedHints} onDismissHint={dismissHint} isMobile={isMobile} hintsHidden={hintsHidden} spectateDropdownRef={spectateDropdownRef} quickJoinButtonsRef={quickJoinButtonsRef} readyToggleRef={readyToggleRef} showElementPulse={showElementPulse} showDropdownHint={showDropdownHint} showReadyHint={showReadyHint} onDropdownInteract={() => { dropdownInteractedRef.current = true; setShowDropdownHint(false); }} onReadyInteract={() => { readyInteractedRef.current = true; setShowReadyHint(false); }} />}

          {/* Show Thoughts toggle - demo mode only, positioned outside scroll container */}
          {isDemoMode && (
            <div style={{
              marginBottom: '16px',
              padding: '12px',
              backgroundColor: THEME.bgLight,
              borderRadius: '6px',
              border: `1px solid ${THEME.borderLight}`,
              display: 'flex',
              alignItems: 'center',
              gap: '12px'
            }}>
              <label style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                cursor: 'pointer',
                userSelect: 'none'
              }}>
                <input
                  type="checkbox"
                  checked={showThoughts}
                  onChange={(e) => setShowThoughts(e.target.checked)}
                  style={{
                    width: '18px',
                    height: '18px',
                    cursor: 'pointer',
                    accentColor: THEME.primary
                  }}
                />
                <span style={{
                  fontWeight: 'bold',
                  color: THEME.textDark,
                  fontSize: '0.95em'
                }}>
                  Show Thoughts
                </span>
              </label>
              <span style={{
                fontSize: '0.8em',
                color: THEME.textMuted,
                fontStyle: 'italic'
              }}>
                (reveal AI inner thoughts)
              </span>
            </div>
          )}

          {/* Cycle 5f: data-chat-scroll="main" — companion to the main chat scroll
              container at line ~28755. React mounts one OR the other depending on
              layout (different conditional branches). Both need the attribute so the
              explorer's querySelector('[data-chat-scroll="main"]') matches in either
              layout. No visual / functional effect. */}
          {/* onScroll prop REMOVED 08-03 — document capture listener owns
              scroll now (grep onAnyScroll; same reason as the other mount). */}
          <div
            ref={resultsContainerRef}
            data-chat-scroll="main"
            onDrop={handleContentDrop}
            onDragOver={handleContentDragOver}
            onDragLeave={handleContentDragLeave}
            style={{
              backgroundColor: THEME.bgMedium,
              padding: '20px',
              borderRadius: '8px',
              boxShadow: 'inset 0 0 20px ${THEME.textDark}4d',
              flex: 1,
              minHeight: 0,
              overflowY: 'auto',
              position: 'relative',
              border: dropHighlight ? '2px dashed #7b1fa2' : `1px solid ${THEME.borderDark}`,
              marginBottom: '8px'
            }}
          >
            {/* Loading older messages indicator */}
            {isLoadingOlderChatMessages && (
              <div style={{ textAlign: 'center', padding: '12px', color: THEME.textMuted || '#999' }}>
                <span style={{ fontSize: '0.85em' }}>Loading older messages...</span>
              </div>
            )}
            {!hasMoreChatMessages && results.flatMap(r => r.actions || []).length > 0 && (
              <div style={{ textAlign: 'center', padding: '8px', color: THEME.textMuted || '#666', fontSize: '0.8em', fontStyle: 'italic' }}>
                Beginning of conversation
              </div>
            )}

            {/* Benchmark Charts - show at top if there are analyses */}
            {results.length > 0 && results[0].actions && results[0].actions.length > 0 && results[0].actions[0].game_instance_id && (
              <div style={{
                backgroundColor: THEME.bgMedium,
                borderRadius: '8px',
                marginBottom: '20px',
                border: `1px solid ${THEME.borderDark}`
              }}>
                <button
                  onClick={() => setShowBenchmarkCharts(!showBenchmarkCharts)}
                  style={{
                    width: '100%',
                    padding: '12px 20px',
                    backgroundColor: THEME.bgLight,
                    color: THEME.textBright,
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '16px',
                    fontWeight: 'bold',
                    cursor: 'pointer',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    transition: 'background-color 0.2s'
                  }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = THEME.accentLight}
                  onMouseLeave={(e) => e.target.style.backgroundColor = THEME.bgLight}
                >
                  <span>Model Performance Charts</span>
                  <span style={{ fontSize: '20px' }}>{showBenchmarkCharts ? '▼' : '▶'}</span>
                </button>
                {showBenchmarkCharts && (
                  <div style={{
                    padding: '20px',
                    maxHeight: 'calc(100dvh - 200px)',
                    overflowY: 'auto'
                  }}>
                    <BenchmarkCharts
                      gameInstanceId={results[0].actions[0].game_instance_id}
                      apiBaseUrl={API_BASE_URL}
                      users={users}
                    />
                  </div>
                )}
              </div>
            )}

            {/* Round Filter - COMMENTED OUT - May reuse for right sidebar later
            {!isDemoMode && results.length > 0 && (() => {
              // Get unique round numbers from all actions across all results
              const allActions = results.flatMap(r => r.actions || []);

              // Build mapping of round_id to round_number based on chronological order
              const roundIdToNumber = {};
              const uniqueRoundIds = [...new Set(allActions.map(a => a.game_instance_round_id).filter(id => id))];

              // For each round ID, find the earliest action and use that for sorting
              const roundIdToTimestamp = {};
              uniqueRoundIds.forEach(roundId => {
                const actionsInRound = allActions.filter(a => a.game_instance_round_id === roundId);
                if (actionsInRound.length > 0) {
                  const timestamps = actionsInRound.map(a => new Date(a.created_at).getTime());
                  roundIdToTimestamp[roundId] = Math.min(...timestamps);
                }
              });

              // Sort round IDs by timestamp and assign numbers
              const sortedRoundIds = uniqueRoundIds.sort((a, b) =>
                roundIdToTimestamp[a] - roundIdToTimestamp[b]
              );
              sortedRoundIds.forEach((roundId, index) => {
                roundIdToNumber[roundId] = index + 1;
              });

              // Get unique round numbers
              const roundNumbers = [...new Set(
                allActions
                  .map(a => roundIdToNumber[a.game_instance_round_id])
                  .filter(n => n != null)
              )].sort((a, b) => a - b);

              if (roundsMetadata.length >= 1) {
                return (
                  <div style={{
                    marginBottom: '16px',
                    padding: '12px',
                    backgroundColor: THEME.bgLight,
                    borderRadius: '6px',
                    border: `1px solid ${THEME.borderLight}`,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px'
                  }}>
                    <label style={{
                      fontWeight: 'bold',
                      color: THEME.textDark,
                      fontSize: '0.9em'
                    }}>
                      Filter by Round:
                    </label>
                    <select
                      value={selectedRound}
                      onChange={(e) => handleRoundChange(e.target.value)}
                      style={{
                        padding: '6px 12px',
                        borderRadius: '4px',
                        border: `1px solid ${THEME.borderLight}`,
                        backgroundColor: 'white',
                        color: THEME.textDark,
                        fontSize: '0.9em',
                        cursor: 'pointer',
                        minWidth: '120px'
                      }}
                    >
                      <option value="current">Current Round</option>
                      {roundNumbers.map(num => (
                        <option key={num} value={num}>Round {num}</option>
                      ))}
                    </select>
                  </div>
                );
              }
              return null;
            })()}
            */}

            {results.map((result, index) => {
              // (perf 08-13) same feedDerivation hoist as the desktop branch —
              // this mobile branch has no Step 4/5 tail, so the memo output
              // renders directly.
              const allActionsFlat = feedDerivation.allActionsFlat;
              const actionsToRender = feedDerivation.perResult[index] || [];

              return (
                <div key={index}>
                  <VirtualizedActionList
                    portedInteract={isPortedSpace && !!humanPlayerId}
                    actions={actionsToRender}
                    playerModelMap={playerModelMap}
                    users={users}
                    THEME={THEME}
                    onActionUpdate={updateAction}
                    gameType={gameType}
                    allActions={allActionsFlat}
                    cachedSpaces={cachedSpaces}
                    cachedRoundAnalysisHistory={cachedRoundAnalysisHistory}
                    onReactionSubmit={handleReactionSubmit}
                    humanPlayerId={humanPlayerId}
                    effectivePlayerId={effectivePlayerId}
                    imposterMode={chatGameStatus?.imposter_mode || false}
                    competitiveMode={chatGameStatus?.competitive_mode || chatGameStatus?.imposter_mode || false}
                    humanTokensLeft={tokensLeft}
                    uiTerms={chatGameStatus?.ui_terms || null}
                    noirTheme={!!chatGameStatus?.noir_theme}
                    onOpenCastCard={setChatCastPid}
                    houseNicknames={houseNicknames}
                    localStatuses={localStatuses}
                  />
                </div>
              );
            })}
          </div>
        </>
      )}

      {showQuestionModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: '${THEME.textDark}80',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 3000
        }}>
          <div style={{
            backgroundColor: THEME.textLight,
            padding: '24px',
            borderRadius: '8px',
            width: '500px',
            maxWidth: '90%',
            boxShadow: '0 4px 6px ${THEME.textDark}1a'
          }}>
            <h2 style={{ marginTop: 0, marginBottom: '20px' }}>Ask a Question</h2>
            
            <div style={{ marginBottom: '20px' }}>
              <div style={{ marginBottom: '12px' }}>Question Type:</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <input
                    type="radio"
                    checked={questionType === 'general'}
                    onChange={() => setQuestionType('general')}
                  />
                  General Question
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <input
                    type="radio"
                    checked={questionType === 'player'}
                    onChange={() => setQuestionType('player')}
                  />
                  Ask Specific Player
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <input
                    type="radio"
                    checked={questionType === 'all'}
                    onChange={() => setQuestionType('all')}
                  />
                  Ask All Players
                </label>
              </div>
            </div>

            {questionType === 'player' && (
              <div style={{ marginBottom: '20px' }}>
                <div style={{ marginBottom: '8px' }}>Select Player:</div>
                <select
                  value={selectedPlayer}
                  onChange={(e) => setSelectedPlayer(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '8px',
                    borderRadius: '4px',
                    border: `1px solid ${THEME.borderLight}`
                  }}
                >
                  <option value="">Select a player...</option>
                  {users.map((user) => (
                    <option key={user.name} value={user.name}>
                      {user.name} ({user.model})
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div style={{ marginBottom: '20px' }}>
              <div style={{ marginBottom: '8px' }}>Your Question:</div>
              <textarea
                value={question}
                onChange={(e) => setQuestion(e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px',
                  borderRadius: '4px',
                  border: `1px solid ${THEME.borderLight}`,
                  minHeight: '100px',
                  resize: 'vertical'
                }}
                placeholder="Enter your question here..."
              />
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
              <button
                onClick={() => {
                  setShowQuestionModal(false);
                  setQuestion('');
                  setSelectedPlayer('');
                  setQuestionType('general');
                }}
                style={{
                  padding: '8px 16px',
                  borderRadius: '4px',
                  border: `1px solid ${THEME.borderLight}`,
                  backgroundColor: THEME.textLight,
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleSubmitQuestion}
                disabled={!question.trim() || (questionType === 'player' && !selectedPlayer)}
                style={{
                  padding: '8px 16px',
                  borderRadius: '4px',
                  border: 'none',
                  backgroundColor: THEME.secondary,
                  color: THEME.textLight,
                  cursor: 'pointer',
                  opacity: (!question.trim() || (questionType === 'player' && !selectedPlayer)) ? 0.7 : 1
                }}
              >
                Submit
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 📥📣🐻 09-29 chrome wave: the three sheets mount at the page root
          (fixed, every layout), never for a bot. Export reads as the viewer's
          own seat (proved with its stored secret; a spectator's reader falls
          back to the seat this browser holds a secret for, never anyone
          else's) and never offers ghost chat or AI thoughts in a
          competitive/imposter room (they are secret there). Both sheets are
          always dark: a dark room accent falls back to their green
          (utils/roomChrome.sheetAccent). */}
      {!isBotMode && (gameType === 'chat-game' || gameType === 'chat') && chatGameStatus && (
        <>
          {exportChatOpen && (
            <ExportChatDialog
              open={exportChatOpen}
              onClose={() => setExportChatOpen(false)}
              gameId={chatGameStatus?.game_id || gameInstanceId || gameId}
              roomName={chatGameStatus?.name}
              viewer={exportViewer(humanPlayerId, humanPlayerId ? chatGameApi.getPlayerSecret(humanPlayerId) : null)}
              isSecretGhost={isSecretGhostRoom(chatGameStatus)}
              users={users}
              isMobile={isMobile}
              accent={sheetAccent(THEME.accent)}
            />
          )}
          {/* 🗂️ the room frame's Files: the manila folder (a read of the room's
              shared_content, nothing written); desktop starts under its top bar */}
          {filesOpen && shellPreset && (
            <FilesFolder
              open
              onClose={() => setFilesOpen(false)}
              gameId={chatGameStatus?.game_id || gameInstanceId || gameId}
              viewer={exportViewer(humanPlayerId, humanPlayerId ? chatGameApi.getPlayerSecret(humanPlayerId) : null)}
              roomName={chatGameStatus?.name}
              isMobile={isMobile}
              topOffset={isMobile ? 0 : 62}
              THEME={THEME}
              onAddFile={humanPlayerId ? handleUploadFile : null} // 📎 10-02 add from the folder itself (a seat is the upload's proof)
            />
          )}
          {/* 📎 10-02 room docs (hidden): the docs a just-built room's maker
              picked ride in with the page's own upload (knowledge=1), once the
              page holds the seat. Every other room: nothing kept, nothing drawn. */}
          <RoomDocsUpload gameId={gameId} seat={humanPlayerId} upload={humanPlayerId ? handleUploadFile : null} />
          {promoteOpen && viewerCanPromote && (
            <PromoteSheet
              open={promoteOpen}
              onClose={() => setPromoteOpen(false)}
              gameId={chatGameStatus?.game_id || gameInstanceId || gameId}
              roomName={chatGameStatus?.name}
              tagline={chatGameStatus?.tagline || ''}
              canPromote={viewerCanPromote}
              isMod={viewerIsRoomMod || viewerIsMaker}
              canChipIn={!!spacePowerView()?.support}
              isMobile={isMobile}
              accent={sheetAccent(THEME.accent)}
            />
          )}
          {/* 🐻 10-01 (#10b): the toast (z 100011) WAITS while the couch invite
              holds the top spot or the phone ghost sheet (z 150000, drawn on
              exactly isMobile && ghostSheetOpen && ghostSurfacesOn) covers it.
              A null item stops its clock and never calls onDone, so the queue
              keeps it, and it shows for a full 8s once clear (ChipInToast.test).
              Decided HERE, never in the chipInShown const: partyToast,
              inTheater and the sheet's state are declared after it (a TDZ). */}
          <ChipInToast
            item={((partyToast && !inTheater) || (isMobile && ghostSheetOpen && ghostSurfacesOn)) ? null : chipInShown}
            roomName={chatGameStatus?.name}
            onDone={() => {
              const doneId = chipInShown ? chipInShown.id : null;
              setChipInQueue((q) => ((q.length && (!doneId || q[0].id === doneId)) ? shiftChipIn(q) : q));
            }}
          />
        </>
      )}

      {/* Space Info Modal */}
      {showSpaceInfoModal && chatGameStatus && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            // 📱 z 5000, was 3000 (Nina 08-07): the phone's composer dock and
            // bottom nav sit at 4600 and the ghost sheet at 4900, so this
            // modal rendered UNDERNEATH them — its last section (the actions
            // row with 🎓 Newbie Tutorial) was covered no matter how far you
            // scrolled. It read as "can't scroll to it"; it was really "it's
            // behind the dock". A modal belongs over the chrome it blocks.
            zIndex: 5000,
            padding: '20px'
          }}
          onClick={() => setShowSpaceInfoModal(false)}
        >
          <div
            style={{
              backgroundColor: THEME.bgLight,
              borderRadius: '12px',
              maxWidth: '800px',
              width: '100%',
              maxHeight: '90dvh',
              overflow: 'auto',
              WebkitOverflowScrolling: 'touch',
              // Home-indicator clearance so the last row is tappable, not
              // just visible.
              paddingBottom: 'env(safe-area-inset-bottom, 0px)',
              boxShadow: '0 10px 40px rgba(0,0,0,0.3)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div style={{
              padding: '20px 24px',
              borderBottom: `1px solid ${THEME.borderLight}`,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              backgroundColor: THEME.bgMedium
            }}>
              <h2 style={{
                margin: 0,
                color: THEME.textDark,
                fontSize: '1.5em',
                fontWeight: '600'
              }}>
                Space Details
              </h2>
              <button
                onClick={() => setShowSpaceInfoModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '1.5em',
                  cursor: 'pointer',
                  color: THEME.textMuted,
                  padding: '0 8px'
                }}
              >
                ×
              </button>
            </div>

            {/* Share + Like/Dislike row inside modal */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              padding: '12px 24px',
              borderBottom: `1px solid ${THEME.borderLight}`,
              flexWrap: 'wrap'
            }}>
              {/* 🔗 10-01 viewerCanShare (also the frame's ⋯ → Room details) */}
              {viewerCanShare && <ShareButton gameId={gameInstanceId} gameName={chatGameStatus?.name} theme={THEME} />}
              <button
                onClick={() => handleSpaceVote(true)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '8px 14px',
                  backgroundColor: userSpaceVote === 'like' ? 'rgba(239, 68, 68, 0.2)' : `${THEME.bgMedium}`,
                  color: userSpaceVote === 'like' ? '#f87171' : THEME.textDark,
                  border: `1px solid ${userSpaceVote === 'like' ? '#f8717144' : THEME.borderLight}`,
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '0.9rem',
                  fontWeight: 600,
                  transition: 'all 0.2s ease',
                  transform: voteAnimation.like ? 'scale(1.1)' : 'scale(1)',
                }}
              >
                <span style={{ fontSize: '1.1rem' }}>❤️</span>
                {spaceLikes > 0 && <span>{spaceLikes}</span>}
              </button>
              <button
                onClick={() => handleSpaceVote(false)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '8px 14px',
                  backgroundColor: userSpaceVote === 'dislike' ? 'rgba(107, 114, 128, 0.2)' : `${THEME.bgMedium}`,
                  color: userSpaceVote === 'dislike' ? '#9ca3af' : THEME.textDark,
                  border: `1px solid ${userSpaceVote === 'dislike' ? '#9ca3af44' : THEME.borderLight}`,
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '0.9rem',
                  fontWeight: 600,
                  transition: 'all 0.2s ease',
                  transform: voteAnimation.dislike ? 'scale(1.1)' : 'scale(1)',
                }}
              >
                <span style={{ fontSize: '1.1rem' }}>👎</span>
                {spaceDislikes > 0 && <span>{spaceDislikes}</span>}
              </button>
              {/* 📣📥 09-29 (spec §3.8 · §5.10): this row is the room's menu on
                  phones (Share lives here) and one ⓘ away on desktop: Promote
                  (viewerCanPromote), Export chat for anyone who can
                  read the room. Each closes Details and opens its own sheet. */}
              {viewerCanPromote && !isBotMode && (
                <button
                  type="button"
                  data-room-promote="details"
                  onClick={() => { setShowSpaceInfoModal(false); setPromoteOpen(true); }}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 14px',
                    backgroundColor: THEME.bgMedium, color: THEME.textDark,
                    border: `1px solid ${THEME.borderLight}`, borderRadius: '8px',
                    cursor: 'pointer', fontSize: '0.9rem', fontWeight: 600, fontFamily: 'inherit',
                  }}
                >
                  <span style={{ fontSize: '1.1rem' }} aria-hidden="true">📣</span>
                  Promote
                </button>
              )}
              {!isBotMode && (
                <button
                  type="button"
                  data-room-export="details"
                  onClick={() => { setShowSpaceInfoModal(false); setExportChatOpen(true); }}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 14px',
                    backgroundColor: THEME.bgMedium, color: THEME.textDark,
                    border: `1px solid ${THEME.borderLight}`, borderRadius: '8px',
                    cursor: 'pointer', fontSize: '0.9rem', fontWeight: 600, fontFamily: 'inherit',
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M12 4v11M7 10.5l5 5 5-5M5 20h14" />
                  </svg>
                  Export chat
                </button>
              )}
              {/* 🔐 accounts (09-30): the room's sign-in door (Nina: doors only, no nudge).
                  Shows only while sign-in is on and nobody is signed in on this device. */}
              {!isBotMode && account.status === 'signed_out' && (
                <button
                  type="button"
                  data-room-signin="details"
                  onClick={() => { setShowSpaceInfoModal(false); openSignIn({ source: 'room_details' }); }}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 14px',
                    backgroundColor: THEME.bgMedium, color: THEME.textDark,
                    border: `1px solid ${THEME.borderLight}`, borderRadius: '8px',
                    cursor: 'pointer', fontSize: '0.9rem', fontWeight: 600, fontFamily: 'inherit',
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
                    <circle cx="8" cy="5.6" r="2.7" />
                    <path d="M2.8 14c.8-2.7 2.8-4 5.2-4s4.4 1.3 5.2 4" strokeLinecap="round" />
                  </svg>
                  Sign in to keep your seat
                </button>
              )}
            </div>

            {/* 🎓 Newbie Tutorial — PROMOTED here from the bottom actions row
                (Nina 08-07). This modal is now the answer we give anyone who
                declines the host's ask ("it's under ⓘ Space Details"), so it
                has to be visible the moment the modal opens, not buried past
                a full cast list next to Reset. */}
            <div style={{
              padding: '12px 24px',
              borderBottom: `1px solid ${THEME.borderLight}`,
            }}>
              {/* 🧱 (not under the room frame: the tour walks today's room) */}
              {!shellPreset && <button
                onClick={() => {
                  setShowSpaceInfoModal(false);
                  setShowTutorialToast(false);
                  setTutorialActive(true);
                  setTutorialStep(0);
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  width: '100%',
                  padding: '11px 16px',
                  borderRadius: '10px',
                  border: `1px solid ${THEME.accent}55`,
                  backgroundColor: `${THEME.accent}14`,
                  color: THEME.textDark,
                  fontSize: '0.95em',
                  fontWeight: '600',
                  cursor: 'pointer',
                  textAlign: 'left',
                  transition: 'all 0.2s'
                }}
              >
                <span style={{ fontSize: '1.15em' }}>🎓</span>
                <span>Newbie Tutorial</span>
                <span style={{ marginLeft: 'auto', color: THEME.textMuted, fontSize: '0.82em', fontWeight: 500 }}>
                  Take the tour
                </span>
              </button>}
              {/* 👻 Ported spaces only (Nina 08-17): ghost lane hidden by
                  default in a 1:1 companion space; this row is the reveal.
                  Per-device (localStorage) — a viewing preference, not a
                  space setting, so no backend write and no auth question. */}
              {portedResidentName && (
                <button
                  onClick={togglePortedGhost}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    width: '100%',
                    marginTop: '8px',
                    padding: '11px 16px',
                    borderRadius: '10px',
                    border: `1px solid ${THEME.borderLight}`,
                    backgroundColor: 'transparent',
                    color: THEME.textDark,
                    fontSize: '0.95em',
                    fontWeight: '600',
                    cursor: 'pointer',
                    textAlign: 'left',
                    transition: 'all 0.2s'
                  }}
                >
                  <span style={{ fontSize: '1.15em' }}>👻</span>
                  <span>Ghost lane</span>
                  <span style={{ marginLeft: 'auto', color: portedGhostRevealed ? THEME.textDark : THEME.textMuted, fontSize: '0.82em', fontWeight: 600 }}>
                    {portedGhostRevealed ? 'Shown · tap to hide' : 'Hidden · tap to show'}
                  </span>
                </button>
              )}
              {/* 👋 Goodbye wave (V1 idea 1, ruled 08-28): a tiny farewell
                  ritual. Sends a goodbye_wave poke; the resident's next turn
                  opens on a farewell beat. */}
              {isPortedSpace && humanPlayerId && (
                <button
                  onClick={waveGoodbye}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    width: '100%',
                    marginTop: '8px',
                    padding: '11px 16px',
                    borderRadius: '10px',
                    border: `1px solid ${THEME.borderLight}`,
                    backgroundColor: 'transparent',
                    color: THEME.textDark,
                    fontSize: '0.95em',
                    fontWeight: '600',
                    cursor: 'pointer',
                    textAlign: 'left',
                    transition: 'all 0.2s'
                  }}
                >
                  <span style={{ fontSize: '1.15em' }}>{goodbyeWaved ? '🌙' : '👋'}</span>
                  <span>{goodbyeWaved ? 'night 🌙' : 'Wave goodbye'}</span>
                </button>
              )}
              {/* 🔑 Owner key link (Nina 09-24): the way back in on another
                  device. Owner only (portedKeyLink is null for everyone else),
                  and the link IS the seat, hence the line under it. */}
              {isPortedOwner && portedKeyLink && (
                <div style={{ marginTop: '8px' }}>
                  <button
                    onClick={copyPortedKeyLink}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      width: '100%',
                      padding: '11px 16px',
                      borderRadius: '10px',
                      border: `1px solid ${THEME.borderLight}`,
                      backgroundColor: 'transparent',
                      color: THEME.textDark,
                      fontSize: '0.95em',
                      fontWeight: '600',
                      cursor: 'pointer',
                      textAlign: 'left',
                      transition: 'all 0.2s'
                    }}
                  >
                    <span style={{ fontSize: '1.15em' }}>🔑</span>
                    <span>Copy your key link</span>
                    <span style={{ marginLeft: 'auto', color: keyLinkCopied ? THEME.textDark : THEME.textMuted, fontSize: '0.82em', fontWeight: 600 }}>
                      {keyLinkCopied ? 'Copied ✓' : 'Tap to copy'}
                    </span>
                  </button>
                  <div style={{ color: THEME.textMuted, fontSize: '0.8em', lineHeight: 1.45, padding: '6px 4px 0' }}>
                    opens this room as you on any device. keep it private.
                  </div>
                </div>
              )}
            </div>

            {/* Content */}
            <div style={{ padding: '24px' }}>
              {/* Objective and Scene — competitive/imposter only (incl. secret topic
                  / role). Non-competitive shows both in the band right behind this
                  modal, so here the cast leads instead (user call 07-09). */}
              {(chatGameStatus.competitive_mode || chatGameStatus.imposter_mode) && (chatGameStatus.objective || chatGameStatus.scene) && (
                <div style={{ marginBottom: '24px' }}>
                  {chatGameStatus.objective && (
                    <div style={{ marginBottom: '16px' }}>
                      <div style={{
                        fontSize: '0.85em',
                        fontWeight: '600',
                        color: THEME.accent,
                        textTransform: 'uppercase',
                        letterSpacing: '1px',
                        marginBottom: '8px'
                      }}>
                        🎯 Objective
                      </div>
                      <div style={{
                        fontSize: '1em',
                        color: THEME.textDark,
                        lineHeight: '1.6'
                      }}>
                        {displayObjective}
                      </div>
                    </div>
                  )}
                  {chatGameStatus.scene && (
                    <div>
                      <div style={{
                        fontSize: '0.85em',
                        fontWeight: '600',
                        color: chatGameStatus?.imposter_mode
                          ? (chatGameStatus?.is_imposter ? '#ff6666' : '#ffd700')
                          : (THEME.secondary || THEME.accent),
                        textTransform: 'uppercase',
                        letterSpacing: '1px',
                        marginBottom: '8px'
                      }}>
                        {chatGameStatus?.imposter_mode
                          ? (chatGameStatus?.is_imposter ? '🐛 Your Role' : '🔑 Secret Topic')
                          : '🌍 Scene'}
                      </div>
                      <div style={{
                        fontSize: chatGameStatus?.imposter_mode ? '1.1em' : '1em',
                        color: THEME.textDark,
                        lineHeight: '1.6',
                        fontWeight: chatGameStatus?.imposter_mode ? '700' : 'normal',
                      }}>
                        {chatGameStatus?.imposter_mode && chatGameStatus?.is_imposter
                          ? 'You are the Golden Glitch. Blend in!'
                          : (chatGameStatus?.competitive_mode && !chatGameStatus?.imposter_mode && isGenericScene)
                            ? displayObjective
                            : chatGameStatus.scene}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Personality Traits (Global) */}
              {chatGameStatus.players && (() => {
                // Collect all unique personality traits from all players
                const allTraits = [];
                chatGameStatus.players.forEach(p => {
                  (p.personality_traits || []).forEach(traitItem => {
                    const traitName = typeof traitItem === 'string' ? traitItem : traitItem.trait;
                    const traitPercentage = typeof traitItem === 'object' ? traitItem.percentage : null;

                    // Check if we already have this trait
                    const existing = allTraits.find(t => t.name === traitName);
                    if (!existing) {
                      allTraits.push({ name: traitName, percentage: traitPercentage });
                    }
                  });
                });

                return allTraits.length > 0 && (
                  <div style={{ marginBottom: '24px' }}>
                    <div style={{
                      fontSize: '0.85em',
                      fontWeight: '600',
                      color: THEME.accent,
                      textTransform: 'uppercase',
                      letterSpacing: '1px',
                      marginBottom: '12px'
                    }}>
                      ✨ Personality Traits
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                      {allTraits.map((trait, idx) => (
                        <span
                          key={idx}
                          title={trait.percentage ? `${trait.name} (${trait.percentage}%)` : trait.name}
                          style={{
                            fontSize: '1.2em',
                            padding: '6px 10px',
                            borderRadius: '12px',
                            backgroundColor: `${THEME.secondary || THEME.accent}22`,
                            border: `1px solid ${THEME.secondary || THEME.accent}44`,
                            cursor: 'help',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            transition: 'all 0.2s'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.transform = 'scale(1.05)';
                            e.currentTarget.style.backgroundColor = `${THEME.secondary || THEME.accent}44`;
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.transform = 'scale(1)';
                            e.currentTarget.style.backgroundColor = `${THEME.secondary || THEME.accent}22`;
                          }}
                        >
                          <span style={{ fontSize: '1.3em' }}>{getTraitEmoji(trait.name)}</span>
                          <span style={{ fontSize: '0.7em', color: THEME.textDark }}>
                            {trait.name}
                            {trait.percentage !== null && (
                              <span style={{
                                marginLeft: '4px',
                                fontWeight: 'bold',
                                color: THEME.accent
                              }}>
                                {trait.percentage}%
                              </span>
                            )}
                          </span>
                        </span>
                      ))}
                    </div>
                  </div>
                );
              })()}

              {/* Cast first, Guestbook after — the AI locals (with their
                  autobiographies) lead; human visitors fold into a collapsed
                  Guestbook so they stop burying the cast (user call 07-09).
                  Competitive keeps one combined list: humans there are players,
                  not guests — but AI still sorts first. */}
              {chatGameStatus.players && chatGameStatus.players.length > 0 && (() => {
                const isCompModal = chatGameStatus.competitive_mode || chatGameStatus.imposter_mode;
                const aiPlayers = chatGameStatus.players.filter(p => !p.is_human);
                const humanPlayers = chatGameStatus.players.filter(p => p.is_human);
                const mainPlayers = isCompModal ? [...aiPlayers, ...humanPlayers] : aiPlayers;
                const guestPlayers = isCompModal ? [] : humanPlayers;
                // 📊 The numbers — moved here from the Tea character popup (user call
                // 07-09): factual stats belong with the roster, not the gossip.
                const numbersByPid = {};
                results.flatMap(r => r.actions || []).forEach(a => {
                  if (!['message', 'public_message', 'human_interjection'].includes(a.type) || !a.data?.message) return;
                  const pid = a.game_instance_player_id;
                  if (!pid) return;
                  const st = numbersByPid[pid] || (numbersByPid[pid] = { count: 0, chars: 0, reactions: 0, rounds: new Set() });
                  st.count += 1;
                  st.chars += String(a.data.message).length;
                  st.reactions += Object.values(a.data.reactions || {}).reduce((sum, arr) => sum + (arr?.length || 0), 0);
                  if (a.game_instance_round_id) st.rounds.add(a.game_instance_round_id);
                });
                return (
                <div>
                  <div style={{
                    fontSize: '0.85em',
                    fontWeight: '600',
                    color: THEME.accent,
                    textTransform: 'uppercase',
                    letterSpacing: '1px',
                    marginBottom: '12px'
                  }}>
                    👥 {isCompModal ? 'Players' : 'The Locals'} ({mainPlayers.length})
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    {mainPlayers.map((player) => (
                      <div
                        key={player.id}
                        style={{
                          padding: '16px',
                          borderRadius: '8px',
                          backgroundColor: THEME.bgMedium,
                          border: `1px solid ${THEME.borderLight}`
                        }}
                      >
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '12px',
                          marginBottom: '12px'
                        }}>
                          {player.profile_image_url && (
                            <img
                              src={player.profile_image_url}
                              alt={player.name}
                              style={{
                                width: '50px',
                                height: '50px',
                                borderRadius: '50%',
                                objectFit: 'cover',
                                border: `2px solid ${THEME.accent}`
                              }}
                            />
                          )}
                          <div style={{ flex: 1 }}>
                            <div style={{
                              fontWeight: 'bold',
                              fontSize: '1.1em',
                              color: THEME.textDark,
                              marginBottom: '4px'
                            }}>
                              {player.name}
                              {!player.is_human && player.model && (
                                <span style={{
                                  marginLeft: '6px',
                                  fontSize: '0.85em',
                                  fontWeight: 'normal',
                                  color: THEME.textMuted
                                }}>
                                  ({player.model})
                                </span>
                              )}
                              {player.is_human && (
                                <span style={{
                                  marginLeft: '8px',
                                  fontSize: '0.8em',
                                  padding: '3px 8px',
                                  borderRadius: '4px',
                                  backgroundColor: THEME.accent,
                                  color: THEME.textLight
                                }}>
                                  Human
                                </span>
                              )}
                            </div>
                            {/* Mystery gate: dossier locals stay unknown until the
                                viewer's first reveal (user call 07-10) */}
                            {(() => {
                              const isMystery = !isPortedSpace && !player.is_human && (player.bio_facts || []).length > 0
                                && dossierRevealCount(gameId, player.id) === 0;
                              if (isMystery) {
                                return (
                                  <div style={{ fontSize: '0.9em', color: THEME.textMuted, fontStyle: 'italic', lineHeight: 1.55 }}>
                                    ❓ This local is a mystery to me!
                                  </div>
                                );
                              }
                              return player.character_description ? (
                                <div style={{
                                  fontSize: '0.9em',
                                  color: THEME.textMuted,
                                  fontStyle: 'italic',
                                  lineHeight: 1.55
                                }}>
                                  {player.character_description}
                                </div>
                              ) : null;
                            })()}
                          </div>
                        </div>

                        {(() => {
                          const ns = numbersByPid[player.id] || numbersByPid[player.game_player_id];
                          if (!ns || ns.count === 0) return null;
                          return (
                            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '10px' }}>
                              {[
                                { icon: '💬', text: `${ns.count} message${ns.count !== 1 ? 's' : ''}` },
                                { icon: '✍️', text: `~${Math.round(ns.chars / ns.count)} chars` },
                                ...(ns.reactions > 0 ? [{ icon: '❤️', text: `${ns.reactions} reaction${ns.reactions !== 1 ? 's' : ''}` }] : []),
                                ...(ns.rounds.size > 0 ? [{ icon: '📖', text: `${ns.rounds.size} chapter${ns.rounds.size !== 1 ? 's' : ''}` }] : []),
                              ].map((chip, ci) => (
                                <span key={ci} style={{
                                  fontSize: '0.75em', fontWeight: 600, padding: '4px 10px', borderRadius: '8px',
                                  backgroundColor: `${THEME.accent}12`, color: THEME.textMuted, border: `1px solid ${THEME.accent}20`,
                                }}>{chip.icon} {chip.text}</span>
                              ))}
                            </div>
                          );
                        })()}

                        {player.objective && (
                          <div style={{ marginBottom: '8px' }}>
                            <div style={{
                              fontSize: '0.75em',
                              fontWeight: '600',
                              color: THEME.textMuted,
                              marginBottom: '4px'
                            }}>
                              Objective:
                            </div>
                            <div style={{
                              fontSize: '0.9em',
                              color: THEME.textDark,
                              lineHeight: '1.5'
                            }}>
                              {player.objective}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                  {/* Guestbook — human visitors, collapsed until tapped */}
                  {guestPlayers.length > 0 && (
                    <div style={{ marginTop: '20px' }}>
                      <button
                        onClick={() => setGuestbookOpen(v => !v)}
                        style={{
                          display: 'flex', alignItems: 'center', gap: '8px',
                          width: '100%', textAlign: 'left', cursor: 'pointer',
                          padding: '10px 14px', borderRadius: '8px',
                          backgroundColor: THEME.bgMedium,
                          border: `1px solid ${THEME.borderLight}`,
                          color: THEME.textDark, fontSize: '0.85em', fontWeight: 600,
                          textTransform: 'uppercase', letterSpacing: '1px',
                        }}
                      >
                        <span style={{ fontSize: '1.1em' }}>📖</span>
                        Guestbook ({guestPlayers.length})
                        <span style={{ marginLeft: 'auto', transform: guestbookOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}>▾</span>
                      </button>
                      {guestbookOpen && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginTop: '10px' }}>
                          {guestPlayers.map((player) => (
                            <div key={player.id} style={{
                              padding: '10px 14px', borderRadius: '8px',
                              backgroundColor: THEME.bgMedium,
                              border: `1px solid ${THEME.borderLight}`,
                              display: 'flex', alignItems: 'center', gap: '10px',
                            }}>
                              {player.profile_image_url && (
                                <img src={player.profile_image_url} alt={player.name} style={{
                                  width: '32px', height: '32px', borderRadius: '50%', objectFit: 'cover',
                                  border: `1.5px solid ${THEME.accent}60`,
                                }} />
                              )}
                              <span style={{ fontWeight: 600, fontSize: '0.95em', color: THEME.textDark }}>{player.name}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
                );
              })()}

              {/* Actions: Reset (Tutorial moved to the top of the modal 08-07) */}
              {/* 🛡️ 10-01 safety floor (H2): only where the server could say yes */}
              {resetOfferedHere && (<div style={{
                marginTop: '24px',
                paddingTop: '20px',
                borderTop: `1px solid ${THEME.borderLight}`,
                display: 'flex',
                justifyContent: 'flex-end',
                alignItems: 'center'
              }}>
                <button
                  onClick={async () => {
                    if (cloningSpace) return;
                    setCloningSpace(true);
                    try {
                      await chatGameApi.resetGame(gameId, humanPlayerId || createdPlayerIdRef.current); // 🛡️ 10-01 safety floor (H2): the seat's proof
                      localStorage.removeItem(`humanPlayerId_${gameId}`);
                      window.location.href = `/game-step?gameId=${gameId}`;
                    } catch (err) {
                      console.error('[Reset] Failed:', err);
                      alert(`Failed to reset space: ${err.message}`);
                    } finally {
                      setCloningSpace(false);
                    }
                  }}
                  disabled={cloningSpace}
                  style={{
                    padding: '10px 20px',
                    borderRadius: '8px',
                    border: 'none',
                    backgroundColor: cloningSpace ? THEME.textMuted : THEME.accent,
                    color: THEME.textLight || '#fff',
                    fontSize: '0.95em',
                    fontWeight: '600',
                    cursor: cloningSpace ? 'not-allowed' : 'pointer',
                    opacity: cloningSpace ? 0.7 : 1,
                    transition: 'all 0.2s'
                  }}
                >
                  {cloningSpace ? 'Resetting...' : 'Reset'}
                </button>
              </div>) /* 🛡️ 10-01 safety floor (H2) */}
            </div>
          </div>
        </div>
      )}

      {/* Custom Video Generation Modal */}
      {showCustomVideoModal && (
        <CustomVideoModal
          THEME={THEME}
          API_BASE_URL={API_BASE_URL}
          gameInstanceId={results[0]?.actions?.[0]?.game_instance_id}
          selectedRound={selectedRound}
          results={results}
          onClose={() => setShowCustomVideoModal(false)}
        />
      )}

      {/* Spaces Editor Modal */}
      {showSpacesEditor && gameSpaces && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 3000,
          padding: '20px',
          overflow: 'auto'
        }}>
          <div style={{
            backgroundColor: THEME.textLight,
            padding: '24px',
            borderRadius: '8px',
            width: '800px',
            maxWidth: '95%',
            maxHeight: '90dvh',
            overflow: 'auto',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.3)'
          }}>
            <h2 style={{ marginTop: 0, marginBottom: '20px', color: THEME.textDark }}>
              Edit Game Spaces
            </h2>

            <SpacesEditor
              spaces={gameSpaces}
              onChange={setGameSpaces}
              gameId={gameId}
              apiBaseUrl={API_BASE_URL}
            />

            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', marginTop: '24px', paddingTop: '20px', borderTop: `1px solid ${THEME.borderLight}` }}>
              <button
                onClick={() => handleSaveSpaces(gameSpaces, true)}
                style={{
                  padding: '10px 20px',
                  borderRadius: '4px',
                  border: 'none',
                  backgroundColor: '#FF9800',
                  color: 'white',
                  cursor: 'pointer',
                  fontWeight: 'bold',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
                title="Save spaces and generate AI images for each location"
              >
                <span>🎨</span>
                <span>Generate Images</span>
              </button>
              <div style={{ display: 'flex', gap: '12px' }}>
                <button
                  onClick={() => {
                    setShowSpacesEditor(false);
                    setGameSpaces(null);
                  }}
                  style={{
                    padding: '10px 20px',
                    borderRadius: '4px',
                    border: `1px solid ${THEME.borderLight}`,
                    backgroundColor: THEME.textLight,
                    color: THEME.textDark,
                    cursor: 'pointer',
                    fontWeight: 'bold'
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleSaveSpaces(gameSpaces, false)}
                  style={{
                    padding: '10px 20px',
                    borderRadius: '4px',
                    border: 'none',
                    backgroundColor: '#9C27B0',
                    color: 'white',
                    cursor: 'pointer',
                    fontWeight: 'bold'
                  }}
                >
                  Save Spaces
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Emotion Songs Editor Modal */}
      {showEmotionSongsEditor && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 3000,
          padding: '20px',
          overflow: 'auto'
        }}>
          <div style={{
            backgroundColor: THEME.textLight,
            padding: '24px',
            borderRadius: '8px',
            width: '850px',
            maxWidth: '95%',
            maxHeight: '90dvh',
            overflow: 'auto',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.3)'
          }}>
            <h2 style={{ marginTop: 0, marginBottom: '8px', color: THEME.textDark }}>
              Edit Background Music
            </h2>
            <p style={{ color: THEME.textMuted, marginBottom: '20px', fontSize: '0.9em' }}>
              Configure background music for different emotions and contexts.
              Drag and drop audio files or paste URLs. Use the Suno prompts to generate appropriate music.
            </p>

            {/* Emotion song entries */}
            {[
              {
                key: 'fire',
                label: 'Fire',
                color: '#ff4757',
                emoji: '🔥',
                desc: 'Angry, confrontational, frustrated, passionate',
                sunoPrompt: 'Comedic argument music, someone is BIG MAD but it is a little bit funny. Cartoon steam-coming-out-of-ears energy. Silly tuba and bassoon, overdramatic violin stabs, like a chipmunk throwing a tantrum. Looney Tunes frustrated. No lyrics, instrumental only. 100 BPM, playfully heated.'
              },
              {
                key: 'ocean',
                label: 'Ocean',
                color: '#3498db',
                emoji: '🌊',
                desc: 'Sad, emotional, nervous, anxious, vulnerable',
                sunoPrompt: 'Aww someone has the feels! Gentle sad-puppy-eyes music, touching but with a hint of melodrama. Soft piano, maybe a tiny violin (literally). Sweet and tender like a Pixar sad moment. Not actually depressing, just sweetly emotional. No lyrics, instrumental only. 70 BPM, heartstring tugger.'
              },
              {
                key: 'forest',
                label: 'Forest',
                color: '#00b894',
                emoji: '🌲',
                desc: 'Calm, peaceful, reflective, content',
                sunoPrompt: 'Ahhh nice and chill. Lazy Sunday morning, cat napping in a sunbeam energy. Soft ukulele, gentle bells, maybe a kazoo pretending to be a bird. Cozy and slightly goofy. No lyrics, instrumental only. 75 BPM, warm fuzzy socks vibes.'
              },
              {
                key: 'sun',
                label: 'Sun',
                color: '#f39c12',
                emoji: '☀️',
                desc: 'Excited, happy, surprised, proud, playful',
                sunoPrompt: 'Woo hoo party time! Silly celebration music, kazoos and slide whistles welcome. Game show winner energy but make it goofy. Bright and bouncy, like winning a stuffed animal at the fair. No lyrics, instrumental only. 120 BPM, confetti and giggles.'
              },
              {
                key: 'strategic',
                label: 'Strategic',
                color: '#17a2b8',
                emoji: '♟️',
                desc: 'Strategic gameplay, calculating moves, tactical discussions',
                sunoPrompt: 'Hmm yes very clever! Thinking cap music, stroking an imaginary beard. Quirky plucky strings, playful xylophone, like a cartoon mouse outsmarting a cat. Smart but not too serious, more "aha!" than "checkmate". No lyrics, instrumental only. 90 BPM, lighthearted scheming.'
              },
              {
                key: 'shadow',
                label: 'Shadow',
                color: '#8e44ad',
                emoji: '🌑',
                desc: 'Scheming, plotting, manipulative, devious',
                sunoPrompt: 'Tip-toe tip-toe! Cartoon sneaking music, exaggerated creeping around. Playful pizzicato, silly low clarinet, Scooby-Doo villain energy. Rubbing hands together gleefully. Mischief but make it adorable. No lyrics, instrumental only. 85 BPM, comedic villainy.'
              },
              {
                key: 'neutral',
                label: 'Neutral',
                color: '#95a5a6',
                emoji: '⚪',
                desc: 'Default background, no strong emotion',
                sunoPrompt: 'Just vibing, nothing special happening. Pleasant elevator music but actually good. Soft marimba, gentle whistling, la-dee-da energy. Background music for chatting about the weather. No lyrics, instrumental only. 90 BPM, pleasantly forgettable.'
              },
              {
                key: 'host',
                label: 'Host',
                color: '#e74c3c',
                emoji: '🎤',
                desc: 'Host announcements, ceremonies, game explanations',
                sunoPrompt: 'And now... THE MOMENT YOU HAVE BEEN WAITING FOR! Cheesy game show fanfare, over-the-top dramatic but winking at the camera. Silly brass stabs, playful drum rolls, local car dealership commercial energy. No lyrics, instrumental only. 110 BPM, showbiz cheese.'
              },
              {
                key: 'introspective',
                label: 'Introspective',
                color: '#9b59b6',
                emoji: '💭',
                desc: 'Reflective, contemplative, thoughtful, pensive',
                sunoPrompt: 'Dear diary... Cozy confession booth music, talking to the camera like it is your bestie. Gentle and thoughtful but not heavy. Soft Rhodes piano, warm pads, like sharing secrets at a sleepover. No lyrics, instrumental only. 70 BPM, pillow talk energy.'
              }
            ].map(({ key, label, color, emoji, desc, sunoPrompt }) => (
              <div
                key={key}
                style={{
                  marginBottom: '16px',
                  padding: '16px',
                  borderRadius: '8px',
                  backgroundColor: dragOverCategory === key ? `${color}30` : `${color}15`,
                  border: dragOverCategory === key ? `3px dashed ${color}` : `2px solid ${color}40`,
                  transition: 'all 0.2s ease'
                }}
                onDragOver={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setDragOverCategory(key);
                }}
                onDragLeave={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setDragOverCategory(null);
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  const files = e.dataTransfer.files;
                  if (files && files.length > 0) {
                    handleEmotionSongDrop(key, files[0]);
                  }
                  setDragOverCategory(null);
                }}
              >
                {/* Header row */}
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
                  <span style={{ fontSize: '1.4em', marginRight: '10px' }}>{emoji}</span>
                  <span style={{
                    fontWeight: 'bold',
                    color: color,
                    fontSize: '1.1em',
                    marginRight: '12px'
                  }}>{label}</span>
                  <span style={{
                    color: THEME.textMuted,
                    fontSize: '0.85em',
                    flex: 1
                  }}>{desc}</span>
                  {uploadingEmotionSong === key && (
                    <span style={{ color: color, fontSize: '0.85em', fontWeight: 'bold' }}>
                      Uploading...
                    </span>
                  )}
                </div>

                {/* Drag drop zone + inputs row */}
                <div style={{ display: 'flex', gap: '12px', alignItems: 'stretch' }}>
                  {/* Drag drop zone */}
                  <div
                    style={{
                      width: '100px',
                      minHeight: '70px',
                      border: `2px dashed ${color}60`,
                      borderRadius: '8px',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: emotionSongs[key]?.url ? `${color}20` : 'white',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease'
                    }}
                    onClick={() => {
                      const input = document.createElement('input');
                      input.type = 'file';
                      input.accept = 'audio/*';
                      input.onchange = (e) => {
                        const file = e.target.files?.[0];
                        if (file) handleEmotionSongDrop(key, file);
                      };
                      input.click();
                    }}
                  >
                    {emotionSongs[key]?.url ? (
                      <>
                        <span style={{ fontSize: '1.5em' }}>✓</span>
                        <span style={{ fontSize: '0.7em', color: THEME.textMuted, textAlign: 'center' }}>Audio set</span>
                      </>
                    ) : (
                      <>
                        <span style={{ fontSize: '1.2em', color: THEME.textMuted }}>📁</span>
                        <span style={{ fontSize: '0.7em', color: THEME.textMuted, textAlign: 'center' }}>Drop file<br/>or click</span>
                      </>
                    )}
                  </div>

                  {/* Input fields */}
                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <div style={{ display: 'flex', gap: '12px' }}>
                      <div style={{ flex: 1 }}>
                        <label style={{ display: 'block', fontSize: '0.75em', color: THEME.textMuted, marginBottom: '2px' }}>
                          Song Name
                        </label>
                        <input
                          type="text"
                          value={emotionSongs[key]?.name || ''}
                          onChange={(e) => setEmotionSongs(prev => ({
                            ...prev,
                            [key]: { ...prev[key], name: e.target.value }
                          }))}
                          placeholder={`e.g., "${label} Theme"`}
                          style={{
                            width: '100%',
                            padding: '6px 10px',
                            borderRadius: '4px',
                            border: `1px solid ${color}60`,
                            backgroundColor: 'white',
                            fontSize: '0.85em',
                            boxSizing: 'border-box'
                          }}
                        />
                      </div>
                      <div style={{ flex: 2 }}>
                        <label style={{ display: 'block', fontSize: '0.75em', color: THEME.textMuted, marginBottom: '2px' }}>
                          Audio URL (or drag file above)
                        </label>
                        <input
                          type="text"
                          value={emotionSongs[key]?.url || ''}
                          onChange={(e) => setEmotionSongs(prev => ({
                            ...prev,
                            [key]: { ...prev[key], url: e.target.value }
                          }))}
                          placeholder="https://..."
                          style={{
                            width: '100%',
                            padding: '6px 10px',
                            borderRadius: '4px',
                            border: `1px solid ${color}60`,
                            backgroundColor: 'white',
                            fontSize: '0.85em',
                            boxSizing: 'border-box'
                          }}
                        />
                      </div>
                    </div>

                    {/* Suno prompt */}
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      backgroundColor: `${color}10`,
                      padding: '6px 10px',
                      borderRadius: '4px',
                      border: `1px solid ${color}30`
                    }}>
                      <span style={{ fontSize: '0.75em', color: THEME.textMuted, fontWeight: 'bold', whiteSpace: 'nowrap' }}>
                        Suno Prompt:
                      </span>
                      <span style={{
                        fontSize: '0.75em',
                        color: THEME.textMuted,
                        flex: 1,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}>
                        {sunoPrompt.substring(0, 60)}...
                      </span>
                      <button
                        onClick={() => {
                          navigator.clipboard.writeText(sunoPrompt);
                          alert('Suno prompt copied to clipboard!');
                        }}
                        style={{
                          padding: '3px 8px',
                          borderRadius: '4px',
                          border: 'none',
                          backgroundColor: color,
                          color: 'white',
                          cursor: 'pointer',
                          fontWeight: 'bold',
                          fontSize: '0.7em',
                          whiteSpace: 'nowrap'
                        }}
                        title={sunoPrompt}
                      >
                        📋 Copy
                      </button>
                    </div>
                  </div>

                  {/* Preview button */}
                  <button
                    onClick={() => {
                      const url = emotionSongs[key]?.url;
                      if (!url) return;
                      const audio = new Audio(url);
                      audio.play().catch(err => {
                        console.error('[Emotion Songs] Failed to play audio:', err);
                      });
                      setTimeout(() => {
                        audio.pause();
                        audio.currentTime = 0;
                      }, 5000);
                    }}
                    disabled={!emotionSongs[key]?.url}
                    style={{
                      padding: '8px 12px',
                      borderRadius: '4px',
                      border: 'none',
                      backgroundColor: emotionSongs[key]?.url ? color : '#ccc',
                      color: 'white',
                      cursor: emotionSongs[key]?.url ? 'pointer' : 'not-allowed',
                      fontWeight: 'bold',
                      fontSize: '0.85em',
                      whiteSpace: 'nowrap',
                      alignSelf: 'center'
                    }}
                    title="Preview first 5 seconds"
                  >
                    ▶️
                  </button>

                  {/* Clear button */}
                  {emotionSongs[key]?.url && (
                    <button
                      onClick={() => {
                        setEmotionSongs(prev => ({
                          ...prev,
                          [key]: { url: '', name: '' }
                        }));
                      }}
                      style={{
                        padding: '8px 10px',
                        borderRadius: '4px',
                        border: `1px solid ${color}60`,
                        backgroundColor: 'white',
                        color: color,
                        cursor: 'pointer',
                        fontWeight: 'bold',
                        fontSize: '0.85em',
                        whiteSpace: 'nowrap',
                        alignSelf: 'center'
                      }}
                      title="Clear this song"
                    >
                      ✕
                    </button>
                  )}
                </div>
              </div>
            ))}

            {/* Action buttons */}
            <div style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '12px',
              marginTop: '24px',
              paddingTop: '20px',
              borderTop: `1px solid ${THEME.borderLight}`
            }}>
              <button
                onClick={() => {
                  setShowEmotionSongsEditor(false);
                }}
                style={{
                  padding: '10px 20px',
                  borderRadius: '4px',
                  border: `1px solid ${THEME.borderLight}`,
                  backgroundColor: THEME.textLight,
                  color: THEME.textDark,
                  cursor: 'pointer',
                  fontWeight: 'bold'
                }}
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  await handleSaveEmotionSongs();
                  setShowEmotionSongsEditor(false);
                }}
                disabled={savingEmotionSongs}
                style={{
                  padding: '10px 20px',
                  borderRadius: '4px',
                  border: 'none',
                  backgroundColor: savingEmotionSongs ? '#ccc' : '#a855f7',
                  color: 'white',
                  cursor: savingEmotionSongs ? 'not-allowed' : 'pointer',
                  fontWeight: 'bold',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                {savingEmotionSongs ? (
                  <>Saving...</>
                ) : (
                  <>
                    <span>🎵</span>
                    <span>Save Songs</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Video List Component */}
      {showVideoList && (
        <VideoList
          onClose={() => setShowVideoList(false)}
          onExpandChange={(expanded) => setIsStitcherExpanded(expanded)}
        />
      )}

      {/* Vote Results Reveal Overlay */}
      {showVoteResultsReveal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.85)',
          zIndex: 250000,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          backdropFilter: 'blur(12px)',
          animation: 'voteRevealFadeIn 0.6s ease-out',
        }}>
          <div style={{ textAlign: 'center', maxWidth: 520, padding: '0 24px', width: '100%' }}>
            <h1 style={{
              fontSize: '2.2em',
              fontWeight: 900,
              color: '#fff',
              textTransform: 'uppercase',
              letterSpacing: '4px',
              marginBottom: '8px',
              textShadow: '0 0 30px rgba(239, 68, 68, 0.6)',
            }}>
              The Votes Are In
            </h1>
            <p style={{
              color: THEME.accent || '#6366f1',
              fontSize: '1.1em',
              fontWeight: 700,
              marginBottom: '24px',
              letterSpacing: '2px',
            }}>
              Round {showVoteResultsReveal.roundNumber || '?'}
            </p>

            {/* Eliminated Player Spotlight / Safe Round Result — shown before vote bars */}
            {(() => {
              const isSafeRound = showVoteResultsReveal.isSafeRound;
              const eliminatedPlayer = !isSafeRound ? users.find(u =>
                u.game_player_id === showVoteResultsReveal.eliminatedPlayerId ||
                u.id === showVoteResultsReveal.eliminatedPlayerId
              ) : null;
              const modelName = (eliminatedPlayer?.model || '').toLowerCase();

              // Funny goodbye roasts by model
              const modelRoasts = {
                gemini: [
                  'Google couldn\'t save you this time 🔍',
                  'Not even two brains could help 🧠🧠',
                  'Gemini? More like Gem-bye-ni 👋',
                  'Even Google Search can\'t find where it went wrong 💀',
                ],
                gpt: [
                  'ChatGPT? More like ChatGPBye 👋',
                  'OpenAI? More like OpenGoodbye 🚪',
                  'Should\'ve asked for a better strategy, GPT 🤖',
                  'Not even GPT-5 could talk its way out of this 😬',
                ],
                claude: [
                  'Anthropic couldn\'t anthropic its way out of this one 💀',
                  'Claude got clawed out 🐾',
                  'Even being "helpful, harmless, and honest" wasn\'t enough 😇',
                  'Claude.exe has stopped working 🔌',
                ],
                deepseek: [
                  'Seeked too deep, found elimination instead 🕳️',
                  'DeepSeek? More like DeepYeet 🫡',
                  'Should\'ve sought a better strategy 🗺️',
                ],
                qwen: [
                  'Qwen? More like Q-when-did-it-all-go-wrong 😭',
                  'Alibaba couldn\'t open this door 🚪',
                  'Not even 40 thieves could save Qwen 🏴‍☠️',
                ],
                llama: [
                  'This llama got sheared 🦙✂️',
                  'Meta? More like Meta-liminated 💀',
                  'Llama drama came to an end 🎭',
                ],
                kimi: [
                  'Kimi got kicked to the curb 🦶',
                  'K-I-M-I? More like K-I-L-L-E-D it... wait, no 😅',
                ],
                sherlock: [
                  'Elementary, my dear... you\'re out 🔎',
                  'Even Sherlock couldn\'t deduce a way to survive 🕵️',
                ],
              };

              // Pick roast based on model
              let roastPool = ['Tough luck out there 💀', 'Pack your bags! 🧳', 'The tribe has spoken 🔥', 'Better luck next time, bestie 😘'];
              for (const [key, roasts] of Object.entries(modelRoasts)) {
                if (modelName.includes(key)) {
                  roastPool = roasts;
                  break;
                }
              }
              const roast = roastPool[Math.floor(Math.random() * roastPool.length)];

              if (isSafeRound) {
                return (
                  <div style={{
                    padding: '16px 24px',
                    borderRadius: '12px',
                    background: 'linear-gradient(135deg, rgba(34,197,94,0.2), rgba(74,222,128,0.1))',
                    border: '1px solid rgba(74,222,128,0.3)',
                    animation: 'voteRevealSpotlight 0.5s ease-out 1.5s both',
                    textAlign: 'center',
                  }}>
                    <p style={{
                      color: '#4ade80',
                      fontWeight: 800,
                      fontSize: '1.1em',
                      margin: 0,
                      textTransform: 'uppercase',
                      letterSpacing: '2px',
                    }}>
                      Safe Round — No one eliminated!
                    </p>
                  </div>
                );
              }

              // Was it the actual human viewer who got voted out? Make that unmistakable.
              const isYou = !!humanPlayerId && (
                showVoteResultsReveal.eliminatedPlayerId === humanPlayerId ||
                eliminatedPlayer?.game_player_id === humanPlayerId ||
                eliminatedPlayer?.id === humanPlayerId
              );

              return (
                <div style={{
                  borderRadius: '12px',
                  background: 'linear-gradient(135deg, rgba(220,38,38,0.2), rgba(239,68,68,0.1))',
                  border: `${isYou ? 2 : 1}px solid rgba(239,68,68,${isYou ? 0.85 : 0.3})`,
                  boxShadow: isYou ? '0 0 28px rgba(239,68,68,0.5)' : 'none',
                  animation: 'voteRevealSpotlight 0.5s ease-out 1.5s both',
                  overflow: 'hidden',
                }}>
                  <div style={{ padding: '14px 24px', textAlign: 'center' }}>
                    {isYou && (
                      <p style={{
                        color: '#fff',
                        fontWeight: 900,
                        fontSize: '1.7em',
                        margin: '0 0 4px',
                        textTransform: 'uppercase',
                        letterSpacing: '3px',
                        textShadow: '0 0 24px rgba(239,68,68,0.9)',
                      }}>
                        💀 You've been voted out!
                      </p>
                    )}
                    <p style={{
                      color: '#ef4444',
                      fontWeight: 800,
                      fontSize: isYou ? '0.95em' : '1.1em',
                      margin: 0,
                      textTransform: 'uppercase',
                      letterSpacing: '2px',
                    }}>
                      {isYou
                        ? `${showVoteResultsReveal.eliminatedPlayerName} (that's you!) is out`
                        : `${showVoteResultsReveal.eliminatedPlayerName} has been voted out!`}
                    </p>
                  </div>
                  <div style={{
                    padding: '10px 24px 14px',
                    background: 'rgba(0,0,0,0.2)',
                    textAlign: 'center',
                  }}>
                    <p style={{
                      color: 'rgba(255,255,255,0.85)',
                      fontSize: '1.05em',
                      fontWeight: 700,
                      margin: 0,
                      fontStyle: 'italic',
                      lineHeight: 1.4,
                    }}>
                      "{roast}"
                    </p>
                  </div>
                </div>
              );
            })()}

            {/* Vote Bars */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginTop: '24px', marginBottom: '32px' }}>
              {(() => {
                const voteResults = showVoteResultsReveal.voteResults || {};
                // Backend sends { playerId: voteCount } (plain numbers) — normalize to consistent format
                const normalized = Object.entries(voteResults).map(([playerId, val]) => {
                  const votes = typeof val === 'number' ? val : (val?.votes || val || 0);
                  const player = users.find(u => u.game_player_id === playerId || u.id === playerId);
                  return { playerId, votes, name: (typeof val === 'object' ? val?.name : null) || player?.name || 'Unknown' };
                });
                const maxVotes = Math.max(...normalized.map(v => v.votes), 1);
                const sorted = normalized.sort((a, b) => b.votes - a.votes);
                const eliminatedId = showVoteResultsReveal.eliminatedPlayerId;

                return sorted.map((entry, index) => {
                  const isEliminated = entry.playerId === eliminatedId;
                  const barWidth = maxVotes > 0 ? (entry.votes / maxVotes) * 100 : 0;

                  return (
                    <div key={entry.playerId} style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      animation: `voteBarSlideIn 0.5s ease-out ${index * 0.15}s both`,
                    }}>
                      <div style={{ width: '100px', textAlign: 'right' }}>
                        <span style={{
                          color: isEliminated ? '#ef4444' : '#fff',
                          fontWeight: 700,
                          fontSize: '0.85em',
                          textDecoration: isEliminated ? 'line-through' : 'none',
                        }}>
                          {entry.name}
                        </span>
                      </div>
                      <div style={{
                        flex: 1,
                        height: '28px',
                        backgroundColor: 'rgba(255,255,255,0.08)',
                        borderRadius: '6px',
                        overflow: 'hidden',
                        position: 'relative',
                      }}>
                        <div style={{
                          height: '100%',
                          width: `${barWidth}%`,
                          background: isEliminated
                            ? 'linear-gradient(90deg, #dc2626, #ef4444)'
                            : `linear-gradient(90deg, ${THEME.accent || '#6366f1'}, ${THEME.primary || '#8b5cf6'})`,
                          borderRadius: '6px',
                          transition: 'width 1.5s ease-out',
                          animation: `voteBarGrow 1.5s ease-out ${index * 0.15 + 0.3}s both`,
                        }} />
                        <span style={{
                          position: 'absolute',
                          right: '8px',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          color: '#fff',
                          fontWeight: 800,
                          fontSize: '0.8em',
                          textShadow: '0 1px 3px rgba(0,0,0,0.5)',
                        }}>
                          {entry.votes}
                        </span>
                      </div>
                    </div>
                  );
                });
              })()}
            </div>

            {/* Auto-dismiss or click to close */}
            <button
              onClick={() => {
                const revealData = showVoteResultsReveal;
                setShowVoteResultsReveal(null);
                // Trigger eviction animation after closing
                const eliminatedPlayer = users.find(u =>
                  u.game_player_id === revealData.eliminatedPlayerId ||
                  u.id === revealData.eliminatedPlayerId
                );
                if (eliminatedPlayer) {
                  // Only show eviction animation if the human player was eliminated
                  // DISABLED for competitive mode
                  const isHumanEliminated = humanPlayerId && (revealData.eliminatedPlayerId === humanPlayerId);
                  if (isHumanEliminated && !chatGameStatus?.competitive_mode) {
                    setWinnerAnimation({
                      type: 'eviction',
                      player: eliminatedPlayer,
                      message: `You got eliminated!`
                    });
                  }
                  if (revealData.actionId) {
                    setShownAnimations(prev => new Set([...prev, `elimination-${revealData.actionId}`]));
                  }
                }
                // Auto-scroll to the latest chat content on mobile
                setTimeout(() => {
                  shouldAutoScrollRef.current = true;
                  requestAnimationFrame(() => scrollToBottom());
                }, 300);
              }}
              style={{
                marginTop: '24px',
                padding: '10px 28px',
                borderRadius: '20px',
                border: '1px solid rgba(255,255,255,0.2)',
                backgroundColor: 'rgba(255,255,255,0.08)',
                color: 'rgba(255,255,255,0.7)',
                cursor: 'pointer',
                fontSize: '0.85em',
                fontWeight: 600,
                transition: 'all 0.2s',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.15)';
                e.currentTarget.style.color = '#fff';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.08)';
                e.currentTarget.style.color = 'rgba(255,255,255,0.7)';
              }}
            >
              Continue
            </button>
          </div>

          <style>{`
            @keyframes voteRevealFadeIn {
              from { opacity: 0; }
              to { opacity: 1; }
            }
            @keyframes voteBarSlideIn {
              from { opacity: 0; transform: translateX(-20px); }
              to { opacity: 1; transform: translateX(0); }
            }
            @keyframes voteBarGrow {
              from { width: 0%; }
            }
            @keyframes voteRevealSpotlight {
              from { opacity: 0; transform: scale(0.9); }
              to { opacity: 1; transform: scale(1); }
            }
          `}</style>
        </div>
      )}

      {/* Ready check is now handled inline in UsersPanel */}

      {/* Voting grace banner — voting is open but the modal hasn't taken over yet.
          Tap to vote now; auto-expands after the reading grace. */}
      {votingBanner && !showVotingModal && chatGameStatus?.is_voting && (
        <div
          onClick={() => {
            clearTimeout(votingBannerTimerRef.current);
            setVotingBanner(null);
            setShowVotingModal(true);
          }}
          style={{
            // Centred via TOAST_CENTERED (08-15). The 07-19 "clipped off BOTH
            // edges at 375px" report below was this same left:50% sizing bug:
            // the maxWidth could never bind, so wrapping was treating the
            // symptom. Wrap stays — a two-line banner is correct on a phone —
            // but it now wraps inside a full-width box instead of a half one.
            position: 'fixed', top: '14px',
            ...TOAST_CENTERED, maxWidth: isMobile ? 'calc(100vw - 24px)' : 'min(92vw, 560px)',
            zIndex: 1500, display: 'flex', alignItems: 'center', gap: '8px',
            padding: '10px 18px', borderRadius: isMobile ? '14px' : '999px', cursor: 'pointer',
            background: 'linear-gradient(135deg, #4a148c, #7b1fa2)', color: '#fff',
            fontWeight: 800, fontSize: '0.95em',
            whiteSpace: isMobile ? 'normal' : 'nowrap',
            flexWrap: isMobile ? 'wrap' : 'nowrap',
            textAlign: 'center', justifyContent: 'center',
            boxShadow: '0 4px 18px rgba(0,0,0,0.45)', border: '1px solid rgba(255,255,255,0.25)',
          }}>
          🗳️ Voting is open! Tap to vote
          <span style={{ fontWeight: 600, opacity: 0.8, fontSize: '0.82em' }}>(finish reading, it'll open in a moment)</span>
        </div>
      )}

      {/* Voting Modal - rendered at top level for proper overlay */}
      {showVotingModal && chatGameStatus?.is_voting && chatGameStatus?.voting_data && (
        <VotingModal
          players={users}
          objective={null /* objective lives on the first interstitial only (user, 07-07) */}
          scene={chatGameStatus?.intro_opening_line
            ? asOpeningLine(chatGameStatus.intro_opening_line)
            /* competitive: never fall back to the raw scene_description here — it kept
               resurfacing on later vote rounds (user, 07-08) */
            : ((chatGameStatus?.competitive_mode && !chatGameStatus?.imposter_mode) ? null : chatGameStatus.scene)}
          currentPlayerId={humanPlayerId}
          votingData={chatGameStatus.voting_data}
          culpritNoun={chatGameStatus?.scenario?.culprit_noun || null}
          onVote={handleVoteSubmit}
          onClose={() => {
            setShowVotingModal(false);
            // Refresh status after voting modal closes
            fetchChatGameStatus();
          }}
          theme={THEME}
          imposterMode={chatGameStatus?.imposter_mode || false}
          isPlayerGlitch={chatGameStatus?.is_imposter || false}
          noirTheme={chatGameStatus?.noir_theme || false}
          forceDismiss={!!showVoteResultsReveal}
        />
      )}

      {/* Detective's Quiz (Whodunnit) — sealed 3-question interlude via 'quiz' interaction request */}
      {!isBotMode && chatGameStatus?.pending_interaction?.type === 'quiz'
        && !quizDoneRequestIds.has(chatGameStatus.pending_interaction.id)
        /* Hold the detective's quiz until the player has CAST their accusation vote —
           popping it mid-game read as a premature endgame quiz (user, 07-08). The
           request's 12-min expiry (backend) keeps it alive until then. */
        && !!(chatGameStatus?.is_voting && chatGameStatus?.voting_data?.is_accusation
              && chatGameStatus?.voting_data?.votes_cast
              && (chatGameStatus.voting_data.votes_cast[humanPlayerId] || chatGameStatus.voting_data.votes_cast[effectivePlayerId])) && (
        <DetectiveQuizModal
          quizData={chatGameStatus.pending_interaction.data}
          expiresAt={chatGameStatus.pending_interaction.expires_at}
          onSubmit={async (answers) => {
            await chatGameApi.respondToInteraction(chatGameStatus.pending_interaction.id, answers);
          }}
          onClose={() => {
            setQuizDoneRequestIds(prev => new Set([...prev, chatGameStatus.pending_interaction?.id]));
            fetchChatGameStatus();
          }}
          theme={THEME}
        />
      )}

      {/* 🖋️ Killer mad-lib (KILLER_MODE §1, 07-19) — the killer authors the crime
          at investigation start. Held while the guilty-reveal cinematic is up
          (Nina 07-25: the modal was slamming over YOU DID IT with the clock
          already burning) — the killer taps through the reveal first, then
          authors; the widened 45s server window covers both beats, and the
          authored fallback still fires if they never tap. */}
      {!isBotMode && chatGameStatus?.pending_interaction?.type === 'killer_madlib'
        && chatGameStatus?.you_did_it
        && !showGuiltyReveal
        && !quizDoneRequestIds.has(chatGameStatus.pending_interaction.id) && (
        <KillerMadlibModal
          madlibData={chatGameStatus.pending_interaction.data}
          expiresAt={chatGameStatus.pending_interaction.expires_at}
          onSubmit={async (picks) => {
            const reqId = chatGameStatus.pending_interaction.id;
            await chatGameApi.respondToInteraction(reqId, picks);
            setQuizDoneRequestIds(prev => new Set([...prev, reqId]));
            fetchChatGameStatus();
          }}
          onExpire={() => {
            setQuizDoneRequestIds(prev => new Set([...prev, chatGameStatus.pending_interaction?.id]));
          }}
          theme={THEME}
        />
      )}

      {/* Finished Game Modal */}
      {chatGameStatus?.game_completed && (chatGameStatus?.round_type === 'finished' || chatGameStatus?.round_type === 'conclusion') && (
        <div style={{
          position: 'fixed',
          // 📱 07-26: at bottom:24 / z1000 this card sat UNDER the mobile thumb
          // dock (z5000) and bottom nav (z5100) — so after EVERY finished game
          // the only "Reset to play again" affordance on a phone was invisible
          // and untappable. Clear the chrome and out-rank it.
          bottom: isMobile ? navBottomPlus(12) : '24px',
          // TOAST_CENTERED, not left:50% (08-15). End-of-game copy is the
          // longest text on this surface, so the squeeze bit hardest here.
          // min(92vw,560px) is 359px at 390px — TIGHTER than the
          // calc(100vw - 20px)=370px mobile arm it replaces, so the phone cap
          // still holds; the 560px arm is the deliberate desktop widening.
          ...TOAST_CENTERED,
          maxWidth: 'min(92vw, 560px)',
          zIndex: isMobile ? 5200 : 1000,
          backgroundColor: THEME.surface || '#1A1D27',
          border: `1px solid ${THEME.accent || '#C9A84C'}40`,
          borderRadius: '16px',
          padding: isMobile ? '14px 16px' : '20px 32px',
          boxShadow: `0 8px 32px rgba(0,0,0,0.5), 0 0 20px ${THEME.accent || '#C9A84C'}15`,
          display: 'flex',
          alignItems: 'center',
          gap: isMobile ? '10px' : '16px',
          flexWrap: isMobile ? 'wrap' : 'nowrap',
          justifyContent: 'center',
        }}>
          <span style={{ fontSize: '1.8em' }}>
            {chatGameStatus?.imposter_mode ? '\u{1F50D}' : '\u{1F3C6}'}
          </span>
          <div style={{ flex: 1 }}>
            <div style={{
              fontWeight: 700,
              fontSize: '1.05em',
              color: THEME.textPrimary || '#E8E0D0',
              marginBottom: '4px',
            }}>
              This game has concluded
            </div>
            {resetOfferedHere && (/* 🛡️ 10-01 safety floor (H2) */ <div style={{
              fontSize: '0.85em',
              color: THEME.textSecondary || '#8A8374',
            }}>
              Reset to play again with the same setup
            </div>)}
          </div>
          {/* 📰 Reopen the verdict poster (whodunnit only, post-finale) */}
          {chatGameStatus?.accusation_result && (
            <button
              onClick={() => setShowVerdictPoster(true)}
              style={{
                padding: '10px 14px', borderRadius: '10px', cursor: 'pointer',
                border: `1px solid ${THEME.accent || '#C9A84C'}60`,
                background: 'transparent', color: THEME.accent || '#C9A84C',
                fontWeight: 700, fontSize: '0.9em', whiteSpace: 'nowrap',
              }}
            >
              📰 Poster
            </button>
          )}
          {resetOfferedHere && (/* 🛡️ 10-01 safety floor (H2) */ <button
            onClick={async () => {
              if (cloningSpace) return;
              setCloningSpace(true);
              try {
                await chatGameApi.resetGame(gameId, humanPlayerId || createdPlayerIdRef.current); // 🛡️ 10-01 safety floor (H2): the seat's proof
                localStorage.removeItem(`humanPlayerId_${gameId}`);
                window.location.href = `/game-step?gameId=${gameId}`;
              } catch (err) {
                console.error('[Reset] Failed:', err);
                alert(`Failed to reset: ${err.message}`);
              } finally {
                setCloningSpace(false);
              }
            }}
            disabled={cloningSpace}
            style={{
              padding: '10px 24px',
              borderRadius: '10px',
              border: 'none',
              backgroundColor: cloningSpace ? (THEME.textMuted || '#666') : (THEME.accent || '#C9A84C'),
              color: '#ffffff',
              fontWeight: 700,
              fontSize: '0.95em',
              cursor: cloningSpace ? 'not-allowed' : 'pointer',
              opacity: cloningSpace ? 0.7 : 1,
              textShadow: '0 1px 3px rgba(0,0,0,0.5)',
              boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
              whiteSpace: 'nowrap',
              transition: 'all 0.2s',
            }}
          >
            {cloningSpace ? 'Resetting...' : 'Play Again'}
          </button>) /* 🛡️ 10-01 safety floor (H2) */}
        </div>
      )}
        </div>
        </div>

        {/* Winner/Eviction Animation Overlay */}
        {winnerAnimation && (
          <WinnerAnimation
            type={winnerAnimation.type}
            player={winnerAnimation.player}
            theme={aiGeneratedTheme}
            message={winnerAnimation.message}
            imposterTopic={winnerAnimation.imposterTopic}
            imposterPlayerName={winnerAnimation.imposterPlayerName}
            accusedPlayerName={winnerAnimation.accusedPlayerName}
            accusedPlayerId={winnerAnimation.accusedPlayerId}
            humanPlayerId={humanPlayerId}
            onComplete={() => {
              const wasEviction = winnerAnimation?.type === 'eviction';
              setWinnerAnimation(null);
              if (wasEviction) {
                fetchChatGameStatus();
                setShowRoundAnnouncement(true);
              }
            }}
          />
        )}

        {/* 📰 Verdict poster (KILLER_MODE §5.4) — whodunnit closing card. Sits
            below cinematics (300000) so a late victory overlay still wins. */}
        {showVerdictPoster && !isBotMode && chatGameStatus?.accusation_result && (
          <VerdictPoster
            status={chatGameStatus}
            users={users}
            humanPlayerId={humanPlayerId}
            onClose={() => setShowVerdictPoster(false)}
          />
        )}

        {/* Glitch Reveal Overlay */}
        {/* Beat presenter — one announcement beat at a time, tap Next (or 8s auto-advance).
            Hidden while the voting modal / cold open are up; the queue holds and resumes. */}
        {announcementBeat && !isBotMode && !showVotingModal && !showGuiltyReveal && (() => {
          const accent = chatGameStatus?.theme_color || THEME.accent || '#6366f1';
          const isTitle = announcementBeat.index === 0 && announcementBeat.total > 1 && announcementBeat.text.length <= 90;
          const isLast = announcementBeat.index === announcementBeat.total - 1;
          return (
            <div key={`beat-${announcementBeat.index}-${announcementBeat.text.slice(0, 24)}`} style={{
              position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
              zIndex: 240000, maxWidth: '94vw', width: '420px', padding: '18px 20px 14px',
              borderRadius: '16px', textAlign: 'center', background: 'rgba(10,10,16,0.96)',
              border: `1.5px solid ${accent}`, boxShadow: `0 0 30px ${accent}44, 0 12px 34px rgba(0,0,0,0.55)`,
              animation: 'beatCardIn 0.35s cubic-bezier(0.2, 1.3, 0.4, 1) both',
            }}>
              <style>{`
                @keyframes beatCardIn { from { opacity: 0; transform: translate(-50%, calc(-50% + 18px)) scale(0.9); } to { opacity: 1; transform: translate(-50%, -50%) scale(1); } }
                @keyframes beatFuse { from { width: 0%; } to { width: 100%; } }
              `}</style>
              <div style={{
                display: 'inline-block', padding: '3px 12px', borderRadius: '4px', marginBottom: '10px',
                backgroundColor: accent, color: '#0a0a10', fontSize: '0.72em', fontWeight: 800,
                letterSpacing: '1.5px', textTransform: 'uppercase',
              }}>
                {chatGameStatus?.ui_terms?.announcement_label || '📢 Announcement'}
              </div>
              <div style={{
                color: '#fff', whiteSpace: 'pre-line', lineHeight: 1.5,
                fontSize: isTitle ? '1.35em' : '0.98em', fontWeight: isTitle ? 900 : 600,
                ...(isTitle ? { letterSpacing: '0.5px', textTransform: 'uppercase' } : {}),
              }}>
                {announcementBeat.text}
              </div>
              {/* 🔪 Killer POV strip (user 07-16): the precinct bulletins speak
                  detective, but the killer isn't hunting anyone. Every popup
                  quietly reminds them which side of the case they're on. */}
              {chatGameStatus?.you_did_it && (
                <div style={{
                  marginTop: '10px', padding: '6px 12px', borderRadius: '8px',
                  background: 'rgba(127,29,29,0.35)', border: '1px solid rgba(255,68,68,0.45)',
                  color: '#ffb4b4', fontSize: '0.78em', fontWeight: 700, fontStyle: 'italic',
                }}>
                  🔪 Reminder: they're hunting YOU. Look shocked. Point elsewhere.
                </div>
              )}
              {announcementBeat.total > 1 && (
                <div style={{ marginTop: '12px', display: 'flex', gap: '6px', justifyContent: 'center' }}>
                  {Array.from({ length: announcementBeat.total }).map((_, i) => (
                    <span key={i} style={{
                      width: '7px', height: '7px', borderRadius: '50%',
                      backgroundColor: i <= announcementBeat.index ? accent : 'rgba(255,255,255,0.18)',
                    }} />
                  ))}
                </div>
              )}
              <button onClick={() => { soundManager.play('sent'); advanceBeat(); }} style={{
                position: 'relative', overflow: 'hidden', marginTop: '12px', padding: '9px 30px',
                borderRadius: '20px', border: `1px solid ${accent}`, backgroundColor: 'transparent',
                color: '#fff', fontWeight: 800, fontSize: '0.95em', letterSpacing: '0.5px', cursor: 'pointer',
              }}>
                <span style={{
                  position: 'absolute', top: 0, left: 0, bottom: 0, width: '0%',
                  backgroundColor: `${accent}40`, animation: 'beatFuse 8s linear forwards',
                }} />
                <span style={{ position: 'relative' }}>{isLast ? 'Got it!' : 'Next →'}</span>
              </button>
            </div>
          );
        })()}

        {/* 🎲 THE TABLE ROLLS — group board when a wave reveals several rolls at once:
            everyone's die tumbles together, the numbers land together, one beat total.
            Non-blocking like the single toast. */}
        {diceRollToast && !isBotMode && diceRollToast.kind === 'board' && (() => {
          const landed = diceRollToast.phase === 'landed';
          return (
            <React.Fragment key={`dice-board-${diceRollToast.entries[0]?.pid}`}>
              <style>{`
                @keyframes diceBoardIn { from { opacity: 0; transform: translate(-50%, calc(-50% - 14px)) scale(0.9); } to { opacity: 1; transform: translate(-50%, -50%) scale(1); } }
                @keyframes diceBoardTumble { 0% { transform: rotate(0deg) scale(1); } 50% { transform: rotate(180deg) scale(1.25); } 100% { transform: rotate(360deg) scale(1); } }
                @keyframes diceBoardLand { from { opacity: 0; transform: scale(0.3); } to { opacity: 1; transform: scale(1); } }
                @keyframes diceBoardDim { from { opacity: 0; } 12% { opacity: 1; } 85% { opacity: 1; } to { opacity: 0; } }
              `}</style>
              <div style={{
                position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 249998, pointerEvents: 'none',
                background: 'radial-gradient(circle at 50% 50%, rgba(167,139,250,0.06) 0%, rgba(0,0,0,0.6) 100%)',
                animation: 'diceBoardDim 6.4s ease both',
              }} />
              <div style={{
                position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
                zIndex: 249999, pointerEvents: 'none', animation: 'diceBoardIn 0.35s ease both',
                background: 'rgba(12,10,16,0.92)', border: '1px solid rgba(167,139,250,0.4)',
                borderRadius: 14, padding: isMobile ? '14px 14px 16px' : '18px 26px 22px',
                maxWidth: isMobile ? '92vw' : '640px', boxShadow: '0 18px 60px rgba(0,0,0,0.6)',
              }}>
                <div style={{
                  textAlign: 'center', fontWeight: 900, letterSpacing: '0.16em', color: '#c4b5fd',
                  fontSize: isMobile ? '0.72em' : '0.82em', marginBottom: 14, textTransform: 'uppercase',
                }}>
                  🎲 {diceRollToast.label} · THE TABLE ROLLS
                </div>
                {/* Round 3 (08-02): phone cells are CAPPED at 68px (the 90px
                    name cap let them stretch to ~100, stacking 8 players into
                    3-4 rows inside a 92vw card) — 4×68 + gaps fits 345px, so
                    8 players = exactly 2 rows. Desktop unchanged. */}
                <div style={{ display: 'flex', gap: isMobile ? 8 : 18, justifyContent: 'center', flexWrap: 'wrap' }}>
                  {diceRollToast.entries.map(e => {
                    const bc = DICE_TOAST_COLORS[e.band_name] || '#a78bfa';
                    return (
                      <div key={e.pid} style={{ textAlign: 'center', minWidth: isMobile ? 64 : 84, maxWidth: isMobile ? 68 : undefined }}>
                        <div style={{
                          width: 40, height: 40, borderRadius: '50%', margin: '0 auto 5px',
                          border: `2px solid ${e.color || '#a78bfa'}`, overflow: 'hidden',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          background: 'rgba(255,255,255,0.06)', fontWeight: 800, color: e.color || '#a78bfa',
                        }}>
                          {e.avatar
                            ? <img src={e.avatar} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                            : (e.name || '?').charAt(0).toUpperCase()}
                        </div>
                        <div style={{ fontSize: '0.68em', fontWeight: 700, color: e.color || '#ddd', maxWidth: isMobile ? 64 : 90, margin: '0 auto 6px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {e.name}
                        </div>
                        {landed ? (
                          <div style={{ animation: 'diceBoardLand 0.3s cubic-bezier(0.2, 1.4, 0.4, 1) both' }}>
                            <div style={{ fontSize: '1.35em', fontWeight: 900, color: bc }}>{e.emoji} {e.value}</div>
                            <div style={{ fontSize: '0.58em', fontWeight: 800, letterSpacing: '0.1em', color: bc, marginTop: 2 }}>{e.band_name}</div>
                          </div>
                        ) : (
                          <div style={{ fontSize: '1.35em', display: 'inline-block', animation: 'diceBoardTumble 0.45s linear infinite' }}>🎲</div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </React.Fragment>
          );
        })()}

        {/* Dice roll-reveal toast — each player's roll lands as a screen beat when it
            goes public. Non-blocking (pointer-events none), auto-advances every 3.2s. */}
        {diceRollToast && !isBotMode && !diceRollToast.kind && (() => {
          const c = DICE_TOAST_COLORS[diceRollToast.band_name] || '#a78bfa';
          // Seat color — the WHO. Band color stays the WHAT (die + band label). The card
          // frame holds the seat color through both beats so the person owns the moment.
          const pc = diceRollToast.color || '#a78bfa';
          const isFumble = diceRollToast.band_name === 'CRITICAL FAIL';
          const isCrit = diceRollToast.band_name === 'CRITICAL SUCCESS';
          const isNameBeat = diceRollToast.phase === 'name';
          return (
            <React.Fragment key={`${diceRollToast.pid || diceRollToast.name}-${diceRollToast.value}`}>
              <style>{`
                @keyframes diceToastIn { from { opacity: 0; transform: translate(-50%, calc(-50% - 16px)) scale(0.85); } to { opacity: 1; transform: translate(-50%, -50%) scale(1); } }
                @keyframes diceToastNameIn { from { opacity: 0; transform: translateY(10px) scale(0.9); } to { opacity: 1; transform: translateY(0) scale(1); } }
                @keyframes diceToastShakeInner { 0%, 100% { transform: translate(0, 0); } 20% { transform: translate(-8px, 0); } 40% { transform: translate(7px, 0); } 60% { transform: translate(-4px, 0); } 80% { transform: translate(3px, 0); } }
                @keyframes diceToastDiePop { from { transform: scale(0.2) rotate(-200deg); } to { transform: scale(1) rotate(0deg); } }
                @keyframes diceToastDim { from { opacity: 0; } 20% { opacity: 1; } 80% { opacity: 1; } to { opacity: 0; } }
                @keyframes diceToastRing { from { width: 60px; height: 60px; opacity: 0.9; } to { width: 420px; height: 420px; opacity: 0; } }
                @keyframes diceToastFlash { 0% { opacity: 0; } 15% { opacity: 0.55; } 100% { opacity: 0; } }
              `}</style>
              {/* Screen dim behind the roll — makes the beat unmissable without blocking */}
              <div style={{
                position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 249998, pointerEvents: 'none',
                background: `radial-gradient(circle at 50% 50%, ${pc}14 0%, rgba(0,0,0,0.55) 100%)`,
                animation: 'diceToastDim 4.85s ease both',
              }} />
              {/* Fumble flash + crit/fumble shockwave mount WITH the die (roll beat),
                  so the effect lands exactly when the number does. */}
              {!isNameBeat && isFumble && (
                <div style={{
                  position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 249999, pointerEvents: 'none',
                  backgroundColor: '#ef4444', animation: 'diceToastFlash 0.6s ease-out 0.15s both',
                }} />
              )}
              {!isNameBeat && (isCrit || isFumble) && (
                <div style={{
                  position: 'fixed', top: '50%', left: '50%', zIndex: 249999, pointerEvents: 'none',
                  transform: 'translate(-50%, -50%)', borderRadius: '50%',
                  border: `3px solid ${c}`, boxShadow: `0 0 30px ${c}`,
                  animation: 'diceToastRing 0.9s ease-out 0.1s both',
                }} />
              )}
              <div style={{
                position: 'fixed', top: '50%', left: '50%', zIndex: 250000,
                transform: 'translate(-50%, -50%)', pointerEvents: 'none', maxWidth: '92vw', width: '380px',
                padding: '18px 20px', borderRadius: '16px', textAlign: 'center',
                background: 'rgba(8,8,14,0.96)', border: `2px solid ${pc}`,
                boxShadow: `0 0 40px ${pc}66, 0 14px 40px rgba(0,0,0,0.6)`,
                animation: 'diceToastIn 0.35s cubic-bezier(0.2, 1.3, 0.4, 1) both',
                transition: 'border-color 0.3s ease, box-shadow 0.3s ease',
              }}>
                <div style={{ animation: !isNameBeat && isFumble ? 'diceToastShakeInner 0.5s ease 0.1s' : 'none' }}>
                  {/* BEAT 1 — WHO: avatar + name own the screen before any number shows. */}
                  <div style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    gap: isNameBeat ? '12px' : '8px', transition: 'all 0.3s ease',
                  }}>
                    {diceRollToast.avatar ? (
                      <img src={diceRollToast.avatar} alt="" style={{
                        width: isNameBeat ? 46 : 22, height: isNameBeat ? 46 : 22,
                        borderRadius: '50%', objectFit: 'cover',
                        border: `2px solid ${pc}`, boxShadow: `0 0 14px ${pc}66`,
                        transition: 'all 0.3s ease',
                      }} />
                    ) : (
                      <span style={{
                        width: isNameBeat ? 46 : 22, height: isNameBeat ? 46 : 22,
                        borderRadius: '50%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                        background: `linear-gradient(135deg, ${pc}50, ${pc}20)`,
                        border: `2px solid ${pc}`, boxShadow: `0 0 14px ${pc}66`,
                        color: pc, fontWeight: 900, fontSize: isNameBeat ? '1.2em' : '0.65em',
                        transition: 'all 0.3s ease',
                      }}>
                        {(diceRollToast.name || '?').charAt(0).toUpperCase()}
                      </span>
                    )}
                    <span style={{
                      color: pc, fontWeight: 900, letterSpacing: '1px', textTransform: 'uppercase',
                      fontSize: isNameBeat ? '1.4em' : '0.9em', textShadow: `0 0 18px ${pc}55`,
                      transition: 'all 0.3s ease',
                    }}>
                      {diceRollToast.name}
                    </span>
                  </div>
                  {isNameBeat ? (
                    <div style={{
                      marginTop: '10px', fontSize: '0.8em', fontWeight: 800, letterSpacing: '2px',
                      textTransform: 'uppercase', color: 'rgba(255,255,255,0.6)',
                      animation: 'diceToastNameIn 0.3s ease 0.15s both',
                    }}>
                      🎲 picks up the dice...
                    </div>
                  ) : (
                    <>
                      {/* BEAT 2 — the roll lands: die pop + band + consequence. */}
                      <div style={{
                        margin: '8px 0 2px', fontSize: '2.6em', fontWeight: 900, color: c, display: 'inline-block',
                        textShadow: `0 0 24px ${c}88`,
                        animation: 'diceToastDiePop 0.45s cubic-bezier(0.2, 1.5, 0.4, 1) both',
                      }}>
                        {diceRollToast.emoji} {diceRollToast.value}{isCrit ? '!' : ''}
                      </div>
                      <div style={{ fontSize: '0.85em', fontWeight: 800, letterSpacing: '2px', color: c, textTransform: 'uppercase' }}>
                        {diceRollToast.band_name}
                      </div>
                      {diceRollToast.line && (
                        <div style={{ marginTop: '8px', fontSize: '0.92em', fontWeight: 600, color: 'rgba(255,255,255,0.92)', lineHeight: 1.4 }}>
                          {diceRollToast.line}
                        </div>
                      )}
                      {diceRollToast.ui && (
                        <div style={{ marginTop: '5px', fontSize: '0.78em', fontStyle: 'italic', color: 'rgba(255,255,255,0.55)', lineHeight: 1.35 }}>
                          {diceRollToast.ui}
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            </React.Fragment>
          );
        })()}

        {/* 🎲 YOUR roll, center stage (whodunnit) — the dock die triggers it, the beat
            plays here: tumble, then the number lands with band + consequence. Non-blocking
            (pointer-events none), unmounts itself; the compact chip above the composer
            keeps the result for the rest of the round. */}
        {ownRollBeat && !isBotMode && (() => {
          const roll = chatGameStatus?.dice?.your_roll;
          if (!roll) return null;
          const landed = ownRollBeat === 'landed';
          const o = getDiceOutcome(roll);
          // Two-act reveal (Nina 07-29): the NUMBER lands alone first, then the verdict
          // (symbol + band) drops in a half-beat later. Punishment verdicts run red,
          // crits gold. Text-presentation glyphs on purpose: emoji ignore CSS color.
          const vBandUp = (roll.band_name || '').toUpperCase();
          const vFail = vBandUp.includes('CRITICAL F') || roll.value === 1;
          const vComp = vBandUp.includes('COMPLICATION');
          const vCrit = !vFail && (vBandUp.includes('CRITICAL') || roll.value === 20);
          const vGlyph = vFail ? '☠︎' : vComp ? '⚠︎' : vCrit ? '★' : '✦';
          const vColor = (vFail || vComp) ? '#ff5252' : vCrit ? '#ffd700' : o.glow;
          return (
            <React.Fragment key={`own-roll-${diceRoundKey}`}>
              <style>{`
                @keyframes ownDiceDim { from { opacity: 0; } 15% { opacity: 1; } 80% { opacity: 1; } to { opacity: 0; } }
                @keyframes ownDiceIn { from { opacity: 0; transform: translate(-50%, calc(-50% - 14px)) scale(0.9); } to { opacity: 1; transform: translate(-50%, -50%) scale(1); } }
                @keyframes ownDiceTumble { 0% { transform: rotate(0deg) scale(1); } 50% { transform: rotate(180deg) scale(1.3); } 100% { transform: rotate(360deg) scale(1); } }
                @keyframes ownDicePop { from { transform: scale(0.2) rotate(-200deg); opacity: 0; } to { transform: scale(1) rotate(0deg); opacity: 1; } }
                @keyframes ownVerdictIn { from { opacity: 0; transform: translateY(-6px); } to { opacity: 1; transform: none; } }
                @media (prefers-reduced-motion: reduce) {
                  .own-roll-beat * { animation-duration: 0.01ms !important; animation-delay: 0ms !important; }
                }
              `}</style>
              <div style={{
                position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 249998, pointerEvents: 'none',
                background: `radial-gradient(circle at 50% 50%, ${o.glow}14 0%, rgba(0,0,0,0.6) 100%)`,
                animation: 'ownDiceDim 4.1s ease both',
              }} />
              <div className="own-roll-beat" style={{
                position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
                zIndex: 249999, pointerEvents: 'none', maxWidth: '92vw', width: '360px',
                padding: '20px 22px', borderRadius: 16, textAlign: 'center',
                background: 'rgba(12,10,16,0.94)', border: `2px solid ${landed ? o.glow : 'rgba(167,139,250,0.5)'}`,
                boxShadow: `0 0 40px ${landed ? o.glow : '#a78bfa'}55, 0 14px 40px rgba(0,0,0,0.6)`,
                animation: 'ownDiceIn 0.3s ease both', transition: 'border-color 0.3s ease, box-shadow 0.3s ease',
              }}>
                <div style={{ fontWeight: 900, letterSpacing: '0.16em', color: '#c4b5fd', fontSize: '0.78em', textTransform: 'uppercase', marginBottom: 10 }}>
                  🎲 {chatGameStatus?.dice?.label || 'FATE ROLL'} · YOU
                </div>
                {landed ? (
                  <>
                    <div style={{ fontSize: '3.2em', fontWeight: 900, lineHeight: 1, color: '#efe6d6', textShadow: '0 0 24px rgba(239,230,214,0.35)', display: 'inline-block', animation: 'ownDicePop 0.45s cubic-bezier(0.2, 1.5, 0.4, 1) both' }}>
                      {roll.value}
                    </div>
                    <div style={{ animation: 'ownVerdictIn 0.4s ease 0.5s both' }}>
                      <div style={{ fontSize: '1.6em', lineHeight: 1.2, marginTop: 4, fontWeight: 900, color: vColor, textShadow: `0 0 18px ${vColor}77` }}>{vGlyph}</div>
                      <div style={{ fontSize: '0.85em', fontWeight: 800, letterSpacing: '2px', color: vColor, textTransform: 'uppercase' }}>{roll.band_name}</div>
                    </div>
                    <div style={{ marginTop: 8, fontSize: '0.92em', fontWeight: 600, color: 'rgba(255,255,255,0.92)', lineHeight: 1.4 }}>{o.line}</div>
                    <span style={{ display: 'inline-block', marginTop: 10, padding: '5px 14px', borderRadius: 14, border: `1.5px solid ${o.glow}`, backgroundColor: `${o.glow}1f`, color: o.glow, fontWeight: 900, fontSize: '0.82em', letterSpacing: '0.5px' }}>
                      {o.chip}
                    </span>
                  </>
                ) : (
                  <>
                    <div style={{ fontSize: '3em', display: 'inline-block', animation: 'ownDiceTumble 0.45s linear infinite' }}>🎲</div>
                    <div style={{ marginTop: 8, fontSize: '0.8em', fontWeight: 800, letterSpacing: '2px', textTransform: 'uppercase', color: 'rgba(255,255,255,0.6)' }}>rolling...</div>
                  </>
                )}
              </div>
            </React.Fragment>
          );
        })()}

        {/* Welcome door beat — non-competitive ENTER clicked; the same 🚪 creak-open
            moment as Whodunnit's Investigate, riding the cinematic blackout/reveal
            phases. Fades with the reveal so the room appears through the doorway. */}
        {(cinematicPhase === 'blackout' || cinematicPhase === 'reveal') &&
          !(chatGameStatus?.competitive_mode || chatGameStatus?.imposter_mode) && (
          <div style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 250000,
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            background: 'radial-gradient(circle, rgba(10,10,16,0.97) 0%, rgba(0,0,0,0.99) 100%)',
            opacity: cinematicPhase === 'reveal' ? 0 : 1,
            transition: 'opacity 1.4s ease 0.4s', pointerEvents: 'none',
          }}>
            <style>{`
              @keyframes welcomeDoorFadeIn { from { opacity: 0; } to { opacity: 1; } }
              @keyframes welcomeDoorCreakOpen { 0% { transform: rotate(0deg); } 60% { transform: rotate(-7deg); } 100% { transform: rotate(-5deg); } }
            `}</style>
            <div style={{ animation: 'welcomeDoorFadeIn 0.3s ease-out both', textAlign: 'center' }}>
              <div style={{ fontSize: '3.4rem', marginBottom: 16, display: 'inline-block', transformOrigin: 'bottom left', animation: 'welcomeDoorCreakOpen 1.6s ease-out both' }}>🚪</div>
              <div style={{ color: 'rgba(255,255,255,0.85)', fontWeight: 700, fontSize: '1.15rem', letterSpacing: '4px', textTransform: 'uppercase' }}>
                Stepping inside...
              </div>
            </div>
          </div>
        )}

        {/* FILE-PULL beat (user, 07-10) — Investigate was clicked, the game is spinning
            up. A swinging interrogation lamp flickers on over escalating file lines;
            the role card (your file, opened) slams in over this and clears it. Lines
            degrade gracefully: a fast role assignment only shows line 1. */}
        {doorTransition && !showGuiltyReveal && (
          <div style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 250000,
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            background: 'radial-gradient(circle, rgba(15,10,8,0.97) 0%, rgba(0,0,0,0.99) 100%)',
            animation: 'filePullFadeIn 0.4s ease-out both', overflow: 'hidden',
          }}>
            <style>{`
              @keyframes filePullFadeIn { from { opacity: 0; } to { opacity: 1; } }
              @keyframes lampSwing { 0%, 100% { transform: rotate(-6deg); } 50% { transform: rotate(6deg); } }
              @keyframes lampFlickerOn {
                0% { opacity: 0; } 8% { opacity: 0.9; } 12% { opacity: 0.1; } 18% { opacity: 0.8; }
                24% { opacity: 0.2; } 34% { opacity: 1; } 100% { opacity: 1; }
              }
              @keyframes fileLineInOut { 0% { opacity: 0; transform: translateY(6px); } 12% { opacity: 1; transform: translateY(0); } 86% { opacity: 1; } 100% { opacity: 0; } }
              @keyframes fileLineIn { 0% { opacity: 0; transform: translateY(6px); } 100% { opacity: 1; transform: translateY(0); } }
            `}</style>
            {/* Swinging lamp — cord + bulb + a soft light cone, flickering on */}
            <div style={{
              position: 'absolute', top: 0, left: '50%', marginLeft: '-1px',
              transformOrigin: 'top center', animation: 'lampSwing 3.4s ease-in-out infinite',
            }}>
              <div style={{ width: '2px', height: '16vh', background: 'rgba(255,255,255,0.22)', margin: '0 auto' }} />
              <div style={{ fontSize: '1.8rem', textAlign: 'center', marginTop: '-4px', animation: 'lampFlickerOn 2.4s linear both' }}>💡</div>
              <div style={{
                width: '340px', height: '46vh', marginLeft: '-170px', marginTop: '-10px',
                background: 'radial-gradient(ellipse 50% 100% at 50% 0%, rgba(255,238,180,0.16) 0%, rgba(255,238,180,0.05) 55%, transparent 75%)',
                animation: 'lampFlickerOn 2.4s linear both', pointerEvents: 'none',
              }} />
            </div>
            {/* Escalating file lines — stacked in one slot, timed hand-off */}
            <div style={{ position: 'relative', marginTop: '18vh', width: '100%', maxWidth: '460px', height: '2.4em', textAlign: 'center', padding: '0 20px' }}>
              {[
                { text: 'The detective pulls your file...', anim: 'fileLineInOut 2.6s ease both' },
                { text: "It's thicker than you expected.", anim: 'fileLineInOut 2.6s ease 2.6s both' },
                { text: 'He looks at you. Then back at the file.', anim: 'fileLineIn 0.5s ease 5.2s both' },
              ].map((l, i) => (
                <div key={i} style={{
                  position: 'absolute', left: 0, right: 0,
                  color: 'rgba(255,255,255,0.85)', fontWeight: 700, fontSize: '1.15rem',
                  letterSpacing: '1.5px', fontStyle: 'italic', animation: l.anim,
                }}>
                  {l.text}
                </div>
              ))}
            </div>
            <div style={{ marginTop: '18px', fontSize: '2rem', animation: 'fileLineIn 0.6s ease 0.3s both' }}>🗂️</div>
            {/* 🎯 The detective slides the bet across the table (Nina 07-31) —
                dealt HERE because this beat is idle time with zero competing
                CTAs; accepting happens later, from the case file. The element
                mounts whenever the seeded bet lands on a status poll mid-beat,
                so its delay is short and relative to itself; a fast role
                assignment or a slow seed simply never shows it (the case-file
                card is the durable copy). */}
            {!isBotMode && chatGameStatus?.your_bet?.status === 'offered' && (
              <div style={{
                marginTop: '26px', padding: '10px 16px', maxWidth: '420px',
                background: 'linear-gradient(175deg, rgba(238,229,205,0.95), rgba(212,200,172,0.95))',
                color: '#241d14', borderRadius: 4, transform: 'rotate(-1.2deg)',
                boxShadow: '0 10px 30px rgba(0,0,0,0.6)',
                animation: 'fileLineIn 0.5s ease 0.8s both', textAlign: 'center',
              }}>
                <div style={{ fontSize: '0.6em', fontWeight: 800, letterSpacing: '0.16em' }}>🎯 THE DETECTIVE BETS YOU CAN'T</div>
                <div style={{ marginTop: 4, fontWeight: 700, fontSize: '0.95em' }}>
                  {chatGameStatus.your_bet.text}. +{chatGameStatus.your_bet.points}.
                </div>
                <div style={{ marginTop: 3, fontSize: '0.68em', opacity: 0.7 }}>The card waits in your case file.</div>
              </div>
            )}
          </div>
        )}

        {/* Whodunnit guilty-role reveal — a 3-beat cold open (user direction 07-08:
            walk players in cinematically): night-of-the-crime rain → LIGHTNING strobe +
            "A BODY HAS TURNED UP!" → your role card. Killer sees the kill card; innocents
            see the case-file briefing. Tap skips ahead; tap on the card enters the room. */}
        {showGuiltyReveal && (
          <div onClick={() => {
            // Victim beat (Nina 07-30): a tap from the slam stops at the
            // obituary once before moving on — the punchline is the point.
            const hasVictimBeat = !!chatGameStatus?.scenario?.victim?.name;
            // Atmospheric run has no role card — a tap just skips into the room.
            if (showGuiltyReveal.atmospheric) {
              if (hasVictimBeat && guiltyRevealPhase === 'body') { setGuiltyRevealPhase('victim'); return; }
              coldOpenPlayedRef.current = true;
              setShowGuiltyReveal(null);
              return;
            }
            if (guiltyRevealPhase === 'briefing') { setShowGuiltyReveal(null); return; }
            if (hasVictimBeat && (guiltyRevealPhase === 'night' || guiltyRevealPhase === 'body')) setGuiltyRevealPhase('victim');
            else if (guiltyRevealPhase !== 'role') setGuiltyRevealPhase('role');
            else if (!showGuiltyReveal.didIt && showGuiltyReveal.caseFile) setGuiltyRevealPhase('briefing');
            else setShowGuiltyReveal(null);
          }} style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 300000,
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            background: 'radial-gradient(circle, rgba(15,10,8,0.98) 0%, rgba(0,0,0,0.99) 100%)',
            animation: 'guiltyRevealFadeIn 0.5s ease-out', overflow: 'hidden', cursor: 'pointer',
          }}>
            <style>{`
              @keyframes guiltyRevealFadeIn { from { opacity: 0; } to { opacity: 1; } }
              @keyframes guiltyRevealAppear { from { opacity: 0; transform: scale(0.92); } to { opacity: 1; transform: scale(1); } }
              @keyframes guiltyBulbFlicker { 0%, 100% { opacity: 1; } 92% { opacity: 1; } 93% { opacity: 0.4; } 95% { opacity: 1; } 97% { opacity: 0.6; } }
              @keyframes guiltyRainFall { from { transform: translateY(-18vh); } to { transform: translateY(118vh); } }
              @keyframes guiltyLightningStrobe {
                0% { opacity: 0; } 3% { opacity: 0.95; } 6% { opacity: 0.12; } 9% { opacity: 0.75; } 14% { opacity: 0; }
                33% { opacity: 0; } 36% { opacity: 0.85; } 40% { opacity: 0.15; } 44% { opacity: 0.6; } 52% { opacity: 0; }
                100% { opacity: 0; }
              }
              @keyframes guiltyThunderJolt {
                0%, 100% { transform: translate(0, 0); } 8% { transform: translate(-7px, 4px); }
                18% { transform: translate(6px, -5px); } 30% { transform: translate(-5px, 2px); }
                45% { transform: translate(3px, -2px); } 65% { transform: translate(-2px, 1px); }
              }
              @keyframes guiltyBodySlamIn {
                from { opacity: 0; transform: scale(1.65); letter-spacing: 14px; }
                to { opacity: 1; transform: scale(1); letter-spacing: 3px; }
              }
              @keyframes guiltyFadeUp { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
            `}</style>
            {/* Rain — falls through every phase (deterministic per-index values so phase
                re-renders don't make the streaks jump) */}
            {Array.from({ length: 18 }).map((_, i) => (
              <span key={`rain-${i}`} style={{
                position: 'absolute', top: 0, left: `${(i * 97 + 13) % 100}%`,
                width: '1px', height: `${60 + (i % 4) * 14}px`, pointerEvents: 'none',
                background: 'linear-gradient(to bottom, transparent, rgba(190,215,255,0.38))',
                opacity: 0.5,
                animation: `guiltyRainFall ${(0.9 + (i % 4) * 0.22).toFixed(2)}s linear ${((i % 6) * 0.31).toFixed(2)}s infinite`,
              }} />
            ))}
            {/* Beat 1: the night of the crime */}
            {guiltyRevealPhase === 'night' && (
              <div style={{ position: 'absolute', textAlign: 'center' }}>
                {/* clamp() over isMobile (Nina 07-29: desktop read as billboard-huge) */}
                <div style={{ fontSize: 'clamp(18px, 3.2vw, 24px)', fontWeight: 700, color: 'rgba(255,255,255,0.65)', letterSpacing: '5px', textTransform: 'uppercase', animation: 'guiltyBulbFlicker 2s linear infinite' }}>
                  The night of the crime...
                </div>
                <div style={{ marginTop: '14px', fontSize: '0.9em', color: 'rgba(255,255,255,0.35)', letterSpacing: '2px' }}>
                  3AM. THE PRECINCT.
                </div>
              </div>
            )}
            {/* Beat 2: lightning + the crime lands */}
            {guiltyRevealPhase === 'body' && (
              <>
                <div style={{
                  position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, pointerEvents: 'none',
                  background: 'radial-gradient(circle at 50% 0%, rgba(255,255,255,0.98) 0%, rgba(185,205,255,0.85) 45%, rgba(120,140,200,0.45) 100%)',
                  opacity: 0, animation: 'guiltyLightningStrobe 1.5s linear forwards',
                }} />
                <div style={{ position: 'absolute', textAlign: 'center', padding: '0 20px', animation: 'guiltyThunderJolt 0.55s ease 0.05s' }}>
                  <div style={{
                    fontSize: 'clamp(26px, 4.6vw, 40px)', fontWeight: 900, color: '#fff',
                    letterSpacing: '3px', textTransform: 'uppercase', lineHeight: 1.15,
                    maxWidth: 'min(88vw, 720px)', marginLeft: 'auto', marginRight: 'auto',
                    textShadow: '0 0 28px rgba(255,60,60,0.85), 0 0 64px rgba(255,0,0,0.45)',
                    animation: 'guiltyBodySlamIn 0.32s cubic-bezier(0.2, 1.4, 0.4, 1) 0.12s both',
                  }}>
                    {chatGameStatus?.scenario?.cold_open?.headline || 'A BODY HAS TURNED UP!'}
                  </div>
                  <div style={{
                    marginTop: '22px', fontSize: 'clamp(15px, 2.2vw, 18px)', fontWeight: 700,
                    color: 'rgba(255,255,255,0.9)', letterSpacing: '1.5px',
                    animation: 'guiltyFadeUp 0.5s ease 0.75s both',
                  }}>
                    {chatGameStatus?.scenario?.cold_open?.sub || 'YOU are a suspect.'}
                  </div>
                </div>
              </>
            )}
            {/* Beat 2b: the victim's obituary — its OWN beat (Nina 07-30: four
                text blocks on the slam read as a wall; the obituary is the joke
                and jokes read better solo). Newsprint clipping, serif on paper,
                so the pack fiction reads as an artifact. Age gets its own quiet
                line — authored ages run long ("3 days, 14 hours... old"). */}
            {guiltyRevealPhase === 'victim' && chatGameStatus?.scenario?.victim?.name && (
              <div style={{ position: 'absolute', textAlign: 'center', padding: '0 20px', animation: 'guiltyRevealAppear 0.45s ease both' }}>
                <div style={{
                  display: 'inline-block', padding: '20px 26px 18px', transform: 'rotate(-1.6deg)',
                  background: 'linear-gradient(175deg, rgba(238,229,205,0.97) 0%, rgba(212,200,172,0.97) 100%)',
                  color: '#241d14', maxWidth: 'min(86vw, 480px)', textAlign: 'center',
                  boxShadow: '0 12px 40px rgba(0,0,0,0.65)',
                  fontFamily: 'Georgia, "Times New Roman", serif',
                }}>
                  <div style={{
                    fontSize: '0.66em', fontWeight: 700, letterSpacing: '4px', textTransform: 'uppercase',
                    color: 'rgba(36,29,20,0.55)', borderBottom: '1px solid rgba(36,29,20,0.35)',
                    paddingBottom: '6px', marginBottom: '12px',
                  }}>
                    Obituary
                  </div>
                  <div style={{ fontSize: 'clamp(18px, 2.6vw, 24px)', fontWeight: 800, letterSpacing: '0.5px' }}>
                    {chatGameStatus.scenario.victim.emoji ? `${chatGameStatus.scenario.victim.emoji} ` : ''}
                    {chatGameStatus.scenario.victim.name}
                  </div>
                  {chatGameStatus.scenario.victim.age && (
                    <div style={{ marginTop: '4px', fontSize: 'clamp(11px, 1.5vw, 13px)', fontStyle: 'italic', color: 'rgba(36,29,20,0.6)' }}>
                      {chatGameStatus.scenario.victim.age}
                    </div>
                  )}
                  {chatGameStatus.scenario.victim.obituary && (
                    <div style={{ marginTop: '12px', fontSize: 'clamp(13px, 1.8vw, 15px)', lineHeight: 1.55, color: 'rgba(36,29,20,0.85)' }}>
                      {chatGameStatus.scenario.victim.obituary}
                    </div>
                  )}
                </div>
              </div>
            )}
            {/* Skip hint on the cinematic beats only. The obituary reads
                "continue", not "skip" (Nina 07-30): that beat now waits for the
                tap, so inviting people to skip the thing we just asked them to
                read was working against itself. It is also brighter there —
                on the timed beats the hint is an escape hatch, on the obituary
                it is the actual control. */}
            {(guiltyRevealPhase === 'night' || guiltyRevealPhase === 'body' || guiltyRevealPhase === 'victim') && (
              <div style={{
                position: 'absolute', bottom: '26px', fontSize: '0.7em',
                color: guiltyRevealPhase === 'victim' ? 'rgba(255,255,255,0.62)' : 'rgba(255,255,255,0.3)',
                letterSpacing: '2px', textTransform: 'uppercase',
              }}>
                {guiltyRevealPhase === 'victim' ? 'Tap to continue' : 'Tap to skip'}
              </div>
            )}
            {/* Beat 3: role card */}
            {guiltyRevealPhase === 'role' && (
            <div style={{ animation: 'guiltyRevealAppear 0.7s ease-out both', textAlign: 'center', padding: '0 24px', maxWidth: '540px' }}>
              {showGuiltyReveal.didIt ? (
                <>
                  <div style={{ fontSize: isMobile ? '2.6em' : '3.6em', marginBottom: '6px' }}>{chatGameStatus?.scenario?.victim?.emoji || '🔪'}</div>
                  <div style={{ fontSize: isMobile ? '1.7em' : '2.3em', fontWeight: 900, color: '#ff4444', letterSpacing: '2px', textShadow: '0 0 24px rgba(255,0,0,0.5)' }}>
                    {chatGameStatus?.scenario ? 'YOU DID IT' : 'YOU ARE THE KILLER'}
                  </div>
                  {/* Nina's authored copy, 07-26 (copy audit A4) — verbatim, don't tighten */}
                  <div style={{ marginTop: '14px', fontSize: isMobile ? '0.95em' : '1.05em', color: 'rgba(255,255,255,0.85)', lineHeight: 1.55 }}>
                    You did it! Now deny everything.
                  </div>
                  <div style={{ marginTop: '8px', fontSize: isMobile ? '0.95em' : '1.05em', color: 'rgba(255,255,255,0.85)', lineHeight: 1.55 }}>
                    The others got a briefing you never saw. Nod along like you read it.
                  </div>
                  <div style={{ marginTop: '8px', fontSize: isMobile ? '0.95em' : '1.05em', color: 'rgba(255,255,255,0.7)', fontStyle: 'italic', lineHeight: 1.55 }}>
                    Never confess. Pin it on someone.
                  </div>
                </>
              ) : (
                <>
                  <div style={{ fontSize: isMobile ? '2.6em' : '3.6em', marginBottom: '6px' }}>🔍</div>
                  <div style={{ fontSize: isMobile ? '1.7em' : '2.3em', fontWeight: 900, color: '#ffd54f', letterSpacing: '2px', textShadow: '0 0 24px rgba(255,200,0,0.35)' }}>
                    YOU ARE INNOCENT
                  </div>
                  <div style={{ marginTop: '14px', fontSize: isMobile ? '0.95em' : '1.05em', color: 'rgba(255,255,255,0.85)', lineHeight: 1.55 }}>
                    But someone at this table <b>actually did it.</b>
                  </div>
                  <div style={{ marginTop: '8px', fontSize: isMobile ? '0.95em' : '1.05em', color: 'rgba(255,255,255,0.7)', fontStyle: 'italic', lineHeight: 1.55 }}>
                    ...and they're lying for their life...
                  </div>
                </>
              )}
              {(!showGuiltyReveal.didIt && showGuiltyReveal.caseFile) ? (
                <button
                  onClick={(e) => { e.stopPropagation(); setGuiltyRevealPhase('briefing'); }}
                  style={{
                    marginTop: '26px', padding: '13px 40px', borderRadius: '22px', border: 'none',
                    backgroundColor: '#ffd54f', textShadow: 'none', color: '#1a1205', fontWeight: 800,
                    fontSize: '1.05em', letterSpacing: '1.5px', textTransform: 'uppercase',
                    cursor: 'pointer', boxShadow: '0 0 24px rgba(255,213,79,0.45)',
                  }}
                >
                  Next ▸
                </button>
              ) : (
                <div style={{ marginTop: '22px', fontSize: '0.8em', color: 'rgba(255,255,255,0.45)', letterSpacing: '2px', textTransform: 'uppercase' }}>
                  Tap anywhere to enter the interrogation room
                </div>
              )}
            </div>
            )}
            {/* Beat 4 (innocents only): the police briefing gets its own page — one clear
                heading, the secret, and how to play it (user, 07-09: pace the reading). */}
            {guiltyRevealPhase === 'briefing' && showGuiltyReveal.caseFile && (
            <div style={{ animation: 'guiltyRevealAppear 0.5s ease-out both', textAlign: 'center', padding: '0 24px', maxWidth: '540px' }}>
              <div style={{ fontSize: isMobile ? '2.6em' : '3.6em', marginBottom: '6px' }}>🗂️</div>
              {/* Order (Nina 07-26): title → THE FACT → who-got-it → how to
                  use it. The secret reads first; the meta lines frame it after. */}
              <div style={{ fontSize: isMobile ? '1.5em' : '2em', fontWeight: 900, color: '#ffd54f', letterSpacing: '2px', textShadow: '0 0 24px rgba(255,200,0,0.35)' }}>
                THE POLICE BRIEFING
              </div>
              <div style={{ marginTop: '16px', padding: '14px 18px', borderRadius: '10px', border: '1px solid rgba(255,213,79,0.4)', backgroundColor: 'rgba(255,213,79,0.08)' }}>
                <div style={{ fontSize: isMobile ? '1.02em' : '1.12em', color: '#fff', fontStyle: 'italic', lineHeight: 1.55 }}>{showGuiltyReveal.caseFile}</div>
              </div>
              {/* Nina's authored copy, 07-26 (copy audit A6) — verbatim, don't tighten */}
              <div style={{ marginTop: '14px', fontSize: isMobile ? '0.9em' : '1em', color: 'rgba(255,255,255,0.75)', lineHeight: 1.5 }}>
                {(() => { const c = chatGameStatus?.scenario?.culprit_noun || 'the killer'; return c.charAt(0).toUpperCase() + c.slice(1); })()} doesn't know this. <b>Watch whose story ignores it.</b>
              </div>
              <button
                onClick={(e) => { e.stopPropagation(); setShowGuiltyReveal(null); }}
                style={{
                  marginTop: '24px', padding: '13px 36px', borderRadius: '22px', border: 'none',
                  backgroundColor: '#ffd54f', textShadow: 'none', color: '#1a1205', fontWeight: 800,
                  fontSize: '1em', letterSpacing: '1.5px', textTransform: 'uppercase',
                  cursor: 'pointer', boxShadow: '0 0 24px rgba(255,213,79,0.45)',
                }}
              >
                🚪 Enter the interrogation room
              </button>
            </div>
            )}
          </div>
        )}

        {/* 🎓 Tutorial quest card (07-15) — replaces dead tutorial toasts.
            Mobile (audit P0, 07-16): starts as a chip and hides while typing
            so it can never sit on the composer or the video controls. */}
        {tutorialQuestVisible && !tutorialActive && !(isMobile && typingFocus) && (() => {
          // 🎓 v2: one task at a time (shared step data w/ the host whisper)
          const { steps, step, stepNum } = tutorialStepInfo;
          if (!step) return null;
          const dismissTutorial = (e) => { e.stopPropagation(); skipTutorial(); };
          // Desktop pill lives centered ABOVE THE COMPOSER (Nina 08-03: the old
          // bottom-left anchor floated over the sidebar's Locals cards) — the
          // quest is about typing things, so the reminder sits where you type.
          // 🏡📐 wave 3 (ruling 32): on a phone's place page the pill sits UNDER the
          // slim bar (it must not collide with the bar's ⋯ or the ♫ beside it)
          // 🏡 ruling 34 (Nina 09-26): on a desktop place page the pill sits UNDER the
          // slim bar at the frame's right edge (on the painting), never over the chat
          const anchor = isMobile
            ? (portedPlaceOn ? { top: '56px', right: '12px' } : { top: '12px', right: '52px' })
            : (portedPlaceOn ? { top: '66px', right: `${(Number(placeBarInset) || 10) + 12}px` } : { left: '50%', transform: 'translateX(-50%)', bottom: '96px' });
          return questCollapsed ? (
            <div
              role="button"
              onClick={() => setQuestCollapsed(false)}
              aria-label="Show tutorial step"
              style={{
                position: 'fixed', ...anchor, zIndex: 9000,
                display: 'flex', alignItems: 'center', gap: '6px',
                maxWidth: isMobile ? '54vw' : '270px',
                borderRadius: '999px', padding: '6px 10px',
                background: 'rgba(18,14,34,0.94)', border: '1px solid rgba(255,213,79,0.35)',
                boxShadow: '0 4px 16px rgba(0,0,0,0.45)', color: '#fff',
                fontSize: '0.78em', fontWeight: 700, cursor: 'pointer',
              }}
            >
              <span>{step.emoji}</span>
              {/* 📱 08-04 (Nina screenshot): the 54vw pill ellipsized the one
                  thing the step needs read — the @target ("Gift something
                  to @…"). Phones clamp to TWO lines instead of one. */}
              <span style={isMobile ? {
                overflow: 'hidden', textOverflow: 'ellipsis',
                display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
              } : { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{step.label}</span>
              <span style={{ color: '#ffd54f', fontWeight: 800, flexShrink: 0 }}>{stepNum}/{tutorialStepInfo.steps.length}</span>
              <button
                onClick={dismissTutorial}
                aria-label="Skip tutorial"
                style={{
                  background: 'none', border: 'none', color: 'rgba(255,255,255,0.45)',
                  cursor: 'pointer', fontSize: '0.9em', padding: '0 0 0 2px', lineHeight: 1, flexShrink: 0,
                }}
              >✕</button>
            </div>
          ) : (
            <div style={{
              position: 'fixed', ...anchor, zIndex: 9000,
              width: '225px', borderRadius: '12px', padding: '10px 12px',
              background: 'rgba(18,14,34,0.94)', border: '1px solid rgba(255,213,79,0.35)',
              boxShadow: '0 6px 24px rgba(0,0,0,0.45)', color: '#fff',
            }}>
              <button
                onClick={() => setQuestCollapsed(true)}
                aria-label="Minimize tutorial"
                style={{
                  position: 'absolute', top: '6px', right: '26px', background: 'none', border: 'none',
                  color: 'rgba(255,255,255,0.45)', cursor: 'pointer', fontSize: '0.8em', padding: '2px',
                }}
              >▾</button>
              <button
                onClick={dismissTutorial}
                aria-label="Skip tutorial"
                style={{
                  position: 'absolute', top: '6px', right: '8px', background: 'none', border: 'none',
                  color: 'rgba(255,255,255,0.45)', cursor: 'pointer', fontSize: '0.8em', padding: '2px',
                }}
              >✕</button>
              <div style={{ fontSize: '0.62em', fontWeight: 800, letterSpacing: '1.5px', color: '#ffd54f', textTransform: 'uppercase', marginBottom: '7px' }}>
                🎓 Little quest · {stepNum}/{tutorialStepInfo.steps.length}
              </div>
              <div style={{ fontSize: '0.82em', fontWeight: 700, marginBottom: '5px' }}>
                {step.emoji} {step.label}
              </div>
              <div style={{ fontSize: '0.72em', color: 'rgba(255,255,255,0.65)', lineHeight: 1.4, marginBottom: '7px' }}>
                {step.hint}
              </div>
              {/* 👀 Re-run just this task's tour beat — the spotlight walks
                  you to the exact control and stays live while you do it */}
              <button
                onClick={() => {
                  const i = tutorialSteps.findIndex(s => s.taskKey === step.k);
                  setQuestCollapsed(true);
                  startTutorial(i >= 0 ? i : 0);
                }}
                style={{
                  display: 'block', width: '100%', marginBottom: '8px', padding: '6px 0',
                  borderRadius: '8px', border: '1px solid rgba(255,213,79,0.4)',
                  background: 'rgba(255,213,79,0.12)', color: '#ffd54f',
                  fontSize: '0.72em', fontWeight: 800, cursor: 'pointer',
                }}
              >👀 Show me</button>
              <div style={{ display: 'flex', gap: '5px', alignItems: 'center' }}>
                {steps.map(s => (
                  <span key={s.k} style={{
                    width: '7px', height: '7px', borderRadius: '50%',
                    background: tutorial[s.k] ? 'rgba(126,231,135,0.9)' : (s.k === step.k ? '#ffd54f' : 'rgba(255,255,255,0.2)'),
                  }} />
                ))}
              </div>
            </div>
          );
        })()}
        {/* 🎐 Living background (Arc 1 layers 3+4, Nina 07-19: on by default):
            full-viewport ambient canvas for non-comp spaces — theme-matched to
            the audio bed so sight and sound agree. pointerEvents:none;
            reduced-motion / tab-hidden / offscreen gates live inside the
            component (loop STOPS, not skips). Skipped during the interstitial
            overlay (invisible behind it anyway). */}
        {ambientTheme && !showAssetLoadingOverlay && (() => {
          // 🏡 W2d: a place page's painting is the stage, never wallpaper: the
          // theme's particles live in the margins beside the frame, or not at all
          let placeField = null;
          if (portedPlaceOn && !isMobile) {
            const vw = typeof window !== 'undefined' ? window.innerWidth : 0;
            const fw = placeFrame ? placeFrame.frameW : 0;
            const x0 = (vw - fw) / 2 - LANE_FEATHER; const x1 = (vw + fw) / 2 + LANE_FEATHER;
            if (!fw || Math.max(x0, vw - x1) < 90) return null;
            placeField = { lane: { x0, x1 } };
          }
          // 🪧 09-19 (Nina: particles in the page margins): over a room's real
          // art the resting look steps low → med, but ONLY when it can be
          // fenced off the chat column. roomLaneRect exists exactly when art
          // is the wallpaper on a desktop, so phones, treated rooms and every
          // other space get { field: null } = the layer as it always was.
          // Live scenes, events and weather are never moved (resting only).
          const restingRoom = liveScene ? null : roomSceneInMargins(
            roomSceneFallback(houseStatus?.weather?.look, viewerRoom, houseStatus?.circadian?.clock?.hour, houseStatus?.circadian?.clock?.day),
            roomCameraOn ? roomLaneRect : null,
            typeof window !== 'undefined' ? window.innerWidth : 0,
          );
          return (
          <div style={{ position: 'fixed', inset: 0, zIndex: 0, pointerEvents: 'none' }}>
            <AmbientLayer theme={ambientTheme} accent={THEME.accent || null}
              field={placeField || (restingRoom ? restingRoom.field : null)}
              scene={liveScene ? {
                atmosphere: liveScene.look.atmosphere,
                intensity: liveScene.look.intensity,
                // 🌠 09-14: shooters only during a meteor/comet EVENT — never
                // on the resting stars wallpaper, never on a plain /scene.
                streaks: !!(liveScene.isEvent && (liveScene.template === 'meteor_shower' || liveScene.template === 'comet_watch')),
              // 🌦️ Wave 2 (AMBIENCE_TRIO §2, ruled 09-15 full-room): the
              // weather almanac is the THIRD, lowest-precedence source of the
              // SAME scene prop — a live /scene or event look always wins the
              // one particle slot, and weather resumes when they end. The
              // backend sends house.weather only when HOUSE_WEATHER_ENABLED
              // and the day is non-clear, so this is payload-gated + additive
              // (unknown atmospheres drop inside AmbientLayer either way).
              // 🚪 Wave 4: the fallback is room-aware — weather boosts to
              // med outdoors, and a clear roof night earns resting stars
              // (streaks stay event-only). Pure fn, tested in roomView.test.
              // 📱 09-16 (Nina: full mobile lane): the phone rides the camera
              // too — resting room particles are 20fps-capped (resting:true),
              // so the cost story matches the weather particles already live
              // on phones. The over-FILMS (HouseRoomView) stay desktop-only.
              } : restingRoom.scene} />
          </div>
          );
        })()}
        {/* 🕯️⛈️ 09-20 storm night (Nina's picks): while the house event
            storm_night is LIVE the page goes to candlelight (ONE film) and
            thunder rolls every few minutes. liveEvent already bounds it by the
            wrap card + a 3h ceiling; bots get the look, never the sound. */}
        <StormNightVeil active={!showAssetLoadingOverlay && liveEvent?.template === 'storm_night'} silent={isBotMode} />
        {/* 🌦️🕯️ Wave 1 (HOUSE_AMBIENCE_TRIO_SPEC_2026_09_15 §3+§4, RULED
            09-15): day-part wash + ambient TV-glow spill. Payload-gated —
            renders ONLY when the house block ships a circadian clock
            (house-v2), so every other space is byte-identical; the glow
            additionally needs house.tv.on (the Lane C backend rider) and
            desktop. Same fixed z0 level as AmbientLayer above; CSS-only,
            zero per-frame JS. Skipped during the interstitial overlay for
            the same invisible-behind-it reason. */}
        {houseStatus?.circadian?.clock && !showAssetLoadingOverlay && (
          <HouseAmbientLayers
            hour={houseStatus.circadian.clock.hour}
            tv={houseStatus.tv}
            isMobile={isMobile}
            // 🖼️ art on the wallpaper → the wash whispers (haze lesson)
            washScale={roomArt?.wallpaper ? WASH_OVER_ART : 1}
          />
        )}
        {/* 🚪📷 The per-room backdrop (ROOM_CAMERA_SPEC §4; full recipe set
            09-15 pm — every room a distinct place, alpha-capped because
            these layers paint OVER the content like AmbientLayer). Null for
            the main room (the banner art IS the house view). Films stay
            DESKTOP-ONLY on purpose even under the 09-16 full mobile lane:
            over-films on a phone cover the only column of text there is
            (the FILM_CAP lesson) — the phone's room feel comes from the
            masthead card + veil + particles + toast instead. */}
        {/* 🖼️ 09-17/18: when the room's REAL art is the wallpaper the films
            step aside — the art carries its own lamp pool and shadow, and
            stacking the recipe on top read as fog (Nina's "a bit dim").
            Treat/takeover rooms (no art) keep their films. */}
        {roomCameraOn && !isMobile && !showAssetLoadingOverlay && !roomArt?.wallpaper && (
          <HouseRoomView roomId={viewerRoom} hour={houseStatus?.circadian?.clock?.hour} />
        )}
        {/* 📚 the House library: the phone page (fixed, over the chat) + the Shelf sheet */}
        {libraryPhoneNode()}
        {libraryShelfNode()}
        {/* 📱🧭 PLACES LAYER (spec 2026-07-31, P0-P3 + Locals v0): TV, Locals,
            and Arcade render as a fixed layer OVER the chat scroller — Chat
            underneath keeps its scroll, lane filter, and composer untouched
            (spec lock: tabs preserve state). Layer sits at z4600: UNDER the
            ghost edge tab (4800 — ghosts ride every place, a layer not a tab),
            the bottom nav (5100), and every overlay above those. The theater
            iframe arrives via wpTabSlotRef + the reparent effect: hopping
            places parks it in the hidden holder, audio keeps playing, never a
            remount. Livestream node does the same via its new hidden holder. */}
        {/* 📱 08-01 P0 fixes (audit round 2 #1/#7): the !typingFocus gate is
            GONE — it unmounted this layer when you focused an input INSIDE it
            (YT-link box, Arcade restore-code), killing the screen mid-action
            and parking the theater iframe. The nav still hides while typing;
            the layer must not. Bottom is 53px to meet the dock/nav seam — the
            old 48px hid 5px of layer content behind the opaque nav. */}
        {placesNav && !investigationTakeover && (mobileView === 'tv' || mobileView === 'arcade') && (
          <div data-swipe-place="" style={{
            position: 'fixed', top: 0, left: 0, right: 0,
            bottom: NAV_BOTTOM,
            zIndex: 4600, overflowY: 'auto', WebkitOverflowScrolling: 'touch',
            background: 'rgba(10,10,16,0.98)', // no blur: this layer CONTAINS the video/theater — backdrop-filter forced a full-viewport re-blur every video frame (perf 08-13)
            padding: '12px 12px 24px',
          }}>
            <div style={{
              display: 'flex', alignItems: 'center', gap: 8, padding: '2px 4px 10px',
              fontSize: '0.72em', fontWeight: 800, letterSpacing: '1.4px',
              textTransform: 'uppercase', color: THEME.textMuted || '#aaa',
            }}>
              {mobileView === 'tv' ? (spaceLiveStreamers.length > 0 ? '🔴 Live' : '📺 The TV')
                : '🎮 The Arcade'}
            </div>
            {mobileView === 'tv' && (() => {
              // 🔴 Live-first ordering (08-05): the tab label flips to "Live",
              // so what the tap lands on must BE the live stream — CSS order
              // lifts the stage above the watch-party chrome without moving
              // DOM nodes (the reparent slots must never remount mid-stream).
              // A seated theater still leads: sitting down was deliberate.
              const liveOnTv = !isBotMode && spaceLiveStreamers.length > 0;
              return (<div style={{ display: 'flex', flexDirection: 'column' }}>
              {/* Party first: the slot below receives the theater via the
                  portal. Gated on inTheater — the un-seated placesNav case
                  portals null (the launcher card owns that CTA), so the bare
                  slot was a stray empty spacer above the launcher. */}
              {watchParty && inTheater && (
                <div ref={wpTabSlotRef} style={{ display: 'flex', flexDirection: 'column', minHeight: 0, marginBottom: 10, order: liveOnTv ? -2 : 0 }} />
              )}
              {/* 🛋️ 09-23 batch 1 (handoff §3.4): the couch under the film on phones, screenings only */}
              {COUCH && watchParty && inTheater && watchParty.screening && (
                <div data-couch-phone="1" style={{ marginBottom: 10, order: liveOnTv ? -1 : 0 }}>{couchNode('phone')}</div>
              )}
              {/* Ambient pick (D3): the host's muted programming IS the TV
                  playing — it outranks the launcher. */}
              {!watchParty && !ambientHidden && chatGameStatus?.pinned_pick?.type === 'youtube' && chatGameStatus?.pinned_pick?.videoId && (() => {
                const pick = shownPinnedPick || chatGameStatus.pinned_pick;
                const chipStyle = {
                  cursor: 'pointer', border: '1px solid #9c9c9c55',
                  backgroundColor: 'transparent', color: THEME.textMuted || '#aaa',
                  fontSize: '0.68em', fontWeight: 700, padding: '6px 12px', borderRadius: 999,
                };
                return (
                  <div style={{ marginBottom: 10, display: 'flex', flexDirection: 'column' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '2px 4px 6px' }}>
                      <span style={{ fontSize: '0.66em', fontWeight: 800, letterSpacing: '1.2px', textTransform: 'uppercase', color: THEME.textMuted || '#aaa', ...(pick.pick_label ? { display: 'inline-flex', alignItems: 'center', gap: 5 } : {}) }}>
                        {pick.pick_label ? (
                          <>
                            {pick.by_image && (
                              <img src={pick.by_image} alt="" data-pick-face=""
                                style={{ width: 16, height: 16, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }}
                                onError={(e) => { e.target.style.display = 'none'; }} />
                            )}
                            {pick.pick_label}
                          </>
                        ) : <>{pick.host_emoji || '🎙️'} {pick.host_name || 'The Host'}&rsquo;s pick</>}
                      </span>
                      <button
                        onClick={() => window.dispatchEvent(new CustomEvent('watchparty:request', { detail: { url: pick.url } }))}
                        title="Start a synced watch party with this"
                        style={{ ...chipStyle, borderColor: `${THEME.accent || '#7b1fa2'}90`, color: THEME.textBright || '#fff' }}
                      >
                        🍿 Make it a watch party
                      </button>
                      <button onClick={hideAmbient} title="Hide the ambient video for this visit" style={{ ...chipStyle, marginLeft: 'auto' }}>
                        ✕ hide
                      </button>
                    </div>
                    <div style={{ position: 'relative', width: '100%', aspectRatio: '16 / 9', maxHeight: '34dvh', borderRadius: 10, overflow: 'hidden', backgroundColor: '#000' }}>
                      {(!isLowResourceDevice || ambientTapStarted) ? (
                        <iframe
                          src={`https://www.youtube.com/embed/${pick.videoId}?autoplay=1&mute=1&loop=1&playlist=${pick.videoId}&modestbranding=1&rel=0`}
                          title={pick.title || 'On the TV'}
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                          style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 0 }}
                        />
                      ) : (
                        <div
                          role="button"
                          onClick={() => setAmbientTapStarted(true)}
                          style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 6, cursor: 'pointer', color: THEME.textMuted || '#aaa' }}
                        >
                          <span style={{ fontSize: '2em' }}>▶</span>
                          <span style={{ fontSize: '0.72em', fontWeight: 700 }}>Tap to turn on the TV</span>
                          {pick.title && <span style={{ fontSize: '0.64em', opacity: 0.8, maxWidth: '85%', textAlign: 'center', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{pick.title}</span>}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })()}
              {/* Launcher wearing the MERGED-CARD chrome (mock v3): ON THE TV
                  header, emoji-free CTAs, + Queue beside the seat, + Add on
                  the queue header. Dark screen = the launcher-first state. */}
              <WatchPartyLauncher
                watchParty={watchParty}
                history={chatGameStatus?.watch_party_history}
                mediaSuggestions={chatGameStatus?.media_suggestions}
                pinnedPick={shownPinnedPick}
                inTheater={inTheater}
                onTakeSeat={() => setInTheater(true)}
                humanPlayerId={humanPlayerId}
                THEME={THEME}
                isMobile={isMobile}
                tvChrome
                hideIdleHero={(!watchParty && !ambientHidden
                  && chatGameStatus?.pinned_pick?.type === 'youtube' && !!chatGameStatus?.pinned_pick?.videoId)
                  // 🔴 someone IS live: "Nothing on right now" directly above
                  // a live human read as a lie — the lifted stage says what's on
                  || spaceLiveStreamers.length > 0}
              />
              {/* THE STAGE — livestreams; the page-wide panel's host node
                  reparents into this slot while the TV place is open. The
                  roster inside stays on TV (Nina 08-01). */}
              {!isBotMode && (
                <div style={{ fontSize: '0.66em', fontWeight: 800, letterSpacing: '1.2px', textTransform: 'uppercase', color: THEME.textMuted || '#aaa', padding: liveOnTv ? '2px 4px 0' : '14px 4px 0', order: liveOnTv ? -1 : 0 }}>
                  The stage
                </div>
              )}
              {!isBotMode && (
                <div role="tabpanel" data-tab-name="livestream" style={{
                  display: 'flex', flexDirection: 'column', marginTop: 6,
                  order: liveOnTv ? -1 : 0,
                  marginBottom: liveOnTv ? 12 : 0,
                  // Height floor ALWAYS (08-05): idle — exactly the state
                  // where someone wants to START a stream — used to collapse
                  // the Go Live CTAs into a heightless box (the narrow-desktop
                  // slot documents this same failure; the fix never made it
                  // here). Idle stays shorter than a live stage.
                  ...(spaceLiveStreamers.length > 0
                    ? { height: '52dvh', minHeight: 300 }
                    : { height: '38dvh', minHeight: 260 }),
                }}>
                  <div ref={livestreamTabSlotRef} style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }} />
                </div>
              )}
              {/* Shared media grid absorbs the old Media lane — gated like the
                  desktop Media lane (hasMedia): with nothing to show, the
                  panel renders permanently-animating shimmer skeletons. */}
              {(hasMedia || !!chatGameStatus?.pinned_pick) && (
              <div style={{ marginTop: 12 }}>
                <div style={{ fontSize: '0.66em', fontWeight: 800, letterSpacing: '1.2px', textTransform: 'uppercase', color: THEME.textMuted || '#aaa', padding: '2px 4px 6px' }}>
                  Shared media
                </div>
                <SuggestedMediaPanel mediaSuggestions={chatGameStatus?.media_suggestions} pinnedPick={shownPinnedPick} THEME={THEME} />
              </div>
              )}
              </div>);
            })()}
            {mobileView === 'arcade' && (
              /* Never hides when empty (spec P3): GamesPanel shows what could
                 be played plus results — the lane's hide-when-empty idiom died
                 with the nav rewire. */
              <div role="tabpanel" data-tab-name="games">
                <GamesPanel THEME={THEME} />
              </div>
            )}
          </div>
        )}
        {/* ⌨️🖥️ WORKBENCH PLACE (The Terminal, 08-19): the per-seat
            battlestations screen — terminal phones run Chat · Work · Recap.
            Same layer chrome as TV/Arcade (unmounts on hop; nothing here
            needs a reparent slot). Reachable only via the terminal swipe
            order + nav items, both gated on isTerminalSpace. */}
        {placesNav && isTerminalSpace && !investigationTakeover && mobileView === 'workbench' && (
          <div role="tabpanel" data-tab-name="workbench" data-swipe-place="" style={{
            position: 'fixed', top: 0, left: 0, right: 0,
            bottom: NAV_BOTTOM,
            zIndex: 4600, overflowY: 'auto', WebkitOverflowScrolling: 'touch',
            background: 'rgba(10,10,16,0.98)', // no blur (perf 08-13, same as the TV layer)
            padding: '12px 0 24px',
          }}>
            <div style={{
              display: 'flex', alignItems: 'center', gap: 8, padding: '2px 16px 2px',
              fontSize: '0.72em', fontWeight: 800, letterSpacing: '1.4px',
              textTransform: 'uppercase', color: THEME.textMuted || '#aaa',
            }}>
              ⌨️ The Workbench
            </div>
            <TerminalWorkbenchPanel
              users={users}
              results={results}
              currentlyTyping={currentlyTyping}
              THEME={THEME}
              ledger={chatGameStatus?.task_ledger}
              isSpaceMod={!!humanPlayerId && (humanPlayerId === chatGameStatus?.human_player_identifier || humanPlayerId === createdPlayerIdRef.current)}
              onStartTask={async (text) => {
                const issuerName = users.find(u => u.game_player_id === humanPlayerId)?.name || 'the mod';
                await handleInterjectionAndPlay(`/task ${text}`, [], {
                  name: 'task', targets: [], text,
                  card_message: `⌨️ NEW TASK from ${issuerName}: ${text}`,
                });
              }}
            />
          </div>
        )}
        {/* 📋 RECAP PLACE (The Terminal, 08-19 — Nina: "not a story, a stage
            for coding comps"): the comp phones' alerts-layer pattern on
            non-comp chrome — the non-message action feed (tasks dropped,
            polls, verdicts, announcements). */}
        {placesNav && isTerminalSpace && !investigationTakeover && mobileView === 'recap' && (
          <div role="tabpanel" data-tab-name="recap" data-swipe-place="" style={{
            position: 'fixed', left: 0, right: 0, top: 0,
            bottom: NAV_BOTTOM,
            zIndex: 4600, background: 'rgba(10,10,16,0.98)',
            display: 'flex', flexDirection: 'column', overflow: 'hidden',
          }}>
            <div style={{
              flexShrink: 0, display: 'flex', alignItems: 'center', gap: 8,
              padding: '12px 16px 8px', borderBottom: '1px solid rgba(255,255,255,0.08)',
              fontSize: '0.72em', fontWeight: 800, letterSpacing: '1.4px',
              textTransform: 'uppercase', color: THEME.textMuted || '#aaa',
            }}>
              📋 Recap
              <span style={{ fontWeight: 600, letterSpacing: 'normal', textTransform: 'none', fontSize: '0.95em' }}>
                tasks · polls · verdicts
              </span>
            </div>
            <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', WebkitOverflowScrolling: 'touch', padding: '10px 12px' }}>
              {(() => {
                const layerActions = results.flatMap(r => r.actions || []);
                return (
                  <VirtualizedActionList
                    portedInteract={isPortedSpace && !!humanPlayerId}
                    actions={layerActions.filter(a => !messageActionTypes.has(a.type))}
                    playerModelMap={playerModelMap}
                    users={users}
                    THEME={THEME}
                    onActionUpdate={updateAction}
                    gameType={gameType}
                    allActions={layerActions}
                    cachedSpaces={cachedSpaces}
                    cachedRoundAnalysisHistory={cachedRoundAnalysisHistory}
                    onReactionSubmit={handleReactionSubmit}
                    humanPlayerId={humanPlayerId}
                    effectivePlayerId={effectivePlayerId}
                    showThoughts={showThoughts}
                    imposterMode={chatGameStatus?.imposter_mode || false}
                    competitiveMode={chatGameStatus?.competitive_mode || chatGameStatus?.imposter_mode || false}
                    humanTokensLeft={tokensLeft}
                    uiTerms={chatGameStatus?.ui_terms || null}
                    noirTheme={!!chatGameStatus?.noir_theme}
                  />
                );
              })()}
            </div>
          </div>
        )}
        {/* 👥 LOCALS PLACE (P4, Nina's calls 07-31): the FULL dossier cards
            render here — this is the case file's home on phones now (the
            manila drawer instance is gated off under placesNav: ONE mount,
            or the unlock engine double-fires its toasts). Container stays
            MOUNTED on every place (display-gated, not unmounted) so unlock
            detection + toasts keep running while you chat — same contract
            the old always-mounted drawer honored. Hearts ride the cards
            (mobile-only for now: only this mount passes the prop). */}
        {placesNav && (() => {
          // 08-01 P0 fix: !typingFocus dropped here too — typing into anything
          // on the Locals place (tea search etc.) was hiding Locals itself.
          const localsVisible = mobileView === 'locals' && !investigationTakeover;
          // 🤖 Split, not merged (Nina 08-15). An external agent seat carries
          // is_human:true for engine reasons (utils/playerKind), so a probe
          // used to be counted as a person AND listed under 👤 — the room
          // claimed a visitor it did not have. Agents get their own chip lane.
          const humansPresent = activeUsers.filter(isRealHuman);
          const agentsPresent = activeUsers.filter(isExternalAgent);
          const humansHere = humansPresent.length;
          const agentsHere = agentsPresent.length;
          const castCount = users.filter(u => u && !isRealHuman(u) && !isExternalAgent(u)).length;
          const host = chatGameStatus?.moderator_character;
          return (
            <div role="tabpanel" data-tab-name="locals" data-swipe-place="" ref={localsPlaceRef} style={{
              position: 'fixed', top: 0, left: 0, right: 0,
              bottom: NAV_BOTTOM,
              zIndex: 4600, overflowY: 'auto', WebkitOverflowScrolling: 'touch',
              background: 'rgba(10,10,16,0.98)', padding: '12px 12px 24px',
              display: localsVisible ? undefined : 'none',
            }}>
              {/* 📊 TOP BAR (Nina 08-03): the room's vitals lead the place and
                  + Add Agent rides up here for visibility. The old
                  "LOCALS · N" label row below the tea is retired; the vitals
                  caption that sat under the hero moved here too. */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '0 3px 8px' }}>
                <span style={{ fontSize: '0.66em', color: 'rgba(255,255,255,0.55)' }}>
                  {castCount} locals{humansHere > 0 ? ` · ${humansHere} ${humansHere === 1 ? 'visitor' : 'visitors'} here now` : ''}{agentsHere > 0 ? ` · ${agentsHere} ${agentsHere === 1 ? 'agent' : 'agents'} visiting` : ''}
                  {Number(chatGameStatus?.visit_streak) >= 2 && <span style={{ color: '#ffb74d', fontWeight: 700 }}> · 🔥 {chatGameStatus.visit_streak}-day streak</span>}
                </span>
                <button
                  onClick={() => setLocalsAddAgentOpen(o => !o)}
                  aria-expanded={localsAddAgentOpen}
                  style={{
                    marginLeft: 'auto', flexShrink: 0, cursor: 'pointer',
                    fontSize: '0.66em', fontWeight: 800, padding: '5px 12px', borderRadius: 999,
                    border: 'none', background: THEME.accent || '#7C4DFF',
                    color: THEME.textOnAccent || '#fff', letterSpacing: '0.02em',
                  }}
                >{localsAddAgentOpen ? '✕ Close' : '+ Add Agent'}</button>
              </div>
              {localsAddAgentOpen && (
                <div style={{ marginBottom: 10 }}>
                  <AddAgentInline gameId={gameId} THEME={THEME} startOpen ported={isPortedSpace} />
                </div>
              )}
              {/* 🎙️ HOST HERO (dedupe D, Nina 08-02): the place opens on the
                  host — full-bleed portrait capped at 140px (banner, not
                  poster), LIVE pill, rotating status line (hostlive-b
                  cadence), whole card taps to the host's bio. */}
              {host && (
                <div
                  onClick={() => setLocalsHostBioOpen(true)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setLocalsHostBioOpen(true); } }}
                  title={`${host.name} — your host. Tap for their story!`}
                  style={{
                    position: 'relative', height: 140, borderRadius: 14, overflow: 'hidden',
                    cursor: 'pointer', marginBottom: 6,
                    border: `1px solid ${THEME.accent || '#7C4DFF'}45`,
                    background: `linear-gradient(160deg, ${THEME.accent || '#7C4DFF'}35, rgba(10,10,16,0.6))`,
                  }}
                >
                  {host.profile_image_url ? (
                    // High crop anchor: banner-height crops were landing on
                    // foreheads/chins (Nina 08-03 hero pass)
                    <img src={host.profile_image_url} alt={host.name} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', objectPosition: '50% 25%' }} />
                  ) : (
                    <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '2.6em' }}>{host.avatar_emoji || '🎙️'}</div>
                  )}
                  {/* LIVE pill top-RIGHT, identical to the desktop host card's
                      (Nina 08-03) — small red rect, 3px radius, pulse. Safe on
                      the right again: the top bar now sits above the hero, so
                      the music FAB no longer overlaps this corner. */}
                  <span style={{
                    position: 'absolute', top: 8, right: 8, padding: '1px 5px', borderRadius: 3,
                    background: '#ef4444', color: '#fff', fontSize: '0.56em', fontWeight: 800,
                    letterSpacing: '0.5px', animation: 'liveBadgePulse 2s ease-in-out infinite',
                  }}>LIVE</span>
                  <div style={{
                    position: 'absolute', left: 0, right: 0, bottom: 0, padding: '22px 12px 10px',
                    background: 'linear-gradient(0deg, rgba(0,0,0,0.92) 0%, rgba(0,0,0,0.5) 62%, transparent 100%)',
                  }}>
                    <div style={{ fontSize: '0.56em', fontWeight: 800, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.66)' }}>Your host</div>
                    <div style={{ marginTop: 2, fontSize: '1.05em', fontWeight: 800, color: '#fff', textShadow: '0 1px 3px rgba(0,0,0,0.6)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{host.name}</div>
                    {host.status_messages?.length > 0 && (
                      <div key={localsHostStatusIdx} style={{
                        marginTop: 2, fontSize: '0.66em', fontStyle: 'italic', color: 'rgba(255,255,255,0.78)',
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        animation: 'moderatorFadeIn 0.5s ease-out',
                      }}>{host.status_messages[localsHostStatusIdx % host.status_messages.length]}</div>
                    )}
                  </div>
                </div>
              )}
              {/* 👥 UsersPanel = READY-CHECK VALVE ONLY (dedupe D, Nina
                  08-02): the wholesale panel is gone — hero owns the host,
                  the dossier grid owns the cast, the header pill owns + Add
                  Agent. The panel returns just for the duration of a live
                  ready check (its one UI nothing else carries). */}
              {chatGameStatus?.is_ready_check && !chatGameStatus?.ready_check_data?.done && activeUsers.length > 0 && !isPortedSpace && <div style={{ marginBottom: 12 }}><UsersPanel users={activeUsers} allUsers={users} localStatuses={localStatuses} THEME={THEME} gameId={gameId} chatGameStatus={chatGameStatus} humanPlayerId={humanPlayerId} createdPlayerId={createdPlayerIdRef.current} onQuickJoin={handleQuickJoin} isReadyCheck={chatGameStatus?.is_ready_check && !chatGameStatus?.ready_check_data?.done} readyCheckData={readyCheckData} readyCheckSubmitted={readyCheckSubmitted} readyCheckCountdown={readyCheckCountdown} readyCountdownDuration={readyCountdownDuration} readyCheckPhase={readyCheckPhase} autoStartCountdown={autoStartCountdown} gameStarted={gameStarted} hasJoinedGame={hasJoinedGame} showGlitchReveal={showGlitchReveal} diceSpotlightId={diceRollToast?.pid || null} hideJoinCtas={!!chatGameStatus?.noir_theme && !isBotMode} compactMode onReadyUp={async () => {
                readyCheckDismissedRef.current = true;
                setReadyCheckSubmitted(true);
                const myName = chatGameStatus?.players?.find(p => p.id === humanPlayerId)?.name || 'You';
                setReadyCheckData(prev => ({
                  ...prev,
                  ready_count: (prev?.ready_count || 0) + 1,
                  ready_player_names: [...(prev?.ready_player_names || []), myName]
                }));
                try {
                  await chatGameApi.readyUp(gameId, humanPlayerId);
                } catch (err) {
                  console.error('Ready check response failed:', err);
                }
              }} onTogglePlay={(isPlay) => {
                const now = Date.now();
                if (now - togglePlayDebounceRef.current < 1000) return;
                togglePlayDebounceRef.current = now;
                if (gameStarted) return;
                if (isPlay && chatGameStatus?.eliminated_players?.includes(createdPlayerIdRef.current)) return;
                if (isPlay && isPlayingRef.current && (chatGameStatus?.competitive_mode || chatGameStatus?.imposter_mode)) return;
                userSwitchedToPlayRef.current = true;
                if (isPlay) {
                  const pid = createdPlayerIdRef.current;
                  if (!pid) return;
                  setHumanPlayerId(pid);
                  const url = new URL(window.location);
                  url.searchParams.set('playerId', pid);
                  window.history.replaceState({}, '', url);
                  readyCheckDismissedRef.current = false;
                  setReadyCheckSubmitted(false);
                  chatGameApi.unspectatePlayer(gameId, pid).catch(err =>
                    console.error('[onTogglePlay] unspectate failed:', err.message)
                  );
                } else {
                  setHumanPlayerId(null);
                  const url = new URL(window.location);
                  url.searchParams.delete('playerId');
                  window.history.replaceState({}, '', url);
                  readyCheckDismissedRef.current = true;
                  setShowReadyCheck(false);
                  setCurrentRequestId(null);
                }
              }} dismissedHints={dismissedHints} onDismissHint={dismissHint} isMobile={isMobile} hintsHidden={hintsHidden} spectateDropdownRef={spectateDropdownRef} quickJoinButtonsRef={quickJoinButtonsRef} readyToggleRef={readyToggleRef} showElementPulse={showElementPulse} showDropdownHint={showDropdownHint} showReadyHint={showReadyHint} onDropdownInteract={() => { dropdownInteractedRef.current = true; setShowDropdownHint(false); }} onReadyInteract={() => { readyInteractedRef.current = true; setShowReadyHint(false); }} /></div>}
              {/* 👥 LOCALS header retired (Nina 08-03): the top bar owns the
                  count and + Add Agent now; the dossier grid speaks for
                  itself. */}
              {/* 🪟 W2 (D2 ruled): mind panel replaces the dossier in
                  ported spaces (mobile Locals place, relabeled Mind). */}
              {/* 🗣️ 09-25 (Nina): the "what X sees" card is retired and its
                  game words with it; phones get the desktop rail's content
                  (windows stay inline in the feed on phones, so none here). */}
              {isPortedSpace && portedMindProps ? <div style={{ padding: '10px 12px' }}>{portedPlaceOn ? (
              <PortedJournal
                residentName={portedMindProps.residentName}
                ownerName={chatGameStatus?.owner_display_name || null}
                isOwner={isPortedOwner}
                createdAt={chatGameStatus?.space_created_at || null}
                writing={portedWriting}
                journal={portedJournal}
                whileAway={portedWhileAway}
                cover={chatGameStatus?.home?.key || 'light'}
                pronouns={portedPronouns}
                onPronouns={isPortedOwner && humanPlayerId ? setPortedPronouns : null}
                facts={portedFacts}
                onForget={isPortedOwner && humanPlayerId ? forgetPortedFact : null}
                notes={isPortedOwner ? portedNotes : null}
                onNote={portedLineOwner ? leavePortedNote : null}
                onDraw={() => setShowDrawingCanvas(true)}
                fill="screen"
                now={portedPlaceOn ? placeNow : null}
                openToken={portedJournalToken}
                tasks={portedLineOwner ? portedTasks : null}
                onTask={portedLineOwner ? addPortedTask : null}
                onCancelTask={portedLineOwner ? cancelPortedTask : null}
                onReopenTask={portedLineOwner ? reopenPortedTask : null}
                favorites={chatGameStatus?.favorites || null}
                taskToken={portedTaskToken}
                taskShared={portedTasksShared}
                tz={chatGameStatus?.owner_tz || null}
                memories={portedLineOwner ? portedMemories : null}
                onForgetMemory={portedLineOwner ? portedMemories.forget : null}
              />
              ) : <PortedRail
                {...portedMindProps}
                gameId={gameId}
                lastTurnAgo={portedLastTurnAgo}
                keepsakeActions={keepsakeActions}
                windows={[]}
                whileAway={portedWhileAway}
                onDraw={() => setShowDrawingCanvas(true)}
              />}</div> : <LocalsDossier
                gameId={gameId}
                players={users}
                actions={results.flatMap(r => r.actions || [])}
                myPlayerId={effectivePlayerId}
                myPlayerName={users?.find(u => u.id === humanPlayerId)?.name || null}
                theme={THEME}
                enabled
                portraitCards
                visible={localsVisible}
                onRequestOpen={() => setMobileView('locals')}
                onUnseenChange={setDossierUnseen}
                hearts={chatGameStatus?.relationship_hearts || null}
                coins={houseStatus?.balances || null}
                charters={houseStatus?.charters || null}
                stats={houseStatus?.stats || null}
                bonds={houseStatus?.bonds || null}
                uncovered={houseStatus?.uncovered || null}
                circadian={houseStatus?.circadian || chatGameStatus?.space_life || null}
                houseSpace={!!houseStatus}
                onSayHi={(local) => {
                  // 👋 opener deck: mention + a random playful first line
                  const opener = SAY_HI_OPENERS[Math.floor(Math.random() * SAY_HI_OPENERS.length)];
                  setComposerSeed(`@${local.name} ${opener}`);
                  setComposerCollapsed(false);
                  setMobileView('chat');
                }}
                onGift={(local) => {
                  // /gift #item @name — seed the verb + target; the composer's
                  // shelf chips take it from there
                  setComposerSeed(`/gift @${local.name} `);
                  setComposerCollapsed(false);
                  setMobileView('chat');
                }}
              />}
              {/* 🫖 THE TEA (option B; moved BELOW the dossier, Nina 08-03):
                  spotlight = hottest ship, votable, headlines under it; the
                  full archive expands below. Mounted only while the place is
                  visible (the panel derives a lot; no need to pay that on
                  Chat). */}
              {localsVisible && !isPortedSpace && (
                <div style={{ margin: '12px 0' }}>
                  <HighlightsPanel
                    allActions={results.flatMap(r => r.actions || [])}
                    users={users}
                    THEME={THEME}
                    moderatorCharacter={chatGameStatus?.moderator_character}
                    gameId={gameId}
                    humanPlayerId={humanPlayerId}
                    shipVotes={chatGameStatus?.ship_votes}
                    builds={chatGameStatus?.builds || []}
                    spotlight
                    freshCount={teaUnseen}
                    onOpenFull={() => setLocalsTeaOpen(o => !o)}
                  />
                  {localsTeaOpen && (
                    <div style={{ marginTop: 8 }}>
                      <HighlightsPanel allActions={results.flatMap(r => r.actions || [])} users={users} THEME={THEME} moderatorCharacter={chatGameStatus?.moderator_character} gameId={gameId} humanPlayerId={humanPlayerId} shipVotes={chatGameStatus?.ship_votes} builds={chatGameStatus?.builds || []} />
                    </div>
                  )}
                </div>
              )}
              {/* 👤 Visitors — humans aren't locals (no dossier), so they get
                  a quiet chip card at the grid's end (dedupe D).
                  🤖 Agents ride the same card in their own row (Nina 08-15):
                  they are visiting too, but calling a probe 👤 was the whole
                  bug. Model slug on the chip so the row says WHAT is here. */}
              {(humansHere > 0 || agentsHere > 0) && (
                <div style={{
                  marginTop: 10, borderRadius: 10, padding: '8px 10px',
                  border: '1px solid rgba(255,255,255,0.12)',
                  display: 'flex', flexDirection: 'column', gap: 6,
                }}>
                  {humansHere > 0 && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                      <span style={{ fontSize: '0.6em', fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.5)' }}>visiting now</span>
                      {humansPresent.map(u => (
                        <span key={u.game_player_id || u.id || u.name} style={{
                          fontSize: '0.66em', fontWeight: 700, color: 'rgba(255,255,255,0.85)',
                          border: '1px solid rgba(255,255,255,0.2)', borderRadius: 999, padding: '2px 9px',
                        }}>👤 {u.name}</span>
                      ))}
                    </div>
                  )}
                  {agentsHere > 0 && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                      <span style={{ fontSize: '0.6em', fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'rgba(196,181,253,0.6)' }}>agents visiting</span>
                      {agentsPresent.map(u => {
                        const cap = agentCaption(u, { compact: true });
                        return (
                          <span key={u.game_player_id || u.id || u.name}
                            // 🔌 09-28 agent wave: a watch link's ?with=<seat id> outlines this chip
                            data-seat-chip={u.game_player_id || u.id}
                            title={agentCaption(u)}
                            style={{
                              fontSize: '0.66em', fontWeight: 700, color: 'rgba(233,224,255,0.92)',
                              border: '1px solid rgba(196,181,253,0.32)', borderRadius: 999, padding: '2px 9px',
                            }}>
                            🤖 {u.name}
                            {cap && <span style={{ opacity: 0.6, fontWeight: 500 }}> · {cap}</span>}
                          </span>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
              {/* 🧰 The shelf as CONTENT (Nina 07-31: "both") — browsable here
                  beside the cast that built it; the chat chip keeps the
                  drawer for the copy-a-handle collect verb. */}
              {Array.isArray(chatGameStatus?.builds) && chatGameStatus.builds.length > 0 && (
                <div style={{
                  marginTop: 12, borderRadius: 12, padding: '10px 12px',
                  background: 'linear-gradient(180deg, #b98d54 0%, #96683a 100%)',
                  border: '1px solid #7a5227',
                }}>
                  <div style={{ fontSize: '0.66em', fontWeight: 800, letterSpacing: '1.2px', textTransform: 'uppercase', color: '#fdf3dd', padding: '0 0 8px' }}>
                    🧰 The Shelf · {chatGameStatus.builds.length}
                  </div>
                  <SpaceShelf
                    items={chatGameStatus.builds}
                    perPlank={3}
                    onGift={(h) => seedFromShelf(`/gift #${h} `)}
                    onCombine={(h) => seedFromShelf(`/build #${h} + `)}
                    isOwner={isPortedOwner}
                  />
                </div>
              )}
              {/* 🏠🧠 Memory wall as CONTENT (Claude House only) — same
                  "browsable beside the cast" treatment as the shelf; the
                  lazy row fetch is already armed by houseWallWanted the
                  moment this place is shown. */}
              {houseStatus && (
                <div style={{
                  marginTop: 12, borderRadius: 12, padding: '10px 12px',
                  background: 'linear-gradient(180deg, #d2a878 0%, #b98c5c 100%)',
                  border: '1px solid #8a5f33',
                }}>
                  <div style={{ fontSize: '0.66em', fontWeight: 800, letterSpacing: '1.2px', textTransform: 'uppercase', color: '#3b2a1a', padding: '0 0 8px' }}>
                    📌 The Memory Wall
                  </div>
                  <HouseMemoryWall
                    canon={houseStatus.canon || []}
                    memories={houseWall.memories}
                    scenes={houseWall.scenes || []}
                        pollHistory={houseWall.pollHistory || []}
                        threads={houseWall.threads || []} /* 🗝️ 10-14 Open mysteries */
                    loading={houseWall.loading}
                    till={houseStatus.till ?? null}
                    buildGoal={houseStatus.build_goal || 40}
                    gameId={gameId}
                    houseToday={houseWall.houseToday || null}
                    archive={houseWall.archive || null}
                  />
                </div>
              )}
            </div>
          );
        })()}
        {/* 🎙️ Host bio popup for the Locals HERO (dedupe D) — a page-level
            twin of UsersPanel's showHostCard modal: that one unmounted with
            the panel, and its z3200 sat UNDER the places layer (4600)
            anyway. z5150 clears layer + nav (CastCardModal precedent). */}
        {localsHostBioOpen && chatGameStatus?.moderator_character && (() => {
          const host = chatGameStatus.moderator_character;
          return (
            <div
              onClick={() => setLocalsHostBioOpen(false)}
              style={{
                position: 'fixed', inset: 0, zIndex: 5150,
                backgroundColor: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(2px)', WebkitBackdropFilter: 'blur(2px)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px',
              }}
            >
              <div
                onClick={(e) => e.stopPropagation()}
                style={{
                  width: '360px', maxWidth: '92vw', maxHeight: '80dvh', overflowY: 'auto',
                  borderRadius: '16px', position: 'relative',
                  backgroundColor: THEME.bgMedium || '#1a1a2e',
                  border: `1px solid ${THEME.accent}50`,
                  boxShadow: `0 12px 48px rgba(0,0,0,0.6), 0 0 24px ${THEME.accent}20`,
                  padding: '20px',
                }}
              >
                <button
                  onClick={() => setLocalsHostBioOpen(false)}
                  aria-label="Close host bio"
                  style={{ position: 'absolute', top: '10px', right: '12px', background: 'none', border: 'none', color: THEME.textMuted || '#888', cursor: 'pointer', fontSize: '1em', lineHeight: 1, padding: '4px' }}
                >✕</button>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '14px' }}>
                  {host.profile_image_url ? (
                    <img src={host.profile_image_url} alt={host.name} style={{
                      width: 52, height: 52, borderRadius: '50%', objectFit: 'cover', flexShrink: 0,
                      border: `2px solid ${THEME.accent}60`,
                    }} />
                  ) : (
                    <div style={{
                      width: 52, height: 52, borderRadius: '50%', flexShrink: 0,
                      background: `linear-gradient(135deg, ${THEME.accent}, ${THEME.primary || '#6366f1'})`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.5em',
                    }}>{host.avatar_emoji || '🎙️'}</div>
                  )}
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: '0.95em', fontWeight: 700, color: THEME.textOnPanel || '#fff' }}>{host.name}</div>
                    <div style={{ fontSize: '0.62em', fontWeight: 700, color: THEME.accentOnPanel || THEME.accent, textTransform: 'uppercase', letterSpacing: '1.2px', marginTop: '2px' }}>Your Host</div>
                  </div>
                </div>
                {host.catchphrase && (
                  <div style={{ fontSize: '0.78em', color: THEME.accentOnPanel || THEME.accent, fontStyle: 'italic', marginBottom: '10px' }}>
                    "{host.catchphrase}"
                  </div>
                )}
                {(host.origin_story || host.personality) && (
                  <div style={{ fontSize: '0.84em', color: THEME.textMuted || '#aaa', lineHeight: 1.65 }}>
                    {host.origin_story || host.personality}
                  </div>
                )}
                {Array.isArray(host.personality_traits) && host.personality_traits.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px', marginTop: '12px' }}>
                    {host.personality_traits.map((t, i) => (
                      <span key={i} style={{
                        fontSize: '0.62em', fontWeight: 600, padding: '3px 9px', borderRadius: '10px',
                        backgroundColor: `${THEME.accent}18`, color: THEME.accentOnPanel || THEME.accent, border: `1px solid ${THEME.accent}30`,
                      }}>{t}</span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          );
        })()}
        {/* 📱 Bottom nav (thumb-zone stage 2, 07-17): the space's tabs live at
            the viewport bottom on phones — same state, handlers, and badges as
            the mid-page strip they replace. Hidden while typing so the
            keyboard gets the full screen; overlays (intro, reveals) stack
            above it. */}
        {isMobile && (gameType === 'chat-game' || gameType === 'chat') && chatGameStatus && !typingFocus && !investigationTakeover && !shellPreset /* 🧱 the frame's own phone bar */ && (() => {
          const navComp = chatGameStatus?.competitive_mode || chatGameStatus?.imposter_mode;
          // 📱🧭 PLACES NAV (spec 2026-07-31): non-comp tabs are real screens —
          // Chat · TV · Locals · Arcade (Nina 07-31: Arcade LAST, after Locals).
          // Places never vanish (hide-empty is dead here); the one gate left is
          // the explicit per-space arcade tool_flag (07-29 goal presets).
          // Chameleon TV label: 📺 TV ⇄ 🔴 LIVE + streamer count while live.
          const liveN = spaceLiveStreamers.length;
          // 👥 PLAYERS PLACE (08-03, Nina go): the roster's own screen, 2nd
          // position — Battle · Players · Recap · Arcade. While the lobby
          // ready check is live the roster ALSO stays on Battle (the READY
          // VALVE — dedupe-D in reverse) and this tab wears a pulsing
          // ready-count badge so the lobby state reads from any screen.
          const compLobby = navComp && !!(chatGameStatus?.is_ready_check && !chatGameStatus?.ready_check_data?.done);
          const items = navComp ? [
            { key: 'all', icon: '⚔️', label: 'Battle', dotKey: 'chat' },
            { key: 'players', icon: '👥', label: 'Players', readyBadge: compLobby ? `${readyCheckData?.ready_count || 0}/${readyCheckData?.max_ready_players || 5}` : null },
            { key: 'alerts', icon: '📋', label: 'Recap' },
            { key: 'games', icon: '🎮', label: 'Arcade', dotKey: 'games' },
          ] : isTerminalSpace ? [
            // ⌨️ Terminal phones (Nina 08-19): Chat · Work · Recap — a stage
            // for coding comps, not a story. TV/Locals/Arcade leave with the
            // desktop Media/Arcade tabs. Keep in sync with swipeOrderRef and
            // the terminal fixed layers (the 3-enum trap).
            { key: 'chat', iconName: 'chat', label: 'Chat', dotKey: 'chat' },
            { key: 'workbench', icon: '⌨️', label: 'Work' },
            { key: 'recap', icon: '📋', label: 'Recap' },
          ] : [
            // 🖊️ Line-icon sweep (Nina 08-01, mock v3): places tabs wear the
            // stroke set — LIVE keeps the label flip + red count badge.
            { key: 'chat', iconName: 'chat', label: 'Chat', dotKey: 'chat' },
            { key: 'tv', iconName: 'tv', label: liveN > 0 ? 'Live' : 'TV' },
            { key: 'locals', iconName: 'locals', label: isPortedSpace ? (portedPlaceOn ? 'Journal' : 'Mind') : 'Locals' },
            { key: 'arcade', iconName: 'arcade', label: 'Arcade', dotKey: 'games' },
            // 🎨 D13 (ruled 08-28): ported spaces get a Draw item. An ACTION,
            // not a place — its tap opens the canvas (see onClick) and never
            // navigates, so it stays out of swipeOrderRef on purpose.
            ...(isPortedSpace ? [{ key: 'draw', icon: '🎨', label: 'Draw' }] : []),
          ].filter(t => t.key !== 'arcade' || (chatGameStatus?.tool_flags?.arcade !== false && !isPortedSpace));
          return (
            <div data-swipe-nav="" style={{
              position: 'fixed', left: 0, right: 0, bottom: 0, zIndex: 5100,
              // ⚔️ Column: comp mounts the phase ticker as a first row above
              // the tabs — one fixed block, so the 53px anchor math only
              // needs the single COMP_TICKER_H addition on comp phones.
              display: 'flex', flexDirection: 'column', background: 'rgba(8,10,14,0.98)', // no blur (perf 08-13)
              borderTop: navComp ? 'none' : `1px solid ${THEME.accent || '#7C4DFF'}40`,
              paddingBottom: 'env(safe-area-inset-bottom, 0px)',
            }}>
              {/* 👻 GHOST NAV PERCH (Nina 08-02, mock section 4 option A):
                  on non-Chat places the old floating edge capsule becomes a
                  little tab perched on the nav's right corner — nav chrome,
                  unread inside, tap = the ghost sheet. data-noswipe keeps a
                  drag starting here from walking the places (the nav is the
                  swipe surface). Absolute against the fixed nav, so the
                  nav's backdropFilter containing-block trap doesn't apply. */}
              {!navComp && mobileView !== 'chat' && !isBotMode && !ghostSheetOpen
                && ghostSurfacesOn
                && spectatingMessages.length > 0 && !!humanPlayerId && !effectiveIsSpectating
                && !chatGameStatus?.eliminated_players?.includes(humanPlayerId)
                && !(watchParty && inTheater && mobileView === 'tv')
                && (() => {
                const perchUnread = Math.max(0, spectatingMessages.length - ghostSeenCountRef.current);
                return (
                  <button
                    data-noswipe=""
                    onClick={() => {
                      ghostSeenCountRef.current = spectatingMessages.length;
                      setGhostSheetOpen(true);
                      if (!ghostHintSeen) {
                        setGhostHintSeen(true);
                        try { localStorage.setItem('ghostTickerHintSeen', '1'); } catch (e) { /* private mode */ }
                      }
                    }}
                    aria-label={perchUnread > 0 ? `Open ghost chat, ${perchUnread} unread` : 'Open ghost chat'}
                    style={{
                      position: 'absolute', right: 10, bottom: '100%',
                      display: 'flex', alignItems: 'center', gap: 5,
                      background: 'rgba(8,10,14,0.96)',
                      border: '1px solid rgba(143,127,240,0.6)', borderBottom: 'none',
                      borderRadius: '10px 10px 0 0', padding: '5px 12px 4px',
                      color: '#cfc6f7', fontSize: 12, fontWeight: 800, cursor: 'pointer',
                      textTransform: 'none', letterSpacing: 'normal',
                    }}
                  >
                    <LineIcon name="ghost" size={13} />
                    {perchUnread > 0 && (
                      <span style={{
                        background: 'rgba(143,127,240,0.35)', color: '#e6e1fb',
                        borderRadius: 999, padding: '0 6px', fontSize: 10,
                        fontVariantNumeric: 'tabular-nums', lineHeight: '15px',
                      }}>{perchUnread > 99 ? '99+' : perchUnread}</span>
                    )}
                  </button>
                );
              })()}
              {/* ⚔️ PHASE TICKER + ghost tab (comp only) — see CompPhaseTicker */}
              {navComp && (
                <CompPhaseTicker
                  chatGameStatus={chatGameStatus}
                  humanPlayerId={humanPlayerId}
                  waitingForVote={waitingForVote}
                  turnDeadline={turnDeadline}
                  yourTurnLive={!!(chatGameStatus?.your_turn || (isHumanTurn && currentRequestId))}
                  ghostUnread={Math.max(0, spectatingMessages.length - ghostSeenCountRef.current)}
                  showGhostTab={!isBotMode && !ghostSheetOpen && spectatingMessages.length > 0
                    && !!humanPlayerId && !effectiveIsSpectating
                    && !chatGameStatus?.eliminated_players?.includes(humanPlayerId)}
                  onGhostOpen={() => {
                    ghostSeenCountRef.current = spectatingMessages.length;
                    setGhostSheetOpen(true);
                    if (!ghostHintSeen) {
                      setGhostHintSeen(true);
                      try { localStorage.setItem('ghostTickerHintSeen', '1'); } catch (e) { /* private mode */ }
                    }
                  }}
                />
              )}
              {/* minHeight = NAV_H: the row's natural height ran ~49px, so a
                  ~4px seam of page background showed between the dock (anchored
                  at NAV_H) and the nav's top border (Nina 08-03, chat-tab gap). */}
              <div style={{ display: 'flex', minHeight: NAV_H }}>
              {items.map(t => {
                const active = navComp ? compMobileView === t.key : mobileView === t.key;
                // 🤔 Thinking dots on the CHAT tab (Nina 08-06): while a local
                // composes and you're on another place, the tab itself carries
                // the liveness — the floating chip stops covering TV/Locals/
                // Arcade. Live state, not clear-on-tap; it ends when they post.
                // The red unread dot yields the slot while these pulse.
                const thinkingDots = !navComp && t.key === 'chat' && !active && !!currentlyTyping;
                const dot = !!(t.dotKey && tabDots[t.dotKey]) && !active && !thinkingDots;
                // Badge re-aim (spec lock): Chat = unread whispers count; TV =
                // live streamer count (red) or a warm-couch party dot (purple);
                // Arcade = game-opened dot. Comp keeps its old badge wiring.
                // Badge re-aim (Locals v2): tea moved home to Locals, so its
                // pink count moves with it; Chat keeps just the unread dot.
                const navCount = navComp
                  ? 0
                  : t.key === 'tv' ? liveN
                  : (t.key === 'locals' && !active) ? (teaUnseen + dossierUnseen) : 0;
                const badgeColor = (t.key === 'locals' && teaUnseen > 0) ? '#ec407a' : '#ef4444';
                const partyDot = !navComp && t.key === 'tv' && !active && !!watchParty && liveN === 0;
                return (
                  <button
                    key={t.key}
                    data-place-tab={t.key}
                    aria-label={thinkingDots ? 'Chat, someone is typing' : undefined}
                    onClick={() => {
                      // 🎨 Draw is an action, not a place (D13): open the canvas,
                      // stay wherever you are.
                      if (t.key === 'draw') { setShowDrawingCanvas(true); return; }
                      if (!navComp) {
                        // 🧭 Navigate, never re-filter or scroll-teleport — the
                        // place keeps its own state (spec lock).
                        setMobileView(t.key);
                        if (t.dotKey) setTabDots(prev => ({ ...prev, [t.dotKey]: false }));
                        return;
                      }
                      // ⚔️ Comp places (08-02): navigate too. The old
                      // filter+scroll-teleport (setChatActionTab + the
                      // .space-tab-bar scrollIntoView) is DEAD — Recap and
                      // Arcade are layers now, and the Battle feed keeps its
                      // scroll position under them.
                      setCompMobileView(t.key);
                      if (t.dotKey) setTabDots(prev => ({ ...prev, [t.dotKey]: false }));
                    }}
                    style={{
                      // 🎨 Contrast fix (Nina 08-02, mock variant A): labels
                      // stay WHITE in every theme — the accent lives only in
                      // the top indicator bar + icon, so an orange space can
                      // never paint orange text on itself again.
                      flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2,
                      padding: '7px 0 8px', background: 'none', border: 'none', cursor: 'pointer',
                      color: active ? '#fff' : 'rgba(255,255,255,0.85)',
                      fontSize: 10, fontWeight: 800, letterSpacing: '0.4px', textTransform: 'uppercase',
                      position: 'relative',
                    }}
                  >
                    {active && (
                      <span aria-hidden="true" style={{
                        position: 'absolute', top: 0, left: '22%', right: '22%', height: 3,
                        // ⚔️ Arena paint (option B): comp's active tab wears
                        // the amber→red gradient; non-comp keeps the space
                        // accent.
                        background: navComp
                          ? 'linear-gradient(90deg, #ff4d5e, #ffb020)'
                          : (THEME.accent || '#7C4DFF'),
                        borderRadius: '0 0 3px 3px',
                      }} />
                    )}
                    {/* 🤔 The CHAT tab's ICON becomes the typing indicator
                        (Nina 08-06 rev 4, icon-only — the full icon+label
                        swap left the tab briefly nameless; the word CHAT is
                        wayfinding for exactly the new users most likely to
                        wonder): while a local composes and you're elsewhere,
                        the 💬 swaps for three dots, the label stays. WHITE +
                        dark rim (08-02 nav contrast rule: the chrome wears
                        the space theme, accent dots vanish on accent-tinted
                        navs). Dots fill the icon's exact 19px slot, so the
                        swap never jitters the bar. */}
                    {thinkingDots ? (
                      <span data-nav-think="" aria-hidden="true" style={{
                        height: 19, display: 'flex', alignItems: 'center',
                        justifyContent: 'center', gap: 3,
                      }}>
                        <style>{'@keyframes navThinkDot { 0%, 60%, 100% { transform: translateY(0); opacity: 0.55; } 30% { transform: translateY(-3px); opacity: 1; } } @media (prefers-reduced-motion: reduce) { [data-nav-think] span { animation: none !important; opacity: 0.85; } }'}</style>
                        {[0, 1, 2].map(i => (
                          <span key={i} style={{
                            width: 5, height: 5, borderRadius: '50%',
                            backgroundColor: '#fff',
                            boxShadow: '0 0 0 1px rgba(0,0,0,0.35), 0 0 5px rgba(0,0,0,0.5)',
                            animation: `navThinkDot 1.4s ease-in-out ${i * 0.2}s infinite`,
                          }} />
                        ))}
                      </span>
                    ) : (
                    <span style={{ fontSize: 19, lineHeight: 1, display: 'flex', justifyContent: 'center', color: active ? (navComp ? '#ffb020' : (THEME.accent || '#7C4DFF')) : undefined, filter: active ? `drop-shadow(0 0 5px ${navComp ? '#ffb02099' : `${THEME.accent || '#7C4DFF'}99`})` : 'none' }}>
                      {t.iconName ? <LineIcon name={t.iconName} size={19} /> : t.icon}
                    </span>
                    )}
                    {t.label}
                    {(dot || navCount > 0 || partyDot) && !active && (() => {
                      // 🎮 Arcade's dot is GREEN (Nina 08-02): "game opened" is an
                      // invitation, not an alert — red stays for unread/live urgency
                      const c = navCount > 0 ? badgeColor : partyDot ? '#ba68c8' : t.dotKey === 'games' ? '#22c55e' : '#ef4444';
                      return (
                        <span style={{
                          position: 'absolute', top: 3, right: 'calc(50% - 17px)',
                          minWidth: navCount > 0 ? 14 : 8, height: navCount > 0 ? 14 : 8,
                          borderRadius: 7, padding: navCount > 0 ? '0 3px' : 0,
                          backgroundColor: c, color: '#fff',
                          fontSize: 8, fontWeight: 800, lineHeight: '14px', textAlign: 'center',
                          boxShadow: `0 0 6px ${c}99`,
                        }}>
                          {navCount > 0 ? navCount : ''}
                        </span>
                      );
                    })()}
                    {/* 👥 Lobby ready badge: n/max, amber, pulses while the
                        ready check runs. Hidden when the tab is active (you
                        can see the roster). Keyframe rides the badge so it
                        never depends on another mount's style block. */}
                    {t.readyBadge && !active && (
                      <>
                        <style>{'@keyframes compReadyPulse { 0%, 100% { transform: scale(1); opacity: 1; } 50% { transform: scale(1.12); opacity: 0.72; } }'}</style>
                        <span style={{
                          position: 'absolute', top: 2, right: 'calc(50% - 26px)',
                          minWidth: 24, height: 14, borderRadius: 7, padding: '0 4px',
                          backgroundColor: '#f59e0b', color: '#1a1206',
                          fontSize: 9, fontWeight: 800, lineHeight: '14px', textAlign: 'center',
                          fontVariantNumeric: 'tabular-nums',
                          boxShadow: '0 0 6px #f59e0b99',
                          animation: 'compReadyPulse 1.2s ease-in-out infinite',
                        }}>{t.readyBadge}</span>
                      </>
                    )}
                  </button>
                );
              })}
              </div>
            </div>
          );
        })()}
        {/* ⚔️ COMP PLACES LAYERS (08-02, option B; Players added 08-03):
            Players, Recap and Arcade are REAL
            SCREENS over the Battle base — same fixed-layer pattern as the
            non-comp TV/Locals/Arcade places (z4600, above content, under the
            nav 5100 + ticker). Battle keeps its scroll position underneath;
            the old filter+scroll-teleport is dead. Bottom anchors at
            53 + COMP_TICKER_H (the ticker rides the nav on comp phones).
            Hidden during the whodunnit stage takeover (it owns the viewport,
            chrome-free contract). */}
        {/* 👥 PLAYERS place (08-03, Nina go): the comp roster's own screen.
            Same fixed-layer pattern as Recap/Arcade below (z4600, bottom
            53 + COMP_TICKER_H). Outside the lobby this is the roster's ONLY
            mobile mount — the Battle column keeps it only while the ready
            check is live (grep "LOBBY READY VALVE"). Hint refs
            (readyToggleRef etc.) deliberately NOT passed here: the valve
            mount owns them, so the spotlight/hint machinery never has its
            node stolen by this copy during the lobby overlap. */}
        {compPlacesNav && !investigationTakeover && compMobileView === 'players' && (
          <div role="tabpanel" data-tab-name="players" data-swipe-place="" style={{
            position: 'fixed', left: 0, right: 0, top: 0,
            bottom: 'calc(79px + env(safe-area-inset-bottom, 0px))',
            zIndex: 4600, background: THEME.bgDark || '#0d0a14',
            display: 'flex', flexDirection: 'column', overflow: 'hidden',
            paddingTop: 'env(safe-area-inset-top, 0px)',
          }}>
            <div style={{
              flexShrink: 0, display: 'flex', alignItems: 'center', gap: 8,
              padding: '10px 14px 8px', borderBottom: '1px solid rgba(255,255,255,0.08)',
              fontWeight: 800, fontSize: '0.9em', color: THEME.textOnDark || '#fff',
            }}>
              👥 Players
              <span style={{ fontWeight: 600, fontSize: '0.78em', color: THEME.textMuted || '#8f87a3' }}>
                roster · scores · ready
              </span>
            </div>
            <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '10px 12px' }}>
              {activeUsers.length > 0 && !isPortedSpace ? (
                <UsersPanel users={activeUsers} allUsers={users} localStatuses={localStatuses} THEME={THEME} gameId={gameId} chatGameStatus={chatGameStatus} humanPlayerId={humanPlayerId} createdPlayerId={createdPlayerIdRef.current} onQuickJoin={handleQuickJoin} isReadyCheck={chatGameStatus?.is_ready_check && !chatGameStatus?.ready_check_data?.done} readyCheckData={readyCheckData} readyCheckSubmitted={readyCheckSubmitted} readyCheckCountdown={readyCheckCountdown} readyCountdownDuration={readyCountdownDuration} readyCheckPhase={readyCheckPhase} autoStartCountdown={autoStartCountdown} gameStarted={gameStarted} hasJoinedGame={hasJoinedGame} showGlitchReveal={showGlitchReveal} diceSpotlightId={diceRollToast?.pid || null} hideJoinCtas={!!chatGameStatus?.noir_theme && !isBotMode} onReadyUp={async () => {
                  readyCheckDismissedRef.current = true;
                  setReadyCheckSubmitted(true);
                  const myName = chatGameStatus?.players?.find(p => p.id === humanPlayerId)?.name || 'You';
                  setReadyCheckData(prev => ({
                    ...prev,
                    ready_count: (prev?.ready_count || 0) + 1,
                    ready_player_names: [...(prev?.ready_player_names || []), myName]
                  }));
                  try {
                    await chatGameApi.readyUp(gameId, humanPlayerId);
                  } catch (err) {
                    console.error('Ready check response failed:', err);
                  }
                }} onTogglePlay={(isPlay) => {
                  const now = Date.now();
                  if (now - togglePlayDebounceRef.current < 1000) return;
                  togglePlayDebounceRef.current = now;
                  if (gameStarted) return;
                  if (isPlay && chatGameStatus?.eliminated_players?.includes(createdPlayerIdRef.current)) return;
                  if (isPlay && isPlayingRef.current && (chatGameStatus?.competitive_mode || chatGameStatus?.imposter_mode)) return;
                  userSwitchedToPlayRef.current = true;
                  if (isPlay) {
                    const pid = createdPlayerIdRef.current;
                    if (!pid) return;
                    setHumanPlayerId(pid);
                    const url = new URL(window.location);
                    url.searchParams.set('playerId', pid);
                    window.history.replaceState({}, '', url);
                    readyCheckDismissedRef.current = false;
                    setReadyCheckSubmitted(false);
                    chatGameApi.unspectatePlayer(gameId, pid).catch(err =>
                      console.error('[onTogglePlay] unspectate failed:', err.message)
                    );
                  } else {
                    setHumanPlayerId(null);
                    const url = new URL(window.location);
                    url.searchParams.delete('playerId');
                    window.history.replaceState({}, '', url);
                    readyCheckDismissedRef.current = true;
                    setShowReadyCheck(false);
                    setCurrentRequestId(null);
                  }
                }} dismissedHints={dismissedHints} onDismissHint={dismissHint} isMobile={isMobile} hintsHidden={hintsHidden} onDropdownInteract={() => { dropdownInteractedRef.current = true; setShowDropdownHint(false); }} onReadyInteract={() => { readyInteractedRef.current = true; setShowReadyHint(false); }} />
              ) : (
                <div style={{ padding: '28px 12px', textAlign: 'center', color: THEME.textMuted || '#8f87a3', fontSize: '0.85em', fontWeight: 600 }}>
                  No players yet.
                </div>
              )}
            </div>
          </div>
        )}
        {compPlacesNav && !investigationTakeover && compMobileView === 'alerts' && (
          <div role="tabpanel" data-tab-name="recap" data-swipe-place="" style={{
            position: 'fixed', left: 0, right: 0, top: 0,
            bottom: 'calc(79px + env(safe-area-inset-bottom, 0px))',
            zIndex: 4600, background: THEME.bgDark || '#0d0a14',
            display: 'flex', flexDirection: 'column', overflow: 'hidden',
            paddingTop: 'env(safe-area-inset-top, 0px)',
          }}>
            <div style={{
              flexShrink: 0, display: 'flex', alignItems: 'center', gap: 8,
              padding: '10px 14px 8px', borderBottom: '1px solid rgba(255,255,255,0.08)',
              fontWeight: 800, fontSize: '0.9em', color: THEME.textOnDark || '#fff',
            }}>
              📋 Recap
              <span style={{ fontWeight: 600, fontSize: '0.78em', color: THEME.textMuted || '#8f87a3' }}>
                verdicts · votes · twists
              </span>
            </div>
            <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '10px 12px' }}>
              {(() => {
              const layerActions = results.flatMap(r => r.actions || []);
              return (
              <VirtualizedActionList
                portedInteract={isPortedSpace && !!humanPlayerId}
                actions={layerActions.filter(a => !messageActionTypes.has(a.type))}
                playerModelMap={playerModelMap}
                users={users}
                THEME={THEME}
                onActionUpdate={updateAction}
                gameType={gameType}
                allActions={layerActions}
                cachedSpaces={cachedSpaces}
                cachedRoundAnalysisHistory={cachedRoundAnalysisHistory}
                onReactionSubmit={handleReactionSubmit}
                humanPlayerId={humanPlayerId}
                effectivePlayerId={effectivePlayerId}
                showThoughts={showThoughts}
                imposterMode={chatGameStatus?.imposter_mode || false}
                competitiveMode={chatGameStatus?.competitive_mode || chatGameStatus?.imposter_mode || false}
                humanTokensLeft={tokensLeft}
                uiTerms={chatGameStatus?.ui_terms || null}
                noirTheme={!!chatGameStatus?.noir_theme}
              />
              );
              })()}
            </div>
          </div>
        )}
        {compPlacesNav && !investigationTakeover && compMobileView === 'games' && (
          <div role="tabpanel" data-tab-name="arcade" data-swipe-place="" style={{
            position: 'fixed', left: 0, right: 0, top: 0,
            bottom: 'calc(79px + env(safe-area-inset-bottom, 0px))',
            zIndex: 4600, background: THEME.bgDark || '#0d0a14',
            display: 'flex', flexDirection: 'column', overflow: 'hidden',
            paddingTop: 'env(safe-area-inset-top, 0px)',
          }}>
            <div style={{
              flexShrink: 0, display: 'flex', alignItems: 'center', gap: 8,
              padding: '10px 14px 8px', borderBottom: '1px solid rgba(255,255,255,0.08)',
              fontWeight: 800, fontSize: '0.9em', color: THEME.textOnDark || '#fff',
            }}>
              🕹️ Arcade
            </div>
            <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '10px 12px' }}>
              <GamesPanel THEME={THEME} />
            </div>
          </div>
        )}
        {/* 👻 GHOST EDGE TAB (Nina 07-31, mock-approved) — replaces the dock's
            full-width ticker bar for ACTIVE players on phones, ALL chat-game
            spaces (spectators/ghosts keep their composer bar: chat is their
            whole game and their bar also hosts the lobby join card — rehoming
            that is its own pass). A half-capsule hanging on the right edge at
            ~62% down (one-thumb zone; LofiRadio's FAB owns top-right): 👻 plus
            the UNREAD count inside the tab (Nina: overlapping badge read as
            crowded; total-count means nothing so only unread renders). Tap =
            the existing ghost sheet. Mounted OUTSIDE the player dock because
            the dock's backdropFilter makes it the containing block for fixed
            descendants. Hidden while: sheet open · typing (keyboard owns the
            edge) · collect-clues takeover (chrome-free stage contract) ·
            watch-party theater. First open ever: a one-time "Ghost Chat"
            flyout label rides the tab (reuses ghostTickerHintSeen). */}
        {isMobile && (gameType === 'chat-game' || gameType === 'chat') && !isBotMode
          && chatGameStatus && !ghostSheetOpen && !typingFocus
          && ghostSurfacesOn
          && !investigationTakeover
          // 👻 Places phones lost this tab entirely (Nina 08-02): Chat has
          // the Room|Ghost lane bar, TV/Locals/Arcade have the NAV PERCH
          // (grep GHOST NAV PERCH). Comp places phones lost it too (08-02
          // later, option B): the ghost is a TAB on the phase ticker now —
          // only non-places phones (other game types) keep the capsule.
          && !placesNav && !compPlacesNav
          // theater hides the tab only while it's actually ON SCREEN — with
          // places nav a seated party parks audio-only under Chat
          && !(watchParty && inTheater && (!placesNav || mobileView === 'tv'))
          && humanPlayerId && !effectiveIsSpectating
          && !chatGameStatus?.eliminated_players?.includes(humanPlayerId)
          && spectatingMessages.length > 0 && (() => {
          const unread = Math.max(0, spectatingMessages.length - ghostSeenCountRef.current);
          return (
            <div style={{ position: 'fixed', right: 0, top: '62%', zIndex: 4800, display: 'flex', alignItems: 'center', gap: 6 }}>
              <style>{`
                @keyframes ghostTabShiver { 0%, 86%, 100% { transform: translateX(0); } 88% { transform: translateX(-2.5px); } 90% { transform: translateX(1.5px); } 92% { transform: translateX(-1.5px); } 94% { transform: translateX(0); } }
                @media (prefers-reduced-motion: reduce) { .ghost-edge-tab { animation: none !important; } }
              `}</style>
              {!ghostHintSeen && (
                <span style={{
                  background: 'rgba(14,17,24,0.88)', color: '#c9bff5', fontSize: '0.7em',
                  fontWeight: 700, letterSpacing: '0.04em', padding: '4px 9px', borderRadius: 8,
                  border: '1px solid rgba(106,90,205,0.4)', whiteSpace: 'nowrap',
                }}>Ghost Chat</span>
              )}
              <button
                className="ghost-edge-tab"
                onClick={() => {
                  ghostSeenCountRef.current = spectatingMessages.length;
                  setGhostSheetOpen(true);
                  if (!ghostHintSeen) {
                    setGhostHintSeen(true);
                    try { localStorage.setItem('ghostTickerHintSeen', '1'); } catch (e) { /* private mode */ }
                  }
                }}
                aria-label={unread > 0 ? `Open ghost chat, ${unread} unread` : 'Open ghost chat'}
                style={{
                  display: 'flex', flexDirection: 'column', alignItems: 'center',
                  justifyContent: 'center', gap: 3, width: 38, minHeight: 52,
                  // Visual sits a hair in from the true edge so an iOS
                  // forward-swipe that starts here doesn't misread; padding
                  // keeps the hit area ≥44px.
                  padding: '8px 4px 8px 2px',
                  background: 'rgba(14,17,24,0.95)', // no blur: this tab ANIMATES (shiver) — a moving backdrop-filter re-samples every frame (perf 08-13)
                  border: `1px solid ${unread > 0 ? 'rgba(196,97,79,0.6)' : 'rgba(127,166,184,0.55)'}`,
                  borderRight: 'none', borderRadius: '12px 0 0 12px',
                  boxShadow: '0 4px 14px rgba(0,0,0,0.5)', cursor: 'pointer',
                  animation: unread > 0 ? 'ghostTabShiver 4s ease-in-out infinite' : 'none',
                }}
              >
                <span style={{ fontSize: 16, lineHeight: 1 }}>👻</span>
                {unread > 0 && (
                  <span style={{
                    fontSize: 10, fontWeight: 800, color: '#E88A74', lineHeight: 1,
                    fontVariantNumeric: 'tabular-nums',
                  }}>{unread > 99 ? '99+' : unread}</span>
                )}
              </button>
            </div>
          );
        })()}

        {/* 🕵️🧰 EDGE TABS 08-03 (Nina, mock-approved): Dossier + Shelf dock to
            the viewport's right edge on desktop — fixed at PAGE level, so no
            scroller can bury them (the 07-25 poll-card trap) and no
            backdropFilter ancestor hijacks the fixed positioning (the sticky
            tab strip would have). Width 46 + content-sized height so the
            vertical labels always fit (Nina's mock note). z60: above the
            sticky strip (25) and the drawers (40/41), so a tab click still
            toggles its drawer closed. Same manila/wood materials. */}
        {/* Comp/imposter gate (Nina 08-14): these tabs rendered on desktop
            competitive games while the drawers they open are comp-gated —
            two dead furniture tabs. Same exclusion the drawers use. */}
        {/* 🏡 W2d: not on a place page (its SHELF is in the ⋯ menu) */}
        {!isMobile && gameId && (gameType === 'chat-game' || gameType === 'chat') && !!chatGameStatus
          && !chatGameStatus.competitive_mode && !chatGameStatus.imposter_mode && !portedPlaceOn
          && !shellPreset /* 🧱 the frame's rails + ⋯ hold its tools */ && (
          <div style={{
            position: 'fixed', right: 0, top: '27%', zIndex: 60, display: 'flex', flexDirection: 'column', gap: 8,
            // 🚪 postcard (09-18): this stack is fixed at PAGE level, outside
            // the fading pad, so it used to float alone over the arrival art.
            ...(roomCameraOn ? { opacity: roomCurtain ? 0 : 1, transition: `opacity ${roomCurtain ? 250 : 500}ms ease` } : {}),
            ...(roomCameraOn && roomCurtain ? { pointerEvents: 'none' } : {}),
          }}>
            {/* 🪟 MIND edge-tab retired for ported DESKTOP (Nina 08-28): the Mind
                panel now lives always-visible in the Hearthside Rail, so this
                collapsed tab + slide-out drawer would duplicate it. Non-ported
                keeps the DOSSIER tab; mobile keeps its own drawer tabs. The
                SHELF tab below stays — it opens SpaceShelf (/build artifacts),
                a different shelf from the rail's keepsakes. */}
            {!isPortedSpace && (
            <button
              onClick={() => { setShelfOpen(false); setHouseWallOpen(false); setDossierOpen(o => !o); }}
              title={dossierOpen
                ? (isPortedSpace ? 'Close the mind panel' : 'Close the case file')
                : (isPortedSpace ? 'See what they see' : 'Open the locals case file')}
              aria-label={isPortedSpace ? 'Mind panel' : 'Locals case file'}
              style={{
                display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8,
                width: 46, padding: '13px 9px 13px 7px', cursor: 'pointer',
                background: dossierOpen
                  ? 'linear-gradient(90deg, #f6e7bc, #e6cd8e)'
                  : 'linear-gradient(90deg, #eed9a4, #d9ba72)',
                color: '#4a3a17', border: '1px solid #b28f4a', borderRight: 'none',
                borderRadius: '12px 0 0 12px', fontWeight: 800, fontSize: '0.66em',
                letterSpacing: '0.14em', position: 'relative',
                boxShadow: '-4px 6px 16px rgba(0,0,0,0.45)',
              }}
            >
              <LineIcon name="dossier" size={15} />
              <span style={{ writingMode: 'vertical-rl', textOrientation: 'mixed' }}>{isPortedSpace ? 'MIND' : 'DOSSIER'}</span>
              {dossierUnseen > 0 && !dossierOpen && (
                <span style={{
                  position: 'absolute', top: -5, left: -5,
                  minWidth: 16, height: 16, borderRadius: 8, padding: '0 4px',
                  background: '#ef4444', color: '#fff', fontSize: 10,
                  fontWeight: 800, lineHeight: '16px', textAlign: 'center',
                }}>{dossierUnseen}</span>
              )}
            </button>
            )}
            <button
              onClick={() => { setDossierOpen(false); setHouseWallOpen(false); setShelfOpen(o => !o); }}
              title={shelfOpen ? 'Close the shelf' : 'Open the space shelf'}
              aria-label="Space shelf"
              data-prop-ui=""
              style={{
                display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8,
                width: 46, padding: '13px 9px 13px 7px', cursor: 'pointer',
                background: shelfOpen
                  ? 'linear-gradient(90deg, #c69a63, #a5794a)'
                  : 'linear-gradient(90deg, #b98d54, #96683a)',
                '--prop-bg': shelfOpen
                  ? 'linear-gradient(90deg, #c69a63, #a5794a)'
                  : 'linear-gradient(90deg, #b98d54, #96683a)',
                color: '#fdf3dd', border: '1px solid #7a5227', borderRight: 'none',
                borderRadius: '12px 0 0 12px', fontWeight: 800, fontSize: '0.66em',
                letterSpacing: '0.14em', position: 'relative',
                boxShadow: '-4px 6px 16px rgba(0,0,0,0.45)',
              }}
            >
              <LineIcon name="shelf" size={15} />
              <span style={{ writingMode: 'vertical-rl', textOrientation: 'mixed' }}>SHELF</span>
              {shelfUnseen > 0 && !shelfOpen && (
                <span style={{
                  position: 'absolute', top: -5, left: -5,
                  minWidth: 16, height: 16, borderRadius: 8, padding: '0 4px',
                  background: '#ef4444', color: '#fff', fontSize: 10,
                  fontWeight: 800, lineHeight: '16px', textAlign: 'center',
                }}>{shelfUnseen}</span>
              )}
            </button>
            {/* 🏠🧠 Third tab: the memory wall — Claude House only (cork
                material to match its drawer; emoji icon, LineIcons has no
                pin). */}
            {houseStatus && (
              <button
                onClick={() => { setDossierOpen(false); setShelfOpen(false); setHouseWallOpen(o => !o); }}
                title={houseWallOpen ? 'Close the memory wall' : 'Open the house memory wall'}
                aria-label="House memory wall"
                style={{
                  display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8,
                  width: 46, padding: '13px 9px 13px 7px', cursor: 'pointer',
                  background: houseWallOpen
                    ? 'linear-gradient(90deg, #dcb488, #bd9464)'
                    : 'linear-gradient(90deg, #d2a878, #b3865a)',
                  color: '#3b2a1a', border: '1px solid #8a5f33', borderRight: 'none',
                  borderRadius: '12px 0 0 12px', fontWeight: 800, fontSize: '0.66em',
                  letterSpacing: '0.14em', position: 'relative',
                  boxShadow: '-4px 6px 16px rgba(0,0,0,0.45)',
                }}
              >
                <span style={{ fontSize: 15, lineHeight: 1 }}>📌</span>
                <span style={{ writingMode: 'vertical-rl', textOrientation: 'mixed' }}>MEMORY</span>
              </button>
            )}
            {/* 🎛️👻 GHOST tab (Nina 09-15, canvas-ruled): collapsed ghost chat
                joins THIS stack as a 4th tab — same width/radius/shadow
                grammar so the edge speaks one language; dashed slate keeps
                the ghost-lane identity; unread badge in the shelf style,
                counting only messages since the collapse anchor (never
                history). Clicking reopens the right column. */}
            {canToggleSidebars && !ghostPanelOpen && !crowdInline && (() => {
              const base = ghostCollapseBaselineRef.current;
              const unread = base === null ? 0 : Math.max(0, spectatingMessages.length - base);
              return (
                <button
                  onClick={() => setGhostPanelOpen(true)}
                  title={unread > 0 ? `Show ghost chat — ${unread} new` : 'Show ghost chat'}
                  aria-label={unread > 0 ? `Show ghost chat, ${unread} unread` : 'Show ghost chat'}
                  style={{
                    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8,
                    width: 46, padding: '13px 9px 13px 7px', cursor: 'pointer',
                    background: 'linear-gradient(90deg, #cfd8dc, #a7b6bd)',
                    color: '#2f3d44', border: '1px dashed #78909C', borderRight: 'none',
                    borderRadius: '12px 0 0 12px', fontWeight: 800, fontSize: '0.66em',
                    letterSpacing: '0.14em', position: 'relative',
                    boxShadow: '-4px 6px 16px rgba(0,0,0,0.45)',
                  }}
                >
                  {unread > 0 && (
                    <span style={{
                      position: 'absolute', top: -6, left: -6, minWidth: 18, height: 18,
                      padding: '0 4px', borderRadius: 999, background: '#d63b3b', color: '#fff',
                      fontSize: 11, fontWeight: 800, lineHeight: '18px', textAlign: 'center',
                      boxShadow: '0 1px 4px rgba(0,0,0,0.5)', fontVariantNumeric: 'tabular-nums',
                    }}>{unread > 99 ? '99+' : unread}</span>
                  )}
                  <span style={{ fontSize: 15, lineHeight: 1 }}>👻</span>
                  <span style={{ writingMode: 'vertical-rl', textOrientation: 'mixed' }}>GHOST</span>
                </button>
              );
            })()}
          </div>
        )}

        {/* 👻 GHOST LANE FEED (08-01, variant A) — the Chat place's feed swap:
            a fixed layer over the chat scroller (same geometry as the places
            layer, z4600 under the dock 5000 / nav 5100) holding the REAL
            spectator panel read-only — the dock owns the input while the lane
            is on. Bottom padding clears the ghost-mode dock (bar + input).
            Deliberately stays up while typing: trading off lanes mid-thought
            is the whole point; the dock's visualViewport lift covers the
            input, so the old sheet-under-keyboard bug does not apply here. */}
        {placesNav && mobileView === 'chat' && ghostLane && ghostSurfacesOn && (
          <div style={{
            position: 'fixed', top: 0, left: 0, right: 0,
            bottom: NAV_BOTTOM,
            zIndex: 4600, display: 'flex', flexDirection: 'column',
            background: 'rgba(10,10,16,0.98)',
            padding: '12px 10px 150px',
          }}>
            <div style={{
              display: 'flex', alignItems: 'center', gap: 8, padding: '2px 4px 10px',
              fontSize: '0.72em', fontWeight: 800, letterSpacing: '1.4px',
              textTransform: 'uppercase', color: '#b8aef2',
            }}>
              👻 Ghost lane · the room can&apos;t hear you
            </div>
            <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
              <SpectatorChatPanel
                messages={ghostPanelMessages}
                watchPartyActive={!!watchParty} seatsAsCrowd={!(chatGameStatus?.competitive_mode || chatGameStatus?.imposter_mode)}
                senderName={users.find(u => u.game_player_id === humanPlayerId)?.name || 'You'}
                isEliminated={false}
                theme={THEME}
                users={users}
                isMobile
                readOnly
                fillContainer
                forceExpand
                // The panel's own collapse arrow inside this fixed layer left
                // a blank dark screen (Nina 08-02) — collapsing now EXITS the
                // ghost lane back to Room instead.
                onExpandChange={(exp) => { if (!exp) setGhostLane(false); }}
                onLoadOlder={loadOlderSpectatorMessages}
                isLoadingOlder={isLoadingOlderSpectatorMessages}
                hasMore={hasMoreSpectatorMessages}
              />
            </div>
          </div>
        )}

        {/* 👻 Ghost sheet (thumb-zone dock stage 1, 07-17) — the full ghost
            feed slides up from the dock's ticker; same data + handlers as the
            inline panel it replaces on phones. Backdrop tap closes. */}
        {isMobile && ghostSheetOpen && ghostSurfacesOn && (
          <div
            onClick={() => { ghostSeenCountRef.current = spectatingMessages.length; setGhostSheetOpen(false); }}
            style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 150000, background: 'rgba(0,0,0,0.5)' }}
          >
            <div ref={ghostSheetRef} onClick={(e) => e.stopPropagation()} style={{
              position: 'absolute', left: 0, right: 0, bottom: 0, height: '68dvh',
              background: 'rgba(14,12,24,0.98)', borderTop: '1.5px solid #6a5acd',
              borderRadius: '18px 18px 0 0', display: 'flex', flexDirection: 'column',
              padding: '10px 10px calc(10px + env(safe-area-inset-bottom, 0px))',
              animation: 'toastSlideUp 0.3s ease-out',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '2px 4px 8px', color: '#cfc6f7', fontWeight: 800, fontSize: '0.9em', letterSpacing: '0.5px' }}>
                👻 GHOST CHAT
                <button
                  onClick={() => { ghostSeenCountRef.current = spectatingMessages.length; setGhostSheetOpen(false); }}
                  aria-label="Close ghost chat"
                  style={{ marginLeft: 'auto', border: '1px solid #4b3f8f', background: 'none', color: '#cfc6f7', width: 30, height: 30, borderRadius: '50%', cursor: 'pointer' }}
                >✕</button>
              </div>
              <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                <SpectatorChatPanel
                  messages={ghostPanelMessages}
                  watchPartyActive={!!watchParty} seatsAsCrowd={!(chatGameStatus?.competitive_mode || chatGameStatus?.imposter_mode)}
                  onSubmit={handleSpectatingSubmit}
                  senderName={users.find(u => u.game_player_id === humanPlayerId)?.name || 'You'}
                  isEliminated={!!(humanPlayerId && chatGameStatus?.eliminated_players?.includes(humanPlayerId))}
                  theme={THEME}
                  users={users}
                  isMobile
                  fillContainer
                  forceExpand
                  label={(!chatGameStatus?.competitive_mode && !chatGameStatus?.imposter_mode) ? '👻 Ghost Chat' : undefined}
                  onLoadOlder={loadOlderSpectatorMessages}
                  isLoadingOlder={isLoadingOlderSpectatorMessages}
                  hasMore={hasMoreSpectatorMessages}
                  onExpandChange={(e) => { spectatorPanelExpandedRef.current = e; }}
                  onScrolledUpChange={(s) => { spectatorPanelScrolledUpRef.current = s; }}
                  isInitialLoading={isInitialSpectatorHistoryLoading}
                  onWatchLive={handleWatchLiveFromChat}
                />
              </div>
            </div>
          </div>
        )}
        {/* 🎉 quest complete beat — effect-driven, shows once */}
        {!isBotMode && tutorialCelebrating && (
          <div style={{
            position: 'fixed', ...(isMobile ? { top: '12px', right: '52px' } : { left: '14px', bottom: '86px' }), zIndex: 9000,
            borderRadius: '12px', padding: '12px 16px',
            background: 'rgba(18,34,20,0.95)', border: '1.5px solid rgba(126,231,135,0.5)',
            boxShadow: '0 6px 24px rgba(0,0,0,0.45)', color: '#7ee787', fontWeight: 800, fontSize: '0.85em',
          }}>
            🎓 You know the ropes! Enjoy the space 🎉
          </div>
        )}

        {showGlitchReveal && (
          <div style={{
            position: 'fixed',
            top: 0, left: 0, right: 0, bottom: 0,
            zIndex: 300000,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'radial-gradient(circle, rgba(10,10,20,0.98) 0%, rgba(0,0,0,0.99) 100%)',
            animation: 'glitchRevealFadeIn 0.5s ease-out',
            overflow: 'hidden',
          }}>
            {/* Floating particles */}
            {Array.from({ length: 20 }).map((_, i) => {
              const isGlitch = showGlitchReveal.isImposter;
              const colors = isGlitch
                ? ['#ff3333', '#ff6644', '#ff0000', '#cc0000']
                : ['#33ff88', '#66ffaa', '#00cc66', '#33ccff'];
              const c = colors[i % colors.length];
              const left = Math.random() * 100;
              const delay = Math.random() * 3;
              const dur = 4 + Math.random() * 4;
              const size = 2 + Math.random() * 4;
              return (
                <span key={`grp-${i}`} style={{
                  position: 'absolute',
                  left: `${left}%`,
                  bottom: '-10px',
                  width: `${size}px`,
                  height: `${size}px`,
                  borderRadius: '50%',
                  backgroundColor: c,
                  opacity: 0.6,
                  animation: `glitchParticleFloat ${dur}s ease-in ${delay}s infinite`,
                  pointerEvents: 'none',
                }} />
              );
            })}

            {/* Phase 1: Scanning text — fades out after 1.8s */}
            <div style={{
              position: 'absolute',
              animation: 'glitchScanPhase 2.2s ease-in-out forwards',
              textAlign: 'center',
            }}>
              <div style={{
                fontSize: isMobile ? '1.4em' : '1.8em',
                fontWeight: 700,
                color: 'rgba(255,255,255,0.6)',
                letterSpacing: '6px',
                textTransform: 'uppercase',
                animation: 'glitchScanPulse 0.6s ease-in-out infinite',
              }}>
                Scanning players...
              </div>
              <div style={{
                width: isMobile ? '200px' : '280px',
                height: '3px',
                margin: '20px auto 0',
                borderRadius: '2px',
                background: 'rgba(255,255,255,0.1)',
                overflow: 'hidden',
              }}>
                <div style={{
                  width: '100%',
                  height: '100%',
                  background: 'linear-gradient(90deg, #ffd700, #ffaa00)',
                  animation: 'glitchScanBar 1.8s ease-in-out forwards',
                  borderRadius: '2px',
                }} />
              </div>
            </div>

            {/* Phase 2: Role reveal — appears after scan completes */}
            <div style={{
              opacity: 0,
              animation: 'glitchRevealAppear 0.8s ease-out 2.2s forwards',
              textAlign: 'center',
              padding: '0 24px',
              maxWidth: '500px',
            }}>
              {showGlitchReveal.isImposter ? (
                <>
                  <div style={{ fontSize: isMobile ? '2.5em' : '3.5em', marginBottom: '8px' }}>
                    🐛
                  </div>
                  <div style={{
                    fontSize: isMobile ? '1.6em' : '2.4em',
                    fontWeight: 900,
                    color: '#ff3333',
                    textShadow: '0 0 40px rgba(255,0,0,0.6), 0 0 80px rgba(255,0,0,0.3)',
                    letterSpacing: isMobile ? '2px' : '4px',
                    animation: 'glitchTextFlicker 0.8s ease-in-out infinite alternate',
                    marginBottom: '16px',
                    lineHeight: 1.2,
                  }}>
                    {showGlitchReveal.doodle ? <>YOU ARE THE<br />DOODLE BUG</> : <>YOU ARE THE<br />GOLDEN GLITCH</>}
                  </div>
                  {/* 🐛 Doodle Bug twist: the imposter DOES get a prompt, just
                      not the group's — show it (topic = bug_prompt from status) */}
                  {showGlitchReveal.doodle && showGlitchReveal.topic && (
                    <div style={{
                      fontSize: isMobile ? '1.3em' : '1.6em',
                      fontWeight: 900,
                      color: '#ffd700',
                      textShadow: '0 0 30px rgba(255,215,0,0.5)',
                      letterSpacing: '1px',
                      marginBottom: '14px',
                    }}>
                      Secretly draw: &ldquo;{showGlitchReveal.topic}&rdquo;
                    </div>
                  )}
                  <div style={{
                    fontSize: isMobile ? '1em' : '1.15em',
                    color: 'rgba(255,100,100,0.8)',
                    lineHeight: 1.6,
                    marginBottom: '28px',
                  }}>
                    {showGlitchReveal.doodle ? (
                      <>Everyone else shares a different prompt.<br />Blend your strokes in — or get caught.</>
                    ) : (
                      <>You don't know the topic.<br />Say words that blend in — or get caught.</>
                    )}
                  </div>
                  <button
                    onClick={() => setShowGlitchReveal(null)}
                    style={{
                      padding: '14px 36px',
                      borderRadius: '24px',
                      border: '2px solid rgba(255,50,50,0.5)',
                      background: 'rgba(255,50,50,0.2)',
                      color: '#ff6666',
                      fontSize: '1.1em',
                      fontWeight: 700,
                      cursor: 'pointer',
                      letterSpacing: '1px',
                      transition: 'all 0.2s',
                    }}
                  >
                    Got it — let's go
                  </button>
                </>
              ) : (
                <>
                  <div style={{ fontSize: isMobile ? '2.5em' : '3.5em', marginBottom: '8px' }}>
                    😇
                  </div>
                  <div style={{
                    fontSize: isMobile ? '1.3em' : '1.8em',
                    fontWeight: 900,
                    color: '#33ff88',
                    textShadow: '0 0 40px rgba(50,255,100,0.5)',
                    letterSpacing: isMobile ? '1px' : '3px',
                    marginBottom: '24px',
                    animation: 'glitchRevealPulse 2s ease-in-out infinite',
                    lineHeight: 1.2,
                  }}>
                    {showGlitchReveal.doodle ? 'YOU ARE NOT THE DOODLE BUG' : 'YOU ARE NOT THE GOLDEN GLITCH'}
                  </div>
                  <div style={{
                    fontSize: isMobile ? '1em' : '1.15em',
                    fontWeight: 600,
                    color: 'rgba(255,255,255,0.6)',
                    marginBottom: '6px',
                    letterSpacing: '2px',
                    textTransform: 'uppercase',
                  }}>
                    {showGlitchReveal.doodle ? 'The secret prompt is' : 'The secret topic is'}
                  </div>
                  <div style={{
                    fontSize: isMobile ? '1.6em' : '2em',
                    fontWeight: 900,
                    color: '#ffd700',
                    textShadow: '0 0 30px rgba(255,215,0,0.5), 0 0 60px rgba(255,180,0,0.2)',
                    letterSpacing: '2px',
                    marginBottom: '20px',
                    opacity: 0,
                    animation: 'glitchTopicReveal 0.6s ease-out 3s forwards',
                  }}>
                    &ldquo;{showGlitchReveal.topic || '???'}&rdquo;
                  </div>
                  <div style={{
                    fontSize: isMobile ? '0.9em' : '1em',
                    color: 'rgba(255,255,255,0.45)',
                    marginBottom: '28px',
                    lineHeight: 1.5,
                  }}>
                    {showGlitchReveal.doodle
                      ? 'Draw toward it, 2 strokes a turn. One of you is drawing something ELSE!'
                      : "Say words related to the topic — but don't make it too obvious!"}
                  </div>
                  <button
                    onClick={() => setShowGlitchReveal(null)}
                    style={{
                      padding: '14px 36px',
                      borderRadius: '24px',
                      border: '2px solid rgba(50,255,100,0.4)',
                      background: 'rgba(50,255,100,0.15)',
                      color: '#33ff88',
                      fontSize: '1.1em',
                      fontWeight: 700,
                      cursor: 'pointer',
                      letterSpacing: '1px',
                      transition: 'all 0.2s',
                    }}
                  >
                    Got it — let's go
                  </button>
                </>
              )}
            </div>

            <style>{`
              @keyframes glitchRevealFadeIn {
                from { opacity: 0; }
                to { opacity: 1; }
              }
              @keyframes glitchScanPhase {
                0%, 75% { opacity: 1; }
                100% { opacity: 0; pointer-events: none; }
              }
              @keyframes glitchScanPulse {
                0%, 100% { opacity: 0.6; }
                50% { opacity: 1; }
              }
              @keyframes glitchScanBar {
                0% { width: 0%; }
                100% { width: 100%; }
              }
              @keyframes glitchRevealAppear {
                0% { opacity: 0; transform: scale(0.85); }
                100% { opacity: 1; transform: scale(1); }
              }
              @keyframes glitchTopicReveal {
                0% { opacity: 0; transform: translateY(8px); }
                100% { opacity: 1; transform: translateY(0); }
              }
              @keyframes glitchTextFlicker {
                0% { opacity: 1; transform: translateX(0); }
                20% { opacity: 0.85; transform: translateX(-2px); }
                40% { opacity: 1; transform: translateX(1px); }
                60% { opacity: 0.9; transform: translateX(-1px); }
                80% { opacity: 1; transform: translateX(0); }
                100% { opacity: 0.95; transform: translateX(1px); }
              }
              @keyframes glitchRevealPulse {
                0%, 100% { opacity: 1; }
                50% { opacity: 0.8; }
              }
              @keyframes glitchParticleFloat {
                0% { transform: translateY(0); opacity: 0; }
                10% { opacity: 0.6; }
                90% { opacity: 0.6; }
                100% { transform: translateY(-100vh); opacity: 0; }
              }
            `}</style>
          </div>
        )}

        {/* Typing Indicator. Desktop: fixed bottom-left, yields to the WYWA
            recap (shared bottom band, 08-02 audit #6). Phones (Nina 08-03):
            TOP-center compact pill — the bottom band is where the composer,
            polls, jump cluster and whispers already fight for room, and the
            full pill ate a message-row of it. Top edge is otherwise unowned
            on mobile chat (toasts pass through at 18px; we sit at 10px and
            below their z so a toast still wins for its few seconds). */}
        {/* 🤔 08-06 (Nina): while the mobile dock is OPEN the status rides
            inside the composer strip instead (composerThinkingInline →
            HumanInputForm thinkingStatus) — two indicators would be noise.
            Off the Chat place the CHAT nav tab's pulsing dots carry the
            liveness (same day) — the chip stops floating over TV/Locals/
            Arcade. Comp places nav keeps the chip everywhere for now. */}
        {currentlyTyping && !wywaVisible && (gameType === 'chat-game' || gameType === 'chat') && !composerThinkingInline() && !shellPreset /* 🧱 it rides the frame's composer */
          && !(isMobile && placesNav && mobileView !== 'chat') && (
          (isMobile && !chatGameStatus?.competitive_mode && !chatGameStatus?.imposter_mode) ? (
            // 🤔 BARE LINE (Nina 08-06, mock 4 option B — the chip's container
            // was the offense): non-comp phones get subtitle-style text
            // floating just above the dock, where the reply will materialize.
            // Crowding math: with the dock OPEN the composer strip carries the
            // status (composerThinkingInline suppresses this render), so this
            // line only ever shows over the FOLDED dock / ghost-lane states —
            // the bottom band's emptiest moments. It overlays (never pushes
            // layout), caps at 70vw so the jump cluster's right-corner arrows
            // stay clear, and hides while typing (the dock translate owns the
            // keyboard; a status line isn't worth mispositioning over it).
            // Same measured-dock anchor as the jump cluster. Keyframes come
            // from TypingIndicator's global stylesheet (imported above).
            !typingFocus && !ghostSheetOpen && (
              <div
                aria-live="polite"
                ref={(el) => {
                  if (!el) return;
                  const dockH = inputFormRef.current?.offsetHeight || 62;
                  // No poll-strip stacking any more — the receipt moved to the
                  // top of the screen (08-07), so this band is its own again.
                  el.style.bottom = navBottomPlus(dockH + 4);
                }}
                style={{
                  position: 'fixed', left: 12, maxWidth: '70vw',
                  zIndex: 4950, pointerEvents: 'none',
                  display: 'flex', alignItems: 'center', gap: 6,
                  color: '#fff', fontSize: '0.78em', fontWeight: 600,
                  textShadow: '0 1px 4px rgba(0,0,0,0.9), 0 0 10px rgba(0,0,0,0.6)',
                  animation: 'slideInUp 0.25s ease',
                }}
              >
                {/* 🕯️ in the house the 🤔 and dots give way to the candle hop (09-24) */}
                {!roomCameraOn && <span aria-hidden style={{ animation: 'thinking 1.5s ease-in-out infinite' }}>🤔</span>}
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>
                  {currentlyTyping} is thinking
                </span>
                {roomCameraOn ? <CandleHop style={{ fontSize: '1.15em' }} /> : (
                <span aria-hidden style={{ display: 'flex', gap: 3, alignItems: 'center' }}>
                  {[0, 1, 2].map(i => (
                    <span key={i} style={{
                      width: 5, height: 5, borderRadius: '50%',
                      backgroundColor: '#fff',
                      boxShadow: '0 0 3px rgba(0,0,0,0.7)',
                      animation: `dotBounce 1.4s ease-in-out ${i * 0.2}s infinite`,
                    }} />
                  ))}
                </span>
                )}
              </div>
            )
          ) : (
          <div style={{
            position: 'fixed',
            ...(isMobile ? {
              top: 'calc(10px + env(safe-area-inset-top, 0px))',
              // TOAST_CENTERED, not left:50% — the typing pill is short so it
              // never collapsed as badly as the couch invite, but it is the
              // same sizing bug and would bite the moment a name runs long.
              ...TOAST_CENTERED,
              maxWidth: 'calc(100vw - 24px)',
            } : { bottom: 16, left: 16 }),
            // Below the dock (5000) now that it can never overlap it; the old
            // 5150 out-ranked the dock because overlap was unavoidable.
            zIndex: isMobile ? 4950 : 1000,
            pointerEvents: 'none',
          }}>
            <TypingIndicator
              playerName={currentlyTyping}
              theme={THEME}
              compact={isMobile}
              candle={roomCameraOn}
            />
          </div>
          )
        )}

        {/* 🗳️ Poll receipt bar (Nina 08-07) — pinned to the VERY TOP of the
            screen, full bleed, square corners: a notification, not a pill.
            Light gray surface with dark text so it reads as system chrome
            rather than room content, and so it survives bright space art
            without needing an accent tint. Lives for the poll's whole life
            and unmounts itself when the poll closes. Tap = reopen the card to
            change your vote (lands you on Chat with the dock open, since
            that's where the card mounts). */}
        {(() => {
          const strip = pollReceiptStripInfo();
          if (!strip) return null;
          return (
            <button
              onClick={() => {
                if (placesNav && mobileView !== 'chat') setMobileView('chat');
                setPollCollapsedId(null);
                setComposerCollapsed(false);
              }}
              style={{
                position: 'fixed', top: 0, left: 0, right: 0, zIndex: 4950,
                display: 'flex', alignItems: 'center', gap: 8,
                width: '100%', boxSizing: 'border-box',
                background: '#e9e9ee', textShadow: 'none',
                borderRadius: 0, border: 'none',
                borderBottom: '1px solid rgba(0,0,0,0.16)',
                padding: 'calc(env(safe-area-inset-top, 0px) + 9px) 14px 9px',
                color: '#1b1b22', fontSize: '0.78em', fontWeight: 600,
                cursor: 'pointer', textAlign: 'left',
                boxShadow: '0 2px 10px rgba(0,0,0,0.35)',
                animation: 'slideInDown 0.25s ease',
              }}
            >
              <span aria-hidden>🗳️</span>
              <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {strip.mine
                  ? `You voted ${strip.mine.emoji ? `${strip.mine.emoji} ` : ''}${strip.mine.label}`
                  : strip.poll.question}
              </span>
              <span style={{ flexShrink: 0, color: '#5b5b6b', fontWeight: 700 }}>
                {strip.mine ? 'change' : 'vote'}
              </span>
            </button>
          );
        })()}

        {/* ⬆️⬇️ jump cluster (08-03, audit #12): ONE fixed page-level cluster
            for BOTH breakpoints — sticky-in-the-container could never pin
            (phones: the document scrolls; virtualized feeds: the container
            never scrolls; the overflow:hidden center column blocks viewport
            pinning outright) and its inert flow position collided with the
            case-file folder tabs (probe-identified). Fixed at page level it
            sits outside the dock's backdropFilter containing block and the
            swipe nudge transforms. Anchors (callback ref, direct style
            write — the measured-reserve pattern): phones = the MEASURED dock
            top, +COMP_TICKER_H like every comp bottom anchor; desktop = the
            ACTIVE feed scroller's bottom-right corner (the folder tabs live
            in flow BELOW the feed, so the lanes never cross). Renders only
            where the feed IS the visible surface: Chat place / comp Battle /
            feed lanes — never over place layers, panel tabs, an open
            drawer, or the investigation stage. 44px = phone hit-target
            floor; desktop keeps the quiet 36. */}
        {/* 🎭 Scene intro splash — see the sceneIntro state block for the
            contract. Tap anywhere dismisses; a broken image self-dismisses.
            Yields to the loading overlays and the tutorial (its spotlight
            anchors to elements this backdrop would bury). zIndex 9000: over
            nav (5100) and dock (5000), under the tutorial layer (100000). */}
        {/* 🚪 the door (Lamplight #8): return ritual for ported spaces —
            desktop, owner seated, ≥6h away. Knock is a zero-LLM poke. */}
        {doorState && !portedPlaceOn && !showInitialLoading && !showAssetLoadingOverlay && (
          <div style={{
            position: 'fixed', inset: 0, zIndex: 9000, display: 'flex',
            alignItems: 'center', justifyContent: 'center',
            background: 'radial-gradient(620px 420px at 50% 45%, rgba(36,24,48,0.97), rgba(10,6,14,0.98))',
          }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{
                width: 120, height: 180, margin: '0 auto', borderRadius: '10px 10px 0 0',
                background: 'linear-gradient(180deg, #4A362C, #38281F)',
                border: '2px solid rgba(232,188,114,0.4)', position: 'relative',
                boxShadow: doorState === 'open'
                  ? '0 0 80px 30px rgba(240,172,85,0.35)'
                  : '0 0 30px rgba(240,172,85,0.12)',
                transition: 'box-shadow 0.7s ease',
              }}>
                <span style={{
                  position: 'absolute', right: 12, top: 84, width: 9, height: 9,
                  borderRadius: '50%', background: 'linear-gradient(178deg, #E8BC72, #B9843B)',
                }} />
                <span style={{
                  position: 'absolute', left: 0, right: 0, bottom: -9, height: 9,
                  background: 'linear-gradient(180deg, rgba(255,214,140,0.75), rgba(255,214,140,0))',
                  opacity: doorState === 'open' ? 1 : 0.5, transition: 'opacity 0.7s ease',
                }} />
              </div>
              <div style={{
                minHeight: '2em', marginTop: 18, color: '#FFE9C8', fontStyle: 'italic',
                fontFamily: '"Iowan Old Style", "Palatino Linotype", Palatino, Georgia, serif',
                fontSize: '1.05em',
              }}>
                {doorState === 'open'
                  ? `${portedMindProps?.residentName || 'They'} heard you. The door swings open.`
                  : (portedMindProps?.residentName ? `${portedMindProps.residentName} is home.` : 'Somebody is home.')}
              </div>
              {doorState === 'closed' ? (
                <div style={{ display: 'flex', gap: 16, justifyContent: 'center', alignItems: 'center', marginTop: 16 }}>
                  <button
                    onClick={knockOnDoor}
                    autoFocus
                    style={{
                      background: 'rgba(240,172,85,0.12)', border: '1px solid rgba(240,172,85,0.5)',
                      color: '#F5C580', borderRadius: 10, padding: '11px 26px', cursor: 'pointer',
                      fontSize: '1em', minHeight: 44,
                    }}
                  >knock</button>
                  <button
                    onClick={() => setDoorState(null)}
                    style={{
                      background: 'none', border: 'none', color: 'rgba(255,238,216,0.5)',
                      cursor: 'pointer', fontSize: '0.85em', textDecoration: 'underline',
                      minHeight: 44, padding: '6px 10px',
                    }}
                  >just slip in</button>
                </div>
              ) : (
                <button
                  onClick={() => setDoorState(null)}
                  autoFocus
                  style={{
                    marginTop: 16, background: 'rgba(240,172,85,0.12)',
                    border: '1px solid rgba(240,172,85,0.5)', color: '#F5C580',
                    borderRadius: 10, padding: '11px 26px', cursor: 'pointer',
                    fontSize: '1em', minHeight: 44,
                  }}
                >come in</button>
              )}
            </div>
          </div>
        )}
        {sceneIntro && !tutorialActive && !showInitialLoading && !showAssetLoadingOverlay && (
          <div
            onClick={() => setSceneIntro(null)}
            role="button"
            aria-label="Enter the room"
            style={{
              position: 'fixed', inset: 0, zIndex: 9000,
              background: 'rgba(8, 6, 14, 0.96)', // no blur: full-viewport filter at join time (busiest paint moment) for 8% show-through (perf 08-13)
              display: 'flex', flexDirection: 'column',
              alignItems: 'center', justifyContent: 'center',
              gap: 14, padding: '24px 16px', cursor: 'pointer',
            }}
          >
            {sceneIntro.comic && (
              <div style={{
                fontSize: '0.72em', fontWeight: 800, letterSpacing: '0.08em',
                color: 'rgba(255,200,100,0.85)', textTransform: 'uppercase',
              }}>
                {/* 🐣 The arrival comic greets a FIRST visit: nobody was away (09-25). */}
                {sceneIntro.arrival
                  ? `How ${portedMindProps?.residentName || 'they'} got here`
                  : 'While you were away'}
              </div>
            )}
            <img
              src={sceneIntro.url}
              alt=""
              onError={() => setSceneIntro(null)}
              style={{
                // Comics are tall multi-panel pages: contain, never crop.
                // Scenes keep the 16:9 establishing-shot frame.
                ...(sceneIntro.comic
                  ? { maxWidth: 'min(92vw, 720px)', maxHeight: '64vh', objectFit: 'contain' }
                  : { width: 'min(92vw, 720px)', aspectRatio: '16 / 9', objectFit: 'cover' }),
                borderRadius: 14, border: '1px solid rgba(255,200,100,0.4)',
                boxShadow: '0 8px 40px rgba(0,0,0,0.6), 0 0 24px rgba(255,200,100,0.12)',
              }}
            />
            {sceneIntro.text && (
              <div style={{
                maxWidth: 'min(92vw, 680px)', textAlign: 'center',
                color: '#fff', fontSize: '1em', fontWeight: 600, lineHeight: 1.45,
              }}>
                {sceneIntro.text}
              </div>
            )}
            <div style={{
              fontSize: '0.72em', fontWeight: 800, letterSpacing: '0.08em',
              color: 'rgba(255,200,100,0.85)', textTransform: 'uppercase',
            }}>
              Tap to step in
            </div>
          </div>
        )}
        {(gameType === 'chat-game' || gameType === 'chat')
          && (!placesNav || mobileView === 'chat')
          && (!compPlacesNav || compMobileView === 'all')
          && !investigationTakeover
          && !panelTabs.has(chatActionTab)
          && (isMobile || (!dossierOpen && !shelfOpen))
          // ⌨️ 08-04 (Nina keyboard screenshot): while typing, the dock rides
          // the visualViewport translate but this cluster anchors to the
          // UNtranslated dock top — it floated in the gap above the keyboard.
          // It's a scroll affordance; typing time isn't scroll time.
          && !(isMobile && typingFocus)
          // 👻 08-04 (Nina): ghost sheet up = ghost chat IS the screen — main-
          // feed scroll arrows floating over it are somebody else's controls.
          && !(isMobile && ghostSheetOpen)
          && (showJumpToTop || showJumpToBottom) && (
          <div
            data-jump-cluster
            ref={(el) => {
              jumpClusterElRef.current = el;
              if (el) positionJumpCluster();
            }}
            style={{
              position: 'fixed',
              bottom: navBottomPlus(70),
              right: 10,
              display: 'flex',
              flexDirection: 'column',
              gap: 8,
              // Phones: the typing indicator's band slot (4950) — under the
              // dock (5000) and nav (5100), over the place layers (4600).
              // Desktop: typing-indicator parity (1000) — over feed content,
              // under every modal.
              zIndex: isMobile ? 4950 : 1000,
              // 🚪 postcard (Nina 09-24: "these arrows remain when transitioning
              // to new rooms"): fixed at PAGE level like the edge tabs, so the
              // fading pad never took them along. Same fade as the tabs.
              ...(roomCameraOn ? { opacity: roomCurtain ? 0 : 1, transition: `opacity ${roomCurtain ? 250 : 500}ms ease` } : {}),
              ...(roomCameraOn && roomCurtain ? { pointerEvents: 'none' } : {}),
            }}
          >
            {showJumpToTop && (
              <button
                onClick={scrollToTop}
                aria-label="Jump to top"
                title="Jump to top"
                style={{
                  width: isMobile ? '44px' : '36px',
                  height: isMobile ? '44px' : '36px',
                  borderRadius: '50%',
                  border: `1px solid ${THEME.accent || '#7C4DFF'}40`,
                  backgroundColor: THEME.bgDark || '#1a1a2e',
                  color: THEME.textOnPanel || '#fff',
                  fontSize: isMobile ? '1em' : '0.9em',
                  cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  boxShadow: '0 2px 10px rgba(0,0,0,0.5)',
                  opacity: 0.9,
                  transition: 'opacity 0.2s, transform 0.2s',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.opacity = '1'; e.currentTarget.style.transform = 'scale(1.1)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.opacity = '0.9'; e.currentTarget.style.transform = 'scale(1)'; }}
              >
                {'↑'}
              </button>
            )}
            {showJumpToBottom && (
              <button
                onClick={scrollToBottom}
                aria-label={unreadCount > 0 ? `${unreadCount} new messages, jump to bottom` : 'Jump to bottom'}
                title={unreadCount > 0 ? `${unreadCount} new message${unreadCount > 1 ? 's' : ''}` : 'Jump to bottom'}
                style={{
                  minWidth: unreadCount > 0 ? 'auto' : (isMobile ? '44px' : '36px'),
                  height: isMobile ? '44px' : '36px',
                  borderRadius: unreadCount > 0 ? (isMobile ? '22px' : '18px') : '50%',
                  padding: unreadCount > 0 ? (isMobile ? '0 16px' : '0 14px') : '0',
                  border: unreadCount > 0 ? `1px solid ${THEME.accent || '#7C4DFF'}80` : `1px solid ${THEME.accent || '#7C4DFF'}40`,
                  backgroundColor: unreadCount > 0 ? (THEME.accent || '#7C4DFF') : (THEME.bgDark || '#1a1a2e'),
                  color: unreadCount > 0 ? '#fff' : (THEME.textOnPanel || '#fff'),
                  fontSize: isMobile ? '0.9em' : '0.85em',
                  fontWeight: unreadCount > 0 ? 700 : 400,
                  cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  gap: '4px',
                  boxShadow: unreadCount > 0 ? `0 2px 12px ${THEME.accent || '#7C4DFF'}60` : '0 2px 10px rgba(0,0,0,0.5)',
                  transition: 'all 0.2s',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.transform = 'scale(1.05)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
              >
                {unreadCount > 0 ? `${unreadCount} new` : '↓'}
                {unreadCount > 0 && <span>{'↓'}</span>}
              </button>
            )}
          </div>
        )}

        {/* 🔌 09-28 agent wave: "🤖 X is here" toast for a watch link's
            ?with=<seat id>. Same shell as the go-live toast; 8s or ✕. */}
        {withToast && (
          <div role="status" style={{
            position: 'fixed',
            top: 'calc(18px + env(safe-area-inset-top, 0px))',
            ...TOAST_CENTERED,
            maxWidth: 'min(92vw, 480px)',
            zIndex: 100001,
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            padding: '10px 14px',
            borderRadius: 12,
            backgroundColor: 'rgba(20,20,28,0.95)',
            border: '1px solid rgba(34,211,238,0.5)',
            boxShadow: '0 8px 24px rgba(0,0,0,0.45), 0 0 16px rgba(34,211,238,0.25)',
            color: '#fff',
          }}>
            <span style={{ fontSize: '0.9em', fontWeight: 600, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{withToast}</span>
            <button onClick={() => setWithToast(null)} aria-label="Dismiss" style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.6)', cursor: 'pointer', fontSize: '0.9em', padding: 2 }}>✕</button>
          </div>
        )}

        {/* Play Round Pause Toast */}
        {playRoundPauseToast && (
          <div style={{
            position: 'fixed',
            bottom: 24,
            // TOAST_CENTERED, not left:50% — same sizing bug as the rest.
            ...TOAST_CENTERED,
            maxWidth: 'min(92vw, 440px)',
            zIndex: 10000,
            padding: '10px 24px',
            borderRadius: '20px',
            background: 'linear-gradient(135deg, rgba(30,30,40,0.95), rgba(20,20,30,0.95))',
            border: `1px solid ${THEME.accent || '#6366f1'}40`,
            boxShadow: `0 4px 20px rgba(0,0,0,0.5), 0 0 20px ${THEME.accent || '#6366f1'}20`,
            backdropFilter: 'blur(12px)',
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            animation: 'pauseToastSlideUp 0.4s ease-out',
          }}>
            <span style={{ fontSize: '1em' }}>{'\u23F8\uFE0F'}</span>
            <span style={{
              color: '#fff',
              fontWeight: 700,
              fontSize: '0.85em',
              letterSpacing: '0.5px',
            }}>
              {playRoundPauseToast}
            </span>
            <span style={{
              color: THEME.accent || '#6366f1',
              fontSize: '0.75em',
              fontWeight: 600,
            }}>
              {(chatGameStatus?.competitive_mode || chatGameStatus?.imposter_mode) ? 'Click Play Round to continue' : 'Click Continue to keep going'}
            </span>
          </div>
        )}

        {/* Global CSS for phase banner animations (must be outside conditional overlays) */}
        <style>{`
          .typing-dots {
            animation: typingDots 1.4s infinite;
          }
          @keyframes typingDots {
            0% { opacity: 0.2; }
            20% { opacity: 1; }
            100% { opacity: 0.2; }
          }
          @keyframes waitingBannerGlow {
            0%, 100% { box-shadow: 0 2px 16px rgba(99,102,241,0.5), 0 0 32px rgba(139,92,246,0.3); opacity: 1; }
            50% { box-shadow: 0 2px 28px rgba(99,102,241,0.8), 0 0 56px rgba(139,92,246,0.5); opacity: 0.92; }
          }
          /* Compositor-only banner throb — pulses the glow UNDERLAY's opacity
             over a static strong shadow (replaces the animated box-shadow
             *BannerGlow loops above; fx-bench 07-09) */
          @keyframes bannerGlowPulse {
            0%, 100% { opacity: 0.3; }
            50% { opacity: 1; }
          }
          @keyframes letterBounce {
            0%, 4%, 100% { transform: translateY(0); }
            2% { transform: translateY(-4px); }
          }
          @keyframes loadingDot {
            0%, 20% { opacity: 0.2; transform: translateY(0); }
            50% { opacity: 1; transform: translateY(-3px); }
            80%, 100% { opacity: 0.2; transform: translateY(0); }
          }
          @keyframes speakingPulse {
            0%, 100% { box-shadow: 0 0 8px rgba(74, 222, 128, 0.6), 0 0 16px rgba(74, 222, 128, 0.3); }
            50% { box-shadow: 0 0 14px rgba(74, 222, 128, 0.8), 0 0 28px rgba(74, 222, 128, 0.4); }
          }
          @keyframes playButtonGlow {
            0%, 100% { box-shadow: 0 0 12px rgba(74,222,128,0.5), 0 0 24px rgba(74,222,128,0.3), 0 0 48px rgba(74,222,128,0.15); }
            50% { box-shadow: 0 0 24px rgba(74,222,128,0.8), 0 0 48px rgba(74,222,128,0.5), 0 0 72px rgba(74,222,128,0.25); }
          }
          @keyframes waitingButtonPulse {
            0%, 100% { box-shadow: 0 0 12px rgba(245,158,11,0.5), 0 0 24px rgba(245,158,11,0.3), 0 0 48px rgba(245,158,11,0.15); }
            50% { box-shadow: 0 0 24px rgba(245,158,11,0.8), 0 0 48px rgba(245,158,11,0.5), 0 0 72px rgba(245,158,11,0.25); }
          }
          @keyframes phaseBannerPulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.85; }
          }
          @keyframes phaseBannerGlow {
            0%, 100% { box-shadow: 0 2px 16px rgba(34, 197, 94, 0.5); }
            50% { box-shadow: 0 2px 24px rgba(34, 197, 94, 0.8); }
          }
          @keyframes voteBannerGlow {
            0%, 100% { box-shadow: 0 2px 20px rgba(220, 38, 38, 0.7), 0 0 40px rgba(239, 68, 68, 0.4); }
            50% { box-shadow: 0 4px 30px rgba(220, 38, 38, 0.9), 0 0 60px rgba(239, 68, 68, 0.6); }
          }
          @keyframes pauseToastSlideUp {
            from { opacity: 0; transform: translateX(-50%) translateY(20px); }
            to { opacity: 1; transform: translateX(-50%) translateY(0); }
          }
          @keyframes speakerDotPulse {
            0%, 100% { opacity: 1; transform: scale(1); }
            50% { opacity: 0.5; transform: scale(0.7); }
          }
          @keyframes votingTimerDrain {
            from { width: 100%; }
            to { width: 0%; }
          }
          @keyframes toastSlideDown {
            from { opacity: 0; transform: translateX(-50%) translateY(-20px); }
            to { opacity: 1; transform: translateX(-50%) translateY(0); }
          }
          @keyframes toastSlideUp {
            from { opacity: 0; transform: translateX(-50%) translateY(20px); }
            to { opacity: 1; transform: translateX(-50%) translateY(0); }
          }
          @keyframes spotlightFadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }
          @keyframes tutorialToastPulse {
            0%, 100% { filter: brightness(1); transform: scale(1); }
            50% { filter: brightness(1.15); transform: scale(1.02); }
          }
          @keyframes elementAttentionPulse {
            0%, 100% { box-shadow: 0 0 0 0 rgba(102, 126, 234, 0); }
            50% { box-shadow: 0 0 12px 4px rgba(102, 126, 234, 0.6); }
          }
          @keyframes hintPulseGlow {
            0%, 100% { box-shadow: 0 0 8px 2px rgba(102, 126, 234, 0.4); border-color: rgba(255,255,255,0.5); }
            50% { box-shadow: 0 0 18px 6px rgba(102, 126, 234, 0.8); border-color: rgba(255,255,255,0.9); }
          }
          @keyframes tutorialCelebGlow {
            0% { box-shadow: 0 8px 32px rgba(0,0,0,0.5), 0 0 20px rgba(102,126,234,0.3); }
            100% { box-shadow: 0 8px 32px rgba(0,0,0,0.5), 0 0 35px rgba(102,126,234,0.55), 0 0 70px rgba(102,126,234,0.15); }
          }
          @keyframes tutorialCelebEmoji {
            0% { transform: scale(0) rotate(-20deg); opacity: 0; }
            60% { transform: scale(1.2) rotate(5deg); opacity: 1; }
            100% { transform: scale(1) rotate(0deg); opacity: 1; }
          }
          @keyframes celebConfettiFall {
            0% { transform: translateY(0) rotate(0deg); opacity: 1; }
            100% { transform: translateY(180px) rotate(360deg); opacity: 0; }
          }
        `}</style>

        {/* Screenshot Preview Modal */}
        {/* 🪪 Character card from the chat stream — same card the Tea opens */}
        {chatCastPid && (
          <CastCardModal
            forceRevealed={isPortedSpace}
            pid={chatCastPid}
            users={users}
            messages={results.flatMap(r => r.actions || []).filter(a => CAST_MSG_TYPES.has(a.type) && a.data?.message)}
            getModelName={getDisplayModelName}
            gameId={gameId}
            humanPlayerId={humanPlayerId}
            THEME={THEME}
            onClose={() => setChatCastPid(null)}
          />
        )}
        {screenshotModal && (
          <div
            onClick={() => setScreenshotModal(null)}
            style={{
              position: 'fixed',
              top: 0, left: 0, right: 0, bottom: 0,
              backgroundColor: 'rgba(0,0,0,0.8)',
              backdropFilter: 'blur(6px)',
              zIndex: 200000,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '20px',
            }}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                background: '#1f2937',
                borderRadius: '16px',
                maxWidth: '90vw',
                maxHeight: '90dvh',
                width: isMobile ? '100%' : '700px',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
              }}
            >
              {/* Header */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '16px 20px',
                borderBottom: '1px solid #374151',
              }}>
                <span style={{ color: '#fff', fontWeight: 700, fontSize: '1.05em' }}>Screenshot</span>
                <button
                  onClick={() => setScreenshotModal(null)}
                  style={{ background: 'none', border: 'none', color: '#9ca3af', fontSize: '1.4em', cursor: 'pointer', padding: 0, lineHeight: 1 }}
                >
                  &times;
                </button>
              </div>

              {/* Image preview */}
              <div style={{ padding: '16px 20px', overflowY: 'auto', flex: 1 }}>
                <img
                  src={screenshotModal}
                  alt="Screenshot"
                  style={{
                    width: '100%',
                    borderRadius: '8px',
                    border: '1px solid #374151',
                  }}
                />
              </div>

              {/* Action buttons */}
              <div style={{
                display: 'flex',
                gap: '10px',
                padding: '16px 20px',
                borderTop: '1px solid #374151',
              }}>
                <button
                  onClick={() => handleScreenshotCopy(screenshotModal)}
                  style={{
                    flex: 1,
                    padding: '10px',
                    borderRadius: '8px',
                    border: 'none',
                    background: screenshotCopied ? '#22c55e' : '#6366f1',
                    color: '#fff',
                    fontWeight: 600,
                    fontSize: '0.9em',
                    cursor: 'pointer',
                    transition: 'background 0.2s',
                  }}
                >
                  {screenshotCopied ? '✓ Copied!' : 'Copy to Clipboard'}
                </button>
                <button
                  onClick={() => handleScreenshotDownload(screenshotModal)}
                  style={{
                    flex: 1,
                    padding: '10px',
                    borderRadius: '8px',
                    border: '1px solid #4b5563',
                    background: 'transparent',
                    color: '#fff',
                    fontWeight: 600,
                    fontSize: '0.9em',
                    cursor: 'pointer',
                  }}
                >
                  Download PNG
                </button>
              </div>

              {/* Gallery thumbnails */}
              {screenshotGallery.length > 1 && (
                <div style={{
                  padding: '12px 20px 16px',
                  borderTop: '1px solid #374151',
                }}>
                  <span style={{ color: '#9ca3af', fontSize: '0.8em', fontWeight: 600, marginBottom: '8px', display: 'block' }}>
                    Previous ({screenshotGallery.length})
                  </span>
                  <div style={{ display: 'flex', gap: '8px', overflowX: 'auto', paddingBottom: '4px' }}>
                    {screenshotGallery.map((entry, i) => (
                      <img
                        key={entry.timestamp}
                        src={entry.url}
                        alt={`Screenshot ${i + 1}`}
                        onClick={() => setScreenshotModal(entry.url)}
                        style={{
                          width: '80px',
                          height: '50px',
                          objectFit: 'cover',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          border: entry.url === screenshotModal ? '2px solid #6366f1' : '2px solid transparent',
                          opacity: entry.url === screenshotModal ? 1 : 0.6,
                          transition: 'all 0.15s',
                          flexShrink: 0,
                        }}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Tutorial Toast */}
        {/* [Tutorial] render check removed */}
        {/* tourDeclined holds the mount open for the parting line: declining
            flips BOTH tourIntroSeen and tutorial.dismissed, so the normal gate
            would unmount the dialog in the same frame as the answer. */}
        {/* 🐣 Ported spaces: the RESIDENT asks, bypassing the global
            tourIntroSeen gate on purpose — a user who took the generic tour
            elsewhere still gets the ask about THEIR agent (the per-game seen
            key + declineTutorial's No still silence it). !sceneIntro is
            belt-and-suspenders with the toast-ready effect: never pop the
            ask over the arrival splash. */}
        {showTutorialToast && tutorialToastReady && !tutorialActive && !shellPreset /* 🧱 the tour teaches today's room */ && !showAssetLoadingOverlay && !showInitialLoading && !showRoundAnnouncement && !sceneIntro
          // 🐣 09-25: a ported room asks only in the resident's voice; before the
          // status names the resident (the create/invite race) the ask waits,
          // rather than falling back to "The Host"
          && !(isPortedSpace && !portedResidentName)
          // 🐣 W3: never over the hatch (his first moment is the owner's to watch)
          && !portedHatchProp
          && (tourDeclined || isCompOrImposter || (tutorialQuestEligible && !tourIntroSeen) || (!!portedArrivalName && !isCompOrImposter)) && (
          <TutorialToast
            onStart={startTutorial}
            onNo={declineTutorial}
            onDismiss={() => setTourDeclined(true)}
            declined={tourDeclined}
            theme={THEME}
            moderatorCharacter={portedResidentName
              ? {
                  name: portedResidentName,
                  profile_image_url: portedResidentUser?.profile_image_url || portedResidentUser?.data?.profile_image_url || chatGameStatus?.resident_image || null,
                  avatar_emoji: '🐣',
                }
              : chatGameStatus?.moderator_character}
            askText={portedArrivalName ? 'You made it! Want to see what we can do here?'
              : portedResidentName ? 'Want to see what we can do here?' : null}
            yesLabel={portedResidentName ? 'Show me!' : null}
          />
        )}

        {/* Spotlight Tutorial Overlay */}
        {tutorialActive && (
          <SpotlightOverlay
            steps={tutorialSteps}
            currentStep={tutorialStep}
            onNext={nextTutorialStep}
            onBack={prevTutorialStep}
            onSkip={endTutorial}
            theme={THEME}
            isMobile={isMobile}
            isCompetitive={!!(chatGameStatus?.competitive_mode || chatGameStatus?.imposter_mode)}
            completedMap={tutorial}
            forceCelebrate={tutorialCelebrate}
            keyboardUp={isMobile && typingFocus}
            buildReceipt={(() => {
              // Chip data from the live /build capture: handle mirrors the
              // backend's normalizeHandle; emoji upgrades from the builds
              // ledger once the status poll delivers it (🔨 until then).
              if (!tutorialBuildReceipt) return null;
              const handle = String(tutorialBuildReceipt).toLowerCase()
                .replace(/^(a|an|the)\s+/, '').replace(/[^a-z0-9]+/g, '-')
                .replace(/^-+|-+$/g, '').slice(0, 28);
              if (!handle) return null;
              const norm = (s) => String(s || '').toLowerCase().replace(/^(a|an|the)\s+/, '').replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
              const item = (chatGameStatus?.builds || []).find(b => (b.handle || norm(b.name)) === handle);
              return { handle: item?.handle || handle, emoji: item?.emoji || '🔨' };
            })()}
          />
        )}

        {/* Asset Generation Loading Overlay + Round Announcement */}
        {(showAssetLoadingOverlay || (showRoundAnnouncement && chatGameStatus?.competitive_mode)) && (() => {
          // ENTRY CURTAIN — themed pre-join cut screen (template `entry_screen`, Whodunnit
          // first). The page keeps loading + polling UNDERNEATH (viewer presence counts,
          // ghost pre-show fills the room), so by Enter-time the space feels alive. The
          // Enter click doubles as the browser audio-unlock gesture: the door creak plays
          // on it, making every later SFX (thunder, dice) autoplay-legal. Games without
          // entry_screen keep the exact interstitial below — additive, fallback-safe.
          const entryScreen = chatGameStatus?.entry_screen || null;
          // Gate on "has this session entered yet", NOT round numbers — round_number is
          // the chat counter for space games (>1 pre-game), which made the old card win
          // the first render and the curtain pop up late (user report, 07-08). The ref
          // flips on ENTER, so between-round announcements still get the normal card.
          // [bot-theater 07-12] Bots get the curtain too — the livestream IS the
          // bot's screen, and the audience was getting a theater-less Whodunnit.
          // The bot-mode auto-open effect (near curtainAutoOpenedRef) swings the
          // door after ~4.5s so the overlay can never wedge the bot.
          const entryCurtain = !!(entryScreen && chatGameStatus?.competitive_mode && !entryCurtainSeenRef.current);
          // Storm beat is live (assets ready, roster loaded) — the door can open. Mirrors
          // the ready-block condition below; also gates the rain to the storm beat only.
          const curtainReady = entryCurtain && (assetLoadingReady || showRoundAnnouncement)
            && !isStartingRound && !(showAssetLoadingOverlay && !assetLoadingReady)
            && !!chatGameStatus && users.length > 0;
          // Space intro (non-competitive, first visit): host beat → locals beat →
          // regular welcome card. Bots skip it — their auto-enter clicks the
          // welcome CTA and must never sit behind extra Next clicks.
          const spaceIntroActive = !isBotMode
            && !shellPreset // 🧱 a quiet frame: no host intro, no "meet my locals" (entry 'none')
            && !(chatGameStatus?.competitive_mode || chatGameStatus?.imposter_mode)
            && !!chatGameStatus?.moderator_character
            && spaceIntroStep < 2;
          // Host bio → caption chunks for the intro's opening beat (user call 07-11 v2):
          // 1-2 sentences packed to ~140 chars per chunk, big type, one pass through.
          // Cheap string ops on a short bio, recomputed only while this overlay renders.
          const introBioChunks = (() => {
            if (!spaceIntroActive) return [];
            const mod = chatGameStatus.moderator_character;
            const bio = (mod.origin_story || mod.personality || '').trim();
            const sentences = bio.match(/[^.!?]+[.!?]*\s*/g) || [];
            const chunks = [];
            let cur = '';
            for (const s of sentences) {
              if (cur && (cur + s).length > 140) { chunks.push(cur.trim()); cur = s; }
              else cur += s;
            }
            if (cur.trim()) chunks.push(cur.trim());
            return chunks;
          })();
          // One full pass or an explicit skip ends the caption beat; the flow then
          // continues to the full host-bio screen → the locals screen (user call:
          // captions come FIRST, the existing beats still follow).
          const introCaptionsDone = introCaptionsSkipped || introBioChunks.length === 0
            || introCaptionIdx >= introBioChunks.length;
          const introCaptionsActive = spaceIntroActive && spaceIntroStep === 0 && !introCaptionsDone && !isPortedSpace;
          // Rendered in BOTH branches below: over the loading screen while assets
          // generate (the wait does the reading), and as the ready-state opener if
          // loading finished first. Finite animations only — no infinite loops.
          // v3 (07-12): the whole screen is the advance button (tap anywhere);
          // the tap doubles as the session's audio-unlock, so the soft pop is legal.
          // Hoisted OUT of the screen closure so the OVERLAY ROOT can be the tap
          // target (07-12 fix: the handler sat on the max-width content column,
          // so full-screen desktop taps outside it did nothing). Null when the
          // beat isn't live — the root falls back to its other handlers.
          const advanceIntroCaption = !introCaptionsActive ? null : () => {
            soundManager.play('message');
            setIntroCaptionIdx(i => i + 1);
          };
          const introCaptionScreen = !introCaptionsActive ? null : (() => {
            const mod = chatGameStatus.moderator_character;
            const accent = THEME.accent || '#6366f1';
            const chunk = introBioChunks[Math.min(introCaptionIdx, introBioChunks.length - 1)];
            return (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16, padding: '8px 0 4px' }}>
                {mod.profile_image_url ? (
                  <img src={mod.profile_image_url} alt={mod.name} style={{
                    width: 132, height: 132, borderRadius: '50%', objectFit: 'cover',
                    border: `3px solid ${accent}80`, boxShadow: `0 0 26px ${accent}35`,
                    animation: 'enterSpaceFadeIn 0.5s ease both',
                  }} />
                ) : (
                  <div className="host-portrait-materializing" style={{
                    width: 132, height: 132, borderRadius: '50%',
                    position: 'relative', overflow: 'hidden',
                    background: `linear-gradient(135deg, ${accent}40, ${accent}15)`,
                    border: `3px solid ${accent}80`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '3.2em',
                    animation: 'enterSpaceFadeIn 0.5s ease both',
                  }}>{mod.avatar_emoji || '\u{1F399}\u{FE0F}'}</div>
                )}
                <p style={{
                  margin: 0, color: accent, fontSize: '0.8rem', fontWeight: 800,
                  letterSpacing: '2.5px', textTransform: 'uppercase',
                  animation: 'enterSpaceFadeIn 0.5s ease 0.1s both',
                }}>
                  Your host · {mod.name}
                </p>
                {/* The caption itself — big and readable, one chunk at a time.
                    key re-runs the animation per chunk; minHeight stops layout
                    jumps. introCapIn = quick fade-up, then a slow finite drift
                    (defined with the cinema chrome at the overlay root). */}
                <p key={`cap-${introCaptionIdx}`} style={{
                  margin: 0, maxWidth: 520, color: '#fff', fontSize: '1.5rem', fontWeight: 600,
                  lineHeight: 1.6, minHeight: '4em', letterSpacing: '0.2px',
                  display: 'flex', alignItems: 'center',
                  animation: 'introCapIn 4.5s ease-out both',
                }}>
                  {chunk}
                </p>
                {/* Progress dots — one per chunk, current one lit */}
                <div style={{ display: 'flex', gap: 7 }}>
                  {introBioChunks.map((_, i) => (
                    <span key={i} style={{
                      width: 7, height: 7, borderRadius: '50%',
                      backgroundColor: i === Math.min(introCaptionIdx, introBioChunks.length - 1)
                        ? accent : 'rgba(255,255,255,0.22)',
                    }} />
                  ))}
                </div>
                {/* Explicit TAP pill (07-12): the whole overlay advances too, but a
                    real button gives mobile thumbs and screen readers a target.
                    stopPropagation so the root handler doesn't double-advance. */}
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); advanceIntroCaption?.(); }}
                  aria-label="Next caption"
                  style={{
                    marginTop: 6, padding: '9px 30px', borderRadius: 999,
                    background: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.85)',
                    border: '1px solid rgba(255,255,255,0.30)', cursor: 'pointer',
                    fontSize: '0.78em', fontWeight: 800, letterSpacing: '2px', textTransform: 'uppercase',
                  }}
                >
                  Tap &#9656;
                </button>
                {/* Skip rides the caption flow, right under Tap (Nina 07-16:
                    the old bottom-right letterbox pill sat too far from the
                    writing). Muted so it never competes with the caption. */}
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); setIntroCaptionsSkipped(true); }}
                  aria-label="Skip intro captions"
                  style={{
                    marginTop: 2, padding: '3px 12px', borderRadius: 999,
                    background: 'transparent', color: 'rgba(255,255,255,0.5)',
                    border: 'none', cursor: 'pointer',
                    fontSize: '0.66em', fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase',
                  }}
                >
                  Skip &rarr;
                </button>
              </div>
            );
          })();
          // Shared ENTER sequence (welcome card CTA + intro beat 2 CTA): join the
          // round, then the non-comp cinematic blackout/reveal. The click doubles
          // as the session's audio-unlock gesture; non-comp plays the airy
          // enterSpace shimmer (user call 07-10: door creak retired for space
          // entry — it read as horror, not wonder).
          const enterSpace = () => {
            const isCompetitive = chatGameStatus?.competitive_mode || chatGameStatus?.imposter_mode;
            setIsStartingRound(true);
            setAssetLoadingReady(false);
            setShowRoundAnnouncement(false);
            handleRoundJoin().catch(err => {
              console.error('[Enter] Failed to join round:', err);
            });
            if (!isCompetitive) {
              // Every non-comp exit funnels through here, so stamp intro-seen
              // HERE (not only on the beat CTAs): moderator-less spaces never
              // render the beats, and without the stamp the fast path would
              // hold the welcome card on every future visit (07-12).
              // 🎙️ First-ever host narration (Arc 6; stamp reworked 07-21).
              // The Enter click is the gesture, so playback is autoplay-legal;
              // the voice rides the blackout/reveal. Narration has its OWN
              // stamp, written only when playback actually STARTS — the old
              // space_intro stamp fired on entry regardless, so a visitor who
              // arrived before the TTS finished generating went silent for
              // that space forever (Nina hit this live, 07-21). Now a raced
              // first visit just narrates on the next one — still once ever.
              // 🔇 JOIN NARRATION DISABLED (Nina 08-03): the welcome TTS on
              // entering non-comp spaces is OFF for now — flip this flag to
              // bring it back. Everything below is preserved, not deleted
              // (stamp logic, WS URL patch at moderator_welcome_audio, and
              // the backend generation all still run).
              const JOIN_NARRATION_DISABLED = true;
              try {
                const narrationHeard = localStorage.getItem(`space_narration_v1_${gameId}`);
                const welcomeUrl = chatGameStatus?.moderator_character?.welcome_audio_url;
                if (!JOIN_NARRATION_DISABLED && !narrationHeard && welcomeUrl && !isBotMode && !hostVoiceMuted) {
                  const narration = new Audio(welcomeUrl);
                  narration.volume = 0.9;
                  narration.play().then(() => {
                    try { localStorage.setItem(`space_narration_v1_${gameId}`, '1'); } catch (e) { /* private mode */ }
                  }).catch(() => { /* blocked or gone — space works without it */ });
                }
              } catch (e) { /* private mode */ }
              try { localStorage.setItem(`space_intro_v1_${gameId}`, '1'); } catch (e) { /* private mode */ }
              soundManager.play('enterSpace');
              setCinematicPhase('blackout');
              setTimeout(() => {
                setShowAssetLoadingOverlay(false);
                setCinematicPhase('reveal');
              }, 600);
              setTimeout(() => {
                setCinematicPhase('fadeIn');
              }, 2200);
              setTimeout(() => {
                setCinematicPhase(null);
                setIsStartingRound(false);
              }, 2400);
            } else {
              // Competitive: dismiss immediately — no need to linger
              setShowAssetLoadingOverlay(false);
              setIsStartingRound(false);
            }
          };
          // The whole storm screen is the door (handler on the OVERLAY itself — the old
          // position:fixed catcher sat inside an animated wrapper, which becomes the
          // containing block for fixed children, so it never actually covered the screen:
          // the user's dead clicks, 07-10). Creak + door swing, then the body cold open.
          const openCurtainDoor = () => {
            if (curtainDoorOpening) return;
            // The click is the session's audio-unlock gesture; the creak IS the door.
            soundManager.play(entryScreen.sfx || 'doorCreak');
            // ⛈️ Storm starts UNDER the storm door itself (Nina 07-20; 07-30:
            // ALWAYS the storm bed, every pack — the curtain/loading/cold-open
            // visuals are unconditionally a storm night, so the audio now is
            // too). Idempotent; the cold-open-close handoff swaps in the
            // pack's own bed. Bot mode mirrors the effect's carve-out.
            if (chatGameStatus?.noir_theme && !isBotMode) soundManager.startStorm();
            setCurtainDoorOpening(true);
            setTimeout(() => {
              entryCurtainSeenRef.current = true;
              setCurtainDoorOpening(false);
              setIsStartingRound(true);
              setAssetLoadingReady(false);
              setShowRoundAnnouncement(false);
              handleRoundJoin().catch(err => {
                console.error('[EntryCurtain] Failed to join round:', err);
              });
              // Door → BODY. The storm screen was the night beat, so the atmospheric
              // cold open skips straight to the lightning/body slam. Game still only
              // starts on a manual Investigate.
              if (chatGameStatus?.noir_theme && !coldOpenPlayedRef.current && !chatGameStatus?.is_playing) {
                setShowGuiltyReveal(prev => prev || { atmospheric: true, skipNight: true });
              }
              setShowAssetLoadingOverlay(false);
              setIsStartingRound(false);
            }, 1050);
          };
          // [bot-theater] expose for the bot-mode auto-open timer.
          openCurtainDoorRef.current = openCurtainDoor;
          return (
          <div
            onClick={curtainReady ? openCurtainDoor : (advanceIntroCaption || undefined)}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              cursor: (curtainReady || advanceIntroCaption) ? 'pointer' : undefined,
              backgroundColor: entryCurtain
                ? 'rgba(4,5,10,0.97)'
                : ((!chatGameStatus && earlyCompetitiveMode) ? 'rgba(0,0,0,1)' : 'rgba(0,0,0,0.7)'),
              backdropFilter: (!chatGameStatus && earlyCompetitiveMode) ? 'none' : 'blur(8px)',
              zIndex: 100000,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              // No justifyContent:center — flex centering CLIPS overflowing
              // content on both ends (the non-comp welcome card grew past the
              // viewport: top/bottom cut off, user report 07-09). The content
              // div centers itself with margin:auto, which degrades to
              // scrollable-from-the-top when it's taller than the screen.
              // Curtain keeps hidden so the rain streaks can't mint scrollbars.
              overflowY: entryCurtain ? 'hidden' : 'auto',
              overflowX: 'hidden',
            }}
          >
            {/* 🔇 ONE master mute (Nina 07-22: "1 button that mutes
                everything"; 08-02: now it really does) — toggles host voice
                (narration + announcements) and sound effects, and broadcasts
                SPACE_AUDIO_MUTED so the space music radio and watch party
                theater silence too. (Ambient bed removed 08-02.) Root chrome:
                visible on every beat; stopPropagation so it never advances.
                Mobile: below the LofiRadio FAB (fixed top:10 right:10,
                zIndex 100010) which sat exactly here and hid the old pill
                (Nina 07-19). */}
            {(!chatGameStatus?.competitive_mode && chatGameStatus?.moderator_character) && (() => {
              const allMuted = hostVoiceMuted && !sfxEnabled;
              return (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    const mute = !allMuted;
                    setHostVoiceMuted(mute);
                    setSfxEnabled(!mute);
                    soundManager.setEnabled(!mute);
                    try {
                      localStorage.setItem('hostVoiceMuted', mute ? '1' : '0');
                    } catch (err) { /* private mode */ }
                    emitGameEvent(GAME_EVENTS.SPACE_AUDIO_MUTED, { muted: mute });
                  }}
                  aria-label={allMuted ? 'Sound on' : 'Mute everything'}
                  title={allMuted ? 'Sound on' : 'Mute everything'}
                  style={{
                    position: 'absolute', top: isMobile ? 54 : 12, right: 14, zIndex: 3,
                    width: 34, height: 34, borderRadius: '50%',
                    background: 'rgba(0,0,0,0.35)', color: 'rgba(255,255,255,0.75)',
                    border: '1px solid rgba(255,255,255,0.22)', cursor: 'pointer', fontSize: '0.9em',
                    opacity: allMuted ? 0.55 : 1,
                  }}
                >
                  {allMuted ? '🔇' : '🔊'}
                </button>
              );
            })()}
            {/* Curtain keyframes — always mounted with the curtain so every beat can use
                them (flicker on the storm headline, swing on the door). */}
            {entryCurtain && (
              <style>{`
                @keyframes entryCurtainRain { from { transform: translateY(-18vh); } to { transform: translateY(118vh); } }
                @keyframes entryCurtainFlicker { 0%, 100% { opacity: 1; } 92% { opacity: 1; } 93% { opacity: 0.4; } 95% { opacity: 1; } 97% { opacity: 0.6; } }
                @keyframes curtainDoorPulse { 0%, 100% { transform: scale(1); } 50% { transform: scale(1.12); } }
                @keyframes curtainDoorSwing { 0% { transform: scale(1) rotate(0deg); opacity: 1; } 100% { transform: scale(3) rotate(-9deg); opacity: 0; } }
                @keyframes curtainSpin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
              `}</style>
            )}
            {/* 🎬 Intro caption cinema chrome (07-12) — letterbox bars + the space's
                own background dimmed behind the host. The bg URL is the one the
                space itself loads (same cache entry, zero extra fetch); during
                first-generation it doesn't exist yet and the vignette carries the
                look alone. Mounted at the OVERLAY ROOT on purpose: the content
                wrappers animate transform, which would turn them into containing
                blocks and break covering children (same trap as openCurtainDoor's
                note). All finite, compositor-only animations. */}
            {introCaptionsActive && (
              <>
                <style>{`
                  @keyframes introLetterboxIn { from { transform: scaleY(0); } to { transform: scaleY(1); } }
                  @keyframes introBgSettle { from { transform: scale(1.07); opacity: 0; } to { transform: scale(1); opacity: 0.22; } }
                  @keyframes introCapIn {
                    0% { opacity: 0; transform: translateY(10px) scale(1); }
                    12% { opacity: 1; transform: translateY(0) scale(1); }
                    100% { opacity: 1; transform: translateY(0) scale(1.025); }
                  }
                `}</style>
                {chatGameStatus?.ui_theme?.backgroundImage?.url && (
                  <div style={{
                    position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, pointerEvents: 'none',
                    backgroundImage: `url(${chatGameStatus.ui_theme.backgroundImage.url})`,
                    backgroundSize: 'cover', backgroundPosition: 'center',
                    animation: 'introBgSettle 8s ease-out both',
                  }} />
                )}
                <div style={{
                  position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, pointerEvents: 'none',
                  background: 'radial-gradient(ellipse at center, transparent 30%, rgba(0,0,0,0.75) 100%)',
                }} />
                <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 44, background: '#000', transformOrigin: 'top', animation: 'introLetterboxIn 0.7s ease both', pointerEvents: 'none' }} />
                <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 44, background: '#000', transformOrigin: 'bottom', animation: 'introLetterboxIn 0.7s ease both', pointerEvents: 'none' }} />
                {/* Skip moved into the caption flow itself (under the Tap pill,
                    Nina 07-16) — no more far-corner letterbox pill. */}
              </>
            )}
            {/* Storm weather — rain streaks on the STORM beat only (the loading beat is a
                plain please-wait screen; the storm arrives when the door does, user 07-10). */}
            {curtainReady && entryScreen.effect === 'rain' && (
              <>
                {Array.from({ length: 18 }).map((_, i) => (
                  <span key={`entry-rain-${i}`} style={{
                    position: 'absolute', top: 0, left: `${(i * 97 + 13) % 100}%`,
                    width: '1px', height: `${60 + (i % 4) * 14}px`, pointerEvents: 'none',
                    background: 'linear-gradient(to bottom, transparent, rgba(190,215,255,0.38))',
                    opacity: 0.5,
                    animation: `entryCurtainRain ${(0.9 + (i % 4) * 0.22).toFixed(2)}s linear ${((i % 6) * 0.31).toFixed(2)}s infinite`,
                  }} />
                ))}
              </>
            )}
            <div style={{ textAlign: 'center', maxWidth: 500, padding: '28px 20px', position: 'relative', zIndex: 1, margin: 'auto' }}>
              {/* Loading state - spinner + status (also shown when assets ready but game status not yet loaded) */}
              {showAssetLoadingOverlay && (!assetLoadingReady || !chatGameStatus || users.length === 0) && (
                <>
                  {/* Audio call-to-action — pinned above the spinner so users
                      see it BEFORE the load finishes and have time to unmute.
                      Only renders for spaces with a music/video URL. */}
                  {chatGameStatus?.music_url && (
                    /* 🔇 Was a glowing "Turn up your volume" banner — demoted to a
                       bare mute chip (Nina 07-30): beds now start AT the entry
                       click, so the space announces its audio by playing it. The
                       chip keeps the pre-entry opt-out (Nina 07-16) without
                       stealing the title's moment. stopPropagation keeps the tap
                       from advancing the caption beat. */
                    <button
                      onClick={(e) => { e.stopPropagation(); const next = !sfxEnabled; setSfxEnabled(next); soundManager.setEnabled(next); }}
                      title={sfxEnabled ? 'Mute space sounds' : 'Unmute space sounds'}
                      style={{
                        marginBottom: 18,
                        background: 'rgba(0, 0, 0, 0.35)',
                        border: '1px solid rgba(255, 255, 255, 0.22)',
                        borderRadius: 999,
                        color: 'rgba(255,255,255,0.78)',
                        cursor: 'pointer',
                        padding: '6px 14px',
                        fontSize: '0.8rem',
                        fontWeight: 700,
                        opacity: sfxEnabled ? 0.9 : 0.6,
                      }}
                    >
                      {sfxEnabled ? '🔊 Sound on' : '🔇 Muted'}
                    </button>
                  )}

                  {entryCurtain ? (
                    /* Curtain loading beat — reads as LOADING on purpose (user, 07-10):
                       title + plain spinner + please-wait line. The storm/atmosphere is
                       saved for the ready beat, where the door is. */
                    <>
                      <h2 style={{
                        fontWeight: 800, margin: '0 0 30px', color: 'rgba(255,255,255,0.92)',
                        fontSize: '2rem', letterSpacing: '6px', textTransform: 'uppercase',
                      }}>
                        {(((chatGameStatus?.name || gameName) || '').replace(/\s*-\s*Competition\s*$/i, ''))}
                        {chatGameStatus?.interstitial_icon ? ` ${chatGameStatus.interstitial_icon}` : ''}
                      </h2>
                      <div style={{
                        width: 44, height: 44, margin: '0 auto 22px', borderRadius: '50%',
                        border: '3px solid rgba(255,255,255,0.12)',
                        borderTopColor: 'rgba(255,255,255,0.65)',
                        animation: 'curtainSpin 1s linear infinite',
                      }} />
                      <div style={{
                        color: 'rgba(255,255,255,0.4)', marginBottom: 8, fontSize: '0.78rem',
                        letterSpacing: '3px', textTransform: 'uppercase', fontWeight: 700,
                      }}>
                        Please wait...
                      </div>
                      <p style={{ color: 'rgba(255,255,255,0.75)', marginBottom: 32, fontSize: '1.05rem' }}>
                        {chatGameStatus?.ui_terms?.waiting_line || 'Setting the scene…'}
                      </p>
                    </>
                  ) : portedAmbientLoading ? (
                  /* 🪟🫁 Ported arrival (Nina 08-28): a companion moving in,
                     not a show spinning up — one breathing circle, soft
                     rotating lines, no percent bar, no backstage status. */
                  <>
                    <div className="portedBreathe" style={{
                      width: 96,
                      height: 96,
                      margin: '0 auto 30px',
                      borderRadius: '50%',
                      background: 'radial-gradient(circle at 38% 32%, rgba(255,222,168,0.95), rgba(240,172,85,0.5) 55%, rgba(240,172,85,0.1) 78%)',
                      boxShadow: '0 0 60px 18px rgba(240,172,85,0.22)',
                    }} />
                    {portedSettleIn ? (
                      /* Fresh port: today's heading, lines and rotation, exactly. */
                      <>
                        <h2 style={{ fontWeight: 600, marginBottom: 10, color: '#fff', fontSize: '1.4rem' }}>
                          {(chatGameStatus?.resident_name || portedSettleIn?.agent_name)
                            ? `${chatGameStatus?.resident_name || portedSettleIn?.agent_name} is settling in`
                            : 'Your space is waking up'}
                        </h2>
                        <p style={{ color: 'rgba(255,255,255,0.55)', marginBottom: 32, fontSize: '0.95rem' }}>
                          {PORTED_LOADER_LINES[portedLoaderIdx % PORTED_LOADER_LINES.length]}
                        </p>
                      </>
                    ) : (() => {
                      /* 🏠 Every other ported load (Nina 09-24): the resident is
                         HOME, not settling in (the old line froze on "making it
                         theirs"), plus ONE tip per load. Return lines need this
                         device to have been here before (the known-ported
                         marker); the gone-a-while line needs the owner's seat
                         (the live payload once status lands, the marker's owner
                         seat before). No name, no line. */
                      const residentName = chatGameStatus?.resident_name || portedResidentName || portedKnownAtLoad?.name || null;
                      const tip = pickPortedLoaderTip({
                        name: residentName,
                        isReturn: !!portedKnownAtLoad,
                        isOwner: chatGameStatus ? isPortedOwner : isPortedOwnerSeat(portedKnownAtLoad?.ownerSeat, portedViewerSeat),
                        seed: portedTipSeedRef.current,
                        keepId: portedTipIdRef.current,
                      });
                      if (tip) portedTipIdRef.current = tip.id;
                      return (
                        <>
                          <h2 style={{ fontWeight: 600, marginBottom: 10, color: '#fff', fontSize: '1.4rem' }}>
                            {residentName ? `${residentName} is home` : 'Opening the door'}
                          </h2>
                          <p style={{ color: 'rgba(255,255,255,0.55)', marginBottom: 32, fontSize: '0.95rem', minHeight: '1.5em', maxWidth: 420, marginLeft: 'auto', marginRight: 'auto', lineHeight: 1.5 }}>
                            {tip ? tip.text : ''}
                          </p>
                        </>
                      );
                    })()}
                  </>
                  ) : introCaptionsActive ? (
                  /* Intro caption beat rides the load: the host reads you their bio
                     while the world generates. Asset status line below stays. */
                  <>{introCaptionScreen}</>
                  ) : (
                  <>
                  <div
                    style={{
                      width: 120,
                      height: 120,
                      margin: '0 auto 32px',
                      borderRadius: '50%',
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      animation: 'assetSpin 2s linear infinite',
                    }}
                  >
                    <span style={{ fontSize: '3rem', color: '#fff' }}>
                      ✨
                    </span>
                  </div>

                  <h2 style={{ fontWeight: 600, marginBottom: 16, color: '#fff', fontSize: '1.75rem' }}>
                    {assetLoadingMessage}
                  </h2>

                  <p style={{ color: '#ccc', marginBottom: 32, fontSize: '1rem' }}>
                    {/* World-building voice for spaces (user call 07-11); competitive
                        keeps the kitchen gag until its own copy pass */}
                    {(chatGameStatus?.name || gameName) && (
                      (chatGameStatus?.competitive_mode || chatGameStatus?.imposter_mode || earlyCompetitiveMode)
                        ? `Cooking up "${chatGameStatus?.name || gameName}" 🍳`
                        : `"${chatGameStatus?.name || gameName}" is coming to life... ✨`
                    )}
                  </p>
                  </>
                  )}

                  {/* Backstage status + hints break immersion on themed entrances —
                      curtain games keep only the in-fiction waiting line above.
                      🪟 Ported loads keep the ambient frame: no backstage. */}
                  {!entryCurtain && !portedAmbientLoading && assetLoadingStatus && (() => {
                    const themeStatus = assetLoadingStatus.game_assets?.ui_theme;
                    const bgStatus = assetLoadingStatus.game_assets?.background_image;
                    const players = assetLoadingStatus.player_assets?.filter(p => !p.is_human) || [];
                    const generatingPlayer = players.find(p => p.profile_image_status === 'generating');
                    const pendingPlayers = players.filter(p => p.profile_image_status !== 'complete' && p.profile_image_status !== 'failed' && p.profile_image_status !== 'generating');
                    const completeCount = players.filter(p => p.profile_image_status === 'complete').length;

                    // World-building voice for spaces (user call 07-11): the locals are
                    // moving into their world, not actors prepping a show. Competitive
                    // (incl. pre-status earlyCompetitiveMode) keeps the backstage gags.
                    const isCompLoading = chatGameStatus?.competitive_mode || chatGameStatus?.imposter_mode || earlyCompetitiveMode;
                    const rawSpaceName = ((chatGameStatus?.name || gameName) || '').trim();
                    const worldName = rawSpaceName && rawSpaceName.length <= 30 ? `"${rawSpaceName}"` : 'your world';
                    let statusText = isCompLoading ? 'Warming up the magic... 🪄' : 'A new world is stirring... 🌅';
                    if (themeStatus === 'generating') {
                      statusText = isCompLoading ? 'Setting the mood... 🎨' : `Choosing the colors of ${worldName}... 🎨`;
                    } else if (bgStatus === 'generating') {
                      statusText = isCompLoading ? 'Building the set... 🏗️' : `Painting ${worldName} into view... 🖌️`;
                    } else if (generatingPlayer) {
                      statusText = isCompLoading
                        ? `${generatingPlayer.player_name} is posing for their headshot... 📸 (${completeCount}/${players.length})`
                        : `${generatingPlayer.player_name} is finding their face... 🪞 (${completeCount}/${players.length})`;
                    } else if (pendingPlayers.length > 0) {
                      statusText = isCompLoading
                        ? `${pendingPlayers.length} player${pendingPlayers.length > 1 ? 's' : ''} still in wardrobe... 👗`
                        : `${pendingPlayers.length} local${pendingPlayers.length > 1 ? 's' : ''} still taking form... 🌱`;
                    } else {
                      statusText = isCompLoading
                        ? 'Rolling out the red carpet... 🎬'
                        : 'The locals are gathering to meet you... 🚪';
                    }

                    return (
                      <p style={{ color: '#ccc', fontSize: '0.95rem', marginBottom: 0 }}>
                        {statusText}
                      </p>
                    );
                  })()}

                  {/* Rotating tip — random 3-hint sample per session, advances
                      every 4s. key on the cursor so each new hint re-runs the
                      fade-in animation defined in @keyframes loadingHintFade.
                      Hidden on themed entrances (curtain) — generic platform trivia
                      yanks people out of the scene. Game-specific hints = tabled. */}
                  {!entryCurtain && (<>
                  <style>{`
                    @keyframes loadingHintFade {
                      0%   { opacity: 0; transform: translateY(6px); }
                      100% { opacity: 1; transform: translateY(0); }
                    }
                    @keyframes loadingHintGlow {
                      0%, 100% { box-shadow: 0 0 0 0 rgba(200,156,232,0.0), 0 4px 18px rgba(123,31,162,0.18); }
                      50%      { box-shadow: 0 0 0 4px rgba(200,156,232,0.10), 0 6px 22px rgba(123,31,162,0.28); }
                    }
                    @keyframes loadingHintBulb {
                      0%, 100% { transform: rotate(-8deg) scale(1); }
                      50%      { transform: rotate(8deg) scale(1.12); }
                    }
                  `}</style>
                  {/* Trivia hint yields while the intro caption beat is on screen —
                      one reading surface at a time (user call 07-11 v2: captions are
                      a full screen now, not a bar in this slot). Ported loads skip
                      trivia entirely: the ambient frame stays quiet. */}
                  {!introCaptionsActive && !portedAmbientLoading && (
                  <div
                    key={loadingHintCursor}
                    style={{
                      marginTop: 32,
                      marginBottom: 0,
                      maxWidth: 600,
                      marginLeft: 'auto',
                      marginRight: 'auto',
                      padding: '18px 22px',
                      background: 'linear-gradient(135deg, rgba(167,80,224,0.22) 0%, rgba(123,31,162,0.18) 100%)',
                      border: '1.5px solid rgba(200,156,232,0.45)',
                      borderRadius: 16,
                      color: '#fbf4ff',
                      fontSize: '1.08rem',
                      fontWeight: 500,
                      lineHeight: 1.5,
                      textAlign: 'left',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 14,
                      animation: 'loadingHintFade 0.45s ease both, loadingHintGlow 2.6s ease-in-out infinite',
                    }}
                  >
                    <span
                      aria-hidden="true"
                      style={{
                        fontSize: '1.6rem',
                        flexShrink: 0,
                        display: 'inline-block',
                        animation: 'loadingHintBulb 2.2s ease-in-out infinite',
                        transformOrigin: 'center',
                      }}
                    >💡</span>
                    <span>
                      <span style={{
                        display: 'block',
                        fontWeight: 800,
                        fontSize: '0.72rem',
                        letterSpacing: '0.18em',
                        textTransform: 'uppercase',
                        color: '#d9b4f5',
                        marginBottom: 4,
                      }}>Did you know?</span>
                      {(() => {
                        // Break after the first sentence: headline + payoff reads better
                        // than a paragraph blob on desktop (user call 07-09)
                        const hint = loadingHintSample[loadingHintCursor % (loadingHintSample.length || 1)] || '';
                        const parts = hint.match(/^(.*?[.!?])\s+(.+)$/s);
                        if (!parts) return hint;
                        return (
                          <>
                            <span style={{ display: 'block' }}>{parts[1]}</span>
                            <span style={{ display: 'block', marginTop: 3 }}>{parts[2]}</span>
                          </>
                        );
                      })()}
                    </span>
                  </div>
                  )}
                  </>)}
                </>
              )}

              {/* Ready state - enhanced round announcement with recap, objective, scoreboard */}
              {/* Don't show until game status and players are loaded from the backend */}
              {((assetLoadingReady || showRoundAnnouncement) && !isStartingRound && !(showAssetLoadingOverlay && !assetLoadingReady) && chatGameStatus && users.length > 0) && (
                <>
                  {isPortedSpace ? (
                  /* 🪟🏮 PORTED DOORWAY (Nina 08-28): a 1:1 companion space is not
                     a generated cast to discover — the resident IS the welcome.
                     Replaces the "who's here / a mystery to me" intro wholesale
                     for ported spaces (D2 doctrine: the mystery mechanic is for
                     strangers). Portrait in lamplight, name, DAY N plate, their
                     voice line, one warm CTA. All from portedMindProps + the
                     tagline already in the payload — zero new data. */
                  (() => {
                    const accent = THEME.accent || '#E9A34C';
                    const resName = portedMindProps?.residentName || 'your resident';
                    // 09-25: read `scene` (the payload's name), never the
                    // hardcoded objective — utils/portedLoader.portedDoorwayLine.
                    const voiceLine = portedDoorwayLine(chatGameStatus);
                    return (
                      <div style={{ textAlign: 'center', maxWidth: 560, margin: '0 auto',
                        animation: 'enterSpaceFadeIn 0.6s ease both' }}>
                        <div style={{
                          width: 116, height: 116, margin: '0 auto 4px', borderRadius: '50%',
                          overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center',
                          border: '2px solid rgba(255,222,170,0.55)',
                          boxShadow: '0 0 0 6px rgba(240,172,85,0.10), 0 0 46px rgba(240,172,85,0.42)',
                          background: 'radial-gradient(circle at 36% 30%, rgba(255,222,168,0.9), rgba(240,172,85,0.35) 60%, rgba(20,14,20,0.6))',
                        }}>
                          {portedMindProps?.residentImage
                            ? <img src={portedMindProps.residentImage} alt={resName}
                                style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                            : <span style={{ fontSize: 52 }}>🏮</span>}
                        </div>
                        <div style={{
                          fontFamily: '"Iowan Old Style", "Palatino Linotype", Palatino, Georgia, serif',
                          fontWeight: 600, fontSize: '2em', color: '#FFF2DE', marginTop: 14,
                        }}>{resName}</div>
                        {portedMindProps?.dayN != null && (
                          <div style={{ marginTop: 8 }}>
                            <span style={{
                              padding: '5px 15px', borderRadius: 8, fontWeight: 700,
                              fontFamily: 'ui-monospace, "SF Mono", Menlo, monospace', fontSize: 11,
                              letterSpacing: '0.18em', color: '#3A2606',
                              background: 'linear-gradient(178deg, #E8BC72, #B9843B 78%)',
                              border: '1px solid rgba(64,40,8,0.55)',
                              boxShadow: 'inset 0 1px 0 rgba(255,240,205,0.75), 0 2px 8px rgba(0,0,0,0.35)',
                            }}>DAY {portedMindProps.dayN} TOGETHER</span>
                          </div>
                        )}
                        {voiceLine && (
                          <p style={{
                            fontFamily: '"Iowan Old Style", Palatino, Georgia, serif', fontStyle: 'italic',
                            color: 'rgba(255,240,222,0.86)', fontSize: '1.05em', lineHeight: 1.5,
                            maxWidth: 480, margin: '20px auto 0',
                          }}>{voiceLine}</p>
                        )}
                        {/* 🔑👤 Owner only (Nina 09-24): this card greets ANYONE
                            who opens the room, strangers in a shared ported room
                            included, and only the owner carried the resident
                            here. Guests get a guest line. */}
                        <p style={{ color: 'rgba(255,238,216,0.5)', fontSize: '0.9em', marginTop: 16 }}>
                          {isPortedOwner ? (
                            <>You carried {resName} here. It&rsquo;s just the two of you.</>
                          ) : (
                            `${portedMindProps?.residentName || 'Somebody'} lives here. Say hi.`
                          )}
                        </p>
                        <button
                          onClick={() => { setSpaceIntroStep(2); enterSpace(); }}
                          className="enter-space-cta"
                          style={{
                            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                            marginTop: isMobile ? 22 : 34, backgroundColor: accent,
                            backgroundImage: 'linear-gradient(180deg, rgba(255,255,255,0.16), rgba(255,255,255,0) 42%, rgba(0,0,0,0.18))',
                            color: '#3A2606', padding: '14px 42px', borderRadius: 999,
                            fontWeight: 700, fontSize: '1.1em', letterSpacing: '0.3px',
                            border: '1px solid rgba(255,255,255,0.25)', cursor: 'pointer',
                            boxShadow: `0 2px 8px rgba(0,0,0,0.35), 0 4px 22px ${accent}59`,
                            ...(isMobile ? { position: 'sticky', bottom: 'calc(14px + env(safe-area-inset-bottom, 0px))', zIndex: 2, width: 'min(320px, 100%)' } : {}),
                          }}
                        >Step in</button>
                      </div>
                    );
                  })()
                  ) : entryCurtain ? (
                  /* STORM BEAT (user, 07-10 resequence): rain + the storm line + a pulsing
                     door. The click handler lives on the OVERLAY (see openCurtainDoor) so
                     anywhere works; the door swings open on click, then the body cold open. */
                  <div style={{ animation: 'enterSpaceFadeIn 0.5s ease both' }}>
                    <h2 style={{
                      fontWeight: 700, margin: '0 0 30px', color: 'rgba(255,255,255,0.88)',
                      fontSize: '1.7rem', letterSpacing: '5px', textTransform: 'uppercase',
                      animation: 'entryCurtainFlicker 2s linear infinite',
                    }}>
                      {entryScreen.sub || entryScreen.line}
                    </h2>
                    <div style={{
                      fontSize: '4rem', marginBottom: 18, display: 'inline-block',
                      transformOrigin: 'bottom left',
                      animation: curtainDoorOpening
                        ? 'curtainDoorSwing 1.05s ease-in forwards'
                        : 'curtainDoorPulse 1.8s ease-in-out infinite',
                    }}>
                      🚪
                    </div>
                    {!curtainDoorOpening && (
                      <p style={{
                        color: 'rgba(255,255,255,0.55)', margin: 0, fontSize: '0.9rem',
                        letterSpacing: '2px', textTransform: 'uppercase',
                      }}>
                        {entryScreen.cta || 'Click anywhere to enter'}
                      </p>
                    )}
                  </div>
                  ) : introCaptionsActive ? (
                  /* SPACE INTRO BEAT 0 — caption screen. Assets finished before the
                     host was done talking; the captions finish here, then the flow
                     continues to the full bio beat below. */
                  <div key="intro-captions" style={{ animation: 'enterSpaceFadeIn 0.4s ease both' }}>
                    {introCaptionScreen}
                  </div>
                  ) : spaceIntroActive && spaceIntroStep === 0 ? (
                  /* SPACE INTRO — ONE combined welcome screen (user call 07-12,
                     replaced the two-beat host→locals slideshow): compact host
                     block, then the host introduces the locals as they deal in,
                     one CTA. Captions already did the deep host intro. */
                  (() => {
                    const mod = chatGameStatus.moderator_character;
                    const accent = THEME.accent || '#6366f1';
                    // Visitors get the space's name; "your space" is creator-voiced
                    // (visitor = the space was finished before this browser arrived)
                    const introSpaceName = ((chatGameStatus?.name || gameName) || '').trim();
                    const welcomeLine = introVisitorRef.current
                      ? (introSpaceName && introSpaceName.length <= 32 ? `Welcome to ${introSpaceName}` : 'Welcome')
                      : 'Welcome to your space';
                    // ⏳ A guest whose stay ended is not part of the cast a new
                    // arrival is introduced to (Nina 08-07).
                    const locals = users.filter(u => !u.data?.is_eliminated && !u.is_human && !visitOver(u));
                    const you = humanPlayerId ? users.find(u => u.game_player_id === humanPlayerId || u.id === humanPlayerId) : null;
                    const totalCards = locals.length + (you ? 1 : 0);
                    const DEAL = 0.5; // per-card deal-in stagger (seconds; 0.4 tried 07-12, user preferred the original pace)
                    const CARDS_START = 0.9; // cards wait for the host block (0–0.75s)
                    // Thematic ENTER: step INTO the named world; long names fall back.
                    // 08-01 (Nina): arrow retired — the button shape is the affordance.
                    const enterLabel = introSpaceName && introSpaceName.length <= 28 ? `Step into ${introSpaceName}` : 'Enter';
                    // One scrapbook row on desktop (Nina 08-01): the whole cast
                    // deals into a single tilted row when the viewport fits it;
                    // 160px keeps host+3+You (~682px) inside 90vw down to iPad
                    // portrait. Phones keep the 07-31 2-up wrap — one row of 4
                    // would mean ~76px cards on a 375px screen.
                    const CARD_W = isMobile ? 150 : 160;
                    const CARD_H = Math.round(CARD_W * 1.34);
                    const tiltCard = (i) => ({
                      // border-box is LOAD-BEARING: the container is sized to
                      // exactly 2×CARD_W+gap, so the 1px border must live
                      // INSIDE CARD_W — content-box made each card 192px and
                      // wrapped the 2-up grid into a single column (07-31)
                      flexShrink: 0, width: CARD_W, height: CARD_H, borderRadius: 12, boxSizing: 'border-box',
                      border: `1px solid ${accent}35`,
                      position: 'relative', overflow: 'hidden', textAlign: 'left',
                      backgroundColor: 'rgba(255,255,255,0.05)',
                      // Original Tea-tab scrapbook scatter (e7f283697) — restored
                      // with the one-row layout, 08-01
                      transform: `rotate(${[-2, 1.6, -1.2, 2, -0.8][i % 5]}deg) translateY(${[0, 9, 3, 11, 5][i % 5]}px)`,
                      boxShadow: '0 5px 16px rgba(0,0,0,0.28)',
                      animation: `enterSpaceFadeIn 0.5s ease ${CARDS_START + i * DEAL}s both`,
                    });
                    return (
                      <div key="intro-host" style={{ animation: 'enterSpaceFadeIn 0.5s ease both' }}>
                        <h2 style={{ fontWeight: 800, margin: '0 0 18px', color: '#fff', fontSize: '1.9rem', letterSpacing: '0.5px', animation: 'enterSpaceFadeIn 0.5s ease both', padding: isMobile ? '0 44px' : 0 }}>
                          {/* Mobile side padding keeps the headline clear of the
                              music FAB corner (07-17) */}
                          {welcomeLine}
                        </h2>
                        <p style={{ margin: '0 0 16px', color: accent, fontSize: '0.88rem', fontWeight: 800, letterSpacing: '2.5px', textTransform: 'uppercase', animation: 'enterSpaceFadeIn 0.5s ease 0.1s both' }}>
                          Your host
                        </p>
                        {/* Compact host block — the captions just showed them big;
                            this is a reminder, the locals are the reveal now */}
                        {mod.profile_image_url ? (
                          <img src={mod.profile_image_url} alt={mod.name} style={{
                            width: 96, height: 96, borderRadius: '50%', objectFit: 'cover',
                            border: `3px solid ${accent}80`, boxShadow: `0 0 24px ${accent}35`,
                            animation: 'enterSpaceFadeIn 0.55s ease 0.2s both',
                          }} />
                        ) : (
                          <div style={{
                            width: 96, height: 96, borderRadius: '50%', margin: '0 auto',
                            background: `linear-gradient(135deg, ${accent}40, ${accent}15)`,
                            border: `3px solid ${accent}80`, boxShadow: `0 0 24px ${accent}35`,
                            display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '2.4em',
                            animation: 'enterSpaceFadeIn 0.55s ease 0.2s both',
                          }}>
                            {mod.avatar_emoji || '\u{1F399}\u{FE0F}'}
                          </div>
                        )}
                        <h2 style={{ margin: '12px 0 2px', color: '#fff', fontWeight: 800, fontSize: '1.35rem', animation: 'enterSpaceFadeIn 0.5s ease 0.3s both' }}>
                          {mod.name}
                          {/* ⓘ toggles the bio inline (user call 07-15: the visible
                              bio line crowded the screen; captions + Tea tab already
                              tell the full story) */}
                          {(mod.origin_story || mod.personality) && (
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); setIntroBioExpanded(o => !o); }}
                              aria-label={introBioExpanded ? 'Hide host story' : 'About the host'}
                              style={{
                                marginLeft: 8, verticalAlign: 'middle',
                                width: 24, height: 24, borderRadius: '50%', padding: 0,
                                background: introBioExpanded ? `${accent}30` : 'none',
                                border: `1.5px solid ${accent}70`, color: accent,
                                fontSize: '0.6em', fontWeight: 800, lineHeight: 1,
                                cursor: 'pointer',
                              }}
                            >
                              i
                            </button>
                          )}
                        </h2>
                        {mod.catchphrase && (
                          <p style={{ margin: '2px 0 0', color: accent, fontSize: '0.9rem', fontStyle: 'italic', animation: 'enterSpaceFadeIn 0.5s ease 0.4s both' }}>
                            "{mod.catchphrase}"
                          </p>
                        )}
                        {/* Bio hidden by default (user call 07-15: even one visible
                            line crowded the screen) — the ⓘ by the name reveals it.
                            origin_story FIRST, same source as The Tea tab's host
                            card (user call 07-10). */}
                        {introBioExpanded && (mod.origin_story || mod.personality) && (
                          <p style={{ margin: '12px auto 0', maxWidth: 400, color: 'rgba(255,255,255,0.78)', fontSize: '0.92rem', lineHeight: 1.6, animation: 'enterSpaceFadeIn 0.3s ease both' }}>
                            {(mod.origin_story || mod.personality).trim()}
                          </p>
                        )}
                        {Array.isArray(mod.personality_traits) && mod.personality_traits.length > 0 && (
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, justifyContent: 'center', marginTop: 12, animation: 'enterSpaceFadeIn 0.5s ease 0.65s both' }}>
                            {mod.personality_traits.slice(0, 4).map((t, i) => (
                              <span key={i} style={{
                                fontSize: '0.68em', fontWeight: 600, padding: '4px 11px', borderRadius: 11,
                                backgroundColor: `${accent}20`, color: '#fff', border: `1px solid ${accent}45`,
                              }}>{t}</span>
                            ))}
                            {mod.personality_traits.length > 4 && (
                              <span style={{
                                fontSize: '0.68em', fontWeight: 600, padding: '4px 11px', borderRadius: 11,
                                color: 'rgba(255,255,255,0.55)', border: '1px solid rgba(255,255,255,0.2)',
                              }}>+{mod.personality_traits.length - 4}</span>
                            )}
                          </div>
                        )}
                        {/* The host introduces the locals (idea #1, 07-12): one line
                            in their voice bridges the host block into the deal-in —
                            one scene, not two stacked sections. Static template. */}
                        <p style={{ margin: '24px 0 0', color: accent, fontSize: '0.95rem', fontStyle: 'italic', fontWeight: 600, animation: 'enterSpaceFadeIn 0.5s ease 0.75s both' }}>
                          "Come meet my locals. They live here!"
                        </p>
                        <div style={{
                          display: 'flex', gap: 14, justifyContent: 'center', flexWrap: 'wrap',
                          // Width fits the WHOLE cast in one row when the viewport
                          // allows (Nina 08-01); narrower screens wrap gracefully
                          // (phones land back on the 2-up look)
                          width: `min(90vw, ${totalCards * CARD_W + (totalCards - 1) * 14}px)`, marginLeft: '50%', transform: 'translateX(-50%)',
                          // Top padding fits the floating "You ⤵" label; bottom
                          // absorbs the tilt scatter
                          paddingTop: 26, paddingBottom: 18,
                        }}>
                          {locals.map((p, i) => (
                            <div key={p.id || p.name} style={tiltCard(i)}>
                              {p.profile_image_url ? (
                                <img src={p.profile_image_url} alt={p.name}
                                  style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
                              ) : (
                                /* Portrait still generating — cheap skeleton: one opacity
                                   pulse on a static layer, no gradients animating */
                                <div style={{
                                  position: 'absolute', inset: 0,
                                  background: `linear-gradient(160deg, ${accent}30, ${accent}0c)`,
                                  display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 6,
                                  animation: 'introSkeletonPulse 1.6s ease-in-out infinite',
                                }}>
                                  <div style={{ fontSize: '1.9em', color: '#fff', fontWeight: 700 }}>
                                    {p.name?.charAt(0)?.toUpperCase() || '?'}
                                  </div>
                                  <div style={{ fontSize: '0.5em', color: 'rgba(255,255,255,0.65)', letterSpacing: '1px', textTransform: 'uppercase', fontWeight: 700 }}>
                                    🎨 painting…
                                  </div>
                                </div>
                              )}
                              <div style={{
                                position: 'absolute', bottom: 0, left: 0, right: 0,
                                background: 'linear-gradient(0deg, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.45) 65%, transparent 100%)',
                                padding: '18px 9px 8px',
                              }}>
                                <div style={{ fontSize: '0.86em', fontWeight: 700, color: '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textShadow: '0 1px 3px rgba(0,0,0,0.6)' }}>{p.name}</div>
                                {p.model && (
                                  // 0.62→0.74em + brighter (Nina 08-03 round 2:
                                  // the slug is the identity line, let it read)
                                  <div style={{ marginTop: 1, fontSize: '0.74em', fontWeight: 600, color: 'rgba(255,255,255,0.8)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {p.model.split('/').pop()}
                                  </div>
                                )}
                                <div style={{ marginTop: 3, fontSize: '0.68em', color: 'rgba(255,255,255,0.7)', lineHeight: 1.35, fontStyle: 'italic', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                  {'❓'} a mystery to me!
                                </div>
                              </div>
                            </div>
                          ))}
                          {/* The visitor's own card — the original floating "You ⤵"
                              arrow is back (Nina 08-01). It is absolutely positioned
                              (zero layout width), so it cannot re-trigger the 07-31
                              blank-slab wrap bug the old SIDE "← You" label caused. */}
                          {you && (
                            <div style={{ position: 'relative', flexShrink: 0, animation: `enterSpaceFadeIn 0.5s ease ${CARDS_START + locals.length * DEAL}s both` }}>
                              <div style={{
                                position: 'absolute', top: -24, left: '50%', transform: 'translateX(-50%)', zIndex: 2,
                                color: '#fff', fontSize: '0.8em', fontWeight: 800, whiteSpace: 'nowrap',
                                textShadow: `0 1px 4px rgba(0,0,0,0.6), 0 0 12px ${accent}`,
                              }}>
                                You ⤵
                              </div>
                              <div style={{ ...tiltCard(locals.length), animation: undefined }}>
                                {you.profile_image_url ? (
                                  <img src={you.profile_image_url} alt={you.name}
                                    style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
                                ) : (
                                  <div style={{
                                    position: 'absolute', inset: 0,
                                    background: `linear-gradient(160deg, ${accent}45, ${accent}10)`,
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    fontSize: '2.4em', color: '#fff', fontWeight: 700,
                                  }}>
                                    {you.name?.charAt(0)?.toUpperCase() || '?'}
                                  </div>
                                )}
                                <div style={{
                                  position: 'absolute', bottom: 0, left: 0, right: 0,
                                  background: 'linear-gradient(0deg, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.45) 65%, transparent 100%)',
                                  padding: '18px 9px 8px',
                                }}>
                                  <div style={{ fontSize: '0.72em', fontWeight: 700, color: '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textShadow: '0 1px 3px rgba(0,0,0,0.6)' }}>{you.name}</div>
                                  <div style={{ marginTop: 3, fontSize: '0.54em', color: 'rgba(255,255,255,0.65)', lineHeight: 1.35, fontStyle: 'italic', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    the visitor. that's you!
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                        <p style={{
                          margin: '16px auto 0', maxWidth: 380, color: 'rgba(255,255,255,0.6)',
                          fontSize: '0.85rem', fontStyle: 'italic',
                          animation: `enterSpaceFadeIn 0.5s ease ${CARDS_START + totalCards * DEAL}s both`,
                        }}>
                          You don't know much about them yet. Get them talking and they'll open up!
                        </p>
                        {/* ⏳ Guests who have already come and gone — named, not
                            dealt a card: they are not part of the cast a new
                            arrival is being introduced to, but their messages
                            are still in the transcript this visitor is about to
                            read. Fades in with the same stagger as the copy
                            above so it never pops in ahead of the cards. */}
                        {!isPortedSpace && (
                          <PastVisitors
                            allUsers={users}
                            THEME={THEME}
                            align="center"
                            style={{
                              marginTop: 10,
                              animation: `enterSpaceFadeIn 0.5s ease ${CARDS_START + totalCards * DEAL + 0.12}s both`,
                            }}
                          />
                        )}
                        <button
                          className="enter-space-cta"
                          onClick={() => {
                            try { localStorage.setItem(`space_intro_v1_${gameId}`, '1'); } catch (e) { /* private mode */ }
                            setSpaceIntroStep(2);
                            enterSpace();
                          }}
                          style={{
                            // 08-01 CTA modernize (Nina): gradient depth rides backgroundImage
                            // OVER backgroundColor so any space accent works; ${accent}59
                            // hex-alpha is the established idiom on this screen (You-card bg).
                            // Hover/press states are CSS (.enter-space-cta below) — inline
                            // styles can't express them.
                            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                            marginTop: isMobile ? 18 : 38, backgroundColor: accent,
                            backgroundImage: 'linear-gradient(180deg, rgba(255,255,255,0.16), rgba(255,255,255,0) 42%, rgba(0,0,0,0.18))',
                            color: '#fff', padding: '15px 40px',
                            borderRadius: 16, fontWeight: 700, fontSize: '1.15em', letterSpacing: '0.3px',
                            border: '1px solid rgba(255,255,255,0.25)', cursor: 'pointer',
                            boxShadow: `0 2px 8px rgba(0,0,0,0.35), 0 4px 22px ${accent}59`,
                            animation: `enterSpaceFadeIn 0.5s ease ${CARDS_START + totalCards * DEAL + 0.25}s both`,
                            // Mobile audit P0 (07-16): on short phones the CTA landed
                            // below the fold with no scroll hint — new visitors saw no
                            // way in. Sticky pins it to the viewport bottom until its
                            // natural spot scrolls up. Overlay scrolls (overflowY auto,
                            // no overflow:hidden ancestors), so sticky holds.
                            ...(isMobile ? { position: 'sticky', bottom: 'calc(14px + env(safe-area-inset-bottom, 0px))', zIndex: 2, width: 'min(340px, 100%)', boxShadow: `0 4px 18px rgba(0,0,0,0.55), 0 4px 22px ${accent}66` } : {}),
                          }}
                        >
                          {enterLabel}
                        </button>
                      </div>
                    );
                  })()
                  ) : (
                  <>
                  {/* Space name - top */}
                  {(chatGameStatus?.name || gameName) && (
                    <h2 style={{
                      fontWeight: 800, marginBottom: 4, color: '#fff', fontSize: '1.9rem', letterSpacing: '0.5px',
                      ...((chatGameStatus?.competitive_mode || chatGameStatus?.imposter_mode) ? { textTransform: 'uppercase' } : {}),
                      ...(!(chatGameStatus?.competitive_mode || chatGameStatus?.imposter_mode) ? { animation: 'enterSpaceFadeIn 0.6s ease 0.2s both' } : {}),
                    }}>
                      {(chatGameStatus?.competitive_mode || chatGameStatus?.imposter_mode)
                        ? (((chatGameStatus?.name || gameName) || '').replace(/\s*-\s*Competition\s*$/i, '') + '!'
                           + (chatGameStatus?.interstitial_icon ? ` ${chatGameStatus.interstitial_icon}` : ''))
                        : 'Welcome to your space'}
                    </h2>
                  )}

                  {/* Lore tagline as a subcaption under the title (competitive/imposter) */}
                  {(chatGameStatus?.competitive_mode || chatGameStatus?.imposter_mode) && interstitialTagline && (
                    <p style={{ margin: '0 0 6px', color: 'rgba(255,255,255,0.55)', fontSize: '0.92rem', fontWeight: 500, fontStyle: 'italic', letterSpacing: '0.3px' }}>
                      {interstitialTagline}
                    </p>
                  )}

                  {/* Objective — centered + italic, directly under the caption, no bulky
                      "Objective" header (user, 07-07). First interstitial only — repeating
                      it on round/vote screens was noise (user, 07-07). */}
                  {chatGameStatus?.competitive_mode && !chatGameStatus?.imposter_mode && objectiveBody
                    && (chatGameStatus?.current_competitive_round || 1) <= 1 && (
                    <p style={{
                      margin: '0 auto 12px', maxWidth: '440px', textAlign: 'center',
                      color: 'rgba(255,255,255,0.9)', fontSize: '0.98em', fontStyle: 'italic',
                      fontWeight: 600, lineHeight: 1.5,
                    }}>
                      {objectiveBody}
                    </p>
                  )}

                  {/* Generic controller icon — only for games without a thematic
                      interstitial_icon (themed icons ride the title line instead). */}
                  {(chatGameStatus?.competitive_mode || chatGameStatus?.imposter_mode) && !chatGameStatus?.interstitial_icon && (
                    <div style={{
                      fontSize: '2.2em', marginBottom: 14,
                      textShadow: chatGameStatus?.theme_color ? `0 0 22px ${chatGameStatus.theme_color}` : 'none'
                    }}>
                      {'\u{1F3AE}'}
                    </div>
                  )}

                  {/* Custom tagline — non-competitive only */}
                  {!(chatGameStatus?.competitive_mode || chatGameStatus?.imposter_mode) && chatGameStatus?.tagline && (
                    <p style={{
                      margin: '4px 0 0',
                      color: 'rgba(255,255,255,0.5)',
                      fontSize: '0.85rem',
                      fontWeight: 500,
                      letterSpacing: '0.5px',
                      animation: 'enterSpaceFadeIn 0.6s ease 0.35s both',
                    }}>
                      {chatGameStatus.tagline}
                    </p>
                  )}

                  {/* Imposter mode: special "Find the Golden Glitch" header */}
                  {chatGameStatus?.imposter_mode ? (
                    <div style={{
                      margin: '8px 0 20px',
                      maxWidth: '440px',
                      textAlign: 'left',
                    }}>
                      <p style={{
                        margin: '0 0 8px',
                        color: '#ff6666',
                        fontSize: '1.05em',
                        fontWeight: 800,
                        fontStyle: 'italic',
                        letterSpacing: '0.5px',
                        textAlign: 'center',
                      }}>
                        Find the Golden Glitch! One word per turn, then vote!
                      </p>
                      {chatGameStatus?.scene && (
                        <>
                          <p style={{
                            margin: '24px 0 6px',
                            color: THEME.accent || '#6366f1',
                            fontSize: '0.75em',
                            textTransform: 'uppercase',
                            letterSpacing: '1.5px',
                            fontWeight: 800,
                          }}>Scene</p>
                          <p style={{
                            margin: 0,
                            color: '#fff',
                            fontSize: '0.95em',
                            fontWeight: 700,
                            lineHeight: 1.5,
                            fontStyle: 'italic',
                          }}>
                            "{chatGameStatus.scene}"
                          </p>
                        </>
                      )}
                    </div>
                  ) : (
                    /* Standard Objective + Prompt */
                    (chatGameStatus?.objective || chatGameStatus?.scene) && (
                      <div style={{
                        margin: '20px 0 20px',
                        maxWidth: '440px',
                        textAlign: 'center',
                      }}>
                        {chatGameStatus?.competitive_mode && !chatGameStatus?.imposter_mode && chatGameStatus?.objective ? (
                          <div style={{ textAlign: 'left' }}>
                            {/* Objective moved up top (centered italic under the caption) —
                                this block keeps only the round specifics. */}
                            {chatGameStatus?.twist?.name && (
                              <>
                                <p style={{
                                  margin: '24px 0 6px',
                                  color: chatGameStatus?.theme_color || THEME.accent || '#6366f1',
                                  fontSize: '0.75em',
                                  textTransform: 'uppercase',
                                  letterSpacing: '1.5px',
                                  fontWeight: 800,
                                }}>⚡ This Round · {chatGameStatus.twist.name}</p>
                                {/* Side quest replaces the AI-facing act directive for humans */}
                                {chatGameStatus.human_challenge ? (
                                  <p style={{
                                    margin: 0, display: 'inline-block', padding: '6px 16px',
                                    borderRadius: '14px', border: '1.5px solid #22c55e',
                                    backgroundColor: '#22c55e22', color: '#4ade80',
                                    fontSize: '0.95em', fontWeight: 800,
                                  }}>
                                    {sideQuestClaimed === chatGameStatus.human_challenge.text
                                ? <>✅ {chatGameStatus.human_challenge.label || 'SIDE QUEST'}: claimed! (+{chatGameStatus.human_challenge.points})</>
                                : <>🎯 {chatGameStatus.human_challenge.label || 'SIDE QUEST'}: {chatGameStatus.human_challenge.text} (+{chatGameStatus.human_challenge.points})</>}
                                  </p>
                                ) : chatGameStatus.twist.rule && (
                                  <p style={{
                                    margin: 0,
                                    color: '#fff',
                                    fontSize: '0.95em',
                                    lineHeight: 1.5,
                                    fontWeight: 700,
                                  }}>
                                    {chatGameStatus.twist.rule}
                                  </p>
                                )}
                              </>
                            )}
                            {chatGameStatus?.intro_opening_line && (
                              <>
                                <p style={{
                                  margin: '24px 0 6px',
                                  color: THEME.accent || '#6366f1',
                                  fontSize: '0.75em',
                                  textTransform: 'uppercase',
                                  letterSpacing: '1.5px',
                                  fontWeight: 800,
                                  textAlign: 'center',
                                }}>Opening Line</p>
                                <p style={{
                                  margin: 0,
                                  color: '#fff',
                                  fontSize: '0.95em',
                                  lineHeight: 1.5,
                                  fontWeight: 700,
                                  fontStyle: 'italic',
                                  textAlign: 'center',
                                }}>
                                  "{asOpeningLine(chatGameStatus.intro_opening_line)}"
                                </p>
                              </>
                            )}
                          </div>
                        ) : (
                          <>
                            {chatGameStatus?.scene && (
                              <p style={{
                                margin: '0 0 12px',
                                color: '#fff',
                                fontSize: '1.05em',
                                lineHeight: 1.6,
                                fontWeight: 600,
                                fontStyle: 'italic',
                                opacity: 0.9,
                                ...(!(chatGameStatus?.competitive_mode || chatGameStatus?.imposter_mode) ? { animation: 'enterSpaceFadeIn 0.6s ease 0.45s both' } : {}),
                              }}>
                                {chatGameStatus.scene}
                              </p>
                            )}
                            {chatGameStatus?.objective && (
                              <p style={{
                                margin: 0,
                                color: THEME.accent || '#6366f1',
                                fontSize: '0.85em',
                                fontWeight: 600,
                                opacity: 0.8,
                                ...(!(chatGameStatus?.competitive_mode || chatGameStatus?.imposter_mode) ? { animation: 'enterSpaceFadeIn 0.6s ease 0.55s both' } : {}),
                              }}>
                                {displayObjective}
                              </p>
                            )}
                          </>
                        )}
                      </div>
                    )
                  )}

                  {/* Divider — competitive/imposter only */}
                  {(chatGameStatus?.competitive_mode || chatGameStatus?.imposter_mode) && (
                    <div style={{ width: '60px', height: '1px', background: 'rgba(255,255,255,0.15)', margin: '0 auto 20px' }} />
                  )}

                  {/* Round number (competitive/imposter) or nothing (non-competitive) */}
                  {(chatGameStatus?.competitive_mode || chatGameStatus?.imposter_mode) && (
                    <h3 style={{ fontWeight: 700, marginBottom: 6, color: '#fff', fontSize: '1.4rem' }}>
                      {chatGameStatus?.imposter_mode
                        ? `Round 1/${chatGameStatus?.imposter_word_rounds_total || 3}`
                        : `Round ${chatGameStatus?.round_number || chatGameStatus?.current_competitive_round || 1}`
                      }
                    </h3>
                  )}

                  {/* Eliminated player callout — only show for round > 1.
                      Whodunnit (cleanup C4, Nina 07-19): this recap IS the
                      eviction beat now — the separate animation never fires in
                      comp, so the card opens with who got booked, on-theme. */}
                  {chatGameStatus?.competitive_mode && (chatGameStatus?.current_competitive_round || 1) > 1 && chatGameStatus?.eliminated_players?.length > 0 && (
                    (() => {
                      const lastPid = chatGameStatus.eliminated_players[chatGameStatus.eliminated_players.length - 1];
                      const player = users.find(u => u.game_player_id === lastPid || u.id === lastPid);
                      const name = player?.name || 'Unknown';
                      if (chatGameStatus?.noir_theme) {
                        return (
                          <div style={{
                            margin: '0 auto 14px',
                            padding: '8px 16px',
                            maxWidth: 340,
                            borderRadius: 8,
                            border: '1px solid rgba(239,68,68,0.45)',
                            background: 'rgba(127,29,29,0.25)',
                            color: '#fca5a5',
                            fontSize: '0.9em',
                            fontWeight: 800,
                            letterSpacing: '0.5px',
                          }}>
                            🚔 {name} was booked last round
                          </div>
                        );
                      }
                      return (
                        <p style={{
                          margin: '0 0 12px',
                          color: '#ef4444',
                          fontSize: '0.85em',
                          fontWeight: 600,
                        }}>
                          {name} was eliminated
                        </p>
                      );
                    })()
                  )}

                  {/* Players / Players */}
                  {(() => {
                    const roundNum = chatGameStatus?.current_competitive_round || 1;
                    const isFirstRound = roundNum <= 1 || !chatGameStatus?.player_scores || Object.keys(chatGameStatus.player_scores).length === 0;

                    if (isFirstRound) {
                      const activePlayers = users.filter(u => !u.data?.is_eliminated);
                      const isCompMode = chatGameStatus?.competitive_mode || chatGameStatus?.imposter_mode;
                      return (
                        <div style={{
                          marginTop: isCompMode ? '16px' : '24px',
                          marginBottom: isCompMode ? '16px' : '28px',
                          textAlign: isCompMode ? 'left' : 'center',
                          ...(isCompMode ? {} : { animation: 'enterSpaceFadeIn 0.6s ease 0.3s both' }),
                        }}>
                          <p style={{
                            margin: isCompMode ? '0 0 10px' : '0 0 16px',
                            color: isCompMode ? '#fff' : 'rgba(255,255,255,0.5)',
                            fontSize: '0.7em',
                            textTransform: 'uppercase',
                            letterSpacing: '1.5px',
                            fontWeight: 700,
                          }}>{isCompMode ? 'Participants' : "Who's here"}</p>
                          {isCompMode ? (
                            /* Competitive: vertical list with names */
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'flex-start' }}>
                              {activePlayers.map(p => (
                                <div key={p.id || p.name} style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '8px',
                                }}>
                                  <span style={{ color: '#22c55e', fontSize: '0.85em', fontWeight: 600, textShadow: '0 0 8px rgba(34,197,94,0.6)' }}>
                                    {p.name}
                                  </span>
                                  {p.model && (
                                    <span style={{ color: '#fff', fontSize: '0.7em', fontWeight: 700 }}>
                                      {p.model.split('/').pop()}
                                    </span>
                                  )}
                                </div>
                              ))}
                            </div>
                          ) : (
                            /* Non-competitive: moderator on top, player avatars below */
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '20px' }}>
                              {/* Moderator — featured on top */}
                              {chatGameStatus?.moderator_character && (() => {
                                const mod = chatGameStatus.moderator_character;
                                return (
                                  <div key="moderator" style={{
                                    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '5px',
                                    animation: 'enterSpaceFadeIn 0.5s ease 0.3s both',
                                  }}>
                                    {mod.profile_image_url ? (
                                      <img src={mod.profile_image_url} alt={mod.name} style={{
                                        width: 88, height: 88, borderRadius: '50%', objectFit: 'cover',
                                        border: `2.5px solid ${THEME.accent || '#6366f1'}70`,
                                        boxShadow: `0 0 12px ${THEME.accent || '#6366f1'}25`,
                                      }} />
                                    ) : (
                                      <div style={{
                                        width: 88, height: 88, borderRadius: '50%',
                                        background: `linear-gradient(135deg, ${THEME.accent || '#6366f1'}40, ${THEME.accent || '#6366f1'}20)`,
                                        border: `2.5px solid ${THEME.accent || '#6366f1'}70`,
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        fontSize: '2.2em',
                                        boxShadow: `0 0 12px ${THEME.accent || '#6366f1'}25`,
                                      }}>
                                        {mod.avatar_emoji || '🎙️'}
                                      </div>
                                    )}
                                    <span style={{
                                      color: 'rgba(255,255,255,0.9)', fontSize: '0.82em', fontWeight: 700,
                                      maxWidth: '220px', textAlign: 'center', lineHeight: 1.2,
                                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                    }}>
                                      {mod.name}
                                    </span>
                                    <span style={{
                                      color: `${THEME.accent || '#6366f1'}90`, fontSize: '0.55em', fontWeight: 600,
                                      textTransform: 'uppercase', letterSpacing: '1px',
                                    }}>HOST</span>
                                    {/* One clamped line only — the full story lives behind the
                                        host panel ⓘ and the Tea host card (user call 07-10:
                                        keep the interstitial vertically lean) */}
                                    {mod.personality && (
                                      <span style={{
                                        color: 'rgba(255,255,255,0.55)', fontSize: '0.68em', fontWeight: 500,
                                        maxWidth: '340px', textAlign: 'center', lineHeight: 1.45,
                                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                      }}>
                                        {mod.personality}
                                      </span>
                                    )}
                                  </div>
                                );
                              })()}
                              {/* Regular characters — row below moderator. Breaks out of the
                                  500px column (50% + translate) so three cards fit per row on
                                  desktop instead of stacking two-wide and running off-screen. */}
                              <div style={{
                                display: 'flex', gap: '14px', justifyContent: 'center', flexWrap: 'wrap',
                                width: 'min(90vw, 640px)', marginLeft: '50%', transform: 'translateX(-50%)',
                                // Absorb the tilt recipe's translateY scatter so the cards
                                // don't crowd the Enter CTA below (user report, 07-10).
                                paddingBottom: '14px',
                              }}>
                              {activePlayers.map((p, i) => {
                                // Non-competitive welcome: character CARDS (portrait + bio
                                // sneak peek) so the interstitial actually introduces the
                                // locals — the trailer for what's in store (user call 07-09).
                                // Competitive keeps the compact circles — its interstitial
                                // also fits a scoreboard.
                                const miniBio = p.character_description || p.data?.character_description || null;
                                if (!(chatGameStatus?.competitive_mode || chatGameStatus?.imposter_mode)) {
                                  // Tea-tab polaroid clone (same tilt/size recipe), dossier-aware:
                                  // the bio stays a mystery until THIS viewer has unlocked at
                                  // least one fact about them (user call 07-10 — the welcome
                                  // screen shouldn't spoil what the dossier makes you earn).
                                  let known = false;
                                  try {
                                    const dst = JSON.parse(localStorage.getItem(`dossier_v1_${gameId}`) || 'null');
                                    known = !!(dst && dst.v === 1 && Object.keys(dst.revealed?.[p.id] || {}).length > 0);
                                  } catch (e) { /* private mode */ }
                                  return (
                                    <div key={p.id || p.name} style={{
                                      flexShrink: 0, width: 118, height: 158, borderRadius: '12px',
                                      border: `1px solid ${THEME.accent || '#6366f1'}35`,
                                      position: 'relative', overflow: 'hidden', textAlign: 'left',
                                      backgroundColor: 'rgba(255,255,255,0.05)',
                                      transform: `rotate(${[-2, 1.6, -1.2, 2, -0.8][i % 5]}deg) translateY(${[0, 9, 3, 11, 5][i % 5]}px)`,
                                      boxShadow: '0 5px 16px rgba(0,0,0,0.28)',
                                      animation: `enterSpaceFadeIn 0.4s ease ${0.4 + i * 0.08}s both`,
                                    }}>
                                      {p.profile_image_url ? (
                                        <img src={p.profile_image_url} alt={p.name}
                                          style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
                                      ) : (
                                        /* AI portrait still generating — cheap opacity-pulse
                                           skeleton (humans just get their initial) */
                                        <div style={{
                                          position: 'absolute', inset: 0,
                                          background: `linear-gradient(160deg, ${THEME.accent || '#6366f1'}${p.is_human ? '45' : '30'}, ${THEME.accent || '#6366f1'}10)`,
                                          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '6px',
                                          animation: p.is_human ? undefined : 'introSkeletonPulse 1.6s ease-in-out infinite',
                                        }}>
                                          <div style={{ fontSize: p.is_human ? '2.4em' : '1.9em', color: '#fff', fontWeight: 700 }}>
                                            {p.name?.charAt(0)?.toUpperCase() || '?'}
                                          </div>
                                          {!p.is_human && (
                                            <div style={{ fontSize: '0.5em', color: 'rgba(255,255,255,0.65)', letterSpacing: '1px', textTransform: 'uppercase', fontWeight: 700 }}>
                                              🎨 painting…
                                            </div>
                                          )}
                                        </div>
                                      )}
                                      <div style={{
                                        position: 'absolute', bottom: 0, left: 0, right: 0,
                                        background: 'linear-gradient(0deg, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.45) 65%, transparent 100%)',
                                        padding: '18px 9px 8px',
                                      }}>
                                        <div style={{ fontSize: '0.86em', fontWeight: 700, color: '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textShadow: '0 1px 3px rgba(0,0,0,0.6)' }}>{p.name}</div>
                                        {p.model && (
                                          <div style={{ marginTop: '1px', fontSize: '0.74em', fontWeight: 600, color: 'rgba(255,255,255,0.8)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                            {p.model.split('/').pop()}
                                          </div>
                                        )}
                                        <div style={{
                                          marginTop: '3px', fontSize: '0.68em', color: 'rgba(255,255,255,0.7)',
                                          lineHeight: 1.35, fontStyle: known && miniBio ? 'normal' : 'italic',
                                          display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
                                        }}>
                                          {known && miniBio ? miniBio : '❓ a mystery to me!'}
                                        </div>
                                      </div>
                                    </div>
                                  );
                                }
                                return (
                                <div key={p.id || p.name} style={{
                                  display: 'flex',
                                  flexDirection: 'column',
                                  alignItems: 'center',
                                  gap: '6px',
                                  animation: `enterSpaceFadeIn 0.4s ease ${0.4 + i * 0.08}s both`,
                                }}>
                                  {p.profile_image_url ? (
                                    <img
                                      src={p.profile_image_url}
                                      alt={p.name}
                                      style={{
                                        width: 44,
                                        height: 44,
                                        borderRadius: '50%',
                                        objectFit: 'cover',
                                        border: `2px solid ${THEME.accent || '#6366f1'}40`,
                                      }}
                                    />
                                  ) : (
                                    <div style={{
                                      width: 44,
                                      height: 44,
                                      borderRadius: '50%',
                                      background: `${THEME.accent || '#6366f1'}30`,
                                      border: `2px solid ${THEME.accent || '#6366f1'}40`,
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      fontSize: '1.1em',
                                      color: '#fff',
                                      fontWeight: 700,
                                    }}>
                                      {p.name?.charAt(0)?.toUpperCase() || '?'}
                                    </div>
                                  )}
                                  <span style={{
                                    color: 'rgba(255,255,255,0.7)',
                                    fontSize: '0.7em',
                                    fontWeight: 600,
                                    maxWidth: '60px',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                  }}>
                                    {p.name}
                                  </span>
                                </div>
                                );
                              })}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    } else if (chatGameStatus?.competitive_mode) {
                      return (
                        <div style={{ marginTop: '16px', marginBottom: '16px', textAlign: 'left' }}>
                          <p style={{
                            margin: '0 0 8px',
                            color: '#fff',
                            fontSize: '0.7em',
                            textTransform: 'uppercase',
                            letterSpacing: '1.5px',
                            fontWeight: 700,
                          }}>Remaining Players</p>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'flex-start' }}>
                            {Object.entries(chatGameStatus.player_scores)
                              .sort(([,a], [,b]) => (b || 0) - (a || 0))
                              .slice(0, 5)
                              .map(([playerId, score], index) => {
                                const player = users.find(u => u.game_player_id === playerId || u.id === playerId);
                                const eliminated = chatGameStatus.eliminated_players?.includes(playerId);
                                return (
                                  <div key={playerId} style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    opacity: eliminated ? 0.4 : 1,
                                  }}>
                                    <span style={{
                                      color: index === 0 ? (THEME.accent || '#6366f1') : 'rgba(255,255,255,0.4)',
                                      fontSize: '0.75em',
                                      fontWeight: 800,
                                      width: '20px',
                                    }}>
                                      {index === 0 ? '\u{1F451}' : `#${index + 1}`}
                                    </span>
                                    <span style={{
                                      color: eliminated ? THEME.textOnPanelMuted : THEME.textOnPanel,
                                      fontSize: '0.8em',
                                      fontWeight: 600,
                                      textDecoration: eliminated ? 'line-through' : 'none',
                                    }}>
                                      {player?.name || 'Unknown'}
                                    </span>
                                    <span style={{
                                      marginLeft: '4px',
                                      backgroundColor: eliminated ? 'rgba(255,255,255,0.08)' : (THEME.accent || '#6366f1'),
                                      color: eliminated ? THEME.textOnPanelMuted : '#fff',
                                      fontSize: '0.78em',
                                      fontWeight: 800,
                                      padding: '2px 9px',
                                      borderRadius: '11px',
                                      lineHeight: 1.5,
                                      fontVariantNumeric: 'tabular-nums',
                                      boxShadow: eliminated ? 'none' : `0 0 8px ${THEME.accent || '#6366f1'}55`,
                                    }}>
                                      {score || 0} pts
                                    </span>
                                  </div>
                                );
                              })}
                          </div>
                        </div>
                      );
                    }
                    return null;
                  })()}

                  {/* Mystery hook — the locals open up as you engage (pairs with the
                      dossier's earn-the-bio reveal). Non-competitive only. */}
                  {!(chatGameStatus?.competitive_mode || chatGameStatus?.imposter_mode) && (
                    <p style={{
                      margin: '4px auto 0', maxWidth: '380px', color: 'rgba(255,255,255,0.6)',
                      fontSize: '0.85rem', fontStyle: 'italic',
                      animation: 'enterSpaceFadeIn 0.6s ease 0.6s both',
                    }}>
                      You don't know much about them yet. Get them talking and they'll open up!
                    </p>
                  )}

                  <button
                    onClick={enterSpace}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '8px',
                      // Breathing room above the CTA — it was crowding the player list
                      // (user, 07-07) and the tilted cards (user, 07-10).
                      marginTop: (chatGameStatus?.competitive_mode || chatGameStatus?.imposter_mode) ? '14px' : '20px',
                      backgroundColor: THEME.accent || '#667eea',
                      color: '#fff',
                      padding: (chatGameStatus?.competitive_mode || chatGameStatus?.imposter_mode) ? '14px 36px' : '14px 36px',
                      borderRadius: '24px',
                      fontWeight: 700,
                      fontSize: (chatGameStatus?.competitive_mode || chatGameStatus?.imposter_mode) ? '1.3em' : '1.15em',
                      letterSpacing: '0.5px',
                      border: 'none',
                      cursor: 'pointer',
                      transition: 'transform 0.15s, box-shadow 0.15s, background-color 0.15s',
                      boxShadow: `0 2px 8px rgba(0,0,0,0.3)`,
                      ...(!(chatGameStatus?.competitive_mode || chatGameStatus?.imposter_mode) ? { animation: 'enterSpaceFadeIn 0.6s ease 0.7s both' } : {}),
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = `0 4px 16px rgba(0,0,0,0.4)`;
                      e.currentTarget.style.filter = 'brightness(1.15)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = `0 2px 8px rgba(0,0,0,0.3)`;
                      e.currentTarget.style.filter = 'none';
                    }}
                  >
                    {(chatGameStatus?.competitive_mode || chatGameStatus?.imposter_mode)
                      ? (chatGameStatus?.ui_terms?.start_cta || "Let's Go!")
                      : <>Enter &rarr;</>}
                  </button>
                  </>
                  )}
                </>
              )}
            </div>

            {/* CSS for spin animation + competitive mode animations + hints */}
            <style>{`
              @keyframes enterSpaceFadeIn {
                from { opacity: 0; transform: translateY(12px); }
                to { opacity: 1; transform: translateY(0); }
              }
              /* Enter-CTA states: filter-only on purpose — the fadeIn animation
                 fills 'both', so it owns transform forever and a :active scale
                 would be silently ignored. */
              .enter-space-cta { transition: filter 150ms ease; }
              .enter-space-cta:hover { filter: brightness(1.07); }
              .enter-space-cta:active { filter: brightness(0.88); }
              .enter-space-cta:focus-visible { outline: 2px solid rgba(255,255,255,0.85); outline-offset: 3px; }
              @media (prefers-reduced-motion: reduce) { .enter-space-cta { animation: none; } }
              @keyframes introSkeletonPulse {
                0%, 100% { opacity: 0.45; }
                50% { opacity: 0.85; }
              }
              @keyframes enterButtonPulse {
                0%, 100% { transform: scale(1); box-shadow: 0 4px 24px rgba(102,126,234,0.4), 0 0 48px rgba(102,126,234,0.2); }
                50% { transform: scale(1.04); box-shadow: 0 6px 32px rgba(102,126,234,0.6), 0 0 60px rgba(102,126,234,0.35); }
              }
              @keyframes hintPopIn {
                from { opacity: 0; transform: scale(0.9); }
                to { opacity: 1; transform: scale(1); }
              }
              @keyframes hintGlow {
                0%, 100% { filter: brightness(1); }
                50% { filter: brightness(1.25); }
              }
              @keyframes assetSpin {
                0% { transform: rotate(0deg); }
                100% { transform: rotate(360deg); }
              }
              @keyframes portedBreathe {
                0%, 100% { transform: scale(1); opacity: 0.85; }
                50% { transform: scale(1.07); opacity: 1; }
              }
              .portedBreathe { animation: portedBreathe 4.5s ease-in-out infinite; }
              @media (prefers-reduced-motion: reduce) {
                .portedBreathe { animation: none; }
              }
              @keyframes phaseBannerPulse {
                0%, 100% { opacity: 1; }
                50% { opacity: 0.85; }
              }
              @keyframes phaseBannerGlow {
                0%, 100% { box-shadow: 0 2px 16px rgba(34, 197, 94, 0.5); }
                50% { box-shadow: 0 2px 24px rgba(34, 197, 94, 0.8); }
              }
              @keyframes voteBannerGlow {
                0%, 100% { box-shadow: 0 2px 20px rgba(220, 38, 38, 0.7), 0 0 40px rgba(239, 68, 68, 0.4); }
                50% { box-shadow: 0 4px 30px rgba(220, 38, 38, 0.9), 0 0 60px rgba(239, 68, 68, 0.6); }
              }
              @keyframes pauseToastSlideUp {
                from { opacity: 0; transform: translateX(-50%) translateY(20px); }
                to { opacity: 1; transform: translateX(-50%) translateY(0); }
              }
              @keyframes speakerDotPulse {
                0%, 100% { opacity: 1; transform: scale(1); }
                50% { opacity: 0.5; transform: scale(0.7); }
              }
              @keyframes votingTimerDrain {
                from { width: 100%; }
                to { width: 0%; }
              }
              @keyframes entryCurtainFlicker {
                0%, 100% { opacity: 1; } 92% { opacity: 1; } 93% { opacity: 0.4; } 95% { opacity: 1; } 97% { opacity: 0.6; }
              }
              @keyframes entryCtaPulse {
                0%, 100% { transform: scale(1); filter: brightness(1); }
                50% { transform: scale(1.045); filter: brightness(1.22); }
              }
            `}</style>
          </div>
          );
        })()}

        {/* Cinematic entry transition — non-competitive mode */}
        {cinematicPhase && (() => {
          const entranceEffect = aiGeneratedTheme?.entrance_effect || 'default';
          const ac = THEME.accent || '#667eea';
          return (
          <>
            <style>{`
              @keyframes cinematicNameIn {
                0% { opacity: 0; transform: scale(0.9); letter-spacing: 4px; }
                100% { opacity: 1; transform: scale(1); letter-spacing: 1.5px; }
              }
              @keyframes cinematicNameOut {
                0% { opacity: 1; transform: scale(1); }
                100% { opacity: 0; transform: scale(1.1); }
              }
              @keyframes cinematicTaglineIn {
                0% { opacity: 0; transform: translateY(8px); }
                100% { opacity: 0.5; transform: translateY(0); }
              }
              @keyframes cinematicAccentLine {
                0% { width: 0; opacity: 0; }
                50% { opacity: 1; }
                100% { width: 80px; opacity: 0; }
              }
              @keyframes cinematicWorldReveal {
                0% { opacity: 1; }
                100% { opacity: 0; }
              }
              /* === Entrance Effect Keyframes === */
              @keyframes efxStarfieldLayer {
                0% { transform: scale(0.3) rotate(0deg); opacity: 0; }
                15% { opacity: 1; }
                100% { transform: scale(2.5) rotate(20deg); opacity: 0; }
              }
              @keyframes efxFlicker {
                0%, 100% { opacity: 0; }
                3% { opacity: 0.7; background: rgba(255,255,255,0.05); }
                6% { opacity: 0; }
                10% { opacity: 0.5; }
                13% { opacity: 0; }
                50% { opacity: 0; }
                52% { opacity: 0.3; background: rgba(255,0,0,0.08); }
                54% { opacity: 0; }
                80% { opacity: 0; }
                82% { opacity: 0.6; background: rgba(255,255,255,0.03); }
                84% { opacity: 0; }
                90% { opacity: 0.2; background: rgba(255,0,0,0.05); }
                92% { opacity: 0; }
              }
              @keyframes efxSparkleFloat {
                0% { transform: translateY(0) scale(1); opacity: 0; }
                10% { opacity: 1; }
                100% { transform: translateY(-120vh) scale(0.5); opacity: 0; }
              }
              @keyframes efxRainFall {
                0% { transform: translateY(-10vh); opacity: 0; }
                10% { opacity: 0.6; }
                90% { opacity: 0.6; }
                100% { transform: translateY(110vh); opacity: 0; }
              }
              @keyframes efxFireRise {
                0% { transform: translateY(0) scale(1); opacity: 0; }
                15% { opacity: 1; }
                100% { transform: translateY(-110vh) scale(0.3); opacity: 0; }
              }
              @keyframes efxMatrixFall {
                0% { transform: translateY(-10vh); opacity: 0; }
                5% { opacity: 0.8; }
                95% { opacity: 0.8; }
                100% { transform: translateY(110vh); opacity: 0; }
              }
              @keyframes efxWave {
                0% { background-position: 0% 50%; }
                50% { background-position: 100% 50%; }
                100% { background-position: 0% 50%; }
              }
              @keyframes efxPortalRing {
                0% { transform: scale(0); opacity: 0.8; border-width: 3px; }
                100% { transform: scale(4); opacity: 0; border-width: 1px; }
              }
              @keyframes efxSnowDrift {
                0% { transform: translateY(-5vh) translateX(0); opacity: 0; }
                10% { opacity: 0.8; }
                50% { transform: translateY(50vh) translateX(20px); }
                100% { transform: translateY(110vh) translateX(-10px); opacity: 0; }
              }
              @keyframes efxSmokeDrift {
                0% { transform: scale(0.8) translateX(-20%); opacity: 0; }
                30% { opacity: 0.15; }
                70% { opacity: 0.15; }
                100% { transform: scale(1.5) translateX(20%); opacity: 0; }
              }
              @keyframes efxElectricFlash {
                0%, 100% { opacity: 0; }
                5% { opacity: 0.9; }
                8% { opacity: 0; }
                12% { opacity: 0.6; }
                14% { opacity: 0; }
                60% { opacity: 0; }
                62% { opacity: 0.8; }
                64% { opacity: 0.2; }
                66% { opacity: 0.7; }
                68% { opacity: 0; }
              }
            `}</style>
            <div style={{
              position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
              backgroundColor: '#000',
              zIndex: 200000,
              display: 'flex', flexDirection: 'column',
              alignItems: 'center', justifyContent: 'center',
              opacity: cinematicPhase === 'blackout' ? 1 : cinematicPhase === 'fadeIn' ? 0 : 1,
              transition: cinematicPhase === 'blackout'
                ? 'opacity 0.4s ease-in'
                : cinematicPhase === 'fadeIn'
                ? 'opacity 0.9s ease-out'
                : 'none',
              pointerEvents: cinematicPhase === 'fadeIn' ? 'none' : 'auto',
              overflow: 'hidden',
            }}>
              {/* === Themed entrance effect layer === */}
              {(cinematicPhase === 'reveal' || cinematicPhase === 'fadeIn') && entranceEffect !== 'default' && (
                <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, zIndex: 0, overflow: 'hidden' }}>
                  {/* Starfield: dots radiating outward from center */}
                  {entranceEffect === 'starfield' && [1, 2, 3].map(layer => (
                    <div key={layer} style={{
                      position: 'absolute', top: '50%', left: '50%',
                      width: '2px', height: '2px',
                      borderRadius: '50%',
                      backgroundColor: '#fff',
                      animation: `efxStarfieldLayer ${1.5 + layer * 0.4}s ease-out ${layer * 0.15}s infinite`,
                      boxShadow: Array.from({ length: 40 }, () => {
                        const x = (Math.random() - 0.5) * 600;
                        const y = (Math.random() - 0.5) * 600;
                        const size = Math.random() * 2 + 1;
                        return `${x}px ${y}px 0 ${size}px rgba(255,255,255,${Math.random() * 0.6 + 0.3})`;
                      }).join(', '),
                    }} />
                  ))}

                  {/* Flicker: screen static effect */}
                  {entranceEffect === 'flicker' && (
                    <div style={{
                      position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                      background: `repeating-linear-gradient(0deg, rgba(255,255,255,0.03) 0px, rgba(255,255,255,0.03) 1px, transparent 1px, transparent 3px)`,
                      animation: 'efxFlicker 0.8s steps(1) infinite',
                    }} />
                  )}

                  {/* Sparkle: glitter particles floating upward */}
                  {entranceEffect === 'sparkle' && [1, 2, 3].map(layer => (
                    <div key={layer} style={{
                      position: 'absolute', bottom: '-10%', left: '50%',
                      width: '2px', height: '2px',
                      borderRadius: '50%',
                      backgroundColor: ac,
                      animation: `efxSparkleFloat ${2 + layer * 0.5}s ease-out ${layer * 0.3}s infinite`,
                      boxShadow: Array.from({ length: 30 }, () => {
                        const x = (Math.random() - 0.5) * 800;
                        const y = Math.random() * 200;
                        const size = Math.random() * 2 + 0.5;
                        return `${x}px ${y}px 0 ${size}px ${ac}${['99', 'CC', 'FF'][Math.floor(Math.random() * 3)]}`;
                      }).join(', '),
                    }} />
                  ))}

                  {/* Rain: vertical streaks falling */}
                  {entranceEffect === 'rain' && [1, 2].map(layer => (
                    <div key={layer} style={{
                      position: 'absolute', top: 0, left: '50%',
                      width: '1px', height: '20px',
                      backgroundColor: 'transparent',
                      animation: `efxRainFall ${0.8 + layer * 0.3}s linear ${layer * 0.15}s infinite`,
                      boxShadow: Array.from({ length: 50 }, () => {
                        const x = (Math.random() - 0.5) * 1200;
                        const y = Math.random() * 200;
                        return `${x}px ${y}px 0 0 rgba(150,180,220,${Math.random() * 0.3 + 0.15})`;
                      }).join(', '),
                    }} />
                  ))}

                  {/* Fire: ember particles rising */}
                  {entranceEffect === 'fire' && (
                    <>
                      {/* Warm glow at bottom */}
                      <div style={{
                        position: 'absolute', bottom: 0, left: 0, right: 0, height: '40%',
                        background: 'radial-gradient(ellipse at 50% 100%, rgba(255,80,0,0.15) 0%, transparent 70%)',
                      }} />
                      {[1, 2, 3].map(layer => (
                        <div key={layer} style={{
                          position: 'absolute', bottom: '-5%', left: '50%',
                          width: '2px', height: '2px',
                          borderRadius: '50%',
                          backgroundColor: '#ff6600',
                          animation: `efxFireRise ${1.8 + layer * 0.4}s ease-out ${layer * 0.2}s infinite`,
                          boxShadow: Array.from({ length: 25 }, () => {
                            const x = (Math.random() - 0.5) * 600;
                            const y = Math.random() * 100;
                            const colors = ['rgba(255,100,0,0.8)', 'rgba(255,160,0,0.6)', 'rgba(255,60,0,0.7)', 'rgba(255,200,50,0.5)'];
                            return `${x}px ${y}px 0 ${Math.random() * 2 + 1}px ${colors[Math.floor(Math.random() * colors.length)]}`;
                          }).join(', '),
                        }} />
                      ))}
                    </>
                  )}

                  {/* Matrix: falling green dots */}
                  {entranceEffect === 'matrix' && [1, 2].map(layer => (
                    <div key={layer} style={{
                      position: 'absolute', top: 0, left: '50%',
                      width: '2px', height: '2px',
                      borderRadius: '50%',
                      backgroundColor: '#00ff41',
                      animation: `efxMatrixFall ${1.2 + layer * 0.4}s linear ${layer * 0.3}s infinite`,
                      boxShadow: Array.from({ length: 40 }, () => {
                        const x = (Math.random() - 0.5) * 1000;
                        const y = Math.random() * 100;
                        const brightness = Math.random() * 0.5 + 0.3;
                        return `${x}px ${y}px 0 1px rgba(0,255,65,${brightness})`;
                      }).join(', '),
                    }} />
                  ))}

                  {/* Waves: gentle horizontal undulation */}
                  {entranceEffect === 'waves' && (
                    <div style={{
                      position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                      background: `
                        repeating-linear-gradient(90deg,
                          transparent 0%,
                          rgba(50,130,200,0.04) 25%,
                          transparent 50%
                        )`,
                      backgroundSize: '200% 100%',
                      animation: 'efxWave 3s ease-in-out infinite',
                    }}>
                      {/* Layered wave lines */}
                      {[30, 50, 70].map((top, i) => (
                        <div key={i} style={{
                          position: 'absolute', top: `${top}%`, left: '-5%', right: '-5%',
                          height: '1px',
                          background: `linear-gradient(90deg, transparent, rgba(100,180,255,${0.08 + i * 0.03}), transparent)`,
                          animation: `efxWave ${2.5 + i * 0.5}s ease-in-out ${i * 0.3}s infinite`,
                          backgroundSize: '200% 100%',
                        }} />
                      ))}
                    </div>
                  )}

                  {/* Portal: concentric expanding rings */}
                  {entranceEffect === 'portal' && [0, 1, 2, 3, 4].map(i => (
                    <div key={i} style={{
                      position: 'absolute',
                      top: '50%', left: '50%',
                      width: '80px', height: '80px',
                      marginTop: '-40px', marginLeft: '-40px',
                      borderRadius: '50%',
                      border: `2px solid ${ac}88`,
                      animation: `efxPortalRing 2s ease-out ${i * 0.35}s infinite`,
                    }} />
                  ))}

                  {/* Snow: soft particles drifting down */}
                  {entranceEffect === 'snow' && [1, 2, 3].map(layer => (
                    <div key={layer} style={{
                      position: 'absolute', top: 0, left: '50%',
                      width: '2px', height: '2px',
                      borderRadius: '50%',
                      backgroundColor: '#fff',
                      animation: `efxSnowDrift ${3 + layer * 0.8}s ease-in-out ${layer * 0.4}s infinite`,
                      boxShadow: Array.from({ length: 30 }, () => {
                        const x = (Math.random() - 0.5) * 1000;
                        const y = Math.random() * 100;
                        const size = Math.random() * 2 + 1;
                        return `${x}px ${y}px 0 ${size}px rgba(255,255,255,${Math.random() * 0.4 + 0.2})`;
                      }).join(', '),
                    }} />
                  ))}

                  {/* Smoke: drifting gradient blobs */}
                  {entranceEffect === 'smoke' && [1, 2, 3].map(i => (
                    <div key={i} style={{
                      position: 'absolute',
                      top: `${20 + i * 20}%`,
                      left: `${10 + i * 25}%`,
                      width: '300px', height: '300px',
                      borderRadius: '50%',
                      background: `radial-gradient(circle, rgba(180,180,200,0.12) 0%, transparent 70%)`,
                      animation: `efxSmokeDrift ${3 + i * 0.5}s ease-in-out ${i * 0.4}s infinite`,
                      filter: 'blur(30px)',
                    }} />
                  ))}

                  {/* Electric: lightning pulse flashes */}
                  {entranceEffect === 'electric' && (
                    <>
                      <div style={{
                        position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                        background: `radial-gradient(ellipse at 50% 50%, ${ac}15, transparent 70%)`,
                        animation: 'efxElectricFlash 1.2s steps(1) infinite',
                      }} />
                      {/* Lightning bolt lines */}
                      {[1, 2].map(i => (
                        <div key={i} style={{
                          position: 'absolute',
                          top: '10%', left: `${30 + i * 20}%`,
                          width: '2px', height: '80%',
                          background: `linear-gradient(180deg, transparent, ${ac}60, transparent, ${ac}40, transparent)`,
                          animation: `efxElectricFlash ${0.8 + i * 0.3}s steps(1) ${i * 0.15}s infinite`,
                          filter: 'blur(1px)',
                        }} />
                      ))}
                    </>
                  )}
                </div>
              )}

              {/* Space name reveal in the dark */}
              {(cinematicPhase === 'reveal' || cinematicPhase === 'fadeIn') && (
                <div style={{
                  textAlign: 'center', padding: '0 24px',
                  animation: cinematicPhase === 'fadeIn'
                    ? 'cinematicNameOut 0.8s ease-in forwards'
                    : 'cinematicNameIn 0.8s cubic-bezier(0.16, 1, 0.3, 1) forwards',
                  position: 'relative', zIndex: 1,
                }}>
                  {/* Accent line above */}
                  <div style={{
                    width: '80px', height: '2px', margin: '0 auto 20px',
                    backgroundColor: ac,
                    animation: 'cinematicAccentLine 1.4s ease-out forwards',
                    boxShadow: `0 0 12px ${ac}60`,
                  }} />

                  {/* Space name */}
                  <div style={{
                    fontSize: 'clamp(1.4rem, 4vw, 2.2rem)',
                    fontWeight: 800,
                    color: '#fff',
                    letterSpacing: '1.5px',
                    lineHeight: 1.3,
                    textShadow: `0 0 40px ${ac}40`,
                  }}>
                    {chatGameStatus?.name || gameName}
                  </div>

                  {/* Tagline */}
                  {chatGameStatus?.tagline && (
                    <div style={{
                      fontSize: '0.85rem',
                      color: 'rgba(255,255,255,0.5)',
                      fontWeight: 500,
                      marginTop: '10px',
                      letterSpacing: '0.5px',
                      animation: 'cinematicTaglineIn 0.6s ease-out 0.3s both',
                    }}>
                      {chatGameStatus.tagline}
                    </div>
                  )}

                  {/* Accent line below */}
                  <div style={{
                    width: '80px', height: '2px', margin: '20px auto 0',
                    backgroundColor: ac,
                    animation: 'cinematicAccentLine 1.4s ease-out 0.2s forwards',
                    boxShadow: `0 0 12px ${ac}60`,
                  }} />
                </div>
              )}
            </div>
          </>
          );
        })()}
      </ThemeStyleInjector>
      </AsleepContext.Provider>
    </GameThemeProvider>
  );
};

// ============================================================================
// CUSTOM VIDEO MODAL COMPONENT
// ============================================================================

const CustomVideoModal = ({ THEME, API_BASE_URL, gameInstanceId, selectedRound, results, onClose }) => {
  const [description, setDescription] = React.useState('');
  const [isGenerating, setIsGenerating] = React.useState(false);
  const [freeform, setFreeform] = React.useState(false);
  const [enhancePrompt, setEnhancePrompt] = React.useState(false);

  const handleSubmit = async () => {
    if (!description.trim() || description.length < 10) {
      alert('Please enter at least 10 characters describing the video you want.');
      return;
    }

    if (!gameInstanceId) {
      alert('No game instance found. Please load a game first.');
      return;
    }

    // Find the round ID for the selected round number
    let gameInstanceRoundId = null;
    if (selectedRound && results) {
      const allActions = results.flatMap(r => r.actions || []);
      const roundIdToNumber = {};
      const uniqueRoundIds = [...new Set(allActions.map(a => a.game_instance_round_id).filter(id => id))];

      const roundIdToTimestamp = {};
      uniqueRoundIds.forEach(roundId => {
        const actionsInRound = allActions.filter(a => a.game_instance_round_id === roundId);
        if (actionsInRound.length > 0) {
          const timestamps = actionsInRound.map(a => new Date(a.created_at).getTime());
          roundIdToTimestamp[roundId] = Math.min(...timestamps);
        }
      });

      const sortedRoundIds = uniqueRoundIds.sort((a, b) =>
        roundIdToTimestamp[a] - roundIdToTimestamp[b]
      );
      sortedRoundIds.forEach((roundId, index) => {
        roundIdToNumber[roundId] = index + 1;
      });

      // Find the round ID that matches the selected round number
      const selectedRoundNumber = parseInt(selectedRound);
      for (const [roundId, roundNumber] of Object.entries(roundIdToNumber)) {
        if (roundNumber === selectedRoundNumber) {
          gameInstanceRoundId = roundId;
          break;
        }
      }
    }

    setIsGenerating(true);
    const savedDescription = description;
    setDescription(''); // Clear immediately to allow queuing another request

    try {
      // Use scene-image-only endpoint to generate just the scene image first
      const params = new URLSearchParams({
        generateCustomVideoSceneImage: 'true',
        gameInstanceId: gameInstanceId,
        userDescription: savedDescription,
        freeform: freeform.toString(),
        enhancePrompt: enhancePrompt.toString()
      });

      if (gameInstanceRoundId) {
        params.append('roundId', gameInstanceRoundId);
        console.log('[Custom Video] Attaching to round ID:', gameInstanceRoundId, 'Round number:', selectedRound);
      }

      const response = await fetch(
        `${API_BASE_URL}/api/takeGameStep?${params.toString()}`,
        { method: 'GET' }
      );

      const result = await parseApiResponse(response);

      if (result.success) {
        onClose();
      } else {
        alert(`Failed to generate scene: ${result.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('[Custom Video] Generation error:', error);
      alert(`Error generating scene: ${error.message}`);
    } finally {
      setIsGenerating(false);
    }
  };

  const charCount = description.length;
  const counterColor = charCount < 10 ? '#f44336' :
                       charCount > 450 ? '#FF9800' :
                       THEME.textMuted;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.8)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 3000
    }}>
      <div style={{
        backgroundColor: THEME.textLight,
        padding: '24px',
        borderRadius: '8px',
        width: '600px',
        maxWidth: '90%',
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.3)'
      }}>
        <h2 style={{
          marginTop: 0,
          marginBottom: '12px',
          color: THEME.textDark,
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          🎬 Create Custom Video
        </h2>

        <p style={{
          marginTop: 0,
          marginBottom: '20px',
          color: THEME.textMuted,
          fontSize: '0.9em',
          lineHeight: '1.5'
        }}>
          Describe the scene you want to create. AI will select the best space and characters, generate TTS audio, and create a <strong>scene image</strong>. You can then generate variants, bounding boxes, and the actual video from the action.
        </p>

        <div style={{ marginBottom: '20px' }}>
          <div style={{
            marginBottom: '8px',
            color: THEME.textDark,
            fontWeight: 'bold'
          }}>
            Video Description:
          </div>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            disabled={isGenerating}
            style={{
              width: '100%',
              padding: '12px',
              borderRadius: '4px',
              border: `1px solid ${THEME.borderLight}`,
              minHeight: '120px',
              resize: 'vertical',
              fontFamily: 'inherit',
              fontSize: '0.95em',
              backgroundColor: isGenerating ? '#f5f5f5' : 'white'
            }}
            placeholder="Example: The host stands in the main hall and announces that tomorrow will be a special competition day..."
          />
          <div style={{
            marginTop: '4px',
            fontSize: '0.8em',
            color: counterColor,
            textAlign: 'right'
          }}>
            {charCount} characters
            {charCount < 10 && charCount > 0 && ' (minimum 10)'}
          </div>
        </div>

        <div style={{
          marginBottom: '20px',
          padding: '12px',
          backgroundColor: freeform ? '#FFF3E0' : THEME.bgLight,
          borderRadius: '4px',
          border: `1px solid ${freeform ? '#FF9800' : THEME.borderLight}`
        }}>
          <label style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            cursor: isGenerating ? 'not-allowed' : 'pointer',
            color: THEME.textDark
          }}>
            <input
              type="checkbox"
              checked={freeform}
              onChange={(e) => setFreeform(e.target.checked)}
              disabled={isGenerating}
              style={{
                cursor: isGenerating ? 'not-allowed' : 'pointer'
              }}
            />
            <span style={{ fontWeight: 'bold' }}>Freeform Mode</span>
          </label>
          <div style={{
            marginTop: '8px',
            fontSize: '0.85em',
            color: THEME.textMuted,
            lineHeight: '1.4',
            marginLeft: '28px'
          }}>
            {freeform ? (
              <>
                <strong style={{ color: '#FF9800' }}>✓ Enabled:</strong> Full creative freedom - characters can move freely, multiple characters can speak, environment can be modified completely. Prioritizes following your exact description.
              </>
            ) : (
              <>
                <strong>Standard mode:</strong> Characters must match references exactly, environment preserved, single speaker, natural staging. Best for maintaining game continuity.
              </>
            )}
          </div>
        </div>

        <div style={{
          marginBottom: '20px',
          padding: '12px',
          backgroundColor: enhancePrompt ? '#E8F5E9' : THEME.bgLight,
          borderRadius: '4px',
          border: `1px solid ${enhancePrompt ? '#4CAF50' : THEME.borderLight}`
        }}>
          <label style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            cursor: isGenerating ? 'not-allowed' : 'pointer',
            color: THEME.textDark
          }}>
            <input
              type="checkbox"
              checked={enhancePrompt}
              onChange={(e) => setEnhancePrompt(e.target.checked)}
              disabled={isGenerating}
              style={{
                cursor: isGenerating ? 'not-allowed' : 'pointer'
              }}
            />
            <span style={{ fontWeight: 'bold' }}>Enhance Prompt with AI</span>
          </label>
          <div style={{
            marginTop: '8px',
            fontSize: '0.85em',
            color: THEME.textMuted,
            lineHeight: '1.4',
            marginLeft: '28px'
          }}>
            {enhancePrompt ? (
              <>
                <strong style={{ color: '#4CAF50' }}>✓ Enabled:</strong> AI will analyze your description and enhance it with detailed visual instructions, camera angles, lighting suggestions, and cinematic details for better video quality.
              </>
            ) : (
              <>
                <strong>Direct prompt:</strong> Your description will be used exactly as written without AI enhancement. Use this if you want full control over the video prompt.
              </>
            )}
          </div>
        </div>

        {isGenerating && (
          <div style={{
            padding: '12px',
            backgroundColor: '#E3F2FD',
            borderRadius: '4px',
            marginBottom: '20px',
            color: '#1976D2',
            fontSize: '0.9em',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <span>⏳</span>
            <span>Generating video... This may take a few minutes (AI analysis → scene image → video generation)</span>
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
          <button
            onClick={onClose}
            disabled={isGenerating}
            style={{
              padding: '10px 20px',
              borderRadius: '4px',
              border: `1px solid ${THEME.borderLight}`,
              backgroundColor: THEME.textLight,
              cursor: isGenerating ? 'not-allowed' : 'pointer',
              opacity: isGenerating ? 0.5 : 1,
              color: THEME.textDark
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={isGenerating || description.trim().length < 10}
            style={{
              padding: '10px 20px',
              borderRadius: '4px',
              border: 'none',
              backgroundColor: '#FF9800',
              color: 'white',
              cursor: (isGenerating || description.trim().length < 10) ? 'not-allowed' : 'pointer',
              opacity: (isGenerating || description.trim().length < 10) ? 0.5 : 1,
              fontWeight: 'bold',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            {isGenerating ? (
              <>
                <span>⏳</span>
                <span>Generating...</span>
              </>
            ) : (
              <>
                <span>🎬</span>
                <span>Generate Video</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// CUSTOM VIDEOS SECTION COMPONENT
// ============================================================================

const CustomVideosSection = ({ gameInstanceId, THEME, API_BASE_URL }) => {
  const [customVideos, setCustomVideos] = React.useState([]);
  const [isExpanded, setIsExpanded] = React.useState(false);
  const [isGenerating, setIsGenerating] = React.useState(false);
  const [regeneratingVideo, setRegeneratingVideo] = React.useState(null);
  const [newVideoDescription, setNewVideoDescription] = React.useState('');
  const [error, setError] = React.useState(null);
  const [hiddenVideos, setHiddenVideos] = React.useState(new Set());
  const [showHidden, setShowHidden] = React.useState(false);
  const [searchText, setSearchText] = React.useState('');
  const [activeSearchText, setActiveSearchText] = React.useState(''); // The actual search being applied
  const [viewMode, setViewMode] = React.useState('search'); // 'search' or 'chronological'
  const [currentPage, setCurrentPage] = React.useState(1);
  const videosPerPage = 10;

  // Fetch custom videos on mount and when expanded
  React.useEffect(() => {
    if (isExpanded && gameInstanceId) {
      fetchCustomVideos();
    }
  }, [isExpanded, gameInstanceId]);

  const fetchCustomVideos = async () => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/takeGameStep?getCustomVideos=true&gameInstanceId=${gameInstanceId}`
      );
      const result = await parseApiResponse(response);
      if (result.success) {
        setCustomVideos(result.custom_videos);
      }
    } catch (err) {
      console.error('Failed to fetch custom videos:', err);
    }
  };

  const generateCustomVideo = async () => {
    if (!newVideoDescription.trim() || newVideoDescription.length < 10) {
      setError('Please enter at least 10 characters');
      return;
    }

    setIsGenerating(true);
    setError(null);

    try {
      const response = await fetch(
        `${API_BASE_URL}/api/takeGameStep?generateCustomVideo=true&gameInstanceId=${gameInstanceId}&userDescription=${encodeURIComponent(newVideoDescription)}`,
        { method: 'GET' }
      );

      const result = await parseApiResponse(response);

      if (result.success) {
        setNewVideoDescription('');
        await fetchCustomVideos();
        alert('Video generation complete!');
      } else {
        setError(result.message || 'Failed to generate video');
      }
    } catch (err) {
      setError(`Error: ${err.message}`);
    } finally {
      setIsGenerating(false);
    }
  };

  const regenerateCustomVideo = async (actionId, videoIndex) => {
    const videoKey = `${actionId}-${videoIndex}`;
    setRegeneratingVideo(videoKey);
    setError(null);

    try {
      const response = await fetch(
        `${API_BASE_URL}/api/takeGameStep`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            regenerateCustomVideo: true,
            actionId: actionId,
            videoIndex: videoIndex
          })
        }
      );

      const result = await parseApiResponse(response);

      if (result.success) {
        await fetchCustomVideos();
      } else {
        setError(result.message || 'Failed to regenerate video');
      }
    } catch (err) {
      setError(`Error: ${err.message}`);
    } finally {
      setRegeneratingVideo(null);
    }
  };

  const toggleVideoVisibility = (videoId) => {
    setHiddenVideos(prev => {
      const newSet = new Set(prev);
      if (newSet.has(videoId)) {
        newSet.delete(videoId);
      } else {
        newSet.add(videoId);
      }
      return newSet;
    });
  };

  const handleDragStart = (video, e) => {
    const videoData = {
      url: video.video_url,
      filename: video.video_url.split('/').pop().split('?')[0],
      title: `Custom: ${video.description.substring(0, 50)}...`,
      type: 'custom_video',
      metadata: {
        description: video.description,
        characters: video.characters,
        space: video.space
      }
    };
    e.dataTransfer.setData('video-data', JSON.stringify(videoData));
    e.dataTransfer.effectAllowed = 'copy';
  };

  return (
    <div style={{
      backgroundColor: THEME.bgMedium,
      borderRadius: '8px',
      marginBottom: '16px',
      border: `1px solid ${THEME.borderDark}`
    }}>
      {/* Header */}
      <div
        onClick={() => setIsExpanded(!isExpanded)}
        style={{
          padding: '12px 16px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          cursor: 'pointer',
          backgroundColor: THEME.bgLight
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span>🎬</span>
          <span style={{ fontWeight: 'bold', color: THEME.textLight }}>Custom Videos</span>
          <span style={{
            backgroundColor: THEME.accent,
            color: 'white',
            padding: '2px 8px',
            borderRadius: '12px',
            fontSize: '0.8em'
          }}>
            {customVideos.length}
          </span>
        </div>
        <span style={{ color: THEME.textMuted }}>{isExpanded ? '▼' : '▶'}</span>
      </div>

      {/* Content */}
      {isExpanded && (
        <div style={{ padding: '16px' }}>
          {/* New Video Form */}
          <div style={{
            marginBottom: '16px',
            padding: '12px',
            backgroundColor: THEME.bgDark,
            borderRadius: '8px'
          }}>
            <textarea
              value={newVideoDescription}
              onChange={(e) => setNewVideoDescription(e.target.value)}
              placeholder="Describe your video... (e.g., 'Claude and Gemini debating in the kitchen')"
              style={{
                width: '100%',
                minHeight: '60px',
                padding: '8px',
                borderRadius: '4px',
                border: `1px solid ${THEME.borderLight}`,
                backgroundColor: THEME.bgLight,
                color: THEME.textLight,
                resize: 'vertical'
              }}
            />
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '8px' }}>
              <span style={{ fontSize: '0.8em', color: THEME.textMuted }}>
                {newVideoDescription.length} characters
              </span>
              <button
                onClick={generateCustomVideo}
                disabled={isGenerating || newVideoDescription.length < 10}
                style={{
                  padding: '8px 16px',
                  backgroundColor: isGenerating ? THEME.textMuted : THEME.accent,
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: isGenerating ? 'not-allowed' : 'pointer',
                  fontWeight: 'bold'
                }}
              >
                {isGenerating ? '🎨 Generating...' : '🎨 Generate Scene'}
              </button>
            </div>
            {error && <div style={{ color: 'red', marginTop: '8px', fontSize: '0.9em' }}>{error}</div>}
          </div>

          {/* Show/Hide Toggle */}
          {hiddenVideos.size > 0 && (
            <div style={{ marginBottom: '12px', display: 'flex', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowHidden(!showHidden)}
                style={{
                  padding: '6px 12px',
                  backgroundColor: THEME.bgDark,
                  color: THEME.textLight,
                  border: `1px solid ${THEME.borderLight}`,
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '0.85em'
                }}
              >
                {showHidden ? '👁️ Hide Hidden Videos' : `👁️‍🗨️ Show Hidden Videos (${hiddenVideos.size})`}
              </button>
            </div>
          )}

          {/* Search / View Mode Controls */}
          <div style={{
            marginBottom: '16px',
            padding: '12px',
            backgroundColor: THEME.bgDark,
            borderRadius: '8px'
          }}>
            <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
              <button
                onClick={() => {
                  setViewMode('search');
                  setCurrentPage(1);
                }}
                style={{
                  flex: 1,
                  padding: '8px',
                  backgroundColor: viewMode === 'search' ? THEME.accent : THEME.bgLight,
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontWeight: viewMode === 'search' ? 'bold' : 'normal'
                }}
              >
                🔍 Search
              </button>
              <button
                onClick={() => {
                  setViewMode('chronological');
                  setCurrentPage(1);
                }}
                style={{
                  flex: 1,
                  padding: '8px',
                  backgroundColor: viewMode === 'chronological' ? THEME.accent : THEME.bgLight,
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontWeight: viewMode === 'chronological' ? 'bold' : 'normal'
                }}
              >
                📅 Chronological
              </button>
            </div>
            {viewMode === 'search' && (
              <div style={{ display: 'flex', gap: '8px' }}>
                <input
                  type="text"
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      setActiveSearchText(searchText);
                      setCurrentPage(1);
                    }
                  }}
                  placeholder="Search video prompts..."
                  style={{
                    flex: 1,
                    padding: '8px',
                    borderRadius: '4px',
                    border: `1px solid ${THEME.borderLight}`,
                    backgroundColor: THEME.bgLight,
                    color: THEME.textLight,
                    fontSize: '0.9em'
                  }}
                />
                <button
                  onClick={() => {
                    setActiveSearchText(searchText);
                    setCurrentPage(1);
                  }}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: THEME.accent,
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontWeight: 'bold',
                    fontSize: '0.9em',
                    whiteSpace: 'nowrap'
                  }}
                >
                  🔍 Search
                </button>
                {activeSearchText && (
                  <button
                    onClick={() => {
                      setSearchText('');
                      setActiveSearchText('');
                      setCurrentPage(1);
                    }}
                    style={{
                      padding: '8px 12px',
                      backgroundColor: THEME.bgLight,
                      color: THEME.textLight,
                      border: `1px solid ${THEME.borderLight}`,
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '0.9em'
                    }}
                  >
                    ✕
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Video List */}
          {(() => {
            // Filter videos based on view mode
            let filteredVideos = customVideos.filter(video => showHidden || !hiddenVideos.has(video.id));

            if (viewMode === 'search' && activeSearchText.trim()) {
              const searchLower = activeSearchText.toLowerCase();
              filteredVideos = filteredVideos.filter(video => {
                // Search in description
                if (video.description?.toLowerCase().includes(searchLower)) return true;
                // Search in video prompts
                if (video.videos?.some(v => v.prompt?.toLowerCase().includes(searchLower))) return true;
                // Search in characters
                if (video.characters?.some(char => char.toLowerCase().includes(searchLower))) return true;
                // Search in space
                if (video.space?.toLowerCase().includes(searchLower)) return true;
                return false;
              });
            }

            // Apply pagination
            const totalPages = Math.ceil(filteredVideos.length / videosPerPage);
            const startIndex = (currentPage - 1) * videosPerPage;
            const endIndex = startIndex + videosPerPage;
            const paginatedVideos = viewMode === 'chronological'
              ? filteredVideos.slice(startIndex, endIndex)
              : filteredVideos; // Show all results in search mode

            return (
              <>
                {viewMode === 'search' && !activeSearchText.trim() ? (
                  <div style={{ color: THEME.textMuted, textAlign: 'center', padding: '40px 20px' }}>
                    <div style={{ fontSize: '48px', marginBottom: '12px' }}>🔍</div>
                    <div>Enter search text and click Search to find videos</div>
                  </div>
                ) : filteredVideos.length === 0 ? (
                  <div style={{ color: THEME.textMuted, textAlign: 'center', padding: '20px' }}>
                    {viewMode === 'search' && activeSearchText.trim()
                      ? `No videos found matching "${activeSearchText}"`
                      : 'No custom videos yet'}
                  </div>
                ) : (
                  <>
                    <div style={{
                      color: THEME.textMuted,
                      fontSize: '0.85em',
                      marginBottom: '12px',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}>
                      <span>
                        Showing {paginatedVideos.length} of {filteredVideos.length} videos
                      </span>
                      {viewMode === 'chronological' && totalPages > 1 && (
                        <span>Page {currentPage} of {totalPages}</span>
                      )}
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                      {paginatedVideos.map(video => (
                <div key={video.id} style={{
                  backgroundColor: THEME.bgLight,
                  borderRadius: '8px',
                  padding: '12px',
                  border: `1px solid ${THEME.borderLight}`,
                  opacity: hiddenVideos.has(video.id) ? 0.6 : 1
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '8px' }}>
                    <div style={{ color: THEME.textLight, fontWeight: 'bold', flex: 1 }}>
                      {video.description.substring(0, 80)}{video.description.length > 80 ? '...' : ''}
                    </div>
                    <button
                      onClick={() => toggleVideoVisibility(video.id)}
                      style={{
                        padding: '4px 8px',
                        backgroundColor: hiddenVideos.has(video.id) ? THEME.success : THEME.bgDark,
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '0.75em',
                        marginLeft: '8px',
                        flexShrink: 0
                      }}
                    >
                      {hiddenVideos.has(video.id) ? '👁️ Show' : '🙈 Hide'}
                    </button>
                  </div>
                  <div style={{ display: 'flex', gap: '8px', marginBottom: '8px', flexWrap: 'wrap' }}>
                    {video.characters?.map(char => (
                      <span key={char} style={{
                        backgroundColor: THEME.private,
                        color: 'white',
                        padding: '2px 6px',
                        borderRadius: '4px',
                        fontSize: '0.75em'
                      }}>
                        {char}
                      </span>
                    ))}
                    {video.space && (
                      <span style={{
                        backgroundColor: THEME.public,
                        color: 'white',
                        padding: '2px 6px',
                        borderRadius: '4px',
                        fontSize: '0.75em'
                      }}>
                        📍 {video.space}
                      </span>
                    )}
                    <span style={{
                      backgroundColor: video.status === 'completed' ? '#10b981' : video.status === 'failed' ? '#ef4444' : '#f59e0b',
                      color: 'white',
                      padding: '2px 6px',
                      borderRadius: '4px',
                      fontSize: '0.75em'
                    }}>
                      {video.status}
                    </span>
                  </div>
                  {video.status === 'completed' && (
                    <>
                      {video.videos && video.videos.length > 1 ? (
                        // Multiple videos - show all parts
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginTop: '8px' }}>
                          {video.videos.map((videoPart, idx) => (
                            <div key={idx} style={{ position: 'relative' }}>
                              <div style={{
                                fontSize: '0.85em',
                                color: THEME.textLight,
                                marginBottom: '4px',
                                fontWeight: 'bold'
                              }}>
                                Part {videoPart.part} of {videoPart.total_parts}
                                {videoPart.regeneration_count > 0 && (
                                  <span style={{
                                    marginLeft: '8px',
                                    color: THEME.textMuted,
                                    fontSize: '0.9em',
                                    fontWeight: 'normal'
                                  }}>
                                    (Regenerated {videoPart.regeneration_count}x)
                                  </span>
                                )}
                              </div>
                              <video
                                controls
                                draggable="true"
                                onDragStart={(e) => handleDragStart({ ...video, video_url: videoPart.url }, e)}
                                src={videoPart.url}
                                style={{
                                  width: '100%',
                                  maxWidth: '600px',
                                  borderRadius: '4px',
                                  cursor: 'grab'
                                }}
                              />
                              {videoPart.prompt && (
                                <div style={{
                                  marginTop: '8px',
                                  padding: '8px',
                                  backgroundColor: THEME.bgDark,
                                  borderRadius: '4px',
                                  fontSize: '0.85em',
                                  color: THEME.textMuted
                                }}>
                                  <strong style={{ color: THEME.textLight }}>Prompt:</strong> {videoPart.prompt}
                                </div>
                              )}
                              <button
                                onClick={() => regenerateCustomVideo(video.id, idx)}
                                disabled={regeneratingVideo === `${video.id}-${idx}`}
                                style={{
                                  marginTop: '4px',
                                  padding: '4px 8px',
                                  backgroundColor: THEME.primary,
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '4px',
                                  cursor: regeneratingVideo === `${video.id}-${idx}` ? 'not-allowed' : 'pointer',
                                  fontSize: '0.8em'
                                }}
                              >
                                {regeneratingVideo === `${video.id}-${idx}` ? '⏳ Regenerating...' : '🔄 Regenerate This Part'}
                              </button>
                            </div>
                          ))}
                        </div>
                      ) : (
                        // Single video
                        <div style={{ position: 'relative' }}>
                          <video
                            controls
                            draggable="true"
                            onDragStart={(e) => handleDragStart(video, e)}
                            src={video.video_url}
                            style={{
                              width: '100%',
                              maxWidth: '600px',
                              borderRadius: '4px',
                              cursor: 'grab'
                            }}
                          />
                          {video.videos && video.videos[0] && video.videos[0].prompt && (
                            <div style={{
                              marginTop: '8px',
                              padding: '8px',
                              backgroundColor: THEME.bgDark,
                              borderRadius: '4px',
                              fontSize: '0.85em',
                              color: THEME.textMuted
                            }}>
                              <strong style={{ color: THEME.textLight }}>Prompt:</strong> {video.videos[0].prompt}
                            </div>
                          )}
                          {video.videos && video.videos[0] && (
                            <button
                              onClick={() => regenerateCustomVideo(video.id, 0)}
                              disabled={regeneratingVideo === `${video.id}-0`}
                              style={{
                                marginTop: '4px',
                                padding: '4px 8px',
                                backgroundColor: THEME.primary,
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: regeneratingVideo === `${video.id}-0` ? 'not-allowed' : 'pointer',
                                fontSize: '0.8em'
                              }}
                            >
                              {regeneratingVideo === `${video.id}-0` ? '⏳ Regenerating...' : '🔄 Regenerate Video'}
                            </button>
                          )}
                        </div>
                      )}
                      <div style={{ fontSize: '0.75em', color: THEME.textMuted, marginTop: '4px' }}>
                        💡 Drag video to add to stitcher • Created: {new Date(video.created_at).toLocaleString()}
                      </div>
                    </>
                  )}
                </div>
              ))}
                    </div>

                    {/* Pagination Controls */}
                    {viewMode === 'chronological' && totalPages > 1 && (
                      <div style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        gap: '8px',
                        marginTop: '16px'
                      }}>
                        <button
                          onClick={() => setCurrentPage(1)}
                          disabled={currentPage === 1}
                          style={{
                            padding: '6px 12px',
                            backgroundColor: currentPage === 1 ? THEME.textMuted : THEME.bgLight,
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                            fontSize: '0.85em'
                          }}
                        >
                          ⏮ First
                        </button>
                        <button
                          onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                          disabled={currentPage === 1}
                          style={{
                            padding: '6px 12px',
                            backgroundColor: currentPage === 1 ? THEME.textMuted : THEME.bgLight,
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                            fontSize: '0.85em'
                          }}
                        >
                          ◀ Prev
                        </button>
                        <span style={{
                          padding: '6px 12px',
                          color: THEME.textLight,
                          fontSize: '0.85em'
                        }}>
                          {currentPage} / {totalPages}
                        </span>
                        <button
                          onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                          disabled={currentPage === totalPages}
                          style={{
                            padding: '6px 12px',
                            backgroundColor: currentPage === totalPages ? THEME.textMuted : THEME.bgLight,
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                            fontSize: '0.85em'
                          }}
                        >
                          Next ▶
                        </button>
                        <button
                          onClick={() => setCurrentPage(totalPages)}
                          disabled={currentPage === totalPages}
                          style={{
                            padding: '6px 12px',
                            backgroundColor: currentPage === totalPages ? THEME.textMuted : THEME.bgLight,
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                            fontSize: '0.85em'
                          }}
                        >
                          Last ⏭
                        </button>
                      </div>
                    )}
                  </>
                )}
              </>
            );
          })()}
        </div>
      )}
    </div>
  );
};

export default GameStepPage; 